// Composer Studio — 에디터 (Phase 3a 셸 → 3b-1 편집 → 3b-2 드래그·리사이즈)
//
// admin WYSIWYG 보고서 양식 빌더. 누적 범위:
//   - 3분할 레이아웃(팔레트 / 캔버스 / 속성 패널)
//   - 캔버스에 RenderTemplate 렌더 + 클릭 선택(data-composer-node-id 역추적) + 아웃라인
//   - 팔레트 클릭 → 노드 추가(선택 그룹 안 / body)
//   - 선택 노드 삭제 / 순서 이동(↑↓, Flow 네이티브 형제 순서) / undo·redo
//   - 속성 패널 편집화(text 본문, layout.width/minHeight, [3-binding] 노드↔카탈로그 바인딩).
//   - 키보드: Delete/Backspace 삭제, ArrowUp/Down 순서, Ctrl+Z/Ctrl+Shift+Z(Ctrl+Y) undo/redo
//   - [3b-2] @dnd-kit 드래그: 형제 재정렬 / 그룹↔body 재배치(삽입선) → moveNodeTo + 히스토리
//   - [3b-2] 리사이즈 핸들: 선택 노드 우변(width)/하변(height) px 조절(pointerup 1히스토리).
//     fill/hug/미지정 노드도 드래그 시작 시 실측 px로 고정(px) 전환(Figma식, 형제 불변).
//
// 범위 외(3c+): 그룹 만들기/해제·다중선택·표 셀 내부 드래그·라이브러리·저장/영속화·카탈로그 API.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject, ReactElement, ReactNode } from 'react';
import TemplateDeleteConfirmBody, { type TemplateDeleteObligation } from './TemplateDeleteConfirmBody';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  createDefaultNode,
  DEFAULT_APPROVAL_CONFIG,
  deriveComposerWidgets,
  collectBindingPlacements,
  syncComposerWidgets,
  insertWidgetAt,
  removeWidgetById,
  moveWidget,
  duplicateComposerWidget,
  duplicateComposerWidgets,
  cloneWidgetsForInsert,
  countDerivedInputsExcludedOnClone,
  countPresetExcludedDerivedInputs,
  insertWidgetsAfterId,
  moveWidgetInSiblings,
  resolveTableFields,
  updateWidget,
  widgetParentId,
  groupWidgets,
  ungroupWidget,
  createGroupWidget,
  createDesignWidget,
  createRegisteredWidget,
  createBranchWidget,
  addBranchWidgetSlot,
  removeBranchWidgetSlot,
  moveBranchWidgetSlot,
  updateBranchWidgetSlot,
  ComposerWidgetTreeView,
  ComposeWritePageShell,
  deriveWriteForm,
  insertNode,
  moveNodeInSiblings,
  moveNodeTo,
  insertNodeIntoCell,
  isCellInsertableKind,
  removeNode,
  updateNode,
  updateCell,
  updateCells,
  mapCells,
  addRow,
  removeRow,
  addCol,
  removeCol,
  setColWidths,
  setRowHeights,
  defaultCellStyle,
  mergeCellRight,
  mergeCellDown,
  mergeCellsRect,
  expandMergeRect,
  unmergeCell,
  canMergeCellRight,
  canMergeCellDown,
  canMergeCellsRect,
  isRectFullySelected,
  canUnmergeCell,
  addRepeatRegion,
  removeRepeatRegion,
  updateRepeatRegion,
  effectiveGroupByCols,
  groupNodes,
  ungroupNode,
  moveNodeOutOfContainer,
  setContainerKind,
  walkBindings,
  pagePx,
  pageMm,
  PAGE_MM_PORTRAIT,
  // 페이지번호 겹침 경고 — 판정·필요 높이를 **rc가 소유한 값**으로 묻는다(줄높이 상수를 패널이
  // 다시 비교하면 조각의 최소 높이가 바뀐 날 패널만 거짓말한다). 번호 «문자열» 쪽 판정은 shared.
  pageNumberBandHeightPx,
  pageNumberOverlapsBody,
  addBranchSlot,
  removeBranchSlot,
  moveBranchSlot,
  updateBranchSlotCondition,
  updateBranchSlotLabel,
  updateBranchConditionBindings,
  insertNodeIntoBranchSlot,
  wrapNodeInBranch,
  resolveGroupColumns,
  resolveWidgetFullWidth,
  resolveWidgetSpanRatio,
  resolveWidgetHidden,
  moveWidgetInSiblingsForView,
  moveWidgetToIndexForView,
  hasSiblingViewOrder,
  clearSiblingViewOrder,
  isEditableListDataSource,
  createRepeatWidgetForDataSource,
  buildRepeatSkeletonWidgets,
  repeatSkeletonWidgetForField,
  repeatMissingFields,
  findEnclosingRepeatWidget,
  COMPOSER_INPUT_LABEL_DEFAULT_STYLE,
  COMPOSER_HEADING_DEFAULT_STYLE,
  COMPOSER_SECTION_TITLE_DEFAULT_STYLE,
  COMPOSER_LABEL_HINT_DEFAULT_STYLE,
  computeFtCellRange,
  collectFtBlockLayout,
  ftSelectionTopLeft,
  ftBlockTargets,
  ftInsertRow,
  ftRemoveRow,
  ftInsertColumn,
  ftRemoveColumn,
  ftMergeCells,
  ftUnmergeCell,
  ftCanMergeCells,
  ftRowSpanInvolvedRowIds,
  normalizeFormTable,
  effectiveOptionsSources,
  applyVariableDynamicOptions,
  collectSortableSelectOptionSources,
  findEntryUserInputMeta,
  collectVariableOptionsSourceKeys,
  collectWidgetDefaultValues,
  clonePresetSubtreeForInsert,
  normalizeSubtreeForPresetSave,
  presetSubtreeEquivalent,
  DataSourceRowsField,
  COMPOSED_ROW_WIDGETS,
  registeredRowWidgetKeys,
  registeredRowWidgetOptions,
  mergeMethodReresolve,
  collectMethodReresolveTouchedKeys,
  methodTriggerSignature,
  resolveFindingRiskMethodValue,
  INJECTED_ROW_SOURCES,
  ANNOTATED_IMAGE_DEFAULT_COLOR,
  DEFAULT_STROKE_WIDTH,
  applyFieldDisplayOverride,
  orderSiblingsForView,
  isRepeatTableNonColumnChild,
  repeatTableColumnChildren,
  repeatTableColumnHeaderLabel,
  repeatTableColumnHeaderSource,
  DateFieldInput,
  DateRangeFieldInput,
  unitFlowChunks,
  branchGainsChunks,
} from '@safetyhub/report-composer';
import type { RepeatTableColumnHeaderSource } from '@safetyhub/report-composer';
import type { RegisteredObjectSubfield } from '@safetyhub/report-composer';
import type { DataSourceRowsRenderer } from '@safetyhub/report-composer';
import type { FtCellRef, PresetUnresolvedRef } from '@safetyhub/report-composer';
import type { ComposerTextStyleDefaults } from '@safetyhub/report-composer';
import type { ComposerNode } from '@safetyhub/report-composer';
import type { ReportTemplate } from '@safetyhub/report-composer';
import type { ComposerWidgetSyncResult } from '@safetyhub/report-composer';
import type { BindingPlacement } from '@safetyhub/report-composer';
import type {
  ApprovalBlockConfig,
  ApprovalFontStyle,
  RenderData,
} from '@safetyhub/report-composer';
import {
  buildCatalogIndex,
  type CatalogEntry,
  type CatalogField,
  type Binding,
  type BindingFormat,
  type FormatScopeSource,
  type WidgetType,
  type ComposerWidget,
  type ComposerWidgetKind,
  type ComposerTextStyle,
  type InputWidget,
  type InputAutoFill,
  type BranchWidget,
  type BranchWidgetSlot,
  type ModalCommitMap,
  type ModalTriggerWidget,
  type FormTableWidget,
  type FormTableColumn,
  type FormTableFixedCell,
  type FormTableRow,
  type InputOption,
  type TemplateVariable,
  type TemplateCatalogParamBindings,
  type CatalogParamResolutionMap,
  type ReportComposerPreviewResponse,
  type ReportComposerPreviewContext,
  type SimulationPreset,
  type PreviewSimulationAxis,
  type PreviewSimulationOption,
  PREVIEW_SIMULATION_SPLIT_AXIS_KEY,
  PREVIEW_SIMULATION_APPROVAL_SIGNED_AXIS_KEY,
  type TextStyle,
  type CheckboxNode,
  type ChoiceGroupNode,
  type GroupNode,
  type ImageNode,
  type ImageOverlayOptions,
  type PageSize,
  type RectStyle,
  type TableCell,
  type TableNode,
  type BranchNode,
  type RepeatNode,
  type ConditionBinding,
  type TableCellStyle,
  type TableCellDiagonal,
  type CellCompute,
  type CellComputeOp,
  type ComputeInput,
  type TableRepeatRegion,
  type BindingLifecycle,
  type SpecialKind,
  type ApprovalLineMetaRow,
  DEFAULT_APPROVAL_LINE_META,
  type DataDef,
  type DataDefField,
  type DataSourceDef,
  type DataSourceFieldMap,
  type SortableSourceFieldMap,
  type DataSourceFieldWidget,
  type DataSourceLookup,
  type RegisteredWidgetWrite,
  REGISTERED_FORM_WIDGETS_META,
  effectiveModalPurpose,
  ftTextCellPrefillKey,
  COMPOSER_FONTS,
  VALUE_SETS,
  COMPOSER_VIEWS,
  composerViewLabel,
  COMPOSER_BASE_VIEW_KEY,
  COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS,
  COMPOSER_AI_CANDIDATE_ROW_FIELDS,
  ROWS_SEED_SUFFIX,
  RISK_METHOD_FLAT_KEY,
  improvementCandidatesToRows,
  comboboxAutoFieldMap,
  DISPLAY_TEMPLATE_FIELD_RE,
  parseDisplayTemplateSegments,
  renderDisplayTemplate,
  sanitizeDisplayTemplateLiteral,
  serializeDisplayTemplateSegments,
  type DisplayTemplateSegment,
  type ComposerViewOverride,
  type GroupWidget,
  type ComposeChromeSlot,
  type ComposeMode,
  type ImprovementCandidatePart,
  type TemplatePolicy,
  type OrganizationCategory,
  ORGANIZATION_CATEGORY_LABELS,
  overlayAxisDefaultVisible,
  type TableFieldDisplayOverride,
  isRepeatHintRowRangeValid,
  // 문서 페이지번호(용지 설정 패널) — 선택지·기본값·경고 판정은 shared 정본에서 파생한다.
  // 패널이 자기 기본값이나 조건을 따로 들면 엔진과 조용히 갈린다.
  type PageNumberFormat,
  type PageNumberPosition,
  PAGE_NUMBER_DEFAULTS,
  PAGE_NUMBER_FORMAT_OPTIONS,
  PAGE_NUMBER_POSITION_OPTIONS,
  pageNumbersNeverShow,
  // 행 순서 선언의 효력 판정 — 서버 규칙 파생(collectSourceOrderRules)과 같은 술어.
  canApplySourceOrder,
  // 동적 이름 뒤 조사(을/를 · 이/가)를 받침으로 고른다 — 「을(를)」 병기를 다시 쓰지 말 것(web 가드가 잡는다).
  objectParticleOf,
  subjectParticleOf,
  withObjectParticle,
} from '@safetyhub/shared';
import { useQuery } from '@tanstack/react-query';
import {
  adminApi,
  extractApiError,
  type BindingSuggestField,
  type BindingSuggestion,
  type ReportTemplateCategory,
  type StoredReportTemplate,
  type WidgetPreset,
} from '../../lib/api';
import { apiErrorMessage } from '../../lib/apiErrorMessage';
import { useToastStore } from '../../stores/toast';
import {
  emptyTemplate,
  mmToPx,
  pxToMm,
  pxToMmAtLeast,
  ptToPx,
  pxToPt,
  defaultMarginsPx,
} from './sampleTemplate';
import { findNodeById } from './findNodeById';
import {
  WidgetModeInertGate,
  WRITE_FORM_MODE_OPTIONS,
  anyWidgetHasValue,
} from './widgetModeInertGate';
import { bindingFormatOptionLabel } from './bindingFormatLabels';
import { computeCellRange, parseCellRef, type CellRef } from './computeCellRange';
import {
  composePatternRowSet,
  countMappingsDroppedFromBand,
  countRelocateSkips,
  planPatternRowSet,
  reducePatternPick,
  retainedBandFor,
  virtualRowFromFillOrdinal,
  type PatternPickInput,
  type PatternPickStage,
  type PatternRowSetPlan,
} from './repeatPatternRowPick';

/**
 * 좌표 픽('cell' 갈래) 시작 옵션 — 픽 state와 이를 시작시키는 패널 prop이 공유하는 형태.
 * (한 곳에서만 선언해 EditorPage/PropertyPanel/RepeatRegionEditor 3중 선언 드리프트를 막는다.)
 */
interface CellPickOpts {
  /** 픽 대상 표(다른 표 셀 클릭은 거부). */
  tableId?: string;
  /** 매핑 허용 행 밴드(null=제약 없음). 강조 오버레이와 픽 게이트가 같은 술어를 공유한다. */
  anchorRows?: { start: number; end: number } | null;
  /** 픽 안내 배너 문구(미지정이면 기본 문구). */
  hint?: string;
  /** 채움 행(표 아래 빈 괘선 행) 클릭 갈래 — 인자는 그 표 채움 행 중 순번(1-based). */
  onFillRow?: (fillOrdinal: number) => void;
}
import {
  buildCellClipboard,
  buildTsv,
  parseTsv,
  serializeCellClipboard,
  parseCellClipboard,
  CELL_CLIPBOARD_KEY,
} from './cellClipboard';
import { adjacentTableCell, nextTableCell } from './cellNavigation';
import { useEditorHistory } from './useEditorHistory';
import { useNumericPreviewInput } from './numericPreviewInput';
import type { ArrowStepConfig, PreviewPatchHandlers } from './numericPreviewInput';
import { DragLayer, decodeDropId } from './DragLayer';
import { InlineTextEditor, type EditingTarget, type CloseReason } from './InlineTextEditor';
import { CellCaptureEditor } from './CellCaptureEditor';
import { EditorPanelLayout, type EditorPanelLayoutHandle } from './EditorPanelLayout';
import { CatalogCombobox, type ComboboxOption } from './CatalogCombobox';
import {
  EXPLORER_COMPACT_PX,
  EXPLORER_EXPANDED_PX,
  TemplateExplorer,
  TemplateExplorerRail,
} from './TemplateExplorer';
import { CatalogEntryPicker, injectedRowSourceEntry } from './CatalogEntryPicker';
import { nextComputeInputName } from './bulkCompute';
import { withWidgetTypeChange } from './widgetTypeChange';
import {
  CatalogPathPicker,
  computeSelectMix,
  mergeBulkSelect,
  type SelectMix,
} from './CatalogPathPicker';
import {
  CATALOG_PARAM_PROVENANCE_LABEL,
  buildCatalogParamItems,
  buildCatalogParamRows,
  catalogEffectiveIdName,
  catalogParamAbsentNote,
  catalogParamBindingSummary,
  catalogParamEffectiveNote,
  collectCatalogConsumers,
  missingRequiredCatalogParams,
  setCatalogParamBinding,
} from './catalogParamBindings';
import {
  type VariableConsumerRef,
  collectVariableConsumers,
  variableDeleteConfirmBody,
  variableRefSummary,
  variableRefTotal,
} from './variableRefs';
import {
  CatalogParamBindingRow,
  CatalogParamEffectiveNote,
  CatalogParamInlineBlock,
  CatalogParamsEditContext,
  type CatalogParamsEditChannel,
} from './CatalogParamEditing';
import { collectTemplateIssues, type TemplateIssue } from './templateIssues';
import { TemplateIssuesBadge } from './TemplateIssuesBadge';
import { type PreviewContextLabels } from './PreviewContextBar';
import { derivePreviewSimulationAxes } from './previewSimulationAxes';
import {
  buildPreviewProvenanceMaterial,
  splitLinkedReportId,
  toApiPreviewContext,
  toBindingProvenance,
  type EditorPreviewContext,
} from './previewLinkedReport';
import {
  applyPresetToSimulation,
  clearPresetStorableSimulation,
} from './simulationPresets';
import {
  SimulationPresetsSection,
  type SimulationPresetsChange,
} from './SimulationPresetsSection';
import {
  clearAllSimulation,
  previewEntitiesChanged,
  releasePlanSimulation,
  restoredPreviewStateFor,
  toPersistablePreviewContext,
  withoutAllSimulationLabels,
} from './previewContextPersistence';
import { SimulationChip } from './SimulationChip';
import { ADMIN_COMPOSER_FORM_WIDGETS } from './widgets/registeredFormWidgets';
import { RealDataModal } from './RealDataModal';
// 본문 미리보기 resolve에 실을 방식(저작자가 캔버스에서 고른 값) 판정 — 순수 층.
import { resolveAuthoredFindingRiskMethod } from './previewMethodOverride';
import { LoadingOverlay, Spinner } from '../../components/LoadingOverlay';
import {
  buildBindingSummary,
  conditionBindingSourceInfo,
  type ConditionSourceInfo,
} from './bindingSummary';
import { BindingBadgeOverlay } from './BindingBadgeOverlay';
import { BranchSlotTabsOverlay } from './BranchSlotTabsOverlay';
import { BranchLinkOverlay } from './BranchLinkOverlay';
import { WriteCanvasBranchTabsOverlay } from './WriteCanvasBranchTabsOverlay';
import { WidgetEdgeResizeOverlay, type SpanEdit } from './WidgetEdgeResizeOverlay';
import { PaddingOverlay } from './PaddingOverlay';
import {
  PagedEditBody,
  mergePreviewContent,
  applySimulatedApproval,
  COMPOSER_SPECIAL_COMPONENTS,
} from './previewRender';
import { PoolHoverPreview } from './PoolHoverPreview';
import {
  PREVIEW_POPOUT_CHANNEL,
  PREVIEW_POPOUT_ROUTE,
  PREVIEW_POPOUT_WINDOW_NAME,
  type PreviewPopoutMessage,
  type PreviewSyncPayload,
} from './previewPopoutChannel';
import { NodeEdgeResizeOverlay } from './NodeEdgeResizeOverlay';
import { SelectionOverlay } from './SelectionOverlay';
import { TableEdgeResizeOverlay } from './TableEdgeResizeOverlay';
import { EditorToolbar } from './EditorToolbar';
import { WriteFormToolbar } from './WriteFormToolbar';
import { ComputeSourceHighlightOverlay } from './ComputeSourceHighlightOverlay';
import { RepeatRowHighlightOverlay, rowWithinAnchorRows } from './RepeatRowHighlightOverlay';
import { ClipboardSourceOverlay } from './ClipboardSourceOverlay';
import { CanvasViewport } from './CanvasViewport';
import { ShortcutsModal } from './ShortcutsModal';
import { HelpLink } from './HelpLink';
import { type ManualLinkKey } from './manualLinks';
// 표형 repeat 열 이동 — 비시각 위젯(hideButton modalTrigger)을 건너뛴 인접 열 인덱스 계산.
import { repeatColumnMoveTargetIndex, canMoveRepeatColumn } from './repeatColumnOrder';
import { ComposerContextMenu, type ComposerContextMenuItem } from './ComposerContextMenu';
import { CellDeleteModal, type CellDeleteChoice } from './CellDeleteModal';
import { TemplatePublishModal } from './TemplatePublishModal';
import { TemplateDuplicateModal } from './TemplateDuplicateModal';
import { TemplateKeyModal } from './TemplateKeyModal';
import { TemplateCategoryManageModal } from './TemplateCategoryManageModal';
import { UNCATEGORIZED_LABEL } from './templateCategoryOptions';
import { SaveAsPresetModal, type SaveAsPresetInput } from './SaveAsPresetModal';
import { FormImportModal } from './FormImportModal';
import { FormImportJobPill } from './FormImportJobPill';
import { useFormImportJob } from './useFormImportJob';
import { DatasetExcelImportModal } from './DatasetExcelImportModal';
import type { DatasetExcelImportResult } from './DatasetExcelImportModal';
import { DatasetCatalogImportModal } from './DatasetCatalogImportModal';
import type { DatasetCatalogImportResult } from './DatasetCatalogImportModal';
import { BindingSuggestModal } from './BindingSuggestModal';
import {
  applyCatalogConnectSuggestions,
  applyPrefillSuggestions,
  applyRepeatHintToTemplate,
  collectAppliedBindings,
  collectBindingSuggestFields,
  deriveWidgetGroupContexts,
  disconnectAppliedBindings,
  insertBindingSuggestField,
  removeImportRepeatHint,
  writeFormLabelsOf,
  REPEAT_HINT_ROW_RANGE_ERROR,
  type ImportRepeatHint,
} from './formImportOps';
import { toUpdateBody } from './templateUpdateBody';
import { SplitAxisSection } from './SplitAxisSection';
import { PostSubmitFlowSection } from './PostSubmitFlowSection';
import {
  clampPreviewSubmitSituationKey,
  previewSubmitSituationOptions,
  resolvePreviewSubmitSituation,
  type PreviewSubmitSituationKey,
} from './previewSubmitSituation';
import { toEditableTemplate } from './templateEditableLoad';
import { titleTemplateTokensFor } from './titleTemplateTokens';
import {
  branchSlotSelectionPatch,
  collectOwnerSlotPaths,
  collectSharedKeyOwners,
  collectSharedKeyOwnerDetails,
  makeMutualExclusionCheck,
  ownerAnchorSelector,
  SharedKeyFocusContext,
  useSharedKeyFocus,
  type SharedKeyFocus,
  type SharedKeyFocusChannel,
} from './sharedKeyHighlight';
import { SharedKeyUsageList } from './SharedKeyUsageList';
import { fillUnplacedWidgets, rebuildWidgetTreeFromBody } from './writeFormAutoLayout';
import { PresetInstanceVersionPanel } from './PresetInstanceVersionPanel';
import { Button, Card, ConfirmModal, Modal, TriStateCheckbox } from '@safetyhub/ui';
import {
  collectAllNodeIds,
  findNodeContainer,
  listDraggables,
  walkNodeTree,
  type NodeContainerLoc,
} from './nodeTreeWalk';
import { cloneNodesWithNewIds } from './nodeTreeClone';
import {
  buildParentMap,
  getAncestorChain,
  resolveDrillTarget,
  resolveSingleClickTarget,
} from './selectionModel';
import {
  applyPasteApprovalMeta,
  applyPasteDependencies,
  applyWidgetClipboardPaste,
  buildNodeClipboardPayload,
  buildWidgetClipboardPayload,
  buildWidgetPasteReport,
  isSameTemplatePaste,
  mergeClipboardDependencies,
  NODE_CLIPBOARD_KEY,
  NODE_CLIPBOARD_TEXT_PREFIX,
  parseNodeClipboard,
  parseNodeClipboardText,
  serializeNodeClipboard,
  shouldShowWidgetPasteReport,
  type NodeClipboardPayload,
  type WidgetPasteReport,
} from './nodeClipboard';
import { resolveWidgetPasteRoute, isWidgetPasteDispatchStale } from './widgetPasteRoute';
import { NodePasteChoiceModal } from './NodePasteChoiceModal';
import { WidgetPasteReportModal } from './WidgetPasteReportModal';
import { isKebabKey, KEBAB_KEY_ERROR, flatKeyError, nextAutoFieldKey } from './keyValidation';
import {
  clearDataSourceFieldWidget,
  patchDataSourceField,
  setDataSourceFieldWidget,
} from './dataSourceWriteForm';
// 서명 슬롯 필드의 위젯 종속 설정(서명자 이름 필드 + 슬롯 키 표시) + "위젯 설정" 칸 분기 판정.
import {
  SignatureSlotFieldSettings,
  dataSourceFieldSettingsKind,
} from './SignatureSlotFieldSettings';
// 사진 업로드(photo) 필드의 발견사항 조치사진 반영 설정(findingWriteBack — 결재 완료 시 점검 반영, 2026-09-14).
import { FindingWriteBackFieldSettings } from './FindingWriteBackFieldSettings';
import {
  widgetOptionsForRepeatRow,
  repeatRowBindingOptions,
  resolveRepeatContractSub,
  isAutoWiredContractOptionsFrom,
  cellWidgetFromRepeatOption,
  type RepeatRowBindingOption,
} from './repeatRowScope';
// 반복 행 스코프 writes target 후보(배치 위젯 ∪ 행 필드 선언, 자기 키 허용 여부) — 순수 모듈.
import { repeatScopeWritesTargets } from './suggestionWritesTargets';
// 등록형 위젯 키별 동작 조건 안내 — 소스 필드 표·트리 등록형 패널이 같은 함수(표면 비대칭 방지).
import { registeredWidgetKeyHint } from './registeredWidgetHints';
// 반복 행 안 역전파 판정(서버 collectRowScopeWriteBackWidgets 게이트 미러) + combobox 합성 prefill.
import {
  comboboxSyntheticPrefill,
  judgeRowScopeWriteBack,
  type RowScopeWriteBackJudgement,
} from './rowScopeWriteBack';
// 위젯 단위 역전파 가용 판정 + select 아래 안내문(순수 함수, co-located spec).
import {
  writeBackAvailability,
  writeBackCatalogScope,
  writeBackHelpText,
  writeBackWritableFieldMatch,
} from './writeBackAvailability';
import { FIELD_LABEL_CLS, INPUT_CLS, SELECT_CLS } from './editorFieldClasses';
import {
  HybridKeyPicker,
  rowFieldPickerOptions,
  type HybridKeyOption,
} from './HybridKeyPicker';
import { TemplateVarInput } from './TemplateVarInput';
// 표시 형식 "적용 값 미리보기" 해소 — 엔진(withFormatScope → getByPath → toText)만 쓰는 순수 층.
// 형식별 컴포넌트는 아래(UnitPreview·SwitchCasePreview·TemplateResultPreview·MapMissNote)에 있고
// **규약은 이 모듈 하나**다(formatPreview 머리말 — 자체 해소를 만들면 렌더와 조용히 갈라진다).
import {
  readFormatPath,
  readFormatScopeRaw,
  resolveMapMissBehavior,
  resolveSwitchPreview,
  resolveTemplatePreview,
  resolveUnitPreview,
  VALUE_PLACEHOLDER,
  type PathPreview,
} from './formatPreview';
import {
  findSuggestionSource,
  patchSuggestionsFrom,
  suggestionSourceOptions,
  suggestionSourceRowFields,
} from './suggestionSources';
import {
  columnAlignPatch,
  columnCollapseLinesPatch,
  columnWidgetPatch,
  columnWriteTargetOptions,
  patchTableColumn,
  patchTableColumnSuggestionsFrom,
  setTableColumnSuggestionWrites,
  siblingColumnOptions,
  TABLE_COLUMN_KEY_READONLY_REASON,
} from './tableColumnFields';
import {
  isTableWidget,
  TABLE_WIDGET_BULK_PROP_ORDER,
  TABLE_WIDGET_PROP_APPLICABLE,
  TABLE_WIDGET_PROP_BULK,
  TABLE_WIDGET_PROP_ORDER,
  type TableWidgetPropKey,
} from './tableWidgetProps';
import {
  coreAddWidgetEntries,
  registeredAddWidgetEntries,
  dispatchAddWidgetValue,
  type AddWidgetCallbacks,
} from './composerAddWidgetCatalog';
import { composerInputPrimaryLabel } from './composerWidgetLabels';
import { resolveWriteAddPlacement, addWidgetGates } from './composerAddPlacement';
import { repeatOverCandidates } from './repeatOverCandidates';
import { canRepeatRequireMinRow, judgeRepeatRequiredMinRow } from './repeatRequiredGate';
import { judgeInspectionSelectionMulti } from './inspectionSelectionGate';
import { RowAddRemoveToggles } from './RowAddRemoveToggles';
import { composerWidgetSiblingIds } from './composerWidgetSelection';
import { ComposeChromeEditor, ComposePageSettingsEditor } from './ComposeChromeEditor';
import { ComposeChromePreviewFrame } from './ComposeChromePreviewFrame';
import {
  serializeCases,
  casesShallowEqual,
  rowsFromCases,
  type CaseRow,
} from './casesEditorUtils';
import ApprovalLineMetaEditor from '../../components/approval-line-meta/ApprovalLineMetaEditor';
import {
  collectRepeatSources,
  declaredChildrenForDataSourceField,
  deriveHeaderLabel,
  findRepeatSource,
  parentRowArrayFieldOptions,
  type RepeatSource,
  type RepeatSourceField,
} from './repeatSources';
import {
  enclosingRepeatNode,
  isRowScopeFieldBinding,
  mergeConditionKeyCandidates,
  rowScopeConditionKeys,
  rowScopeFieldSourceInfo,
  withVariableKeyOptions,
  type RowScopeConditionKey,
  type RowScopeFieldSourceInfo,
} from './rowScopeKeys';
import {
  treeWidgetTriggersRiskInjection,
  widgetKeyImpliesRiskInjection,
  RISK_NESTED_BINDING_HINT,
} from './riskInjectionHint';
import {
  isJoinByKeyDef,
  readJoinConfig,
  writeJoinConfig,
  readEmptyValue,
  writeEmptyValue,
  isUnknownSourceKey,
  nextDataDefKey,
  newJoinByKeyDef,
  summarizeComputeLines,
  readMergeFieldEntries,
} from './dataDefJoin';
import {
  countTablePlacements,
  detachTableWidgetDataDef,
  tableFieldsMode,
} from './dataDefFieldsNormalize';
import {
  renameDataSourceFieldEverywhere,
  addDataSourceColumn,
  deleteDataSourceColumn,
  deleteDataSourceFieldEverywhere,
  countDataSourceFieldCells,
  unmapDataSourceColumn,
  moveDataSourceColumn,
  isAutoFieldCellContent,
} from './rowsColumnOps';
import { rangeReturnPaths, scopeReturnPaths, resolveSelectField } from './catalogPaths';
import {
  addScopeSource,
  carryScopeSources,
  duplicateScopeKeys,
  formatReadsScope,
  isScopeSourceUsable,
  removeScopeSourceAt,
  patchScopeSourceAt,
  scopeSourceIssue,
  scopeSourcesOf,
  scopeUsageHint,
  setScopeSourceOrigin,
  shouldShowScopeEditor,
} from './formatScopeSources';
import {
  extractDataSourceDryRun,
  formatDryRunValue,
  DRY_RUN_MODAL_ROW_LIMIT,
  type DryRunResult,
} from './dataSourceDryRun';
import {
  parseCondition,
  serializeCondition,
  opTakesValue,
  shouldStartDirectValueInput,
  extractEqClauses,
  collectUnevaluableClauses,
  type ConditionModel,
  type ConditionClause,
  type ConditionOp,
  type ConditionJoiner,
} from './conditionExpr';

/** Ctrl/⌘ + <key> (shift/alt 없이) 조합인지. */
function isPlainMod(e: KeyboardEvent, key: string): boolean {
  return (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === key;
}

/** 폼 입력 요소(INPUT/TEXTAREA/contenteditable)에 포커스가 있는지 — 단축키가 텍스트 입력을 방해하지 않게. */
function isFormInputFocused(): boolean {
  const ae = document.activeElement as HTMLElement | null;
  if (!ae) return false;
  // 캡처 textarea(셀 탐색용 상시 포커스)는 "입력 중"이 아니다 — 편집 여부는 editingTarget로 가드하므로,
  // 탐색 모드(셀 선택)에서 Ctrl+D/M/B 등 셀 단축키가 캡처 포커스 때문에 죽지 않게 제외한다.
  if (ae.dataset && ae.dataset.composerCapture === 'true') return false;
  return ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable;
}

/**
 * Ctrl+A(scope-aware 노드 전체선택)용 — 주어진 컨테이너 위치의 형제 노드 id 전체를 반환한다.
 * loc이 null이거나 body면 최상위 노드 전체. 그룹/스택/셀 content/분기 슬롯/repeat 템플릿 모두
 * "해당 컨테이너의 직속 자식(형제)"로 일관 적용한다(scope-aware, Figma식). 컨테이너 미해석 시 빈 배열.
 */
function containerSiblingIds(body: ComposerNode[], loc: NodeContainerLoc | null): string[] {
  if (!loc || loc.kind === 'body') return body.map((n) => n.id);
  if (loc.kind === 'group') {
    const g = findNodeById(body, loc.parentId);
    return g && (g.kind === 'group' || g.kind === 'stack') ? g.children.map((n) => n.id) : [];
  }
  if (loc.kind === 'cell') {
    const t = findNodeById(body, loc.tableId);
    if (!t || t.kind !== 'table') return [];
    const cell = t.cells.find((c) => c.row === loc.row && c.col === loc.col);
    return cell?.content?.map((n) => n.id) ?? [];
  }
  if (loc.kind === 'branch-slot') {
    const b = findNodeById(body, loc.branchId);
    if (!b || b.kind !== 'branch') return [];
    const slot = b.branches.find((s) => s.id === loc.slotId);
    return slot?.children.map((n) => n.id) ?? [];
  }
  // repeat-template — 반복 템플릿이 그룹/스택이면 그 자식들이 형제.
  const tmpl = findNodeById(body, loc.templateId);
  return tmpl && (tmpl.kind === 'group' || tmpl.kind === 'stack')
    ? tmpl.children.map((n) => n.id)
    : [];
}

/**
 * Ctrl+Delete 행/열 삭제 대상 + preselect 기본 선택을 산출한다(admin 로컬 유틸).
 * - refs가 가리키는 셀들의 점유 슬롯(병합 span 포함)을 모아 실제로 걸친 행/열 집합을 구한다.
 *   (병합 span 내부 슬롯을 가리키는 ref도 그 슬롯을 덮는 실제 셀로 resolve.)
 * - fullRows: 걸친 각 행이 0..colCount-1 전 열을 점유(전체 행 선택)면 참 → 참이면 기본 '행'.
 * - fullCols: 대칭(0..rowCount-1 전 행 점유). 둘 다 아니면 기본 '행'.
 * (병합용 isRectFullySelected와 목적이 달라 별도 로컬 헬퍼로 둔다 — templateOps 오염 방지.)
 */
function analyzeCellDeletion(
  table: TableNode,
  refs: { row: number; col: number }[],
): { rows: number[]; cols: number[]; defaultChoice: CellDeleteChoice } {
  const occupied = new Set<string>();
  const rowSet = new Set<number>();
  const colSet = new Set<number>();
  for (const ref of refs) {
    // ref가 병합 span 내부 슬롯을 가리킬 수 있으니 그 슬롯을 덮는 실제 셀을 찾는다.
    const cell = table.cells.find((c) => {
      const rs = c.rowspan ?? 1;
      const cs = c.colspan ?? 1;
      return (
        ref.row >= c.row && ref.row < c.row + rs && ref.col >= c.col && ref.col < c.col + cs
      );
    });
    if (!cell) continue;
    const rs = cell.rowspan ?? 1;
    const cs = cell.colspan ?? 1;
    for (let rr = cell.row; rr < cell.row + rs; rr++) {
      for (let cc = cell.col; cc < cell.col + cs; cc++) {
        occupied.add(`${rr}::${cc}`);
        rowSet.add(rr);
        colSet.add(cc);
      }
    }
  }
  const rows = [...rowSet].sort((a, b) => a - b);
  const cols = [...colSet].sort((a, b) => a - b);
  const fullRows =
    rows.length > 0 &&
    rows.every((r) => {
      for (let c = 0; c < table.colCount; c++) {
        if (!occupied.has(`${r}::${c}`)) return false;
      }
      return true;
    });
  const fullCols =
    cols.length > 0 &&
    cols.every((c) => {
      for (let r = 0; r < table.rowCount; r++) {
        if (!occupied.has(`${r}::${c}`)) return false;
      }
      return true;
    });
  const defaultChoice: CellDeleteChoice = fullRows ? 'row' : fullCols ? 'col' : 'row';
  return { rows, cols, defaultChoice };
}

/** 작성 탭 formTable 키보드 셀 내비게이션 방향키 델타. */
const FT_ARROW_DELTA: Record<string, { dr: number; dc: number }> = {
  ArrowUp: { dr: -1, dc: 0 },
  ArrowDown: { dr: 1, dc: 0 },
  ArrowLeft: { dr: 0, dc: -1 },
  ArrowRight: { dr: 0, dc: 1 },
};

/**
 * 작성 탭 formTable 키보드 셀 내비게이션 좌표계(2026-07-14, 양식 탭 셀 이동 동형) —
 * fixed 행×열 격자에서 병합(row/colSpan) 커버 위치를 anchor 위치로 환원(resolve)하고,
 * 선택 가능한(커버되지 않은) 위치를 행 우선 순서(order)로 나열한다. Tab 순회·Ctrl+A 전체 선택 공용.
 */
function buildFtCellNav(w: FormTableWidget): {
  rows: { id: string }[];
  cols: { id: string }[];
  resolve: (ri: number, ci: number) => { ri: number; ci: number };
  order: { ri: number; ci: number }[];
} {
  const rows = w.rows.filter((r) => r.type === 'fixed');
  const cols = w.columns;
  const colIdx = new Map(cols.map((c, i) => [c.id, i]));
  const coveredBy = new Map<string, { ri: number; ci: number }>();
  rows.forEach((r, ri) => {
    if (r.type !== 'fixed') return;
    for (const cell of r.cells) {
      const ci = colIdx.get(cell.columnId);
      if (ci === undefined) continue;
      const rs = Math.max(1, cell.rowSpan ?? 1);
      const cs = Math.max(1, cell.colSpan ?? 1);
      if (rs <= 1 && cs <= 1) continue;
      for (let dr = 0; dr < rs; dr++) {
        for (let dc = 0; dc < cs; dc++) {
          if (dr === 0 && dc === 0) continue;
          coveredBy.set(`${ri + dr}:${ci + dc}`, { ri, ci });
        }
      }
    }
  });
  const resolve = (ri: number, ci: number): { ri: number; ci: number } =>
    coveredBy.get(`${ri}:${ci}`) ?? { ri, ci };
  const order: { ri: number; ci: number }[] = [];
  rows.forEach((_, ri) => {
    cols.forEach((__, ci) => {
      if (!coveredBy.has(`${ri}:${ci}`)) order.push({ ri, ci });
    });
  });
  return { rows, cols, resolve, order };
}

/**
 * COMPOSER_SPECIAL_COMPONENTS / applySimulatedApproval / mergePreviewContent / PagedEditBody는
 * 팝아웃 미리보기 창과 공유하기 위해 ./previewRender로 추출했다(동작 불변).
 */

/**
 * placeholder(샘플) 모드 렌더 데이터 — mergePreviewContent(template, {}) 위에서 반복영역(over)
 * 배열을 단일 빈 항목 `[{}]`로 덮어써 "바인딩 뼈대 1행"만 보이게 한다. 다단(columns≥2)이면 데이터가
 * 1개라 1단만 라벨이 채워지고 2단은 빈칸으로 남는다(단수 구조는 유지). 편집 캔버스는 editing=true라
 * 빈값 셀에 bindingLabels로 `{필드라벨}` placeholder를 표시한다. 기본행(defaultRows)/샘플 배열로
 * 여러 행이 채워지는 것을 막는다. 실데이터 모드(previewResult)는 무영향.
 */
function buildPlaceholderSkeleton(template: ReportTemplate): RenderData {
  const rd = mergePreviewContent(template, {});
  const overKeys = new Set<string>();
  const walk = (nodes: ComposerNode[]) => {
    for (const n of nodes) {
      if (n.kind === 'table') {
        for (const r of n.repeatRegions ?? []) overKeys.add(r.over);
        // 셀 content에 중첩된 노드(중첩 표 등)도 순회 — 누락 시 그 표만 placeholder에서 뼈대 미적용.
        for (const cell of n.cells) if (cell.content) walk(cell.content);
      } else if (n.kind === 'group' || n.kind === 'stack') {
        walk(n.children);
      } else if (n.kind === 'branch') {
        for (const slot of n.branches) walk(slot.children); // 슬롯 안 중첩 표도 순회.
      } else if (n.kind === 'repeat') {
        walk([n.template]); // repeat 노드의 template(GroupNode) 안 중첩 표도 순회.
      }
    }
  };
  walk(template.body);
  if (overKeys.size === 0) return rd;
  const out: RenderData = { ...rd };
  for (const key of overKeys) out[key] = [{}];
  return out;
}

/** 에디터 상단 뷰 모드 — 양식 편집 / 작성폼 */
type ViewMode = 'form' | 'writeForm';

/**
 * WidgetType — 작성폼 편집 select 옵션(table은 dataType=array에서만 노출).
 * 'aiTrigger'는 예약만 된 미구현 슬롯(렌더 placeholder만, 사용 0 + AI 후보 주입은 P3 관례 키
 * 경로로 이미 해결)이라 후보에서 숨김(2026-07-16 사용자 확정 — 타입 유니온은 하위호환 유지,
 * 기존 지정 위젯은 widgetOptionsForDataType의 current 보존으로 표시 유지).
 */
const WRITE_WIDGETS: WidgetType[] = [
  'text',
  'textarea',
  'select',
  'radio',
  'checkbox',
  'date',
  'time',
  'number',
  'photo',
  'sortableSelect',
  'combobox',
];

/** 위젯 종류 select 표시 라벨 — 전 옵션 '한글 (영문)' 병기(2026-07-17 일관화). 모노 배지(w.widget raw)는 코드값 유지. */
const WIDGET_OPTION_LABELS: Partial<Record<WidgetType, string>> = {
  text: '한 줄 텍스트 (text)',
  textarea: '여러 줄 텍스트 (textarea)',
  // string[] 항목 편집 목록 — 반복(행 스코프) 주 어휘(P4a에서 후보 노출, 렌더는 P1 기존재).
  'textarea-list': '여러 줄 목록 (textarea-list)',
  // textarea-list의 한 줄 판 — 각 항목이 한 줄 input(string[] 동형).
  'text-list': '한 줄 목록 (text-list)',
  select: '선택 목록 (select)',
  combobox: '제안형 콤보 (combobox)',
  radio: '라디오 (radio)',
  checkbox: '체크박스 (checkbox)',
  date: '날짜 (date)',
  // 기간(시작~종료) 입력 — signature와 같은 이유로 라벨만 둔다: 행 필드 선언에서만 생성돼
  // 위젯 종류 후보에는 안 나오지만, 그렇게 만들어진 위젯을 선택하면 "현재값 보존" 옵션으로 떠서
  // 코드값 대신 읽을 라벨이 필요하다.
  'date-range': '기간 (date-range)',
  time: '시간 (time)',
  number: '숫자 (number)',
  photo: '사진 (photo)',
  // 읽기 전용 이미지 표시(값=URL) — 반복 행 카드 사진 등. 입력 아님.
  image: '이미지 표시 (image)',
  // 사진 위에 사각형·선을 겹쳐 보여 주는 표시 위젯 — image의 상위 호환(값이 URL 문자열이면 평면 표시).
  'annotated-image': '주석 이미지 (annotated-image)',
  // 서명 슬롯 칸(표시 전용) — 행 필드 선언에서만 생성된다(일반 위젯 후보 아님). 위젯 종류 select에
  // 그대로 나타나는 일은 없지만, 현재값 보존 옵션으로 뜰 때 코드값 대신 읽을 라벨이 필요하다.
  signature: '서명 (signature)',
  // 데이터셋 편집 표(행=데이터 목록) — formTable('양식 표', 칸 단위 구성)과 명명 구분(2026-07-18 사용자 확정).
  table: '데이터 표 (table)',
  sortableSelect: '선택 정렬 목록 (sortableSelect)',
};

/**
 * 바인딩 데이터 구조(dataType)별 입력 가능한 위젯 후보.
 * 작성폼 탭 위젯 select를 type에 맞는 위젯으로만 제한한다.
 * dataType 미지정(undefined)이면 전체 WRITE_WIDGETS 노출(하위호환).
 */
const WIDGET_OPTIONS_BY_DATATYPE: Record<NonNullable<InputWidget['dataType']>, WidgetType[]> = {
  string: ['text', 'textarea', 'select', 'radio', 'combobox'],
  number: ['number', 'text'],
  enum: ['select', 'radio'],
  date: ['date', 'text'],
  boolean: ['checkbox'],
  array: ['table'],
};

/**
 * dataType + 현재 위젯값에 맞는 위젯 후보 list.
 * - dataType 미지정 → 전체 WRITE_WIDGETS.
 * - 현재 선택된 위젯(current)이 후보에 없으면 그 값도 포함(잘못 저장된 값이 빈 select 되는 것 방지,
 *   photo/aiTrigger 등 특수 위젯도 현재값으로 보존).
 */
function widgetOptionsForDataType(
  dataType: InputWidget['dataType'],
  current: WidgetType,
): WidgetType[] {
  const base = dataType ? WIDGET_OPTIONS_BY_DATATYPE[dataType] : WRITE_WIDGETS;
  return base.includes(current) ? base : [current, ...base];
}

/**
 * 제목 규칙 입력 — 텍스트 입력 + 토큰 삽입 버튼(클릭 시 커서 위치에 삽입).
 * 직접 타이핑 부담 완화(행 표시 조건 빌더와 같은 취지). 변경은 부모가 apply로 undo/isDirty 편입.
 */
function TitleTemplateField({
  value,
  policy,
  onChange,
}: {
  value: string;
  /** 이 템플릿의 전역 정책 — 분할 축이 있어야 `{group}` 버튼이 노출된다(titleTemplateTokensFor). */
  policy: TemplatePolicy | undefined;
  onChange: (next: string) => void;
}): ReactElement {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const insertToken = (token: string): void => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    onChange(value.slice(0, start) + token + value.slice(end));
    // 커서를 삽입한 토큰 뒤로 — 리렌더(value 반영) 후 복원해야 유효.
    requestAnimationFrame(() => {
      const pos = start + token.length;
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  };
  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
        placeholder="예: {site} {seq}차 안전점검 보고서 - {date}"
      />
      <div className="mt-1 flex flex-wrap gap-1">
        {titleTemplateTokensFor(policy).map(({ token, label, hint }) => (
          <button
            key={token}
            type="button"
            onClick={() => insertToken(token)}
            title={hint ? `${token} 삽입 — ${hint}` : `${token} 삽입`}
            className="px-1.5 py-0.5 text-[11px] border border-gray-300 rounded bg-white text-gray-600 hover:bg-gray-50"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// 로드 정규화(toEditableTemplate)는 ./templateEditableLoad로, 갱신 body 조립(toUpdateBody)·정책
// 정규화(normalizeTemplatePolicy)는 ./templateUpdateBody로 옮겼다 — 결과 모달의 초안 일괄 편집도
// 같은 필드 집합으로 저장해야 하고(두 벌이 되면 한쪽에서만 축이 빠져 조용히 소실된다), 로드/저장
// **대칭 규약을 spec으로 대조**하려면 두 목록이 모두 EditorPage 밖에서 import 가능해야 한다
// (./templateFieldSymmetry.test.ts).

/**
 * 템플릿 전역 속성 — 노출·중복 정책 구획(2026-07-26).
 * 적용 정본은 서버 2곳(사용자용 목록 필터=기관 축 / 생성 가드=기관+현장 업종 KSIC prefix) —
 * 여기서는 저작만 한다. 전부 비우면 전체 공개 + 중복 차단(기본)이고, 저장 시
 * normalizeTemplatePolicy가 기본값 항목을 걷어낸 뒤 빈 정책은 null(명시적 해제)로 보낸다.
 */
function TemplatePolicySection({
  policy,
  dataSources,
  catalog,
  onChange,
}: {
  policy: TemplatePolicy | undefined;
  /** multi 정책 유효성 판정(judgeInspectionSelectionMulti)의 소스 축 — 전 점검 순회 소스 유무. */
  dataSources: DataSourceDef[] | undefined;
  /** 같은 판정의 선언 축 — 어느 entry가 전 점검을 순회하나(`traversesLinkedInspections`)는 카탈로그가 말한다. */
  catalog: CatalogEntry[] | undefined;
  onChange: (next: TemplatePolicy) => void;
}) {
  // 대분류(level 1) 업종 목록 — 노출 업종 선택지. 하위 코드는 서버의 KSIC 계층 prefix
  // 매칭으로 커버되므로 트리 picker 없이 대분류 multiselect로 충분(2026-07-26 확정).
  const [industries, setIndustries] = useState<
    Array<{ id: string; code: string; name: string }>
  >([]);
  const [industriesFailed, setIndustriesFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .getIndustries({ level: 1, isActive: true })
      .then((res) => {
        if (!cancelled) {
          setIndustries((res.data ?? []).map(({ id, code, name }) => ({ id, code, name })));
        }
      })
      .catch(() => {
        if (!cancelled) setIndustriesFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCategories = policy?.audience?.organizationCategories ?? [];
  const selectedCodes = policy?.audience?.industryCodes ?? [];
  // multi 정책이 이 양식의 소스로 실제 합본이 되는지(서버 조립 미러) — 미충족이면 select 아래 amber 경고.
  const multiVerdict = judgeInspectionSelectionMulti(policy, dataSources, catalog);

  const toggleList = (list: string[], value: string, on: boolean): string[] => {
    const set = new Set(list);
    if (on) set.add(value);
    else set.delete(value);
    return [...set];
  };

  const patchAudience = (
    patch: Partial<NonNullable<TemplatePolicy['audience']>>,
  ): void => onChange({ ...policy, audience: { ...policy?.audience, ...patch } });

  return (
    <div className="pt-2 border-t border-gray-100">
      <span className="text-xs font-medium text-gray-600">노출·중복 정책</span>
      <p className="mt-1 text-[11px] text-gray-400">
        이 양식이 어떤 조직·현장에 보일지와, 같은 점검으로 여러 번 만들 수 있을지를 정합니다.
        아무것도 지정하지 않으면 모든 조직에 보이고 중복 생성은 차단됩니다.
      </p>

      <label className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-600">
        <span className="shrink-0">같은 점검으로 중복 생성</span>
        <select
          value={policy?.duplicatePolicy === 'allow' ? 'allow' : 'block'}
          onChange={(e) => {
            // 'allow'만 명시 저장 — 차단 복귀는 필드 제거(미지정=기본 차단과 동일 표시).
            const next: TemplatePolicy = { ...policy };
            if (e.target.value === 'allow') next.duplicatePolicy = 'allow';
            else delete next.duplicatePolicy;
            onChange(next);
          }}
          className="px-1.5 py-1 text-xs border border-gray-300 rounded"
        >
          <option value="block">차단 (기본)</option>
          <option value="allow">허용</option>
        </select>
      </label>

      {/* 점검 개수(policy.allowNoInspection · allowMultipleInspections) — 이 양식에 점검을 몇 건 붙여 만들 수
          있나. 둘 다 끄면 정확히 1건(기본). **서버가 생성 시 집행한다**(shared judgeInspectionCount —
          구 `inspectionSelection: 'single'|'multi'`는 선언만 있고 집행이 0건이어서 2026-10-09 두 불리언으로
          이관). true만 명시 저장(false=기본이라 키 제거 — duplicatePolicy 동형).
          복수 허용은 서버 조립이 합본을 지원하는 양식(항목 소스 inspection-risk-assessment-rows)에서만
          뜻이 있다 — 그 조건을 judgeInspectionSelectionMulti(서버 조립 미러, co-located spec)가 이 양식의
          소스로 판정해 미충족이면 아래 amber 경고를 낸다(정적 안내문만으로는 SI 등에 켜도 침묵 — 5차 은닉
          감사 B, 서버 조용한 게이트 ↔ admin 미러 규약). */}
      <div className="mt-2">
        <span className="block text-[11px] text-gray-500 mb-0.5">점검 개수</span>
        <p className="text-[11px] text-gray-400 mb-1">
          둘 다 끄면 점검 정확히 하나로만 만들 수 있습니다(기본). 서버가 생성 요청의 점검 수를 이 선언으로
          검사합니다 — 화면 흐름(점검 선택 모달)도 같은 선언을 읽습니다.
        </p>
        <label className="flex items-center gap-1.5 py-0.5 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={policy?.allowNoInspection === true}
            onChange={(e) => {
              const next: TemplatePolicy = { ...policy };
              if (e.target.checked) next.allowNoInspection = true;
              else delete next.allowNoInspection;
              onChange(next);
            }}
          />
          <span>점검 없이(현장만 지정) 만들 수 있음 — 발견사항을 전제하지 않는 양식(TBM 교육일지 등)</span>
        </label>
        <label className="flex items-center gap-1.5 py-0.5 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={policy?.allowMultipleInspections === true}
            onChange={(e) => {
              const next: TemplatePolicy = { ...policy };
              if (e.target.checked) next.allowMultipleInspections = true;
              else delete next.allowMultipleInspections;
              onChange(next);
            }}
          />
          <span>여러 점검을 한 문서에 묶을 수 있음 (합본)</span>
        </label>
        <p className="mt-1 text-[11px] text-gray-400">
          「여러 점검」을 켜면 점검 상세에서 이 양식을 고를 때 포함할 점검을 고르는 모달(현재 점검
          기본 선택, 최소 1개, 정렬)이 먼저 뜨고, 고른 점검 전부가 한 문서에 묶입니다. 항목 소스가
          「위험성평가 결과서 항목」 조립인 양식에서만 항목이 전 점검으로 이어지고, 그 밖의 모든 조회(헤더
          점검일·점검자 포함)는 첫 점검 기준입니다.
        </p>
      </div>
      {multiVerdict.kind === 'ineligible' && multiVerdict.reason === 'no-traversing-source' ? (
        <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-2">
          <p className="text-[11px] font-medium text-amber-700">
            이 양식엔 전 점검을 순회하는 소스(「위험성평가 결과서 항목」 조립)가 없어 첫 점검만 조립됩니다.
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-amber-700">
            점검 선택 모달은 뜨고 고른 점검이 문서에 묶이지만, 항목·헤더 등 모든 조회가 첫 점검
            기준이라 나머지 점검의 내용은 문서에 실리지 않습니다 — 그 소스를 데이터에 추가하거나
            「여러 점검」을 끄세요.
          </p>
        </div>
      ) : null}

      <div className="mt-2">
        <span className="block text-[11px] text-gray-500 mb-0.5">노출할 기관 유형</span>
        <p className="text-[11px] text-gray-400 mb-1">전부 해제하면 모든 기관 유형에 보입니다.</p>
        {(Object.keys(ORGANIZATION_CATEGORY_LABELS) as OrganizationCategory[]).map((cat) => (
          <label
            key={cat}
            className="flex items-center gap-1.5 py-0.5 text-xs text-gray-600 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedCategories.includes(cat)}
              onChange={(e) =>
                patchAudience({
                  organizationCategories: toggleList(selectedCategories, cat, e.target.checked),
                })
              }
            />
            <span>{ORGANIZATION_CATEGORY_LABELS[cat]}</span>
          </label>
        ))}
      </div>

      <div className="mt-2">
        <span className="block text-[11px] text-gray-500 mb-0.5">노출할 업종</span>
        <p className="text-[11px] text-gray-400 mb-1">
          전부 해제하면 모든 업종에 보입니다. 대분류를 고르면 그 아래 세부 업종의 현장까지
          포함됩니다. 업종을 고른 양식은 업종이 지정되지 않은 현장에서는 쓸 수 없습니다.
        </p>
        {industriesFailed ? (
          <p className="text-[11px] text-red-500">업종 목록을 불러오지 못했습니다.</p>
        ) : (
          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded px-1.5 py-1">
            {industries.map((ind) => (
              <label
                key={ind.id}
                className="flex items-center gap-1.5 py-0.5 text-xs text-gray-600 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedCodes.includes(ind.code)}
                  onChange={(e) =>
                    patchAudience({
                      industryCodes: toggleList(selectedCodes, ind.code, e.target.checked),
                    })
                  }
                />
                <span>{ind.name}</span>
                <span className="text-gray-400">({ind.code})</span>
              </label>
            ))}
          </div>
        )}
        {selectedCodes.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {selectedCodes.map((code) => (
              <span
                key={code}
                className="px-1.5 py-0.5 text-[11px] bg-brand-50 text-brand-700 rounded"
              >
                {industries.find((i) => i.code === code)?.name ?? code}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 조립형 데이터 소스(dataSources) key의 템플릿 내 사용처 수를 센다(속성 패널 "사용처 N곳" 배지·삭제 확인용).
 *  - 반복영역 over === key (TableRepeatRegion.over / RepeatNode.over)
 *  - 셀·텍스트 바인딩 bindingKey === key 또는 `key.`로 시작(flat 적재된 하위 경로 소비)
 *  - 셀 집계(aggregate.over) === key (배열 소비 — 삭제 시 깨지므로 사용처로 포함)
 */
function countDataSourceUsage(template: ReportTemplate, key: string): number {
  let count = 0;
  const matchesBinding = (bindingKey: string | undefined): boolean =>
    !!bindingKey && (bindingKey === key || bindingKey.startsWith(`${key}.`));
  walkNodeTree(template.body, (n) => {
    // 노드 자체 바인딩(텍스트/이미지/스페셜 등)
    const nodeBinding = (n as { binding?: { bindingKey?: string } }).binding;
    if (matchesBinding(nodeBinding?.bindingKey)) count++;
    if (n.kind === 'repeat' && n.over === key) count++;
    if (n.kind === 'table') {
      for (const r of n.repeatRegions ?? []) if (r.over === key) count++;
      for (const c of n.cells) {
        if (matchesBinding(c.binding?.bindingKey)) count++;
        if (c.aggregate?.over === key) count++;
      }
    }
  });
  // 작성 탭(composerWidgetTree)의 참조도 포함 — RepeatWidget.over(반복 소스)는 본문과 별개로
  // 작성 페이지에서만 쓰일 수 있다(예: previous-status-src는 본문이 previous-inspection-findings를
  // 쓰고 작성 탭에서만 over). body만 세면 "미사용" 오판(2026-07-21 사용자 제보). group/repeat/branch/
  // modalTrigger 자식을 재귀 순회한다.
  // input 위젯 축 — bindingKey(flat 하위 경로 소비) + optionsSources(콤보/정렬선택 후보 rows,
  // deprecated 단수 optionsSource 포함) + writes(단건/`key[].field` 배열 일괄 실쓰기). 누락 시
  // 선택지 전용 데이터셋(예: assessment-methods — 평가 기법 콤보의 optionsSources)이 "미사용"으로
  // 오표시되고 삭제 게이트도 무경고 통과(2026-07-26 사용자 제보 — compute 입력 누락과 같은 부류).
  const countInputAxes = (w: InputWidget): void => {
    if (matchesBinding(w.bindingKey)) count++;
    const sources = w.optionsSources?.length
      ? w.optionsSources
      : w.optionsSource
        ? [w.optionsSource]
        : [];
    if (sources.includes(key)) count++;
    for (const wr of w.writes ?? []) {
      if (wr.target === key || wr.target.startsWith(`${key}.`) || wr.target.startsWith(`${key}[].`))
        count++;
    }
  };
  const walkWidgets = (widgets: ComposerWidget[] | undefined): void => {
    for (const w of widgets ?? []) {
      if (w.kind === 'repeat' && w.over === key) count++;
      if (w.kind === 'input') countInputAxes(w);
      // formTable — 반복 행(sourceKey=행 데이터 소스) + 고정 셀 input 위젯(콤보 등)까지 순회.
      // walkWidgets가 formTable에 미진입하면 그 안의 소비가 전부 누락된다.
      if (w.kind === 'formTable') {
        for (const row of w.rows ?? []) {
          if (row.type === 'repeat' && row.sourceKey === key) count++;
          if (row.type === 'fixed')
            for (const cell of row.cells ?? []) if (cell.widget) countInputAxes(cell.widget);
        }
      }
      if (w.kind === 'group' || w.kind === 'repeat') walkWidgets(w.children);
      else if (w.kind === 'branch')
        for (const slot of w.branches ?? []) walkWidgets(slot.children);
      else if (w.kind === 'modalTrigger') walkWidgets(w.children);
    }
  };
  walkWidgets(template.composerWidgetTree);
  // 내장 데이터셋 조립(compute)의 입력 참조 — 조인 base/join, zip left/right, slice source가
  // 이 소스를 소비한다. 누락 시 조인 전용 소스(예: current-status-src)가 "미사용"으로 오표시되고
  // 삭제 확인 게이트도 경고 없이 통과해 조인이 조용히 깨짐(2026-07-26 사용자 제보).
  for (const def of template.dataDefs ?? []) {
    const inputs = def.compute?.inputs;
    if (!inputs) continue;
    for (const v of Object.values(inputs)) if (v === key) count++;
  }
  return count;
}

/**
 * 팔레트 — 배치 가능한 프리미티브 목록(클릭 = createDefaultNode + insertNode).
 * radioGroup 항목만 예외: 클릭 시 handleAddNode 대신 handleAddRadioGroup으로 자동 공유키 + 기본
 * 옵션 2개(예/아니오)를 갖는 체크박스 세트를 즉시 삽입한다(모달 없이 — 이후 속성 패널에서 조정).
 * (kind는 셀 삽입 가능 여부 판정에만 쓰이는 group — 실제 삽입 노드도 group.)
 */
const PALETTE_ITEMS: {
  kind: ComposerNode['kind'];
  label: string;
  icon: string;
  special?: SpecialKind;
  /** true면 클릭 시 라디오 그룹(공유키 체크박스 세트)을 즉시 삽입한다. */
  radioGroup?: boolean;
}[] = [
  { kind: 'text', label: '텍스트', icon: 'T' },
  { kind: 'rect', label: '사각형', icon: '▭' },
  { kind: 'image', label: '이미지', icon: '🖼' },
  { kind: 'line', label: '선', icon: '─' },
  { kind: 'table', label: '표', icon: '▦' },
  { kind: 'group', label: '그룹', icon: '▣' },
  { kind: 'stack', label: '스택', icon: '☰' },
  { kind: 'spacer', label: '여백', icon: '↕' },
  { kind: 'checkbox', label: '체크박스', icon: '☑' },
  { kind: 'choiceGroup', label: '선택지 그룹', icon: '◈' },
  { kind: 'group', label: '라디오 그룹', icon: '◉', radioGroup: true },
  { kind: 'branch', label: '분기', icon: '⋔' },
  { kind: 'repeat', label: '반복', icon: '🔁' },
  { kind: 'special', label: '결재란', icon: '✍', special: 'approvalBlock' },
];

/** 표 셀 안에 넣을 수 있는 팔레트 항목(table/repeat/special 제외) — 셀 편집기 '요소 추가' 버튼용. */
// 셀 편집기 '요소 추가' 스트립 — 라디오 그룹은 제외(셀 선택 중엔 메인 팔레트의 라디오 그룹 버튼이
// 선택 셀로 라우팅하므로 중복 진입점 불필요 + onAddNode는 라디오 그룹 삽입 경로를 타지 않음).
const CELL_INSERT_ITEMS = PALETTE_ITEMS.filter(
  (item) => !item.radioGroup && isCellInsertableKind(item.kind),
);

/**
 * 선택 노드가 "라디오 그룹"(공유 bindingKey 체크박스 세트)인지 판정한다.
 * group이고 자식이 1개 이상이며 전부 checkbox이고, 모두 같은 non-empty bindingKey를 공유할 때
 * 그 공유 키와 옵션(각 자식 checkbox) 목록을 반환한다. 아니면 null(일반 group 편집기만 노출).
 * (결정 기록 2026-07-07-composer-radio-group-via-shared-key-checkbox: "공유키 체크박스 + 작성폼 자동 파생")
 */
function getRadioGroupInfo(
  node: ComposerNode,
): { bindingKey: string; options: CheckboxNode[] } | null {
  if (node.kind !== 'group') return null;
  if (node.children.length === 0) return null;
  const options: CheckboxNode[] = [];
  for (const c of node.children) {
    if (c.kind !== 'checkbox') return null; // 자식이 하나라도 체크박스가 아니면 라디오 그룹 아님
    options.push(c);
  }
  const key = options[0].binding?.bindingKey;
  if (!key) return null;
  if (!options.every((c) => c.binding?.bindingKey === key)) return null;
  return { bindingKey: key, options };
}

/** 마지막 편집(로드/저장)한 템플릿 key — 새로고침 후 자동 복원용. */
const LAST_TEMPLATE_KEY = 'composer.lastTemplateKey';

/** 템플릿별 미리보기 컨텍스트(선택 엔티티) localStorage 키 prefix */
const PREVIEW_CTX_KEY = 'composer-preview-context';
// "placeholder로" 클릭 시 per-key로 저장 — 새로고침 후 자동 재적용(실데이터) 억제. "실데이터 적용" 시 해제.
const PREVIEW_SUPPRESS_KEY = 'composer-preview-suppress';
// 선택 엔티티의 라벨(요약 칩용) per-key 영속 — 새로고침 후 IDs만으론 이름 표시 불가하므로 함께 저장.
const PREVIEW_LABELS_KEY = 'composer-preview-labels';

// 문서 상태 배지(TemplateIssuesBadge)의 클릭 이동 목적지 — 보고서 탭 전역 속성 패널의 두 섹션.
// ⚠️ id는 **보고서 탭 호출부에만** 붙인다. 두 섹션 컴포넌트는 작성 탭 전역 코어에서도 같은 props로
// 렌더되므로(드리프트 차단용 spread), 컴포넌트 안에 id를 두면 두 탭이 동시에 마운트되는 순간
// 중복 id가 된다.
const CATALOG_PARAMS_ANCHOR_ID = 'composer-catalog-params-section';
const VARIABLES_ANCHOR_ID = 'composer-variables-section';

/**
 * 데이터 카드의 이동 표식 — 반복영역/반복 노드의 「소스 편집 →」이 찾아가는 자리.
 *
 * 위 두 앵커와 달리 **id가 아니라 data 속성**이다. 카드는 동적 목록이라 호출부에 id를 둘 수 없고
 * (컴포넌트 안에서 그려진다), 그렇다고 컴포넌트 안에 id를 두면 위 주석이 경계한 중복 id 문제를
 * 그대로 떠안는다. data 속성은 두 탭에 동시에 있어도 무해하고, 조회를 DataSection 자기 루트로
 * 한정하면 "내 탭의 카드"만 잡힌다. 선언 key가 그대로 값이므로 CSS selector 이스케이프에 기대지
 * 않도록 조회는 속성 전수 비교로 한다(아래 revealCardKey effect).
 *
 * ⚠️ 이 상수는 **조회 쪽 전용**이다 — 카드에 붙이는 쪽은 두 목록 렌더러(DataDefsEditor /
 * DataSourcesEditor)가 `data-datacard={def.key}` 리터럴로 쓴다(JSX 리터럴이 house style).
 * 값을 바꾸면 그 두 자리도 함께 고칠 것.
 */
const DATA_CARD_ATTR = 'data-datacard';

/**
 * body에서 id 노드까지의 경로(조상들 + 자기 자신, 바깥→안 순서)를 찾는다. 못 찾으면 null.
 * group/stack.children와 **표 셀 content**를 재귀한다 — 셀 안 노드는 그 표(table) 노드를 조상으로
 * 포함한다(셀 자체는 노드 id가 없어 경로에 넣지 못하므로 표까지만; 브레드크럼/드릴업은 아래에서
 * 셀 단위를 별도로 처리한다). repeat.template(단일 그룹)도 조상 경로에 포함 — 반복 단위 자식이
 * 선택·드릴업 대상이므로 repeat → template group → 자식 순으로 경로를 잇는다.
 */
function findNodePath(nodes: ComposerNode[], id: string): ComposerNode[] | null {
  for (const n of nodes) {
    if (n.id === id) return [n];
    if (n.kind === 'group' || n.kind === 'stack') {
      // group/stack 모두 자식 컨테이너 — 조상 경로(브레드크럼/드릴업)에 포함.
      const sub = findNodePath(n.children, id);
      if (sub) return [n, ...sub];
    } else if (n.kind === 'repeat') {
      // repeat.template(단일 그룹)을 조상으로 포함 — 반복 단위 안 노드 선택 시 repeat까지 드릴업.
      const sub = findNodePath([n.template], id);
      if (sub) return [n, ...sub];
    } else if (n.kind === 'branch') {
      // 슬롯 children 안 노드 — branch를 조상으로 포함(슬롯 자체는 노드 id가 없어 경로엔 branch까지).
      // ESC 드릴업이 슬롯 자식 → branch로 올라가게 한다.
      for (const slot of n.branches) {
        const sub = findNodePath(slot.children, id);
        if (sub) return [n, ...sub];
      }
    } else if (n.kind === 'table') {
      // 셀 content 안 노드 — 표(table)를 조상으로 포함(셀은 노드가 아니라 경로에 못 넣음).
      for (const cell of n.cells) {
        if (!cell.content) continue;
        const sub = findNodePath(cell.content, id);
        if (sub) return [n, ...sub];
      }
    }
  }
  return null;
}

/**
 * id 노드의 직속 부모 컨테이너 식별 키를 반환한다(body 최상위면 null, 트리에 없으면 undefined).
 * 형제 여부 판정(다중선택은 같은 부모 형제끼리만)에 사용.
 *  - body 직속: null
 *  - group/stack 안: 그 컨테이너의 노드 id
 *  - 표 셀 content 안(직속 부모가 셀): 셀 합성 키 `${tableId}::${row}::${col}` — 같은 표라도 다른
 *    셀이면 다른 키가 되어 서로 형제로 오판되지 않는다(parseCellRef로 복원 가능, 노드 id엔 '::' 없음).
 */
/** 캔버스 클릭 선택 모델 — 'isolation'(그룹 우선: 최상위 승격+더블클릭 드릴) / 'deepest'(최심부 우선: 이전 동작). */
type CanvasSelectionMode = 'isolation' | 'deepest';
/** 선택 방식 localStorage 키. */
const SELECTION_MODE_STORAGE_KEY = 'composer:selection-mode';

// ── 작성(writeForm) 탭 group isolation 승격 헬퍼 ──
// 양식 탭 selectionModel(resolveSingleClickTarget)의 위젯 트리 버전. 작성 탭 트리는 컨테이너가
// group 하나뿐이라 노드 트리보다 단순하다(widgetParentId만으로 조상 체인 구성).
/**
 * 작성 탭 위젯 트리에서 id의 deepest→top 조상 체인 `[id, 부모, …, 최상위]`. 미발견/최상위면 짧게 종료.
 * 분기 슬롯 id는 **투명 계층으로 스킵**(위젯이 아니라 선택/컨텍스트 대상이 못 됨) — widgetParentId가
 * 슬롯 안 위젯→슬롯 id→branch 위젯 id로 이어주므로, 체인에는 위젯 id만 남긴다. 스킵 없이 슬롯 id가
 * 최상위로 판정되면 그룹 우선 선택이 위젯 아닌 id를 선택해 조용히 무산된다(2026-07-21 제보).
 */
function writeWidgetAncestorChain(tree: ComposerWidget[], id: string): string[] {
  const chain: string[] = [id];
  let cur = id;
  for (;;) {
    const p = widgetParentId(tree, cur);
    if (p === undefined || p === null) break; // 최상위(null) 또는 미발견(undefined)
    // 슬롯 id(위젯 트리에 실체 없는 부모 키)는 체인에서 제외하고 통과만 한다.
    if (selectedWidgetById(tree, p) !== null) chain.push(p);
    cur = p;
  }
  return chain;
}
/**
 * 작성 탭 단일클릭 승격 대상 id — 현재 컨텍스트(group id | null) 기준 직계 자식까지 승격.
 * 양식 탭 resolveSingleClickTarget과 동형:
 * - contextId=null → 조상 체인 최상위(root 직속) 선택.
 * - contextId가 체인의 인덱스 k: k===0(컨텍스트 자체 클릭)→contextId, 그 외→chain[k-1](컨텍스트 직계 자식).
 * - contextId가 체인에 없음(컨텍스트 밖 클릭) → 최상위(root 레벨 이탈).
 */
function resolveWriteSingleClickTarget(
  tree: ComposerWidget[],
  clickedId: string,
  contextId: string | null,
): string {
  const chain = writeWidgetAncestorChain(tree, clickedId);
  const topLevel = chain[chain.length - 1];
  if (contextId === null) return topLevel;
  const k = chain.indexOf(contextId);
  if (k === -1) return topLevel;
  if (k === 0) return contextId;
  return chain[k - 1];
}

/**
 * 대상 부모(parentId)가 modalTrigger 스코프 안인지 — 자신이 modalTrigger이거나 조상 체인에
 * modalTrigger가 있으면 true. 모달 버튼 중첩 금지(모달 안에 모달 버튼 배치 차단, 트리 '＋'의
 * allowModalTrigger와 동형) 게이트를 dnd 드롭·붙여넣기 경로에 적용하는 데 사용.
 * (selectedWidgetById/widgetParentId는 함수 선언 hoisting으로 정의 위치와 무관하게 참조 가능.)
 */
function isParentInsideModalScope(tree: ComposerWidget[], parentId: string | null): boolean {
  let cur: string | null = parentId;
  while (cur != null) {
    if (selectedWidgetById(tree, cur)?.kind === 'modalTrigger') return true;
    cur = widgetParentId(tree, cur) ?? null;
  }
  return false;
}
/** 위젯 subtree(자신 포함)에 modalTrigger가 있는지 — 모달 중첩 금지 게이트의 드롭/붙여넣기 대상 판정. */
function widgetSubtreeHasModalTrigger(w: ComposerWidget): boolean {
  if (w.kind === 'modalTrigger') return true;
  // repeat 행 템플릿 안 모달 버튼(행 스코프 조립의 정규 구성)도 모달 스코프로의 드롭 차단 대상.
  if (w.kind === 'group' || w.kind === 'repeat') return w.children.some(widgetSubtreeHasModalTrigger);
  return false;
}

/**
 * 클립보드 payload를 **두 채널에 기록**한다 — localStorage(같은 origin 탭/창) + OS 클립보드 텍스트
 * (NODE_CLIPBOARD_TEXT_PREFIX sentinel, crossorigin 상용계↔개발계·원격 데스크톱·다른 PC).
 *
 * 노드 복사(handleNodeCopy)와 작성 탭 위젯 복사/잘라내기(handleWidgetCopy/Cut)가 **같은 배선을 공유**한다 —
 * 두 축이 갈라지면 한쪽만 OS 채널을 못 타 "이 창에선 붙는데 다른 창에선 안 붙는" 비대칭이 생긴다.
 * OS write는 포커스·권한·미지원(비보안 컨텍스트)으로 실패할 수 있고 그때도 localStorage 채널은 살아 있다.
 * 실패를 삼키지 않도록 onOsWriteError로 호출처에 알린다(노드 축은 기존대로 미전달=무통지).
 */
function writeClipboardPayloadChannels(
  payload: NodeClipboardPayload,
  onOsWriteError?: () => void,
): void {
  const serialized = serializeNodeClipboard(payload);
  try {
    localStorage.setItem(NODE_CLIPBOARD_KEY, serialized); // 같은 origin 탭/창 간 채널
  } catch {
    /* storage 차단 무시 */
  }
  // write는 포커스된 문서의 사용자 제스처(keydown/click)에선 권한 프롬프트 없이 허용된다.
  const written = navigator.clipboard?.writeText(NODE_CLIPBOARD_TEXT_PREFIX + serialized);
  if (!written) {
    onOsWriteError?.(); // navigator.clipboard 미지원(비보안 컨텍스트)
    return;
  }
  void written.catch(() => onOsWriteError?.());
}

/**
 * 클립보드 payload를 읽어 dispatch로 넘기는 **공통 트리거**(노드 축·위젯 축 공유).
 *
 * OS 클립보드(텍스트) 우선 — 항상 최신 복사를 반영한다. same-origin 복사도 writeText로 OS에 실리고,
 * crossorigin(상용계↔개발계)·원격 데스크톱 복사는 OS 채널만 존재하기 때문. localStorage는 이 origin의
 * 캐시라 stale일 수 있어(과거 복사분이 남으면 crossorigin 복사를 가림 — 실제 버그였음), OS에 payload가
 * 없을 때만 폴백한다. read 권한 거부/미지원(비보안 컨텍스트)이면 catch에서 localStorage로.
 *
 * 반환 true = 이 호출이 처리(호출부 keydown은 동기 시점에 preventDefault). 어느 채널에도 payload가
 * 없으면 onEmpty(있으면)를 부르고 false를 반환한다(노드 축은 기존대로 미전달=무통지).
 */
function triggerClipboardPayloadPaste(
  inFlightRef: { current: boolean },
  dispatch: (payload: NodeClipboardPayload) => void,
  onEmpty?: () => void,
): boolean {
  // localStorage 폴백(동기) — OS 클립보드에 접근할 수 없거나 payload가 없을 때만 사용.
  const pasteFromLocalStorage = (): boolean => {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(NODE_CLIPBOARD_KEY);
    } catch {
      /* storage 차단 무시 */
    }
    const local = parseNodeClipboard(raw);
    if (local) dispatch(local);
    else onEmpty?.();
    return local != null;
  };
  if (navigator.clipboard?.readText) {
    // OS read가 이미 진행 중이면 스킵(return false). 이 가드를 상단 if 조건에 두면 in-flight 시
    // 아래 localStorage 분기로 fall-through해 stale 붙여넣기가 즉시 실행 + 대기중 read resolve로
    // 이중 붙여넣기가 발생한다(리뷰 W1). readText 지원 환경에선 OS 경로만 타야 한다.
    // read는 최초 1회 권한 프롬프트가 뜰 수 있으나 허용 시 이후 무프롬프트.
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    void navigator.clipboard
      .readText()
      .then((text) => {
        const os = parseNodeClipboardText(text);
        if (os) dispatch(os);
        else pasteFromLocalStorage();
      })
      .catch(() => {
        pasteFromLocalStorage();
      })
      .finally(() => {
        inFlightRef.current = false;
      });
    return true;
  }
  // navigator.clipboard 미지원(비보안 컨텍스트) → localStorage만.
  return pasteFromLocalStorage();
}

/**
 * 작성 탭 붙여넣기 **대상 위치** 해석(2026-07-16 사용자 확정 규약 — cut/copy·payload 경로 공유):
 *  - 컨테이너(섹션 group·modalTrigger) 선택 → 그 **안** 말미("그릇에 담기")
 *  - 리프 위젯 선택 → anchor(주 선택) 바로 다음(같은 부모)
 *  - 선택 없음 → isolation 컨텍스트(writeSelectionContextId) 그룹 말미, 없으면 root 말미(null).
 */
function resolveWidgetPasteTarget(
  tree: ComposerWidget[],
  selectedWidgetId: string | null,
  writeSelectionContextId: string | null,
): {
  selectedWidget: ComposerWidget | null;
  selectedIsContainer: boolean;
  pasteParentId: string | null;
} {
  const selectedWidget = selectedWidgetId ? selectedWidgetById(tree, selectedWidgetId) : null;
  const selectedIsContainer =
    selectedWidget?.kind === 'group' || selectedWidget?.kind === 'modalTrigger';
  const pasteParentId = selectedWidget
    ? selectedIsContainer
      ? selectedWidget.id
      : (widgetParentId(tree, selectedWidget.id) ?? null)
    : writeSelectionContextId && widgetParentId(tree, writeSelectionContextId) !== undefined
      ? writeSelectionContextId
      : null;
  return { selectedWidget, selectedIsContainer, pasteParentId };
}

/** 해석된 대상 위치에 붙여넣을 위젯들을 삽입한 새 트리(cut 경로·payload 경로 공유 — 위치 규약 단일 지점). */
function insertWidgetsForPaste(
  tree: ComposerWidget[],
  insertable: ComposerWidget[],
  selectedWidget: ComposerWidget | null,
  selectedIsContainer: boolean,
  pasteParentId: string | null,
): ComposerWidget[] {
  if (selectedWidget && !selectedIsContainer) {
    return insertWidgetsAfterId(tree, selectedWidget.id, insertable);
  }
  return insertable.reduce(
    (acc, w) => insertWidgetAt(acc, w, pasteParentId, Number.MAX_SAFE_INTEGER),
    tree,
  );
}

function parentIdOf(nodes: ComposerNode[], id: string): string | null | undefined {
  const loc = findNodeContainer(nodes, id);
  if (!loc) return undefined;
  if (loc.kind === 'body') return null;
  if (loc.kind === 'group') return loc.parentId;
  if (loc.kind === 'branch-slot') return `${loc.branchId}::slot::${loc.slotId}`; // branch slot
  if (loc.kind === 'repeat-template') return loc.templateId; // repeat 단위(template group) 자식
  return `${loc.tableId}::${loc.row}::${loc.col}`; // cell
}

/**
 * 트리에서 직접 children id 집합이 childIds와 *정확히 일치*하는 group/stack 컨테이너 id를 찾는다.
 * groupNodes 직후 새로 만들어진 컨테이너를 특정(선택 이동)하는 데 사용. 못 찾으면 null.
 * group/stack.children뿐 아니라 표 셀 content 안에 새로 생긴 컨테이너(셀 안 노드 묶기)도 찾는다.
 */
function findContainerWithExactChildren(nodes: ComposerNode[], childIds: string[]): string | null {
  const want = new Set(childIds);
  let found: string | null = null;
  const check = (n: ComposerNode) => {
    const ids = n.kind === 'group' || n.kind === 'stack' ? n.children.map((c) => c.id) : null;
    if (ids && ids.length === want.size && ids.every((id) => want.has(id))) {
      found = n.id;
      return true;
    }
    return false;
  };
  const walk = (arr: ComposerNode[]) => {
    for (const n of arr) {
      if (n.kind === 'group' || n.kind === 'stack') {
        if (check(n)) return;
        walk(n.children);
        if (found) return;
      } else if (n.kind === 'branch') {
        for (const slot of n.branches) {
          walk(slot.children);
          if (found) return;
        }
      } else if (n.kind === 'repeat') {
        // 반복 단위(template group) 안에서 묶기로 생긴 컨테이너도 특정 대상에 포함.
        walk([n.template]);
        if (found) return;
      } else if (n.kind === 'table') {
        for (const cell of n.cells) {
          if (!cell.content) continue;
          walk(cell.content);
          if (found) return;
        }
      }
    }
  };
  walk(nodes);
  return found;
}

/**
 * 다단(모델 A) 셀 판정 — 주어진 table의 columns≥2 반복영역들을 순회해 (row,col)을 분류한다.
 *  - 'pattern': 1단 패턴 칼럼범위(colStart~colEnd) = 운영자가 바인딩/편집하는 원본(헤더 정적 행 포함).
 *  - 'generated': colEnd 우측 N단 폭 안 = 엔진(expandTableCells)이 가로 복제로 생성할 읽기전용
 *    자리(헤더·데이터 모두). 여기에 바인딩하면 안 된다.
 *  - null: columns≥2 반복영역과 무관한 일반 셀.
 * **행 무관**(엔진이 정적 헤더도 칼럼범위면 가로 복제하므로 — 헤더+데이터 모두 1단 블록).
 * colStart 기본 0, colEnd 기본 colCount-1. 결정: 2026-06-30-composer-multicolumn-model-a.
 */
function classifyMultiColumnCell(
  table: TableNode | null,
  _row: number,
  col: number,
): 'pattern' | 'generated' | null {
  if (!table) return null;
  for (const region of table.repeatRegions ?? []) {
    const columns = region.columns ?? 1;
    if (columns < 2) continue;
    const colStart = region.colStart ?? 0;
    const colEnd = region.colEnd ?? table.colCount - 1;
    const patternWidth = colEnd - colStart + 1;
    if (col >= colStart && col <= colEnd) return 'pattern';
    // colEnd 우측으로 N단 폭(patternWidth*(columns-1))까지가 복제 생성 자리. 그 밖은 무관(null).
    if (col > colEnd && col <= colStart + patternWidth * columns - 1) return 'generated';
  }
  return null;
}

/**
 * 반복영역 셀 판정 — (row,col)이 어떤 repeatRegion의 데이터 밴드 범위 안에 드는지.
 * 행이 [rowStart, rowEnd], 열이 [colStart ?? 0, colEnd ?? colCount-1]이면 그 region을 반환(없으면 null).
 * 다단(columns≥2) 전용인 classifyMultiColumnCell과 달리 일반(단일 단) 반복영역도 포함한다 —
 * 반복 셀의 출처/계산은 반복영역 매핑이 관리하므로 단일 셀 편집기에서 출처·계산을 막는 데 쓴다.
 */
function isRepeatRegionCell(
  table: TableNode | null,
  row: number,
  col: number,
): TableRepeatRegion | null {
  if (!table) return null;
  for (const region of table.repeatRegions ?? []) {
    const colStart = region.colStart ?? 0;
    const colEnd = region.colEnd ?? table.colCount - 1;
    if (row >= region.rowStart && row <= region.rowEnd && col >= colStart && col <= colEnd) {
      return region;
    }
  }
  return null;
}

/**
 * 셀 안 노드 삽입(insertNodeIntoCell) 차단 사유를 반환한다(삽입 가능하면 null).
 * 팔레트·셀 편집기 "요소 추가"에서 공통으로 게이트한다. 차단 3종:
 *   ① 반복영역 셀(출처=반복 매핑이 관리) ② 다단 패턴/생성 셀 ③ 값 셀(binding/compute/aggregate).
 * op(insertNodeIntoCell) 자체는 값 셀에도 허용(순수 함수)이므로 차단은 여기(admin)서 판단한다.
 */
function cellNodeInsertBlockReason(
  table: TableNode | null,
  row: number,
  col: number,
): string | null {
  if (!table) return null;
  if (isRepeatRegionCell(table, row, col)) {
    return '반복영역 셀에는 요소를 넣을 수 없습니다. 반복 매핑이 이 셀을 관리합니다.';
  }
  if (classifyMultiColumnCell(table, row, col) !== null) {
    return '다단 패턴 셀에는 요소를 넣을 수 없습니다.';
  }
  const cell = table.cells.find((c) => c.row === row && c.col === col);
  if (cell && (cell.binding || cell.compute || cell.aggregate)) {
    return '값 셀에는 요소를 넣을 수 없습니다. 값을 제거 후 사용하세요.';
  }
  return null;
}

/**
 * 단일 선택 셀이 dataDef radio 필드에 바인딩돼 있으면 그 공유키+옵션을 반환한다(아니면 null).
 * 셀을 덮는 반복영역(region.over)이 가리키는 dataDef의 fields에서, 셀 binding.bindingKey와 같은
 * key의 필드를 찾아 widget==='radio' && options?.length>0 이면 채택한다. "옵션 체크박스 생성" 버튼
 * (각 옵션 = 공유키 체크박스)의 게이트 + 데이터 소스. 바인딩 셀은 값을 텍스트로만 렌더하므로,
 * 이 필드의 옵션을 `[ ]양호 [ ]불량` 라디오 그룹(공유 bindingKey + 옵션별 checkedValue)으로 펼친다.
 * (결정 기록 2026-07-07-composer-radio-group-via-shared-key-checkbox)
 */
function resolveRadioOptionField(
  template: ReportTemplate,
  ref: CellRef,
  /** 셀 지정으로 *새로 설정할* bindingKey(아직 셀 binding 미반영)를 검사할 때 지정. 미지정=셀 현재 binding. */
  bindingKeyOverride?: string,
): { bindingKey: string; options: { value: string; label: string }[] } | null {
  const host = findNodeById(template.body, ref.tableId);
  const table = host && host.kind === 'table' ? host : null;
  if (!table) return null;
  const cell = table.cells.find((c) => c.row === ref.row && c.col === ref.col);
  const bindingKey = bindingKeyOverride ?? cell?.binding?.bindingKey;
  if (!bindingKey) return null;
  const region = isRepeatRegionCell(table, ref.row, ref.col);
  if (!region) return null;
  const def = (template.dataDefs ?? []).find((d) => d.key === region.over);
  if (!def) return null;
  const field = def.fields.find((f) => f.key === bindingKey);
  if (!field || field.widget !== 'radio' || !field.options || field.options.length === 0) {
    return null;
  }
  return { bindingKey: field.key, options: field.options };
}

/**
 * dataDef radio 필드 셀 → 선택지 그룹(choiceGroup) 노드. 선택지 목록·값·라벨은 노드에 저장하지 않고
 * 렌더 시점에 dataDef options에서 파생한다(단일 정본 — 옵션 변경 시 박제 사본 드리프트 차단).
 * "선택지 그룹 생성" 버튼과 셀 지정 자동 생성이 공유한다. id는 body 충돌 회피 발급.
 * (종전 buildRadioGroupNode의 checkedValue 박제 체크박스 세트를 대체 — 결정 기록
 * 2026-07-15-composer-choice-group-node. 팔레트 '라디오 그룹' 손저작은 별도 존치.)
 */
function buildChoiceGroupNodeForField(
  body: ComposerNode[],
  idCounter: MutableRefObject<number>,
  bindingKey: string,
): ChoiceGroupNode {
  const taken = new Set(collectAllNodeIds(body));
  let id: string;
  do {
    id = `choicegroup-${++idCounter.current}`;
  } while (taken.has(id));
  const node = createDefaultNode('choiceGroup', id) as ChoiceGroupNode;
  node.direction = 'row';
  node.gap = 8;
  node.binding = { bindingKey, source: 'input', lifecycle: 'snapshot' };
  return node;
}

/**
 * 단일 선택 셀이 이미지 표시 필드에 바인딩돼 있으면 그 bindingKey를 반환한다(아니면 null).
 * 셀을 덮는 반복영역(region.over)이 가리키는 선언의 fields에서 찾는다 — 두 축:
 *  - 데이터 소스(template.dataSources) 필드 widget 'image'|'annotated-image'
 *  - 내장 데이터셋(template.dataDefs) 칼럼 widget 'photo'(P3 표 행별 사진 — 작성폼 셀에서
 *    업로드한 URL이 본문 반복 셀에서 텍스트로 새지 않게 같은 특례를 태운다)
 * 바인딩 셀은 값을 텍스트로만 렌더하므로(URL/객체가 문자열로 새 나온다), radio 선례
 * (resolveRadioOptionField→choiceGroup)와 동형으로 칼럼 매핑(셀 지정) 시 plain binding 대신
 * image 노드를 content로 자동 생성하는 게이트 + 데이터 소스다.
 */
function resolveImageField(
  template: ReportTemplate,
  ref: CellRef,
  /** 셀 지정으로 *새로 설정할* bindingKey(아직 셀 binding 미반영)를 검사할 때 지정. 미지정=셀 현재 binding. */
  bindingKeyOverride?: string,
): { bindingKey: string } | null {
  const host = findNodeById(template.body, ref.tableId);
  const table = host && host.kind === 'table' ? host : null;
  if (!table) return null;
  const cell = table.cells.find((c) => c.row === ref.row && c.col === ref.col);
  const bindingKey = bindingKeyOverride ?? cell?.binding?.bindingKey;
  if (!bindingKey) return null;
  const region = isRepeatRegionCell(table, ref.row, ref.col);
  if (!region) return null;
  const ds = (template.dataSources ?? []).find((d) => d.key === region.over);
  const dsField = ds?.fields.find((f) => f.field === bindingKey);
  if (dsField && (dsField.widget === 'image' || dsField.widget === 'annotated-image')) {
    return { bindingKey: dsField.field };
  }
  const def = (template.dataDefs ?? []).find((d) => d.key === region.over);
  const defField = def?.fields.find((f) => f.key === bindingKey);
  if (defField?.widget === 'photo') return { bindingKey: defField.key };
  return null;
}

/**
 * 이미지 표시 필드 셀 → image 노드. annotated 값(AnnotatedImageValue 객체)은 rc ImagePrim이
 * 값 형태로 자동 분기하므로 kind는 'image' 하나면 된다(위젯 'annotated-image'도 동일 노드).
 * createDefaultNode 기본(layout width:'fill' + minHeight + fit:'contain')을 그대로 쓴다 —
 * 높이 고정은 하지 않는다(annotatedPhoto는 원본 비율 전체가 보여야 0~1 좌표가 유효).
 * id는 body 충돌 회피 발급(buildChoiceGroupNodeForField와 동일 규약).
 */
function buildImageNodeForField(
  body: ComposerNode[],
  idCounter: MutableRefObject<number>,
  bindingKey: string,
  lifecycle: BindingLifecycle,
): ImageNode {
  const taken = new Set(collectAllNodeIds(body));
  let id: string;
  do {
    id = `image-${++idCounter.current}`;
  } while (taken.has(id));
  const node = createDefaultNode('image', id) as ImageNode;
  node.binding = { bindingKey, lifecycle };
  return node;
}

/** 프리셋 미해소 참조 축 → 사용자용 한글 라벨(삽입 안내 모달) — rc PresetRefAxis와 대응. */
const PRESET_REF_AXIS_LABEL: Record<PresetUnresolvedRef['axis'], string> = {
  visibleWhen: '표시 조건',
  enabledWhen: '활성 조건',
  labelSuffixFrom: '라벨 병기',
  'writes.target': '전파 대상',
  'actionItem.opensWidgetId': '드롭다운 액션 대상',
  'commit.to': '모달 확인 착지',
  'compute.input': '계산 셀 입력',
};

/**
 * 작성 위젯 삽입 위치 공통 헬퍼 — afterId 지정 시 그 위젯 바로 다음(같은 부모 형제 — 우클릭 컨텍스트
 * 메뉴의 "우클릭 대상 바로 다음" 규칙, 2026-07-17), 미지정 시 parentId 말미(트리 팔레트 "+ 위젯" 등
 * 기존 경로 무변경). afterId 앵커를 못 찾으면 insertWidgetsAfterId가 root 말미 폴백(위젯 유실 방지).
 */
function insertWidgetPositioned(
  tree: ComposerWidget[],
  widget: ComposerWidget,
  parentId: string | null,
  afterId?: string | null,
): ComposerWidget[] {
  return afterId != null
    ? insertWidgetsAfterId(tree, afterId, [widget])
    : insertWidgetAt(tree, widget, parentId, Number.MAX_SAFE_INTEGER);
}

/**
 * undo/redo 선택 복원 메타(2026-07-17) — 히스토리 스냅샷과 짝으로 캡처/복원되는 세션 전용 상태.
 * 템플릿(저장 JSON)에는 절대 들어가지 않는다(useEditorHistory가 불투명 메타로 별도 보관).
 * v1 범위: 작성 탭 위젯 선택 + 양식 탭 노드 선택만. ftFocusCell(표 셀 포커스)·modalAuthoringId
 * (모달 저작 모드)·selectedChrome 등 부가 UI 상태는 복원 제외(후속 확장 여지).
 */
interface EditorSelectionMeta {
  /** 작성 탭 위젯 다중선택 집합(불변식: 비어있지 않으면 anchor 포함) */
  widgetIds: string[];
  /** 작성 탭 위젯 anchor(주 선택, selectedWidgetId) */
  widgetAnchor: string | null;
  /** 양식 탭 노드 anchor(주 선택, selectedNodeId) */
  nodeId: string | null;
  /** 양식 탭 노드 다중선택 집합 — anchor만 복원하면 stale 집합이 불변식(anchor∈집합)을 깨므로 동반 캡처 */
  nodeIds: string[];
}

/**
 * 표시 형식의 "적용 구간 미리보기" 원료 = 캔버스 작성값(writeCanvasValues).
 *
 * 위젯 속성 패널은 이 값을 `previewValues` prop으로 받지만, 표 셀·일괄 셀·계산·바인딩 편집기는
 * 속성 패널에서 다섯 겹 아래에 있어 prop으로 내리면 중간 컴포넌트 하나만 빠뜨려도 미리보기가
 * **조용히 죽는다**. 실제로 그 네 호출부가 `catalog`만 넘겨 `RangeCriteriaPreview`가 늘
 * "컨텍스트를 고르면 표시됩니다"만 띄우고 있었다 — 그 화면에선 컨텍스트를 골라도 영영 안 뜨는
 * 거짓 안내였다(2026-08-06 리뷰). 새 채널이 아니라 **속성 패널이 쓰는 값 그대로**를 흘린다.
 *
 * 명시적 `previewValues` prop이 있으면 그쪽이 이긴다(기존 호출부 동작 불변).
 */
const PreviewValuesContext = createContext<Record<string, unknown> | undefined>(undefined);

export default function EditorPage() {
  // admin 빌더는 슈퍼관리자 권한(report-builder)으로 게이트한다(피처 플래그 아님 — 플래그는 web
  // 사용자 COMPOSED 보고서 롤아웃 전용). super_admin은 plag 대상이 아니라 권한으로 분리.
  const { data: permissionsData, isLoading: permLoading } = useQuery({
    queryKey: ['admin', 'my-permissions'],
    queryFn: () => adminApi.getMyPermissions(),
    staleTime: 5 * 60 * 1000,
  });
  const composerEnabled =
    !permLoading && (permissionsData?.data ?? []).includes('report-builder');

  const showToast = useToastStore((s) => s.showToast);

  // 히스토리에 선택 스냅샷 메타 동반(EditorSelectionMeta) — undo/redo가 그 시점 선택으로 복귀
  // (Figma류 관례). 아래 콜백들은 하단에 선언된 선택 state·catalogIndex를 참조하지만, 훅이
  // 이벤트 시점(commit/undo/redo)에만 호출하므로 TDZ 무관 + 매 렌더 최신 클로저가 훅 내부
  // optionsRef로 유지된다. 선택 변경 자체는 히스토리 엔트리를 만들지 않음(표준 동작 — 선택만
  // 바꾸고 undo하면 마지막 커밋 시점 선택으로 감).
  const history = useEditorHistory<EditorSelectionMeta>(emptyTemplate, {
    captureMeta: () => ({
      widgetIds: selectedWidgetIds,
      widgetAnchor: selectedWidgetId,
      nodeId: selectedNodeId,
      nodeIds: selectedNodeIds,
    }),
    restoreMeta: (meta, restored) => {
      // 복원 트리는 그 선택이 present였던 시점의 트리라 id 존재가 구성상 보장되지만,
      // 방어적으로 존재 확인해 없는 id는 제외한다(캡처 경계가 넓어져도 stale 선택 차단).
      const tree = syncComposerWidgets(
        restored.composerWidgetTree,
        deriveComposerWidgets(restored, catalogIndex),
      ).tree;
      const widgetIds = meta.widgetIds.filter((id) => selectedWidgetById(tree, id) !== null);
      let widgetAnchor =
        meta.widgetAnchor && selectedWidgetById(tree, meta.widgetAnchor)
          ? meta.widgetAnchor
          : null;
      // 불변식 유지: widgetIds 비어있지 않으면 anchor 포함(selectWidgetSingle/toggle 규약).
      // 캡처값이 이미 규약을 만족하고 filter가 같은 존재 판정을 쓰므로 setState 직접 세팅 안전.
      if (widgetIds.length > 0 && (widgetAnchor === null || !widgetIds.includes(widgetAnchor))) {
        widgetAnchor = widgetIds[widgetIds.length - 1];
      }
      setSelectedWidgetId(widgetAnchor);
      setSelectedWidgetIds(widgetIds);
      const nodeIds = meta.nodeIds.filter((id) => findNodeById(restored.body, id) !== null);
      let nodeAnchor = meta.nodeId && findNodeById(restored.body, meta.nodeId) ? meta.nodeId : null;
      // 불변식 유지: selectedNodeIds.length>0이면 selectedNodeId는 그 중 하나(selectSingleNode 규약).
      if (nodeIds.length > 0 && (nodeAnchor === null || !nodeIds.includes(nodeAnchor))) {
        nodeAnchor = nodeIds[nodeIds.length - 1];
      }
      setSelectedNodeId(nodeAnchor);
      setSelectedNodeIds(nodeIds);
    },
  });
  const {
    present: template,
    apply: applyHistory,
    replacePresent: replacePresentHistory,
    commitPresent: commitPresentHistory,
    discardTransient,
    reset,
    undo,
    redo,
    canUndo,
    canRedo,
  } = history;

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // 작성 페이지 3-패널 셸(writeForm 탭) 위젯 선택 — 좌 트리/중 미리보기 클릭 선택 → 우 속성창(Slice A).
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  // 작성 탭 위젯 다중선택 집합(양식 탭 selectedNodeIds 패턴 복제). selectedWidgetId=anchor(주 선택)로
  // 유지 — 속성창 등 단일 reader 불변. 불변식: 비어있지 않으면 selectedWidgetId를 포함.
  const [selectedWidgetIds, setSelectedWidgetIds] = useState<string[]>([]);
  // 좌 트리 행 hover ↔ 중앙 캔버스 실시간 연동 — 어떤 캔버스 위젯인지 옅은 하이라이트로 표시(2026-07-12).
  const [treeHoverWidgetId, setTreeHoverWidgetId] = useState<string | null>(null);
  // P1b: 캔버스에서 "모달 편집 열기"로 열린 modalTrigger id — 그 모달을 열린 상태로 유지하고 콘텐츠를
  // interactive(선택/인라인 편집)로 저작. null=닫힘. 속성창 버튼/Esc로 토글(선택 상태와 독립).
  const [modalAuthoringId, setModalAuthoringId] = useState<string | null>(null);
  // 캔버스 표(formTable) 셀 더블클릭/클릭 → 속성 패널 해당 행 펼침+셀 카드 스크롤/하이라이트 연동(2026-07-12).
  // source: 'canvas'=더블클릭(패널 스크롤 수행) / 'panel'=속성 셀 카드 클릭(스크롤 점프 없이 포커스만).
  // 복수 선택(2026-07-13): cells 배열의 마지막 = anchor(단일 셀 속성 뷰·복사 기준). 같은 표 안 고정
  // 셀만 복수 담김(반복 행 셀은 항상 단일). Ctrl/⌘+클릭으로 토글, 전부 해제되면 null(표 선택 단계).
  const [ftFocusCell, setFtFocusCell] = useState<{
    tableId: string;
    cells: { rowId: string; columnId: string }[];
    source: 'canvas' | 'panel';
  } | null>(null);
  // anchor 셀(복수 선택의 마지막) — 단일 셀 속성/복사/스크롤 등 기존 단일 소비처 호환. 없으면 null.
  const ftAnchorCell = useMemo(
    () =>
      ftFocusCell && ftFocusCell.cells.length > 0
        ? {
            tableId: ftFocusCell.tableId,
            ...ftFocusCell.cells[ftFocusCell.cells.length - 1],
            source: ftFocusCell.source,
          }
        : null,
    [ftFocusCell],
  );
  // Shift 범위 선택이 현재 위젯 트리(computeFtCellRange)를 읽어야 하나, focusFtCell는 트리(=
  // composerWidgetSync, 하단 선언)보다 앞에 정의된다. 안정 콜백(빈 deps) 유지 + 최신 트리 접근을
  // 위해 렌더마다 갱신되는 ref로 트리를 노출한다(latest-value ref 패턴, TDZ·stale 클로저 회피).
  const composerTreeRef = useRef<ComposerWidget[]>([]);
  // 셀 포커스 반영(캔버스/패널 공용). range=Shift 사각 범위(우선), additive=Ctrl/⌘ 토글(같은 표만),
  // 아니면 단일 선택 시작. 전부 토글 해제되면 null(표 선택 단계). source로 스크롤 여부 구분(canvas만 스크롤).
  const focusFtCell = useCallback(
    (
      loc: { tableId: string; rowId: string; columnId: string },
      opts?: { additive?: boolean; range?: boolean; source?: 'canvas' | 'panel' },
    ) => {
      const { tableId, rowId, columnId } = loc;
      const source = opts?.source ?? 'canvas';
      const cell = { rowId, columnId };
      setFtFocusCell((prev) => {
        // Shift+클릭 사각 범위 — additive보다 우선. 같은 표에 anchor(cells 마지막)가 있을 때만 범위,
        // 없으면 단일 선택 시작(anchor 설정). computeFtCellRange가 결과 마지막에 anchor를 유지한다.
        if (opts?.range) {
          if (!prev || prev.tableId !== tableId || prev.cells.length === 0) {
            return { tableId, cells: [cell], source };
          }
          const w = selectedWidgetById(composerTreeRef.current, tableId);
          if (!w || w.kind !== 'formTable') {
            return { tableId, cells: [cell], source };
          }
          const anchor = prev.cells[prev.cells.length - 1];
          return { tableId, cells: computeFtCellRange(w, anchor, cell), source };
        }
        if (!opts?.additive || !prev || prev.tableId !== tableId) {
          return { tableId, cells: [cell], source };
        }
        const exists = prev.cells.some((c) => c.rowId === rowId && c.columnId === columnId);
        const nextCells = exists
          ? prev.cells.filter((c) => !(c.rowId === rowId && c.columnId === columnId))
          : [...prev.cells, cell];
        return nextCells.length === 0 ? null : { tableId, cells: nextCells, source };
      });
    },
    [],
  );
  // 속성 패널 셀 카드 hover → 캔버스 표 안 해당 셀 하이라이트(포커스와 별개 — 스크롤 유발 없음).
  const [ftHoverCell, setFtHoverCell] = useState<{
    tableId: string;
    rowId: string;
    columnId: string;
  } | null>(null);
  // 작성 탭 Ctrl+Delete — formTable 포커스 셀이 걸친 행/열 삭제 모달(양식 탭 CellDeleteModal 재사용).
  const [ftDeleteModal, setFtDeleteModal] = useState<{
    tableId: string;
    rowIds: string[];
    colIds: string[];
    choice: CellDeleteChoice;
  } | null>(null);
  // chrome(헤더/하단 컨트롤 바) 슬롯 선택 — 미리보기 프레임 클릭 → 우 속성창에 그 슬롯 버튼 구성.
  // 위젯 선택과 상호배타(속성창은 한 번에 하나만 표시). null=chrome 미선택(위젯 속성 표시).
  const [selectedChrome, setSelectedChrome] = useState<ComposeChromeSlot | null>(null);
  // 노드 다중선택 집합(Phase B). selectedNodeId는 anchor(주 선택)로 유지 — 기존 단일 readers 불변.
  // 불변식: selectedNodeIds.length>0이면 selectedNodeId는 그 중 하나. 단일 선택이면 [id](또는 []).
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  // 단일 노드 선택 헬퍼 — anchor + 집합을 함께 세팅해 stale 다중선택이 남지 않게 한다.
  // 기존 "단일 노드 선택" 의도의 setSelectedNodeId(X) 호출을 전부 이 헬퍼로 교체한다(셀 선택이
  // tableId를 세팅하는 곳 포함 — 그 경우도 단일). 안정적(빈 deps)이라 하위 콜백 deps에 넣지 않아도 안전.
  const selectSingleNode = useCallback((id: string | null) => {
    setSelectedNodeId(id);
    setSelectedNodeIds(id ? [id] : []);
  }, []);
  // 선택 컨텍스트(group isolation root) — 현재 드릴 진입한 컨테이너 노드 id(group/stack/table/
  // repeat/branch). null이면 body 레벨. 단일 클릭 승격(resolveSingleClickTarget)·더블클릭 드릴
  // (resolveDrillTarget)·Esc pop이 이 값에 의존한다. 결정 기록 2026-07-06-composer-selection-model.
  const [selectionContextId, setSelectionContextId] = useState<string | null>(null);
  // 작성(writeForm) 탭 선택 컨텍스트(group isolation root) — 현재 드릴 진입한 섹션(group) 위젯 id.
  // null이면 root 레벨. 양식 탭 selectionContextId의 위젯 트리 버전(작성 탭 컨테이너는 group 하나뿐).
  // 단일클릭 승격(resolveWriteSingleClickTarget)·더블클릭 드릴(handleWidgetDrill)·Esc pop이 의존.
  const [writeSelectionContextId, setWriteSelectionContextId] = useState<string | null>(null);
  // branch 노드별 활성 슬롯 인덱스(편집 캔버스 탭) — RenderTemplate에 editing과 함께 전달해 그 슬롯만
  // 렌더한다(조건 미평가). 노드 삭제/템플릿 전환 등으로 stale 키가 남아도 렌더 시 0으로 clamp되어 무해.
  const [activeBranchSlots, setActiveBranchSlots] = useState<Record<string, number>>({});
  // 특정 branch의 활성 슬롯을 지정(탭/속성패널/오버레이 공용).
  const setActiveBranchSlot = useCallback((branchId: string, slotIndex: number) => {
    setActiveBranchSlots((prev) => ({ ...prev, [branchId]: slotIndex }));
  }, []);
  // 분기 관계 연결선(F9) — 슬롯 탭에서 그 슬롯 안 중첩 branch로 선을 긋는 표시 전용 모드.
  const [showBranchLinks, setShowBranchLinks] = useState(false);
  // 편집 캔버스의 현재 낱장 수 — PagedEditBody가 배치 확정 시 통지(onPageCount)한다.
  // 쓰는 곳은 **용지 설정 패널의 페이지번호 경고** 하나다: 「번호 출력 시작 페이지」가 전체 장수보다
  // 크면 엔진이 전 낱장을 조용히 skip하므로, 화면이 그 사실을 말해야 한다(안 말하면 저작자는 켰다고
  // 믿는다 — 은닉보다 나쁘다). 0은 「아직 측정 전」이고 경고 판정이 그 상태를 구분한다.
  const [canvasPageCount, setCanvasPageCount] = useState(0);
  // 같은 값이면 React가 리렌더를 생략하므로 측정 루프가 이 state를 흔들지 않는다.
  const handleCanvasPageCount = useCallback((n: number) => setCanvasPageCount(n), []);
  // 표 셀 선택 상태(T1 → 다중선택 확장) — 셀 클릭 시만 채워지고, 다른 노드 선택/팔레트 추가 등
  // 시 빈 배열로 클리어한다(셀↔노드 선택 상호 배타 유지). shift/ctrl 클릭으로 여러 셀을 누적
  // 선택해 스타일을 일괄 적용한다. anchor = selectedCells[0].
  const [selectedCells, setSelectedCells] = useState<CellRef[]>([]);
  // Shift+화살표 범위 확장의 "확장 끝점(focus)" 추적(엑셀식) — anchor(selectedCells[0])는 고정하고
  // focus만 화살표 방향으로 이동시켜 computeCellRange로 직사각 범위를 재계산한다. 확장 시(shift+화살표·
  // shift+클릭·드래그)에만 갱신하고, 그 외 선택 변화(단일 클릭·plain 화살표 등)는 focus가 현재 선택에
  // 없으면 anchor로 자동 폴백해 리셋 효과를 낸다(사용 시점 검증). null이면 anchor를 focus로 본다.
  const rangeFocusRef = useRef<CellRef | null>(null);
  // 단일 셀 선택일 때만(길이 1) 기존 CellEditor/행열/병합 핸들러가 동작한다(다중일 땐 BulkCellEditor).
  const singleCell = selectedCells.length === 1 ? selectedCells[0] : null;

  // 복사/잘라내기 소스 셀(엑셀식 marching ants) — 복사 성공 시 기록, 붙여넣기/Esc·선택 해제/재복사/
  // 편집 진입 시 해제. ClipboardSourceOverlay가 외곽 점선 테두리로 표시한다.
  const [clipboardSource, setClipboardSource] = useState<{ tableId: string; cells: CellRef[] } | null>(null);

  // 캔버스 우클릭 컨텍스트 메뉴 — scope: 양식 탭('form' 노드) / 작성 탭('write' 위젯),
  // target: 'selection'(대상 위 — 우클릭 시 클릭과 동일 규칙으로 선택 보정 완료) / 'canvas'(빈 영역).
  // 항목은 렌더 시점의 현재 상태로 조립(빌더 함수) — 열려 있는 동안 선택은 바뀌지 않는다.
  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    scope: 'form' | 'write';
    target: 'selection' | 'canvas';
    /**
     * form 우클릭의 **원시 대상**(승격 전 셀/노드)이 가리키는 작성 위젯 id — "위젯으로 이동" 활성 판정.
     * 우클릭 오프너가 그룹 우선 승격+셀 선택 해제를 하므로, 승격 후 선택 기반(crossNavTargetWidgetId)
     * 으로는 바인딩 노드/셀 위 우클릭이 비활성으로 오판됨(2026-07-16 제보).
     */
    formNavWidgetId?: string | null;
    /**
     * write 우클릭의 **원시 대상**(승격 전 위젯 id) — '위젯 추가'의 삽입 위치 판정 전용.
     * 캔버스 우클릭은 그룹 우선 선택(isolation)이면 선택을 상위 컨테이너로 승격시키므로, 승격 후
     * 선택으로 위치를 정하면 "반복을 가리켜 추가했는데 반복의 형제로 들어가는" 어긋남이 생긴다
     * (2026-07-22 사용자 제보). 위 formNavWidgetId와 같은 결의 보정.
     * ⚠️ 삽입 위치에만 쓴다 — 이동/삭제/프리셋 등 나머지 항목은 종전대로 **선택**에 작용한다.
     */
    addAnchorId?: string | null;
  } | null>(null);

  // 셀 full-fidelity 클립보드는 localStorage(CELL_CLIPBOARD_KEY) 단일 채널로 보관한다(탭/창 간 공유 +
  // 같은 탭 반복 붙여넣기). 붙여넣기 text가 스냅샷 tsv와 일치하면 내부 복사로 보고 값+서식+구조를 적용,
  // 다르면(엑셀 등 외부 TSV) 값만 적용(회귀 0). React state로 두지 않음(크로스탭이 목적).

  // R2 — 계산 입력 "값 소스" 콤보에서 "기존 값" 옵션 호버 시, 그 바인딩이 위치한 캔버스 셀/노드를
  // 앰버로 하이라이트할 대상 id(`cell:..` | `node:..`). 미호버/드롭다운 닫힘 시 null.
  const [highlightedSourceId, setHighlightedSourceId] = useState<string | null>(null);
  // R3 — 셀 직접 클릭으로 계산 입력을 지정하는 "셀 선택" 픽 모드. 활성 시 캔버스 클릭이 일반 선택
  // 대신 그 셀/노드의 바인딩을 apply(현재 입력의 onPatch 클로저)로 복사한다. token = 어느 입력 행이
  // 픽을 시작했는지(버튼 강조용 식별자). null이면 비활성(기존 클릭 동작 100% 동일).
  // 두 갈래(pickKind):
  //  - 'binding'(기본/미지정): 클릭 셀·노드의 *바인딩*을 apply에 넘긴다(계산 입력 "기존 값" 복사용, 기존 동작).
  //  - 'cell': 클릭 셀의 *좌표 ref*(tableId/row/col)를 apply에 넘긴다(반복영역 칼럼↔셀 매핑용, 빈 셀도 허용).
  // 'cell' 갈래는 매핑 가능 행 강조/제약용으로 tableId·anchorRows를 함께 싣는다.
  //  - tableId: 픽 대상 표(다른 표 클릭 거부).
  //  - anchorRows: 매핑 허용 행 밴드(null=제약 없음). **반복 영역의 rowStart~rowEnd**를 싣는다 —
  //    다행 반복(항목당 2행+)에서는 같은 소스의 필드가 영역 안 서로 다른 행에 앉으므로,
  //    종전의 "첫 셀이 정한 행 하나" 제약은 아래 행 지정을 막는 결함이었다(2026-08-28
  //    안전보건교육 계획 2행 배치 실측에서 적발).
  //  - hint: 픽 안내 배너 문구(미지정이면 기본 문구). 2단계 픽처럼 단계마다 안내가 달라지는 흐름용.
  //  - onFillRow: 표 아래 **채움 행**(data-composer-fill-row — 빈 괘선으로 페이지를 채우는 가상 행,
  //    셀 dataset 없음) 클릭을 받는 갈래. 지정하면 그 표의 채움 행 클릭이 거부 대신 이 콜백으로
  //    간다(인자 = 그 표 채움 행 중 몇 번째인가, 1-based). 「패턴 행 설정」의 "표 밖까지 확장"용.
  const [cellPick, setCellPick] = useState<
    | { token: string; pickKind?: 'binding'; apply: (binding: Binding) => void }
    | ({
        token: string;
        pickKind: 'cell';
        apply: (ref: CellRef) => void;
      } & CellPickOpts)
    | null
  >(null);
  // R3 픽 모드 호버 미리보기 — 픽 활성 중 캔버스 위 셀/노드에 마우스를 올리면 "클릭하면 이게 선택된다"를
  // 보여줄 대상. id(`cell:..`|`node:..`) + hasBinding(바인딩 유무 → 파랑/빨강). 픽 비활성/빈영역 시 null.
  const [pickHover, setPickHover] = useState<{ id: string; hasBinding: boolean } | null>(null);

  // 캔버스 인라인 텍스트 편집 대상(더블클릭/F2/재클릭 진입). text 노드 또는 정적/빈 텍스트 셀.
  // null이면 인라인 에디터 미표시(속성 패널로만 편집).
  const [editingTarget, setEditingTarget] = useState<EditingTarget | null>(null);
  // 엑셀식 "셀 선택 중 글자 입력 → 편집 자동 진입(IME 안전, 덮어쓰기)"이 캡처 textarea로 진행 중인지.
  // true면 editingTarget(셀)을 캡처 textarea가 직접 편집(InlineTextEditor 미마운트 — 동일 DOM이라 첫
  // 자모 보존). false면 일반 편집(InlineTextEditor 마운트). 결정 기록 2026-06-28-composer-cell-type-to-edit.
  const [captureEditing, setCaptureEditing] = useState(false);
  // 상시 캡처 textarea ref — 셀 선택(탐색) 상태에서 .focus()로 포커스를 유지해 IME가 처음부터 작동.
  const captureRef = useRef<HTMLTextAreaElement>(null);

  // 상단 뷰 모드(양식 편집 / 작성폼). 기본 'form'.
  // 뷰 모드(양식 편집/작성폼) — localStorage 영속(새로고침 후 마지막 탭 복원, 2026-07-04).
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem('composer.viewMode');
      if (saved === 'form' || saved === 'writeForm') return saved;
    } catch { /* storage 차단 무시 */ }
    return 'form';
  });
  useEffect(() => {
    try { localStorage.setItem('composer.viewMode', viewMode); } catch { /* storage 차단 무시 */ }
  }, [viewMode]);
  // 작성 탭 "양식 내 위치" 브레드크럼/우클릭 이동 → 양식(form) 탭 전환 + 그 body 노드 선택.
  // 표 셀 바인딩(placement.cell)이면 **그 셀까지 선택**(2026-07-16 사용자 확정) + 셀 앵커 플래시
  // (미발견 시 표 노드 앵커 폴백). 셀 없는 배치는 종전대로 셀 선택 초기화 + 노드 플래시.
  const navigateToBodyNode = useCallback(
    (nodeId: string, cell?: { row: number; col: number }) => {
      setViewMode('form');
      setSelectionContextId(null);
      selectSingleNode(nodeId);
      if (cell) {
        setSelectedCells([{ tableId: nodeId, row: cell.row, col: cell.col }]);
        flashAndScroll([
          `[data-composer-cell="${nodeId}::${cell.row}::${cell.col}"]`,
          `[data-composer-node-id="${nodeId}"]`,
        ]);
        return;
      }
      setSelectedCells([]);
      flashAndScroll(`[data-composer-node-id="${nodeId}"]`);
    },
    [selectSingleNode],
  );

  // 작성폼 반응형 뷰(데스크톱/바형/폴드형) — 저작 캔버스에서 뷰별 오버라이드 편집 대상.
  // 결정: 2026-07-09-composer-responsive-view-overrides. 기본=base(데스크톱). localStorage 영속.
  const [writeFormView, setWriteFormView] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('composer.writeFormView');
      if (saved && COMPOSER_VIEWS.some((v) => v.key === saved)) return saved;
    } catch { /* storage 차단 무시 */ }
    return COMPOSER_BASE_VIEW_KEY;
  });
  useEffect(() => {
    try { localStorage.setItem('composer.writeFormView', writeFormView); } catch { /* storage 차단 무시 */ }
  }, [writeFormView]);
  // 비교 뷰 — 주 캔버스 옆에 나란히 표시할 다른 뷰(read-only 실측). null=미표시. localStorage 영속.
  const [compareView, setCompareView] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem('composer.compareView');
      if (saved && COMPOSER_VIEWS.some((v) => v.key === saved)) return saved;
    } catch { /* storage 차단 무시 */ }
    return null;
  });
  useEffect(() => {
    try {
      if (compareView) localStorage.setItem('composer.compareView', compareView);
      else localStorage.removeItem('composer.compareView');
    } catch { /* storage 차단 무시 */ }
  }, [compareView]);
  // 작성 상황 미리보기 시뮬레이션(2026-09-06 §3) — 생성(단건)/분할 이어 작성/분할 마지막/수정.
  // 모드(visibleIn 해소 축)는 여기서 파생되고, 이어질 흐름 CTA는 시뮬이 아니라 편집 중
  // policy.postSubmit에서 온다(저작자가 자기 선언의 효과를 눈으로 확인하는 것이 목적).
  const [previewSituationKey, setPreviewSituationKey] =
    useState<PreviewSubmitSituationKey>('create-single');
  const previewSubmitSituation = useMemo(
    () => resolvePreviewSubmitSituation(previewSituationKey, template.policy),
    [previewSituationKey, template.policy],
  );
  const previewComposeMode: ComposeMode = previewSubmitSituation.mode;
  const previewSituationOptions = useMemo(
    () => previewSubmitSituationOptions(template.policy),
    [template.policy],
  );
  // 목록에 없는 값을 select에 주면 칸이 빈칸이 된다 — 그리는 상황(위 파생)과 같은 값을 보인다.
  const previewSituationValue = clampPreviewSubmitSituationKey(previewSituationKey, template.policy);
  const activeViewDef = useMemo(
    () => COMPOSER_VIEWS.find((v) => v.key === writeFormView) ?? COMPOSER_VIEWS[0],
    [writeFormView],
  );
  // 뷰별 선택 단말(해상도 프리셋) 인덱스 — 캔버스 미리보기 폭/비율. localStorage 영속.
  const [deviceByView, setDeviceByView] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('composer.deviceByView');
      if (saved) return JSON.parse(saved) as Record<string, number>;
    } catch { /* 무시 */ }
    return {};
  });
  useEffect(() => {
    try { localStorage.setItem('composer.deviceByView', JSON.stringify(deviceByView)); } catch { /* 무시 */ }
  }, [deviceByView]);
  const activeDevice = useMemo(() => {
    const list = activeViewDef.devices ?? [];
    if (list.length === 0) return undefined;
    const idx = Math.min(deviceByView[writeFormView] ?? 0, list.length - 1);
    return list[idx];
  }, [activeViewDef, deviceByView, writeFormView]);
  // 캔버스 미리보기 폭 — 선택 단말 폭 우선, 없으면 뷰 기본 폭.
  const canvasPreviewWidth = activeDevice?.width ?? activeViewDef.authoringWidthPx ?? null;
  // 비교 캔버스(동반 read-only) — 활성 뷰와 같으면 무효(단순 가드: select엔 남되 렌더 없음).
  const effectiveCompare = compareView && compareView !== writeFormView ? compareView : null;
  const compareViewDef = useMemo(
    () => (effectiveCompare ? COMPOSER_VIEWS.find((v) => v.key === effectiveCompare) : undefined),
    [effectiveCompare],
  );
  // 비교 뷰의 선택 단말 — 뷰별 저장(deviceByView)이라 활성 단말과 독립적으로 정합.
  const compareDevice = useMemo(() => {
    const list = compareViewDef?.devices ?? [];
    if (!effectiveCompare || list.length === 0) return undefined;
    const idx = Math.min(deviceByView[effectiveCompare] ?? 0, list.length - 1);
    return list[idx];
  }, [compareViewDef, deviceByView, effectiveCompare]);
  const compareWidth = compareDevice?.width ?? compareViewDef?.authoringWidthPx ?? 794;
  const compareHeight = compareDevice?.height ?? 640;

  // 패딩 시각화 오버레이 표시 여부(localStorage 영속, 기본 켜짐).
  const [showPadding, setShowPadding] = useState<boolean>(() => {
    try { return localStorage.getItem('composer.showPadding') !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('composer.showPadding', String(showPadding)); } catch { /* storage 차단 무시 */ }
  }, [showPadding]);
  // 배지(바인딩🔗/계산ƒ/집계∑) 오버레이 표시 여부 — "배지 보기" 토글(localStorage 영속, 기본 켜짐).
  const [showBadges, setShowBadges] = useState<boolean>(() => {
    try { return localStorage.getItem('composer.showBadges') !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('composer.showBadges', String(showBadges)); } catch { /* storage 차단 무시 */ }
  }, [showBadges]);
  // 캔버스 선택 방식 토글 — 'isolation'(그룹 우선, 기본): 단일클릭이 현재 컨텍스트 자식까지 승격 선택 +
  // 더블클릭 드릴 + Esc 컨텍스트 pop + 표 셀은 그 표로 드릴한 뒤에만 선택. 'deepest'(최심부 우선, 이전
  // 복원): 클릭한 최심부 노드를 승격 없이 직접 선택 + 선택된 text 재클릭=인라인 편집 + 더블클릭=편집만
  // (드릴 없음) + Esc=부모로 드릴업 + 표 셀 즉시 선택. localStorage 영속(기본 'isolation').
  const [selectionMode, setSelectionMode] = useState<CanvasSelectionMode>(() => {
    try {
      return localStorage.getItem(SELECTION_MODE_STORAGE_KEY) === 'deepest' ? 'deepest' : 'isolation';
    } catch { return 'isolation'; }
  });
  useEffect(() => {
    try { localStorage.setItem(SELECTION_MODE_STORAGE_KEY, selectionMode); } catch { /* storage 차단 무시 */ }
  }, [selectionMode]);
  // 모드 전환 시 선택 컨텍스트를 body 레벨(null)로 리셋 — 'deepest'는 컨텍스트 개념이 없어 stale
  // isolation 컨텍스트가 남으면 셀 게이팅/승격이 오작동한다. 초기 mount 시엔 이미 null이라 무해.
  useEffect(() => {
    setSelectionContextId(null);
  }, [selectionMode]);
  // 작성 탭 선택 컨텍스트도 모드/탭 전환 시 root 레벨(null)로 리셋 — 'deepest'는 컨텍스트 개념이
  // 없고, 탭 전환 시 stale 컨텍스트가 남으면 다음 진입에서 승격/드릴이 오작동한다.
  useEffect(() => {
    setWriteSelectionContextId(null);
  }, [selectionMode, viewMode]);

  // ── 미리보기 실데이터 컨텍스트(엔티티 캐스케이드) ──
  // 편집 캔버스 + 미리보기 탭 공통. 조직/현장/점검/계정을 골라 그 실데이터로 렌더.
  // resolved content는 시스템/computed 카탈로그만 채우며, 캔버스는 placeholder와 병합한다.
  // 결정 기록: docs/decisions/2026-06-22-composer-preview-context-entity-cascade.md
  // 「보고서 연결」 축(linkedReportId)까지 담는 admin 확장 형상 — previewLinkedReport.ts JSDoc.
  const [previewContext, setPreviewContext] = useState<EditorPreviewContext>({});
  const [previewResult, setPreviewResult] = useState<ReportComposerPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // 편집(바인딩/조립데이터) 후 자동 재조회 진행 표시 — 비차단(편집 계속 가능), 500ms 지연 후에만 표시.
  const [refetching, setRefetching] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // 카탈로그(노드 바인딩 편집용) — 마운트 시 1회 fetch. catalogIndex: catalogKey→entry 조회용
  // (deriveWriteForm이 input 바인딩의 위젯 메타를 채운다). 팝아웃 sync 페이로드가 값 출처 재료
  // (reportScoped 키)를 실어야 해서 미리보기 상태 곁에 둔다 — 선언 순서 제약일 뿐 의미는 그대로.
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const catalogIndex = useMemo(() => buildCatalogIndex(catalog), [catalog]);

  // 값 출처(provenance) 재료 — 채워진 값이 모사인지/연결 보고서 저장값인지 화면에 알리는 축.
  // 게이트는 `previewResult` 존재(=실데이터 반영 중)다. 바인딩 보기(실데이터 미반영) 상태의 값은
  // 전부 placeholder 표본이라 출처를 말할 것이 없고, 모사는 서버 resolve content로만 화면에 닿는다.
  // previewContext가 바뀌면 previewResult가 통째로 무효화되므로(아래 effect), previewResult가
  // 살아 있는 동안의 previewContext는 **그 결과를 만든 요청 그대로**다 — 재료가 화면과 갈리지 않는다.
  const provenanceMaterial = useMemo(
    () =>
      previewResult
        ? buildPreviewProvenanceMaterial(previewContext, catalogIndex)
        : undefined,
    [previewResult, previewContext, catalogIndex],
  );
  const bindingProvenance = useMemo(
    () => (provenanceMaterial ? toBindingProvenance(provenanceMaterial) : undefined),
    [provenanceMaterial],
  );

  // 결재 서명 단계 모사 값(`__approvalSigned`, 2026-09-04) — 서버가 명시적으로 건너뛰는 축이라
  // 클라(applySimulatedApproval)가 적용 지점이다. 게이트는 previewResult 존재(=실데이터 반영 중,
  // 모사 칩 표시 조건과 동치): 스칼라 모사가 서버 resolve content로만 화면에 닿으므로, 이 축만
  // [해제] 후에도 남아 있으면 "무엇이 모사 중인가"가 칩·화면 사이에서 갈라진다.
  const approvalSignedLevel = useMemo(() => {
    if (!previewResult) return undefined;
    const v = previewContext.previewSimulation?.[PREVIEW_SIMULATION_APPROVAL_SIGNED_AXIS_KEY];
    return typeof v === 'number' && v > 0 ? v : undefined;
  }, [previewResult, previewContext]);

  // ── 팝아웃 미리보기 창 연동(EditorPage 상주 — 모든 뷰에서 항상 방송) ──
  // 팝업이 mount 시 방송하는 'ready'에 현재 스냅샷으로 응답하고, 이후 template/previewResult 변경마다
  // sync를 debounce(150ms) 방송한다(팝업은 자체 mergePreviewContent 재렌더 → 서버 왕복 없음). 팝업
  // content는 미리보기가 쓰는 것과 동일: 실데이터면 previewResult.content, 없으면 {}(placeholder).
  // 채널이 EditorPage mount 시 열려 편집(양식편집/작성폼) 중에도 실시간 반영(팝업 안 열렸으면 무해).
  const popoutWindowRef = useRef<Window | null>(null);
  const popoutChannelRef = useRef<BroadcastChannel | null>(null);
  const syncPayload = useMemo<PreviewSyncPayload>(
    () => ({
      template,
      content: previewResult ? previewResult.content : {},
      hasRealData: !!previewResult,
      viewMode,
      approvalSignedLevel,
      provenance: provenanceMaterial,
    }),
    [template, previewResult, viewMode, approvalSignedLevel, provenanceMaterial],
  );
  // ready 핸드셰이크가 최신 스냅샷으로 응답하도록 ref로 보관(채널 effect는 mount 시 1회만 등록).
  const latestSyncRef = useRef(syncPayload);
  useEffect(() => {
    latestSyncRef.current = syncPayload;
  }, [syncPayload]);
  // "F8에서 편집" 클릭으로 chrome 편집을 요청했으나 팝업이 아직 mount 전이면(방금 window.open),
  // 즉시 방송한 enterChromeEdit이 유실된다. 팝업 ready 도착 시 이 플래그가 서면 재방송 후 클리어.
  const chromeEditPendingRef = useRef(false);

  useEffect(() => {
    const ch = new BroadcastChannel(PREVIEW_POPOUT_CHANNEL);
    popoutChannelRef.current = ch;
    ch.onmessage = (ev: MessageEvent<PreviewPopoutMessage>) => {
      if (ev.data.type === 'ready') {
        ch.postMessage({ type: 'sync', ...latestSyncRef.current } satisfies PreviewPopoutMessage);
        // 방금 열린 팝업이라면 유실된 chrome 편집 요청을 여기서 재전달(1회).
        if (chromeEditPendingRef.current) {
          chromeEditPendingRef.current = false;
          ch.postMessage({ type: 'enterChromeEdit' } satisfies PreviewPopoutMessage);
        }
      } else if (ev.data.type === 'detailChromeEdit') {
        // 팝아웃 chrome 편집 결과 → template.detailChrome 갱신(undo 퍼널). 갱신은 syncPayload effect가
        // 다시 팝업으로 sync해 바가 즉시 반영(라벨 입력은 팝업이 로컬 state로 mid-typing 리셋 방지).
        // applyHistory는 useEditorHistory useCallback([update])라 참조 안정 — mount-once 클로저에서 안전.
        const next = ev.data.detailChrome;
        applyHistory((t) => ({ ...t, detailChrome: next }));
      } else if (ev.data.type === 'save') {
        // 팝업 저장 버튼/Ctrl+S → 부모가 저장 실행. saveRef/busyRef는 매 렌더 갱신(최신 참조),
        // mount-once 클로저에서도 안전(effect는 렌더 후 실행돼 초기화 완료).
        if (!busyRef.current) void saveRef.current();
      }
    };
    return () => {
      ch.postMessage({ type: 'closed' } satisfies PreviewPopoutMessage);
      ch.close();
      popoutChannelRef.current = null;
    };
  }, []);

  useEffect(() => {
    const ch = popoutChannelRef.current;
    if (!ch) return;
    const t = window.setTimeout(() => {
      ch.postMessage({ type: 'sync', ...syncPayload } satisfies PreviewPopoutMessage);
    }, 150);
    return () => window.clearTimeout(t);
  }, [syncPayload]);

  // 미배치 풀 행 hover → 팝업 미리보기에 그 바인딩이 놓인 body 노드 하이라이트를 방송(팝업 미개설 시
  // 수신자 없어 무해). 연타 노이즈 방지: 같은 nodeId 연속 방송은 스킵(ref 비교, 디바운스 불요).
  const lastHighlightNodeRef = useRef<string | null>(null);
  const broadcastPreviewHighlight = useCallback(
    (nodeId: string | null, cell?: { row: number; col: number }) => {
      const ch = popoutChannelRef.current;
      if (!ch) return;
      if (lastHighlightNodeRef.current === nodeId) return;
      lastHighlightNodeRef.current = nodeId;
      ch.postMessage({ type: 'highlight-node', nodeId, cell } satisfies PreviewPopoutMessage);
    },
    [],
  );

  // ── 미배치 풀 위젯 hover 미니 미리보기(에디터 내부 자동 툴팁, 팝아웃 방송과 병행) ──
  // 풀 행에 커서를 올리면 그 바인딩이 놓인 body 부분의 실제 렌더 미니 미리보기를 커서 옆에 표시.
  // 표시 지연 ~250ms(리스트 훑을 때 난사 방지), 숨김은 즉시. 배치 0곳 위젯은 placement 없어 미표시.
  const [poolHoverPreview, setPoolHoverPreview] = useState<{
    placement: BindingPlacement;
    anchorRect: DOMRect;
  } | null>(null);
  const poolPreviewShowTimerRef = useRef<number>();
  const poolPreviewHideTimerRef = useRef<number>();
  const showPoolPreview = useCallback(
    (placement: BindingPlacement | null, anchorRect: DOMRect | null) => {
      window.clearTimeout(poolPreviewShowTimerRef.current);
      window.clearTimeout(poolPreviewHideTimerRef.current);
      if (!placement || !anchorRect) {
        // 행 leave: 즉시 숨기지 않고 ~200ms 유예 — 커서가 팝오버로 이동할 시간(패닝/줌 상호작용).
        poolPreviewHideTimerRef.current = window.setTimeout(() => {
          setPoolHoverPreview(null);
        }, 200);
        return;
      }
      poolPreviewShowTimerRef.current = window.setTimeout(() => {
        setPoolHoverPreview({ placement, anchorRect });
      }, 250);
    },
    [],
  );
  // 팝오버 진입 → 숨김 유예 취소(유지). 팝오버 이탈 → 즉시 숨김.
  const keepPoolPreview = useCallback(() => {
    window.clearTimeout(poolPreviewHideTimerRef.current);
  }, []);
  const hidePoolPreview = useCallback(() => {
    window.clearTimeout(poolPreviewShowTimerRef.current);
    window.clearTimeout(poolPreviewHideTimerRef.current);
    setPoolHoverPreview(null);
  }, []);
  useEffect(
    () => () => {
      window.clearTimeout(poolPreviewShowTimerRef.current);
      window.clearTimeout(poolPreviewHideTimerRef.current);
    },
    [],
  );

  const openPreviewPopout = useCallback(() => {
    const existing = popoutWindowRef.current;
    if (existing && !existing.closed) {
      existing.focus(); // 이미 열린 창은 새로 열지 않고 focus(중복 창 방지).
      return;
    }
    popoutWindowRef.current = window.open(
      PREVIEW_POPOUT_ROUTE,
      PREVIEW_POPOUT_WINDOW_NAME,
      'width=900,height=1000',
    );
  }, []);
  // "F8 미리보기에서 편집" — 팝아웃을 열고 chrome 편집 모드 진입을 방송. 이미 열려 있으면 즉시 수신,
  // 방금 연 경우엔 팝업 ready 도착 시 재방송(chromeEditPendingRef)해 유실을 막는다.
  const openChromeEditInPopout = useCallback(() => {
    chromeEditPendingRef.current = true;
    openPreviewPopout();
    popoutChannelRef.current?.postMessage(
      { type: 'enterChromeEdit' } satisfies PreviewPopoutMessage,
    );
  }, [openPreviewPopout]);
  // 오버레이 재측정 트리거(CanvasViewport remeasureDep) — template(노드 추가/삭제/리사이즈)뿐 아니라
  // 슬롯 전환(activeBranchSlots)과 미리보기 resolve 응답 도착(previewResult — 시뮬레이션/실데이터가
  // 디바운스+API 왕복 뒤에 콘텐츠 높이를 바꿈)도 DOM 높이를 바꾸므로 포함.
  // ⚠️ 인라인 배열 리터럴 금지 — 매 렌더 새 참조면 tick→재렌더 무한 루프. useMemo 필수.
  const remeasureDep = useMemo(
    () => [template, activeBranchSlots, previewResult],
    [template, activeBranchSlots, previewResult],
  );
  // "placeholder로"로 실데이터를 끈 상태 — 새로고침 후 자동 재적용을 억제(엔티티 선택은 유지). per-key 영속.
  const [autoApplySuppressed, setAutoApplySuppressed] = useState(false);
  // 선택 엔티티의 라벨(요약 칩 표시용) — IDs만으론 이름 표시 불가하므로 별도 보관·영속.
  const [previewContextLabels, setPreviewContextLabels] = useState<PreviewContextLabels>({});
  // "실제 데이터 반영" 모달 열림.
  const [realDataModalOpen, setRealDataModalOpen] = useState(false);
  // 미리보기에 **적용 중인** 모사 프리셋 id(2026-09-04 성격 분리) — 속성 패널의 인라인 수정이
  // 즉시 반영되는 대상. 세션 한정 state다: 영속하지 않고, 템플릿 로드 시 기본 프리셋
  // (defaultSimulationPresetId)으로 초기화된다(결정 §4 — 재로드하면 기본으로 복귀).
  const [appliedPresetId, setAppliedPresetId] = useState<string | null>(null);
  // 실데이터 자동 적용 dedupe — 마지막으로 적용한 (currentKey+previewContext) 시그니처.
  // 같은 조합엔 재fetch하지 않고, 엔티티 변경 시엔 시그니처가 달라져 자동 재적용된다.
  const lastAppliedCtxRef = useRef<string | null>(null);
  // 지금 실린 모사 값이 **이 데이터에서** 아직 한 번도 서버를 통과하지 못했는가.
  // 켜지는 시점 둘: 새로고침 복원(저장된 모사를 되살렸을 때) · 엔티티 변경(다른 점검이면 계획
  // 산출 행 id가 남의 것일 수 있다). 첫 자동 적용의 성패가 갈리는 순간 끈다 — stale 해제가
  // **전환 1회로 끝나는** 근거다.
  const simulationUnverifiedRef = useRef(false);
  // currentKey: 현재 편집 중인 DB 템플릿 key(로드/저장 완료된 것). 신규(미저장)면 null.
  // (handleApplyContext/자동 재적용 effect가 참조하므로 preview 상태와 함께 앞쪽에 선언)
  const [currentKey, setCurrentKey] = useState<string | null>(null);

  // 컨텍스트에 1개 이상 엔티티가 지정되었는지(=실데이터 적용 대상).
  const hasPreviewContext =
    !!previewContext.companyId ||
    !!previewContext.siteId ||
    !!previewContext.inspectionId ||
    !!previewContext.inspectionIds?.length ||
    !!previewContext.userId;

  // previewContext(엔티티 선택)가 바뀌면 이전 resolve 결과를 무효화한다(다른 엔티티 = 다른 실데이터).
  // template 편집(바인딩/레이아웃/format)은 mergePreviewContent가 새 template으로 재머지하므로
  // 결과를 유지한다 — format 등은 즉시 반영되고, bindingKey가 바뀐 셀만 placeholder로 자연 degrade
  // (그 경우 아래 바인딩 시그니처 감시가 실데이터를 자동 재조회해 복구). 이로써 편집 중 실데이터 유지.
  useEffect(() => {
    setPreviewResult(null);
    setPreviewError(null);
  }, [previewContext]);

  // ── 바인딩 변경 시 실데이터 자동 재조회 ──
  // previewResult는 반영 시점 템플릿 기준 스냅샷이라, 이후 **새 바인딩 추가**(새 catalogKey 포함)나
  // catalogKey/select 변경은 스냅샷에 값이 없어 placeholder로 남는다("바인딩이 안 먹었나" 오해).
  // 데이터 재조회가 필요한 바인딩 속성만 직렬화한 시그니처를 감시해, 변할 때만 디바운스 재조회한다.
  // 레이아웃/format/스타일/텍스트 편집은 시그니처 불변 → 재조회 없음(기존 재머지 유지).
  const bindingSignature = useMemo(
    () => computeBindingSignature(template.body),
    [template.body],
  );

  // 분기 슬롯 시뮬레이션 — 활성 슬롯(activeBranchSlots) 조건 eq 절을 데이터 소스 필터로 승격.
  // "그 슬롯 조건에 맞는 데이터가 들어온 것처럼" 실데이터 미리보기(placeholder 모드는 API 미호출이라 무관).
  const { simulate: branchSimulate, branchesByCatalogKey } = useMemo(
    () => buildBranchSimulate(template.body, activeBranchSlots),
    [template.body, activeBranchSlots],
  );
  // 시뮬레이션 필터 + 조립/내장 데이터셋 시그니처 — 활성 슬롯 전환·dataSources(필드 매핑/lookup)·dataDefs
  // 편집은 bindingSignature(body 기반)를 안 바꾸지만 서버 resolve 결과를 바꾸므로 별도로 재조회를 트리거한다
  // (예: 조치사항 칼럼 값 출처를 raw→refined 변경 시 실데이터 즉시 반영).
  const simulateSignature = useMemo(
    () => JSON.stringify([branchSimulate ?? null, template.dataSources ?? null, template.dataDefs ?? null]),
    [branchSimulate, template.dataSources, template.dataDefs],
  );
  // 작성 탭 캔버스에서 사용자가 직접 편집한 값(오버레이). 템플릿에 저장되지 않음 — wtApply 경유
  // 금지라 히스토리/dirty에 무영향. prefill 컨텍스트 변경/[해제] 시 초기화(스테일 방지, 아래 effect).
  // (previewMergedData와 **본문 미리보기 방식 오버라이드**가 참조하므로 그보다 앞서 선언 — TDZ 방지.)
  const [writeCanvasEdits, setWriteCanvasEdits] = useState<Record<string, unknown>>({});
  // 본문 미리보기에 실을 위험도 평가 방식 — 저작자가 캔버스에서 **직접 고른** 값만(edits 계층).
  // 없으면 undefined → 서버 계층 폴백(= 현행 동작 그대로, 회귀 0). 판정 규칙과 "왜 순환이 없는가"는
  // previewMethodOverride.ts JSDoc 참조(요지: edits는 preview에서 파생되지 않는 유일한 계층이다).
  const previewMethodOverride = useMemo(
    () => resolveAuthoredFindingRiskMethod(template, writeCanvasEdits),
    [template, writeCanvasEdits],
  );
  // 재조회 판단용 복합 시그니처(바인딩 + 시뮬레이션). 둘 중 하나라도 바뀌면 실데이터를 재조회한다.
  const combinedResolveSignature = useMemo(
    () => `${bindingSignature}\u0000${simulateSignature}`,
    [bindingSignature, simulateSignature],
  );
  // 실데이터 재조회 **요청 축** — 위 복합 시그니처(템플릿 파생) + 저작자가 고른 방식.
  // 방식을 별도 축으로 덧대는 이유: 방식이 바뀌면 서버가 다른 config로 조립하므로(구간표·분기 행렬)
  // 기존 스냅샷은 옛 방식 산출이라 재조회해야 한다. 최초 로드에선 edits가 비어 오버라이드가 항상
  // undefined → 축이 상수라 스퓨리어스 재조회가 없다(엔티티 전환 시엔 edits 초기화로 함께 리셋).
  const resolveRequestSignature = useMemo(
    () => `${combinedResolveSignature}|method=${previewMethodOverride ?? ''}`,
    [combinedResolveSignature, previewMethodOverride],
  );
  // API 전송용 컨텍스트 — 엔티티 선택 + 시뮬레이션 필터 병합(엔티티만 담은 previewContext는 그대로 유지).
  // 보고서 연결 중이면 **값 모사만** 벗긴다(배타 — 결정 2026-09-04 §3 개정: 분할 채널은 보존해
  // 조합 가능. 서버 무시 가드와 같은 규칙을 클라도 진다. state에는 남겨 연결 해제 시 모사가 그대로 복귀).
  const previewContextForApi = useMemo<EditorPreviewContext>(() => {
    const base = toApiPreviewContext(previewContext);
    return branchSimulate ? { ...base, simulate: branchSimulate } : base;
  }, [previewContext, branchSimulate]);
  // 본문 미리보기 resolve 전용 컨텍스트 — 위 컨텍스트에 저작자 선택 방식만 덧댄다.
  // ⚠️ **previewContextForApi 자체에 넣지 말 것.** 그 identity 변화가 writeCanvasEdits 초기화
  //    effect(엔티티 스코프 리셋)의 트리거라, 방식을 고르는 순간 edits가 통째로 지워져 그 선택이
  //    스스로를 되돌린다(그 리셋은 "다른 엔티티의 스테일 값 방지"용이라 방식 편집과 무관해야 한다).
  //    prefill 경로는 종전대로 previewContextForApi에 자기 방식 값을 얹는다(그 계층은 불변).
  const previewResolveContextForApi = useMemo<EditorPreviewContext>(
    () =>
      previewMethodOverride
        ? { ...previewContextForApi, findingRiskMethod: previewMethodOverride }
        : previewContextForApi,
    [previewContextForApi, previewMethodOverride],
  );
  // 서버 전송 분리형 — linkedReportId는 **body 최상위** 축이다(splitLinkedReportId JSDoc이
  // 정본 — context 안에 남기면 서버 sub-DTO forbidNonWhitelisted가 400). preview 채널은
  // 둘 다 싣고, 이 축을 받지 않는 prefill·split-plan 채널은 context만 싣는다.
  const previewResolveApiParts = useMemo(
    () => splitLinkedReportId(previewResolveContextForApi),
    [previewResolveContextForApi],
  );
  // 응답 simulateMisses(catalogKey 목록) → 기여 branchId 집합(슬롯 탭 배지용).
  const missedBranchIds = useMemo<Set<string>>(() => {
    const misses = previewResult?.simulateMisses;
    if (!misses || misses.length === 0) return new Set();
    const ids = new Set<string>();
    for (const catalogKey of misses) {
      for (const branchId of branchesByCatalogKey[catalogKey] ?? []) ids.add(branchId);
    }
    return ids;
  }, [previewResult, branchesByCatalogKey]);
  // 현재 previewResult가 기반한 시그니처. resolve 성공 시 그 시점 시그니처를 기록해, 최초 반영 직후
  // 즉시 재조회를 막고(시그니처 동일 → skip) 이후 변화만 재조회한다.
  const resolvedBindingSigRef = useRef<string | null>(null);
  // 재조회 경쟁 가드 — 마지막 요청 시퀀스만 반영(빠른 연속 편집으로 요청이 겹칠 때 오래된 응답 폐기).
  const refetchSeqRef = useRef(0);

  // 바인딩 변경 후 실데이터 조용히 재조회. 기존 previewResult는 성공 시에만 교체(깜빡임 방지) +
  // 실패 시 기존 스냅샷 유지 + 콘솔 경고만(toast 스팸 금지). 컨텍스트 없으면/실데이터 꺼졌으면 no-op.
  const refetchPreviewForBindingChange = useCallback(async () => {
    if (!hasPreviewContext || !previewResult) return;
    const seq = ++refetchSeqRef.current;
    const sigAtRequest = resolveRequestSignature;
    // 재조회가 500ms 이상 걸릴 때만 비차단 스피너 표시(빠른 재resolve는 깜빡임 없음, AI 정제 캐시 미스면 수 초라 뜸).
    const showTimer = window.setTimeout(() => {
      if (seq === refetchSeqRef.current) setRefetching(true);
    }, 500);
    try {
      const res = await adminApi.previewReportComposerByContext(
        template,
        previewResolveApiParts.context,
        previewResolveApiParts.linkedReportId,
      );
      if (seq !== refetchSeqRef.current) return; // 더 새 요청이 진행 중 → 폐기
      if (res.success && res.data) {
        setPreviewResult(res.data);
        resolvedBindingSigRef.current = sigAtRequest;
      } else {
        console.warn('[composer] 바인딩 변경 후 실데이터 재조회 실패:', res.error?.message);
      }
    } catch (e) {
      if (seq !== refetchSeqRef.current) return;
      console.warn('[composer] 바인딩 변경 후 실데이터 재조회 오류:', e);
    } finally {
      window.clearTimeout(showTimer);
      if (seq === refetchSeqRef.current) setRefetching(false);
    }
  }, [
    hasPreviewContext,
    previewResult,
    template,
    previewResolveApiParts,
    resolveRequestSignature,
  ]);

  // 요청 축(바인딩 + 시뮬레이션 필터 + 저작자 선택 방식)이 (반영된 값과) 달라졌고 실데이터 반영 중이면
  // ~600ms 디바운스 후 재조회.
  useEffect(() => {
    if (!previewResult || !hasPreviewContext) return;
    if (resolvedBindingSigRef.current === resolveRequestSignature) return; // 변화 없음(최초 반영 직후 포함)
    const timer = setTimeout(() => {
      void refetchPreviewForBindingChange();
    }, 600);
    return () => clearTimeout(timer);
  }, [resolveRequestSignature, previewResult, hasPreviewContext, refetchPreviewForBindingChange]);

  // "실데이터 적용" — 현재 컨텍스트로 서버 resolve 호출. 디바운스 대신 명시 버튼(단순·안전).
  //
  // 성공 여부를 **반환한다** — 자동 적용은 실패했을 때 stale 모사 축을 걷어내고 한 번 더 시도해야
  // 해서, 호출자가 결과를 알아야 한다(호출자 대부분은 종전대로 `void`로 버린다).
  const handleApplyContext = useCallback(async (): Promise<boolean> => {
    if (!hasPreviewContext) {
      setPreviewError('조직/현장/점검/계정 중 하나 이상을 선택하세요.');
      return false;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await adminApi.previewReportComposerByContext(
        template,
        previewResolveApiParts.context,
        previewResolveApiParts.linkedReportId,
      );
      if (res.success && res.data) {
        setPreviewResult(res.data);
        // 이 resolve가 기반한 요청 축(바인딩+시뮬레이션+방식)을 기록 — 직후 감시 effect가 즉시 재조회하지 않게.
        resolvedBindingSigRef.current = resolveRequestSignature;
        setAutoApplySuppressed(false); // 실데이터 적용 → 억제 해제(다음 새로고침도 실데이터)
        if (currentKey) {
          try { localStorage.removeItem(`${PREVIEW_SUPPRESS_KEY}:${currentKey}`); } catch { /* 무시 */ }
        }
        return true;
      }
      const msg = res.error?.message ?? '실데이터를 불러오지 못했습니다.';
      setPreviewError(msg);
      showToast(msg, 'error');
      return false;
    } catch (err) {
      /**
       * **서버가 말한 사유를 그대로 싣는다** (2026-09-09).
       *
       * 종전엔 `catch {`가 에러를 **바인딩조차 하지 않고** 「실데이터 미리보기 중 오류가
       * 발생했습니다」만 띄웠다. axios는 non-2xx에서 reject하므로 400·404·403이 **전부** 이
       * 가지로 온다 — 즉 바로 위의 `res.error?.message` 가지는 HTTP 에러에서는 죽은 코드였고,
       * 서버가 어느 칸이 문제인지 말해 줘도 저작자에게는 한 글자도 닿지 않았다.
       */
      const apiError = extractApiError(err);
      /**
       * 연결한 보고서가 이 컨텍스트에 없으면 **그 축만 놓고 한 번 더** 시도한다.
       *
       * 사연 둘 다 처방이 같다: 연결한 보고서가 사라졌거나(정리된 사본 등) 저작자가 점검을
       * 바꿨거나. 놓지 않으면 localStorage에 남은 컨텍스트가 **에디터를 열 때마다** 같은 400을
       * 되풀이하고, 저작자는 어느 축을 고쳐야 하는지 모른 채 오류만 본다(2026-09-09 dev 실측:
       * `c1pjbfxmb…` = 이미 정리된 C1 리허설 사본을 가리키고 있었다).
       *
       * ⚠️ **그 코드일 때만** 놓는다. 「아무 400이면 놓는다」로 하면 무관한 실패에서 저작자가
       * 고른 보고서를 뺏는다 — 위 `handleUnverifiedSimulationFailure`가 모사 축에 대해 세워 둔
       * 것과 같은 선이다(해제할 것이 없으면 아무 말도 하지 않는다).
       */
      if (
        apiError.code === 'LINKED_REPORT_NOT_IN_CONTEXT' &&
        previewContext.linkedReportId !== undefined
      ) {
        // 형상 분리는 `splitLinkedReportId`가 소유한다 — 여기서 손으로 구조분해하면
        // 그 축의 모양을 아는 자리가 둘이 된다.
        setPreviewContext(splitLinkedReportId(previewContext).context);
        // 값·라벨은 한 쌍이다 — 값만 놓으면 요약 칩·재오픈 복원이 "보고서: ○○"를 계속 그린다
        // (라벨은 localStorage에 값과 따로 저장된다). 라벨도 같은 자리에서 지운다(2026-09-14).
        setPreviewContextLabels((prev) => {
          if (prev.report === undefined) return prev;
          const next = { ...prev };
          delete next.report;
          return next;
        });
        showToast('연결한 보고서를 더 이상 찾을 수 없어 연결을 해제했습니다.', 'info');
        setPreviewError(null);
        return false;
      }
      setPreviewError(apiError.message);
      showToast(apiError.message, 'error');
      return false;
    } finally {
      setPreviewLoading(false);
    }
  }, [
    hasPreviewContext,
    template,
    previewResolveApiParts,
    resolveRequestSignature,
    showToast,
    currentKey,
    // 연결 해제 회복 경로가 previewContext를 직접 읽는다(2026-09-09) — 빠뜨리면 그 분기가 stale
    // 컨텍스트로 splitLinkedReportId를 돌려 낡은 값을 되살린다(2026-09-14 명시).
    previewContext,
  ]);

  // 조립 데이터 소스 dry-run — 현재(미저장 포함) 템플릿을 선택 컨텍스트로 서버 resolve해 content 반환.
  // DataSourcesEditor의 카드별 "조립 미리보기" 버튼이 호출, 각 def.key 산출을 추출해 카드 안에 표시.
  // 실데이터 미리보기(handleApplyContext)와 동일 endpoint/컨텍스트를 재사용한다(서버 신규 endpoint 없음).
  const runDataSourceDryRun = useCallback(async (): Promise<Record<string, unknown>> => {
    if (!hasPreviewContext) {
      throw new Error('실 데이터 컨텍스트를 먼저 선택하세요.');
    }
    const res = await adminApi.previewReportComposerByContext(
      template,
      previewResolveApiParts.context,
      previewResolveApiParts.linkedReportId,
    );
    if (res.success && res.data) return res.data.content;
    throw new Error(res.error?.message ?? '조립 미리보기를 불러오지 못했습니다.');
  }, [hasPreviewContext, template, previewResolveApiParts]);

  // 카탈로그 행 박제 조회(직접 입력 데이터셋 "카탈로그에서 행 가져오기" 모달) — 서버 prefill
  // 채널(resolvePrefill)의 dataDef.prefillFrom 해석을 그대로 재사용한다: 합성 dataDef 하나만
  // 실은 최소 템플릿을 보내 result[probeKey]로 행 배열을 받는다. 동적 초기 행(prefillFrom)이
  // 작성 시점에 받는 것과 **같은 머신·같은 값**이라, 저작자가 박제하는 행이 곧 그 값이다
  // (새 endpoint/resolve 로직 발명 금지 — 조립 미리보기(runDataSourceDryRun)와 같은 규율).
  const fetchCatalogRowsForImport = useCallback(
    async (catalogKey: string, select?: string): Promise<Record<string, unknown>[]> => {
      if (!hasPreviewContext) {
        throw new Error('실 데이터 컨텍스트를 먼저 선택하세요.');
      }
      const probeKey = 'catalog-row-import-probe';
      // 빈 배열을 명시해 서버 walker들(body/위젯 트리 스캔)이 undefined를 밟지 않게
      // 한다 — DTO는 template 내부 구조를 검증하지 않아(super_admin 신뢰) 여기가 형상 책임자다.
      //
      // catalogParams는 싣는다 — 서버 contextWithResolvedRiskMethod가 template.catalogParams의
      // `{from:'literal'}` 방식 지정을 정본으로 컨텍스트에 덮으므로, 빼면 방식 의존 카탈로그
      // (기준표 등)가 작성 시점과 다른 값으로 박제된다. variables는 싣지 않는다 —
      // `{from:'variable'}` 해소는 서버에 작성값(content)이 없어 어차피 클라 전송 컨텍스트의
      // findingRiskMethod(previewResolveContextForApi가 이미 실음)를 쓰고, variables를 실으면
      // 그 prefillFrom/optionsFrom이 무관한 카탈로그 resolve 게이트(AI 조립 포함)를 연다.
      const probeTemplate = {
        key: template.key,
        name: template.name,
        body: [],
        composerWidgetTree: [],
        dataSources: [],
        variables: [],
        catalogParams: template.catalogParams,
        dataDefs: [
          {
            key: probeKey,
            label: '',
            fields: [],
            defaultRows: [],
            prefillFrom: { catalogKey, ...(select ? { select } : {}) },
          },
        ],
      } as unknown as ReportTemplate;
      // prefill 채널은 linkedReportId를 받지 않는다 — context만(splitLinkedReportId JSDoc).
      const res = await adminApi.prefillReportComposerByContext(
        probeTemplate,
        previewResolveApiParts.context,
      );
      if (!res.success || !res.data) {
        throw new Error(res.error?.message ?? '시스템 조회 행을 불러오지 못했습니다.');
      }
      const rows = res.data[probeKey];
      if (!Array.isArray(rows)) {
        throw new Error(
          '선택한 실 데이터 컨텍스트에서 이 조회의 행을 찾지 못했습니다. 컨텍스트(현장·점검)를 확인하세요.',
        );
      }
      return rows as Record<string, unknown>[];
    },
    [hasPreviewContext, template.key, template.name, template.catalogParams, previewResolveApiParts],
  );

  // 모달 [반영] — draft 컨텍스트+라벨 확정. suppress 해제 + 시그니처 초기화로 자동적용 effect가 resolve.
  //
  // ⚠️ 엔티티가 바뀌어도 **모사는 그대로 간다**(결정 2026-09-03 §영속의 범위). 다만 다른 점검이면
  // 계획 산출 행 id가 남의 것일 수 있으므로 "미검증"으로 표시해 둔다 — 적용이 실패하면 그때
  // 그 축만 해제한다(미리 지우면 저작자가 고른 갈래가 멀쩡한 경우에도 사라진다).
  // 모사만 바꾼 [반영]은 표시하지 않는다: 방금 고른 값을 무관한 실패 한 번에 뺏기지 않게.
  const handleRealDataConfirm = useCallback(
    (ctx: EditorPreviewContext, labels: PreviewContextLabels) => {
      if (ctx.previewSimulation !== undefined && previewEntitiesChanged(previewContext, ctx)) {
        simulationUnverifiedRef.current = true;
      }
      setPreviewContext(ctx);
      setPreviewContextLabels(labels);
      setAutoApplySuppressed(false);
      lastAppliedCtxRef.current = null;
      if (currentKey) {
        try { localStorage.removeItem(`${PREVIEW_SUPPRESS_KEY}:${currentKey}`); } catch { /* 무시 */ }
      }
      setRealDataModalOpen(false);
    },
    [currentKey, previewContext],
  );

  // [해제] — 실데이터 걷어내고 바인딩 캔버스 고정(엔티티 선택/라벨은 유지, 새로고침 후에도 유지). 재반영 대비.
  const handleRealDataRelease = useCallback(() => {
    setPreviewResult(null);
    setAutoApplySuppressed(true);
    lastAppliedCtxRef.current = null;
    if (currentKey) {
      try { localStorage.setItem(`${PREVIEW_SUPPRESS_KEY}:${currentKey}`, 'true'); } catch { /* 무시 */ }
    }
  }, [currentKey]);

  // (작성 탭 캔버스 편집값 `writeCanvasEdits`는 미리보기 resolve 컨텍스트가 읽으므로 위쪽에서 선언한다.)

  // 미리보기 탭용 렌더 데이터 — resolved content를 placeholder에 병합(시스템 필드는 실값, input/미해석
  // 필드는 샘플값 유지). 실데이터 결과 없으면 빈 content로 resolve해 dataDefs.defaultRows(기본행)를
  // placeholder 위에 seed한다(표/repeat 미리보기). 미리보기 탭은 채워진 샘플을 보여준다.
  const previewMergedData = useMemo<RenderData>(() => {
    const base = previewResult
      ? mergePreviewContent(template, previewResult.content)
      : mergePreviewContent(template, {});
    // 작성 탭 캔버스에서 편집한 입력값을 bindingKey 단위 얕은 merge로 덮는다(실제 제출 compose의
    // "작성값이 서버 파생값을 override" 규칙과 동일 방향). ⚠️ 한계: 서버가 재계산하는 파생값
    // (위험도 재산출 등)은 클라 merge로 움직이지 않는다 — 정확한 재산출은 후속 서버 re-compose 필요.
    return Object.keys(writeCanvasEdits).length ? { ...base, ...writeCanvasEdits } : base;
  }, [previewResult, template, writeCanvasEdits]);

  // 편집 캔버스 전용 렌더 데이터 — placeholder(샘플) 모드에선 반복영역을 바인딩 뼈대 1행만 표시
  // (buildPlaceholderSkeleton). 실데이터 모드면 previewMergedData와 동일. 편집 중 양식 구조를
  // 명확히 보기 위함(내장 defaultRows로 여러 행이 채워지지 않게).
  const canvasData = useMemo<RenderData>(
    () => (previewResult ? previewMergedData : buildPlaceholderSkeleton(template)),
    [previewResult, previewMergedData, template],
  );

  // (팔레트/속성 접힘 상태는 EditorPanelLayout 셸이 storageKey로 자체 영속 — 별도 state 불필요.)
  // 작성 탭 패널 셸의 방향 토글(좌중우/상중하) 포탈 타깃 — 자체 툴바 행 대신 반응형 뷰 행
  // 맨뒤에 이식해 세로 한 줄 절약(2026-07-10). 양식 탭 셸은 포탈 미지정(자체 행 유지).
  const [panelToolbarEl, setPanelToolbarEl] = useState<HTMLSpanElement | null>(null);

  // ── 템플릿 영속화 상태 (3c-2) ──
  const [templateList, setTemplateList] = useState<StoredReportTemplate[]>([]);
  const [listLoading, setListLoading] = useState(false);
  // ── 양식 분류(category) (2026-09-20 결정) ──
  // 분류 목록(서버 sortOrder·name 순) — 선택기 그룹 헤더 순서·속성 「분류」 select 선택지·관리 모달 초기값.
  const [templateCategories, setTemplateCategories] = useState<ReportTemplateCategory[]>([]);
  // 현재 편집 중 양식의 분류 id(null=미분류). `enabled`와 같은 이유로 편집 funnel(undo/history) 밖의
  // 별도 state — `template`은 shared ReportTemplate이라 분류 축이 없다(결정 §6: 타입은 admin에만).
  // 저장은 별도 PATCH가 아니라 handleSave의 본문 body에 실어 함께 간다.
  const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(null);
  // 저장/로드 시점의 분류 id — isDirty 파생의 짝(template 참조 비교와 OR).
  const [savedCategoryId, setSavedCategoryId] = useState<string | null>(null);
  // 「분류 관리」 모달 열림 여부.
  const [categoryManageOpen, setCategoryManageOpen] = useState(false);
  // 최초 목록 로드 완료 여부 — 새로고침 복원이 빈 목록에서 조기 소진되지 않게.
  const [listLoadedOnce, setListLoadedOnce] = useState(false);
  // (catalog/catalogIndex state는 팝아웃 sync 페이로드가 값 출처 재료를 실어야 해 미리보기 상태 곁으로 올라갔다.)
  // 모사 축 후보 — 편집 중 템플릿의 **`lifecycle:'live'` 바인딩이 읽는 스칼라** + `policy.split`
  // 에서 파생한다(결정 2026-09-01 §①). live 값은 보고서가 만들어진 뒤에 쌓이는 것이라 컨텍스트
  // 미리보기로는 영영 못 보는 집합이고, 그게 정확히 모사가 필요한 자리다.
  // 저장본이 아니라 **편집본**을 보는 이유: 방금 정책을 켠 저작자가 저장 없이 확인할 수 있어야 한다.
  // 도메인 단어는 파생 함수와 바로 아래(계획→선택지 매핑)에만 있고, 모달은 산출만 렌더한다.
  const simulationAxes = useMemo(
    () => derivePreviewSimulationAxes(catalog, template),
    [catalog, template],
  );
  // 데이터 모사 칩 문구(§⑥) — 지금 **값이 실린** 축만. 라벨은 저작자가 고른 순간 보관한 것을 쓰고,
  // 없으면 축 라벨만으로 떨어진다. "모사 중인 것 전부"가 한 줄에 보여야 한다.
  const simulationChips = useMemo(() => {
    const sim = previewContext.previewSimulation;
    if (!sim) return [];
    return simulationAxes
      .filter((axis) => sim[axis.key] !== undefined)
      .map((axis) => {
        const label = previewContextLabels.simulation?.[axis.key];
        return label ? `${axis.label}: ${label}` : axis.label;
      });
  }, [previewContext, simulationAxes, previewContextLabels]);
  // 「전체 해제」(모사 칩 hover 팝오버) — 모사 값·모사 라벨만 걷어내고 엔티티 선택은 남긴다.
  // 컨텍스트가 바뀌므로 자동 적용 effect가 실데이터 그대로의 갈래로 다시 resolve한다.
  // 적용 중 프리셋 표시도 함께 내린다 — 값이 없는데 "적용 중"이 남으면 화면이 거짓말한다(§⑥).
  const handleClearAllSimulation = useCallback(() => {
    setPreviewContext((prev) => clearAllSimulation(prev));
    setPreviewContextLabels((prev) => withoutAllSimulationLabels(prev));
    setAppliedPresetId(null);
  }, []);

  // ── 모사 프리셋 적용/해제/즉시 반영(2026-09-04 성격 분리 — 속성 패널이 조작 주체) ──
  // 모사 값·라벨 한 쌍을 previewContext/labels에 함께 싣는다(한쪽만 갱신하면 §⑥ 위반 —
  // previewContextPersistence 헤더의 "둘은 한 쌍" 규약).
  const setSimulationLabels = useCallback((labels: Record<string, string>) => {
    setPreviewContextLabels((prev) => {
      const next: PreviewContextLabels = { ...prev };
      if (Object.keys(labels).length > 0) next.simulation = labels;
      else delete next.simulation;
      return next;
    });
  }, []);
  const applySimulationPair = useCallback(
    (pair: { simulation: Record<string, unknown> | undefined; labels: Record<string, string> }) => {
      setPreviewContext((prev) => {
        const next: EditorPreviewContext = { ...prev };
        if (pair.simulation === undefined) delete next.previewSimulation;
        else next.previewSimulation = pair.simulation;
        return next;
      });
      setSimulationLabels(pair.labels);
    },
    [setSimulationLabels],
  );

  // [적용] — 대체 의미론(plan 축 값 유지, applyPresetToSimulation 헤더가 정본).
  const handleApplyPreset = useCallback(
    (preset: SimulationPreset) => {
      applySimulationPair(
        applyPresetToSimulation(
          previewContext.previewSimulation,
          previewContextLabels.simulation,
          preset,
          simulationAxes,
        ),
      );
      setAppliedPresetId(preset.id);
    },
    [applySimulationPair, previewContext, previewContextLabels, simulationAxes],
  );

  // [해제] — storable 축 전부 걷기(plan 축 값 유지).
  const handleUnapplyPreset = useCallback(() => {
    applySimulationPair(
      clearPresetStorableSimulation(
        previewContext.previewSimulation,
        previewContextLabels.simulation,
        simulationAxes,
      ),
    );
    setAppliedPresetId(null);
  }, [applySimulationPair, previewContext, previewContextLabels, simulationAxes]);

  // 프리셋 목록·기본 지정 갱신(속성 패널 onChange) — 템플릿 draft 반영(apply 1회 = undo 1스텝) +
  // **적용 중 프리셋의 수정은 미리보기에 즉시 반영**(즉석 모사가 이 흐름으로 흡수 — 결정
  // §검토한 대안). 값이 실제로 달라진 경우에만 컨텍스트를 갈아 끼운다: 이름만 바꿨는데 새
  // previewContext 객체를 만들면 resolve 무효화 effect가 스냅샷을 비우는데, 자동 적용 sig는
  // 그대로라 재조회가 없어 미리보기가 placeholder로 떨어진 채 남는다.
  // `plan` 축 선택지 조회 — ⚠️ **부수효과 있음**(서버가 provider classifier를 돌린다: TbmMeta
  // upsert + AI 정제 캐시 생성). 그래서 useEffect·폴링에 걸지 않고 **콜백으로만** 내려보낸다.
  // 호출 시점은 PreviewContextBar가 소유한다("저작자가 그 축을 추가하는 조작에서 1회", §④).
  //
  // 계획(SplitPlan) → 선택지 매핑이 여기 있는 이유: opaque 값의 형상(`{groupKey,rowIds}`)을 아는
  // 것은 이 페이지의 일이고, 모달은 그 값을 **해석 없이 되돌려 주기만** 한다(결정 §③).
  const loadAxisOptions = useCallback(
    async (
      axis: PreviewSimulationAxis,
      ctx: ReportComposerPreviewContext,
    ): Promise<PreviewSimulationOption[]> => {
      if (axis.key !== PREVIEW_SIMULATION_SPLIT_AXIS_KEY) return axis.options ?? [];
      // split-plan 채널은 linkedReportId를 받지 않는다 — 모달 draft에 실려 있어도 벗겨 보낸다.
      const res = await adminApi.splitPlanForReportComposerByContext(
        template,
        splitLinkedReportId(ctx).context,
      );
      if (!res.success || !res.data) {
        throw new Error(res.error?.message ?? '분할 그룹을 불러오지 못했습니다.');
      }
      return (res.data.plan?.groups ?? []).map((group) => ({
        label: `${group.label} (${group.rowIds.length}건)`,
        value: { groupKey: group.groupKey, rowIds: group.rowIds },
      }));
    },
    [template],
  );
  const [busy, setBusy] = useState(false); // load/save/delete 진행 중(버튼 비활성).
  // 저장 동기 인플라이트 가드 — busy state(비동기)만으론 같은 tick 연타 시 이중 PATCH가 가능해 ref로 즉시 차단.
  const saveInFlightRef = useRef(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  // Ctrl+Delete 행/열 삭제 모달 — rows/cols=삭제 후보 인덱스, choice=현재 라디오 선택.
  const [deleteModal, setDeleteModal] = useState<
    { tableId: string; rows: number[]; cols: number[]; choice: CellDeleteChoice } | null
  >(null);
  // 미저장 변경 여부 — "마지막 저장/로드 스냅샷"과 present의 **참조 비교** 파생값.
  // 히스토리가 객체 참조를 그대로 보존하므로 undo로 저장 시점까지 되돌리면 같은 참조로 복귀 →
  // dirty가 정확히 꺼진다. 플래그 방식의 한계였던 (a) 편집 후 undo 복귀에도 dirty 유지, (b) 저장
  // 직후 undo에도 clean 유지가 모두 해소(2026-07-04 사용자 보고 (a)로 전환). 참조 비교 1회라
  // 렌더 비용 0(과거 full JSON 비교 회귀 아님). null = 저장본 없음(신규 미저장) → 상시 dirty.
  const [savedSnapshot, setSavedSnapshot] = useState<ReportTemplate | null>(emptyTemplate);
  // 분류(category)는 편집 funnel 밖 별도 state라 참조 비교에 안 잡힌다 — 저장 시점 값과 대조해 OR.
  // (undo로는 되돌아오지 않는다 — enabled와 같은 성격의 메타라 감수. 저장은 같은 body로 나간다.)
  const isDirty =
    savedSnapshot === null || template !== savedSnapshot || currentCategoryId !== savedCategoryId;

  // 미저장 이탈 가드 — dirty 상태에서 리로드/탭 닫기 시 브라우저 네이티브 확인을 띄운다.
  // 앱 내 이동(불러오기/새 문서)은 각자 확인 모달이 있지만 브라우저 이탈은 막는 곳이 없어,
  // 적용만 하고 저장 전 리로드하면 조용히 소실됐다(2026-07-27 데이터 연결 적용분 소실 실사고).
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Chrome 구버전은 returnValue 설정까지 있어야 확인이 뜬다(문구는 브라우저 고정).
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── 공개(enabled) 상태 (3c-3) ──
  // 현재 로드/저장된 템플릿의 "일반 사용자 공개" 스위치. 편집 상태(undo/isDirty)와 분리된 별도 state로
  // 관리한다(toEditableTemplate이 enabled를 벗겨내므로 편집 funnel엔 없음). load/save/new/delete에서 갱신.
  const [currentEnabled, setCurrentEnabled] = useState(false);
  // 공개 토글 확인 모달 — 대상 key + 적용할 다음 상태(true=공개/false=비공개), null이면 닫힘.
  // key를 함께 드는 이유: 헤더 토글(열린 양식)과 탐색기 행 ⋯(임의 양식)이 같은 모달·PATCH를 쓴다.
  const [publishTarget, setPublishTarget] = useState<{ key: string; next: boolean } | null>(null);
  // enabled PATCH 진행 중 — 토글/확인 버튼 비활성(중복 클릭 방지).
  const [publishBusy, setPublishBusy] = useState(false);
  // 복제(duplicate) 원본 — null이면 key 입력 모달 닫힘. 헤더(열린 양식)·탐색기 행 ⋯(임의 양식) 공용.
  const [duplicateSource, setDuplicateSource] = useState<{ key: string; name: string } | null>(null);
  // 새 템플릿(new) key 입력 모달 열림 여부. 확인 시 빈 양식으로 시작.
  const [newModalOpen, setNewModalOpen] = useState(false);
  // 새 양식이 시작될 때 미리 붙일 분류 id — 탐색기 분류 헤더 ⋯ 「이 분류에 새 양식」만 설정(그 외 null).
  const pendingNewCategoryRef = useRef<string | null>(null);
  // 양식 업로드(form-import) Job — 업로드 → AI 분석 → 초안 템플릿 생성. 폴링·새로고침 복구는
  // 훅(페이지)이 소유한다: 모달을 닫아도 분석이 계속되고 헤더 pill이 같은 상태를 읽는다.
  const formImportJob = useFormImportJob({ enabled: composerEnabled, showToast });
  // 범용 확인 모달 pending 상태 — 무엇을 확인 중인지 + 확인 시 실행할 콜백. null이면 닫힘.
  // (네이티브 window.confirm의 동기 흐름을 비동기 모달 흐름으로 재구성한다.)
  const [confirmState, setConfirmState] = useState<{
    title: string;
    // ReactNode — 양식 삭제 확인이 「주인을 잃는 법정 의무」 목록을 싣는다(L24 · `TemplateDeleteConfirmBody`)
    body: ReactNode;
    confirmLabel?: string;
    danger?: boolean;
    /**
     * 겹침 층 — **여는 쪽이 정한다**(모달 위에서 여는 확인은 `z-[80]`). 미지정이면 root(z-[70]).
     * 같은 층에 두고 DOM 순서에 기대면, 그 아래 모달의 렌더 위치가 바뀌는 순간 확인이 뒤에 숨는다.
     */
    overlayZClassName?: string;
    overlayBgClassName?: string;
    onConfirm: () => void;
  } | null>(null);
  // 노드 붙여넣기 선택 모달(타 템플릿 붙여넣기) pending payload — null이면 닫힘. 같은 템플릿
  // 붙여넣기는 모달 없이 즉시 처리하므로, 이 상태는 "다른 템플릿에서 복사한 노드"일 때만 세팅된다.
  const [nodePasteModalPayload, setNodePasteModalPayload] = useState<NodeClipboardPayload | null>(
    null,
  );
  // 편집 funnel 3종 — dirty가 스냅샷 참조 비교 파생값이 되면서 별도 표시가 불필요해짐.
  // 히스토리 뮤테이션을 그대로 노출한다(콜사이트 무변경).
  const apply = applyHistory;
  const replacePresent = replacePresentHistory;
  const commitPresent = commitPresentHistory;

  // 프리셋 목록·기본 지정 변경(속성 패널) — 템플릿 draft 반영 + 적용 중 프리셋이면 미리보기 동기.
  // ⚠️ 위치: `apply`(const) 선언 **뒤**여야 한다 — useCallback deps 배열은 렌더 중 즉시 평가라
  // 선언 전 참조는 TDZ ReferenceError(첫 렌더 크래시)다(merge 재검증 tsc TS2448로 적발, 이동).
  const handlePresetsChange = useCallback(
    (change: SimulationPresetsChange) => {
      apply((t) => ({
        ...t,
        simulationPresets: change.presets,
        defaultSimulationPresetId: change.defaultPresetId,
      }));
      if (!appliedPresetId) return;
      const edited = change.presets.find((p) => p.id === appliedPresetId);
      if (!edited) {
        // 적용 중 프리셋이 삭제됨 — 벌도 내린다(원본 없는 "적용 중"은 유령).
        handleUnapplyPreset();
        return;
      }
      const applied = applyPresetToSimulation(
        previewContext.previewSimulation,
        previewContextLabels.simulation,
        edited,
        simulationAxes,
      );
      const simChanged =
        JSON.stringify(applied.simulation ?? null) !==
        JSON.stringify(previewContext.previewSimulation ?? null);
      const labelsChanged =
        JSON.stringify(applied.labels) !==
        JSON.stringify(previewContextLabels.simulation ?? {});
      if (simChanged) applySimulationPair(applied);
      // 라벨만 달라졌으면(select 라벨 정정 등) 라벨 state만 — 값이 같은데 previewContext 객체를
      // 갈면 resolve 스냅샷만 비워지고 재조회는 없다(sig 동일).
      else if (labelsChanged) setSimulationLabels(applied.labels);
    },
    [
      apply,
      appliedPresetId,
      handleUnapplyPreset,
      applySimulationPair,
      setSimulationLabels,
      previewContext,
      previewContextLabels,
      simulationAxes,
    ],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  // 새 노드 id 카운터 — `${kind}-${n}` 고유 부여.
  const idCounter = useRef(0);

  // ── 양식 탐색기 레일(결정 2026-09-20-composer-template-explorer) — 탭 바깥 페이지 레벨 2분할의
  // 좌측 패널. 접힘/폭 정본은 셸(storageKey 영속)이고, 여기선 Ctrl+K/Esc가 부를 명령 핸들만 둔다
  // (헤더 ≡ 토글과 그 눌림 미러는 2026-09-20 제거 — 레일 아이콘·Ctrl+K가 진입점).
  const explorerLayoutRef = useRef<EditorPanelLayoutHandle>(null);
  // 검색 입력 포커스 요청 카운터 — Ctrl+K·레일 아이콘이 펼침과 함께 올린다.
  const [explorerFocusTick, setExplorerFocusTick] = useState(0);
  const openExplorer = useCallback(() => {
    explorerLayoutRef.current?.expand('explorer');
    setExplorerFocusTick((n) => n + 1);
  }, []);
  const closeExplorer = useCallback(() => {
    explorerLayoutRef.current?.collapse('explorer');
    // 접힌 뒤 포커스가 사라진 입력에 남지 않게 에디터 컨테이너로 되돌린다(단축키 스코프 복귀).
    containerRef.current?.focus();
  }, []);

  // 선택한 미리보기 컨텍스트(엔티티 + 모사 값)를 템플릿별로 localStorage에 저장 — 새로고침 후 복원용.
  // currentKey(=로드/저장된 템플릿)가 있을 때만 저장(신규 미저장 템플릿은 스킵).
  //
  // 무엇이 빠지고 왜 빠지는지는 `toPersistablePreviewContext`가 소유한다(2026-09-03 개정 —
  // 모사 값은 이제 **영속한다**. 착각 방지는 영속 차단이 아니라 상시 모사 배너가, stale 정리는
  // 복원 후 자동 적용 실패 시 `releasePlanSimulation`이 맡는다).
  useEffect(() => {
    if (!currentKey) return;
    try {
      const persistable = toPersistablePreviewContext(previewContext);
      localStorage.setItem(`${PREVIEW_CTX_KEY}:${currentKey}`, JSON.stringify(persistable));
    } catch { /* storage 차단 무시 */ }
  }, [currentKey, previewContext]);

  // 선택 엔티티 라벨(요약 칩용)도 템플릿별로 영속 — 새로고침 후 IDs만으론 이름을 못 뿌리므로.
  useEffect(() => {
    if (!currentKey) return;
    try {
      localStorage.setItem(`${PREVIEW_LABELS_KEY}:${currentKey}`, JSON.stringify(previewContextLabels));
    } catch { /* storage 차단 무시 */ }
  }, [currentKey, previewContextLabels]);

  // 모사 값의 stale 정리 — 모사가 **아직 이 데이터에서 검증되지 않은 동안** 첫 자동 적용이
  // 실패했을 때만, 그 전환당 **한 번만** 돈다(새로고침 복원 직후 · 엔티티 변경 직후).
  //
  // 왜 실패 응답을 읽지 않고 축의 성질로 귀속하는가: 서버는 이 경우 plain `BadRequestException`을
  // 던지고 예외 필터가 그것을 400 공통 코드로 내려, "분할 대상에 없는 항목입니다"인지 다른 400인지
  // **응답만으론 구분할 수 없다**(문구 매칭은 서버 문구가 바뀌는 날 조용히 죽는다). 대신 stale해질
  // 수 있는 것이 무엇인지는 확실하다 — 계획 산출물(행 id)을 실은 `plan` 출처 축뿐이고, 스칼라 모사는
  // 서버가 값을 그대로 주입할 뿐이라 그런 실패를 원리적으로 만들지 못한다. 그래서 그 축만 걷어낸다.
  //
  // 재적용은 **컨텍스트가 바뀌었으므로 아래 자동 적용 effect가 알아서** 한 번 더 돈다. 플래그는 이미
  // 내려갔으니 그 시도가 또 실패해도 여기로 다시 들어오지 않는다(무한 재시도 없음).
  const handleUnverifiedSimulationFailure = useCallback((appliedSig: string) => {
    if (!simulationUnverifiedRef.current) return;
    // 왕복 도중 저작자가 컨텍스트를 바꿨으면(=마지막 적용 시그니처가 다르면) 그 실패는 이제 옛
    // 컨텍스트의 것이다 — 여기서 되쓰면 저작자가 방금 고른 선택을 옛 값으로 덮는다.
    if (lastAppliedCtxRef.current !== appliedSig) return;
    simulationUnverifiedRef.current = false;
    const released = releasePlanSimulation(previewContext, previewContextLabels, simulationAxes);
    // 해제할 것이 없으면 아무 말도 하지 않는다 — 실패 원인이 모사가 아니었다는 뜻이고,
    // 그때 "모사를 해제했다"고 안내하면 저작자가 없는 원인을 쫓는다.
    if (released.releasedKeys.length === 0) return;
    setPreviewContext(released.context);
    setPreviewContextLabels(released.labels);
    showToast('이전 모사 값이 더 이상 유효하지 않아 해제했습니다.', 'info');
  }, [previewContext, previewContextLabels, simulationAxes, showToast]);

  // (「현재 값을 프리셋으로 저장」 경로는 2026-09-04 성격 분리로 제거 — 프리셋 저작·적용은
  //  전역 속성 패널 SimulationPresetsSection이 직접 진다.)

  // 실데이터 자동 적용 — 반영 상태(=!autoApplySuppressed)면 복원 시·엔티티 변경 시 자동으로
  // resolve한다(별도 적용 버튼 없음). 시그니처 dedupe로 같은 조합 중복 fetch 방지.
  // 엔티티 미완성(hasPreviewContext=false)이면 그대로 바인딩 캔버스(= "실데이터 없으면 바인딩").
  // 미저장 신규 템플릿(currentKey=null)도 반영 가능 — 시그니처만 '(new)'로 구분(영속은 저장본만).
  //
  // ⚠️ 모사가 **미검증인 동안 도는 첫 적용만** stale을 정리한다(위 handleUnverifiedSimulationFailure).
  useEffect(() => {
    if (autoApplySuppressed || !hasPreviewContext) return;
    const sig = `${currentKey ?? '(new)'}|${JSON.stringify(previewContext)}`;
    if (lastAppliedCtxRef.current === sig) return;
    lastAppliedCtxRef.current = sig;
    void (async () => {
      const ok = await handleApplyContext();
      // 한 번이라도 통과했으면 복원값은 더 이상 "미검증"이 아니다 — 이후의 실패(엔티티를 바꿔서
      // 난 것 등)까지 모사 탓으로 돌려 저작자가 고른 갈래를 뺏으면 안 된다.
      if (ok) simulationUnverifiedRef.current = false;
      else handleUnverifiedSimulationFailure(sig);
    })();
  }, [
    currentKey,
    previewContext,
    hasPreviewContext,
    handleApplyContext,
    autoApplySuppressed,
    handleUnverifiedSimulationFailure,
  ]);

  // 드래그 진행 중 여부(드롭존 표시 토글). 줌/팬/recenter·캔버스 박스·오버레이 좌표 인프라는
  // CanvasViewport로 추출됨(form/preview 두 탭이 공유) — 편집 오버레이는 render-prop으로 주입한다.
  const [isDragging, setIsDragging] = useState(false);

  const selectedNode = useMemo(
    () => (selectedNodeId ? findNodeById(template.body, selectedNodeId) : null),
    [selectedNodeId, template],
  );

  // 선택 노드 속성 패널의 바인딩 편집기에 넘길 "다른 노드/셀이 이미 쓰는 식별 키" 집합(자기 제외).
  // 중복 키 경고에 사용. 한 번에 한 노드만 선택되므로 selectedNode 기준으로 계산한다.
  const selectedNodeUsedBindingKeys = useMemo(
    () =>
      selectedNode
        ? collectUsedBindingKeys(template.body, `node:${selectedNode.id}`)
        : undefined,
    [template.body, selectedNode],
  );

  // 식별 키 칸 포커스 → 같은 키를 쓰는 **다른** 자리를 캔버스에 강조(sharedKeyHighlight.ts).
  // 경고는 "겹친다"만 말하고 어디와 겹치는지는 못 알려줬다. 채널은 useMemo([])로 참조를 영구
  // 고정한다 — 훅의 effect deps에 들어가므로 반드시 안정 참조여야 한다(재보고 루프 방지).
  // 그래서 채널 함수가 읽어야 하는 body는 deps가 아니라 ref로 넘긴다(아래 sharedKeyBodyRef).
  const [sharedKeyFocus, setSharedKeyFocus] = useState<SharedKeyFocus | null>(null);
  // 경고 옆 "사용처" 목록에서 커서를 올린 자리 — 그 한 자리만 캔버스에 강조한다(선택은 불변).
  const [sharedKeyHoverOwnerId, setSharedKeyHoverOwnerId] = useState<string | null>(null);
  // 채널 함수가 읽는 최신 body(latest-value ref 패턴) — 채널 참조를 고정하려면 body를 deps에 못 넣는다.
  const sharedKeyBodyRef = useRef(template.body);
  sharedKeyBodyRef.current = template.body;

  /**
   * 검출된 자리로 **도달** — 조상 분기 슬롯 탭을 그 자리의 경로로 전환한 뒤 캔버스를 스크롤 + 플래시.
   *
   * 필요한 이유: 식별 키 중복 검출은 템플릿 **정적 분석**이라 캔버스에 안 그려진 자리도 잡는다
   * (비활성 분기 슬롯 안, 실데이터 반영 중 0행 반복 안). 선택만 옮기면 "패널만 열리고 캔버스엔
   * 안 보이는" 반쪽 도달이 되고, hover 스크롤은 DOM이 없어 조용히 no-op이 된다.
   *
   * 하지 **않는** 것 둘:
   *  - 선택(selectSingleNode)을 바꾸지 않는다 — 호출자가 정한다(목록은 유지, 배지는 자기가 옮긴다).
   *  - 뷰 모드(실데이터 반영/샘플)를 대신 바꾸지 않는다. 저작자 소유라, 전환해도 앵커가 없으면
   *    사유만 알린다(notifyIfHidden) — 배지 경로는 조용히 두어 기존 동작보다 나빠지지 않게 한다.
   */
  const revealOwnerAnchor = useCallback(
    (ownerId: string, opts?: { notifyIfHidden?: boolean }) => {
      const body = sharedKeyBodyRef.current;
      const slotPath = collectOwnerSlotPaths(body).get(ownerId) ?? [];
      const patch = branchSlotSelectionPatch(body, slotPath);
      // 빈 patch면 setState를 걸지 않는다 — 슬롯 밖 자리까지 매번 재렌더시킬 이유가 없다.
      if (Object.keys(patch).length > 0) {
        setActiveBranchSlots((prev) => ({ ...prev, ...patch }));
      }
      const selector = ownerAnchorSelector(ownerId);
      if (!selector) return;
      // 슬롯 전환은 다음 커밋에 반영되므로 앵커를 기다렸다 잡는다(findAnchorEl이 rAF+폴링을 담당).
      void findAnchorEl([selector]).then((el) => {
        if (el) {
          flashTarget(el, SHARED_KEY_FLASH_OUTLINE);
          return;
        }
        if (opts?.notifyIfHidden) {
          showToast(
            '이 자리는 지금 캔버스에 그려져 있지 않습니다 — 실데이터 반영 중 0행 반복 안이라면 [실데이터 해제] 후 또는 반복에 데이터가 실리면 표시됩니다.',
            'info',
          );
        }
      });
    },
    [showToast],
  );
  // 채널이 useMemo([])로 고정돼야 하므로(아래) 최신 함수는 ref로 넘긴다 — body와 같은 이유.
  const revealOwnerAnchorRef = useRef(revealOwnerAnchor);
  revealOwnerAnchorRef.current = revealOwnerAnchor;

  const sharedKeyChannel = useMemo<SharedKeyFocusChannel>(
    () => ({
      setFocus: setSharedKeyFocus,
      setHoverOwner: (ownerId) => {
        setSharedKeyHoverOwnerId(ownerId);
        // 강조 자리가 뷰포트 밖이면 강조가 떠도 안 보인다 — 속성 패널 hover 강조와 같은 스크롤 규약
        // (150ms 지연 + block:'nearest')을 그대로 쓴다. 앵커가 없으면(비활성 슬롯) 조용히 no-op.
        hoverScrollToAnchor(ownerId ? ownerAnchorSelector(ownerId) : null);
      },
      // 목록 항목 클릭 — hover와 달리 슬롯 전환까지 해서 **도달**시키고, 못 닿으면 사유를 알린다.
      revealOwner: (ownerId) => revealOwnerAnchorRef.current(ownerId, { notifyIfHidden: true }),
      getOwnerDetails: (key, selfOwnerId) =>
        collectSharedKeyOwnerDetails(sharedKeyBodyRef.current, key, selfOwnerId),
    }),
    [],
  );
  const sharedKeyOwnerIds = useMemo(
    () =>
      sharedKeyFocus
        ? collectSharedKeyOwners(template.body, sharedKeyFocus.key, sharedKeyFocus.selfOwnerId)
        : [],
    [template.body, sharedKeyFocus],
  );

  // 선택 노드의 조상 체인(바깥→안). 속성 패널 브레드크럼(상위 이동)에 사용. 셀 안 노드면 그 표(table)
  // 노드가 최상위 조상으로 포함될 수 있다(셀 자체는 노드가 아니라 체인에 못 들어감).
  const ancestorGroups = useMemo(() => {
    if (!selectedNodeId) return [] as ComposerNode[];
    const path = findNodePath(template.body, selectedNodeId);
    return path ? path.slice(0, -1) : [];
  }, [selectedNodeId, template]);

  // 컨텍스트 유효성 가드 — 템플릿 로드/노드 삭제/undo로 selectionContextId가 가리키는 컨테이너가
  // 사라지면 body 레벨(null)로 리셋(stale 컨텍스트로 인한 승격/드릴 오작동 방지).
  useEffect(() => {
    if (selectionContextId && !findNodeById(template.body, selectionContextId)) {
      setSelectionContextId(null);
    }
  }, [template, selectionContextId]);

  // 다중 선택된 셀들의 실제 데이터(BulkCellEditor tri-state 표시용). 각 ref의 tableId 노드를
  // 트리에서 찾아 (row,col) 일치 셀을 수집한다(존재하지 않는 셀은 제외).
  const selectedCellData = useMemo<TableCell[]>(() => {
    const result: TableCell[] = [];
    for (const ref of selectedCells) {
      const node = findNodeById(template.body, ref.tableId);
      if (!node || node.kind !== 'table') continue;
      const cell = node.cells.find((c) => c.row === ref.row && c.col === ref.col);
      if (cell) result.push(cell);
    }
    return result;
  }, [selectedCells, template]);

  // 인라인 편집 진입 시 복사 소스 표시 해제(엑셀 동형 — 셀 편집을 시작하면 marching ants 사라짐).
  useEffect(() => {
    if (editingTarget) setClipboardSource(null);
  }, [editingTarget]);

  // ── 작성폼 파생 + 저장 오버라이드 병합 (3e-A) ──
  // (catalogIndex는 팝아웃 sync 페이로드가 값 출처 재료를 실어야 해 미리보기 상태 곁으로 올라갔다.)
  // body 바인딩의 자동 채움 출처 맵 — 서버 collectPrefillBindings의 "bindingKey+catalogKey 합성"과
  // 동일 규칙(명시 prefillFrom 우선, repeat/aiAction 제외). 파생 미배치 위젯 정보 패널 표시용.
  const bindingPrefillMap = useMemo(() => {
    const map = new Map<string, { catalogKey: string; select?: string }>();
    walkBindings(template.body, (b) => {
      if (!b.bindingKey || b.repeatOver || b.aiAction || map.has(b.bindingKey)) return;
      const pf =
        b.prefillFrom ??
        (b.catalogKey
          ? { catalogKey: b.catalogKey, ...(b.select ? { select: b.select } : {}) }
          : undefined);
      if (pf) map.set(b.bindingKey, pf);
    });
    return map;
  }, [template.body]);
  // repeat 패턴 셀 placeholder({필드라벨})용 bindingKey→라벨 맵.
  // collectRepeatSources의 모든 소스 fields(dataDef=field.key / catalog=field.path) + 카탈로그 returns(path) 라벨을 합친다.
  const bindingLabels = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const src of collectRepeatSources(
      template.dataDefs,
      catalog,
      template.dataSources,
    )) {
      for (const f of src.fields) {
        if (!(f.key in map)) map[f.key] = f.label;
      }
    }
    for (const entry of catalog) {
      for (const r of entry.returns) {
        if (!(r.path in map)) map[r.path] = r.label;
      }
    }
    return map;
  }, [template.dataDefs, catalog, template.dataSources]);
  // ⚠️ 2026-10-02(S6): 종전에는 이 아래에 `mergedWriteForm`(= `mergeWriteForm(derivedWriteForm,
  // template.writeForm)`) memo가 하나 더 있었고 소비처 3곳이 그것을 읽었다. flat override 축이
  // 철거되면서 소비처를 이 파생본으로 바꿨는데, **값은 세 곳 다 동일**하다 — 데이터가 아니라
  // 구조로 그렇다: `writeFormFields`에서 실제로 읽는 속성은 `prefillFrom`·`dataType`(+ lookup용
  // `bindingKey`) **셋뿐**이고, `mergeWriteForm`이 덮던 집합(`label`·`widget`·`required`·`options`·
  // `defaultValue`·`mode`·`writeBack`)과 **교집합이 0**이었다. stored-only orphan도 애초에 제외돼
  // 키 집합·순서가 derived 그대로였다. 소비처: 아래 taken 키 집합 · ftWidget prefill 해소 ·
  // 속성창 `writeFormFields` prop.
  const derivedWriteForm = useMemo(
    () => deriveWriteForm(template, catalogIndex),
    [template, catalogIndex],
  );
  // ── 작성 페이지 위젯 트리(2026-07-08 신모델, 단계 1b·2b) ──
  // 정본 = template.composerWidgetTree(저작형). deriveComposerWidgets(바인딩 파생 flat 풀)와 sync해
  // 배치 트리 + 미배치(unplaced) + orphan을 산출한다(트리 무변형). 트리 미지정=전 풀 미배치.
  // 현 단계는 read-only 미리보기(첫 시각 조각) — 저작 UI(단계 3)/반응형 렌더러(단계 4)는 후속.
  const composerWidgetSync = useMemo(
    () => syncComposerWidgets(template.composerWidgetTree, deriveComposerWidgets(template, catalogIndex)),
    [template, catalogIndex],
  );
  // focusFtCell(상단 선언)이 Shift 범위 계산 시 읽는 최신 트리 노출(latest-value ref 패턴).
  composerTreeRef.current = composerWidgetSync.tree;

  /**
   * 새 bindingKey를 발급할 때 **이미 쓰이는 키** 집합(배치 트리 + 미배치 풀 + 양식) — 이 3원 조립의
   * 정본이다. 키 출처가 하나 더 생겼을 때 한 호출처만 갱신되면 나머지가 **이미 쓰는 키를 재배정**해
   * 두 위젯이 같은 bindingKey를 갖고 값을 서로 덮어쓴다. 복제/붙여넣기/프리셋 삽입·교체 공용.
   *
   * `excludeKeys`는 **배치 트리 축에서만** 차감한다(프리셋 인스턴스 교체: 대체될 서브트리 자신의
   * 키는 자리를 되돌려줘 같은 키를 그대로 재사용하게 한다). 미배치 풀·양식 축은 교체와 무관하므로
   * 차감하지 않는다 — 축을 넓히면 불필요한 키 재발급이 생긴다.
   */
  const collectPasteTakenKeys = useCallback(
    (tree: ComposerWidget[], excludeKeys?: ReadonlySet<string>): Set<string> => {
      const taken = new Set<string>();
      for (const k of collectAllInputBindingKeys(tree)) {
        if (!excludeKeys?.has(k)) taken.add(k);
      }
      for (const u of composerWidgetSync.unplaced) taken.add(u.bindingKey);
      for (const f of derivedWriteForm) taken.add(f.bindingKey);
      return taken;
    },
    [composerWidgetSync.unplaced, derivedWriteForm],
  );

  // 파생(비-treeOwned) 위젯의 bindingKey → 양식(body) 내 배치 위치 맵. 미배치 풀에 같은 카탈로그
  // 라벨이 반복돼 구분이 안 되는 문제를 조상 브레드크럼(섹션 › 표 › 열/행 헤더)으로 해소한다.
  // 템플릿당 1회 순회 후 lookup만 하도록 useMemo(풀 100+행이 각자 walk하지 않게).
  const bindingPlacementMap = useMemo(
    () => collectBindingPlacements(template.body),
    [template.body],
  );

  // 양식 탭 선택(셀/노드)이 가리키는 바인딩이 배치된 작성 위젯 id — 양식→작성 역방향 내비게이션 대상.
  // 우선순위: ① 단일 셀 선택이면 그 셀 binding ② 선택 노드 자신 binding ③ 표 노드면 첫 바인딩 셀.
  // 매치가 없으면 null(컨텍스트 메뉴/패널 버튼 disabled·숨김).
  const crossNavTargetWidgetId = useMemo<string | null>(() => {
    let bindingKey: string | undefined;
    if (singleCell) {
      const node = findNodeById(template.body, singleCell.tableId);
      if (node && node.kind === 'table') {
        const cell = node.cells.find((c) => c.row === singleCell.row && c.col === singleCell.col);
        bindingKey = cell?.binding?.bindingKey;
        // 셀 직접 바인딩이 없으면 그 행을 포함하는 반복영역의 행 소스 키(행 목록 위젯) 폴백.
        if (!bindingKey) {
          bindingKey = node.repeatRegions?.find(
            (r) => singleCell.row >= r.rowStart && singleCell.row <= r.rowEnd,
          )?.over;
        }
      }
    } else if (selectedNode) {
      if (selectedNode.binding?.bindingKey) bindingKey = selectedNode.binding.bindingKey;
      else if (selectedNode.kind === 'table') {
        bindingKey =
          selectedNode.cells.find((c) => c.binding?.bindingKey)?.binding?.bindingKey ??
          selectedNode.repeatRegions?.[0]?.over;
      } else if (selectedNode.kind === 'repeat') {
        bindingKey = selectedNode.over;
      }
    }
    if (!bindingKey) return null;
    return findWidgetByBindingKey(composerWidgetSync.tree, bindingKey)?.id ?? null;
  }, [singleCell, selectedNode, template.body, composerWidgetSync.tree]);

  // ── 작성 탭 캔버스 초기값 seed(WYSIWYG) ──
  // 실제 작성 페이지(web new-composed)는 빈 폼이 아니라 ① 각 input defaultValue ② 실데이터 반영 시
  // prefill(현장/컨텍스트)로 채워진 "작성 시작 시점"에서 출발한다. 작성 탭 캔버스도 그 모습을 그대로 보인다.
  // (캔버스는 계속 저작 모드 — onChange no-op, 값 표시만.)

  // ① defaultValue seed(컨텍스트 무관, 항상). rc 공용 헬퍼(collectWidgetDefaultValues)로 수집 —
  //    web new-composed.tsx의 초기값 규칙과 동일 소스: group 재귀 + formTable 고정 셀 위젯 포함,
  //    widget:'table'은 dataDef.defaultRows deep clone으로 seed. modalTrigger children은 모달
  //    스코프 전용이라 flat 시드 제외(ModalTriggerButton이 열림 시 스코프에 직접 시드 — 헬퍼 doc).
  const writeCanvasDefaultValues = useMemo<Record<string, unknown>>(
    () => collectWidgetDefaultValues(composerWidgetSync.tree, template.dataDefs),
    [composerWidgetSync.tree, template.dataDefs],
  );

  // 작성 탭 컨텍스트 유효성 가드 — 위젯 삭제/undo/템플릿 전환으로 그 섹션(group) 위젯이 사라지면
  // root 레벨(null)로 리셋(stale 컨텍스트로 인한 승격/드릴 오작동 방지). widgetParentId가
  // undefined면 트리에 없음(삭제됨). (양식 탭 selectionContextId 유효성 가드 동형.)
  useEffect(() => {
    if (
      writeSelectionContextId &&
      widgetParentId(composerWidgetSync.tree, writeSelectionContextId) === undefined
    ) {
      setWriteSelectionContextId(null);
    }
  }, [composerWidgetSync.tree, writeSelectionContextId]);

  // ② prefill 실데이터 seed(양식 탭 "실데이터 반영" 상태일 때만). 반영된 엔티티 컨텍스트로 서버 resolvePrefill을
  //    호출해 defaultValue 위에 덮는다(web getComposerPrefill과 동일 서버 로직). [해제] 시 자동으로 {}로 원복.
  const [writeCanvasPrefill, setWriteCanvasPrefill] = useState<Record<string, unknown>>({});
  // 위젯 트리의 prefill 지정 서명 — treeOwned 위젯의 카탈로그/행 인덱스/셀 위젯 배치를 편집하면
  // 즉시 prefill 재조회가 돌도록 하는 트리거(body 바인딩 변경은 previewResult 갱신이 이미 담당).
  const composerPrefillSignature = useMemo(() => {
    const parts: string[] = [];
    const visit = (u: InputWidget): void => {
      const pf =
        u.prefillFrom ??
        (u.widget === 'registered'
          ? REGISTERED_FORM_WIDGETS_META.find((m) => m.key === u.widgetKey)?.defaultPrefillFrom
          : undefined);
      if (pf) parts.push(`${u.bindingKey}→${pf.catalogKey}:${pf.select ?? ''}`);
      // sortableSelect·combobox 후보 소스도 prefill 재조회 트리거 — 서버 resolvePrefill이 카탈로그
      // list 소스를 `result[sourceKey]=rows`로 채우므로(3-e), 소스 지정/해제 시 재조회가 돌아야
      // 캔버스에 후보(combobox는 제안 목록)가 뜬다.
      // (미포함 시: 소스를 골라도 서명이 그대로라 prefill이 재조회되지 않아 rows가 영영 0 —
      //  "후보 항목이 없습니다"로 보임. 2026-07-16 사용자 제보로 발견.)
      if (u.widget === 'sortableSelect' || u.widget === 'combobox') {
        const srcs = effectiveOptionsSources(u);
        if (srcs.length > 0) parts.push(`${u.bindingKey}⇐${srcs.join(',')}`);
      }
    };
    const walk = (nodes: ComposerWidget[]): void => {
      for (const w of nodes) {
        if (w.kind === 'input') visit(w);
        else if (w.kind === 'heading') {
          // heading 자동 채움도 서버 수집 대상(widget.id 합성) — 지정/해제 시 캔버스 prefill 재조회 트리거.
          if (w.prefillFrom) parts.push(`${w.id}→${w.prefillFrom.catalogKey}:${w.prefillFrom.select ?? ''}`);
        } else if (w.kind === 'group') walk(w.children);
        // 모달 children도 서버 수집 대상(resolvePrefill의 walk가 modalTrigger로 재귀) —
        // 모달 안 위젯의 소스/prefill 지정도 재조회 트리거가 되어야 캔버스가 실데이터를 받는다.
        else if (w.kind === 'modalTrigger') walk(w.children);
        else if (w.kind === 'formTable') {
          for (const row of w.rows) {
            if (row.type !== 'fixed') continue;
            for (const c of row.cells) {
              if (c.widget) visit(c.widget);
              // 텍스트 셀 자동 채움도 서버 수집 대상(합성 키) — 지정/해제 시 캔버스 prefill 재조회 트리거.
              else if (c.prefillFrom)
                parts.push(
                  `${ftTextCellPrefillKey(w.id, row.id, c.columnId)}→${c.prefillFrom.catalogKey}:${c.prefillFrom.select ?? ''}`,
                );
            }
          }
        }
      }
    };
    walk(composerWidgetSync.tree);
    // 변수 optionsFrom(select 옵션 동적 소스, 개정 4)도 서버 수집 대상 — 서버 resolvePrefill이
    // 카탈로그 rows를 result[catalogKey]로 채우므로, 소스 지정/해제 시 재조회가 돌아야
    // 캔버스 변수 select에 동적 옵션이 뜬다(sortableSelect 소스와 동일 이유).
    for (const v of template.variables ?? []) {
      if (v.optionsFrom) parts.push(`var:${v.key}⇐${v.optionsFrom.catalogKey}`);
    }
    return parts.sort().join('|');
  }, [composerWidgetSync.tree, template.variables]);
  // ④ 캔버스에 넘길 최종 값 — defaultValue → prefill(실데이터) → edits(사용자 편집) 순 우선.
  //    해제 시 prefill·edits 모두 비어 defaultValue만.
  //    (방식 재resolve 트리거 판정이 이 값을 읽으므로 캔버스 렌더보다 앞에서 선언한다 — 같은 합성본을
  //     두 번 만들면 한쪽만 고쳐져 갈라진다.)
  const writeCanvasValues = useMemo<Record<string, unknown>>(
    () => ({ ...writeCanvasDefaultValues, ...writeCanvasPrefill, ...writeCanvasEdits }),
    [writeCanvasDefaultValues, writeCanvasPrefill, writeCanvasEdits],
  );
  // 파라미터 지정 편집 채널의 "해소 세트 이름" 조인이 읽는 행 원본. 채널 memo의 deps에 이 값을
  // 넣으면 작성 캔버스 입력마다 채널 identity가 흔들리므로, 최신값은 ref로만 흘린다(saveRef 동형).
  const writeCanvasValuesRef = useRef(writeCanvasValues);
  writeCanvasValuesRef.current = writeCanvasValues;

  // 평가방식(findingRiskMethod) 재resolve 트리거 — **카탈로그 파라미터 지정이 참조하는 변수**의
  // 캔버스 현재 값(default→prefill→edits 우선). 이 값이 바뀌면 그 방식으로
  // prefill을 재조회하고, 위험도/기준표/__seed만 fresh로 재정렬한다(P4e). 판정은 rc 단일 지점.
  // 서버에 실어 보낼 방식 값 — 서버 resolveFindingRiskMethod와 같은 규칙(지정이 축을 소유).
  const canvasMethodValue = resolveFindingRiskMethodValue(template, writeCanvasValues);
  // 트리거 변수들의 값 시그니처(단일 스칼라) — 트리거가 여럿일 수 있어 값 하나 비교로는 부족하다.
  const canvasMethodTriggerSig = useMemo(
    () => methodTriggerSignature(template, writeCanvasValues),
    [template, writeCanvasValues],
  );
  // 방식 변경 감지 기준(edits 재정렬은 최초 prefill이 아니라 실제 방식 변경에만). context 전환 시 리셋.
  const reconcileMethodBasisRef = useRef<{ trigger: string } | null>(null);
  useEffect(() => {
    // 실데이터 반영 상태(previewResult 존재 + 엔티티 컨텍스트)에서만 prefill을 얹는다. 그 외엔 defaultValue만.
    if (!hasPreviewContext || !previewResult) {
      setWriteCanvasPrefill({});
      reconcileMethodBasisRef.current = null;
      return;
    }
    let cancelled = false;
    // prefill 채널은 linkedReportId를 받지 않는다 — context만(splitLinkedReportId JSDoc).
    const prefillBaseContext = splitLinkedReportId(previewContextForApi).context;
    // 방식 값이 있으면 findingRiskMethod로 전달 — resolvePrefill이 그 방식 config로 위험도·기준표 재산출.
    const contextForApi = canvasMethodValue
      ? { ...prefillBaseContext, findingRiskMethod: canvasMethodValue }
      : prefillBaseContext;
    // 300ms 디바운스 — 카탈로그/인덱스/방식 편집 연타(서명 연속 변경)에 요청 폭주 방지.
    const timer = setTimeout(() => {
      adminApi
        .prefillReportComposerByContext(template, contextForApi)
        .then((res) => {
          if (cancelled) return;
          const fresh = res.data ?? {};
          // 방식이 직전 반영 기준과 다르면(실제 변경) 캔버스 edits의 위험도/기준표를 fresh로 재정렬한다.
          // (최초 prefill은 basis=null이라 재정렬 없이 기준만 기록 — 스퓨리어스 폐기 방지.)
          const basis = reconcileMethodBasisRef.current;
          const methodChanged = basis !== null && basis.trigger !== canvasMethodTriggerSig;
          reconcileMethodBasisRef.current = { trigger: canvasMethodTriggerSig };
          if (methodChanged) {
            setWriteCanvasEdits((prevEdits) => {
              const currentValues = {
                ...writeCanvasDefaultValues,
                ...writeCanvasPrefill,
                ...prevEdits,
              };
              const merged = mergeMethodReresolve(currentValues, fresh, template);
              const touched = collectMethodReresolveTouchedKeys(fresh, template);
              // 위험도/기준표/__seed 키만 edits에 실어 fresh prefill 위에서 비위험도 편집 승계본을
              // 표시(옛 방식 위험도 편집은 폐기). 그 외 사용자 입력 edits는 그대로 보존.
              const nextEdits = { ...prevEdits };
              for (const key of touched) nextEdits[key] = merged[key];
              return nextEdits;
            });
          }
          setWriteCanvasPrefill(fresh);
        })
        .catch(() => {
          // prefill 실패(네트워크/컨텍스트 불충분/소유권)는 graceful 무시 — defaultValue fallback. 콘솔 스팸 금지.
          if (!cancelled) setWriteCanvasPrefill({});
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // previewResult는 실데이터 resolve마다 새 참조 → 컨텍스트/바인딩 변경 시 prefill도 함께 갱신.
    // composerPrefillSignature — 위젯 prefill 지정(카탈로그·인덱스·셀 배치) 편집 즉시 반영(2026-07-12).
    // canvasMethodTriggerSig — 파라미터 지정이 참조하는 변수 값 변경 시
    //   findingRiskMethod 재조회 + 위험도/기준표 재정렬(P4). 값 자체(canvasMethodValue)가 아니라
    //   시그니처를 deps로 두는 이유: 트리거 변수가 여럿일 수 있고, 지정이 literal이면 값은 고정이다.
  }, [
    hasPreviewContext,
    previewResult,
    previewContextForApi,
    composerPrefillSignature,
    canvasMethodTriggerSig,
  ]);

  // ②-b AI 생성 데이터 후보 실데이터 seed — 트리의 sortableSelect가 AI 파트 소스
  //     (COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS)를 쓰면, 반영된 점검 컨텍스트로 후보를 resolve해
  //     resolvedRows 채널로 주입한다(web new-composed와 동일 감지·변환 — 캔버스가 최종 작성 화면과
  //     같은 후보를 보여야 그 결과로 디자인 가능, 2026-07-16 사용자 확정).
  //     ⚠️ values 채널이 아니라 resolvedRows인 이유: 모달(ModalTriggerButton) 안 sortableSelect는
  //        본문 values와 격리된 스코프(modalValues={})라 values로는 도달 불가 — resolvedRows만 전파된다.
  const [aiCandidateRows, setAiCandidateRows] = useState<
    Record<string, Record<string, unknown>[]>
  >({});
  const [aiCandidatesLoading, setAiCandidatesLoading] = useState(false);
  // 트리에서 감지된 AI 파트(정렬 고정 — 시그니처 안정성). 소스 키 편집 시에만 재계산.
  const detectedAiParts = useMemo<ImprovementCandidatePart[]>(() => {
    const sourceKeys = collectSortableSelectOptionSources(composerWidgetSync.tree);
    return (
      Object.keys(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS) as ImprovementCandidatePart[]
    ).filter((p) => sourceKeys.includes(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p]));
  }, [composerWidgetSync.tree]);
  // 후보 resolve 대상 점검 — 컨텍스트 캐스케이드의 단일 점검(inspectionId 우선, 없으면 목록 첫 항목).
  const aiCandidateInspectionId =
    previewContext.inspectionId ?? previewContext.inspectionIds?.[0];
  // ⚠️ deps에 previewResult 객체가 아니라 **불리언**을 쓴다 — previewResult는 바인딩 편집마다 재조회로
  //    새 참조가 되는데, AI 후보는 점검+파트에만 의존하므로 그때마다 재호출하면 AI 비용만 태운다
  //    (prefill effect가 previewResult 참조를 deps로 쓰는 것과 의도적으로 다름 — 그쪽은 값싼 조회).
  const hasRealData = !!previewResult;
  useEffect(() => {
    // 게이트: 실데이터 반영 상태 + 점검/회사 컨텍스트 + 감지 파트 ≥1. 그 외엔 호출 0(AI 비용 방지) +
    // rows 없음 = 후보 빈 목록(현행 동작 유지 — 버튼 숨김 hideWhenNoData).
    if (
      !hasPreviewContext ||
      !hasRealData ||
      !aiCandidateInspectionId ||
      !previewContext.companyId ||
      detectedAiParts.length === 0
    ) {
      setAiCandidateRows({});
      setAiCandidatesLoading(false);
      return;
    }
    const companyId = previewContext.companyId;
    const inspectionId = aiCandidateInspectionId;
    let cancelled = false;
    // prefill effect와 동형 300ms 디바운스 — 소스 키 편집 연타에 요청 폭주 방지.
    // 캐시가 비어 실제 AI 생성이 도는 경우 수십 초까지 걸리므로 비차단 인디케이터로 알린다.
    const timer = setTimeout(() => {
      setAiCandidatesLoading(true);
      adminApi
        .improvementCandidatesForReportComposer(inspectionId, companyId, detectedAiParts)
        .then((res) => {
          if (cancelled || !res.data) return;
          const next: Record<string, Record<string, unknown>[]> = {};
          for (const p of detectedAiParts) {
            const rows = improvementCandidatesToRows(res.data, [p]); // 그 파트 rows만
            if (rows.length > 0) next[COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p]] = rows;
          }
          setAiCandidateRows(next);
        })
        .catch(() => {
          // 후보 resolve 실패(네트워크/컨텍스트 불충분/소유권)는 graceful 무시 — 빈 목록으로 degrade.
          if (!cancelled) setAiCandidateRows({});
        })
        .finally(() => {
          if (!cancelled) setAiCandidatesLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // detectedAiParts는 배열 참조라 join으로 값 비교(같은 파트 조합이면 재호출 없음 — AI 비용 방지).
    // (admin eslint에는 react-hooks 플러그인이 없어 exhaustive-deps disable 주석을 달면 그 자체가
    //  "Definition for rule was not found" error가 된다 — 주석 없이 둔다.)
  }, [
    hasPreviewContext,
    hasRealData,
    aiCandidateInspectionId,
    previewContext.companyId,
    detectedAiParts.join(','),
  ]);

  // ②-c 캔버스 resolvedRows — sortableSelect 후보 소스의 행 주입 채널(모달 스코프 포함).
  //     ① 카탈로그/조립 list 소스 rows: 서버 resolvePrefill이 values 채널(result[sourceKey])로 넣어주지만,
  //        모달 안 sortableSelect는 본문 values와 격리라 도달하지 못한다 → 소스 키에 해당하는 배열만
  //        추려 resolvedRows로도 넣는다.
  //     ② AI 후보 rows(위) — 키가 ①과 달라 충돌 없음. 뒤에 병합해 AI 정본을 보장.
  // ③ 캔버스 라이브 즉시 등록(submitAction) 반환 행 — prefill 파생 rows 위에 합류(등록 직후 제안
  //    목록 즉시 반영, 재조회 없이). 컨텍스트 전환 시 초기화(아래 effect — 스테일 방지).
  const [canvasSubmittedRows, setCanvasSubmittedRows] = useState<
    Record<string, Record<string, unknown>[]>
  >({});
  const writeCanvasResolvedRows = useMemo<Record<string, Record<string, unknown>[]>>(() => {
    const rows: Record<string, Record<string, unknown>[]> = {};
    // 변수 optionsFrom 소스 키(개정 4)도 동일 채널 — 모달 안 변수 select까지 rows 도달.
    const sourceKeys = [
      ...new Set([
        ...collectSortableSelectOptionSources(composerWidgetSync.tree),
        ...collectVariableOptionsSourceKeys(template.variables),
      ]),
    ];
    for (const key of sourceKeys) {
      const value = writeCanvasPrefill[key];
      if (Array.isArray(value)) rows[key] = value as Record<string, unknown>[];
    }
    // 등록 반환 행 합류 — 같은 행(id, 없으면 name)은 교체, 신규는 append.
    for (const [key, submitted] of Object.entries(canvasSubmittedRows)) {
      let merged = rows[key] ?? [];
      for (const row of submitted) {
        const idx = merged.findIndex((r) =>
          row.id !== undefined ? r.id === row.id : r.name === row.name,
        );
        merged = idx >= 0 ? merged.map((r, i) => (i === idx ? row : r)) : [...merged, row];
      }
      rows[key] = merged;
    }
    return { ...rows, ...aiCandidateRows };
  }, [composerWidgetSync.tree, template.variables, writeCanvasPrefill, canvasSubmittedRows, aiCandidateRows]);

  // ③ 캔버스 편집값 오버레이(writeCanvasEdits)는 previewMergedData(양식 반영)보다 앞서 선언됨 — 위 참조.
  // 캔버스 입력 위젯 onChange → bindingKey 단위로 편집분 기록(얕은 오버레이).
  const handleWriteCanvasEdit = useCallback((bindingKey: string, value: unknown) => {
    setWriteCanvasEdits((prev) => ({ ...prev, [bindingKey]: value }));
  }, []);
  // prefill 컨텍스트(엔티티)가 바뀌거나 실데이터가 해제되면 편집분 초기화 — 다른 컨텍스트의 스테일 값 방지.
  // previewResult 재조회(바인딩 변경)에는 반응하지 않게 deps는 컨텍스트/반영여부만.
  // ⚠️ deps는 반드시 **previewContextForApi**(엔티티 스코프)여야 한다 — 방식 오버라이드를 얹은
  //    previewResolveContextForApi로 바꾸면 방식을 고르는 순간 그 선택(=edits)이 스스로 지워진다.
  useEffect(() => {
    setWriteCanvasEdits({});
    setCanvasSubmittedRows({}); // 등록 반환 행도 컨텍스트 스코프 — 함께 초기화(스테일 방지)
    reconcileMethodBasisRef.current = null; // 방식 재정렬 기준도 컨텍스트 스코프 — 교차 재정렬 방지
  }, [hasPreviewContext, previewContextForApi]);

  // (④ 캔버스에 넘길 최종 값 `writeCanvasValues`는 방식 재resolve 트리거가 읽으므로 위쪽에서 선언한다.)

  // 캔버스 트리에 변수 동적 옵션 축(optionsFrom) 반영(개정 4) — 트리 배치본(placed copy)은 배치
  // 시점 스냅샷이라 변수 선언 편집을 모른다. 렌더 진입 전에 변수 선언 기준으로 덮어(id 보존 —
  // 선택/dnd 무영향) 변수 선언을 옵션 축의 단일 정본으로 유지한다(web new-composed 동형).
  const writeCanvasTree = useMemo(
    () => applyVariableDynamicOptions(composerWidgetSync.tree, template.variables),
    [composerWidgetSync.tree, template.variables],
  );

  // 작성 캔버스 DOM. 경계 드래그 오버레이의 rect 앵커(position:relative 컨테이너)이자, 분기 슬롯 탭
  // 오버레이의 **위젯 조회 스코프**다(그 오버레이 자신은 A4 박스 직속 — 여긴 Card가 클립한다).
  // callback ref로 취득.
  const [writeCanvasEl, setWriteCanvasEl] = useState<HTMLDivElement | null>(null);
  // 분기 탭 오버레이 재측정 트리거 — 트리(추가/삭제/리사이즈로 DOM 변동)·활성 슬롯 전환(슬롯별 DOM
  // 높이 상이)·뷰 변화 시 rect 재측정. 인라인 리터럴 금지(effect deps 매 렌더 재실행 방지).
  const writeBranchRemeasureKey = useMemo(
    () => `${composerWidgetSync.tree.length}:${JSON.stringify(activeBranchSlots)}:${writeFormView}`,
    [composerWidgetSync.tree, activeBranchSlots, writeFormView],
  );
  // 등록형 위젯 실동작용 렌더 컨텍스트 — "실데이터 반영" 상태(엔티티 컨텍스트 +
  // resolve 성공)에서만 companyId/siteId를 주입해 위젯이 실제 현장 데이터를 조회·선택하게 한다.
  // prefill seed와 동일 게이트(hasPreviewContext && previewResult) — placeholder 모드/미선택이면 undefined로
  // 넘겨 위젯이 안내("실데이터 반영 시 선택 가능")로 degrade.
  const registeredWidgetContext = useMemo<
    { companyId?: string; siteId?: string } | undefined
  >(
    () =>
      hasPreviewContext && previewResult
        ? { companyId: previewContext.companyId, siteId: previewContext.siteId }
        : undefined,
    [hasPreviewContext, previewResult, previewContext.companyId, previewContext.siteId],
  );

  // 행 그리드(dataSourceRows) 캔버스 렌더 — 라이브 모드(실데이터 반영)에서만 web 실폼과 **같은**
  // rc DataSourceRowsField를 주입해 캔버스↔실폼 렌더 결을 일치시킨다(WYSIWYG). 샘플 모드는 미주입
  // → rc placeholder(편집 보조 뼈대) 유지(라이브=WYSIWYG / 샘플=뼈대 규약).
  // rows·seed는 prefill 채널(writeCanvasValues[def.key(+__seed)]) — 서버 resolvePrefill이 web
  // 작성 페이지와 동일 조립. 컨텍스트 미선택 등으로 rows가 비면 실폼의 빈 상태와 동일하게 렌더.
  // 행 편집은 rc가 넘겨주는 onChange 사용 — 캔버스=writeCanvasEdits 오버레이(다른 입력 위젯과
  // 동일 채널), 비교 패널=no-op(읽기 전용)가 그대로 적용된다.
  const canvasDataSourceRowsRenderer = useMemo<DataSourceRowsRenderer | undefined>(() => {
    if (!hasPreviewContext || !previewResult) return undefined;
    const defs = template.dataSources ?? [];
    return (widget, value, onChange) => {
      const def = defs.find((d) => d.key === widget.bindingKey);
      if (!def) return null;
      return (
        <DataSourceRowsField
          def={def}
          value={value}
          seed={writeCanvasValues[widget.bindingKey + ROWS_SEED_SUFFIX]}
          registeredWidgets={COMPOSED_ROW_WIDGETS}
          // 등록형 행 위젯의 작성 화면 컨텍스트(web 실폼 주입과 동일 형상 — WYSIWYG).
          rowWidgetContext={{ values: writeCanvasValues, resolvedRows: writeCanvasResolvedRows }}
          // 캔버스 표식 — 사진 칸 클릭 확대만 막는다(모양은 실폼과 동일). 캔버스에서 모달이 뜨면
          // 트리·속성 패널을 덮어 저작이 끊긴다(photo 업로드 버튼 inert 선례와 같은 축).
          authoringCanvas
          onChange={(nextRows) => onChange(widget.bindingKey, nextRows)}
        />
      );
    };
  }, [
    hasPreviewContext,
    previewResult,
    template.dataSources,
    writeCanvasValues,
    writeCanvasResolvedRows,
  ]);

  // 카탈로그 행 즉시 등록(submitAction 모달) — 캔버스 라이브 모드 배선(실데이터 반영 + 현장 컨텍스트
  // 있을 때만, web new-composed 배선과 동형). 성공 시 반환 행을 canvasSubmittedRows에 합류(제안
  // 즉시 반영)하고 rc에 반환(콤보 세팅). 실패는 null(rc가 모달 유지 + 인라인 오류).
  const canSubmitCatalogRow = !!registeredWidgetContext?.companyId && !!registeredWidgetContext?.siteId;
  const handleCanvasCatalogRowSubmit = useCallback(
    async (
      catalogKey: string,
      submitValues: Record<string, string>,
    ): Promise<Record<string, unknown> | null> => {
      const companyId = registeredWidgetContext?.companyId;
      const siteId = registeredWidgetContext?.siteId;
      if (!companyId || !siteId) return null;
      try {
        const res = await adminApi.submitReportComposerCatalogRow(catalogKey, {
          companyId,
          siteId,
          values: submitValues,
        });
        if (!res.success || !res.data) return null;
        const row = res.data;
        setCanvasSubmittedRows((prev) => ({
          ...prev,
          [catalogKey]: [...(prev[catalogKey] ?? []), row],
        }));
        return row;
      } catch {
        return null;
      }
    },
    [registeredWidgetContext?.companyId, registeredWidgetContext?.siteId],
  );

  // ⚠️ 메인 캔버스와 "비교" 패널이 **공유해야 하는** ComposerWidgetTreeView props 묶음.
  // 두 곳에 props를 각각 나열했더니 한쪽에만 추가된 것이 생겼다 — activeBranchSlots(2026-07-21
  // 메인에만 추가)가 비교 패널에 없어서, 분기 슬롯 저작 중 메인엔 보이는 내용이 비교에선 통째로
  // 사라졌다(rc branch는 override 없으면 condition 폴백, 매칭 없으면 null). onCatalogRowSubmit도
  // 비교에만 없어 submitAction 액션이 비교에서만 숨었다. (2026-07-22 리뷰 적발)
  // 두 표면이 갈라지지 않게 여기 한 곳에서 만들고, 다른 값만 호출부에서 덮는다
  // (비교 패널: view=비교 뷰 / onChange=no-op(읽기 전용) / interactive 미전달).
  const writeCanvasSharedProps = useMemo(
    () => ({
      tree: writeCanvasTree,
      values: writeCanvasValues,
      resolvedRows: writeCanvasResolvedRows,
      // 분기 저작 활성 슬롯 override — 슬롯 탭/트리/속성 전환이 렌더에 반영되는 배선(P5-3).
      activeBranchSlots,
      dataSources: template.dataSources,
      dataDefs: template.dataDefs,
      registeredFormWidgets: ADMIN_COMPOSER_FORM_WIDGETS,
      registeredWidgetContext,
      // 라이브 모드에서만 행 그리드 실렌더(web 실폼과 같은 rc 컴포넌트) — 샘플 모드는 미주입으로
      // rc placeholder 뼈대 유지. 미배선이면 submitAction 콤보 액션도 숨는다(실폼 external 동형).
      dataSourceRowsRenderer: canvasDataSourceRowsRenderer,
      onCatalogRowSubmit: canSubmitCatalogRow ? handleCanvasCatalogRowSubmit : undefined,
    }),
    [
      writeCanvasTree,
      writeCanvasValues,
      writeCanvasResolvedRows,
      activeBranchSlots,
      template.dataSources,
      template.dataDefs,
      registeredWidgetContext,
      canvasDataSourceRowsRenderer,
      canSubmitCatalogRow,
      handleCanvasCatalogRowSubmit,
    ],
  );

  // 작성 페이지 위젯 트리 저작 핸들러(단계 3) — 순수 함수로 composerWidgetTree를 갱신(undo 대상).
  const wtApply = useCallback(
    (fn: (tree: ComposerWidget[]) => ComposerWidget[]) =>
      apply((t) => ({ ...t, composerWidgetTree: fn(t.composerWidgetTree ?? []) })),
    [apply],
  );
  // 미배치 입력 위젯을 트리에 배치(parentId=null이면 root 말미, 그룹 지정 시 그 그룹 말미,
  // afterId 지정 시 그 위젯 바로 다음 — 우클릭 컨텍스트 메뉴 경로). 배치 직후 그 위젯 선택
  // ("추가=선택" 규약 — 풀 버튼/dnd/우클릭 메뉴 모든 배치 진입점 공통, 속성창 연동).
  // 강제 단일화(2026-07-20 결정): 편집형 조립 데이터소스의 신규 배치는 행 그리드(dataSourceRows)
  // 위젯 대신 **반복 위젯 + 뼈대 children**으로 삽입한다 — 풀 배치 버튼/dnd/우클릭 메뉴 전 진입점이
  // 이 핸들러를 지나므로 여기서 일괄 대체(기존에 이미 배치된 dataSourceRows 렌더는 P4 은퇴까지 유지).
  const handleWidgetPlace = useCallback(
    (widget: ComposerWidget, parentId: string | null, afterId?: string | null) => {
      let toInsert = widget;
      if (widget.kind === 'input' && widget.widget === 'dataSourceRows') {
        const def = (template.dataSources ?? []).find((d) => d.key === widget.bindingKey);
        if (def) toInsert = createRepeatWidgetForDataSource(def, () => crypto.randomUUID());
      }
      wtApply((tree) => insertWidgetPositioned(tree, toInsert, parentId, afterId));
      selectAfterAddRef.current(toInsert.id);
    },
    [wtApply, template.dataSources],
  );
  // 트리에서 위젯 제거(input이면 미배치 풀로 복귀, design/group이면 삭제).
  const handleWidgetRemove = useCallback(
    (id: string) => wtApply((tree) => removeWidgetById(tree, id).tree),
    [wtApply],
  );
  // 형제 내 reorder(↑/↓).
  const handleWidgetReorder = useCallback(
    (id: string, dir: 'up' | 'down') =>
      wtApply((tree) =>
        writeFormView === COMPOSER_BASE_VIEW_KEY
          ? moveWidgetInSiblings(tree, id, dir)
          : moveWidgetInSiblingsForView(tree, id, dir, writeFormView),
      ),
    [wtApply, writeFormView],
  );
  // 위젯을 다른 부모(root/그룹)로 이동(말미로).
  const handleWidgetMoveTo = useCallback(
    (id: string, parentId: string | null) =>
      wtApply((tree) => moveWidget(tree, id, parentId, Number.MAX_SAFE_INTEGER)),
    [wtApply],
  );
  // 위젯을 부모(섹션/모달 스코프) 밖으로 한 단계 승격 — 양식 탭 moveNodeOutOfContainer 동형
  // (조부모 배열의 부모 바로 다음 위치로 삽입). 최상위/미발견은 무변경.
  const handleWidgetMoveOut = useCallback(
    (id: string) =>
      wtApply((tree) => {
        const parentId = widgetParentId(tree, id);
        if (parentId == null) return tree; // 최상위(null) 또는 미발견(undefined)
        const grandId = widgetParentId(tree, parentId) ?? null;
        const grandSiblings =
          grandId == null
            ? tree
            : (() => {
                const g = selectedWidgetById(tree, grandId);
                return g && (g.kind === 'group' || g.kind === 'modalTrigger') ? g.children : [];
              })();
        const parentIdx = grandSiblings.findIndex((w) => w.id === parentId);
        if (parentIdx < 0) return tree;
        // moveWidget의 index는 제거 후 대상 배열 기준 — 제거는 부모 children에서 일어나
        // 조부모 배열 인덱스에 영향 없음(부모 다음 위치 = parentIdx + 1 그대로 유효).
        return moveWidget(tree, id, grandId, parentIdx + 1);
      }),
    [wtApply],
  );
  // Slice B: index 지정 이동(중앙 미리보기 직접드래그) — moveWidget의 index는 제거 후 대상 배열 기준.
  const handleWidgetMoveToIndex = useCallback(
    (id: string, parentId: string | null, index: number) =>
      wtApply((tree) => moveWidget(tree, id, parentId, index)),
    [wtApply],
  );
  // Slice B: 중앙 직접드래그 전용 센서(좌측 아웃라인 DndContext와 독립) + drop 종료 핸들러.
  const writeFormCanvasSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  // 캔버스 충돌 판정 — 포인터가 실제 들어있는 드롭존 우선(pointerWithin), 여백 등 미포함 시에만
  // closestCenter fallback(빈 append 슬롯류). 상세 사유는 DndContext 사용처 주석 참조.
  const writeFormCanvasCollision = useCallback<CollisionDetection>((args) => {
    const within = pointerWithin(args);
    return within.length > 0 ? within : closestCenter(args);
  }, []);
  // 드래그 중 위젯 id — DragOverlay(포인터 추종 반투명 요약 카드)로 "드래그 중 + 어디에 삽입"
  // 피드백 제공(2026-07-13 사용자 요청 "드래그 가능 여부 자체를 알 수 없다"). 삽입 지점은
  // 드롭존 isOver 파란 라인이 함께 표시.
  const [canvasDragWidgetId, setCanvasDragWidgetId] = useState<string | null>(null);
  const handleCanvasDragStart = useCallback((e: DragStartEvent) => {
    const a = e.active.data.current as { id?: string } | undefined;
    setCanvasDragWidgetId(a?.id ?? null);
  }, []);
  const handleCanvasDragEnd = useCallback(
    (e: DragEndEvent) => {
      setCanvasDragWidgetId(null);
      const a = e.active.data.current as
        | { id: string; parentId: string | null; index: number }
        | undefined;
      const o = e.over?.data.current as { parentId: string | null; index: number } | undefined;
      if (!a || !e.over || !o) return;
      if (a.id === undefined) return;
      // 모달 버튼 중첩 금지 — modalTrigger(또는 그를 포함한 서브트리)를 모달 스코프로 드롭 차단
      // (트리 '＋'의 allowModalTrigger 게이트를 dnd 경로에 동형 적용).
      const dragTree = composerWidgetSync.tree;
      const dragged = selectedWidgetById(dragTree, a.id);
      if (
        dragged &&
        widgetSubtreeHasModalTrigger(dragged) &&
        isParentInsideModalScope(dragTree, o.parentId)
      )
        return;
      let target = o.index;
      // 같은 부모 내 앞→뒤 insert-before면 앞 형제 제거로 인한 시프트 -1 보정.
      // (append 슬롯은 index=MAX_SAFE_INTEGER라 -1 돼도 insertWidgetAt이 length로 clamp — 무해.)
      if (a.parentId === o.parentId && a.index < target) target -= 1;
      // 비-base 뷰 + 같은 부모 = 그 뷰 표시순만 갱신(base 배열·구조 불변).
      // base 뷰 또는 다른 부모로 이동(구조 변경)은 base 배열 조작(moveWidget).
      if (writeFormView !== COMPOSER_BASE_VIEW_KEY && a.parentId === o.parentId) {
        wtApply((tree) => moveWidgetToIndexForView(tree, a.id, target, writeFormView));
      } else {
        handleWidgetMoveToIndex(a.id, o.parentId, target);
      }
    },
    [handleWidgetMoveToIndex, wtApply, writeFormView, composerWidgetSync.tree],
  );
  // 텍스트 입력형 위젯(텍스트/입력 라벨) 추가 직후 인라인 편집 자동 진입(2026-07-15 사용자 요청).
  // rc 캔버스에 editRequest로 전달(nonce로 재요청 구분), 선택은 정확 선택(트리 소스 — isolation 승격
  // 우회)으로 admin이 맞춘다. add 핸들러(여기)가 selectWidget(아래 정의)을 참조해야 하므로
  // pasteNodesHandlerRef 패턴의 ref 경유(선언 순서 제약 회피 — 할당은 아래 effect).
  const [wtEditRequest, setWtEditRequest] = useState<{ id: string; nonce: number } | null>(null);
  const autoEditAfterAddRef = useRef<(id: string) => void>(() => {});
  // 추가된 위젯은 종류 무관 항상 선택 상태(속성창 연동, 2026-07-17 사용자 확정) — 텍스트/입력은
  // autoEditAfterAddRef(선택+인라인 편집), 그 외(섹션/구분선/여백/표/등록형/모달 버튼)는 선택만.
  const selectAfterAddRef = useRef<(id: string) => void>(() => {});
  // 요청은 rc가 소비한 뒤 곧 해제 — 남겨두면 탭 전환 복귀(리마운트) 시 재진입한다.
  useEffect(() => {
    if (!wtEditRequest) return;
    const t = window.setTimeout(() => setWtEditRequest(null), 0);
    return () => window.clearTimeout(t);
  }, [wtEditRequest]);
  // F2 → 트리 행 라벨 인라인 rename 요청(2026-10-03). 위 wtEditRequest(캔버스 인라인 편집)와 **다른
  // 채널**이다: 이쪽은 트리 패널 행이 소비하고 대상은 heading text / input label뿐이다. 해제도 같은
  // 이유로 0ms 뒤 — 행은 그 전에 effect로 소비하고, 소비 후엔 행의 로컬 state가 편집을 든다.
  //
  // ⚠️ **접힌 조상 아래 행의 F2는 「해제 타이머보다 펼침 리렌더가 먼저 돈다」는 스케줄러 순서에
  // 의존한다.** 해제는 `setTimeout(…, 0)` 매크로태스크이고 조상 펼침은 트리 편집기 effect의
  // `setCollapsedIds` 리렌더이므로, 행이 요청을 소비하려면 그 리렌더가 타이머보다 앞서야 한다
  // (현 React 스케줄러의 MessageChannel 태스크가 `setTimeout(0)`보다 앞서 돌아 동작하고 RTL
  // 케이스가 초록이다). 위 `wtEditRequest`의 관례를 미러한 것이라 **코드로 고정하지 않았다** —
  // 런타임·React 버전이 바뀌어 「접힌 섹션에서만 F2가 조용히 죽는」 증상이 나면 코드가 아니라
  // 이 순서를 먼저 보라(처방은 해제를 소비 신호 기반으로 바꾸는 것이고, 그때 두 채널을 함께).
  const [wtRenameRequest, setWtRenameRequest] = useState<ComposerTreeRenameRequest>(null);
  useEffect(() => {
    if (!wtRenameRequest) return;
    const t = window.setTimeout(() => setWtRenameRequest(null), 0);
    return () => window.clearTimeout(t);
  }, [wtRenameRequest]);

  // 새 그룹/디자인 위젯 추가(parentId=null이면 root, 그룹 지정 시 그 안, afterId 지정 시 그 위젯 바로 다음).
  const handleWidgetAddGroup = useCallback(
    (parentId: string | null, afterId?: string | null) => {
      // id 발급을 updater 밖에서 — updater 순수(StrictMode 안전) + 추가 직후 선택에 id 필요.
      const widget = createGroupWidget(crypto.randomUUID());
      wtApply((tree) => insertWidgetPositioned(tree, widget, parentId, afterId));
      selectAfterAddRef.current(widget.id);
    },
    [wtApply],
  );
  const handleWidgetAddDesign = useCallback(
    (kind: 'separator' | 'heading' | 'spacer', parentId: string | null, afterId?: string | null) => {
      // heading의 UI 명칭은 '텍스트'(자유 텍스트 요소) — 기본 문구도 동일(2026-07-12).
      const widget = createDesignWidget(
        crypto.randomUUID(),
        kind,
        kind === 'heading' ? '텍스트' : undefined,
      );
      wtApply((tree) => insertWidgetPositioned(tree, widget, parentId, afterId));
      // 텍스트 위젯은 추가 직후 문구 인라인 편집 진입(선택 포함), 구분선/여백은 선택만.
      if (kind === 'heading') autoEditAfterAddRef.current(widget.id);
      else selectAfterAddRef.current(widget.id);
    },
    [wtApply],
  );
  // 독립 입력 위젯(treeOwned) 추가 — body 바인딩 파생이 아닌 트리 직접 생성. 카탈로그(자동 채움)는
  // 속성 패널에서 지정하며 서버가 명시 prefillFrom을 수집(양식에 숨은 바인딩 불요, 2026-07-12).
  const handleWidgetAddInput = useCallback(
    (parentId: string | null, afterId?: string | null) => {
      // bindingKey 발급을 updater 밖에서(현재 트리 기준) — updater는 순수 삽입만(StrictMode 안전).
      const taken = collectAllInputBindingKeys(composerWidgetSync.tree);
      let n = 1;
      while (taken.has(`own-input-${n}`)) n += 1;
      const widget: InputWidget = {
        kind: 'input',
        id: crypto.randomUUID(),
        bindingKey: `own-input-${n}`,
        widget: 'text',
        label: '새 입력',
        treeOwned: true,
      };
      wtApply((tree) => insertWidgetPositioned(tree, widget, parentId, afterId));
      // 입력 위젯도 텍스트 입력형 — 추가 직후 라벨('새 입력') 인라인 편집 진입.
      autoEditAfterAddRef.current(widget.id);
    },
    [wtApply, composerWidgetSync.tree],
  );
  // 커스텀 모달 버튼 위젯 추가 — 버튼 + 빈 모달 셸(콘텐츠 저작은 P1b). bindingKey는 모달 UI 상태
  // 저장 키라 다른 입력과 충돌 없게 고유 발급(collectAllInputBindingKeys 회피). 기본 라벨/제목='구성'.
  const handleWidgetAddModalTrigger = useCallback(
    // afterId는 다른 add 핸들러와 동형(리프 anchor 우클릭 시 그 바로 다음) — 미지정이면 말미.
    // 2026-07-22 우클릭 팔레트에 '모달 버튼'을 넣으며 시그니처를 맞춤(이전엔 말미 전용이라 누락돼 있었다).
    (parentId: string | null, afterId?: string | null) => {
      const taken = collectAllInputBindingKeys(composerWidgetSync.tree);
      let n = 1;
      while (taken.has(`modal-${n}`)) n += 1;
      const widget: ModalTriggerWidget = {
        kind: 'modalTrigger',
        id: crypto.randomUUID(),
        bindingKey: `modal-${n}`,
        label: '구성',
        modalTitle: '구성',
        // 목적 카테고리(정본) — 신규 생성은 purpose 명시 + top-level commit dual-write
        // (commit은 타입 필수 + 구버전 읽기 호환. 결정 2026-07-16-composer-modal-purpose-category).
        purpose: { kind: 'valueCommit', commit: [] },
        commit: [],
        children: [],
      };
      wtApply((tree) => insertWidgetPositioned(tree, widget, parentId, afterId));
      selectAfterAddRef.current(widget.id);
    },
    [wtApply, composerWidgetSync.tree],
  );
  // 반복 위젯 추가(palette "+위젯"/우클릭) — 빈 반복(over 미지정) 삽입. 대상 데이터소스는 속성 패널
  // over select에서 지정하고, 최초 지정 시 뼈대 자동 생성(범용 구조 컨테이너 — branch와 동일 결,
  // 2026-07-20 사용자 요청). 데이터소스 이름을 팔레트에 나열하지 않아(구 optgroup 제거) 팔레트 범용성
  // 유지 + 미배치 풀 배치(강제 단일화)와 공존. bindingKey 없음(over는 데이터소스 참조).
  const handleWidgetAddRepeat = useCallback(
    (parentId: string | null, afterId?: string | null) => {
      const widget: ComposerWidget = { kind: 'repeat', id: crypto.randomUUID(), over: '', children: [] };
      wtApply((tree) => insertWidgetPositioned(tree, widget, parentId, afterId));
      selectAfterAddRef.current(widget.id);
    },
    [wtApply],
  );
  // 분기 위젯 추가(palette "+위젯"/우클릭) — 슬롯 2개(빈 else) 팩토리. 투명 컨테이너라 게이트 없음
  // (모든 컨테이너 안 허용, 중첩 branch도 본문처럼 허용). 슬롯 조건은 속성 패널에서 지정하고,
  // 추가 직후 선택해 속성 패널에서 바로 편집하게 한다. 반복(handleWidgetAddRepeat) 동형.
  const handleWidgetAddBranch = useCallback(
    (parentId: string | null, afterId?: string | null) => {
      const widget = createBranchWidget(crypto.randomUUID());
      wtApply((tree) => insertWidgetPositioned(tree, widget, parentId, afterId));
      selectAfterAddRef.current(widget.id);
    },
    [wtApply],
  );
  // ── 분기 위젯 슬롯 조작 핸들러 (속성 패널 WidgetBranchSlotsEditor 공용) — 본문 BranchSlotsEditor
  //    핸들러(handleAddBranchSlot 등) 동형. rc 슬롯 ops를 wtApply(undo 1스텝) 경유. ──
  const handleWidgetAddBranchSlot = useCallback(
    (branchId: string) => wtApply((tree) => addBranchWidgetSlot(tree, branchId)),
    [wtApply],
  );
  const handleWidgetRemoveBranchSlot = useCallback(
    (branchId: string, slotId: string) => wtApply((tree) => removeBranchWidgetSlot(tree, branchId, slotId)),
    [wtApply],
  );
  const handleWidgetMoveBranchSlot = useCallback(
    (branchId: string, slotId: string, dir: 'up' | 'down') =>
      wtApply((tree) => moveBranchWidgetSlot(tree, branchId, slotId, dir)),
    [wtApply],
  );
  const handleWidgetUpdateBranchSlot = useCallback(
    (branchId: string, slotId: string, patch: { label?: string; condition?: string }) =>
      wtApply((tree) => updateBranchWidgetSlot(tree, branchId, slotId, patch)),
    [wtApply],
  );
  // 등록형 폼 위젯 추가(palette "+위젯") — 카탈로그(REGISTERED_FORM_WIDGETS_META) 키로 생성.
  const handleWidgetAddRegistered = useCallback(
    (widgetKey: string, parentId: string | null, afterId?: string | null) => {
      // id 발급을 updater 밖에서 — updater 순수(StrictMode 안전) + 추가 직후 선택에 id 필요.
      const widget = createRegisteredWidget(crypto.randomUUID(), widgetKey);
      wtApply((tree) => insertWidgetPositioned(tree, widget, parentId, afterId));
      selectAfterAddRef.current(widget.id);
    },
    [wtApply],
  );
  // 작성폼 표 위젯 추가 — 기본 2열 + 헤더 셀 행(구분/내용) + 고정 행 1개(빈 텍스트 셀 2개).
  // 헤더=일반 셀 모델(2026-07-13): columns.label 대신 구형 기본을 normalizeFormTable로 신형 변환해
  // 삽입(헤더 시각 박제 단일 소스 유지 — 별도 스타일 하드코딩 금지). 열·셀 id는 자동 생성.
  const handleWidgetAddFormTable = useCallback(
    (parentId: string | null, afterId?: string | null) => {
      // 표 구성(id 발급 포함)을 updater 밖에서 — updater 순수(StrictMode 안전) + 추가 직후 선택.
      const c1 = crypto.randomUUID();
      const c2 = crypto.randomUUID();
      const table: FormTableWidget = normalizeFormTable({
        kind: 'formTable',
        id: crypto.randomUUID(),
        columns: [
          { id: c1, label: '구분' },
          { id: c2, label: '내용' },
        ],
        rows: [
          {
            type: 'fixed',
            id: crypto.randomUUID(),
            cells: [
              { columnId: c1, text: '' },
              { columnId: c2, text: '' },
            ],
          },
        ],
      });
      wtApply((tree) => insertWidgetPositioned(tree, table, parentId, afterId));
      selectAfterAddRef.current(table.id);
    },
    [wtApply],
  );
  // 위젯 필드 편집(heading text, group columns 등).
  const handleWidgetUpdate = useCallback(
    (id: string, updater: (w: ComposerWidget) => ComposerWidget) =>
      wtApply((tree) => updateWidget(tree, id, updater)),
    [wtApply],
  );
  // 위젯 수치 입력 실시간 미리보기(replacePresent — 히스토리 미적재) / 확정(commitPresent — undo 1스텝).
  // formTable 표 구조의 열 너비/행 높이 px 입력(DimInput unit="px")이 양식 탭 테이블 노드와 동형으로 쓴다.
  const handleWidgetPreview = useCallback(
    (id: string, updater: (w: ComposerWidget) => ComposerWidget) =>
      replacePresent((t) => ({
        ...t,
        composerWidgetTree: updateWidget(t.composerWidgetTree ?? [], id, updater),
      })),
    [replacePresent],
  );
  const handleWidgetCommit = useCallback(
    (id: string, updater: (w: ComposerWidget) => ComposerWidget) =>
      commitPresent((t) => ({
        ...t,
        composerWidgetTree: updateWidget(t.composerWidgetTree ?? [], id, updater),
      })),
    [commitPresent],
  );
  // **여러 위젯을 서로 다른 값으로 한 번에** 갱신하는 미리보기/커밋 채널 — 위 단건 채널을 두 번
  // 부르면 미리보기는 서로를 덮고(마지막 것만 반영) 커밋은 undo 2스텝이 된다. 폭 비율 경계 드래그
  // (WidgetEdgeResizeOverlay)가 좌/우 위젯을 한 트랜잭션으로 조정하는 용도.
  // (handleWidgetsUpdate는 **같은** updater를 여러 id에 적용하는 다중 선택 일괄 편집이라 별개 축.)
  const handleWidgetEditsPreview = useCallback(
    (edits: { id: string; updater: (w: ComposerWidget) => ComposerWidget }[]) =>
      replacePresent((t) => ({
        ...t,
        composerWidgetTree: edits.reduce(
          (tree, e) => updateWidget(tree, e.id, e.updater),
          t.composerWidgetTree ?? [],
        ),
      })),
    [replacePresent],
  );
  const handleWidgetEditsCommit = useCallback(
    (edits: { id: string; updater: (w: ComposerWidget) => ComposerWidget }[]) =>
      commitPresent((t) => ({
        ...t,
        composerWidgetTree: edits.reduce(
          (tree, e) => updateWidget(tree, e.id, e.updater),
          t.composerWidgetTree ?? [],
        ),
      })),
    [commitPresent],
  );
  // 위젯 경계 드래그(폭 비율) — 오버레이가 준 비율 쌍을 **활성 뷰**에 기록한다(base=위젯 span,
  // 그 외=responsive[view].span). 기록 규약은 속성 패널 spanOnView와 같은 헬퍼로 일원화.
  const spanDragEdits = useCallback(
    (edits: SpanEdit[]) =>
      edits.map((e) => ({
        id: e.id,
        updater: (w: ComposerWidget) => setWidgetSpanForView(w, writeFormView, e.ratio),
      })),
    [writeFormView],
  );
  const handleWidgetSpanDragPreview = useCallback(
    (edits: SpanEdit[]) => handleWidgetEditsPreview(spanDragEdits(edits)),
    [spanDragEdits, handleWidgetEditsPreview],
  );
  const handleWidgetSpanDragCommit = useCallback(
    (edits: SpanEdit[]) => handleWidgetEditsCommit(spanDragEdits(edits)),
    [spanDragEdits, handleWidgetEditsCommit],
  );
  // 오버레이의 위젯 조회 — 드래그 시작 시점의 지정 비율(responsive 오버라이드 포함)을 읽는다.
  const writeCanvasWidgetById = useCallback(
    (id: string) => selectedWidgetById(writeCanvasTree, id),
    [writeCanvasTree],
  );

  // ── 표(formTable) 고정 셀 복사/붙여넣기 — 패널 버튼 + 캔버스 Ctrl+C/V 공용(2026-07-12) ──
  // 붙여넣기는 위젯을 새 id/bindingKey의 독립(treeOwned) 복제본으로 생성(값 충돌 방지).
  // 복사 시 출처 prefill을 해소해 담아 두어 파생 위젯 복제본도 같은 데이터 경로를 가진다.
  const [ftCellClipboard, setFtCellClipboard] = useState<FtCellClipboard | null>(null);
  const resolveFtWidgetPrefill = useCallback(
    (u: InputWidget) =>
      u.prefillFrom ??
      derivedWriteForm.find((f) => f.bindingKey === u.bindingKey)?.prefillFrom ??
      (u.widget === 'registered'
        ? REGISTERED_FORM_WIDGETS_META.find((m) => m.key === u.widgetKey)?.defaultPrefillFrom
        : undefined) ??
      bindingPrefillMap.get(u.bindingKey),
    [derivedWriteForm, bindingPrefillMap],
  );
  // 위젯 셀 payload에 prefill 해소를 동봉(복제본이 같은 데이터 경로 유지) — 단일/블록 복사 공용.
  const resolveFtCellPayload = useCallback(
    (cell: FormTableFixedCell): FormTableFixedCell => {
      const cw = cell.widget;
      const pf = cw ? resolveFtWidgetPrefill(cw) : undefined;
      return {
        ...cell,
        ...(cw ? { widget: { ...cw, ...(pf ? { prefillFrom: pf } : {}) } } : {}),
      };
    },
    [resolveFtWidgetPrefill],
  );
  const copyFtCell = useCallback(
    (cell: FormTableFixedCell, src?: { tableId: string; rowId: string }) => {
      setFtCellClipboard({ kind: 'single', cell: resolveFtCellPayload(cell), src });
    },
    [resolveFtCellPayload],
  );
  // 블록 복사 — 선택(2+)의 bounding box를 top-left 기준 상대 오프셋 슬롯으로 수집(빈 위치 null 포함).
  const copyFtBlock = useCallback(
    (tableId: string, cells: { rowId: string; columnId: string }[]) => {
      const w = selectedWidgetById(composerWidgetSync.tree, tableId);
      if (!w || w.kind !== 'formTable') return;
      const layout = collectFtBlockLayout(w, cells);
      if (!layout) return;
      const blockCells = layout.slots.map((s) => {
        if (!s.ref) return { dr: s.dr, dc: s.dc, cell: null };
        const found = findFtFixedCell(composerWidgetSync.tree, { tableId, ...s.ref });
        return {
          dr: s.dr,
          dc: s.dc,
          cell: found ? resolveFtCellPayload(found) : null,
          srcRowId: s.ref.rowId,
        };
      });
      setFtCellClipboard({
        kind: 'block',
        rows: layout.rows,
        cols: layout.cols,
        cells: blockCells,
        srcTableId: tableId,
      });
    },
    [composerWidgetSync.tree, resolveFtCellPayload],
  );
  // 복사 셀 1개 → 대상 셀 N개에 일괄 붙여넣기(한 번의 트리 업데이트 = undo 1스텝). 각 대상마다 위젯
  // id/bindingKey를 유니크 배정 — taken 집합에 배정 키를 누적해 N회 반복에도 충돌 방지.
  const pasteFtCells = useCallback(
    (tableId: string, cells: { rowId: string; columnId: string }[]) => {
      const clip = ftCellClipboard;
      if (!clip || cells.length === 0) return;
      const taken = collectPasteTakenKeys(composerWidgetSync.tree);
      // 위젯 payload를 셀마다 새 id + 유니크 bindingKey('-copy' taken 누적)로 독립 복제.
      const cloneWidget = (src: InputWidget): InputWidget => {
        const base = `${src.bindingKey}-copy`;
        let key = base;
        let n = 2;
        while (taken.has(key)) key = `${base}-${n++}`;
        taken.add(key); // 다음 대상에서 같은 키 재배정 방지
        return { ...src, id: crypto.randomUUID(), bindingKey: key, treeOwned: true };
      };
      const w0 = selectedWidgetById(composerWidgetSync.tree, tableId);
      if (!w0 || w0.kind !== 'formTable') return;
      // 블록 붙여넣기 대상(top-left anchor + 오프셋 → 그리드 좌표) — 배치 후 선택 갱신에도 사용.
      let placed: { rowId: string; columnId: string }[] = [];
      if (clip.kind === 'block') {
        const anchor = ftSelectionTopLeft(w0, cells);
        if (!anchor) return;
        const targets = ftBlockTargets(w0, anchor, clip.cells);
        const slotByOffset = new Map(clip.cells.map((c) => [`${c.dr},${c.dc}`, c]));
        placed = targets.map((t) => t.target);
        handleWidgetUpdate(tableId, (x) => {
          if (x.kind !== 'formTable') return x;
          let next = x;
          for (const { dr, dc, target } of targets) {
            const slot = slotByOffset.get(`${dr},${dc}`);
            if (slot === undefined) continue;
            const payload = slot.cell;
            if (payload === null) {
              next = ftSetFixedCell(next, target.rowId, target.columnId, null);
              continue;
            }
            const cloned = payload.widget ? cloneWidget(payload.widget) : undefined;
            next = ftSetFixedCell(next, target.rowId, target.columnId, {
              columnId: target.columnId,
              ...(payload.text !== undefined ? { text: payload.text } : {}),
              ...(cloned ? { widget: cloned } : {}),
              // 계산 셀(compute)·병합(rowSpan)도 전파 — 셀 종류 상호배타라 widget과 공존 없음
              // (compute 누락으로 계산 셀 붙여넣기가 빈 셀이 되던 버그, 2026-07-13).
              ...(payload.compute ? { compute: payload.compute } : {}),
              // 텍스트 셀 자동 채움(prefillFrom)도 전파 — 텍스트 셀에만 존재(widget 셀은 위젯 clone이 자체 보유).
              ...(payload.prefillFrom ? { prefillFrom: payload.prefillFrom } : {}),
              ...(payload.colSpan ? { colSpan: payload.colSpan } : {}),
              ...(payload.rowSpan ? { rowSpan: payload.rowSpan } : {}),
              ...ftKeepCellAlign(payload),
            });
          }
          // 계산 셀 행 상대 재매핑 — 전 셀 배치 **후** 수행(같은 붙여넣기로 들어온 대상 행 위젯의
          // clone 키가 next에 반영된 뒤 조회돼야 새 키로 치환됨). 같은 표 복사본만 대상.
          if (clip.srcTableId === tableId) {
            for (const { dr, dc, target } of targets) {
              const slot = slotByOffset.get(`${dr},${dc}`);
              if (!slot?.cell?.compute || !slot.srcRowId || slot.srcRowId === target.rowId)
                continue;
              const row = next.rows.find((r) => r.id === target.rowId);
              const cur =
                row?.type === 'fixed'
                  ? row.cells.find((c) => c.columnId === target.columnId)
                  : undefined;
              if (!cur?.compute) continue;
              next = ftSetFixedCell(next, target.rowId, target.columnId, {
                ...cur,
                compute: ftRemapComputeRow(next, cur.compute, slot.srcRowId, target.rowId),
              });
            }
          }
          return next;
        });
      } else {
        const src = clip.cell;
        handleWidgetUpdate(tableId, (x) => {
          if (x.kind !== 'formTable') return x;
          let next = x;
          for (const { rowId, columnId } of cells) {
            const cloned = src.widget ? cloneWidget(src.widget) : undefined;
            next = ftSetFixedCell(next, rowId, columnId, {
              columnId,
              ...(src.text !== undefined ? { text: src.text } : {}),
              ...(cloned ? { widget: cloned } : {}),
              // 계산 셀(compute)·병합(rowSpan) 전파 — 블록 브랜치와 동일(compute 누락 버그 fix).
              ...(src.compute ? { compute: src.compute } : {}),
              // 텍스트 셀 자동 채움(prefillFrom) 전파 — 블록 브랜치와 동일.
              ...(src.prefillFrom ? { prefillFrom: src.prefillFrom } : {}),
              ...(src.colSpan ? { colSpan: src.colSpan } : {}),
              ...(src.rowSpan ? { rowSpan: src.rowSpan } : {}),
              ...ftKeepCellAlign(src),
            });
          }
          // 계산 셀 행 상대 재매핑 — 배치 후 대상 행별 치환(블록 브랜치와 동일 규약).
          if (src.compute && clip.src && clip.src.tableId === tableId) {
            const srcRowId = clip.src.rowId;
            for (const { rowId, columnId } of cells) {
              if (srcRowId === rowId) continue;
              const row = next.rows.find((r) => r.id === rowId);
              const cur =
                row?.type === 'fixed'
                  ? row.cells.find((c) => c.columnId === columnId)
                  : undefined;
              if (!cur?.compute) continue;
              next = ftSetFixedCell(next, rowId, columnId, {
                ...cur,
                compute: ftRemapComputeRow(next, cur.compute, srcRowId, rowId),
              });
            }
          }
          return next;
        });
      }
      // 블록 붙여넣기 후 선택을 배치된 위치로 갱신(스프레드시트 관례) — 단일 붙여넣기는 기존 선택 유지.
      if (placed.length > 0) {
        setFtFocusCell((prev) => ({
          tableId,
          cells: placed,
          source: prev?.tableId === tableId ? prev.source : 'canvas',
        }));
      }
    },
    [ftCellClipboard, composerWidgetSync, collectPasteTakenKeys, handleWidgetUpdate],
  );
  const pasteFtCell = useCallback(
    (tableId: string, rowId: string, columnId: string) => pasteFtCells(tableId, [{ rowId, columnId }]),
    [pasteFtCells],
  );
  // 고정 셀 N개 내용 제거(한 번의 트리 업데이트) — 위젯 셀은 제거 후 '입력' 상태 유지(빈 셀 객체),
  // 텍스트/비움 셀은 null(비움). 반복 행 셀은 대상에서 제외(고정 셀 전용). Delete/패널 "내용 지우기" 공용.
  const clearFtCells = useCallback(
    (tableId: string, cells: { rowId: string; columnId: string }[]) => {
      if (cells.length === 0) return;
      handleWidgetUpdate(tableId, (x) => {
        if (x.kind !== 'formTable') return x;
        let next = x;
        for (const { rowId, columnId } of cells) {
          const row = next.rows.find((r) => r.id === rowId);
          if (!row || row.type !== 'fixed') continue;
          const cell = row.cells.find((c) => c.columnId === columnId);
          next = ftSetFixedCell(
            next,
            rowId,
            columnId,
            cell?.widget
              ? { columnId, ...(cell.colSpan ? { colSpan: cell.colSpan } : {}) }
              : null,
          );
        }
        return next;
      });
    },
    [handleWidgetUpdate],
  );
  // Ctrl+B 해제 시 "이전 굵기" 복원용 세션 메모(대상 키 → 굵게 직전 fontWeight, undefined=미지정).
  // 굵게 적용 시 기록, 해제 시 복원(기록 없으면 미지정=위젯 기본 — 종전 동작 fallback). 템플릿에
  // 저장하지 않는 에디터 세션 로컬이라 새로고침 후엔 기본 해제로 동작. Ctrl+B 세 표면(작성 탭
  // 위젯/작성 탭 표 셀/양식 탭 노드·셀) 공용. (2026-07-16 사용자 요청: 해제=기본이 아니라 이전 값)
  const boldPrevWeightRef = useRef(new Map<string, number | undefined>());
  // 캔버스 Ctrl+C/V/B — 포커스된 표 셀 대상(입력 편집·텍스트 선택 중엔 브라우저 기본 동작 유지).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const k = e.key.toLowerCase();
      if (k !== 'c' && k !== 'v' && k !== 'b') return;
      if (!ftFocusCell) return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable)
      )
        return;
      if (k === 'b') {
        // 굵게(fontWeight 700) 토글 — 양식 탭 Ctrl+B와 동형(Word식: 존재 셀 전부 700이면 해제,
        // 하나라도 아니면 전체 700). 빈 슬롯(셀 없음)은 판정·적용 모두 제외. undo 1스텝.
        // 해제는 굵게 직전 굵기 복원(boldPrevWeightRef — 기록 없으면 미지정=기본).
        const refs = ftFocusCell.cells.filter((ref) =>
          findFtFixedCell(composerWidgetSync.tree, { tableId: ftFocusCell.tableId, ...ref }),
        );
        if (refs.length === 0) return;
        e.preventDefault();
        const allBold = refs.every(
          (ref) =>
            findFtFixedCell(composerWidgetSync.tree, { tableId: ftFocusCell.tableId, ...ref })
              ?.style?.fontWeight === 700,
        );
        handleWidgetUpdate(ftFocusCell.tableId, (x) => {
          if (x.kind !== 'formTable') return x;
          let next = x;
          for (const { rowId, columnId } of refs) {
            // updater의 최신 상태(next) 기준 재조회 — 바깥 스냅샷 stale 방지.
            const row = next.rows.find((r) => r.id === rowId);
            const cell =
              row?.type === 'fixed' ? row.cells.find((c) => c.columnId === columnId) : undefined;
            if (!cell) continue;
            const prevKey = `ft:${ftFocusCell.tableId}:${rowId}:${columnId}`;
            if (!allBold) boldPrevWeightRef.current.set(prevKey, cell.style?.fontWeight);
            next = ftSetFixedCell(next, rowId, columnId, {
              ...cell,
              style: ftMergeStyle(cell.style, {
                fontWeight: allBold ? boldPrevWeightRef.current.get(prevKey) : 700,
              }),
            });
          }
          return next;
        });
        return;
      }
      if (k === 'c') {
        if ((window.getSelection()?.toString() ?? '') !== '') return; // 텍스트 복사 우선
        // 복수 선택(2+)은 블록 복사, 단일은 anchor 셀 1개 복사.
        if (ftFocusCell.cells.length > 1) {
          copyFtBlock(ftFocusCell.tableId, ftFocusCell.cells);
          e.preventDefault();
        } else if (ftAnchorCell) {
          const cell = findFtFixedCell(composerWidgetSync.tree, ftAnchorCell);
          if (cell) {
            copyFtCell(cell, { tableId: ftAnchorCell.tableId, rowId: ftAnchorCell.rowId });
            e.preventDefault();
          }
        }
      } else if (ftCellClipboard) {
        // 붙여넣기 — 단일은 선택된 전 셀에 복제, 블록은 선택 top-left를 anchor로 1회 배치.
        pasteFtCells(ftFocusCell.tableId, ftFocusCell.cells);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    ftFocusCell,
    ftAnchorCell,
    ftCellClipboard,
    composerWidgetSync.tree,
    copyFtCell,
    copyFtBlock,
    pasteFtCells,
    handleWidgetUpdate,
  ]);

  // 캔버스 텍스트 위젯 더블클릭 인라인 편집 커밋 — kind로 대상 필드 판정(narrow 후 spread, union 직접 spread 금지).
  // heading=문구(빈 문자열 허용), group=섹션명(빈 값→undefined=무제), input=라벨(빈 값→undefined=라벨 제거).
  const handleWidgetTextChange = useCallback(
    (id: string, text: string) => {
      handleWidgetUpdate(id, (w) => {
        if (w.kind === 'heading') return { ...w, text };
        if (w.kind === 'group') return { ...w, title: text.trim() === '' ? undefined : text };
        if (w.kind === 'input') return { ...w, label: text.trim() === '' ? undefined : text };
        if (w.kind === 'formTable') return { ...w, title: text.trim() === '' ? undefined : text };
        return w;
      });
    },
    [handleWidgetUpdate],
  );
  // 셀 편집 진입(F2/편집 중 Tab 공용) — 셀 안 컨트롤 focus, 없으면 td dblclick 재발행(정적
  // 텍스트 셀=rc 인라인 편집, 자동 채움 셀=rc가 사유 툴팁, 계산/빈 셀=no-op). defer=true면
  // rAF 한 틱 뒤 실행 — 직전 편집 커밋(blur)이 유발한 재렌더 후에 DOM을 조회(Tab 연쇄 이동용).
  const enterFtCellEdit = useCallback(
    (tableId: string, rowId: string, columnId: string, defer?: boolean) => {
      const run = () => {
        const sel = `[data-ftcell="${tableId}::${rowId}::${columnId}"]`;
        const ctrl = document.querySelector<HTMLElement>(
          `${sel} input, ${sel} textarea, ${sel} select`,
        );
        if (ctrl) {
          ctrl.focus();
          return;
        }
        const td = document.querySelector<HTMLElement>(sel);
        if (td) {
          // 셀 안 좌표 주입 — rc 편집 차단 툴팁이 커서 기준(네이티브 hover 동형)이라
          // 합성 이벤트도 셀 위치에 뜨도록(미주입 시 0,0 → rc가 rect fallback).
          const r = td.getBoundingClientRect();
          td.dispatchEvent(
            new MouseEvent('dblclick', {
              bubbles: true,
              cancelable: true,
              clientX: r.left + Math.min(24, r.width / 2),
              clientY: r.top + r.height / 2,
            }),
          );
        }
      };
      if (defer) requestAnimationFrame(run);
      else run();
    },
    [],
  );
  // formTable 텍스트 셀 더블클릭 인라인 편집 커밋 — 셀 text만 갱신(undo 1스텝). 빈 문자열 허용
  // (텍스트 셀 유지 — 종류 전환/제거는 속성 패널 관할). 텍스트 셀 외(위젯/계산/빈 셀)는 방어 no-op.
  // 자동 채움(prefillFrom)은 **유지** — heading 텍스트 위젯(handleWidgetTextChange)·양식 탭 텍스트
  // 노드(binding 보존)와 동형(2026-07-14 사용자 확정: "수정해도 카탈로그는 유지"). 편집된 text는
  // 해소 실패 시 fallback으로 쓰이고, 채움이 살아 있는 한 화면은 계속 해소값(채움 해제는 속성 패널).
  const handleFtCellTextChange = useCallback(
    (tableId: string, rowId: string, columnId: string, text: string) => {
      handleWidgetUpdate(tableId, (w) => {
        if (w.kind !== 'formTable') return w;
        const row = w.rows.find((r) => r.id === rowId);
        if (!row || row.type !== 'fixed') return w;
        const cell = row.cells.find((c) => c.columnId === columnId);
        if (!cell || cell.text === undefined) return w;
        return ftSetFixedCell(w, rowId, columnId, { ...cell, text });
      });
    },
    [handleWidgetUpdate],
  );
  // 다중 선택 위젯 공통 속성 일괄 편집(undo 1스텝) — 선택 전체에 같은 updater 적용(속성 패널 다중 모드).
  const handleWidgetsUpdate = useCallback(
    (ids: string[], updater: (w: ComposerWidget) => ComposerWidget) =>
      wtApply((tree) => ids.reduce((t, id) => updateWidget(t, id, updater), tree)),
    [wtApply],
  );
  // Ctrl+B — 선택 위젯의 문자열 굵게(700) 토글. 문자열 스타일 축: heading=textStyle /
  // input=labelStyle(라벨) / group(제목 있음)=titleStyle. modalTrigger 버튼 라벨은 스타일 축이
  // 없어 제외. Word식(양식 탭·표 셀 동형): 대상 전부 700이면 해제, 하나라도 아니면 전체 700.
  // undo 1스텝(handleWidgetsUpdate). 처리했으면 true(호출부 preventDefault 판단).
  const handleWidgetBoldToggle = useCallback((): boolean => {
    const ids =
      selectedWidgetIds.length > 0 ? selectedWidgetIds : selectedWidgetId ? [selectedWidgetId] : [];
    const styleOf = (w: ComposerWidget): { fontWeight?: number } | null =>
      w.kind === 'heading'
        ? (w.textStyle ?? {})
        : w.kind === 'input'
          ? (w.labelStyle ?? {})
          : w.kind === 'group' && w.title
            ? (w.titleStyle ?? {})
            : null;
    const targets = ids
      .map((id) => selectedWidgetById(composerWidgetSync.tree, id))
      .filter((w): w is ComposerWidget => !!w && styleOf(w) !== null);
    if (targets.length === 0) return false;
    const allBold = targets.every((w) => styleOf(w)?.fontWeight === 700);
    // 굵게 적용 시 대상별 직전 굵기 기록 → 해제 시 복원(기록 없으면 미지정=기본).
    if (!allBold) {
      for (const w of targets) {
        boldPrevWeightRef.current.set(`w:${w.id}`, styleOf(w)?.fontWeight);
      }
    }
    const weightFor = (id: string) =>
      allBold ? boldPrevWeightRef.current.get(`w:${id}`) : 700;
    handleWidgetsUpdate(
      targets.map((w) => w.id),
      (w) => {
        if (w.kind === 'heading') {
          return { ...w, textStyle: { ...w.textStyle, fontWeight: weightFor(w.id) } };
        }
        if (w.kind === 'input') {
          return { ...w, labelStyle: { ...w.labelStyle, fontWeight: weightFor(w.id) } };
        }
        if (w.kind === 'group' && w.title) {
          return { ...w, titleStyle: { ...w.titleStyle, fontWeight: weightFor(w.id) } };
        }
        return w;
      },
    );
    return true;
  }, [selectedWidgetIds, selectedWidgetId, composerWidgetSync.tree, handleWidgetsUpdate]);
  // 다중 선택 위젯 일괄 제거(undo 1스텝) — input은 미배치 풀 복귀, design/group은 삭제(단일 경로와 동일).
  const handleWidgetsRemove = useCallback(
    (ids: string[]) =>
      wtApply((tree) => ids.reduce((t, id) => removeWidgetById(t, id).tree, tree)),
    [wtApply],
  );

  // ── 작성 탭 위젯 선택(단일/다중) — 양식 탭 selectSingleNode/toggleNodeInSelection 패턴 복제 ──
  // 단일 선택: anchor + 집합을 함께 세팅(stale 다중선택 방지). chrome 선택은 호출부에서 해제.
  const selectWidgetSingle = useCallback((id: string | null) => {
    setSelectedWidgetId(id);
    setSelectedWidgetIds(id ? [id] : []);
  }, []);
  // Ctrl/⌘ 클릭 토글 — 같은 부모 형제면 집합에서 토글(최소 1개 유지, 제거 시 anchor 이동),
  // 부모가 다르거나 anchor 없으면 새 단일 선택. (widgetParentId로 형제 판정 — view 무관 구조.)
  // Ctrl/⌘+클릭 토글 — cross-section(다른 부모) 다중 선택 허용(2026-07-11 완화, 표준 관례).
  // 단 **조상-자손 동시 선택은 금지**(삭제/이동 의미 모호 — 자손은 조상에 포함): 추가하려는
  // 위젯과 조상/자손 관계인 기존 선택은 자동 제외(Figma식). 묶기(Ctrl+G)는 여전히 같은 부모
  // 형제일 때만 활성(groupWidgets/캔 판정 불변) — 토글 허용 범위와 별개.
  const toggleWidgetInSelection = useCallback(
    (clickedId: string) => {
      const tree = composerWidgetSync.tree;
      const anchor = selectedWidgetId;
      const base = selectedWidgetIds.length > 0 ? selectedWidgetIds : anchor ? [anchor] : [];
      if (base.length === 0) {
        selectWidgetSingle(clickedId);
        return;
      }
      if (base.includes(clickedId)) {
        // 이미 선택 → 제거(최소 1개 유지: 유일 선택이면 anchor만 이동).
        if (base.length <= 1) {
          setSelectedWidgetIds(base);
          setSelectedWidgetId(clickedId);
          return;
        }
        const next = base.filter((id) => id !== clickedId);
        setSelectedWidgetIds(next);
        setSelectedWidgetId(clickedId === anchor ? next[next.length - 1] : anchor);
        return;
      }
      // 추가 — 조상-자손 충돌 자동 제외.
      const clickedW = selectedWidgetById(tree, clickedId);
      const clickedSubtree =
        clickedW && clickedW.kind === 'group' ? composerSubtreeIds(clickedW) : null;
      const conflicts = (id: string): boolean => {
        if (clickedSubtree?.has(id)) return true; // 기존 선택이 클릭 대상(그룹)의 자손
        const w = selectedWidgetById(tree, id);
        return w?.kind === 'group' && composerSubtreeIds(w).has(clickedId); // 기존 선택(그룹)이 클릭 대상의 조상
      };
      const next = [...base.filter((id) => !conflicts(id)), clickedId];
      setSelectedWidgetIds(next);
      setSelectedWidgetId(clickedId);
    },
    [composerWidgetSync.tree, selectedWidgetId, selectedWidgetIds, selectWidgetSingle],
  );
  // 트리 행·캔버스 공통 선택 진입점 — additive(Ctrl/⌘)면 토글, 아니면 단일. chrome 선택은 항상 해제.
  // 선택 순간 트리 hover 하이라이트도 해제 — 행 클릭이 유발한 재구성으로 mouseleave가 유실되면
  // hover 링이 캔버스에 고착됨(2026-07-13 사용자 제보 "다른 위젯 선택해도 표에 표시 잔존").
  // Shift+클릭 범위 선택 — 보고서 탭 rangeSelectToNode와 동형: anchor~clicked "사이" 형제 전체.
  // 깊이가 달라도 **최근접 공통 컨테이너(LCA)의 직계 자식으로 양쪽을 승격**해 그 형제 축에서
  // 범위 선택(2026-07-14 결정: canvas-modifier-click-cross-depth — 파일 탐색기/레이어 패널 직관,
  // 결과가 항상 같은 부모 형제라 묶기·이동과 정합). 조상-자손 관계는 조상 단독 선택으로 수렴.
  // 앵커 유지(반복 Shift+클릭 재범위). 셀 포커스 중이면 그 표가 앵커로 승격(셀→표).
  // 형제 순서는 base 배열(토글의 형제 판정과 동일 축).
  const rangeSelectToWidget = useCallback(
    (clickedId: string) => {
      const tree = composerWidgetSync.tree;
      const anchor = ftFocusCell?.tableId ?? selectedWidgetId;
      if (anchor == null || anchor === clickedId) {
        selectWidgetSingle(clickedId);
        return;
      }
      // 자기 자신부터 최상위(부모 null)까지의 조상 id 체인. 미발견이면 null.
      const chainOf = (id: string): string[] | null => {
        const ch = [id];
        let cur = id;
        for (;;) {
          const p = widgetParentId(tree, cur);
          if (p === undefined) return null;
          if (p === null) return ch;
          ch.push(p);
          cur = p;
        }
      };
      const aChain = chainOf(anchor);
      const cChain = chainOf(clickedId);
      if (!aChain || !cChain) {
        toggleWidgetInSelection(clickedId);
        return;
      }
      // LCA 승격 단위 쌍 — clicked 쪽 "컨테이너→그 직계 조상" 맵을 깊은 쪽 우선으로 만들고,
      // anchor 체인을 깊은 쪽부터 훑어 처음 만나는 공통 컨테이너가 최근접 공통 컨테이너.
      const cUnitByContainer = new Map<string | null, string>();
      for (const cu of cChain) {
        const key = widgetParentId(tree, cu) ?? null;
        if (!cUnitByContainer.has(key)) cUnitByContainer.set(key, cu);
      }
      let au: string | null = null;
      let cu: string | null = null;
      for (const a of aChain) {
        const key = widgetParentId(tree, a) ?? null;
        const c = cUnitByContainer.get(key);
        if (c !== undefined) {
          au = a;
          cu = c;
          break;
        }
      }
      if (au == null || cu == null) {
        toggleWidgetInSelection(clickedId);
        return;
      }
      const parent = widgetParentId(tree, au) ?? null;
      const siblings =
        parent === null
          ? tree
          : (() => {
              const p = selectedWidgetById(tree, parent);
              return p && p.kind === 'group' ? p.children : [];
            })();
      const ids = siblings.map((s) => s.id);
      const ai = ids.indexOf(au);
      const ci = ids.indexOf(cu);
      if (ai < 0 || ci < 0) {
        toggleWidgetInSelection(clickedId);
        return;
      }
      const [lo, hi] = ai <= ci ? [ai, ci] : [ci, ai];
      setSelectedWidgetIds(ids.slice(lo, hi + 1));
      setSelectedWidgetId(au); // 앵커 유지(승격 단위) — 반복 Shift+클릭이 같은 앵커에서 재범위.
    },
    [composerWidgetSync.tree, ftFocusCell, selectedWidgetId, selectWidgetSingle, toggleWidgetInSelection],
  );
  // source: 'canvas'=캔버스 클릭(isolation 모드면 group 승격 적용), 'tree'/미지정=트리 행 등(승격 없이
  // 그 위젯 직접 선택 — 레이어 패널은 항상 deep 접근이라 승격 우회). 양식 탭 캔버스 handleCanvasClick의
  // resolveSingleClickTarget 승격을 작성 탭 위젯 트리에 이식(2026-07-14).
  const selectWidget = useCallback(
    (id: string, additive?: boolean, range?: boolean, source?: 'canvas' | 'tree') => {
      setSelectedChrome(null);
      setTreeHoverWidgetId(null);
      // isolation + 캔버스 클릭이면 현재 컨텍스트 직계 자식까지 승격. 그 외(deepest/트리)는 직접.
      const promote = source === 'canvas' && selectionMode === 'isolation';
      const tree = composerWidgetSync.tree;
      // 컨텍스트 밖(조상 체인에 컨텍스트 없음) 클릭이면 컨텍스트를 root로 리셋(양식 탭 handleCanvasClick
      // 동형) — 승격 결과는 리셋 여부와 무관(체인에 없으면 resolveWriteSingleClickTarget이 최상위 반환).
      if (promote && writeSelectionContextId !== null) {
        const chain = writeWidgetAncestorChain(tree, id);
        if (!chain.includes(writeSelectionContextId)) setWriteSelectionContextId(null);
      }
      const targetId = promote
        ? resolveWriteSingleClickTarget(tree, id, writeSelectionContextId)
        : id;
      if (range) {
        // range는 셀 포커스를 앵커 계산에 쓰므로 rangeSelectToWidget이 먼저 읽고, 이후 해제.
        // isolation에선 클릭 대상을 승격 단위로 넘겨 LCA 로직이 컨텍스트 형제 축에서 범위선택(앵커는
        // 직전 승격 선택으로 이미 승격 단위 — LCA 로직 자체는 무변경).
        rangeSelectToWidget(targetId);
        setFtFocusCell(null);
        return;
      }
      // 셀 포커스 해제 — 다른 위젯/표 선택 시 stale 셀 포커스가 남으면, 그 표를 다시 클릭할 때
      // "첫 클릭=표 선택" 단계를 건너뛰고 곧장 셀로 가는 오동작(2026-07-14 제보). 표를 캔버스
      // 여백으로 재선택(드릴업)하는 경우도 셀 해제가 제스처 모델(표→셀 드릴다운)과 일치.
      setFtFocusCell(null);
      if (additive) toggleWidgetInSelection(targetId);
      else selectWidgetSingle(targetId);
    },
    [
      toggleWidgetInSelection,
      selectWidgetSingle,
      rangeSelectToWidget,
      selectionMode,
      writeSelectionContextId,
      composerWidgetSync.tree,
    ],
  );
  // 양식 탭 노드/셀 → 배치된 작성 위젯으로 이동(양식→작성 역방향). 작성 탭 전환 + 정확 선택(트리 소스)
  // + 탭 전환 커밋 후 그 위젯 DOM으로 스크롤 + 플래시. 모달 children 매치도 선택만(모달 자동 오픈 없음).
  const navigateToWriteWidget = useCallback(
    (widgetId: string) => {
      setViewMode('writeForm');
      selectWidget(widgetId, false, false, 'tree');
      flashAndScroll(`[data-composer-widget-id="${widgetId}"]`);
    },
    [selectWidget],
  );
  // 작성 탭 더블클릭 드릴다운(isolation 전용) — 양식 탭 resolveDrillTarget 동형. 승격 단위가 섹션(group)
  // 이고 클릭 지점이 그 안쪽이면 컨텍스트를 그 group으로 진입시키고, 새 컨텍스트 기준 직계 자식을 선택.
  // 리프/이미 최심부(승격 단위=클릭 위젯)면 no-op(텍스트 인라인 편집·formTable 셀 드릴은 렌더 래퍼가 담당).
  const handleWidgetDrill = useCallback(
    (widgetId: string) => {
      if (selectionMode !== 'isolation') return; // deepest: 드릴 개념 없음(현행 유지)
      const tree = composerWidgetSync.tree;
      const target = resolveWriteSingleClickTarget(tree, widgetId, writeSelectionContextId);
      if (target === widgetId) {
        // 승격 단위 자체를 더블클릭(그룹 본문 여백 등) — 그 컨테이너로 진입 + 첫 자식 선택(양식 탭 동형).
        // repeat(행 템플릿)도 드릴 사다리 대상(P4a — 행 인스턴스 클릭이 승격으로 repeat에 머무는
        // isolation에서 행 템플릿 위젯까지 내려가는 경로). branch(분기)도 대상 — 자식은 **활성 슬롯**의
        // children(캔버스가 활성 슬롯만 렌더 — 2026-07-21 제보: 드릴이 분기에서 멈춰 슬롯 위젯 선택 불가).
        const self = selectedWidgetById(tree, widgetId);
        const selfChildren =
          self?.kind === 'branch'
            ? (self.branches[
                Math.max(0, Math.min(activeBranchSlots[self.id] ?? 0, self.branches.length - 1))
              ]?.children ?? [])
            : self?.kind === 'group' || self?.kind === 'repeat'
              ? self.children
              : null;
        if (!self || !selfChildren || selfChildren.length === 0) return; // 리프 — 드릴 불가
        setSelectedChrome(null);
        setTreeHoverWidgetId(null);
        setFtFocusCell(null);
        setWriteSelectionContextId(widgetId);
        selectWidgetSingle(selfChildren[0].id);
        return;
      }
      const targetW = selectedWidgetById(tree, target);
      // 섹션·반복·분기만 드릴 진입 대상(분기는 슬롯 투명 — 체인이 슬롯 id를 스킵해 직계 자식이 위젯).
      if (
        !targetW ||
        (targetW.kind !== 'group' && targetW.kind !== 'repeat' && targetW.kind !== 'branch')
      )
        return;
      const inner = resolveWriteSingleClickTarget(tree, widgetId, target);
      setSelectedChrome(null);
      setTreeHoverWidgetId(null);
      setFtFocusCell(null);
      setWriteSelectionContextId(target);
      selectWidgetSingle(inner);
    },
    [selectionMode, composerWidgetSync.tree, writeSelectionContextId, selectWidgetSingle, activeBranchSlots],
  );
  // 다중 선택은 유지한 채 anchor(주 선택)만 전환 — 속성 패널 요약 칩 클릭(단일 reader가 그 위젯 기준으로).
  // 다중 선택(2개+) 묶기(Ctrl/⌘+G) — 스마트 분기(2026-07-11 사용자 요청):
  //   선택에 섹션이 **정확히 1개** 포함 → 나머지 위젯을 그 섹션 말미로 **포함**(cross-parent 허용).
  //   그 외(전부 비섹션) → 기존대로 같은 부모 형제를 **새 섹션으로 묶기**(groupWidgets가 형제 검증).
  // (조상·자손 동시 선택은 선택 단계에서 배제되므로 포함 이동이 사이클을 만들 수 없음.)
  const handleWidgetGroupSelected = useCallback(() => {
    const ids = selectedWidgetIds;
    // 단일 위젯도 허용 — 새 섹션으로 감싸 들어가기(2026-07-16 사용자 확정). 해제는 기존
    // Ctrl+Shift+G/툴바 '해제' 그대로.
    if (ids.length < 1) return;
    const tree = composerWidgetSync.tree;
    const groupIds = ids.filter((id) => selectedWidgetById(tree, id)?.kind === 'group');
    // 포함 모드(선택 중 섹션 1개로 나머지 이동)는 2개+ 선택에서만 — 단일 섹션 선택 + Ctrl+G는
    // "그 섹션을 새 섹션으로 감싸기"(아래 wrap 경로)로 동작해야 한다.
    if (ids.length >= 2 && groupIds.length === 1) {
      const targetId = groupIds[0];
      const rest = ids.filter((id) => id !== targetId);
      wtApply((t) =>
        rest.reduce((acc, id) => moveWidget(acc, id, targetId, Number.MAX_SAFE_INTEGER), t),
      );
      selectWidgetSingle(targetId);
      return;
    }
    let newGroupId: string | null = null;
    wtApply((tree2) => {
      const r = groupWidgets(tree2, ids, crypto.randomUUID());
      newGroupId = r.groupId;
      return r.tree;
    });
    if (newGroupId) selectWidgetSingle(newGroupId);
  }, [selectedWidgetIds, composerWidgetSync.tree, wtApply, selectWidgetSingle]);
  // 선택이 섹션(group) 1개면 해제(Ctrl/⌘+Shift+G) — 풀린 자식들을 선택.
  const handleWidgetUngroupSelected = useCallback(() => {
    const id = selectedWidgetId;
    if (!id) return;
    const w = selectedWidgetById(composerWidgetSync.tree, id);
    if (!w || w.kind !== 'group') return;
    const childIds = w.children.map((c) => c.id);
    wtApply((tree) => ungroupWidget(tree, id));
    setSelectedWidgetIds(childIds);
    setSelectedWidgetId(childIds.length > 0 ? childIds[childIds.length - 1] : null);
  }, [selectedWidgetId, composerWidgetSync.tree, wtApply]);
  // 선택 위젯 복제(Ctrl/⌘+D) — 단일/복수 모두. 트리 소유(design/group/formTable/treeOwned·registered
  // input)를 각자 원본 바로 다음 형제로 딥 클론. taken은 pasteFtCells와 동일 수집(트리+미배치+양식 바인딩)으로
  // 클론 bindingKey('-copy') 유니크 보장. body 바인딩 파생 input은 duplicate*가 null/skip → no-op.
  // 복수는 undo 1스텝(wtApply 1회) + 복제본 전체를 다중 선택(anchor=첫 복제본). 조상-자손 동시 선택은
  // 선택 단계에서 배제되므로 중복 복제 없음.
  const handleWidgetDuplicate = useCallback(() => {
    const ids =
      selectedWidgetIds.length > 0
        ? selectedWidgetIds
        : selectedWidgetId
          ? [selectedWidgetId]
          : [];
    if (ids.length === 0) return;
    const taken = collectPasteTakenKeys(composerWidgetSync.tree);
    // 파생(바인딩 배치) input은 clone 규칙이 제외 — 조용한 유실 방지 안내(2026-07-16 제보: 파생
    // textarea가 든 섹션 복제에서 위젯이 소리 없이 빠져 "왜 안 보이지" 혼란). 결과가 기대와 다른
    // 중요 안내라 토스트 대신 **모달**(사용자 확정 — 토스트는 놓치기 쉬움).
    const sources = ids
      .map((id) => selectedWidgetById(composerWidgetSync.tree, id))
      .filter((w): w is ComposerWidget => w != null);
    const excluded = countDerivedInputsExcludedOnClone(sources);
    const notifyExcluded = (): void => {
      if (excluded > 0) setCloneExcludedNotice({ count: excluded, action: '복제' });
    };
    if (ids.length === 1) {
      const result = duplicateComposerWidget(
        composerWidgetSync.tree,
        ids[0],
        taken,
        () => crypto.randomUUID(),
      );
      if (!result) {
        notifyExcluded(); // 파생 input 단독 복제 시도 = 전부 제외
        return;
      }
      wtApply(() => result.tree);
      selectWidgetSingle(result.newId); // 새 위젯 단일 선택
      notifyExcluded();
      return;
    }
    const result = duplicateComposerWidgets(
      composerWidgetSync.tree,
      ids,
      taken,
      () => crypto.randomUUID(),
    );
    if (result.newIds.length === 0) {
      notifyExcluded(); // 전부 복제 불가
      return;
    }
    wtApply(() => result.tree);
    setSelectedWidgetIds(result.newIds);
    setSelectedWidgetId(result.newIds[0]); // anchor=첫 복제본
    notifyExcluded();
  }, [selectedWidgetId, selectedWidgetIds, composerWidgetSync, collectPasteTakenKeys, wtApply, selectWidgetSingle]);

  // ── 위젯 프리셋 v1 (2026-07-17 결정) — 라이브러리(플랫폼) 로드 + 저장/삽입/버전 적용 ──
  // v1 노출은 admin Studio(super admin) + 플랫폼 프리셋만 → listWidgetPresets()는 companyId 없이,
  // createWidgetPreset은 ownerCompanyId 생략(=플랫폼). 격리·소유 계약은 서버가 강제.
  const [presetLibrary, setPresetLibrary] = useState<WidgetPreset[]>([]);
  const refreshPresetLibrary = useCallback(async () => {
    try {
      const res = await adminApi.listWidgetPresets();
      if (res.success && res.data) setPresetLibrary(res.data);
    } catch {
      // 목록 조회 실패는 무시(프리셋 삽입 메뉴가 비게 됨) — 저작 흐름 자체는 지속.
    }
  }, []);
  useEffect(() => {
    if (composerEnabled) void refreshPresetLibrary();
  }, [composerEnabled, refreshPresetLibrary]);

  // "프리셋으로 저장" 대상 — 선택 단일 루트 위젯의 정규화 서브트리 + fork 안내 여부(presetRef 있으면).
  // widgetId는 저장 성공 시 원본 위젯을 그 프리셋의 인스턴스로 스탬프하기 위해 보존(2026-07-19
  // 사용자 제보 — 스탬프 없이는 원본이 프리셋과 무관한 위젯으로 남아 발행/버전 관리 불가).
  const [saveAsPresetTarget, setSaveAsPresetTarget] = useState<{
    widgetId: string;
    subtree: ComposerWidget;
    forkNotice: boolean;
    /** 프리셋 클론이 제외할 파생(바인딩 배치) 입력 수 — 0이면 모달이 고지하지 않는다. */
    excludedDerivedCount: number;
  } | null>(null);
  const [saveAsPresetBusy, setSaveAsPresetBusy] = useState(false);
  // 프리셋 삽입/버전 적용 후 묶음 밖(미해소) 참조 안내 — 삽입 후 저작자가 대상 지정 필요.
  const [presetInsertNotice, setPresetInsertNotice] = useState<{
    presetName?: string;
    refs: PresetUnresolvedRef[];
  } | null>(null);

  // 선택 단일 루트 위젯을 "프리셋으로 저장" — 정규화(루트 presetRef 제거=자연 fork)로 모달 오픈.
  const handleSaveAsPreset = useCallback(
    (widgetId: string) => {
      const w = selectedWidgetById(composerWidgetSync.tree, widgetId);
      if (!w) return;
      setSaveAsPresetTarget({
        widgetId,
        subtree: normalizeSubtreeForPresetSave(w),
        forkNotice: w.presetRef != null,
        // 프리셋 클론이 제외할 파생 입력 수 — 저장 모달이 결정 전에 고지한다(조용한 유실 차단).
        excludedDerivedCount: countPresetExcludedDerivedInputs(w),
      });
    },
    [composerWidgetSync.tree],
  );

  // 저장 확정 — POST createWidgetPreset(v1 발행, 플랫폼). 성공 시 토스트 + 라이브러리 갱신.
  const handleConfirmSaveAsPreset = useCallback(
    async (input: SaveAsPresetInput) => {
      const target = saveAsPresetTarget;
      if (!target) return;
      setSaveAsPresetBusy(true);
      try {
        const res = await adminApi.createWidgetPreset({
          // key는 서버 자체 발급(preset-<random8>) — 정본 참조는 불변 id라 입력 마찰 제거.
          name: input.name,
          description: input.description,
          subtree: target.subtree,
          changeNote: input.changeNote,
          // v1: ownerCompanyId 생략 = 플랫폼 프리셋(super admin).
        });
        if (res.success && res.data) {
          // 원본 위젯을 방금 만든 프리셋의 인스턴스(v1)로 스탬프 — 저장한 원본에서 바로 버전
          // 표시·"새 버전으로 발행"이 가능해진다(fork 저장이면 이전 presetRef를 새 것으로 대체).
          const presetId = res.data.id;
          const version = res.data.currentVersion ?? 1;
          handleWidgetUpdate(target.widgetId, (x) => ({
            ...x,
            presetRef: { presetId, version },
          }));
          showToast(`프리셋 "${input.name}"을 저장했습니다.`, 'success');
          setSaveAsPresetTarget(null);
          await refreshPresetLibrary();
        } else {
          showToast(res.error?.message ?? '프리셋 저장에 실패했습니다.', 'error');
        }
      } catch (e) {
        showToast(e instanceof Error ? e.message : '프리셋 저장에 실패했습니다.', 'error');
      } finally {
        setSaveAsPresetBusy(false);
      }
    },
    [saveAsPresetTarget, showToast, refreshPresetLibrary, handleWidgetUpdate],
  );

  // 프리셋 삽입 — GET presets/:id로 currentVersion 스냅샷 → clonePresetSubtreeForInsert(트리 전체
  // bindingKey Set) → 루트 presetRef 스탬프 → 지정 위치 삽입(기존 위젯 추가와 동일 undo 1스텝).
  // unresolvedRefs 있으면 안내(삽입 후 대상 지정 필요).
  const handleInsertPreset = useCallback(
    async (presetId: string, parentId: string | null, afterId?: string | null) => {
      const res = await adminApi.getWidgetPreset(presetId).catch(() => null);
      if (!res || !res.success || !res.data) {
        showToast('프리셋을 불러올 수 없습니다.', 'error');
        return;
      }
      const detail = res.data;
      const version = detail.versions.find((v) => v.version === detail.currentVersion);
      if (!version) {
        showToast('프리셋 현재 버전 스냅샷을 찾을 수 없습니다.', 'error');
        return;
      }
      const taken = collectPasteTakenKeys(composerWidgetSync.tree);
      const { widget: clone, unresolvedRefs } = clonePresetSubtreeForInsert(
        version.subtree,
        taken,
        () => crypto.randomUUID(),
      );
      const stamped: ComposerWidget = {
        ...clone,
        presetRef: { presetId: detail.id, version: detail.currentVersion },
      };
      wtApply((tree) => insertWidgetPositioned(tree, stamped, parentId, afterId ?? null));
      selectAfterAddRef.current(stamped.id);
      // 파생(바인딩 배치) 입력은 클론이 제외한다 — 스냅샷에 있어도 삽입본엔 없으니 알린다.
      // 안 알리면 "빈 섹션"만 남아 원인을 못 찾는다(2026-08-19 도그푸딩에서 실제로 그랬다).
      const excludedDerived = countPresetExcludedDerivedInputs(version.subtree);
      if (excludedDerived > 0) {
        setCloneExcludedNotice({ count: excludedDerived, action: '프리셋 삽입' });
      }
      if (unresolvedRefs.length > 0) {
        setPresetInsertNotice({ presetName: detail.name, refs: unresolvedRefs });
      }
    },
    [composerWidgetSync, collectPasteTakenKeys, wtApply],
  );

  // 프리셋 인스턴스 버전 적용(업데이트·롤백 통일) — 로컬 수정 감지(배치 스냅샷 ↔ 현재 서브트리
  // presetSubtreeEquivalent) → 수정 있으면 경고 모달(유지/교체), 없으면 즉시 교체. 교체 = 선택 버전
  // 스냅샷을 clonePresetSubtreeForInsert로 클론 + presetRef 스탬프 후 **같은 위치 대체**(undo 1스텝).
  const handleApplyPresetVersion = useCallback(
    (
      widgetId: string,
      presetId: string,
      placedSnapshot: ComposerWidget | undefined,
      chosenSnapshot: ComposerWidget,
      chosenVersion: number,
    ) => {
      const current = selectedWidgetById(composerWidgetSync.tree, widgetId);
      if (!current) return;
      const doReplace = (): void => {
        // taken = 트리+미배치+양식 bindingKey 중 **교체 대상 서브트리 자신의 키는 제외**(자리 되돌려줌).
        const ownKeys = collectAllInputBindingKeys([current]);
        const taken = collectPasteTakenKeys(composerWidgetSync.tree, ownKeys);
        const { widget: clone, unresolvedRefs } = clonePresetSubtreeForInsert(
          chosenSnapshot,
          taken,
          () => crypto.randomUUID(),
        );
        const stamped: ComposerWidget = {
          ...clone,
          presetRef: { presetId, version: chosenVersion },
        };
        wtApply((tree) => updateWidget(tree, widgetId, () => stamped)); // 같은 위치 대체(undo 1스텝)
        selectWidgetSingle(stamped.id);
        if (unresolvedRefs.length > 0) setPresetInsertNotice({ refs: unresolvedRefs });
      };
      const modified = placedSnapshot ? !presetSubtreeEquivalent(current, placedSnapshot) : false;
      if (modified) {
        setConfirmState({
          title: '로컬 수정이 있는 인스턴스',
          body:
            '이 위젯은 삽입 후 직접 수정한 내용이 있습니다. 선택한 버전으로 교체하면 그 수정 내용이 사라집니다. 교체할까요?',
          confirmLabel: '교체',
          danger: true,
          onConfirm: () => {
            setConfirmState(null);
            doReplace();
          },
        });
      } else {
        doReplace();
      }
    },
    [composerWidgetSync, collectPasteTakenKeys, wtApply, selectWidgetSingle],
  );

  // 프리셋 인스턴스의 현재 서브트리를 새 버전으로 발행 — normalizeSubtreeForPresetSave(루트 출처 메타
  // 제거)로 스냅샷을 만들어 publishWidgetPresetVersion → 성공 시 이 인스턴스 presetRef.version을 새 버전으로
  // 재스탬프(로컬 수정 감지가 "수정 없음"으로 복귀, undo 1스텝) + 라이브러리 갱신 + 토스트. 성공 시 true.
  const handlePublishPresetFromInstance = useCallback(
    async (widgetId: string, presetId: string, changeNote: string): Promise<boolean> => {
      const current = selectedWidgetById(composerWidgetSync.tree, widgetId);
      if (!current) return false;
      const snapshot = normalizeSubtreeForPresetSave(current);
      try {
        const res = await adminApi.publishWidgetPresetVersion(presetId, {
          subtree: snapshot,
          changeNote,
        });
        if (res.success && res.data) {
          const newVersion = res.data.preset.currentVersion;
          wtApply((tree) =>
            updateWidget(tree, widgetId, (w) => ({
              ...w,
              presetRef: { presetId, version: newVersion },
            })),
          );
          showToast(`새 버전 v${newVersion}을 발행했습니다.`, 'success');
          await refreshPresetLibrary();
          return true;
        }
        showToast(res.error?.message ?? '버전 발행에 실패했습니다.', 'error');
        return false;
      } catch (e) {
        showToast(e instanceof Error ? e.message : '버전 발행에 실패했습니다.', 'error');
        return false;
      }
    },
    [composerWidgetSync.tree, wtApply, showToast, refreshPresetLibrary],
  );

  // 프리셋 이름/설명 수정(메타만, 버전 스냅샷 불변) — PATCH + 라이브러리 갱신 + 토스트. 성공 시 true.
  const handleUpdatePresetMeta = useCallback(
    async (
      presetId: string,
      body: { name: string; description: string },
    ): Promise<boolean> => {
      try {
        const res = await adminApi.updateWidgetPresetMeta(presetId, body);
        if (res.success && res.data) {
          showToast('프리셋 정보를 수정했습니다.', 'success');
          await refreshPresetLibrary();
          return true;
        }
        showToast(res.error?.message ?? '프리셋 수정에 실패했습니다.', 'error');
        return false;
      } catch (e) {
        showToast(e instanceof Error ? e.message : '프리셋 수정에 실패했습니다.', 'error');
        return false;
      }
    },
    [showToast, refreshPresetLibrary],
  );

  // 프리셋 soft delete — 배치 인스턴스는 복사본이라 무영향(출처 메타만 detach). DELETE + 라이브러리
  // 갱신 + 토스트. 성공 시 true(패널이 detach 표시로 전환).
  const handleDeletePresetFromInstance = useCallback(
    async (presetId: string): Promise<boolean> => {
      try {
        const res = await adminApi.deleteWidgetPreset(presetId);
        if (res.success) {
          showToast('프리셋을 삭제했습니다. 배치된 인스턴스는 그대로 유지됩니다.', 'success');
          await refreshPresetLibrary();
          return true;
        }
        showToast(res.error?.message ?? '프리셋 삭제에 실패했습니다.', 'error');
        return false;
      } catch (e) {
        showToast(e instanceof Error ? e.message : '프리셋 삭제에 실패했습니다.', 'error');
        return false;
      }
    },
    [showToast, refreshPresetLibrary],
  );

  // ── 작성 탭 위젯 복사/잘라내기/붙여넣기(Ctrl+C/X/V) ──
  //  - **copy(P4)**: 노드 축과 같은 payload 채널(localStorage + OS 클립보드 텍스트)로 기록한다
  //    (buildWidgetClipboardPayload — 위젯 + 파생 근거 body 노드 + 의존 데이터). 붙여넣기는
  //    applyWidgetClipboardPaste(순수 계산)로 클론·키 재발급·멱등 병합 후 한 apply에 삽입(undo 1스텝).
  //    새로고침·다른 창·다른 PC에서도 붙는다(이 줄기의 목적).
  //  - **cut(이동 시맨틱, 2026-07-16 사용자 확정)**: 아래 React state 버퍼를 **그대로 유지**한다.
  //    잘라내기 시 트리에서 제거된 상태라, 붙여넣기 1회차는 스냅샷 원본 그대로(id·bindingKey 무재발급 —
  //    파생 배치 위젯도 보존되어 섹션째 이동). "이동"은 환경 간 개념이 없고, payload로 옮기면 무재발급
  //    시맨틱과 P3 클론·재발급 경로가 충돌하므로 채널을 나눈 것이다(회귀 위험 최소).
  //    ⚠️ cut도 **payload 채널을 함께 기록**한다(copy 등가) — 1회 소비 후 이 버퍼를 비우므로 2회차부터는
  //    payload 경로(clone 재발급)로 붙는다. 기존의 "1회 소비 후 copy 모드 강등"과 같은 의도이고, 강등
  //    버퍼를 남기면 payload 채널의 최신 복사를 가려 stale 붙여넣기가 된다. 잘라낸 것을 다른 창에서
  //    붙여넣는 것도 이 기록으로 가능해진다(기록 시점이 cut 시점인 것이 중요 — 의존 수집이 원본 템플릿
  //    기준이어야 한다).
  //    ⚠️ 이 버퍼는 **소유 템플릿에 묶인다**(templateKey) — 잘라내고 다른 템플릿을 불러오면 이동
  //    경로(무재발급 삽입)가 성립하지 않는다(근거 body 노드·의존 데이터가 없는 곳에 orphan으로 꽂힘).
  //    EditorPage는 템플릿 전환 시 언마운트되지 않아 버퍼가 살아남으므로, 붙여넣기에서 key를 대조해
  //    다르면 payload 경로로 폴백한다(payload는 sourceTemplateKey로 타 템플릿 이식을 이미 처리).
  const [widgetCutClipboard, setWidgetCutClipboard] = useState<{
    widgets: ComposerWidget[];
    /** 잘라낸 시점의 템플릿 key — 붙여넣기 대상이 같은 템플릿일 때만 이동 경로를 태운다. */
    templateKey: string;
  } | null>(null);
  // 붙여넣기 경합 가드용 최신 template.key(latest-value ref 패턴) — readText resolve 이후 실행되는
  // 콜백이 "트리거 시점 key"와 대조하는 데 쓴다(F4).
  const templateKeyRef = useRef(template.key);
  templateKeyRef.current = template.key;
  // crossorigin readText 붙여넣기 in-flight 가드(노드 축 nodePasteReadInFlightRef와 동형) — Ctrl+V 연타
  // 시 다중 readText가 각각 resolve→붙여넣기되어 이중 삽입되는 것을 막는다.
  const widgetPasteReadInFlightRef = useRef(false);
  // 복제·붙여넣기에서 파생(바인딩 배치) 위젯이 제외됐을 때의 안내 모달 — 결과가 기대와 다른 중요
  // 사안이라 토스트 대신 모달(2026-07-16 사용자 확정: "토스트로 파악하기 어려울 수 있으니 중요사안은 모달로").
  const [cloneExcludedNotice, setCloneExcludedNotice] = useState<{
    count: number;
    // '프리셋 삽입'은 같은 유실(파생 input 제외)이라 같은 모달을 쓰되, 이유 문단만 갈라진다
    // (복제는 "한 곳에만 배치", 프리셋은 "다른 양식으로 옮겨갈 수 없음" — 2026-08-19 도그푸딩).
    action: '복제' | '붙여넣기' | '프리셋 삽입';
    /** cut 붙여넣기의 중복 배치 제외(잘라낸 파생 키가 그 사이 재배치됨) — 첫 문장을 "이미 배치" 변형으로. */
    alreadyPlaced?: boolean;
  } | null>(null);
  // 위젯 붙여넣기 이식 리포트(P5) — 3분류 결과. **항상 띄우지 않는다**: 표시 조건은
  // shouldShowWidgetPasteReport(순수 함수) 판정으로, 미해소가 있거나 다른 템플릿에서 의존물이 함께
  // 이식됐을 때만 세팅한다(같은 템플릿 + 미해소 0이면 기존처럼 조용히 붙는다).
  const [widgetPasteReport, setWidgetPasteReport] = useState<WidgetPasteReport | null>(null);
  // 선택(단일/복수) 위젯의 현재 subtree 스냅샷 — 미발견 id는 제외. copy/cut 공용.
  const snapshotSelectedWidgets = useCallback((): ComposerWidget[] => {
    const ids =
      selectedWidgetIds.length > 0
        ? selectedWidgetIds
        : selectedWidgetId
          ? [selectedWidgetId]
          : [];
    if (ids.length === 0) return [];
    const tree = composerWidgetSync.tree;
    const snapshot: ComposerWidget[] = [];
    for (const id of ids) {
      const w = selectedWidgetById(tree, id);
      if (w) snapshot.push(w);
    }
    return snapshot;
  }, [selectedWidgetId, selectedWidgetIds, composerWidgetSync.tree]);
  // 위젯 복사(P4) — 스냅샷을 payload v2로 조립(위젯 + 파생 근거 body 노드 + 의존 dataDefs/dataSources)해
  // localStorage + OS 클립보드에 기록한다. 복사분 없으면 false(호출부가 preventDefault 여부 판단).
  const handleWidgetCopy = useCallback(() => {
    const snapshot = snapshotSelectedWidgets();
    if (snapshot.length === 0) return false;
    writeClipboardPayloadChannels(buildWidgetClipboardPayload(snapshot, template), () =>
      showToast(
        'OS 클립보드에 복사하지 못했습니다. 이 브라우저 안에서는 붙여넣을 수 있습니다.',
        'error',
      ),
    );
    // 새 복사가 클립보드의 정본 — 남아 있는 cut 버퍼를 비운다. 붙여넣기가 cut 버퍼를 **무조건 우선**
    // 하므로, 비우지 않으면 "A 잘라내기 → B 복사 → Ctrl+V"에서 A가 붙는다(P4 채널 분리 회귀).
    setWidgetCutClipboard(null);
    return true;
  }, [snapshotSelectedWidgets, template, showToast]);
  // 잘라내기 1회차 붙여넣기 = **이동**(기존 규약 그대로 — 클론 없이 스냅샷 원본 삽입).
  // 삽입 위치 규약·다중 선택은 payload 경로와 공유(resolveWidgetPasteTarget/insertWidgetsForPaste).
  const pasteCutWidgets = useCallback(
    (cutWidgets: ComposerWidget[]): boolean => {
      const tree = composerWidgetSync.tree;
      const { selectedWidget, selectedIsContainer, pasteParentId } = resolveWidgetPasteTarget(
        tree,
        selectedWidgetId,
        writeSelectionContextId,
      );
      const taken = collectPasteTakenKeys(tree);
      // 이동: id·bindingKey 무재발급 — 원본은 잘라내기로 제거된 상태라 트리 유일성 유지, 파생 배치
      // 위젯도 보존되어 섹션째 이동 가능.
      // 모달 버튼 중첩 금지 가드 — copy처럼 필터로 빼면 그 위젯이 유실(트리에서 이미 제거됨)되므로
      // 삽입 자체를 차단+안내. 클립보드는 cut 그대로 유지돼 다른 위치에 다시 붙여넣을 수 있다.
      if (
        isParentInsideModalScope(tree, pasteParentId) &&
        cutWidgets.some(widgetSubtreeHasModalTrigger)
      ) {
        showToast(
          '모달 안에는 모달 열기 버튼이 든 위젯을 붙여넣을 수 없습니다. 다른 위치에 붙여넣으세요.',
          'error',
        );
        return true; // 처리됨(preventDefault) — 삽입 없음, 클립보드 유지
      }
      // 중복 배치 방지 가드 — 잘라낸 파생 위젯의 bindingKey가 그 사이 트리에 재배치됐으면(제거로 풀에
      // 재파생된 것을 사용자가 배치) 그 위젯만 제외 + 안내("이미 배치" 변형 문구).
      const stripRes = stripPlacedDerivedInputs(cutWidgets, collectAllInputBindingKeys(tree));
      if (stripRes.excluded > 0)
        setCloneExcludedNotice({
          count: stripRes.excluded,
          action: '붙여넣기',
          alreadyPlaced: true,
        });
      // id 충돌 안전 가드 — 스냅샷 id가 트리에 잔존하면(잘라내기 제거를 undo한 경우 등) 그 위젯만
      // clone 경로로 폴백(스냅샷 키를 taken에 선등록해 '-copy' 키가 원본 삽입분과 충돌하지 않게).
      for (const k of collectAllInputBindingKeys(stripRes.widgets)) taken.add(k);
      const treeIds = new Set<string>();
      for (const w of tree) for (const tid of composerSubtreeIds(w)) treeIds.add(tid);
      const insertable = stripRes.widgets.flatMap((w) => {
        const collision = [...composerSubtreeIds(w)].some((tid) => treeIds.has(tid));
        return collision ? cloneWidgetsForInsert([w], taken, () => crypto.randomUUID()) : [w];
      });
      if (insertable.length === 0) return false; // 전부 중복 배치 제외 — 붙일 게 없음
      const newIds = insertable.map((w) => w.id);
      wtApply((t) =>
        insertWidgetsForPaste(t, insertable, selectedWidget, selectedIsContainer, pasteParentId),
      );
      setSelectedWidgetIds(newIds);
      setSelectedWidgetId(newIds[0]);
      // 1회 소비 — 버퍼를 비워 2회차부터는 payload 채널(cut 시점에 기록됨)의 clone 경로로 붙는다.
      setWidgetCutClipboard(null);
      return true;
    },
    [
      composerWidgetSync.tree,
      selectedWidgetId,
      writeSelectionContextId,
      collectPasteTakenKeys,
      wtApply,
      showToast,
    ],
  );

  // payload 경로 붙여넣기(P4 본체) — applyWidgetClipboardPaste(순수 계산)로 위젯 클론·키 재발급·
  // 동봉 body 노드 멱등 병합을 계산한 뒤, **한 apply**로 의존 데이터 이식 + 결재 메타 + 노드 삽입 +
  // 위젯 삽입을 묶는다(undo 1스텝). 위치 규약과 모달 중첩 금지 가드는 기존 copy 경로와 동일.
  const pasteWidgetPayload = useCallback(
    (payload: NodeClipboardPayload) => {
      if (payload.widgets.length === 0) {
        // 양식 탭에서 복사한 노드 전용(v1) payload — 작성 탭에 붙일 위젯이 없다. 조용히 실패 금지.
        showToast('복사한 항목에 작성 위젯이 없습니다. 양식 탭에서 붙여넣으세요.', 'error');
        return;
      }
      const tree = composerWidgetSync.tree;
      const { selectedWidget, selectedIsContainer, pasteParentId } = resolveWidgetPasteTarget(
        tree,
        selectedWidgetId,
        writeSelectionContextId,
      );
      // 모달 버튼 중첩 금지 — 모달 스코프엔 modalTrigger(포함 서브트리) 붙여넣기 제외(그 외는 정상 삽입).
      // (modalTrigger 자체를 선택한 붙여넣기도 pasteParentId=그 id라 이 가드가 동일 적용.)
      const source = isParentInsideModalScope(tree, pasteParentId)
        ? payload.widgets.filter((w) => !widgetSubtreeHasModalTrigger(w))
        : payload.widgets;
      if (source.length === 0) {
        showToast(
          '모달 안에는 모달 열기 버튼이 든 위젯을 붙여넣을 수 없습니다. 다른 위치에 붙여넣으세요.',
          'error',
        );
        return;
      }
      const effective = source === payload.widgets ? payload : { ...payload, widgets: source };
      const res = applyWidgetClipboardPaste(
        effective,
        {
          template,
          widgetTree: tree,
          takenBindingKeys: collectPasteTakenKeys(tree),
        },
        {
          newWidgetId: () => crypto.randomUUID(),
          existingNodeIds: new Set(collectAllNodeIds(template.body)),
          nodeIdCounter: idCounter,
        },
      );
      if (res.widgets.length === 0) return;
      // 결재란 배치 nodeId remap — handlePasteNodes와 동일 규칙(미스면 dangling 방지로 드롭).
      const remappedPlacement = (() => {
        const p = effective.approvalPlacement;
        if (!p) return undefined;
        if (!p.nodeId) return p;
        const mapped = res.nodeIdMap.get(p.nodeId);
        return mapped ? { nodeId: mapped } : undefined;
      })();
      // 이식 리포트(P5) — 붙여넣기 **적용 전** 대상의 결재 설정을 기준으로 판정한다(적용 후에는
      // coupled 정책 판정이 항상 "있음"으로 뒤집힌다). 표시 여부는 순수 함수가 결정.
      const report = buildWidgetPasteReport({
        payload: effective,
        result: res,
        targetTemplateKey: template.key,
        env: {
          catalogIndex,
          presetIds: new Set(presetLibrary.map((p) => p.id)),
        },
        targetApprovalBefore: template,
        // 미해소 참조 재검증 근거 — 붙여넣기 **직전** 대상 스냅샷(이 시점 template/tree는 아직 apply 전).
        targetBodyBefore: template.body,
        targetWidgetTreeBefore: tree,
        // payload에 배치가 있었는데 remap 결과가 없으면 dangling 방지로 드롭된 것.
        approvalPlacementDropped:
          effective.approvalPlacement !== undefined && remappedPlacement === undefined,
      });
      if (shouldShowWidgetPasteReport(report)) setWidgetPasteReport(report);
      const newIds = res.widgets.map((w) => w.id);
      apply((t) => {
        // 의존 소스 + 결재 메타 이식(노드 붙여넣기 'with-deps'와 같은 헬퍼·같은 규칙).
        // 재발급 맵(res.bindingKeyMap)을 함께 넘겨 dataDef lockstep(P4)이 실제 반영에도 적용된다 —
        // 계산(res.dataDefs/addedDefKeys)만 lockstep이고 여기서 원본 payload로 재병합하면 표 위젯이
        // 고아가 된다("바인딩 삭제됨" 2026-08-07 실사고).
        let next = applyPasteApprovalMeta(
          applyPasteDependencies(t, effective, res.bindingKeyMap),
          effective,
          remappedPlacement,
        );
        // 동봉 body 노드는 body 말미에 순서대로 append(작성 탭엔 노드 선택 컨텍스트가 없다).
        for (const clone of res.nodes) next = insertNode(next, clone);
        return {
          ...next,
          composerWidgetTree: insertWidgetsForPaste(
            next.composerWidgetTree ?? [],
            res.widgets,
            selectedWidget,
            selectedIsContainer,
            pasteParentId,
          ),
        };
      });
      setSelectedWidgetIds(newIds);
      setSelectedWidgetId(newIds[0]);
    },
    [
      composerWidgetSync.tree,
      selectedWidgetId,
      writeSelectionContextId,
      collectPasteTakenKeys,
      template,
      apply,
      showToast,
      catalogIndex,
      presetLibrary,
    ],
  );

  // 위젯 붙여넣기 트리거(Ctrl+V·우클릭 메뉴 공유) — **로컬 cut 버퍼가 있으면 이동 경로 우선**,
  // 없으면 payload 채널(OS 클립보드 → localStorage 폴백, 노드 축과 동일 우선순위·in-flight 가드).
  // 반환 true = 이 호출이 처리(호출부는 동기 시점에 preventDefault).
  const handleWidgetPaste = useCallback(() => {
    // 경로 판정(이동 vs payload) + 경합 staleness 판정은 widgetPasteRoute 모듈이 소유한다
    // (F1 cut 버퍼 수명 · F2 템플릿 전환 orphan · F4 readText 경합 — 회귀 가드 테스트 동반).
    const route = resolveWidgetPasteRoute(widgetCutClipboard, template.key);
    if (route.kind === 'move') return pasteCutWidgets(route.widgets);
    const dispatch = (payload: NodeClipboardPayload) => {
      // stale이면 조용히 실패하지 않고 안내 후 중단한다.
      if (isWidgetPasteDispatchStale(route, templateKeyRef.current)) {
        showToast('템플릿이 바뀌어 붙여넣기를 취소했습니다. 다시 붙여넣으세요.', 'error');
        return;
      }
      pasteWidgetPayload(payload);
    };
    return triggerClipboardPayloadPaste(widgetPasteReadInFlightRef, dispatch, () =>
      showToast('붙여넣을 항목이 없습니다.', 'error'),
    );
  }, [widgetCutClipboard, template.key, pasteCutWidgets, pasteWidgetPayload, showToast]);
  // 작성 탭 위젯 선택 전체 해제(Esc/chrome 선택 시).
  const clearWidgetSelection = useCallback(() => {
    setSelectedWidgetId(null);
    setSelectedWidgetIds([]);
  }, []);
  // 위젯 잘라내기(Ctrl/⌘+X) = 이동 시맨틱 — 선택(단일/복수) 서브트리 스냅샷을 cut 모드로 저장 후
  // 트리에서 제거(Delete와 동일 경로, undo 1스텝). 클립보드 저장이 제거보다 먼저. 잘라낸 파생 위젯은
  // 제거로 풀에 재파생되지만 cut 붙여넣기는 스냅샷 원본을 쓰므로 무관(재배치 시 중복 배치 가드가 처리).
  const handleWidgetCut = useCallback(() => {
    const snapshot = snapshotSelectedWidgets();
    if (snapshot.length === 0) return false;
    setWidgetCutClipboard({ widgets: snapshot, templateKey: template.key });
    // payload 채널도 함께 기록(copy 등가) — 이동 1회 소비 후/다른 창 붙여넣기의 근거. 기록 시점이
    // **제거 전**이어야 의존 수집(파생 근거 body 노드·dataDefs)이 원본 템플릿 기준으로 정확하다.
    writeClipboardPayloadChannels(buildWidgetClipboardPayload(snapshot, template), () =>
      showToast(
        'OS 클립보드에 복사하지 못했습니다. 이 브라우저 안에서는 붙여넣을 수 있습니다.',
        'error',
      ),
    );
    handleWidgetsRemove(snapshot.map((w) => w.id));
    clearWidgetSelection();
    return true;
  }, [snapshotSelectedWidgets, template, showToast, handleWidgetsRemove, clearWidgetSelection]);
  // 모달 콘텐츠 저작 열기/닫기 — 열기(id=modalTrigger)는 그 모달 스코프로 "진입"(섹션 드릴 동형):
  // isolation 컨텍스트를 modalTrigger로 설정해 캔버스에서 모달 안 위젯을 직접 선택·추가·붙여넣기·
  // dnd 대상이 되게 한다(컨텍스트 미설정 시 isolation 승격이 최상위 modalTrigger로 올라가 내부
  // 위젯을 못 고름 — 2026-07-15 nonmodal-dnd 결정의 1급 저작 표면 전제). 닫기(null)는 컨텍스트를
  // root로 복귀. 선택은 유지(열 때 modalTrigger 속성창의 편집 토글 노출 맥락 보존, deepest 모드는
  // 컨텍스트 미사용이라 무해).
  const handleModalAuthoringToggle = useCallback((id: string | null) => {
    setModalAuthoringId(id);
    setWriteSelectionContextId(id);
  }, []);
  // ⚠️ 모달 저작 **탈출 해치**(capture 단계 Esc) — dim(ModalAuthoringDim)은 inset-0 클릭 차단이라
  // 모달이 사라지면 캔버스가 잠긴 채 나갈 방법이 없다. 실제로 그런 상태가 만들어진다: 반복 행 안
  // 모달을 저작하는 중 rows가 0이 되면 행 서브트리가 언마운트돼 모달만 사라지고 host의
  // modalAuthoringId는 남는다(해제 호출처는 속성 패널 버튼과 rc 닫기 콜백뿐, 2026-07-22 리뷰 적발).
  // capture 단계인 이유: 에디터의 다른 Esc 핸들러(셀 포커스 해제·isolation 드릴업)보다 먼저 소비해
  // "모달 저작 중 Esc = 모달 닫기"를 최우선 규약으로 두기 위함.
  useEffect(() => {
    if (modalAuthoringId == null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      handleModalAuthoringToggle(null);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [modalAuthoringId, handleModalAuthoringToggle]);
  // 선택 위젯 삭제 — Delete 키와 동일(선택 집합 제거 + 선택 해제). 우클릭 메뉴가 공유.
  const handleWidgetDeleteSelected = useCallback(() => {
    const ids =
      selectedWidgetIds.length > 0 ? selectedWidgetIds : selectedWidgetId ? [selectedWidgetId] : [];
    if (ids.length === 0) return;
    handleWidgetsRemove(ids);
    clearWidgetSelection();
  }, [selectedWidgetIds, selectedWidgetId, handleWidgetsRemove, clearWidgetSelection]);
  // 작성 탭 위젯 우클릭 — 클릭과 동일 선택 규칙(selectWidget 'canvas' = isolation 승격 포함)으로
  // 보정 후 컨텍스트 메뉴. 이미 선택 집합 안이면 다중 선택 유지(탐색기 관례 — 메뉴가 집합에 작용).
  // 셀 포커스는 해제(v1 메뉴는 위젯 단위 — 셀 전용 항목은 후속). 입력/인라인 편집 요소 위는
  // 브라우저 기본 메뉴 유지(텍스트 복사 등).
  const handleWriteWidgetContextMenu = useCallback(
    (widgetId: string, e: React.MouseEvent) => {
      const t = e.target as HTMLElement;
      // 컨트롤 위 우클릭: **포커스돼 편집 중인 컨트롤만** 네이티브 메뉴(텍스트 클립보드 등),
      // 그 외(캔버스에서 위젯을 가리키는 우클릭)는 위젯 메뉴(2026-07-16 제보: input 위 우클릭이
      // 항상 시스템 메뉴). 인라인 편집기(contenteditable)는 편집 중이므로 항상 네이티브.
      const ctrl = t.closest('input, textarea, select');
      if (ctrl && document.activeElement === ctrl) return;
      if (t.closest('[contenteditable="true"]')) return;
      e.preventDefault();
      e.stopPropagation(); // 조상 위젯 래퍼/프레임 빈 영역 핸들러로 중복 전파 방지
      setFtFocusCell(null);
      // "선택 유지" 판정은 raw id 정확 일치가 아니라 **조상 체인 포함**으로 — 선택된 섹션 *내부*
      // (자식 위젯 영역)를 우클릭하면 raw id는 자식이라 정확 일치 가드를 통과해 재선택이 돌고,
      // 드릴 컨텍스트가 그 섹션에 남아 있으면 승격이 자식을 반환해 선택이 섹션→자식으로 튐
      // (2026-07-16 제보: 섹션 우클릭 시 하위 자식이 선택되며 메뉴). 클릭 지점이 현재 선택의
      // subtree 안이면 선택을 유지하고 메뉴만 연다(탐색기/Figma 관례 — 메뉴가 선택에 작용).
      const chain = writeWidgetAncestorChain(composerWidgetSync.tree, widgetId);
      const inSelection = chain.some(
        (id) => selectedWidgetIds.includes(id) || selectedWidgetId === id,
      );
      if (!inSelection) {
        selectWidget(widgetId, false, false, 'canvas');
      }
      // addAnchorId = **승격 전** 원시 대상 — 그룹 우선 선택이 켜져 있어도 "가리킨 컨테이너 안에
      // 추가"가 되도록(위 ctxMenu 타입 주석 참조). 선택 자체는 위 규칙 그대로 둔다.
      setCtxMenu({
        x: e.clientX,
        y: e.clientY,
        scope: 'write',
        target: 'selection',
        addAnchorId: widgetId,
      });
    },
    [selectedWidgetIds, selectedWidgetId, selectWidget, composerWidgetSync.tree],
  );
  // 트리 행 우클릭 — 캔버스 위젯 우클릭과 동일 메뉴. 선택 소스만 'tree'(정확 선택 — isolation
  // 그룹 승격 우회: 트리 행은 정확히 그 위젯을 가리키는 조작이므로). 컨트롤 위 우클릭은 skip.
  const handleTreeWidgetContextMenu = useCallback(
    (widgetId: string, e: React.MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, select, button, [contenteditable="true"]')) return;
      e.preventDefault();
      e.stopPropagation();
      setFtFocusCell(null);
      if (!selectedWidgetIds.includes(widgetId) && selectedWidgetId !== widgetId) {
        selectWidget(widgetId, false, false, 'tree');
      }
      // 트리는 애초에 정확 선택이라 addAnchorId가 선택과 같지만, 다중 선택 중 다른 행을 우클릭한
      // 경우(선택 유지)에도 "가리킨 행 기준"으로 추가되도록 명시 전달.
      setCtxMenu({
        x: e.clientX,
        y: e.clientY,
        scope: 'write',
        target: 'selection',
        addAnchorId: widgetId,
      });
    },
    [selectedWidgetIds, selectedWidgetId, selectWidget],
  );
  // 추가 직후 자동 편집 진입 루틴 할당 — 정확 선택(트리 소스, isolation 승격 우회) + rc 편집 요청.
  // (선언 순서상 add 핸들러들이 selectWidget을 직접 참조할 수 없어 ref 경유 — 위 autoEditAfterAddRef.)
  useEffect(() => {
    autoEditAfterAddRef.current = (id: string) => {
      selectWidget(id, false, false, 'tree');
      setWtEditRequest({ id, nonce: Date.now() });
    };
    // 선택만 필요한 추가 경로(섹션/구분선/여백/표/등록형/모달 버튼) — 인라인 편집 없이 동일 정확 선택.
    selectAfterAddRef.current = (id: string) => {
      selectWidget(id, false, false, 'tree');
    };
  }, [selectWidget]);

  // 작성 탭 툴바 활성 조건 — 단축키/핸들러와 같은 판정을 재사용(새 로직 없이 값만 파생).
  // 묶기: ① 섹션 1개+위젯 선택(그 섹션에 포함) 또는 ② 2개+ 선택이 모두 같은 부모의 형제(새 섹션 묶기).
  const writeFormCanGroup = useMemo(() => {
    const ids = selectedWidgetIds;
    if (ids.length < 1) return false;
    const tree = composerWidgetSync.tree;
    // 단일 선택 = 새 섹션으로 감싸기(2026-07-16) — 트리에 배치된 위젯이면 가능.
    if (ids.length === 1) return widgetParentId(tree, ids[0]) !== undefined;
    if (ids.filter((id) => selectedWidgetById(tree, id)?.kind === 'group').length === 1)
      return true; // 포함 모드(cross-parent 허용)
    const parent0 = widgetParentId(tree, ids[0]);
    if (parent0 === undefined) return false; // 트리에 없음
    return ids.every((id) => widgetParentId(tree, id) === parent0);
  }, [selectedWidgetIds, composerWidgetSync.tree]);
  // 해제: 단일 선택이 섹션(group)일 때(handleWidgetUngroupSelected와 동일 판정).
  const writeFormIsGroupSelected = useMemo(() => {
    if (selectedWidgetIds.length !== 1) return false;
    const w = selectedWidgetById(composerWidgetSync.tree, selectedWidgetIds[0]);
    return !!w && w.kind === 'group';
  }, [selectedWidgetIds, composerWidgetSync.tree]);

  // ── P2a: 결재란 시뮬레이션 미리보기 ──
  // template.approvalLineMeta(템플릿 내장 결재라인 구조)가 있으면, 실데이터 없이도

  // (writeForm 오버라이드 편집은 composerWidgetTree 정본화로 대체 — 속성창이 InputWidget을 직접 편집.
  //  2026-10-02 S6으로 **flat 축이 철거됐다** — `WriteFormNode`·`ReportTemplate.writeForm`·admin
  //  `mergeWriteForm`이 전부 삭제됐고, 남은 소비는 파생(`derivedWriteForm` = `InputWidget[]`)뿐이다.
  //  DB `write_form` 컬럼도 S7(`20261002100000_drop_report_template_write_form`)에 drop됐다 — 이 축은
  //  타입·코드·컬럼 전부 사라졌으니 되살리지 말 것.)

  // 조립형 데이터 소스(dataSources) 삭제 — 참조(반복영역 over/바인딩/집계)가 있으면 확인 모달 후 삭제.
  // over/binding은 남겨 "(알 수 없음)" 표시 — 다른 소스로 재지정하거나 참조 정리로 해소.
  const handleDeleteDataSource = useCallback(
    (key: string) => {
      const usage = countDataSourceUsage(template, key);
      const doDelete = () =>
        apply((t) => ({
          ...t,
          dataSources: (t.dataSources ?? []).filter((d) => d.key !== key),
        }));
      if (usage > 0) {
        setConfirmState({
          title: '조립 데이터 소스 삭제',
          body: `${usage}곳이 '${key}'를 참조 중입니다. 삭제하면 해당 참조는 "(알 수 없음)" 소스가 되어 다른 소스로 재지정해야 합니다.`,
          confirmLabel: '삭제',
          danger: true,
          onConfirm: () => {
            setConfirmState(null);
            doDelete();
          },
        });
        return;
      }
      doDelete();
    },
    [apply, template],
  );

  // 데이터셋(dataDefs) 삭제 — 통합 "데이터" 섹션에서 조립 소스와 같은 확인 게이트를 쓴다.
  // 한 목록으로 보이는 카드가 삭제만 다르게 동작하면(한쪽만 경고) 함정이 되므로 맞춘다.
  const handleDeleteDataDef = useCallback(
    (key: string) => {
      const usage = countDataSourceUsage(template, key);
      const doDelete = () =>
        apply((t) => ({ ...t, dataDefs: (t.dataDefs ?? []).filter((d) => d.key !== key) }));
      if (usage > 0) {
        setConfirmState({
          title: '데이터 삭제',
          body: `${usage}곳이 '${key}'를 참조 중입니다. 삭제하면 해당 참조는 "(알 수 없음)" 소스가 되어 다른 소스로 재지정해야 합니다.`,
          confirmLabel: '삭제',
          danger: true,
          onConfirm: () => {
            setConfirmState(null);
            doDelete();
          },
        });
        return;
      }
      doDelete();
    },
    [apply, template],
  );

  // 조립 데이터 소스 사용처 수(속성 패널 "사용처 N곳" 배지·삭제 확인) — 선언별 카운트.
  const dataSourceUsage = useMemo(() => {
    const m = new Map<string, number>();
    for (const ds of template.dataSources ?? []) m.set(ds.key, countDataSourceUsage(template, ds.key));
    return m;
  }, [template]);

  // 조립 데이터 소스 key 충돌 검사 대상 = dataDefs key.
  const dataSourceReservedKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const d of template.dataDefs ?? []) keys.add(d.key);
    return keys;
  }, [template.dataDefs]);

  // 데이터셋(dataDefs) 사용처 수 — 통합 "데이터" 섹션에서 조립 소스와 같은 카운터를 쓴다.
  // countDataSourceUsage는 key만 보므로(over/바인딩/집계/compute 입력) 두 저장 배열에 그대로 통한다.
  const dataDefUsage = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of template.dataDefs ?? []) m.set(d.key, countDataSourceUsage(template, d.key));
    return m;
  }, [template]);

  // 데이터셋 key 충돌 검사 대상 = 조립 데이터 소스 key(위 dataSourceReservedKeys의 역방향).
  // 두 배열이 한 목록으로 보이므로 충돌 검사도 양방향이어야 한다(2026-07-26 표면 통합).
  const dataDefReservedKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const d of template.dataSources ?? []) keys.add(d.key);
    return keys;
  }, [template.dataSources]);

  // 양식 전역 변수 참조 조망 — 변수 편집기의 "참조하는 곳 N곳" 한 줄 + 삭제 게이트.
  // 리네임/삭제가 지정을 부순 **뒤에야** 경고가 뜨던 것을 사전 인지로 바꾸는 축이다
  // (결정 2026-08-10-composer-system-query-terminology §4).
  // 라벨에는 raw key가 아니라 조회 이름을 싣는다 — 저작자가 읽는 것은 목록에 보이는 그 이름이다.
  const variableRefs = useMemo(
    () => collectVariableConsumers(template, (key) => catalog.find((e) => e.key === key)?.label),
    [template, catalog],
  );

  // 전역 변수 삭제 — 참조가 있으면 파급(형태별 내역)을 보인 뒤 확인. dataSources/dataDefs 삭제와
  // 같은 게이트를 쓴다(한 패널의 삭제가 서로 다르게 동작하면 그게 함정이다).
  const handleDeleteVariable = useCallback(
    (index: number) => {
      const target = (template.variables ?? [])[index];
      if (!target) return;
      const refs = variableRefs.get(target.key);
      const doDelete = () =>
        apply((t) => ({ ...t, variables: (t.variables ?? []).filter((_, i) => i !== index) }));
      if (variableRefTotal(refs) > 0) {
        setConfirmState({
          title: '전역 변수 삭제',
          body: variableDeleteConfirmBody(target.key, refs),
          confirmLabel: '삭제',
          danger: true,
          onConfirm: () => {
            setConfirmState(null);
            doDelete();
          },
        });
        return;
      }
      doDelete();
    },
    [apply, template.variables, variableRefs],
  );

  // 조립 데이터 소스(dataSources) 필드 키 rename — DataSourcesEditor 필드 키 편집(blur/Enter 확정)이 호출.
  // 선언 필드 키 + 직접 반복 표 셀(over===dsKey)을 한 스텝으로 rename(정본-파생 동기).
  // 확정 후 동기 건수 + format 경로 경고를 토스트로 안내.
  const handleRenameDataSourceField = useCallback(
    (params: { dsKey: string; oldField: string; newField: string }) => {
      const { dsKey, oldField, newField } = params;
      if (oldField === newField) return;
      // 동기 건수 사전 집계(적용 전 template 기준) — 직접 반복 표 셀 N + format 경로 경고.
      let cellCount = 0;
      let formatWarn = false;
      walkNodeTree(template.body, (n) => {
        if (n.kind !== 'table') return;
        if (!(n.repeatRegions ?? []).some((r) => r.over === dsKey)) return;
        for (const c of n.cells) {
          if (c.binding && c.binding.bindingKey === oldField) cellCount += 1;
          // format 문자열(template/itemTemplate/switch on·cases 등) 안의 경로 `{oldField.…}`는
          // 자동 rewrite 대상이 아니므로 감지만 해 경고(보수적: 직렬화 문자열 부분 일치).
          if (c.binding?.format) {
            const s = JSON.stringify(c.binding.format);
            if (s.includes(`{${oldField}.`) || s.includes(`'${oldField}.`)) formatWarn = true;
          }
        }
      });
      apply((t) => renameDataSourceFieldEverywhere(t, params));
      const summary = `조립 필드 키 '${oldField}' → '${newField}' — 셀 ${cellCount} 동기`;
      if (formatWarn) {
        showToast(
          `${summary} (경고: 일부 셀 format 경로 '{${oldField}.…}'는 자동 변경되지 않으니 수동 수정하세요)`,
          'info',
        );
      } else {
        showToast(summary, 'success');
      }
    },
    [apply, template.body, showToast],
  );

  // 데이터 소스 패널의 필드 삭제 — 선언만 지우면 표에 **선언 없는 키를 가리키는 바인딩**이
  // 남는다(같은 패널의 rename은 셀까지 전파하는데 삭제만 안 했다). 파급 범위를 rename과 맞추고,
  // 배치된 셀이 있으면 무엇이 함께 풀리는지 알린 뒤 진행한다.
  const handleDeleteDataSourceField = useCallback(
    (params: { dsKey: string; field: string }) => {
      const { dsKey, field } = params;
      const cellCount = countDataSourceFieldCells(template, dsKey, field);
      const doDelete = () => apply((t) => deleteDataSourceFieldEverywhere(t, params));
      if (cellCount === 0) {
        doDelete();
        return;
      }
      setConfirmState({
        title: '배치된 칼럼 삭제',
        body:
          `'${field}' 칼럼은 표 ${cellCount}곳에 배치돼 있습니다. ` +
          '삭제하면 그 셀의 바인딩도 함께 풀립니다(셀 자체와 손저작 내용은 남습니다).',
        confirmLabel: '삭제',
        danger: true,
        onConfirm: () => {
          setConfirmState(null);
          doDelete();
        },
      });
    },
    [apply, template],
  );

  // ── 조립/수동 데이터 소스(dataSources) over 영역의 칼럼 편집 (동적 데이터셋 통합 D3) ──
  // 정본=셀 바인딩, 선언(dataSourceDef.fields)은 자동 동기. rename은 위
  // handleRenameDataSourceField(renameDataSourceFieldEverywhere)를 재사용한다.

  // "＋ 새 동적 데이터셋(빈)…" — dataSources에 kind:'manual' 선언을 추가한다.
  // setOver 있으면 그 반복영역 over까지 같은 apply(=undo 1스텝)로 지정.
  const handleCreateManualDataSource = useCallback(
    (params: { key: string; setOver?: { tableId: string; regionIndex: number } }) => {
      const { key, setOver } = params;
      apply((t) => {
        const def: DataSourceDef = {
          key,
          label: key,
          cardinality: 'list',
          kind: 'manual',
          primary: '',
          fields: [],
          editMode: 'editable',
          allowAddRemove: true,
        };
        let next: ReportTemplate = { ...t, dataSources: [...(t.dataSources ?? []), def] };
        if (setOver) {
          next = updateRepeatRegion(next, setOver.tableId, setOver.regionIndex, { over: key });
        }
        return next;
      });
    },
    [apply],
  );

  // "＋칼럼 추가"(dataSource) — 셀 바인딩 + 선언 fields append + 영역 칼럼범위 재계산을 한 스텝으로.
  const handleAddDataSourceColumn = useCallback(
    (params: {
      tableId: string;
      regionIndex: number;
      row: number;
      col: number;
      dsKey: string;
      field: string;
      label: string;
      lifecycle: BindingLifecycle;
    }) => {
      apply((t) => addDataSourceColumn(t, params));
    },
    [apply],
  );

  /**
   * 「소스 편집 →」이 지목한 데이터 선언 key(null=요청 없음). DataSection이 처리 후 비운다.
   *
   * 펼침 상태(카드 index Set)를 DataSection이 소유하므로 여기서 카드를 직접 열 수 없다 — 대신
   * "이 key로 가라"는 요청만 내려보내고 해소(key→index)·펼침·스크롤은 그 섹션이 한다.
   */
  const [dataCardReveal, setDataCardReveal] = useState<string | null>(null);

  /**
   * 반복영역/반복 노드의 소스 → 그 소스를 **고치는 자리**(데이터 섹션 카드)로 이동.
   *
   * 데이터 섹션은 선택이 없을 때만 그려지는 전역 패널이라 스크롤 전에 선택을 먼저 비워야 한다
   * (handleGoToIssue와 같은 이유·같은 순서). viewMode 지정은 지금은 무동작이다 — 이 버튼은 보고서
   * 탭 속성 패널에만 있다. 그래도 남기는 것은 목적지가 그 탭이라는 사실을 호출부가 보장하게 해
   * 나중에 작성 탭에서 같은 버튼을 달아도 이동이 깨지지 않게 하기 위함이다.
   */
  const handleGoToDataCard = useCallback(
    (key: string) => {
      setViewMode('form');
      setSelectedCells([]);
      setSelectedChrome(null);
      selectSingleNode(null);
      setDataCardReveal(key);
    },
    [selectSingleNode],
  );

  // 데이터/변수 섹션은 **보고서 탭 전역 패널과 작성 탭 전역 코어 두 곳**에서 같은 상태를 같은
  // 컴포넌트로 편집한다(사본·재구현 아님). props를 두 벌 나열하면 한쪽만 고치는 드리프트가 나므로
  // 한 객체로 묶어 spread한다 — 새 prop도 여기 한 곳에만 추가하면 양쪽이 함께 얻는다.
  const dataSectionProps = {
    dataDefs: template.dataDefs,
    dataSources: template.dataSources,
    catalog,
    dataDefUsage,
    dataSourceUsage,
    dataDefReservedKeys,
    dataSourceReservedKeys,
    tablePlacements: (key: string) => countTablePlacements(template.composerWidgetTree, key),
    onChangeDataDefs: (next: DataDef[]) => apply((t) => ({ ...t, dataDefs: next })),
    onChangeDataSources: (next: DataSourceDef[]) => apply((t) => ({ ...t, dataSources: next })),
    onRenameDataSourceField: handleRenameDataSourceField,
    onDeleteDataSourceField: handleDeleteDataSourceField,
    onDeleteDataDef: handleDeleteDataDef,
    onDeleteDataSource: handleDeleteDataSource,
    hasPreviewContext,
    onDryRun: runDataSourceDryRun,
    onFetchCatalogRows: fetchCatalogRowsForImport,
    revealCardKey: dataCardReveal,
    onRevealHandled: () => setDataCardReveal(null),
  };
  const variablesEditorProps = {
    variables: template.variables,
    catalog,
    variableRefs,
    onDelete: handleDeleteVariable,
    onChange: (next: TemplateVariable[]) => apply((t) => ({ ...t, variables: next })),
  };

  // 칼럼 ↔ 셀 매핑 해제 — 선언은 그대로 두고 바인딩만 푼다(삭제와 달리 되돌릴 수 있다).
  const handleUnmapDataSourceColumn = useCallback(
    (params: { tableId: string; field: string }) => {
      apply((t) => unmapDataSourceColumn(t, params));
    },
    [apply],
  );

  // 칼럼 삭제(dataSource) — 셀 바인딩 해제 + 선언 fields 제거를 한 스텝으로.
  //
  // ⚠️ 조립형(assembled) 필드는 `from`(주 소스 경로)이 이 패널에 안 보인다 — 그 칸은 전역 속성의
  // **데이터 소스 패널**(DataSourceFromEditor)에 있다. 그래서 여기서 삭제하면 '＋칼럼 추가'로
  // 다시 넣어도 `from: ''`인 빈 필드가 생기고, 저작자는 어디서 다시 이으라는 건지 알 수 없다
  // (2026-08-11 사용자 제보). 되돌리기 자체는 실행 취소(Ctrl+Z)와 데이터 소스 패널로 가능하니,
  // 확인 모달은 "못 되돌린다"가 아니라 **어디서 되돌리는지**를 알려 준다.
  const handleDeleteDataSourceColumn = useCallback(
    (params: { tableId: string; dsKey: string; field: string }) => {
      const doDelete = () => apply((t) => deleteDataSourceColumn(t, params));
      const def = (template.dataSources ?? []).find((d) => d.key === params.dsKey);
      const field = (def?.fields ?? []).find((f) => f.field === params.field);
      // 확인이 필요한 조건: 조립형 + from이 실제 경로(상수/빈 문자열은 잃을 연결이 없다).
      const from = field?.from;
      const losesLookup =
        def?.kind === 'assembled' && typeof from === 'string' && from.trim() !== '';
      if (!losesLookup) {
        doDelete();
        return;
      }
      setConfirmState({
        title: '조회 연결 칼럼 삭제',
        body:
          `'${field?.label ?? params.field}' 칼럼은 시스템 조회 값(${from})에 연결돼 있습니다. ` +
          '삭제하면 이 연결이 사라집니다 — 실행 취소(Ctrl+Z)로 되돌리거나, ' +
          '"＋칼럼 추가 > 조회 경로에서 새 칼럼"으로 다시 만들 수 있습니다' +
          '(다시 만들면 칼럼 키·작성폼 설정은 기본값으로 돌아갑니다). ' +
          '셀 매핑만 풀 생각이라면 삭제 대신 "해제"를 쓰세요.',
        confirmLabel: '삭제',
        danger: true,
        onConfirm: () => {
          setConfirmState(null);
          doDelete();
        },
      });
    },
    [apply, template],
  );

  // 칼럼 순서 이동(dataSource) — 선언 fields 순서만. 무변경 시 참조 유지.
  const handleMoveDataSourceColumn = useCallback(
    (params: { dsKey: string; field: string; dir: -1 | 1 }) => {
      apply((t) => moveDataSourceColumn(t, params));
    },
    [apply],
  );

  // (오버레이 재측정 tick은 CanvasViewport 내부에서 zoom/remeasureDep(=template) 기준으로 처리한다.)

  // group id → direction 조회(드롭존 삽입선 방향 결정에 사용).
  const groupDirectionOf = useCallback(
    (groupId: string): 'row' | 'column' => {
      const n = findNodeById(template.body, groupId);
      return n && n.kind === 'group' && n.direction === 'row' ? 'row' : 'column';
    },
    [template],
  );

  // 셀 드래그 범위 선택(엑셀/HWP식) — CanvasViewport가 셀 위 mousedown→threshold 초과 이동을
  // 감지해 콜백한다. 드래그 시작 시 anchor 셀을 단일 선택하고, 이동 중엔 computeCellRange로
  // anchor~target 직사각 범위(병합 슬롯 제외, anchor 선두)를 selectedCells로 갱신한다.
  const handleCellDragStart = useCallback((anchor: CellRef) => {
    setEditingTarget(null); // 드래그 선택 시작 시 인라인 편집 종료(멱등).
    setCaptureEditing(false);
    selectSingleNode(anchor.tableId);
    rangeFocusRef.current = anchor; // 드래그 시작점이 확장 끝점 초기값.
    setSelectedCells([anchor]);
  }, [selectSingleNode]);
  const handleCellDragMove = useCallback(
    (anchor: CellRef, target: CellRef) => {
      const tableNode = findNodeById(template.body, anchor.tableId);
      if (!tableNode || tableNode.kind !== 'table') return;
      rangeFocusRef.current = target; // 드래그 끝점 → Shift+화살표가 이어받을 focus.
      setSelectedCells(computeCellRange(tableNode, anchor, target));
    },
    [template],
  );

  // 노드(표/그룹/텍스트) ctrl/meta 클릭 토글 — Phase B의 형제 다중선택 규칙을 헬퍼로 추출.
  // 같은 부모 형제면 selectedNodeIds에서 clicked를 토글(추가/제거, 최소 1개 유지, 제거 시 anchor 이동),
  // 부모가 다르거나 anchor가 없으면 새 단일 선택. 셀 선택은 항상 해제(셀↔노드 상호 배타).
  // 노드 분기와 셀 분기(타 표 편입) 양쪽에서 호출 — 동작을 한 곳으로 일원화한다.
  const toggleNodeInSelection = useCallback(
    (clickedNodeId: string) => {
      const anchor = selectedNodeId;
      const clickedParent = parentIdOf(template.body, clickedNodeId);
      const anchorParent = anchor != null ? parentIdOf(template.body, anchor) : undefined;
      // 같은 부모 형제일 때만 토글(null 체크를 인라인해 anchor를 string으로 좁힘).
      if (anchor != null && clickedParent !== undefined && anchorParent === clickedParent) {
        // 집합에서 clicked 토글. base는 현재 집합(비면 anchor 단독).
        const base = selectedNodeIds.length > 0 ? selectedNodeIds : [anchor];
        let next: string[];
        let nextAnchor: string;
        if (base.includes(clickedNodeId)) {
          if (base.length <= 1) {
            // 유일 선택 → 최소 1개 유지(해제하지 않음).
            next = base;
            nextAnchor = clickedNodeId;
          } else {
            next = base.filter((id) => id !== clickedNodeId);
            // 제거 대상이 anchor였으면 anchor를 남은 마지막 항목으로(불변식 유지).
            nextAnchor = clickedNodeId === anchor ? next[next.length - 1] : anchor;
          }
        } else {
          next = [...base, clickedNodeId];
          nextAnchor = clickedNodeId; // anchor = 마지막 클릭.
        }
        setSelectedCells([]);
        setSelectedNodeIds(next);
        setSelectedNodeId(nextAnchor);
        return;
      }
      // 부모가 다르거나 anchor 없음 → 새 단일 선택 시작.
      setSelectedCells([]);
      selectSingleNode(clickedNodeId);
    },
    [selectedNodeId, selectedNodeIds, template, selectSingleNode],
  );

  // 노드 Shift+클릭 범위 선택 — anchor(현재 selectedNodeId)~clicked "사이"의 형제 전체 선택.
  // 깊이가 달라도 **최근접 공통 컨테이너(LCA)의 직계 자식으로 양쪽을 승격**해 그 형제 축에서 범위
  // 선택(2026-07-14 결정: canvas-modifier-click-cross-depth — 작성 페이지와 동일 규약). 셀/branch
  // slot/repeat 템플릿 경계는 그 표/branch/템플릿 노드로 승격해 계속 오른다. 조상-자손 관계는 조상
  // 단독 선택으로 수렴. 앵커 유지(반복 Shift+클릭 재범위, 파일 탐색기/Figma식). 셀 선택은 항상 해제.
  const rangeSelectToNode = useCallback(
    (clickedNodeId: string) => {
      const anchor = selectedNodeId;
      if (anchor == null || anchor === clickedNodeId) {
        setSelectedCells([]);
        selectSingleNode(clickedNodeId);
        return;
      }
      // 자기 자신부터 body까지 { 승격 단위, 컨테이너 키 } 체인. 미발견이면 null(토글 폴백).
      const chainOf = (id: string): { unit: string; key: string }[] | null => {
        const out: { unit: string; key: string }[] = [];
        let cur = id;
        for (;;) {
          const loc = findNodeContainer(template.body, cur);
          if (!loc) return null;
          const key =
            loc.kind === 'body'
              ? 'body'
              : loc.kind === 'group'
                ? `g:${loc.parentId}`
                : loc.kind === 'branch-slot'
                  ? `s:${loc.branchId}:${loc.slotId}`
                  : loc.kind === 'repeat-template'
                    ? `t:${loc.templateId}`
                    : `c:${loc.tableId}:${loc.row}:${loc.col}`;
          out.push({ unit: cur, key });
          if (loc.kind === 'body') return out;
          cur =
            loc.kind === 'group'
              ? loc.parentId
              : loc.kind === 'branch-slot'
                ? loc.branchId
                : loc.kind === 'repeat-template'
                  ? loc.templateId
                  : loc.tableId;
        }
      };
      const aChain = chainOf(anchor);
      const cChain = chainOf(clickedNodeId);
      if (!aChain || !cChain) {
        toggleNodeInSelection(clickedNodeId);
        return;
      }
      const cUnitByKey = new Map<string, string>();
      for (const e of cChain) if (!cUnitByKey.has(e.key)) cUnitByKey.set(e.key, e.unit);
      let au: string | null = null;
      let cu: string | null = null;
      for (const e of aChain) {
        const c = cUnitByKey.get(e.key);
        if (c !== undefined) {
          au = e.unit;
          cu = c;
          break;
        }
      }
      if (au == null || cu == null) {
        toggleNodeInSelection(clickedNodeId);
        return;
      }
      const siblings = containerSiblingIds(template.body, findNodeContainer(template.body, au));
      const ai = siblings.indexOf(au);
      const ci = siblings.indexOf(cu);
      if (ai < 0 || ci < 0) {
        toggleNodeInSelection(clickedNodeId);
        return;
      }
      const [lo, hi] = ai <= ci ? [ai, ci] : [ci, ai];
      setSelectedCells([]);
      setSelectedNodeIds(siblings.slice(lo, hi + 1));
      setSelectedNodeId(au); // 앵커 유지(승격 단위) — 반복 Shift+클릭이 같은 앵커에서 재범위.
    },
    [selectedNodeId, template.body, toggleNodeInSelection, selectSingleNode],
  );

  // 캔버스 클릭 위임 — 셀 클릭(data-composer-cell)을 우선 처리하고, 아니면 가장 가까운
  // data-composer-node-id 노드를 선택한다.
  // - plain: 단일 셀 선택.
  // - shift+click: 같은 표면 anchor(selectedCells[0])~clicked 직사각 범위 내 *실제 존재하는* 셀 전체.
  // - ctrl/meta+click: 같은 표면 clicked 셀 토글(있으면 제거, 없으면 추가). 다른 표 셀이면
  //   셀 선택을 접고 표(노드) 다중선택으로 편입(toggleNodeInSelection).
  // - 드래그: 위 handleCellDrag* 콜백(엑셀식 직사각 범위).
  // 인라인 편집 진입 헬퍼(리뷰 W4) — 셀/텍스트 편집 진입 로직이 모드·경로별(deepest/isolation, 클릭/더블클릭)로
  // 흩어져 whack-a-mole 되지 않게 일원화. 진입 가드(정적/빈 셀 여부 등)는 각 호출부에 유지한다.
  const enterNodeTextEdit = (id: string) => {
    setSelectedCells([]);
    selectSingleNode(id);
    setEditingTarget({ kind: 'node', id });
  };
  const enterCellTextEdit = (cellRef: { tableId: string; row: number; col: number }) => {
    selectSingleNode(cellRef.tableId);
    setSelectedCells([cellRef]);
    setEditingTarget({ kind: 'cell', tableId: cellRef.tableId, row: cellRef.row, col: cellRef.col });
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // R3 — 셀 선택 픽 모드: 활성 시 클릭한 셀/노드의 바인딩을 계산 입력으로 복사하고 픽 종료.
    // 일반 선택/편집 로직을 전혀 타지 않도록 여기서 return(비활성일 땐 분기 자체를 건너뛰어 기존 동작 동일).
    if (cellPick) {
      // 좌표 픽 모드(반복영역 칼럼↔셀 매핑) — 클릭 셀의 ref를 그대로 apply에 넘기고 픽 종료.
      // 바인딩 유무 무관(빈 셀도 허용). 셀이 아닌 곳을 누르면 픽을 유지하고 안내만 한다.
      if (cellPick.pickKind === 'cell') {
        // 채움 행 갈래 — onFillRow를 준 픽만. 채움 tr(data-composer-fill-row=tableId)은 셀 dataset이
        // 없어 아래 셀 해소에서 null로 떨어지므로, 셀보다 **먼저** 본다. 중첩 표(바깥 셀 안의 표)의
        // 채움 행을 눌러도 closest는 가장 안쪽 tr을 주고 그 tableId가 대상과 다르면 여기서 걸린다.
        if (cellPick.onFillRow) {
          const fillTr = (e.target as HTMLElement).closest<HTMLElement>(
            'tr[data-composer-fill-row]',
          );
          const fillTableId = fillTr?.dataset.composerFillRow ?? null;
          if (fillTr && fillTableId) {
            if (cellPick.tableId && fillTableId !== cellPick.tableId) {
              showToast('같은 표의 행을 지정하세요.', 'error');
              return;
            }
            // 순번(1-based) — 같은 표의 채움 tr 중 몇 번째인가. 채움 행은 표 본문 뒤에 연속으로
            // 붙으므로 순번이 곧 "표 끝에서 몇 칸 아래"다(가상 행 번호 환산은 호출부 몫).
            const tableEl = fillTr.closest('table');
            const siblings = tableEl
              ? Array.from(
                  tableEl.querySelectorAll<HTMLElement>('tr[data-composer-fill-row]'),
                ).filter((tr) => tr.dataset.composerFillRow === fillTableId)
              : [];
            const ordinal = siblings.indexOf(fillTr) + 1;
            if (ordinal > 0) {
              // 픽 상태는 먼저 비운다 — 콜백이 다음 단계 픽을 arm하면 그것이 최종 상태가 된다
              // (뒤에 setCellPick(null)을 두면 새 픽이 곧바로 지워진다).
              setCellPick(null);
              cellPick.onFillRow(ordinal);
              return;
            }
          }
        }
        const cellEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-cell]');
        const ref = cellEl ? parseCellRef(cellEl.dataset.composerCell ?? '') : null;
        if (ref) {
          // 같은 표 제약: 다른 표 셀이면 거부(픽 유지).
          if (cellPick.tableId && ref.tableId !== cellPick.tableId) {
            showToast('같은 표의 셀을 지정하세요.', 'error');
            return;
          }
          // 행 밴드 제약: 반복 영역의 행 범위(rowStart~rowEnd) 안만 허용(밖이면 거부, 픽 유지).
          // 판정은 강조 오버레이와 같은 술어(rowWithinAnchorRows) — 보이는 범위 = 허용 범위.
          if (!rowWithinAnchorRows(ref.row, cellPick.anchorRows)) {
            showToast('이 반복 영역의 행 범위 안의 셀만 지정할 수 있습니다.', 'error');
            return;
          }
          // 비우기 → apply 순서(위 채움 행 갈래와 동일 이유): apply가 다음 단계 픽을 arm할 수 있다.
          setCellPick(null);
          cellPick.apply(ref);
        } else {
          showToast('표 안의 셀을 클릭하세요.', 'error');
        }
        return;
      }
      const cellEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-cell]');
      if (cellEl) {
        const ref = parseCellRef(cellEl.dataset.composerCell ?? '');
        if (ref) {
          const tableNode = findNodeById(template.body, ref.tableId);
          const binding =
            tableNode && tableNode.kind === 'table'
              ? tableNode.cells.find((c) => c.row === ref.row && c.col === ref.col)?.binding
              : undefined;
          if (binding) {
            cellPick.apply(binding);
            setCellPick(null);
          } else {
            showToast('바인딩이 없는 셀입니다. 값이 지정된 셀/항목을 클릭하세요.', 'error');
          }
          return;
        }
      }
      const nodeEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-node-id]');
      const pickNodeId = nodeEl?.dataset.composerNodeId ?? null;
      if (pickNodeId) {
        const node = findNodeById(template.body, pickNodeId);
        if (node?.binding) {
          cellPick.apply(node.binding);
          setCellPick(null);
        } else {
          showToast('바인딩이 없는 항목입니다. 값이 지정된 셀/항목을 클릭하세요.', 'error');
        }
        return;
      }
      // 빈 영역 클릭 — 픽 유지(취소는 ESC 또는 버튼 재클릭). 선택 로직은 타지 않는다.
      return;
    }
    // 단일 클릭(다른 곳 선택)은 인라인 편집을 종료한다. textarea blur로 이미 커밋된 경우엔
    // 멱등(이미 null). 더블클릭 시퀀스에선 dblclick 핸들러가 직후 다시 설정하므로 무해.
    if (editingTarget) {
      setEditingTarget(null);
      setCaptureEditing(false);
    }
    // 셀 안 content 노드를 직접 클릭했으면(그 노드의 data-composer-node-id가 셀 td 내부) 셀이 아니라
    // 그 노드를 선택한다 — 셀 content 편집(insertNodeIntoCell로 넣은 노드)의 캔버스 선택 경로.
    // 빈 셀/텍스트 셀은 td 자체가 타깃이라 closest 노드가 표(td의 조상)로 잡혀 이 분기를 타지 않고
    // 아래 셀 선택 로직으로 간다. 수식키(shift/ctrl/meta) 클릭은 body 노드와 동일하게
    // toggleNodeInSelection으로 — 같은 셀 형제만 집합 유지(합성 셀 부모 키), 아니면 단일 전환.
    // (수식키를 셀 분기로 흘리면 표/셀 선택이 노드 클릭을 가로채 셀 안 다중선택이 불가능해진다.)
    {
      const cellHost = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-cell]');
      const nodeHost = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-node-id]');
      const inCellNodeId = nodeHost?.dataset.composerNodeId;
      if (cellHost && nodeHost && inCellNodeId && cellHost.contains(nodeHost)) {
        // 셀 content 노드 직접 선택: 'deepest' 모드는 항상 즉시 선택(옛 동작). 'isolation'은 그 표가
        // 현재 선택 컨텍스트일 때만(= 더블클릭으로 표 안에 드릴한 뒤). 아직 표 밖 컨텍스트면 return하지
        // 않고 아래 일반 노드 로직으로 흘려보내 resolveSingleClickTarget이 표를 단위로 승격 선택하게 한다.
        const cellHostRef = parseCellRef(cellHost.dataset.composerCell ?? '');
        if (selectionMode === 'deepest' || (cellHostRef && selectionContextId === cellHostRef.tableId)) {
          if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
            rangeSelectToNode(inCellNodeId); // Shift=범위(같은 셀 content 형제)
            return;
          }
          if (e.ctrlKey || e.metaKey) {
            toggleNodeInSelection(inCellNodeId); // Ctrl/Cmd=개별 토글
            return;
          }
          setSelectedCells([]);
          selectSingleNode(inCellNodeId);
          return;
        }
      }
    }
    const cellEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-cell]');
    if (cellEl) {
      // `tableId::row::col` 파싱(parseCellRef). tableId 자체에 '::'가 없다는 전제(node id는 `${kind}-${n}` 형식).
      const clicked = parseCellRef(cellEl.dataset.composerCell ?? '');
      // 셀 선택: 'deepest' 모드는 항상 즉시 선택(옛 동작). 'isolation'은 그 표가 현재 선택 컨텍스트일
      // 때만(= 더블클릭으로 표 안에 드릴한 뒤). 아직 표 밖 컨텍스트면 return하지 않고 아래 일반 노드
      // 로직으로 흘려보내 표를 단위로 승격 선택.
      if (clicked && (selectionMode === 'deepest' || selectionContextId === clicked.tableId)) {
        const { tableId, row, col } = clicked;
        // Ctrl/Cmd+클릭이 현재 anchor 표 밖(또는 이미 노드 다중선택 중)을 향하면, 셀 선택이 아니라
        // 표(노드) 다중선택으로 편입한다. anchor 표 = selectedCells[0]?.tableId ?? selectedNodeId
        // (셀 선택 중이면 selectSingleNode로 selectedNodeId===그 표 id 불변식이 성립). 같은 표 안
        // Ctrl(셀 토글)·shift(범위)·plain 클릭은 아래 기존 로직 그대로.
        if (e.ctrlKey || e.metaKey) {
          const anchorTableId = selectedCells[0]?.tableId ?? selectedNodeId;
          const crossTable = anchorTableId != null && anchorTableId !== tableId;
          if (crossTable || selectedNodeIds.length > 1) {
            // toggleNodeInSelection이 셀 해제까지 처리(형제면 [현재표, 클릭표], 비형제면 클릭표 단일).
            toggleNodeInSelection(tableId);
            return;
          }
        }
        // Shift+클릭이 다른 표를 향하면 셀 범위가 아니라 표(노드) 범위 선택 — 앵커 표~클릭 표 사이 형제
        // 전체(Ctrl 교차선택과 대칭). 같은 표 안 Shift는 아래 셀 범위(computeCellRange)로 진행.
        if (e.shiftKey) {
          const anchorTableId = selectedCells[0]?.tableId ?? selectedNodeId;
          const crossTable = anchorTableId != null && anchorTableId !== tableId;
          if (crossTable || selectedNodeIds.length > 1) {
            rangeSelectToNode(tableId);
            return;
          }
        }
        // 재선택 = 편집 진입(파일명 바꾸기류): 편집 중 아님(editingTarget null)이고, plain
        // 클릭(shift/ctrl 아님)이며, 이미 정확히 그 한 셀만 선택된 상태이고, 정적 텍스트
        // 셀(content·binding 없음)이면 인라인 편집으로. (편집 중 다른 곳 클릭은 커밋/종료만.)
        if (!editingTarget && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
          const onlyThisCell =
            selectedCells.length === 1 &&
            selectedCells[0].tableId === tableId &&
            selectedCells[0].row === row &&
            selectedCells[0].col === col;
          if (onlyThisCell) {
            const tableNode = findNodeById(template.body, tableId);
            if (tableNode && tableNode.kind === 'table') {
              const cell = tableNode.cells.find((c) => c.row === row && c.col === col);
              if (cell && !cell.content && !cell.binding) {
                selectSingleNode(tableId);
                setSelectedCells([clicked]);
                setEditingTarget({ kind: 'cell', tableId, row, col });
                return;
              }
            }
          }
        }
        selectSingleNode(tableId);
        // 클릭 지점을 확장 끝점(focus)으로 기록 — 이후 Shift+화살표가 이 지점에서 이어 확장한다.
        // (shift+클릭 범위의 끝, plain 단일 클릭의 그 셀, ctrl 토글의 클릭 셀 모두 clicked 기준.)
        rangeFocusRef.current = clicked;
        setSelectedCells((prev) => {
          const anchor = prev[0];
          const sameTable = anchor && anchor.tableId === tableId;
          if (e.shiftKey && sameTable) {
            // anchor~clicked 직사각 범위(computeCellRange) — merged로 덮인 슬롯은 cells에 없어 자동 제외, anchor 선두.
            const tableNode = findNodeById(template.body, tableId);
            if (tableNode && tableNode.kind === 'table') {
              return computeCellRange(tableNode, anchor, clicked);
            }
          }
          if ((e.ctrlKey || e.metaKey) && sameTable) {
            const exists = prev.some((c) => c.row === row && c.col === col);
            if (exists) {
              return prev.filter((c) => !(c.row === row && c.col === col));
            }
            return [...prev, clicked];
          }
          // plain(또는 다른 표) → 단일 선택.
          return [clicked];
        });
        return;
      }
    }
    // 일반 노드 클릭. deepestId = 클릭이 실제 적중한 최심부 노드.
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-node-id]');
    const deepestId = target?.dataset.composerNodeId ?? null;

    if (selectionMode === 'deepest') {
      // 최심부 우선(이전 복원) — 승격 없이 최심부 노드를 직접 선택.
      //  Shift=앵커~클릭 사이 형제 범위, Ctrl/Cmd=개별 토글(같은 부모 형제끼리만). 편집 중이면 스킵.
      if (deepestId && !editingTarget && e.shiftKey && !e.ctrlKey && !e.metaKey) {
        rangeSelectToNode(deepestId);
        return;
      }
      if (deepestId && !editingTarget && (e.ctrlKey || e.metaKey)) {
        toggleNodeInSelection(deepestId);
        return;
      }
      // 재선택 = 편집 진입(파일명 바꾸기류): 편집 중 아님(editingTarget null)이고, plain 클릭이며,
      // 이미 그 노드만 단독 선택된 상태(selectedNodeId===id && 셀 선택 없음)이고, text 노드면 인라인 편집으로.
      if (deepestId && !editingTarget && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        const onlyThisNode =
          selectedNodeId === deepestId && selectedCells.length === 0 && selectedNodeIds.length <= 1;
        if (onlyThisNode) {
          const node = findNodeById(template.body, deepestId);
          if (node && node.kind === 'text') {
            enterNodeTextEdit(deepestId);
            return;
          }
        }
      }
      setSelectedCells([]);
      selectSingleNode(deepestId); // deepestId null이면 선택 해제(빈 캔버스 클릭 — 컨텍스트 개념 없음).
      return;
    }

    // group isolation 승격(기본).
    //  - plain/Shift/Ctrl 모두 현재 컨텍스트 직속 자식까지 승격(resolveSingleClickTarget)한 id로 동작:
    //    plain=단일 선택, Shift=형제 범위선택, Ctrl/Cmd=형제 개별 토글(비연속 다중선택). 셋 다 같은
    //    컨텍스트 레벨의 형제끼리라 다중선택→그룹화가 일관되게 성립. (최심부 직접 선택이 필요하면 더블클릭 드릴.)
    // (isolation에서는 "선택된 text 재클릭=인라인 편집" 없음 — 편집은 더블클릭 드릴이 리프 text에 닿을 때만.)
    //
    // 컨텍스트 밖(다른 최상위 서브트리) 클릭이면 selectionContextId를 리셋한다 — 안 하면 stale 컨텍스트가
    // 남아 이후 Esc가 무관한 옛 컨테이너로 선택을 점프시킨다(리뷰 W1). resolveSingleClickTarget은 컨텍스트
    // 이탈 시 최상위를 반환하므로 이번 클릭의 승격 결과는 리셋 여부와 무관하게 동일하다.
    if (deepestId && selectionContextId !== null) {
      const chain = getAncestorChain(buildParentMap(template.body), deepestId);
      if (!chain.includes(selectionContextId)) setSelectionContextId(null);
    }
    const promoted =
      deepestId !== null
        ? resolveSingleClickTarget(template.body, deepestId, selectionContextId)
        : null;
    if (promoted && !editingTarget && e.shiftKey && !e.ctrlKey && !e.metaKey) {
      rangeSelectToNode(promoted);
      return;
    }
    if (promoted && !editingTarget && (e.ctrlKey || e.metaKey)) {
      // 컨텍스트 형제 레벨로 승격 후 개별 토글(단일/Shift와 동일 레벨 — 비연속 형제 다중선택).
      toggleNodeInSelection(promoted);
      return;
    }
    if (promoted) {
      setSelectedCells([]);
      selectSingleNode(promoted);
      return;
    }
    // 빈 캔버스(노드/셀 아님) 클릭 — 컨텍스트·선택 모두 body 레벨로 리셋.
    setSelectionContextId(null);
    setSelectedCells([]);
    selectSingleNode(null);
  };

  // 양식 탭 캔버스 우클릭 — 클릭과 동일한 선택 해석(isolation 승격 + stale 컨텍스트 리셋)으로
  // 대상을 보정한 뒤 컨텍스트 메뉴를 연다. 이미 선택 집합 안이면 다중 선택 유지(탐색기 관례).
  // 셀 선택은 해제(v1 메뉴는 노드 단위 — 셀 전용 항목은 후속), 셀 위 우클릭은 그 표 노드가 대상.
  // 입력/인라인 편집 요소 위는 브라우저 기본 메뉴 유지(텍스트 복사 등), 픽 모드 중엔 미개입.
  const handleCanvasContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    if (t.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (cellPick) return;
    e.preventDefault();
    const nodeEl = t.closest<HTMLElement>('[data-composer-node-id]');
    const cellEl = t.closest<HTMLElement>('[data-composer-cell]');
    const deepestId =
      nodeEl?.dataset.composerNodeId ??
      (cellEl ? (parseCellRef(cellEl.dataset.composerCell ?? '')?.tableId ?? null) : null);
    if (deepestId) {
      const ancestorChain = getAncestorChain(buildParentMap(template.body), deepestId);
      // 컨텍스트 밖 우클릭이면 stale 컨텍스트 리셋(handleCanvasClick 승격 경로와 동일).
      if (selectionContextId !== null && !ancestorChain.includes(selectionContextId)) {
        setSelectionContextId(null);
      }
      const promoted =
        selectionMode === 'deepest'
          ? deepestId
          : resolveSingleClickTarget(template.body, deepestId, selectionContextId);
      // 원시 대상(승격 전 셀/노드) 기준 "위젯으로 이동" 대상 캡처 — 승격이 그룹으로 선택을 바꾸고
      // 셀 선택도 해제하므로, 승격 후 선택 기반 판정으로는 바인딩 위 우클릭이 비활성 오판(2026-07-16 제보).
      const formNavWidgetId = (() => {
        let bindingKey: string | undefined;
        const cellRef = cellEl ? parseCellRef(cellEl.dataset.composerCell ?? '') : null;
        if (cellRef) {
          const tnode = findNodeById(template.body, cellRef.tableId);
          if (tnode && tnode.kind === 'table') {
            bindingKey = tnode.cells.find(
              (c) => c.row === cellRef.row && c.col === cellRef.col,
            )?.binding?.bindingKey;
            // 셀 직접 바인딩이 없으면 그 행을 포함하는 반복영역의 행 소스 키(행 목록 위젯).
            if (!bindingKey) {
              bindingKey = tnode.repeatRegions?.find(
                (r) => cellRef.row >= r.rowStart && cellRef.row <= r.rowEnd,
              )?.over;
            }
          }
        }
        if (!bindingKey) {
          const node = findNodeById(template.body, deepestId);
          if (node?.binding?.bindingKey) bindingKey = node.binding.bindingKey;
          else if (node?.kind === 'table') {
            bindingKey =
              node.cells.find((c) => c.binding?.bindingKey)?.binding?.bindingKey ??
              node.repeatRegions?.[0]?.over;
          } else if (node?.kind === 'repeat') {
            bindingKey = node.over;
          }
        }
        if (!bindingKey) return null;
        return findWidgetByBindingKey(composerWidgetSync.tree, bindingKey)?.id ?? null;
      })();
      setSelectedCells([]);
      // "선택 유지" 판정은 승격 id 정확 일치가 아니라 **조상 체인 포함**으로 — 선택된 그룹 *내부*
      // (자식 노드 영역)를 우클릭하면 드릴 컨텍스트가 그 그룹에 남아 있을 때 승격이 자식을 반환해
      // 선택이 그룹→자식으로 튐(작성 탭 handleWriteWidgetContextMenu와 동형 — 2026-07-16 제보).
      // 클릭 지점이 현재 선택의 subtree 안이면 선택을 유지하고 메뉴만 연다(탐색기/Figma 관례).
      const inSelection = ancestorChain.some((id) => selectedNodeIds.includes(id));
      if (!inSelection) selectSingleNode(promoted);
      setCtxMenu({ x: e.clientX, y: e.clientY, scope: 'form', target: 'selection', formNavWidgetId });
      return;
    }
    // 빈 캔버스 — 선택·컨텍스트 리셋 후 캔버스 메뉴(요소 추가/붙여넣기).
    setSelectionContextId(null);
    setSelectedCells([]);
    selectSingleNode(null);
    setCtxMenu({ x: e.clientX, y: e.clientY, scope: 'form', target: 'canvas' });
  };

  // R3 픽 모드 호버 추적 — 픽 활성 중 캔버스 위 셀/노드 위에서 마우스 이동 시, 그 대상의 바인딩 유무를
  // 판별해 pickHover에 기록(오버레이가 파랑=선택가능/빨강=불가로 표시). 픽 비활성 시 첫 줄에서 즉시 반환해
  // 오버헤드 0. 동일 id면 setState 스킵해 불필요 재렌더를 막는다(mousemove는 매우 빈번).
  const handleCanvasPickHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cellPick) return;
    const cellEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-cell]');
    if (cellEl) {
      const ref = parseCellRef(cellEl.dataset.composerCell ?? '');
      if (ref) {
        const id = `cell:${ref.tableId}:${ref.row}:${ref.col}`;
        const tableNode = findNodeById(template.body, ref.tableId);
        const binding =
          tableNode && tableNode.kind === 'table'
            ? tableNode.cells.find((c) => c.row === ref.row && c.col === ref.col)?.binding
            : undefined;
        setPickHover((prev) => (prev?.id === id ? prev : { id, hasBinding: !!binding }));
        return;
      }
    }
    const nodeEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-node-id]');
    const nodeId = nodeEl?.dataset.composerNodeId ?? null;
    if (nodeId) {
      const id = `node:${nodeId}`;
      const node = findNodeById(template.body, nodeId);
      setPickHover((prev) => (prev?.id === id ? prev : { id, hasBinding: !!node?.binding }));
      return;
    }
    // 빈 영역 — 호버 해제.
    setPickHover((prev) => (prev === null ? prev : null));
  };

  // 픽 모드가 꺼지면(적용/취소) 호버 미리보기도 함께 클리어.
  useEffect(() => {
    if (!cellPick) setPickHover(null);
  }, [cellPick]);

  // 캔버스 더블클릭. 'deepest' 모드는 드릴 없이 편집만(정적/빈 텍스트 셀·text 노드 인라인 편집).
  // 'isolation' 모드는 group isolation 드릴(resolveDrillTarget) — 컨테이너면 그 안으로 컨텍스트를
  // 진입하고 하위 자식을 선택, 리프 text에 닿으면 인라인 편집으로 연결. 표로 드릴 시엔 셀 선택
  // 서브모드로 진입(클릭 셀 선택 + 표 노드 선택). 결정 기록 2026-07-06-composer-selection-model.
  const handleCanvasDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (selectionMode === 'deepest') {
      // 최심부 우선(이전 복원) — 드릴 없이 편집만. 정적/빈 텍스트 셀 또는 text 노드에 인라인 편집 진입.
      const cellEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-cell]');
      if (cellEl) {
        const cellRef = parseCellRef(cellEl.dataset.composerCell ?? '');
        if (cellRef) {
          const tableNode = findNodeById(template.body, cellRef.tableId);
          if (tableNode && tableNode.kind === 'table') {
            const cell = tableNode.cells.find((c) => c.row === cellRef.row && c.col === cellRef.col);
            // 정적/빈 텍스트 셀만(content·binding 셀은 속성 패널 사용). 셀이 없으면(병합 슬롯) 무시.
            if (cell && !cell.content && !cell.binding) {
              enterCellTextEdit(cellRef);
            }
          }
          return;
        }
      }
      const nodeElDeep = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-node-id]');
      const nodeIdDeep = nodeElDeep?.dataset.composerNodeId;
      if (!nodeIdDeep) return;
      const node = findNodeById(template.body, nodeIdDeep);
      if (node && node.kind === 'text') {
        enterNodeTextEdit(nodeIdDeep);
      }
      return;
    }

    // group isolation 드릴(기본).
    const nodeEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-node-id]');
    const deepestId = nodeEl?.dataset.composerNodeId ?? null;
    if (!deepestId) return;

    const cellEl = (e.target as HTMLElement).closest<HTMLElement>('[data-composer-cell]');
    const cellRef = cellEl ? parseCellRef(cellEl.dataset.composerCell ?? '') : null;

    const { nextContextId, selectId } = resolveDrillTarget(template.body, deepestId, selectionContextId);

    // 드릴 발생 — 컨테이너 안으로 컨텍스트 진입.
    if (nextContextId && nextContextId !== selectionContextId) {
      const nextNode = findNodeById(template.body, nextContextId);
      // 표로 드릴 → 셀 선택 서브모드(클릭 셀 선택 + 표 노드 선택으로 셀 선택 불변식 유지).
      if (nextNode && nextNode.kind === 'table') {
        setSelectionContextId(nextContextId);
        selectSingleNode(nextContextId);
        setSelectedCells(cellRef && cellRef.tableId === nextContextId ? [cellRef] : []);
        return;
      }
      setSelectionContextId(nextContextId);
      selectSingleNode(selectId);
      setSelectedCells([]);
      return;
    }

    // 드릴 불가(resolveDrillTarget이 컨텍스트 유지) — 남은 케이스 처리:
    // (a) 표 셀(빈/정적) 직접 더블클릭인데 그 표가 아직 컨텍스트 밖이면 표로 드릴(셀 서브모드).
    //     (빈 셀은 deepestId=표라 resolveDrillTarget이 드릴하지 못하므로 여기서 처리.)
    if (cellRef && selectionContextId !== cellRef.tableId) {
      const tableNode = findNodeById(template.body, cellRef.tableId);
      if (tableNode && tableNode.kind === 'table') {
        setSelectionContextId(cellRef.tableId);
        selectSingleNode(cellRef.tableId);
        setSelectedCells([cellRef]);
        return;
      }
    }
    // (b) 리프 text 노드 → 인라인 편집 진입.
    const leaf = findNodeById(template.body, selectId);
    if (leaf && leaf.kind === 'text') {
      enterNodeTextEdit(selectId);
      return;
    }
    // (c) 이미 표 컨텍스트 안 — 정적/빈 텍스트 셀 더블클릭 시 인라인 셀 편집(content·binding 셀 제외).
    if (cellRef && selectionContextId === cellRef.tableId) {
      const tableNode = findNodeById(template.body, cellRef.tableId);
      if (tableNode && tableNode.kind === 'table') {
        const cell = tableNode.cells.find((c) => c.row === cellRef.row && c.col === cellRef.col);
        if (cell && !cell.content && !cell.binding) {
          enterCellTextEdit(cellRef);
        }
      }
    }
  };

  // 인라인 에디터 커밋 — text 노드면 updateNode(text), 셀이면 updateCell(text). undo 대상.
  // commitPresent로 묶어, live(replacePresent)가 캡처한 transient 시작 스냅샷(원본)을 past에
  // push → undo 1회로 입력 전 원본으로 복귀(apply는 live 입력값을 past에 넣어 undo가 깨짐).
  // 키보드 종료(Enter/Esc) 후 컨테이너 포커스 복원 — textarea 언마운트로 포커스가 document.body(컨테이너
  // 밖)로 빠져, 컨테이너 스코프에 붙은 화살표/F2 keydown 리스너가 먹지 않는 것을 막는다. textarea 언마운트가
  // 다음 렌더에 일어나므로 rAF로 한 틱 뒤 복원. blur(다른 곳 클릭)/auto(대상 소멸)는 사용자 포커스를 강탈하지
  // 않도록 복원하지 않는다(handleInlineTab의 Tab 경로 rAF 복원과 동일 메커니즘).
  // 셀 선택 여부를 ref로 추적 — 편집 종료 후 포커스 복원 대상(셀=캡처 textarea / 그 외=컨테이너)
  // 결정에 사용. rAF 콜백 안에서 최신값을 읽기 위해 ref로 둔다.
  const selectedCellsRef = useRef(selectedCells);
  selectedCellsRef.current = selectedCells;

  // 편집 진입 시점의 원본 텍스트(노드/셀). 커밋 시 값이 그대로면 apply를 건너뛰어(undo 히스토리·
  // dirty 미발생) "편집만 하고 무변경 완료 → 저장 버튼이 primary로 변하는" 오작동을 막는다.
  // editingTarget이 null→비null로 바뀌는 순간(아직 live 편집 전이라 template=원본)의 값을 캡처한다.
  const editStartTextRef = useRef('');

  // 키보드 종료(Enter/Esc) 후 포커스 복원. textarea 언마운트로 포커스가 document.body로 빠지면
  // 컨테이너 스코프 단축키/캡처가 먹지 않으므로 rAF로 한 틱 뒤 복원한다. 셀 선택이 남아 있으면
  // 캡처 textarea로(다음 타이핑이 IME 안전하게 이어지도록), 아니면 컨테이너로 복원한다.
  // blur(다른 곳 클릭)/auto(대상 소멸)는 사용자 포커스를 강탈하지 않도록 복원하지 않는다.
  const restoreContainerFocus = useCallback((reason: CloseReason) => {
    if (reason !== 'keyboard') return;
    requestAnimationFrame(() => {
      if (selectedCellsRef.current.length > 0 && captureRef.current) {
        captureRef.current.focus({ preventScroll: true });
      } else {
        containerRef.current?.focus({ preventScroll: true });
      }
    });
  }, []);

  // 셀 픽 종료(완료/취소/ESC/콤보 전환 공통) 후 컨테이너 포커스 복원 — 픽은 캔버스 마우스 클릭으로
  // 끝나 포커스가 document.body로 빠지므로, 복원하지 않으면 컨테이너 스코프 단축키(Ctrl+Z 등)가
  // 죽는다(2026-07-05 보고: 셀 지정 직후 undo 무반응). cellPick이 활성→해제로 전이될 때만 복원한다.
  const prevCellPickActiveRef = useRef(false);
  useEffect(() => {
    const active = cellPick !== null;
    if (prevCellPickActiveRef.current && !active) restoreContainerFocus('keyboard');
    prevCellPickActiveRef.current = active;
  }, [cellPick, restoreContainerFocus]);

  const handleInlineCommit = useCallback(
    (value: string, reason: CloseReason) => {
      const tgt = editingTarget;
      if (!tgt) return;
      if (value === editStartTextRef.current) {
        // 무변경 — apply 건너뜀(undo 히스토리·dirty 미발생). 진행 중 transient(live) 스냅샷만 폐기.
        // (transient가 없으면 discardTransient는 no-op.)
        discardTransient();
      } else if (tgt.kind === 'node') {
        commitPresent((t) => updateNode(t, tgt.id, { text: value }));
      } else {
        commitPresent((t) => updateCell(t, tgt.tableId, tgt.row, tgt.col, { text: value }));
      }
      setEditingTarget(null);
      setCaptureEditing(false);
      restoreContainerFocus(reason);
    },
    [commitPresent, discardTransient, editingTarget, restoreContainerFocus],
  );

  // 인라인 에디터 취소(Esc) — live로 바뀐 present를 transient 시작 스냅샷(원본)으로 복원 후 닫기.
  const handleInlineCancel = useCallback(
    (reason: CloseReason) => {
      discardTransient();
      setEditingTarget(null);
      setCaptureEditing(false);
      restoreContainerFocus(reason);
    },
    [discardTransient, restoreContainerFocus],
  );

  // 인라인 편집 중 입력 live 반영 — 현재 대상의 셀/노드 text를 present-only로 갱신(히스토리 미기록).
  // 셀 text가 즉시 갱신되어 행 높이가 auto면 실시간으로 반응한다. 최종값은 onCommit(handleInlineCommit)이
  // 정식 apply로 1엔트리 커밋하므로 undo 스택은 오염되지 않는다(handleResizeLive와 동일 메커니즘).
  const handleInlineLive = useCallback(
    (value: string) => {
      const tgt = editingTarget;
      if (!tgt) return;
      if (tgt.kind === 'node') {
        replacePresent((t) => updateNode(t, tgt.id, { text: value }));
      } else {
        replacePresent((t) => updateCell(t, tgt.tableId, tgt.row, tgt.col, { text: value }));
      }
    },
    [replacePresent, editingTarget],
  );

  // 인라인 편집 중 Tab — 현재 값은 InlineTextEditor가 먼저 커밋(setEditingTarget(null))하고,
  // 여기서 다음/이전 실제 셀로 선택을 옮긴다. 이동 후에는 편집 모드로 들어가지 않고 그 셀이
  // 선택된 상태만 된다(편집은 커밋의 null 유지로 이미 종료). 다음 셀이 없으면(표 끝/시작)
  // 선택 변경 없이 편집 종료만.
  const handleInlineTab = useCallback(
    (dir: 'next' | 'prev') => {
      const tgt = editingTarget;
      if (!tgt || tgt.kind !== 'cell') return;
      const tableNode = findNodeById(template.body, tgt.tableId);
      if (!tableNode || tableNode.kind !== 'table') return;
      const dest = nextTableCell(tableNode, { tableId: tgt.tableId, row: tgt.row, col: tgt.col }, dir);
      if (!dest) return;
      selectSingleNode(dest.tableId);
      setSelectedCells([dest]);
      // Tab 이동 후 새 셀이 선택(탐색)된 상태가 되므로, 다음 타이핑이 IME 안전하게 이어지도록
      // 캡처 textarea로 포커스를 복원한다(없으면 컨테이너). textarea 언마운트/전환이 다음 렌더에
      // 일어나므로 rAF로 한 틱 뒤. preventScroll로 focus 시 캔버스 스크롤 튐을 막는다.
      requestAnimationFrame(() =>
        (captureRef.current ?? containerRef.current)?.focus({ preventScroll: true }),
      );
    },
    [editingTarget, template],
  );

  // 현재 인라인 편집 대상의 초기 텍스트(노드 text 또는 셀 text). 대상이 없으면 null.
  // (캡처 textarea 편집은 InlineTextEditor를 마운트하지 않으므로 이 값을 쓰지 않는다 — 빈 값에서 시작.)
  const editingInitialText = useMemo(() => {
    if (!editingTarget) return null;
    if (editingTarget.kind === 'node') {
      const node = findNodeById(template.body, editingTarget.id);
      return node && node.kind === 'text' ? (node.text ?? '') : null;
    }
    const tableNode = findNodeById(template.body, editingTarget.tableId);
    if (!tableNode || tableNode.kind !== 'table') return null;
    const cell = tableNode.cells.find(
      (c) => c.row === editingTarget.row && c.col === editingTarget.col,
    );
    return cell ? (cell.text ?? '') : null;
  }, [editingTarget, template]);

  // 편집 진입(editingTarget null→비null) 시점의 원본 텍스트를 캡처(무변경 커밋 판정용). 이 시점엔
  // 아직 live 편집이 없어 template이 원본이므로 editingInitialText가 곧 원본이다. deps는 editingTarget만
  // 두어(template 제외) 진입 순간의 값만 잡고 이후 live 갱신에 덮이지 않게 한다.
  useEffect(() => {
    if (!editingTarget) return;
    editStartTextRef.current = editingInitialText ?? '';
  }, [editingTarget]);

  // 캡처 textarea에 첫 글자 입력 감지 → 편집 자동 진입(엑셀식). 현재 anchor 셀이 정적 텍스트
  // 셀(content·binding 없음)이면 그 셀을 캡처 textarea로 편집(덮어쓰기)하고 true를 반환한다.
  // 동일 textarea를 편집 모드로 전환할 뿐이라(조합 이관 없음) 한글 첫 자모가 보존된다. 비대상
  // 셀이면 false(CellCaptureEditor가 입력을 무시). 결정 기록 2026-06-28-composer-cell-type-to-edit.
  const handleCaptureTypingStart = useCallback((): boolean => {
    if (selectedCells.length < 1) return false;
    const anchor = selectedCells[0];
    const tableNode = findNodeById(template.body, anchor.tableId);
    if (!tableNode || tableNode.kind !== 'table') return false;
    const cell = tableNode.cells.find((c) => c.row === anchor.row && c.col === anchor.col);
    if (!cell || cell.content || cell.binding) return false; // 정적 텍스트 셀만
    setEditingTarget({ kind: 'cell', tableId: anchor.tableId, row: anchor.row, col: anchor.col });
    setCaptureEditing(true);
    return true;
  }, [selectedCells, template]);

  // 캡처 textarea 포커스 유지 — 셀 선택(탐색) 상태에서 포커스를 캡처 textarea에 두어, 다음 입력이
  // 처음부터 그 textarea에서(IME 포함) 조합되게 한다(한글 첫 자모 보존). 편집 중(editingTarget)이거나
  // 폼 외 뷰에선 스킵. 사용자가 의도적으로 포커스한 다른 입력(속성 패널 등)은 강탈하지 않는다.
  useEffect(() => {
    if (viewMode !== 'form') return;
    if (editingTarget) return; // 편집 중(캡처/InlineTextEditor) — 포커스 유지
    if (selectedCells.length < 1) return; // 셀 선택 없음 — 노드 탐색은 컨테이너 포커스
    const cap = captureRef.current;
    if (!cap) return;
    const ae = document.activeElement as HTMLElement | null;
    if (
      ae &&
      ae !== cap &&
      (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)
    ) {
      return; // 다른 입력 포커스 중 — 강탈 금지
    }
    cap.focus({ preventScroll: true });
  }, [selectedCells, editingTarget, viewMode]);

  // 이미 만들어진 노드를 현재 선택 위치에 삽입한다. 삽입 위치:
  //  - 단일 셀 선택 → 그 셀 content에
  //  - 그룹 자체 선택 → 그 그룹 안 끝에
  //  - 그룹 안 자식(표 등)/branch 슬롯/repeat 자식 선택 → 같은 컨테이너의 선택 바로 뒤(재선택 불필요)
  //  - 최상위 노드 선택/미선택 → body 끝에 append
  // handleAddNode(팔레트 단일 프리미티브)와 라디오 그룹 헬퍼(사전 조립한 group)가 공유하는 삽입 로직.
  // node.kind로 셀 삽입 가능 여부를 판정하므로, 호출부는 삽입할 노드를 그대로 넘기면 된다.
  // 반환 = 실제 삽입 여부(셀 게이트 차단 시 false) — 호출부의 후속 동작(자동 편집 진입 등) 판정용.
  const insertNodeAtSelection = useCallback(
    (node: ComposerNode): boolean => {
      // 단일 셀 선택 상태 → 팔레트/셀 편집기 '요소 추가'는 body 대신 그 셀 content에 삽입한다.
      if (singleCell) {
        const hostNode = findNodeById(template.body, singleCell.tableId);
        const tbl = hostNode && hostNode.kind === 'table' ? hostNode : null;
        if (!isCellInsertableKind(node.kind)) {
          showToast('표 셀에는 넣을 수 없는 요소입니다.', 'error');
          return false;
        }
        const reason = cellNodeInsertBlockReason(tbl, singleCell.row, singleCell.col);
        if (reason) {
          showToast(reason, 'error');
          return false;
        }
        apply((t) => insertNodeIntoCell(t, singleCell.tableId, singleCell.row, singleCell.col, node));
        setSelectedCells([]);
        selectSingleNode(node.id);
        return true;
      }
      // branch 노드 선택 → 활성 슬롯 children 끝에 삽입(탭이 가리키는 슬롯).
      if (selectedNode && selectedNode.kind === 'branch') {
        const raw = activeBranchSlots[selectedNode.id] ?? 0;
        const activeIdx = raw >= 0 && raw < selectedNode.branches.length ? raw : 0;
        const slotId = selectedNode.branches[activeIdx].id;
        apply((t) => insertNodeIntoBranchSlot(t, selectedNode.id, slotId, node));
        setSelectedCells([]);
        selectSingleNode(node.id);
        return true;
      }
      // branch 슬롯 안 자식 선택 → 같은 슬롯의 선택 노드 바로 뒤에 삽입(listDraggables는 슬롯 미하강).
      // 단 group/stack(자식 컨테이너)은 제외 — 아래 "그룹 안 삽입"(insertNode parentGroupId) 경로로
      // 떨어져 그 컨테이너 children으로 들어가게 한다(insertNode는 branch 슬롯 하강을 지원). 그러지
      // 않으면 슬롯 안 그룹/스택을 선택해도 그 안이 아니라 형제로 삽입되는 버그.
      if (selectedNode && selectedNode.kind !== 'group' && selectedNode.kind !== 'stack') {
        const loc = findNodeContainer(template.body, selectedNode.id);
        if (loc?.kind === 'branch-slot') {
          apply((t) => insertNodeIntoBranchSlot(t, loc.branchId, loc.slotId, node, loc.index + 1));
          setSelectedCells([]);
          selectSingleNode(node.id);
          return true;
        }
        // 반복 단위(template group) 안 자식 선택 → 같은 template group의 선택 노드 바로 뒤에 삽입
        // (listDraggables는 repeat.template 미하강이므로 findNodeContainer로 위치를 잡는다).
        if (loc?.kind === 'repeat-template') {
          apply((t) => insertNode(t, node, { parentGroupId: loc.templateId, index: loc.index + 1 }));
          setSelectedCells([]);
          selectSingleNode(node.id);
          return true;
        }
      }
      let opts: Parameters<typeof insertNode>[2];
      if (selectedNode) {
        if (selectedNode.kind === 'group' || selectedNode.kind === 'stack') {
          // group/stack 모두 자식 컨테이너 — 선택 시 그 안 끝에 삽입.
          opts = { parentGroupId: selectedNode.id };
        } else {
          const entry = listDraggables(template.body).find((e) => e.node.id === selectedNode.id);
          if (entry) {
            opts = entry.parentId
              ? // 그룹 내 자식 선택 → 같은 그룹에, 선택 노드 바로 뒤로 삽입.
                { parentGroupId: entry.parentId, index: entry.index + 1 }
              : // 최상위 노드 선택 → body의 그 노드 **바로 뒤**에 삽입(끝 append 아님 — 그룹 자식과 동일 규칙).
                { index: entry.index + 1 };
          }
          // 선택 없음(entry 부재) → opts undefined → body append.
        }
      }
      apply((t) => insertNode(t, node, opts));
      setSelectedCells([]);
      selectSingleNode(node.id);
      return true;
    },
    [apply, selectedNode, template.body, selectSingleNode, singleCell, showToast, activeBranchSlots],
  );

  const handleAddNode = useCallback(
    (kind: ComposerNode['kind'], special?: SpecialKind) => {
      // 기존 모든 노드 id(seed분 + 추가분 + 표 셀 콘텐츠)와 충돌하지 않을 때까지 카운터 증가.
      // 삭제 후 재추가에도 견고(카운터는 단조 증가, 집합으로 점유 id 회피).
      const existingIds = new Set(collectAllNodeIds(template.body));
      let newId: string;
      do {
        newId = `${special ?? kind}-${++idCounter.current}`;
      } while (existingIds.has(newId));
      const inserted = insertNodeAtSelection(createDefaultNode(kind, newId, special));
      // 텍스트 노드는 추가 직후 인라인 편집 진입(2026-07-15 사용자 요청 — 텍스트 입력형은 추가=바로 타이핑).
      // 삽입이 게이트에 막혔으면(셀 차단 등) 진입하지 않는다.
      if (inserted && kind === 'text') setEditingTarget({ kind: 'node', id: newId });
      // 결재란을 처음 놓으면 결재라인 구조도 기본값으로 세운다 — 빈 결재란은 캔버스에 칸이
      // 하나도 안 그려져 "무엇을 설정해야 하는지" 보이지 않는다(2026-08-11 사용자 요청).
      // **이미 구조가 있으면 건드리지 않는다** — 두 번째 결재란을 놓거나 저작자가 의도적으로
      // 비운 구조를 되살리면 안 된다.
      if (inserted && special === 'approvalBlock') {
        apply((t) => (t.approvalLineMeta ? t : { ...t, approvalLineMeta: DEFAULT_APPROVAL_LINE_META }));
      }
    },
    [template.body, insertNodeAtSelection, apply],
  );

  // 팔레트 라디오 그룹 클릭 — 모달 없이 즉시 기본 라디오 그룹을 삽입한다. 자동 공유키(radio-N,
  // 기존 bindingKey와 충돌 회피) + 기본 옵션 2개(예/아니오, label=값)를 갖는 체크박스 세트를 가로
  // 그룹으로 묶어 한 apply(undo 1스텝)로 삽입한다. checkedValue는 non-empty여야 작성폼 라디오 파생이
  // 동작하므로 label을 그대로 값으로 쓴다. 삽입 후 그룹을 선택(속성 패널에서 이어서 조정).
  // (결정 기록 2026-07-07-composer-radio-group-via-shared-key-checkbox)
  const handleAddRadioGroup = useCallback(() => {
    // 그룹 + 각 체크박스 id를 하나의 taken 집합으로 순차 발급(서로/기존 노드와 충돌 회피).
    const taken = new Set(collectAllNodeIds(template.body));
    const genId = (prefix: string): string => {
      let id: string;
      do {
        id = `${prefix}-${++idCounter.current}`;
      } while (taken.has(id));
      taken.add(id);
      return id;
    };
    // 자동 공유키 — 기존 모든 bindingKey를 스캔해 충돌 없는 radio-N(N=1,2,…)을 생성.
    const usedKeys = new Set<string>();
    walkBindings(template.body, (binding) => {
      if (binding.bindingKey) usedKeys.add(binding.bindingKey);
    });
    let keyN = 1;
    let bindingKey = `radio-${keyN}`;
    while (usedKeys.has(bindingKey)) bindingKey = `radio-${++keyN}`;

    const children: CheckboxNode[] = ['예', '아니오'].map((label) => {
      const cb = createDefaultNode('checkbox', genId('checkbox')) as CheckboxNode;
      cb.label = label;
      cb.checkedValue = label;
      cb.binding = { bindingKey, source: 'input', lifecycle: 'snapshot' };
      return cb;
    });
    const group = createDefaultNode('group', genId('group')) as GroupNode;
    // 인라인 `[v]예 [ ]아니오` — 가로 배치 + content-hug(width auto)로 라벨 폭만큼만 차지.
    group.direction = 'row';
    group.gap = 8;
    group.layout = { width: 'auto' };
    group.children = children;
    insertNodeAtSelection(group);
  }, [template.body, insertNodeAtSelection]);

  // 셀 편집기 "선택지 그룹 생성" — 선택 셀이 바인딩한 dataDef radio 필드를 선택지 그룹(choiceGroup)으로
  // 그 셀 content에 삽입한다(옵션 목록은 저장하지 않고 렌더 시점 dataDef options 파생 — 드리프트 차단).
  // insertNodeAtSelection의 셀 게이트(cellNodeInsertBlockReason: 반복영역·값 셀 차단)를 우회한다 —
  // 이 셀은 라디오 필드 셀임이 resolveRadioOptionField로 이미 확정됐고, content는 binding보다 우선
  // 렌더되므로 셀 binding은 그대로 두어 content만 넣는다.
  const handleGenerateOptionCheckboxes = useCallback(() => {
    if (!singleCell) return;
    const src = resolveRadioOptionField(template, singleCell);
    if (!src) return;
    const node = buildChoiceGroupNodeForField(template.body, idCounter, src.bindingKey);
    apply((t) => insertNodeIntoCell(t, singleCell.tableId, singleCell.row, singleCell.col, node));
    setSelectedCells([]);
    selectSingleNode(node.id);
  }, [template, singleCell, apply, selectSingleNode]);

  // 속성 패널 "라디오 그룹" 편집기 — 옵션 추가. 공유키를 그대로 쓰는 체크박스를 그룹 children 끝에
  // append한다(id는 전역 충돌 회피 발급). label/값 편집·삭제·키 rename은 onPatch({children})로
  // 처리되나, 옵션 추가만 신규 id 발급이 필요해 메인에서 담당한다.
  const handleAddRadioOption = useCallback(
    (groupId: string) => {
      const group = findNodeById(template.body, groupId);
      if (!group || group.kind !== 'group') return;
      const info = getRadioGroupInfo(group);
      if (!info) return;
      const taken = new Set(collectAllNodeIds(template.body));
      let id = `checkbox-${++idCounter.current}`;
      while (taken.has(id)) id = `checkbox-${++idCounter.current}`;
      const n = info.options.length + 1;
      const cb = createDefaultNode('checkbox', id) as CheckboxNode;
      cb.label = `옵션${n}`;
      cb.checkedValue = `옵션${n}`;
      cb.binding = { bindingKey: info.bindingKey, source: 'input', lifecycle: 'snapshot' };
      apply((t) =>
        updateNode(t, groupId, { children: [...group.children, cb] } as Partial<ComposerNode>),
      );
    },
    [template.body, apply],
  );

  // 팔레트/셀 편집기 '요소 추가' 게이트 — 단일 셀 선택 시 그 셀의 삽입 차단 사유(없으면 null).
  const cellInsertBlockReason = useMemo(() => {
    if (!singleCell) return null;
    const hostNode = findNodeById(template.body, singleCell.tableId);
    const tbl = hostNode && hostNode.kind === 'table' ? hostNode : null;
    return cellNodeInsertBlockReason(tbl, singleCell.row, singleCell.col);
  }, [singleCell, template]);

  // 선택 노드를 복제(Ctrl/Cmd+D) — 각 원본과 같은 컨테이너의 바로 뒤에 삽입하고 복제본을 선택.
  // 다중선택이면 선택 노드 전체를 한 apply(=undo 1회)로 복제(P3에서 anchor 1개 → 집합 복제로 통일).
  // 셀 선택 상태(selectedCells)에서는 무시(노드 복제만 지원).
  const handleDuplicateNode = useCallback(() => {
    if (selectedCells.length > 0) return;
    // 복제 대상: 다중선택이면 전체, 아니면 anchor 하나(불변식상 selectedNodeIds에 포함).
    const ids = selectedNodeIds.length > 0 ? selectedNodeIds : selectedNodeId ? [selectedNodeId] : [];
    if (ids.length === 0) return;
    // 각 원본 노드 + 컨테이너 위치를 원본 트리에서 먼저 수집(부재 id는 누락 필터).
    const originals = ids
      .map((id) => {
        const node = findNodeById(template.body, id);
        const loc = findNodeContainer(template.body, id);
        return node && loc ? { node, loc } : null;
      })
      .filter((x): x is { node: ComposerNode; loc: NodeContainerLoc } => x !== null);
    if (originals.length === 0) return;
    // 배치 클론(공유 existing 집합) — 원본 순서 보존, 노드 간 id 충돌 회피.
    const existing = new Set(collectAllNodeIds(template.body));
    const clones = cloneNodesWithNewIds(
      originals.map((o) => o.node),
      existing,
      idCounter,
    );
    // 삽입 op = {원본 위치, 대응 클론}. 같은 컨테이너 안에서 앞 노드를 먼저 삽입하면 뒤 노드의 index가
    // 밀리므로, index 내림차순으로 삽입해(높은 index부터) 낮은 index를 불변으로 유지한다. 서로 다른
    // 컨테이너끼리는 간섭 없음. body/그룹뿐 아니라 표 셀·분기 슬롯·repeat 안 노드도 원본과 같은 컨테이너에 삽입.
    const ops = originals
      .map((o, i) => ({ loc: o.loc, clone: clones[i] }))
      .sort((a, b) => b.loc.index - a.loc.index);
    apply((t) => {
      let next = t;
      for (const { loc, clone } of ops) {
        const at = loc.index + 1;
        if (loc.kind === 'cell') {
          next = insertNodeIntoCell(next, loc.tableId, loc.row, loc.col, clone, at);
        } else if (loc.kind === 'branch-slot') {
          next = insertNodeIntoBranchSlot(next, loc.branchId, loc.slotId, clone, at);
        } else if (loc.kind === 'repeat-template') {
          next = insertNode(next, clone, { parentGroupId: loc.templateId, index: at });
        } else if (loc.kind === 'group') {
          next = insertNode(next, clone, { parentGroupId: loc.parentId, index: at });
        } else {
          next = insertNode(next, clone, { index: at }); // body
        }
      }
      return next;
    });
    setSelectedCells([]);
    // 복제본 선택(단일이면 selectSingleNode, 복수면 집합 선택 + 첫 클론 anchor).
    if (clones.length === 1) {
      selectSingleNode(clones[0].id);
    } else {
      setSelectedNodeId(clones[0].id);
      setSelectedNodeIds(clones.map((c) => c.id));
    }
    // 복제 후 컨테이너 포커스 복원 — Ctrl+D는 window 리스너라 포커스가 body(마우스로 노드 선택 시)에
    // 있어도 복제되지만, 복원하지 않으면 컨테이너 스코프 undo(Ctrl+Z) keydown이 리스너에 도달하지
    // 못해 되돌리기가 죽는다(2026-07-07 보고, cellPick 복원과 동일 계열).
    restoreContainerFocus('keyboard');
  }, [
    apply,
    selectedNodeId,
    selectedNodeIds,
    selectedCells,
    template.body,
    selectSingleNode,
    restoreContainerFocus,
  ]);

  // 클립보드 노드 붙여넣기(P2 연산) — payload의 노드 배열을 배치 클론해 삽입한다.
  //  - mode 'with-deps': 대상 템플릿에 없는 dataDefs/dataSources를 이식(applyPasteDependencies) 후 삽입.
  //    'nodes-only': 의존 소스 미이식(바인딩 문자열만 유지 — 대상에 같은 key 있으면 연결, 없으면 미해결).
  //  - id는 하나의 공유 existing 집합으로 배치 재발급(cloneNodesWithNewIds) → 노드 간 충돌 없음.
  //  - 삽입 위치: 선택 노드가 있으면 그 컨테이너의 바로 뒤에 순서대로(뒤 노드일수록 index+1+i로 시프트),
  //    없으면 body 끝에 append. 의존 이식 + 전 clones 삽입을 한 apply로 묶어 undo 1스텝.
  const handlePasteNodes = useCallback(
    (payload: NodeClipboardPayload, mode: 'nodes-only' | 'with-deps') => {
      if (selectedCells.length > 0) return; // 셀 선택 중엔 노드 붙여넣기 비활성(셀 붙여넣기와 분리)
      if (payload.nodes.length === 0) return;
      // 배치 클론 — 기존 body id + 서로 간 충돌 회피(공유 existing). idMap으로 old→new id 매핑 수집.
      const existing = new Set(collectAllNodeIds(template.body));
      const idMap = new Map<string, string>();
      const clones = cloneNodesWithNewIds(payload.nodes, existing, idCounter, idMap);
      // 결재란 배치 nodeId를 재발급된 클론 id로 remap. 복사 서브트리에 그 approvalBlock이 없어 idMap이
      // 미스하면(배치가 다른 approvalBlock을 가리키는 등) placement를 드롭한다 — 소스 노드 id를 그대로
      // 이식하면 대상에 없는 dangling id가 되기 때문(리뷰 W2). nodeId 없는 배치는 remap 불요로 그대로.
      const remappedPlacement = (() => {
        const p = payload.approvalPlacement;
        if (!p) return undefined;
        if (!p.nodeId) return p;
        const mapped = idMap.get(p.nodeId);
        return mapped ? { nodeId: mapped } : undefined;
      })();
      // 삽입 위치는 현재 선택 노드의 컨테이너(handleDuplicateNode 분기 패턴 재사용).
      const loc = selectedNodeId ? findNodeContainer(template.body, selectedNodeId) : null;
      apply((t) => {
        // 의존 소스 + 결재 메타 이식(with-deps만) — body는 불변, 대상에 결재 설정 없을 때만 결재 메타 적용.
        let next =
          mode === 'with-deps'
            ? applyPasteApprovalMeta(applyPasteDependencies(t, payload), payload, remappedPlacement)
            : t;
        clones.forEach((clone, i) => {
          const at = (loc?.index ?? -1) + 1 + i; // 선택 뒤 순서대로(뒤일수록 시프트), 미선택이면 무시(append)
          if (loc?.kind === 'cell') {
            next = insertNodeIntoCell(next, loc.tableId, loc.row, loc.col, clone, at);
          } else if (loc?.kind === 'branch-slot') {
            next = insertNodeIntoBranchSlot(next, loc.branchId, loc.slotId, clone, at);
          } else if (loc?.kind === 'repeat-template') {
            next = insertNode(next, clone, { parentGroupId: loc.templateId, index: at });
          } else if (loc?.kind === 'group') {
            next = insertNode(next, clone, { parentGroupId: loc.parentId, index: at });
          } else if (loc?.kind === 'body') {
            next = insertNode(next, clone, { index: at });
          } else {
            next = insertNode(next, clone); // 선택 없음/위치 미상 → body 끝에 순서대로 append
          }
        });
        return next;
      });
      setSelectedCells([]);
      // 붙여넣은 clones를 선택 상태로(단일이면 selectSingleNode, 복수면 집합 선택 + 첫 노드 anchor).
      if (clones.length === 1) {
        selectSingleNode(clones[0].id);
      } else {
        setSelectedNodeId(clones[0].id);
        setSelectedNodeIds(clones.map((c) => c.id));
      }
      // 붙여넣기 후 컨테이너 포커스 복원 — Ctrl+V(window)/모달 후 포커스가 body면 undo(Ctrl+Z)가
      // 컨테이너 리스너에 도달하지 못해 죽는다(2026-07-07, handleDuplicateNode와 동일 계열).
      restoreContainerFocus('keyboard');
    },
    [apply, selectedNodeId, selectedCells, template.body, selectSingleNode, restoreContainerFocus],
  );

  // Ctrl+V keydown 리스너가 항상 최신 handlePasteNodes를 참조하도록 ref로 보관한다(stale 클로저 방지).
  // 리스너 deps에서 handlePasteNodes를 빼 재등록을 줄인다. 모달 onChoose는 JSX라 직접 참조해도 최신.
  const pasteNodesHandlerRef = useRef(handlePasteNodes);
  // crossorigin readText 붙여넣기 in-flight 가드(리뷰 W3) — Ctrl+V 연타 시 다중 readText가 각각
  // resolve→dispatchPaste되어 이중/다중 붙여넣기되는 것을 막는다(같은 origin 동기 경로는 무관).
  const nodePasteReadInFlightRef = useRef(false);
  useEffect(() => {
    pasteNodesHandlerRef.current = handlePasteNodes;
  }, [handlePasteNodes]);

  // 붙여넣기 선택 모달 안내용 — '함께' 선택 시 현재 템플릿에 새로 이식될 의존 소스 key 목록(없으면 빈 배열).
  const nodePasteAddedDependencyKeys = useMemo(() => {
    if (!nodePasteModalPayload) return [] as string[];
    const merged = mergeClipboardDependencies(template, nodePasteModalPayload);
    return [...merged.addedDefKeys, ...merged.addedSourceKeys];
  }, [nodePasteModalPayload, template]);

  // "분기로 감싸기" — 선택한 일반 노드를 그 자리에서 branch로 감싼다(슬롯0 condition '' + 노드, 슬롯1 else 빈).
  // 노드별 condition 폐지 후 "조건부 표시"의 단일 진입점. 표 셀 안 노드는 branch를 셀에 넣을 수 없어 거부(안내).
  // 감싼 뒤 새 branch를 선택해 속성 패널에서 슬롯 조건을 바로 편집하게 한다. undo 1스텝(apply)으로 되돌린다.
  const handleWrapInBranch = useCallback(() => {
    if (!selectedNodeId || selectedCells.length > 0) return;
    const node = findNodeById(template.body, selectedNodeId);
    if (!node) return;
    // branch는 이미 분기 컨테이너 → 감싸기 제외(중첩 branch 방지).
    if (node.kind === 'branch') return;
    const loc = findNodeContainer(template.body, selectedNodeId);
    if (loc?.kind === 'cell') {
      showToast('표 셀 안의 노드는 분기로 감쌀 수 없습니다(셀에 분기 삽입 불가).', 'error');
      return;
    }
    // 새 branch id 예측(wrapNodeInBranch 내부 규칙과 동일: branch-N fresh) — 감싼 뒤 선택.
    const existingIds = new Set(collectAllNodeIds(template.body));
    let n = 1;
    let branchId = `branch-${n}`;
    while (existingIds.has(branchId)) {
      n += 1;
      branchId = `branch-${n}`;
    }
    apply((t) => wrapNodeInBranch(t, selectedNodeId));
    setSelectedCells([]);
    selectSingleNode(branchId);
  }, [apply, selectedNodeId, selectedCells, template.body, selectSingleNode, showToast]);

  // ── branch 슬롯 조작 핸들러 (속성 패널 BranchSlotsEditor 공용) ──
  const handleAddBranchSlot = useCallback(
    (branchId: string) => {
      apply((t) => addBranchSlot(t, branchId));
    },
    [apply],
  );
  const handleRemoveBranchSlot = useCallback(
    (branchId: string, slotId: string) => {
      apply((t) => removeBranchSlot(t, branchId, slotId));
    },
    [apply],
  );
  const handleMoveBranchSlot = useCallback(
    (branchId: string, slotId: string, dir: 'up' | 'down') => {
      apply((t) => moveBranchSlot(t, branchId, slotId, dir));
    },
    [apply],
  );
  const handleUpdateBranchSlotCondition = useCallback(
    (branchId: string, slotId: string, condition: string | undefined) => {
      apply((t) => updateBranchSlotCondition(t, branchId, slotId, condition));
    },
    [apply],
  );
  const handleUpdateBranchSlotLabel = useCallback(
    (branchId: string, slotId: string, label: string | undefined) => {
      apply((t) => updateBranchSlotLabel(t, branchId, slotId, label));
    },
    [apply],
  );
  const handleUpdateBranchConditionBindings = useCallback(
    (branchId: string, next: ConditionBinding[]) => {
      apply((t) => updateBranchConditionBindings(t, branchId, next));
    },
    [apply],
  );

  const handleDelete = useCallback(() => {
    if (!selectedNodeId) return;
    // 다중선택이면 선택 노드 전체를 한 apply(=undo 1회)로 삭제. removeNode는 부재 id에 no-op이라
    // (부모가 먼저 지워져 자식이 사라진 경우 등) reduce 순서와 무관하게 안전하다.
    if (selectedNodeIds.length > 1) {
      const ids = selectedNodeIds;
      apply((t) => ids.reduce((acc, id) => removeNode(acc, id), t));
    } else {
      apply((t) => removeNode(t, selectedNodeId));
    }
    setSelectedCells([]);
    selectSingleNode(null);
  }, [apply, selectedNodeId, selectedNodeIds, selectSingleNode]);

  const handleMove = useCallback(
    (dir: 'up' | 'down') => {
      if (!selectedNodeId) return;
      // 다중선택 중엔 순서 이동 비활성 — anchor 하나만 옮기면 부분 이동으로 혼란스럽다(no-op).
      if (selectedNodeIds.length > 1) return;
      apply((t) => moveNodeInSiblings(t, selectedNodeId, dir));
    },
    [apply, selectedNodeId, selectedNodeIds],
  );

  // 다중선택 노드를 group/stack 컨테이너로 묶는다(Phase B). groupNodes가 형제 검증(다른 부모/부재면
  // no-op). 성공 시 새로 생긴 컨테이너를 선택으로 이동(children 집합 정확 일치로 특정).
  const handleGroupSelected = useCallback(
    (kind: 'group' | 'stack') => {
      if (selectedNodeIds.length < 2) return;
      const ids = selectedNodeIds;
      const grouped = groupNodes(template, ids, kind);
      if (grouped === template) return; // 형제 아님 등 no-op
      apply(() => grouped);
      selectSingleNode(findContainerWithExactChildren(grouped.body, ids));
    },
    [selectedNodeIds, template, apply, selectSingleNode],
  );

  // 선택 컨테이너(group/stack) 해제 — Ctrl+Shift+G와 툴바 "그룹 해제"가 공유(중복 구현 방지).
  const handleUngroupSelected = useCallback(() => {
    if (!selectedNodeId) return;
    const node = findNodeById(template.body, selectedNodeId);
    if (!node || (node.kind !== 'group' && node.kind !== 'stack')) return;
    apply((t) => ungroupNode(t, selectedNodeId));
    selectSingleNode(null);
  }, [selectedNodeId, template.body, apply, selectSingleNode]);

  // 선택 노드를 부모 컨테이너(group/stack 또는 branch 슬롯) 밖으로 한 단계 승격("밖으로 내보내기").
  // 최상위(body 직속)·셀 content 직속은 대상 아님 — moveNodeOutOfContainer가 참조 동일로 무변경 반환.
  const handleMoveOutSelected = useCallback(() => {
    if (!selectedNodeId) return;
    const loc = findNodeContainer(template.body, selectedNodeId);
    if (!loc || (loc.kind !== 'group' && loc.kind !== 'branch-slot')) return;
    apply((t) => {
      const nextBody = moveNodeOutOfContainer(t.body, selectedNodeId);
      if (nextBody === t.body) return t; // 무변경 → 히스토리 스텝 방지(참조 유지)
      return { ...t, body: nextBody };
    });
  }, [selectedNodeId, template.body, apply]);

  // 선택 컨테이너의 종류를 group⇄stack으로 전환(구조 유지, id 불변이라 선택도 유지).
  const handleToggleContainerKind = useCallback(() => {
    if (!selectedNode || (selectedNode.kind !== 'group' && selectedNode.kind !== 'stack')) return;
    const next = selectedNode.kind === 'group' ? 'stack' : 'group';
    apply((t) => setContainerKind(t, selectedNode.id, next));
  }, [apply, selectedNode]);

  // 툴바 문맥 활성화 파생값.
  // canGroup: 다중선택(2개+)이 모두 같은 부모의 형제인지(groupNodes의 형제 검증과 동일 기준).
  const canGroupSelected = useMemo(() => {
    if (selectedNodeIds.length < 2) return false;
    const parents = selectedNodeIds.map((id) => parentIdOf(template.body, id));
    if (parents.some((p) => p === undefined)) return false; // 부재 노드 포함
    return parents.every((p) => p === parents[0]);
  }, [selectedNodeIds, template.body]);
  // containerKind: 단일 선택 노드가 group/stack이면 그 종류, 아니면 null.
  const selectedContainerKind = useMemo<'group' | 'stack' | null>(() => {
    if (selectedNodeIds.length > 1) return null;
    if (selectedNode && (selectedNode.kind === 'group' || selectedNode.kind === 'stack')) {
      return selectedNode.kind;
    }
    return null;
  }, [selectedNodeIds.length, selectedNode]);
  // canMoveOut: 단일 선택 노드의 직속 부모가 group/stack 또는 branch 슬롯이면 "밖으로 내보내기" 가능.
  //  - body 직속(최상위)·표 셀 content 직속(loc.kind === 'cell')·부재는 제외.
  const canMoveOutSelected = useMemo(() => {
    if (selectedNodeIds.length !== 1 || !selectedNodeId) return false;
    const loc = findNodeContainer(template.body, selectedNodeId);
    return !!loc && (loc.kind === 'group' || loc.kind === 'branch-slot');
  }, [selectedNodeIds.length, selectedNodeId, template.body]);

  const handlePatch = useCallback(
    (patch: Partial<ComposerNode>) => {
      if (!selectedNodeId) return;
      apply((t) => updateNode(t, selectedNodeId, patch));
    },
    [apply, selectedNodeId],
  );

  // 결재라인 구조(template.approvalLineMeta) 편집(P2a-2) — 노드가 아니라 템플릿 레벨 필드라
  // apply((t) => ({ ...t, approvalLineMeta: 병합값 }))로 갱신(undo 대상). 미리보기는
  // applySimulatedApproval이 이 필드를 읽으므로 자동 반영(별도 배선 불필요).
  const updateApprovalLineMeta = useCallback(
    (patch: Partial<{ defaultApprovalLine: ApprovalLineMetaRow[]; maxAdditionalLines: number }>) => {
      apply((t) => {
        const current = t.approvalLineMeta ?? { defaultApprovalLine: [], maxAdditionalLines: 0 };
        return { ...t, approvalLineMeta: { ...current, ...patch } };
      });
    },
    [apply],
  );

  // 단일 선택 셀 patch(text/align/style 등) → updateCell(undo 대상).
  const handleCellPatch = useCallback(
    (patch: Partial<TableCell>) => {
      if (!singleCell) return;
      const { tableId, row, col } = singleCell;
      // 다단(모델 A) 1단 밖 N단 자리에는 바인딩 적용을 막는다 — 엔진(expandTableCells)이 1단
      // 패턴을 가로 복제해 자동 생성하는 읽기전용 자리이므로 운영자는 1단(좌측 패턴)에만 바인딩한다.
      if (patch.binding) {
        const table = selectedNode && selectedNode.kind === 'table' ? selectedNode : null;
        if (classifyMultiColumnCell(table, row, col) === 'generated') {
          showToast('N단 자동 생성 자리입니다. 1단(좌측 패턴)에만 바인딩하세요.', 'error');
          return;
        }
      }
      apply((t) => updateCell(t, tableId, row, col, patch));
    },
    [apply, singleCell, selectedNode, showToast],
  );

  // 단일 셀 숫자 입력 실시간 미리보기/확정(NumericField) — style 등 수치 변경에 replacePresent/commitPresent
  // 2단계 배선. binding 가드(다단 자동 생성 자리)는 숫자 style patch엔 무관하므로 생략(handleCellPatch 전용).
  const handleCellPatchLive = useCallback(
    (patch: Partial<TableCell>) => {
      if (!singleCell) return;
      const { tableId, row, col } = singleCell;
      replacePresent((t) => updateCell(t, tableId, row, col, patch));
    },
    [replacePresent, singleCell],
  );
  const handleCellPatchCommit = useCallback(
    (patch: Partial<TableCell>) => {
      if (!singleCell) return;
      const { tableId, row, col } = singleCell;
      commitPresent((t) => updateCell(t, tableId, row, col, patch));
    },
    [commitPresent, singleCell],
  );

  // 다중 선택 셀 일괄 patch(style/align) → updateCells(undo 대상).
  // 모든 셀이 같은 표(anchor.tableId)에 속한다는 전제(클릭 핸들러가 보장).
  const handleBulkCellPatch = useCallback(
    (patch: Partial<TableCell>) => {
      const anchor = selectedCells[0];
      if (!anchor) return;
      const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
      apply((t) => updateCells(t, anchor.tableId, refs, patch));
    },
    [apply, selectedCells],
  );
  // 다중 선택 셀 숫자 입력 실시간 미리보기/확정(NumericField) — replacePresent/commitPresent 2단계.
  const handleBulkCellPatchLive = useCallback(
    (patch: Partial<TableCell>) => {
      const anchor = selectedCells[0];
      if (!anchor) return;
      const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
      replacePresent((t) => updateCells(t, anchor.tableId, refs, patch));
    },
    [replacePresent, selectedCells],
  );
  const handleBulkCellPatchCommit = useCallback(
    (patch: Partial<TableCell>) => {
      const anchor = selectedCells[0];
      if (!anchor) return;
      const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
      commitPresent((t) => updateCells(t, anchor.tableId, refs, patch));
    },
    [commitPresent, selectedCells],
  );

  // 선택된 셀들의 binding에 format만 일괄 머지(select/catalogKey/lifecycle 보존). 바인딩 없는 셀은 skip.
  // mapCells로 셀별 변환(updateCells와 달리 binding 통째 교체가 아니라 기존 binding 보존하며 format만 갱신).
  const handleBulkBindingFormat = useCallback(
    (format: BindingFormat | undefined) => {
      const anchor = selectedCells[0];
      if (!anchor) return;
      const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
      apply((t) =>
        mapCells(t, anchor.tableId, refs, (cell) =>
          cell.binding ? { ...cell, binding: { ...cell.binding, format } } : cell,
        ),
      );
    },
    [apply, selectedCells],
  );

  // 선택된 셀들의 binding만 일괄 제거(undo 대상) — text/compute는 보존(바인딩만 해제).
  // BulkBindingAssignPanel의 "선택한 N개 셀의 바인딩 제거" 버튼에서 호출. 셀 선택 없으면 no-op.
  const handleBulkClearBinding = useCallback(() => {
    const anchor = selectedCells[0];
    if (!anchor) return;
    const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
    apply((t) =>
      mapCells(t, anchor.tableId, refs, (cell) =>
        cell.binding ? { ...cell, binding: undefined } : cell,
      ),
    );
  }, [apply, selectedCells]);

  // 선택된 셀들에 바인딩을 일괄 "추가"한다(단일 셀처럼 출처/경로를 한 번 지정, 두 모드).
  //  - 'individual'(N:N): 같은 출처/경로를 모든 셀에 적용하되 bindingKey는 셀별 유니크 →
  //    deriveWriteForm(bindingKey 단위 dedup)이 작성폼 위젯을 셀 수만큼 만든다.
  //  - 'grouped'(N:1): 배열 출처(cardinality 'list')를 선택 셀 영역에 자동 매핑한다. 행=항목 인덱스,
  //    열=항목 필드 순서로 select='<rowIdx>.<field.path>'를 부여하고, bindingKey는 1개 공유 →
  //    작성폼 위젯이 1개(배열 입력)로 묶인다. 배열 아닌 출처에서는 호출되지 않는다(패널이 차단).
  // 엔진/shared는 손대지 않고 bindingKey 공유 여부만으로 N:1/N:N을 가른다(핵심).
  const handleBulkBindingAssign = useCallback(
    (binding: Binding, mode: BulkBindMode) => {
      const anchor = selectedCells[0];
      if (!anchor || !binding.catalogKey) return;
      const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
      // catalogKey[+select] 기반 base 키(단일 셀 BindingEditor의 autoKey 규칙과 동일).
      const autoBase = binding.select
        ? `${binding.catalogKey}.${binding.select}`
        : binding.catalogKey;

      if (mode === 'individual') {
        // 혼합(mixed) 셀이면 사용자가 바꾼 세그먼트(필드 등)만 통일하고, 손대지 않은
        // 혼합 세그먼트(예 항목 인덱스)는 셀별 원래값을 보존한다(전체 collapse 방지).
        const entry = catalog.find((e) => e.key === binding.catalogKey);
        // selectedCells는 CellRef(row/col)뿐이라 binding이 없다 → 실제 셀 데이터(selectedCellData)에서 원래 select를 읽는다.
        const origSelects = selectedCellData.map((c) => c.binding?.select ?? '');
        const mix = entry ? computeSelectMix(entry, origSelects) : undefined;
        apply((t) =>
          mapCells(t, anchor.tableId, refs, (cell) => {
            const mergedSelect =
              entry && mix && mix.anyMixed
                ? mergeBulkSelect(entry, binding.select, cell.binding?.select ?? '', mix)
                : binding.select;
            return {
              ...cell,
              // 바인딩과 상호배타인 text/compute는 비워 바인딩이 가려지지 않게 한다.
              text: undefined,
              compute: undefined,
              binding: {
                ...binding,
                select: mergedSelect,
                bindingKey: `${autoBase}-r${cell.row}c${cell.col}`,
              },
            };
          }),
        );
        return;
      }

      // 묶음(N:1) — 배열 출처만. 영역 좌상단을 원점으로 행=인덱스, 열=필드.
      const entry = catalog.find((e) => e.key === binding.catalogKey);
      if (!entry || entry.cardinality !== 'list') return; // 패널이 막지만 안전망.
      // 열에 매핑할 필드는 항목의 스칼라 leaf만(중첩 array/object 필드는 셀 1칸에 부적합).
      const fields = entry.returns.filter(
        (f) => f.type !== 'array' && f.type !== 'object',
      );
      const minRow = Math.min(...selectedCells.map((c) => c.row));
      const minCol = Math.min(...selectedCells.map((c) => c.col));
      const sharedKey = `${binding.catalogKey}-group`;
      apply((t) =>
        mapCells(t, anchor.tableId, refs, (cell) => {
          const rowIdx = cell.row - minRow; // 배열 항목 인덱스
          const colIdx = cell.col - minCol; // 항목 필드 순서
          const field = fields[colIdx];
          if (!field) return cell; // 필드 범위를 벗어난 열은 매핑하지 않음(빈 셀 유지).
          return {
            ...cell,
            text: undefined,
            compute: undefined,
            binding: { ...binding, bindingKey: sharedKey, select: `${rowIdx}.${field.path}` },
          };
        }),
      );
    },
    [apply, selectedCells, selectedCellData, catalog],
  );

  // 선택된 셀들의 compute만 일괄 제거(undo 대상) — text/binding은 보존(계산만 해제).
  // BulkComputeAssignPanel의 "선택 셀 계산 제거" 버튼/출처 비우기에서 호출. 셀 선택 없으면 no-op.
  const handleBulkClearCompute = useCallback(() => {
    const anchor = selectedCells[0];
    if (!anchor) return;
    const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
    apply((t) =>
      mapCells(t, anchor.tableId, refs, (cell) => (cell.compute ? { ...cell, compute: undefined } : cell)),
    );
  }, [apply, selectedCells]);

  // 선택된 셀들에 계산(compute)을 일괄 적용한다(개별 고정 — 같은 compute(draft) 통째를 모든 선택 셀에 복제).
  // 묶음(grouped)은 제거됨: 셀 픽 가능한 단일+개별이 행별 "계"를 더 명확히 처리한다.
  // compute는 binding보다 우선 표시되므로 text만 비우고 binding은 보존(계산 제거 시 fallback 복귀).
  const handleBulkComputeAssign = useCallback(
    (draft: CellCompute) => {
      const anchor = selectedCells[0];
      if (!anchor) return;
      const refs = selectedCells.map((c) => ({ row: c.row, col: c.col }));
      apply((t) => mapCells(t, anchor.tableId, refs, (cell) => ({ ...cell, text: undefined, compute: draft })));
    },
    [apply, selectedCells],
  );

  // ── 셀 클립보드(복사/잘라내기/붙여넣기, 값=text TSV, 엑셀 호환) ──
  // 캡처 textarea(탐색 모드)에서만 동작 — CellCaptureEditor가 clipboard 이벤트를 위임한다. 셀 데이터는
  // EditorPage에 있으므로 TSV 생성/적용 로직을 여기 둔다(이벤트/권한은 clipboardData, navigator 권한 API 미사용).
  // 1차 범위는 값(text)만 — 서식 복사는 후속.

  // 복사 — 선택 셀들의 사각형 범위를 TSV로 clipboardData에 넣는다(병합 슬롯=빈 문자열). 처리 시 true.
  const handleCellCopy = useCallback(
    (clipboard: DataTransfer): boolean => {
      if (selectedCells.length < 1) return false;
      const anchor = selectedCells[0];
      const tableNode = findNodeById(template.body, anchor.tableId);
      if (!tableNode || tableNode.kind !== 'table') return false;
      clipboard.setData('text/plain', buildTsv(tableNode, selectedCells));
      // localStorage에 full-fidelity 스냅샷(값+서식+구조) 기록(탭/창 간 내부 붙여넣기 시 온전 복원).
      // cut도 이 경로를 타므로 자동 기록됨.
      const payload = buildCellClipboard(tableNode, selectedCells);
      if (payload) {
        try {
          localStorage.setItem(CELL_CLIPBOARD_KEY, serializeCellClipboard(payload));
        } catch {
          // localStorage 쓰기 실패(용량/사생활 모드 등)는 무시 — TSV 값 채널은 이미 설정됨.
        }
      }
      // 소스 셀 기록 — marching ants 표시(cut도 동일하게 표시, 엑셀 동형).
      setClipboardSource({ tableId: anchor.tableId, cells: [...selectedCells] });
      return true;
    },
    [selectedCells, template],
  );

  // 잘라내기 — 복사 후 원본 선택 셀 text를 ''로 비운다(서식 유지, Delete 내용지우기와 동일 경로).
  const handleCellCut = useCallback(
    (clipboard: DataTransfer): boolean => {
      if (!handleCellCopy(clipboard)) return false;
      handleBulkCellPatch({ text: '' });
      return true;
    },
    [handleCellCopy, handleBulkCellPatch],
  );

  // 붙여넣기 — TSV를 2D로 파싱해 anchor(좌상단) 기준 row+i, col+j 셀에 채운다.
  // 내부 복사(paste text === 스냅샷 tsv)면 localStorage 스냅샷의 값+서식+구조(binding/compute/aggregate 포함)를
  // full 적용, 외부 TSV면 값만(회귀 0). 표 경계(rowCount/colCount) 초과분·병합으로 덮인 슬롯(셀 부재)은 스킵.
  // updateCell을 한 apply 안에서 누적 적용해 단일 undo로 묶는다.
  //
  // ⚠️ span(rowspan/colspan)은 스냅샷엔 담기지만 붙여넣기 적용은 이번 범위서 보류한다 — 앵커 셀에만 span을
  // 붙이고 덮이는 슬롯을 비우는 그리드 재구성은 위험하므로(그리드 무결성 > span 완성도) 후속으로 남긴다.
  const handleCellPaste = useCallback(
    (text: string): boolean => {
      if (selectedCells.length < 1) return false;
      const anchor = selectedCells[0];
      const tableNode = findNodeById(template.body, anchor.tableId);
      if (!tableNode || tableNode.kind !== 'table') return false;
      const grid = parseTsv(text);
      if (grid.length === 0) return false;
      // 내부 복사 판별 — paste text가 localStorage 스냅샷 tsv와 일치하면 full 적용(엑셀 등 외부는 null).
      let internal = null as ReturnType<typeof parseCellClipboard>;
      try {
        const snapshot = parseCellClipboard(localStorage.getItem(CELL_CLIPBOARD_KEY));
        if (snapshot && snapshot.tsv === text) internal = snapshot;
      } catch {
        internal = null; // localStorage 접근 실패(사생활 모드 등) → 외부 TSV로 처리.
      }
      apply((t) => {
        let next = t;
        for (let i = 0; i < grid.length; i++) {
          const row = anchor.row + i;
          if (row >= tableNode.rowCount) break; // 행 경계 초과 무시
          const cols = grid[i];
          for (let j = 0; j < cols.length; j++) {
            const col = anchor.col + j;
            if (col >= tableNode.colCount) break; // 열 경계 초과 무시
            const snap = internal ? internal.cells[i]?.[j] : null;
            let patch: Partial<TableCell>;
            if (snap) {
              // full 적용 — snap.text 우선(없으면 tsv 값). span은 보류(그리드 무결성 우선)라 제외.
              patch = { ...snap, text: snap.text ?? cols[j] };
              delete patch.rowspan;
              delete patch.colspan;
            } else {
              patch = { text: cols[j] };
            }
            // 병합으로 덮인 슬롯(셀 부재)은 updateCell이 무변경 반환(자동 스킵).
            next = updateCell(next, anchor.tableId, row, col, patch);
          }
        }
        return next;
      });
      setClipboardSource(null); // 붙여넣기 완료 → 소스 표시 해제. localStorage는 유지(반복 붙여넣기).
      return true;
    },
    [selectedCells, template, apply],
  );

  // 표 전체 셀 일괄 patch — 표 패널 인라인 BulkCellEditor에서 모든 셀에 style/속성 적용(undo 대상).
  const handlePatchAllCells = useCallback(
    (tableId: string, patch: Partial<TableCell>) => {
      apply((t) => {
        const tbl = findNodeById(t.body, tableId);
        if (!tbl || tbl.kind !== 'table') return t;
        const refs = tbl.cells.map((c) => ({ row: c.row, col: c.col }));
        return updateCells(t, tableId, refs, patch);
      });
    },
    [apply],
  );
  // 표 전체 셀 일괄 숫자 입력 실시간 미리보기(replacePresent) — 다중선택 host(handleBulkCellPatchLive)와 동형.
  const handlePatchAllCellsLive = useCallback(
    (tableId: string, patch: Partial<TableCell>) => {
      replacePresent((t) => {
        const tbl = findNodeById(t.body, tableId);
        if (!tbl || tbl.kind !== 'table') return t;
        const refs = tbl.cells.map((c) => ({ row: c.row, col: c.col }));
        return updateCells(t, tableId, refs, patch);
      });
    },
    [replacePresent],
  );
  // 표 전체 셀 일괄 숫자 입력 확정(commitPresent) — 선행 미리보기 transient를 undo 1스텝으로 닫음.
  const handlePatchAllCellsCommit = useCallback(
    (tableId: string, patch: Partial<TableCell>) => {
      commitPresent((t) => {
        const tbl = findNodeById(t.body, tableId);
        if (!tbl || tbl.kind !== 'table') return t;
        const refs = tbl.cells.map((c) => ({ row: c.row, col: c.col }));
        return updateCells(t, tableId, refs, patch);
      });
    },
    [commitPresent],
  );

  // 셀 선택 해제(표 노드로 복귀) — selectedCells만 클리어, selectedNodeId(=tableId)는 유지.
  const handleBackToTable = useCallback(() => {
    setSelectedCells([]);
  }, []);

  // 표 행/열 구조 편집(T2) — 모두 apply((t)=>op(...))로 undo 대상. (행/열 핸들러는 단일 셀
  // 컨텍스트(CellEditor)에서만 호출되나, 다중선택 잔존도 안전하게 처리한다.)
  // 행/열 삭제 후 selectedCells에서 사라진 좌표(>=at)를 가리키는 셀을 제거한다.
  const handleAddRow = useCallback(
    (tableId: string, atRow: number, opts?: { fullWidth?: boolean }) => {
      apply((t) => addRow(t, tableId, atRow, opts));
    },
    [apply],
  );
  const handleRemoveRow = useCallback(
    (tableId: string, atRow: number) => {
      apply((t) => removeRow(t, tableId, atRow));
      setSelectedCells((prev) =>
        prev.filter((c) => !(c.tableId === tableId && c.row >= atRow)),
      );
    },
    [apply],
  );
  const handleAddCol = useCallback(
    (tableId: string, atCol: number) => {
      apply((t) => addCol(t, tableId, atCol));
    },
    [apply],
  );
  const handleRemoveCol = useCallback(
    (tableId: string, atCol: number) => {
      apply((t) => removeCol(t, tableId, atCol));
      setSelectedCells((prev) =>
        prev.filter((c) => !(c.tableId === tableId && c.col >= atCol)),
      );
    },
    [apply],
  );
  // Ctrl+Delete 모달 확인 — 선택 choice에 따라 후보 행/열 전부를 내림차순으로 한 apply에
  // 묶어 삭제(단일 undo). 내림차순이라 앞선 삭제가 뒤 인덱스를 밀어도 안전하다.
  const handleConfirmCellDelete = useCallback(() => {
    if (!deleteModal) return;
    const { tableId, rows, cols, choice } = deleteModal;
    if (choice === 'row') {
      const desc = [...rows].sort((a, b) => b - a);
      apply((t) => {
        let nt = t;
        for (const r of desc) nt = removeRow(nt, tableId, r);
        return nt;
      });
    } else {
      const desc = [...cols].sort((a, b) => b - a);
      apply((t) => {
        let nt = t;
        for (const c of desc) nt = removeCol(nt, tableId, c);
        return nt;
      });
    }
    setSelectedCells([]);
    setDeleteModal(null);
  }, [deleteModal, apply]);
  // 작성 탭 formTable 행/열 삭제 확정(Ctrl+Delete 모달) — 선택 셀들이 걸친 행 전부 또는 열 전부를
  // 한 updater(undo 1스텝)로 삭제. 삭제 후 셀 포커스 해제(좌표 무효화).
  const handleConfirmFtCellDelete = useCallback(() => {
    if (!ftDeleteModal) return;
    const { tableId, rowIds, colIds, choice } = ftDeleteModal;
    handleWidgetUpdate(tableId, (x) => {
      if (x.kind !== 'formTable') return x;
      let t = x;
      if (choice === 'row') for (const id of rowIds) t = ftRemoveRow(t, id);
      else for (const id of colIds) t = ftRemoveColumn(t, id);
      return t;
    });
    setFtFocusCell(null);
    setFtDeleteModal(null);
  }, [ftDeleteModal, handleWidgetUpdate]);
  // 표 열/행 크기 확정(commitPresent) — mm 입력 blur/Enter + '균등' 버튼. mm 입력의 실시간
  // 미리보기(handlePreviewColWidths/RowHeights, replacePresent)가 선행하면 그 transient를
  // undo 1스텝으로 닫는다. 선행 미리보기 없이 단발 호출(균등)이어도 commitPresent는 1엔트리.
  const handleSetColWidths = useCallback(
    (tableId: string, widths: (number | string)[]) => {
      commitPresent((t) => setColWidths(t, tableId, widths));
    },
    [commitPresent],
  );
  const handleSetRowHeights = useCallback(
    (tableId: string, heights: (number | string)[]) => {
      commitPresent((t) => setRowHeights(t, tableId, heights));
    },
    [commitPresent],
  );
  // 표 열/행 크기 실시간 미리보기(replacePresent — 히스토리 미적재). mm 입력 타이핑/화살표 중.
  const handlePreviewColWidths = useCallback(
    (tableId: string, widths: (number | string)[]) => {
      replacePresent((t) => setColWidths(t, tableId, widths));
    },
    [replacePresent],
  );
  const handlePreviewRowHeights = useCallback(
    (tableId: string, heights: (number | string)[]) => {
      replacePresent((t) => setRowHeights(t, tableId, heights));
    },
    [replacePresent],
  );

  // 표 반복영역(repeatRegion) + 페이지 분할 옵션(T4) — 모두 apply((t)=>op(...))로 undo 대상.
  // 페이지 분할 옵션(startOnNewPage/groupStartOnNewPage/groupContinuationMark)은 표 노드 필드라
  // updateNode로 patch한다(기존 tablePolicy 드롭다운 대체 — tablePolicy 필드/렌더 동작은 그대로 유지).
  const handleSetTableFlag = useCallback(
    (tableId: string, patch: Partial<TableNode>) => {
      apply((t) => updateNode(t, tableId, patch));
    },
    [apply],
  );
  const handleAddRepeatRegion = useCallback(
    (tableId: string, region: TableRepeatRegion) => {
      apply((t) => addRepeatRegion(t, tableId, region));
    },
    [apply],
  );
  const handleRemoveRepeatRegion = useCallback(
    (tableId: string, index: number) => {
      apply((t) => removeRepeatRegion(t, tableId, index));
    },
    [apply],
  );
  const handleUpdateRepeatRegion = useCallback(
    (tableId: string, index: number, patch: Partial<TableRepeatRegion>) => {
      apply((t) => updateRepeatRegion(t, tableId, index, patch));
    },
    [apply],
  );
  // 패턴 행 범위 설정 — 캔버스 2단계 픽(시작 행 → 끝 행)이 확정한 계획을 **한 번의 apply**로 적용한다
  // (부족분 행 생성 + 매핑 이동 + 밴드 갱신이 단일 undo 스텝). 다행 반복(항목당 2행+)의 정규 진입:
  // 끝 행을 표 아래 채움 행에 찍으면 그만큼 실제 행이 만들어진다(2026-08-28 — 채움 행을 실제 행으로
  // 오인해 없는 끝 행 번호를 입력하던 혼선의 처방).
  //
  // 2026-08-28 통합: 종전의 별도 「행 지정」(reassignRepeatRegionRow — 밴드 이동 + 매핑 동반 이동)은
  // 이 경로가 흡수했다. 합성 순서(행 생성 → 옛 밴드 clamp → reassign → 최종 밴드)는 rc op들의
  // 좌표 시프트·가드와 맞물려 있어 composePatternRowSet(순수, spec 보유) 한 곳에 모아 두었다.
  // ⚠ 단일 undo가 요건이라 `handleReassignRepeatRegionRow`(자체 apply를 가진 래퍼)는 재사용할 수
  //   없다 — 재사용하는 것은 rc op 자체다. 그래서 그 래퍼와 프롭은 제거했다.
  const handleSetRepeatPatternRows = useCallback(
    (tableId: string, index: number, plan: PatternRowSetPlan) => {
      // 옮기지 못한 칼럼(대상 셀 점유) 안내 — 판정은 행 생성 이후 상태에서 해야 정확하므로
      // 순수 함수에 위임한다(apply 안에서 토스트하면 StrictMode 이중 호출로 두 번 뜬다).
      const skipped = countRelocateSkips(template, tableId, index, plan);
      apply((t) => composePatternRowSet(t, tableId, index, plan));
      if (skipped > 0) {
        showToast(
          `칼럼 ${skipped}개는 대상 칸이 이미 쓰이고 있어 옮기지 못했습니다 — 새 패턴 행에서 다시 매핑하세요.`,
          'info',
        );
      }
    },
    [apply, template, showToast],
  );

  // 반복영역 칼럼↔셀 매핑 — 좌표 픽으로 고른 셀에 소스 필드 key를 bindingKey로 박는다(정적 text 제거).
  // 반복 컨텍스트에서 cell.binding.bindingKey = 순회 항목의 필드 경로로 정책화(고급 "식별 키" 수동 입력 대체).
  const setCellBinding = useCallback(
    (
      tableId: string,
      row: number,
      col: number,
      bindingKey: string,
      lifecycle: BindingLifecycle,
    ) => {
      // 셀 지정으로 매핑하는 필드가 dataDef radio(widget='radio'+options)면, plain binding 대신
      // 선택지 그룹(choiceGroup)을 자동 생성한다 — 바인딩 셀은 값을 텍스트로만 렌더해 라디오 표시가
      // 안 되므로(2026-07-07 결정). 옵션은 노드에 박제하지 않고 렌더 시점 dataDef options 파생
      // (2026-07-15 choiceGroup 결정 — 박제 checkbox 세트가 test2 드리프트의 생산 경로였음).
      // radio 아니면 기존 plain binding(회귀 0).
      const radio = resolveRadioOptionField(template, { tableId, row, col }, bindingKey);
      if (radio) {
        const group = buildChoiceGroupNodeForField(template.body, idCounter, radio.bindingKey);
        // cell.binding도 세팅한다 — 렌더는 content(그룹)>binding이라 그룹이 보이지만, 칼럼 매핑 UI가
        // cell.binding.bindingKey로 "지정됨"을 판정하므로(안 하면 '미지정' 오표시+행 anchor 실패). content는
        // 먼저 비워 재픽 시 그룹이 중복 append되지 않게 replace한다.
        apply((t) =>
          insertNodeIntoCell(
            updateCell(t, tableId, row, col, {
              binding: { bindingKey, lifecycle },
              text: undefined,
              content: [],
            }),
            tableId,
            row,
            col,
            group,
          ),
        );
        return;
      }
      // 이미지 표시 필드(데이터 소스 'image'|'annotated-image' + dataDef 'photo')도 radio와 동형
      // 특례 — 바인딩 셀은 값을 텍스트로만 렌더해 이미지가 안 보이므로, binding 세팅(칼럼 매핑 UI
      // '지정됨' 판정 + 그룹 병합 비교 키) + content=[image 노드] 자동 생성으로 replace한다
      // (재픽 중복 append 방지 동일).
      const imageField = resolveImageField(template, { tableId, row, col }, bindingKey);
      if (imageField) {
        const imageNode = buildImageNodeForField(
          template.body,
          idCounter,
          imageField.bindingKey,
          lifecycle,
        );
        apply((t) =>
          insertNodeIntoCell(
            updateCell(t, tableId, row, col, {
              binding: { bindingKey, lifecycle },
              text: undefined,
              content: [],
            }),
            tableId,
            row,
            col,
            imageNode,
          ),
        );
        return;
      }
      // plain 재지정: 이전 매핑이 특례(자동 생성 image/choicegroup 콘텐츠)였다면 그 잔존 노드도
      // 함께 비운다 — content>binding 렌더 우선이라 유령 이미지/선택지가 계속 보인다.
      // 판정(isAutoFieldCellContent)은 규약 id 단일 노드 + 이전 binding 키 일치일 때만(손저작 보존).
      const hostTable = findNodeById(template.body, tableId);
      const curCell =
        hostTable?.kind === 'table'
          ? hostTable.cells.find((c) => c.row === row && c.col === col)
          : undefined;
      apply((t) =>
        updateCell(t, tableId, row, col, {
          binding: { bindingKey, lifecycle },
          text: undefined,
          ...(isAutoFieldCellContent(curCell?.content, curCell?.binding?.bindingKey)
            ? { content: [] }
            : {}),
        }),
      );
    },
    [apply, template],
  );

  // 표 셀 병합/분할(T3) — 모두 apply((t)=>op(...))로 undo 대상. 좌표는 그대로 유지되므로
  // 병합/해제 후에도 현재 셀 선택을 유지한다(selectedCells 미변경).
  const handleMergeCellRight = useCallback(
    (tableId: string, row: number, col: number) => {
      apply((t) => mergeCellRight(t, tableId, row, col));
    },
    [apply],
  );
  const handleMergeCellDown = useCallback(
    (tableId: string, row: number, col: number) => {
      apply((t) => mergeCellDown(t, tableId, row, col));
    },
    [apply],
  );
  const handleUnmergeCell = useCallback(
    (tableId: string, row: number, col: number) => {
      // 해제 전 anchor의 span으로 복원될 영역을 계산 → 해제 후 그 셀들을 모두 선택.
      const table = findNodeById(template.body, tableId);
      let cells: CellRef[] = [{ tableId, row, col }];
      if (table && table.kind === 'table') {
        const cell = table.cells.find((c) => c.row === row && c.col === col);
        if (cell) {
          const rs = cell.rowspan ?? 1;
          const cs = cell.colspan ?? 1;
          cells = [];
          for (let r = row; r < row + rs; r++) {
            for (let cc = col; cc < col + cs; cc++) {
              cells.push({ tableId, row: r, col: cc });
            }
          }
        }
      }
      apply((t) => unmergeCell(t, tableId, row, col));
      setSelectedCells(cells);
    },
    [apply, template.body],
  );
  // 다중 선택 직사각 범위 병합(Excel/HWP식) — selectedCells 좌표에서 min/max를 산출해 op 호출.
  const handleMergeCells = useCallback(
    (tableId: string, cells: { row: number; col: number }[]) => {
      if (cells.length < 2) return;
      const rows = cells.map((c) => c.row);
      const cols = cells.map((c) => c.col);
      const r0 = Math.min(...rows);
      const r1 = Math.max(...rows);
      const c0 = Math.min(...cols);
      const c1 = Math.max(...cols);
      // 병합 후 좌상단 단일 셀 선택으로 전환(다중 선택 패널 → 단일 셀 패널).
      // 범위가 기존 병합과 교차하면 anchor가 범위 밖(더 위/왼쪽)으로 확장될 수 있어 expandMergeRect로 보정.
      let anchorRow = r0;
      let anchorCol = c0;
      const table = findNodeById(template.body, tableId);
      if (table && table.kind === 'table') {
        // 비연결(빈칸 사이) 선택은 병합 거부 — 선택이 rect를 꽉 채울 때만 진행(Ctrl+M 경로 포함).
        if (!isRectFullySelected(table, cells)) return;
        const ex = expandMergeRect(table, r0, c0, r1, c1);
        anchorRow = ex.r0;
        anchorCol = ex.c0;
      }
      apply((t) => mergeCellsRect(t, tableId, r0, c0, r1, c1));
      setSelectedCells([{ tableId, row: anchorRow, col: anchorCol }]);
    },
    [apply, template.body],
  );

  // 리사이즈 드래그 중 연속 미리보기(히스토리 미기록 — present만 직접 갱신).
  const handleResizeLive = useCallback(
    (patch: Partial<ComposerNode>) => {
      if (!selectedNodeId) return;
      replacePresent((t) => updateNode(t, selectedNodeId, patch));
    },
    [replacePresent, selectedNodeId],
  );

  // 리사이즈 종료(pointerup) — 1개 히스토리 엔트리 커밋.
  const handleResizeCommit = useCallback(
    (patch: Partial<ComposerNode>) => {
      if (!selectedNodeId) return;
      commitPresent((t) => updateNode(t, selectedNodeId, patch));
    },
    [commitPresent, selectedNodeId],
  );

  // 표 열/행 경계 드래그 리사이즈(TableEdgeResizeOverlay) — 드래그 중 replacePresent(미리보기),
  // pointerup에 commitPresent(undo 1스텝). 속성 패널 mm 입력(handleSetColWidths/RowHeights)과 같은
  // colWidths/rowHeights 데이터를 쓰므로 양쪽이 일관된다.
  const handleColEdgeLive = useCallback(
    (widths: (number | string)[]) => {
      if (!selectedNodeId) return;
      replacePresent((t) => setColWidths(t, selectedNodeId, widths));
    },
    [replacePresent, selectedNodeId],
  );
  const handleColEdgeCommit = useCallback(
    (widths: (number | string)[]) => {
      if (!selectedNodeId) return;
      commitPresent((t) => setColWidths(t, selectedNodeId, widths));
    },
    [commitPresent, selectedNodeId],
  );
  const handleRowEdgeLive = useCallback(
    (heights: (number | string)[]) => {
      if (!selectedNodeId) return;
      replacePresent((t) => setRowHeights(t, selectedNodeId, heights));
    },
    [replacePresent, selectedNodeId],
  );
  const handleRowEdgeCommit = useCallback(
    (heights: (number | string)[]) => {
      if (!selectedNodeId) return;
      commitPresent((t) => setRowHeights(t, selectedNodeId, heights));
    },
    [commitPresent, selectedNodeId],
  );

  // @dnd-kit 센서 — 핸들에서만 드래그가 시작되도록 약간의 거리 임계값.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      setIsDragging(false);
      const activeId = String(e.active.id);
      const overId = e.over ? String(e.over.id) : null;
      if (!overId) return;
      const slot = decodeDropId(overId);
      if (!slot) return;

      // 같은 컨테이너 내 재배치 시, 자기 자신 앞/뒤 드롭(무의미)은 moveNodeTo가 가드.
      // moveNodeTo의 index는 "노드 제거 후 target 배열 기준"이므로, 같은 부모에서
      // 자기 자신보다 뒤 index로 드롭한 경우 1칸 보정한다.
      const drags = listDraggables(template.body);
      const dragging = drags.find((d) => d.node.id === activeId);
      let index = slot.index;
      if (dragging && dragging.parentId === slot.parentId && dragging.index < slot.index) {
        index = slot.index - 1;
      }
      apply((t) => moveNodeTo(t, activeId, slot.parentId, index));
      setSelectedCells([]);
      selectSingleNode(activeId);
    },
    [apply, template, selectSingleNode],
  );

  // F1 → 단축키 목록 열기 / F8 → 새 창 미리보기 열기 / F4 → 작성 페이지 ↔ 보고서 탭 토글 /
  // F9 → 분기 관계 연결선 토글(양식 탭 전용).
  // 넷 다 viewMode/입력 포커스 무관하게 동작(document 스코프, F키라 입력란에서도 문자 미입력).
  // 브라우저 기본 도움말(F1 일부 환경)을 막기 위해 preventDefault. 탭 전환에 Ctrl+Tab/Ctrl+숫자는
  // 브라우저 예약이라 불가, Alt+숫자는 아래 반응형 뷰 전환에 기사용 → F4 단일 키 토글.
  // Alt+1..9 → 작성 페이지 탭 반응형 뷰 전환(COMPOSER_VIEWS 순서 — 뷰 추가 시 자동 확장).
  // Ctrl+숫자는 브라우저(탭 전환) 예약이라 Alt 사용. 입력 포커스 중에도 안전(문자 미입력).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F1') {
        e.preventDefault();
        setShowShortcuts(true);
      } else if (e.key === 'F8') {
        e.preventDefault();
        // 보고서(양식) 탭 전용 — 작성 페이지는 캔버스 "비교" 보기가 상위 호환(위 버튼 주석 참조).
        if (viewMode !== 'writeForm') openPreviewPopout();
      } else if (e.key === 'F4' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setViewMode((m) => (m === 'form' ? 'writeForm' : 'form'));
      } else if (e.key === 'F9' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        // F9 → 분기 관계 연결선 토글(양식 탭 전용 — 본문 branch 노드가 대상이라 작성 탭엔 없다).
        // 재입력으로 닫힌다(아래 Esc 취소와 두 경로). 후보였던 Ctrl+Shift+B는 **이미 시뮬레이션
        // 열기**라 쓸 수 없다(아래 별도 effect). F9는 패널·뷰 토글 계열(F1/F4/F8)과 같은 갈래이고
        // 브라우저 기본 동작이 없어 가로챌 것도 없다.
        e.preventDefault();
        if (viewMode === 'form') setShowBranchLinks((v) => !v);
      } else if (
        e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        /^Digit[1-9]$/.test(e.code)
      ) {
        if (viewMode !== 'writeForm') return;
        const view = COMPOSER_VIEWS[Number(e.code.slice(5)) - 1];
        if (view) {
          e.preventDefault();
          setWriteFormView(view.key);
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openPreviewPopout, viewMode]);

  // 키보드 단축키 — 에디터 컨테이너 스코프(전역 오염 방지, 입력 포커스 중엔 무시).
  // 'form' 모드 전체 단축키 + 'writeForm' 모드는 undo/redo만(아래 게이트) — 작성폼 탭 편집
  // (행 필드/라벨 등)도 히스토리에 쌓이는데 복귀 수단이 전무하던 문제 보완(2026-07-04 사용자 보고:
  // 값을 손으로 원복해도 dirty 유지 — 참조 비교상 의도 동작이라 Ctrl+Z가 정확한 복귀 수단).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || (viewMode !== 'form' && viewMode !== 'writeForm')) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      // document 스코프 + 컨테이너 내부/body 타깃만 수용 — 배치/삭제 클릭으로 포커스된 요소가
      // 언마운트되면 activeElement가 body로 떨어져, 컨테이너 리스너로는 keydown이 아예 오지 않아
      // Ctrl+Z가 죽던 문제의 근본 수정(2026-07-11 사용자 보고: 풀 '배치' 후 단축키 무반응, 버튼은
      // 동작 — 2026-07-04 3차 보고와 동일 계열). 모달 등 컨테이너 밖 포탈 입력은 여전히 제외.
      if (!(el.contains(t) || t === document.body)) return;
      // 캡처 textarea(상시 포커스)는 "탐색 모드"에서 셀 내비/단축키를 그대로 컨테이너로 위임해야
      // 하므로 일반 입력(isEditing)으로 보지 않는다. 단 캡처 textarea가 *편집 모드*(captureEditing)일
      // 땐 자체에서 Enter/Esc/Tab/커서를 처리하므로 isEditing으로 취급(아래 단축키 비활성).
      const isCapture = t.dataset?.composerCapture === 'true';
      const isEditing = isCapture
        ? captureEditing
        : t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;

      // undo / redo — **입력 편집 중(isEditing)엔 먼저 blur로 편집을 확정한 뒤 실행한다.**
      // (v1: 편집 중 앱 undo 허용 → draft형 입력의 stale draft가 blur 커밋으로 undo를 되살리고
      //  미커밋 미리보기+직전 스텝 2단 점프. v2: 편집 취소(Esc 동등)로 소비 → 폐기라 히스토리에
      //  안 남아 redo 불능(2026-07-04 2차 보고). 정본 v3: blur가 draft를 정식 1스텝으로 커밋한 뒤
      //  undo — 값은 편집 전으로 되돌아가고 그 스텝이 future에 남아 Shift+Ctrl+Z로 재실행 가능.
      //  Esc는 여전히 "폐기"(redo 없음) — 취소와 되돌리기의 의미 분리.)
      // blur()는 동기라 커밋(setState 함수형)→undo가 순서대로 합성된다. 무변경 편집이면 blur가
      // onRevert(no-op)로 끝나 일반 undo와 동일.
      // ⚠️ blur 후 반드시 컨테이너로 포커스 복원 — 안 하면 activeElement가 body로 떨어져
      // 이후 keydown이 컨테이너 리스너(이 핸들러)를 아예 거치지 않아 Shift+Ctrl+Z(redo)가
      // 죽는다(2026-07-04 3차 보고: 툴바 버튼은 되는데 단축키만 무반응).
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (isEditing && typeof (t as HTMLElement).blur === 'function') {
          (t as HTMLElement).blur();
          restoreContainerFocus('keyboard');
        }
        undo();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        ((e.shiftKey && e.key.toLowerCase() === 'z') || e.key.toLowerCase() === 'y')
      ) {
        e.preventDefault();
        if (isEditing && typeof (t as HTMLElement).blur === 'function') {
          (t as HTMLElement).blur();
          restoreContainerFocus('keyboard');
        }
        redo();
        return;
      }

      // 작성 탭('writeForm') 위젯 단축키 — 다중선택 Delete/그룹(Ctrl+G)/해제(Ctrl+Shift+G)/Esc.
      // 입력 포커스(isEditing: 라벨/제목 인라인 편집) 중엔 무시(브라우저 기본 동작 유지).
      if (viewMode === 'writeForm') {
        // 셀 편집 모드(셀 안 컨트롤/인라인 텍스트 에디터 포커스) 중 Tab — 편집 커밋(blur) 후
        // 다음/이전 셀로 이동 + **그 셀도 편집 진입 유지**(엑셀식 연쇄 편집, 2026-07-14 사용자
        // 확정 — 양식 탭 handleInlineTab의 "이동 후 선택"과 달리 편집 유지). isEditing 가드보다
        // 먼저 판정해야 하며, 편집 중 셀의 좌표는 포커스 요소가 속한 td의 data-ftcell이 정본
        // (ftFocusCell과 무관하게 동작 — F2/더블클릭 컨트롤 편집 모두 커버). 셀 밖 입력(속성
        // 패널 등)의 Tab은 브라우저 기본.
        if (isEditing && e.key === 'Tab' && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const ftcellKey = t.closest?.('[data-ftcell]')?.getAttribute('data-ftcell');
          if (ftcellKey) {
            const [tId, rId, cId] = ftcellKey.split('::');
            const ftw = tId ? selectedWidgetById(composerWidgetSync.tree, tId) : undefined;
            if (ftw && ftw.kind === 'formTable') {
              const nav = buildFtCellNav(ftw);
              const ri = nav.rows.findIndex((r) => r.id === rId);
              const ci = nav.cols.findIndex((c) => c.id === cId);
              const idx =
                ri >= 0 && ci >= 0
                  ? nav.order.findIndex((p) => p.ri === ri && p.ci === ci)
                  : -1;
              // repeat 행 셀 등 격자 밖이면 미개입(브라우저 기본 Tab 유지).
              if (idx >= 0 && nav.order.length > 0) {
                e.preventDefault();
                t.blur(); // 인라인 텍스트 에디터는 blur=커밋, 위젯 컨트롤 값은 onChange로 이미 반영
                const p =
                  nav.order[
                    (idx + (e.shiftKey ? -1 : 1) + nav.order.length) % nav.order.length
                  ];
                const nextRowId = nav.rows[p.ri].id;
                const nextColId = nav.cols[p.ci].id;
                focusFtCell(
                  { tableId: tId, rowId: nextRowId, columnId: nextColId },
                  { source: 'canvas' },
                );
                // 편집 모드 유지 — 이동한 셀도 편집 진입(defer: 커밋 재렌더 후 DOM 조회).
                enterFtCellEdit(tId, nextRowId, nextColId, true);
                return;
              }
            }
          }
        }
        if (isEditing) return;
        // 행/열 삭제 모달 열림 중엔 캔버스 단축키 전면 무시 — Esc/Enter는 모달이 document
        // keydown으로 처리(그대로 두면 Esc가 셀 포커스 해제로도 새어 이중 동작).
        if (ftDeleteModal) return;
        // F2 — 선택 위젯의 트리 행 라벨 rename 진입(더블클릭 규약의 키보드 대응, 2026-10-03).
        // ⚠️ 조건에 `!ftFocusCell`을 두고 **early return을 쓰지 않는다** — 셀 포커스 중 F2는 아래
        // ftFocusCell 블록의 「셀 컨트롤 편집 진입」이 정본이라, 여기서 `if (ftFocusCell) return`으로
        // 쓰면(Ctrl+D 등 다른 단축키의 모양) 그 경로가 통째로 죽는다. 같은 키의 두 의미가 **배타
        // 게이트로** 갈리는 자리다.
        if (e.key === 'F2' && !ftFocusCell) {
          const target = selectedWidgetId
            ? selectedWidgetById(composerWidgetSync.tree, selectedWidgetId)
            : null;
          // rename 가능 kind는 트리 행과 **같은 술어를 공유**한다(composerCanRenameWidget).
          // 그 외 선택(구분선·여백·표 등)은 미개입 — preventDefault도 하지 않아 브라우저 기본이 남는다.
          if (target && composerCanRenameWidget(target)) {
            e.preventDefault();
            setWtRenameRequest({ id: target.id, nonce: Date.now() });
          }
          return;
        }
        // Ctrl/⌘+Shift+G — 선택 섹션 1개 해제(G 판정을 G 앞에 둬 Ctrl+G와 분기).
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'g') {
          e.preventDefault();
          handleWidgetUngroupSelected();
          return;
        }
        // Ctrl/⌘+G — 다중선택(2개+) 같은 부모 형제를 섹션으로 묶기.
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'g') {
          e.preventDefault();
          handleWidgetGroupSelected();
          return;
        }
        // Ctrl/⌘+D — 선택 위젯 복제(단일/복수, 브라우저 북마크 기본 차단). 셀 포커스 중엔 미적용.
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'd') {
          e.preventDefault();
          if (ftFocusCell) return; // 셀 포커스 중엔 위젯 복제 미적용(위젯 선택 시만)
          handleWidgetDuplicate();
          return;
        }
        // Ctrl/⌘+B — 선택 위젯의 문자열 굵게 토글(heading 텍스트/input 라벨/섹션 제목).
        // 셀 포커스 중엔 window 셀 핸들러(2026-07-13 도입)가 담당(개입 안 함). 대상 없는
        // 선택(구분선·여백 등)은 미개입(2026-07-16 사용자 확인 — 위젯 축 누락 보완).
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'b') {
          if (ftFocusCell) return;
          if (handleWidgetBoldToggle()) e.preventDefault();
          return;
        }
        // Ctrl/⌘+C — 위젯 복사. 우선순위: 셀 포커스(window keydown 핸들러가 담당) > 텍스트 선택(브라우저
        // 기본) > 위젯 복사. isEditing(input/textarea/contentEditable)은 이 writeForm 블록 진입 전에 이미 return됨.
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'c') {
          if (ftFocusCell) return; // 셀 복사 우선(개입 안 함)
          if ((window.getSelection()?.toString() ?? '') !== '') return; // 텍스트 복사 우선
          if (handleWidgetCopy()) e.preventDefault();
          return;
        }
        // Ctrl/⌘+X — 위젯 잘라내기(이동). 가드는 Ctrl+C와 동형(셀 포커스/텍스트 선택 우선).
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'x') {
          if (ftFocusCell) return; // 셀 잘라내기(개입 안 함)
          if ((window.getSelection()?.toString() ?? '') !== '') return; // 텍스트 잘라내기 우선
          if (handleWidgetCut()) e.preventDefault();
          return;
        }
        // Ctrl/⌘+V — 위젯 붙여넣기(셀 포커스 중이면 셀 붙여넣기 우선 → 개입 안 함).
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'v') {
          if (ftFocusCell) return; // 셀 붙여넣기 우선(개입 안 함)
          if (handleWidgetPaste()) e.preventDefault();
          return;
        }
        // ── 셀 포커스 키보드 내비게이션(양식 탭 셀 이동 동형, 2026-07-14) — 화살표=이동,
        // Shift+화살표=anchor 고정 직사각 확장, Tab/Shift+Tab=행 우선 다음·이전 셀(순환),
        // Ctrl+A=표 전체 셀, F2=셀 컨트롤 편집 진입, Ctrl+M=병합/해제 토글.
        // 컨트롤 편집 중(isEditing)엔 이 블록에 도달하지 않아 브라우저 기본(캐럿/Tab) 유지. ──
        if (ftFocusCell && ftFocusCell.cells.length > 0) {
          const ftw = selectedWidgetById(composerWidgetSync.tree, ftFocusCell.tableId);
          if (ftw && ftw.kind === 'formTable') {
            const nav = buildFtCellNav(ftw);
            const anchor = ftFocusCell.cells[ftFocusCell.cells.length - 1];
            const ari = nav.rows.findIndex((r) => r.id === anchor.rowId);
            const aci = nav.cols.findIndex((c) => c.id === anchor.columnId);
            const arrow = FT_ARROW_DELTA[e.key];
            const inFixedGrid = ari >= 0 && aci >= 0; // repeat 행 포커스면 격자 밖 — 내비 미적용
            if (arrow && inFixedGrid && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              if (e.shiftKey) {
                // 범위 확장: 현재 범위에서 anchor 반대 코너를 한 칸 이동 → 직사각 재계산(anchor 유지).
                const rIdx = ftFocusCell.cells
                  .map((c) => nav.rows.findIndex((r) => r.id === c.rowId))
                  .filter((i) => i >= 0);
                const cIdx = ftFocusCell.cells
                  .map((c) => nav.cols.findIndex((cc) => cc.id === c.columnId))
                  .filter((i) => i >= 0);
                const minR = Math.min(...rIdx);
                const maxR = Math.max(...rIdx);
                const minC = Math.min(...cIdx);
                const maxC = Math.max(...cIdx);
                const cri = Math.min(
                  Math.max((ari === minR ? maxR : minR) + arrow.dr, 0),
                  nav.rows.length - 1,
                );
                const cci = Math.min(
                  Math.max((aci === minC ? maxC : minC) + arrow.dc, 0),
                  nav.cols.length - 1,
                );
                focusFtCell(
                  {
                    tableId: ftFocusCell.tableId,
                    rowId: nav.rows[cri].id,
                    columnId: nav.cols[cci].id,
                  },
                  { range: true, source: 'canvas' },
                );
                return;
              }
              // 단일 이동: 한 칸 진행 — 자기 병합 스팬 내부면 스팬 너머까지, 커버 위치는 anchor로 환원.
              let ri = ari;
              let ci = aci;
              for (;;) {
                const nri = ri + arrow.dr;
                const nci = ci + arrow.dc;
                if (nri < 0 || nri >= nav.rows.length || nci < 0 || nci >= nav.cols.length) break;
                ri = nri;
                ci = nci;
                const res = nav.resolve(ri, ci);
                if (!(res.ri === ari && res.ci === aci)) {
                  ri = res.ri;
                  ci = res.ci;
                  break;
                }
              }
              const fin = nav.resolve(ri, ci);
              focusFtCell(
                {
                  tableId: ftFocusCell.tableId,
                  rowId: nav.rows[fin.ri].id,
                  columnId: nav.cols[fin.ci].id,
                },
                { source: 'canvas' },
              );
              return;
            }
            // Tab/Shift+Tab — 행 우선 다음·이전 셀 순환(셀 "선택" 상태 전용 — 편집 중엔 미도달).
            if (e.key === 'Tab' && inFixedGrid) {
              e.preventDefault();
              const idx = nav.order.findIndex((p) => p.ri === ari && p.ci === aci);
              const n = nav.order.length;
              if (n > 0) {
                const next = nav.order[((idx < 0 ? 0 : idx) + (e.shiftKey ? -1 : 1) + n) % n];
                focusFtCell(
                  {
                    tableId: ftFocusCell.tableId,
                    rowId: nav.rows[next.ri].id,
                    columnId: nav.cols[next.ci].id,
                  },
                  { source: 'canvas' },
                );
              }
              return;
            }
            // Ctrl/⌘+A — 이 표의 전체(fixed) 셀 선택(브라우저 전체 선택 차단).
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
              e.preventDefault();
              setFtFocusCell({
                tableId: ftFocusCell.tableId,
                cells: nav.order.map((p) => ({
                  rowId: nav.rows[p.ri].id,
                  columnId: nav.cols[p.ci].id,
                })),
                source: 'canvas',
              });
              return;
            }
            // F2 — anchor 셀 편집 진입(더블클릭 규약의 키보드 대응): 컨트롤 focus, 없으면
            // td dblclick 재발행(정적 텍스트 셀=인라인 편집, 차단 셀=사유 툴팁). 편집 중 Tab
            // 연쇄 이동과 같은 enterFtCellEdit 공용 경로.
            if (e.key === 'F2') {
              e.preventDefault();
              enterFtCellEdit(ftFocusCell.tableId, anchor.rowId, anchor.columnId);
              return;
            }
            // Ctrl/⌘+M — 병합/해제 토글: 복수 직사각(전원 1×1) 선택=병합(내용 손실 시 확인 모달),
            // 단일 병합(span>1) 셀=해제. 캔버스 셀 패널의 병합 규약과 동일(top-left 보존).
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'm') {
              e.preventDefault();
              const cells = ftFocusCell.cells;
              if (cells.length === 1 && inFixedGrid) {
                const row = ftw.rows.find((r) => r.id === anchor.rowId);
                const cell =
                  row && row.type === 'fixed'
                    ? row.cells.find((c) => c.columnId === anchor.columnId)
                    : undefined;
                if (cell && ((cell.colSpan ?? 1) > 1 || (cell.rowSpan ?? 1) > 1)) {
                  const rs = Math.max(1, cell.rowSpan ?? 1);
                  const cs = Math.max(1, cell.colSpan ?? 1);
                  handleWidgetUpdate(ftFocusCell.tableId, (x) =>
                    x.kind === 'formTable'
                      ? ftUnmergeCell(x, { rowId: anchor.rowId, columnId: anchor.columnId })
                      : x,
                  );
                  // 해제로 복원된 영역 전체 선택(2026-07-14 사용자 확정, 양식 탭 handleUnmergeCell
                  // 동형) — anchor(원래 셀)는 마지막 유지.
                  const restored: { rowId: string; columnId: string }[] = [];
                  for (let dr = 0; dr < rs; dr++) {
                    for (let dc = 0; dc < cs; dc++) {
                      if (dr === 0 && dc === 0) continue;
                      const r = nav.rows[ari + dr];
                      const c = nav.cols[aci + dc];
                      if (r && c) restored.push({ rowId: r.id, columnId: c.id });
                    }
                  }
                  setFtFocusCell({
                    tableId: ftFocusCell.tableId,
                    cells: [
                      ...restored,
                      { rowId: anchor.rowId, columnId: anchor.columnId },
                    ],
                    source: 'canvas',
                  });
                }
                return;
              }
              if (!ftCanMergeCells(ftw, cells)) return;
              const doMerge = () => {
                handleWidgetUpdate(ftFocusCell.tableId, (x) =>
                  x.kind === 'formTable' ? (ftMergeCells(x, cells) ?? x) : x,
                );
                // 병합 후 top-left 단일 포커스(패널 병합과 동일 규약).
                const tl = [...cells].sort((a, b) => {
                  const ar = nav.rows.findIndex((r) => r.id === a.rowId);
                  const br = nav.rows.findIndex((r) => r.id === b.rowId);
                  if (ar !== br) return ar - br;
                  return (
                    nav.cols.findIndex((c) => c.id === a.columnId) -
                    nav.cols.findIndex((c) => c.id === b.columnId)
                  );
                })[0];
                setFtFocusCell({ tableId: ftFocusCell.tableId, cells: [tl], source: 'canvas' });
              };
              // top-left 외 셀에 내용이 있으면 손실 확인(패널 mergeSelection과 동일 판정).
              const tlFirst = [...cells].sort((a, b) => {
                const ar = nav.rows.findIndex((r) => r.id === a.rowId);
                const br = nav.rows.findIndex((r) => r.id === b.rowId);
                if (ar !== br) return ar - br;
                return (
                  nav.cols.findIndex((c) => c.id === a.columnId) -
                  nav.cols.findIndex((c) => c.id === b.columnId)
                );
              })[0];
              const hasLoss = cells.some((c) => {
                if (c.rowId === tlFirst.rowId && c.columnId === tlFirst.columnId) return false;
                const r = ftw.rows.find((rr) => rr.id === c.rowId);
                if (!r || r.type !== 'fixed') return false;
                const cc = r.cells.find((x) => x.columnId === c.columnId);
                return (
                  !!cc &&
                  (!!cc.widget ||
                    !!cc.compute ||
                    (typeof cc.text === 'string' && cc.text.trim() !== ''))
                );
              });
              if (hasLoss) {
                setConfirmState({
                  title: '셀 병합',
                  body: '병합하면 좌상단 셀만 남고 나머지 셀의 내용이 사라집니다. 병합하시겠습니까?',
                  confirmLabel: '병합',
                  danger: true,
                  onConfirm: () => {
                    setConfirmState(null);
                    doMerge();
                  },
                });
              } else {
                doMerge();
              }
              return;
            }
          }
        }
        // Ctrl/⌘+Enter — 셀 포커스 중 anchor 행 아래에 행 추가(양식 탭 테이블 노드와 동형,
        // 2026-07-14 사용자 요청). 반복(repeat) 행은 제외(행 추가 의미가 데이터 행 추가와 겹침 —
        // 그건 캔버스 '+ 행 추가' 버튼 관할). 삽입 후 셀 포커스는 유지(패널 삽입 규약과 동일).
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          if (!ftFocusCell || ftFocusCell.cells.length === 0) return;
          const ftw = selectedWidgetById(composerWidgetSync.tree, ftFocusCell.tableId);
          if (!ftw || ftw.kind !== 'formTable') return;
          const anchor = ftFocusCell.cells[ftFocusCell.cells.length - 1];
          const anchorRow = ftw.rows.find((r) => r.id === anchor.rowId);
          if (!anchorRow || anchorRow.type !== 'fixed') return;
          e.preventDefault();
          handleWidgetUpdate(ftFocusCell.tableId, (x) =>
            x.kind === 'formTable' ? ftInsertRow(x, anchor.rowId, 'below') : x,
          );
          return;
        }
        // Ctrl/⌘+Delete — 셀 포커스 중이면 포커스 셀이 걸친 행/열 삭제 모달(양식 탭 테이블
        // 노드의 Ctrl+Delete와 동형, 2026-07-14 사용자 요청). plain Delete(셀 내용 지우기)보다
        // 먼저 판정해야 아래 분기로 새지 않는다.
        if ((e.ctrlKey || e.metaKey) && e.key === 'Delete') {
          if (!ftFocusCell) return;
          const ftw = selectedWidgetById(composerWidgetSync.tree, ftFocusCell.tableId);
          if (!ftw || ftw.kind !== 'formTable') return;
          const rowIds = [...new Set(ftFocusCell.cells.map((c) => c.rowId))].filter((id) =>
            ftw.rows.some((r) => r.id === id),
          );
          const colIds = [...new Set(ftFocusCell.cells.map((c) => c.columnId))].filter((id) =>
            ftw.columns.some((c) => c.id === id),
          );
          if (rowIds.length === 0 && colIds.length === 0) return;
          e.preventDefault();
          setFtDeleteModal({ tableId: ftFocusCell.tableId, rowIds, colIds, choice: 'row' });
          return;
        }
        // Delete/Backspace — 셀 포커스 중엔 그 셀 내용만 제거(위젯=제거 후 '입력' 상태 유지,
        // 텍스트=비움). 셀 포커스는 표 위젯이 선택된 상태라, 기존 규칙대로면 표 전체가
        // 삭제되는 함정(2026-07-13 셀=1급 선택 전환의 파생) — 드릴다운 단계가 우선.
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (ftFocusCell) {
            e.preventDefault();
            clearFtCells(ftFocusCell.tableId, ftFocusCell.cells);
            return;
          }
          const ids = selectedWidgetIds.length > 0 ? selectedWidgetIds : selectedWidgetId ? [selectedWidgetId] : [];
          if (ids.length === 0) return;
          e.preventDefault();
          handleWidgetsRemove(ids);
          clearWidgetSelection();
          return;
        }
        // Tab/Shift+Tab — 선택 위젯에서 트리 순서(전위 순회)의 다음/이전 위젯으로 이동(끝↔처음
        // 순환, 2026-07-14 사용자 요청). 셀 포커스 중 Tab은 위 셀 순회가 우선(여기 도달 시
        // 미개입 — repeat 행 포커스 등 격자 밖 포함). 위젯 미선택은 브라우저 기본(포커스 이동) 유지.
        // 선택은 트리 패널과 동일하게 정확 id(source:'tree' — isolation 승격 우회)로.
        if (e.key === 'Tab' && !e.ctrlKey && !e.metaKey && !e.altKey) {
          if (ftFocusCell || !selectedWidgetId) return;
          const order: string[] = [];
          const walkIds = (ws: ComposerWidget[]): void => {
            for (const w of ws) {
              order.push(w.id);
              if (w.kind === 'group') walkIds(w.children);
            }
          };
          walkIds(composerWidgetSync.tree);
          const idx = order.indexOf(selectedWidgetId);
          if (idx < 0 || order.length < 2) return;
          e.preventDefault();
          const next = order[(idx + (e.shiftKey ? -1 : 1) + order.length) % order.length];
          selectWidget(next, false, false, 'tree');
          return;
        }
        // Esc — 드릴업(양식 탭 테이블/isolation Esc와 동형): 셀 포커스 해제(표 선택 유지) →
        //   isolation 컨텍스트 pop(부모 섹션으로) → 위젯/chrome 선택 해제.
        if (e.key === 'Escape') {
          e.preventDefault();
          if (ftFocusCell) {
            setFtFocusCell(null);
            return;
          }
          // isolation: 컨텍스트가 있으면 그 섹션(group)을 단위 선택하고 컨텍스트를 부모로 올린다
          // (부모 없으면 root=null). 양식 탭 selectionContextId Esc pop과 동형.
          if (selectionMode === 'isolation' && writeSelectionContextId != null) {
            const parentContext =
              widgetParentId(composerWidgetSync.tree, writeSelectionContextId) ?? null;
            selectWidgetSingle(writeSelectionContextId);
            setWriteSelectionContextId(parentContext);
            setSelectedChrome(null);
            return;
          }
          clearWidgetSelection();
          setSelectedChrome(null);
          return;
        }
        return; // 그 외 키는 작성 탭에서 미처리(양식 탭 전용 단축키로 흐르지 않게).
      }

      // 작성폼 탭은 undo/redo까지만 — 이하 캔버스 단축키(Esc 드릴업/Delete/화살표 등)는 양식 탭 전용.
      if (viewMode !== 'form') return;

      if (isEditing) return; // 텍스트 편집 중엔 아래 단축키 비활성(인라인 에디터가 Esc 자체 처리)

      // Esc — 상위로 한 단계.
      //  'isolation': 선택 컨텍스트를 한 단계 위로 pop.
      //    1) 셀 선택 중: 셀만 해제(표 노드·표 컨텍스트 유지 — 한 번 더 Esc로 표 밖으로).
      //    2) 컨텍스트 있음: 컨텍스트 컨테이너를 단위 선택 + 컨텍스트를 부모(buildParentMap)로 pop.
      //    3) 컨텍스트 없음(body 레벨)에서 노드 선택 중: 선택 해제.
      //  'deepest'(이전 복원): 상위로 드릴업 — 셀 선택 → 표 노드(셀 해제) / 노드 선택 → 부모 그룹
      //    (없으면 선택 해제). 셀 content 최상위 노드(직속 부모가 셀)면 그 셀 선택으로 전환.
      if (e.key === 'Escape') {
        e.preventDefault();
        setClipboardSource(null); // 소스 표시 해제(엑셀 동형).
        if (selectedCells.length > 0) {
          setSelectedCells([]); // 셀 해제 → 표 노드 유지(컨텍스트 개념은 isolation에서만)
          return;
        }
        if (selectionMode === 'deepest') {
          if (selectedNodeId) {
            // 직속 부모가 셀(합성 키 `${tableId}::${row}::${col}`)이면 → 그 셀 선택으로 전환.
            const parentKey = parentIdOf(template.body, selectedNodeId);
            const cellRef = parentKey ? parseCellRef(parentKey) : null;
            if (cellRef) {
              selectSingleNode(cellRef.tableId); // 셀 선택 불변식(selectedNodeId===tableId) 유지
              setSelectedCells([cellRef]);
              return;
            }
            const parent = ancestorGroups[ancestorGroups.length - 1];
            setSelectedCells([]);
            selectSingleNode(parent ? parent.id : null);
          }
          return;
        }
        if (selectionContextId != null) {
          // 컨텍스트 컨테이너를 단위 선택하고 컨텍스트를 그 부모로 올린다(부모 없으면 body=null).
          const parentContext = buildParentMap(template.body).get(selectionContextId) ?? null;
          setSelectedCells([]);
          selectSingleNode(selectionContextId);
          setSelectionContextId(parentContext);
          return;
        }
        if (selectedNodeId) {
          setSelectedCells([]);
          selectSingleNode(null);
        }
        return;
      }

      // Tab / Shift+Tab — 노드 선택 중 같은 부모 안 형제 순환 선택(마지막→첫으로 wrap). 그룹 밖으로
      // 나가려면 Esc(상위), 안으로는 더블클릭(드릴)이라 Tab은 "형제 순회"만 담당. 셀 선택 중엔 미적용
      // (아래 엑셀식 셀 내비가 처리). listDraggables 범위(body + group/stack 자식)의 노드만 대상.
      if (e.key === 'Tab' && selectedNodeId && selectedCells.length === 0) {
        const entries = listDraggables(template.body);
        const self = entries.find((en) => en.node.id === selectedNodeId);
        if (self) {
          e.preventDefault();
          // 같은 부모(parentId) 형제만 문서 순서대로(listDraggables가 이미 순서 보존).
          const siblings = entries.filter((en) => en.parentId === self.parentId);
          const pos = siblings.findIndex((en) => en.node.id === selectedNodeId);
          if (pos >= 0 && siblings.length > 0) {
            const delta = e.shiftKey ? -1 : 1;
            const nextPos = (pos + delta + siblings.length) % siblings.length; // wrap 순환
            setSelectedCells([]);
            selectSingleNode(siblings[nextPos].node.id);
          }
          return;
        }
      }

      // 셀 선택 중 — 엑셀식 셀 내비게이션(F2=편집 진입, 화살표=셀 이동). editingTarget 중엔 위
      // isEditing 가드로 이미 return됨(textarea 포커스). anchor=selectedCells[0].
      if (selectedCells.length > 0 && !editingTarget) {
        const anchor = selectedCells[0];
        const tableNode = findNodeById(template.body, anchor.tableId);
        if (tableNode && tableNode.kind === 'table') {
          // Ctrl/⌘+Enter — anchor 셀의 행 아래에 빈 행 추가(rowspan 고려). 선택을 새 행의
          // 같은 열로 옮겨 연속 추가가 가능하게 한다(좌표 기반 선택이라 다음 렌더에 반영).
          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            const anchorCell = tableNode.cells.find((c) => c.row === anchor.row && c.col === anchor.col);
            // anchor가 반복영역 안이면 독립 정적 행이 되도록 반복 블록(rowEnd) 바로 다음에 삽입.
            // (아니면 anchor 행 바로 아래.) addRow가 repeatRegions 인덱스를 shift해 정합성 유지.
            const containing = (tableNode.repeatRegions ?? []).filter(
              (r) => anchor.row >= r.rowStart && anchor.row <= r.rowEnd,
            );
            const atRow = containing.length
              ? Math.max(...containing.map((r) => r.rowEnd)) + 1
              : anchor.row + (anchorCell?.rowspan ?? 1);
            // 다단(columns≥2) 표에 추가하는 독립 행은 전체 폭 단일 셀로(엔진이 물리 전폭 확장).
            // containing이 있으면 그 region 기준(단일단·다단 공존 시 오적용 방지, W2).
            // containing이 비면(반복영역 밖 독립행 위) 테이블에 다단 region이 있는지로 판정 —
            // 독립행은 물리 전폭이어야 다른 독립행과 일관되게 보이기 때문.
            const isMultiColumn = containing.length
              ? containing.some((r) => (r.columns ?? 1) > 1)
              : (tableNode.repeatRegions ?? []).some((r) => (r.columns ?? 1) > 1);
            handleAddRow(anchor.tableId, atRow, isMultiColumn ? { fullWidth: true } : undefined);
            selectSingleNode(anchor.tableId);
            // 전폭 단일 셀 행은 col0이므로 선택도 col0으로(아니면 anchor 열 유지).
            setSelectedCells([{ tableId: anchor.tableId, row: atRow, col: isMultiColumn ? 0 : anchor.col }]);
            return;
          }
          // F2 — anchor가 정적 텍스트 셀(content·binding 없음)이면 인라인 편집 진입.
          if (e.key === 'F2') {
            const cell = tableNode.cells.find((c) => c.row === anchor.row && c.col === anchor.col);
            if (cell && !cell.content && !cell.binding) {
              e.preventDefault();
              setEditingTarget({ kind: 'cell', tableId: anchor.tableId, row: anchor.row, col: anchor.col });
            }
            return;
          }
          // Delete · Backspace — 엑셀식 "내용 지우기": 선택된 모든 셀의 text만 ''로 비운다.
          // 서식(style)·정렬(contentAlign)·병합(rowspan/colspan)·바인딩(binding/content/aggregate)
          // 등 그 외 필드는 보존(updateCells가 `{...cell, text:''}`로 부분 병합). apply 한 번으로
          // 묶여 undo 1회로 복원된다. (셀 선택 중엔 아래 노드 삭제 단축키를 타지 않음.)
          // Ctrl/⌘+Delete는 별도 window keydown이 행/열 삭제 모달을 띄우므로 여기선 제외.
          if ((e.key === 'Delete' || e.key === 'Backspace') && !(e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleBulkCellPatch({ text: '' });
            return;
          }
          // 화살표 — plain은 방향 인접 실제 셀로 단일 선택 이동(경계면 현 위치 유지).
          // Shift+화살표는 엑셀식 범위 확장: anchor 고정, 확장 끝점(focus)만 이동해 직사각 재계산.
          // Ctrl/⌘+Shift+화살표(끝까지 점프)는 이번 범위 밖 → 제외(ctrl/meta면 plain·shift 모두 미처리).
          const arrowDir =
            e.key === 'ArrowUp'
              ? 'up'
              : e.key === 'ArrowDown'
                ? 'down'
                : e.key === 'ArrowLeft'
                  ? 'left'
                  : e.key === 'ArrowRight'
                    ? 'right'
                    : null;
          if (arrowDir && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            if (e.shiftKey) {
              // 확장 끝점 결정 — 직전 focus가 현재 anchor 표에 속하고 현재 선택 안에 살아 있으면
              // 그것을 이어받고, 아니면(단일 클릭·plain 이동 등으로 선택이 바뀌었으면) anchor로 폴백.
              const prevFocus = rangeFocusRef.current;
              const focus =
                prevFocus &&
                prevFocus.tableId === anchor.tableId &&
                selectedCells.some(
                  (c) => c.row === prevFocus.row && c.col === prevFocus.col,
                )
                  ? prevFocus
                  : anchor;
              const nextFocus = adjacentTableCell(tableNode, focus, arrowDir);
              if (nextFocus) {
                rangeFocusRef.current = nextFocus;
                setSelectedCells(computeCellRange(tableNode, anchor, nextFocus));
              }
              return;
            }
            const dest = adjacentTableCell(tableNode, anchor, arrowDir);
            if (dest) {
              rangeFocusRef.current = dest; // plain 이동 → focus도 새 단일 셀로 리셋.
              selectSingleNode(dest.tableId);
              setSelectedCells([dest]);
            }
            return;
          }
          // 엑셀식 "셀 선택 중 글자 입력 → 편집 자동 진입"은 더 이상 여기서 keydown으로 처리하지
          // 않는다(div 포커스라 IME 첫 자모가 깨졌던 1차 구현 49ccf874a 대체). 이제는 상시 캡처
          // textarea가 포커스를 갖고 native beforeinput/compositionstart로 직접 감지·승격한다
          // (handleCaptureTypingStart, CellCaptureEditor). 결정 기록 2026-06-28-composer-cell-type-to-edit.
        }
      }

      // 셀 선택 중엔 노드 삭제/순서이동 단축키 비활성(표 노드를 실수로 삭제/이동 방지).
      if (selectedCells.length > 0) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodeId) {
          e.preventDefault();
          handleDelete();
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        if (selectedNodeId) {
          e.preventDefault();
          handleMove('up');
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        if (selectedNodeId) {
          e.preventDefault();
          handleMove('down');
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, handleDelete, handleMove, handleAddRow, handleBulkCellPatch, selectedNodeId, selectedCells, selectionContextId, selectionMode, viewMode, ancestorGroups, editingTarget, captureEditing, template, restoreContainerFocus, selectedWidgetId, selectedWidgetIds, handleWidgetsRemove, handleWidgetGroupSelected, handleWidgetUngroupSelected, handleWidgetDuplicate, handleWidgetBoldToggle, handleWidgetCopy, handleWidgetCut, handleWidgetPaste, clearWidgetSelection, ftFocusCell, ftDeleteModal, composerWidgetSync, handleWidgetUpdate, clearFtCells, writeSelectionContextId, selectWidgetSingle, selectWidget, focusFtCell, enterFtCellEdit]);

  // R3 — 픽 모드 ESC 취소. capture 단계로 가로채 컨테이너 ESC 핸들러(셀 선택 해제 등)보다 우선.
  // 픽 비활성 시엔 리스너 자체가 없어 기존 ESC 동작에 영향 없음.
  useEffect(() => {
    if (!cellPick) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setCellPick(null);
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [cellPick]);

  // 분기 관계 연결선 ESC 취소 — 위 픽 모드와 **같은 패턴**(capture 단계로 컨테이너 ESC 핸들러보다
  // 우선). 표시가 꺼져 있으면 리스너 자체가 없어 기존 ESC 동작(선택 해제·상위 복귀·인라인 종료)에
  // 영향이 0이다. 켜져 있는 동안만 ESC가 "모드 해제"를 먼저 먹는다 — 전환 모드의 관례이며,
  // 픽 모드가 이미 같은 규칙이라 사용자가 겪는 규칙이 하나로 유지된다.
  useEffect(() => {
    if (!showBranchLinks) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setShowBranchLinks(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [showBranchLinks]);

  // 탭을 떠나면 표시를 끈다 — F9는 양식 탭에서만 켤 수 있는데(위 단축키 분기) 끄는 경로가
  // 그 탭 안에만 있어(재입력·Esc), 켠 채 다른 탭으로 가면 상태가 남는다. 그러면 위 capture
  // 리스너가 계속 붙어 있어 **작성 탭의 Esc를 먹어치운다**(선택 해제·인라인 종료가 안 먹힘).
  // 게다가 그 탭에서는 F9로 끌 수도 없어 사용자가 빠져나올 방법이 없다.
  useEffect(() => {
    if (viewMode !== 'form') setShowBranchLinks(false);
  }, [viewMode]);

  // ── 템플릿 목록 로드 ──
  const refreshList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await adminApi.listReportTemplates();
      if (res.success && res.data) {
        setTemplateList(res.data);
      } else {
        showToast(res.error?.message ?? '템플릿 목록을 불러오지 못했습니다.', 'error');
      }
    } catch {
      showToast('템플릿 목록 조회 중 오류가 발생했습니다.', 'error');
    } finally {
      setListLoading(false);
      setListLoadedOnce(true);
    }
  }, [showToast]);

  // ── 양식 분류 목록 로드 — 선택기 그룹 헤더 순서·「분류」 select 선택지. 실패해도 에디터는 동작
  // (분류가 비면 선택기는 동봉 category.name으로 그룹을 세운다 — buildTemplateOptions 참조).
  const refreshCategories = useCallback(async (): Promise<ReportTemplateCategory[] | null> => {
    try {
      const res = await adminApi.listReportTemplateCategories();
      if (res.success && res.data) {
        setTemplateCategories(res.data);
        return res.data;
      }
      showToast(res.error?.message ?? '양식 분류 목록을 불러오지 못했습니다.', 'error');
    } catch {
      showToast('양식 분류 목록 조회 중 오류가 발생했습니다.', 'error');
    }
    return null;
  }, [showToast]);

  // 마운트 시 목록 1회 로드(피처 플래그 활성일 때만). 분류 목록도 같이.
  useEffect(() => {
    if (composerEnabled) {
      void refreshList();
      void refreshCategories();
    }
  }, [composerEnabled, refreshList, refreshCategories]);

  // (템플릿 선택 콤보박스는 2026-09-20 양식 탐색기로 대체 — 순서 정본 buildTemplateOptions/
  //  buildExplorerTree는 templateCategoryOptions.ts, 탐색기 본체는 TemplateExplorer.tsx.)
  // 새로고침 복원 가드(handleLoad 정의 후의 effect에서 사용).
  const restoredRef = useRef(false);

  // 마운트 시 카탈로그 1회 로드(바인딩 편집용). 실패해도 에디터는 동작(목록 비어 있음).
  useEffect(() => {
    if (!composerEnabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await adminApi.getReportComposerCatalog();
        if (!cancelled && res.success && res.data) setCatalog(res.data);
      } catch {
        // 무시 — 바인딩 select 목록이 비게 되며, 사용자는 텍스트 입력은 가능.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [composerEnabled]);

  // ── 템플릿 불러오기(load) 실제 수행 — 확인 게이트 없이 서버에서 로드. ──
  //
  // `restoreSimulation`: 저장된 **모사 값·모사 라벨**까지 되살릴지. 기본은 false다 — 모사 영속은
  // "같은 템플릿의 새로고침 생존" 전용이고(결정 2026-09-03 §영속의 범위), 저작자가 목록에서 다른
  // 템플릿을 고르는 것은 갈래를 이어 보겠다는 뜻이 아니다. 새로고침 자동 복원 경로만 true를 준다.
  // (엔티티 선택 복원은 종전대로 두 경로 모두 한다 — 그건 "어느 데이터로 보는가"라 전환과 무관하다.)
  const performLoad = useCallback(
    async (key: string, opts?: { restoreSimulation?: boolean }) => {
      if (!key) return;
      const restoreSimulation = opts?.restoreSimulation === true;
      setBusy(true);
      try {
        const res = await adminApi.getReportTemplate(key);
        if (res.success && res.data) {
          const tmpl = toEditableTemplate(res.data);
          reset(tmpl);
          setCurrentKey(res.data.key);
          setCurrentEnabled(res.data.enabled); // 공개 상태는 편집 상태와 분리해 별도 보관.
          // 분류(category)도 편집 상태 밖 — 로드 직후 = 저장본과 동일(깨끗).
          setCurrentCategoryId(res.data.categoryId ?? null);
          setSavedCategoryId(res.data.categoryId ?? null);
          // 저장된 미리보기 컨텍스트(선택 엔티티) 복원 — 없으면 비움. 자동 재적용은 effect가 담당.
          lastAppliedCtxRef.current = null;
          // 컨텍스트와 라벨을 **한 번에** 정한다 — 무엇을 되살리는지는 `restoredPreviewStateFor`가
          // 소유한다(값과 라벨은 한 쌍이라 따로 정하면 어긋난다). 저장소 읽기만 여기 남는다.
          try {
            const savedCtx = localStorage.getItem(`${PREVIEW_CTX_KEY}:${res.data.key}`);
            const savedLabels = localStorage.getItem(`${PREVIEW_LABELS_KEY}:${res.data.key}`);
            const restored = restoredPreviewStateFor({
              savedContext: savedCtx ? (JSON.parse(savedCtx) as EditorPreviewContext) : {},
              savedLabels: savedLabels ? (JSON.parse(savedLabels) as PreviewContextLabels) : {},
              restoreSimulation,
            });
            let ctx: EditorPreviewContext = restored.context;
            let labels: PreviewContextLabels = restored.labels;
            let appliedId: string | null = null;
            // 기본 프리셋(결정 2026-09-04 §4) — **로드 시마다** 반영하며 영속 previewContext보다
            // 우선한다(저작 중 조정·해제는 세션 한정, 재로드하면 기본으로 복귀). 대체 의미론이라
            // 복원된 plan 축 값은 유지된다. 단 **보고서 연결이 복원돼 있으면 건너뛴다**(배타 §3 —
            // 반영해도 전송에서 벗겨지는데 배너·"적용 중"만 남으면 화면이 거짓말한다).
            const defaultPreset = tmpl.defaultSimulationPresetId
              ? tmpl.simulationPresets?.find((p) => p.id === tmpl.defaultSimulationPresetId)
              : undefined;
            if (defaultPreset && !ctx.linkedReportId) {
              // 축은 로드한 템플릿 기준으로 즉석 파생 — state의 simulationAxes는 아직 이전
              // 템플릿 것이다(reset 직후 같은 tick).
              const axesForLoaded = derivePreviewSimulationAxes(catalog, tmpl);
              const applied = applyPresetToSimulation(
                ctx.previewSimulation,
                labels.simulation,
                defaultPreset,
                axesForLoaded,
              );
              ctx = { ...ctx };
              if (applied.simulation === undefined) delete ctx.previewSimulation;
              else ctx.previewSimulation = applied.simulation;
              labels = { ...labels };
              if (Object.keys(applied.labels).length > 0) labels.simulation = applied.labels;
              else delete labels.simulation;
              appliedId = defaultPreset.id;
            }
            // 되살린 모사 값은 아직 이 데이터에서 유효한지 모른다(계획 산출 행 id는 그 시점의
            // 것이다) — 첫 자동 적용이 실패하면 그 축만 걷어내도록 표시해 둔다.
            simulationUnverifiedRef.current = ctx.previewSimulation !== undefined;
            setPreviewContext(ctx);
            setPreviewContextLabels(labels);
            setAppliedPresetId(appliedId);
          } catch {
            simulationUnverifiedRef.current = false;
            setPreviewContext({});
            setPreviewContextLabels({});
            setAppliedPresetId(null);
          }
          try {
            setAutoApplySuppressed(localStorage.getItem(`${PREVIEW_SUPPRESS_KEY}:${res.data.key}`) === 'true');
          } catch { setAutoApplySuppressed(false); }
          setSelectedCells([]);
          selectSingleNode(null);
          setSavedSnapshot(tmpl); // 로드 직후 = 저장본과 동일(깨끗) — reset 대상과 같은 참조.
          try { localStorage.setItem(LAST_TEMPLATE_KEY, res.data.key); } catch { /* storage 차단 무시 */ }
        } else {
          showToast(res.error?.message ?? '템플릿을 불러오지 못했습니다.', 'error');
        }
      } catch {
        showToast('템플릿 불러오기 중 오류가 발생했습니다.', 'error');
      } finally {
        setBusy(false);
      }
    },
    // catalog: 기본 프리셋 반영의 축 파생(derivePreviewSimulationAxes)용 — 카탈로그 로드 전이면
    // 빈 배열로도 안전하다(excluded 판정은 split 예약 키를 무조건 포함).
    [reset, showToast, catalog],
  );

  // ── 템플릿 불러오기(load) — 미저장 변경이 있으면 확인 모달을 거친 뒤 로드. ──
  // `opts`는 performLoad로 그대로 흘린다(모사 복원 여부 — 그쪽 JSDoc 참조).
  const handleLoad = useCallback(
    (key: string, opts?: { restoreSimulation?: boolean }) => {
      if (!key) return;
      if (isDirty) {
        setConfirmState({
          title: '템플릿 불러오기',
          body: '저장하지 않은 변경이 있습니다. 다른 템플릿을 불러오시겠습니까?',
          confirmLabel: '불러오기',
          onConfirm: () => {
            setConfirmState(null);
            void performLoad(key, opts);
          },
        });
        return;
      }
      void performLoad(key, opts);
    },
    [isDirty, performLoad],
  );

  // 새로고침 복원 — 최초 목록 로드 완료 후 1회, localStorage의 마지막 템플릿을 자동 로드.
  // **모사 값을 되살리는 유일한 경로다**(결정 2026-09-03 §영속의 범위) — 목록에서 고르는 전환은
  // 같은 함수를 쓰지만 이 옵션 없이 부르므로 모사가 따라오지 않는다.
  useEffect(() => {
    if (restoredRef.current || !composerEnabled || !listLoadedOnce) return;
    restoredRef.current = true;
    if (currentKey) return; // 이미 로드됨(예: 직접 선택)
    let stored: string | null = null;
    try { stored = localStorage.getItem(LAST_TEMPLATE_KEY); } catch { /* 무시 */ }
    if (stored && templateList.some((t) => t.key === stored)) {
      void handleLoad(stored, { restoreSimulation: true });
    }
  }, [composerEnabled, listLoadedOnce, templateList, currentKey, handleLoad]);

  // ── 저장(save) — currentKey 있으면 update, 없으면 create ──
  const handleSave = useCallback(async () => {
    // 동기 인플라이트 가드 — busy state는 비동기라 같은 tick 연타(Ctrl+S)에 이중 PATCH가 샐 수 있어
    // ref로 즉시 차단한다(busy state는 버튼 UI용으로 유지). finally에서 해제.
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setBusy(true);
    try {
      if (currentKey) {
        // 기존 템플릿 갱신. key 변경(rename) 시 kebab/중복 사전검사 + body에 key 포함.
        const keyChanged = template.key !== currentKey;
        if (keyChanged) {
          if (!isKebabKey(template.key)) {
            showToast(KEBAB_KEY_ERROR, 'error');
            setBusy(false);
            return;
          }
          if (templateList.some((t) => t.key === template.key && t.key !== currentKey)) {
            showToast(`이미 존재하는 key입니다: ${template.key}`, 'error');
            setBusy(false);
            return;
          }
        }
        // 분류(category)는 본문과 같은 body로 — 바뀐 경우에만 싣는다(undefined=서버 skip, null=미분류 해제).
        // 별도 PATCH를 두면 「본문은 저장됐는데 분류는 실패」 같은 반쪽 상태가 생긴다.
        const categoryPatch =
          currentCategoryId !== savedCategoryId ? { categoryId: currentCategoryId } : {};
        const body = keyChanged
          ? { ...toUpdateBody(template), ...categoryPatch, key: template.key }
          : { ...toUpdateBody(template), ...categoryPatch };
        const res = await adminApi.updateReportTemplate(currentKey, body);
        if (res.success && res.data) {
          showToast('템플릿이 저장되었습니다.', 'success');
          setCurrentKey(res.data.key); // rename 시 새 key로 동기화
          setCurrentEnabled(res.data.enabled); // 서버 응답의 공개 상태로 동기화(본문 저장은 enabled 미변경).
          setSavedSnapshot(template); // 저장 완료 — 직렬화한 present가 저장본(요청 중 추가 편집은 dirty 유지).
          // 서버 응답의 분류로 동기화 — 요청 중 select를 또 바꿨어도 응답이 정본(그 차이는 dirty로 남는다).
          setSavedCategoryId(res.data.categoryId ?? null);
          try { localStorage.setItem(LAST_TEMPLATE_KEY, res.data.key); } catch { /* storage 차단 무시 */ }
          void refreshList();
          void refreshCategories(); // 분류별 양식 수(templateCount)가 배정 변경을 따라오게.
        } else {
          showToast(res.error?.message ?? '저장에 실패했습니다.', 'error');
        }
      } else {
        // 신규 생성 — key kebab 검증(서버도 검증하지만 UX).
        if (!isKebabKey(template.key)) {
          showToast(KEBAB_KEY_ERROR, 'error');
          setBusy(false);
          return;
        }
        if (templateList.some((t) => t.key === template.key)) {
          showToast(`이미 존재하는 key입니다: ${template.key}`, 'error');
          setBusy(false);
          return;
        }
        // 신규 양식의 「분류」도 생성 body에 실어 한 번에(null=미분류).
        const res = await adminApi.createReportTemplate({ ...template, categoryId: currentCategoryId });
        if (res.success && res.data) {
          showToast('새 템플릿이 생성되었습니다.', 'success');
          setCurrentKey(res.data.key);
          setCurrentEnabled(res.data.enabled); // 신규 생성 기본 false — 서버 응답을 그대로 반영.
          setSavedSnapshot(template); // 저장 완료 — 직렬화한 present가 저장본.
          setSavedCategoryId(res.data.categoryId ?? null);
          try { localStorage.setItem(LAST_TEMPLATE_KEY, res.data.key); } catch { /* storage 차단 무시 */ }
          void refreshList();
          void refreshCategories(); // 분류별 양식 수(templateCount)가 배정 변경을 따라오게.
        } else {
          showToast(res.error?.message ?? '생성에 실패했습니다.', 'error');
        }
      }
    } catch (err) {
      /**
       * **서버가 말한 거부 사유를 그대로 싣는다** (2026-10-27).
       *
       * 종전엔 `catch {`가 에러를 **바인딩조차 하지 않고** 「저장 중 오류가 발생했습니다」만
       * 띄웠다. axios는 non-2xx에서 reject하므로 400·403·409가 **전부** 이 가지로 온다 — 즉
       * 위의 `res.error?.message` 두 갈래(저장/생성 실패)는 **HTTP 에러에서는 죽은 코드**이고,
       * 서버가 「"위임받은 담당자"는 첫 번째 결재란에서만 지정할 수 있습니다 … 담당자를 비워
       * 두세요」처럼 처방까지 말해 줘도 저작자에게는 한 글자도 닿지 않았다.
       *
       * 같은 파일의 `handleApplyContext` catch가 2026-09-09에 같은 결함을 먼저 고쳤다 —
       * 그때 저장 축이 남은 것이고, 둘은 같은 이유로 같은 처방을 쓴다.
       *
       * `apiErrorMessage`를 경유하는 이유는 그 JSDoc에 있다(응답 본문에 `error`가 없는 갈래에서
       * axios 영문이 새지 않게). 폴백은 종전 문구 그대로라 동작은 넓어지기만 한다.
       */
      showToast(apiErrorMessage(err, '저장 중 오류가 발생했습니다.'), 'error');
    } finally {
      setBusy(false);
      saveInFlightRef.current = false;
    }
  }, [currentKey, template, refreshList, refreshCategories, showToast, templateList, currentCategoryId, savedCategoryId]);

  // 최신 handleSave/busy를 ref로 보관 — 편집기(Inline/Capture)의 Ctrl+S가 커밋 setState 후
  // setTimeout(0)로 저장을 부를 때, flush된 뒤의 최신 handleSave(방금 커밋한 텍스트 포함)를 읽게 한다.
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // 편집 중 Ctrl/Cmd+S 콜백 — 편집기가 현재 값을 먼저 커밋(setState)한 직후 호출한다. 같은 이벤트에서
  // 바로 저장하면 handleSave가 재렌더 전의 옛 template을 읽으므로, setTimeout(0)로 커밋 flush 뒤 최신
  // handleSave를 실행해 방금 커밋한 텍스트가 저장에 포함되게 한다(무변경 커밋이어도 저장은 트리거).
  const handleEditorSaveShortcut = useCallback(() => {
    setTimeout(() => {
      if (!busyRef.current) void saveRef.current();
    }, 0);
  }, []);

  // 시뮬레이션 열기 — 저장본 템플릿 기준(새 탭 전용 라우트가 서버에서 다시 로드 — 미저장 편집
  // 미반영 보장). isDirty(신규 미저장 포함)면 실행 대신 안내. 새 탭인 이유는 매뉴얼 링크와 동일 —
  // 에디터는 저장되지 않은 편집 상태를 들고 있어 같은 탭 이동은 작업 유실이다.
  // 결정: docs/decisions/2026-07-26-composer-simulation.md
  const handleOpenSimulation = useCallback(() => {
    if (!currentKey || isDirty) {
      showToast('저장 후 시뮬레이션할 수 있습니다.', 'error');
      return;
    }
    window.open(
      `/report-composer/simulation?templateKey=${encodeURIComponent(currentKey)}`,
      '_blank',
      'noopener',
    );
  }, [currentKey, isDirty, showToast]);

  // Ctrl/Cmd+Shift+B → 시뮬레이션 열기 (Composer Studio 마운트 중에만 — 브라우저 북마크 바 토글은
  // 페이지 가로채기로 흡수, 에디터 밖에서는 브라우저 기본 동작 유지. Ctrl+F5는 하드 새로고침과
  // 정면 충돌이라 기각 — 결정 기록 참조). 캔버스 셀 Ctrl+B(굵게)는 shiftKey를 제외하므로 비충돌.
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        handleOpenSimulation();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, handleOpenSimulation]);

  // Ctrl/Cmd+S → 저장 (Composer Studio 마운트 중에만, 모든 뷰/포커스에서 동작).
  // 브라우저 기본 "페이지 저장" 다이얼로그를 차단. (편집 중에는 편집기가 stopPropagation하므로 여기
  // 도달하지 않고 editor의 onSaveShortcut→handleEditorSaveShortcut 경로로 처리된다.)
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (!busy) void handleSave();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, busy, handleSave]);

  // Ctrl/Cmd+K → 양식 탐색기 펼침 + 검색 포커스(결정 2026-09-20 §4 — 헤더 콤보박스를 대체하는
  // 「빠른 열기」). 입력 포커스 중에도 동작(문자 미입력·브라우저 주소창 포커스 기본 동작 차단).
  // Esc(접힘)는 탐색기 안에 포커스가 있을 때만 — 에디터의 다른 Esc 핸들러(셀 해제·드릴업)와
  // 겹치지 않게 탐색기 루트 onKeyDown이 담당한다.
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openExplorer();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, openExplorer]);

  // Ctrl/Cmd+D → 선택 노드 복제 (Composer Studio 마운트 중에만).
  // 브라우저 기본 "북마크 추가" 다이얼로그를 차단.
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'd')) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;
      e.preventDefault();
      handleDuplicateNode();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, viewMode, editingTarget, handleDuplicateNode]);

  // 선택 노드(들) 복사 — localStorage(같은 origin 탭/창 채널) + OS 클립보드(텍스트, crossorigin
  // 상용계↔개발계·원격 데스크톱 지원) 이중 기록. Ctrl+C keydown과 우클릭 메뉴가 공유.
  // 복사분 없으면 false(호출부가 preventDefault 여부 판단).
  const handleNodeCopy = useCallback((): boolean => {
    if (selectedNodeIds.length === 0) return false;
    // 선택 노드 전체를 원본 트리에서 수집(부재 id 누락 필터).
    const nodes = selectedNodeIds
      .map((id) => findNodeById(template.body, id))
      .filter((n): n is ComposerNode => n != null);
    if (nodes.length === 0) return false;
    // 두 채널 기록은 위젯 복사와 공유(writeClipboardPayloadChannels) — 기존 동작 그대로 무통지.
    writeClipboardPayloadChannels(buildNodeClipboardPayload(nodes, template));
    // 노드 복사도 **같은 payload 채널을 덮으므로** 위젯 cut 버퍼를 비운다(handleWidgetCopy와 동일 이유).
    // 안 비우면 "위젯 잘라내기 → 노드 복사 → 작성 탭 Ctrl+V"에서 잘라낸 위젯이 붙는다.
    // (handleNodeCut도 이 함수를 거치므로 함께 적용된다.)
    setWidgetCutClipboard(null);
    return true;
  }, [selectedNodeIds, template]);

  // Ctrl/Cmd+C → 선택 노드(들)를 노드 클립보드에 복사 (Composer Studio 마운트 중에만).
  // 셀 선택 중(selectedCells)이면 무시 — 셀 복사는 CellCaptureEditor의 clipboardData 이벤트가 처리(별개 경로).
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'c')) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;
      if (selectedCells.length > 0) return; // 셀 복사 경로가 처리 — 노드 복사 무시
      if (handleNodeCopy()) e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, viewMode, editingTarget, selectedCells, handleNodeCopy]);

  // 선택 노드(들) 잘라내기 = 복사 + 선택 삭제(undo 1스텝) — Ctrl+X keydown과 우클릭 메뉴가 공유.
  // 노드 id는 붙여넣기가 재발급 + 결재란 배치 remap도 기존 붙여넣기 처리, bindingKey는 payload가
  // 보존하므로 위젯과 달리 이동 시맨틱(원본 보존) 불요. 복사 실패(선택 없음)면 no-op(false).
  const handleNodeCut = useCallback((): boolean => {
    if (!handleNodeCopy()) return false;
    handleDelete();
    return true;
  }, [handleNodeCopy, handleDelete]);

  // Ctrl/Cmd+X → 선택 노드(들) 잘라내기 (Composer Studio 마운트 중에만). 가드는 Ctrl+C와 동형 —
  // 셀 선택 중(selectedCells)이면 무시(셀 경로와 분리).
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'x')) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;
      if (selectedCells.length > 0) return; // 셀 선택 중 — 노드 잘라내기 무시
      if (handleNodeCut()) e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, viewMode, editingTarget, selectedCells, handleNodeCut]);

  // 노드 클립보드 붙여넣기 공통 트리거 — Ctrl+V keydown과 우클릭 메뉴가 공유.
  // 같은 템플릿이면 즉시 붙여넣기(의존 소스 이미 존재 — 병합 no-op), 다른 템플릿이면 붙여넣기
  // 선택 모달을 열어 의존 소스 이식 여부(노드만/함께)를 묻는다.
  // 채널 우선순위(OS 클립보드 → localStorage 폴백)·in-flight 가드는 위젯 축과 공유하는
  // triggerClipboardPayloadPaste가 담당한다. 위젯 동봉(v2) payload가 와도 handlePasteNodes는
  // payload.nodes만 삽입한다 — **양식 탭에서는 노드만 붙는다**(위젯 무시)가 탭 간 규칙.
  // 반환 true = 이 호출이 처리(호출부 keydown은 동기 시점에 preventDefault).
  const triggerNodePaste = useCallback((): boolean => {
    // payload를 같은/다른 템플릿 분기로 붙여넣는 공통 종단(localStorage·OS 클립보드 채널 공유).
    const dispatchPaste = (payload: NodeClipboardPayload) => {
      if (isSameTemplatePaste(payload, template.key)) {
        pasteNodesHandlerRef.current(payload, 'with-deps');
      } else {
        setNodePasteModalPayload(payload);
      }
    };
    return triggerClipboardPayloadPaste(nodePasteReadInFlightRef, dispatchPaste);
  }, [template.key]);

  // Ctrl/Cmd+V → 노드 클립보드 붙여넣기 (Composer Studio 마운트 중에만).
  // 셀 선택 중이면 무시(셀 붙여넣기와 분리).
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'v')) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;
      if (selectedCells.length > 0) return; // 셀 붙여넣기 경로와 분리
      if (triggerNodePaste()) e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, viewMode, editingTarget, selectedCells, triggerNodePaste]);

  // Ctrl/Cmd+G → 다중선택 노드를 그룹으로 묶기 / Ctrl/Cmd+Shift+G → 선택 컨테이너 해제 (Composer Studio).
  // 브라우저 기본 "찾기 다음"(Ctrl+G)을 차단. 인라인 편집/입력 포커스/폼 외 뷰에선 무시.
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey || e.key.toLowerCase() !== 'g' || !(e.ctrlKey || e.metaKey)) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;
      if (!e.shiftKey) {
        // Ctrl+G — 2개 이상 다중선택 시 그룹으로 묶기.
        if (selectedNodeIds.length < 2) return;
        e.preventDefault();
        handleGroupSelected('group');
      } else {
        // Ctrl+Shift+G — 선택 노드가 group/stack이면 해제(툴바 "그룹 해제"와 동일 로직).
        if (selectedContainerKind === null) return;
        e.preventDefault();
        handleUngroupSelected();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    composerEnabled,
    viewMode,
    editingTarget,
    selectedNodeIds,
    selectedContainerKind,
    handleGroupSelected,
    handleUngroupSelected,
  ]);

  // Ctrl/Cmd+M → 표 셀 병합/해제 토글 (Composer Studio 마운트 중에만).
  // 선택 상태로 동작 자동 판단: 다중 셀 선택 → 병합, 병합 셀 단일 선택 → 해제.
  // 인라인 편집 중이거나 입력 요소 포커스 시엔 무시(텍스트 입력 방해 방지).
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'm')) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;
      if (selectedCells.length === 0) return;
      e.preventDefault();
      if (selectedCells.length >= 2) {
        // 다중 선택 → 병합(같은 표일 때만). handleMergeCells가 직사각/병합가능 가드.
        const tableId = selectedCells[0].tableId;
        if (selectedCells.every((c) => c.tableId === tableId)) {
          handleMergeCells(tableId, selectedCells);
        }
      } else {
        // 단일 선택 + 병합 셀이면 해제.
        const { tableId, row, col } = selectedCells[0];
        const table = findNodeById(template.body, tableId);
        if (table && table.kind === 'table' && canUnmergeCell(table, row, col)) {
          handleUnmergeCell(tableId, row, col);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    composerEnabled,
    viewMode,
    editingTarget,
    selectedCells,
    template.body,
    handleMergeCells,
    handleUnmergeCell,
  ]);

  // Ctrl/Cmd+Delete → 선택 셀이 속한 행/열 삭제 모달(Composer Studio 마운트 중에만).
  // "행 삭제 / 열 삭제"를 물어 선택 셀들이 걸친 행 전부 또는 열 전부를 한 apply로 삭제(undo 1회).
  // 인라인 편집 중이거나 입력 요소 포커스 시엔 무시(plain Delete=셀 내용 지우기와 구분).
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'delete')) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;
      if (selectedCells.length < 1) return;
      const tableId = selectedCells[0].tableId;
      if (!selectedCells.every((c) => c.tableId === tableId)) return;
      const table = findNodeById(template.body, tableId);
      if (!table || table.kind !== 'table') return;
      const { rows, cols, defaultChoice } = analyzeCellDeletion(table, selectedCells);
      if (rows.length === 0 && cols.length === 0) return;
      e.preventDefault();
      setDeleteModal({ tableId, rows, cols, choice: defaultChoice });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [composerEnabled, viewMode, editingTarget, selectedCells, template.body]);

  // Ctrl/Cmd+B → 선택한 text 노드 또는 셀(단일/다중)의 굵게(fontWeight 700) 토글.
  // Word식 토글: 대상이 전부 700이면 해제(undefined), 하나라도 아니면 전체 700.
  // 인라인 편집 중이거나 입력 요소 포커스 시엔 무시(텍스트 입력 방해 방지). 셀 선택 우선.
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'b')) return;
      if (viewMode !== 'form') return;
      if (editingTarget || isFormInputFocused()) return;

      // 1) 셀 선택 우선 — 단일/다중 모두 처리.
      if (selectedCells.length > 0) {
        const tableId = selectedCells[0].tableId;
        if (!selectedCells.every((c) => c.tableId === tableId)) return;
        const table = findNodeById(template.body, tableId);
        if (!table || table.kind !== 'table') return;
        const cellsData = table.cells.filter((c) =>
          selectedCells.some((cr) => cr.row === c.row && cr.col === c.col),
        );
        if (cellsData.length === 0) return;
        e.preventDefault();
        // 전부 700이면 해제, 하나라도 아니면 전체 700. updateCells가 style을 머지하므로
        // fontWeight만 토글되고 나머지 셀 style은 보존된다.
        // 해제는 굵게 직전 굵기 복원 — 셀마다 다를 수 있어 개별 patch(reduce, undo 1스텝).
        const allBold = cellsData.every((c) => c.style?.fontWeight === 700);
        if (!allBold) {
          for (const c of cellsData) {
            boldPrevWeightRef.current.set(`cell:${tableId}:${c.row}:${c.col}`, c.style?.fontWeight);
          }
          handleBulkCellPatch({ style: { fontWeight: 700 } });
        } else {
          apply((t) =>
            cellsData.reduce(
              (acc, c) =>
                updateCells(acc, tableId, [{ row: c.row, col: c.col }], {
                  style: {
                    fontWeight: boldPrevWeightRef.current.get(`cell:${tableId}:${c.row}:${c.col}`),
                  },
                }),
              t,
            ),
          );
        }
        return;
      }

      // 2) text 노드 선택. updateNode는 style을 통째 교체하므로 기존 style을 펼쳐 머지.
      // 다중선택 시에도 anchor(selectedNode) 하나만 토글(의도) — 집합 일괄 굵게는 미지원.
      // 해제는 굵게 직전 굵기 복원(boldPrevWeightRef — 기록 없으면 미지정=기본).
      if (selectedNode && selectedNode.kind === 'text') {
        e.preventDefault();
        const isBold = selectedNode.style?.fontWeight === 700;
        if (!isBold) {
          boldPrevWeightRef.current.set(`node:${selectedNode.id}`, selectedNode.style?.fontWeight);
        }
        const nextWeight = isBold
          ? boldPrevWeightRef.current.get(`node:${selectedNode.id}`)
          : 700;
        apply((t) =>
          updateNode(t, selectedNode.id, {
            style: { ...selectedNode.style, fontWeight: nextWeight },
          }),
        );
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    composerEnabled,
    viewMode,
    editingTarget,
    selectedCells,
    selectedNode,
    template.body,
    apply,
    handleBulkCellPatch,
  ]);

  // Ctrl/Cmd+A → scope-aware 전체선택 (Composer Studio 마운트 중에만). 브라우저 기본 전체선택 차단.
  //  1) 셀 선택 모드(표에 드릴인): 그 표의 모든 셀 선택 (기존).
  //  2) 노드 모드: 선택 노드의 직속 컨테이너 형제 전체 선택(컨테이너 안이면 그 안 형제만, 최상위면
  //     body 전체). 표 노드를 "노드로" 선택한 상태는 노드 모드라 형제 선택(셀 전체선택은 드릴인 후).
  //  3) **작성 탭(writeForm)**: 위젯 축 동형 — 선택 위젯의 직속 컨테이너 형제 전체(컨테이너 안이면
  //     그 안 형제만, 최상위면 root 전체). 선택이 없으면 isolation 컨텍스트(모달·섹션 드릴인) 기준.
  //     양식 표 셀 포커스 중(ftFocusCell)은 **작성 탭 셀 핸들러의 Ctrl+A(그 표 전체 셀)가 담당**하므로
  //     여기서 개입하지 않는다(양식 탭의 selectedCells 분기와 같은 우선순위 규약).
  // 인라인 편집/입력 포커스 시 무시.
  useEffect(() => {
    if (!composerEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPlainMod(e, 'a')) return;
      if (viewMode !== 'form' && viewMode !== 'writeForm') return;
      if (editingTarget || isFormInputFocused()) return;

      // 3) 작성 탭 → 위젯 scope-aware 형제 전체 선택.
      if (viewMode === 'writeForm') {
        if (ftFocusCell) return; // 표 셀 전체선택 우선(셀 핸들러 담당) — 기본 동작도 그쪽이 차단
        // 작성 탭에서도 Ctrl+A는 "우리 것" — 대상 유무와 무관하게 브라우저 기본 전체선택을 차단한다
        // (빈 트리에서 빌더 UI 텍스트가 선택되던 양식 탭 버그와 동일 근거, 2026-07-07).
        e.preventDefault();
        const ids = composerWidgetSiblingIds(
          composerWidgetSync.tree,
          selectedWidgetId,
          writeSelectionContextId,
          widgetParentId,
        );
        if (ids.length === 0) return;
        setSelectedWidgetIds(ids);
        setSelectedWidgetId(selectedWidgetId && ids.includes(selectedWidgetId) ? selectedWidgetId : ids[0]);
        return;
      }

      // 폼 뷰·비편집이면 Ctrl+A는 항상 "우리 것" — 선택 대상(셀/노드) 유무와 무관하게 브라우저 기본
      // 전체선택(빌더 UI 텍스트 선택)을 먼저 차단한다(빈 템플릿에서 UI가 선택되던 버그, 2026-07-07).
      e.preventDefault();

      // 1) 셀 선택 모드 → 그 표의 모든 셀 선택.
      if (selectedCells.length > 0) {
        const tableId = selectedCells[0].tableId;
        const table = findNodeById(template.body, tableId);
        if (!table || table.kind !== 'table') return;
        // anchor(선두)는 좌상단(row→col 최소). 병합으로 덮인 슬롯은 cells에 없어 자동 제외.
        const sorted = [...table.cells].sort((x, y) => x.row - y.row || x.col - y.col);
        setSelectedCells(sorted.map((c) => ({ tableId, row: c.row, col: c.col })));
        selectSingleNode(tableId);
        return;
      }

      // 2) 노드 모드 → scope-aware 형제 전체 선택.
      const loc = selectedNodeId ? findNodeContainer(template.body, selectedNodeId) : null;
      const siblingIds = containerSiblingIds(template.body, loc);
      if (siblingIds.length === 0) return;
      setSelectedCells([]);
      setSelectedNodeId(
        selectedNodeId && siblingIds.includes(selectedNodeId) ? selectedNodeId : siblingIds[0],
      );
      setSelectedNodeIds(siblingIds);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    composerEnabled,
    viewMode,
    editingTarget,
    selectedCells,
    selectedNodeId,
    template.body,
    ftFocusCell,
    composerWidgetSync.tree,
    selectedWidgetId,
    writeSelectionContextId,
  ]);

  // ── 신규(new) — key 입력 모달을 연다(name은 key와 동일 기본값, 속성 패널에서 변경). ──
  // 미저장 변경 경고는 모달 안내문으로 통합(별도 confirm 단계 없음 — 복제 모달과 동일 패턴).
  const handleNew = useCallback(() => {
    pendingNewCategoryRef.current = null; // 헤더/레일 경로는 미분류로 시작(탐색기 「이 분류에 새 양식」만 선설정).
    setNewModalOpen(true);
  }, []);

  // 신규 확인 — key 입력 모달에서 검증 통과한 key로 빈 양식을 시작한다.
  const handleConfirmNew = useCallback(
    (rawKey: string) => {
      const trimmedKey = rawKey.trim();
      // 모달이 kebab/중복을 이미 검증하지만 방어적으로 재확인(콜백 경로 안전).
      if (!isKebabKey(trimmedKey)) {
        showToast(KEBAB_KEY_ERROR, 'error');
        return;
      }
      if (templateList.some((t) => t.key === trimmedKey)) {
        showToast(`이미 존재하는 key입니다: ${trimmedKey}`, 'error');
        return;
      }
      // 빈 양식(emptyTemplate)을 시작점으로 복제(deep clone) + key 부여, name은 key와 동일(속성 패널에서 변경).
      const starter: ReportTemplate = {
        ...structuredClone(emptyTemplate),
        key: trimmedKey,
        name: trimmedKey,
      };
      reset(starter);
      setCurrentKey(null); // 아직 저장 전이므로 create 경로.
      setCurrentEnabled(false); // 신규(미저장)는 비공개 — 토글은 currentKey 없을 때 미렌더.
      // 신규는 미분류로 시작(속성 「분류」에서 고르면 생성 body에 실린다) — 단 탐색기 「이 분류에 새 양식」
      // 경로는 그 분류를 미리 붙인다(저장본은 없으므로 savedCategoryId는 null → 그 자체로 dirty).
      setCurrentCategoryId(pendingNewCategoryRef.current);
      pendingNewCategoryRef.current = null;
      setSavedCategoryId(null);
      setPreviewContext({}); // 신규는 엔티티 선택 없이 깨끗하게 시작.
      setPreviewContextLabels({});
      setAutoApplySuppressed(false);
      lastAppliedCtxRef.current = null;
      simulationUnverifiedRef.current = false; // 복원값이 없는 시작 — 정리할 stale도 없다.
      setSelectedCells([]);
      selectSingleNode(null);
      setSavedSnapshot(null); // 신규는 저장본 없음 → 상시 dirty(저장 유도).
      try { localStorage.removeItem(LAST_TEMPLATE_KEY); } catch { /* storage 차단 무시 */ }
      setNewModalOpen(false);
    },
    [reset, showToast, templateList],
  );

  // ── 양식 업로드(form-import) — 이미지/PDF 양식에서 템플릿 초안 생성 ──
  // 설계: docs/decisions/2026-07-27-composer-form-import-ir.md (초안 스캐폴드 생성기)
  // 진행 중인 Job이 있으면 그 진행 상황을 보여 준다(새 업로드로 리셋하지 않는다 — 훅 판단).
  // 새로고침 복구(runJobRecovery)도 훅 안으로 옮겼다.
  const handleOpenFormImport = formImportJob.openModal;

  // 생성된 초안을 에디터로 실제 로드하는 부분 — 확인 게이트를 통과한 뒤에만 호출한다.
  const loadFormImportDraft = useCallback(
    async (templateKey: string) => {
      // 결과 소비 — 서버 Job 정리 + 모달 닫기(pill도 함께 사라진다).
      formImportJob.consumeResult();
      await refreshList();
      await performLoad(templateKey);
      showToast('양식에서 만든 초안을 불러왔습니다. 확인 후 저장·공개하세요.', 'success');
    },
    // consumeResult는 훅의 useCallback이라 안정적이다(formImportJob 객체 자체는 매 렌더 새로 만들어짐).
    [formImportJob.consumeResult, refreshList, performLoad, showToast],
  );

  // 생성된 초안을 에디터에서 열기 — 미저장 변경이 있으면 handleLoad와 동형의 확인 게이트를 거친다.
  //
  // ⚠️ consumeResult(결과 소비=Job 삭제 + 모달 닫기)는 **확인 뒤에만** 해야 한다. 게이트를
  //    통째로 앞에 두지 않고 로드 단계(loadFormImportDraft)를 따로 뺀 이유가 이것이다 — 소비를
  //    먼저 하면 "취소했는데 결과 목록이 사라지는" 상태가 되고, 배치 결과에서는 아직 열지도 않은
  //    나머지 초안 목록까지 함께 날아간다(초안 자체는 남지만 어느 것이 방금 만든 것인지 잃는다).
  const handleFormImportOpenEditor = useCallback(
    (templateKey: string) => {
      if (isDirty) {
        setConfirmState({
          title: '초안 불러오기',
          body: '저장하지 않은 변경이 있습니다. 양식에서 만든 초안을 불러오면 현재 편집 내용은 사라집니다.',
          confirmLabel: '불러오기',
          // 결과 모달(root z-[70]) 위에 뜨는 확인이라 한 층 올린다 — 같은 층이면 DOM 순서에
          // 기대게 되고, 결과 모달이 뒤로 옮겨지는 순간 확인이 뒤에 숨는다.
          overlayZClassName: 'z-[80]',
          overlayBgClassName: 'bg-black/25',
          onConfirm: () => {
            setConfirmState(null);
            void loadFormImportDraft(templateKey);
          },
        });
        return;
      }
      void loadFormImportDraft(templateKey);
    },
    [isDirty, loadFormImportDraft],
  );

  // ── 데이터 연결 후보 제안(P2) + 반복 영역 제안 원클릭(P3) ──
  // 설계: docs/decisions/2026-07-27-form-import-p2-binding-suggest.md
  // 임포트 초안 전용이 아니라 손저작 템플릿에도 쓰는 범용 도구(온디맨드 — 편집 상태 기준).
  const [bindingSuggestOpen, setBindingSuggestOpen] = useState(false);
  // 요청 대상 필드 — 열림 시점 편집 상태 스냅샷(적용으로 template이 바뀌어도 모달 목록·요청은 불변).
  const [bindingSuggestFields, setBindingSuggestFields] = useState<BindingSuggestField[]>([]);

  /**
   * bindingKey → 작성폼 라벨(파생+override 병합본) — suggest 라벨·반복 골격 필드 라벨 공용.
   * 규칙은 formImportOps.writeFormLabelsOf가 정본이다(결과 모달의 초안 일괄 적용도 같은 함수를
   * 쓴다 — 두 벌이 되면 같은 초안에 에디터로 만든 라벨과 모달로 만든 라벨이 갈린다).
   */
  const buildWriteFormLabels = useCallback(
    (): Record<string, string> => writeFormLabelsOf(template, catalogIndex),
    [template, catalogIndex],
  );

  const handleOpenBindingSuggest = useCallback(() => {
    setBindingSuggestFields(
      collectBindingSuggestFields(
        template,
        buildWriteFormLabels(),
        deriveWidgetGroupContexts(template.composerWidgetTree),
      ),
    );
    setBindingSuggestOpen(true);
  }, [template, buildWriteFormLabels]);

  // 적용된 연결 현황 — 후보 목록(열림 시점 스냅샷)과 달리 라이브 파생: 적용/해제가 즉시 반영.
  const appliedBindings = useMemo(
    () => (bindingSuggestOpen ? collectAppliedBindings(template, buildWriteFormLabels()) : []),
    [bindingSuggestOpen, template, buildWriteFormLabels],
  );

  // 연결/채움 해제(undo 1스텝) — 같은 키의 모든 셀/노드에서 출처 제거, 수동 입력 복귀.
  // 해제한 필드는 모달 후보 목록(열림 시점 스냅샷)에 즉시 추가한다 — 전체 보관된 후보에서
  // 직전 제안이 바로 되살아나 "해제 → 다른 후보로 재연결" 흐름이 한 열림 안에서 닫힌다.
  const handleDisconnectBinding = useCallback(
    (bindingKey: string) => {
      const preview = disconnectAppliedBindings(template, [bindingKey]);
      if (preview.disconnectedKeys.length === 0) {
        showToast('해제할 연결을 찾지 못했습니다(이미 해제됐을 수 있습니다).', 'error');
        return;
      }
      apply((t) => disconnectAppliedBindings(t, [bindingKey]).next);
      setBindingSuggestFields((prev) => {
        const label = buildWriteFormLabels()[bindingKey];
        const context = deriveWidgetGroupContexts(template.composerWidgetTree)[bindingKey];
        // 맨 뒤 append가 아니라 본문 트리 순서 위치에 삽입 — 목록의 원래 정렬 유지.
        return insertBindingSuggestField(template, prev, {
          bindingKey,
          ...(label ? { label } : {}),
          ...(context ? { context } : {}),
        });
      });
      showToast(
        '자동 채움 출처를 해제했습니다 — 수동 입력으로 복귀합니다. 저장해야 반영됩니다.',
        'success',
      );
    },
    [template, apply, showToast, buildWriteFormLabels],
  );

  // 후보 적용(undo 1스텝) — 'connect'=카탈로그 완전 연결(시스템 값 자동 유입, 작성 입력 제외 —
  // "최소 수동입력" 제품 원칙의 기본값) / 'prefill'=자동 채움(입력 유지+초기값). 저장은 기존 저장 버튼.
  const handleApplyBindingSuggestions = useCallback(
    (items: BindingSuggestion[], mode: 'connect' | 'prefill') => {
      const op = mode === 'connect' ? applyCatalogConnectSuggestions : applyPrefillSuggestions;
      const preview = op(template, items);
      if (preview.appliedKeys.length === 0) {
        showToast('적용할 대상 필드를 찾지 못했습니다(필드가 삭제됐을 수 있습니다).', 'error');
        return;
      }
      apply((t) => op(t, items).next);
      showToast(
        mode === 'connect'
          ? `${preview.appliedKeys.length}개 필드를 시스템 데이터에 연결했습니다 — 작성 입력칸에서 빠집니다. 작성 탭에서 남은 위젯을 정리하고 저장하세요.`
          : `자동 채움 ${preview.appliedKeys.length}개 필드에 적용했습니다 — 저장해야 반영됩니다.`,
        'success',
      );
    },
    [template, apply, showToast],
  );

  // 반복 영역 제안 원클릭 — 반복영역 + 수동 데이터셋 골격 + hint 소진을 undo 1스텝으로.
  // 작성 페이지 트리 정리(행 데이터셋 위젯 배치·기존 입력 orphan 제거)는 기존 트리 UI로 사람이 확정.
  const handleApplyImportRepeatHint = useCallback(
    (hint: ImportRepeatHint) => {
      const labels = buildWriteFormLabels();
      const preview = applyRepeatHintToTemplate(template, hint, labels);
      if (preview.error) {
        showToast(preview.error, 'error');
        return;
      }
      apply((t) => applyRepeatHintToTemplate(t, hint, labels).next);
      showToast(
        `반복영역을 적용했습니다 — 데이터셋 '${preview.dsKey}' 골격(필드 ${preview.fieldCount}개) 생성. ` +
          '작성 페이지 트리에서 행 데이터셋 위젯을 배치하고 기존 입력 위젯(orphan)을 정리하세요.',
        'success',
      );
    },
    [template, buildWriteFormLabels, apply, showToast],
  );
  const handleDismissImportRepeatHint = useCallback(
    (hint: ImportRepeatHint) => {
      apply((t) => removeImportRepeatHint(t, hint));
      showToast('반복 영역 제안을 지웠습니다.', 'info');
    },
    [apply, showToast],
  );

  // ── 작성 탭 위젯 자동 배치(P1) — body 구조에서 결정적 스캐폴드(writeFormAutoLayout) ──
  // 풀은 작성 탭 미배치 풀과 같은 소스(deriveComposerWidgets). 실행은 apply 1스텝(undo 한 번에
  // 복귀) + 결과 토스트. 미배치 채우기(비파괴)는 즉시, 전체 재구성(파괴)은 confirmState 퍼널 경유.
  const handleAutoLayoutFill = useCallback(() => {
    const preview = fillUnplacedWidgets(template, deriveComposerWidgets(template, catalogIndex));
    if (preview.placedCount === 0) {
      showToast('배치할 미배치 위젯이 없습니다.', 'info');
      return;
    }
    apply((t) => fillUnplacedWidgets(t, deriveComposerWidgets(t, catalogIndex)).next);
    showToast(
      `미배치 위젯 ${preview.placedCount}개를 소속 그룹에 배치했습니다 — 저장해야 반영됩니다.`,
      'success',
    );
  }, [template, catalogIndex, apply, showToast]);

  const handleAutoLayoutRebuild = useCallback(() => {
    setConfirmState({
      title: '작성 트리 전체 재구성',
      // 권장 순서 가이드(2026-07-27 사용자 확정) — 연결된 칸은 파생에서 빠지므로 바인딩을
      // 먼저 마칠수록 재구성 결과가 깔끔하다(반복영역도 먼저 적용해야 데이터셋 위젯으로 배치).
      body:
        '현재 작성 트리를 버리고 양식 구조에서 새로 만듭니다. 실행취소로 되돌릴 수 있습니다.\n\n' +
        'TIP: 보고서 탭에서 데이터 연결과 반복 영역을 먼저 마치면 더 깔끔하게 배치됩니다' +
        '(연결된 칸은 작성 입력에서 빠집니다).',
      confirmLabel: '전체 재구성',
      danger: true,
      onConfirm: () => {
        setConfirmState(null);
        const preview = rebuildWidgetTreeFromBody(
          template,
          deriveComposerWidgets(template, catalogIndex),
        );
        apply((t) => rebuildWidgetTreeFromBody(t, deriveComposerWidgets(t, catalogIndex)).next);
        showToast(
          `작성 트리를 재구성했습니다 — 그룹 ${preview.groupCount}개 · 위젯 ${preview.widgetCount}개. 저장해야 반영됩니다.`,
          'success',
        );
      },
    });
  }, [template, catalogIndex, apply, showToast]);

  // ── 복제(duplicate) — 저장된 템플릿을 서버측 콘텐츠 복사로 새 key/name 생성 후 로드 ──
  // key 입력은 TemplateDuplicateModal로 받는다(이름은 '{name} (복제)' 자동, 속성 패널에서 수정).
  // 콘텐츠 재조립은 서버가 저장본에서 수행(필드 누락 방지). 미저장 변경은 복제본에 미반영.
  // 헤더 버튼은 열린 양식(currentKey), 탐색기 행 ⋯은 임의 key — 같은 모달·API(§3 「양쪽」).
  const handleDuplicateKey = useCallback(
    (key: string) => {
      const name = key === currentKey ? template.name : templateList.find((t) => t.key === key)?.name;
      setDuplicateSource({ key, name: name ?? key });
    },
    [currentKey, template.name, templateList],
  );
  const handleDuplicate = useCallback(() => {
    if (!currentKey) return; // 저장된 템플릿만 복제 가능(버튼 disabled와 일치).
    handleDuplicateKey(currentKey);
  }, [currentKey, handleDuplicateKey]);

  // 복제 확인 — 모달에서 검증 통과한 새 key를 받아 서버 복제 요청.
  // 이름은 서버 DTO 필수라 '{원본이름} (복제)'로 자동 전송. 성공 시 모달 닫고 복제본 로드,
  // 실패 시 모달 유지(key 재입력 가능) + 에러 토스트.
  const handleConfirmDuplicate = useCallback(
    async (newKey: string) => {
      if (!duplicateSource) return;
      setBusy(true);
      try {
        const res = await adminApi.duplicateReportTemplate(duplicateSource.key, {
          key: newKey,
          name: `${duplicateSource.name} (복제)`,
        });
        if (res.success && res.data) {
          setDuplicateSource(null); // 성공 시에만 닫기.
          showToast('템플릿을 복제했습니다.', 'success');
          await refreshList();
          // 방금 만든 복제본을 로드 — 확인 게이트 없는 performLoad 사용(복제 직후 nested 확인 모달 방지).
          await performLoad(res.data.key);
        } else {
          showToast(res.error?.message ?? '복제에 실패했습니다.', 'error');
        }
      } catch {
        showToast('복제 중 오류가 발생했습니다.', 'error');
      } finally {
        setBusy(false);
      }
    },
    [duplicateSource, showToast, refreshList, performLoad],
  );

  // ── 삭제(delete) 실제 수행 — 확인 게이트 없이 서버 삭제. ──
  // 열린 양식을 지웠으면 에디터를 빈 템플릿으로 되돌리고, 다른 양식(탐색기 행 ⋯)이면 목록만 갱신.
  const performDelete = useCallback(async (key: string) => {
    setBusy(true);
    try {
      const res = await adminApi.deleteReportTemplate(key);
      if (res.success) {
        showToast('템플릿이 삭제되었습니다.', 'success');
        if (key === currentKey) {
          setCurrentKey(null);
          setCurrentEnabled(false); // 삭제 후 빈 템플릿 = 비공개로 정리.
          setCurrentCategoryId(null); // 삭제 후 빈 템플릿 = 미분류(깨끗).
          setSavedCategoryId(null);
          reset(emptyTemplate);
          setSelectedCells([]);
          selectSingleNode(null);
          setSavedSnapshot(emptyTemplate); // 삭제 후 빈 템플릿으로 리셋 = 깨끗(reset 대상과 같은 참조).
          try { localStorage.removeItem(LAST_TEMPLATE_KEY); } catch { /* storage 차단 무시 */ }
        }
        void refreshList();
        void refreshCategories(); // 삭제된 양식이 빠진 분류의 templateCount 갱신.
      } else {
        showToast(res.error?.message ?? '삭제에 실패했습니다.', 'error');
      }
    } catch {
      showToast('삭제 중 오류가 발생했습니다.', 'error');
    } finally {
      setBusy(false);
    }
  }, [currentKey, reset, refreshList, refreshCategories, showToast]);

  // ── 삭제(delete) — danger 확인 모달을 거친 뒤 삭제. key 생략 시 열린 양식. ──
  // L24(US-0723): 확인 모달을 열기 «전»에 이 양식을 쓰는 법정 의무를 읽어 본문에 싣는다 — 비차단(확인하면 지운다).
  // 조회가 실패해도 삭제 경로는 막지 않고 「확인하지 못했다」를 따로 말한다(빈 목록과 같은 모양이면 경고를 놓친다).
  const handleDeleteTemplate = useCallback(async (key?: string) => {
    const target = key ?? currentKey;
    if (!target) return;
    let obligations: TemplateDeleteObligation[] | null = null;
    try {
      const impact = await adminApi.getReportTemplateDeleteImpact(target);
      if (impact.success && impact.data) obligations = impact.data.obligations;
    } catch {
      obligations = null;
    }
    setConfirmState({
      title: '템플릿 삭제',
      body: <TemplateDeleteConfirmBody templateKey={target} obligations={obligations} />,
      confirmLabel: '삭제',
      danger: true,
      onConfirm: () => {
        setConfirmState(null);
        void performDelete(target);
      },
    });
  }, [currentKey, performDelete]);

  // ── 공개(enabled) 토글 확인 — 본문 저장과 분리된 단독 PATCH ──
  // 편집 중 미저장 변경(isDirty)과 무관하게 enabled만 갱신한다(body 등 다른 필드는 payload에 넣지 않음).
  // 대상은 publishTarget.key — 열린 양식이면 헤더 토글 state(currentEnabled)도 함께 맞춘다.
  const handleConfirmPublish = useCallback(async () => {
    if (publishTarget === null) return;
    const { key, next } = publishTarget;
    setPublishBusy(true);
    try {
      const res = await adminApi.updateReportTemplate(key, { enabled: next });
      if (res.success && res.data) {
        const enabledNow = res.data.enabled;
        if (key === currentKey) setCurrentEnabled(enabledNow);
        // 목록 캐시(탐색기 행 ●/○ 원본)의 해당 항목 enabled도 동기화.
        setTemplateList((prev) =>
          prev.map((t) => (t.key === key ? { ...t, enabled: enabledNow } : t)),
        );
        showToast(next ? '템플릿을 공개했습니다.' : '템플릿을 비공개로 전환했습니다.', 'success');
        setPublishTarget(null); // 성공 시에만 모달 닫기.
      } else {
        // 실패 — state는 성공 시에만 바꾸므로 원복 불필요. 모달은 열어 두어 재시도 가능.
        showToast(res.error?.message ?? '공개 상태 변경에 실패했습니다.', 'error');
      }
    } catch {
      showToast('공개 상태 변경 중 오류가 발생했습니다.', 'error');
    } finally {
      setPublishBusy(false);
    }
  }, [currentKey, publishTarget, showToast]);

  // ── 탐색기 콜백 — 배정 반영 뒤 목록·분류 재조회 + 열린 양식이면 속성 「분류」 select 동기화 ──
  // 드래그 배정은 서버에 바로 반영되므로 저장본 기준값(savedCategoryId)을 따라 내린다. 사용자가
  // select를 그새 다른 값으로 바꿔 둔 상태(dirty)면 그 선택은 보존한다(저장 시 새 배정으로 나간다).
  const handleExplorerAssigned = useCallback(
    (moved: { key: string; categoryId: string | null }[]) => {
      void refreshList();
      void refreshCategories();
      const mine = currentKey ? moved.find((m) => m.key === currentKey) : undefined;
      if (!mine) return;
      const prevSaved = savedCategoryId;
      setSavedCategoryId(mine.categoryId);
      setCurrentCategoryId((cur) => (cur === prevSaved ? mine.categoryId : cur));
    },
    [currentKey, savedCategoryId, refreshList, refreshCategories],
  );
  const handleExplorerCategoriesChanged = useCallback(() => {
    void refreshList();
    void refreshCategories().then((categories) => {
      // 편집 중 양식의 분류가 방금 지워졌으면 서버는 이미 미분류다(관리 모달 onSaved와 동형).
      if (!categories || !savedCategoryId) return;
      if (categories.some((c) => c.id === savedCategoryId)) return;
      setSavedCategoryId(null);
      setCurrentCategoryId((cur) => (cur === savedCategoryId ? null : cur));
    });
  }, [refreshList, refreshCategories, savedCategoryId]);
  // 「이 분류에 새 양식」 — 새 템플릿 key 모달을 열되, 시작 시 그 분류가 미리 붙도록 기억한다.
  const handleExplorerNewTemplate = useCallback((categoryId: string | null) => {
    pendingNewCategoryRef.current = categoryId;
    setNewModalOpen(true);
  }, []);

  // ── 캔버스 우클릭 메뉴 항목 조립(양식/작성 탭) — 렌더 시점의 현재 상태로 구성 ──
  // 열기 핸들러가 우클릭 시 클릭과 동일 규칙으로 선택을 이미 보정했으므로(셀 선택은 해제),
  // 항목 액션은 전부 기존 선택 기반 핸들러(단축키와 동일 경로)를 그대로 공유한다.
  const buildFormCtxItems = (target: 'selection' | 'canvas'): ComposerContextMenuItem[] => {
    // 요소 추가 — 팔레트 전체(insertNodeAtSelection 규칙: 선택 그룹 안/선택 뒤/미선택=body 말미.
    // 셀 선택은 우클릭 시 해제되므로 셀 게이트는 여기 도달 전 소거 — 잔여 케이스는 내부 toast가 안내).
    const addItems: ComposerContextMenuItem[] = PALETTE_ITEMS.map((item) => ({
      type: 'action' as const,
      label: item.label,
      onSelect: () => {
        if (item.radioGroup) handleAddRadioGroup();
        else handleAddNode(item.kind, item.special);
      },
    }));
    if (target === 'canvas') {
      return [
        { type: 'submenu', label: '요소 추가', items: addItems },
        { type: 'separator' },
        // 붙여넣기 가능 여부는 OS 클립보드가 비동기라 동기 판정 불가 — 항상 활성, 내용 없으면 no-op.
        { type: 'action', label: '붙여넣기', shortcut: 'Ctrl+V', onSelect: () => void triggerNodePaste() },
      ];
    }
    const hasSelection = selectedNodeIds.length > 0;
    return [
      { type: 'submenu', label: '요소 추가', items: addItems },
      { type: 'separator' },
      {
        type: 'action',
        // 우클릭 원시 대상(승격 전 셀/노드) 기준 — 선택 기반(crossNavTargetWidgetId)은 그룹 승격
        // 때문에 비활성 오판(2026-07-16 제보). 오프너가 캡처한 값 우선, 없으면 선택 기반 폴백.
        label: '위젯으로 이동',
        disabled: !(ctxMenu?.formNavWidgetId ?? crossNavTargetWidgetId),
        disabledReason: '배치된 작성 위젯이 없습니다.',
        onSelect: () => {
          const target = ctxMenu?.formNavWidgetId ?? crossNavTargetWidgetId;
          if (target) navigateToWriteWidget(target);
        },
      },
      { type: 'separator' },
      {
        type: 'action',
        label: '잘라내기',
        shortcut: 'Ctrl+X',
        disabled: !hasSelection,
        onSelect: () => void handleNodeCut(),
      },
      {
        type: 'action',
        label: '복사',
        shortcut: 'Ctrl+C',
        disabled: !hasSelection,
        onSelect: () => void handleNodeCopy(),
      },
      { type: 'action', label: '붙여넣기', shortcut: 'Ctrl+V', onSelect: () => void triggerNodePaste() },
      {
        type: 'action',
        label: '복제',
        shortcut: 'Ctrl+D',
        disabled: !hasSelection,
        onSelect: handleDuplicateNode,
      },
      { type: 'separator' },
      {
        type: 'action',
        label: '삭제',
        shortcut: 'Delete',
        danger: true,
        disabled: !hasSelection,
        onSelect: handleDelete,
      },
    ];
  };
  const buildWriteCtxItems = (
    target: 'selection' | 'canvas',
    /** 우클릭 원시 대상(승격 전) — '위젯 추가'의 삽입 위치에만 쓴다. ctxMenu.addAnchorId. */
    addAnchorId?: string | null,
  ): ComposerContextMenuItem[] => {
    const tree = composerWidgetSync.tree;
    // 위젯 추가/배치 위치(anchor 선택·모달/isolation fallback 포함)는 composerAddPlacement의
    // resolveWriteAddPlacement가 정본 — 여기선 트리·페이지 상태를 인자로 넘기기만 한다.
    const anchor = selectedWidgetId ? selectedWidgetById(tree, selectedWidgetId) : null;
    const placement = resolveWriteAddPlacement({
      target,
      selectedAnchor: anchor,
      rawAddAnchor: addAnchorId ? selectedWidgetById(tree, addAnchorId) : null,
      parentIdOf: (id) => widgetParentId(tree, id),
      modalAuthoringId,
      writeSelectionContextId,
    });
    const addAfterId = placement.afterId;
    const addParentId = placement.parentId;
    // 반복·모달 버튼 게이트는 트리 팔레트(AddWidgetControl)와 동일 정본(addWidgetGates).
    const addParentWidget = addParentId ? selectedWidgetById(tree, addParentId) : null;
    const { allowRepeat, allowModalTrigger } = addWidgetGates(addParentWidget?.kind);
    // 기본/특화 구성은 트리 팔레트 "+ 위젯"(AddWidgetControl)과 **같은 카탈로그**에서 파생한다 —
    // 목록을 두 벌 유지하면 진입점에 따라 만들 수 있는 위젯이 달라진다(2026-07-22 실사고).
    // 특정 데이터소스에 바인딩된 반복(반복: <데이터셋 이름>)은 팔레트/우클릭이 아니라 **미배치 풀**에서
    // 배치한다(풀 배치=handleWidgetPlace가 반복 위젯+뼈대로 변환 — 강제 단일화). 데이터소스 이름 나열은
    // 이 템플릿 고유 데이터라 범용 위젯 팔레트에 노출하지 않는다(2026-07-20 사용자 확정).
    const ctxAddCallbacks: AddWidgetCallbacks = {
      onAddGroup: () => handleWidgetAddGroup(addParentId, addAfterId),
      onAddDesign: (kind) => handleWidgetAddDesign(kind, addParentId, addAfterId),
      onAddRegistered: (widgetKey) =>
        handleWidgetAddRegistered(widgetKey, addParentId, addAfterId),
      onAddFormTable: () => handleWidgetAddFormTable(addParentId, addAfterId),
      onAddInput: () => handleWidgetAddInput(addParentId, addAfterId),
      onAddModalTrigger: () => handleWidgetAddModalTrigger(addParentId, addAfterId),
      onAddRepeat: () => handleWidgetAddRepeat(addParentId, addAfterId),
      onAddBranch: () => handleWidgetAddBranch(addParentId, addAfterId),
      // 프리셋은 별도 search-list 항목('프리셋')이 담당 — 추가 목록에는 포함하지 않는다.
      onInsertPreset: (presetId) => void handleInsertPreset(presetId, addParentId, addAfterId),
    };
    const ctxRegisteredEntries = registeredAddWidgetEntries();
    const addItems: ComposerContextMenuItem[] = [
      ...coreAddWidgetEntries({ allowModalTrigger, allowRepeat }).map(
        (e): ComposerContextMenuItem => ({
          type: 'action',
          label: e.label,
          onSelect: () => {
            dispatchAddWidgetValue(e.value, ctxAddCallbacks);
          },
        }),
      ),
      ...(ctxRegisteredEntries.length > 0
        ? [
            { type: 'separator' as const },
            ...ctxRegisteredEntries.map(
              (e): ComposerContextMenuItem => ({
                type: 'action',
                label: e.label,
                onSelect: () => {
                  dispatchAddWidgetValue(e.value, ctxAddCallbacks);
                },
              }),
            ),
          ]
        : []),
    ];
    // 미배치 위젯 배치 — 검색 목록 서브메뉴(미입력=전체 스크롤 선택, 입력=풀 검색과 동일 매칭:
    // 라벨/bindingKey/widget 타입). 배치 위치는 위젯 추가와 동일 규칙(addParentId/addAfterId), 배치 후 선택.
    const unplacedItem: ComposerContextMenuItem = {
      type: 'search-list',
      label: '미배치 위젯 배치',
      placeholder: '검색 — 라벨·바인딩 키·타입',
      disabled: composerWidgetSync.unplaced.length === 0,
      disabledReason: '미배치 위젯이 없습니다.',
      // 서브메뉴 전환/메뉴 닫힘 시 미니 미리보기 잔존 정리(mouseleave 미발화 경로).
      onHoverClear: () => showPoolPreview(null, null),
      options: composerWidgetSync.unplaced.map((u) => ({
        id: u.id,
        label: composerInputPrimaryLabel(u),
        hint: `${u.bindingKey} · ${u.widget}`,
        // 항목 hover → 풀 행과 동일한 미니 미리보기(모달 편집 중 우클릭 포함, 2026-07-16 사용자 확정).
        onHover: (rect: DOMRect | null) => {
          if (!rect) {
            showPoolPreview(null, null);
            return;
          }
          const pl = bindingPlacementsForWidget(u, bindingPlacementMap)?.[0];
          if (pl) showPoolPreview(pl, rect);
        },
        onSelect: () => {
          handleWidgetPlace(u, addParentId, addAfterId);
          // 배치 직후 정확 선택(속성창 연동). 단 dataSourceRows는 repeat로 대체 삽입돼 u.id가
          // 트리에 없음 — handleWidgetPlace 내부 selectAfterAddRef가 새 repeat id를 선택한다.
          if (u.widget !== 'dataSourceRows') selectWidget(u.id, false, false, 'tree');
        },
      })),
    };
    // 프리셋 라이브러리 삽입 — 검색 목록 서브메뉴(v1은 플랫폼 프리셋만). 항목 클릭 → GET presets/:id
    // 스냅샷 클론+presetRef 스탬프 후 위젯 추가와 동일 위치·undo 규칙으로 삽입. 플랫폼은 "제공" 배지(hint).
    const presetItem: ComposerContextMenuItem = {
      type: 'search-list',
      label: '프리셋',
      placeholder: '검색 — 프리셋 이름·key',
      disabled: presetLibrary.length === 0,
      disabledReason: '저장된 프리셋이 없습니다.',
      options: presetLibrary.map((p) => ({
        id: p.id,
        label: p.name,
        hint: `${p.ownerCompanyId === null ? '제공 · ' : ''}${p.key} · v${p.currentVersion}`,
        onSelect: () => void handleInsertPreset(p.id, addParentId, addAfterId),
      })),
    };
    // ⚠️ 붙여넣기는 **비활성 판정을 두지 않는다**(노드 우클릭 메뉴와 동형) — 클립보드가 OS 채널로
    // 옮겨간 뒤로는 붙일 게 있는지 동기적으로 알 수 없다(readText는 비동기). 로컬 상태(cut 버퍼)로
    // 판정하면 다른 창·다른 PC에서 복사한 payload가 "복사된 위젯이 없습니다"로 막힌다. 붙일 게 없으면
    // 실행 시 토스트로 알린다.
    if (target === 'canvas') {
      return [
        { type: 'submenu', label: '위젯 추가', items: addItems },
        unplacedItem,
        // 프리셋은 코드 위젯(추가/미배치)과 성격이 달라 구분선으로 분리(2026-07-19 사용자 요청).
        { type: 'separator' },
        presetItem,
        { type: 'separator' },
        {
          type: 'action',
          label: '붙여넣기',
          shortcut: 'Ctrl+V',
          onSelect: () => void handleWidgetPaste(),
        },
      ];
    }
    const hasSelection = selectedWidgetIds.length > 0 || selectedWidgetId != null;
    // "프리셋으로 저장"은 단일 루트 위젯만(v1 범위 — 섹션 묶음은 그룹으로 감싸 저장). 다중 선택 시 비활성.
    const singleSelectedForPreset = anchor != null && selectedWidgetIds.length <= 1;
    // 작성 위젯 → 양식 노드로 이동(작성→양식 순방향). anchor 위젯이 파생(body 배치)이면 첫 배치가 대상 —
    // 표 셀 배치면 그 **셀까지 선택**(placement.cell 전달, 2026-07-16 사용자 확정).
    const navPlacement = anchor
      ? bindingPlacementsForWidget(anchor, bindingPlacementMap)?.[0]
      : undefined;
    // 구조 이동(순서·부모) — 트리 행의 "⋯" 콤보를 우클릭 메뉴로 일원화(2026-07-22 사용자 확정:
    // 행에는 ↑/↓ 인라인만 남기고 나머지 구조 조작은 우클릭 메뉴). 단일 선택 + anchor가 트리 위젯일
    // 때만 노출 — 표 셀 위젯은 그리드 슬롯이라 순서/부모 개념이 없다(widgetParentId=undefined).
    const anchorParentId = anchor ? widgetParentId(tree, anchor.id) : undefined;
    const moveGroup: ComposerContextMenuItem[] =
      anchor && anchorParentId !== undefined && selectedWidgetIds.length <= 1
        ? (() => {
            // 자기 자신/후손 섹션으로는 이동 불가(순환) — 트리 행 콤보와 동일 필터.
            const subtree = composerSubtreeIds(anchor);
            const sectionTargets = collectGroupOptions(tree).filter((g) => !subtree.has(g.id));
            const hasParent = typeof anchorParentId === 'string';
            // '밖으로'는 handleWidgetMoveOut이 **조부모를 root/섹션/모달로만** 인정하므로, 분기 슬롯
            // 안이나 반복 안 섹션에서는 조용히 무동작한다(2026-07-22 리뷰 지적 — 활성인데 눌러도
            // 반응 없음, undo 스텝조차 안 남음). 그런 경우는 아예 비활성화하고 사유를 알린다.
            const moveOutGrandId = hasParent
              ? (widgetParentId(tree, anchorParentId) ?? null)
              : null;
            const moveOutGrandKind = moveOutGrandId
              ? selectedWidgetById(tree, moveOutGrandId)?.kind
              : undefined;
            const canMoveOut =
              hasParent &&
              (moveOutGrandId == null ||
                moveOutGrandKind === 'group' ||
                moveOutGrandKind === 'modalTrigger');
            const items: ComposerContextMenuItem[] = [
              {
                type: 'action',
                label: '위로',
                onSelect: () => handleWidgetReorder(anchor.id, 'up'),
              },
              {
                type: 'action',
                label: '아래로',
                onSelect: () => handleWidgetReorder(anchor.id, 'down'),
              },
              { type: 'separator' },
              {
                type: 'action',
                label: '밖으로 (부모 바로 다음)',
                disabled: !canMoveOut,
                disabledReason: !hasParent
                  ? '이미 최상위입니다.'
                  : '분기 슬롯·반복 안에서는 한 단계 밖으로 이동할 수 없습니다. "루트로"나 "다른 섹션으로"를 쓰세요.',
                onSelect: () => handleWidgetMoveOut(anchor.id),
              },
              {
                type: 'action',
                label: '루트로',
                disabled: !hasParent,
                disabledReason: '이미 최상위입니다.',
                onSelect: () => handleWidgetMoveTo(anchor.id, null),
              },
              { type: 'separator' },
              {
                // 섹션이 많을 수 있어 검색 목록(미배치/프리셋과 동일 셸) — 3단 중첩이지만
                // '이동' 패널은 항목이 적어 스크롤 컨테이너가 아니므로 손자 패널이 잘리지 않는다
                // (ComposerContextMenu의 hasNestedSub 처리). 라벨 앞 전각 공백은
                // collectGroupOptions의 계층 들여쓰기라 그대로 둔다.
                type: 'search-list',
                label: '다른 섹션으로',
                placeholder: '검색 — 섹션명',
                disabled: sectionTargets.length === 0,
                disabledReason: '이동할 섹션이 없습니다.',
                options: sectionTargets.map((g) => ({
                  id: g.id,
                  label: g.label,
                  onSelect: () => handleWidgetMoveTo(anchor.id, g.id),
                })),
              },
            ];
            return [{ type: 'separator' }, { type: 'submenu', label: '이동', items }];
          })()
        : [];
    return [
      { type: 'submenu', label: '위젯 추가', items: addItems },
      unplacedItem,
      // 프리셋은 코드 위젯(추가/미배치)과 성격이 달라 구분선으로 분리(2026-07-19 사용자 요청).
      { type: 'separator' },
      presetItem,
      ...moveGroup,
      { type: 'separator' },
      {
        type: 'action',
        label: '양식 노드로 이동',
        disabled: !navPlacement,
        disabledReason: '양식에 배치된 바인딩이 아닙니다.',
        onSelect: () => {
          if (navPlacement) navigateToBodyNode(navPlacement.nodeId, navPlacement.cell);
        },
      },
      {
        type: 'action',
        label: '프리셋으로 저장',
        disabled: !singleSelectedForPreset,
        disabledReason: '단일 위젯을 선택하세요(섹션 묶음은 그룹으로 감싸 저장).',
        onSelect: () => {
          if (anchor) handleSaveAsPreset(anchor.id);
        },
      },
      { type: 'separator' },
      {
        type: 'action',
        label: '잘라내기',
        shortcut: 'Ctrl+X',
        disabled: !hasSelection,
        onSelect: () => void handleWidgetCut(),
      },
      {
        type: 'action',
        label: '복사',
        shortcut: 'Ctrl+C',
        disabled: !hasSelection,
        onSelect: () => void handleWidgetCopy(),
      },
      {
        type: 'action',
        label: '붙여넣기',
        shortcut: 'Ctrl+V',
        onSelect: () => void handleWidgetPaste(),
      },
      {
        type: 'action',
        label: '복제',
        shortcut: 'Ctrl+D',
        disabled: !hasSelection,
        onSelect: handleWidgetDuplicate,
      },
      { type: 'separator' },
      {
        type: 'action',
        label: '삭제',
        shortcut: 'Delete',
        danger: true,
        disabled: !hasSelection,
        onSelect: handleWidgetDeleteSelected,
      },
    ];
  };

  // 카탈로그 파라미터 편집 채널 — 바인딩 에디터가 "출처를 고른 그 자리에서" 파라미터를 채우게 한다.
  // 저장 자리는 전역 편집기(CatalogParamsEditor)와 **같은** `template.catalogParams`라, 한쪽에서
  // 고친 값이 다른 쪽에 그대로 보인다(진입점만 둘, 저장소는 하나 — 2026-08-06 결정 개정 1).
  // 해소 스탬프(previewResult.paramResolution)도 같이 흘린다 — 선언("미지정이면 무엇을 하나")만으로는
  // 저작자가 자기 선택을 검증할 수 없어, 실제 낙점 값을 같은 자리에서 보여준다(결정 2026-08-10 §3).
  const catalogParamsChannel = useMemo<CatalogParamsEditChannel>(
    () => ({
      bindings: template.catalogParams,
      variables: template.variables ?? [],
      ...(previewResult?.paramResolution
        ? { resolution: previewResult.paramResolution }
        : {}),
      hasPreviewContext: !!previewResult,
      // 해소된 세트 id → 이름. 새 조회를 발명하지 않고 **이미 받아 둔 행**만 본다: prefill 결과
      // (변수 optionsFrom 소스가 여기로 들어온다)를 먼저, 없으면 미리보기 응답 content. 둘 다
      // 없으면 undefined → 화면은 종전대로 raw id로 degrade한다(가짜 이름 금지).
      // 행은 ref로 읽는다 — 작성 캔버스 입력마다 바뀌는 값이라 deps에 넣으면 채널 identity가
      // 매 타자마다 흔들린다(ref는 호출 시점의 최신값이라 표시도 늦지 않는다).
      effectiveNameOf: (catalogKey, id) =>
        catalogEffectiveIdName(
          catalog.find((e) => e.key === catalogKey),
          id,
          (sourceKey) =>
            writeCanvasValuesRef.current[sourceKey] ?? previewResult?.content[sourceKey],
        ),
      onPickPreviewContext: () => setRealDataModalOpen(true),
      setBinding: (catalogKey, paramName, binding) =>
        apply((t) => ({
          ...t,
          catalogParams: setCatalogParamBinding(t.catalogParams, catalogKey, paramName, binding),
        })),
    }),
    [template.catalogParams, template.variables, previewResult, catalog, apply],
  );

  // 문서 상태 배지의 원료 — 각 패널이 이미 내던 저작 경고를 한 자리에서 센다(집계는 순수 층).
  // 미리보기 해소(previewResult)에는 의존하지 않는다: 배지는 "저장물의 상태"라 실데이터 반영 여부와
  // 무관해야 하고, 그래야 컨텍스트를 안 고른 저작자에게도 같은 숫자가 보인다.
  // 변수 참조 조망은 변수 편집기용으로 이미 만들어 둔 것을 넘긴다 — 전수 순회 한 벌을 아낀다.
  const templateIssues = useMemo(
    // 공개 상태(currentEnabled)는 편집 funnel 밖 state라 따로 넘긴다 — `write-tree-missing`이
    // 공개된 양식에만 뜨는 게이트에 쓰인다(공개 토글을 켜면 그 자리에서 배지가 다시 센다).
    () => collectTemplateIssues(template, catalog, variableRefs, currentEnabled),
    [template, catalog, variableRefs, currentEnabled],
  );

  /**
   * 지금 고른 노드에 걸리는 **페이지 넘침** 경고 문구 — 속성 패널의 높이 편집 옆에 그대로 띄운다.
   *
   * 배지만으로는 처방이 닫히지 않는다: 배지는 저장 버튼 옆(문서 전역)에 있고 값을 고치는 자리는
   * 속성 패널이라, 저작자가 "몇 px 이하로"를 외워서 옮겨와야 했다. 경고를 **조작 지점 옆에** 두면
   * 그 왕복이 사라진다.
   *
   * 단위 소유자(보통 반복 노드)와 처방 대상 사진 **양쪽**에서 걸리게 한다 — 저작자는 사진을 고르지
   * 반복을 고르지 않는다.
   */
  const selectedOverflowNotes = useMemo(() => {
    const id = selectedNode?.id;
    if (!id) return [];
    return templateIssues
      .filter(
        (issue) =>
          (issue.kind === 'layout-overflow-certain' ||
            issue.kind === 'layout-overflow-possible') &&
          (issue.nodeId === id || issue.imageNodeIds?.includes(id)),
      )
      .map((issue) => issue.label);
  }, [templateIssues, selectedNode?.id]);

  /**
   * 배지 항목 클릭 → 그 경고를 고칠 자리로 이동.
   *
   * 조회 파라미터·전역 변수 섹션은 **선택이 없을 때만** 그려지는 템플릿 전역 패널 안에 있어,
   * 스크롤 전에 선택을 먼저 비워야 한다(패널이 없는 상태에서 scrollIntoView는 조용히 아무 일도
   * 하지 않는다). 그 상태 반영이 한 렌더 뒤라 rAF로 미룬다.
   */
  const handleGoToIssue = useCallback(
    (issue: TemplateIssue) => {
      setSelectedCells([]);
      setSelectedChrome(null);
      if (issue.target === 'write-canvas') {
        // 작성 트리 자체가 없는 경고 — 데려갈 곳은 본문 탭이 아니라 **작성 탭**이다. 선택할 노드도
        // 없으므로(그 트리가 비어 있다는 것이 경고의 내용) 탭 전환만 하고 선택은 비운다.
        setViewMode('writeForm');
        selectSingleNode(null);
        return;
      }
      setViewMode('form');
      if (issue.target === 'canvas') {
        // 캔버스 노드에 걸린 경고(식별 키 중복·페이지 넘침) — 그 노드를 선택해 보이게 한다
        // (셀이면 그 표). 선택만으로는 **비활성 분기 슬롯 안** 자리에 닿지 못한다(패널만 열리고
        // 캔버스엔 없다). 검출이 정적 분석이라 그런 자리까지 잡으므로 슬롯 전환·스크롤을 함께
        // 건다. 배지는 선택을 옮기는 것이 본래 의미라 유지 — 도달만 얹는다. 전환 후에도 앵커가
        // 없으면 조용히 둔다(선택은 이미 옮겨 두었으니 기존 동작 이하로 나빠지지 않는다).
        selectSingleNode(issue.nodeId ?? null);
        if (issue.nodeId) revealOwnerAnchor(`node:${issue.nodeId}`);
        return;
      }
      selectSingleNode(null);
      const anchorId =
        issue.target === 'variables' ? VARIABLES_ANCHOR_ID : CATALOG_PARAMS_ANCHOR_ID;
      requestAnimationFrame(() => {
        document.getElementById(anchorId)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    },
    [selectSingleNode, revealOwnerAnchor],
  );

  if (!composerEnabled) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-bold mb-2">보고서 양식 빌더</h1>
        <p className="text-gray-600">
          피처 플래그 <code className="px-1 bg-gray-100 rounded">report-composer</code>가 비활성
          상태입니다. 피처 플래그 페이지에서 활성화한 뒤 다시 시도하세요.
        </p>
      </div>
    );
  }

  return (
    // -my-4 + calc(100%+2rem): Layout main p-8(32px)의 상하 여백을 각 16px씩 회수해 에디터
    // 가용 높이를 늘린다(전역 Layout 무접촉 — 이 페이지만). 헤더 아래 여백도 mb-3→mb-1.5.
    // -mx-8: 같은 취지로 **좌우 32px씩(합 64px)도 전량 회수**한다(2026-07-22 사용자 요청 — 패널
    // 양옆 빈 띠). 세로와 달리 가로는 폭이 auto라 음수 마진만으로 늘어나 calc가 필요 없다.
    // 3분할 패널 자체가 경계선을 가지므로 full-bleed로 붙여도 답답하지 않다.
    // data-composer-capture: 매뉴얼 스크린샷 촬영 앵커. 이 요소만 찍으면 admin 셸(사이드바·헤더)이
    // 빠져 **업체용 매뉴얼에서도 재촬영 없이 재사용**할 수 있고, admin 셸이 바뀌어도 스크린샷이
    // 같이 낡지 않는다. 촬영 절차는 `docs/manual/README.md`의 이미지 정책 참조.
    // Provider: 표시 형식 "적용 구간 미리보기" 원료를 깊은 편집기까지 흘린다(PreviewValuesContext 주석).
    // CatalogParamsEditContext: 바인딩 자리 인라인 파라미터 편집 채널(같은 이유로 prop 대신 컨텍스트).
    // SharedKeyFocusContext: 식별 키 칸의 포커스를 캔버스 오버레이까지 흘린다 — 발동 자리가
    // 속성 패널 깊숙한 여러 곳(노드 바인딩·셀 바인딩·분기 조건 키)이라 prop 스레딩 대신 컨텍스트.
    <PreviewValuesContext.Provider value={writeCanvasValues}>
    <CatalogParamsEditContext.Provider value={catalogParamsChannel}>
    <SharedKeyFocusContext.Provider value={sharedKeyChannel}>
    <div
      ref={containerRef}
      tabIndex={-1}
      data-composer-capture="editor"
      className="-mx-8 -my-4 flex h-[calc(100%+2rem)] flex-col outline-none"
    >
      {/* 헤더 2행(타이틀·탭)만 좌우 여백 — 루트가 -mx-8로 full-bleed라 그대로 두면 타이틀과 단축키
          버튼이 화면 가장자리에 붙는다(2026-07-22 사용자 제보). 패널 영역은 자체 경계선이 있어 full-bleed 유지. */}
      {/* 헤더(결정 2026-09-20 §3 이동표): 좌 = [≡ 탐색기 토글] 현재 양식 이름 · key · 공개 · 변경사항 /
          우 = 열린 양식 하나를 다루는 것만(저장·복제·삭제·시뮬레이션) + 도움(F1·매뉴얼). 양식 **집합**을
          다루던 콤보박스·분류 관리·새 템플릿·양식 가져오기는 좌측 양식 탐색기로 갔다. */}
      <div className="mb-1.5 flex items-center justify-between gap-3 px-4">
        <div className="flex min-w-0 items-center gap-2">
          {/* 탐색기 토글은 헤더에 두지 않는다 — 접힌 레일의 폴더 아이콘·Ctrl+K가 그 자리라 같은
              이름의 버튼이 70px 옆에 둘 있었다(2026-09-20 사용자 결정, 결정 기록 §3). */}
          <h1 className="shrink-0 text-xs font-medium text-gray-500">Composer Studio</h1>
          <span className="text-gray-300" aria-hidden>
            |
          </span>
          {/* 현재 양식 표시 — 콤보박스가 겸하던 「지금 무엇이 열려 있나」. 신규 미저장은 편집 중 이름. */}
          <div
            className="flex min-w-0 items-baseline gap-2"
            data-testid="current-template-heading"
            title={currentKey ? `${template.name} · ${currentKey}` : undefined}
          >
            {currentKey || isDirty ? (
              <>
                <span className="truncate text-lg font-bold">{template.name || '(이름 없음)'}</span>
                <code className="shrink-0 rounded bg-gray-100 px-1 text-xs text-gray-500">
                  {currentKey ?? '신규(미저장)'}
                </code>
              </>
            ) : (
              <span className="text-sm text-gray-400">열린 양식 없음 — 탐색기에서 고르세요 (Ctrl+K)</span>
            )}
          </div>
          {/* 편집 상태 배지 — 상호 배타: 수정 중이면 "• 변경됨"만, 저장 반영 상태면 "저장됨"만. */}
          {(currentKey || isDirty) && (
            <span className="shrink-0 text-xs whitespace-nowrap">
              {isDirty ? (
                <span className="text-amber-600">• 변경됨</span>
              ) : (
                <span className="text-green-600">저장됨</span>
              )}
            </span>
          )}
          {/* 공개(enabled) 토글 — 저장된 템플릿(currentKey)일 때만. 클릭 시 확인 모달을 거쳐 단독 PATCH.
              공개=green 계열, 비공개=gray 계열(저장 상태 배지 톤과 통일). */}
          {currentKey && (
            <button
              type="button"
              onClick={() => setPublishTarget({ key: currentKey, next: !currentEnabled })}
              disabled={busy || publishBusy}
              aria-pressed={currentEnabled}
              title={
                currentEnabled
                  ? '일반 사용자에게 공개됨 — 클릭하여 비공개로 전환'
                  : '비공개 — 클릭하여 일반 사용자에게 공개'
              }
              className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                currentEnabled
                  ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                  : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              <span
                className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                  currentEnabled ? 'bg-green-500' : 'bg-gray-300'
                }`}
                aria-hidden
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                    currentEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                  }`}
                />
              </span>
              {currentEnabled ? '공개' : '비공개'}
            </button>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {/* 문서 상태 배지 — 저장 **직전** 자리에 둔다. 저작 경고는 전부 각 패널 안에 숨어 있어,
              내보내기 전에 눈에 걸리려면 수명주기 버튼 옆이어야 한다(경고 0이면 스스로 미렌더). */}
          <TemplateIssuesBadge issues={templateIssues} onGo={handleGoToIssue} />
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy}
            title="저장 (기존이면 갱신, 신규면 생성) (Ctrl+S)"
            className={`px-3 py-1.5 text-sm rounded border disabled:opacity-50 disabled:cursor-not-allowed ${
              isDirty
                ? 'border-brand-600 bg-brand-600 text-white hover:bg-brand-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            💾 저장
          </button>
          {/* 「새 템플릿」·「양식 업로드로 시작」은 탐색기 「＋ 새 양식 ▾」로 이동(§3). 변환 Job 진행/완료
              pill은 헤더에 남긴다 — 모달을 닫아도 Job이 계속 돌고, 탐색기가 접혀 있어도 보여야 한다
              (Job 없으면 스스로 미렌더, 클릭하면 같은 모달). */}
          <FormImportJobPill job={formImportJob.state} onOpen={formImportJob.openModal} />
          <button
            type="button"
            onClick={handleDuplicate}
            disabled={busy || !currentKey}
            title={
              currentKey
                ? '현재 템플릿을 복제해 새 템플릿 만들기'
                : '저장된 템플릿만 복제할 수 있습니다'
            }
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ⧉ 복제
          </button>
          <button
            type="button"
            onClick={() => void handleDeleteTemplate()}
            disabled={busy || !currentKey}
            title={currentKey ? '현재 템플릿 삭제' : '저장된 템플릿만 삭제할 수 있습니다'}
            className="px-3 py-1.5 text-sm rounded border border-red-300 bg-white text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🗑 삭제
          </button>

          <span className="mx-1 h-5 w-px bg-gray-300" aria-hidden />

          {/* 시뮬레이션 — 저장본 기준 작성→최종 보고서 dry-run(새 탭). 미저장이면 클릭 시 안내. */}
          <button
            type="button"
            onClick={handleOpenSimulation}
            disabled={busy}
            title={
              currentKey && !isDirty
                ? '저장된 템플릿으로 작성→최종 보고서 시뮬레이션 (Ctrl+Shift+B, 새 탭)'
                : '저장 후 시뮬레이션할 수 있습니다'
            }
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ▶ 시뮬레이션
          </button>

          {/* 데이터 연결 후보 제안 — 현재 편집 상태의 수동 입력 필드를 AI가 카탈로그와 매칭(P2).
              저장본이 아닌 편집 상태 기준이라 미저장이어도 사용 가능. */}
          {/* 🔗 데이터 연결 제안은 보고서 탭 편집 툴바(EditorToolbar)로 이동(2026-07-27 결정 —
              헤더=문서 수명주기 / 탭 툴바=그 탭을 편집하는 도구). */}
          {/* 실행취소/다시실행은 캔버스 상단 편집 툴바(EditorToolbar)로 일원화(Phase C). */}
          <button
            type="button"
            onClick={() => setShowShortcuts(true)}
            title="단축키 목록 보기 (F1)"
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            ⌨ 단축키
          </button>
          {/* 매뉴얼 — 도움 어포던스라 단축키 옆.
              ⚠️ **반드시 새 탭**: 에디터는 저장되지 않은 편집 상태를 들고 있어 같은 탭 이동은 작업 유실이다. */}
          <a
            href="/manual/composer"
            target="_blank"
            rel="noreferrer"
            title="Composer 매뉴얼 열기 (새 탭)"
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            📖 매뉴얼
          </a>
        </div>
      </div>

      {/* 페이지 레벨 2분할(결정 2026-09-20 §1): 양식 탐색기 레일(접힘 48 / 펼침 260, 셸 storageKey 영속)
          | 탭 영역(탭 행 + 탭별 3분할 셸). 탭 바깥이라 보고서/작성 탭·시뮬레이션에서도 탐색기가 남는다.
          두 패널 모두 chromeless(셸 헤더 없음) + row 고정(방향 토글 없음). */}
      <div className="flex min-h-0 flex-1">
      <EditorPanelLayout
        ref={explorerLayoutRef}
        storageKey="composer-explorer-rail"
        lockOrientation="row"
        panels={[
          {
            id: 'explorer',
            title: '양식 탐색기',
            chromeless: true,
            defaultCompact: true,
            compactWidthPx: EXPLORER_COMPACT_PX,
            compactThresholdPx: EXPLORER_COMPACT_PX * 2,
            defaultWidthPx: EXPLORER_EXPANDED_PX,
            renderCompact: () => (
              <TemplateExplorerRail onExpand={openExplorer} onNewTemplate={handleNew} busy={busy} />
            ),
            content: (
              <TemplateExplorer
                templates={templateList}
                categories={templateCategories}
                listLoading={listLoading}
                currentKey={currentKey}
                isDirty={isDirty}
                busy={busy}
                focusSearchTick={explorerFocusTick}
                onCollapse={closeExplorer}
                onOpen={handleLoad}
                onNewTemplate={handleExplorerNewTemplate}
                onImport={handleOpenFormImport}
                onManageCategories={() => setCategoryManageOpen(true)}
                onDuplicate={handleDuplicateKey}
                onTogglePublish={(key, next) => setPublishTarget({ key, next })}
                onDeleteTemplate={handleDeleteTemplate}
                onAssigned={handleExplorerAssigned}
                onCategoriesChanged={handleExplorerCategoriesChanged}
              />
            ),
          },
          {
            id: 'workspace',
            title: '편집 영역',
            chromeless: true,
            content: (
              <>
      {/* 뷰 모드 세그먼트 토글 (3e-A) + 실데이터 반영 컨트롤(세 탭 전역 상태이므로 탭 행 우측). */}
      <div className="mb-3 flex flex-wrap items-center gap-3 px-4">
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
          {(
            [
              // 저작 순서(보고서 양식 먼저 → 작성 페이지) = 탭 순서 = 기본 탭('form')이 첫 자리
              // (2026-07-18 사용자 확정 — Studio는 저작자 화면이라 저작 워크플로가 기준).
              { mode: 'form', label: '보고서' },
              { mode: 'writeForm', label: '작성 페이지' },
            ] as { mode: ViewMode; label: string }[]
          ).map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              aria-pressed={viewMode === mode}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === mode
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 실데이터 컨트롤 — 반영 중이면 요약 칩(클릭=엔티티 변경)+해제, 아니면 [실제 데이터 반영]. */}
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {hasPreviewContext && !autoApplySuppressed ? (
            <>
              <button
                type="button"
                onClick={() => setRealDataModalOpen(true)}
                title="클릭하면 엔티티를 변경합니다"
                className="flex min-w-0 items-center gap-1 rounded-md border border-green-300 bg-green-50 px-2 py-1 text-xs text-green-800 hover:bg-green-100"
              >
                <span className="max-w-[280px] truncate">
                  {[
                    previewContextLabels.company,
                    previewContextLabels.site,
                    previewContextLabels.inspection,
                    // 보고서 연결 중(결정 2026-09-04) — 무엇을 보고 있는지 칩이 계속 말해야 한다
                    // (명시 선택이 자동연결과 다른 근거가 바로 이 가시성이다).
                    previewContext.linkedReportId
                      ? `보고서: ${previewContextLabels.report ?? '연결됨'}`
                      : undefined,
                    previewContextLabels.user,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '실데이터'}
                </span>
              </button>
              <button
                type="button"
                onClick={handleRealDataRelease}
                title="실데이터를 걷어내고 바인딩(필드 라벨) 상태로 봅니다"
                className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
              >
                실데이터 해제
              </button>
              {/* 모사 칩(결정 2026-09-01 §⑥, 2026-09-03 개정) — **주입형 모사는 항상 성공한다.**
                  필터형처럼 `simulateMisses`로 실패를 알려 줄 것이 없어, 저작자가 모사값을
                  실데이터로 착각할 여지가 있다. 그래서 실데이터 칩 바로 옆에 무엇을 모사 중인지
                  계속 띄운다. 상세(축별 한 줄)와 「전체 해제」는 hover 팝오버가 담당한다 —
                  단독 행 배너는 공간을 먹어 제거(사용자 결정, SimulationChip 헤더 참조). */}
              <SimulationChip
                chips={simulationChips}
                onOpenModal={() => setRealDataModalOpen(true)}
                onClearAll={handleClearAllSimulation}
              />
              {previewLoading && <span className="text-xs text-gray-400">불러오는 중…</span>}
              {/* 보고서 연결 시 본문 행의 출처(결정 2026-09-04 §2의 2026-09-14 개정) — 서버가
                  `linkedReportContent`로 「어느 조립 데이터셋이 저장 행이고 어느 것이 조립 폴백인지」를
                  말해 주는데 admin이 안 읽어 저작자는 미리보기 값이 저장 content인지 새 조립인지 알 수
                  없었다(2026-09-14 은닉 감사 3차 B). gap 배지 규약 동형 — 요약 칩 옆 한 줄. */}
              {previewResult?.linkedReportContent && (
                <span
                  className="text-xs text-gray-500"
                  title="저장 행: 연결 보고서의 content 행을 그대로 표시 / 조립 폴백: 저장 content에 없어 컨텍스트에서 새로 조립(스냅샷 세대가 달라 소스 키가 없는 경우)"
                >
                  저장 행: {previewResult.linkedReportContent.rowSourceKeys.join(', ') || '(없음)'} ·
                  조립 폴백:{' '}
                  {previewResult.linkedReportContent.assembledFallbackKeys.join(', ') || '(없음)'}
                </span>
              )}
              {previewResult && previewResult.gaps.length > 0 && (
                <span className="text-xs text-amber-600">
                  {/* 자동연결 폐기(2026-09-04) 후 저작 미리보기에 "보고서 연결" 개념이 없다 —
                      보고서-스코프 gap(결재/상태 등)은 모사 축(「보고서 데이터」)으로 지정하거나
                      빈값이 정상이라는 안내로 갱신. */}
                  미해석 {previewResult.gaps.length}건(결재/상태 등 보고서 값은 모사로 지정하거나 빈값)
                </span>
              )}
              {previewError && <span className="text-xs text-red-600">{previewError}</span>}
            </>
          ) : (
            <Button variant="primary" size="sm" onClick={() => setRealDataModalOpen(true)}>
              실제 데이터 반영
            </Button>
          )}
        </div>

        {/* 새 창 미리보기 — **보고서(양식) 탭 전용**(2026-07-22 사용자 확정). 작성 페이지에선 아예
            감춘다(비활성 버튼은 "왜 안 되지"만 남긴다): 캔버스의 "비교" 보기가 이미 상위 호환이라
            실데이터(prefill)·반복 실행·등록형 위젯·작성 chrome까지 갖춘 실렌더를 뷰별로 나란히 띄운다.
            팝아웃의 고유 가치는 보고서 쪽(A4 페이지네이션 실측·상세 chrome 저작·결재 시뮬)에 있다.
            ml-auto로 탭 행 우측 끝 — 저작 대상이 아니라 부수 도구라 좌측 컨트롤군과 떨어뜨린다. */}
        {viewMode !== 'writeForm' && (
          <button
            type="button"
            onClick={openPreviewPopout}
            title="미리보기를 새 창으로 열기(실시간 반영) — F8"
            className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
          >
            <span aria-hidden>↗</span> 새 창 미리보기
          </button>
        )}
      </div>

      {/* 실데이터 resolve(조립/AI 정제 포함) 진행 중 — 화면 중앙 로딩 오버레이. resolve 동안 렌더 데이터가
          바뀌므로 blocking=true로 편집/조작을 차단(로딩 중 상호작용 방지). */}
      {previewLoading && (
        <LoadingOverlay blocking label="실데이터 불러오는 중… (AI 정제 포함 시 수 초 소요)" />
      )}
      {/* 편집 후 자동 재조회(바인딩/조립데이터 변경) — 비차단 코너 스피너(편집 계속 가능, 500ms+ 걸릴 때만). */}
      {refetching && !previewLoading && (
        <div className="fixed bottom-4 right-4 z-[75] flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs text-gray-600 shadow-lg ring-1 ring-black/5">
          <Spinner size={14} />
          변경 반영 중… (AI 정제 시 수 초)
        </div>
      )}
      {/* AI 생성 데이터 후보 실데이터 resolve — 캐시가 비면 실제 생성이 돌아 수십 초까지 걸리므로
          비차단 코너 스피너(편집 계속 가능). 위 재조회 스피너와 동시 표시되지 않게 아래로 쌓는다. */}
      {aiCandidatesLoading && !previewLoading && (
        <div
          className={`fixed right-4 z-[75] flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs text-gray-600 shadow-lg ring-1 ring-black/5 ${
            refetching ? 'bottom-16' : 'bottom-4'
          }`}
        >
          <Spinner size={14} />
          AI 생성 데이터 불러오는 중… (생성 시 수십 초)
        </div>
      )}

      {viewMode === 'writeForm' ? (
        <div className="flex flex-1 min-h-0 flex-col">
          {/* 전역 반응형 뷰 스위처 — 캔버스·속성창·트리 단수 오버라이드 대상을 함께 지배(결정 2026-07-09).
              미리보기 패널 안이 아니라 작성 탭 상단에 둬 "이 뷰가 전체 저작에 적용됨"을 명확히. */}
          {/* px-4 — 헤더 2행·양식 탭 툴바와 같은 좌측 기준선(구 px-3은 4px 어긋났다, 2026-07-22).
              규칙: 경계선을 가진 풀블리드 바/패널은 패딩 0, 그 위 문서성 요소는 px-4. */}
          <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-white px-4 py-1.5">
            <span className="text-[11px] font-medium text-gray-500">반응형 뷰</span>
            {COMPOSER_VIEWS.map((v, vi) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setWriteFormView(v.key)}
                className={`rounded px-2 py-0.5 text-xs ${
                  writeFormView === v.key
                    ? 'bg-brand-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={`${v.authoringWidthPx ? `${v.authoringWidthPx}px` : '전폭'} (Alt+${vi + 1})`}
              >
                {v.label}
                {v.isBase ? ' (기본)' : ''}
              </button>
            ))}
            {/* 대표 단말 해상도 선택 — 활성 뷰의 프리셋으로 캔버스 폭 실측. */}
            {(activeViewDef.devices?.length ?? 0) > 0 ? (
              <>
                <span className="ml-2 text-[11px] text-gray-400">단말</span>
                <select
                  className="rounded border border-gray-300 px-1 py-0.5 text-xs"
                  value={Math.min(
                    deviceByView[writeFormView] ?? 0,
                    (activeViewDef.devices?.length ?? 1) - 1,
                  )}
                  onChange={(e) =>
                    setDeviceByView((prev) => ({ ...prev, [writeFormView]: Number(e.target.value) }))
                  }
                >
                  {activeViewDef.devices?.map((d, i) => (
                    <option key={d.label} value={i}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            {/* 작성 상황 시뮬레이션(2026-09-06 §3) — chrome 항목 visibleIn 해소(모드)와 submit
                라벨(카운터·전환 예고)을 상황별로 미리보기. 분할 갈래는 policy.split이 선언된
                템플릿에만 나온다(축이 없으면 일어날 수 없는 상황이라 시뮬이 거짓을 보인다).
                버튼이 아니라 select인 이유: 선택지가 넷이라 이 툴바 행에서 폭을 감당 못 한다. */}
            <label className="ml-2 flex items-center gap-1 text-[11px] font-medium text-gray-500">
              {/* 접근명은 감싼 label 텍스트("상황") — aria-label로 덮으면 보이는 라벨과 갈린다. */}
              <span>상황</span>
              <select
                className="rounded border border-gray-300 px-1 py-0.5 text-xs font-normal text-gray-600"
                value={previewSituationValue}
                onChange={(e) =>
                  setPreviewSituationKey(e.target.value as PreviewSubmitSituationKey)
                }
              >
                {previewSituationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            {/* 비교 뷰 — 주 캔버스 옆에 다른 뷰의 read-only 실측 캔버스를 나란히 표시. 활성 뷰는 제외. */}
            <span className="ml-2 text-[11px] font-medium text-gray-500">비교</span>
            <select
              className="rounded border border-gray-300 px-1 py-0.5 text-xs"
              value={compareView ?? ''}
              onChange={(e) => setCompareView(e.target.value || null)}
            >
              <option value="">없음</option>
              {COMPOSER_VIEWS.filter((v) => v.key !== writeFormView).map((v) => (
                <option key={v.key} value={v.key}>
                  {v.label}
                </option>
              ))}
            </select>
            {writeFormView !== COMPOSER_BASE_VIEW_KEY ? (
              <span className="ml-1 text-[11px] text-brand-600">
                이 뷰 오버라이드 편집 중 (캔버스·속성·단수 반영)
              </span>
            ) : (
              <span className="ml-1 text-[11px] text-gray-400">기본(데스크톱) 값 편집 중</span>
            )}
            {/* 패널 셸 방향 토글 포탈 타깃 — 자체 툴바 행 대신 이 행 맨뒤(세로 한 줄 절약). */}
            <span ref={setPanelToolbarEl} className="ml-auto inline-flex items-center gap-2" />
          </div>
          <div className="flex flex-1 min-h-0">
          <EditorPanelLayout
            storageKey="composer-writeform-editor"
            toolbarPortalEl={panelToolbarEl}
            panels={[
              {
                id: 'tree',
                title: '위젯 트리',
                // 좌/우 패널은 **해상도 무관 고정 px**(2026-07-22 사용자 확정, 구 20% → 16.5% → px).
                // 내용의 자연 폭이 정해져 있어서다: 트리 행은 고정 컨트롤 116px + 배지(max 88px)가
                // 먼저 차지하므로, 비율로 두면 좁은 화면(1366px→16.5%≈215px)에서 라벨 자리가 사라지고
                // 넓은 화면에선 캔버스만 낭비된다. 잔여는 가운데(preview, flex-1)가 흡수 — 즉 화면이
                // 커질수록 캔버스만 넓어진다. 양식 탭(팔레트 200 / 속성 300)과 같은 결.
                defaultWidthPx: 328,
                content: (
                  // 가로 스크롤을 만들지 않는다(2026-07-22 사용자 확정) — 패널 폭 안에서 행이 접히고
                  // 넘치는 라벨만 …로 잘린다. 래퍼를 내용 폭(w-max)에 맞추면 스크롤이 생기므로 금지.
                  <div className="p-2">
                    <ComposerWidgetTreeEditor
                      sync={composerWidgetSync}
                      placementMap={bindingPlacementMap}
                      // 모달 저작 중이면 트리 팔레트의 추가 대상도 그 모달(우클릭 경로와 동일 규약).
                      rootParentId={modalAuthoringId ?? null}
                      selectedId={selectedWidgetId}
                      selectedIds={selectedWidgetIds}
                      // F2 rename 요청 — 트리 편집기가 접힌 조상을 펴고 그 행까지 흘린다.
                      renameRequest={wtRenameRequest}
                      // 접힘 상태 localStorage 영속(템플릿별) — 새로고침 후 복원.
                      persistKey={template.key}
                      onSelect={selectWidget}
                      onHoverWidget={setTreeHoverWidgetId}
                      onPoolHover={broadcastPreviewHighlight}
                      onPoolPreview={showPoolPreview}
                      onRowContextMenu={handleTreeWidgetContextMenu}
                      onPlace={handleWidgetPlace}
                      onReorder={handleWidgetReorder}
                      onMoveTo={handleWidgetMoveTo}
                      onAddGroup={handleWidgetAddGroup}
                      onAddDesign={handleWidgetAddDesign}
                      onAddRegistered={handleWidgetAddRegistered}
                      onAddFormTable={handleWidgetAddFormTable}
                      onAddInput={handleWidgetAddInput}
                      onAddModalTrigger={handleWidgetAddModalTrigger}
                      onAddRepeat={handleWidgetAddRepeat}
                      onAddBranch={handleWidgetAddBranch}
                      onInsertPreset={(presetId, parentId) =>
                        void handleInsertPreset(presetId, parentId)
                      }
                      presetOptions={presetLibrary.map((p) => ({ id: p.id, name: p.name }))}
                      onUpdate={handleWidgetUpdate}
                      // 분기 슬롯 트리 서브행 — 활성 슬롯 하이라이트/전환(캔버스 슬롯 탭과 공유 state).
                      activeBranchSlots={activeBranchSlots}
                      onSetActiveBranchSlot={setActiveBranchSlot}
                      // 표 셀 위젯 행 — 클릭=표 선택+셀 포커스(캔버스 onFormTableCellFocus 배선과
                      // 동일 부수효과·source 'canvas'=캔버스 스크롤), hover=셀 하이라이트 채널.
                      onFocusCell={(tableId, rowId, columnId) => {
                        setSelectedChrome(null);
                        setTreeHoverWidgetId(null);
                        selectWidgetSingle(tableId);
                        focusFtCell({ tableId, rowId, columnId }, { source: 'canvas' });
                      }}
                      onHoverCell={setFtHoverCell}
                      focusedCellKey={
                        ftAnchorCell
                          ? `${ftAnchorCell.tableId}::${ftAnchorCell.rowId}::${ftAnchorCell.columnId}`
                          : null
                      }
                    />
                  </div>
                ),
              },
              {
                id: 'preview',
                title: '작성폼 미리보기',
                content: (
                  <div className="relative flex h-full flex-col">
                    {/* 모달 편집 모드 — **중앙(미리보기) 패널만** 차단하는 레이어. 좌 트리·우 속성
                        패널은 모달 저작 중에도 조작 가능(2026-07-22 사용자 확정 — 의도된 동작). */}
                    {modalAuthoringId != null && <ModalAuthoringDim />}
                    {/* 작성 탭 편집 툴바 — 캔버스 위젯 조작(undo/redo·묶기·삭제 등)이라 캔버스 상단에
                        배치(양식 탭 EditorToolbar와 동일 관례, 사용자 정정 2026-07-11). 단축키와 동일 핸들러. */}
                    <WriteFormToolbar
                      canUndo={canUndo}
                      canRedo={canRedo}
                      onUndo={undo}
                      onRedo={redo}
                      selectionCount={selectedWidgetIds.length}
                      canGroup={writeFormCanGroup}
                      isGroupSelected={writeFormIsGroupSelected}
                      onGroup={handleWidgetGroupSelected}
                      onUngroup={handleWidgetUngroupSelected}
                      onRemove={() => {
                        const ids =
                          selectedWidgetIds.length > 0
                            ? selectedWidgetIds
                            : selectedWidgetId
                              ? [selectedWidgetId]
                              : [];
                        if (ids.length === 0) return;
                        handleWidgetsRemove(ids);
                        clearWidgetSelection();
                      }}
                      onClearSelection={() => {
                        clearWidgetSelection();
                        setSelectedChrome(null);
                        // 명시적 "선택 해제"는 isolation 드릴 컨텍스트도 완전 이탈(빈 영역 클릭과 동일).
                        setWriteSelectionContextId(null);
                      }}
                      selectionMode={selectionMode}
                      onToggleSelectionMode={setSelectionMode}
                      onAutoLayoutFill={handleAutoLayoutFill}
                      onAutoLayoutRebuild={handleAutoLayoutRebuild}
                    />
                    {/* 캔버스에서 편집한 입력값을 prefill/기본값 상태로 되돌린다(edits={}). 편집분이 있을 때만 노출. */}
                    {Object.keys(writeCanvasEdits).length > 0 && (
                      <div className="flex justify-end px-2 py-1">
                        <button
                          type="button"
                          onClick={() => setWriteCanvasEdits({})}
                          className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100"
                        >
                          입력값 초기화
                        </button>
                      </div>
                    )}
                    {/* 반응형 뷰 스위처는 작성 탭 상단 전역 바로 이동(캔버스·속성 공통 지배). 여기선 폭만 반영. */}
                    {/* CanvasViewport로 줌/팬/fit 제공 — 단말 해상도(canvasPreviewWidth)를 pageWidth로 넘기고
                        fitToWidth로 캔버스 전체가 패널에 들어오게 자동 축소(마운트·단말 전환 시 재계산). 이후
                        Ctrl+휠 줌 / Space·중간버튼 패닝 가능. persistKey 병용: 새로고침 시 저장된 줌·스크롤
                        복원이 초기 fit보다 우선(단말 전환 시엔 fit 재실행 — CanvasViewport 공존 규칙). */}
                    <div className="flex min-h-0 flex-1">
                      {/* effectiveCompare 시 pageWidth = 주 캔버스 + 24(gap-6) + 비교 캔버스 폭.
                          fitToWidth가 pageWidth 변경을 감지해 자동 재fit — 두 캔버스가 함께 축소돼 패널에 들어온다.
                          비교 미설정(null)이면 기존과 동일한 단일 캔버스·pageWidth 경로. */}
                      <CanvasViewport
                        pageWidth={
                          effectiveCompare
                            ? (canvasPreviewWidth ?? 794) + 24 + compareWidth
                            : (canvasPreviewWidth ?? 794)
                        }
                        fitToWidth
                        persistKey={`write:${template.key}`}
                        // 분기 위젯 슬롯 탭 오버레이(P3) — 위젯 바로 위에 그 위젯의 전체 슬롯 탭 바
                        // (겹치면 밀어내기). 클릭=활성 슬롯 전환+branch 선택.
                        // 본문 BranchSlotTabsOverlay와 **같은 층**(A4 박스 직속)에 둔다: 작성 캔버스는
                        // ComposeChromePreviewFrame의 `Card overflow-hidden` 안이라, 폼 맨 위 분기 위젯의
                        // 바(박스 위쪽 바깥 = 음수 y)가 거기 있으면 잘린다.
                        // 위젯 DOM 조회만 작성 캔버스로 스코프(비교 패널의 같은 위젯 id 배제).
                        overlays={(canvasBox) => (
                          <WriteCanvasBranchTabsOverlay
                            box={canvasBox}
                            canvas={writeCanvasEl}
                            tree={writeCanvasTree}
                            activeBranchSlots={activeBranchSlots}
                            onSelectSlot={setActiveBranchSlot}
                            onSelectWidget={(id) => selectWidget(id)}
                            remeasureKey={writeBranchRemeasureKey}
                          />
                        )}
                        // 캔버스 박스 밖(회색 배경) 클릭 = 선택 해제(양식 탭과 동일 UX).
                        // isolation 드릴 컨텍스트도 root로 리셋 — 남겨두면 다음 일반 클릭이 stale
                        // 컨텍스트의 직계 자식으로 승격돼 "해제 후 첫 클릭에 자식이 바로 선택"되는
                        // 오동작(2026-07-14 사용자 제보). Esc의 단계별 pop과 달리 빈 영역 클릭=완전 이탈.
                        onOutsideClick={() => {
                          clearWidgetSelection();
                          setSelectedChrome(null);
                          setWriteSelectionContextId(null);
                        }}
                      >
                        {/* 프레임 안 빈 영역(위젯/chrome 밖) 클릭도 선택 해제 — 위젯·chrome·grip은
                            각자 stopPropagation이라 여기까지 버블되는 클릭 = 빈 영역.
                            isolation 컨텍스트 리셋 포함(onOutsideClick과 동일 — 빈 영역=완전 이탈). */}
                        <div
                          className="flex items-start gap-6"
                          onClick={() => {
                            clearWidgetSelection();
                            setSelectedChrome(null);
                            setWriteSelectionContextId(null);
                          }}
                          // 빈 영역 우클릭 — 클릭과 동일하게 선택/컨텍스트 리셋 후 캔버스 메뉴
                          // (위젯 추가/붙여넣기). 위젯 래퍼는 stopPropagation이라 여기 도달 = 빈 영역.
                          onContextMenu={(e) => {
                            const t = e.target as HTMLElement;
                            if (t.closest('input, textarea, select, [contenteditable="true"]'))
                              return;
                            e.preventDefault();
                            clearWidgetSelection();
                            setSelectedChrome(null);
                            setWriteSelectionContextId(null);
                            setFtFocusCell(null);
                            setCtxMenu({
                              x: e.clientX,
                              y: e.clientY,
                              scope: 'write',
                              target: 'canvas',
                            });
                          }}
                        >
                          <div className="shrink-0" style={{ width: canvasPreviewWidth ?? 794 }}>
                            {/* 헤더·하단 컨트롤 바(chrome)를 실제 작성 화면처럼 상시 프레임으로 감싼다.
                                프레임(헤더/하단) 클릭 = 그 chrome 슬롯 선택(위젯 선택 해제) → 우 속성창에 슬롯 구성.
                                프레임 안쪽(위젯) 클릭 = 콘텐츠(위젯) 선택. chrome/콘텐츠 개념 분리. */}
                            <ComposeChromePreviewFrame
                              config={template.composeChrome}
                              title={`${template.name} 작성`}
                              subtitle={previewContextLabels.site}
                              activeView={writeFormView}
                              mode={previewComposeMode}
                              submitSituation={previewSubmitSituation}
                              selectedSlot={selectedChrome}
                              onSelectSlot={(s) => {
                                setSelectedChrome(s);
                                clearWidgetSelection();
                                setFtFocusCell(null); // 셀 포커스도 해제(selectWidget과 동일 규약)
                              }}
                            >
                              {/* Slice B: 중앙 미리보기 직접드래그(순서/그룹 이동). 좌측 아웃라인과 독립 DndContext. */}
                              {/* collisionDetection: pointerWithin 우선 + closestCenter fallback.
                                  드롭존이 위젯 전체 박스라 closestCenter(중심 거리) 단독은 세로로 큰 위젯(표)
                                  상단에 놓을 때 섹션 밖 드롭존을 오판(2026-07-13 사용자 제보 "섹션 밖으로 이동").
                                  포인터가 든 드롭존을 우선하면 큰 위젯 위 어디든 그 위젯 insert-before로 판정.
                                  autoScroll=false: 드래그 중 캔버스 가장자리 자동 스크롤이 가로로 화면을 밀어
                                  드롭 판정까지 왜곡(같은 제보 "옆으로 스크롤"). 세로 이동은 휠 스크롤로 가능. */}
                              <DndContext
                                sensors={writeFormCanvasSensors}
                                collisionDetection={writeFormCanvasCollision}
                                autoScroll={false}
                                onDragStart={handleCanvasDragStart}
                                onDragCancel={() => setCanvasDragWidgetId(null)}
                                onDragEnd={handleCanvasDragEnd}
                              >
                                {/* composer-write-canvas: 컨트롤 focus 링 억제 스코프(index.css) —
                                    선택 링과 이중 표시 방지. 편집 캔버스에만(비교 패널·web 무영향). */}
                                {/* 모달 편집 모드 차단 레이어는 패널 루트(ModalAuthoringDim — 에디터 전체)로
                                    이동(2026-07-15) — 스크롤러 밖이라 wheel 체이닝도 차단. */}
                                <div
                                  className="composer-write-canvas"
                                  ref={setWriteCanvasEl}
                                  style={{ position: 'relative' }}
                                >
                                <ComposerWidgetTreeView
                                  {...writeCanvasSharedProps}
                                  onChange={handleWriteCanvasEdit}
                                  view={writeFormView}
                                  interactive={{
                                    selectedId: selectedWidgetId ?? undefined,
                                    selectedIds: selectedWidgetIds,
                                    hoveredId: treeHoverWidgetId ?? undefined,
                                    // P1b: 저작 열림 modalTrigger — 모달을 열린 상태로 유지·저작(Esc/오버레이=닫기).
                                    modalAuthoringId: modalAuthoringId ?? undefined,
                                    onModalAuthoringClose: () => handleModalAuthoringToggle(null),
                                    onSelect: (id, opts) =>
                                      selectWidget(id, opts?.additive, opts?.range, 'canvas'),
                                    // 더블클릭 드릴다운(isolation 전용 — deepest엔 미전달): 미선택 위젯 더블클릭=
                                    // 드릴(선택), 선택된 텍스트 위젯 더블클릭=인라인 편집(래퍼 분기). deepest에
                                    // 전달하면 래퍼가 편집 대신 드릴(no-op)로 새어 편집 회귀 발생.
                                    onWidgetDrill:
                                      selectionMode === 'isolation' ? handleWidgetDrill : undefined,
                                    onWidgetTextChange: handleWidgetTextChange,
                                    // 폴백 선택 래퍼(반복 행 children 등 renderWrapper 미주입 경로) 우클릭 위임 —
                                    // 일반 위젯은 renderWrapper(ComposerCanvasWidget)가 처리하나, 반복 children은
                                    // 이 콜백이 없으면 우클릭이 반복 컨테이너로 버블됨(2026-07-20 제보).
                                    onWidgetContextMenu: handleWriteWidgetContextMenu,
                                    // 추가 직후 자동 편집 진입 요청(텍스트/입력 라벨) — 소비 후 곧 해제됨.
                                    editRequest: wtEditRequest ?? undefined,
                                    onFormTableCellTextChange: handleFtCellTextChange,
                                    onFormTableCellFocus: (tableId, rowId, columnId, opts) => {
                                      // 표 위젯 선택 유지(셀 포커스 invariant). selectWidget은 금지 —
                                      // 그 안의 셀 포커스 해제가 additive/range의 prev를 지워 복수 셀
                                      // 선택이 단일로 무너짐(2026-07-14 회귀). 부수효과만 직접 수행.
                                      setSelectedChrome(null);
                                      setTreeHoverWidgetId(null);
                                      selectWidgetSingle(tableId);
                                      focusFtCell(
                                        { tableId, rowId, columnId },
                                        {
                                          additive: opts?.additive,
                                          range: opts?.range,
                                          source: 'canvas',
                                        },
                                      );
                                    },
                                    // 캔버스 표 안 셀 하이라이트 — 포커스(복수)와 hover(앰버 위치 표시)는
                                    // 별도 채널. 상호배타로 묶으면 셀 포커스 중(계산 셀 편집기 등) hover가
                                    // 전달되지 않음(2026-07-13 사용자 제보). 포커스는 그 표 선택 중일 때만.
                                    focusedCells:
                                      ftFocusCell && selectedWidgetId === ftFocusCell.tableId
                                        ? ftFocusCell.cells.map((c) => ({
                                            tableId: ftFocusCell.tableId,
                                            ...c,
                                          }))
                                        : undefined,
                                    hoveredCell: ftHoverCell ?? undefined,
                                    renderWrapper: (ctx, ch) => (
                                      <ComposerCanvasWidget
                                        ctx={ctx}
                                        view={writeFormView}
                                        onContextMenu={handleWriteWidgetContextMenu}
                                      >
                                        {ch}
                                      </ComposerCanvasWidget>
                                    ),
                                    renderDropSlot: (pid, layout) => (
                                      <ComposerCanvasDropSlot
                                        parentId={pid}
                                        className={layout?.className}
                                        style={layout?.style}
                                      />
                                    ),
                                  }}
                                />
                                {/* 분기 위젯 슬롯 탭 오버레이(P3)는 여기 없다 — CanvasViewport의
                                    `overlays`(A4 박스 직속)로 올렸다. 이 자리는 ComposeChromePreviewFrame의
                                    `Card overflow-hidden` 안이라, 폼 맨 위 분기 위젯의 바(위젯 위로
                                    떠서 박스 바깥 = 음수 y)가 에러 없이 잘린다. 아래 CanvasViewport
                                    overlays 참조. */}
                                {/* 위젯 경계 드래그(폭 비율) — 같은 줄 인접 위젯 사이 gap에 투명
                                    hit strip. 드래그하면 두 위젯의 span이 합 보존으로 재분배된다
                                    (다른 칸·줄 끝 빈 트랙 불변). 표 셀 경계와 같은 감각, 레이아웃 중립. */}
                                <WidgetEdgeResizeOverlay
                                  canvas={writeCanvasEl}
                                  tree={writeCanvasTree}
                                  view={writeFormView}
                                  widgetById={writeCanvasWidgetById}
                                  onPreview={handleWidgetSpanDragPreview}
                                  onCommit={handleWidgetSpanDragCommit}
                                  remeasureKey={writeBranchRemeasureKey}
                                />
                                </div>
                                {/* 드래그 중 포인터 추종 카드 — "지금 이 위젯을 옮기는 중" 피드백.
                                    삽입 지점은 드롭존 isOver 파란 라인이 표시(둘이 한 세트). */}
                                <DragOverlay dropAnimation={null}>
                                  {canvasDragWidgetId ? (
                                    <div
                                      className="pointer-events-none inline-flex items-center gap-1 rounded border border-brand-400 px-2 py-1 text-xs text-gray-700 shadow-md"
                                      style={{ background: 'rgba(255,255,255,0.9)' }}
                                    >
                                      <span className="text-gray-400">⠿</span>
                                      {(() => {
                                        const w = selectedWidgetById(
                                          composerWidgetSync.tree,
                                          canvasDragWidgetId,
                                        );
                                        return w ? composerWidgetSummary(w) : '위젯';
                                      })()}
                                    </div>
                                  ) : null}
                                </DragOverlay>
                              </DndContext>
                            </ComposeChromePreviewFrame>
                          </div>
                          {effectiveCompare && compareViewDef && (
                            <div className="shrink-0" style={{ width: compareWidth }}>
                              <p className="mb-1 text-xs font-medium text-gray-500">
                                비교: {compareViewDef.label}
                                {compareDevice ? ` · ${compareDevice.label}` : ''} (읽기 전용)
                              </p>
                              <Card padding="none" className="overflow-hidden">
                                <ComposeWritePageShell
                                  config={template.composeChrome}
                                  activeView={effectiveCompare}
                                  mode={previewComposeMode}
                                  submitSituation={previewSubmitSituation}
                                  title={`${template.name} 작성`}
                                  subtitle={previewContextLabels.site}
                                  getButtonProps={() => ({ onClick: () => {} })}
                                  minHeight={compareHeight}
                                >
                                  {/* 메인 캔버스와 **같은 props 묶음**(writeCanvasSharedProps)에서 파생 —
                                      각자 나열하면 한쪽에만 추가된 props가 생겨 렌더가 갈라진다.
                                      다른 것만 덮는다: view=비교 뷰 / onChange=no-op(읽기 전용). */}
                                  <ComposerWidgetTreeView
                                    {...writeCanvasSharedProps}
                                    onChange={() => {}}
                                    view={effectiveCompare}
                                  />
                                </ComposeWritePageShell>
                              </Card>
                            </div>
                          )}
                        </div>
                      </CanvasViewport>
                    </div>
                  </div>
                ),
              },
              {
                id: 'props',
                title: '속성',
                // 좌(tree)와 대칭 고정 px — 위 tree 주석 참조(2026-07-22 사용자 확정).
                defaultWidthPx: 328,
                content: (
                  <div className="space-y-3 p-2">
                    {/* chrome 슬롯(헤더/하단 바) 선택 시 그 슬롯 버튼 구성, 아니면 선택 위젯 속성.
                        chrome=페이지 틀 / 위젯=폼 콘텐츠 — 속성창은 선택 대상에 따라 하나만 표시. */}
                    {selectedChrome ? (
                      <ComposeChromeEditor
                        slot={selectedChrome}
                        config={template.composeChrome}
                        onChange={(next) => apply((t) => ({ ...t, composeChrome: next }))}
                      />
                    ) : (
                      <>
                        {/* 뷰 컨텍스트 — 선택 유무와 무관하게 패널 최상단(지금 어느 뷰를 편집 중이고
                            무엇이 이 뷰에만 저장되는지). base 뷰에서는 스스로 미렌더. */}
                        <WriteFormViewContextHeader activeView={writeFormView} />
                        {/* 미선택(위젯·셀 없음) 시 페이지 전역 설정(본문 폭 등) — 보고서 탭의 전역 설정 모델. */}
                        {selectedWidgetIds.length === 0 && !ftFocusCell && (
                          <>
                            <ComposePageSettingsEditor
                              config={template.composeChrome}
                              onChange={(next) => apply((t) => ({ ...t, composeChrome: next }))}
                            />
                            {/* 템플릿 전역 코어 — 보고서 탭 전역 패널과 **같은 상태를 같은 컴포넌트로**
                                편집한다(사본·재구현 아님, 호출부 props 동일). 작성폼이 소비하는 축이라
                                여기서도 손댈 수 있어야 탭을 오가지 않는다. 위 페이지 설정은 이 탭 전용,
                                아래 두 섹션은 양 탭 공통 — 그 경계를 캡션으로 표시한다. */}
                            <div className="pt-2 border-t border-gray-100">
                              <p className="text-[11px] leading-snug text-gray-400">
                                아래 두 항목은 <b className="font-medium text-gray-500">템플릿 전역</b>
                                입니다 — 보고서 탭 전역 속성과 같은 내용을 편집합니다.
                              </p>
                            </div>
                            {/* 순서는 보고서 탭 전역 패널과 동일(데이터 → 변수) — 탭 간 공간 기억 일관.
                                props는 보고서 탭 호출부와 같은 객체를 spread(드리프트 차단). */}
                            <DataSection {...dataSectionProps} />
                            <VariablesEditor {...variablesEditorProps} />
                          </>
                        )}
                        <WriteFormPropertyPanel
                        placementMap={bindingPlacementMap}
                        onNavigateToBodyNode={navigateToBodyNode}
                        selectedWidgets={selectedWidgetIds
                          .map((id) => selectedWidgetById(composerWidgetSync.tree, id))
                          .filter((w): w is ComposerWidget => w != null)}
                        unplacedSelected={selectedWidgetIds
                          .map((id) => composerWidgetSync.unplaced.find((u) => u.id === id))
                          .filter((w): w is InputWidget => w != null)}
                        unplacedPool={composerWidgetSync.unplaced}
                        ftFocusCell={ftFocusCell}
                        onFtCellHover={(c) => {
                          setFtHoverCell(c);
                          // 강조 셀이 캔버스 뷰포트 밖이면 보이게 스크롤(hoverScrollToAnchor 참조).
                          hoverScrollToAnchor(
                            c ? `[data-ftcell="${c.tableId}::${c.rowId}::${c.columnId}"]` : null,
                          );
                        }}
                        onFtCellFocus={(c, opts) => {
                          // 카드 → 셀 뷰 전환으로 카드가 언마운트되면 mouseleave가 안 오므로
                          // hover를 여기서 즉시 해제(stale hover 고착 방지).
                          setFtHoverCell(null);
                          focusFtCell(c, {
                            additive: opts?.additive,
                            range: opts?.range,
                            source: 'panel',
                          });
                        }}
                        onFtCellFocusClear={() => setFtFocusCell(null)}
                        ftCellClipboard={ftCellClipboard}
                        onFtCellCopy={copyFtCell}
                        onFtCellBlockCopy={copyFtBlock}
                        onFtCellPaste={pasteFtCell}
                        onFtCellPasteMany={pasteFtCells}
                        onFtCellsContentClear={clearFtCells}
                        onFtRequestConfirm={(o) =>
                          setConfirmState({
                            ...o,
                            onConfirm: () => {
                              setConfirmState(null);
                              o.onConfirm();
                            },
                          })
                        }
                        writeValues={writeCanvasValues}
                        bindingPrefillMap={bindingPrefillMap}
                        anchorId={selectedWidgetId}
                        writeFormFields={derivedWriteForm}
                        onWidgetUpdate={handleWidgetUpdate}
                        onWidgetPreview={handleWidgetPreview}
                        onWidgetCommit={handleWidgetCommit}
                        onWidgetRevert={discardTransient}
                        onWidgetsUpdate={handleWidgetsUpdate}
                        onRemove={handleWidgetRemove}
                        onRemoveMany={handleWidgetsRemove}
                        onTreeUpdate={wtApply}
                        onPlace={handleWidgetPlace}
                        activeView={writeFormView}
                        tree={composerWidgetSync.tree}
                        dataDefs={template.dataDefs}
                        variables={template.variables}
                        dataSources={template.dataSources}
                        catalogParams={template.catalogParams}
                        onDataSourcesChange={(next) => apply((t) => ({ ...t, dataSources: next }))}
                        // 표 칼럼 참조 모드(P3) — 정본(dataDefs) 쓰기·detach는 템플릿 원자 갱신
                        // (1 undo 스텝), detach 차단 판정은 전 축 참조 카운트 재사용.
                        onTemplateUpdate={apply}
                        countKeyRefs={(key) => countDataSourceUsage(template, key)}
                        catalog={catalog}
                        modalAuthoringId={modalAuthoringId}
                        onModalAuthoringToggle={handleModalAuthoringToggle}
                        onApplyPresetVersion={handleApplyPresetVersion}
                        onPublishPresetFromInstance={handlePublishPresetFromInstance}
                        onUpdatePresetMeta={handleUpdatePresetMeta}
                        onDeletePreset={handleDeletePresetFromInstance}
                        onSelectWidget={(id) => selectWidget(id)}
                        activeBranchSlots={activeBranchSlots}
                        onSetActiveBranchSlot={setActiveBranchSlot}
                        onWidgetAddBranchSlot={handleWidgetAddBranchSlot}
                        onWidgetRemoveBranchSlot={handleWidgetRemoveBranchSlot}
                        onWidgetMoveBranchSlot={handleWidgetMoveBranchSlot}
                        onWidgetUpdateBranchSlot={handleWidgetUpdateBranchSlot}
                        onHoverHighlightWidget={(id) => {
                          setTreeHoverWidgetId(id);
                          // 속성 패널 강조 채널만 스크롤 동반 — 트리 행 hover(onHoverWidget,
                          // 좌측 트리)는 같은 state를 쓰지만 이 래퍼를 안 거치므로 기존대로
                          // 스크롤 없음(트리 스캔 중 캔버스 널뜀 방지).
                          hoverScrollToAnchor(
                            id ? `[data-composer-widget-id="${id}"]` : null,
                          );
                        }}
                      />
                      </>
                    )}
                    {/* 편집형 list 데이터셋의 필드별 작성 위젯 매핑은 상시 섹션(구 DataSourceWidgetsEditor)이
                        아니라 해당 dataSourceRows 위젯 선택 시 그 속성으로 이동(2026-07-11 Phase 1).
                        AI 제안(flat writeFormExtras)은 편집기·데이터 경로 모두 철거(2026-07-25). */}
                  </div>
                ),
              },
            ]}
          />
          </div>
        </div>
      ) : (
      <div className="flex flex-1 min-h-0 flex-col gap-2">
      {/* 실데이터 컨텍스트 컨트롤은 탭 행(뷰 모드 세그먼트 우측)으로 이동 — 세 탭 전역 상태. */}

      {/* 편집 툴바 (Phase C) — 캔버스 상단 고정. 줌·가운데정렬은 CanvasViewport에 병존.
          px-4 래퍼: EditorToolbar는 rounded+border+shadow **카드**라 루트의 -mx-8 full-bleed 아래
          그대로 두면 둥근 모서리가 뷰포트 가장자리에 붙는다(2026-07-22 리뷰 적발 — 작성 탭만 보고
          양식 탭을 놓쳤다). 헤더 2행과 같은 여백 기준(16px)으로 맞춘다. */}
      <div className="px-4">
        <EditorToolbar
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        selectionCount={selectedNodeIds.length}
        canGroup={canGroupSelected}
        isContainer={selectedContainerKind !== null}
        containerKind={selectedContainerKind}
        onGroup={handleGroupSelected}
        onUngroup={handleUngroupSelected}
        canMoveOut={canMoveOutSelected}
        onMoveOut={handleMoveOutSelected}
        onToggleKind={handleToggleContainerKind}
        showPadding={showPadding}
        onToggleShowPadding={setShowPadding}
        showBadges={showBadges}
        onToggleShowBadges={setShowBadges}
        selectionMode={selectionMode}
        onToggleSelectionMode={setSelectionMode}
        onOpenBindingSuggest={handleOpenBindingSuggest}
        bindingSuggestDisabled={busy}
        />
      </div>

      {/* 셸은 root가 h-full이라 flex-1 부모가 높이를 확정해줘야 함(작성 탭 writeForm과 동일 래퍼). */}
      <div className="flex flex-1 min-h-0">
      <EditorPanelLayout
        // 저장된 key(currentKey — 로드/저장 완료본) 기준 — template.key는 key 입력 필드 타이핑마다
        // 바뀌어 storageKey churn(패널 상태 재로드·유령 항목 누적)을 일으킨다. 신규 미저장은 'new'.
        storageKey={`composer-form-editor:${currentKey ?? 'new'}`}
        panels={[
          {
            id: 'palette',
            title: '요소 팔레트',
            compactWidthPx: 48,
            compactThresholdPx: 96,
            defaultWidthPx: 200,
            // 접힌 아이콘 레일 — 접힌 상태에서도 아이콘 클릭으로 즉시 추가(quick-add) 유지. 펴기는 onExpand.
            renderCompact: ({ onExpand }) => (
              <div className="flex h-full flex-col items-center gap-1 overflow-y-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <button
                  type="button"
                  onClick={onExpand}
                  title="팔레트 펼치기"
                  aria-label="팔레트 펼치기"
                  className="w-6 h-6 flex items-center justify-center text-gray-500 rounded hover:bg-gray-100"
                >
                  »
                </button>
                {/* 접힘 상태 — 아이콘만 세로 나열, 클릭 시 바로 추가(라디오 그룹도 즉시 삽입). */}
                <div className="mt-1 flex flex-col items-center gap-1">
              {PALETTE_ITEMS.map((item) => {
                const cellKindOk = isCellInsertableKind(item.kind);
                const disabled = !!singleCell && (!cellKindOk || !!cellInsertBlockReason);
                return (
                  <button
                    key={item.radioGroup ? 'radio-group' : (item.special ?? item.kind)}
                    type="button"
                    onClick={() =>
                      item.radioGroup
                        ? handleAddRadioGroup()
                        : handleAddNode(item.kind, item.special)
                    }
                    disabled={disabled}
                    title={
                      !singleCell
                        ? `${item.label} 추가`
                        : !cellKindOk
                          ? `표 셀에는 ${withObjectParticle(item.label)} 넣을 수 없습니다`
                          : (cellInsertBlockReason ?? `선택한 셀에 ${item.label} 추가`)
                    }
                    aria-label={`${item.label} 추가`}
                    className="w-7 h-7 flex items-center justify-center text-sm rounded border border-gray-200 bg-white text-gray-700 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200"
                  >
                    <span aria-hidden>{item.icon}</span>
                  </button>
                );
              })}
                </div>
              </div>
            ),
            content: (
              <div className="p-3">
          {/* 셀 선택 중 — 팔레트 추가가 선택 셀 content로 라우팅됨을 알리는 대상 배지. */}
          {singleCell && (
            <div
              className={`mb-2 rounded border px-2 py-1.5 text-[11px] leading-snug ${
                cellInsertBlockReason
                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              }`}
            >
              삽입 위치: 선택한 셀 ({singleCell.row}, {singleCell.col})
              {cellInsertBlockReason && <div className="mt-0.5">{cellInsertBlockReason}</div>}
            </div>
          )}
          <ul className="space-y-1">
            {PALETTE_ITEMS.map((item) => {
              // 셀 선택 중엔 셀 삽입 가능 kind만 활성(허용 외 kind + 차단 사유는 비활성 + title 안내).
              const cellKindOk = isCellInsertableKind(item.kind);
              const disabled = !!singleCell && (!cellKindOk || !!cellInsertBlockReason);
              const title = !singleCell
                ? `${item.label} 추가`
                : !cellKindOk
                  ? `표 셀에는 ${withObjectParticle(item.label)} 넣을 수 없습니다`
                  : (cellInsertBlockReason ?? `선택한 셀에 ${item.label} 추가`);
              return (
                <li key={item.radioGroup ? 'radio-group' : (item.special ?? item.kind)}>
                  <button
                    type="button"
                    onClick={() =>
                      item.radioGroup
                        ? handleAddRadioGroup()
                        : handleAddNode(item.kind, item.special)
                    }
                    disabled={disabled}
                    title={title}
                    className="w-full text-left px-3 py-2 text-sm rounded border border-gray-200 bg-white text-gray-700 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200"
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className="w-4 text-center text-gray-500" aria-hidden>
                        {item.icon}
                      </span>
                      {item.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-[11px] text-gray-400 leading-relaxed">
            그룹을 선택한 상태로 추가하면 그룹 안에 들어갑니다. 노드 테두리 안쪽에 마우스를 올리면 이동
            커서로 바뀌며, 거기서 끌어 순서·그룹 이동합니다. 선택한 노드의 테두리 바깥 경계(4변)에 올리면
            크기 조절 커서(↔/↕)로 바뀌며, 셀 경계처럼 끌어 크기를 조절합니다.
            <span className="text-gray-500">Esc</span>로 상위(셀→표→부모 그룹)를 선택합니다.{' '}
            {/* 노드 종류 전체는 매뉴얼로 — 팔레트 목록만으론 각 노드가 무엇인지 알 수 없다. */}
            <HelpLink topic="nodePalette" />
          </p>
              </div>
            ),
          },
          {
            id: 'canvas',
            title: '캔버스',
            content: (
              // 캔버스 — flex 컨텍스트 래퍼로 감싸 CanvasViewport의 flex-1이 채워지게. props/내용은 무변경.
              <div className="flex h-full min-h-0">
        {/* 중앙: 캔버스 (CanvasViewport — 줌/팬/recenter 공통, DndContext로 드래그 재정렬/재배치) */}
        <CanvasViewport
          // 캔버스 박스 폭 — page(size+orientation)에서 산출(landscape면 297mm 폭). RenderTemplate 폭과 정합.
          pageWidth={pagePx(template.page).w}
          // 새로고침/탭 복귀 시 줌·패닝 복원(템플릿별) — 2026-07-04 사용자 확정 UX.
          persistKey={`edit:${template.key}`}
          // DndContext로 박스를 감싸 드래그 재정렬/재배치(form 전용). preview는 wrapCanvas 미지정.
          wrapCanvas={(box) => (
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setIsDragging(false)}
            >
              {box}
            </DndContext>
          )}
          onCanvasClick={handleCanvasClick}
          onCanvasDoubleClick={handleCanvasDoubleClick}
          onCanvasContextMenu={handleCanvasContextMenu}
          // R3 픽 모드 호버 미리보기 — 픽 활성 중에만 동작(핸들러 첫 줄 가드), 그 외 0 오버헤드.
          onCanvasMouseMove={handleCanvasPickHover}
          onCanvasMouseLeave={() => setPickHover(null)}
          // 셀 드래그 범위 선택(엑셀식) — 셀 위 mousedown→threshold 초과 이동 시 콜백.
          onCellDragStart={handleCellDragStart}
          onCellDragMove={handleCellDragMove}
          // A4 박스 바깥(회색 배경) 클릭 → 선택 해제(편집 중이면 인라인 종료 후).
          onOutsideClick={() => {
            if (editingTarget) {
              setEditingTarget(null);
              setCaptureEditing(false);
            }
            setSelectedCells([]);
            selectSingleNode(null);
            setClipboardSource(null); // 선택 해제 → 소스 표시 해제.
          }}
          // template 변화 시(노드 추가/삭제/리사이즈로 DOM 변동) 오버레이 rect 재측정 트리거.
          // activeBranchSlots 포함: 분기 슬롯 전환은 template 불변 UI state지만 DOM 높이가 바뀌므로
          // 미포함 시 선택 링/배지가 옛 rect(가로줄 잔상)로 남는다(2026-07-03 사용자 신고).
          // ⚠️ 인라인 배열 리터럴 금지 — 매 렌더 새 참조면 tick→재렌더 무한 루프. useMemo 필수.
          remeasureDep={remeasureDep}
          // 편집 오버레이 — 박스(미스케일) 직속, canvasEl/zoom으로 좌표 측정.
          overlays={(canvasEl, zoom) => (
            <>
              {/* 선택 인디케이터 — 맨 위 오버레이(CSS outline은 이웃에 덮여 일부만 보이는 문제 해결).
                  다중선택이면 각 노드마다 outline을 그린다(셀 선택 중이면 표시 안 함, 단일 회귀 0). */}
              {selectedCells.length === 0 &&
                (selectedNodeIds.length > 1
                  ? selectedNodeIds
                  : [selectedNodeId].filter((v): v is string => v !== null)
                ).map((id) => <SelectionOverlay key={id} canvas={canvasEl} nodeId={id} />)}
              {/* R2 — 계산 입력 "기존 값" 콤보 호버 시 해당 셀/노드 앰버 하이라이트 */}
              <ComputeSourceHighlightOverlay canvas={canvasEl} sourceId={highlightedSourceId} />
              {/* 식별 키 칸 포커스 중 — 같은 키를 쓰는 **다른** 자리를 teal 파선으로 강조.
                  경고가 "겹친다"고만 말하던 관계를 눈으로 짚게 한다(라디오식 의도 공유 포함). */}
              {sharedKeyOwnerIds.map((id) => (
                <ComputeSourceHighlightOverlay
                  key={`shared-key:${id}`}
                  canvas={canvasEl}
                  sourceId={id}
                  tone="shared"
                />
              ))}
              {/* 경고 옆 "사용처" 목록 항목 hover — 그 한 자리만 강조(+ 채널이 스크롤도 태운다).
                  목록을 열려면 키 칸에서 포커스가 빠지므로 위 포커스 강조와는 동시에 뜨지 않는다. */}
              <ComputeSourceHighlightOverlay
                canvas={canvasEl}
                sourceId={sharedKeyHoverOwnerId}
                tone="shared"
              />
              {/* R3 — 픽 모드 중 캔버스 호버 미리보기(파랑=바인딩 있음/클릭 가능, 빨강=없음/클릭 불가) */}
              {cellPick && pickHover && (
                <ComputeSourceHighlightOverlay
                  canvas={canvasEl}
                  sourceId={pickHover.id}
                  tone={pickHover.hasBinding ? 'valid' : 'invalid'}
                />
              )}
              {/* 반복영역 셀 지정 픽 — 영역 행 밴드(rowStart~rowEnd)를 강조 + 밖은 dim(매핑 가능 행 가이드) */}
              {cellPick?.pickKind === 'cell' && (
                <RepeatRowHighlightOverlay
                  canvas={canvasEl}
                  tableId={cellPick.tableId}
                  anchorRows={cellPick.anchorRows}
                />
              )}
              {/* R3 — 픽 모드 안내 배너(클릭 차단 안 함) */}
              {cellPick && (
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 60,
                    pointerEvents: 'none',
                  }}
                  className="rounded-full bg-brand-600 text-white text-[11px] px-3 py-1 shadow-lg whitespace-nowrap"
                >
                  {(cellPick.pickKind === 'cell' ? cellPick.hint : undefined) ??
                    '계산 입력에 사용할 셀/항목을 클릭하세요'}{' '}
                  · ESC 취소
                </div>
              )}
              {/* 복사/잘라내기 소스 셀 표시(marching ants) — 외곽 점선, 선택(파랑)과 구분되는 초록 */}
              <ClipboardSourceOverlay canvas={canvasEl} source={clipboardSource} />
              {/* 패딩 영역 시각화(편집 보조) — 패딩 있는 모든 노드의 박스↔콘텐츠 사이 반투명 띠("패딩 보기" 토글) */}
              {showPadding && <PaddingOverlay canvas={canvasEl} body={template.body} />}
              {/* (c) 바인딩 배지 오버레이 — 노드🔗/셀(ƒ·∑) 배지 + hover 한글 요약("배지 보기" 토글) */}
              {showBadges && (
                <BindingBadgeOverlay canvas={canvasEl} body={template.body} catalog={catalog} />
              )}
              {/* 분기 슬롯 탭 오버레이 — branch 노드 바로 위에 그 branch의 전체 슬롯 탭 바.
                  ⚠️ 아래 BranchLinkOverlay보다 **먼저** 렌더돼야 한다: 탭 바는 layout effect에서
                  겹침만큼 밀려 최종 위치가 정해지고, 연결선은 그 탭 rect를 실측해 출발점을 잡는다.
                  형제 layout effect는 JSX 순서대로 도므로 순서를 바꾸면 선이 한 박자 옛 자리에 남는다.
                  전체 슬롯이 상시 떠 있으므로 F9(연결선 모드)가 쓰는 비활성 슬롯 앵커도 늘 있다. */}
              <BranchSlotTabsOverlay
                canvas={canvasEl}
                body={template.body}
                activeBranchSlots={activeBranchSlots}
                missedBranchIds={missedBranchIds}
                onSelectSlot={setActiveBranchSlot}
                onSelectBranch={(id) => {
                  setSelectedCells([]);
                  selectSingleNode(id);
                }}
              />
              {/* 분기 관계 연결선(F9) — 슬롯 탭 → 그 슬롯 안 중첩 branch 노드. 활성 슬롯만 선을
                  그을 수 있고(비활성 슬롯 children은 캔버스 미렌더 = rect 부재), 비활성 탭에는
                  ▸ 표식으로 "안에 분기가 더 있다"만 암시한다. 탭(zIndex 26) 아래(25)에 깔린다. */}
              {showBranchLinks && (
                <BranchLinkOverlay
                  canvas={canvasEl}
                  body={template.body}
                  activeBranchSlots={activeBranchSlots}
                />
              )}
              {/* 드래그 핸들 + 삽입 드롭존 오버레이 */}
              <DragLayer
                canvas={canvasEl}
                body={template.body}
                isDragging={isDragging}
                groupDirectionOf={groupDirectionOf}
                onSelect={(id) => {
                  setSelectedCells([]);
                  selectSingleNode(id);
                }}
              />
              {/* 선택 노드 4변 경계 드래그 리사이즈(시각 핸들 없음, 테두리 바깥을 잡아 끈다).
                  이동(DragLayer 안쪽 strip)과 공간이 겹치지 않게 바깥 전용. 드래그 중·셀 선택 중엔 숨김.
                  branch(투명 논리 컨테이너)는 자체 크기가 없어 제외 — 크기는 슬롯 콘텐츠가 결정.
                  표(table)는 TableEdgeResizeOverlay(셀/열/행 경계)가 크기를 전담하므로 여기서 제외
                  (외곽 경계 이중 처리 회피 — 셀 오버레이 우선). */}
              {selectedNode &&
                selectedNode.kind !== 'branch' &&
                selectedNode.kind !== 'table' &&
                !isDragging &&
                selectedCells.length === 0 &&
                !editingTarget && (
                  <NodeEdgeResizeOverlay
                    canvas={canvasEl}
                    node={selectedNode}
                    zoom={zoom}
                    onPreview={handleResizeLive}
                    onCommit={handleResizeCommit}
                  />
                )}
              {/* 표 열/행 경계 드래그 리사이즈(엑셀/한글식) — 선택 표의 셀 경계 ±3px hit strip.
                  셀 선택 중에도 유효(열/행 크기 조정은 셀 편집과 독립). 드래그 중·인라인 편집 중엔 숨김. */}
              {selectedNode && selectedNode.kind === 'table' && !isDragging && !editingTarget && (
                <TableEdgeResizeOverlay
                  canvas={canvasEl}
                  node={selectedNode}
                  zoom={zoom}
                  onColsLive={handleColEdgeLive}
                  onColsCommit={handleColEdgeCommit}
                  onRowsLive={handleRowEdgeLive}
                  onRowsCommit={handleRowEdgeCommit}
                />
              )}
              {/* 상시 캡처 textarea(셀 타이핑 자동 편집, IME 안전) — 셀 선택 시 포커스를 유지해
                  한글 첫 자모부터 조합되게 하고, 첫 글자 입력 시 같은 textarea를 선택 셀 위로 승격해
                  편집한다(InlineTextEditor 미마운트, 조합 이관 없음). 결정 2026-06-28-composer-cell-type-to-edit. */}
              <CellCaptureEditor
                textareaRef={captureRef}
                canvas={canvasEl}
                zoom={zoom}
                editing={captureEditing}
                target={
                  captureEditing && editingTarget && editingTarget.kind === 'cell'
                    ? { tableId: editingTarget.tableId, row: editingTarget.row, col: editingTarget.col }
                    : null
                }
                onTypingStart={handleCaptureTypingStart}
                onCommit={handleInlineCommit}
                onSaveShortcut={handleEditorSaveShortcut}
                onCancel={handleInlineCancel}
                onPreview={handleInlineLive}
                onTab={handleInlineTab}
                onClipboardCopy={handleCellCopy}
                onClipboardCut={handleCellCut}
                onClipboardPaste={handleCellPaste}
              />
              {/* 인라인 텍스트 에디터(더블클릭/F2/재클릭 진입) — 대상 텍스트가 유효할 때만.
                  캡처 textarea 편집(captureEditing) 중에는 마운트하지 않는다(같은 셀을 캡처 textarea가 편집). */}
              {editingTarget && editingInitialText !== null && !captureEditing && (
                <InlineTextEditor
                  // 대상이 바뀌면(Tab 이동 등 같은 배치 내 셀A→셀B 전환 포함) 반드시 remount —
                  // closedRef/defaultValue/focus가 초기화되어야 새 셀 편집이 올바로 시작된다.
                  key={
                    editingTarget.kind === 'cell'
                      ? `cell:${editingTarget.tableId}:${editingTarget.row}:${editingTarget.col}`
                      : `node:${editingTarget.id}`
                  }
                  canvas={canvasEl}
                  target={editingTarget}
                  zoom={zoom}
                  initialText={editingInitialText}
                  onCommit={handleInlineCommit}
                  onSaveShortcut={handleEditorSaveShortcut}
                  onCancel={handleInlineCancel}
                  onPreview={handleInlineLive}
                  onTab={handleInlineTab}
                />
              )}
            </>
          )}
        >
          <SelectionStyles
            selectedNodeId={selectedNodeId}
            selectedCells={selectedCells}
            selectedNode={selectedNode}
          />
          {/* 여백(spacer) 가시화 — 편집 캔버스 전용(출력 무영향) */}
          <SpacerHints body={template.body} />
          {/* (다단 1단 강조 힌트 제거 — identityCol 수렴으로 2단 band 셀까지 파랑이 번져 반복표 선택이
              일반표와 달라 보였다. 선택 표현은 SelectionOverlay(외곽)+SelectionStyles(셀 점선)로 통일.) */}
          <PagedEditBody
            template={template}
            data={applySimulatedApproval(template, canvasData, approvalSignedLevel)}
            specialComponents={COMPOSER_SPECIAL_COMPONENTS}
            // 실데이터 반영 중엔 미해소(live/보고서-스코프)만 amber placeholder — 일반 빈값은
            // 실렌더처럼 빈칸, 저작 중 원리적으로 없는 값만 구분 표시(진짜 빈 실데이터/깨진 바인딩 변별).
            bindingPlaceholders={previewResult ? 'unresolvable-only' : true}
            catalog={catalogIndex}
            // 미해소(빈값) amber의 대칭 — 채워진 값이 모사/연결 보고서 저장값이면 점선 밑줄.
            bindingProvenance={bindingProvenance}
            editing
            // 반복행 필드 라벨({점검유무} 등)도 실데이터 반영 중엔 숨김 — 빈 필드는 실렌더처럼 빈칸
            // (카탈로그 placeholder와 동일 정책). 해제(바인딩 보기) 상태에서만 라벨 표시.
            bindingLabels={previewResult ? undefined : bindingLabels}
            // branch 활성 슬롯(탭) — editing이라 조건 미평가, 이 인덱스 슬롯만 렌더.
            activeBranchSlots={activeBranchSlots}
            // 낱장 수 통지 — 용지 설정 패널의 페이지번호 경고가 이 값으로 판정한다.
            onPageCount={handleCanvasPageCount}
          />
        </CanvasViewport>
              </div>
            ),
          },
          {
            id: 'property',
            title: '속성',
            compactWidthPx: 40,
            compactThresholdPx: 80,
            defaultWidthPx: 300,
            // 접힌 세로 레일 — 펴기는 onExpand.
            renderCompact: ({ onExpand }) => (
              <div className="flex h-full flex-col items-center py-2">
                <button
                  type="button"
                  onClick={onExpand}
                  title="속성 펼치기"
                  aria-label="속성 펼치기"
                  className="w-6 h-6 flex items-center justify-center text-gray-500 rounded hover:bg-gray-100"
                >
                  «
                </button>
                <span
                  className="mt-3 text-[11px] text-gray-400 tracking-wider"
                  style={{ writingMode: 'vertical-rl' }}
                >
                  속성
                </span>
              </div>
            ),
            content: (
              <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1">
              {/* 양식 노드/셀 → 배치된 작성 위젯으로 이동(셀 선택 시 셀 바인딩 우선). 매치 있을 때만 노출. */}
              {crossNavTargetWidgetId && (
                <button
                  type="button"
                  onClick={() => navigateToWriteWidget(crossNavTargetWidgetId)}
                  title="이 항목이 배치된 작성 탭 위젯으로 이동"
                  className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                >
                  위젯으로 이동
                </button>
              )}
            </div>
            {selectedNode && selectedNodeIds.length <= 1 && (
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => handleMove('up')}
                  title="위로 이동 (↑)"
                  className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => handleMove('down')}
                  title="아래로 이동 (↓)"
                  className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                >
                  ↓
                </button>
                {/* 부모(group/stack/branch 슬롯) 밖 승격 — 툴바 "그룹(스택) 밖으로"와 동일 경로.
                    위젯 속성 패널 "섹션 밖으로"의 노드 대응물(2026-07-16 사용자 요청). */}
                {canMoveOutSelected && (
                  <button
                    type="button"
                    onClick={handleMoveOutSelected}
                    title="그룹(스택) 밖으로 이동 — 부모 바로 다음 위치"
                    className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    그룹(스택) 밖으로
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDelete}
                  title="삭제 (Delete)"
                  className="px-2 py-1 text-xs rounded border border-red-300 bg-white text-red-600 hover:bg-red-50"
                >
                  삭제
                </button>
              </div>
            )}
          </div>
          {/* 노드 id 표시(클릭 복사) — 위젯 속성 패널과 대칭(지칭 대화·디버깅용). */}
          {selectedNode && selectedNodeIds.length <= 1 && (
            <div className="-mt-2 mb-2">
              <CopyableIdLine id={selectedNode.id} />
            </div>
          )}
          {selectedCells.length > 1 && selectedNode && selectedNode.kind === 'table' ? (
            <BulkCellEditor
              cells={selectedCellData}
              onPatch={handleBulkCellPatch}
              onPreview={handleBulkCellPatchLive}
              onCommit={handleBulkCellPatchCommit}
              onRevert={discardTransient}
              onBindingFormat={handleBulkBindingFormat}
              onBindingAssign={handleBulkBindingAssign}
              onClearCells={handleBulkClearBinding}
              onComputeAssign={handleBulkComputeAssign}
              onClearCompute={handleBulkClearCompute}
              computeAvailableBindings={collectAvailableBindings(template.body, catalog, '')}
              onHighlightSource={setHighlightedSourceId}
              onStartCellPick={(token, apply) => setCellPick({ token, apply })}
              onCancelCellPick={() => setCellPick(null)}
              activePickToken={cellPick?.token ?? null}
              catalog={catalog}
              onBack={handleBackToTable}
              keyOptions={
                // 표시 형식 일괄의 경로 칸 후보 — 선택이 한 표 안에 있을 때만 산출한다. 다른 표에
                // 걸친 선택은 (row,col)을 selectedNode로 풀면 엉뚱한 반복영역이 잡힌다.
                selectedCells.every((c) => c.tableId === selectedNode.id)
                  ? tableCellRowFieldOptions(selectedNode, selectedCellData, template, catalog)
                  : []
              }
              canMergeCells={(() => {
                // 선택 셀이 모두 같은 표일 때만 직사각 범위 병합 후보. min/max 범위를 canMergeCellsRect로 판정.
                if (selectedCells.length < 2) return false;
                if (!selectedCells.every((c) => c.tableId === selectedNode.id)) return false;
                const rows = selectedCells.map((c) => c.row);
                const cols = selectedCells.map((c) => c.col);
                return (
                  canMergeCellsRect(
                    selectedNode,
                    Math.min(...rows),
                    Math.min(...cols),
                    Math.max(...rows),
                    Math.max(...cols),
                  ) &&
                  // 비연결(빈칸 사이) 선택은 병합 불가 — 선택이 rect를 꽉 채울 때만.
                  isRectFullySelected(
                    selectedNode,
                    selectedCells.map((c) => ({ row: c.row, col: c.col })),
                  )
                );
              })()}
              onMergeCells={() =>
                handleMergeCells(
                  selectedNode.id,
                  selectedCells.map((c) => ({ row: c.row, col: c.col })),
                )
              }
            />
          ) : singleCell && selectedNode && selectedNode.kind === 'table' ? (
            <CellEditor
              table={selectedNode}
              row={singleCell.row}
              col={singleCell.col}
              catalog={catalog}
              template={template}
              onPatch={handleCellPatch}
              onPreview={handleCellPatchLive}
              onCommit={handleCellPatchCommit}
              onRevert={discardTransient}
              onBack={handleBackToTable}
              onAddRow={handleAddRow}
              onRemoveRow={handleRemoveRow}
              onAddCol={handleAddCol}
              onRemoveCol={handleRemoveCol}
              onMergeRight={handleMergeCellRight}
              onMergeDown={handleMergeCellDown}
              onUnmerge={handleUnmergeCell}
              onAddNode={handleAddNode}
              onGenerateOptionCheckboxes={handleGenerateOptionCheckboxes}
              onSelectNode={(id) => {
                setSelectedCells([]);
                selectSingleNode(id);
              }}
              onMoveCellChild={(childId, dir) =>
                apply((t) => moveNodeInSiblings(t, childId, dir))
              }
              onRemoveCellChild={(childId) => apply((t) => removeNode(t, childId))}
              onHighlightSource={setHighlightedSourceId}
              onStartCellPick={(token, apply) => setCellPick({ token, apply })}
              onCancelCellPick={() => setCellPick(null)}
              activePickToken={cellPick?.token ?? null}
              onGoToDataCard={handleGoToDataCard}
            />
          ) : selectedNodeIds.length > 1 ? (
            // 노드 다중선택 — 단일 속성 대신 묶기 액션 패널(Phase B).
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700">{selectedNodeIds.length}개 노드 선택됨</div>
              <p className="text-xs text-gray-500">
                같은 부모의 형제 노드들을 하나의 컨테이너로 묶습니다.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handleGroupSelected('group')}
                  title="선택한 노드를 그룹(방향 배치 컨테이너)으로 묶기 (Ctrl+G)"
                  className="px-3 py-1.5 text-xs rounded border border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100"
                >
                  그룹으로 묶기 <span className="text-brand-400">(Ctrl+G)</span>
                </button>
                {/* 스택 직접 묶기 버튼은 제거(그룹으로 묶은 뒤 그룹⇄스택 전환으로 대체 — 진입점 중복) */}
              </div>
              {/* 선택 노드들에 폭/높이/최소높이/정렬/패딩을 일괄 적용(혼합 tri-state). */}
              <BulkNodePropsEditor
                nodeIds={selectedNodeIds}
                template={template}
                apply={apply}
                replacePresent={replacePresent}
                commitPresent={commitPresent}
                discardTransient={discardTransient}
              />
            </div>
          ) : selectedNode ? (
            <PropertyPanel
              node={selectedNode}
              overflowNotes={selectedOverflowNotes}
              isTopLevel={isPageFlowPosition(template.body, selectedNode.id)}
              onPatch={handlePatch}
              onPreview={handleResizeLive}
              onCommit={handleResizeCommit}
              catalog={catalog}
              dataDefs={template.dataDefs}
              dataSources={template.dataSources}
              approvalLineMeta={template.approvalLineMeta}
              onUpdateApprovalLineMeta={updateApprovalLineMeta}
              onAddRow={handleAddRow}
              onRemoveRow={handleRemoveRow}
              onAddCol={handleAddCol}
              onRemoveCol={handleRemoveCol}
              onSetColWidths={handleSetColWidths}
              onSetRowHeights={handleSetRowHeights}
              onPreviewColWidths={handlePreviewColWidths}
              onPreviewRowHeights={handlePreviewRowHeights}
              onRevert={discardTransient}
              onSetTableFlag={handleSetTableFlag}
              onAddRepeatRegion={handleAddRepeatRegion}
              onRemoveRepeatRegion={handleRemoveRepeatRegion}
              onUpdateRepeatRegion={handleUpdateRepeatRegion}
              onSetRepeatPatternRows={handleSetRepeatPatternRows}
              importRepeatHints={template.importMeta?.repeatHints}
              onApplyImportRepeatHint={handleApplyImportRepeatHint}
              onDismissImportRepeatHint={handleDismissImportRepeatHint}
              onCreateManualDataSource={handleCreateManualDataSource}
              onAddDataSourceColumn={handleAddDataSourceColumn}
              onRenameDataSourceColumn={handleRenameDataSourceField}
              onDeleteDataSourceColumn={handleDeleteDataSourceColumn}
              onUnmapDataSourceColumn={handleUnmapDataSourceColumn}
              onMoveDataSourceColumn={handleMoveDataSourceColumn}
              onHighlightSource={setHighlightedSourceId}
              onGoToDataCard={handleGoToDataCard}
              setCellBinding={setCellBinding}
              onStartCellPick={(token, applyRef, opts) =>
                setCellPick({
                  token,
                  pickKind: 'cell',
                  apply: applyRef,
                  tableId: opts?.tableId,
                  anchorRows: opts?.anchorRows,
                  hint: opts?.hint,
                  onFillRow: opts?.onFillRow,
                })
              }
              onCancelCellPick={() => setCellPick(null)}
              activePickToken={cellPick?.token ?? null}
              onSelectNode={(id) => {
                setSelectedCells([]);
                selectSingleNode(id);
              }}
              ancestorGroups={ancestorGroups}
              onPatchAllCells={handlePatchAllCells}
              onPatchAllCellsLive={handlePatchAllCellsLive}
              onPatchAllCellsCommit={handlePatchAllCellsCommit}
              onToggleContainerKind={handleToggleContainerKind}
              onWrapInBranch={handleWrapInBranch}
              onAddRadioOption={handleAddRadioOption}
              usedBindingKeys={selectedNodeUsedBindingKeys}
              activeBranchSlots={activeBranchSlots}
              onSetActiveBranchSlot={setActiveBranchSlot}
              onAddBranchSlot={handleAddBranchSlot}
              onRemoveBranchSlot={handleRemoveBranchSlot}
              onMoveBranchSlot={handleMoveBranchSlot}
              onUpdateBranchSlotCondition={handleUpdateBranchSlotCondition}
              onUpdateBranchSlotLabel={handleUpdateBranchSlotLabel}
              onUpdateBranchConditionBindings={handleUpdateBranchConditionBindings}
            />
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">템플릿명</label>
                <input
                  type="text"
                  value={template.name}
                  onChange={(e) => apply((t) => ({ ...t, name: e.target.value }))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  placeholder="표시 이름"
                />
              </div>
              {/* 분류(category) — 편집 funnel 밖 별도 state(currentCategoryId). 저장(💾)이 본문과 같은 body로
                  보낸다(별도 PATCH 없음). 선택지는 분류 목록 + 「미분류」(=null). 결정: 2026-09-20 */}
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1" htmlFor="template-category-select">
                  분류
                  <HelpLink topic="templateCategories" />
                </label>
                <select
                  id="template-category-select"
                  value={currentCategoryId ?? ''}
                  onChange={(e) => setCurrentCategoryId(e.target.value || null)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                >
                  <option value="">{UNCATEGORIZED_LABEL}</option>
                  {templateCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                  {/* 목록에 없는 분류를 가리키는 저장본(분류가 방금 지워졌거나 목록 미도착) — 값이
                      select에서 조용히 「미분류」로 보이지 않게 그 id를 임시 항목으로 유지한다. */}
                  {currentCategoryId && !templateCategories.some((c) => c.id === currentCategoryId) && (
                    <option value={currentCategoryId}>
                      {templateList.find((t) => t.categoryId === currentCategoryId)?.category?.name ??
                        '(삭제된 분류)'}
                    </option>
                  )}
                </select>
                <p className="mt-1 text-[11px] text-gray-400">
                  템플릿 선택 목록의 묶음입니다. 분류는 상단 「분류 관리」에서 추가·정렬합니다.
                </p>
              </div>
              {/* 제목 규칙(titleTemplate) — 생성 시 서버가 토큰 치환해 보고서 제목으로 쓴다.
                  지정 시 서버 치환이 작성 페이지 제목보다 우선. 비우면 저장 시 해제(null).
                  결정: 2026-08-17-composer-title-template */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">제목 규칙</label>
                <TitleTemplateField
                  value={template.titleTemplate ?? ''}
                  policy={template.policy}
                  onChange={(next) =>
                    apply((t) => ({ ...t, titleTemplate: next || undefined }))
                  }
                />
                <p className="mt-1 text-[11px] text-gray-400">
                  보고서 생성 시 토큰을 치환해 제목으로 씁니다. 비워 두면 기본형(템플릿명 - 현장명
                  (날짜))을 씁니다. 차수는 연결된 점검의 현장 내 순번입니다.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">key (고유 식별자)</label>
                <input
                  type="text"
                  value={template.key}
                  onChange={(e) => apply((t) => ({ ...t, key: e.target.value }))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded font-mono"
                  placeholder="kebab-case"
                />
                {!isKebabKey(template.key) && (
                  <p className="mt-1 text-xs text-red-600">kebab-case(소문자/숫자/하이픈)여야 합니다.</p>
                )}
                {currentKey &&
                  template.key !== currentKey &&
                  templateList.some((t) => t.key === template.key && t.key !== currentKey) && (
                    <p className="mt-1 text-xs text-red-600">이미 존재하는 key입니다.</p>
                  )}
                {!currentKey &&
                  templateList.some((t) => t.key === template.key) && (
                    <p className="mt-1 text-xs text-red-600">이미 존재하는 key입니다.</p>
                  )}
              </div>
              {/* 용지 크기 — A4/A3/LETTER select. 선택지는 엔진 정본인 rc page.ts의 PAGE_MM_PORTRAIT
                  키에서 파생한다(하드코딩 금지 — 지원 용지가 늘면 여기도 자동으로 따라온다).
                  변경은 apply로 undo/isDirty에 편입되고, 캔버스 폭·페이지 분할은 pagePx/pageMm를
                  거치므로 별도 배선 없이 즉시 반영된다. 옆 mm 표기는 방향(swap)까지 반영된 실치수. */}
              <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">용지 크기</span>
                <div className="flex items-center gap-2">
                  <select
                    aria-label="용지 크기"
                    value={template.page.size}
                    onChange={(e) =>
                      apply((t) => ({
                        ...t,
                        page: { ...t.page, size: e.target.value as PageSize },
                      }))
                    }
                    className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
                  >
                    {(Object.keys(PAGE_MM_PORTRAIT) as PageSize[]).map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-500">
                    {pageMm(template.page).w} × {pageMm(template.page).h} mm
                  </span>
                </div>
              </div>
              {/* 용지 방향 — 세로(기본)/가로 세그먼트 토글. 변경은 apply(undo/isDirty 편입). */}
              <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">방향</span>
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="group" aria-label="용지 방향">
                  {([
                    ['portrait', '세로'],
                    ['landscape', '가로'],
                  ] as const).map(([value, label]) => {
                    const active = (template.page.orientation ?? 'portrait') === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={active}
                        onClick={() =>
                          apply((t) => ({ ...t, page: { ...t.page, orientation: value } }))
                        }
                        className={`px-3 py-1 text-xs ${
                          active ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* 페이지 여백 — 한글식 mm 입력(저장은 px). 템플릿별로 보존. */}
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-600">페이지 여백 (mm)</label>
                  <button
                    type="button"
                    onClick={() => apply((t) => ({ ...t, page: { ...t.page, margins: defaultMarginsPx() } }))}
                    title="기존 보고서와 동일한 A4 기본 여백 위10·아래15·좌우10mm로 설정"
                    className="text-[11px] text-brand-600 hover:underline"
                  >
                    기본값
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  {([
                    ['top', '위'],
                    ['bottom', '아래'],
                    ['left', '왼쪽'],
                    ['right', '오른쪽'],
                  ] as const).map(([side, label]) => (
                    <label key={side} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <span className="w-8 shrink-0">{label}</span>
                      <NumericField
                        value={template.page.margins[side]}
                        min={0}
                        step={0.01}
                        toDisplay={pxToMm}
                        fromDisplay={mmToPx}
                        className="w-full px-1.5 py-1 border border-gray-300 rounded"
                        onPreview={(px) =>
                          replacePresent((t) => ({
                            ...t,
                            page: { ...t.page, margins: { ...t.page.margins, [side]: px } },
                          }))
                        }
                        onCommit={(px) =>
                          commitPresent((t) => ({
                            ...t,
                            page: { ...t.page, margins: { ...t.page.margins, [side]: px ?? 0 } },
                          }))
                        }
                        onRevert={discardTransient}
                      />
                    </label>
                  ))}
                </div>
              </div>
              {/* 문서 페이지번호 — 템플릿 전역 속성(page.pageNumber). 기본은 **꺼짐**이라 기존 양식은
                  무영향이고, 켜는 것이 저작자 결정이다(결재 완료본은 법적 증빙이라 이미 배포된 문서의
                  외형이 조용히 바뀌면 사고 — 결정 §3).

                  선택지·기본값·경고 판정은 전부 shared 정본에서 파생한다(PAGE_NUMBER_*). 여기서 union을
                  손으로 열거하거나 조건을 다시 쓰면 값이 늘어난 날 패널만 조용히 갈린다.

                  번호는 편집 캔버스에도 **출력과 같은 자리·같은 문자열로** 뜬다(rc PageNumberLabel 한
                  조각을 낱장 래퍼 둘이 공유) — 그래서 이 패널을 만지면 캔버스에서 바로 결과가 보인다.
                  결정: 2026-09-30-composer-document-page-numbers */}
              <div className="pt-2 border-t border-gray-100">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs font-medium text-gray-600">페이지번호 표시</span>
                  <input
                    type="checkbox"
                    checked={template.page.pageNumber?.enabled ?? PAGE_NUMBER_DEFAULTS.enabled}
                    onChange={(e) =>
                      apply((t) => ({
                        ...t,
                        page: {
                          ...t.page,
                          pageNumber: { ...t.page.pageNumber, enabled: e.target.checked },
                        },
                      }))
                    }
                  />
                </label>
                {!(template.page.pageNumber?.enabled ?? PAGE_NUMBER_DEFAULTS.enabled) ? (
                  <p className="mt-1 text-[11px] text-gray-400">
                    켜면 모든 낱장 하단 여백에 번호가 찍힙니다(인쇄·PDF·공유 포함). 편집 캔버스에도
                    같은 자리에 보입니다.
                  </p>
                ) : (
                  <div className="mt-1.5 space-y-1.5">
                    {/* 형식 — 옆 예시는 shared formatPageNumber가 만든 문자열이라 실제로 찍히는 것과 같다. */}
                    <label className="flex items-center justify-between gap-2 text-xs text-gray-600">
                      <span className="shrink-0">형식</span>
                      <select
                        aria-label="페이지번호 형식"
                        value={template.page.pageNumber?.format ?? PAGE_NUMBER_DEFAULTS.format}
                        onChange={(e) =>
                          apply((t) => ({
                            ...t,
                            page: {
                              ...t.page,
                              pageNumber: {
                                ...t.page.pageNumber,
                                format: e.target.value as PageNumberFormat,
                              },
                            },
                          }))
                        }
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
                      >
                        {PAGE_NUMBER_FORMAT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label} ({opt.sample})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center justify-between gap-2 text-xs text-gray-600">
                      <span className="shrink-0">위치</span>
                      <select
                        aria-label="페이지번호 위치"
                        value={template.page.pageNumber?.position ?? PAGE_NUMBER_DEFAULTS.position}
                        onChange={(e) =>
                          apply((t) => ({
                            ...t,
                            page: {
                              ...t.page,
                              pageNumber: {
                                ...t.page.pageNumber,
                                position: e.target.value as PageNumberPosition,
                              },
                            },
                          }))
                        }
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
                      >
                        {PAGE_NUMBER_POSITION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {/* 번호 출력 시작 페이지 — 몇 번째 낱장부터 «표시»하나. 여백 입력과 같은 배선
                        (replacePresent 미리보기 → commitPresent 확정 → discardTransient 복원). */}
                    <label className="flex items-center justify-between gap-2 text-xs text-gray-600">
                      <span className="shrink-0">번호 출력 시작 페이지</span>
                      <NumericField
                        value={
                          template.page.pageNumber?.startAtPage ?? PAGE_NUMBER_DEFAULTS.startAtPage
                        }
                        min={1}
                        step={1}
                        title="몇 번째 낱장부터 번호를 표시할지. 2로 두면 1장째(표지)엔 번호가 찍히지 않습니다."
                        className="w-16 px-1.5 py-1 border border-gray-300 rounded text-right"
                        onPreview={(n) =>
                          replacePresent((t) => ({
                            ...t,
                            page: {
                              ...t.page,
                              pageNumber: { ...t.page.pageNumber, startAtPage: n },
                            },
                          }))
                        }
                        onCommit={(n) =>
                          commitPresent((t) => ({
                            ...t,
                            page: {
                              ...t.page,
                              pageNumber: { ...t.page.pageNumber, startAtPage: n ?? undefined },
                            },
                          }))
                        }
                        onRevert={discardTransient}
                      />
                    </label>
                    {/* 「페이지 시작 번호」는 고급으로 접는다 — 기본 1이라 대부분 저작자는 이 칸을
                        보지 않는다. 축을 없앤 것이 아니라 접은 것이다(결정 §재고: 고정하면 표지 관례
                        둘 중 하나를 표현할 수 없다). */}
                    <details className="rounded border border-gray-200 bg-gray-50 px-2 py-1">
                      <summary className="text-[11px] text-gray-500 cursor-pointer">고급</summary>
                      <label className="mt-1.5 flex items-center justify-between gap-2 text-xs text-gray-600">
                        <span className="shrink-0">페이지 시작 번호</span>
                        <NumericField
                          value={
                            template.page.pageNumber?.startNumber ??
                            PAGE_NUMBER_DEFAULTS.startNumber
                          }
                          min={1}
                          step={1}
                          title="위 낱장에 찍히는 번호. 표지를 세지 않으려면 1, 표지를 세되 표지에만 안 찍으려면 2."
                          className="w-16 px-1.5 py-1 border border-gray-300 rounded text-right"
                          onPreview={(n) =>
                            replacePresent((t) => ({
                              ...t,
                              page: {
                                ...t.page,
                                pageNumber: { ...t.page.pageNumber, startNumber: n },
                              },
                            }))
                          }
                          onCommit={(n) =>
                            commitPresent((t) => ({
                              ...t,
                              page: {
                                ...t.page,
                                pageNumber: { ...t.page.pageNumber, startNumber: n ?? undefined },
                              },
                            }))
                          }
                          onRevert={discardTransient}
                        />
                      </label>
                      <p className="mt-1 text-[11px] text-gray-400">
                        「번호 출력 시작 페이지」가 정하는 것은 <b>어느 낱장부터 찍나</b>이고, 이
                        값이 정하는 것은 <b>그 낱장에 찍히는 숫자</b>입니다. 둘은 독립입니다.
                      </p>
                    </details>
                    {/* ⚠️ 켰는데 아무 낱장에도 안 찍히는 상태를 화면이 말한다 — 엔진이 조용히 skip하는
                        것을 숨기면 저작자는 켰다고 믿는다(은닉보다 나쁘다: 화면이 반대 사실을 말한다).
                        판정은 조건을 다시 쓰지 않고 shared pageNumbersNeverShow에 **묻는다**.
                        비차단이다 — 장수는 데이터에 따라 바뀌므로 저장을 막지 않는다. */}
                    {pageNumbersNeverShow(canvasPageCount, template.page.pageNumber) && (
                      <p className="text-[11px] text-amber-600">
                        지금 이 양식은 {canvasPageCount}장인데 번호 출력 시작 페이지가{' '}
                        {template.page.pageNumber?.startAtPage ?? PAGE_NUMBER_DEFAULTS.startAtPage}
                        이라 <b>어느 낱장에도 번호가 찍히지 않습니다</b>. 저장은 됩니다 — 실제 문서는
                        데이터에 따라 더 길어질 수 있습니다.
                      </p>
                    )}
                    {/* ⚠️ 위 경고와 **같은 계열**이다 — 엔진이 조용히 겹치는 것을 화면이 숨기면
                        저작자는 겹친 화면을 보고 「옵션이 고장났다」고 읽는다. 하단 여백이 번호 띠보다
                        얇으면 번호가 본문 위로 올라오는데, 그래도 **보이게 하는 것이 옳다**(「켰는데
                        안 보인다」가 「겹친다」보다 나쁘다 — rc PAGE_NUMBER_MIN_HEIGHT_PX). 그 대가를
                        화면이 말한다.
                        판정·필요 높이는 **rc가 소유한 값**을 읽는다(`pageNumberOverlapsBody` /
                        `pageNumberBandHeightPx`) — 여기서 `margins.bottom < 상수`를 다시 쓰면 규칙이
                        두 곳이 되고, 조각의 최소 높이가 바뀐 날 패널만 거짓말한다.
                        비차단이다 — 저장을 막지 않는다. */}
                    {pageNumberOverlapsBody(template.page) && (
                      <p className="text-[11px] text-amber-600">
                        하단 여백이 {pxToMm(template.page.margins.bottom)}mm로 얇아 번호가{' '}
                        <b>본문 위로 올라옵니다</b>. 겹치지 않게 하려면 하단 여백을{' '}
                        {/* ⚠️ 여기는 `pxToMm`이 아니라 `pxToMmAtLeast`다 — 「이 값을 넣으면 해소된다」가
                            참이어야 하고, 반올림은 그것을 깨뜨린다(2026-09-30 dev 실측이 잡은 결함:
                            `pxToMm(18)=4.76` → 17.99px라 안내대로 넣어도 경고가 남았다). 위 줄의
                            현재값 표시는 `pxToMm`이 옳다 — 목적이 다르다. */}
                        {pxToMmAtLeast(pageNumberBandHeightPx(template.page))}mm 이상으로 두세요. 저장은 됩니다.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {/* 위임 작성(delegationMeta) — enabled면 문서 유형 선택에서 직접/위임 분기 노출.
                  변경은 apply(undo/isDirty 편입). 결정: 2026-07-06-composer-report-delegation-design */}
              <div className="pt-2 border-t border-gray-100">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs font-medium text-gray-600">위임 작성 허용</span>
                  <input
                    type="checkbox"
                    checked={template.delegationMeta?.enabled ?? false}
                    onChange={(e) =>
                      apply((t) => ({
                        ...t,
                        delegationMeta: { ...t.delegationMeta, enabled: e.target.checked },
                      }))
                    }
                  />
                </label>
                <p className="mt-1 text-[11px] text-gray-400">
                  허용하면 문서 유형 선택에서 직접 작성/위임을 고를 수 있습니다.
                </p>
                {/* 발송 채널은 위임 시점 선택이 유일 정본이라 템플릿 기본값 저작 UI를 두지 않는다
                    (2026-07-26 — 모달의 직전 선택 복원에 밀려 실효가 없었다). */}
                {template.delegationMeta?.enabled && (
                  <div className="mt-2 space-y-2">
                    <label className="flex items-center justify-between gap-2 text-xs text-gray-600">
                      <span className="shrink-0">토큰 유효기간(일)</span>
                      <input
                        type="number"
                        min={1}
                        value={template.delegationMeta?.tokenTtlDays ?? ''}
                        placeholder="기본값"
                        title="미지정 시 일일안전점검일지 위임과 동일한 기본 유효기간을 사용합니다."
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value, 10);
                          const ttl = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
                          apply((t) => ({
                            ...t,
                            delegationMeta: {
                              ...t.delegationMeta,
                              enabled: t.delegationMeta?.enabled ?? true,
                              tokenTtlDays: ttl,
                            },
                          }));
                        }}
                        className="w-20 px-1.5 py-1 text-xs border border-gray-300 rounded text-right"
                      />
                    </label>
                  </div>
                )}
              </div>
              {/* 진입 전 사용자 입력(entryUserInput) — 템플릿 전역 스위치(2026-07-16 3차 정정).
                  게이트: 작성폼에 AI 후보 정렬 선택 위젯이 있을 때만 노출(없으면 무의미).
                  문구는 대상 위젯의 entryInput override → 파트 메타 fallback(2026-07-25 개정).
                  결정: 2026-07-16-composer-ai-guidance-regenerate */}
              {(() => {
                const entry = findEntryUserInputMeta(template.composerWidgetTree ?? []);
                if (!entry) return null;
                const on = template.entryUserInput ?? false;
                // override 저장은 findEntryUserInputMeta가 지목한 첫 위젯의 entryInput에.
                // 빈 값이면 필드를 제거해 파트 메타 fallback으로 되돌린다(= "기본 문구 쓰기").
                const setEntryInput = (patch: { label?: string; placeholder?: string }): void =>
                  handleWidgetUpdate(entry.widgetId, (w) => {
                    if (w.kind !== 'input') return w;
                    const merged = { ...w.entryInput, ...patch };
                    const label = merged.label?.trim() ?? '';
                    const placeholder = merged.placeholder?.trim() ?? '';
                    const next = { ...w };
                    // 둘 다 비면 override 자체를 제거 — 해소가 파트 메타 fallback으로 되돌아간다.
                    if (!label && !placeholder) delete next.entryInput;
                    else next.entryInput = { label, ...(placeholder ? { placeholder } : {}) };
                    return next;
                  });
                return (
                  <div className="pt-2 border-t border-gray-100">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs font-medium text-gray-600">진입 전 사용자 입력 받기</span>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) =>
                          // 해제는 명시적 false로 저장 — `|| undefined`면 JSON 직렬화에서 필드가
                          // 탈락해 서버 update가 skip(끄기가 영원히 미반영, 2026-07-25 실사고).
                          apply((t) => ({ ...t, entryUserInput: e.target.checked }))
                        }
                      />
                    </label>
                    <p className="mt-1 text-[11px] text-gray-400">
                      작성폼에 AI 후보 정렬 선택 위젯이 있어 켤 수 있습니다. 켜면 보고서 작성 진입
                      전(문서 유형 선택)에 사용자 지시문을 입력받아 첫 AI 생성에 반영합니다. 같은
                      입력이면 다시 생성하지 않습니다.
                    </p>
                    {on && (
                      <div className="mt-2 space-y-2">
                        <p className="text-[11px] text-gray-400">
                          입력란 문구 — 비워 두면 회색으로 보이는 기본 문구를 씁니다.
                        </p>
                        <label className="block">
                          <span className="block text-[11px] text-gray-500 mb-0.5">라벨</span>
                          <input
                            type="text"
                            value={entry.source.override?.label ?? ''}
                            placeholder={entry.source.fallback.label}
                            onChange={(e) => setEntryInput({ label: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </label>
                        <label className="block">
                          <span className="block text-[11px] text-gray-500 mb-0.5">
                            입력 힌트(placeholder)
                          </span>
                          <input
                            type="text"
                            value={entry.source.override?.placeholder ?? ''}
                            placeholder={entry.source.fallback.placeholder}
                            onChange={(e) => setEntryInput({ placeholder: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* 분할 축(policy.split) — 작성 페이지 진입 데이터를 무엇으로 묶어 나눌지.
                  같은 policy를 쓰는 축이라 노출·중복 정책 바로 위에 둔다(두 writer가 흩어지면
                  한쪽 patch가 다른 축을 덮는 사고가 난다). 2026-09-02 P5-b */}
              <SplitAxisSection
                policy={template.policy}
                onChange={(next) => apply((t) => ({ ...t, policy: next }))}
              />
              {/* 제출 후 흐름(policy.postSubmit) — 작성을 마치면 이어질 화면. 같은 policy를 쓰는
                  축이라 분할 축 곁에 둔다(두 writer가 흩어지면 한쪽 patch가 다른 축을 덮는다).
                  선언 효과(전환 예고 접미)는 작성 탭 미리보기의 「상황」 시뮬로 확인. 2026-09-06 §1 */}
              <PostSubmitFlowSection
                policy={template.policy}
                onChange={(next) => apply((t) => ({ ...t, policy: next }))}
              />
              {/* 노출·중복 정책(policy) — 문서 유형 노출 대상(기관/업종)과 중복 생성 허용.
                  적용은 서버(목록 필터 + 생성 가드) — 여기선 저작만. 2026-07-26 결정 */}
              <TemplatePolicySection
                policy={template.policy}
                dataSources={template.dataSources}
                catalog={catalog}
                onChange={(next) => apply((t) => ({ ...t, policy: next }))}
              />
              {/* props는 작성 탭 전역 코어 호출부와 같은 객체를 spread(드리프트 차단). */}
              <DataSection {...dataSectionProps} />
              {/* 문서 상태 배지의 이동 목적지 — id는 이 탭 호출부에만(작성 탭도 같은 컴포넌트를
                  렌더하므로 컴포넌트 안에 두면 중복 id가 된다). */}
              <div id={VARIABLES_ANCHOR_ID}>
                <VariablesEditor {...variablesEditorProps} />
              </div>
              {/* 카탈로그 파라미터 지정(2026-08-06 결정 개정 1) — 변수 편집기 **바로 아래**가 자리다.
                  변수를 참조하는 축이라 나란히 있어야 "어느 변수가 어디에 꽂혔는지" 짝이 한 화면에서 보인다. */}
              <div id={CATALOG_PARAMS_ANCHOR_ID}>
              <CatalogParamsEditor
                template={template}
                catalog={catalog}
                resolution={previewResult?.paramResolution}
                hasPreviewContext={!!previewResult}
                onChange={(next) => apply((t) => ({ ...t, catalogParams: next }))}
              />
              </div>
              {/* 모사 데이터 프리셋(2026-09-03 결정, 2026-09-04 성격 분리 확장) — 저작·적용/해제·
                  기본 지정이 모두 이 구획 몫. 적용 중 프리셋의 수정은 미리보기 즉시 반영. */}
              <SimulationPresetsSection
                presets={template.simulationPresets}
                axes={simulationAxes}
                onChange={handlePresetsChange}
                defaultPresetId={template.defaultSimulationPresetId}
                appliedPresetId={appliedPresetId}
                onApply={handleApplyPreset}
                onUnapply={handleUnapplyPreset}
                linkedReportActive={!!previewContext.linkedReportId}
              />
              {/* 상세 페이지 chrome(헤더·하단 액션바) 상태별 표시 구성 — detailChrome.
                  저작은 F8 미리보기 팝아웃에서(상태·해상도별 실측). 여기선 진입 버튼만 노출. */}
              <Card elevation="none" className="shrink-0">
                <h3 className="mb-1 text-sm font-semibold text-gray-700">헤더·하단 컨트롤 바 구성</h3>
                <p className="mb-3 text-xs text-gray-500">
                  보고서 화면 상단 헤더·하단 컨트롤 바의 버튼을 F8 미리보기 창에서 결재 진행 상태·해상도별로 편집합니다.
                </p>
                <button
                  type="button"
                  onClick={openChromeEditInPopout}
                  className="rounded border border-brand-300 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
                >
                  F8 미리보기에서 편집
                </button>
              </Card>
              <p className="text-xs text-gray-400">
                캔버스에서 요소를 클릭하면 해당 요소의 속성이 여기에 표시됩니다.
              </p>
            </div>
          )}
              </div>
            ),
          },
        ]}
      />
      </div>
      </div>
      )}
              </>
            ),
          },
        ]}
      />
      </div>
      <ShortcutsModal open={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {/* 캔버스 우클릭 컨텍스트 메뉴 — 양식(노드)/작성(위젯) 탭 공용 셸.
          추가/복사/붙여넣기/복제/삭제를 기존 단축키 핸들러와 공유(body portal, 경계 플립). */}
      {ctxMenu ? (
        <ComposerContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={
            ctxMenu.scope === 'form'
              ? buildFormCtxItems(ctxMenu.target)
              : buildWriteCtxItems(ctxMenu.target, ctxMenu.addAnchorId)
          }
          onClose={() => {
            setCtxMenu(null);
            // 메뉴 닫힘 시 미배치 항목 hover 미니 미리보기 잔존 정리(옵션 mouseleave 미발화 경로).
            showPoolPreview(null, null);
          }}
        />
      ) : null}
      {/* 공개(enabled) 토글 확인 모달 — publishTarget이 적용할 다음 상태. */}
      <TemplatePublishModal
        next={publishTarget?.next ?? null}
        busy={publishBusy}
        onConfirm={() => void handleConfirmPublish()}
        onCancel={() => setPublishTarget(null)}
      />
      {/* 복제 key 입력 모달 — 확인 시 서버 복제 요청. 이름은 '{원본} (복제)' 자동.
          원본이 열린 양식일 때만 미저장 경고(다른 양식의 복제는 편집 상태와 무관). */}
      <TemplateDuplicateModal
        open={duplicateSource !== null}
        sourceKey={duplicateSource?.key ?? ''}
        sourceName={duplicateSource?.name ?? ''}
        isDirty={isDirty && duplicateSource?.key === currentKey}
        existingKeys={templateList.map((t) => t.key)}
        busy={busy}
        onConfirm={(newKey) => void handleConfirmDuplicate(newKey)}
        onClose={() => setDuplicateSource(null)}
      />
      {/* 양식 분류(category) 관리 모달 — 모달 안에서 편집하고 「저장」 1회로 diff만 전송. 닫힌 뒤
          분류·템플릿 목록을 다시 읽는다(삭제된 분류의 양식은 서버가 미분류로 돌린다 — 목록이 그 사실을 보인다). */}
      <TemplateCategoryManageModal
        open={categoryManageOpen}
        categories={templateCategories}
        onClose={() => setCategoryManageOpen(false)}
        onSaved={() => {
          void refreshList();
          void refreshCategories().then((categories) => {
            // 편집 중 양식의 분류가 방금 지워졌으면 서버는 이미 미분류다 — 저장본 값을 따라 내려
            // 속성 select가 「(삭제된 분류)」로 남지 않게. 사용자가 그새 다른 분류를 골랐으면(dirty)
            // 그 선택은 그대로 둔다(저장 시 새 배정으로 나간다).
            if (!categories || !savedCategoryId) return;
            if (categories.some((c) => c.id === savedCategoryId)) return;
            setSavedCategoryId(null);
            setCurrentCategoryId((cur) => (cur === savedCategoryId ? null : cur));
          });
        }}
      />
      {/* 새 템플릿 key 입력 모달 — 미저장 변경 경고는 warning으로 통합(별도 confirm 없음). */}
      <TemplateKeyModal
        open={newModalOpen}
        title="새 템플릿 시작"
        confirmLabel="시작"
        defaultKey=""
        keyLabel="새 템플릿 key"
        hintExample="daily-report"
        existingKeys={templateList.map((t) => t.key)}
        busy={busy}
        warning={
          isDirty ? '저장하지 않은 변경이 있습니다 — 새 템플릿을 시작하면 반영되지 않습니다.' : undefined
        }
        onConfirm={handleConfirmNew}
        onClose={() => setNewModalOpen(false)}
      />
      {/* 양식 업로드 → AI 분석 → 템플릿 초안. 완료 후 '에디터에서 열기'로 초안을 로드한다. */}
      <FormImportModal
        open={formImportJob.modalOpen}
        job={formImportJob.state}
        warning={
          isDirty
            ? '저장하지 않은 변경이 있습니다 — 초안을 에디터에서 열면 현재 편집 내용은 사라집니다.'
            : undefined
        }
        // 지금 에디터에 로드된 초안은 모달이 서버에 직접 PATCH하면 안 된다(에디터의 미저장
        // 편집분과 충돌 + 이후 에디터 저장이 방금 적용분을 덮어씀) — 모달이 이 key로 막는다.
        currentKey={currentKey}
        // 반복 제안 적용 시 데이터셋 필드 라벨을 만드는 데 쓴다(카탈로그 연결 바인딩 라벨 보정).
        catalogIndex={catalogIndex}
        onClose={formImportJob.closeModal}
        onUpload={(file, sheetNames) => void formImportJob.startUpload(file, sheetNames)}
        onRetry={formImportJob.reset}
        onCancelJob={formImportJob.cancelJob}
        onOpenEditor={handleFormImportOpenEditor}
      />
      {/* 데이터 연결 후보 제안(P2) — 수동 입력 필드 ↔ 카탈로그 AI 매칭, 적용=prefillFrom(undo 퍼널). */}
      <BindingSuggestModal
        open={bindingSuggestOpen}
        // 캐시 키 — 저장된 템플릿이면 직전 분석 결과 재사용(새 호출은 모달의 "재분석"으로만).
        templateKey={currentKey}
        fields={bindingSuggestFields}
        catalog={catalog}
        onApply={handleApplyBindingSuggestions}
        applied={appliedBindings}
        onDisconnect={handleDisconnectBinding}
        onClose={() => setBindingSuggestOpen(false)}
      />
      {/* 범용 확인 모달 — 불러오기(미저장)/삭제(danger) 등 네이티브 confirm 대체. */}
      <ConfirmModal
        open={confirmState !== null}
        title={confirmState?.title ?? ''}
        body={confirmState?.body ?? ''}
        confirmLabel={confirmState?.confirmLabel}
        danger={confirmState?.danger}
        overlayZClassName={confirmState?.overlayZClassName}
        overlayBgClassName={confirmState?.overlayBgClassName}
        busy={busy}
        onConfirm={() => confirmState?.onConfirm()}
        onClose={() => setConfirmState(null)}
      />
      {/* 파생(바인딩 배치) 위젯 복제 제외 안내 — 결과가 기대와 다른 중요 사안이라 모달(토스트 대체). */}
      <Modal
        open={cloneExcludedNotice !== null}
        onClose={() => setCloneExcludedNotice(null)}
        title="바인딩 배치 위젯 제외 안내"
        size="md"
        footer={
          <Button variant="primary" autoFocus onClick={() => setCloneExcludedNotice(null)}>
            확인
          </Button>
        }
      >
        <div className="space-y-2 text-sm text-gray-700">
          <p>
            {cloneExcludedNotice?.alreadyPlaced ? (
              <>
                바인딩 배치 위젯 {cloneExcludedNotice.count}개는 이미 양식에 배치되어 있어{' '}
                {cloneExcludedNotice.action}에서 제외했습니다.
              </>
            ) : (
              <>
                바인딩 배치 위젯 {cloneExcludedNotice?.count}개는 {cloneExcludedNotice?.action}에서
                제외했습니다.
              </>
            )}
          </p>
          {cloneExcludedNotice?.action === '프리셋 삽입' ? (
            <p className="text-xs leading-5 text-gray-500">
              배치 위젯은 <b>그 양식의 본문 바인딩과 1:1</b>로 묶여 있어 프리셋에 실려 다른 양식으로
              옮겨갈 수 없습니다. 이 프리셋을 재사용하려면 저장할 때 트리에서 추가한 입력(바인딩 배치가
              아닌 것)으로 구성해야 합니다. 지금 필요한 입력은 미배치 위젯 풀에서 배치하세요.
            </p>
          ) : (
            <p className="text-xs leading-5 text-gray-500">
              배치 위젯은 양식 바인딩과 1:1로 연결되어 <b>한 곳에만 배치</b>할 수 있습니다(복제하면 같은
              값이 두 곳에 놓여 한쪽 수정이 다른 쪽에도 반영됩니다). 사본 위치에 입력이 필요하면 미배치
              위젯 풀에서 배치하거나 새 위젯을 추가하세요.
            </p>
          )}
        </div>
      </Modal>
      {/* 위젯 붙여넣기 이식 리포트(P5) — 이식됨/기존 재사용/미해소. 조건 충족 시에만 세팅되므로
          여기서는 report 유무로만 열고 닫는다(표시 규칙은 shouldShowWidgetPasteReport 소유). */}
      <WidgetPasteReportModal
        report={widgetPasteReport}
        onClose={() => setWidgetPasteReport(null)}
      />
      {/* 위젯 프리셋 v1 — "프리셋으로 저장" 입력 모달(플랫폼 발행). */}
      <SaveAsPresetModal
        open={saveAsPresetTarget !== null}
        busy={saveAsPresetBusy}
        forkNotice={saveAsPresetTarget?.forkNotice ?? false}
        excludedDerivedCount={saveAsPresetTarget?.excludedDerivedCount ?? 0}
        onConfirm={(input) => void handleConfirmSaveAsPreset(input)}
        onClose={() => setSaveAsPresetTarget(null)}
      />
      {/* 프리셋 삽입/버전 적용 후 묶음 밖(미해소) 참조 안내 — 삽입 후 저작자가 대상 지정 필요. */}
      <Modal
        open={presetInsertNotice !== null}
        onClose={() => setPresetInsertNotice(null)}
        title="프리셋 삽입 — 지정이 필요한 참조"
        size="md"
        footer={
          <Button variant="primary" autoFocus onClick={() => setPresetInsertNotice(null)}>
            확인
          </Button>
        }
      >
        <div className="space-y-2 text-sm text-gray-700">
          <p>
            {presetInsertNotice?.presetName ? (
              <>
                프리셋 <b>{presetInsertNotice.presetName}</b>
                {objectParticleOf(presetInsertNotice.presetName)} 삽입했습니다.{' '}
              </>
            ) : (
              <>버전을 적용했습니다. </>
            )}
            묶음 밖을 가리키는 참조 {presetInsertNotice?.refs.length ?? 0}건은 자동으로 연결하지
            못했습니다. 삽입된 위젯의 속성에서 대상을 지정하세요.
          </p>
          <ul className="max-h-48 space-y-1 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
            {presetInsertNotice?.refs.map((r, i) => (
              <li key={`${r.widgetId}-${r.axis}-${i}`} className="leading-snug">
                <span className="font-medium text-gray-700">{PRESET_REF_AXIS_LABEL[r.axis] ?? r.axis}</span>
                {' → '}
                <code className="rounded bg-white px-1 text-gray-500">{r.value}</code>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
      {/* 노드 붙여넣기 선택 모달 — 타 템플릿 붙여넣기 시 의존 소스 이식 여부(노드만/함께)를 확인. */}
      <NodePasteChoiceModal
        open={nodePasteModalPayload !== null}
        addedDependencyKeys={nodePasteAddedDependencyKeys}
        hasApprovalMeta={
          !!(nodePasteModalPayload?.approvalLineMeta || nodePasteModalPayload?.approvalPlacement)
        }
        onChoose={(mode) => {
          if (nodePasteModalPayload) handlePasteNodes(nodePasteModalPayload, mode);
          setNodePasteModalPayload(null);
        }}
        onCancel={() => setNodePasteModalPayload(null)}
      />
      <CellDeleteModal
        open={deleteModal !== null}
        choice={deleteModal?.choice ?? 'row'}
        rowCount={deleteModal?.rows.length ?? 0}
        colCount={deleteModal?.cols.length ?? 0}
        onChoiceChange={(choice) =>
          setDeleteModal((dm) => (dm ? { ...dm, choice } : dm))
        }
        onConfirm={handleConfirmCellDelete}
        onCancel={() => setDeleteModal(null)}
      />
      {/* 작성 탭 formTable 행/열 삭제(Ctrl+Delete) — 양식 탭과 동일 모달 재사용. */}
      <CellDeleteModal
        open={ftDeleteModal !== null}
        choice={ftDeleteModal?.choice ?? 'row'}
        rowCount={ftDeleteModal?.rowIds.length ?? 0}
        colCount={ftDeleteModal?.colIds.length ?? 0}
        onChoiceChange={(choice) => setFtDeleteModal((m) => (m ? { ...m, choice } : m))}
        onConfirm={handleConfirmFtCellDelete}
        onCancel={() => setFtDeleteModal(null)}
      />
      {/* 실데이터 반영 모달 — 열릴 때만 mount(draft를 현재 previewContext로 초기화). */}
      {realDataModalOpen && (
        <RealDataModal
          initialContext={previewContext}
          initialLabels={previewContextLabels}
          onConfirm={handleRealDataConfirm}
          onCancel={() => setRealDataModalOpen(false)}
          simulationAxes={simulationAxes}
          onLoadAxisOptions={loadAxisOptions}
        />
      )}
      {/* 미배치 풀 위젯 hover 미니 미리보기 팝오버 — 커서 옆 툴팁식(pointer-events:none). */}
      {poolHoverPreview && (
        <PoolHoverPreview
          template={template}
          content={previewResult ? previewResult.content : {}}
          hasRealData={!!previewResult}
          approvalSignedLevel={approvalSignedLevel}
          bindingProvenance={bindingProvenance}
          placement={poolHoverPreview.placement}
          anchorRect={poolHoverPreview.anchorRect}
          onMouseEnter={keepPoolPreview}
          onMouseLeave={hidePoolPreview}
        />
      )}
    </div>
    </SharedKeyFocusContext.Provider>
    </CatalogParamsEditContext.Provider>
    </PreviewValuesContext.Provider>
  );
}

/**
 * 내장 데이터셋(dataDefs) 에디터 — 요소 미선택 시 속성 패널에 노출(N2-A).
 *
 * 템플릿에 박아두는 범용 리스트/배열 데이터(표/repeat가 over로 참조)의 형태(칼럼)와 기본행을 편집한다.
 * 모든 변경은 onChange(다음 dataDefs 배열)로 상위 apply에 전달 — history undo 대상.
 * 기능적·최소 구현(키/라벨/칼럼/기본행 추가·삭제·편집).
 */
/**
 * 양식 전역 변수(Template Variables) 편집기 — 전역 속성 패널.
 *
 * 변수는 작성폼에 위젯으로 노출되지만 보고서엔 렌더되지 않는다(노드 무관 전역 선언).
 * 작성 입력값은 content[key](top-level) 저장 → RenderTemplate.mergeRepeatScope가 모든 노드/행 scope에
 * 병합 → 어느 노드든 바인딩/switch(on: key)/조건이 key로 참조. (첫 사례: 위험도 방식 methodType.)
 * 결정: docs/decisions/2026-07-07-composer-template-variables.md
 */
/**
 * 저장된 prefillFrom이 CatalogPrefillPicker의 선택 목록(kind='data'|'computed') 밖을 가리키는가.
 *
 * picker는 entry를 못 찾으면 콤보를 **빈칸으로** 그리고 select 경로 입력도 감춰서, 설정이 있는데
 * "미설정"처럼 보이는 유령 상태가 된다(부분 커버리지 편집기의 전형적 함정). 그 경우 저장값을
 * 읽기 전용으로라도 노출해 "안 보임 ≠ 없음"을 분명히 한다.
 */
function prefillOutOfPickerScope(
  prefillFrom: { catalogKey: string; select?: string },
  catalog: CatalogEntry[] | undefined,
): boolean {
  const entry = (catalog ?? []).find((e) => e.key === prefillFrom.catalogKey);
  return !entry || (entry.kind !== 'data' && entry.kind !== 'computed');
}

function VariablesEditor({
  variables,
  catalog,
  variableRefs,
  onDelete,
  onChange,
}: {
  variables?: TemplateVariable[];
  /** 카탈로그 entry 목록 — 옵션 동적 소스(optionsFrom)의 list entry/필드 선택지(개정 4). */
  catalog?: CatalogEntry[];
  /** 변수 key → 이 변수를 참조하는 자리들(형태별). 조망 한 줄 + 삭제 게이트 원료. */
  variableRefs: Map<string, VariableConsumerRef[]>;
  /** 삭제 요청(index) — 참조가 있으면 EditorPage가 파급 확인 모달을 띄운 뒤 삭제한다. */
  onDelete: (index: number) => void;
  onChange: (next: TemplateVariable[]) => void;
}) {
  const vars = variables ?? [];

  // 코어 픽커 주입 entries — legacyHidden 제외는 코어(CatalogEntryPicker)가 하므로 형상 필터만
  // (list data entry — 단수/입력/AI entry는 rows 형상이 아님). legacyHidden 저장값도 코어 보존
  // 그룹이 라벨로 표시 유지한다.
  const pickerListEntries = (catalog ?? []).filter(
    (e) => e.kind === 'data' && e.cardinality === 'list',
  );
  // 옵션 동적 소스 후보(신규 선택용 — 기본값 유도 등) — 픽커 목록에서 legacy 숨김만 제외한 파생.
  const listCatalogEntries = pickerListEntries.filter((e) => !e.legacyHidden);
  // entry의 top-level 스칼라 필드 path 목록 — value/label 필드 선택지(중첩 object/array 제외).
  const catalogFieldPaths = (key: string): { path: string; label: string }[] => {
    const entry = (catalog ?? []).find((e) => e.key === key);
    return (entry?.returns ?? [])
      .filter((r) => r.type === 'string' || r.type === 'number' || r.type === 'enum')
      .map((r) => ({ path: r.path, label: r.label }));
  };

  const patchVar = (i: number, patch: Partial<TemplateVariable>) => {
    onChange(vars.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  };

  const addVariable = () => {
    // 중복되지 않는 기본 key 생성(var1, var2, ...). 점 없는 flat 키(switch on:key 참조용).
    let n = vars.length + 1;
    let key = `var${n}`;
    while (vars.some((v) => v.key === key)) key = `var${++n}`;
    onChange([...vars, { key, label: '새 변수', widget: 'select', options: [] }]);
  };

  // widget 변경 시 select가 아니면 select 전용 키(options/optionsFrom)만 제거, select면 빈 배열 보장.
  // ⚠️ 위젯 무관 설정(prefillFrom·rememberScope·default)은 **반드시 보존** —
  // 종전엔 key/label/default만 남겨 재구성해 위젯을 잠깐 바꾸면 자동 채움·기억 범위가 조용히 소실됐다.
  const changeWidget = (i: number, widget: TemplateVariable['widget']) => {
    onChange(
      vars.map((v, idx) => {
        if (idx !== i) return v;
        if (widget === 'select') return { ...v, widget, options: v.options ?? [] };
        const next: TemplateVariable = { ...v, widget };
        delete next.options;
        delete next.optionsFrom;
        return next;
      }),
    );
  };

  const patchOption = (i: number, oi: number, patch: Partial<InputOption>) => {
    patchVar(i, {
      options: (vars[i].options ?? []).map((o, oidx) => (oidx === oi ? { ...o, ...patch } : o)),
    });
  };
  const addOption = (i: number) => {
    patchVar(i, { options: [...(vars[i].options ?? []), { value: '', label: '' }] });
  };
  const removeOption = (i: number, oi: number) => {
    patchVar(i, { options: (vars[i].options ?? []).filter((_, oidx) => oidx !== oi) });
  };

  return (
    <div className="border-t border-gray-200 pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-700">양식 전역 변수</span>
        <button
          type="button"
          onClick={addVariable}
          className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
        >
          + 변수
        </button>
      </div>
      {vars.length === 0 && (
        <p className="text-xs text-gray-400">
          작성폼에 노출되지만 보고서엔 렌더되지 않는 전역 값(예: 위험성 평가 방식). 노드가 key로 참조합니다.
        </p>
      )}
      <div className="space-y-3">
        {vars.map((v, i) => {
          const keyDup = vars.some((x, idx) => idx !== i && x.key === v.key);
          const keyErr = flatKeyError(v.key);
          const refs = variableRefs.get(v.key);
          const refTotal = variableRefTotal(refs);
          return (
            <div key={i} className="border border-gray-200 rounded p-2 space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 space-y-1">
                  <input
                    type="text"
                    value={v.key}
                    onChange={(e) => patchVar(i, { key: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded font-mono"
                    placeholder="key (점 없이, 예: methodType)"
                  />
                  {keyErr && <p className="text-xs text-red-600">{keyErr}</p>}
                  {keyDup && <p className="text-xs text-red-600">중복된 key입니다.</p>}
                  <input
                    type="text"
                    value={v.label}
                    onChange={(e) => patchVar(i, { label: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    placeholder="작성폼 표시명"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => onDelete(i)}
                  className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                  title="변수 삭제"
                >
                  삭제
                </button>
              </div>

              {/* 참조 조망 — 이 변수를 누가 쓰나. 리네임/삭제의 파급을 **고치기 전에** 보인다
                  (결정 2026-08-10 §4). 참조 0도 회색 문구로 명시한다 — 줄이 통째로 없으면
                  "아직 안 센 것"과 "정말 없는 것"이 구분되지 않는다. */}
              <p
                className={`text-[11px] leading-snug ${refTotal > 0 ? 'text-gray-500' : 'text-gray-400'}`}
                title={refTotal > 0 ? variableRefSummary(refs) : undefined}
              >
                {refTotal > 0
                  ? `참조하는 곳 ${refTotal}곳 — ${variableRefSummary(refs)}`
                  : '참조 없음'}
              </p>

              {/* 위젯 종류 */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 w-16 shrink-0">위젯</span>
                <select
                  value={v.widget}
                  onChange={(e) => changeWidget(i, e.target.value as TemplateVariable['widget'])}
                  className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="select">선택(select)</option>
                  <option value="text">텍스트(text)</option>
                  <option value="number">숫자(number)</option>
                  <option value="checkbox">체크박스(checkbox)</option>
                </select>
              </div>

              {/* last-used 기억 범위 — 현장 저장소에 마지막 값 기억 후 다음 작성 기본값 */}
              <div className="flex items-center gap-1">
                <span
                  className="text-xs text-gray-500 w-16 shrink-0"
                  title="작성 저장값을 현장에 기억해 다음 작성의 기본값으로 우선 사용. 현장 공유=모든 보고서 타입 공유 / 타입별=이 보고서 타입만."
                >
                  기억 범위
                </span>
                <select
                  value={v.rememberScope ?? ''}
                  onChange={(e) =>
                    patchVar(i, {
                      rememberScope:
                        e.target.value === ''
                          ? undefined
                          : (e.target.value as 'site' | 'site-per-report'),
                    })
                  }
                  className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="">기억 안 함</option>
                  <option value="site">현장 공유(모든 타입)</option>
                  <option value="site-per-report">보고서 타입별</option>
                </select>
              </div>

              {/* 옵션 소스 — 정적(직접 입력) / 카탈로그 목록(list rows에서 동적 생성, 개정 4) */}
              {v.widget === 'select' && (
                <div className="flex items-center gap-1">
                  <span
                    className="text-xs text-gray-500 w-16 shrink-0"
                    title="시스템 조회 목록: list 조회 rows에서 옵션을 동적 생성(value/label 필드 지정). 정적 옵션은 rows 미해소 환경(외부 위임 등)의 폴백으로 유지됩니다."
                  >
                    옵션 소스
                  </span>
                  <select
                    value={v.optionsFrom ? 'catalog' : 'static'}
                    onChange={(e) => {
                      if (e.target.value === 'static') {
                        patchVar(i, { optionsFrom: undefined });
                        return;
                      }
                      const first = listCatalogEntries[0];
                      const fields = first ? catalogFieldPaths(first.key) : [];
                      patchVar(i, {
                        optionsFrom: {
                          catalogKey: first?.key ?? '',
                          valueField: fields[0]?.path ?? 'id',
                          labelField: fields[1]?.path ?? fields[0]?.path ?? 'name',
                        },
                      });
                    }}
                    className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                  >
                    <option value="static">정적 옵션</option>
                    <option value="catalog">시스템 조회 목록</option>
                  </select>
                </div>
              )}
              {v.widget === 'select' && v.optionsFrom && (
                <>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500 w-16 shrink-0">시스템 조회</span>
                    {/* 공통 코어 CatalogEntryPicker(P3 이관) — 검색·그룹 헤더·legacyHidden 제외와
                        목록 밖 저장값의 보존 표기("기존 값(보존)")를 코어가 보장한다. */}
                    <CatalogEntryPicker
                      entries={pickerListEntries}
                      value={v.optionsFrom.catalogKey || undefined}
                      onChange={(key) => {
                        // 같은 값 재선택 no-op은 코어 가드(shouldEmitEntryChange)가 보장 —
                        // 여기 도달하면 실제 변경이라 값/라벨 필드를 재유도한다.
                        const fields = catalogFieldPaths(key ?? '');
                        patchVar(i, {
                          optionsFrom: {
                            catalogKey: key ?? '',
                            valueField: fields[0]?.path ?? 'id',
                            labelField: fields[1]?.path ?? fields[0]?.path ?? 'name',
                          },
                        });
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500 w-16 shrink-0">값/라벨</span>
                    <select
                      value={v.optionsFrom.valueField}
                      onChange={(e) =>
                        patchVar(i, {
                          optionsFrom: v.optionsFrom
                            ? { ...v.optionsFrom, valueField: e.target.value }
                            : undefined,
                        })
                      }
                      className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                      title="옵션 값(value) 필드 — 저장되는 값"
                    >
                      {catalogFieldPaths(v.optionsFrom.catalogKey).map((f) => (
                        <option key={f.path} value={f.path}>
                          {f.label} ({f.path})
                        </option>
                      ))}
                    </select>
                    <select
                      value={v.optionsFrom.labelField}
                      onChange={(e) =>
                        patchVar(i, {
                          optionsFrom: v.optionsFrom
                            ? { ...v.optionsFrom, labelField: e.target.value }
                            : undefined,
                        })
                      }
                      className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                      title="옵션 라벨(label) 필드 — 표시되는 이름"
                    >
                      {catalogFieldPaths(v.optionsFrom.catalogKey).map((f) => (
                        <option key={f.path} value={f.path}>
                          {f.label} ({f.path})
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* select 위젯 옵션(값/라벨) — optionsFrom 지정 시 rows 미해소 환경의 폴백 */}
              {v.widget === 'select' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">
                      {v.optionsFrom ? '옵션(폴백)' : '옵션'}
                    </span>
                    <button
                      type="button"
                      onClick={() => addOption(i)}
                      className="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                    >
                      + 옵션
                    </button>
                  </div>
                  <div className="space-y-1">
                    {(v.options ?? []).map((o, oi) => (
                      <div key={oi} className="flex items-center gap-1">
                        <input
                          type="text"
                          value={o.value}
                          onChange={(e) => patchOption(i, oi, { value: e.target.value })}
                          className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded font-mono"
                          placeholder="값"
                        />
                        <input
                          type="text"
                          value={o.label}
                          onChange={(e) => patchOption(i, oi, { label: e.target.value })}
                          className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded"
                          placeholder="라벨"
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(i, oi)}
                          className="px-1.5 py-1 text-xs text-gray-400 hover:text-red-500"
                          aria-label="옵션 삭제"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {(v.options ?? []).length === 0 && (
                      <p className="text-[11px] text-gray-400">옵션을 추가하세요.</p>
                    )}
                  </div>
                </div>
              )}

              {/* 기본값(작성폼 pre-fill) */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 w-16 shrink-0">기본값</span>
                {v.widget === 'select' ? (
                  <select
                    value={(v.default as string) ?? ''}
                    onChange={(e) =>
                      patchVar(i, { default: e.target.value === '' ? undefined : e.target.value })
                    }
                    className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                  >
                    <option value="">없음</option>
                    {(v.options ?? []).map((o, oi) => (
                      <option key={oi} value={o.value}>
                        {o.label || o.value}
                      </option>
                    ))}
                  </select>
                ) : v.widget === 'checkbox' ? (
                  <input
                    type="checkbox"
                    checked={Boolean(v.default)}
                    onChange={(e) => patchVar(i, { default: e.target.checked || undefined })}
                  />
                ) : (
                  <input
                    type={v.widget === 'number' ? 'number' : 'text'}
                    value={v.default === undefined || v.default === null ? '' : String(v.default)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') return patchVar(i, { default: undefined });
                      patchVar(i, { default: v.widget === 'number' ? Number(raw) : raw });
                    }}
                    className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded"
                    placeholder="미지정"
                  />
                )}
              </div>

              {/* 초깃값 자동 채움(prefillFrom) — 서버 getComposerPrefill이 카탈로그 값을 resolve해
                  content[key]를 seed한다(정적 기본값보다 우선, 기억 범위가 있으면 그보다는 후순위).
                  종전엔 에디터에 없어 마이그레이션/DB로만 넣던 은닉 설정이었다(조회조차 불가). */}
              <div>
                <span
                  className="block mb-0.5 text-xs text-gray-500"
                  title="작성 시작 시 시스템 조회로 값을 읽어 이 변수의 초깃값으로 채웁니다. 위 정적 기본값보다 우선하며, 기억 범위(last-used)가 있으면 그 값이 더 우선입니다."
                >
                  초깃값 자동 채움
                </span>
                <CatalogPrefillPicker
                  catalog={catalog ?? []}
                  value={v.prefillFrom}
                  onChange={(pf) => {
                    // 해제(✕)는 필드 자체를 제거 — undefined 키를 남기면 저장 형상이 지저분해진다.
                    const next: TemplateVariable = { ...v };
                    if (pf) next.prefillFrom = pf;
                    else delete next.prefillFrom;
                    onChange(vars.map((x, idx) => (idx === i ? next : x)));
                  }}
                />
                {v.prefillFrom && prefillOutOfPickerScope(v.prefillFrom, catalog) && (
                  <p className="mt-0.5 text-[11px] text-amber-600 break-all">
                    저장된 값{' '}
                    <span className="font-mono">
                      {v.prefillFrom.catalogKey}
                      {v.prefillFrom.select ? ` · ${v.prefillFrom.select}` : ''}
                    </span>
                    은 위 선택 목록에 없는 데이터 소스라 편집할 수 없습니다(설정은 그대로 유지).
                  </p>
                )}
                {v.prefillFrom &&
                  !v.prefillFrom.select &&
                  !prefillOutOfPickerScope(v.prefillFrom, catalog) && (
                    <p className="mt-0.5 text-[11px] text-amber-600">
                      반환 필드가 없어 entry 전체가 채워집니다 — 스칼라 변수라면 필드를 고르세요.
                    </p>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 카탈로그 파라미터 지정 편집기 — 양식 탭 전역 설정(변수 편집기 바로 아래).
 *
 * 카탈로그 entry가 코드로 선언한 `params` 중 `provenance:'user'`인 것을, 저작자가 템플릿 수준
 * `catalogParams[catalogKey][paramName]`에 **직접 입력**(literal) 또는 **전역 변수 참조**(variable)로
 * 채운다(결정 2026-08-06-catalog-param-binding-axis 개정 1 — 지정 자리는 템플릿 수준).
 *
 * 표시 규율 셋:
 *  - **자동으로 채워지는 것도 보여준다** — `'context'`/`'const'` 파라미터는 편집 불가로 그리되 숨기지
 *    않는다. 무엇이 저절로 들어가는지 모르면 저작자가 지정의 의미를 판단할 수 없다.
 *  - **후보를 좁히되 막지 않는다** — `accepts` 대조는 힌트라 안 맞는 변수도 optgroup '그 외'로 남긴다
 *    (결정 §2. 후보에서 빼면 그게 은닉이고, 값 형상이 실제로 둘 섞여 있다).
 *  - **상속/자동/저작한 값이 구분돼 읽히고, 되돌릴 수단이 있다**
 *    (feedback-authoring-ui-resolved-value-hides-inherit-vs-auto).
 *
 * 파라미터 한 개를 그리는 행은 `CatalogParamEditing.tsx`의 `CatalogParamBindingRow`다 — 바인딩
 * 에디터의 인라인 블록(`CatalogParamInlineBlock`)이 **같은 컴포넌트**를 쓴다.
 *
 * 역할 구도: **일상 편집은 인라인**, 이 섹션은 둘을 맡는다 —
 *  1. 이 양식의 지정 **전체 조망**(값 소스와 경계표가 같은 변수를 가리키는지 한 화면 대조).
 *  2. **바인딩 에디터가 없는 참조의 유일한 편집처** — 변수 `prefillFrom`·셀 `rangesFrom`처럼
 *     단수 entry를 직접 참조하는 카탈로그는 인라인 진입점이 아예 없다(결정 개정 1의 그 자리).
 *
 * 그래서 기본 상태는 편집 폼 나열이 아니라 **접힌 조망 표**다(결정 개정 2 후속, 2026-08-09
 * 사용자 확정) — 카탈로그별 행에 [라벨 | 파라미터별 지정 요약 | 이 카탈로그를 참조하는 소비처
 * (`collectCatalogConsumers`) | 필수 미지정 배지 | 자동(context/const) 표시]를 압축해 짝 맞춤
 * 대조가 한눈에 되게 한다. 행을 펼치면 기존 `CatalogParamBindingRow` 편집이 그대로 나온다
 * (편집 제거는 실사용 후 재결정). 참조가 하나도 없는데 지정만 남은 카탈로그는 "참조 없음"으로
 * 드러낸다(유령 지정 발견 축 — unknownEntry와 별개: entry는 살아 있어도 참조는 없을 수 있다).
 */
function CatalogParamsEditor({
  template,
  catalog,
  resolution,
  hasPreviewContext,
  onChange,
}: {
  template: ReportTemplate;
  /** 카탈로그 entry 목록 — 파라미터 선언(params)과 표시명의 출처. */
  catalog?: CatalogEntry[];
  /** 미리보기 해소 스탬프 — 펼친 행에서 "지금 실제로 낙점된 값"을 보여주는 원료. */
  resolution?: CatalogParamResolutionMap;
  /** 실데이터 미리보기 반영 여부 — false면 값을 지어내지 않고 안내로 degrade. */
  hasPreviewContext?: boolean;
  onChange: (next: TemplateCatalogParamBindings | undefined) => void;
}) {
  const rows = useMemo(() => buildCatalogParamRows(template, catalog), [template, catalog]);
  const consumers = useMemo(() => collectCatalogConsumers(template), [template]);
  const variables = template.variables ?? [];
  const missing = missingRequiredCatalogParams(rows);
  // 펼친 카탈로그 key 집합 — 기본은 전부 접힘(조망이 기본, 편집은 선택 진입).
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="border-t border-gray-200 pt-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700">조회 파라미터 지정</span>
        {missing.length > 0 && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700 ring-1 ring-amber-200">
            필수 미지정 {missing.length}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-400">
          이 양식이 참조하는 데이터 소스 중 저작자가 채울 파라미터를 선언한 것이 없습니다.
        </p>
      ) : (
        <>
          {/* 두 편집처가 같은 값이라는 사실을 여기서 먼저 말한다 — 인라인 캡션과 짝을 이루는
              상호 참조(한쪽만 알면 "어디가 진짜인가"로 읽힌다). */}
          <p className="mb-1 text-[11px] leading-snug text-gray-500">
            각 파라미터는 해당 시스템 조회를 바인딩한 자리에서도 편집할 수 있습니다 — 같은 값입니다.
          </p>
          <p className="mb-2 text-[11px] leading-snug text-gray-400">
            행을 펼치면 값을 직접 적거나 위 전역 변수에서 고릅니다. <b>미지정일 때의 동작은 조회마다
            다릅니다</b> — 각 파라미터 아래에 그 조회가 선언한 문장으로 표시되며, 폴백이 없는
            파라미터는 <b>필수</b>로 표시됩니다. 참조 줄의 숫자는 <b>참조 자리 수</b>이며,
            실제 조회는 자리 수와 무관하게 조회당 1회입니다.
          </p>
          <div className="space-y-2">
            {rows.map((row) => {
              const open = expandedKeys.has(row.catalogKey);
              const refs = consumers.get(row.catalogKey) ?? [];
              const authorable = row.params.filter((item) => item.authorable);
              const automatic = row.params.filter((item) => !item.authorable);
              const rowMissing = row.params.filter((item) => item.missingRequired).length;
              return (
                <div key={row.catalogKey} className="rounded border border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(row.catalogKey)}
                    className="flex w-full items-baseline justify-between gap-2 px-2 py-1.5 text-left hover:bg-gray-50"
                    title={open ? '접어서 조망으로 돌아갑니다.' : '펼쳐서 지정을 편집합니다.'}
                  >
                    <span className="min-w-0 truncate text-xs font-medium text-gray-700">
                      <span className="mr-1 text-[10px] text-gray-400">{open ? '▾' : '▸'}</span>
                      {row.label ?? row.catalogKey}
                    </span>
                    <span className="flex shrink-0 items-baseline gap-1.5">
                      {rowMissing > 0 && (
                        <span className="rounded bg-amber-50 px-1 py-0.5 text-[10px] text-amber-700 ring-1 ring-amber-200">
                          필수 미지정 {rowMissing}
                        </span>
                      )}
                      <span className="font-mono text-[10px] text-gray-400">{row.catalogKey}</span>
                    </span>
                  </button>
                  <div className="space-y-1.5 px-2 pb-2">
                    {row.unknownEntry && (
                      <p className="text-[11px] leading-snug text-amber-600">
                        시스템 조회 목록에 없는 소스입니다 — 저장된 지정만 보여줍니다(해제는 가능).
                      </p>
                    )}
                    {/* 소비처 — 짝 맞춤 대조의 핵심이라 접힘/펼침 양쪽에 상시 표시. */}
                    <p className="text-[11px] leading-snug text-gray-500">
                      <span className="text-gray-400">참조: </span>
                      {refs.length === 0
                        ? '없음 — 이 양식 어디서도 참조하지 않는데 지정만 남아 있습니다(해제를 권합니다).'
                        : refs
                            .map((r) => (r.count > 1 ? `${r.label} ${r.count}곳` : r.label))
                            .join(' · ')}
                    </p>
                    {!open && (
                      <>
                        {authorable.length > 0 && (
                          <p className="text-[11px] leading-snug text-gray-500">
                            <span className="text-gray-400">지정: </span>
                            {authorable
                              .map(
                                (item) =>
                                  `${item.param.label || item.param.name} ← ${catalogParamBindingSummary(item.param, item.binding, variables)}`,
                              )
                              .join(' · ')}
                          </p>
                        )}
                        {automatic.length > 0 && (
                          <p className="text-[11px] leading-snug text-gray-400">
                            자동:{' '}
                            {automatic
                              .map(
                                (item) =>
                                  `${item.param.name} ← ${catalogParamBindingSummary(item.param, undefined, variables)}`,
                              )
                              .join(' · ')}
                          </p>
                        )}
                      </>
                    )}
                    {open && (
                      <CatalogParamEffectiveNote
                        catalogKey={row.catalogKey}
                        resolution={resolution}
                        hasPreviewContext={hasPreviewContext}
                      />
                    )}
                    {open &&
                      row.params.map((item) => (
                        <CatalogParamBindingRow
                          key={item.param.name}
                          item={item}
                          variables={variables}
                          onSetBinding={(binding) =>
                            onChange(
                              setCatalogParamBinding(
                                template.catalogParams,
                                row.catalogKey,
                                item.param.name,
                                binding,
                              ),
                            )
                          }
                        />
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 통합 "데이터" 섹션의 유형 — 카드 배지 + "＋ 데이터 추가" 메뉴가 공유하는 축(2026-07-26 결정).
 *
 * 저장은 종전대로 두 배열(`dataSources` / `dataDefs`)로 남고 **표면만 한 목록**이다. 유형은 저장
 * 배열이 아니라 *사용자가 인지하는 성격*으로 가른다 — 그래서 `dataSources`의 수동(kind:'manual')
 * 선언과 `dataDefs`의 정적 데이터셋이 같은 `direct` 유형으로 묶인다(둘 다 "직접 입력").
 */
type DataKind = 'assembled' | 'synced' | 'direct';

const DATA_KIND_LABEL: Record<DataKind, string> = {
  assembled: '시스템 조회 조립',
  synced: '동기화 데이터',
  direct: '직접 입력',
};

/** 배지 색 — 위젯 트리 kind 배지(ring-1 + text-[10px])와 같은 결. */
const DATA_KIND_BADGE_CLASS: Record<DataKind, string> = {
  assembled: 'text-brand-600 ring-brand-200',
  synced: 'text-teal-700 ring-teal-200',
  direct: 'text-gray-600 ring-gray-300',
};

/** 조립 데이터 소스 한 건의 유형 — 조립(카탈로그 조합) / 수동 선언은 직접 입력. */
function dataSourceKind(def: DataSourceDef): DataKind {
  return def.kind === 'manual' ? 'direct' : 'assembled';
}

/**
 * 데이터셋 한 건의 유형 — compute(조립기) 보유면 동기화 데이터, 아니면 직접 입력.
 *
 * "병합"이 아니라 "동기화"인 이유: join-by-key는 조립기 **한 방식**일 뿐이고 zip/slice 등도
 * 같은 자리에 온다. 이 유형의 본질은 *무엇을 하느냐*(병합)가 아니라 **렌더마다 원료를 따라
 * 다시 만들어진다**는 동작이다. (‘실시간’류 명칭은 바인딩 lifecycle의 '실시간(live)'과 부딪혀 기각.)
 */
function dataDefKind(def: DataDef): DataKind {
  return def.compute ? 'synced' : 'direct';
}

/** 카드 헤더 유형 배지. */
function DataKindBadge({ kind }: { kind: DataKind }) {
  return (
    <span
      className={`inline-block shrink-0 whitespace-nowrap rounded bg-white px-1.5 py-0.5 text-[10px] ring-1 ${DATA_KIND_BADGE_CLASS[kind]}`}
    >
      {DATA_KIND_LABEL[kind]}
    </span>
  );
}

/**
 * 카드 흐름 요약 한 줄 — `원료 → 이 목록 → 사용처 N곳`.
 *
 * 카드를 접은 채로도 "이 데이터가 어디서 와서 어디에 쓰이는지"가 한 줄로 읽히게 한다.
 * 사용처 0은 amber 칩("미사용")으로 — 종전 조립 소스 카드의 경고를 이 줄로 흡수했다(중복 표시 방지).
 */
function DataFlowSummary({ origin, label, usage }: { origin: string; label: string; usage: number }) {
  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-[11px] text-gray-400">
      <span className="min-w-0 truncate" title={origin}>
        {origin}
      </span>
      <span aria-hidden>→</span>
      <span className="min-w-0 truncate text-gray-500" title={label}>
        {label}
      </span>
      <span aria-hidden>→</span>
      {usage > 0 ? (
        <span className="whitespace-nowrap">사용처 {usage}곳</span>
      ) : (
        <span
          className="whitespace-nowrap rounded border border-amber-200 bg-amber-50 px-1 text-amber-600"
          title="아직 반복영역·동적 데이터셋·바인딩 어디서도 참조하지 않습니다."
        >
          미사용
        </span>
      )}
    </div>
  );
}

/** 조립 데이터 소스의 "원료" 문구 — 주 소스(+보조 소스 수) 카탈로그 라벨. */
function dataSourceOrigin(def: DataSourceDef, catalog: CatalogEntry[]): string {
  if (def.kind === 'manual') return `직접 입력 · 칼럼 ${def.fields.length}개`;
  const primary = catalog.find((e) => e.key === def.primary);
  const base = def.primary ? (primary?.label ?? def.primary) : '(주 소스 미지정)';
  const lookups = def.lookups?.length ?? 0;
  return lookups > 0 ? `${base} 외 ${lookups}` : base;
}

/** 데이터셋의 "원료" 문구 — 동기화면 입력 소스 라벨들, 직접 입력이면 기본행 수. */
function dataDefOrigin(def: DataDef, sources: RepeatSource[]): string {
  if (!def.compute) return `직접 입력 행 ${def.defaultRows.length}개`;
  const inputs: string[] = Object.values(def.compute.inputs ?? {});
  // 미해소 키는 라벨을 못 찾으므로 키 원문을 그대로 보인다(조용히 비우면 원인 추적이 끊긴다).
  const named = inputs.filter((k) => k.trim() !== '');
  if (named.length === 0) return '(입력 소스 미지정)';
  return named.map((k) => sources.find((s) => s.key === k)?.label ?? k).join(' + ');
}

/**
 * "＋ 데이터 추가" 단일 진입 — 세 유형 중 하나를 골라 그 유형의 빈 카드를 만든다.
 *
 * 종전에는 두 섹션에 개별 추가 버튼이 흩어져 있어(조립 소스 / 빈 데이터셋 / 조인 데이터셋 / 데이터셋)
 * "무엇을 만들어야 하는지"가 저장 구조 지식을 요구했다. 진입을 하나로 모으고 선택지를 *유형*으로
 * 표현해, 만들 때 고르는 축과 카드 배지가 같아지게 한다.
 */
function AddDataMenu({
  onAdd,
  onStartFromExcel,
}: {
  onAdd: (kind: DataKind) => void;
  /** 엑셀 헤더로 칼럼까지 만들어 직접 입력 데이터셋을 시작 — 빈 카드로 시작하는 '직접 입력'의 변형. */
  onStartFromExcel: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // 바깥 클릭·Esc로 닫기 — 좁은 속성 패널이라 메뉴가 열린 채 남으면 카드 목록을 가린다.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // badge는 만들어질 데이터의 유형, select는 실제 동작 — "엑셀에서 시작"은 결과물이 직접 입력
  // 데이터셋이라 같은 배지를 달되 빈 카드 대신 엑셀 헤더로 칼럼·행을 함께 만든다.
  const items: { id: string; badge: DataKind; title: string; desc: string; select: () => void }[] = [
    {
      id: 'assembled',
      badge: 'assembled',
      title: '서버 데이터에서 조립',
      desc: '시스템 조회(주 소스+보조 소스)를 조합해 행을 자동으로 채웁니다.',
      select: () => onAdd('assembled'),
    },
    {
      id: 'synced',
      badge: 'synced',
      title: '동기화 데이터',
      desc: '다른 데이터 목록을 원료로, 원료가 바뀌면 함께 갱신되는 목록 — 병합·구간 등.',
      select: () => onAdd('synced'),
    },
    {
      id: 'direct',
      badge: 'direct',
      title: '직접 입력',
      desc: '칼럼과 기본 행을 양식에 적어 둡니다 — 담당자가 작성 화면에서 채웁니다.',
      select: () => onAdd('direct'),
    },
    {
      id: 'direct-excel',
      badge: 'direct',
      title: '엑셀에서 시작',
      desc: '엑셀 파일의 헤더 줄을 칼럼으로, 나머지 줄을 기본 행으로 한 번에 만듭니다.',
      select: onStartFromExcel,
    },
  ];

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 whitespace-nowrap"
      >
        ＋ 데이터 추가
      </button>
      {open && (
        <div
          role="menu"
          // 속성 패널 폭이 좁아 우측 정렬 + 고정 폭. z-20이면 카드 위로 뜨기 충분(모달 아님).
          className="absolute right-0 z-20 mt-1 w-64 rounded border border-gray-200 bg-white p-1 shadow-lg"
        >
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                it.select();
              }}
              className="block w-full rounded px-2 py-1.5 text-left hover:bg-gray-50"
            >
              <span className="flex items-center gap-1.5">
                <DataKindBadge kind={it.badge} />
                <span className="text-xs font-medium text-gray-700">{it.title}</span>
              </span>
              <span className="mt-0.5 block text-[11px] leading-snug text-gray-400">{it.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 통합 "데이터" 섹션 — 조립 데이터 소스(dataSources)와 데이터셋(dataDefs)을 한 목록으로 보인다.
 *
 * 2026-07-26 결정(표면 통합): 종전 두 섹션("조립 데이터 소스" / "내장 데이터셋")은 *저장 배열*로
 * 나뉜 구획이라 사용자에게는 "조립"이 두 군데로 쪼개져 보였다. 저장 구조(두 배열)와 엔진은 그대로
 * 두고 **표면만** 한 섹션으로 합치고, 카드마다 유형 배지를 달아 갈래를 배지로 읽게 한다.
 *
 * 카드 내부 UI는 유형별 기존 편집기(DataSourcesEditor / DataDefsEditor)를 그대로 재사용한다 —
 * 두 편집기는 이제 섹션 머리·추가 버튼 없이 **지정된 index의 카드만** 그리는 목록 렌더러다.
 * 표시 순서는 데이터 흐름 순(카탈로그 조립 → 동기화 데이터 → 직접 입력)이고, "직접 입력"은 두 저장
 * 배열에 걸쳐 있어 두 편집기를 각각 두 번 렌더한다. 그래서 펼침 상태(expanded)를 **여기로 올려**
 * 같은 배열을 그리는 두 인스턴스가 한 Set을 공유하게 한다(삭제 시 index 시프트 보정이 어긋나지
 * 않도록 — 인스턴스마다 따로 두면 한쪽 삭제가 다른 쪽 펼침을 엉뚱한 카드로 옮긴다).
 */
/** export: 「소스 편집 →」 이동 처리(펼침+스크롤) 렌더 계약 spec — DataSourceEditJump.rtl.test.tsx */
export function DataSection({
  dataDefs,
  dataSources,
  catalog,
  dataDefUsage,
  dataSourceUsage,
  dataDefReservedKeys,
  dataSourceReservedKeys,
  tablePlacements,
  onChangeDataDefs,
  onChangeDataSources,
  onRenameDataSourceField,
  onDeleteDataSourceField,
  onDeleteDataDef,
  onDeleteDataSource,
  hasPreviewContext,
  onDryRun,
  onFetchCatalogRows,
  revealCardKey,
  onRevealHandled,
}: {
  dataDefs?: DataDef[];
  dataSources?: DataSourceDef[];
  catalog: CatalogEntry[];
  dataDefUsage: Map<string, number>;
  dataSourceUsage: Map<string, number>;
  /** dataDefs key 충돌 검사 대상(= dataSources key) */
  dataDefReservedKeys: Set<string>;
  /** dataSources key 충돌 검사 대상(= dataDefs key) */
  dataSourceReservedKeys: Set<string>;
  /** key를 쓰는 배치 table 위젯 수(countTablePlacements) — 정본 편집 파급 안내("배치 N곳 반영"). */
  tablePlacements: (key: string) => number;
  onChangeDataDefs: (next: DataDef[]) => void;
  onChangeDataSources: (next: DataSourceDef[]) => void;
  onRenameDataSourceField: (params: { dsKey: string; oldField: string; newField: string }) => void;
  /** 필드 삭제 — 선언 제거 + 그 소스를 반복하는 표의 셀 바인딩 해제(rename과 같은 파급 범위) */
  onDeleteDataSourceField: (params: { dsKey: string; field: string }) => void;
  onDeleteDataDef: (key: string) => void;
  onDeleteDataSource: (key: string) => void;
  hasPreviewContext: boolean;
  onDryRun: () => Promise<Record<string, unknown>>;
  /** 카탈로그 행 박제 조회(직접 입력 데이터셋 "카탈로그에서 행 가져오기") — 서버 prefill 채널. */
  onFetchCatalogRows: (catalogKey: string, select?: string) => Promise<Record<string, unknown>[]>;
  /**
   * 「소스 편집 →」이 지목한 선언 key — 그 카드를 펼치고 화면에 보이게 한다(null=요청 없음).
   * 값이 바뀌는 것이 곧 요청이라, 처리 후 onRevealHandled로 비운다(비우지 않으면 이 섹션이
   * 재마운트될 때마다 옛 요청이 되살아나 저작 중에 화면이 제멋대로 튄다 — 이 섹션은 선택이
   * 비었을 때만 그려져 마운트/언마운트가 잦다).
   */
  revealCardKey?: string | null;
  onRevealHandled?: () => void;
}) {
  const defs = dataDefs ?? [];
  const sources = dataSources ?? [];

  // 펼침 상태 — 배열별 index Set. 같은 배열을 두 번 렌더하므로 여기서 한 벌만 들고 내려보낸다.
  const [defsExpanded, setDefsExpanded] = useState<Set<number>>(new Set());
  const [sourcesExpanded, setSourcesExpanded] = useState<Set<number>>(new Set());
  /**
   * 엑셀 가져오기 모달 — 두 진입점(카드의 "엑셀에서 행 추가" / 추가 메뉴의 "엑셀에서 시작")이
   * 같은 모달의 두 모드다. 상태를 이 섹션이 쥐는 이유는 반영 대상이 결국 dataDefs 배열이라
   * 두 경로 모두 여기서 onChangeDataDefs로 수렴하기 때문이다.
   */
  const [excelImport, setExcelImport] = useState<
    { mode: 'map'; defIndex: number } | { mode: 'compose' } | null
  >(null);
  /**
   * 카탈로그 행 박제 모달 — 대상 데이터셋 index. 엑셀 map 모드와 같은 자리(직접 입력 카드
   * 기본행 구획)에서 열리고, 반영도 같은 곳(defaultRows)으로 수렴한다.
   */
  const [catalogImport, setCatalogImport] = useState<{ defIndex: number } | null>(null);

  // 유형별 원본 index 묶음 — 카드 내부 편집기는 원본 index로 patch하므로 index를 보존해 넘긴다.
  const assembledIdx = sources.flatMap((d, i) => (dataSourceKind(d) === 'assembled' ? [i] : []));
  const manualIdx = sources.flatMap((d, i) => (dataSourceKind(d) === 'direct' ? [i] : []));
  const syncedIdx = defs.flatMap((d, i) => (dataDefKind(d) === 'synced' ? [i] : []));
  const staticIdx = defs.flatMap((d, i) => (dataDefKind(d) === 'direct' ? [i] : []));

  const addByKind = (kind: DataKind) => {
    if (kind === 'synced') {
      // 동기화 데이터의 기본 조립기는 join-by-key(유일한 저작 UI 보유 방식) — zip/slice 등 다른
      // 방식은 편집 UI가 없어 읽기 전용 요약으로만 보인다.
      const newIdx = defs.length;
      onChangeDataDefs([
        ...defs,
        newJoinByKeyDef(nextDataDefKey(defs, 'join', dataDefReservedKeys), '새 동기화 데이터'),
      ]);
      setDefsExpanded((prev) => new Set(prev).add(newIdx));
      return;
    }
    if (kind === 'direct') {
      const newIdx = defs.length;
      onChangeDataDefs([
        ...defs,
        {
          key: nextDataDefKey(defs, 'data', dataDefReservedKeys),
          label: '새 데이터',
          fields: [],
          defaultRows: [],
        },
      ]);
      setDefsExpanded((prev) => new Set(prev).add(newIdx));
      return;
    }
    // assembled — 카탈로그 조합 소스. key는 dataDefs key와도 겹치지 않아야 한다.
    let n = sources.length + 1;
    let key = `source-${n}`;
    while (sources.some((d) => d.key === key) || dataSourceReservedKeys.has(key))
      key = `source-${++n}`;
    const newIdx = sources.length;
    onChangeDataSources([
      ...sources,
      { key, label: '새 조립 소스', cardinality: 'list', kind: 'assembled', primary: '', fields: [] },
    ]);
    setSourcesExpanded((prev) => new Set(prev).add(newIdx));
  };

  // 동기화 데이터 카드의 "원료" 라벨 산출용 — 입력 소스 키를 사람이 읽는 라벨로 바꾼다.
  const repeatSources = useMemo(
    () => collectRepeatSources(defs, catalog, sources),
    [defs, catalog, sources],
  );

  const sharedSourceProps = {
    dataSources,
    catalog,
    usage: dataSourceUsage,
    reservedKeys: dataSourceReservedKeys,
    expanded: sourcesExpanded,
    setExpanded: setSourcesExpanded,
    onChange: onChangeDataSources,
    onRenameField: onRenameDataSourceField,
    onDeleteField: onDeleteDataSourceField,
    onDelete: onDeleteDataSource,
    hasPreviewContext,
    onDryRun,
  };
  const sharedDefProps = {
    dataDefs,
    usage: dataDefUsage,
    reservedKeys: dataDefReservedKeys,
    expanded: defsExpanded,
    setExpanded: setDefsExpanded,
    repeatSources,
    tablePlacements,
    onChange: onChangeDataDefs,
    onDelete: onDeleteDataDef,
    onImportExcel: (i: number) => setExcelImport({ mode: 'map', defIndex: i }),
    onImportCatalog: (i: number) => setCatalogImport({ defIndex: i }),
  };

  // 엑셀 반영 — 매핑 모드는 대상 데이터셋의 기본행을 갱신하고, 구성 모드는 데이터셋을 새로 만든다.
  const applyExcelImport = (result: DatasetExcelImportResult) => {
    if (result.mode === 'map') {
      if (!excelImport || excelImport.mode !== 'map') return;
      const i = excelImport.defIndex;
      onChangeDataDefs(
        defs.map((d, idx) => {
          if (idx !== i) return d;
          const defaultRows = result.replace ? result.rows : [...d.defaultRows, ...result.rows];
          return { ...d, defaultRows };
        }),
      );
    } else {
      const newIdx = defs.length;
      onChangeDataDefs([
        ...defs,
        {
          key: nextDataDefKey(defs, 'data', dataDefReservedKeys),
          label: '새 데이터',
          fields: result.fields,
          defaultRows: result.rows,
        },
      ]);
      // 방금 만든 카드를 펼쳐 둔다 — 칼럼 이름·key를 바로 다듬을 수 있게(＋데이터 추가와 동일).
      setDefsExpanded((prev) => new Set(prev).add(newIdx));
    }
    setExcelImport(null);
  };

  const excelTargetDef = excelImport?.mode === 'map' ? defs[excelImport.defIndex] : undefined;

  // 카탈로그 행 박제 반영 — 엑셀 map 모드와 같은 수렴점(defaultRows 추가/교체). 박제이므로
  // 카탈로그 참조는 저장하지 않는다(가져온 행만 남는다 — 동적 축은 prefillFrom이 따로 있다).
  const applyCatalogImport = (result: DatasetCatalogImportResult) => {
    if (!catalogImport) return;
    const i = catalogImport.defIndex;
    onChangeDataDefs(
      defs.map((d, idx) => {
        if (idx !== i) return d;
        const defaultRows = result.replace ? result.rows : [...d.defaultRows, ...result.rows];
        return { ...d, defaultRows };
      }),
    );
    setCatalogImport(null);
  };

  /**
   * 「소스 편집 →」 이동 처리 — 지목된 key의 카드를 펼치고 그 자리로 스크롤한다.
   *
   * 펼침 상태가 **배열 index Set**이라 key를 두 배열에서 각각 찾아 해당 축에만 넣는다("직접 입력"이
   * dataSources/dataDefs 두 갈래에 걸쳐 있는 것과 같은 이유 — 2026-07-26 표면 통합).
   * 스크롤을 rAF로 미루는 것은 펼침 반영이 한 렌더 뒤이기 때문이고(접힌 카드로 스크롤하면 위치가
   * 어긋난다), 조회를 이 섹션 루트로 한정하는 것은 같은 컴포넌트가 두 탭에서 렌더되기 때문이다.
   */
  const sectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!revealCardKey) return;
    const sourceIdx = sources.findIndex((d) => d.key === revealCardKey);
    if (sourceIdx >= 0) setSourcesExpanded((prev) => new Set(prev).add(sourceIdx));
    const defIdx = defs.findIndex((d) => d.key === revealCardKey);
    if (defIdx >= 0) setDefsExpanded((prev) => new Set(prev).add(defIdx));
    const raf = requestAnimationFrame(() => {
      const cards = sectionRef.current?.querySelectorAll(`[${DATA_CARD_ATTR}]`) ?? [];
      // selector 이스케이프에 기대지 않는다 — 선언 key는 저작자가 자유롭게 적는 값이다.
      const target = Array.from(cards).find(
        (el) => el.getAttribute(DATA_CARD_ATTR) === revealCardKey,
      );
      target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      // 요청 소진은 스크롤을 **마친 뒤**다 — 여기서 앞당기면 그 상태 변경이 이 effect를 정리해
      // 아래 cancelAnimationFrame이 자기 프레임을 취소한다(이동이 조용히 사라진다).
      onRevealHandled?.();
    });
    return () => cancelAnimationFrame(raf);
    // defs/sources는 이동 시점의 값만 읽는다 — 의존에 넣으면 데이터 편집마다 재스크롤된다.
  }, [revealCardKey]);

  const catalogTargetDef = catalogImport ? defs[catalogImport.defIndex] : undefined;
  // 원천 후보는 초기 행(prefillFrom) 지정과 같은 정본(datasetPrefillEntries) — 두 축이 고를 수
  // 있는 카탈로그 범위가 갈리면 "여기선 되는데 저기선 안 되는" 혼란이 된다.
  const catalogImportEntries = useMemo(() => datasetPrefillEntries(catalog), [catalog]);

  return (
    <div ref={sectionRef} className="border-t border-gray-200 pt-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="flex items-center gap-1 text-xs font-semibold text-gray-700 whitespace-nowrap">
          <span title="반복영역·표·바인딩이 소비할 명명 데이터 — 시스템 조회 조립 / 동기화 데이터 / 직접 입력">
            데이터
          </span>
          <HelpLink topic="dataKinds" />
        </span>
        <AddDataMenu onAdd={addByKind} onStartFromExcel={() => setExcelImport({ mode: 'compose' })} />
      </div>
      {defs.length === 0 && sources.length === 0 && (
        <p className="text-xs text-gray-400">
          표·반복이 소비할 명명 데이터를 만듭니다 — 서버 데이터를 조합(시스템 조회 조립)하거나, 다른
          목록을 원료로 삼거나(동기화 데이터), 칼럼·기본 행을 양식에 적어 둡니다(직접 입력).
        </p>
      )}
      {/* 데이터 흐름 순: 원료(카탈로그 조립) → 가공(동기화 데이터) → 양식이 들고 있는 값(직접 입력).
          "직접 입력"은 두 저장 배열에 걸쳐 있어 목록 끝에 두 편집기를 잇대어 붙인다. */}
      <div className="space-y-3">
        {assembledIdx.length > 0 && (
          <DataSourcesEditor {...sharedSourceProps} renderIndices={assembledIdx} />
        )}
        {syncedIdx.length > 0 && <DataDefsEditor {...sharedDefProps} renderIndices={syncedIdx} />}
        {manualIdx.length > 0 && (
          <DataSourcesEditor {...sharedSourceProps} renderIndices={manualIdx} />
        )}
        {staticIdx.length > 0 && <DataDefsEditor {...sharedDefProps} renderIndices={staticIdx} />}
      </div>
      {/* 조건부 마운트 — 모달 상태(단계·시트·매핑)의 초기화를 마운트에 맡긴다. 상시 마운트면
          재열기 첫 프레임에 직전 세션이 비치고, 닫은 뒤에도 파싱 격자가 계속 메모리에 남는다. */}
      {excelImport &&
        (excelImport.mode === 'map' ? (
          excelTargetDef ? (
            <DatasetExcelImportModal
              open
              mode="map"
              datasetLabel={excelTargetDef.label || excelTargetDef.key}
              fields={excelTargetDef.fields}
              existingRowCount={excelTargetDef.defaultRows.length}
              onClose={() => setExcelImport(null)}
              onApply={applyExcelImport}
            />
          ) : null
        ) : (
          <DatasetExcelImportModal
            open
            mode="compose"
            nextFieldKey={(existingKeys) => nextAutoFieldKey(existingKeys)}
            onClose={() => setExcelImport(null)}
            onApply={applyExcelImport}
          />
        ))}
      {/* 카탈로그 행 박제 모달 — 엑셀과 같은 조건부 마운트 규약(마운트가 곧 초기 상태). */}
      {catalogImport && catalogTargetDef ? (
        <DatasetCatalogImportModal
          open
          datasetLabel={catalogTargetDef.label || catalogTargetDef.key}
          fields={catalogTargetDef.fields}
          existingRowCount={catalogTargetDef.defaultRows.length}
          entries={catalogImportEntries}
          returnPaths={datasetRowsReturnPaths}
          hasPreviewContext={hasPreviewContext}
          fetchRows={onFetchCatalogRows}
          onClose={() => setCatalogImport(null)}
          onApply={applyCatalogImport}
        />
      ) : null}
    </div>
  );
}

function DataDefsEditor({
  dataDefs,
  usage,
  reservedKeys,
  expanded,
  setExpanded,
  repeatSources,
  tablePlacements,
  renderIndices,
  onChange,
  onDelete,
  onImportExcel,
  onImportCatalog,
}: {
  dataDefs?: DataDef[];
  /** 선언 key별 사용처 수(반복영역 over/seed/바인딩/집계/조립 입력) — 흐름 요약 줄에 표시 */
  usage: Map<string, number>;
  /** key 충돌 검사 대상(= 조립 데이터 소스 key). 한 목록으로 보이므로 양방향 검사한다. */
  reservedKeys: Set<string>;
  /** 카드 펼침 상태 — DataSection이 소유(같은 배열을 두 인스턴스가 나눠 그리므로 공유해야 한다). */
  expanded: Set<number>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<number>>>;
  /** 조인 입력 후보 통합 소스 목록(DataSection이 한 번만 계산해 내려보낸다). */
  repeatSources: RepeatSource[];
  /** key를 쓰는 배치 table 위젯 수 — "정본 편집 = 배치 N곳 반영" 파급 안내(P3, N=0이면 미표시). */
  tablePlacements: (key: string) => number;
  /** 이 인스턴스가 그릴 카드의 **원본 index** 목록 — 유형별 구획을 흐름 순으로 나눠 그린다. */
  renderIndices: number[];
  onChange: (next: DataDef[]) => void;
  /** 선언 삭제 — 참조 있으면 호출부가 확인 모달 처리 */
  onDelete: (key: string) => void;
  /** 이 index 데이터셋에 엑셀 행 가져오기 모달 열기(직접 입력 데이터셋 카드에서만 노출). */
  onImportExcel: (i: number) => void;
  /** 이 index 데이터셋에 카탈로그 행 박제 모달 열기(엑셀과 같은 게이트 — 직접 입력 전용). */
  onImportCatalog: (i: number) => void;
}) {
  const defs = dataDefs ?? [];

  const toggleExpanded = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  // 한 def를 index로 갱신(불변 교체).
  const patchDef = (i: number, patch: Partial<DataDef>) => {
    onChange(defs.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  };

  // 한 def를 통째로 교체(불변) — 순수 헬퍼(dataDefJoin)가 새 def를 통째로 돌려주는 경로용.
  const replaceDef = (i: number, next: DataDef) => {
    onChange(defs.map((d, idx) => (idx === i ? next : d)));
  };

  // 한 def의 한 칼럼(field)을 index로 갱신(불변 교체).
  const patchField = (i: number, fi: number, patch: Partial<DataDefField>) => {
    const oldKey = defs[i].fields[fi].key;
    const newKey = patch.key;
    // 필드 key 변경 시 기본행(defaultRows) 키도 함께 마이그레이션 — 안 하면 셀 바인딩(새 key)이
    // 기본행의 옛 key 값을 못 찾아 빈값 렌더(2026-07-09 버그: dataSources는 rename cascade하나 dataDef 누락).
    const keyChanged = newKey !== undefined && newKey !== oldKey;
    patchDef(i, {
      fields: defs[i].fields.map((x, xi) => (xi === fi ? { ...x, ...patch } : x)),
      ...(keyChanged
        ? {
            defaultRows: defs[i].defaultRows.map((row) => {
              if (!(oldKey in row)) return row;
              const next: Record<string, unknown> = {};
              for (const [k, v] of Object.entries(row)) next[k === oldKey ? newKey : k] = v;
              return next;
            }),
          }
        : {}),
    });
  };

  // 삭제 — 참조 확인 게이트는 onDelete(호출부)가 담당하고, 여기선 펼침 상태 정합만 유지한다
  // (조립 데이터 소스 카드와 동일한 분업. 실제 배열 제거는 onDelete → onChange 결과로 반영).
  const removeDataDef = (i: number) => {
    onDelete(defs[i].key);
    // 삭제된 index 제거 + 그보다 큰 index는 한 칸 당김(펼침 상태 정합 유지).
    const shift = (prev: Set<number>) => {
      const next = new Set<number>();
      prev.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      });
      return next;
    };
    setExpanded(shift);
  };

  // 섹션 머리·추가 버튼은 DataSection(통합 "데이터" 섹션)이 소유한다 — 이 컴포넌트는 지정된
  // index의 카드만 그리는 목록 렌더러다(2026-07-26 표면 통합).
  return (
    <div className="space-y-3">
      {renderIndices.map((i) => {
          const def = defs[i];
          if (!def) return null;
          const keyDup =
            defs.some((d, idx) => idx !== i && d.key === def.key) || reservedKeys.has(def.key);
          // 조인(join-by-key)만 전용 편집기 대상. 그 외 조립 방식(zip-two-sources/slice-range 등)은
          // 편집 UI가 없어 읽기 전용 요약만 — 조회조차 못 하면 은닉 설정이 된다.
          const isJoin = isJoinByKeyDef(def);
          const otherCompute = !!def.compute && !isJoin;
          // 레거시 병합 필드(mergeFields) — 편집 UI는 철거(접두 자동 노출로 대체), 은닉 방지용
          // 읽기 요약에만 쓴다.
          const legacyMerge = isJoin ? readMergeFieldEntries(def) : [];
          // 기본행 격자는 **유효 칼럼** 기준 — 조립(compute) 데이터셋은 선언이 0개여도 파생 칼럼
          // (base 상속 + 접두 자동 노출 + 레거시 출력 키)으로 기본행을 적을 수 있어야 한다
          // (compute 실패 시 최후 폴백). collectRepeatSources 2-pass가 이미 파생 목록으로 교체해
          // 두므로 그 소스를 그대로 쓴다(비-compute면 def.fields).
          const derivedSource = def.compute
            ? repeatSources.find((s) => s.kind === 'dataDef' && s.key === def.key)
            : undefined;
          const rowFields: { key: string; label: string }[] = derivedSource
            ? derivedSource.fields
            : def.fields;
          const effectiveFieldCount = rowFields.length;
          // 트리 walk이라 렌더당 1회만 부른다(표시 조건과 문구가 같은 값을 쓴다).
          const placements = tablePlacements(def.key);
          return (
            <div
              key={i}
              data-datacard={def.key}
              className="border border-gray-200 rounded p-2 space-y-2"
            >
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => toggleExpanded(i)}
                  className="mt-1 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700"
                  title={expanded.has(i) ? '접기' : '펼치기'}
                  aria-expanded={expanded.has(i)}
                >
                  {expanded.has(i) ? '▾' : '▸'}
                </button>
                <div className="flex-1 min-w-0 space-y-1">
                  <input
                    type="text"
                    value={def.key}
                    onChange={(e) => patchDef(i, { key: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded font-mono"
                    placeholder="key (점 없이, 예: risk-rows-src)"
                  />
                  {flatKeyError(def.key) && (
                    <p className="text-xs text-red-600">{flatKeyError(def.key)}</p>
                  )}
                  {keyDup && (
                    <p className="text-xs text-red-600">
                      중복된 key입니다(조립 데이터 소스와도 충돌 불가).
                    </p>
                  )}
                  <input
                    type="text"
                    value={def.label}
                    onChange={(e) => patchDef(i, { label: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    placeholder="표시명"
                  />
                  {/* 흐름 요약 — 원료 → 이 목록 → 사용처. 접었을 때만 구조 요약(칼럼/기본행)을 덧붙인다. */}
                  <DataFlowSummary
                    origin={dataDefOrigin(def, repeatSources)}
                    label={def.label || def.key}
                    usage={usage.get(def.key) ?? 0}
                  />
                  {/* 파급 안내(P3 참조 모델) — 이 정본의 칼럼 편집이 배치된 표들에 live 반영됨을
                      카드에서 미리 알린다(N=0이면 노이즈라 미표시). */}
                  {placements > 0 && (
                    <p className="text-[11px] text-brand-600">
                      이 데이터셋을 쓰는 배치 {placements}곳에 반영됩니다.
                    </p>
                  )}
                  {!expanded.has(i) && (
                    <p className="text-[11px] text-gray-400">
                      {/* 조립 데이터셋은 상속·접두·출력을 합친 유효 칼럼 수(선언 수가 아니라 실제 쓸 수 있는 수) */}
                      칼럼 {effectiveFieldCount} · 기본행 {def.defaultRows.length}
                      {legacyMerge.length > 0 && ` · 레거시 병합 ${legacyMerge.length}`}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <DataKindBadge kind={dataDefKind(def)} />
                  <button
                    type="button"
                    onClick={() => removeDataDef(i)}
                    className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                    title="이 데이터 삭제"
                  >
                    삭제
                  </button>
                </div>
              </div>

              {expanded.has(i) && (
                <>
              {/* 카드 안 순서는 **구성 → 구조 → 저장 정책 → 값**(2026-07-26 사용자 확정).
                  "어떤 소스를 쓰는가"(조인 구성)가 나머지 구획의 내용을 통째로 결정하므로 최상단에
                  둔다 — 자동 노출되는 칼럼(base 상속·조인소스 접두)은 전부 이 선택의 결과다. */}

              {/* 조인(join-by-key) 데이터셋 전용 — 조인 구성 편집(칼럼은 접두 자동 노출이라 저작 없음) */}
              {isJoin && (
                <DataDefJoinConfigEditor
                  def={def}
                  sources={repeatSources}
                  onChange={(next) => replaceDef(i, next)}
                />
              )}

              {/* 편집 UI가 없는 조립 방식 — 최소한 조회는 되도록 읽기 전용 요약 */}
              {otherCompute && (
                <div className="rounded border border-gray-200 bg-gray-50 px-1.5 py-1 space-y-0.5">
                  <p className="text-[11px] font-semibold text-gray-600">조립 구성(읽기 전용)</p>
                  {summarizeComputeLines(def).map((line, li) => (
                    <p key={li} className="text-[11px] font-mono text-gray-600 break-all">
                      {line}
                    </p>
                  ))}
                  <p className="text-[11px] text-gray-400">
                    이 조립 방식은 아직 편집 UI가 없습니다 — 값 변경은 마이그레이션/DB로 합니다.
                  </p>
                </div>
              )}

              {/* 칼럼(fields) 편집 — 직접 입력 데이터셋 전용. 조립(compute) 데이터셋의 칼럼은
                  자동 노출(base 상속 + 조인소스 접두 + 레거시 출력 키)이라 저작 대상이 아니고,
                  잔존 선언은 로드 시 정규화(stripComputeDefFields)로 비워져 구획 자체를 그리지
                  않는다(2026-07-26 사용자 확정 — 노출이 복잡성만 더함). */}
              {!def.compute && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">칼럼</span>
                    <button
                      type="button"
                      onClick={() =>
                        patchDef(i, {
                          fields: [
                            ...def.fields,
                            // 개수 기반 키는 삭제 후 재추가 시 기존 키와 충돌(중복 키 칼럼) — 충돌 회피 발급.
                            { key: nextAutoFieldKey(def.fields.map((f) => f.key)), label: '' },
                          ],
                        })
                      }
                      className="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                    >
                      + 칼럼
                    </button>
                  </div>
                  <div className="space-y-1">
                    {def.fields.map((f, fi) => (
                      <DataDefFieldRow
                        key={fi}
                        field={f}
                        persistScope={def.persistScope}
                        onPatch={(patch) => patchField(i, fi, patch)}
                        onRemove={() =>
                          patchDef(i, { fields: def.fields.filter((_, xi) => xi !== fi) })
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 영속 범위(persistScope) — 작성 편집값을 어디에 저장/재사용할지.
                  칼럼 구조 아래, 기본행 위 — 저장 정책은 "무엇을 담는가"가 정해진 뒤의 이야기다. */}
              <div className="flex items-center gap-1">
                <span
                  className="text-xs text-gray-500 w-16 shrink-0"
                  title="작성 페이지 편집값의 저장 범위. 현장/조직이면 같은 현장·조직의 다음 보고서가 prefill로 재사용."
                >
                  영속 범위
                </span>
                <select
                  value={def.persistScope ?? 'report'}
                  onChange={(e) =>
                    patchDef(i, {
                      persistScope:
                        e.target.value === 'report'
                          ? undefined
                          : (e.target.value as 'site' | 'organization'),
                    })
                  }
                  className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                >
                  <option value="report">보고서(기본)</option>
                  <option value="site">현장 재사용</option>
                  <option value="organization">조직 재사용</option>
                </select>
              </div>

              {/* 기본행(defaultRows) 편집 — field별 셀 텍스트 */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">기본행</span>
                  <div className="flex items-center gap-1">
                    {/* 엑셀 가져오기는 칼럼이 저작 대상인 직접 입력 데이터셋 전용 — 조립(compute)
                        데이터셋의 칼럼은 파생이라 엑셀 칸을 붙일 계약 자체가 없다. */}
                    {!def.compute && (
                      <button
                        type="button"
                        onClick={() => onImportExcel(i)}
                        disabled={rowFields.length === 0}
                        title="엑셀 파일에서 행을 가져옵니다(칼럼 구성은 그대로)"
                        className="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                      >
                        ⬆ 엑셀에서 행 추가
                      </button>
                    )}
                    {/* 카탈로그 행 박제 — 엑셀과 같은 게이트(직접 입력 전용·칼럼 필요). 지금 조회한
                        값을 기본행으로 고정한다. (동적 초기 행 축 DataDef.prefillFrom은 모델·서버에
                        있으나 매핑 UI가 없어 저작 노출은 제거 — 2026-08-09 사용자 결정, 완성형은
                        백로그 dataset-prefill-from-mapping) */}
                    {!def.compute && (
                      <button
                        type="button"
                        onClick={() => onImportCatalog(i)}
                        disabled={rowFields.length === 0}
                        title="시스템 조회를 지금 실행해 그 행을 기본행으로 박아 둡니다 — 이후 조회 결과가 바뀌어도 양식은 그대로입니다"
                        className="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                      >
                        ⬇ 시스템 조회에서 행 가져오기
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        patchDef(i, {
                          defaultRows: [
                            ...def.defaultRows,
                            Object.fromEntries(rowFields.map((f) => [f.key, ''])),
                          ],
                        })
                      }
                      disabled={rowFields.length === 0}
                      className="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                    >
                      + 행
                    </button>
                  </div>
                </div>
                {rowFields.length === 0 ? (
                  <p className="text-xs text-gray-400">먼저 칼럼을 추가하세요.</p>
                ) : (
                  <div className="space-y-1.5">
                    {def.defaultRows.map((row, ri) => (
                      <div key={ri} className="flex items-start gap-1">
                        <div className="flex-1 min-w-0 space-y-1">
                          {rowFields.map((f) => {
                            // 값 **형상이 문자열이 아닌** 칼럼만 전용 컨트롤로 갈라 준다 —
                            // 그 외(선택·숫자·사진 등)는 종전대로 텍스트 칸이다. widget 선언은
                            // def.fields에만 있다(compute 파생 칼럼은 위젯 없음 → 텍스트 그대로).
                            const colWidget = def.fields.find((df) => df.key === f.key)?.widget;
                            const patchCell = (next: unknown): void =>
                              patchDef(i, {
                                defaultRows: def.defaultRows.map((r, xi) =>
                                  xi === ri ? { ...r, [f.key]: next } : r,
                                ),
                              });
                            // 날짜/기간 칼럼은 작성 화면과 **같은 컨트롤**(rc)로 받는다 —
                            // checkbox와 같은 이유다: 기간 값은 객체({start,end})라 아래 텍스트
                            // 칸(String(row[key]))에 흘리면 [object Object]가 보이고, 그 칸을
                            // 건드리는 순간 그 문자열이 저장값이 되어 기본행이 망가진다.
                            if (colWidget === 'date' || colWidget === 'date-range') {
                              const name = f.label || f.key;
                              return colWidget === 'date' ? (
                                <DateFieldInput
                                  key={f.key}
                                  ariaLabel={name}
                                  value={row[f.key]}
                                  placeholder={name}
                                  className="w-full px-1.5 py-1 text-xs border border-gray-200 rounded"
                                  onChange={(next) => patchCell(next)}
                                />
                              ) : (
                                <DateRangeFieldInput
                                  key={f.key}
                                  ariaLabel={name}
                                  value={row[f.key]}
                                  startPlaceholder={`${name} 시작`}
                                  endPlaceholder={`${name} 종료`}
                                  className="w-full px-1.5 py-1 text-xs border border-gray-200 rounded"
                                  onChange={(next) => patchCell(next)}
                                />
                              );
                            }
                            // checkbox 칼럼은 실제 체크박스로 편집 + boolean 저장 — 렌더 셀
                            // (composerWidgetTreeRender)이 Boolean(value)로 읽으므로 문자열
                            // 'false'가 체크로 보이는 함정을 원천 차단. 기존 문자열 값 읽기는
                            // true/'true'만 체크로 정규화.
                            const isCheckbox = colWidget === 'checkbox';
                            if (isCheckbox) {
                              const v = row[f.key];
                              return (
                                <label
                                  key={f.key}
                                  className="flex items-center gap-1.5 px-1.5 py-1 text-xs text-gray-600"
                                >
                                  <input
                                    type="checkbox"
                                    checked={v === true || v === 'true'}
                                    onChange={(e) =>
                                      patchDef(i, {
                                        defaultRows: def.defaultRows.map((r, xi) =>
                                          xi === ri ? { ...r, [f.key]: e.target.checked } : r,
                                        ),
                                      })
                                    }
                                  />
                                  {f.label || f.key}
                                </label>
                              );
                            }
                            return (
                              <input
                                key={f.key}
                                type="text"
                                value={String(row[f.key] ?? '')}
                                onChange={(e) =>
                                  patchDef(i, {
                                    defaultRows: def.defaultRows.map((r, xi) =>
                                      xi === ri ? { ...r, [f.key]: e.target.value } : r,
                                    ),
                                  })
                                }
                                className="w-full px-1.5 py-1 text-xs border border-gray-200 rounded"
                                placeholder={f.label || f.key}
                              />
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...def.defaultRows];
                            // 해당 행의 복사본을 바로 아래에 삽입(비슷한 행 연속 입력 편의).
                            next.splice(ri + 1, 0, { ...def.defaultRows[ri] });
                            patchDef(i, { defaultRows: next });
                          }}
                          className="px-1.5 py-1 text-xs text-gray-400 hover:text-brand-600"
                          title="행 복제(아래에 복사본 추가)"
                        >
                          ⧉
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            patchDef(i, {
                              defaultRows: def.defaultRows.filter((_, xi) => xi !== ri),
                            })
                          }
                          className="px-1.5 py-1 text-xs text-gray-400 hover:text-red-600"
                          title="행 삭제"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          );
      })}
    </div>
  );
}

/**
 * 정적 선택 옵션(`{value,label}[]`) 편집기 — **칼럼(DataDefField)과 작성 위젯(InputWidget)이 공유**한다.
 *
 * 같은 개념(선택지 목록)을 두 패널이 각자 그리면 문구·동작이 갈라진다. 위젯 쪽은 "직접 입력" 방식일
 * 때만(= optionsFrom 미지정) 쓰이고, 데이터에서 가져오는 방식이면 이 편집기 대신 소스·필드 지정 UI가
 * 뜬다(rc는 `resolveDynamicSelectOptions(...) ?? node.options` 순으로 소비 — 정적은 폴백이다).
 *
 * value=저장되는 값, label=화면에 보이는 글자. 라벨을 비우면 렌더가 값을 그대로 보여준다.
 */
function InputOptionsEditor({
  options,
  onChange,
}: {
  options: { value: string; label: string }[] | undefined;
  onChange: (next: { value: string; label: string }[]) => void;
}) {
  const list = options ?? [];
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-gray-400">선택 옵션</span>
        <button
          type="button"
          onClick={() => onChange([...list, { value: '', label: '' }])}
          className="px-1.5 py-0.5 text-[11px] border border-gray-300 rounded hover:bg-gray-50"
        >
          + 옵션
        </button>
      </div>
      {list.length === 0 ? (
        <p className="text-[11px] text-gray-400">옵션을 추가하세요.</p>
      ) : (
        list.map((o, oi) => (
          <div key={oi} className="flex items-center gap-1">
            <input
              type="text"
              value={o.value}
              onChange={(e) =>
                onChange(list.map((x, xi) => (xi === oi ? { ...x, value: e.target.value } : x)))
              }
              className="flex-1 min-w-0 px-1.5 py-1 text-[11px] border border-gray-300 rounded font-mono"
              placeholder="값"
            />
            <input
              type="text"
              value={o.label}
              onChange={(e) =>
                onChange(list.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)))
              }
              className="flex-1 min-w-0 px-1.5 py-1 text-[11px] border border-gray-300 rounded"
              placeholder="표시"
            />
            <button
              type="button"
              onClick={() => onChange(list.filter((_, xi) => xi !== oi))}
              className="px-1 py-1 text-[11px] text-gray-400 hover:text-red-600"
              title="옵션 삭제"
            >
              ×
            </button>
          </div>
        ))
      )}
    </>
  );
}

/**
 * 표 셀 위젯 어휘(`DataDefField.widget`) → 표시 라벨 — 정적 데이터셋 칼럼 편집기의 select 후보.
 *
 * `Record<Union, string>`이라 shared union에 위젯을 더하거나 빼면 tsc가 여기를 잡는다 — 종전엔
 * `<option>`을 손나열해(2026-08-30 리뷰 W-reuse9) 어휘가 늘어도 후보가 조용히 빠졌다. 후보 목록
 * (`DATA_DEF_FIELD_WIDGETS`)은 이 키 순서에서 파생한다. 동적 데이터셋 축(`DATA_SOURCE_FIELD_WIDGET_LABEL`)
 * 과는 **어휘·라벨이 다른 별개 축**이다(「직접 입력」은 표면 한 유형·저장 두 갈래 —
 * `.claude/docs/domain-rules.md`).
 * 'text'가 기본이라 `f.widget ?? 'text'`로 고르고, 정규화(`columnWidgetPatch`)는 tableColumnFields가 정본.
 */
const DATA_DEF_FIELD_WIDGET_LABEL: Record<NonNullable<DataDefField['widget']>, string> = {
  text: '텍스트',
  textarea: '여러 줄',
  number: '숫자',
  checkbox: '체크',
  select: '선택',
  radio: '라디오',
  photo: '사진',
  // 달력 셀 — 값은 date=yyyy-MM-dd 문자열 / 기간={시작,종료} 객체(동적 데이터셋 행 축과
  // 같은 계약). 이 어휘가 없으면 날짜 칸이 텍스트가 되어 자유 문자열("3/5")이 섞인다.
  // 선택 옵션 같은 부가 배선은 없다(columnWidgetPatch가 options를 걷어낸다).
  date: '날짜',
  'date-range': '기간',
};
const DATA_DEF_FIELD_WIDGETS = Object.keys(DATA_DEF_FIELD_WIDGET_LABEL) as NonNullable<
  DataDefField['widget']
>[];

/**
 * 칼럼 선언(`DataDefField`) 한 줄 — key/라벨/입력 위젯 + 접기 줄 수 + 옵션 + 메타 저장 제외.
 *
 * **두 호출부가 공유한다**(같은 타입을 두 편집기가 각자 그리면 문구·정규화가 갈라진다):
 *  - 내장 데이터셋(`DataDef.fields`) — 직접 입력 데이터셋의 칼럼 편집 + 조립(compute) 데이터셋에
 *    남은 레거시 선언(라벨·위젯 override) 정리 목록. key 편집·삭제 가능.
 *  - **배치된 표 위젯(`InputWidget.fields`)** — TableWidgetColumnsEditor(2026-08-07 신설).
 *    key는 읽기 전용이고 삭제 축이 없으며, 검색 제안 소스 편집기를 `extra`로 잇는다.
 */
function DataDefFieldRow({
  field: f,
  persistScope,
  onPatch,
  onRemove,
  keyEditable = true,
  keyReadonlyTitle,
  extra,
}: {
  field: DataDefField;
  /** '메타 저장 제외' 체크는 현장/조직 영속일 때만 의미가 있다. */
  persistScope: DataDef['persistScope'];
  onPatch: (patch: Partial<DataDefField>) => void;
  /** 미지정이면 삭제(×) 자체를 감춘다 — 배치된 위젯 칼럼처럼 삭제 축이 없는 호출부용. */
  onRemove?: () => void;
  /**
   * key 편집 허용(기본 true=DataDef). false면 읽기 전용 표시 — 위젯 `fields[]`의 key는
   * `content[bindingKey]` 행 객체의 저장 키인데 DataDef의 defaultRows 마이그레이션 같은
   * 대응 장치가 없어, 바꾸면 기존 작성값이 끊긴다.
   */
  keyEditable?: boolean;
  /** keyEditable=false일 때 읽기 전용 이유(툴팁). */
  keyReadonlyTitle?: string;
  /** 호출부 고유 축을 이 행 끝에 잇는다(위젯 칼럼의 검색 제안 소스 등). */
  extra?: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        {keyEditable ? (
          <input
            type="text"
            value={f.key}
            onChange={(e) => onPatch({ key: e.target.value })}
            className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded font-mono"
            placeholder="필드 key"
          />
        ) : (
          <span
            className="flex-1 min-w-0 truncate rounded border border-transparent bg-gray-50 px-1.5 py-1 font-mono text-xs text-gray-500"
            title={keyReadonlyTitle}
          >
            {f.key}
          </span>
        )}
        <input
          type="text"
          value={f.label}
          onChange={(e) => onPatch({ label: e.target.value })}
          className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded"
          placeholder="라벨"
        />
        <select
          value={f.widget ?? 'text'}
          // text=기본(undefined로 정규화), select/radio 아니면 options 제거(혼동 방지) —
          // 정규화 식은 tableColumnFields가 단일 정본(위젯 칼럼 편집기와 공유).
          onChange={(e) =>
            onPatch(
              columnWidgetPatch(e.target.value as NonNullable<DataDefField['widget']> | 'text'),
            )
          }
          className="w-16 shrink-0 px-1 py-1 text-xs border border-gray-300 rounded bg-white"
          title="작성 페이지 표 셀 입력 위젯"
        >
          {DATA_DEF_FIELD_WIDGETS.map((w) => (
            <option key={w} value={w}>
              {DATA_DEF_FIELD_WIDGET_LABEL[w]}
            </option>
          ))}
        </select>
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="px-1.5 py-1 text-xs text-gray-400 hover:text-red-600"
            title="칼럼 삭제"
          >
            ×
          </button>
        ) : null}
      </div>
      {/* 셀 정렬 — 작성 페이지 표에서 이 칼럼의 헤더·셀 내용 정렬(모든 셀 위젯 공통).
          표시 축이라 값·저장 형상에 손대지 않고, 기본(미지정)이면 키 자체를 싣지 않는다. */}
      <label className="flex items-center gap-1 pl-1 text-[11px] text-gray-500">
        <span title="작성 페이지 표에서 이 칼럼 셀 내용 정렬. 기본=지금 그대로(텍스트 왼쪽·체크 가운데).">
          정렬
        </span>
        <select
          value={f.align ?? ''}
          onChange={(e) => onPatch(columnAlignPatch(e.target.value))}
          className="px-1 py-0.5 text-[11px] border border-gray-300 rounded bg-white"
        >
          <option value="">기본</option>
          <option value="start">왼쪽</option>
          <option value="center">가운데</option>
          <option value="end">오른쪽</option>
        </select>
      </label>
      {/* 긴 텍스트 접기 — 텍스트로 표시되는 칼럼만. 표시 축이라 값·양식 출력에 손대지 않는다
          (본문·PDF는 언제나 전문). 위젯 collapseLines와 같은 규약. */}
      {(f.widget === undefined || f.widget === 'text' || f.widget === 'textarea') && (
        <label className="flex items-center gap-1 pl-1 text-[11px] text-gray-500">
          <span title="읽기 표시 셀에서 N줄까지만 보이고 나머지는 '펼치기'. 본문·PDF는 항상 전문.">
            접기 줄 수
          </span>
          <input
            type="number"
            min={1}
            value={f.collapseLines ?? ''}
            placeholder="없음"
            onChange={(e) => onPatch(columnCollapseLinesPatch(e.target.value))}
            className="w-14 px-1 py-0.5 text-[11px] border border-gray-300 rounded"
          />
        </label>
      )}
      {(persistScope === 'site' || persistScope === 'organization') && (
        <label className="flex items-center gap-1 pl-1 text-[11px] text-gray-500">
          <input
            type="checkbox"
            checked={!!f.excludeFromPersist}
            onChange={(e) => onPatch({ excludeFromPersist: e.target.checked || undefined })}
          />
          메타 저장 제외(매 보고서 새로)
        </label>
      )}
      {(f.widget === 'select' || f.widget === 'radio') && (
        <div className="pl-2 ml-1 border-l-2 border-gray-100 space-y-1">
          <InputOptionsEditor
            options={f.options}
            onChange={(options) => onPatch({ options })}
          />
        </div>
      )}
      {extra}
    </div>
  );
}

/**
 * 표 위젯(`InputWidget.widget==='table'`)의 **칼럼 편집기** — 종전 "칼럼 순서·너비" 목록을 칼럼
 * 속성 편집기로 승격(2026-08-07)한 뒤, P3 참조 모델로 역할을 재정의(2026-08-08)한 것.
 *
 * 위젯의 칼럼 상태(tableFieldsMode)에 따라 두 모드로 갈린다:
 *  - **참조 모드**(사본 없음 + 정본 존재 — P3 로드 정규화가 켠 상태): 목록은 정본
 *    (`DataDef.fields`)을 보여주고, **선언 축**(라벨·셀 위젯·선택 옵션·검색 제안·메타 저장 제외)
 *    편집은 `ctx.onTemplateUpdate`로 **정본에 쓴다**(그 데이터셋을 쓰는 전 배치 반영 — 상단에
 *    "배치 N곳 반영" 안내). **표시 축 중 정렬·접기**는 신설 `widget.fieldDisplay[fieldKey]`
 *    오버라이드에 쓴다("기본"=키 삭제=정본 따름 — 이 위젯만 다르게). 순서·너비·읽기전용은
 *    종전대로 위젯 축. "이 표만 칼럼 구성을 다르게"는 [데이터셋 복제 후 전환](detach) 동선 —
 *    bindingKey가 저장 content 키라 **다른 참조가 있으면 버튼을 선제 비활성**하고 사유를 함께
 *    보여준다(countKeyRefs를 렌더 시점에 판정).
 *  - **동결 모드**(사본 잔존): 종전 그대로 사본을 편집한다. 정본 없음(카탈로그 스캐폴드 표)이면
 *    정보 배너만, 정본과 다르면(드리프트) "정본과 다름" 배너 + [정본 채택(사본 제거)] 수동 동선,
 *    정본과 같으면(로드 walk 밖 위젯) [참조로 전환] 제안.
 *
 * 편집기는 `DataDefFieldRow` 공용 재사용(데이터 탭의 내장 데이터셋 칼럼과 같은 편집기).
 * 호출은 `tableColumns` 디스크립터(WidgetTableColumnsControl) 한 곳뿐이다 — 단일 속성 패널과
 * 양식 표 셀 패널이 그 디스크립터를 공유해 **셀 안 표도 같은 편집기**로 들어온다(2026-08-07 이관).
 *
 * 여기서 하지 않는 것:
 *  - **key 편집** — `content[bindingKey]` 행 객체의 저장 키라 바꾸면 기존 작성값이 끊긴다(읽기 전용).
 *  - **칼럼 추가·삭제** — 데이터 형상 변경은 데이터 탭(정본) 소관이다(참조 모드는 자동 반영).
 *
 * 결정: docs/decisions/2026-08-07-datadef-reference-model-design.md
 */
/**
 * 표 칼럼 **표시 축**(배치본 `fieldDisplay` 소유) 키 정본 — shared `TableFieldDisplayOverride`와 1:1.
 * `satisfies Record<keyof …, true>`라 축이 늘면 여기 누락을 tsc가 막는다. 참조 모드에서 patch를
 * 표시 축(배치본)/선언 축(정본 DataDef.fields)으로 가르는 판정과 컨트롤 표시값 계산이 이 목록만 본다.
 * 병합 규칙 자체는 rc `applyFieldDisplayOverride`가 정본.
 */
const TABLE_FIELD_DISPLAY_KEYS = Object.keys({
  align: true,
  collapseLines: true,
} satisfies Record<keyof TableFieldDisplayOverride, true>) as (keyof TableFieldDisplayOverride)[];

function TableWidgetColumnsEditor({
  w,
  onWidgetUpdate,
  ctx,
  labelCls,
  inputCls,
}: {
  w: InputWidget;
  onWidgetUpdate: (id: string, updater: (x: ComposerWidget) => ComposerWidget) => void;
  ctx: WidgetPropCtx;
  labelCls: string;
  inputCls: string;
}) {
  // 아래 로컬 상태는 전부 **이 위젯 한정**이라 호출부가 `key={w.id}`로 인스턴스를 갈아 초기화한다
  // (패널은 선택만 바뀌고 재마운트되지 않는다 — 그 key가 없으면 앞 위젯 상태가 그대로 얹힌다).
  // 펼친 칼럼(속성 편집 노출) — 단일. 접힌 줄은 종전 목록과 같은 한 줄(표시 축만).
  const [openKey, setOpenKey] = useState<string | null>(null);
  // 드리프트 배너 [사본 유지] — 이 패널 마운트 동안만 접어 둔다(상태 자체는 사본 잔존 그대로).
  const [driftDismissed, setDriftDismissed] = useState(false);
  // detach 차단 사유(다른 참조 수) — 시도 시 계산해 안내.
  const [detachBlockedRefs, setDetachBlockedRefs] = useState<number | null>(null);
  const mode = tableFieldsMode(w, ctx.dataDefs);
  const isReference = mode.kind === 'reference';
  // 편집·표시 기준 칼럼 — 참조 모드는 정본, 동결 모드는 사본(렌더 해소 규칙과 동일한 사본 우선).
  const fields = isReference ? mode.def.fields : (w.fields ?? []);
  // 이 데이터셋을 쓰는 배치 수 — 참조 모드 파급 안내("N곳 반영")용.
  const placementCount = isReference ? countTablePlacements(ctx.tree, w.bindingKey) : 0;
  // detach 가부는 **렌더 시점에** 판정해 버튼을 선제 비활성한다(눌러 봐야 사유를 알던 동선 제거).
  // 참조 수에는 이 위젯 자신의 bindingKey 소비 1이 포함되므로, 그걸 뺀 나머지가 "이 위젯 밖 참조".
  // countKeyRefs 미주입 호스트는 0(=차단 없음)으로 degrade — 종전 동작 그대로.
  const detachOtherRefs = isReference
    ? Math.max((ctx.countKeyRefs?.(w.bindingKey) ?? 1) - 1, 0)
    : 0;
  // 선제 판정이 우선, 없으면 클릭 시 방어 판정이 남긴 값(정상 흐름에선 도달하지 않는다).
  const detachBlockedBy = detachOtherRefs > 0 ? detachOtherRefs : detachBlockedRefs;
  const detachBlocked = detachBlockedBy != null && detachBlockedBy > 0;
  /** 동결 모드 전용 — 사본 배열 교체(위젯 소유). */
  const updateCopyFields = (next: DataDefField[]): void =>
    onWidgetUpdate(w.id, (x) => (x.kind === 'input' ? { ...x, fields: next } : x));
  /** 참조 모드 전용 — 정본(template.dataDefs) 칼럼 배열 교체(전 배치 반영, undo 1스텝). */
  const updateCanonicalFields = (next: DataDefField[]): void =>
    ctx.onTemplateUpdate?.((t) => ({
      ...t,
      dataDefs: (t.dataDefs ?? []).map((d) =>
        d.key === w.bindingKey ? { ...d, fields: next } : d,
      ),
    }));
  const updateFields = isReference ? updateCanonicalFields : updateCopyFields;
  /**
   * 칼럼 patch 라우팅 — 동결 모드는 종전대로 사본에. 참조 모드는 **표시 축**
   * (`TABLE_FIELD_DISPLAY_KEYS`)만 `fieldDisplay` 오버라이드로 갈라 쓰고(값 undefined=키 삭제=
   * 정본 따름), 나머지 선언 축은 정본에.
   *
   * ⚠️ patch는 **단일 축 전제**다(컨트롤 하나가 바꾼 값 하나). 표시 축과 선언 축을 한 번에 섞어
   * 넘기면 위젯 갱신과 정본 갱신이 각각 히스토리에 쌓여 undo가 2스텝이 된다.
   */
  const patchColumn = (fieldKey: string, patch: Partial<DataDefField>): void => {
    if (!isReference) {
      updateCopyFields(patchTableColumn(fields, fieldKey, patch));
      return;
    }
    const displayKeys = TABLE_FIELD_DISPLAY_KEYS.filter((k) => k in patch);
    if (displayKeys.length > 0) {
      onWidgetUpdate(w.id, (x) => {
        if (x.kind !== 'input') return x;
        const cur = { ...(x.fieldDisplay ?? {}) };
        const entry: TableFieldDisplayOverride = { ...(cur[fieldKey] ?? {}) };
        for (const k of displayKeys) {
          const v = patch[k];
          if (v === undefined) delete entry[k];
          else Object.assign(entry, { [k]: v });
        }
        if (Object.keys(entry).length === 0) delete cur[fieldKey];
        else cur[fieldKey] = entry;
        const next = { ...x };
        if (Object.keys(cur).length === 0) delete next.fieldDisplay;
        else next.fieldDisplay = cur;
        return next;
      });
    }
    const declKeys = TABLE_FIELD_DISPLAY_KEYS as string[];
    const decl = Object.fromEntries(
      Object.entries(patch).filter(([k]) => !declKeys.includes(k)),
    ) as Partial<DataDefField>;
    if (Object.keys(decl).length > 0) updateCanonicalFields(patchTableColumn(fields, fieldKey, decl));
  };
  /**
   * 행 편집기에 보여줄 칼럼 — 참조 모드에선 표시 축 값을 **오버라이드만** 반영한다(빈 값=
   * "기본"=정본 따름). 정본 값을 그대로 보이면 "기본"으로 되돌릴 방법이 사라진다(해소값을
   * 컨트롤 value로 쓰지 않는 span 규약과 같은 이유).
   */
  const displayedField = (f: DataDefField): DataDefField => {
    if (!isReference) return f;
    const shown: DataDefField = { ...f };
    for (const k of TABLE_FIELD_DISPLAY_KEYS) delete shown[k];
    return applyFieldDisplayOverride(shown, w.fieldDisplay?.[f.key]);
  };
  /** 사본 제거(참조/정본 채택) — 이후 렌더·편집이 정본을 따른다. */
  const adoptCanonical = (): void =>
    onWidgetUpdate(w.id, (x) => {
      if (x.kind !== 'input') return x;
      const next = { ...x };
      delete next.fields;
      return next;
    });
  // 유효 순서 = fieldOrder 적용 후(렌더와 동일 규칙: 지정 순 + 미포함은 원래 순서 뒤).
  const ord = w.fieldOrder ?? [];
  const byKey = new Map(fields.map((f) => [f.key, f] as const));
  const effectiveAll = [
    ...ord.map((k) => byKey.get(k)).filter((f): f is DataDefField => f != null),
    ...fields.filter((f) => !ord.includes(f.key)),
  ];
  // 구분 그룹 표시 중인 칼럼은 렌더에서 숨겨지므로 **표시 축**(순서·너비·읽기전용)에서 뺀다
  // (2026-07-15 사용자 지적). 목록에서 통째로 감추지는 않는다 — 칼럼 선언(라벨·전파 대상)은
  // 여전히 저작 대상이고, 감추면 이 편집기가 메우려는 공백이 그대로 재발한다.
  const groupKey =
    w.groupByField && fields.some((f) => f.key === w.groupByField) ? w.groupByField : undefined;
  const orderable = groupKey ? effectiveAll.filter((f) => f.key !== groupKey) : effectiveAll;
  const move = (idx: number, dir: -1 | 1): void => {
    const keys = orderable.map((f) => f.key);
    const to = idx + dir;
    if (to < 0 || to >= keys.length) return;
    [keys[idx], keys[to]] = [keys[to], keys[idx]];
    // 숨긴 구분 칼럼은 전체 순서상 현재 인덱스 자리에 재삽입(목록 밖 보존).
    if (groupKey) {
      const gIdx = effectiveAll.findIndex((f) => f.key === groupKey);
      keys.splice(gIdx, 0, groupKey);
    }
    // 전체 키 배열을 fieldOrder로 저장(부분 지정보다 예측 가능). 기본 순서와 같아지면 제거.
    const isDefault = keys.every((k, i) => fields[i]?.key === k);
    onWidgetUpdate(w.id, (x) => {
      if (x.kind !== 'input') return x;
      const next = { ...x };
      if (isDefault) delete next.fieldOrder;
      else next.fieldOrder = keys;
      return next;
    });
  };
  const toggleReadonly = (key: string, on: boolean): void =>
    onWidgetUpdate(w.id, (x) => {
      if (x.kind !== 'input') return x;
      const next = { ...x };
      const cur = new Set(next.readonlyFields ?? []);
      if (on) cur.add(key);
      else cur.delete(key);
      if (cur.size === 0) delete next.readonlyFields;
      else next.readonlyFields = [...cur];
      return next;
    });
  const setWidth = (key: string, raw: string): void =>
    onWidgetUpdate(w.id, (x) => {
      if (x.kind !== 'input') return x;
      const n = Number(raw);
      const next = { ...x };
      const fw = { ...(next.fieldWidths ?? {}) };
      if (raw === '' || !Number.isFinite(n) || n <= 0) delete fw[key];
      else fw[key] = Math.min(100, n);
      if (Object.keys(fw).length === 0) delete next.fieldWidths;
      else next.fieldWidths = fw;
      return next;
    });
  return (
    <div>
      <label className={labelCls}>
        {fields.length > 1 ? '칼럼(순서·너비·속성)' : '칼럼 설정'}
      </label>
      {/* 모드 배너 — 이 목록이 무엇을 편집하는지(정본/사본)를 항상 먼저 밝힌다. */}
      {isReference ? (
        <p className="mb-1 rounded border border-brand-200 bg-brand-50 px-2 py-1 text-[11px] leading-4 text-brand-700">
          칼럼 구성은 <b>데이터셋 정본</b>입니다 — 이 데이터셋을 쓰는 배치 {placementCount}곳에
          반영됩니다. 정렬·접기 줄 수만 이 표 전용(기본=정본 따름)입니다.
        </p>
      ) : mode.kind === 'frozen-no-def' ? (
        <p className="mb-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] leading-4 text-gray-500">
          이 표는 대응 데이터셋이 없어 <b>자체 칼럼</b>을 가집니다 — 여기 편집은 이 표에만
          적용됩니다.
        </p>
      ) : mode.kind === 'frozen-drift' && !driftDismissed ? (
        <div className="mb-1 space-y-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5">
          <p className="text-[11px] leading-4 text-amber-700">
            이 표의 칼럼 사본이 데이터셋 <b>정본과 다릅니다</b> — 데이터 탭에서 정본을 고쳐도 이
            표에는 반영되지 않는 상태입니다.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[11px] text-amber-700 hover:bg-amber-100"
              onClick={() =>
                ctx.requestConfirm
                  ? ctx.requestConfirm({
                      title: '정본 채택(사본 제거)',
                      body: '이 표의 칼럼 사본을 지우고 데이터셋 정본을 따르게 합니다. 사본에만 있던 칼럼 설정(라벨·위젯·옵션·제안 소스 차이)은 사라지고, 이후 데이터 탭의 수정이 이 표에 바로 반영됩니다.',
                      confirmLabel: '정본 채택',
                      danger: true,
                      onConfirm: adoptCanonical,
                    })
                  : adoptCanonical()
              }
            >
              정본 채택(사본 제거)
            </button>
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-[11px] text-amber-600 hover:bg-amber-100"
              title="사본을 그대로 두고 이 안내만 접습니다(다시 열면 재표시)."
              onClick={() => setDriftDismissed(true)}
            >
              사본 유지
            </button>
          </div>
        </div>
      ) : mode.kind === 'frozen-equal' ? (
        <div className="mb-1 flex items-center justify-between gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1">
          <p className="text-[11px] leading-4 text-gray-500">
            칼럼 사본이 정본과 동일합니다 — 참조로 전환하면 데이터 탭 수정이 바로 반영됩니다.
          </p>
          <button
            type="button"
            className="shrink-0 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
            onClick={adoptCanonical}
          >
            참조로 전환
          </button>
        </div>
      ) : null}
      <div className="space-y-1">
        {effectiveAll.map((f) => {
          const isGroup = f.key === groupKey;
          const orderIdx = orderable.findIndex((x) => x.key === f.key);
          const open = openKey === f.key;
          return (
            <div key={f.key} className="rounded border border-gray-200 bg-white">
              <div className="flex items-center justify-between gap-2 px-2 py-1 text-xs">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-1 text-left hover:text-brand-700"
                  aria-expanded={open}
                  onClick={() => setOpenKey(open ? null : f.key)}
                  title={open ? '칼럼 속성 접기' : '칼럼 속성 펼치기(라벨·셀 위젯·접기·검색 제안)'}
                >
                  <span className="shrink-0 text-gray-400">{open ? '▾' : '▸'}</span>
                  <span className="min-w-0 truncate">{f.label || f.key}</span>
                  {isGroup ? (
                    <span
                      className="shrink-0 rounded bg-gray-100 px-1 text-[10px] text-gray-500"
                      title="구분 그룹으로 쓰여 표에서는 숨겨지는 칼럼입니다(그룹 머리글로 표시)."
                    >
                      구분
                    </span>
                  ) : null}
                </button>
                {/* 표시 축은 구분(숨김) 칼럼에는 의미가 없어 감춘다 — 종전 목록과 동일 규약. */}
                {isGroup ? null : (
                  <>
                    {/* 칼럼 읽기전용 — 값 표시 고정, 추가 행은 최초 1회 입력 후 잠금. */}
                    <label
                      className="flex shrink-0 items-center gap-1 text-[10px] text-gray-500"
                      title="이 칼럼은 값만 표시. '+ 추가'로 만든 행은 최초 1회 입력 가능하고, 입력 확정(포커스 이탈) 후 잠깁니다 — 정정은 삭제 후 재추가(행 추가/삭제·다른 칼럼은 그대로)"
                    >
                      <input
                        type="checkbox"
                        checked={w.readonlyFields?.includes(f.key) ?? false}
                        onChange={(e) => toggleReadonly(f.key, e.target.checked)}
                      />
                      읽기전용
                    </label>
                    {/* 칼럼 너비(%) — 빈 값=자동 분배. formTable 열 너비와 동형 규약. */}
                    <span className="flex shrink-0 items-center gap-0.5">
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={w.fieldWidths?.[f.key] ?? ''}
                        placeholder="자동"
                        className="w-14 rounded border border-gray-300 px-1 py-0.5 text-right text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
                        onChange={(e) => setWidth(f.key, e.target.value)}
                      />
                      <span className="text-[10px] text-gray-400">%</span>
                    </span>
                    {/* 순서 이동은 칼럼이 2개 이상일 때만 — 하나뿐이면 늘 비활성이라 노이즈다. */}
                    {orderable.length > 1 && (
                      <span className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          disabled={orderIdx === 0}
                          className="px-1 text-gray-500 hover:text-gray-800 disabled:text-gray-300"
                          aria-label={`${f.label || f.key} 위로`}
                          onClick={() => move(orderIdx, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          disabled={orderIdx === orderable.length - 1}
                          className="px-1 text-gray-500 hover:text-gray-800 disabled:text-gray-300"
                          aria-label={`${f.label || f.key} 아래로`}
                          onClick={() => move(orderIdx, 1)}
                        >
                          ↓
                        </button>
                      </span>
                    )}
                  </>
                )}
              </div>
              {open ? (
                <div className="space-y-1 border-t border-gray-100 px-2 py-2">
                  <DataDefFieldRow
                    field={displayedField(f)}
                    // 참조 모드는 정본을 직접 편집하므로 영속 축(메타 저장 제외)도 정본 그대로
                    // 저작 가능. 동결 모드(사본)는 종전대로 미저작(아래 읽기 안내로 대체) —
                    // 서버는 데이터셋 정의만 읽어 사본의 이 값은 죽은 값이다.
                    persistScope={mode.kind === 'reference' ? mode.def.persistScope : undefined}
                    keyEditable={false}
                    keyReadonlyTitle={TABLE_COLUMN_KEY_READONLY_REASON}
                    onPatch={(patch) => patchColumn(f.key, patch)}
                    extra={
                      <TableColumnSuggestionEditor
                        fields={fields}
                        field={f}
                        onFieldsChange={updateFields}
                        labelCls={labelCls}
                        inputCls={inputCls}
                      />
                    }
                  />
                  {!isReference && f.excludeFromPersist ? (
                    <p className="text-[11px] leading-4 text-amber-600">
                      메타 저장 제외: 켜짐 — 다만 이 축은 <b>내장 데이터셋 정의</b>의 칼럼 선언에서만
                      소비됩니다(서버가 데이터셋 정의만 읽음). 변경은 데이터 탭의 데이터셋 설정에서
                      하세요.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
        {w.fieldOrder ? (
          <button
            type="button"
            className="text-[11px] text-brand-600 hover:text-brand-700"
            onClick={() =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'input') return x;
                const next = { ...x };
                delete next.fieldOrder;
                return next;
              })
            }
          >
            기본 순서로 복원
          </button>
        ) : null}
      </div>
      {isReference ? (
        <div className="mt-2 space-y-1 border-t border-gray-100 pt-2">
          {/* detach — "이 표만 칼럼 구성을 다르게". bindingKey가 저장 content 키라 이 위젯 밖
              참조(본문 바인딩·반복 over·집계·계산 입력 등)가 있으면 전환이 그 참조를 끊는다 —
              참조가 있으면 **버튼을 잠그고 사유를 먼저 보여준다**(일괄 리네임은 시도하지 않는다,
              결정 P3). 클릭 시 재판정은 방어로 남긴다. */}
          <button
            type="button"
            disabled={detachBlocked}
            className="rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            title={
              detachBlocked
                ? `이 표의 값 키(${w.bindingKey})를 본문 ${detachBlockedBy}곳이 참조하고 있어 전환할 수 없습니다.`
                : '정본 데이터셋을 복제해 이 표만 그 사본을 참조하게 합니다 — 이후 이 표의 칼럼 편집은 다른 배치에 영향을 주지 않습니다.'
            }
            onClick={() => {
              // 방어 재판정 — 버튼이 이미 잠겨 있어 정상 흐름에선 참이 되지 않는다.
              const others = (ctx.countKeyRefs?.(w.bindingKey) ?? 1) - 1;
              if (others > 0) {
                setDetachBlockedRefs(others);
                return;
              }
              setDetachBlockedRefs(null);
              const run = (): void =>
                ctx.onTemplateUpdate?.((t) => {
                  const detached = detachTableWidgetDataDef(t, w);
                  return detached ? detached.template : t;
                });
              if (ctx.requestConfirm) {
                ctx.requestConfirm({
                  title: '데이터셋 복제 후 이 표만 전환',
                  body: `데이터셋 정본을 복제해 이 표만 사본을 참조하게 합니다. 이후 이 표의 칼럼 편집은 다른 배치 ${Math.max(placementCount - 1, 0)}곳에 영향을 주지 않으며, 이 표의 저장 값 키가 새 키로 바뀝니다(기존 정본·다른 배치는 그대로).`,
                  confirmLabel: '복제 후 전환',
                  // 저장 값 키가 바뀌는 전환이라 danger — 취소가 기본 포커스(모달 default 액션 규약).
                  danger: true,
                  onConfirm: run,
                });
              } else {
                run();
              }
            }}
          >
            데이터셋 복제 후 이 표만 전환
          </button>
          {detachBlocked ? (
            <p className="text-[11px] leading-4 text-amber-600">
              이 표의 값 키({w.bindingKey})를 다른 {detachBlockedBy}곳(본문 바인딩·반복영역·집계
              등)이 참조하고 있어 전환할 수 없습니다 — 참조를 정리한 뒤 다시 시도하세요.
            </p>
          ) : null}
        </div>
      ) : null}
      <p className="mt-1 text-[11px] leading-4 text-gray-400">
        칼럼 이름을 눌러 펼치면 라벨·셀 입력 위젯·접기 줄 수·선택 옵션·검색 제안을 고칠 수 있습니다.
        {isReference ? (
          <>
            {' '}
            칼럼 구성 편집은 <b>데이터셋 정본</b>에 반영되고, 정렬·접기 줄 수는 이 표 전용
            오버라이드입니다(기본=정본 따름).
          </>
        ) : (
          <>
            {' '}
            여기 값은 <b>이 위젯이 소유</b>하며 내장 데이터셋 정의와 자동 동기화되지 않습니다.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * 표 칼럼의 **검색 제안 축**(`DataDefField.suggestionsFrom` + `suggestionWrites`) 편집기 —
 * 위젯 축(WidgetSuggestionsFromControl)의 셀 판.
 *
 * 위젯 축과 다른 점은 **스코프가 그 행**이라는 것뿐이다: 필터 필드도 전파 대상도 본문 바인딩이
 * 아니라 **같은 표의 다른 칼럼**이다(rc composerWidgetTreeRender 셀 경로). 정규화·소스 교체 규약은
 * 같은 정본(patchSuggestionsFrom)을 쓰고, 전파 매핑 UI도 WritesMappingEditor 단일 정본을 재사용한다.
 *
 * 칼럼의 `suggestionsFrom.writes`(본문 바인딩 대상)는 **편집하지 않는다** — 셀 경로가 읽지 않아
 * 저작하면 효과 없는 값이 된다. 이미 값이 있으면 읽기로만 알린다(보존은 patchSuggestionsFrom이 담당).
 */
function TableColumnSuggestionEditor({
  fields,
  field: f,
  onFieldsChange,
  labelCls,
  inputCls,
}: {
  fields: DataDefField[];
  field: DataDefField;
  onFieldsChange: (next: DataDefField[]) => void;
  labelCls: string;
  inputCls: string;
}) {
  const cfg = f.suggestionsFrom;
  const source = findSuggestionSource(cfg?.catalogKey);
  const siblings = siblingColumnOptions(fields, f.key);
  const patch = (p: {
    catalogKey?: string;
    filterByRowField?: string;
    requireSelection?: boolean;
  }): void => onFieldsChange(patchTableColumnSuggestionsFrom(fields, f.key, p));
  const currentFilter = cfg?.filterByRowField ?? '';
  // 셀 제안을 소비하는 위젯은 text·textarea뿐이다(rc composerWidgetTreeRender). 그 외 칼럼에
  // 편집기를 열어 두면 효과 없는 값을 만들게 되므로 축을 감춘다 — 다만 이미 값이 있으면
  // 읽기로 알린다(은닉 금지: 위젯을 바꿨더니 조용히 사라진 것처럼 보이면 안 된다).
  const consumesSuggestions =
    f.widget === undefined || f.widget === 'text' || f.widget === 'textarea';
  if (!consumesSuggestions) {
    return cfg ? (
      <p className="pl-1 text-[11px] leading-4 text-amber-600">
        검색 제안 소스({cfg.catalogKey})가 저장돼 있으나 이 셀 위젯 종류는 제안을 쓰지 않습니다 —
        텍스트/여러 줄 셀에서만 동작합니다.
      </p>
    ) : null;
  }
  return (
    <div className="space-y-1 pl-1">
      <div>
        <label className={labelCls}>검색 제안 소스</label>
        <select
          className={inputCls}
          value={cfg?.catalogKey ?? ''}
          onChange={(e) => patch({ catalogKey: e.target.value })}
        >
          <option value="">없음(일반 입력)</option>
          {suggestionSourceOptions(cfg?.catalogKey).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] leading-4 text-gray-400">
          {source?.description ??
            '이 칼럼 셀 입력 중 서버 검색 결과를 드롭다운으로 제안합니다(text·여러 줄 셀만 소비).'}
        </p>
      </div>
      {cfg ? (
        <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
          {/* 선택 강제 — 종전 목록 한 줄짜리 '선택 전용' 체크를 이 축 안으로 흡수(2026-08-07).
              칸의 성질이라 소스를 바꿔도 유지된다(위젯 축과 같은 규약). */}
          <label className="flex items-start gap-1.5 text-[11px] leading-4 text-gray-600">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={cfg.requireSelection === true}
              onChange={(e) => patch({ requireSelection: e.target.checked })}
            />
            <span>
              검색 결과에서만 선택(자유 입력 금지)
              <span className="mt-0.5 block text-gray-400">
                켜면 타이핑한 글자는 검색어로만 쓰이고, 제안을 골라야 값이 들어갑니다. 고르지 않고
                빠져나오면 방금 추가한 행은 남지 않습니다.
              </span>
            </span>
          </label>
          {/* 검색 필터 — 값 스코프가 **그 행**이라 후보는 같은 표의 다른 칼럼이다. */}
          <div>
            <label className={labelCls}>검색 필터 칼럼 (같은 행)</label>
            <select
              className={inputCls}
              value={currentFilter}
              onChange={(e) => patch({ filterByRowField: e.target.value })}
            >
              <option value="">필터 없음(전체 검색)</option>
              {currentFilter && !siblings.some((s) => s.key === currentFilter) ? (
                <option value={currentFilter}>{currentFilter} (목록에 없음 — 보존)</option>
              ) : null}
              {siblings.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] leading-4 text-gray-400">
              같은 행의 그 칼럼 값이 검색 필터로 전달됩니다(값이 비면 필터 없이 검색).
              {source && source.filterKeys.length > 0
                ? ` 이 소스가 이해하는 필터 키는 ${source.filterKeys.join(' · ')} — 칼럼 key가 다르면 필터 없이 검색됩니다.`
                : ''}
            </p>
          </div>
          {/* 선택 전파 매핑(suggestionWrites) — 대상이 bindingKey가 아니라 같은 행의 칼럼 key인
              것만 위젯 writes와 다르다. 편집 UI는 WritesMappingEditor 단일 정본 재사용. */}
          <WritesMappingEditor
            writes={f.suggestionWrites ?? []}
            onChange={(next) => onFieldsChange(setTableColumnSuggestionWrites(fields, f.key, next))}
            sourceOptions={suggestionSourceRowFields(cfg.catalogKey)}
            // target 후보 — 같은 표의 칼럼(자기 포함: 제안 raw로 자기 셀을 덮어쓰는 지정이 실제로
            // 쓰인다 — SI 조문 칸). 배열 일괄 대상은 이 축에 없다.
            bodyTargets={columnWriteTargetOptions(fields).map((c) => ({
              bindingKey: c.key,
              label: c.label,
            }))}
            arrayTargets={[]}
            title="선택 전파 매핑(같은 행)"
            emptyText="제안을 고르면 이 칼럼에만 값이 들어갑니다. 같은 행의 다른 칼럼도 함께 채우려면 매핑을 추가하세요."
            sourceLabel="제안 필드"
            sourcePlaceholder="제안 행 필드 검색…"
            targetPlaceholder="대상 칼럼 검색…"
            helpText="제안에서 고른 행의 값만 전파됩니다(자유 타이핑에는 전파하지 않음)."
            labelCls={labelCls}
          />
          {cfg.writes && cfg.writes.length > 0 ? (
            <p className="text-[11px] leading-4 text-amber-600">
              본문 전파(writes) {cfg.writes.length}건이 저장돼 있으나 표 셀 경로는 읽지 않습니다 —
              같은 행 전파는 위 &apos;선택 전파 매핑&apos;을 쓰세요.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** 조인 구성 편집기 행 라벨(폭 고정으로 컨트롤 좌변 정렬). */
const JOIN_ROW_LABEL_CLS = 'text-[11px] text-gray-500 w-16 shrink-0';

/**
 * 조인(join-by-key) 내장 데이터셋의 **조인 구성** 편집기.
 *
 * 저작 대상은 조인 구성뿐이다:
 *   - inputs.base / inputs.join   : 기준 배열 / 값을 실어올 조인 소스(소스 키 콤보 + 직접 입력)
 *   - options.baseKey / joinKey   : 양쪽 행 안의 조인 키 경로(각 소스 칼럼 콤보 + 직접 입력)
 *   - options.emptyValue          : 무매칭 시 채울 값(미지정=빈 문자열)
 *
 * 칼럼은 저작하지 않는다 — 엔진이 조인 소스의 **모든 칼럼**을 `조인소스.칼럼` flat 키로 자동
 * 산출해 바인딩 후보로 전량 노출한다(2026-07-26 접두 자동 노출). 종전 options.mergeFields
 * (출력키 ← 경로) 편집 UI는 철거했고, 남은 레거시 값은 읽기 전용 요약으로만 보인다(은닉 방지).
 */
function DataDefJoinConfigEditor({
  def,
  sources,
  onChange,
}: {
  def: DataDef;
  /** 조인 입력 후보 — collectRepeatSources(내장 데이터셋 + 카탈로그 배열 + 조립 데이터 소스) */
  sources: RepeatSource[];
  onChange: (next: DataDef) => void;
}) {
  const config = readJoinConfig(def);
  const baseSource = sources.find((s) => s.key === config.base);
  const joinSource = sources.find((s) => s.key === config.join);
  // 소스 키 콤보 후보 — 필드 경로 콤보(JoinPathCombo)와 같은 {key,label} 형상이라 컨트롤을 공유한다.
  const sourceOptions = useMemo(
    () => sources.map((s) => ({ key: s.key, label: s.label })),
    [sources],
  );
  const sourceKeys = useMemo(() => sources.map((s) => s.key), [sources]);
  const baseUnknown = isUnknownSourceKey(config.base, sourceKeys);
  const joinUnknown = isUnknownSourceKey(config.join, sourceKeys);
  // 레거시 병합 필드(mergeFields) — 편집 불가, 요약 표시만(기존 저장 양식의 바인딩 호환).
  const legacyEntries = readMergeFieldEntries(def);

  return (
    <div>
      {/* 조인 구성 — 엔진 joinByKey의 입력(inputs)·조인 키(options) 저작 */}
      <div className="mb-1.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-1 space-y-1">
        <p className="text-[11px] font-semibold text-gray-600">조인 구성</p>

        <div className="flex items-start gap-1">
          <span
            className={`${JOIN_ROW_LABEL_CLS} mt-1.5`}
            title="행 수·순서의 정본이 되는 기준 배열. 조인 소스에만 있는 행은 결과에 포함되지 않습니다."
          >
            기준 소스
          </span>
          <JoinPathCombo
            value={config.base ?? ''}
            candidates={sourceOptions}
            placeholder="예: current-status-src"
            directTitle="기준(base) 배열의 소스 키 — 내장 데이터셋/시스템 조회 배열/조립 데이터 소스."
            comboTitle="행의 정본이 될 기준 배열 소스"
            emptyLabel="(소스 선택)"
            onChange={(v) => onChange(writeJoinConfig(def, { base: v }))}
          />
        </div>
        {baseUnknown && (
          <p className="pl-1 text-[11px] text-amber-600">
            기준 소스 “{config.base}”를 찾지 못했습니다 — 조인 결과가 빈 배열이 됩니다.
          </p>
        )}

        <div className="flex items-start gap-1">
          <span
            className={`${JOIN_ROW_LABEL_CLS} mt-1.5`}
            title="값을 실어올 조인 소스. 이 소스의 모든 칼럼이 `조인소스.칼럼` 이름으로 표 칸 후보에 자동 노출됩니다."
          >
            조인 소스
          </span>
          <JoinPathCombo
            value={config.join ?? ''}
            candidates={sourceOptions}
            placeholder="예: riskRows-src"
            directTitle="조인(join) 배열의 소스 키 — 내장 데이터셋/시스템 조회 배열/조립 데이터 소스."
            comboTitle="값을 실어올 조인 소스"
            emptyLabel="(소스 선택)"
            onChange={(v) => onChange(writeJoinConfig(def, { join: v }))}
          />
        </div>
        {joinUnknown && (
          <p className="pl-1 text-[11px] text-amber-600">
            조인 소스 “{config.join}”를 찾지 못했습니다 — 실어올 값이 모두 무매칭 값이 됩니다.
          </p>
        )}

        <div className="flex items-start gap-1">
          <span className={`${JOIN_ROW_LABEL_CLS} mt-1.5`} title="기준 행 안에서 조인 키로 쓸 값의 경로">
            기준 키
          </span>
          <JoinPathCombo
            value={config.baseKey ?? ''}
            candidates={baseSource?.fields ?? []}
            placeholder="예: id"
            directTitle="기준 행 안의 조인 키 경로(중첩은 점 표기)."
            comboTitle="기준 소스의 어떤 필드를 조인 키로 쓸지"
            onChange={(v) => onChange(writeJoinConfig(def, { baseKey: v }))}
          />
        </div>

        <div className="flex items-start gap-1">
          <span className={`${JOIN_ROW_LABEL_CLS} mt-1.5`} title="조인 행 안에서 조인 키로 쓸 값의 경로">
            조인 키
          </span>
          <JoinPathCombo
            value={config.joinKey ?? ''}
            candidates={joinSource?.fields ?? []}
            placeholder="예: findingId"
            directTitle="조인 행 안의 조인 키 경로(중첩은 점 표기)."
            comboTitle="조인 소스의 어떤 필드를 조인 키로 쓸지"
            onChange={(v) => onChange(writeJoinConfig(def, { joinKey: v }))}
          />
        </div>

        {(!config.baseKey || !config.joinKey) && (
          <p className="pl-1 text-[11px] text-amber-600">
            기준 키·조인 키가 모두 지정돼야 병합이 실행됩니다 — 지금은 기준 행이 그대로 쓰입니다.
          </p>
        )}

        <div className="flex items-center gap-1">
          <span
            className={JOIN_ROW_LABEL_CLS}
            title="조인 키가 일치하는 행이 없을 때 실어올 값 자리에 채울 값. 미지정이면 빈 문자열."
          >
            무매칭 값
          </span>
          <input
            type="text"
            value={readEmptyValue(def)}
            onChange={(e) => onChange(writeEmptyValue(def, e.target.value))}
            className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded"
            placeholder="빈칸(기본)"
          />
        </div>
      </div>

      <p className="mt-1 text-[11px] text-gray-400">
        조인 소스의 모든 칼럼은{' '}
        <span className="font-mono">
          {config.join?.trim() ? config.join.trim() : '조인소스'}.칼럼
        </span>{' '}
        이름으로 표 칸·바인딩 후보에 자동 노출됩니다 — 따로 칼럼을 저작하지 않습니다.
      </p>

      {/* 레거시 병합 필드(mergeFields) — 편집 UI 없이 읽기 전용 한 줄(은닉 방지, zip/slice 요약과
          동일 톤). top-level 항목은 로드 시 자동 이행(migrateLegacyMergeFields)되므로 여기 남는
          건 이행 불가 잔존(dotted srcPath·format 참조 등)뿐 — 값 정리는 마이그레이션/DB 경로. */}
      {legacyEntries.length > 0 && (
        <p className="mt-1 text-[11px] font-mono text-gray-400 break-all">
          레거시 병합 필드 {legacyEntries.length}건:{' '}
          {legacyEntries.map((e) => `${e.outKey} ← ${e.srcPath}`).join(', ')}
        </p>
      )}
    </div>
  );
}

/**
 * 조인 저작 공용 콤보 — 후보 목록({key,label}) 선택 + 고급 값 "직접 입력" 폴백.
 * (DataSourceFromEditor의 직접 입력 전환 패턴과 동형.)
 *
 * 조인 구성의 **소스 키·조인 키 경로**가 같은 컨트롤을 쓴다 — 후보가 소스 목록이냐 칼럼
 * 목록이냐만 다르고 "목록에서 고르거나 직접 적는다"는 규약은 같다.
 */
function JoinPathCombo({
  value,
  candidates,
  onChange,
  placeholder = '예: currentSafetyMeasure',
  directTitle = 'join 소스 행 안의 값 경로(중첩은 점 표기, 예: primary.note).',
  comboTitle = 'join 소스의 어떤 필드를 실어올지',
  emptyLabel = '(필드 선택)',
}: {
  value: string;
  candidates: RepeatSourceField[];
  onChange: (path: string) => void;
  /** 직접 입력 모드 placeholder */
  placeholder?: string;
  /** 직접 입력 모드 title(툴팁) */
  directTitle?: string;
  /** 콤보 모드 title(툴팁) */
  comboTitle?: string;
  /** 콤보 미선택 항목 라벨 */
  emptyLabel?: string;
}) {
  const known = candidates.some((c) => c.key === value);
  // 후보에 없는 값이거나 후보 목록 자체가 없으면(소스 미해결) 직접 입력으로 시작.
  const [direct, setDirect] = useState(!known && (value !== '' || candidates.length === 0));

  return (
    <div className="flex-1 min-w-0 space-y-1">
      {direct ? (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded font-mono"
          placeholder={placeholder}
          title={directTitle}
        />
      ) : (
        <select
          value={value}
          onChange={(e) => {
            if (e.target.value === DIRECT_INPUT_SENTINEL) setDirect(true);
            else onChange(e.target.value);
          }}
          className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
          title={comboTitle}
        >
          <option value="">{emptyLabel}</option>
          {candidates.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label || c.key}
              {c.nested ? ' (객체/배열)' : ''}
            </option>
          ))}
          {value && !known && <option value={value}>{value} (알 수 없음)</option>}
          <option value={DIRECT_INPUT_SENTINEL}>직접 입력…</option>
        </select>
      )}
      {direct && candidates.length > 0 && (
        <button
          type="button"
          onClick={() => setDirect(false)}
          className="pl-0.5 text-[11px] text-brand-600 hover:underline"
        >
          목록에서 선택
        </button>
      )}
    </div>
  );
}

/** 카탈로그 entry의 평탄(비중첩) 반환 필드 path 목록 — 키 매치(match) 경로 콤보 후보(스칼라 비교). */
function flatReturnPaths(entry: CatalogEntry | undefined): { path: string; label: string }[] {
  if (!entry) return [];
  return entry.returns
    .filter((r) => r.type !== 'object' && r.type !== 'array' && (r.fields?.length ?? 0) === 0)
    .map((r) => ({ path: r.path, label: r.label }));
}

/**
 * 자동 채움(prefill) 반환 필드 콤보 후보 — 스칼라 top-level(flatReturnPaths와 동일) +
 * **enum 키(valueSet) 배열 필드 전개**. `categories`처럼 조건 경로([key=값])가 필요한 배열
 * 필드를 일반 사용자가 경로 문법 없이 라벨 선택만으로 쓰게 한다(2026-07-13 사용자 요청).
 * 예: 근로자 구분별 인원(key: enum worker-categories, count) →
 *     "근로자 구분별 인원 · 사무직 — 인원 수" = 'categories[key=office].count'
 */
function prefillReturnPaths(entry: CatalogEntry | undefined): { path: string; label: string }[] {
  if (!entry) return [];
  const out: { path: string; label: string }[] = [];
  for (const r of entry.returns) {
    if (r.type !== 'object' && r.type !== 'array' && (r.fields?.length ?? 0) === 0) {
      out.push({ path: r.path, label: r.label });
      continue;
    }
    if (r.type !== 'array') continue;
    const nested = r.fields ?? [];
    const enumField = nested.find(
      (f): f is Extract<CatalogField, { type: 'enum' }> => f.type === 'enum',
    );
    if (!enumField) continue;
    const options = VALUE_SETS[enumField.valueSetId] ?? [];
    const scalars = nested.filter(
      (f) =>
        f !== enumField && f.type !== 'object' && f.type !== 'array' && (f.fields?.length ?? 0) === 0,
    );
    for (const opt of options) {
      for (const s of scalars) {
        out.push({
          path: `${r.path}[${enumField.path}=${opt.value}].${s.path}`,
          label: `${r.label} · ${opt.label} — ${s.label}`,
        });
      }
    }
  }
  return out;
}

/**
 * 행 배열을 줄 수 있는 카탈로그 entry 후보 — 카탈로그 행 가져오기(박제)의 원천 목록.
 * (동적 초기 행 UI는 2026-08-09 제거됐지만, 박제 모달이 같은 정본 필터를 쓴다.)
 */
function datasetPrefillEntries(catalog: CatalogEntry[]): CatalogEntry[] {
  // seedOnly 카탈로그(예: finding-ai-refinements)는 제외 — 직접 조회 대상이 아니고(반복 소스
  // 콤보들과 같은 관례), 박제 조회가 AI 생성·캐시 쓰기 부수효과를 일으킬 수 있다.
  return catalog.filter(
    (e) =>
      !e.seedOnly &&
      (e.kind === 'data' || e.kind === 'computed') &&
      (e.cardinality === 'list' || e.returns.some((r) => r.type === 'array')),
  );
}

/**
 * 데이터셋 행 원천의 반환 필드 후보 — 배열 반환 필드만.
 *
 * `cardinality:'list'` entry는 값 자체가 행 배열이라 **고를 필드가 없다**(빈 목록 → 콤보 미노출,
 * select 없이 entry 전체가 행이 된다). 단수 entry는 top-level 배열 필드만 후보다 — 배열 아래로
 * 내려간 경로는 행 배열이 아니라 그 행의 값 하나라 표를 채우지 못한다.
 */
function datasetRowsReturnPaths(
  entry: CatalogEntry | undefined,
): { path: string; label: string }[] {
  if (!entry || entry.cardinality === 'list') return [];
  return entry.returns
    .filter((r) => r.type === 'array')
    .map((r) => ({ path: r.path, label: r.label }));
}

/**
 * 조립형 소스의 `from`(값 출처) 경로 후보 — 주 소스(`primary.<path>`) + 각 보조 소스(`alias.<path>`).
 *
 * 데이터 소스 패널의 from 콤보와 반복영역 '＋칼럼 추가'의 조회 경로 후보가 **같은 목록**을 본다.
 * 두 곳이 서로 다른 후보를 보이면 "여기선 되는데 저기선 안 되는" 화면이 되고, 그 차이는 화면만
 * 봐서는 원인을 알 수 없다. 저장 값은 데이터 계약이라 `primary.` 접두사를 유지하고 표시 라벨만
 * '주'로 바꾼다. 중첩(객체/배열) 경로도 포함한다(fromReturnPaths 규약).
 */
function dataSourceFromPathOptions(
  def: DataSourceDef | undefined,
  catalog: CatalogEntry[],
): { value: string; label: string }[] {
  if (!def || def.kind !== 'assembled') return [];
  const byKey = (k: string): CatalogEntry | undefined => catalog.find((e) => e.key === k);
  return [
    ...fromReturnPaths(byKey(def.primary)).map((p) => ({
      value: `primary.${p.path}`,
      label: `주 · ${p.label}`,
    })),
    ...(def.lookups ?? []).flatMap((lk) =>
      fromReturnPaths(byKey(lk.source)).map((p) => ({
        value: `${lk.alias}.${p.path}`,
        label: `${lk.alias} · ${p.label}`,
      })),
    ),
  ];
}

/**
 * from(값 출처) 경로 콤보 전용 후보 — top-level 반환 필드를 **전부** 포함한다(중첩 object/array 포함).
 * 필드 from은 객체/배열 경로(예: primary.riskEstimation)도 가리킬 수 있어 flatReturnPaths(스칼라만)와
 * 달리 중첩도 노출하되, 라벨에 `(객체)`/`(배열)`를 붙여 스칼라와 구분한다. (키 매치는 flatReturnPaths 유지)
 */
function fromReturnPaths(entry: CatalogEntry | undefined): { path: string; label: string }[] {
  if (!entry) return [];
  return entry.returns.map((r) => {
    const suffix =
      r.type === 'array'
        ? ' (배열)'
        : r.type === 'object' || (r.fields?.length ?? 0) > 0
          ? ' (객체)'
          : '';
    return { path: r.path, label: `${r.label}${suffix}` };
  });
}

/**
 * 조립 데이터 소스 필드의 `from`(값 출처) 편집기 — 경로(primary.<path>/alias.<path>) 콤보 + 상수 입력.
 * 알려진 경로 옵션에 없는 값(접두사 단독·고급 dotted path)은 "직접 입력" 텍스트로 편집한다.
 */
function DataSourceFromEditor({
  value,
  pathOptions,
  onChange,
}: {
  value: string | { const: string | number | boolean | null };
  pathOptions: { value: string; label: string }[];
  onChange: (from: string | { const: string | number | boolean | null }) => void;
}) {
  const isConst = typeof value === 'object' && value !== null;
  const pathValue = typeof value === 'string' ? value : '';
  const knownPath = pathOptions.some((o) => o.value === pathValue);
  // 경로 값이 옵션에 없고 비어있지 않으면 직접 입력 모드로 시작.
  const [direct, setDirect] = useState(!isConst && !knownPath && pathValue !== '');

  return (
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center gap-1">
        <select
          value={isConst ? 'const' : 'path'}
          onChange={(e) => {
            if (e.target.value === 'const') onChange({ const: '' });
            else {
              setDirect(false);
              onChange('');
            }
          }}
          className="w-16 shrink-0 px-1 py-1 text-xs border border-gray-300 rounded bg-white"
          title="값 출처 종류"
        >
          <option value="path">필드</option>
          <option value="const">상수</option>
        </select>
        {isConst ? (
          <input
            type="text"
            value={String((value as { const: string | number | boolean | null }).const ?? '')}
            onChange={(e) => onChange({ const: e.target.value })}
            className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded"
            placeholder="상수 값"
          />
        ) : direct ? (
          <input
            type="text"
            value={pathValue}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded font-mono"
            placeholder="예: primary.필드 / alias.필드"
            title="주 소스는 primary. 접두사, 보조 소스는 해당 alias. 접두사로 입력하세요(예: primary.workplaceName)."
          />
        ) : (
          <select
            value={pathValue}
            onChange={(e) => {
              if (e.target.value === '__direct__') setDirect(true);
              else onChange(e.target.value);
            }}
            className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
          >
            <option value="">(필드 선택)</option>
            {pathOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {pathValue && !knownPath && <option value={pathValue}>{pathValue} (알 수 없음)</option>}
            <option value="__direct__">직접 입력…</option>
          </select>
        )}
      </div>
      {!isConst && direct && (
        <button
          type="button"
          onClick={() => setDirect(false)}
          className="text-[11px] text-brand-600 hover:underline pl-0.5"
        >
          목록에서 선택
        </button>
      )}
    </div>
  );
}

/**
 * 동적 데이터셋 필드 작성폼 위젯 후보(DataSourceFieldWidget) → 표시 라벨.
 *
 * select 후보 목록(`DATA_SOURCE_FIELD_WIDGETS`)은 이 Record의 키에서 **파생**한다 — 종전엔 배열을
 * 따로 손나열해(2026-08-30 리뷰 W-reuse9) union에 위젯을 더하면 라벨은 tsc가 강제하는데 후보는
 * 조용히 빠졌다. `Record<Union, string>`은 키 누락·초과 둘 다 tsc가 잡으므로 목록은 여기 순서가
 * 곧 select 순서다.
 */
const DATA_SOURCE_FIELD_WIDGET_LABEL: Record<DataSourceFieldWidget, string> = {
  text: '한 줄',
  textarea: '여러 줄',
  'textarea-list': '여러 줄(목록)',
  'text-list': '한 줄(목록)',
  number: '숫자',
  // 달력 입력 — 값은 yyyy-MM-dd 문자열. 이 어휘가 없으면 날짜 칸이 한 줄 텍스트가 되어
  // 자유 문자열("3/5", "다음주")이 섞인다.
  date: '날짜',
  // 기간(시작~종료) — 값은 `{ start, end }` 객체. 한쪽만 입력된 상태도 정상으로 허용한다.
  'date-range': '기간',
  // 행 토글(boolean) — 값 미정의 행은 선언 defaultValue로 폴백 표시(shared 'checkbox' JSDoc).
  checkbox: '체크박스',
  // 사진 업로드(값=URL) — image(표시 전용)와 다른 **입력** 축. 세부 선언(visibleWhen·photoAnalysis
  // 등)의 저작 UI는 후속 — 값은 spread patch라 편집을 거쳐도 보존된다(2026-09-07 추기 4).
  photo: '사진 업로드',
  readonly: '읽기 전용',
  // 읽기 전용 이미지 표시(값=URL) — 사진 필드.
  image: '이미지',
  // 이미지 + 도형 오버레이(값=`{ url, shapes, strokes }`) — 값이 URL 문자열이면 이미지와 동일 표시.
  'annotated-image': '주석 이미지',
  // 그 행 인물에게 서명을 받는 칸 — 값은 content가 아니라 증빙 테이블에 산다(작성자 위조 불가).
  signature: '서명',
  registered: '특화',
};
/** select 후보 목록 — 라벨 Record의 키 순서 그대로(손나열 금지, 위 JSDoc). */
const DATA_SOURCE_FIELD_WIDGETS = Object.keys(
  DATA_SOURCE_FIELD_WIDGET_LABEL,
) as DataSourceFieldWidget[];

/**
 * 조립형/수동 데이터 소스(dataSources) 에디터 — 요소 미선택 시 전역 속성 패널에 노출(Phase B-2).
 *
 * 종류(kind): 'assembled'=복수 원시 카탈로그 entry를 조합(주 소스 1개 + from 매핑 + 보조 소스 lookup) /
 * 'manual'=조립 없는 빈 데이터셋(작성폼 편집본이 곧 content). editMode(list 전용)가 작성폼 노출을 결정하고,
 * 각 필드는 작성폼 위젯 명세(widget/widgetKey/editable)를 갖는다(동적 데이터셋 통합 D1~D3).
 * 소비는 명시 키 참조(반복영역 over·flat 바인딩), 조립 실행은 서버 assembler.
 * 모든 변경은 onChange(다음 dataSources 배열)로 상위 apply에 전달 — history undo 대상.
 */
function DataSourcesEditor({
  dataSources,
  catalog,
  usage,
  reservedKeys,
  expanded,
  setExpanded,
  renderIndices,
  onChange,
  onRenameField,
  onDeleteField,
  onDelete,
  hasPreviewContext,
  onDryRun,
}: {
  dataSources?: DataSourceDef[];
  catalog: CatalogEntry[];
  /** 선언 key별 사용처 수(반복영역 over/seed/바인딩/집계) */
  usage: Map<string, number>;
  /** key 충돌 검사 대상(dataDefs key + 작성폼 rows bindingKey) */
  reservedKeys: Set<string>;
  /** 카드 펼침 상태 — DataSection이 소유(같은 배열을 두 인스턴스가 나눠 그리므로 공유해야 한다). */
  expanded: Set<number>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<number>>>;
  /** 이 인스턴스가 그릴 카드의 **원본 index** 목록 — 유형별 구획을 흐름 순으로 나눠 그린다. */
  renderIndices: number[];
  onChange: (next: DataSourceDef[]) => void;
  /**
   * 조립 필드 키 rename(전역 참조 동기) — 기존 필드 키를 바꿀 때 호출(신규 빈 키는 onChange로 직접 patch).
   * 선언 필드 키 + 직접 반복 표 셀 + seed 행 컬렉션(참조 표까지)을 한 스텝으로 rename한다.
   */
  onRenameField: (params: { dsKey: string; oldField: string; newField: string }) => void;
  /**
   * 필드 삭제 — 선언 fields 제거 + 그 소스를 반복하는 표의 해당 셀 바인딩 해제를 한 스텝으로.
   * (선언만 지우면 표에 선언 없는 키를 가리키는 유령 바인딩이 남는다. rename과 파급 범위 동일.)
   * 배치된 셀이 있으면 호출부가 확인 모달을 띄운다.
   */
  onDeleteField: (params: { dsKey: string; field: string }) => void;
  /** 선언 삭제 — 참조 있으면 호출부가 확인 모달 처리 */
  onDelete: (key: string) => void;
  /** 실 데이터 컨텍스트(조직/현장/점검/계정)가 1개 이상 선택됐는지 — dry-run 가능 여부 */
  hasPreviewContext: boolean;
  /** 조립 dry-run — 현재(미저장 포함) 템플릿을 선택 컨텍스트로 resolve해 content 반환 */
  onDryRun: () => Promise<Record<string, unknown>>;
}) {
  const defs = dataSources ?? [];
  // 필드 키 편집 버퍼 — di=소스 인덱스, fi=필드 인덱스, value=버퍼, error=검증 메시지.
  // 타이핑 글자마다 rename이 폭주하지 않도록 blur/Enter 확정 시에만 커밋.
  const [fieldEdit, setFieldEdit] = useState<{
    di: number;
    fi: number;
    value: string;
    error?: string;
  } | null>(null);
  const toggleExpanded = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const patchDef = (i: number, patch: Partial<DataSourceDef>) => {
    onChange(defs.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  };
  const patchField = (i: number, fi: number, patch: Partial<DataSourceFieldMap>) => {
    patchDef(i, {
      fields: defs[i].fields.map((x, xi) => (xi === fi ? { ...x, ...patch } : x)),
    });
  };
  // 필드 키 검증 — 빈값/점(.)(flatKeyError) + 같은 소스 내 중복(자기 자신 제외). 무변경이면 null.
  const validateFieldKey = (di: number, fi: number, raw: string): string | null => {
    const fe = flatKeyError(raw);
    if (fe) return fe;
    const trimmed = raw.trim();
    const current = defs[di]?.fields[fi]?.field ?? '';
    if (trimmed === current) return null;
    const dup = (defs[di]?.fields ?? []).some((x, xi) => xi !== fi && x.field === trimmed);
    return dup ? '이미 쓰이는 필드 키입니다.' : null;
  };
  // 필드 키 확정 — 무변경이면 편집 종료, 검증 실패면 에러 유지(버퍼 보존). 신규(빈 키)는 참조가 없어
  // onChange로 직접 patch, 기존 키는 onRenameField(전역 참조 동기) 경로로.
  const commitFieldKey = (di: number, fi: number, raw: string) => {
    const trimmed = raw.trim();
    const current = defs[di]?.fields[fi]?.field ?? '';
    if (trimmed === current) {
      setFieldEdit(null);
      return;
    }
    const err = validateFieldKey(di, fi, raw);
    if (err) {
      setFieldEdit({ di, fi, value: raw, error: err });
      return;
    }
    if (current.trim() === '') {
      patchField(di, fi, { field: trimmed });
    } else {
      onRenameField({ dsKey: defs[di].key, oldField: current, newField: trimmed });
    }
    setFieldEdit(null);
  };
  const patchLookup = (i: number, li: number, patch: Partial<DataSourceLookup>) => {
    const lookups = defs[i].lookups ?? [];
    patchDef(i, { lookups: lookups.map((x, xi) => (xi === li ? { ...x, ...patch } : x)) });
  };

  const removeDataSource = (i: number) => {
    // 참조 확인은 onDelete(호출부)가 담당 — 여기선 expanded 정합만 유지하고 실제 삭제는 onChange 결과에 맡긴다.
    onDelete(defs[i].key);
    setExpanded((prev) => {
      const next = new Set<number>();
      prev.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      });
      return next;
    });
  };

  const entryByKey = (k: string) => catalog.find((e) => e.key === k);

  // 섹션 머리·추가 버튼은 DataSection(통합 "데이터" 섹션)이 소유한다 — 이 컴포넌트는 지정된
  // index의 카드만 그리는 목록 렌더러다(2026-07-26 표면 통합).
  return (
    <div className="space-y-3">
      {renderIndices.map((i) => {
          const def = defs[i];
          if (!def) return null;
          const keyDup =
            defs.some((d, idx) => idx !== i && d.key === def.key) || reservedKeys.has(def.key);
          // 수동 데이터셋(kind:'manual')은 조립이 없어 주 소스/보조 소스/from을 숨기고 필드 명세만 편집한다.
          const isManual = def.kind === 'manual';
          const isList = def.cardinality === 'list';
          // 행 순서 선언이 **지금 효력을 내는가** — 서버 파생(collectSourceOrderRules)과 같은
          // shared 술어. 주 소스가 비면 정렬을 걸 배열을 가리킬 키가 없어 선언이 무시되는데,
          // 종전엔 저작 화면이 isList만 보고 칸을 띄워 「고른 대로 보이지만 아무 일도 없는」
          // 상태를 만들었다(2026-10-05 매뉴얼 대조에서 발견). 칸은 그대로 두고 안내로 알린다.
          const orderEffective = canApplySourceOrder(def);
          const editMode: 'editable' | 'readonly' = def.editMode ?? 'editable';
          const primaryEntry = entryByKey(def.primary);
          const cardinalityMismatch =
            !!primaryEntry && primaryEntry.cardinality !== def.cardinality;
          const primaryOptions = catalog.filter((e) => e.cardinality === def.cardinality);
          const pathOptions = dataSourceFromPathOptions(def, catalog);
          const primaryPaths = flatReturnPaths(primaryEntry);
          const useCount = usage.get(def.key) ?? 0;
          return (
            <div
              key={i}
              data-datacard={def.key}
              className="border border-gray-200 rounded p-2 space-y-2"
            >
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => toggleExpanded(i)}
                  className="mt-1 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700"
                  title={expanded.has(i) ? '접기' : '펼치기'}
                  aria-expanded={expanded.has(i)}
                >
                  {expanded.has(i) ? '▾' : '▸'}
                </button>
                <div className="flex-1 min-w-0 space-y-1">
                  <input
                    type="text"
                    value={def.key}
                    onChange={(e) => patchDef(i, { key: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded font-mono"
                    placeholder="key (점 없이, 예: risk-rows-src)"
                  />
                  {flatKeyError(def.key) && (
                    <p className="text-xs text-red-600">{flatKeyError(def.key)}</p>
                  )}
                  {keyDup && (
                    <p className="text-xs text-red-600">
                      중복된 key입니다(데이터셋·동적 데이터셋과도 충돌 불가).
                    </p>
                  )}
                  <input
                    type="text"
                    value={def.label}
                    onChange={(e) => patchDef(i, { label: e.target.value })}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    placeholder="표시명"
                  />
                  {/* 흐름 요약 — 원료 → 이 목록 → 사용처. 종전 "사용처/미사용" 칩을 이 줄이 흡수했다. */}
                  <DataFlowSummary
                    origin={dataSourceOrigin(def, catalog)}
                    label={def.label || def.key}
                    usage={useCount}
                  />
                  {/* 메타 요약 — 단위별 nowrap 칩 + flex-wrap: 좁은 폭에서 단어 중간이 아닌 칩 경계로만 줄바꿈. */}
                  {!expanded.has(i) && (
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-gray-400 min-w-0">
                      <span className="whitespace-nowrap">{isList ? '행 배열' : '묶음'}</span>
                      <span className="whitespace-nowrap">칼럼 {def.fields.length}</span>
                      {isList && (
                        <span className="whitespace-nowrap">
                          {editMode === 'readonly' ? '읽기 전용' : '편집'}
                        </span>
                      )}
                      {!isManual && def.primary && (
                        <span className="font-mono truncate max-w-[160px]" title={def.primary}>
                          {def.primary}
                        </span>
                      )}
                      {/* 정렬 선언은 접힌 카드에서도 보여야 한다 — 표 순서가 바뀌는 축인데
                          펼쳐야만 보이면 "왜 이 순서지"를 카드 밖에서 찾게 된다. */}
                      {!isManual && isList && def.order?.by && (
                        <span
                          className={`whitespace-nowrap ${orderEffective ? '' : 'text-amber-600'}`}
                          title={
                            orderEffective
                              ? undefined
                              : '주 소스를 정해야 이 순서가 적용됩니다 — 지금은 효력이 없습니다'
                          }
                        >
                          정렬{' '}
                          {primaryPaths.find((p) => p.path === def.order!.by)?.label ??
                            def.order.by}{' '}
                          {def.order.direction === 'asc' ? '오름차순' : '내림차순'}
                          {/* 무효한 선언을 유효한 것처럼 보이면 "왜 이 순서가 아닌지"를 카드 밖에서
                              찾게 된다 — 선언은 지우지 않고 효력 없음만 덧붙인다. */}
                          {orderEffective ? '' : ' (효력 없음)'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <DataKindBadge kind={dataSourceKind(def)} />
                  <button
                    type="button"
                    onClick={() => removeDataSource(i)}
                    className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                    title="이 데이터 삭제"
                  >
                    삭제
                  </button>
                </div>
              </div>

              {expanded.has(i) && (
                <>
                  {/* 선언 종류(kind) — 조립(카탈로그 조합) / 수동(빈 데이터셋). 수동은 주/보조/from 숨김. */}
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500 w-16 shrink-0" title="선언 종류">
                      종류
                    </span>
                    <div className="flex-1 min-w-0 inline-flex rounded border border-gray-300 overflow-hidden">
                      {(['assembled', 'manual'] as const).map((k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => {
                            if (def.kind === k) return;
                            // manual↔assembled 전환 — from/주소스/보조소스는 소비 안 되면 유지(전환만). manual은 '' 관례.
                            patchDef(i, {
                              kind: k,
                              ...(k === 'manual' ? { primary: '', lookups: undefined } : {}),
                            });
                          }}
                          className={`flex-1 px-1.5 py-1 text-[11px] ${
                            def.kind === k ? 'bg-brand-50 text-brand-700 font-medium' : 'bg-white text-gray-600'
                          }`}
                        >
                          {k === 'assembled' ? '조립' : '수동(빈)'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* 결과 형상(cardinality) */}
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500 w-16 shrink-0" title="결과 형상">
                      형상
                    </span>
                    <select
                      value={def.cardinality}
                      onChange={(e) =>
                        patchDef(i, { cardinality: e.target.value as 'single' | 'list' })
                      }
                      className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                    >
                      <option value="list">행 배열(list)</option>
                      <option value="single">묶음 1개(single)</option>
                    </select>
                  </div>
                  {/* 편집 정책(editMode) + 행 추가/삭제 — cardinality:'list' 전용(single은 flat 소비라 무의미). */}
                  {isList && (
                    <>
                      <div className="flex items-center gap-1">
                        <span
                          className="text-xs text-gray-500 w-16 shrink-0"
                          title="작성폼 노출 결정 — 편집 가능: 선언이 곧 작성폼 행 데이터셋 / 읽기 전용: 위젯 미노출(resolve 산출 렌더만)"
                        >
                          편집
                        </span>
                        <div className="flex-1 min-w-0 inline-flex rounded border border-gray-300 overflow-hidden">
                          {(['editable', 'readonly'] as const).map((m) => (
                            <button
                              key={m}
                              type="button"
                              onClick={() => patchDef(i, { editMode: m })}
                              className={`flex-1 px-1.5 py-1 text-[11px] ${
                                editMode === m
                                  ? 'bg-brand-50 text-brand-700 font-medium'
                                  : 'bg-white text-gray-600'
                              }`}
                            >
                              {m === 'editable' ? '편집 가능' : '읽기 전용'}
                            </button>
                          ))}
                        </div>
                      </div>
                      {editMode === 'editable' && (
                        <label className="flex items-center gap-1 pl-[4.5rem] text-[11px] text-gray-500">
                          <input
                            type="checkbox"
                            checked={def.allowAddRemove !== false}
                            onChange={(e) => patchDef(i, { allowAddRemove: e.target.checked })}
                          />
                          작성폼에서 행 추가/삭제 허용
                        </label>
                      )}
                      {editMode === 'editable' && (
                        // 하위 축(추가/삭제 분리 — 2026-09-14 사용자 결정). 표시값·비활성 판정은
                        // RowAddRemoveToggles(shared effectiveRowAddRemove·rowAddRemoveMaster)가 맡는다.
                        <RowAddRemoveToggles
                          def={def}
                          variant="dataSource"
                          onAddChange={(checked) => patchDef(i, { allowAdd: checked })}
                          onRemoveChange={(checked) => patchDef(i, { allowRemove: checked })}
                          masterOffNote="상위 「행 추가/삭제 허용」이 꺼져 있어 추가·삭제가 모두 막힙니다(상위를 켜면 저장된 하위 체크가 그대로 적용됩니다)."
                        />
                      )}
                      {editMode === 'editable' && (
                        // 삭제의 뜻을 저작자가 알아야 한다 — 저장 content가 정본이라(상세·편집 재진입
                        // 모두 content 행을 읽고 조립은 폴백) 지운 행은 다음 편집에서 다시 조립되지
                        // 않는다(2026-09-14 은닉 감사 3차 B).
                        <p className="pl-[4.5rem] text-[10px] leading-4 text-gray-400">
                          삭제 = 문서에서 제외. 추가만 끄면(예: 발견사항별 항목) 작성자는 행을 뺄 수만
                          있습니다. 편집 재진입 시 삭제된 행은 복원되지 않습니다(저장 전엔 저장하지
                          않고 나가면 되돌아옵니다).
                        </p>
                      )}
                    </>
                  )}
                  {/* 주 소스(primary) — 조립(assembled) 전용 */}
                  {!isManual && (
                    <>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500 w-16 shrink-0" title="주 소스 시스템 조회 항목">
                      주 소스
                    </span>
                    {/* 공통 코어 CatalogEntryPicker(P3 이관) — 카탈로그 전체급 목록이라 검색이
                        가장 필요한 자리. 형상 불일치/미상 저장값은 코어 보존 그룹이 표시 유지하고
                        아래 cardinalityMismatch 경고가 사유를 설명한다. */}
                    <CatalogEntryPicker
                      entries={primaryOptions}
                      value={def.primary || undefined}
                      onChange={(key) => patchDef(i, { primary: key ?? '' })}
                      allowClear
                      placeholder={`주 소스 선택 — ${def.cardinality}`}
                    />
                  </div>
                  {cardinalityMismatch && (
                    <p className="text-xs text-red-600">
                      주 소스 형상({primaryEntry?.cardinality})이 이 소스 형상({def.cardinality})과
                      다릅니다.
                    </p>
                  )}

                  {/* 행 순서(정렬) — 주 소스 행을 어떤 기준으로 세울지.
                      ⚠️ **노출 게이트(isList)와 효력 게이트(canApplySourceOrder)는 다르다.** 칸은
                      list면 띄우고(저장된 선언이 사라진 것처럼 보이지 않게), 서버 파생과 같은
                      술어로 「지금 효력이 있나」만 아래 안내·조망 줄에 표시한다. 종전에는 노출만
                      있고 효력 표시가 없어 주 소스 전에 고른 정렬이 조용히 무시됐다(2026-10-05).
                      기준 후보가 def 필드가 아니라 **주 소스(primary) 반환 경로**인 이유 —
                      정렬은 조립 산출이 아니라 resolved 소스 배열에 걸린다(DataSourceDef.order
                      JSDoc). 그래서 아래 보조 소스 "키 매치"의 primary 행 필드와 같은 후보 축이다. */}
                  {isList && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <span
                          className="text-xs text-gray-500 w-16 shrink-0"
                          title="주 소스 행을 세울 기준 — 미지정이면 시스템이 준 원본 순서 그대로"
                        >
                          행 순서
                        </span>
                        <select
                          value={def.order?.by ?? ''}
                          onChange={(e) => {
                            const by = e.target.value;
                            patchDef(i, {
                              order: by
                                ? { by, direction: def.order?.direction ?? 'desc' }
                                : undefined,
                            });
                          }}
                          className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                          title="정렬 기준 필드"
                        >
                          <option value="">원본 순서(정렬 없음)</option>
                          {primaryPaths.map((p) => (
                            <option key={p.path} value={p.path}>
                              {p.label} ({p.path})
                            </option>
                          ))}
                          {/* 저장된 기준이 지금 주 소스의 반환 경로에 없을 때도 선택을 보존한다 —
                              보조 소스 매치 select와 같은 관례. 목록에서 빠지면 카드를 여는 것만으로
                              정렬 선언이 조용히 지워진다. */}
                          {def.order?.by &&
                            !primaryPaths.some((p) => p.path === def.order!.by) && (
                              <option value={def.order.by}>{def.order.by}</option>
                            )}
                        </select>
                        {def.order && (
                          <select
                            value={def.order.direction ?? 'desc'}
                            onChange={(e) =>
                              patchDef(i, {
                                order: {
                                  by: def.order!.by,
                                  direction: e.target.value as 'asc' | 'desc',
                                },
                              })
                            }
                            className="w-28 shrink-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                            title="정렬 방향"
                          >
                            <option value="desc">내림차순</option>
                            <option value="asc">오름차순</option>
                          </select>
                        )}
                      </div>
                      {def.order?.by &&
                        !!primaryEntry &&
                        !primaryPaths.some((p) => p.path === def.order!.by) && (
                          <p className="pl-[4.5rem] text-[11px] leading-4 text-amber-600">
                            기준 「{def.order.by}」{subjectParticleOf(def.order.by)} 지금 주 소스의 항목에 없습니다 — 주 소스를
                            바꾸셨다면 기준을 다시 고르거나 「원본 순서」로 되돌리세요.
                          </p>
                        )}
                      {/* 효력 게이트 안내 — 주 소스가 없으면 서버가 규칙을 만들지 않는다
                          (canApplySourceOrder). 칸을 숨기지 않는 이유는 저장된 선언이 사라진
                          것처럼 보이지 않게 하기 위해서다(위 '선택 보존' 관례와 같은 방향).
                          ⚠️ `def.order?.by` 게이트가 **같이** 걸려 있어야 한다 — 선언이 없으면
                          「고른 기준」이 없으니 이 문구가 고르지도 않은 것을 고른 것처럼 말하고,
                          새 소스를 assembled·list로 만든 직후(주 소스 고르기 전)의 **정상 상태**에
                          경고가 떠 배경음이 된다. 접힌 카드 요약도 같은 게이트다(위 '정렬 선언은
                          접힌 카드에서도' 절). */}
                      {def.order?.by && !orderEffective && (
                        <p className="pl-[4.5rem] text-[11px] leading-4 text-amber-600">
                          주 소스를 정해야 이 순서가 적용됩니다 — 지금은 고른 기준이 무시됩니다.
                        </p>
                      )}
                      {def.order && orderEffective && (
                        <p className="pl-[4.5rem] text-[11px] leading-4 text-gray-400">
                          그룹 선택 모달과 문서가 같은 순서를 씁니다. 값이 없는 행은 항상 맨 뒤로
                          갑니다.
                        </p>
                      )}
                    </div>
                  )}

                  {/* 최대 행 수(rowLimit) — 행 순서 곁: 정렬된 순서 위에서부터 이 수만큼만 담는
                      조립 컷 + 작성 페이지 행 추가 게이트(DataSourceDef.rowLimit JSDoc 정본).
                      노출 게이트는 행 순서와 동일(kind assembled·cardinality list — isList,
                      !isManual 블록 안). 빈 값/1 미만은 키 삭제(제한 없음). */}
                  {isList && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <span
                          className="text-xs text-gray-500 w-16 shrink-0"
                          title="정렬된 순서 위에서부터 이 수만큼만 담음 — 비우면 제한 없음"
                        >
                          최대 행 수
                        </span>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={def.rowLimit ?? ''}
                          onChange={(e) => {
                            const n = Math.floor(Number(e.target.value));
                            patchDef(i, {
                              rowLimit: Number.isInteger(n) && n >= 1 ? n : undefined,
                            });
                          }}
                          className="w-24 shrink-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                          title="최대 행 수"
                          placeholder="제한 없음"
                        />
                      </div>
                      {def.rowLimit != null && (
                        <p className="pl-[4.5rem] text-[11px] leading-4 text-gray-400">
                          행 순서(정렬)에서 위에서부터 이 수만큼만 담습니다. 작성 페이지에서도 이
                          수를 넘겨 행을 추가할 수 없습니다. 비우면 제한이 없습니다.
                        </p>
                      )}
                    </div>
                  )}

                  {/* 보조 소스(lookups) — 필드 from이 alias를 참조하므로 alias 정의를 필드보다 먼저 노출 */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500" title="주 소스 행과 단일 키로 결합할 보조 소스">
                        보조 소스(lookup)
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          patchDef(i, {
                            lookups: [
                              ...(def.lookups ?? []),
                              { alias: `lk${(def.lookups?.length ?? 0) + 1}`, source: '' },
                            ],
                          })
                        }
                        className="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                      >
                        + 보조 소스
                      </button>
                    </div>
                    {(def.lookups ?? []).length === 0 ? (
                      <p className="text-[11px] text-gray-400">
                        없음. 다른 시스템 조회를 키로 결합하려면 추가하세요.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {(def.lookups ?? []).map((lk, li) => {
                          const lkPaths = flatReturnPaths(entryByKey(lk.source));
                          const hasMatch = !!lk.match;
                          return (
                            <div key={li} className="space-y-1 border border-gray-100 rounded p-1">
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={lk.alias}
                                  onChange={(e) => patchLookup(i, li, { alias: e.target.value })}
                                  className="w-16 shrink-0 px-1.5 py-1 text-xs border border-gray-300 rounded font-mono"
                                  placeholder="alias"
                                  title="fields.from에서 이 소스를 가리키는 접두사"
                                />
                                {/* 공통 코어 CatalogEntryPicker(P3 이관) — 무필터 전체 카탈로그
                                    (legacyHidden 제외는 코어가), 미상 저장값은 보존 그룹. lookup은
                                    N행 반복이라 행마다 파라미터 블록이 중복되지 않게 paramsBlock 끔
                                    (주 소스·전역 편집기가 커버). */}
                                <CatalogEntryPicker
                                  entries={catalog}
                                  value={lk.source || undefined}
                                  onChange={(key) => patchLookup(i, li, { source: key ?? '' })}
                                  allowClear
                                  placeholder="소스 조회 선택…"
                                  paramsBlock={false}
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    patchDef(i, {
                                      lookups: (def.lookups ?? []).filter((_, xi) => xi !== li),
                                    })
                                  }
                                  className="px-1.5 py-1 text-xs text-gray-400 hover:text-red-600"
                                  title="보조 소스 삭제"
                                >
                                  ×
                                </button>
                              </div>
                              <label className="flex items-center gap-1 pl-1 text-[11px] text-gray-500">
                                <input
                                  type="checkbox"
                                  checked={hasMatch}
                                  onChange={(e) =>
                                    patchLookup(i, li, {
                                      match: e.target.checked
                                        ? { primaryField: '', sourceField: '' }
                                        : undefined,
                                    })
                                  }
                                />
                                키 매치(체크 해제=단순 병합, single 보조 소스)
                              </label>
                              {hasMatch && lk.match && (
                                <div className="flex items-center gap-1 pl-1">
                                  <select
                                    value={lk.match.primaryField}
                                    onChange={(e) =>
                                      patchLookup(i, li, {
                                        match: { ...lk.match!, primaryField: e.target.value },
                                      })
                                    }
                                    className="flex-1 min-w-0 px-1 py-1 text-[11px] border border-gray-300 rounded bg-white font-mono"
                                    title="primary 행 필드"
                                  >
                                    <option value="">(primary 필드)</option>
                                    {primaryPaths.map((p) => (
                                      <option key={p.path} value={p.path}>
                                        {p.path}
                                      </option>
                                    ))}
                                    {lk.match.primaryField &&
                                      !primaryPaths.some((p) => p.path === lk.match!.primaryField) && (
                                        <option value={lk.match.primaryField}>
                                          {lk.match.primaryField}
                                        </option>
                                      )}
                                  </select>
                                  <span className="text-[11px] text-gray-400">=</span>
                                  <select
                                    value={lk.match.sourceField}
                                    onChange={(e) =>
                                      patchLookup(i, li, {
                                        match: { ...lk.match!, sourceField: e.target.value },
                                      })
                                    }
                                    className="flex-1 min-w-0 px-1 py-1 text-[11px] border border-gray-300 rounded bg-white font-mono"
                                    title="보조 소스 행 필드"
                                  >
                                    <option value="">(소스 필드)</option>
                                    {lkPaths.map((p) => (
                                      <option key={p.path} value={p.path}>
                                        {p.path}
                                      </option>
                                    ))}
                                    {lk.match.sourceField &&
                                      !lkPaths.some((p) => p.path === lk.match!.sourceField) && (
                                        <option value={lk.match.sourceField}>
                                          {lk.match.sourceField}
                                        </option>
                                      )}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                    </>
                  )}

                  {/* 필드 매핑(fields) — 조립: from 경로/상수, 수동: 필드 명세만. 작성폼 위젯(widget/widgetKey/editable) 포함. */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">칼럼</span>
                      <button
                        type="button"
                        onClick={() =>
                          patchDef(i, {
                            fields: [
                              ...def.fields,
                              {
                                // 개수 기반 키 충돌 방지 — 위 dataDef "＋ 칼럼"과 동일 규약.
                                field: nextAutoFieldKey(def.fields.map((f) => f.field)),
                                from: '',
                                widget: 'text',
                                editable: true,
                              },
                            ],
                          })
                        }
                        className="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                      >
                        + 칼럼
                      </button>
                    </div>
                    {def.fields.length === 0 ? (
                      <p className="text-xs text-gray-400">
                        {isManual ? '데이터셋 칼럼을 추가하세요.' : '조립 결과 칼럼을 추가하세요.'}
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {def.fields.map((f, fi) => {
                          // 이 필드의 키 편집 버퍼(없으면 null) — 확정(blur/Enter) 시에만 rename 커밋.
                          const editing =
                            fieldEdit && fieldEdit.di === i && fieldEdit.fi === fi ? fieldEdit : null;
                          return (
                          <div key={fi} className="space-y-1 border border-gray-100 rounded p-1">
                            <div className="flex items-center gap-1">
                              <div className="flex-1 min-w-0">
                                <input
                                  type="text"
                                  value={editing ? editing.value : f.field}
                                  onChange={(e) =>
                                    setFieldEdit({ di: i, fi, value: e.target.value })
                                  }
                                  onBlur={() => {
                                    if (editing) commitFieldKey(i, fi, editing.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (editing) commitFieldKey(i, fi, editing.value);
                                    } else if (e.key === 'Escape') {
                                      setFieldEdit(null);
                                    }
                                  }}
                                  className="w-full min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded font-mono"
                                  placeholder="칼럼 key"
                                  title="조립 결과 행 객체의 필드 키. 변경 확정(blur/Enter) 시 이 소스를 참조하는 표 셀·seed 행 컬렉션과 함께 rename됩니다."
                                />
                                {editing?.error && (
                                  <p className="text-[11px] text-red-600">{editing.error}</p>
                                )}
                              </div>
                              <input
                                type="text"
                                value={f.label ?? ''}
                                onChange={(e) =>
                                  patchField(i, fi, { label: e.target.value || undefined })
                                }
                                className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded"
                                placeholder="라벨(선택)"
                              />
                              <button
                                type="button"
                                // 선언만 지우면 표에 유령 바인딩이 남는다 — 호출부 op가 셀까지
                                // 함께 정리한다(rename과 같은 파급 범위).
                                onClick={() => onDeleteField({ dsKey: def.key, field: f.field })}
                                className="px-1.5 py-1 text-xs text-gray-400 hover:text-red-600"
                                title="필드 삭제(배치된 셀의 바인딩도 함께 해제)"
                              >
                                ×
                              </button>
                            </div>
                            {/* 값 출처(from) — 조립(assembled) 전용. 수동 데이터셋은 조립이 없어 숨긴다. */}
                            {!isManual && (
                              <div className="flex items-center gap-1">
                                <span className="text-[11px] text-gray-400 w-8 shrink-0">값</span>
                                <DataSourceFromEditor
                                  value={f.from}
                                  pathOptions={pathOptions}
                                  onChange={(from) => patchField(i, fi, { from })}
                                />
                              </div>
                            )}
                            {/* 작성폼 위젯(widget/widgetKey/editable)은 작성폼 탭에서 설정(D4-2 이관,
                                사용자 확정 2026-07-05). 여기(양식 탭 전역 패널)는 데이터 계약만 다룬다. */}
                            {isList && editMode === 'editable' && (
                              <p className="text-[11px] text-gray-400">
                                필드 위젯은 작성폼 탭에서 설정합니다.
                              </p>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* 조립 dry-run 미리보기 — 실 데이터로 이 선언을 조립해 카드 안에 표시. */}
                  <DataSourceDryRunPanel
                    def={def}
                    hasPreviewContext={hasPreviewContext}
                    onDryRun={onDryRun}
                  />
                </>
              )}
            </div>
          );
      })}
    </div>
  );
}

/**
 * 조립 데이터 소스 dry-run 미리보기 패널 — 카드별 "조립 미리보기" 버튼 + 결과 모달.
 * onDryRun(부모)이 현재(미저장 포함) 템플릿을 선택 컨텍스트로 서버 resolve → content 반환하면,
 * 이 def.key의 산출을 extractDataSourceDryRun으로 추출해 모달 표(list=상위 20행 / single=필드-값)로 보인다.
 * 속성 패널이 좁아 결과는 카드 인라인이 아니라 모달(z-[70])에 표시한다.
 * 컨텍스트 미선택이면 버튼 disabled + 안내(미리보기 탭에서 실 데이터 컨텍스트 선택).
 */
function DataSourceDryRunPanel({
  def,
  hasPreviewContext,
  onDryRun,
}: {
  def: DataSourceDef;
  hasPreviewContext: boolean;
  onDryRun: () => Promise<Record<string, unknown>>;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DryRunResult | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setOpen(true);
    try {
      const content = await onDryRun();
      setResult(extractDataSourceDryRun(content, def, DRY_RUN_MODAL_ROW_LIMIT));
    } catch (e) {
      setResult(null);
      setError(e instanceof Error ? e.message : '조립 미리보기 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [def, onDryRun]);

  return (
    <div className="border-t border-gray-100 pt-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!hasPreviewContext || loading}
          onClick={() => void run()}
          className="px-2 py-0.5 text-xs border border-brand-300 text-brand-700 rounded hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            hasPreviewContext
              ? '현재 선언을 실 데이터로 조립해 봅니다(미저장 편집 포함).'
              : '미리보기 탭에서 실 데이터 컨텍스트를 먼저 선택하세요.'
          }
        >
          {loading ? '조립 중…' : '조립 미리보기'}
        </button>
        {!hasPreviewContext && (
          <span className="text-[11px] text-gray-400">
            미리보기 탭에서 실 데이터 컨텍스트를 먼저 선택
          </span>
        )}
      </div>
      {open && (
        <DataSourceDryRunModal
          def={def}
          loading={loading}
          error={error}
          result={result}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * 조립 미리보기 결과 모달 — 로딩/에러/결과(표)를 모달 폭으로 표시.
 * 포탈·오버레이·z-[70]·Esc/오버레이 닫기·포커스 트랩은 공용 [[Modal]]이 소유한다.
 * 결과 표가 길 수 있어 패널을 뷰포트에 가두고 스크롤시킨다(ShortcutsModal과 같은 방식).
 */
function DataSourceDryRunModal({
  def,
  loading,
  error,
  result,
  onClose,
}: {
  def: DataSourceDef;
  loading: boolean;
  error: string | null;
  result: DryRunResult | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      ariaLabel={`조립 미리보기 — ${def.label || def.key}`}
      // ⛔ 예외(이유①+② 둘 다): ⓐ 아래 panelClassName이 `max-h-[80vh]`를 들고 있어 base
      // `bodyScroll`의 `max-h-full`과 **같은 property로 부딪힌다** ⓑ 본문 서브트리에 **포탈 안 된**
      // 떠 있는 UI가 여럿이다(`CatalogCombobox`·`PreviewContextBar`·`SimulationPresetsSection`의
      // `absolute z-30 … max-h-60` 리스트박스 · `SimulationChip` 툴팁 · `TemplateIssuesBadge`) —
      // base가 본문에 `overflow-y-auto`를 걸면 그것들이 **새로 클리핑된다**(tsc·spec·jsdom 전부
      // 못 보는 축이다). 그리고 `overflow-auto`는 양축이라 가로 넘침도 이 패널이 소유한다.
      bodyScroll={false}
      panelClassName="max-h-[80vh] overflow-auto"
      title={
        <>
          조립 미리보기
          <span className="ml-2 font-mono text-xs font-normal text-gray-400">{def.key}</span>
        </>
      }
    >
      {loading && <p className="text-xs text-gray-400">조립 결과를 불러오는 중…</p>}
      {!loading && error && (
        <p className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
      {!loading && !error && result && <DryRunResultView result={result} />}
    </Modal>
  );
}

/** dry-run 결과 렌더 — empty(원인 안내) / single(필드-값 2열) / list(칼럼 표 + 요약). */
function DryRunResultView({ result }: { result: DryRunResult }) {
  if (result.kind === 'empty') {
    return (
      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1">
        {result.reason}
      </p>
    );
  }
  if (result.kind === 'single') {
    return (
      <table className="w-full text-[11px] border border-gray-200 border-collapse">
        <tbody>
          {result.fields.map((f) => (
            <tr key={f.field} className="border-b border-gray-100 last:border-b-0">
              <th className="text-left font-medium text-gray-500 bg-gray-50 px-1.5 py-1 border-r border-gray-200 align-top whitespace-nowrap">
                {f.label}
              </th>
              <td className="px-1.5 py-1 text-gray-700 break-all">
                {formatDryRunValue(f.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  // list
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] border border-gray-200 border-collapse">
          <thead>
            <tr className="bg-gray-50">
              {result.columns.map((c) => (
                <th
                  key={c.field}
                  className="text-left font-medium text-gray-500 px-1.5 py-1 border border-gray-200 whitespace-nowrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, ri) => (
              <tr key={ri}>
                {result.columns.map((c) => (
                  <td
                    key={c.field}
                    className="px-1.5 py-1 border border-gray-200 text-gray-700 break-all"
                  >
                    {formatDryRunValue(row[c.field])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">
        상위 {result.rows.length}행 표시
        {result.hiddenRows > 0 ? ` · …외 ${result.hiddenRows}행` : ''} (총 {result.totalRows}행)
      </p>
    </div>
  );
}

/**
 * 선택 아웃라인 스타일 주입.
 * 셀 선택(selectedCells)이 있으면 선택된 각 셀(`[data-composer-cell="..."]`)에 outline을 입히고,
 * 없으면 선택 노드(`[data-composer-node-id]`)에 입힌다(셀 선택 시 테이블 노드 outline 안 함).
 * 동적 selector에 outline을 입힌다(렌더 트리 무수정).
 */
function SelectionStyles({
  selectedNodeId,
  selectedCells,
  selectedNode,
}: {
  selectedNodeId: string | null;
  selectedCells: CellRef[];
  selectedNode: ComposerNode | null;
}) {
  // CSS attribute selector value 이스케이프(따옴표/백슬래시).
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  if (selectedCells.length > 0) {
    const selectors = selectedCells
      .map((c) => `[data-composer-cell="${esc(`${c.tableId}::${c.row}::${c.col}`)}"]`)
      .join(',');
    return (
      <style>{`${selectors}{outline:2px solid #4f46e5 !important;outline-offset:-1px;}`}</style>
    );
  }
  if (!selectedNodeId) return null;
  const safe = esc(selectedNodeId);
  // 표 노드 선택(셀 미선택) → 표 외곽 + 모든 셀 윤곽을 함께 표시(셀 단위 인지).
  // `::` 구분자로 prefix 충돌 방지(table-1::가 table-12::에 매칭 안 됨).
  if (selectedNode && selectedNode.kind === 'table') {
    return (
      <style>{`[data-composer-cell^="${safe}::"]{outline:1px dashed #818cf8 !important;outline-offset:-1px;}`}</style>
    );
  }
  // 노드 박스 outline은 SelectionOverlay(맨 위 오버레이)가 그린다(이웃에 덮이는 문제 해결).
  return null;
}

/**
 * 여백(spacer) 노드 가시화 — 양식 편집 캔버스에서만 빗금 배경+점선+"여백" 라벨로 표시한다.
 * 출력(web/PDF)은 이 <style>이 없는 RenderTemplate를 쓰므로 무영향(에디터 전용 데코).
 * body+group을 재귀로 훑어 spacer id를 모아 동적 selector로 스타일링한다(렌더 트리 무수정).
 */
function SpacerHints({ body }: { body: ComposerNode[] }) {
  const ids: string[] = [];
  const walk = (nodes: ComposerNode[]) => {
    for (const n of nodes) {
      if (n.kind === 'spacer') ids.push(n.id);
      else if (n.kind === 'group' || n.kind === 'stack') walk(n.children);
      else if (n.kind === 'branch') for (const slot of n.branches) walk(slot.children);
      else if (n.kind === 'repeat') walk([n.template]);
    }
  };
  walk(body);
  if (ids.length === 0) return null;
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  const sel = ids.map((id) => `[data-composer-node-id="${esc(id)}"]`).join(',');
  const after = ids.map((id) => `[data-composer-node-id="${esc(id)}"]::after`).join(',');
  return (
    <style>{`${sel}{background-image:repeating-linear-gradient(45deg,#f8fafc 0 5px,#e2e8f0 5px 10px);outline:1px dashed #cbd5e1;outline-offset:-1px;position:relative;}${after}{content:'여백';position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:9px;color:#94a3b8;letter-spacing:1px;pointer-events:none;white-space:nowrap;}`}</style>
  );
}

/**
 * 반복영역이 **세로 채우기 다단**(`columns≥2` && `flow` 미지정/`'column'`)인지. 세로 채우기는 같은 물리
 * 행에서 각 단(column-copy)이 서로 다른 구간의 데이터라, 각 단의 묶음(groupByCol) 그룹 경계가 행에서
 * 정렬되지 않는다 → 단일 행-레벨 페이지 컷으로 "모든 단 그룹 통째 유지"가 미정의. 가로 채우기(`flow='row'`)는
 * 한 밴드=연속 항목이라 그룹이 물리 행과 정렬돼 단일 단처럼 동작하므로 제외. 이 영역은 groupStartOnNewPage
 * (묶음행 keep-together)를 비활성/무시한다.
 */
function isVerticalMultiColumnRegion(region: TableRepeatRegion): boolean {
  return (region.columns ?? 1) > 1 && (region.flow ?? 'column') === 'column';
}

// PagedEditBody는 팝아웃 미리보기 창과 공유하기 위해 ./previewRender로 추출했다(동작 불변).

/**
 * 다중 셀 일괄 편집기 — 2개 이상 셀이 선택됐을 때 표시한다.
 *
 * 컨트롤 변경 시 onPatch(Partial<TableCell>)를 호출하면 EditorPage가 updateCells로 선택된 모든
 * 셀에 일괄 적용한다(style은 각 셀 기존 style에 머지). 선택된 셀들의 기존 값은 혼합일 수 있으므로
 * 컨트롤에 기존 값을 강제 표시하지 않고("(변경 안 함)"/미결정), *변경 시에만* 일괄 적용한다.
 *
 * 일괄 대상: 폰트/B·I·U/크기/글자색/배경/정렬. text/binding 편집은 단일 셀 전용(제외).
 * B/I/U는 토글 버튼(클릭=전 셀 set, 다시=해제). 글자색/배경은 입력 후 적용 + 해제 버튼 제공.
 */
// 다중 선택 항목들의 getter 값이 모두 같으면 그 값을, 다르면 mixed=true를 반환한다(셀/노드 공통).
// items가 비면 value는 undefined(첫 요소 없음), mixed=false로 둔다(컨트롤이 빈 상태로 표시).
function fold<E, T>(
  items: E[],
  get: (item: E) => T,
): { value: T | undefined; mixed: boolean } {
  if (items.length === 0) return { value: undefined, mixed: false };
  const vals = items.map(get);
  const first = vals[0];
  const mixed = vals.some((v) => !Object.is(v, first));
  return { value: first, mixed };
}
// 셀/노드 전용 별칭 — 콜사이트 무변경(제네릭 E는 인자 타입에서 추론).
const foldCells = fold;
const foldNodes = fold;

// select 옵션에서 "혼합" 상태를 나타내는 sentinel 값(disabled 옵션으로 표시).
const MIXED_OPTION = '__MIXED__';

/** 다중 셀 일괄 바인딩 추가 모드 — 개별(N:N, 셀별 독립 위젯) / 묶음(N:1, 배열 자동매핑 1위젯). */
type BulkBindMode = 'individual' | 'grouped';

/**
 * 단일 셀 스타일 편집 필드(TableCellStyle 공용) — 폰트/이탤릭·밑줄/굵기/크기(pt)/자간/줄간격/글자색/
 * 배경/테두리(+변별)/패딩/기본초기화. 양식 탭 표 셀 단일 뷰와 formTable 고정 셀 단일 뷰가 공유
 * (parity P2.5, 2026-07-13 — formTable 전용 재작성 대신 이 컴포넌트로 통일).
 * style=셀 기존 style. onPatch/onPreview/onCommit은 병합된 **전체** style을 받는다(호출처가 셀에 반영).
 * backgroundKey(데이터 바인딩 배경)는 TableCell 전용이라 backgroundKeySlot으로 주입(formTable은 생략).
 */
function SingleCellStyleFields({
  style,
  heading = '셀 스타일',
  onPatch,
  onPreview,
  onCommit,
  onRevert,
  onReset,
  backgroundKeySlot,
}: {
  style: TableCellStyle;
  heading?: string;
  onPatch: (style: TableCellStyle) => void;
  onPreview: (style: TableCellStyle) => void;
  onCommit: (style: TableCellStyle) => void;
  onRevert: () => void;
  onReset: () => void;
  backgroundKeySlot?: ReactNode;
}) {
  const patchStyle = (next: Partial<TableCellStyle>) => onPatch({ ...style, ...next });
  const patchStyleLive = (next: Partial<TableCellStyle>) => onPreview({ ...style, ...next });
  const patchStyleCommit = (next: Partial<TableCellStyle>) => onCommit({ ...style, ...next });
  const toggleBtn = (active: boolean) =>
    `px-2 py-1 text-xs rounded border ${
      active
        ? 'bg-brand-600 text-white border-brand-600'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`;
  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">{heading}</dt>
      <dd className="space-y-2">
        {/* 폰트 */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">폰트</span>
          <select
            className={SELECT_CLS}
            value={style.fontFamily ?? ''}
            onChange={(e) => patchStyle({ fontFamily: e.target.value || undefined })}
          >
            <option value="">(기본)</option>
            {COMPOSER_FONTS.map((f) => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        {/* 이탤릭 / 밑줄 (볼드는 굵기 드롭다운으로 통합) */}
        <div className="flex gap-1">
          <button
            type="button"
            aria-pressed={!!style.italic}
            title="이탤릭"
            className={`${toggleBtn(!!style.italic)} italic`}
            onClick={() => patchStyle({ italic: style.italic ? undefined : true })}
          >
            I
          </button>
          <button
            type="button"
            aria-pressed={!!style.underline}
            title="밑줄"
            className={`${toggleBtn(!!style.underline)} underline`}
            onClick={() => patchStyle({ underline: style.underline ? undefined : true })}
          >
            U
          </button>
        </div>

        {/* 굵기(font-weight) — B 토글과 공존. */}
        <FontWeightSelect
          value={style.fontWeight}
          onChange={(w) => patchStyle({ fontWeight: w })}
          boldShortcut
        />

        {/* 크기(pt) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">크기(pt)</span>
          <NumericField
            value={style.fontSize ?? undefined}
            min={1}
            step={0.1}
            placeholder="(기본)"
            toDisplay={pxToPt}
            fromDisplay={ptToPx}
            onPreview={(px) => patchStyleLive({ fontSize: px })}
            onCommit={(px) => patchStyleCommit({ fontSize: px })}
            onRevert={onRevert}
          />
        </div>

        {/* 자간(%) */}
        <LetterSpacingInput
          value={style.letterSpacing}
          onPreview={(s) => patchStyleLive({ letterSpacing: s })}
          onCommit={(s) => patchStyleCommit({ letterSpacing: s })}
          onRevert={onRevert}
        />

        {/* 줄간격(line-height) */}
        <LineHeightInput
          value={style.lineHeight}
          onPreview={(s) => patchStyleLive({ lineHeight: s })}
          onCommit={(s) => patchStyleCommit({ lineHeight: s })}
          onRevert={onRevert}
        />

        {/* 글자색 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">글자색</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={style.color ?? '#000000'}
            onChange={(e) => patchStyle({ color: e.target.value })}
          />
          {style.color !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => patchStyle({ color: undefined })}
            >
              초기화
            </button>
          )}
        </div>

        {/* 배경색 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">배경</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={style.background ?? '#ffffff'}
            onChange={(e) => patchStyle({ background: e.target.value })}
          />
          {style.background !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => patchStyle({ background: undefined })}
            >
              초기화
            </button>
          )}
        </div>

        {backgroundKeySlot}

        {/* 테두리 — 선 스타일·두께(mm)·색 분리 입력(4변 공통 base) */}
        <BorderEditor
          value={style.border ?? undefined}
          onChange={(b) => patchStyle({ border: b })}
          onPreview={(b) => patchStyleLive({ border: b })}
          onRevert={onRevert}
        />

        {/* 변별 테두리 — 상/하/좌/우 각 변별 override(비우면 공통 base 상속). 공용 컴포넌트.
            noneValue='hidden' — 셀은 border-collapse라 '없음'을 강제 hidden으로(이웃 셀 테두리까지 억제). */}
        <BorderOverridesEditor
          value={style.borderOverrides}
          onChange={(ov) => patchStyle({ borderOverrides: ov })}
          noneValue="hidden"
          noneSegmentLabel="없음(강제)"
        />

        {/* 대각선(사선) — 「해당 없음」 칸·머리행/열 교차 칸 관례. 방향이 곧 표시 여부(선은 항상 그려짐). */}
        <CellDiagonalEditor
          diagonal={style.diagonal}
          line={style.diagonalLine}
          onPatch={patchStyle}
          onPreview={patchStyleLive}
          onRevert={onRevert}
        />

        {/* 패딩(mm) — 4방향 */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">패딩(mm)</span>
          <BoxSpacingFields
            value={style.padding}
            onChange={(v) => patchStyle({ padding: v })}
            onPreview={(v) => patchStyleLive({ padding: v })}
            onRevert={onRevert}
          />
        </div>

        {/* 기본 셀 스타일로 초기화(테두리+패딩 기본값, 그 외 스타일 제거) */}
        <button
          type="button"
          onClick={onReset}
          title="이 셀의 스타일을 기본값(실선 테두리 + 패딩)으로 되돌립니다"
          className="text-[11px] text-brand-600 hover:underline self-start"
        >
          기본값으로 초기화
        </button>
      </dd>
    </div>
  );
}

/**
 * 다중 셀 일괄 스타일 편집 필드(TableCellStyle 공용, tri-state) — 폰트/이탤릭·밑줄/굵기/크기/자간/
 * 줄간격/글자색/배경/테두리/패딩/기본초기화. 양식 탭 BulkCellEditor와 formTable 다중 셀 뷰가 공유
 * (parity P2.5). styles=선택 셀들의 style 배열 → 각 속성 fold로 혼합(–) 판정. 변경 시 onPatch(next)
 * (Partial<TableCellStyle>) — 호출처가 각 셀 기존 style에 머지 적용한다.
 * 변별 테두리(BorderOverridesEditor)는 원본 BulkCellEditor와 동일하게 미노출(공통 base border만).
 */
function BulkCellStyleFields({
  styles,
  heading = '셀 스타일 일괄 적용',
  onPatch,
  onPreview,
  onCommit,
  onRevert,
}: {
  styles: (TableCellStyle | undefined)[];
  heading?: string;
  onPatch: (next: Partial<TableCellStyle>) => void;
  onPreview: (next: Partial<TableCellStyle>) => void;
  onCommit: (next: Partial<TableCellStyle>) => void;
  onRevert: () => void;
}) {
  const patchStyle = onPatch;
  const patchStyleLive = onPreview;
  const patchStyleCommit = onCommit;
  const revertPatch = onRevert;
  const fontWeight = fold(styles, (s) => s?.fontWeight ?? null);
  const italic = fold(styles, (s) => !!s?.italic);
  const underline = fold(styles, (s) => !!s?.underline);
  const fontFamily = fold(styles, (s) => s?.fontFamily ?? '');
  const fontSize = fold(styles, (s) => s?.fontSize ?? null);
  const letterSpacing = fold(styles, (s) => s?.letterSpacing ?? null);
  const lineHeight = fold(styles, (s) => s?.lineHeight ?? null);
  const color = fold(styles, (s) => s?.color ?? '');
  const background = fold(styles, (s) => s?.background ?? '');
  const border = fold(styles, (s) => s?.border ?? '');
  // 패딩은 4방향(BoxSpacing). 표시값: 모든 셀이 동일한 side만 채우고, 혼합 side는 비워 둔다.
  const bulkPad: BoxPad = {};
  for (const side of ['top', 'right', 'bottom', 'left'] as const) {
    const f = fold(styles, (s) => s?.padding?.[side] ?? null);
    if (!f.mixed && f.value != null) bulkPad[side] = f.value;
  }
  const bulkPadValue = Object.keys(bulkPad).length > 0 ? bulkPad : undefined;
  // B/I/U tri-state 토글: 균일 ON이면 클릭 시 전체 해제, 그 외(OFF/혼합)면 전체 ON.
  const toggleClass = (on: boolean, mixed: boolean, extra: string) =>
    [
      'px-2 py-1 text-xs rounded border min-w-[2rem]',
      extra,
      on
        ? 'bg-brand-500 text-white border-brand-500'
        : mixed
          ? 'bg-white text-gray-700 border-dashed border-gray-400 opacity-80'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
    ].join(' ');
  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">{heading}</dt>
      <dd className="space-y-2">
        {/* 폰트 */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">폰트</span>
          <select
            className={SELECT_CLS}
            value={fontFamily.mixed ? MIXED_OPTION : (fontFamily.value ?? '')}
            onChange={(e) => {
              if (e.target.value === MIXED_OPTION) return;
              patchStyle({ fontFamily: e.target.value || undefined });
            }}
          >
            {fontFamily.mixed && (
              <option value={MIXED_OPTION} disabled>
                — 혼합 —
              </option>
            )}
            <option value="">(기본)</option>
            {COMPOSER_FONTS.map((f) => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        {/* 이탤릭 / 밑줄 — tri-state 토글 (볼드는 굵기 드롭다운으로 통합) */}
        <div className="flex gap-1">
          <button
            type="button"
            title={italic.mixed ? '이탤릭: 혼합 (클릭 시 전체 적용)' : '이탤릭'}
            className={toggleClass(!!italic.value && !italic.mixed, italic.mixed, 'italic')}
            onClick={() =>
              patchStyle({
                italic: italic.value && !italic.mixed ? undefined : true,
              })
            }
          >
            {italic.mixed ? 'I–' : 'I'}
          </button>
          <button
            type="button"
            title={underline.mixed ? '밑줄: 혼합 (클릭 시 전체 적용)' : '밑줄'}
            className={toggleClass(!!underline.value && !underline.mixed, underline.mixed, 'underline')}
            onClick={() =>
              patchStyle({
                underline: underline.value && !underline.mixed ? undefined : true,
              })
            }
          >
            {underline.mixed ? 'U–' : 'U'}
          </button>
        </div>

        {/* 굵기(font-weight) — tri-state. B 토글과 공존. */}
        <FontWeightSelect
          value={fontWeight.mixed ? undefined : (fontWeight.value ?? undefined)}
          mixed={fontWeight.mixed}
          onChange={(w) => patchStyle({ fontWeight: w })}
          boldShortcut
        />

        {/* 크기(pt) — 실시간 미리보기 + Enter/blur 확정 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">크기(pt)</span>
          <NumericField
            value={fontSize.value ?? undefined}
            mixed={fontSize.mixed}
            min={1}
            step={0.1}
            placeholder="pt"
            toDisplay={pxToPt}
            fromDisplay={ptToPx}
            onPreview={(px) => patchStyleLive({ fontSize: px })}
            onCommit={(px) => patchStyleCommit({ fontSize: px })}
            onRevert={revertPatch}
          />
        </div>

        {/* 자간(%) — tri-state */}
        <LetterSpacingInput
          value={letterSpacing.mixed ? undefined : (letterSpacing.value ?? undefined)}
          mixed={letterSpacing.mixed}
          onPreview={(s) => patchStyleLive({ letterSpacing: s })}
          onCommit={(s) => patchStyleCommit({ letterSpacing: s })}
          onRevert={revertPatch}
        />

        {/* 줄간격(line-height) — tri-state */}
        <LineHeightInput
          value={lineHeight.mixed ? undefined : (lineHeight.value ?? undefined)}
          mixed={lineHeight.mixed}
          onPreview={(s) => patchStyleLive({ lineHeight: s })}
          onCommit={(s) => patchStyleCommit({ lineHeight: s })}
          onRevert={revertPatch}
        />

        {/* 글자색 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">글자색</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={color.mixed || !color.value ? '#000000' : color.value}
            onChange={(e) => patchStyle({ color: e.target.value })}
          />
          {color.mixed && <span className="text-[11px] text-gray-400">(혼합)</span>}
          <button
            type="button"
            className="text-[11px] text-gray-500 underline"
            onClick={() => patchStyle({ color: undefined })}
          >
            초기화
          </button>
        </div>

        {/* 배경색 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">배경</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={background.mixed || !background.value ? '#ffffff' : background.value}
            onChange={(e) => patchStyle({ background: e.target.value })}
          />
          {background.mixed && <span className="text-[11px] text-gray-400">(혼합)</span>}
          <button
            type="button"
            className="text-[11px] text-gray-500 underline"
            onClick={() => patchStyle({ background: undefined })}
          >
            초기화
          </button>
        </div>

        {/* 테두리 — 선 스타일·두께(mm)·색 분리 입력 */}
        <BorderEditor
          value={border.mixed ? undefined : (border.value ?? undefined)}
          mixed={border.mixed}
          onChange={(b) => patchStyle({ border: b })}
          onPreview={(b) => patchStyleLive({ border: b })}
          onRevert={onRevert}
        />

        {/* 패딩(mm) — 4방향. 변경 시 모든 셀에 동일 적용(혼합 side는 0으로 균등화). */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">패딩(mm)</span>
          <BoxSpacingFields
            value={bulkPadValue}
            onChange={(v) => patchStyle({ padding: v })}
            onPreview={(v) => patchStyleLive({ padding: v })}
            onRevert={onRevert}
          />
        </div>

        {/* 기본 셀 스타일로 초기화 — 선택 셀 전체를 기본값(테두리+패딩)으로, 그 외 스타일 제거 */}
        <button
          type="button"
          onClick={() =>
            patchStyle({
              ...defaultCellStyle(),
              fontWeight: undefined,
              background: undefined,
              fontFamily: undefined,
              fontSize: undefined,
              letterSpacing: undefined,
              lineHeight: undefined,
              italic: undefined,
              underline: undefined,
              color: undefined,
            })
          }
          className="text-[11px] text-brand-600 hover:underline"
        >
          기본값으로 초기화
        </button>
      </dd>
    </div>
  );
}

function BulkCellEditor({
  cells,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
  onBindingFormat,
  onBindingAssign,
  onClearCells,
  onComputeAssign,
  onClearCompute,
  computeAvailableBindings,
  onHighlightSource,
  onStartCellPick,
  onCancelCellPick,
  activePickToken,
  catalog,
  onBack,
  onMergeCells,
  canMergeCells,
  keyOptions,
}: PreviewPatchHandlers<Partial<TableCell>> & {
  cells: TableCell[];
  onPatch: (patch: Partial<TableCell>) => void;
  /** 있으면 "바인딩 일괄" 섹션 표시 — 선택 셀들의 binding.format만 일괄 적용(select/경로는 보존·읽기). */
  onBindingFormat?: (format: BindingFormat | undefined) => void;
  /** 있으면 "바인딩 추가" 섹션 표시 — 출처를 한 번 지정해 모드별(개별/묶음)로 선택 셀에 일괄 바인딩. */
  onBindingAssign?: (binding: Binding, mode: BulkBindMode) => void;
  /** 있으면 "바인딩 추가" 패널에 "선택 셀 바인딩 제거" 버튼 표시 — 선택 셀들의 binding만 일괄 해제. */
  onClearCells?: () => void;
  /** 있으면 "계산 추가" 섹션 표시 — 개별로 선택 셀에 일괄 compute 적용. */
  onComputeAssign?: (draft: CellCompute) => void;
  /** 있으면 "계산 추가" 패널에 "선택 셀 계산 제거" 버튼 표시 — 선택 셀들의 compute만 일괄 해제. */
  onClearCompute?: () => void;
  /** 계산 추가(개별) "기존 값" 콤보 후보 — 보고서 내 다른 바인딩. */
  computeAvailableBindings?: AvailableBinding[];
  /** R2 — 계산 추가(개별) "기존 값" 콤보 호버 시 캔버스 셀 강조(단일 셀과 동형). */
  onHighlightSource?: (availableBindingId: string | null) => void;
  /** R3 — 계산 추가(개별)에서 캔버스 셀 클릭 픽(단일 셀과 동형). */
  onStartCellPick?: (token: string, apply: (binding: Binding) => void) => void;
  onCancelCellPick?: () => void;
  activePickToken?: string | null;
  /** 바인딩 일괄 섹션의 출처 라벨 표시용 카탈로그(onBindingFormat과 함께 전달). */
  catalog?: CatalogEntry[];
  /** 있으면 "표로 돌아가기" 버튼 표시(셀 다중선택 모드). 없으면 표 패널 인라인(전체 셀) 모드. */
  onBack?: () => void;
  /** 다중선택 모드에서 직사각 범위 병합 실행(있으면 "선택 영역 병합" 버튼 표시). */
  onMergeCells?: () => void;
  /** 직사각 범위 병합 가능 여부(canMergeCellsRect 결과). false면 버튼 disabled. */
  canMergeCells?: boolean;
  /**
   * 표시 형식 일괄의 경로 칸(병기 값·구간 키) 후보 — 선택 셀 **전원이 같은 값 스코프**일 때만
   * 전달된다(tableCellRowFieldOptions가 판정). 스코프가 갈리면 빈 목록이라 자유 텍스트로 degrade —
   * 일괄 적용이 한쪽 스코프에만 있는 경로를 다른 셀에 박아 넣지 않게 한다.
   */
  keyOptions?: HybridKeyOption[];
}) {
  // ── 바인딩 일괄(format만) — onBindingFormat이 있을 때만 ──
  // 바인딩된 셀이 1개 이상일 때 섹션 표시. 출처/데이터 경로는 읽기(혼합 시 '—'), 표시 형식만 일괄 적용.
  const boundCount = cells.filter((c) => c.binding).length;
  const bindCatalog = foldCells(cells, (c) => c.binding?.catalogKey ?? '');
  const bindSelect = foldCells(cells, (c) => c.binding?.select ?? '');
  const bindFormatKind = foldCells(cells, (c) => c.binding?.format?.kind ?? 'none');
  // FormatFields 편집용 공통 format — 바인딩된 셀들의 format이 완전히 동일할 때만 그 객체, 아니면 kind 기본값(혼합 kind면 미표시).
  const bindFormatJson = foldCells(
    cells.filter((c) => c.binding),
    (c) => JSON.stringify(c.binding?.format ?? null),
  );
  const commonFormat: BindingFormat | undefined =
    !bindFormatJson.mixed && bindFormatJson.value && bindFormatJson.value !== 'null'
      ? (JSON.parse(bindFormatJson.value) as BindingFormat)
      : undefined;
  const formatForFields: BindingFormat | undefined = bindFormatKind.mixed
    ? undefined
    : commonFormat ??
      (bindFormatKind.value && bindFormatKind.value !== 'none'
        ? buildDefaultFormat(bindFormatKind.value)
        : undefined);

  // 속성별 균일/혼합 계산(정규화된 getter 사용).
  // 콘텐츠 정렬 tri-state. legacy align은 contentAlignH로 흡수해 표시(편집 시 align 제거).
  const contentAlignH = foldCells(cells, (c) => c.contentAlignH ?? '');
  const contentAlignV = foldCells(cells, (c) => c.contentAlignV ?? '');

  return (
    <dl className="space-y-3 text-sm">
      <div className="flex items-center justify-between">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            ← 표로 돌아가기
          </button>
        ) : (
          <span className="text-xs font-semibold text-gray-500">전체 셀 일괄 스타일</span>
        )}
        <span className="text-xs text-gray-500 font-semibold">
          {onBack ? `다중 셀 (${cells.length}개 선택)` : `${cells.length}개 셀`}
        </span>
      </div>

      <p className="text-[11px] text-gray-400 leading-relaxed">
        {onBack
          ? '선택한 모든 셀에 스타일이 일괄 적용됩니다. 값이 셀마다 다르면 혼합(–) 상태로 표시됩니다. 텍스트/바인딩은 셀을 하나만 선택했을 때 편집할 수 있습니다.'
          : '표의 모든 셀에 스타일이 일괄 적용됩니다. 값이 셀마다 다르면 혼합(–) 상태로 표시됩니다.'}
      </p>

      {/* 직사각 범위 병합(Excel/HWP식) — 같은 표의 직사각 범위 전체가 1×1 셀일 때만 활성. */}
      {onMergeCells && (
        <button
          type="button"
          onClick={onMergeCells}
          disabled={!canMergeCells}
          title={canMergeCells ? '선택한 직사각 범위를 하나로 병합 (Ctrl+M)' : '직사각 범위(병합되지 않은 셀)만 병합할 수 있습니다'}
          className={[
            'w-full px-2 py-1.5 text-xs rounded border',
            canMergeCells
              ? 'border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100'
              : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed',
          ].join(' ')}
        >
          선택 영역 병합 <span className="text-gray-400">(Ctrl+M)</span>
        </button>
      )}

      {/* 콘텐츠 정렬 일괄(가로 contentAlignH × 세로 contentAlignV) — 노드와 동일한 9방향 그리드.
          혼합(mixed)이면 그리드는 미선택(undefined)으로 표시되고, 선택 시 전체 셀에 일괄 적용된다. */}
      <CellContentAlignEditor
        label="셀 정렬 일괄"
        valueH={contentAlignH.mixed ? undefined : (contentAlignH.value || undefined)}
        valueV={contentAlignV.mixed ? undefined : (contentAlignV.value || undefined)}
        mixedH={contentAlignH.mixed}
        mixedV={contentAlignV.mixed}
        onPick={(h, v) =>
          onPatch({
            contentAlignH: (h === 'start' ? undefined : h) as TableCell['contentAlignH'],
            contentAlignV: (v === 'start' ? undefined : v) as TableCell['contentAlignV'],
          })
        }
      />

      {/* 스타일 일괄 적용 — 공용 컴포넌트(양식 탭·formTable 다중 뷰 공유, parity P2.5). */}
      <BulkCellStyleFields
        styles={cells.map((c) => c.style)}
        onPatch={(next) => onPatch({ style: next })}
        onPreview={(next) => onPreview({ style: next })}
        onCommit={(next) => onCommit({ style: next })}
        onRevert={onRevert}
      />

      {/* 바인딩 추가 — 출처를 한 번 지정해 선택 셀에 일괄 바인딩(개별 N:N / 묶음 N:1 자동매핑). */}
      {onBindingAssign && catalog && (
        <BulkBindingAssignPanel
          cellCount={cells.length}
          cells={cells}
          catalog={catalog}
          onAssign={onBindingAssign}
          onClearCells={onClearCells}
          hasAnyBinding={cells.some((c) => !!c.binding)}
        />
      )}

      {/* 계산 추가 — 개별로 선택 셀에 compute 일괄 적용. */}
      {onComputeAssign && catalog && (
        <BulkComputeAssignPanel
          cellCount={cells.length}
          cells={cells}
          catalog={catalog}
          onAssign={onComputeAssign}
          onClearCells={onClearCompute}
          hasAnyCompute={cells.some((c) => !!c.compute)}
          availableBindings={computeAvailableBindings}
          onHighlightSource={onHighlightSource}
          onStartCellPick={onStartCellPick}
          onCancelCellPick={onCancelCellPick}
          activePickToken={activePickToken}
        />
      )}

      {/* 바인딩 일괄 — 표시 형식만 일괄 적용(출처/데이터 경로는 칸별이라 읽기 표시만). 바인딩된 셀 있을 때만. */}
      {onBindingFormat && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">바인딩 일괄</dt>
          {boundCount === 0 ? (
            <dd className="text-[11px] text-gray-400">선택한 칸에 바인딩 없음</dd>
          ) : (
            <dd className="space-y-2">
              {/* 출처(읽기) */}
              <div>
                <span className="text-[11px] text-gray-500 block mb-0.5">출처</span>
                <span className="text-xs text-gray-500">
                  {bindCatalog.mixed
                    ? '—'
                    : catalog?.find((e) => e.key === bindCatalog.value)?.label ??
                      (bindCatalog.value || '(없음)')}
                </span>
              </div>

              {/* 데이터 경로(읽기) */}
              <div>
                <span className="text-[11px] text-gray-500 block mb-0.5">데이터 경로</span>
                <span className="text-xs text-gray-500 break-all">
                  {bindSelect.mixed ? '—' : bindSelect.value || '(전체/없음)'}
                </span>
                <span className="text-[11px] text-gray-400 block">칸마다 다를 수 있음(개별 편집)</span>
              </div>

              {/* 표시 형식(편집) — kind 일괄 + FormatFields 하위 필드 일괄 */}
              <div>
                <span className="text-[11px] text-gray-500 block mb-0.5">표시 형식</span>
                <select
                  className={SELECT_CLS}
                  value={bindFormatKind.mixed ? MIXED_OPTION : (bindFormatKind.value ?? 'none')}
                  onChange={(e) => {
                    if (e.target.value === MIXED_OPTION) return;
                    onBindingFormat(
                      buildDefaultFormat(e.target.value as BindingFormat['kind'] | 'none', formatForFields),
                    );
                  }}
                >
                  {bindFormatKind.mixed && (
                    <option value={MIXED_OPTION} disabled>
                      — 혼합 —
                    </option>
                  )}
                  {FORMAT_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {formatKindOptionLabel(k)}
                    </option>
                  ))}
                </select>
                {/* 하위 필드(unit 값 등) — 공통 format이 있을 때만(혼합 kind면 미표시). 변경 시 전 셀 일괄 적용. */}
                <FormatFields
                  format={formatForFields}
                  onChange={(format) => onBindingFormat(format)}
                  catalog={catalog}
                  keyOptions={keyOptions}
                />
              </div>

              <p className="text-[11px] text-gray-400 leading-relaxed">
                {boundCount}/{cells.length}칸 바인딩됨 · 표시 형식만 일괄 적용(데이터 경로는 칸별)
              </p>
            </dd>
          )}
        </div>
      )}
    </dl>
  );
}

/**
 * 다중 셀 일괄 "바인딩 추가" 패널 — 단일 셀과 동일한 출처/경로 선택 UI(BindingEditor)를 재사용해
 * 임시 바인딩(draft)을 구성한 뒤, 모드별로 선택 셀 전체에 일괄 적용한다(handleBulkBindingAssign).
 *  - 개별(N:N): 같은 출처·경로, bindingKey만 셀별 유니크 → 작성폼 위젯이 셀 수만큼(셀별 독립 입력).
 *  - 묶음(N:1): 배열 출처(cardinality 'list')를 셀 영역에 자동 매핑(행=항목 인덱스, 열=필드) →
 *    bindingKey 1개 공유로 작성폼 위젯 1개(배열 입력). 배열 아닌 출처에서는 비활성 + 안내.
 */
function BulkBindingAssignPanel({
  cellCount,
  cells,
  catalog,
  onAssign,
  onClearCells,
  hasAnyBinding,
}: {
  cellCount: number;
  /** 선택 셀들(각 binding 포함) — 현재 바인딩을 패널에 복원/혼합(tri-state) 표시하기 위함. */
  cells: TableCell[];
  catalog: CatalogEntry[];
  onAssign: (binding: Binding, mode: BulkBindMode) => void;
  /** 있으면 "선택한 N개 셀의 바인딩 제거" 버튼 표시 — 선택 셀들의 binding만 일괄 해제(text/compute 보존). */
  onClearCells?: () => void;
  /** 선택 셀 중 binding 있는 셀이 하나라도 있는지 — 제거 버튼 활성/비활성을 셀 상태와 동기화. */
  hasAnyBinding?: boolean;
}) {
  // 비어 있는 임시 바인딩(출처 미선택) — BindingEditor가 곧장 출처 콤보를 노출하도록 초기화.
  const emptyDraft = (): Binding => ({ bindingKey: 'bulk-binding-draft', lifecycle: 'snapshot' });

  // ── 선택 셀들의 공통 바인딩 산출(복원/혼합 표시용) ──
  // catalogKey/select/bindingKey를 foldCells로 균일/혼합 판정. 바인딩 없는 셀은 ''로 접혀 혼합 처리됨.
  const catFold = foldCells(cells, (c) => c.binding?.catalogKey ?? '');
  const commonCatalogKey = !catFold.mixed && catFold.value ? catFold.value : undefined;
  const selFold = foldCells(cells, (c) => c.binding?.select ?? '');
  const commonSelect = !selFold.mixed && selFold.value ? selFold.value : undefined;
  const keyFold = foldCells(cells, (c) => c.binding?.bindingKey ?? '');
  // 일부 셀에 바인딩이 있으나 공통 출처조차 안 되면 혼합(출처 placeholder "— 혼합 —").
  const isMixed = cells.some((c) => !!c.binding) && !commonCatalogKey;
  // 출처는 동일한데 셀마다 select 경로가 제각각이면 select 세그먼트(레벨) 단위로 비교해
  // 일치하는 레벨은 선택 유지, 갈라지는 레벨만 '-'/혼합으로 표시한다(commonCatalogKey 존재 =
  // 모든 셀이 같은 출처로 바인딩됨 보장 → selFold.mixed가 곧 select 혼합).
  const mixEntry = commonCatalogKey ? catalog.find((e) => e.key === commonCatalogKey) : undefined;
  const selectMix = useMemo<SelectMix | undefined>(() => {
    if (!commonCatalogKey || !selFold.mixed || !mixEntry) return undefined;
    return computeSelectMix(mixEntry, cells.map((c) => c.binding?.select ?? ''));
  }, [commonCatalogKey, selFold.mixed, mixEntry, cells]);
  // bindingKey 패턴으로 모드 추론 — 동일 `*-group` 키=묶음 / 셀별 유니크 키=개별 / 혼합·미바인딩=미정(undefined).
  const inferredMode: BulkBindMode | undefined = commonCatalogKey
    ? !keyFold.mixed && keyFold.value?.endsWith('-group')
      ? 'grouped'
      : 'individual'
    : undefined;

  // 공통값 → 복원 draft/mode(셀 기존 바인딩을 패널에 표시). 첫 바인딩 셀에서 format 등 공통 속성을 보존하되
  // 셀별 유니크 부분(bindingKey)·묶음의 select는 일반화한다. commonCatalogKey가 있으면 모든 셀이 바인딩됨이 보장.
  const buildRestoredDraft = (): Binding => {
    if (!commonCatalogKey) return emptyDraft();
    const firstBound = cells.find((c) => c.binding)?.binding;
    const base = commonSelect ? `${commonCatalogKey}.${commonSelect}` : commonCatalogKey;
    return {
      ...firstBound,
      catalogKey: commonCatalogKey,
      select: commonSelect,
      bindingKey: base,
      lifecycle: firstBound?.lifecycle ?? 'snapshot',
    };
  };

  // 적용 방식 라디오는 항상 하나가 선택된 상태 — 모드가 불명확(혼합)해도 개별을 기본 선택한다.
  const [mode, setMode] = useState<BulkBindMode>(() => inferredMode ?? 'individual');
  const [draft, setDraft] = useState<Binding | undefined>(buildRestoredDraft);

  // BindingEditor가 요구하는 최소 ComposerNode 형태(임시 draft 바인딩 보유 — CellBindingEditor와 동형).
  const fakeNode = {
    id: 'bulk-binding-draft',
    kind: 'text',
    layout: { width: 'fill' },
    binding: draft,
  } as ComposerNode;

  const draftKey = draft?.catalogKey;
  const draftEntry = draftKey ? catalog.find((e) => e.key === draftKey) : undefined;
  const isListSource = draftEntry?.cardinality === 'list';
  // 적용 모드 — 항상 하나가 선택됨(혼합/미정은 개별 기본). effMode는 적용 발화에 사용.
  const effMode: BulkBindMode = mode;
  // 적용 가능: 출처가 선택됐고, 묶음 모드면 배열(list) 출처여야 함.
  const canApply = !!draft?.catalogKey && (effMode === 'individual' || isListSource);
  // 선택 셀들의 모드가 제각각이라 개별을 기본 선택했음을 안내(라디오는 개별로 선택된 상태).
  const modeAmbiguous = inferredMode === undefined && cells.some((c) => !!c.binding);

  // 단일 셀과 동일하게 "출처를 지정하는 순간 즉시 반영" — 별도 적용 버튼 없이 draft/mode가 바뀔 때마다
  // 선택 셀 전체에 onAssign(또는 출처 비움 시 onClearCells)을 발화한다.
  //  - 콜백(onAssign/onClearCells)은 selectedCells 의존이라 매번 새로 생기므로 ref로 최신값만 유지하고,
  //    effect 의존은 draft 직렬화 + mode 값만 둔다 → 사용자가 출처/경로/모드를 바꿀 때만 발화.
  //  - 복원(아래 useEffect)으로 set된 경우는 발화하면 안 됨 → 복원 시그니처를 lastSyncedSigRef에 기록하고
  //    현재 시그니처가 그 값과 같으면 즉시반영을 스킵한다(셀 값 표시 ≠ 재적용).
  const onAssignRef = useRef(onAssign);
  const onClearCellsRef = useRef(onClearCells);
  onAssignRef.current = onAssign;
  onClearCellsRef.current = onClearCells;
  const didMountRef = useRef(false);
  const prevHadKeyRef = useRef(!!draft?.catalogKey); // 직전에 출처가 있었는지 — 출처 비움(제거) 발화 판정용.
  const draftSig = JSON.stringify(draft ?? null);
  const curSig = `${draftSig}|${mode}`;
  // 마지막 복원 동기화로 set된 (draft,mode) 시그니처 — curSig가 이 값과 같은 동안은 즉시반영을 발화하지 않음.
  const lastSyncedSigRef = useRef<string>(curSig);

  // ── 선택 변경 시 draft/mode 복원(적용과 구분) ──
  // 선택 셀들의 바인딩 시그니처가 바뀌면 공통값으로 draft/mode를 되돌린다. 복원은 onAssign을 발화하면 안 되므로
  // 복원한 시그니처를 lastSyncedSigRef에 먼저 기록한다(아래 즉시반영 effect가 curSig 일치 시 스킵).
  // 적용→template 갱신→cells 변경→복원이 다시 도는 루프는, 방금 적용한 값과 복원값이 동일해(또는 시그니처 일치)
  // 즉시반영이 스킵되므로 안정적으로 수렴한다.
  const cellsBindingSig = JSON.stringify(cells.map((c) => c.binding ?? null));
  useEffect(() => {
    const d = buildRestoredDraft();
    const m = inferredMode ?? 'individual';
    lastSyncedSigRef.current = `${JSON.stringify(d)}|${m}`;
    prevHadKeyRef.current = !!d.catalogKey;
    setDraft(d);
    setMode(m);
  }, [cellsBindingSig]);

  useEffect(() => {
    // 초기 마운트는 스킵 — 복원 초기값으로 onAssign/onClearCells가 발화하지 않게 한다.
    if (!didMountRef.current) {
      didMountRef.current = true;
      prevHadKeyRef.current = !!draft?.catalogKey;
      return;
    }
    // 복원(셀 값 표시)으로 set된 상태 — 재적용하지 않는다.
    if (curSig === lastSyncedSigRef.current) {
      prevHadKeyRef.current = !!draft?.catalogKey;
      return;
    }
    if (draft?.catalogKey) {
      // 적용 가능할 때만(묶음 모드면 배열 출처여야 함 — canApply 재사용).
      if (canApply) onAssignRef.current(draft, effMode);
      prevHadKeyRef.current = true;
    } else {
      // 출처를 비웠고 직전엔 출처가 있었으면 → 선택 셀 바인딩 제거(단일 셀: 출처 비우기=제거와 일관).
      if (prevHadKeyRef.current) onClearCellsRef.current?.();
      prevHadKeyRef.current = false;
    }
    // draftSig/mode 값에만 의존(콜백은 ref로 최신값 유지). draft/canApply/effMode/curSig는 closure 최신값 사용.
  }, [draftSig, mode]);

  return (
    <div className="rounded border border-brand-100 bg-brand-50/30 p-2">
      <div className="text-xs font-semibold text-brand-700">바인딩 추가 (선택 {cellCount}칸)</div>

      <div className="mt-2 space-y-2">
          {/* 모드 토글 — 출처 선택 전에 적용 방식을 고른다. 선택 셀들의 모드가 제각각이어도
              라디오는 항상 하나가 선택됨(개별 기본). 모드가 불명확하면 별도 작은 라벨로 안내한다. */}
          <div>
            <span className="text-[11px] text-gray-500 block mb-0.5">
              적용 방식
              {modeAmbiguous && (
                <span className="ml-1 text-gray-400">— 셀마다 다름(개별 기본) —</span>
              )}
            </span>
            <div className="flex flex-col gap-1">
              <label className="inline-flex items-start gap-1.5 text-xs cursor-pointer">
                <input
                  type="radio"
                  name="bulk-bind-mode"
                  checked={mode === 'individual'}
                  onChange={() => setMode('individual')}
                  className="mt-0.5 shrink-0"
                />
                <span>
                  <span className="font-medium">개별</span>
                  <span className="text-gray-400">
                    {' '}
                    — 칸마다 같은 출처를 독립 입력(작성폼 {cellCount}개)
                  </span>
                </span>
              </label>
              <label className="inline-flex items-start gap-1.5 text-xs cursor-pointer">
                <input
                  type="radio"
                  name="bulk-bind-mode"
                  checked={mode === 'grouped'}
                  onChange={() => setMode('grouped')}
                  className="mt-0.5 shrink-0"
                />
                <span>
                  <span className="font-medium">묶음</span>
                  <span className="text-gray-400">
                    {' '}
                    — 배열 출처를 영역에 자동매핑(행=항목, 열=필드, 작성폼 1개)
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* 출처/경로 선택 — 단일 셀과 동일 BindingEditor 재사용(임시 draft에 반영). */}
          {/* hideRemoveButton: draft는 임시 입력이라 "바인딩 제거"가 셀에 반영되지 않아 오해를 부른다 →
              제거는 아래 "선택 셀 바인딩 제거" 버튼(실제 셀 반영)으로만 노출. */}
          <BindingEditor
            node={fakeNode}
            catalog={catalog}
            hideRemoveButton
            sourcePlaceholder={isMixed ? '— 혼합 —' : undefined}
            hideSelectPath={mode === 'grouped'}
            selectMix={selectMix}
            onPatch={(patch) => {
              if ('binding' in patch) {
                setDraft((patch as { binding?: Binding }).binding);
              }
            }}
          />

          {/* 묶음 모드인데 배열 출처가 아니면 비활성 + 안내. */}
          {mode === 'grouped' && draft?.catalogKey && !isListSource && (
            <p className="text-[11px] text-amber-600 leading-relaxed">
              묶음 매핑은 배열(목록) 출처에서만 가능합니다. 개별 모드를 쓰거나 목록 출처를 선택하세요.
            </p>
          )}
          {/* 개별 모드 + 배열 출처 함정 안내(자동 전환 대신 사용자 선택 존중 + 경고). */}
          {mode === 'individual' && isListSource && (
            <p className="text-[11px] text-amber-600 leading-relaxed">
              배열(목록) 출처는 묶음 모드를 권장합니다. 개별 모드는 각 셀이 항목 전체를 가리켜 표시되지 않을 수 있습니다.
            </p>
          )}

          {/* 적용 버튼 없음 — 단일 셀과 동일하게 출처/경로를 지정하는 순간 선택 셀에 즉시 반영된다.
              출처를 비우면 선택 셀의 바인딩이 함께 제거된다. */}
          <p className="text-[11px] text-gray-400 leading-relaxed">
            출처를 지정하면 선택한 {cellCount}개 셀에 즉시 반영됩니다.
          </p>

          {/* 선택 셀의 바인딩만 일괄 제거(실제 셀 반영, undo 대상) — draft의 출처 비우기와 구분된다. */}
          {onClearCells && (
            <button
              type="button"
              onClick={onClearCells}
              disabled={!hasAnyBinding}
              className={[
                'w-full px-2 py-1.5 text-xs rounded border',
                hasAnyBinding
                  ? 'border-red-300 bg-white text-red-600 hover:bg-red-50'
                  : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed',
              ].join(' ')}
            >
              선택한 {cellCount}개 셀의 바인딩 제거
            </button>
          )}
        </div>
    </div>
  );
}

/**
 * 다중 셀 일괄 "계산(compute) 추가" 패널 — 개별 고정(단일 셀 CellComputeEditor로 draft를 구성해
 * 모든 선택 셀에 통째 복제). 묶음(grouped)은 제거됨 — 셀 픽 가능한 단일+개별이 행별 "계"를 더 명확히 처리한다.
 * 적용 버튼 없이 draft가 유효해질 때마다 즉시 반영(입력/수식 비우면 계산 제거).
 */
function BulkComputeAssignPanel({
  cellCount,
  cells,
  catalog,
  onAssign,
  onClearCells,
  hasAnyCompute,
  availableBindings,
  onHighlightSource,
  onStartCellPick,
  onCancelCellPick,
  activePickToken,
}: {
  cellCount: number;
  /** 선택 셀들(각 compute 포함) — 공통 compute를 패널에 복원/혼합 표시하기 위함. */
  cells: TableCell[];
  catalog: CatalogEntry[];
  onAssign: (draft: CellCompute) => void;
  /** 있으면 "선택 셀 계산 제거" 버튼 표시 — 선택 셀들의 compute만 일괄 해제(text/binding 보존). */
  onClearCells?: () => void;
  /** 선택 셀 중 compute 있는 셀이 하나라도 있는지 — 제거 버튼 활성/비활성 동기화. */
  hasAnyCompute?: boolean;
  /** CellComputeEditor의 "기존 값" 콤보 후보(단일 셀과 동일 UX). */
  availableBindings?: AvailableBinding[];
  /** R2 — "기존 값" 콤보 옵션 호버 시 해당 바인딩의 캔버스 셀 강조(단일 셀과 동형). */
  onHighlightSource?: (availableBindingId: string | null) => void;
  /** R3 — 캔버스 셀 직접 클릭으로 계산 입력 지정(단일 셀과 동형). */
  onStartCellPick?: (token: string, apply: (binding: Binding) => void) => void;
  onCancelCellPick?: () => void;
  activePickToken?: string | null;
}) {
  // 선택 셀들의 공통 compute(복원용) — 모두 직렬화 동일하면 그 값, 아니면 혼합(기본 draft).
  const computeFold = foldCells(cells, (c) => JSON.stringify(c.compute ?? null));
  const commonCompute: CellCompute | undefined =
    !computeFold.mixed && computeFold.value && computeFold.value !== 'null'
      ? (JSON.parse(computeFold.value) as CellCompute)
      : undefined;
  const buildRestoredDraft = (): CellCompute =>
    commonCompute ?? { inputs: [], mode: 'op', op: 'sum' };

  // draft(단일 CellComputeEditor 편집 대상). 계산 제거 시 undefined 가능.
  const [draft, setDraft] = useState<CellCompute | undefined>(buildRestoredDraft);

  // 유효성 — 입력 1개+ 또는 expr 비어있지 않음.
  const individualValid = (c: CellCompute | undefined): boolean =>
    !!c && (c.inputs.length >= 1 || (c.mode === 'expr' && !!c.expr?.trim()));

  // 즉시 반영 — draft가 바뀔 때만 onAssign/onClearCells 발화.
  //  - 콜백은 selectedCells 의존이라 매번 새로 생기므로 ref로 최신값만 유지하고, effect 의존은 시그니처만.
  //  - 복원(선택 변경)으로 set된 경우는 발화 금지 → 복원 시그니처를 lastSyncedSigRef에 기록 후 스킵.
  const onAssignRef = useRef(onAssign);
  const onClearCellsRef = useRef(onClearCells);
  onAssignRef.current = onAssign;
  onClearCellsRef.current = onClearCells;
  const didMountRef = useRef(false);
  const prevValidRef = useRef(individualValid(draft));
  const curSig = JSON.stringify(draft ?? null);
  const lastSyncedSigRef = useRef<string>(curSig);

  // 선택 변경 시 공통 compute로 draft 복원(적용과 구분). 복원 시그니처를 lastSyncedSigRef에 먼저 기록.
  const cellsComputeSig = JSON.stringify(cells.map((c) => c.compute ?? null));
  useEffect(() => {
    const d = buildRestoredDraft();
    setDraft(d);
    lastSyncedSigRef.current = JSON.stringify(d ?? null);
    prevValidRef.current = individualValid(d);
  }, [cellsComputeSig]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      prevValidRef.current = individualValid(draft);
      return;
    }
    if (curSig === lastSyncedSigRef.current) {
      prevValidRef.current = individualValid(draft);
      return;
    }
    if (individualValid(draft)) {
      onAssignRef.current(draft as CellCompute);
      prevValidRef.current = true;
    } else {
      if (prevValidRef.current) onClearCellsRef.current?.();
      prevValidRef.current = false;
    }
  }, [curSig]);

  // CellComputeEditor용 가짜 셀(좌표 -1) — compute draft만 보유.
  const fakeCell = { row: -1, col: -1, compute: draft } as TableCell;

  return (
    <div className="rounded border border-emerald-100 bg-emerald-50/30 p-2">
      <div className="text-xs font-semibold text-emerald-700">계산 추가 (선택 {cellCount}칸)</div>
      <p className="mt-1 text-[11px] text-gray-500 leading-relaxed">
        계산값이 설정되면 바인딩 대신 이 값이 표시됩니다(여러 API/입력 조합).
      </p>

      <div className="mt-2 space-y-2">
        {/* 단일 셀 계산 편집기를 draft로 재사용(상수/직접지정 입력 + 연산/수식). 모든 선택 셀에 통째 복제. */}
        <CellComputeEditor
          cell={fakeCell}
          catalog={catalog}
          availableBindings={availableBindings ?? []}
          onPatch={(patch) => {
            if ('compute' in patch) setDraft((patch as { compute?: CellCompute }).compute);
          }}
          onHighlightSource={onHighlightSource}
          onStartCellPick={onStartCellPick}
          onCancelCellPick={onCancelCellPick}
          activePickToken={activePickToken}
        />

        <p className="text-[11px] text-gray-400 leading-relaxed">
          유효한 계산을 구성하면 선택한 {cellCount}개 셀에 즉시 반영됩니다.
        </p>

        {/* 선택 셀의 계산만 일괄 제거(실제 셀 반영, undo 대상). */}
        {onClearCells && (
          <button
            type="button"
            onClick={onClearCells}
            disabled={!hasAnyCompute}
            className={[
              'w-full px-2 py-1.5 text-xs rounded border',
              hasAnyCompute
                ? 'border-red-300 bg-white text-red-600 hover:bg-red-50'
                : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed',
            ].join(' ')}
          >
            선택한 {cellCount}개 셀의 계산 제거
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 표 셀 편집기 (T1) — 선택된 (row,col) 셀의 내용/속성을 편집한다.
 *
 * 모든 변경은 onPatch(Partial<TableCell>)로 updateCell을 거친다(undo 대상).
 * style 변경은 항상 `{ style: { ...cell.style, ... } }` 통째 새 객체로 넘긴다(얕은 머지라
 * 부분 style 머지는 위험).
 *
 * 범위 외(T2~T4): 행/열 추가·삭제, 병합(rowspan/colspan), 반복영역, content(중첩노드) 편집.
 */
function CellEditor({
  table,
  row,
  col,
  catalog,
  template,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
  onBack,
  onAddRow,
  onRemoveRow,
  onAddCol,
  onRemoveCol,
  onMergeRight,
  onMergeDown,
  onUnmerge,
  onAddNode,
  onGenerateOptionCheckboxes,
  onSelectNode,
  onMoveCellChild,
  onRemoveCellChild,
  onHighlightSource,
  onStartCellPick,
  onCancelCellPick,
  activePickToken,
  onGoToDataCard,
}: {
  table: TableNode;
  row: number;
  col: number;
  catalog: CatalogEntry[];
  /** 편집 중 템플릿 — 계산 입력의 "기존 값" 후보 수집(availableBindings)용. */
  template: ReportTemplate;
  onPatch: (patch: Partial<TableCell>) => void;
  /** 숫자 입력 실시간 미리보기(replacePresent) — 이 셀에. */
  onPreview: (patch: Partial<TableCell>) => void;
  /** 숫자 입력 확정(commitPresent) — 이 셀에. */
  onCommit: (patch: Partial<TableCell>) => void;
  /** 숫자 입력 미리보기 폐기(discardTransient) — Esc/이탈. */
  onRevert: () => void;
  onBack: () => void;
  onAddRow: (tableId: string, atRow: number) => void;
  onRemoveRow: (tableId: string, atRow: number) => void;
  onAddCol: (tableId: string, atCol: number) => void;
  onRemoveCol: (tableId: string, atCol: number) => void;
  onMergeRight: (tableId: string, row: number, col: number) => void;
  onMergeDown: (tableId: string, row: number, col: number) => void;
  onUnmerge: (tableId: string, row: number, col: number) => void;
  /** 셀 안 요소 추가 — 팔레트와 동일 경로(handleAddNode→insertNodeIntoCell). 선택 셀에 삽입됨. */
  onAddNode: (kind: ComposerNode['kind'], special?: SpecialKind) => void;
  /** 셀이 바인딩한 dataDef radio 필드의 옵션을 공유키 체크박스 그룹으로 이 셀에 삽입(값 셀 게이트 우회). */
  onGenerateOptionCheckboxes: () => void;
  /** 셀 content 자식 노드 선택(캔버스 클릭 선택과 동일 경로). */
  onSelectNode: (id: string) => void;
  /** 셀 content 자식 노드를 형제 배열 안에서 위/아래로 이동(moveNodeInSiblings). */
  onMoveCellChild: (childId: string, dir: 'up' | 'down') => void;
  /** 셀 content 자식 노드 삭제(removeNode, 확인 없이 즉시 — undo 가능). */
  onRemoveCellChild: (childId: string) => void;
  /** R2 — 계산 입력 "기존 값" 콤보 호버 시 하이라이트할 바인딩 id(null=해제). */
  onHighlightSource?: (availableBindingId: string | null) => void;
  /** R3 — 셀 직접 클릭 픽 모드 시작(token=입력 행 식별자, apply=클릭 셀 바인딩 적용). */
  onStartCellPick?: (token: string, apply: (binding: Binding) => void) => void;
  /** R3 — 픽 모드 취소(버튼 재클릭). */
  onCancelCellPick?: () => void;
  /** R3 — 현재 픽을 시작한 입력 행 token(버튼 강조용). */
  activePickToken?: string | null;
  /** 행 필드 바인딩의 「소스 편집 →」 이동(handleGoToDataCard) — BindingEditor로 전달. */
  onGoToDataCard?: (key: string) => void;
}) {
  // 계산 우선 시, 대기 중인 fallback 바인딩 편집기는 기본 접힘(토글로 펼침).
  const [showFallbackBinding, setShowFallbackBinding] = useState(false);
  // 이 셀의 바인딩 편집기에 넘길 "다른 노드/셀이 이미 쓰는 식별 키" 집합(자기 셀 제외) — 중복 경고용.
  const usedBindingKeys = useMemo(
    () => collectUsedBindingKeys(template.body, `cell:${table.id}:${row}:${col}`),
    [template.body, table.id, row, col],
  );
  // 표가 반복(repeat) 노드 template 안이면 그 repeat — 셀 바인딩을 "반복 항목 필드" 콤보로 저작한다
  // (종전엔 행 필드를 식별 키 직접 입력으로만 지정 가능 — 일반 노드 RepeatItemFieldCombo와 패리티).
  const enclosingRepeat = useMemo(() => {
    const path = findNodePath(template.body, table.id);
    if (!path) return undefined;
    return [...path.slice(0, -1)].reverse().find((n): n is RepeatNode => n.kind === 'repeat');
  }, [template, table.id]);
  // 반복 소스의 행 필드 후보 원천 — dataDef/카탈로그/조립 데이터 소스 통합(repeatSources).
  const repeatRowSource = useMemo(
    () =>
      enclosingRepeat
        ? findRepeatSource(
            collectRepeatSources(template.dataDefs, catalog, template.dataSources),
            enclosingRepeat,
          )
        : undefined,
    [enclosingRepeat, template, catalog],
  );
  // 이 셀의 값 스코프 필드 후보 — 표시 형식의 경로 칸(병기 값·구간 키) 저작에 쓴다. 반복영역 셀이면
  // 그 영역 소스, 밖이면 위 enclosingRepeat와 같은 결과다(판정 정본은 tableCellRowFieldOptions).
  const cellScopeKeyOptions = useMemo(
    () => tableCellRowFieldOptions(table, [{ row, col }], template, catalog),
    [table, row, col, template, catalog],
  );
  const cell = table.cells.find((c) => c.row === row && c.col === col);

  if (!cell) {
    return (
      <div className="space-y-3 text-sm">
        <button
          type="button"
          onClick={onBack}
          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
        >
          ← 표로 돌아가기
        </button>
        <p className="text-gray-400">선택한 셀을 찾을 수 없습니다(병합으로 덮인 슬롯일 수 있음).</p>
      </div>
    );
  }

  const style: TableCellStyle = cell.style ?? {};

  // 셀 content 직계 자식(중첩 노드) — 네비게이션 목록(선택/이동/삭제)에 사용.
  const contentNodes = cell.content ?? [];
  const hasContent = contentNodes.length > 0;
  const hasCompute = !!cell.compute;

  // 반복영역 셀이면 출처(바인딩)·계산은 반복영역 매핑이 관리하므로 단일 셀 편집기에서 막는다.
  // 다단 패턴 셀(isMultiColPattern)은 이미 전용 안내 배지가 있어 새 배지는 단일 단 반복에서만 노출.
  const repeatRegion = isRepeatRegionCell(table, row, col);
  const isMultiColPattern = classifyMultiColumnCell(table, row, col) === 'pattern';
  // 셀 안 요소 추가 차단 사유(팔레트와 동일 게이트) — 없으면 null.
  const cellInsertBlock = cellNodeInsertBlockReason(table, row, col);
  // 이 셀이 dataDef radio 필드에 바인딩돼 있으면 그 옵션 목록(공유키 체크박스 생성용). 아니면 null.
  // 값 셀 게이트(cellInsertBlock)와 무관하게 노출 — 라디오 필드 셀 전용 삽입 경로.
  const optionField = resolveRadioOptionField(template, { tableId: table.id, row, col });

  // 반복 행 필드 후보 + 현재 바인딩의 행 필드 여부 — 콤보 표시와 중복 키 경고 억제, lifecycle
  // 컨트롤 비활성(행 값은 mergeRepeatScope 행 객체에서 직접 읽혀 snapshot/live 구분 미적용)에 사용.
  // 행 필드 바인딩은 행 scope에서 자기 행 값을 읽으므로 다른 표·노드와 키가 같아도 값이 공유되지 않는다
  // (전역 텍스트 중복 검사(usedBindingKeys)가 행 scope를 모르는 오탐 → 이 경우 경고를 숨긴다).
  const rowKeyOptions = rowFieldPickerOptions(repeatRowSource?.fields);
  const isRowFieldBinding = isRowScopeFieldBinding(cell.binding, repeatRowSource);
  // 출처 정면 표시 재료(노드 패널과 동형) — 이동은 편집 카드가 있는 선언만.
  const rowFieldInfo = rowScopeFieldSourceInfo(cell.binding, repeatRowSource);
  const rowFieldSourceProp = rowFieldInfo
    ? {
        ...rowFieldInfo,
        cardKey:
          repeatRowSource &&
          hasEditableDataCard(repeatRowSource.key, template.dataDefs, template.dataSources)
            ? repeatRowSource.key
            : undefined,
      }
    : undefined;

  // 계산 입력의 "기존 값" 후보 — 보고서 내 다른 셀/노드의 바인딩(현재 셀 제외).
  const availableBindings = collectAvailableBindings(
    template.body,
    catalog,
    `cell:${table.id}:${row}:${col}`,
  );

  return (
    <dl className="space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
        >
          ← 표로 돌아가기
        </button>
        <span className="text-xs text-gray-400 font-mono">
          셀 ({row}, {col})
        </span>
      </div>

      {/* 다단(모델 A) 1단 패턴 셀 안내 배지 — 속성 변경이 엔진이 복제 생성하는 모든 단·행에 전파됨. */}
      {isMultiColPattern && (
        <div className="rounded border border-brand-200 bg-brand-50 px-2 py-1.5 text-[11px] leading-snug text-brand-700">
          🔁 반복 템플릿 셀 — 속성 변경이 생성되는 모든 단·행에 적용됩니다.
        </div>
      )}

      {/* 반복영역(단일 단) 셀 안내 배지 — 출처는 반복영역 매핑이 관리. 다단 패턴은 위 배지로 대체. */}
      {repeatRegion && !isMultiColPattern && (
        <div className="rounded border border-violet-200 bg-violet-50 px-2 py-1.5 text-[11px] leading-snug text-violet-700">
          🔁 반복 필드 (데이터 소스: {repeatRegion.over || '미지정'}) — 이 셀은 반복영역이 관리합니다.
        </div>
      )}

      {/* 행/열 조작(T2) — 선택 셀 좌표 기준 삽입/삭제 */}
      <div>
        <dt className="text-xs font-semibold text-gray-500 mb-1">
          행/열 ({table.rowCount} × {table.colCount})
        </dt>
        <dd className="space-y-1.5">
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-6">행</span>
            <button
              type="button"
              className={STRUCT_BTN_CLS}
              title="이 행 위에 삽입"
              onClick={() => onAddRow(table.id, row)}
            >
              ↑ 위 삽입
            </button>
            <button
              type="button"
              className={STRUCT_BTN_CLS}
              title="이 행 아래에 삽입 (Ctrl+Enter)"
              onClick={() => onAddRow(table.id, row + 1)}
            >
              ↓ 아래 삽입
            </button>
            <button
              type="button"
              className={STRUCT_DEL_BTN_CLS}
              title="이 행 삭제"
              disabled={table.rowCount <= 1}
              onClick={() => onRemoveRow(table.id, row)}
            >
              행 삭제
            </button>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-6">열</span>
            <button
              type="button"
              className={STRUCT_BTN_CLS}
              title="이 열 왼쪽에 삽입"
              onClick={() => onAddCol(table.id, col)}
            >
              ← 좌 삽입
            </button>
            <button
              type="button"
              className={STRUCT_BTN_CLS}
              title="이 열 오른쪽에 삽입"
              onClick={() => onAddCol(table.id, col + 1)}
            >
              → 우 삽입
            </button>
            <button
              type="button"
              className={STRUCT_DEL_BTN_CLS}
              title="이 열 삭제"
              disabled={table.colCount <= 1}
              onClick={() => onRemoveCol(table.id, col)}
            >
              열 삭제
            </button>
          </div>
        </dd>
      </div>

      {/* 셀 병합/분할(T3) — 이웃 셀 흡수 + 병합 해제 */}
      <div>
        <dt className="text-xs font-semibold text-gray-500 mb-1">
          병합 ({cell.colspan ?? 1}열 × {cell.rowspan ?? 1}행)
        </dt>
        <dd className="flex items-center gap-1">
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="오른쪽 인접 셀과 병합"
            disabled={!canMergeCellRight(table, row, col)}
            onClick={() => onMergeRight(table.id, row, col)}
          >
            → 오른쪽 병합
          </button>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="아래 인접 셀과 병합"
            disabled={!canMergeCellDown(table, row, col)}
            onClick={() => onMergeDown(table.id, row, col)}
          >
            ↓ 아래 병합
          </button>
          <button
            type="button"
            className={STRUCT_DEL_BTN_CLS}
            title="병합 해제 (Ctrl+M)"
            disabled={!canUnmergeCell(table, row, col)}
            onClick={() => onUnmerge(table.id, row, col)}
          >
            병합 해제 <span className="text-gray-400">(Ctrl+M)</span>
          </button>
        </dd>
      </div>

      {/* 셀 안 요소 추가 — 팔레트와 동일 경로(onAddNode→insertNodeIntoCell). 선택 셀 content에 삽입. */}
      <div>
        <dt className="text-xs font-semibold text-gray-500 mb-1">요소 추가</dt>
        <dd className="space-y-1.5">
          {/* dataDef radio 필드 셀 — 옵션을 공유키 체크박스 그룹으로 즉시 삽입(값 셀 게이트와 별개). */}
          {optionField && (
            <button
              type="button"
              onClick={onGenerateOptionCheckboxes}
              title={`이 셀에 선택지 그룹 생성 — 옵션 ${optionField.options.length}개는 내장 데이터셋에서 자동 파생(옵션 변경 시 즉시 반영)`}
              className="w-full px-2 py-1 text-xs rounded border border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100"
            >
              + 선택지 그룹 생성 (옵션 {optionField.options.length}개)
            </button>
          )}
          {cellInsertBlock ? (
            <p className="text-[11px] text-amber-600 leading-snug">{cellInsertBlock}</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {CELL_INSERT_ITEMS.map((item) => (
                <button
                  key={item.special ?? item.kind}
                  type="button"
                  onClick={() => onAddNode(item.kind, item.special)}
                  title={`선택한 셀에 ${item.label} 추가`}
                  className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-brand-50 hover:border-brand-300"
                >
                  + {item.label}
                </button>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400 leading-snug">
            추가한 요소는 캔버스에서 직접 클릭해 선택·편집합니다.
          </p>
        </dd>
      </div>

      {/* 내용: content(중첩노드) 셀은 자식 노드 네비게이션 목록, 그 외 text + binding 편집 */}
      {hasContent ? (
        <div>
          {/* 셀 content 직계 자식 1단 — 선택(드릴다운)·순서 이동·삭제. 그룹 자식 목록과 동일 패턴. */}
          <dt className="text-xs font-semibold text-gray-500 mb-1">
            셀 안 노드 ({contentNodes.length})
          </dt>
          <dd className="space-y-1.5">
            <ul className="space-y-0.5">
              {contentNodes.map((child, idx) => (
                <li key={child.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onSelectNode(child.id)}
                    title={`${child.id} 선택`}
                    className="flex-1 min-w-0 text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{shortNodeLabel(child)}</span>
                    <span className="text-[10px] text-gray-400 font-mono shrink-0">{child.id}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onMoveCellChild(child.id, 'up')}
                    disabled={idx === 0}
                    title="위로 이동"
                    className="px-1.5 py-1 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => onMoveCellChild(child.id, 'down')}
                    disabled={idx === contentNodes.length - 1}
                    title="아래로 이동"
                    className="px-1.5 py-1 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveCellChild(child.id)}
                    title="삭제 (Ctrl+Z로 되돌리기)"
                    className="px-1.5 py-1 text-xs rounded border border-red-300 bg-white text-red-600 hover:bg-red-50"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-gray-400 leading-snug">
              셀 안 노드는 위 목록 또는 캔버스에서 직접 클릭해 선택·편집합니다. 위 “요소 추가”로 더 넣을 수 있습니다.
            </p>
          </dd>
        </div>
      ) : (
        <>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">텍스트</label>
            <textarea
              value={cell.text ?? ''}
              onChange={(e) => onPatch({ text: e.target.value })}
              rows={3}
              disabled={!!cell.binding || hasCompute}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              placeholder={
                hasCompute
                  ? '🧮 계산 우선 (계산을 제거하면 바인딩/텍스트 사용)'
                  : cell.binding
                    ? '🔗 바인딩 사용 중 (해제하려면 아래 바인딩을 제거)'
                    : '정적 텍스트 (비우면 바인딩 값 사용)'
              }
            />
            {hasCompute ? (
              <p className="text-[11px] text-gray-400 mt-0.5">
                계산 우선 — 계산을 제거하면 바인딩/텍스트가 사용됩니다.
              </p>
            ) : (
              cell.binding && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  이 칸은 바인딩으로 채워집니다. 정적 텍스트를 쓰려면 바인딩을 제거하세요.
                </p>
              )
            )}
          </div>
        </>
      )}

      {/* 콘텐츠 정렬(가로 contentAlignH × 세로 contentAlignV) — 노드와 동일한 9방향 그리드. */}
      <CellContentAlignEditor
        valueH={cell.contentAlignH}
        valueV={cell.contentAlignV}
        onPick={(h, v) =>
          onPatch({
            contentAlignH: h === 'start' ? undefined : h,
            contentAlignV: v === 'start' ? undefined : v,
          })
        }
      />

      {/* 스타일 — 공용 컴포넌트(양식 탭·formTable 단일 뷰 공유, parity P2.5). 배경 데이터 바인딩
          (backgroundKey)은 TableCell 전용이라 backgroundKeySlot으로 배경색 다음에 주입. */}
      <SingleCellStyleFields
        style={style}
        onPatch={(s) => onPatch({ style: s })}
        onPreview={(s) => onPreview({ style: s })}
        onCommit={(s) => onCommit({ style: s })}
        onRevert={onRevert}
        onReset={() => onPatch({ style: defaultCellStyle() })}
        backgroundKeySlot={
          /* 배경 데이터 바인딩(고급) — RenderData flat 경로(예: levelCriteriaArr.0.color)에서 색을
             읽어 셀 배경으로. 값이 색 형식이 아니면 무시(정적 배경 유지). 투명도(0~1)로 옅게 칠함. */
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[11px] text-gray-500 w-full">배경 바인딩 키(고급)</span>
            <input
              type="text"
              placeholder="예: levelCriteriaArr.0.color"
              className="h-7 flex-1 min-w-[8rem] border border-gray-300 rounded px-1 text-[11px]"
              value={cell.backgroundKey ?? ''}
              onChange={(e) => onPatch({ backgroundKey: e.target.value || undefined })}
            />
            <NumericField
              value={cell.backgroundKeyAlpha ?? undefined}
              min={0}
              max={1}
              step={0.05}
              stepConfig={{ base: 0.1, shift: 0.5, alt: 0.01 }}
              title="투명도(0~1, 기본 1)"
              className="h-7 w-14 border border-gray-300 rounded px-1 text-[11px]"
              onPreview={(v) => onPreview({ backgroundKeyAlpha: v })}
              onCommit={(v) => onCommit({ backgroundKeyAlpha: v })}
              onRevert={onRevert}
            />
            {cell.backgroundKey !== undefined && (
              <button
                type="button"
                className="text-[11px] text-gray-500 underline"
                onClick={() => onPatch({ backgroundKey: undefined, backgroundKeyAlpha: undefined })}
              >
                초기화
              </button>
            )}
          </div>
        }
      />

      {/* 바인딩 (content 셀이 아닐 때만 — text/binding 상호 대체). 계산 사용 중이면 비파괴 공존:
          계산이 우선 렌더되고 바인딩은 "대기(fallback)" 상태로 보존되며, 접이식 편집기로 펼쳐 편집 가능. */}
      {!hasContent && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">바인딩</dt>
          <dd>
            {repeatRegion ? (
              <div className="space-y-2">
                {/* 문구가 **지금 상태**를 말하게 한다 — 종전에는 매핑 여부와 무관하게 같은 안내라,
                    매핑을 해제한 셀에서도 "출처는 반복영역이 관리합니다"만 보여 무엇이 매인
                    상태인지 알 수 없었다(2026-08-11 사용자 제보). 매핑된 셀은 어느 칼럼인지까지
                    밝힌다(패널이 그 사실을 갖고 있으면서 안 보여 주고 있었다). */}
                {cell.binding ? (
                  <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 leading-relaxed">
                    이 셀은{' '}
                    <span className="font-semibold text-gray-700">
                      {cellScopeKeyOptions.find((o) => o.key === cell.binding?.bindingKey)?.label ??
                        cell.binding.bindingKey}
                    </span>{' '}
                    칼럼에 매핑돼 있습니다. 출처를 바꾸거나 해제하려면 표 설정의 반복영역에서
                    칼럼↔셀 매핑을 사용하세요.
                  </p>
                ) : (
                  <div className="space-y-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    <p className="text-[11px] text-amber-800 leading-relaxed">
                      이 셀은 아직 칼럼에 매핑되지 않았습니다. 표 설정의 반복영역에서 "칼럼 → 셀
                      지정"으로 칼럼을 지정하세요.{' '}
                      {/* 표시 형식은 binding에 붙는 속성이라 매핑 없이는 편집 대상이 없다.
                          이유를 적지 않으면 "원래 있던 UI가 사라졌다"로 읽힌다(2026-08-11 제보). */}
                      <span className="text-amber-700">
                        표시 형식은 바인딩에 딸린 설정이라 매핑한 뒤에 나옵니다.
                      </span>
                    </p>
                    <button
                      type="button"
                      className="text-[11px] px-1.5 py-0.5 border border-amber-300 rounded text-amber-800 hover:bg-amber-100"
                      onClick={onBack}
                    >
                      표 설정 열기
                    </button>
                  </div>
                )}
                {/* 표시 형식은 출처(매핑)와 별개 — 매핑된 셀이면 여기서 format을 편집 가능(bindingKey/select/lifecycle 보존). */}
                {cell.binding ? (
                  <div className="space-y-1">
                    <BindingRow label="표시 형식" help="bindingFormat">
                      <select
                        value={cell.binding.format?.kind ?? 'none'}
                        onChange={(e) => {
                          const next = e.target.value as BindingFormat['kind'] | 'none';
                          if (cell.binding)
                            onPatch({
                              binding: {
                                ...cell.binding,
                                format: buildDefaultFormat(next, cell.binding.format),
                              },
                            });
                        }}
                        className={SELECT_CLS}
                      >
                        {FORMAT_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {formatKindOptionLabel(k)}
                          </option>
                        ))}
                      </select>
                    </BindingRow>
                    <FormatFields
                      format={cell.binding.format}
                      onChange={(format) => {
                        if (cell.binding) onPatch({ binding: { ...cell.binding, format } });
                      }}
                      catalog={catalog}
                      keyOptions={cellScopeKeyOptions}
                    />
                  </div>
                ) : null /* 미매핑 안내는 위 배너가 이미 낸다(같은 말을 두 번 하지 않는다) */}
              </div>
            ) : hasCompute ? (
              <div className="space-y-1.5">
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-relaxed">
                  계산 우선 · 바인딩 대기 — 계산을 제거하면 이 바인딩이 사용됩니다.
                </p>
                <button
                  type="button"
                  onClick={() => setShowFallbackBinding((v) => !v)}
                  className="text-[11px] text-gray-600 hover:text-gray-900"
                >
                  {showFallbackBinding ? '바인딩 접기 ▴' : '바인딩 보기/편집 ▾'}
                </button>
                {showFallbackBinding && (
                  <CellBindingEditor
                    cell={cell}
                    tableId={table.id}
                    catalog={catalog}
                    onPatch={onPatch}
                    usedKeys={isRowFieldBinding ? undefined : usedBindingKeys}
                    keyOptions={rowKeyOptions}
                    rowScopeFieldBinding={isRowFieldBinding}
                    rowScopeFieldSource={rowFieldSourceProp}
                    onGoToDataCard={onGoToDataCard}
                  />
                )}
              </div>
            ) : (
              <CellBindingEditor
                cell={cell}
                tableId={table.id}
                catalog={catalog}
                onPatch={onPatch}
                usedKeys={isRowFieldBinding ? undefined : usedBindingKeys}
                keyOptions={rowKeyOptions}
                rowScopeFieldBinding={isRowFieldBinding}
                rowScopeFieldSource={rowFieldSourceProp}
                onGoToDataCard={onGoToDataCard}
              />
            )}
          </dd>
        </div>
      )}

      {/* 계산 (content 셀이 아닐 때만 — 여러 입력을 op/수식으로 조합. 설정 시 binding보다 우선) */}
      {!hasContent && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">계산</dt>
          <dd>
            <CellComputeEditor
              cell={cell}
              catalog={catalog}
              onPatch={onPatch}
              availableBindings={availableBindings}
              onHighlightSource={onHighlightSource}
              onStartCellPick={onStartCellPick}
              onCancelCellPick={onCancelCellPick}
              activePickToken={activePickToken}
              disabled={!!repeatRegion}
              keyOptions={cellScopeKeyOptions}
            />
          </dd>
        </div>
      )}
    </dl>
  );
}

/**
 * 셀 바인딩 편집 — 기존 BindingEditor를 셀 컨텍스트로 어댑트한다.
 * BindingEditor는 `node.binding`을 읽고 `onPatch({ binding })`을 호출하므로, 셀을 binding만 가진
 * 가짜 노드로 감싸 재사용하고, patch의 binding을 셀 patch로 되돌린다.
 */
function CellBindingEditor({
  cell,
  tableId,
  catalog,
  onPatch,
  usedKeys,
  keyOptions,
  rowScopeFieldBinding,
  rowScopeFieldSource,
  onGoToDataCard,
}: {
  cell: TableCell;
  /** 소속 표 id — bindingKey 시드에 포함(표 없는 `cell-r-c` 시드는 다른 표의 같은 좌표와 충돌). */
  tableId: string;
  catalog: CatalogEntry[];
  onPatch: (patch: Partial<TableCell>) => void;
  /** 다른 노드/셀이 쓰는 bindingKey 집합(이 셀 제외) — 식별 키 중복 경고용 */
  usedKeys?: Set<string>;
  /** 식별 키 콤보 후보(반복 행 필드 등) — bare 바인딩일 때 식별 키를 콤보+직접 입력으로 저작. */
  keyOptions?: HybridKeyOption[];
  /** 행 스코프 필드 바인딩 판정(isRowScopeFieldBinding) — true면 lifecycle 컨트롤 비활성. */
  rowScopeFieldBinding?: boolean;
  /** 행 필드 바인딩의 출처 정면 표시 재료 — BindingEditor로 그대로 전달. */
  rowScopeFieldSource?: RowScopeFieldSourceInfo & { cardKey?: string };
  /** 「소스 편집 →」 이동 콜백 — BindingEditor로 그대로 전달. */
  onGoToDataCard?: (key: string) => void;
}) {
  // BindingEditor가 요구하는 최소 ComposerNode 형태(셀엔 id가 없으므로 bindingKey 제안용 임시 id).
  // 시드에 tableId 포함 — 표가 다르면 같은 (행,열)이라도 키가 갈린다(insertNodeIntoCell 승격 id와 동형).
  const fakeNode = {
    id: cell.binding?.bindingKey ?? `${tableId}-r${cell.row}-c${cell.col}`,
    kind: 'text',
    layout: { width: 'fill' },
    binding: cell.binding,
  } as ComposerNode;

  return (
    <BindingEditor
      node={fakeNode}
      catalog={catalog}
      usedKeys={usedKeys}
      keyOptions={keyOptions}
      rowScopeFieldBinding={rowScopeFieldBinding}
      rowScopeFieldSource={rowScopeFieldSource}
      onGoToDataCard={onGoToDataCard}
      editTargetId={`${tableId}:${cell.row}:${cell.col}`}
      // 공유 키 강조의 자기 자신 — collectUsedBindingKeys가 쓰는 셀 owner id와 같은 형식.
      selfOwnerId={`cell:${tableId}:${cell.row}:${cell.col}`}
      onPatch={(patch) => {
        // BindingEditor는 binding만 patch한다. 셀의 binding으로 전달.
        if ('binding' in patch) {
          const binding = (patch as { binding?: Binding }).binding;
          // binding을 설정할 때는 기존 cell.text를 함께 제거해 상호배타를 보장한다.
          // (빈 text가 남으면 렌더가 text 경로를 타 binding이 가려지는 버그 방지.)
          // binding 제거 시엔 text를 건드리지 않는다.
          onPatch(binding ? { binding, text: undefined } : { binding: undefined });
        }
      }}
    />
  );
}

/** 계산 연산(op) 코드 → 한글 라벨. CellComputeOp 전 값 처리(exhaustive Record). */
const CELL_COMPUTE_OP_LABELS: Record<CellComputeOp, string> = {
  sum: '합',
  subtract: '빼기',
  multiply: '곱',
  divide: '나누기',
  avg: '평균',
  min: '최소',
  max: '최대',
};
const CELL_COMPUTE_OPS = Object.keys(CELL_COMPUTE_OP_LABELS) as CellComputeOp[];

/**
 * 보고서에 이미 존재하는 바인딩 1건 — 계산 입력의 "기존 값" 콤보 후보.
 * id는 안정적 식별 키(콤보 value 매칭용), label은 buildBindingSummary 기반 한글 풀이, binding은 복사 원본.
 */
interface AvailableBinding {
  id: string;
  label: string;
  binding: Binding;
}

/**
 * 템플릿 body 전체(중첩 group/stack/table 셀/repeat 포함)를 순회해 binding을 가진 모든 노드/셀의
 * 바인딩을 모은다. 계산 입력의 "기존 값" 콤보 후보로 쓰며, 같은 바인딩(직렬화 동일)은 1건으로 dedupe한다.
 * selfCellId(현재 compute 편집 중인 셀)는 제외한다(자기참조/순환 방지). compute 전용 셀은 binding이 없어 자동 제외.
 */
function collectAvailableBindings(
  body: ComposerNode[],
  catalog: CatalogEntry[],
  selfCellId: string,
): AvailableBinding[] {
  const out: AvailableBinding[] = [];
  const seen = new Set<string>();
  const add = (id: string, binding: Binding) => {
    const sig = JSON.stringify(binding);
    if (seen.has(sig)) return;
    seen.add(sig);
    out.push({ id, label: buildBindingSummary(binding, catalog).text, binding });
  };
  const walk = (nodes: ComposerNode[]) => {
    for (const n of nodes) {
      if (n.binding) add(`node:${n.id}`, n.binding);
      if (n.kind === 'group' || n.kind === 'stack') walk(n.children);
      else if (n.kind === 'branch') for (const slot of n.branches) walk(slot.children);
      else if (n.kind === 'table') {
        for (const c of n.cells) {
          const cid = `cell:${n.id}:${c.row}:${c.col}`;
          if (c.binding && cid !== selfCellId) add(cid, c.binding);
          if (c.content) walk(c.content);
        }
      } else if (n.kind === 'repeat') walk([n.template]);
    }
  };
  walk(body);
  return out;
}

/**
 * 템플릿의 "데이터 재조회가 필요한 바인딩 속성"만 직렬화한 시그니처 문자열.
 *
 * 실데이터 미리보기(previewResult)는 반영 시점 템플릿 기준 스냅샷이라, 이후 **새 바인딩 추가**나
 * 카탈로그/select 변경은 스냅샷에 값이 없어 placeholder로 남는다. 이 시그니처가 바뀌면 실데이터를
 * 자동 재조회한다. 순회는 공용 walkBindings로 위임(compute 입력 바인딩 포함, 동형 순회).
 *
 * 포함(서버 resolve에 영향): owner(source+좌표), bindingKey, catalogKey/select, 레거시 source/api/
 * aiAction, lifecycle, repeatOver, prefillFrom.
 * 제외(표시/클라 전용 → 재조회 불필요): format, constValue. 레이아웃/스타일/텍스트 편집엔 불변.
 */
function computeBindingSignature(body: ComposerNode[]): string {
  const parts: string[] = [];
  walkBindings(body, (binding, owner) => {
    const ownerId = owner.cell
      ? `cell:${owner.cell.tableId}:${owner.cell.row}:${owner.cell.col}`
      : `node:${owner.nodeId}`;
    parts.push(
      [
        owner.source,
        ownerId,
        binding.bindingKey ?? '',
        binding.catalogKey ?? '',
        binding.select ?? '',
        binding.source ?? '',
        binding.api ? JSON.stringify(binding.api) : '',
        binding.aiAction ?? '',
        binding.lifecycle ?? '',
        binding.repeatOver ?? '',
        binding.prefillFrom ? JSON.stringify(binding.prefillFrom) : '',
      ].join('|'),
    );
  });
  return parts.join('\n');
}

/**
 * 활성 분기 슬롯의 조건 eq 절을 데이터 소스 시뮬레이션 필터로 승격한다(미리보기 전용).
 *
 * "그 슬롯 조건에 맞는 현장 데이터가 들어온 것처럼" 미리보기하기 위해, 각 branch의 **활성 슬롯**
 * (activeBranchSlots) condition에서 eq 절을 뽑고, 그 branch의 conditionBindings에 존재하는 key만
 * 골라 그 binding의 catalogKey/select로 `{ [catalogKey]: { [selectField]: value } }`를 구성한다.
 * 여러 branch가 같은 catalogKey를 가리키면 필드가 병합된다(뒤 branch가 같은 필드면 덮어씀).
 *
 * else 슬롯(조건 없음)·truthy/neq 절은 특정 값 대체가 불가하므로 시뮬레이션 대상이 아니다.
 * 반환: simulate(대상 없으면 undefined) + branchesByCatalogKey(catalogKey→기여 branchId 목록,
 * 응답 simulateMisses를 miss branch로 역매핑하는 데 사용).
 */
function buildBranchSimulate(
  body: ComposerNode[],
  activeBranchSlots: Record<string, number>,
): {
  simulate?: Record<string, Record<string, string>>;
  branchesByCatalogKey: Record<string, string[]>;
} {
  const simulate: Record<string, Record<string, string>> = {};
  const branchesByCatalogKey: Record<string, string[]> = {};
  walkNodeTree(body, (node) => {
    if (node.kind !== 'branch') return;
    const bindings = node.conditionBindings ?? [];
    if (bindings.length === 0) return;
    const rawIdx = activeBranchSlots[node.id] ?? 0;
    const idx = Math.max(0, Math.min(rawIdx, node.branches.length - 1));
    const slot = node.branches[idx];
    if (!slot?.condition) return; // else 슬롯(조건 없음)은 시뮬레이션 대상 아님
    const eq = extractEqClauses(slot.condition);
    for (const cb of bindings) {
      const value = eq[cb.key];
      if (value === undefined) continue;
      const catalogKey = cb.binding.catalogKey;
      const field = cb.binding.select;
      if (!catalogKey || !field) continue;
      (simulate[catalogKey] ??= {})[field] = value;
      const arr = (branchesByCatalogKey[catalogKey] ??= []);
      if (!arr.includes(node.id)) arr.push(node.id);
    }
  });
  return {
    simulate: Object.keys(simulate).length > 0 ? simulate : undefined,
    branchesByCatalogKey,
  };
}

/**
 * 편집 중인 소유자(selfOwnerId)를 제외한, 템플릿 내 다른 노드/셀이 이미 쓰는 bindingKey 집합.
 * BindingEditor의 "식별 키" 중복 경고에 사용한다.
 *
 * 순회는 공용 walkBindings로 위임한다. 셀 compute 입력 바인딩(source==='computeInput')도 포함해
 * 계산 입력 키와의 충돌까지 감지한다(값 공유 함정 방지). selfOwnerId는 `node:id` 또는
 * `cell:tableId:row:col` 형식으로, walkBindings owner에서 동형 문자열을 만들어 비교한다.
 *
 * 제외 규칙 둘은 성격이 같다 — **값이 실제로 섞이지 않는** 공유다(오탐 경고는 진짜 경고까지 죽인다):
 *  ① 라디오 그룹 멤버(checkedValue 있는 checkbox) — 같은 키 공유가 상호배타의 구현이다.
 *  ② 같은 branch의 **다른 슬롯**에 있는 자리 — 동시에 렌더되지 않으므로 같은 필드의 레이아웃
 *    변형이다(단일회차용 제목 칸 ↔ 다회차용 제목 칸). 판정은 self와 상대의 슬롯 경로 대조
 *    (makeMutualExclusionCheck) — 다른 branch끼리나 같은 슬롯 안은 여전히 중복으로 센다.
 *
 * ⚠️ 가시화(collectSharedKeyOwners·사용처 목록)에는 이 제외를 옮기지 말 것. 경고는 "실수인가"를
 * 묻고 가시화는 "어디와 이어졌나"를 보여 준다 — 의도된 공유일수록 눈으로 확인하고 싶은 관계다
 * (sharedKeyHighlight.ts 상단 주석이 이 원칙의 정본).
 */
function collectUsedBindingKeys(body: ComposerNode[], selfOwnerId: string): Set<string> {
  const out = new Set<string>();
  const isMutuallyExclusive = makeMutualExclusionCheck(body, selfOwnerId);
  walkBindings(body, (binding, owner) => {
    if (!binding.bindingKey) return;
    // 라디오 그룹 멤버(checkedValue 있는 checkbox)는 같은 bindingKey 공유가 의도된 정상 동작
    // (라디오식 상호배타). 이를 "사용 중"으로 세면 그룹 편집 시 '이미 다른 노드에서 사용 중' 경고가
    // 오탐으로 떠 혼란스러우므로 usedKeys 집계에서 제외한다. 진짜 중복(text 2개 등)은 그대로 경고.
    if (owner.node?.kind === 'checkbox' && owner.node.checkedValue) return;
    const ownerId = owner.cell
      ? `cell:${owner.cell.tableId}:${owner.cell.row}:${owner.cell.col}`
      : `node:${owner.nodeId}`;
    if (ownerId === selfOwnerId) return;
    if (isMutuallyExclusive(ownerId)) return;
    out.add(binding.bindingKey);
  });
  return out;
}

/** 계산 입력의 binding이 availableBindings 중 어떤 항목과 동일한지(직렬화 일치) → 그 id, 없으면 undefined. */
function matchAvailableBindingId(
  binding: Binding | undefined,
  available: AvailableBinding[],
): string | undefined {
  if (!binding) return undefined;
  const sig = JSON.stringify(binding);
  return available.find((ab) => JSON.stringify(ab.binding) === sig)?.id;
}

/**
 * 셀 계산 편집 — 여러 입력(바인딩/상수)을 op(선언형 연산) 또는 expr(안전 산술식)로 조합한다.
 *
 * 입력의 바인딩 편집은 BindingEditor를 가짜 노드로 감싸 재사용한다(셀 바인딩과 동일 방식).
 * 입력의 per-input format은 엔진이 무시하고 raw 숫자로 읽으므로, 결과 표시 형식만 별도 지정한다.
 * compute가 설정되면 우선순위상 binding보다 이 값이 표시된다(content>text>aggregate>compute>binding).
 */
function CellComputeEditor({
  cell,
  catalog,
  onPatch,
  availableBindings,
  onHighlightSource,
  onStartCellPick,
  onCancelCellPick,
  activePickToken,
  disabled,
  keyOptions,
}: {
  cell: TableCell;
  catalog: CatalogEntry[];
  onPatch: (patch: Partial<TableCell>) => void;
  /** 보고서 내 기존 바인딩 후보(입력 "기존 값" 콤보용). */
  availableBindings: AvailableBinding[];
  /**
   * 결과 표시 형식의 경로 칸(병기 값·구간 키) 후보 — 이 셀의 값 스코프 필드. 계산 결과가 놓이는
   * 자리가 셀 자신이라 스코프도 셀과 같다. 스코프를 모르는 호출부(일괄 draft)는 넘기지 않는다.
   */
  keyOptions?: HybridKeyOption[];
  /** R2 — 콤보 호버 시 캔버스 하이라이트. */
  onHighlightSource?: (availableBindingId: string | null) => void;
  /** R3 — 셀 직접 클릭 픽 모드 시작/취소/활성 식별. */
  onStartCellPick?: (token: string, apply: (binding: Binding) => void) => void;
  onCancelCellPick?: () => void;
  activePickToken?: string | null;
  /** 반복영역 셀 등 계산이 무의미한 셀에서 "계산 추가"를 막는다(출처는 반복영역 매핑이 관리). */
  disabled?: boolean;
}) {
  const compute = cell.compute;

  // 계산 없음 → 추가 버튼. 계산은 binding보다 우선하지만(비파괴 공존) 기존 binding은 보존한다 —
  // 계산을 다시 제거하면 그 binding이 fallback으로 자동 복귀한다.
  // disabled(반복영역 셀)면 추가 버튼을 비활성화한다.
  if (!compute) {
    return disabled ? (
      <p className="text-[11px] text-gray-400 leading-relaxed">
        반복영역 셀은 계산을 사용할 수 없습니다(값은 반복 항목 필드에서 옵니다).
      </p>
    ) : (
      <button
        type="button"
        onClick={() => onPatch({ compute: { inputs: [], mode: 'op', op: 'sum' } })}
        className="text-xs px-2 py-1 border border-gray-300 rounded text-brand-600 hover:bg-brand-50"
      >
        + 계산 추가
      </button>
    );
  }

  // compute 부분 갱신 → 통째 병합 patch.
  const patchCompute = (partial: Partial<CellCompute>) =>
    onPatch({ compute: { ...compute, ...partial } });

  // 입력 1개 부분 갱신(immutable 배열 교체).
  const patchInput = (index: number, partial: Partial<ComputeInput>) => {
    const next = compute.inputs.map((inp, i) => (i === index ? { ...inp, ...partial } : inp));
    patchCompute({ inputs: next });
  };

  const removeInput = (index: number) => {
    patchCompute({ inputs: compute.inputs.filter((_, i) => i !== index) });
  };

  const addInput = () => {
    const name = nextComputeInputName(compute.inputs.length);
    patchCompute({ inputs: [...compute.inputs, { name, id: crypto.randomUUID() }] });
  };

  const formatKind: BindingFormat['kind'] | 'none' = compute.format?.kind ?? 'none';

  return (
    <div className="space-y-2 rounded border border-brand-100 bg-brand-50/30 p-2">
      <p className="text-[11px] text-gray-500 leading-relaxed">
        계산값이 설정되면 바인딩 대신 이 값이 표시됩니다(여러 API/입력 조합).
      </p>

      {/* 모드: op(연산) | expr(수식) */}
      <BindingRow label="방식">
        <div className="flex gap-3">
          <label className="flex items-center gap-1 text-xs cursor-pointer">
            <input
              type="radio"
              name={`compute-mode-${cell.row}-${cell.col}`}
              checked={compute.mode === 'op'}
              onChange={() => patchCompute({ mode: 'op', op: compute.op ?? 'sum' })}
            />
            연산
          </label>
          <label className="flex items-center gap-1 text-xs cursor-pointer">
            <input
              type="radio"
              name={`compute-mode-${cell.row}-${cell.col}`}
              checked={compute.mode === 'expr'}
              onChange={() => patchCompute({ mode: 'expr', expr: compute.expr ?? '' })}
            />
            수식
          </label>
        </div>
      </BindingRow>

      {compute.mode === 'op' ? (
        <BindingRow label="연산">
          <select
            className={SELECT_CLS}
            value={compute.op ?? 'sum'}
            onChange={(e) => patchCompute({ op: e.target.value as CellComputeOp })}
          >
            {CELL_COMPUTE_OPS.map((op) => (
              <option key={op} value={op}>
                {CELL_COMPUTE_OP_LABELS[op]}
              </option>
            ))}
          </select>
        </BindingRow>
      ) : (
        <BindingRow label="수식">
          <input
            type="text"
            className={INPUT_CLS}
            value={compute.expr ?? ''}
            placeholder="(a + b) * 0.5"
            onChange={(e) => patchCompute({ expr: e.target.value })}
          />
          <p className="text-[10px] text-gray-400 mt-0.5">
            사용 가능한 입력: {compute.inputs.map((inp) => inp.name).filter(Boolean).join(', ') || '(없음 — 아래에서 추가)'}
          </p>
        </BindingRow>
      )}

      {/* 입력 목록 */}
      <div className="space-y-2">
        <span className="text-[11px] text-gray-500 font-mono block">입력</span>
        {compute.inputs.length === 0 && (
          <p className="text-[11px] text-gray-400">입력이 없습니다. 아래에서 추가하세요.</p>
        )}
        {compute.inputs.map((input, i) => (
          <ComputeInputRow
            key={input.id ?? `idx-${i}`}
            input={input}
            index={i}
            mode={compute.mode}
            catalog={catalog}
            availableBindings={availableBindings}
            onPatch={(partial) => patchInput(i, partial)}
            onRemove={() => removeInput(i)}
            rowKey={`${cell.row}-${cell.col}-${i}`}
            onHighlightSource={onHighlightSource}
            onStartCellPick={onStartCellPick}
            onCancelCellPick={onCancelCellPick}
            activePickToken={activePickToken}
          />
        ))}
        <button
          type="button"
          onClick={addInput}
          className="text-xs px-2 py-1 border border-gray-300 rounded text-brand-600 hover:bg-brand-50"
        >
          + 입력 추가
        </button>
      </div>

      {/* 표시 형식(결과에만 적용) */}
      <BindingRow label="표시 형식" help="bindingFormat">
        <select
          value={formatKind}
          onChange={(e) =>
            patchCompute({
              format: buildDefaultFormat(e.target.value as BindingFormat['kind'] | 'none', compute.format),
            })
          }
          className={SELECT_CLS}
        >
          {FORMAT_KINDS.map((k) => (
            <option key={k} value={k}>
              {formatKindOptionLabel(k)}
            </option>
          ))}
        </select>
      </BindingRow>
      <FormatFields
        format={compute.format}
        onChange={(format) => patchCompute({ format })}
        catalog={catalog}
        keyOptions={keyOptions}
      />

      <button
        type="button"
        onClick={() => onPatch({ compute: undefined })}
        className="text-[11px] text-red-500 hover:underline self-start"
      >
        계산 제거
      </button>
    </div>
  );
}

/**
 * 계산 입력 1개의 편집 행 — 이름(수식 모드 전용) + 값 소스 콤보(상수 / 기존 값 / 직접 지정).
 *
 * 값 소스:
 *  - 상수: 숫자 직접 입력(const).
 *  - 기존 값: 보고서 내 다른 셀/노드의 바인딩을 structuredClone으로 복사해 사용(자기완결, bindingKey 동일 → 같은 값 resolve).
 *  - 직접 지정: 풀 BindingEditor로 새 바인딩을 직접 작성.
 *
 * 콤보 value는 데이터(const/매칭 바인딩/비매칭 바인딩/미설정)에서 파생하되, "직접 지정"은 바인딩이
 * 아직 없을 때도 선택 상태를 유지해야 하므로 로컬 customForced로 보강한다(derived가 none일 때만 효력).
 */
function ComputeInputRow({
  input,
  index,
  mode,
  catalog,
  availableBindings,
  onPatch,
  onRemove,
  rowKey,
  onHighlightSource,
  onStartCellPick,
  onCancelCellPick,
  activePickToken,
}: {
  input: ComputeInput;
  index: number;
  mode: CellCompute['mode'];
  catalog: CatalogEntry[];
  availableBindings: AvailableBinding[];
  onPatch: (partial: Partial<ComputeInput>) => void;
  onRemove: () => void;
  /** R3 — 이 입력 행의 픽 token(버튼 강조용). */
  rowKey?: string;
  /** R2 — 콤보 호버 시 캔버스 하이라이트(picked id 또는 null). */
  onHighlightSource?: (availableBindingId: string | null) => void;
  /** R3 — 셀 직접 클릭 픽 모드 시작/취소/활성 식별. */
  onStartCellPick?: (token: string, apply: (binding: Binding) => void) => void;
  onCancelCellPick?: () => void;
  activePickToken?: string | null;
}) {
  const [customForced, setCustomForced] = useState(false);

  // 이 입력 행이 현재 픽 모드를 시작한 행인지(버튼 강조/라벨용).
  const pickActive = !!rowKey && activePickToken === rowKey;

  const matchedId = matchAvailableBindingId(input.binding, availableBindings);
  // 데이터에서 파생한 소스 모드.
  const derived: 'const' | 'picked' | 'custom' | 'none' =
    input.const !== undefined
      ? 'const'
      : input.binding
        ? matchedId
          ? 'picked'
          : 'custom'
        : 'none';
  // 직접 지정 선택했으나 아직 바인딩이 없을 때만 customForced가 효력(데이터가 모드를 정하면 무시).
  const source = derived === 'none' && customForced ? 'custom' : derived;

  const comboValue =
    source === 'const'
      ? 'const'
      : source === 'picked'
        ? `picked:${matchedId}`
        : source === 'custom'
          ? 'custom'
          : undefined;

  const options: ComboboxOption[] = [
    { value: 'const', label: '상수(숫자 직접 입력)', group: '상수' },
    ...(availableBindings.length === 0
      ? [{ value: '__none__', label: '기존 값 없음', group: '기존 값' }]
      : availableBindings.map((ab) => ({
          value: `picked:${ab.id}`,
          label: ab.label,
          group: '기존 값',
        }))),
    { value: 'custom', label: '직접 지정(새 바인딩 작성)', group: '직접 지정' },
  ];

  const handleSource = (value: string | undefined) => {
    if (!value || value === '__none__') return; // 안내용 항목 — 동작 없음
    if (value === 'const') {
      setCustomForced(false);
      onPatch({ const: 0, binding: undefined });
      return;
    }
    if (value === 'custom') {
      setCustomForced(true);
      onPatch({ const: undefined });
      return;
    }
    if (value.startsWith('picked:')) {
      const id = value.slice('picked:'.length);
      const ab = availableBindings.find((a) => a.id === id);
      if (!ab) return;
      setCustomForced(false);
      // 복사라 자기완결 — 원본 셀이 바뀌어도 독립. bindingKey 동일 → resolve 시 같은 값 읽음.
      onPatch({ binding: structuredClone(ab.binding), const: undefined });
    }
  };

  return (
    <div className="rounded border border-gray-200 bg-white p-2 space-y-1.5">
      {/* 이름(수식 변수명)은 수식 모드에서만 — 연산 모드는 입력 순서/전체를 조합하므로 이름 불필요 */}
      <div className="flex items-center gap-1">
        {mode === 'expr' ? (
          <>
            <span className="text-[10px] text-gray-400 w-16 shrink-0">수식 변수명</span>
            <input
              type="text"
              className={`${INPUT_CLS} flex-1`}
              value={input.name}
              placeholder={nextComputeInputName(index)}
              onChange={(e) => onPatch({ name: e.target.value })}
            />
          </>
        ) : (
          <span className="text-[10px] text-gray-400 flex-1">입력 {index + 1}</span>
        )}
        <button
          type="button"
          title="입력 삭제"
          className="text-gray-400 hover:text-red-500 px-1"
          onClick={onRemove}
        >
          ✕
        </button>
      </div>

      {/* 값 소스: 상수 / 기존 값(보고서 내 바인딩 복사) / 직접 지정 */}
      {/* 닫힌 콤보(이미 '기존 값' 셀이 선택된 상태) 위 호버 시에도 그 셀을 강조 — 드롭다운을 열어
          옵션에 호버하지 않아도 어떤 셀인지 미리보기(매칭된 picked 바인딩이 있을 때만). */}
      <div
        onMouseEnter={() => matchedId && onHighlightSource?.(matchedId)}
        onMouseLeave={() => onHighlightSource?.(null)}
      >
        {/* eslint-disable-next-line local/catalog-picker-required -- 수식 입력 값 소스(상수/보고서 내 바인딩 복사/직접 지정) — 카탈로그 엔트리 아님 */}
        <CatalogCombobox
          value={comboValue}
          options={options}
          onChange={handleSource}
          placeholder="값 소스 선택..."
          onHoverOption={(v) =>
            onHighlightSource?.(v && v.startsWith('picked:') ? v.slice('picked:'.length) : null)
          }
        />
      </div>

      {/* R3 — 캔버스 셀/노드를 직접 클릭해 그 바인딩을 이 입력으로 가져오기. */}
      {onStartCellPick && (
        <button
          type="button"
          onClick={() => {
            if (pickActive) {
              onCancelCellPick?.();
              return;
            }
            if (!rowKey) return;
            onStartCellPick(rowKey, (binding) =>
              onPatch({ binding: structuredClone(binding), const: undefined }),
            );
          }}
          className={`w-full text-xs px-2 py-1 rounded border ${
            pickActive
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          {pickActive ? '셀 클릭 대기… (ESC 취소)' : '+ 셀 선택(캔버스 클릭)'}
        </button>
      )}

      {source === 'const' && (
        <input
          type="number"
          className={INPUT_CLS}
          value={input.const ?? 0}
          onChange={(e) => onPatch({ const: e.target.value === '' ? 0 : Number(e.target.value) })}
        />
      )}

      {source === 'picked' && (
        <p className="text-[10px] text-gray-500 leading-relaxed">
          기존 값 사용: {availableBindings.find((a) => a.id === matchedId)?.label}
        </p>
      )}

      {source === 'custom' && (
        <>
          <BindingEditor
            node={
              {
                id: `compute-input-${index}`,
                kind: 'text',
                layout: { width: 'fill' },
                binding: input.binding,
              } as ComposerNode
            }
            catalog={catalog}
            onPatch={(patch) => {
              if ('binding' in patch) {
                onPatch({ binding: (patch as { binding?: Binding }).binding });
              }
            }}
          />
          <p className="text-[10px] text-gray-400">형식은 결과에만 적용됩니다.</p>
        </>
      )}

      {source === 'none' && (
        <p className="text-[10px] text-gray-400">값 소스를 선택하세요.</p>
      )}
    </div>
  );
}

/** 선택 노드 속성 편집 패널 */
/**
 * 결재란(approvalBlock) 구성 옵션 편집기(P2a-3).
 *
 * variant(고정 양식 선택)를 대체해, 결재칸의 출력 토글(단계명란/이름란)·치수(칸 너비·각 영역
 * 높이)·폰트(단계명란/이름란)를 자유롭게 설정한다. 값은 node.props.approvalConfig에 저장되며,
 * ApprovalBlockSpecial → StepperLayout으로 흘러 캔버스/미리보기에 즉시 반영된다.
 * 빈 입력 = 미설정(해당 필드 삭제) = layout CSS 기본값 사용(회귀 0).
 */
function ApprovalConfigEditor({
  node,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
}: {
  node: ComposerNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
}) {
  // special 노드에서만 렌더되므로 props는 Record. approvalConfig는 부분 검증해 읽는다.
  const props = (node as { props?: Record<string, unknown> }).props ?? {};
  const config = (props.approvalConfig && typeof props.approvalConfig === 'object'
    ? (props.approvalConfig as ApprovalBlockConfig)
    : {}) as ApprovalBlockConfig;

  // config 일부 갱신 → props.approvalConfig 통째 교체(얕은 병합). undefined 값은 키 제거.
  const buildConfig = (next: Partial<ApprovalBlockConfig>): Partial<ComposerNode> => {
    const merged: ApprovalBlockConfig = { ...config, ...next };
    // undefined 필드 정리(미설정 = CSS 기본).
    (Object.keys(merged) as (keyof ApprovalBlockConfig)[]).forEach((k) => {
      if (merged[k] === undefined) delete merged[k];
    });
    return { props: { ...props, approvalConfig: merged } } as Partial<ComposerNode>;
  };
  const patchConfig = (next: Partial<ApprovalBlockConfig>) => onPatch(buildConfig(next));
  // 숫자 입력(NumericField) 미리보기/확정 — replace/commit 경유(patchConfig와 동일 정규화).
  const patchConfigLive = (next: Partial<ApprovalBlockConfig>) => onPreview(buildConfig(next));
  const patchConfigCommit = (next: Partial<ApprovalBlockConfig>) => onCommit(buildConfig(next));

  // 출력 토글 기본값 — 미설정 시 표시(true).
  const showStepLabel = config.showStepLabel ?? true;
  const showName = config.showName ?? true;

  // 단일 mm 치수 입력 + 초기화(SMS 기본값) — 영역 섹션 내부용. 빈값=CSS 기본, 저장은 px.
  const heightField = (
    label: string,
    key: 'cellWidth' | 'stepLabelHeight' | 'signatureHeight' | 'nameHeight',
    title: string,
  ) => (
    <div className="flex flex-col gap-0.5 text-[11px] text-gray-500">
      <div className="flex items-center justify-between gap-1">
        <span className="min-w-0 truncate">{label}</span>
        <button
          type="button"
          title={title}
          onClick={() => patchConfig({ [key]: DEFAULT_APPROVAL_CONFIG[key] } as Partial<ApprovalBlockConfig>)}
          className="text-[10px] text-gray-400 hover:text-brand-600 underline whitespace-nowrap shrink-0"
        >
          초기화
        </button>
      </div>
      <NumericField
        value={config[key] ?? undefined}
        min={0}
        step={0.01}
        toDisplay={pxToMm}
        fromDisplay={mmToPx}
        onPreview={(px) => patchConfigLive({ [key]: px } as Partial<ApprovalBlockConfig>)}
        onCommit={(px) => patchConfigCommit({ [key]: px } as Partial<ApprovalBlockConfig>)}
        onRevert={onRevert}
      />
    </div>
  );

  // 단일 패딩(mm) 입력 + 초기화 — 영역 섹션 내부용. 미지정=CSS 기본.
  const padField = (
    key: 'stepLabelPadding' | 'signaturePadding' | 'namePadding',
    title: string,
  ) => (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[11px] text-gray-500">패딩(mm)</span>
        <button
          type="button"
          title={title}
          onClick={() => patchConfig({ [key]: { ...DEFAULT_APPROVAL_CONFIG[key] } } as Partial<ApprovalBlockConfig>)}
          className="text-[10px] text-gray-400 hover:text-brand-600 underline"
        >
          초기화
        </button>
      </div>
      <BoxSpacingFields
        value={config[key]}
        onChange={(v) => patchConfig({ [key]: v } as Partial<ApprovalBlockConfig>)}
        onPreview={(v) => patchConfigLive({ [key]: v } as Partial<ApprovalBlockConfig>)}
        onRevert={onRevert}
      />
    </div>
  );

  // 영역 배경(RectStyle의 fill만) — 테두리는 칸 외곽(cellBorder)·구분선(sectionDivider)이 일원 담당하므로
  // showBorder={false}로 테두리 편집은 숨긴다(역할 분리). 미지정=CSS 기본, fill='transparent'로 '배경 없음' 명시.
  const rectField = (
    key: 'stepLabelRect' | 'signatureRect' | 'nameRect',
  ) => (
    <div>
      <span className="text-[11px] text-gray-500 mb-0.5 block">배경</span>
      <RectStyleFields
        value={config[key]}
        onChange={(rs) => patchConfig({ [key]: rs } as Partial<ApprovalBlockConfig>)}
        showBorder={false}
        defaultFill="transparent"
      />
    </div>
  );

  return (
    <div className="space-y-3">
      <label className="text-xs font-semibold text-gray-500 block">결재란 구성</label>

      {/* 공통 — 칸 너비(모든 영역 공유) + 역할 분리 테두리(칸 외곽 + 영역 구분선) */}
      <div className="grid grid-cols-2 gap-2">
        {heightField('칸 너비(mm)', 'cellWidth', '칸 너비를 SMS 기본값(20.7mm)으로')}
      </div>
      {/* 칸 외곽 테두리 — 결재란 칸 바깥선(칸 사이 세로선은 한쪽만 자동 처리).
          기본=CSS 기본 유지 / 지정 / 없음('none')=테두리 제거. */}
      <BorderEditor
        label="결재란 외곽 테두리"
        value={config.cellBorder}
        onChange={(b) => patchConfig({ cellBorder: b })}
        onPreview={(b) => patchConfigLive({ cellBorder: b })}
        onRevert={onRevert}
        allowNone
        baseValue={DEFAULT_APPROVAL_CONFIG.cellBorder}
      />
      {/* 영역 구분선 — 단계명란↔서명란↔이름란 사이 가로선. 마지막 영역 아래변은 칸 외곽이 담당.
          기본=CSS 기본 유지 / 지정 / 없음('none')=구분선 제거. */}
      <BorderEditor
        label="영역 구분선"
        value={config.sectionDivider}
        onChange={(b) => patchConfig({ sectionDivider: b })}
        onPreview={(b) => patchConfigLive({ sectionDivider: b })}
        onRevert={onRevert}
        allowNone
        baseValue={DEFAULT_APPROVAL_CONFIG.sectionDivider}
      />

      {/* ── 단계명란 ── (헤더 토글로 표시/숨김, off면 세부 숨김) */}
      <div className="border-t border-gray-200 pt-2 space-y-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
          <input
            type="checkbox"
            checked={showStepLabel}
            onChange={(e) => patchConfig({ showStepLabel: e.target.checked })}
          />
          단계명란
        </label>
        {showStepLabel && (
          <div className="space-y-2 pl-1">
            <div className="grid grid-cols-2 gap-2">
              {heightField('높이(mm)', 'stepLabelHeight', '단계명란 높이를 SMS 기본값(≈7.41mm)으로')}
            </div>
            {padField('stepLabelPadding', '단계명란 패딩을 SMS 기본값(상하 0.79·좌우 2.65mm)으로')}
            <ApprovalFontEditor label="폰트" font={config.stepLabelFont} onChange={(font) => patchConfig({ stepLabelFont: font })} onPreview={(font) => patchConfigLive({ stepLabelFont: font })} onRevert={onRevert} defaultColor={DEFAULT_APPROVAL_CONFIG.stepLabelFont?.color} />
            {rectField('stepLabelRect')}
          </div>
        )}
      </div>

      {/* ── 서명란 ── (항상 표시) */}
      <div className="border-t border-gray-200 pt-2 space-y-2">
        <span className="text-xs font-semibold text-gray-600 block">서명란</span>
        <div className="space-y-2 pl-1">
          <div className="grid grid-cols-2 gap-2">
            {heightField('높이(mm)', 'signatureHeight', '서명란 높이를 SMS 기본값(10.58mm)으로')}
          </div>
          {padField('signaturePadding', '서명란 패딩을 SMS 기본값(상하 1.06·좌우 1.59mm)으로')}
          {rectField('signatureRect')}
        </div>
      </div>

      {/* ── 이름란 ── (헤더 토글로 표시/숨김, off면 세부 숨김) */}
      <div className="border-t border-gray-200 pt-2 space-y-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
          <input
            type="checkbox"
            checked={showName}
            onChange={(e) => patchConfig({ showName: e.target.checked })}
          />
          이름란
        </label>
        {showName && (
          <div className="space-y-2 pl-1">
            <div className="grid grid-cols-2 gap-2">
              {heightField('높이(mm)', 'nameHeight', '이름란 높이를 SMS 기본값(≈6mm)으로')}
            </div>
            {padField('namePadding', '이름란 패딩을 SMS 기본값(상하 1.06·좌우 1.59mm)으로')}
            <ApprovalFontEditor label="폰트" font={config.nameFont} onChange={(font) => patchConfig({ nameFont: font })} onPreview={(font) => patchConfigLive({ nameFont: font })} onRevert={onRevert} defaultColor={DEFAULT_APPROVAL_CONFIG.nameFont?.color} />
            {rectField('nameRect')}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 결재란 영역(단계명란/이름란) 폰트 편집기 — 폰트 select + 크기 + B/I 토글(P2a-3).
 * 기존 텍스트 노드 폰트 UI(TextStyleEditor) 패턴을 결재란 폰트(ApprovalFontStyle)에 맞춰 재사용.
 * 모든 필드 빈값 = font undefined로 전달(상위가 키 제거 → CSS 기본).
 */
function ApprovalFontEditor({
  label,
  font,
  onChange,
  onPreview,
  onRevert,
  defaultColor,
}: {
  label: string;
  font: ApprovalFontStyle | undefined;
  onChange: (font: ApprovalFontStyle | undefined) => void;
  /** 크기(pt)/자간/줄간격 실시간 미리보기(replacePresent). 미전달 시 onChange 폴백(즉시 확정). */
  onPreview?: (font: ApprovalFontStyle | undefined) => void;
  /** 미리보기 폐기(discardTransient) — Esc/이탈. 미전달 시 no-op. */
  onRevert?: () => void;
  /**
   * 글자색 "초기화" 시 적용할 기본 색(결재란 단계명란/이름란 전용, 구 SMS 본문색 #111 등).
   * 전달 시 "초기화"는 color를 undefined(=CSS 검정)가 아니라 이 값으로 되돌린다(BorderEditor baseValue 패턴).
   * 미전달이면 기존대로 undefined(CSS 기본). active(초기화 노출) 판별도 색이 이 값과 같으면 "기본"으로 본다.
   */
  defaultColor?: string;
}) {
  const f = font ?? {};

  // 일부 필드 갱신 후 모든 필드가 비면 undefined(미설정)로 정리.
  const build = (next: Partial<ApprovalFontStyle>): ApprovalFontStyle | undefined => {
    const merged: ApprovalFontStyle = { ...f, ...next };
    (Object.keys(merged) as (keyof ApprovalFontStyle)[]).forEach((k) => {
      const v = merged[k];
      if (v === undefined || v === false || v === '') delete merged[k];
    });
    return Object.keys(merged).length === 0 ? undefined : merged;
  };
  const patch = (next: Partial<ApprovalFontStyle>) => onChange(build(next));
  // 숫자 입력 실시간 미리보기 — onPreview 미전달이면 onChange 폴백.
  const patchLive = (next: Partial<ApprovalFontStyle>) => (onPreview ?? onChange)(build(next));
  const patchRevert = onRevert ?? (() => {});

  const toggleBtn = (active: boolean) =>
    `px-2 py-1 text-xs rounded border ${
      active
        ? 'bg-brand-600 text-white border-brand-600'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`;

  return (
    <div className="space-y-1.5">
      <span className="text-[11px] text-gray-500 block">{label}</span>
      <select
        className={SELECT_CLS}
        value={f.fontFamily ?? ''}
        onChange={(e) => patch({ fontFamily: e.target.value || undefined })}
      >
        <option value="">(기본)</option>
        {COMPOSER_FONTS.map((font2) => (
          <option key={font2.value} value={font2.value} style={{ fontFamily: font2.value }}>
            {font2.label}
          </option>
        ))}
      </select>
      <div className="flex items-center gap-1">
        <NumericField
          value={f.fontSize ?? undefined}
          min={1}
          step={0.1}
          placeholder="크기(pt)"
          toDisplay={pxToPt}
          fromDisplay={ptToPx}
          onPreview={(px) => patchLive({ fontSize: px })}
          onCommit={(px) => patch({ fontSize: px })}
          onRevert={patchRevert}
        />
        <button
          type="button"
          aria-pressed={!!f.italic}
          title="이탤릭"
          className={`${toggleBtn(!!f.italic)} italic`}
          onClick={() => patch({ italic: f.italic ? undefined : true })}
        >
          I
        </button>
      </div>
      {/* 굵기(font-weight) — B 토글과 공존(지정 시 우선). */}
      <FontWeightSelect value={f.fontWeight} onChange={(w) => patch({ fontWeight: w })} />
      {/* 자간(%) */}
      <LetterSpacingInput
        value={f.letterSpacing}
        onPreview={(s) => patchLive({ letterSpacing: s })}
        onCommit={(s) => patch({ letterSpacing: s })}
        onRevert={patchRevert}
      />
      {/* 줄간격(line-height) */}
      <LineHeightInput
        value={f.lineHeight}
        onPreview={(s) => patchLive({ lineHeight: s })}
        onCommit={(s) => patch({ lineHeight: s })}
        onRevert={patchRevert}
      />
      {/* 글자색 */}
      <div className="flex items-center gap-1">
        <span className="text-[11px] text-gray-500 w-9">글자색</span>
        <input
          type="color"
          className="h-7 w-10 border border-gray-300 rounded p-0.5"
          value={f.color ?? defaultColor ?? '#000000'}
          onChange={(e) => patch({ color: e.target.value })}
        />
        {/* 초기화 — defaultColor 전달 시 그 색(#111 등)으로, 미전달이면 undefined(CSS 기본)으로 되돌린다.
            현재 색이 기본과 같으면(undefined 또는 defaultColor) 버튼 숨김. */}
        {f.color !== undefined && f.color !== defaultColor && (
          <button
            type="button"
            className="text-[11px] text-gray-500 underline"
            onClick={() => patch({ color: defaultColor })}
          >
            초기화
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 결재라인 구조 섹션(P2a-2) — 고정 단계 + 최대 추가 결재 수. 세로로 길어 접이식(기본 펼침)으로
 * 묶어 패널 스크롤 부담을 줄인다(P2a-3 패널 개선).
 */
function ApprovalLineStructureSection({
  approvalLineMeta,
  onUpdateApprovalLineMeta,
}: {
  approvalLineMeta?: { defaultApprovalLine: ApprovalLineMetaRow[]; maxAdditionalLines: number };
  onUpdateApprovalLineMeta: (
    patch: Partial<{ defaultApprovalLine: ApprovalLineMetaRow[]; maxAdditionalLines: number }>,
  ) => void;
}) {
  const [open, setOpen] = useState(true);
  const lines = approvalLineMeta?.defaultApprovalLine ?? [];
  const lineCount = lines.length;

  // 한도 초과 경고는 두지 않는다 — **양식이 곧 판정의 base**라 양식이 자기 한도를 위반하는
  // 상태 자체가 없다(서버: 비-locked 총수 ≤ 양식 단독 비-locked 칸 수 + maxAdditionalLines).
  // 2026-08-13에 옛 판정(비-locked 총수 ≤ 한도)을 근거로 경고를 넣었다가, 그 판정이 폐기되면서
  // 함께 걷어냈다. (prefix 경고는 별개 축이라 유지)

  return (
    <div className="mt-4 pt-3 border-t border-gray-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 mb-2"
        aria-expanded={open}
      >
        <span>
          결재라인 구조{lineCount > 0 ? ` (고정 ${lineCount}단계)` : ''}
        </span>
        <span className="text-gray-400">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <>
          <ApprovalLineMetaEditor
            lines={lines}
            onChange={(lines) => onUpdateApprovalLineMeta({ defaultApprovalLine: lines })}
            disableMemberType
            compact
            // 양식은 칸의 **뼈대**를 정의하고 현장·작성자가 운영에 맞게 손대는 것이 기본이라,
            // 새 칸을 잠근 채 시작하면 저작자가 매번 풀어야 한다.
            // 여기서 만든 칸은 고정이든 편집 허용이든 `maxAdditionalLines` 한도를 쓰지 않는다 —
            // 양식이 곧 한도 판정의 base다.
            newRowLocked={false}
          />
          <div className="mt-3">
            {/* 한도는 **양식이 정의한 칸 위에 더 붙일 수 있는 칸 수**다 — 위에서 만든 칸(고정·편집
                허용 모두)은 세지 않고, 현장·작성자가 그 위에 만드는 칸만 센다
                (서버 `setReportApprovalLines` ── 비-locked 총수 ≤ 양식 단독 비-locked 칸 수 +
                maxAdditionalLines). 담당자 지정 여부와는 무관하다(미지정 칸도 한 칸).
                라벨에 "양식 외"를 명시하는 이유: "편집 가능한 결재란 최대 수"는 위에서 만든 편집
                허용 칸까지 세는 것으로 읽혀, 옛 판정(모델 B)의 오해를 그대로 남긴다. */}
            <label className="text-[11px] text-gray-500 mb-1 block" htmlFor="approval-max-additional">
              양식 외 추가 결재란 최대 수
            </label>
            <input
              id="approval-max-additional"
              type="number"
              min={0}
              max={10}
              value={approvalLineMeta?.maxAdditionalLines ?? 0}
              onChange={(e) => {
                const raw = Number(e.target.value);
                const clamped = Number.isFinite(raw)
                  ? Math.min(10, Math.max(0, Math.trunc(raw)))
                  : 0;
                onUpdateApprovalLineMeta({ maxAdditionalLines: clamped });
              }}
              className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              <strong>위에서 만든 칸은 세지 않습니다</strong> — 고정이든 편집 허용이든 양식이
              정의한 칸은 면제이고, 현장·작성자가 그 위에 <strong>더 만들 수 있는</strong> 칸
              수입니다. 담당자 지정 여부와는 무관합니다(담당자를 비워 둔 칸도 한 칸).
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/** 그룹 자식 목록 등에서 노드를 짧게 식별하는 한글 라벨. */
function shortNodeLabel(n: ComposerNode): string {
  switch (n.kind) {
    case 'text':
      return n.text ? `텍스트 "${n.text.slice(0, 14)}"` : '텍스트';
    case 'table':
      return `표 ${n.rowCount}×${n.colCount}`;
    case 'special':
      return n.special === 'approvalBlock' ? '결재란' : n.special;
    case 'group':
      return `그룹 (${n.children.length})`;
    case 'stack':
      return `스택 (${n.children.length})`;
    case 'repeat':
      return `반복 (${n.over})`;
    case 'branch':
      return `분기 (${n.branches.length}슬롯)`;
    case 'image':
      return '이미지';
    case 'rect':
      return '사각형';
    case 'line':
      return '선';
    case 'spacer':
      return '여백';
    case 'checkbox':
      return n.label ? `체크박스 "${n.label.slice(0, 14)}"` : '체크박스';
    case 'choiceGroup':
      return '선택지 그룹';
    default:
      return (n as ComposerNode).kind;
  }
}

/**
 * 반복(repeat) 노드 편집 — 순회할 데이터 소스(over) 선택 + 반복 단위(template) 자식 드릴다운.
 *
 * 소스 목록은 collectRepeatSources(내장 dataDef / 카탈로그 list entry / 조립 dataSource)를
 * 재사용해 표 반복영역 소스 콤보와 일관되게 제시한다(seedOnly 카탈로그는 소비 측에서 제외).
 *  - 카탈로그 list entry 선택: over=key + binding{ bindingKey:key, catalogKey:key, lifecycle:'snapshot' }
 *    (resolveNodeBinding이 그 배열을 RenderData[key]에 적재 → RenderTemplate case 'repeat'의 over가 소비).
 *  - dataDef/dataSource 선택: over=key만(resolver가 def.key로 flat 적재) + binding 제거.
 * 반복 단위(template group)와 그 자식은 캔버스에서 직접 클릭 선택·편집한다(여기선 드릴다운 버튼만 제공).
 */
/**
 * 페이지 흐름 속성(pageBreak·startOnNewPage·pageFill·fillSplitGroup)은 페이지 계획이 **평탄화된
 * 최상위 흐름**에서 읽는다 — body 직계 + 하강 branch의 슬롯 직계 자식(branchPagination
 * flattenBodyForMeasure가 branch를 자식들로 치환해 흐름에 편입, 중첩 branch 재귀 포함). 그 밖의
 * 진짜 중첩(group 자식·repeat template·표 셀 content)은 부모가 통째 측정되어 무동작이므로 편집
 * UI를 이 안내와 함께 비활성화해 무동작 함정을 막는다. tablePolicy는 렌더러가 CSS(breakInside)로
 * 어느 깊이서든 소비하므로 제외.
 *
 * ⚠️ branch 직계는 위치상 허용하되 근사다 — 엔진의 실제 하강은 활성 슬롯 판정(branchDescentSlot:
 * 슬롯 자식에 표/반복이 있어야 등) 조건부라, 하강하지 않는 branch 안에서는 여전히 무시된다.
 * 완전 판정은 렌더 데이터 의존이라 에디터에서 못 하고, 과잉 차단(동작하는 자리까지 잠금 —
 * 2026-08-31 TBM root branch에서 실사용 적발)보다 위치 근사 허용이 낫다.
 */
const NESTED_PAGE_FLOW_HINT =
  '이 위치에서는 동작하지 않습니다 — 페이지 분할·채움은 문서 최상위·분기 슬롯 직계, 그리고 페이지를 나눠 흐르는 반복 단위의 직계 노드에서만 적용됩니다.';

/**
 * 노드가 페이지 흐름 위치인지 판정 — 참인 세 갈래(전부 엔진 소비 경로의 위치 미러):
 * 1. body 직계 / branch 슬롯 직계(중첩 branch 재귀) — flattenBodyForMeasure 하강 규칙.
 * 2. **세로 나열 반복 단위의 청크**(2026-09-05 확장 + 유일 경계 승격): 단위 template의
 *    `unitFlowChunks` 청크열 소속이면 참(청크 repeat 안의 청크도 재귀 — 측정 재귀와 동형).
 *    pageBreak를 여기 선언하는 순간 선언 존재로 엔진 청크 모드가 서고(chunkFlowActive), 유일
 *    경계 형상도 unitHasBreakDecl 승격으로 청크 경로가 소비하므로 sole·분할 청크 조건은 두지
 *    않는다. 다단 단위·group에 묻힌 자리는 종전대로 거짓(엔진이 안 읽는다).
 * 3. **단위 안 이득 branch 노드 자체**(2026-09-05 전파 일원화): 평탄화로 청크열에는 없지만
 *    branch pageBreak가 각 슬롯 첫 청크로 전파돼 소비된다(unitFlowChunks 주석) — 슬롯별 이중
 *    선언 없이 branch 한 곳 선언이 정본이 되도록 이 자리를 연다. 이득 판정은 엔진과 같은
 *    함수(branchGainsChunks).
 * 판정 함수(unitFlowChunks)가 렌더 데이터 없이 template 구조만 보므로 여기 미러는 근사가 아니라
 * 동치다 — 남는 근사는 branch 활성 슬롯(상단 NESTED_PAGE_FLOW_HINT 주석의 한계) + 선언 없는
 * 단위의 startOnNewPage 무소비 축(inChunkUnit 주석).
 */
export function isPageFlowPosition(body: ComposerNode[], nodeId: string): boolean {
  // nodeId가 이 repeat 단위 계보의 청크인가 — **세로 나열 단위의 unitFlowChunks 소속**이면 참
  // (재귀 포함). 유일 경계·분할 청크 존재 조건은 두지 않는다(2026-09-05 승격): 이 위치에
  // pageBreak를 선언하는 순간 선언 존재로 청크 모드가 서고(엔진 chunkFlowActive), 유일 경계
  // 형상도 unitHasBreakDecl 승격으로 청크 경로가 소비한다 — 조건을 두면 "선언해야 열리는
  // 자리를 선언 전에 잠그는" 순환이 된다. 남는 근사: 선언 없는 단위의 startOnNewPage는 단위
  // 안에서 원래 무소비(계획 규약)라 그 축만 무동작 허용(과잉 차단보다 낫다 — branch 근사 동형).
  const inChunkUnit = (r: RepeatNode): boolean => {
    if ((r.columns ?? 1) > 1) return false;
    const chunks = unitFlowChunks(r.template);
    if (chunks.some((c) => c.id === nodeId || (c.kind === 'repeat' && inChunkUnit(c)))) {
      return true;
    }
    // 이득 branch **노드 자체**도 흐름 위치(2026-09-05 전파 일원화) — 평탄화로 청크열에는 없지만
    // 엔진이 branch pageBreak를 각 슬롯 첫 청크에 전파해 소비한다(unitFlowChunks 주석). 이득
    // 판정은 엔진과 같은 함수(branchGainsChunks) — 비이득 branch는 원자 청크라 위 청크 검사에서
    // 이미 참이고, 그 **슬롯 자식**은 엔진이 안 읽으므로(원자 통짜) 여기서 내려가지 않는다.
    // 하강은 이득 branch 슬롯만(엔진 평탄화와 같은 규칙 — 슬롯 자식 청크는 위 검사 몫이고,
    // 여기서는 중첩 이득 branch 노드 자신만 찾는다).
    const gainsBranchHit = (nodes: ComposerNode[]): boolean =>
      nodes.some(
        (n) =>
          n.kind === 'branch' &&
          branchGainsChunks(n) &&
          (n.id === nodeId || n.branches.some((b) => gainsBranchHit(b.children))),
      );
    return gainsBranchHit(r.template.children);
  };
  const walk = (nodes: ComposerNode[]): boolean =>
    nodes.some(
      (n) =>
        n.id === nodeId ||
        (n.kind === 'branch' && n.branches.some((b) => walk(b.children))) ||
        (n.kind === 'repeat' && inChunkUnit(n)),
    );
  return walk(body);
}

/**
 * 속성 패널의 「바인딩」 섹션을 이 노드 종류에 노출할지 — 숨김은 **중복 편집 회피**가 근거이지
 * "렌더가 무시한다"가 아니다(그 단정으로 표를 가렸던 것이 2026-09-01 오해).
 *
 * rc resolver의 `walkNode`는 kind 분기 **이전에** `resolveNodeBinding(node, …)`을 무조건 부르므로
 * 모든 종류의 node.binding이 RenderData[bindingKey]에 적재된다(compose 측 walkComposeNode도 동일).
 * 표에서 이 배선은 실사용 중이다 — TBM 회의록 단일회차 참석자 표가 표 노드 binding으로 반복영역
 * `over`와 같은 키에 live 값을 덮는다(tbm-meeting-log.template.ts attendeesSection). 숨겨 두면
 * 코드로 만든 그 템플릿을 저작자가 에디터에서 재현할 수 없다.
 *
 * 여전히 숨기는 둘(각자 다른 이유):
 * - `repeat`: 배열 소스 바인딩을 「반복 데이터」 콤보(RepeatSourceEditor)가 일원 관리한다 —
 *   같은 값을 두 자리에서 편집하게 만들지 않는다. (표는 해당 없음 — 표 반복영역 콤보는
 *   `region.over`만 만지고 node.binding에 손대지 않아 편집 자리가 겹치지 않는다.)
 * - `group`: 라디오 그룹에서 "공유 키"(자식 체크박스들의 binding.bindingKey)와 혼동을 유발한다.
 *   (사용자 결정 2026-07-07: 일단 모든 그룹에서 숨기고 필요 시 재도입.)
 */
export function nodeKindHasBindingEditor(kind: ComposerNode['kind']): boolean {
  return kind !== 'repeat' && kind !== 'group';
}

/**
 * 페이지 흐름 옵션의 중첩 게이트 3속성(라벨 커서/툴팁/disabled) 산출 — 7곳 나열이 스타일
 * (opacity vs 회색 텍스트)·문구 2갈래로 갈라졌던 것을 일치화. on(현재 값)이 남아 있으면
 * 잔존값 해제는 허용하고 새로 켜는 것만 막는다. 비최상위 안내 각주는 짝인
 * <NestedPageFlowNote/>가 담당(라벨 툴팁과 같은 문구).
 */
function pageFlowGate(
  isTopLevel: boolean,
  on: boolean,
  enabledTitle: string,
): { labelClass: string; title: string; disabled: boolean } {
  const enabled = isTopLevel || on;
  return {
    labelClass: enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
    title: isTopLevel ? enabledTitle : NESTED_PAGE_FLOW_HINT,
    disabled: !enabled,
  };
}

/** 비최상위 노드의 페이지 흐름 안내 각주 — pageFlowGate와 짝(문구·스타일 단일화). */
function NestedPageFlowNote({ isTopLevel }: { isTopLevel: boolean }) {
  if (isTopLevel) return null;
  return <p className="text-[11px] leading-4 text-gray-400">{NESTED_PAGE_FLOW_HINT}</p>;
}

/** export: 「소스 편집 →」 노출·호출 계약 spec — DataSourceEditJump.rtl.test.tsx */
export function RepeatSourceEditor({
  node,
  isTopLevel,
  catalog,
  dataDefs,
  dataSources,
  parentRowFieldSources,
  onPatch,
  onSelectNode,
  onGoToDataCard,
}: {
  node: RepeatNode;
  /** repeat 노드가 body 최상위인가 — startOnNewPage는 최상위에서만 동작(NESTED_PAGE_FLOW_HINT). */
  isTopLevel: boolean;
  catalog: CatalogEntry[];
  dataDefs: DataDef[] | undefined;
  dataSources: DataSourceDef[] | undefined;
  /**
   * 부모 반복(조상 repeat)의 배열 행 필드 후보(`parentRowArrayFieldOptions`) — 중첩 반복의 over가
   * 최상위 소스가 아니라 부모 행 필드일 때 미선택으로 보이지 않게(2026-09-05 attendeeTriples 사례).
   * 판정 재료(조상 체인)가 없는 컨텍스트는 미전달 → 종전 표시 유지.
   */
  parentRowFieldSources?: { key: string; label: string }[];
  onPatch: (patch: Partial<ComposerNode>) => void;
  onSelectNode: (id: string) => void;
  /** 「소스 편집 →」 — 이 반복이 쓰는 선언의 편집 카드로 이동(선택 해제 + 카드 펼침 + 스크롤). */
  onGoToDataCard?: (key: string) => void;
}) {
  const sources = collectRepeatSources(dataDefs, catalog, dataSources).filter(
    (s) => !s.seedOnly,
  );
  // 현재값 매칭 — over 키 일치 우선, 없으면 binding(catalogKey+select) 기반 역추적(findRepeatSource).
  // 최상위 소스에 없으면 부모 행 필드(중첩 반복) 후보에서 찾는다.
  const parentFieldMatch = parentRowFieldSources?.find((p) => p.key === node.over);
  const currentKey = findRepeatSource(sources, node)?.key ?? parentFieldMatch?.key ?? '';
  const currentKnown = currentKey !== '';
  const templateChildren = node.template.children;
  const applySource = (key: string): void => {
    if (!key) {
      onPatch({ over: '', binding: undefined } as Partial<ComposerNode>);
      return;
    }
    // 부모 행 필드(중첩 반복): 안쪽 반복이 부모 행 객체의 그 배열을 스코프에서 직접 읽으므로
    // binding이 없어야 한다(bare over — 세션 부 참석자·안전조치가 실전 형상).
    if (parentRowFieldSources?.some((p) => p.key === key)) {
      onPatch({ over: key, binding: undefined } as Partial<ComposerNode>);
      return;
    }
    const src = sources.find((s) => s.key === key);
    if (!src) return;
    if (src.kind === 'catalog') {
      // 카탈로그 list entry: 배열을 bindingKey(=over)에 적재하도록 repeat 노드에 binding을 세팅.
      onPatch({
        over: src.key,
        binding: { bindingKey: src.key, catalogKey: src.key, lifecycle: 'snapshot' },
      } as Partial<ComposerNode>);
    } else if (src.kind === 'catalogField') {
      // 단건 entry의 배열 필드: binding(catalogKey=entryKey, select=fieldPath)으로 배열을 적재.
      onPatch({
        over: src.key,
        binding: {
          bindingKey: src.key,
          catalogKey: src.entryKey,
          select: src.fieldPath,
          lifecycle: 'snapshot',
        },
      } as Partial<ComposerNode>);
    } else {
      // dataDef/dataSource/form: resolver가 def.key로 flat 적재 → over만 지정, binding 제거.
      onPatch({ over: src.key, binding: undefined } as Partial<ComposerNode>);
    }
  };
  return (
    <div className="space-y-2">
      <div>
        <dt className="text-xs font-semibold text-gray-500 mb-1">반복 데이터</dt>
        <dd className="space-y-1">
          <select
            value={currentKey}
            onChange={(e) => applySource(e.target.value)}
            className={INPUT_CLS}
          >
            <option value="">— 반복할 데이터 선택 —</option>
            {/* 부모 행 필드(중첩 반복) — 이 반복이 부모 반복 안일 때만 후보가 있다. */}
            {parentRowFieldSources && parentRowFieldSources.length > 0 && (
              <optgroup label="부모 행 필드(중첩 반복)">
                {parentRowFieldSources.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </optgroup>
            )}
            {sources.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label} ({SOURCE_KIND_LABEL[s.kind]})
              </option>
            ))}
          </select>
          {parentFieldMatch && (
            <p className="text-[11px] text-gray-400 leading-snug">
              부모 반복의 행마다 그 행의 「{parentFieldMatch.label}」 목록을 반복합니다.
            </p>
          )}
          {node.over && !currentKnown && (
            <p className="text-[11px] text-amber-600 leading-snug">
              현재 반복 데이터 키 <span className="font-mono">{node.over}</span> 는 목록에
              없습니다(직접 지정했거나 삭제된 소스).
            </p>
          )}
          {/* 선택된 소스를 고치는 자리로 바로 이동 — 저작자가 카드를 눈으로 찾지 않게. */}
          {onGoToDataCard && hasEditableDataCard(currentKey, dataDefs, dataSources) && (
            <div>
              <DataSourceEditLink onGo={() => onGoToDataCard(currentKey)} />
            </div>
          )}
          <p className="text-[11px] text-gray-400 leading-snug">
            선택한 데이터의 항목 수만큼 아래 <b>반복 단위</b>가 반복 렌더됩니다. 항목 필드는 반복 단위 안
            노드의 바인딩(필드 키)으로 읽습니다.
          </p>
        </dd>
      </div>
      <div>
        <dt className="text-xs font-semibold text-gray-500 mb-1">배치</dt>
        <dd className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5" title="2 이상이면 항목을 가로 N단으로 배치합니다(카드 2단 등).">
            <span className="text-xs text-gray-600">단 수</span>
            <select
              value={node.columns ?? 1}
              onChange={(e) => {
                const v = Number(e.target.value);
                onPatch({ columns: v >= 2 ? v : undefined } as Partial<ComposerNode>);
              }}
              className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5" title="항목 간 간격(mm) — 다단 grid 간격 / 세로 나열 행 간격.">
            <span className="text-xs text-gray-600">간격(mm)</span>
            <input
              type="number"
              min={0}
              step={0.5}
              placeholder="0"
              key={`${node.id}-gap-${node.gap ?? 'none'}`}
              defaultValue={node.gap !== undefined ? pxToMm(node.gap) : ''}
              onBlur={(e) => {
                const v = Number(e.target.value);
                const next = Number.isFinite(v) && v > 0 ? mmToPx(v) : undefined;
                if (next !== node.gap) onPatch({ gap: next } as Partial<ComposerNode>);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              className="w-16 text-xs border border-gray-300 rounded px-1.5 py-0.5"
            />
          </label>
          {(() => {
            // 중첩 게이트(pageFlowGate 공용) — 새로 켜는 것만 막고 잔존값 해제는 허용.
            const gate = pageFlowGate(
              isTopLevel,
              !!node.startOnNewPage,
              '반복 전체가 남은 공간에 통째로 안 들어가면 새 페이지에서 시작합니다(그 후에도 페이지보다 크면 항목 단위로 분할 — 표의 동일 옵션과 같은 의미).',
            );
            return (
              <label className={`flex items-center gap-1.5 ${gate.labelClass}`} title={gate.title}>
                <input
                  type="checkbox"
                  checked={!!node.startOnNewPage}
                  disabled={gate.disabled}
                  onChange={(e) =>
                    onPatch({ startOnNewPage: e.target.checked || undefined } as Partial<ComposerNode>)
                  }
                />
                <span className="text-xs text-gray-600">새 페이지에서 시작</span>
              </label>
            );
          })()}
          <NestedPageFlowNote isTopLevel={isTopLevel} />
        </dd>
      </div>
      <div>
        <dt className="text-xs font-semibold text-gray-500 mb-1">반복 단위</dt>
        <dd className="space-y-1">
          <button
            type="button"
            onClick={() => onSelectNode(node.template.id)}
            title={`${node.template.id} 선택`}
            className="w-full text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
          >
            <span className="truncate">{shortNodeLabel(node.template)}</span>
            <span className="text-[10px] text-gray-400 font-mono shrink-0">{node.template.id}</span>
          </button>
          {templateChildren.length === 0 ? (
            <p className="text-xs text-gray-400">
              반복 단위가 비어 있습니다. 반복 단위를 선택한 뒤 팔레트로 요소를 추가하세요.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {templateChildren.map((child) => (
                <li key={child.id}>
                  <button
                    type="button"
                    onClick={() => onSelectNode(child.id)}
                    title={`${child.id} 선택`}
                    className="w-full text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{shortNodeLabel(child)}</span>
                    <span className="text-[10px] text-gray-400 font-mono shrink-0">{child.id}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-gray-400 leading-snug">
            편집 캔버스는 데이터가 없으면 반복 단위를 1회(뼈대)만 표시합니다.
          </p>
        </dd>
      </div>
    </div>
  );
}

/**
 * 분기 슬롯 편집 — branch 노드의 슬롯 목록(조건 편집 + 순서/추가/삭제) + 활성 슬롯 자식 드릴다운.
 * 슬롯 헤더 클릭 = 활성 슬롯 전환(캔버스 탭과 공유 state). condition 빈 입력 = undefined(else)로 정규화.
 */
/**
 * 분기 조건 데이터 편집기(branch 노드 전용) — 슬롯 condition이 비교할 식별 키(key) + 출처(binding)를
 * 행 단위로 추가/삭제/편집한다. 출처 선택은 셀/노드 바인딩과 동일한 BindingEditor(가짜 노드로 감쌈)를
 * 재사용한다. 편집은 onChange(next)로 위임(EditorPage가 updateBranchConditionBindings op 경유 → undo 통합).
 *
 * key는 RenderData 적재 키(evaluateCondition 좌변)로, 다른 노드/셀 bindingKey와 겹치면 값 공유 함정이
 * 생기므로 usedBindingKeys 중복 경고 톤을 재사용한다(BindingEditor 식별 키 경고와 동일 색).
 */
function BranchConditionBindingsEditor({
  node,
  catalog,
  usedBindingKeys,
  onChange,
}: {
  node: BranchNode;
  catalog: CatalogEntry[];
  usedBindingKeys?: Set<string>;
  onChange: (next: ConditionBinding[]) => void;
}) {
  const items = node.conditionBindings ?? [];

  const replaceAt = (index: number, next: ConditionBinding): void => {
    onChange(items.map((it, i) => (i === index ? next : it)));
  };
  const removeAt = (index: number): void => {
    onChange(items.filter((_, i) => i !== index));
  };
  const addRow = (): void => {
    // 신규 행: 빈 key + 최소 binding(스냅샷). 출처는 BindingEditor로 이어서 고른다.
    const next: ConditionBinding = {
      key: '',
      binding: { bindingKey: '', lifecycle: 'snapshot' },
    };
    onChange([...items, next]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <dt className="text-xs font-semibold text-gray-500">조건 데이터 ({items.length})</dt>
        <button
          type="button"
          onClick={addRow}
          className="text-xs px-2 py-0.5 rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
        >
          + 조건 키
        </button>
      </div>
      <p className="text-[11px] text-gray-400 leading-snug">
        슬롯 조건(아래)이 비교할 식별 키와 그 값 출처입니다. 여기 등록한 키가 각 슬롯 조건의{' '}
        <b>키 선택</b> 목록에 나타납니다(비교 연산·값도 목록에서 고릅니다 — 값 후보가 정해진
        필드면 선택, 아니면 직접 입력).
      </p>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">
          없음 — 슬롯 조건에서 비교할 데이터가 있으면 "+ 조건 키"로 추가하세요.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((cb, i) => (
            <ConditionBindingRow
              key={i}
              index={i}
              item={cb}
              branchNodeId={node.id}
              catalog={catalog}
              keyDuplicate={!!cb.key && !!usedBindingKeys?.has(cb.key)}
              onReplace={(next) => replaceAt(i, next)}
              onRemove={() => removeAt(i)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 조건 데이터 한 행 — 식별 키 입력 + 출처(BindingEditor).
 *
 * 별도 컴포넌트인 이유는 훅 때문이다. 식별 키 칸은 포커스 동안 같은 키를 쓰는 다른 자리를
 * 캔버스에 강조하는데(useSharedKeyFocus), 훅은 map 콜백 안에서 호출할 수 없다.
 */
function ConditionBindingRow({
  index,
  item,
  branchNodeId,
  catalog,
  keyDuplicate,
  onReplace,
  onRemove,
}: {
  index: number;
  item: ConditionBinding;
  /** 소유 branch 노드 id — 공유 키 강조에서 자기 자신을 빼는 기준. */
  branchNodeId: string;
  catalog: CatalogEntry[];
  keyDuplicate: boolean;
  onReplace: (next: ConditionBinding) => void;
  onRemove: () => void;
}) {
  // 조건 키는 branch 노드가 RenderData에 적재하는 키다 → 소유자는 그 branch 노드.
  const sharedKeyFocusProps = useSharedKeyFocus(`node:${branchNodeId}`, item.key);
  // BindingEditor가 요구하는 최소 ComposerNode 형태(조건 바인딩엔 노드 id가 없으므로 임시 id).
  const fakeNode = {
    id: item.binding.bindingKey || `${branchNodeId}-cond-${index}`,
    kind: 'text',
    layout: { width: 'fill' },
    binding: item.binding,
  } as ComposerNode;
  return (
    <li className="rounded border border-gray-200 p-1.5 space-y-1">
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={item.key}
          onChange={(e) => onReplace({ ...item, key: e.target.value })}
          placeholder="식별 키 — 예: methodType"
          className={
            keyDuplicate
              ? `${INPUT_CLS} font-mono border-red-400 focus:ring-red-400`
              : `${INPUT_CLS} font-mono`
          }
          aria-invalid={keyDuplicate || undefined}
          title="슬롯 조건이 참조하는 식별 키(RenderData 적재 키). 다른 바인딩 키와 겹치면 값이 공유됩니다 — 포커스를 두면 겹치는 자리가 캔버스에 표시됩니다."
          {...sharedKeyFocusProps}
        />
        <button
          type="button"
          onClick={onRemove}
          title="조건 키 삭제"
          className="text-[11px] px-1 rounded border border-red-200 text-red-600"
        >
          ✕
        </button>
      </div>
      {keyDuplicate && (
        <p className="text-[11px] text-red-600 leading-snug">
          이미 다른 노드에서 사용 중인 키입니다 — 같은 키는 입력값을 공유합니다.
        </p>
      )}
      {/* 목록은 경고와 별개로 항상 — 상호배타 슬롯만 공유하는 키는 경고가 없어도 값 공유 관계는
          보여야 한다(컴포넌트가 사용처 0곳이면 스스로 안 그린다). */}
      <SharedKeyUsageList bindingKey={item.key} selfOwnerId={`node:${branchNodeId}`} />
      <BindingEditor
        node={fakeNode}
        catalog={catalog}
        hideRemoveButton
        onPatch={(patch) => {
          if ('binding' in patch) {
            const binding = (patch as { binding?: Binding }).binding;
            // 조건 데이터는 항상 binding이 있어야 한다(제거 시 최소 binding 유지).
            onReplace({ ...item, binding: binding ?? { bindingKey: '', lifecycle: 'snapshot' } });
          }
        }}
      />
    </li>
  );
}

// 조건 빌더 연산자/결합자 옵션(라벨은 일반 사용자 기준 한글).
const CONDITION_OP_OPTIONS: Array<{ value: ConditionOp; label: string }> = [
  { value: 'eq', label: '같음' },
  { value: 'neq', label: '다름' },
  { value: 'truthy', label: '값 있음' },
  { value: 'falsy', label: '값 없음' },
];
const CONDITION_JOINER_OPTIONS: Array<{ value: ConditionJoiner; label: string }> = [
  { value: 'and', label: '그리고' },
  { value: 'or', label: '또는' },
];

/**
 * '＋칼럼 추가' 콤보에서 **조회 경로 후보**를 선언 필드 후보와 구분하는 접두사.
 * 두 후보군(선언 필드 키 / from 경로)이 한 select에 섞이므로 값 공간을 갈라 둔다.
 * 필드 키에는 콜론이 올 수 없어(flatKeyError) 충돌하지 않는다.
 */
const ADD_COL_PATH_PREFIX = 'path:';

/** EnumValueInput의 "직접 입력…" 선택지 sentinel value(enum value-set와 충돌하지 않는 값). */
const DIRECT_INPUT_SENTINEL = '\u0000__direct__';

/**
 * enum 값 입력 하이브리드(콤보 select + 직접 입력) 위젯.
 *
 * - 기본은 라벨 콤보(select). 맨 끝 "직접 입력…" 선택 시 텍스트 입력으로 전환(⇄ 버튼으로 콤보 복귀).
 * - 현재 값이 옵션 목록(value)에 없으면 자동으로 직접 입력 모드로 표시(값 보존 — select가 목록 밖
 *   값을 삼키지 않게). 이때는 값이 목록 값과 일치하기 전까지 콤보 복귀가 비활성.
 * - 직접 입력 중 값이 목록의 value와 일치하면 콤보 복귀 시 그 항목이 선택된다(value=현재 값).
 * - 모드는 로컬 UI state만 사용(직렬화·스키마 무변경 — 값은 그대로 onChange).
 */
function EnumValueInput({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const [manualRaw, setManualRaw] = useState(false);
  const forcedRaw = shouldStartDirectValueInput(value, options);
  const rawMode = manualRaw || forcedRaw;

  if (rawMode) {
    return (
      <span className="flex flex-1 min-w-[6rem] items-center gap-1">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="값 직접 입력"
          className={`${INPUT_CLS} flex-1 min-w-[4rem] font-mono`}
          title="비교 값(직접 입력)"
        />
        <button
          type="button"
          onClick={() => setManualRaw(false)}
          disabled={forcedRaw}
          title={
            forcedRaw
              ? '현재 값이 목록에 없어 직접 입력만 가능합니다.'
              : '목록에서 선택으로 전환'
          }
          className="text-[11px] px-1 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
        >
          ⇄
        </button>
      </span>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === DIRECT_INPUT_SENTINEL) {
          setManualRaw(true); // 값 유지, 텍스트 입력으로 전환
          return;
        }
        onChange(e.target.value);
      }}
      className={`${SELECT_CLS} flex-1 min-w-[6rem]`}
      title="비교 값"
    >
      <option value="">(값 선택)</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      <option value={DIRECT_INPUT_SENTINEL}>직접 입력…</option>
    </select>
  );
}

/**
 * 슬롯 condition 빌더 코어(본문·위젯 슬롯 공용) — 빌더(콤보박스) 기본 + "직접 입력"(화이트리스트 문법
 * 텍스트) 토글.
 *
 * conditionExpr 모델을 편집하고 serializeCondition으로 문자열을 만들어 onChange로 넘긴다(기존 op 경유 —
 * 스키마·엔진 무변경). 기존 condition이 화이트리스트 문법을 벗어나 parseCondition이 null이면 자동으로
 * 직접 입력 모드 + 안내 배지가 뜬다. 빌더→직접은 항상, 직접→빌더는 파싱 가능할 때만 전환된다.
 *
 * 평가 불가 절(하이픈 식별자 등 — evaluateCondition이 조용히 참으로 두는 함정) 경고도 여기서
 * 소유한다. 호출측마다 따로 두면 판정 규칙이 사본으로 갈라진다(판정은 conditionExpr 정본 위임).
 *
 * 후보 공급원만 호출측이 주입한다(본문=conditionBindings/카탈로그, 위젯=작성폼 select/radio 키):
 *   - registeredKeys: 키 콤보 후보(순서 보존). 현재 식의 미등록 키는 여기에 union되어 함께 표시된다.
 *   - valueSetForKey: 키별 값 enum 옵션(있으면 VALUE 라벨 콤보, 없으면 텍스트). 값있음/값없음이면 값 숨김.
 *   - rawPlaceholder/rawTitle: 직접 입력 텍스트박스의 예시/안내(본문↔위젯 문맥 차이).
 */
function SlotConditionBuilder({
  condition,
  registeredKeys,
  valueSetForKey,
  keyLabelFor,
  keyTitleFor,
  rawPlaceholder,
  rawTitle,
  onChange,
}: {
  condition: string | undefined;
  registeredKeys: string[];
  valueSetForKey: (key: string) => { value: string; label: string }[] | undefined;
  /**
   * 키 select의 option 표시 텍스트를 사람이 읽는 라벨로 치환(저장값 value는 키 그대로).
   * undefined 반환(또는 미전달) 시 키를 그대로 표시 — 출처를 못 푼 키의 폴백이다.
   */
  keyLabelFor?: (key: string) => string | undefined;
  /**
   * 키 select option의 hover title(마우스오버 툴팁) 치환 — 그 키 값의 출처 안내(2026-07-21).
   * 미전달이면 원 키를 title로 표시.
   */
  keyTitleFor?: (key: string) => string;
  rawPlaceholder: string;
  rawTitle: string;
  onChange: (condition: string | undefined) => void;
}) {
  const parsed = useMemo(() => parseCondition(condition), [condition]);
  const parseable = parsed !== null;
  // 사용자 명시 토글(직접 입력) 상태. 파싱 불가면 강제로 raw.
  const [rawToggled, setRawToggled] = useState(false);
  const rawMode = rawToggled || !parseable;
  // 평가 불가 절(하이픈 식별자 등) — 저장은 막지 않고 경고만. 이런 절이 있으면 parseCondition이
  // null이라 항상 raw 모드이므로, 직접 입력 아래 한 자리면 모든 진입점(본문 슬롯·위젯 슬롯·
  // 표 행 조건)이 같은 경고를 얻는다.
  const unevaluable = useMemo(() => collectUnevaluableClauses(condition), [condition]);

  // 편집 중 미완성 절(값 미입력 eq/neq)을 화면에 유지하기 위한 로컬 드래프트 모델.
  // serializeCondition이 완성 절만 직렬화하므로 미완성 절은 저장값(condition)에 포함되지 않지만,
  // 사용자가 값을 채울 때까지 절이 화면에서 사라지지 않도록 여기 로컬로 보관한다.
  // condition이 외부(raw 입력 등)에서 바뀌면 드래프트를 재동기화(lastEmittedRef로 자기 방출과 구분).
  const [draft, setDraft] = useState<ConditionModel>(() => parsed ?? { clauses: [] });
  const lastEmittedRef = useRef<string | undefined>(condition);
  useEffect(() => {
    if (condition !== lastEmittedRef.current) {
      setDraft(parsed ?? { clauses: [] });
      lastEmittedRef.current = condition;
    }
  }, [condition, parsed]);
  const model: ConditionModel = draft;

  // 사용 가능한 키: 주입 후보 키(registeredKeys) + 현재 식에 있는 미등록 키(union, 순서 보존).
  const registeredSet = new Set(registeredKeys);
  const availableKeys: string[] = [];
  for (const k of [...registeredKeys, ...model.clauses.map((c) => c.key)]) {
    if (k && !availableKeys.includes(k)) availableKeys.push(k);
  }

  const emit = (next: ConditionModel): void => {
    setDraft(next);
    const s = serializeCondition(next);
    const outgoing = s === '' ? undefined : s;
    lastEmittedRef.current = outgoing;
    onChange(outgoing);
  };

  const updateClause = (index: number, patch: Partial<ConditionClause>): void => {
    const clauses = model.clauses.map((c, i) => {
      if (i !== index) return c;
      const op = patch.op ?? c.op;
      const next: ConditionClause = {
        joiner: patch.joiner !== undefined ? patch.joiner : c.joiner,
        key: patch.key ?? c.key,
        op,
      };
      // 값 연산자(eq/neq)면 value 유지/기본 ''; 값 없는 연산자면 value 제거.
      if (opTakesValue(op)) {
        next.value = patch.value !== undefined ? patch.value : c.value ?? '';
      }
      return next;
    });
    emit({ clauses });
  };

  const addClause = (): void => {
    // 새 절 기본 연산자는 'eq'(같음). 값은 비워 두고(enum이면 미선택 placeholder) 사용자가 채우게 한다.
    // 값 미입력 상태에서는 serializeCondition이 이 절을 제외하므로 잘못된 `key === ''`로 저장되지 않는다.
    const clause: ConditionClause = {
      joiner: model.clauses.length === 0 ? undefined : 'and',
      key: availableKeys[0] ?? '',
      op: 'eq',
    };
    emit({ clauses: [...model.clauses, clause] });
  };

  const removeClause = (index: number): void => {
    const clauses = model.clauses.filter((_, i) => i !== index);
    // 새 첫 절이 되면 결합자 제거(첫 절은 joiner 없음).
    if (clauses.length > 0 && clauses[0].joiner !== undefined) {
      clauses[0] = { ...clauses[0], joiner: undefined };
    }
    emit({ clauses });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold text-gray-500">조건</span>
        <div className="flex items-center gap-1">
          {!parseable && (
            <span
              className="text-[10px] px-1 rounded bg-amber-100 text-amber-700 border border-amber-200"
              title="현재 조건이 빌더 문법(괄호·점표기·숫자 등)을 벗어나 직접 입력만 가능합니다."
            >
              복합식
            </span>
          )}
          <button
            type="button"
            onClick={() => setRawToggled((v) => !v)}
            disabled={rawMode && !parseable}
            className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            title={
              parseable
                ? '빌더 ↔ 직접 입력 전환'
                : '빌더 문법을 벗어나 직접 입력만 가능합니다.'
            }
          >
            {rawMode ? '빌더로' : '직접 입력'}
          </button>
        </div>
      </div>

      {rawMode ? (
        <div className="space-y-0.5">
          <input
            type="text"
            value={condition ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              onChange(v.trim() === '' ? undefined : v);
            }}
            placeholder={rawPlaceholder}
            className={`${INPUT_CLS} font-mono`}
            title={rawTitle}
          />
          {unevaluable.length > 0 ? (
            <p className="text-[10px] leading-tight text-amber-600">
              평가할 수 없는 절: {unevaluable.join(', ')} — 영문/숫자/언더스코어만
              가능합니다(하이픈 불가). 이 절은 무시되어 항상 참으로 취급됩니다(조건이 의도보다
              느슨해집니다).
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-1">
          {model.clauses.length === 0 ? (
            <p className="text-[11px] text-gray-400">
              조건 없음(else · 무조건 매칭) — "+ 조건 추가"로 만드세요.
            </p>
          ) : (
            <ul className="space-y-1">
              {model.clauses.map((clause, ci) => {
                const options = valueSetForKey(clause.key);
                const keyInList = clause.key === '' || availableKeys.includes(clause.key);
                return (
                  <li key={ci} className="flex flex-wrap items-center gap-1">
                    {ci > 0 && (
                      <select
                        value={clause.joiner ?? 'and'}
                        onChange={(e) =>
                          updateClause(ci, { joiner: e.target.value as ConditionJoiner })
                        }
                        className={`${SELECT_CLS} w-16`}
                        title="앞 조건과의 결합"
                      >
                        {CONDITION_JOINER_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    )}
                    <select
                      value={clause.key}
                      onChange={(e) => updateClause(ci, { key: e.target.value })}
                      className={`${SELECT_CLS} flex-1 min-w-[6rem] font-mono ${
                        clause.key === '' ? 'text-gray-400' : ''
                      }`}
                      title="비교할 조건 키"
                    >
                      <option value="" disabled>
                        키 선택
                      </option>
                      {availableKeys.map((k) => (
                        <option key={k} value={k} title={keyTitleFor?.(k) ?? k}>
                          {keyLabelFor?.(k) ?? k}
                          {registeredSet.has(k) ? '' : ' (미등록)'}
                        </option>
                      ))}
                      {!keyInList && (
                        <option value={clause.key} title={keyTitleFor?.(clause.key) ?? clause.key}>
                          {keyLabelFor?.(clause.key) ?? clause.key} (미등록)
                        </option>
                      )}
                    </select>
                    <select
                      value={clause.op}
                      onChange={(e) => updateClause(ci, { op: e.target.value as ConditionOp })}
                      className={`${SELECT_CLS} w-20`}
                      title="연산자"
                    >
                      {CONDITION_OP_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {opTakesValue(clause.op) &&
                      (options ? (
                        <EnumValueInput
                          value={clause.value ?? ''}
                          options={options}
                          onChange={(v) => updateClause(ci, { value: v })}
                        />
                      ) : (
                        <input
                          type="text"
                          value={clause.value ?? ''}
                          onChange={(e) => updateClause(ci, { value: e.target.value })}
                          placeholder="값"
                          className={`${INPUT_CLS} flex-1 min-w-[5rem] font-mono`}
                          title="비교 값(리터럴)"
                        />
                      ))}
                    <button
                      type="button"
                      onClick={() => removeClause(ci)}
                      title="조건 삭제"
                      className="text-[11px] px-1 rounded border border-red-200 text-red-600"
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={addClause}
            className="text-[11px] px-2 py-0.5 rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
          >
            + 조건 추가
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 본문 분기 슬롯 condition 편집기 — SlotConditionBuilder 위임(본문판 후보 공급).
 *
 * 키 후보는 **두 갈래를 병존**시킨다:
 *  ① `conditionBindings` — 저작자가 그 branch 노드에 명시 선언한 조건 데이터(카탈로그 출처).
 *  ② 행 스코프 키(`rowScope`) — 이 branch가 반복 안에 있을 때 그 행 객체의 필드(+등록형 객체
 *     필드의 계약 자식). 엔진이 `mergeRepeatScope`로 행을 스코프에 머지하므로 조건이 실제로
 *     이 키들을 읽는데, 후보가 ①뿐이라 화면에는 "(미등록)"으로만 보였다(DLR
 *     `photoUrl && !excludePhotos`가 그 증상 — 조건 데이터를 하나도 선언하지 않고도 동작하는
 *     정상 저작인데, 새로 만드는 저작자는 그런 키를 쓸 수 있다는 사실 자체를 알 수 없었다).
 *
 * 값 옵션은 ①에만 있다 — 그 key의 conditionBinding 출처(catalogKey/select) 카탈로그 필드가
 * enum이면 VALUE_SETS 라벨 콤보. 행 필드는 값 목록이 런타임 데이터라 텍스트 입력으로 둔다.
 *
 * 표시 라벨은 **두 갈래 모두** 사람 말이다(`conditionBindingSourceInfo`). ①만 키 원문으로 두던
 * 비대칭은 결함으로 확정됐다 — 한 목록 안에서 어떤 키는 이름으로 어떤 키는 식별자로 보이면
 * 후자가 "아직 덜 연결된 키"로 읽힌다.
 */
function SlotConditionEditor({
  condition,
  conditionBindings,
  catalog,
  dataSources,
  rowScope,
  rowScopeLabel,
  onChange,
}: {
  condition: string | undefined;
  conditionBindings: ConditionBinding[];
  catalog: CatalogEntry[];
  /** 템플릿 데이터 소스 — 조건 데이터가 소스를 읽을 때(bindingKey=소스 키) 표시명 해석에 쓴다. */
  dataSources?: DataSourceDef[];
  /** 이 branch를 감싼 반복의 행 스코프 키 후보(반복 밖이면 빈 배열). */
  rowScope: RowScopeConditionKey[];
  /** 그 반복 소스의 표시명 — 키 출처 안내 문구에 쓴다. */
  rowScopeLabel?: string;
  onChange: (condition: string | undefined) => void;
}) {
  const bindingKeys = conditionBindings.map((cb) => cb.key).filter(Boolean);
  const registeredKeys = mergeConditionKeyCandidates(bindingKeys, rowScope);
  // key → enum value-set 옵션(있으면). 없으면 텍스트 입력.
  const valueSetForKey = (key: string): { value: string; label: string }[] | undefined => {
    const cb = conditionBindings.find((c) => c.key === key);
    if (!cb) return undefined;
    const entry = catalog.find((e) => e.key === cb.binding.catalogKey);
    if (!entry) return undefined;
    const field = resolveSelectField(entry, cb.binding.select);
    return field && field.type === 'enum' ? VALUE_SETS[field.valueSetId] : undefined;
  };
  // 조건 데이터 키의 출처 해석(카탈로그 / 데이터 소스). 못 풀면 undefined → 키 원문 폴백.
  const conditionSourceFor = (key: string): ConditionSourceInfo | undefined => {
    const cb = conditionBindings.find((c) => c.key === key);
    return cb ? conditionBindingSourceInfo(cb.binding, catalog, dataSources) : undefined;
  };
  // 키 select 표시 라벨 — 조건 데이터 키와 행 스코프 키 **양쪽 다** 사람 라벨을 붙인다.
  // (조건 데이터 키만 원문으로 남겨 두던 비대칭이 결함으로 확정됐다 — 같은 목록 안에서 어떤 키는
  //  이름으로, 어떤 키는 식별자로 보이면 후자가 "덜 완성된 것"으로 읽힌다.)
  const keyLabelFor = (key: string): string | undefined => {
    if (bindingKeys.includes(key)) return conditionSourceFor(key)?.label;
    return rowScope.find((r) => r.key === key)?.label;
  };
  const keyTitleFor = (key: string): string => {
    if (bindingKeys.includes(key)) {
      const info = conditionSourceFor(key);
      return info ? `${key} — ${info.sourceText}(조건 데이터)` : key;
    }
    const row = rowScope.find((r) => r.key === key);
    if (!row) return key;
    const source = rowScopeLabel ? `'${rowScopeLabel}' 반복` : '이 반복';
    return row.contractChild
      ? `${key} — ${source}의 이 행 값(중첩 경로)`
      : `${key} — ${source}의 이 행 값`;
  };
  return (
    <SlotConditionBuilder
      condition={condition}
      registeredKeys={registeredKeys}
      valueSetForKey={valueSetForKey}
      keyLabelFor={keyLabelFor}
      keyTitleFor={keyTitleFor}
      rawPlaceholder="예: methodType === 'FREQUENCY_SEVERITY' && matrixType === '3x3' (비우면 else)"
      rawTitle="화이트리스트 문법: 식별키 / !식별키 / 식별키 === '리터럴' / && · || 결합. 비우면 else(무조건 매칭)."
      onChange={onChange}
    />
  );
}

/**
 * 유효한 widgetKey 목록·옵션(레지스트리 파생, 모듈 1회 산출) — '위젯 키' select가 소비한다.
 * 레지스트리는 정적 선언이라 렌더마다 다시 만들 이유가 없다.
 */
const REGISTERED_ROW_WIDGET_KEYS = registeredRowWidgetKeys();
const REGISTERED_ROW_WIDGET_OPTIONS = registeredRowWidgetOptions();

// 위험도 조립 표준 분기 키(서버 resolvePrefill 3-g 주입) — 작성폼 값에 항상 존재하는 고정 후보.
// 문자열은 shared `RISK_METHOD_FLAT_KEY`가 정본(서버 주입·rc 재resolve 교체 목록·web 편집 로드와 같은 것).
const RISK_METHOD_TYPE_KEY = RISK_METHOD_FLAT_KEY;
// 그 키의 고정 값 후보(위험성평가 방법). value=저장값, label=표시.
const RISK_METHOD_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'FREQUENCY_SEVERITY', label: '빈도·강도(매트릭스)' },
  { value: 'THREE_LEVEL', label: '상중하(3단계)' },
];

/**
 * scopeNodeId(조건을 거는 노드 — branch 슬롯 또는 formTable)를 포함하는 최근접 repeat 위젯의 행
 * 템플릿(children)을 반환. repeat 밖이면 null(=본문 트리 전체 스코프). 조건 키 후보 수집 범위
 * 판정용 — 반복 안의 select/radio는 행 상대키라 행 템플릿만, 밖이면 트리 전체를 후보로 삼는다.
 */
function findWidgetRepeatScopeChildren(
  tree: ComposerWidget[],
  scopeNodeId: string,
): ComposerWidget[] | null {
  // repeatChildren = 현재 하강 중인 최근접 repeat의 children(없으면 null). 대상 노드를 만나면 그 시점
  // repeatChildren이 스코프. undefined=이 서브트리에서 scopeNodeId 못 찾음(탐색 계속).
  const search = (
    nodes: ComposerWidget[],
    repeatChildren: ComposerWidget[] | null,
  ): ComposerWidget[] | null | undefined => {
    for (const n of nodes) {
      if (n.id === scopeNodeId) return repeatChildren;
      let found: ComposerWidget[] | null | undefined;
      if (n.kind === 'repeat') found = search(n.children, n.children);
      else if (n.kind === 'group' || n.kind === 'modalTrigger') found = search(n.children, repeatChildren);
      else if (n.kind === 'branch') {
        for (const s of n.branches) {
          found = search(s.children, repeatChildren);
          if (found !== undefined) break;
        }
      }
      if (found !== undefined) return found;
    }
    return undefined;
  };
  const res = search(tree, null);
  return res === undefined ? null : res;
}

/**
 * 조건 빌더의 값 등치 후보가 될 select/radio 입력 위젯을 노드 스코프에서 수집한다(bindingKey
 * 첫 등장 우선, 순서 보존). 스코프는 findWidgetRepeatScopeChildren(반복 안=행 템플릿, 밖=트리 전체).
 * options는 정적 InputOption[]만 싣는다(동적 optionsFrom만 있으면 값 콤보 불가 → 텍스트 폴백).
 *
 * scopeNodeId는 branch 노드(분기 슬롯 조건)뿐 아니라 formTable 노드(행 표시 조건)도 받는다 —
 * 두 조건 편집기가 같은 후보 규약을 쓴다.
 */
function collectBranchScopeSelectRadio(
  tree: ComposerWidget[],
  scopeNodeId: string,
): { bindingKey: string; label?: string; options?: InputOption[] }[] {
  const scope = findWidgetRepeatScopeChildren(tree, scopeNodeId) ?? tree;
  const out: { bindingKey: string; label?: string; options?: InputOption[] }[] = [];
  const seen = new Set<string>();
  const take = (n: InputWidget): void => {
    if (n.widget !== 'select' && n.widget !== 'radio') return;
    if (!n.bindingKey || seen.has(n.bindingKey)) return;
    seen.add(n.bindingKey);
    out.push({ bindingKey: n.bindingKey, label: n.label, options: n.options });
  };
  const walk = (nodes: ComposerWidget[]): void => {
    for (const n of nodes) {
      if (n.kind === 'input') take(n);
      else if (n.kind === 'group' || n.kind === 'modalTrigger' || n.kind === 'repeat')
        walk(n.children);
      else if (n.kind === 'branch') for (const s of n.branches) walk(s.children);
      else if (n.kind === 'formTable') {
        // 표 안 셀 위젯도 같은 폼의 값이라 조건 후보다 — 표를 통과해 버리면 "같은 표에서 고른
        // 값으로 그 표의 행을 켜고 끈다"는 가장 흔한 저작이 후보 목록에 안 뜬다. 고정 행 셀만 —
        // 반복 행은 데이터가 행 존재를 결정하므로 그 값으로 조건을 걸 자리가 아니다.
        for (const r of n.rows) {
          if (r.type !== 'fixed') continue;
          for (const c of r.cells) if (c.widget) take(c.widget);
        }
      }
    }
  };
  walk(scope);
  return out;
}

/**
 * 분기 위젯 슬롯 condition 편집기(작성폼) — SlotConditionBuilder 위임(본문 SlotConditionEditor 동형).
 * 본문과 차이는 후보 공급원뿐: 키=주입 고정 키(riskAssessmentMethodType) + branch 스코프 select/radio
 * bindingKey, 값=riskAssessmentMethodType 고정 2후보 또는 그 select/radio의 정적 options(없으면 텍스트).
 */
function WidgetSlotConditionEditor({
  condition,
  tree,
  branchId,
  onChange,
}: {
  condition: string | undefined;
  tree: ComposerWidget[];
  branchId: string;
  onChange: (condition: string | undefined) => void;
}) {
  const scopeWidgets = useMemo(
    () => collectBranchScopeSelectRadio(tree, branchId),
    [tree, branchId],
  );
  const registeredKeys = useMemo(() => {
    const keys: string[] = [RISK_METHOD_TYPE_KEY];
    for (const w of scopeWidgets) if (!keys.includes(w.bindingKey)) keys.push(w.bindingKey);
    return keys;
  }, [scopeWidgets]);
  const valueSetForKey = (key: string): { value: string; label: string }[] | undefined => {
    if (key === RISK_METHOD_TYPE_KEY) return RISK_METHOD_TYPE_OPTIONS;
    const w = scopeWidgets.find((x) => x.bindingKey === key);
    // 정적 options만 값 콤보로(동적 optionsFrom은 값 목록을 미리 알 수 없어 텍스트 폴백).
    return w && w.options && w.options.length > 0 ? w.options : undefined;
  };
  // 키 select 표시 라벨(저장값=키 그대로). 고정 키는 사람이 읽는 문구, 나머지는 그 위젯의 label(있으면).
  const keyLabelFor = (key: string): string | undefined => {
    // '평가기준' select(위험성평가 개요)에서 고른 세트로부터 서버가 자동 파생하는 값 — 같은 이름으로
    // 표기해 사용자가 두 지점을 연결하게(2026-07-21 사용자 확정: "평가 방식(자동)" → "평가기준").
    if (key === RISK_METHOD_TYPE_KEY) return '평가기준';
    const w = scopeWidgets.find((x) => x.bindingKey === key);
    return w?.label?.trim() || undefined;
  };
  // 반복 안 branch면 조건 키(select/radio)는 행 상대 스코프, 밖이면 폼 전역 — 출처 문구 구분.
  const inRepeat = findWidgetRepeatScopeChildren(tree, branchId) !== null;
  // 키 option hover title — 그 키 값이 어디서 오는지(서버 자동 vs 작성폼 위젯) 안내(2026-07-21).
  const keyTitleFor = (key: string): string => {
    if (key === RISK_METHOD_TYPE_KEY)
      return `${key} — 위험성평가 개요의 '평가기준' 선택에서 서버가 자동 제공`;
    const w = scopeWidgets.find((x) => x.bindingKey === key);
    if (w)
      return `${key} — '${w.label?.trim() || key}' 위젯의 ${inRepeat ? '이 행의' : '이 폼의'} 현재 값`;
    return key;
  };
  return (
    <SlotConditionBuilder
      condition={condition}
      registeredKeys={registeredKeys}
      valueSetForKey={valueSetForKey}
      keyLabelFor={keyLabelFor}
      keyTitleFor={keyTitleFor}
      rawPlaceholder="예: riskAssessmentMethodType === 'THREE_LEVEL' (비우면 else)"
      rawTitle="화이트리스트 문법: 식별키 / !식별키 / 식별키 === '리터럴' / && · || 결합. 키는 작성폼 값. 비우면 else(무조건 매칭)."
      onChange={onChange}
    />
  );
}

/**
 * 본문 branch 노드 슬롯 에디터(속성 패널). export는 RTL 대상이기 때문이다 — 조건 키 표시 라벨은
 * 해석기(`conditionBindingSourceInfo`)가 옳아도 배선이 빠지면 화면에서 그대로 키 원문이 남는
 * 자리라, 순수 spec만으로는 그 층을 덮지 못한다(`DataSection`·`BindingEditor`와 같은 이유).
 */
export function BranchSlotsEditor({
  node,
  catalog,
  dataSources,
  rowScope,
  rowScopeLabel,
  activeIndex,
  onSetActive,
  onAddSlot,
  onRemoveSlot,
  onMoveSlot,
  onUpdateCondition,
  onUpdateLabel,
  onSelectNode,
}: {
  node: BranchNode;
  catalog: CatalogEntry[];
  /** 템플릿 데이터 소스 — 조건 데이터 키 라벨 해석(bindingKey=소스 키)에 쓴다. */
  dataSources?: DataSourceDef[];
  /** 이 branch를 감싼 반복의 행 스코프 조건 키 후보(반복 밖이면 빈 배열). */
  rowScope: RowScopeConditionKey[];
  /** 그 반복 소스의 표시명 — 후보 안내 문구용. */
  rowScopeLabel?: string;
  activeIndex: number;
  onSetActive: (index: number) => void;
  onAddSlot: () => void;
  onRemoveSlot: (slotId: string) => void;
  onMoveSlot: (slotId: string, dir: 'up' | 'down') => void;
  onUpdateCondition: (slotId: string, condition: string | undefined) => void;
  onUpdateLabel: (slotId: string, label: string | undefined) => void;
  onSelectNode: (id: string) => void;
}) {
  // "사용 가능한 키" 칩 — 키당 1개로 접는다. 존재 캐리어는 **같은 key를 snapshot/live 두 행**으로
  // 선언하는 관용(템플릿 carrierPair)이라 접지 않으면 같은 이름이 두 번 나열된다. 두 행은
  // lifecycle만 다르고 출처가 같아 라벨도 같으므로 첫 행으로 해석한다.
  const conditionKeyChips = useMemo(() => {
    const out: { key: string; label?: string; title: string }[] = [];
    for (const cb of node.conditionBindings ?? []) {
      if (!cb.key || out.some((c) => c.key === cb.key)) continue;
      const info = conditionBindingSourceInfo(cb.binding, catalog, dataSources);
      out.push({
        key: cb.key,
        label: info?.label,
        title: info ? `${cb.key} — ${info.sourceText}(조건 데이터)` : cb.key,
      });
    }
    return out;
  }, [node.conditionBindings, catalog, dataSources]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <dt className="text-xs font-semibold text-gray-500">분기 슬롯 ({node.branches.length})</dt>
        <button
          type="button"
          onClick={onAddSlot}
          className="text-xs px-2 py-0.5 rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
        >
          + 슬롯
        </button>
      </div>
      <p className="text-[11px] text-gray-400 leading-snug">
        조건이 참인 <b>첫</b> 슬롯의 자식만 렌더됩니다. 조건을 비우면 else(무조건 매칭) 슬롯이 됩니다.
        편집 캔버스는 활성(청록) 슬롯만 표시합니다.
      </p>
      {/* 사용 가능한 조건 데이터 키 — 아래 조건 키 select와 **같은 라벨 규약**으로 표시한다(원키는
          hover 툴팁). 여기만 키 원문이면 select에서 고른 이름과 이 줄이 서로 다른 것을 가리키는
          것처럼 보인다. 출처를 못 푼 키는 원문 그대로(폴백) — 그 자리는 실제로 연결이 끊긴 자리다. */}
      {conditionKeyChips.length > 0 && (
        <p className="text-[11px] text-teal-600 leading-snug">
          사용 가능한 키:{' '}
          {conditionKeyChips.map((chip, i) => (
            <span key={chip.key}>
              {i > 0 && ', '}
              <span className={chip.label ? undefined : 'font-mono'} title={chip.title}>
                {chip.label ?? chip.key}
              </span>
            </span>
          ))}
        </p>
      )}
      {/* 반복 안 분기 — 조건 데이터를 선언하지 않아도 그 행의 필드로 조건을 걸 수 있다는 사실을
          명시한다. 조건 키 콤보에 이미 후보로 뜨지만, 그 목록을 열어 보기 전에는 "조건 데이터를
          먼저 선언해야 하나" 하고 막히는 자리라 여기서 한 줄로 알린다. */}
      {rowScope.length > 0 && (
        <p className="text-[11px] text-teal-600 leading-snug">
          이 분기는 <b>{rowScopeLabel || '반복'}</b>의 행마다 평가되므로, 조건 데이터를 선언하지
          않아도 그 행의 필드(
          <span className="font-mono">
            {rowScope.slice(0, 3).map((r) => r.key).join(', ')}
            {rowScope.length > 3 ? ' …' : ''}
          </span>
          )를 조건 키로 바로 고를 수 있습니다.
        </p>
      )}
      <ul className="space-y-1.5">
        {node.branches.map((slot, i) => {
          const isActive = i === activeIndex;
          const isElse = slot.condition === undefined;
          return (
            <li
              key={slot.id}
              className={`rounded border p-1.5 ${isActive ? 'border-teal-400 bg-teal-50/50' : 'border-gray-200'}`}
            >
              <div className="flex items-center gap-1 mb-1">
                <button
                  type="button"
                  onClick={() => onSetActive(i)}
                  title="이 슬롯을 활성화(캔버스에 표시)"
                  className={`text-[11px] px-1.5 py-0.5 rounded ${
                    isActive
                      ? 'bg-teal-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {isActive ? '● ' : ''}슬롯 {i + 1}
                  {isElse ? ' (else)' : ''}
                </button>
                <div className="ml-auto flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => onMoveSlot(slot.id, 'up')}
                    disabled={i === 0}
                    title="위로"
                    className="text-[11px] px-1 rounded border border-gray-200 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => onMoveSlot(slot.id, 'down')}
                    disabled={i === node.branches.length - 1}
                    title="아래로"
                    className="text-[11px] px-1 rounded border border-gray-200 disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveSlot(slot.id)}
                    disabled={node.branches.length <= 1}
                    title="슬롯 삭제(최소 1개 유지)"
                    className="text-[11px] px-1 rounded border border-red-200 text-red-600 disabled:opacity-30"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <input
                type="text"
                value={slot.label ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  onUpdateLabel(slot.id, v.trim() === '' ? undefined : v);
                }}
                placeholder="슬롯 이름(선택) — 예: 3x3 매트릭스"
                className={`${INPUT_CLS} mb-1`}
                title="슬롯 표시 이름(선택). 지정 시 탭/빈 슬롯 힌트에 조건 대신 이 이름이 우선 표시됩니다."
              />
              <SlotConditionEditor
                condition={slot.condition}
                conditionBindings={node.conditionBindings ?? []}
                catalog={catalog}
                dataSources={dataSources}
                rowScope={rowScope}
                rowScopeLabel={rowScopeLabel}
                onChange={(condition) => onUpdateCondition(slot.id, condition)}
              />
              {/* 슬롯별 노드 목록 — 활성 슬롯만이 아니라 각 슬롯이 보여줄 내용(children)을 그 슬롯
                  항목 아래에 나열(2026-07-21 사용자 확정 — 작성폼 위젯판과 동일 UX 통일). 클릭=선택.
                  비활성 슬롯 노드는 캔버스 미렌더라 선택 시 하이라이트는 속성/트리에서 확인. */}
              <div className="mt-1.5">
                <p className="text-[10px] font-semibold text-gray-400 mb-0.5">
                  이 슬롯의 노드 ({slot.children.length})
                </p>
                {slot.children.length === 0 ? (
                  <p className="text-[11px] text-gray-400">
                    비어 있음 — 이 슬롯을 활성화하고 팔레트에서 요소를 추가하세요.
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {slot.children.map((child) => (
                      <li key={child.id}>
                        <button
                          type="button"
                          onClick={() => onSelectNode(child.id)}
                          title={`${child.id} 선택`}
                          className="w-full text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{shortNodeLabel(child)}</span>
                          <span className="text-[10px] text-gray-400 font-mono shrink-0">
                            {child.id}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * 분기 위젯 슬롯 에디터(작성폼 P3) — 본문 BranchSlotsEditor 동형(위젯판). 슬롯 목록(라벨·조건·↑/↓·삭제·
 * 추가) + 활성 슬롯 전환(캔버스 탭·트리 서브행과 공유 state) + 활성 슬롯 자식 드릴다운.
 *
 * 조건은 WidgetSlotConditionEditor(SlotConditionBuilder 위임)로 본문과 동형 빌더(콤보) 편집. 본문과
 * 차이는 후보 공급원뿐: BranchWidgetSlot엔 conditionBindings 축이 없어(본문 BranchNode 전용) 키/값
 * 후보를 작성폼 값(주입 riskAssessmentMethodType + branch 스코프 select/radio)에서 얻는다. condition
 * undefined/''는 모두 else(무조건 매칭) 의미라 빈 문자열로 저장한다(위젯 슬롯 규약, shared 타입).
 */
function WidgetBranchSlotsEditor({
  node,
  tree,
  activeIndex,
  onSetActive,
  onAddSlot,
  onRemoveSlot,
  onMoveSlot,
  onUpdateCondition,
  onUpdateLabel,
  onSelectNode,
}: {
  node: BranchWidget;
  /** 조건 빌더 키/값 후보(작성폼 select/radio) 수집용 전체 위젯 트리. */
  tree: ComposerWidget[];
  activeIndex: number;
  onSetActive: (index: number) => void;
  onAddSlot: () => void;
  onRemoveSlot: (slotId: string) => void;
  onMoveSlot: (slotId: string, dir: 'up' | 'down') => void;
  onUpdateCondition: (slotId: string, condition: string | undefined) => void;
  onUpdateLabel: (slotId: string, label: string | undefined) => void;
  onSelectNode: (id: string) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-gray-500">분기</p>
      <div className="flex items-center justify-between">
        <dt className="text-xs font-semibold text-gray-500">분기 슬롯 ({node.branches.length})</dt>
        <button
          type="button"
          onClick={onAddSlot}
          className="text-xs px-2 py-0.5 rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
        >
          + 슬롯
        </button>
      </div>
      <p className="text-[11px] text-gray-400 leading-snug">
        조건이 참인 <b>첫</b> 슬롯의 자식만 렌더됩니다. 조건을 비우면 else(무조건 매칭) 슬롯이 됩니다.
        편집 캔버스는 활성(청록) 슬롯만 표시합니다.
      </p>
      <p className="text-[11px] text-teal-600 leading-snug">
        문법: <span className="font-mono">키</span>(truthy) · <span className="font-mono">!키</span> ·{' '}
        <span className="font-mono">키 === &apos;리터럴&apos;</span> ·{' '}
        <span className="font-mono">&amp;&amp;</span>/<span className="font-mono">||</span> 결합. 키는
        작성폼 값(bindingKey)입니다.
      </p>
      {/* 조건 데이터 출처 안내 — 조건 키가 서버 자동 제공값인지 작성폼 위젯 값인지 명시(2026-07-21
          사용자: 출처가 화면 어디에도 안 보여 혼란). collectBranchScopeSelectRadio 결과 재사용(중복
          수집 로직 신설 안 함). compact 접이식. */}
      {(() => {
        const scopeWidgets = collectBranchScopeSelectRadio(tree, node.id);
        const inRepeat = findWidgetRepeatScopeChildren(tree, node.id) !== null;
        return (
          <details className="rounded border border-gray-200 bg-gray-50 px-2 py-1">
            <summary className="cursor-pointer text-[11px] font-medium text-gray-500">
              조건 데이터 출처 ({scopeWidgets.length + 1})
            </summary>
            <ul className="mt-1 space-y-0.5 text-[11px] leading-snug text-gray-600">
              <li>
                <span className="font-mono text-gray-500">평가기준</span> — 위험성평가 개요의
                &apos;평가기준&apos; 선택에서 자동 제공(서버)
              </li>
              {scopeWidgets.map((sw) => (
                <li key={sw.bindingKey}>
                  <span className="font-mono text-gray-500">{sw.bindingKey}</span> —{' '}
                  {sw.label ? `'${sw.label}' ` : ''}위젯의 {inRepeat ? '이 행의' : '이 폼의'} 현재 값
                </li>
              ))}
            </ul>
          </details>
        );
      })()}
      <ul className="space-y-1.5">
        {node.branches.map((slot, i) => {
          const isActive = i === activeIndex;
          // condition undefined/'' = else(무조건 매칭). 위젯 슬롯은 두 값이 동의어.
          const isElse = (slot.condition ?? '').trim() === '';
          return (
            <li
              key={slot.id}
              className={`rounded border p-1.5 ${isActive ? 'border-teal-400 bg-teal-50/50' : 'border-gray-200'}`}
            >
              <div className="flex items-center gap-1 mb-1">
                <button
                  type="button"
                  onClick={() => onSetActive(i)}
                  title="이 슬롯을 활성화(캔버스에 표시)"
                  className={`text-[11px] px-1.5 py-0.5 rounded ${
                    isActive
                      ? 'bg-teal-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {isActive ? '● ' : ''}슬롯 {i + 1}
                  {isElse ? ' (else)' : ''}
                </button>
                <div className="ml-auto flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => onMoveSlot(slot.id, 'up')}
                    disabled={i === 0}
                    title="위로"
                    className="text-[11px] px-1 rounded border border-gray-200 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => onMoveSlot(slot.id, 'down')}
                    disabled={i === node.branches.length - 1}
                    title="아래로"
                    className="text-[11px] px-1 rounded border border-gray-200 disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveSlot(slot.id)}
                    disabled={node.branches.length <= 1}
                    title="슬롯 삭제(최소 1개 유지)"
                    className="text-[11px] px-1 rounded border border-red-200 text-red-600 disabled:opacity-30"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <input
                type="text"
                value={slot.label ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  onUpdateLabel(slot.id, v.trim() === '' ? undefined : v);
                }}
                placeholder="슬롯 이름(선택) — 예: 3x3 매트릭스"
                className={`${INPUT_CLS} mb-1`}
                title="슬롯 표시 이름(선택). 지정 시 탭/빈 슬롯 힌트에 조건 대신 이 이름이 우선 표시됩니다."
              />
              <WidgetSlotConditionEditor
                condition={slot.condition}
                tree={tree}
                branchId={node.id}
                onChange={(condition) => onUpdateCondition(slot.id, condition)}
              />
              {/* 슬롯별 위젯 목록 — 활성 슬롯만이 아니라 각 슬롯이 보여줄 내용(children)을 그 슬롯
                  항목 아래에 나열(2026-07-21 사용자 확정: "슬롯마다 슬롯에서 보여주는 내용을 보여주자").
                  클릭=그 위젯 선택(드릴다운). 비활성 슬롯 위젯 선택 시에도 캔버스는 활성 슬롯만
                  렌더하므로 하이라이트는 트리/속성에서 확인. */}
              <div className="mt-1.5">
                <p className="text-[10px] font-semibold text-gray-400 mb-0.5">
                  이 슬롯의 위젯 ({slot.children.length})
                </p>
                {slot.children.length === 0 ? (
                  <p className="text-[11px] text-gray-400">
                    비어 있음 — 트리의 이 슬롯 행 &quot;+&quot;로 위젯을 추가하세요.
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {slot.children.map((child) => (
                      <li key={child.id}>
                        <button
                          type="button"
                          onClick={() => onSelectNode(child.id)}
                          title={`${child.id} 선택`}
                          className="w-full text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{composerWidgetSummary(child)}</span>
                          <span className="text-[10px] text-gray-400 font-mono shrink-0">
                            {child.kind}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** overlay 저작값 정규화 — 전 필드 기본값이면 undefined(라운드트립 노이즈 방지, pageFillMode 관례).
 *  기본값: shapesVisible/strokesVisible true / strokeWidth 2 / color 미지정(개별 색)
 *  / showLabels true / lineStyle 'dashed' / showFill true (구 보고서 HazardOverlay 결).
 *  구 3값 mode/strokesMode는 **기록하지 않는다**(신형 이관 strip) — 'never'만 기본 숨김
 *  (visible:false)으로 접고, 'always'/'byKey'는 기본 표시로 접는다. key/strokesKey는 데이터
 *  레벨 오버라이드(에디터 UI 미노출 — 규약 키 대신 읽을 키)라 있으면 그대로 보존한다. */
function normalizeImageOverlay(ov: ImageOverlayOptions): ImageOverlayOptions | undefined {
  const next: ImageOverlayOptions = {};
  // 축별 기본 표시 — shared overlayAxisDefaultVisible(rc 렌더 판정과 같은 헬퍼) 공유.
  if (!overlayAxisDefaultVisible(ov.shapesVisible, ov.mode)) {
    next.shapesVisible = false;
  }
  if (ov.key !== undefined && ov.key !== '') next.key = ov.key;
  if (!overlayAxisDefaultVisible(ov.strokesVisible, ov.strokesMode)) {
    next.strokesVisible = false;
  }
  if (ov.strokesKey !== undefined && ov.strokesKey !== '') next.strokesKey = ov.strokesKey;
  if (
    ov.strokeWidth !== undefined &&
    Number.isFinite(ov.strokeWidth) &&
    ov.strokeWidth > 0 &&
    ov.strokeWidth !== DEFAULT_STROKE_WIDTH
  )
    next.strokeWidth = ov.strokeWidth;
  if (ov.color !== undefined && ov.color !== '') next.color = ov.color;
  if (ov.showLabels === false) next.showLabels = false;
  if (ov.lineStyle === 'solid') next.lineStyle = 'solid';
  if (ov.showFill === false) next.showFill = false;
  return Object.keys(next).length > 0 ? next : undefined;
}

/** image 노드 주석 오버레이 저작 섹션(도메인 중립 — 위험영역 사진은 한 사례) — node.overlay(ImageOverlayOptions).
 *  값이 주석 객체(AnnotatedImageValue { url, shapes, strokes })일 때만 렌더에 반영되고,
 *  평면 URL 값이면 엔진이 무시한다(섹션 제목 툴팁으로 안내). */
function ImageOverlaySection({
  node,
  onPatch,
}: {
  node: ImageNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
}) {
  const ov = node.overlay ?? {};
  // 축별 effective 기본 표시 — shared overlayAxisDefaultVisible(normalize·rc 렌더와 동일 규칙).
  const shapesVisible = overlayAxisDefaultVisible(ov.shapesVisible, ov.mode);
  const strokesVisible = overlayAxisDefaultVisible(ov.strokesVisible, ov.strokesMode);
  const patch = (part: Partial<ImageOverlayOptions>) =>
    onPatch({ overlay: normalizeImageOverlay({ ...ov, ...part }) } as Partial<ComposerNode>);
  return (
    <div>
      <label
        className="text-xs font-semibold text-gray-500 mb-1 block"
        title="값이 주석 이미지(사각형·수기 선 오버레이 포함)일 때만 적용됩니다 — 예: 발견사항 위험영역 사진. 일반 이미지 값에는 영향이 없습니다."
      >
        주석 오버레이
      </label>
      <div className="space-y-2">
        {/* 레이어별 소그룹(2026-08-05 사용자 확정 — [표시]/[스타일] 2분법 폐지): 스타일 옵션이
            전부 사각형 전용이 되면서(수기 선 스타일은 그린 시점 데이터가 정본 — AnnotatedImageView
            renderStroke) 옵션이 레이어 단위로 나뉜다. 출력 체크는 기본값만 정한다: 작성 토글은
            규약 콘텐츠 키(OVERLAY_SHAPES/STROKES_CONTENT_KEY)를 렌더가 항상 읽으므로, 작성자
            제어 여부는 "그 키의 체크박스를 작성폼에 배치했는가"가 결정한다(값 없으면 기본값).
            patch 시 normalizeImageOverlay가 구 3값 mode/strokesMode를 신형으로 이관(strip)한다. */}
        <div className="space-y-1.5">
          <span className="text-[11px] font-semibold text-gray-500 block">지정 영역</span>
          <label
            className="flex items-center gap-1.5 text-xs text-gray-600"
            title="사각형(도형) 주석 표시 기본값 — 예: 발견사항 위험영역. 작성 페이지에 해당 체크 항목이 배치돼 있으면 작성자가 보고서마다 변경합니다."
          >
            <input
              type="checkbox"
              checked={shapesVisible}
              onChange={(e) => patch({ shapesVisible: e.target.checked })}
            />
            출력
          </label>
          {/* 외곽선 스타일 — 기본 점선(구 보고서 결). */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14 shrink-0">외곽선</span>
            <select
              value={ov.lineStyle ?? 'dashed'}
              onChange={(e) =>
                patch({ lineStyle: e.target.value === 'solid' ? 'solid' : undefined })
              }
              className={SELECT_CLS}
            >
              <option value="dashed">점선 (기본)</option>
              <option value="solid">실선</option>
            </select>
          </div>
          {/* 테두리 두께 — 사각형 전용(수기 선은 그린 시점 두께 그대로). */}
          <div className="flex items-center gap-1">
            <span
              className="text-[11px] text-gray-500 w-14 shrink-0"
              title="사각형 테두리 두께입니다(비워 두면 기본 2px)."
            >
              선 두께(px)
            </span>
            <input
              type="number"
              min={1}
              value={ov.strokeWidth ?? ''}
              onChange={(e) =>
                patch({ strokeWidth: e.target.value === '' ? undefined : Number(e.target.value) })
              }
              placeholder={String(DEFAULT_STROKE_WIDTH)}
              className={INPUT_CLS}
            />
          </div>
          {/* 테두리 색 — 사각형 전용. 미지정=자동(데이터 개별 색 — 예: 위험성 수준색). */}
          <div className="flex items-center gap-1">
            <span
              className="text-[11px] text-gray-500 w-14 shrink-0"
              title="사각형 테두리·배지·채움 색입니다. 지정하면 데이터 개별 색보다 우선하고, 자동이면 각 도형의 자체 색을 씁니다."
            >
              선 색
            </span>
            <input
              type="color"
              className="h-7 w-10 border border-gray-300 rounded p-0.5"
              value={ov.color ?? ANNOTATED_IMAGE_DEFAULT_COLOR}
              onChange={(e) => patch({ color: e.target.value })}
            />
            {ov.color !== undefined ? (
              <button
                type="button"
                className="text-[11px] text-gray-500 underline"
                onClick={() => patch({ color: undefined })}
              >
                초기화
              </button>
            ) : (
              <span className="text-[11px] text-gray-400">자동 (데이터 색)</span>
            )}
          </div>
          {/* 라벨 배지 — 도형 데이터의 label 문자열을 배지로(내용은 데이터가 결정 — 예: 순번 '#1') */}
          <label
            className="flex items-center gap-1.5 text-xs text-gray-600"
            title="사각형에 딸린 라벨 문자열을 색 배지로 표시합니다. 라벨 내용은 데이터가 정합니다 — 예: 발견사항 순번 '#1'."
          >
            <input
              type="checkbox"
              checked={ov.showLabels !== false}
              onChange={(e) => patch({ showLabels: e.target.checked ? undefined : false })}
            />
            도형 라벨 배지 표시
          </label>
          {/* 채움 — 도형 색 20% 반투명(색은 데이터가 공급 — 예: 위험성 수준색) */}
          <label
            className="flex items-center gap-1.5 text-xs text-gray-600"
            title="사각형 내부를 도형 색의 20% 반투명으로 채웁니다. 색은 데이터가 정합니다 — 예: 위험성 수준색."
          >
            <input
              type="checkbox"
              checked={ov.showFill !== false}
              onChange={(e) => patch({ showFill: e.target.checked ? undefined : false })}
            />
            영역 반투명 채움
          </label>
        </div>
        <div className="space-y-1.5">
          <span className="text-[11px] font-semibold text-gray-500 block">그리기 메모</span>
          <label
            className="flex items-center gap-1.5 text-xs text-gray-600"
            title="수기 선 주석 표시 기본값 — 예: 점검의 그리기 메모. 작성 페이지에 해당 체크 항목이 배치돼 있으면 작성자가 보고서마다 변경합니다. 스타일(색·두께)은 그린 그대로 표시됩니다."
          >
            <input
              type="checkbox"
              checked={strokesVisible}
              onChange={(e) => patch({ strokesVisible: e.target.checked })}
            />
            출력
          </label>
        </div>
      </div>
    </div>
  );
}

function PropertyPanel({
  node,
  overflowNotes,
  isTopLevel,
  onPatch,
  onPreview,
  onCommit,
  catalog,
  dataDefs,
  dataSources,
  approvalLineMeta,
  onUpdateApprovalLineMeta,
  onAddRow,
  onRemoveRow,
  onAddCol,
  onRemoveCol,
  onSetColWidths,
  onSetRowHeights,
  onPreviewColWidths,
  onPreviewRowHeights,
  onRevert,
  onSetTableFlag,
  onAddRepeatRegion,
  onRemoveRepeatRegion,
  onUpdateRepeatRegion,
  onSetRepeatPatternRows,
  importRepeatHints,
  onApplyImportRepeatHint,
  onDismissImportRepeatHint,
  onCreateManualDataSource,
  onAddDataSourceColumn,
  onRenameDataSourceColumn,
  onDeleteDataSourceColumn,
  onUnmapDataSourceColumn,
  onMoveDataSourceColumn,
  onHighlightSource,
  onGoToDataCard,
  setCellBinding,
  onStartCellPick,
  onCancelCellPick,
  activePickToken,
  onSelectNode,
  ancestorGroups,
  onPatchAllCells,
  onPatchAllCellsLive,
  onPatchAllCellsCommit,
  onToggleContainerKind,
  onWrapInBranch,
  onAddRadioOption,
  usedBindingKeys,
  activeBranchSlots,
  onSetActiveBranchSlot,
  onAddBranchSlot,
  onRemoveBranchSlot,
  onMoveBranchSlot,
  onUpdateBranchSlotCondition,
  onUpdateBranchSlotLabel,
  onUpdateBranchConditionBindings,
}: {
  node: ComposerNode;
  /**
   * 이 노드에 걸린 **페이지 넘침 경고 문구**(없으면 빈 배열) — 높이 편집 바로 아래에 띄운다.
   * 판정은 rc `findOversizeUnits`, 문구 생성은 `templateIssues`가 이미 했고 여기선 표시만 한다.
   */
  overflowNotes: string[];
  /**
   * 선택 노드가 body 최상위인가 — 페이지 배치(pageBreak 등)는 페이지 계획이 body 최상위 노드에서만
   * 읽으므로(pageMeasure의 top-level 순회), 중첩 노드에서는 체크박스를 비활성화해 무동작 함정을 막는다.
   */
  isTopLevel: boolean;
  onPatch: (patch: Partial<ComposerNode>) => void;
  /** 숫자 입력 실시간 미리보기(replacePresent) — 선택 노드에. onRevert이 revert. */
  onPreview: (patch: Partial<ComposerNode>) => void;
  /** 숫자 입력 확정(commitPresent) — 선택 노드에. */
  onCommit: (patch: Partial<ComposerNode>) => void;
  catalog: CatalogEntry[];
  /** 반복영역 소스/칼럼 매핑(collectRepeatSources)에 쓸 템플릿 dataDefs 전체 */
  dataDefs: DataDef[] | undefined;
  /** 반복영역 소스 콤보에 조립형 데이터 소스(cardinality='list')를 나열하는 데 쓸 템플릿 dataSources 전체 */
  dataSources: DataSourceDef[] | undefined;
  approvalLineMeta?: { defaultApprovalLine: ApprovalLineMetaRow[]; maxAdditionalLines: number };
  onUpdateApprovalLineMeta: (
    patch: Partial<{ defaultApprovalLine: ApprovalLineMetaRow[]; maxAdditionalLines: number }>,
  ) => void;
  onAddRow: (tableId: string, atRow: number) => void;
  onRemoveRow: (tableId: string, atRow: number) => void;
  onAddCol: (tableId: string, atCol: number) => void;
  onRemoveCol: (tableId: string, atCol: number) => void;
  onSetColWidths: (tableId: string, widths: (number | string)[]) => void;
  onSetRowHeights: (tableId: string, heights: (number | string)[]) => void;
  /** 열 너비 실시간 미리보기(replacePresent — 히스토리 미적재) */
  onPreviewColWidths: (tableId: string, widths: (number | string)[]) => void;
  /** 행 높이 실시간 미리보기(replacePresent — 히스토리 미적재) */
  onPreviewRowHeights: (tableId: string, heights: (number | string)[]) => void;
  /** 치수 입력 미리보기 폐기(discardTransient) — Esc/이탈 시 원값 복원 */
  onRevert: () => void;
  onSetTableFlag: (tableId: string, patch: Partial<TableNode>) => void;
  onAddRepeatRegion: (tableId: string, region: TableRepeatRegion) => void;
  onRemoveRepeatRegion: (tableId: string, index: number) => void;
  onUpdateRepeatRegion: (
    tableId: string,
    index: number,
    patch: Partial<TableRepeatRegion>,
  ) => void;
  /**
   * 패턴 행 범위 설정 — 부족분 행 생성(표 끝) + 매핑 이동(겹치지 않는 새 범위일 때) + 밴드 갱신을
   * 단일 undo 스텝으로 적용. 종전의 별도 「행 지정」을 흡수했다(2026-08-28).
   */
  onSetRepeatPatternRows: (tableId: string, index: number, plan: PatternRowSetPlan) => void;
  /** 양식 업로드 초안의 반복 영역 제안(importMeta.repeatHints) — 표 선택 시 배지로 노출(P3). */
  importRepeatHints?: ImportRepeatHint[];
  /** 반복 영역 제안 원클릭 적용(반복영역 + 수동 데이터셋 골격 + hint 소진, undo 1스텝) */
  onApplyImportRepeatHint?: (hint: ImportRepeatHint) => void;
  /** 반복 영역 제안 무시(hint만 제거) */
  onDismissImportRepeatHint?: (hint: ImportRepeatHint) => void;
  /** "＋ 새 동적 데이터셋(빈)…" — dataSources에 kind:'manual' 선언 추가(+setOver 시 over 지정을 한 undo 스텝). */
  onCreateManualDataSource: (params: {
    key: string;
    setOver?: { tableId: string; regionIndex: number };
  }) => void;
  /** (dataSource over) "＋칼럼 추가": 셀 바인딩 + 선언 fields append + 영역 칼럼범위를 한 undo 스텝으로 */
  onAddDataSourceColumn: (params: {
    tableId: string;
    regionIndex: number;
    row: number;
    col: number;
    dsKey: string;
    field: string;
    label: string;
    lifecycle: BindingLifecycle;
    /** 조회 경로에서 새 칼럼을 만들 때의 값 출처(선언 fields의 from). 미지정=''(직접 입력 칼럼) */
    from?: string;
  }) => void;
  /** (dataSource over) 칼럼 키 rename — 선언 fields + 직접 반복 표 셀 + seed 행 컬렉션을 한 undo 스텝으로 */
  onRenameDataSourceColumn: (params: {
    dsKey: string;
    oldField: string;
    newField: string;
  }) => void;
  /** (dataSource over) 칼럼 삭제: 셀 바인딩 해제 + 선언 fields 제거를 한 undo 스텝으로 */
  onDeleteDataSourceColumn: (params: { tableId: string; dsKey: string; field: string }) => void;
  /** (반복영역) 칼럼↔셀 매핑 해제: 선언은 두고 바인딩만 푼다(삭제와 달리 복구 가능) */
  onUnmapDataSourceColumn: (params: { tableId: string; field: string }) => void;
  /** (dataSource over) 칼럼 순서 이동(선언 fields만 — 표 좌표 무관) */
  onMoveDataSourceColumn: (params: { dsKey: string; field: string; dir: -1 | 1 }) => void;
  /** "셀 지정" 버튼 호버 시 매핑 셀 캔버스 하이라이트(`cell:tableId:row:col`, null=해제) */
  onHighlightSource?: (id: string | null) => void;
  /**
   * 「소스 편집 →」 — 반복(표 반복영역 / repeat 노드)이 쓰는 선언의 편집 카드로 이동.
   * 표시(여기)와 편집 자리(데이터 섹션)가 갈린 축이라 왕복을 없앤다.
   */
  onGoToDataCard?: (key: string) => void;
  /** 반복영역 칼럼↔셀 매핑: 고른 셀에 소스 필드 key를 bindingKey로 설정 */
  setCellBinding: (
    tableId: string,
    row: number,
    col: number,
    bindingKey: string,
    lifecycle: BindingLifecycle,
  ) => void;
  /** 좌표 픽(cell 모드) 시작 — apply에 클릭 셀의 ref를 넘긴다. opts로 매핑 가능 행 강조/제약 정보 전달 */
  onStartCellPick: (
    token: string,
    apply: (ref: CellRef) => void,
    opts?: CellPickOpts,
  ) => void;
  /** 진행 중 좌표 픽 취소 */
  onCancelCellPick: () => void;
  /** 현재 픽을 시작한 토큰(버튼 활성 표시용). 비활성이면 null */
  activePickToken: string | null;
  /** 그룹 자식 목록에서 노드 클릭 시 그 노드를 선택 */
  onSelectNode: (id: string) => void;
  /** 선택 노드의 조상 그룹 체인(바깥→안) — 상위 그룹 이동 브레드크럼 */
  ancestorGroups: ComposerNode[];
  /** 표 전체 셀 일괄 style/속성 적용(인라인 BulkCellEditor) */
  onPatchAllCells: (tableId: string, patch: Partial<TableCell>) => void;
  /** 표 전체 셀 일괄 숫자 입력 실시간 미리보기(replacePresent). */
  onPatchAllCellsLive: (tableId: string, patch: Partial<TableCell>) => void;
  /** 표 전체 셀 일괄 숫자 입력 확정(commitPresent). */
  onPatchAllCellsCommit: (tableId: string, patch: Partial<TableCell>) => void;
  /** group⇄stack 종류 전환(툴바 handleToggleContainerKind와 공유 — id 불변, 선택 유지) */
  onToggleContainerKind: () => void;
  /** 선택 노드를 branch로 감싸기(조건부 표시) — 슬롯0에 노드 + 슬롯1 else 빈, 감싼 뒤 branch 선택 */
  onWrapInBranch: () => void;
  /** 라디오 그룹(공유키 체크박스 세트)에 옵션 1개 추가(신규 id 발급 필요 → 메인 담당) */
  onAddRadioOption: (groupId: string) => void;
  /** 같은 템플릿의 다른 노드/셀이 쓰는 bindingKey 집합(자기 제외) — 바인딩 편집기 중복 경고용 */
  usedBindingKeys?: Set<string>;
  /** branch 노드별 활성 슬롯 인덱스(캔버스 탭과 공유 state) */
  activeBranchSlots: Record<string, number>;
  /** branch 활성 슬롯 지정(슬롯 클릭 시 캔버스 탭 전환) */
  onSetActiveBranchSlot: (branchId: string, index: number) => void;
  /** branch에 빈 슬롯 추가 */
  onAddBranchSlot: (branchId: string) => void;
  /** branch에서 슬롯 제거(최소 1개 유지) */
  onRemoveBranchSlot: (branchId: string, slotId: string) => void;
  /** branch 슬롯 순서 이동 */
  onMoveBranchSlot: (branchId: string, slotId: string, dir: 'up' | 'down') => void;
  /** branch 슬롯 condition 편집(undefined=else) */
  onUpdateBranchSlotCondition: (branchId: string, slotId: string, condition: string | undefined) => void;
  /** branch 슬롯 label(표시 이름) 편집(빈 문자열/undefined=미설정) */
  onUpdateBranchSlotLabel: (branchId: string, slotId: string, label: string | undefined) => void;
  /** branch 조건 데이터 바인딩 목록 교체(빈 배열=제거) */
  onUpdateBranchConditionBindings: (branchId: string, next: ConditionBinding[]) => void;
}) {
  // 선택 노드의 직속 부모 컨텍스트(레이아웃 컨트롤 활성/비활성 판단용). ancestorGroups는 group/stack
  // 조상 체인(바깥→안)이라 마지막 요소가 직속 부모. 없으면 'flow'(최상위 본문 흐름).
  const directParent = ancestorGroups[ancestorGroups.length - 1];
  // 선택 노드가 branch일 때 그 분기가 놓인 **행 스코프** — 조건 키 후보 공급(반복 밖이면 빈 목록).
  // 반복영역 셀 안에는 노드를 넣을 수 없으므로(cellNodeInsertBlockReason) branch를 감싸는 반복은
  // 언제나 repeat 노드다 — 조상 체인만 보면 판정이 끝난다.
  const branchRowScope = useMemo((): { keys: RowScopeConditionKey[]; label?: string } => {
    if (node.kind !== 'branch') return { keys: [] };
    const repeat = enclosingRepeatNode(ancestorGroups);
    if (!repeat) return { keys: [] };
    const source = findRepeatSource(
      collectRepeatSources(dataDefs, catalog, dataSources),
      repeat,
    );
    return { keys: rowScopeConditionKeys(source), label: source?.label };
  }, [node.kind, ancestorGroups, dataDefs, catalog, dataSources]);
  // 부모 반복(조상 repeat)의 **배열 행 필드** 후보 — 본문 중첩 반복(표 반복영역·repeat 노드)의
  // over가 최상위 소스가 아니라 부모 행 필드일 때, 두 편집기가 "(알 수 없음)"/미선택 대신
  // 이름으로 보여주고 고를 수 있게 한다(2026-09-05 attendeeTriples 사례 — 파생 근거는
  // parentRowArrayFieldOptions JSDoc).
  const parentRowFieldSources = useMemo((): { key: string; label: string }[] => {
    if (node.kind !== 'repeat' && node.kind !== 'table') return [];
    const repeat = enclosingRepeatNode(ancestorGroups);
    if (!repeat) return [];
    const source = findRepeatSource(collectRepeatSources(dataDefs, catalog, dataSources), repeat);
    return parentRowArrayFieldOptions(source, catalog, dataSources);
  }, [node.kind, ancestorGroups, dataDefs, catalog, dataSources]);
  const parentCtx: 'group-row' | 'group-column' | 'stack' | 'flow' =
    directParent?.kind === 'group'
      ? directParent.direction === 'row'
        ? 'group-row'
        : 'group-column'
      : directParent?.kind === 'stack'
        ? 'stack'
        : 'flow';
  // content-hug: 결재란은 크기가 내용으로 고정 → 크기 컨트롤 비활성, 정렬은 위치 지정용으로 유효.
  const isContentHug = node.kind === 'special' && node.special === 'approvalBlock';
  // 최소 높이는 '높이=자동(콘텐츠)'일 때만 의미(고정/채움이면 무의미) → 그 경우에만 노출.
  const heightVal = node.layout.height;
  const heightEffDefault = parentCtx === 'group-row' ? 'fill' : 'auto';
  const heightMode =
    heightVal === undefined ? heightEffDefault : heightVal === 'fill' ? 'fill' : typeof heightVal === 'number' ? 'mm' : 'auto';
  const showMinHeight = !isContentHug && heightMode === 'auto';
  // 채움 비율(grow)은 가로 그룹에서 '너비=채움'인 자식이 여러 채움 형제와 비율을 다툴 때만 의미.
  const showFillRatio = !isContentHug && parentCtx === 'group-row' && node.layout.width === 'fill';
  // 라디오 그룹의 옵션 체크박스인가 — 직속 부모 그룹이 라디오 그룹(자식 전부 같은 키 공유 checkbox)이면
  // 이 체크박스의 개별 하단 바인딩 편집은 desync 위험(공유 키 이탈)이라 읽기전용 안내로 대체한다.
  const radioParentInfo =
    node.kind === 'checkbox' && ancestorGroups.length > 0
      ? getRadioGroupInfo(ancestorGroups[ancestorGroups.length - 1])
      : null;
  const isRadioOption = radioParentInfo !== null;

  return (
    <dl className="space-y-3 text-sm">
      {/* === 1. 식별·네비 === */}
      {/* 상위 그룹 이동 브레드크럼 — 조상 그룹 클릭 시 그 그룹 선택, 끝은 현재 노드. */}
      {ancestorGroups.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[11px] -mt-1">
          {ancestorGroups.map((g) => (
            <span key={g.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onSelectNode(g.id)}
                title={`${g.id} 선택`}
                className="px-1.5 py-0.5 rounded border border-gray-200 text-brand-600 hover:bg-brand-50"
              >
                {shortNodeLabel(g)}
              </button>
              <span className="text-gray-300">›</span>
            </span>
          ))}
          <span className="px-1.5 py-0.5 text-gray-500 font-medium">{shortNodeLabel(node)}</span>
        </div>
      )}
      <Field label="ID" value={node.id} mono />
      <Field label="종류(kind)" value={node.kind} mono />
      {node.kind === 'special' && <Field label="위젯" value={node.special} mono />}

      {/* group⇄stack 전환 — 툴바와 동일 기능을 패널에서도(종류 표시 바로 아래, 구조 유지·종류만 변경). */}
      {(node.kind === 'group' || node.kind === 'stack') && (
        <div className="flex items-center gap-2 -mt-1">
          <button
            type="button"
            onClick={onToggleContainerKind}
            title="구조(자식·레이아웃) 유지, 컨테이너 종류만 변경"
            className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            {node.kind === 'group' ? '⊟ 스택으로 전환' : '⊞ 그룹으로 전환'}
          </button>
        </div>
      )}

      {/* 그룹 내 노드 목록(네비게이션) — 식별·네비 섹션 상단 배치. 클릭 시 자식 드릴다운. */}
      {node.kind === 'group' && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">
            그룹 내 노드 ({node.children.length})
          </dt>
          <dd>
            {node.children.length === 0 ? (
              <p className="text-xs text-gray-400">비어 있음</p>
            ) : (
              <ul className="space-y-0.5">
                {node.children.map((child) => (
                  <li key={child.id}>
                    <button
                      type="button"
                      onClick={() => onSelectNode(child.id)}
                      title={`${child.id} 선택`}
                      className="w-full text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{shortNodeLabel(child)}</span>
                      <span className="text-[10px] text-gray-400 font-mono shrink-0">{child.id}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </div>
      )}

      {/* 스택 내 노드 목록(네비게이션) — 자식 분류 안내 + 드릴다운. 상단 배치. */}
      {node.kind === 'stack' && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">
            스택 내 노드 ({node.children.length})
          </dt>
          <dd className="space-y-1.5">
            <p className="text-[11px] text-gray-400 leading-snug">
              모든 자식이 같은 칸에 z-겹침됩니다. 각 자식을 선택해 크기(채움/고정)와 정렬(가로/세로)을 설정하세요.
              칸 크기는 가장 큰 자식에 맞춰집니다.
            </p>
            {node.children.length === 0 ? (
              <p className="text-xs text-gray-400">비어 있음</p>
            ) : (
              <ul className="space-y-0.5">
                {node.children.map((child) => (
                  <li key={child.id}>
                    <button
                      type="button"
                      onClick={() => onSelectNode(child.id)}
                      title={`${child.id} 선택`}
                      className="w-full text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{shortNodeLabel(child)}</span>
                      <span className="text-[10px] text-gray-400 font-mono shrink-0">{child.id}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </div>
      )}

      {/* 분기 조건 데이터(branch 노드) — 슬롯 조건이 비교할 식별 키+출처. 슬롯 목록 위에 둔다. */}
      {node.kind === 'branch' && (
        <BranchConditionBindingsEditor
          node={node}
          catalog={catalog}
          usedBindingKeys={usedBindingKeys}
          onChange={(next) => onUpdateBranchConditionBindings(node.id, next)}
        />
      )}

      {/* 분기 슬롯 편집(branch 노드) — 슬롯별 조건 + 순서/추가/삭제 + 활성 슬롯 자식 목록(드릴다운).
          반복 안 분기면 그 행 스코프 키를 조건 후보로 함께 공급한다(엔진 mergeRepeatScope 대응). */}
      {node.kind === 'branch' && (
        <BranchSlotsEditor
          node={node}
          catalog={catalog}
          dataSources={dataSources}
          rowScope={branchRowScope.keys}
          rowScopeLabel={branchRowScope.label}
          activeIndex={(() => {
            const raw = activeBranchSlots[node.id] ?? 0;
            return raw >= 0 && raw < node.branches.length ? raw : 0;
          })()}
          onSetActive={(i) => onSetActiveBranchSlot(node.id, i)}
          onAddSlot={() => onAddBranchSlot(node.id)}
          onRemoveSlot={(slotId) => onRemoveBranchSlot(node.id, slotId)}
          onMoveSlot={(slotId, dir) => onMoveBranchSlot(node.id, slotId, dir)}
          onUpdateCondition={(slotId, condition) =>
            onUpdateBranchSlotCondition(node.id, slotId, condition)
          }
          onUpdateLabel={(slotId, label) => onUpdateBranchSlotLabel(node.id, slotId, label)}
          onSelectNode={onSelectNode}
        />
      )}

      {/* 반복(repeat) 노드 — 순회 데이터 소스(over) 선택 + 반복 단위(template) 자식 드릴다운. */}
      {node.kind === 'repeat' && (
        <RepeatSourceEditor
          node={node}
          isTopLevel={isTopLevel}
          catalog={catalog}
          dataDefs={dataDefs}
          dataSources={dataSources}
          parentRowFieldSources={parentRowFieldSources}
          onPatch={onPatch}
          onSelectNode={onSelectNode}
          onGoToDataCard={onGoToDataCard}
        />
      )}

      {/* === 2. 레이아웃 (공통 — 먼저) === */}
      {/* 그룹 레이아웃 — flex 방향(수직 column / 수평 row) + 간격(mm). */}
      {node.kind === 'group' && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">그룹 레이아웃</dt>
          <dd className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-16">방향</span>
              <div className="inline-flex rounded border border-gray-300 overflow-hidden">
                {([
                  ['column', '수직'],
                  ['row', '수평'],
                ] as const).map(([dir, label]) => (
                  <button
                    key={dir}
                    type="button"
                    onClick={() => onPatch({ direction: dir } as Partial<ComposerNode>)}
                    className={`px-2.5 py-1 text-xs ${
                      (node.direction ?? 'column') === dir
                        ? 'bg-brand-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-16">간격(mm)</span>
              <NumericField
                value={node.gap ?? undefined}
                min={0}
                step={0.01}
                placeholder="0"
                toDisplay={pxToMm}
                fromDisplay={mmToPx}
                className="w-20 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
                onPreview={(px) => onPreview({ gap: px } as Partial<ComposerNode>)}
                onCommit={(px) => onCommit({ gap: px } as Partial<ComposerNode>)}
                onRevert={onRevert}
              />
            </div>
            {/* 자식 기본 정렬은 콘텐츠 정렬(contentAlign*)로 일원화됨.
                자식 교차축 정렬은 각 자식의 '콘텐츠 정렬' 또는 위 레이아웃 섹션 ContentAlignEditor가 담당. */}
          </dd>
        </div>
      )}

      {/* 레이아웃 편집(너비/높이/최소높이/늘림/정렬/여백) — 모든 노드 공통(UI 일관성). 결재란
          (content-hug)은 크기 컨트롤을 비활성(회색)으로 노출하고 정렬만 유효하게 둔다. 정렬 토글은
          부모 컨텍스트(주축 여부)·채움 여부에 따라 개별 비활성. 늘림(grow)은 수평 그룹에서만 노출.
          단 branch(투명 논리 컨테이너)는 자체 크기/정렬/최소높이/채움/여백이 없어 레이아웃 섹션
          전체를 숨기고(결재란 content-hug 비활성 분기의 확장 — 크기뿐 아니라 정렬까지 전부), 대신
          "크기는 슬롯 콘텐츠가 결정" 안내 한 줄만 노출한다. */}
      {node.kind === 'branch' ? (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">레이아웃</dt>
          <dd>
            <p className="text-[11px] text-gray-400 leading-snug">
              분기 크기는 슬롯 콘텐츠가 결정합니다 — 고정하려면 슬롯 안 그룹에 지정하세요.
            </p>
            {/* branch는 레이아웃 섹션을 통째로 숨기므로 넘침 경고도 함께 묻힌다 — 여기서 되살린다.
                조작할 크기 축이 없는 노드라 처방은 "슬롯 안"을 가리키지만, 어느 분기가 넘치는지는
                이 자리에서 보여야 한다. */}
            {overflowNotes.map((note) => (
              <p
                key={note}
                className="mt-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800"
              >
                ⚠ {note}
              </p>
            ))}
          </dd>
        </div>
      ) : (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">레이아웃</dt>
          <dd className="space-y-2">
            <WidthEditor node={node} onPatch={onPatch} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} disabled={isContentHug} parentCtx={parentCtx} />
            <HeightEditor node={node} onPatch={onPatch} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} disabled={isContentHug} parentCtx={parentCtx} />
            {showMinHeight && <MinHeightEditor node={node} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />}
            {/* 페이지 넘침 경고 — 저장 버튼 옆 배지와 **같은 문구**를 조작 지점 옆에 되풀이한다.
                배지는 "문서에 무엇이 어긋났나"를 세고, 여기는 "지금 만지는 이 값을 얼마로 하면
                닫히나"를 답한다. 처방에 필요한 숫자가 문구에 이미 들어 있어 왕복이 없다. */}
            {overflowNotes.map((note) => (
              <p
                key={note}
                className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800"
              >
                ⚠ {note}
              </p>
            ))}
            {showFillRatio && <GrowEditor node={node} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />}
            <AlignEditor node={node} onPatch={onPatch} parentCtx={parentCtx} isContentHug={isContentHug} />
            <ContentAlignEditor node={node} onPatch={onPatch} />
            <PaddingEditor node={node} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />
          </dd>
        </div>
      )}

      {/* === 3. 스타일 === */}
      {/* 텍스트 스타일 편집(text 노드 한정) */}
      {node.kind === 'text' && <TextStyleEditor node={node} onPatch={onPatch} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />}

      {/* 박스 스타일 — 사각형 컨테이너(채움색/테두리/모서리). group/rect/stack은 node.style, text는 node.boxStyle
          (text의 node.style은 폰트류 TextStyle이라 이름 충돌 회피). 모두 RectStyle·미설정=장식 없음. */}
      {(node.kind === 'group' || node.kind === 'rect' || node.kind === 'stack' || node.kind === 'text') && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">
            {node.kind === 'rect' ? '사각형 스타일' : node.kind === 'text' ? '텍스트 박스 스타일' : '박스 스타일'}
          </dt>
          <dd>
            <RectStyleFields
              value={node.kind === 'text' ? node.boxStyle : node.style}
              onChange={(next) =>
                onPatch(
                  (node.kind === 'text' ? { boxStyle: next } : { style: next }) as Partial<ComposerNode>,
                )
              }
              onPreview={(next) =>
                onPreview(
                  (node.kind === 'text' ? { boxStyle: next } : { style: next }) as Partial<ComposerNode>,
                )
              }
              onRevert={onRevert}
            />
          </dd>
        </div>
      )}

      {/* === 4. 콘텐츠·데이터 === */}
      {/* 텍스트 본문 편집(text 노드 한정) */}
      {node.kind === 'text' && (
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1 block">텍스트</label>
          <textarea
            value={node.text ?? ''}
            onChange={(e) => onPatch({ text: e.target.value } as Partial<ComposerNode>)}
            rows={3}
            disabled={!!node.binding}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            placeholder={node.binding ? '🔗 바인딩 사용 중 (해제하려면 아래 바인딩을 제거)' : '정적 텍스트 (비우면 바인딩 값 사용)'}
          />
          {node.binding && (
            <p className="text-[11px] text-gray-400 mt-0.5">
              이 칸은 바인딩으로 채워집니다. 정적 텍스트를 쓰려면 바인딩을 제거하세요.
            </p>
          )}
        </div>
      )}

      {/* 이미지 맞춤(objectFit) — 박스 크기와 원본 비율이 다를 때의 처리. 이미지 src는 바인딩으로
          채우므로(하단 공통 섹션) 여기서는 표시 방식만 다룬다.
          ⚠️ 엔진(primitives ImagePrim)은 fit이 undefined일 때 objectFit을 아예 걸지 않는다 →
          CSS 기본값 fill(늘이기)로 렌더된다. 즉 '미지정'이 곧 늘이기이며 contain이 기본이 아니다.
          shared의 ImageNode.fit 타입도 'contain' | 'cover' 둘뿐이라 늘이기는 미지정으로 표현한다. */}
      {node.kind === 'image' && (
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1 block">이미지 맞춤</label>
          <select
            value={node.fit ?? ''}
            onChange={(e) =>
              onPatch({ fit: (e.target.value || undefined) as ImageNode['fit'] } as Partial<ComposerNode>)
            }
            className={SELECT_CLS}
          >
            <option value="">늘이기 (기본 — 박스에 꽉 채우되 비율 무시)</option>
            <option value="contain">비율 유지 (박스 안에 전부 보이게)</option>
            <option value="cover">채우기 (비율 유지하고 넘치는 부분 잘라냄)</option>
          </select>
          {/* 주석 이미지의 높이 의미 안내 — 신규 축 없이 기존 레이아웃 '높이' 필드가 상한이 된다. */}
          <p className="text-[11px] text-gray-400 mt-1">
            주석(도형) 이미지 값에서는 레이아웃 높이가 고정이 아니라 비율 유지 축소
            상한으로 동작합니다(오버레이 좌표 자동 정합).
          </p>
        </div>
      )}

      {/* 주석 오버레이 저작(node.overlay) — 값이 주석 이미지 객체일 때만 렌더에 반영(평면 URL은 무시).
          표시 판정은 엔진(RenderTemplate case 'image')이, 스타일 반영은 AnnotatedImageView가 담당. */}
      {node.kind === 'image' && (
        <ImageOverlaySection node={node} onPatch={onPatch} />
      )}

      {/* 체크박스 속성(라벨/박스크기/checkedValue/정적 checked) — 바인딩은 하단 공통 섹션 */}
      {node.kind === 'checkbox' && <CheckboxPropsEditor node={node} onPatch={onPatch} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />}

      {/* 선택지 그룹 속성(표현 스타일만) — 선택지 목록·값·라벨은 dataDef options에서 자동 파생.
          바인딩(행 필드)은 하단 공통 섹션. */}
      {node.kind === 'choiceGroup' && <ChoiceGroupPropsEditor node={node} onPatch={onPatch} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />}

      {/* 라디오 그룹 속성(공유키 rename + 옵션 라벨/값/추가/삭제) — group이고 자식이 전부
          같은 키를 공유하는 체크박스일 때만 노출(일반 group에는 미노출). */}
      {node.kind === 'group' && getRadioGroupInfo(node) && (
        <RadioGroupPropsEditor group={node} onPatch={onPatch} onAddOption={() => onAddRadioOption(node.id)} />
      )}

      {/* 표 구조 편집(T2) — 표 노드 선택(셀 미선택) 시 */}
      {node.kind === 'table' && (
        <TableStructureEditor
          table={node}
          isTopLevel={isTopLevel}
          onAddRow={onAddRow}
          onRemoveRow={onRemoveRow}
          onAddCol={onAddCol}
          onRemoveCol={onRemoveCol}
          onSetColWidths={onSetColWidths}
          onSetRowHeights={onSetRowHeights}
          onPreviewColWidths={onPreviewColWidths}
          onPreviewRowHeights={onPreviewRowHeights}
          onRevert={onRevert}
          onSetTableFlag={onSetTableFlag}
          onPatchAllCells={onPatchAllCells}
          onPatchAllCellsLive={onPatchAllCellsLive}
          onPatchAllCellsCommit={onPatchAllCellsCommit}
        />
      )}

      {/* 표 외곽 테두리 — 전체 셀 테두리와 별개(외곽 1.5px/셀 1px 등, 또는 강제 hidden으로 테두리 없는 표). */}
      {node.kind === 'table' && <TableOuterBorderSection node={node} onPatch={onPatch} />}

      {/* 결재란(approvalBlock) 구성 옵션 + 결재라인 구조 — variant select 대신 자유 구성(P2a-3) */}
      {node.kind === 'special' && node.special === 'approvalBlock' && (
        <div>
          {/* 결재란 구성 옵션(출력 토글/치수/폰트/영역별 패딩) — node.props.approvalConfig 저장 */}
          <ApprovalConfigEditor node={node} onPatch={onPatch} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />

          {/* 결재라인 구조 편집(P2a-2) — 고정 단계 + 최대 추가 결재 수. 편집 시 캔버스
              시뮬레이션 미리보기가 applySimulatedApproval(template.approvalLineMeta) 경유로 자동 반영.
              세로로 길어 접이식(기본 펼침)으로 스크롤 부담을 줄인다.
              크기/정렬(레이아웃)은 위 공통 레이아웃 섹션이 담당(content-hug라 크기 비활성·정렬만 유효). */}
          <ApprovalLineStructureSection
            approvalLineMeta={approvalLineMeta}
            onUpdateApprovalLineMeta={onUpdateApprovalLineMeta}
          />
        </div>
      )}

      {/* 반복 영역 제안 배지(form-import P3) — 양식 업로드 초안의 AI 제안(importMeta). 적용은
          기존 수동 플로우 산출물과 동형(반복영역+수동 데이터셋 골격), 무시는 제안만 제거. */}
      {/* 표 격자와 어긋난 제안(2026-08-19 수정 **이전에** 저장된 초안에만 남아 있다)도 카드는
          그린다 — 다만 [적용] 없이 사유 + [무시]만. 종전에는 통째로 숨겼는데, 그러면 [무시]까지
          같이 사라져 그 제안을 **치울 방법이 없어진다**(importMeta가 초안에 영구 잔존).
          로드 정규화(toEditableTemplate)로 조용히 버리지 않는 이유: 그곳의 정규화들은 전부
          무손실 이행(형상 변환·중복 사본 참조화)인데 이 드롭은 AI 제안과 사유를 흔적 없이
          없애고, 사람이 표에 행을 더해 유효해질 수 있는 제안까지 미리 지운다. 공유 규약도
          "에디터는 거부하고 사유를 보여 준다"(packages/shared form-import-repeat-hint.ts)이다. */}
      {node.kind === 'table' &&
        (importRepeatHints ?? [])
          .filter((h) => h.tableNodeId === node.id)
          .map((h) => {
            const applicable = isRepeatHintRowRangeValid(h, node.rowCount);
            return (
              <div
                key={`${h.tableNodeId}-${h.rowStart}-${h.rowEnd}`}
                className={
                  applicable
                    ? 'rounded border border-amber-300 bg-amber-50 p-2'
                    : 'rounded border border-gray-300 bg-gray-50 p-2'
                }
              >
                <p
                  className={
                    applicable
                      ? 'text-[11px] font-medium leading-snug text-amber-800'
                      : 'text-[11px] font-medium leading-snug text-gray-600'
                  }
                >
                  반복 영역 제안: {h.rowStart}~{h.rowEnd}행
                  {applicable ? '' : ' — 적용 불가'}
                </p>
                <p
                  className={
                    applicable
                      ? 'mt-0.5 text-[11px] leading-snug text-amber-700'
                      : 'mt-0.5 text-[11px] leading-snug text-gray-500'
                  }
                >
                  {h.reason}
                </p>
                {/* 사유는 적용 시도 시 거부 문구와 같은 상수 — 두 벌이면 같은 상태를 두 문장으로 설명한다. */}
                {applicable ? null : (
                  <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
                    {REPEAT_HINT_ROW_RANGE_ERROR} 이 표는 {node.rowCount}행입니다 — 무시하거나, 행을
                    맞춘 뒤 다시 확인하세요.
                  </p>
                )}
                <div className="mt-1.5 flex items-center gap-1.5">
                  {/* 죽은 버튼을 남기지 않는다 — 적용 불가면 [적용] 자체를 그리지 않고 [무시]만 남긴다. */}
                  {applicable ? (
                    <Button variant="warning" size="xs" onClick={() => onApplyImportRepeatHint?.(h)}>
                      적용
                    </Button>
                  ) : null}
                  {/* 경고 톤 유지 — 이 짝은 amber 제안 카드 안이라 중립 secondary면 카드에서 떠 보인다.
                      `Button`에 outline-warning variant가 없어 색만 얹는다. */}
                  <Button
                    variant="secondary"
                    size="xs"
                    className={
                      applicable
                        ? 'border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-800'
                        : undefined
                    }
                    onClick={() => onDismissImportRepeatHint?.(h)}
                  >
                    무시
                  </Button>
                </div>
              </div>
            );
          })}
      {/* 반복영역(동적 표) 편집 — 표 노드 한정. 표 구조와 분리해 바인딩 섹션 바로 위에 배치. */}
      {node.kind === 'table' && (
        <RepeatRegionEditor
          table={node}
          isTopLevel={isTopLevel}
          catalog={catalog}
          dataDefs={dataDefs}
          dataSources={dataSources}
          parentRowFieldSources={parentRowFieldSources}
          onAddRow={onAddRow}
          onAddRepeatRegion={onAddRepeatRegion}
          onRemoveRepeatRegion={onRemoveRepeatRegion}
          onUpdateRepeatRegion={onUpdateRepeatRegion}
          onSetRepeatPatternRows={onSetRepeatPatternRows}
          onCreateManualDataSource={onCreateManualDataSource}
          onAddDataSourceColumn={onAddDataSourceColumn}
          onRenameDataSourceColumn={onRenameDataSourceColumn}
          onDeleteDataSourceColumn={onDeleteDataSourceColumn}
          onUnmapDataSourceColumn={onUnmapDataSourceColumn}
          onMoveDataSourceColumn={onMoveDataSourceColumn}
          onHighlightSource={onHighlightSource}
          onGoToDataCard={onGoToDataCard}
          setCellBinding={setCellBinding}
          onStartCellPick={onStartCellPick}
          onCancelCellPick={onCancelCellPick}
          activePickToken={activePickToken}
        />
      )}

      {/* 페이지 배치 — 페이지 흐름 개념(inner 레이아웃과 별개, 바인딩 섹션 바로 위).
          공통: pageBreak 'before'(모든 노드 — 이 노드 앞에서 무조건 페이지 나눔, 구 SMS forceNewPage 동형.
          표/repeat의 "새 페이지에서 시작"은 통째로 안 들어갈 때만인 조건부 — 의미 다름).
          group/stack/table: 페이지 채움(PageFillEditor — 채움 체크 + 모드 + 표 채움행 높이). */}
      <div>
        <dt className="text-xs font-semibold text-gray-500 mb-1">페이지 배치</dt>
        <dd className="space-y-1.5">
          {(() => {
            // 중첩 게이트(pageFlowGate 공용) — 종전 이 자리만 별도 문구/스타일로 갈라져 있던 것을 일치화.
            const gate = pageFlowGate(
              isTopLevel,
              node.pageBreak === 'before',
              "이 노드 앞에서 무조건 페이지를 나눕니다(항상 새 페이지 머리에서 시작). 표/반복의 '새 페이지에서 시작'(통째로 안 들어갈 때만)과 달리 조건 없이 항상 적용됩니다.",
            );
            return (
              <label className={`flex items-center gap-2 ${gate.labelClass}`} title={gate.title}>
                <input
                  type="checkbox"
                  checked={node.pageBreak === 'before'}
                  disabled={gate.disabled}
                  onChange={(e) =>
                    onPatch({ pageBreak: e.target.checked ? 'before' : undefined } as Partial<ComposerNode>)
                  }
                  className="w-3.5 h-3.5"
                />
                <span className="text-xs text-gray-600">항상 새 페이지에서 시작</span>
              </label>
            );
          })()}
          <NestedPageFlowNote isTopLevel={isTopLevel} />
          {(node.kind === 'group' || node.kind === 'stack' || node.kind === 'table') && (
            <PageFillEditor node={node} isTopLevel={isTopLevel} onPatch={onPatch} onPreview={onPreview} onCommit={onCommit} onRevert={onRevert} />
          )}
        </dd>
      </div>

      {/* 바인딩 편집 (3-binding) — 노출 여부는 nodeKindHasBindingEditor(위 JSDoc이 근거 정본). */}
      {nodeKindHasBindingEditor(node.kind) && (
        <div>
          <dt className="text-xs font-semibold text-gray-500 mb-1">바인딩</dt>
          <dd className="space-y-1.5">
            {isRadioOption ? (
              <p className="text-[11px] text-gray-500 leading-relaxed">
                이 체크박스는 라디오 그룹의 옵션입니다. 공유 키(
                <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-700">{radioParentInfo.bindingKey}</code>
                )는 그룹을 선택해 "라디오 그룹" 편집기에서 변경하세요. (라벨/값은 옵션별로 위에서 편집)
              </p>
            ) : (
              (() => {
                // 반복 단위 안 노드 — 조상 repeat의 행 필드를 식별 키 하이브리드(BindingKeyPicker)
                // 후보로 전달(종전 별도 RepeatItemFieldCombo를 대체 — 셀 패널과 UX 통일).
                // 행 필드 바인딩은 행 scope에서 값이 분리되므로 전역 중복 키 경고(오탐)를 숨긴다.
                const enclosingRepeat = [...ancestorGroups]
                  .reverse()
                  .find((n): n is RepeatNode => n.kind === 'repeat');
                const rowSource = enclosingRepeat
                  ? findRepeatSource(
                      collectRepeatSources(dataDefs, catalog, dataSources),
                      enclosingRepeat,
                    )
                  : undefined;
                const isRowField = isRowScopeFieldBinding(node.binding, rowSource);
                // 출처 정면 표시 재료 — 이동은 편집 카드가 있는 선언(dataSources/dataDefs)만
                // (hasEditableDataCard — 시스템 조회 소스는 이 화면에 고칠 자리가 없다).
                const rowFieldInfo = rowScopeFieldSourceInfo(node.binding, rowSource);
                const rowFieldCardKey =
                  rowSource && hasEditableDataCard(rowSource.key, dataDefs, dataSources)
                    ? rowSource.key
                    : undefined;
                return (
                  <BindingEditor
                    node={node}
                    onPatch={onPatch}
                    catalog={catalog}
                    usedKeys={isRowField ? undefined : usedBindingKeys}
                    keyOptions={rowFieldPickerOptions(rowSource?.fields)}
                    rowScopeFieldBinding={isRowField}
                    rowScopeFieldSource={
                      rowFieldInfo ? { ...rowFieldInfo, cardKey: rowFieldCardKey } : undefined
                    }
                    onGoToDataCard={onGoToDataCard}
                    // 행 필드 바인딩이면 중복 경고는 끄지만(행 scope라 오탐) 공유 자리 강조는
                    // 그대로 둔다 — "어디서 같은 키를 쓰나"는 경고와 무관하게 알고 싶은 정보다.
                    selfOwnerId={`node:${node.id}`}
                  />
                );
              })()
            )}
          </dd>
        </div>
      )}

      {/* === 5. 고급·메타 === */}
      {/* 조건부 표시 — 노드별 condition은 폐지됨. 선택 노드를 branch로 감싸 조건에 따라 표시/숨김한다.
          branch(이미 분기)·결재란(approvalBlock)은 감싸기 대상 제외. */}
      {node.kind !== 'branch' &&
        !(node.kind === 'special' && node.special === 'approvalBlock') && (
          <div>
            <dt className="text-xs font-semibold text-gray-500 mb-1">조건부 표시</dt>
            <dd>
              <button
                type="button"
                onClick={onWrapInBranch}
                title="이 노드를 분기(branch) 슬롯0에 넣고 else 빈 슬롯을 더합니다. 슬롯 조건이 참인 첫 슬롯만 표시됩니다. (표 셀 안 노드는 감쌀 수 없음)"
                className="w-full px-2 py-1 text-xs rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
              >
                분기로 감싸기 (조건부 표시)
              </button>
              <p className="mt-1 text-[10px] text-gray-400 leading-relaxed">
                선택 노드를 분기 슬롯으로 감싸 조건에 따라 표시/숨김합니다. 감싼 뒤 분기 노드의 슬롯 조건을 편집하세요.
              </p>
            </dd>
          </div>
        )}
    </dl>
  );
}

/**
 * 치수(열 너비/행 높이) 입력 — 편집 중에는 로컬 문자열 state로 부분 입력/빈값('auto')/'%'를 허용하고,
 * 타이핑/화살표로 값이 변하는 동안 onPreview(replacePresent)로 캔버스에 즉시 반영, Enter/blur에
 * onCommit(commitPresent)으로 확정한다(undo 1스텝). Esc는 편집 시작 전 값으로 복원(onRevert).
 * 화살표 ↑/↓ ±1, Shift ±10, Alt ±0.1(표시 단위 기준). 숫자가 아닌 값('auto'/'%')은 증감/미리보기 skip.
 * unit: 표시 단위 — 양식 탭(인쇄 지면)=mm(기본), 작성 페이지(웹 폼)=px(저장은 양쪽 다 px).
 * 결정: docs/decisions/2026-07-13-writeform-units-web-native.md
 */
function DimInput({
  value,
  onPreview,
  onCommit,
  onRevert,
  unit = 'mm',
}: {
  value: number | string;
  onPreview: (raw: string) => void;
  onCommit: (raw: string) => void;
  onRevert: () => void;
  unit?: 'mm' | 'px';
}) {
  const display =
    typeof value === 'number' ? String(unit === 'px' ? value : pxToMm(value)) : String(value);
  const input = useNumericPreviewInput({
    display,
    // 순수 숫자(mm)만 증감/미리보기 대상. 'auto'/'%'/부분입력은 null → skip.
    parse: (raw) => {
      const t = raw.trim();
      return /^\d+(\.\d+)?$/.test(t) ? Number(t) : null;
    },
    format: (n) => String(n),
    onPreview,
    onCommit,
    onRevert,
    min: 0,
  });
  return <input type="text" className={INPUT_CLS} placeholder="auto" {...input} />;
}

/**
 * 속성 패널 공통 숫자 필드(label 없이 input만) — useNumericPreviewInput 배선.
 *
 * 실시간 미리보기(onPreview=replacePresent) + Enter/blur 확정(onCommit=commitPresent) +
 * Esc 복원(onRevert=discardTransient) + 화살표 증감(stepConfig)을 한 곳에 모아, 속성 패널의 개별
 * 숫자 입력들이 각자 배선을 반복하지 않도록 한다(DimInput/BulkMmInput과 같은 취지의 범용판).
 *
 * 값은 stored 단위(px 등)로 주고받고, 표시/증감은 display 단위(mm·pt·% 등)로 — toDisplay/fromDisplay 변환.
 * 빈칸은 미리보기 skip, commit 시에만 undefined로 전달(빈값=미설정 처리는 호출측 책임).
 */
function NumericField({
  value,
  mixed = false,
  step = 1,
  stepConfig,
  min,
  max,
  placeholder,
  className = INPUT_CLS,
  title,
  disabled = false,
  toDisplay = (n) => n,
  fromDisplay = (n) => n,
  onPreview,
  onCommit,
  onRevert,
}: {
  /** 확정값(stored 단위, 미설정=undefined). */
  value: number | undefined;
  /** 다중 선택 혼합 — 값 비우고 placeholder '혼합'. */
  mixed?: boolean;
  /** HTML step 속성(스피너 클릭 증감). 화살표 키 증감은 stepConfig가 결정. */
  step?: number;
  /** 화살표 키 증감 스텝(입력 성격별 — 예: lineHeight base 0.1/alt 0.01). 미지정=1/10/0.1. */
  stepConfig?: ArrowStepConfig;
  /** display 단위 min(화살표 클램프 + input min). */
  min?: number;
  /** display 단위 max(화살표 클램프 + input max). */
  max?: number;
  placeholder?: string;
  className?: string;
  title?: string;
  disabled?: boolean;
  /** stored → display 숫자(예: pxToMm/pxToPt). 기본 항등. */
  toDisplay?: (n: number) => number;
  /** display 숫자 → stored(예: mmToPx/ptToPx). 기본 항등. */
  fromDisplay?: (n: number) => number;
  /** 유효 입력 실시간 미리보기(replacePresent). */
  onPreview: (stored: number) => void;
  /** 확정(commitPresent). 빈값=undefined. */
  onCommit: (stored: number | undefined) => void;
  /** 미리보기 폐기(discardTransient) — Esc/무변경/이탈. */
  onRevert: () => void;
}) {
  const display = mixed || value == null ? '' : String(toDisplay(value));
  const input = useNumericPreviewInput({
    display,
    // 빈칸/'-'은 null(미리보기 skip). 그 외 유한 숫자(음수 허용 — 자간 등)만 유효.
    parse: (raw) => {
      const t = raw.trim();
      if (t === '' || t === '-') return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    },
    format: (n) => String(n),
    onPreview: (raw) => onPreview(fromDisplay(Number(raw.trim()))),
    onCommit: (raw) => {
      const t = raw.trim();
      onCommit(t === '' ? undefined : fromDisplay(Number(t)));
    },
    onRevert,
    min,
    max,
    stepConfig,
  });
  return (
    <input
      type="number"
      step={step}
      {...(min != null ? { min } : {})}
      {...(max != null ? { max } : {})}
      disabled={disabled}
      title={title}
      placeholder={mixed ? '혼합' : placeholder}
      className={className}
      {...input}
    />
  );
}

/**
 * 표 구조 편집(T2) — 표 노드 자체 선택(셀 미선택) 시 노출.
 *
 * 셀 선택 컨텍스트가 없으므로 기준은 "끝"이다: 맨 끝 행/열 추가, 마지막 행/열 삭제.
 * (셀 기준 위/아래·좌/우 삽입은 CellEditor에서 제공.) 열별 너비(colWidths)는 텍스트로 입력한다.
 * 모든 변경은 상위 콜백(apply 경유)으로 undo 대상.
 */
function TableStructureEditor({
  table,
  isTopLevel,
  onAddRow,
  onRemoveRow,
  onAddCol,
  onRemoveCol,
  onSetColWidths,
  onSetRowHeights,
  onPreviewColWidths,
  onPreviewRowHeights,
  onRevert,
  onSetTableFlag,
  onPatchAllCells,
  onPatchAllCellsLive,
  onPatchAllCellsCommit,
}: {
  table: TableNode;
  /** 표가 body 최상위인가 — startOnNewPage는 최상위에서만 동작(NESTED_PAGE_FLOW_HINT). */
  isTopLevel: boolean;
  onAddRow: (tableId: string, atRow: number) => void;
  onRemoveRow: (tableId: string, atRow: number) => void;
  onAddCol: (tableId: string, atCol: number) => void;
  onRemoveCol: (tableId: string, atCol: number) => void;
  onSetColWidths: (tableId: string, widths: (number | string)[]) => void;
  onSetRowHeights: (tableId: string, heights: (number | string)[]) => void;
  /** 열 너비 실시간 미리보기(replacePresent — 히스토리 미적재) */
  onPreviewColWidths: (tableId: string, widths: (number | string)[]) => void;
  /** 행 높이 실시간 미리보기(replacePresent — 히스토리 미적재) */
  onPreviewRowHeights: (tableId: string, heights: (number | string)[]) => void;
  /** 치수 입력 미리보기 폐기(discardTransient) — Esc/이탈 시 원값 복원 */
  onRevert: () => void;
  /** 페이지 분할 옵션(startOnNewPage/groupStartOnNewPage/groupContinuationMark) 토글. */
  onSetTableFlag: (tableId: string, patch: Partial<TableNode>) => void;
  /** 표 전체 셀 일괄 style/속성 적용(인라인 BulkCellEditor) */
  onPatchAllCells: (tableId: string, patch: Partial<TableCell>) => void;
  /** 표 전체 셀 일괄 숫자 입력 실시간 미리보기(replacePresent). */
  onPatchAllCellsLive: (tableId: string, patch: Partial<TableCell>) => void;
  /** 표 전체 셀 일괄 숫자 입력 확정(commitPresent). */
  onPatchAllCellsCommit: (tableId: string, patch: Partial<TableCell>) => void;
}) {
  // colWidths를 colCount 길이에 맞춘 표시용 배열(미지정 칸은 'auto'로 채움).
  const widths: (number | string)[] = Array.from(
    { length: table.colCount },
    (_, i) => table.colWidths?.[i] ?? 'auto',
  );

  // 한 칸 raw 입력 → 전체 colWidths 배열 재구성(숫자=mm 입력→px 저장, '%'/문자열 유지, 빈값은 'auto').
  const buildWidths = (idx: number, raw: string): (number | string)[] => {
    const next = widths.slice();
    const trimmed = raw.trim();
    if (trimmed === '') {
      next[idx] = 'auto';
    } else if (/^\d+(\.\d+)?$/.test(trimmed)) {
      next[idx] = mmToPx(Number(trimmed)); // mm 입력(소수 허용) → px 저장
    } else {
      next[idx] = trimmed;
    }
    return next;
  };

  // rowHeights를 rowCount 길이에 맞춘 표시용 배열(미지정 칸은 'auto').
  const heights: (number | string)[] = Array.from(
    { length: table.rowCount },
    (_, i) => table.rowHeights?.[i] ?? 'auto',
  );
  const buildHeights = (idx: number, raw: string): (number | string)[] => {
    const next = heights.slice();
    const trimmed = raw.trim();
    if (trimmed === '') {
      next[idx] = 'auto';
    } else if (/^\d+(\.\d+)?$/.test(trimmed)) {
      next[idx] = mmToPx(Number(trimmed)); // mm 입력(소수 허용) → px 저장
    } else {
      next[idx] = trimmed; // '%' 등
    }
    return next;
  };
  // 균등 분배 — 각 행/열을 (100/N)%로(stretch·fixed layout에서 정확히 N등분, 내용 불균형 무관).
  const distributeRowsEven = () => {
    const pct = `${(100 / table.rowCount).toFixed(4).replace(/\.?0+$/, '')}%`;
    onSetRowHeights(table.id, Array.from({ length: table.rowCount }, () => pct));
  };
  const distributeColsEven = () => {
    const pct = `${(100 / table.colCount).toFixed(4).replace(/\.?0+$/, '')}%`;
    onSetColWidths(table.id, Array.from({ length: table.colCount }, () => pct));
  };

  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">
        표 구조 ({table.rowCount} × {table.colCount})
      </dt>
      <dd className="space-y-2">
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-6">행</span>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="맨 끝에 행 추가"
            onClick={() => onAddRow(table.id, table.rowCount)}
          >
            + 끝 행
          </button>
          <button
            type="button"
            className={STRUCT_DEL_BTN_CLS}
            title="마지막 행 삭제"
            disabled={table.rowCount <= 1}
            onClick={() => onRemoveRow(table.id, table.rowCount - 1)}
          >
            − 마지막 행
          </button>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-6">열</span>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="맨 끝에 열 추가"
            onClick={() => onAddCol(table.id, table.colCount)}
          >
            + 끝 열
          </button>
          <button
            type="button"
            className={STRUCT_DEL_BTN_CLS}
            title="마지막 열 삭제"
            disabled={table.colCount <= 1}
            onClick={() => onRemoveCol(table.id, table.colCount - 1)}
          >
            − 마지막 열
          </button>
        </div>

        {/* 열 너비(colWidths) — 숫자(mm, 저장은 px)/'%'/'auto'. 균등=행 높이 균등과 대칭(%N등분 박제). */}
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[11px] text-gray-500">열 너비 (mm / % / auto)</span>
            <button
              type="button"
              onClick={distributeColsEven}
              title="모든 열을 균등(N등분)으로 — 내용과 무관하게 같은 폭"
              className="text-[11px] text-brand-600 hover:underline"
            >
              균등
            </button>
          </div>
          <div className="space-y-1">
            {widths.map((w, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="text-[11px] text-gray-400 font-mono w-8">열{i}</span>
                <DimInput
                  value={w}
                  onPreview={(raw) => onPreviewColWidths(table.id, buildWidths(i, raw))}
                  onCommit={(raw) => onSetColWidths(table.id, buildWidths(i, raw))}
                  onRevert={onRevert}
                />
              </div>
            ))}
          </div>
        </div>

        {/* 행 높이(rowHeights) — 숫자(mm)/'%'(stretch 시 비율)/'auto'(내용) */}
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[11px] text-gray-500">행 높이 (mm / % / auto)</span>
            <button
              type="button"
              onClick={distributeRowsEven}
              title="모든 행을 균등(N등분)으로 — 늘어난 높이도 내용과 무관하게 양분"
              className="text-[11px] text-brand-600 hover:underline"
            >
              균등
            </button>
          </div>
          <div className="space-y-1">
            {heights.map((h, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="text-[11px] text-gray-400 font-mono w-8">행{i}</span>
                <DimInput
                  value={h}
                  onPreview={(raw) => onPreviewRowHeights(table.id, buildHeights(i, raw))}
                  onCommit={(raw) => onSetRowHeights(table.id, buildHeights(i, raw))}
                  onRevert={onRevert}
                />
              </div>
            ))}
          </div>
        </div>

        {/* 페이지 분할 옵션(표 전체) — 표가 페이지를 넘길 때 동작(편집 캔버스 A4 낱장 기준).
            묶음행(groupByCol) 관련 옵션은 반복영역별 설정이라 아래 반복영역 편집기로 이동했다. */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-1">페이지 분할</span>
          <div className="space-y-1.5">
            {/* tablePolicy — 표가 페이지 경계를 만났을 때 행 단위로 쪼갤지(rows) 통째로 다음 장에
                넘길지(whole). 엔진은 whole일 때 table에 breakInside:avoid, rows일 때 각 tr에
                breakInside:auto를 건다(RenderTemplate).
                필수 필드라 표 생성 시 항상 'rows'가 들어간다(templateOps.createTable) — 빈 값 옵션을
                두지 않는 이유. 옛 템플릿에 값이 없더라도 기본값 rows로 표시해 선택 시 정규화된다. */}
            <label className="flex items-center gap-1.5 text-[11px] text-gray-700">
              <span className="shrink-0">페이지 넘김</span>
              <select
                value={table.tablePolicy ?? 'rows'}
                onChange={(e) =>
                  onSetTableFlag(table.id, {
                    tablePolicy: e.target.value as TableNode['tablePolicy'],
                  })
                }
                title="표가 페이지를 넘길 때 행 단위로 나눌지, 표 전체를 통째로 다음 장으로 넘길지 정합니다."
                className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
              >
                <option value="rows">행 단위로 나눔 (기본)</option>
                <option value="whole">통째로 유지</option>
              </select>
            </label>
            {(() => {
              // 중첩 게이트(pageFlowGate 공용) — 새로 켜는 것만 막고 잔존값 해제는 허용.
              const gate = pageFlowGate(
                isTopLevel,
                !!table.startOnNewPage,
                '표가 남은 공간에 통째로 안 들어가면 새 페이지에서 시작합니다(그 후에도 페이지보다 크면 행 분할).',
              );
              return (
                <label
                  className={`flex items-start gap-1.5 text-[11px] text-gray-700 ${gate.labelClass}`}
                  title={gate.title}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!table.startOnNewPage}
                    disabled={gate.disabled}
                    onChange={(e) => onSetTableFlag(table.id, { startOnNewPage: e.target.checked })}
                  />
                  <span>행 분할 시 새 페이지에서 시작</span>
                </label>
              );
            })()}
            <NestedPageFlowNote isTopLevel={isTopLevel} />
          </div>
        </div>

        {/* 전체 셀 일괄 스타일 — 표의 모든 셀에 폰트/정렬/색/테두리 일괄 적용(셀 선택 불필요). */}
        <div className="pt-2 border-t border-gray-200">
          <BulkCellEditor
            cells={table.cells}
            onPatch={(patch) => onPatchAllCells(table.id, patch)}
            onPreview={(patch) => onPatchAllCellsLive(table.id, patch)}
            onCommit={(patch) => onPatchAllCellsCommit(table.id, patch)}
            onRevert={onRevert}
          />
        </div>
      </dd>
    </div>
  );
}

/**
 * 지정된 over 키가 **데이터 섹션에 편집 카드를 가진 선언**인지 — 「소스 편집 →」 노출 게이트.
 *
 * 카드가 있는 것은 저작자가 만든 두 갈래(동적 `dataSources` / 정적 `dataDefs`)뿐이다. 시스템 조회
 * (catalog·catalogField)는 서버 카탈로그라 이 화면에 고칠 자리가 없고, 삭제된 소스를 가리키는 깨진
 * 참조도 마찬가지다 — 둘 다 버튼을 내려 **죽은 이동**을 만들지 않는다.
 */
function hasEditableDataCard(
  key: string | undefined,
  dataDefs: DataDef[] | undefined,
  dataSources: DataSourceDef[] | undefined,
): boolean {
  if (!key) return false;
  return (
    (dataSources ?? []).some((d) => d.key === key) || (dataDefs ?? []).some((d) => d.key === key)
  );
}

/**
 * 「소스 편집 →」 — 지금 반복이 쓰는 데이터 소스를 **고치는 자리**로 이동하는 링크.
 *
 * 종전에는 소스 이름을 눈으로 읽고 → 선택을 풀고 → 데이터 섹션에서 그 카드를 찾아야 했다. 표시와
 * 편집 자리가 갈린 축이라 왕복이 길다(문서 상태 배지 → 변수/파라미터 섹션 이동과 같은 처방).
 * 반복영역·반복 노드 두 자리가 같은 버튼을 쓰도록 한 곳에 둔다.
 */
function DataSourceEditLink({ onGo }: { onGo: () => void }) {
  return (
    <button
      type="button"
      onClick={onGo}
      className="text-[11px] text-brand-600 hover:underline"
      title="이 데이터 소스의 편집 카드(데이터 섹션)로 이동합니다 — 선택이 해제됩니다."
    >
      소스 편집 →
    </button>
  );
}

/** 반복영역 소스 종류별 그룹 라벨(select optgroup 구분 표시). */
const SOURCE_KIND_LABEL: Record<RepeatSource['kind'], string> = {
  dataDef: '내장 데이터셋',
  catalog: '시스템 조회',
  dataSource: '조립 데이터 소스',
  catalogField: '시스템 조회 하위 목록',
};

/**
 * 반복영역 소스 select의 optgroup 옵션 — 종류(내장/카탈로그/조립 데이터 소스)별로 그룹 구분해 나열한다.
 * (데이터셋 신규 생성은 전역 속성 패널 "＋ 빈 데이터셋"으로 일원화 — 콤보의 "새로 만들기" 진입점은 제거, 2026-07-05.)
 */
function RepeatSourceOptionGroups({ sources }: { sources: RepeatSource[] }) {
  const kinds: RepeatSource['kind'][] = ['dataDef', 'catalog', 'dataSource'];
  return (
    <>
      {kinds.map((k) => {
        const list = sources.filter((s) => s.kind === k);
        if (list.length === 0) return null;
        return (
          <optgroup key={k} label={SOURCE_KIND_LABEL[k]}>
            {list.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </optgroup>
        );
      })}
    </>
  );
}

/**
 * 반복영역(동적 표) 편집기 — 표 노드의 repeatRegions를 편집한다. 표 구조(행/열·치수)와 분리해
 * 속성 패널에서 바인딩 섹션 바로 위에 독립 배치한다. 순회할 데이터 배열만큼 rowStart~rowEnd 행을
 * 복제하며, 단(columns)·채우기·그룹 칼럼·최소 행 등 모델 A 다단 옵션을 함께 다룬다.
 */
/** export: 「소스 편집 →」 노출·호출 계약 spec — DataSourceEditJump.rtl.test.tsx */
export function RepeatRegionEditor({
  table,
  isTopLevel,
  catalog,
  dataDefs,
  dataSources,
  parentRowFieldSources,
  onAddRow,
  onAddRepeatRegion,
  onRemoveRepeatRegion,
  onUpdateRepeatRegion,
  onSetRepeatPatternRows,
  // onCreateManualDataSource — combo "새로 만들기" 제거로 미사용(생성은 전역 속성 패널로 일원화, 2026-07-05.
  // 여기선 destructure 생략).
  onAddDataSourceColumn,
  onRenameDataSourceColumn,
  onDeleteDataSourceColumn,
  onUnmapDataSourceColumn,
  onMoveDataSourceColumn,
  onHighlightSource,
  onGoToDataCard,
  setCellBinding,
  onStartCellPick,
  onCancelCellPick,
  activePickToken,
}: {
  table: TableNode;
  /** 표가 body 최상위인가 — 묶음행 페이지 옵션은 최상위에서만 동작(NESTED_PAGE_FLOW_HINT). */
  isTopLevel: boolean;
  catalog: CatalogEntry[];
  /** 반복영역 소스/칼럼 매핑(collectRepeatSources)에 쓸 템플릿 dataDefs 전체 */
  dataDefs: DataDef[] | undefined;
  /** 소스 콤보에 나열할 조립형 데이터 소스(cardinality='list') 원본 */
  dataSources: DataSourceDef[] | undefined;
  /**
   * 부모 반복(조상 repeat)의 배열 행 필드 후보(`parentRowArrayFieldOptions`) — 표가 부모 반복
   * 안일 때 over가 부모 행 필드(중첩 반복)면 "(알 수 없음)" 대신 이름으로 표시·선택
   * (2026-09-05 attendeeTriples 사례). 판정 재료가 없는 컨텍스트는 미전달 → 종전 표시 유지.
   */
  parentRowFieldSources?: { key: string; label: string }[];
  /** 패턴 행 추가용(맨 끝 행 삽입) — 생성 시 "새 행을 만들어 지정"에서 새 행을 만들고 그 행을 region이 가리키게 한다 */
  onAddRow: (tableId: string, atRow: number) => void;
  onAddRepeatRegion: (tableId: string, region: TableRepeatRegion) => void;
  onRemoveRepeatRegion: (tableId: string, index: number) => void;
  onUpdateRepeatRegion: (
    tableId: string,
    index: number,
    patch: Partial<TableRepeatRegion>,
  ) => void;
  /**
   * 패턴 행 범위 설정 — 부족분 행 생성(표 끝) + 매핑 이동(겹치지 않는 새 범위일 때) + 밴드 갱신을
   * 단일 undo 스텝으로 적용. 종전의 별도 「행 지정」을 흡수했다(2026-08-28).
   */
  onSetRepeatPatternRows: (tableId: string, index: number, plan: PatternRowSetPlan) => void;
  /** "＋ 새 동적 데이터셋(빈)…" — dataSources에 kind:'manual' 선언 추가(+setOver 시 over 지정을 한 undo 스텝). */
  onCreateManualDataSource: (params: {
    key: string;
    setOver?: { tableId: string; regionIndex: number };
  }) => void;
  /** (dataSource over) "＋칼럼 추가": 셀 바인딩 + 선언 fields append + 영역 칼럼범위를 한 undo 스텝으로 */
  onAddDataSourceColumn: (params: {
    tableId: string;
    regionIndex: number;
    row: number;
    col: number;
    dsKey: string;
    field: string;
    label: string;
    lifecycle: BindingLifecycle;
    /** 조회 경로에서 새 칼럼을 만들 때의 값 출처(선언 fields의 from). 미지정=''(직접 입력 칼럼) */
    from?: string;
  }) => void;
  /** (dataSource over) 칼럼 키 rename — 선언 fields + 직접 반복 표 셀 + seed 행 컬렉션을 한 undo 스텝으로 */
  onRenameDataSourceColumn: (params: {
    dsKey: string;
    oldField: string;
    newField: string;
  }) => void;
  /** (dataSource over) 칼럼 삭제: 셀 바인딩 해제 + 선언 fields 제거를 한 undo 스텝으로 */
  onDeleteDataSourceColumn: (params: { tableId: string; dsKey: string; field: string }) => void;
  /** (반복영역) 칼럼↔셀 매핑 해제: 선언은 두고 바인딩만 푼다(삭제와 달리 복구 가능) */
  onUnmapDataSourceColumn: (params: { tableId: string; field: string }) => void;
  /** (dataSource over) 칼럼 순서 이동(선언 fields만 — 표 좌표 무관) */
  onMoveDataSourceColumn: (params: { dsKey: string; field: string; dir: -1 | 1 }) => void;
  /** "셀 지정" 버튼 호버 시 매핑 셀 캔버스 하이라이트(`cell:tableId:row:col`, null=해제) */
  onHighlightSource?: (id: string | null) => void;
  /** 「소스 편집 →」 — 이 영역이 쓰는 선언의 편집 카드로 이동(선택 해제 + 카드 펼침 + 스크롤). */
  onGoToDataCard?: (key: string) => void;
  setCellBinding: (
    tableId: string,
    row: number,
    col: number,
    bindingKey: string,
    lifecycle: BindingLifecycle,
  ) => void;
  onStartCellPick: (
    token: string,
    apply: (ref: CellRef) => void,
    opts?: CellPickOpts,
  ) => void;
  onCancelCellPick: () => void;
  activePickToken: string | null;
}) {
  const showToast = useToastStore((s) => s.showToast);
  // 내장 데이터셋(DataDef) + 카탈로그 배열(cardinality='list')
  // + 조립형 데이터 소스(cardinality='list')를 단일 소스 목록으로 통합.
  // seedOnly 카탈로그(예: finding-ai-refinements)는 직접 반복 소스에서 숨김 — 조립 선언 경유로만 소비.
  // catalogField(단건 entry의 배열 필드)는 repeat 노드 전용 — 표 반복영역은 노드 binding 배선이 없어 제외.
  const sources = collectRepeatSources(dataDefs, catalog, dataSources).filter(
    (s) => !s.seedOnly && s.kind !== 'catalogField',
  );
  const regions = table.repeatRegions ?? [];
  // "＋칼럼 추가" 후보 필드 선택 인라인 UI 열림 상태(regionIndex 식별).
  const [addColSelect, setAddColSelect] = useState<{ regionIndex: number } | null>(null);
  // "＋칼럼 추가" 키 직접 입력 상태 — seed 콤보의 "직접 입력…" 선택 시 인라인 키 타이핑으로 전환
  // (seed에 없는 키(예: improvementPlan) 칼럼용 — 이전엔 자동 키 픽으로 떨어져 라벨과 동작이 어긋났음).
  const [addColDirect, setAddColDirect] = useState<{
    regionIndex: number;
    value: string;
    error?: string;
  } | null>(null);
  // (빈 데이터셋 신규 생성은 전역 속성 패널 "＋ 빈 데이터셋"으로 일원화 — 반복영역 콤보의 "새로 만들기"
  //  인라인 키 입력 흐름은 제거했다. 2026-07-05.)
  // 아코디언 — 한 번에 하나의 반복영역만 펼침(-1=전부 닫힘). 기본 첫 영역.
  const [openRegionIndex, setOpenRegionIndex] = useState(0);
  // 신규 반복영역 생성 중 상태(재구성 흐름): ① 소스 선택 → ② 행 지정 픽 → ③ region 생성.
  // null=비활성(기존 영역 편집만). over=선택한 데이터 소스 키(빈 문자열=미선택).
  const [creating, setCreating] = useState<{ over: string } | null>(null);
  const createToken = `repeat-new-${table.id}`;
  const createPickActive = activePickToken === createToken;
  // 칼럼 키 인라인 rename 상태 — 열린 영역 한 필드만 편집(regionIndex+field로 식별).
  const [colRename, setColRename] = useState<{
    regionIndex: number;
    field: string;
    value: string;
    error?: string;
  } | null>(null);
  // 소스 선택 후 캔버스 행 지정 픽 시작 — 클릭 셀의 행을 패턴 행(rowStart=rowEnd)으로 region 생성.
  const startCreateRowPick = (over: string) => {
    onStartCellPick(
      createToken,
      (ref) => {
        onAddRepeatRegion(table.id, { over, rowStart: ref.row, rowEnd: ref.row });
        setOpenRegionIndex(regions.length); // 새로 추가된 영역(맨 끝)을 펼친다.
        setCreating(null);
      },
      { tableId: table.id, anchorRows: null },
    );
  };
  // ── 「패턴 행 설정」 2단계 캔버스 픽 ────────────────────────────────────────────
  // ① 시작 행의 셀 클릭 → ② 끝 행 클릭(실제 행의 셀 또는 표 아래 **채움 행**).
  // 채움 행에 찍으면 그만큼 실제 행을 만들어 그 자리까지 패턴을 확장한다(행 생성+밴드 갱신이 단일 undo).
  // 단계 전이·행 번호 산술·적용 합성은 repeatPatternRowPick(순수)에 있고, 여기선 픽 arm/토스트만 한다.
  //
  // 2026-08-28 통합: 종전의 별도 버튼 「행 지정」(밴드 이동 + 매핑 동반 이동)을 이 픽이 흡수했다.
  // 새 밴드가 옛 밴드와 한 행도 겹치지 않으면 매핑이 따라오고(=옮긴다), 겹치면 제자리다(=넓히거나 줄인다).
  const patternPickToken = (regionIndex: number, phase: 'start' | 'end') =>
    `repeat-pattern-${table.id}-${regionIndex}-${phase}`;
  // ⚠ 반환 타입 명시 필수 — 거부/진행 시 자기 자신을 다시 arm하는 재귀 참조라, 애노테이션이 없으면
  // 초기화식 안에서 자신을 참조한다는 이유로 암묵 any(TS7023)가 된다.
  const startPatternRowPick = (regionIndex: number, stage: PatternPickStage): void => {
    const handle = (input: PatternPickInput): void => {
      const out = reducePatternPick(stage, input);
      if (out.type === 'reject') {
        // 클릭 해소가 픽을 이미 비웠으므로 같은 단계로 다시 arm한다(사용자에겐 "픽 유지"로 보인다).
        showToast(out.message, 'error');
        startPatternRowPick(regionIndex, stage);
        return;
      }
      if (out.type === 'advance') {
        startPatternRowPick(regionIndex, out.stage);
        return;
      }
      const current = (table.repeatRegions ?? [])[regionIndex];
      const oldBand = current
        ? { rowStart: current.rowStart, rowEnd: current.rowEnd }
        : undefined;
      const plan = planPatternRowSet({
        rowCount: table.rowCount,
        rowStart: out.rowStart,
        targetRow: out.targetRow,
        oldBand,
      });
      if (!plan) {
        showToast('그 범위로는 지정할 수 없습니다.', 'error');
        return;
      }
      // 옮기든(relocate) 제자리든(keep), **살아남지 못하는 옛 매핑**은 조용히 두지 않는다 — 그 셀은
      // 정적 행에 남아 "매핑했는데 값이 안 나온다"로 나중에 발현한다.
      const dropped = oldBand
        ? countMappingsDroppedFromBand(table.cells, oldBand, retainedBandFor(plan, oldBand))
        : 0;
      onSetRepeatPatternRows(table.id, regionIndex, plan);
      if (out.swapped) {
        showToast(
          `끝 행이 시작 행보다 위여서 맞바꿨습니다 — 패턴 행 ${plan.rowStart}~${plan.rowEnd}.`,
          'info',
        );
      }
      if (dropped > 0) {
        showToast(
          `기존 매핑 ${dropped}개가 패턴 밖에 남았습니다 — 새 패턴 행에서 다시 매핑하세요.`,
          'info',
        );
      }
    };
    onStartCellPick(
      patternPickToken(regionIndex, stage.phase),
      (ref) => handle({ kind: 'cell', row: ref.row }),
      {
        tableId: table.id,
        anchorRows: null,
        // 문안은 사용자 확정(2026-08-28) — 1행 패턴을 "같은 행을 다시 클릭"으로 명시해야
        // 「행 지정」을 없앤 뒤에도 1행 패턴 지정법을 헤매지 않는다.
        hint:
          stage.phase === 'start'
            ? '반복패턴 시작행을 클릭하세요.'
            : '반복패턴 끝행을 클릭하세요. 패턴행이 한 행이라면 동일한 행을 클릭하세요. (표 아래 빈 행을 클릭하면 실제 행이 추가됩니다)',
        onFillRow: (ordinal) =>
          handle({
            kind: 'fill',
            virtualRow: virtualRowFromFillOrdinal(table.rowCount, ordinal),
          }),
      },
    );
  };
  // "새 행을 만들어 지정"(생성 중 보조) — 기존 동작: 맨 끝에 새 패턴 행 추가 + 그 행을 region이 가리키게 한다.
  const createWithNewRow = (over: string) => {
    onCancelCellPick();
    const newRow = table.rowCount;
    onAddRow(table.id, table.rowCount);
    onAddRepeatRegion(table.id, { over, rowStart: newRow, rowEnd: newRow });
    setOpenRegionIndex(regions.length);
    setCreating(null);
  };
  // 생성 취소 — 픽 취소 + 생성 패널 닫기(아직 region 미생성이라 잔존물 없음).
  const cancelCreate = () => {
    onCancelCellPick();
    setCreating(null);
  };

  return (
    /* 반복영역(repeatRegion) — 동적 표(데이터 배열만큼 행 복제) */
    <div>
      <span
        className="text-[11px] text-gray-500 block mb-0.5"
        title="순회할 데이터 배열만큼 rowStart~rowEnd 행을 복제합니다"
      >
        반복영역 (동적 표)
      </span>
          {regions.length === 0 && (
            <p className="text-[11px] text-gray-400 mb-1">
              없음 (정적 표). 데이터 배열을 행으로 펼치려면 추가하세요.
            </p>
          )}
          <div className="space-y-2">
            {regions.map((region, i) => {
              const source = sources.find((s) => s.key === region.over);
              // 평탄 필드만 자동 매핑 후보(중첩 객체/배열 필드는 고급 경로로 회피).
              const flatFields = source ? source.fields.filter((f) => !f.nested) : [];
              const lifecycle: BindingLifecycle =
                source?.kind === 'catalog' ? 'live' : 'snapshot';
              // ── 편집형 소스 판별(동적 데이터셋 통합 D3) — 조립/수동 데이터 소스(dataSource)는
              //    셀 바인딩이 정본이라 칼럼 편집(＋추가·rename·삭제·이동)을 지원한다.
              const isDataSource = source?.kind === 'dataSource';
              const isEditableSource = isDataSource;
              // dataSource over 선언 — editMode 배지 표시 + 칼럼 편집 대상.
              const dsDef = isDataSource
                ? (dataSources ?? []).find((d) => d.key === region.over)
                : undefined;
              const dsEditMode: 'editable' | 'readonly' = dsDef?.editMode ?? 'editable';
              // 칼럼 편집 콜백 — 선언(dataSources) 경로.
              const addEditableColumn = (p: {
                row: number;
                col: number;
                field: string;
                label: string;
                from?: string;
              }) => {
                // 방어: 픽 좌표가 이 반복영역 밴드 행 범위 밖이면 거부(밴드 밖 셀에 컬렉션 필드를
                // 바인딩하면 칼럼범위 재계산과 맞물려 표가 붕괴 — 2026-07-05). 순수 연산에도 동일 가드가 있다.
                const bandMin = Math.min(region.rowStart, region.rowEnd);
                const bandMax = Math.max(region.rowStart, region.rowEnd);
                if (p.row < bandMin || p.row > bandMax) {
                  showToast('반복영역(밴드) 행 안의 셀만 칼럼으로 추가할 수 있습니다.', 'error');
                  return;
                }
                onAddDataSourceColumn({
                  tableId: table.id,
                  regionIndex: i,
                  row: p.row,
                  col: p.col,
                  dsKey: region.over,
                  field: p.field,
                  label: p.label,
                  lifecycle,
                  from: p.from,
                });
              };
              const renameEditableColumn = (oldField: string, newField: string) => {
                onRenameDataSourceColumn({ dsKey: region.over, oldField, newField });
              };
              const deleteEditableColumn = (field: string) => {
                onDeleteDataSourceColumn({ tableId: table.id, dsKey: region.over, field });
              };
              const moveEditableColumn = (field: string, dir: -1 | 1) => {
                onMoveDataSourceColumn({ dsKey: region.over, field, dir });
              };
              // ＋칼럼 추가 후보 — 아직 셀 미매핑인 선언 필드(하이브리드: 직접 입력도 가능).
              const addColCandidates: RepeatSourceField[] = isDataSource
                ? flatFields.filter((f) => !table.cells.some((c) => c.binding?.bindingKey === f.key))
                : [];
              // 조립형 소스의 **아직 선언에 안 쓴 조회 경로** — 선언 필드가 아니라 "새로 선언할
              // 후보"다. 이게 없으면 조회 값을 표에 끌어오려면 데이터 소스 패널에서 필드를 먼저
              // 만들고 돌아와야 했고, 삭제한 칼럼도 여기선 되살릴 길이 없었다(2026-08-11 사용자
              // 제보). 후보 목록이 넓어질 뿐 선언 쓰기는 같은 op(addDataSourceColumn)를 탄다 —
              // 편집기를 둘로 만들지 않기 위한 선이다.
              const usedFroms = new Set(
                (dsDef?.fields ?? [])
                  .map((f) => (typeof f.from === 'string' ? f.from : ''))
                  .filter((v) => v !== ''),
              );
              const addColPathCandidates = isDataSource
                ? dataSourceFromPathOptions(dsDef, catalog).filter((o) => !usedFroms.has(o.value))
                : [];
              // 후보/직접입력 키가 이미 매핑돼 disabled/거부되는지 — 셀만 검사(후보가 이미 미매핑).
              const isAddColKeyUsed = (key: string): boolean =>
                table.cells.some((c) => c.binding?.bindingKey === key);
              // 헤더 행(다단 N복제 대상) 파생값 — 'none'=헤더 복제 안 함 / [s,e]=그 범위 / undefined=자동(바로 위 1행).
              const hrNone = region.headerRows === 'none';
              const hrArr = Array.isArray(region.headerRows) ? region.headerRows : null;
              const hrStart = hrArr ? hrArr[0] : undefined;
              const hrEnd = hrArr ? hrArr[1] : undefined;
              // 순번 prefix(반복 항목 앞 1./a. 등) 파생값 — 콜백에서 narrowing 유지용 로컬.
              const ip = region.indexPrefix;
              // 시작/끝 입력 갱신 — 둘 다 채워졌을 때만 [s,e], 아니면 undefined(자동)로 정규화.
              const setHeaderRow = (which: 'start' | 'end', raw: string) => {
                const num = raw === '' ? undefined : Number(raw);
                const s = which === 'start' ? num : hrStart;
                const e = which === 'end' ? num : hrEnd;
                onUpdateRepeatRegion(table.id, i, {
                  headerRows: s !== undefined && e !== undefined ? [s, e] : undefined,
                });
              };
              // 부모 행 필드(중첩 반복) 매칭 — 최상위 소스에 없는 over의 정상 갈래.
              const parentMatch = parentRowFieldSources?.find((p) => p.key === region.over);
              // 아코디언 헤더 라벨 = 데이터 소스명(없으면 미선택/폴백).
              const isOpen = openRegionIndex === i;
              const headerLabel = source
                ? source.label
                : (parentMatch?.label ??
                  (region.over ? `반복영역 ${i + 1}` : '(소스 미선택)'));
              return (
              <div key={i} className="border border-gray-200 rounded">
                {/* 아코디언 헤더(항상 표시) — 열림/닫힘 토글 + 소스명 + 삭제 */}
                <div className="flex items-center gap-1 p-1.5">
                  <button
                    type="button"
                    className="flex-1 flex items-center gap-1 text-left min-w-0"
                    title={isOpen ? '접기' : '펼치기'}
                    onClick={() => setOpenRegionIndex(isOpen ? -1 : i)}
                  >
                    <span className="text-[11px] text-gray-400 shrink-0">
                      {isOpen ? '▾' : '▸'}
                    </span>
                    <span className="text-xs text-gray-700 truncate">{headerLabel}</span>
                  </button>
                  <button
                    type="button"
                    className={STRUCT_DEL_BTN_CLS}
                    title="반복영역 삭제"
                    onClick={() => onRemoveRepeatRegion(table.id, i)}
                  >
                    삭제
                  </button>
                </div>

                {/* 아코디언 본문(열린 영역만 렌더) */}
                {isOpen && (
                <div className="px-1.5 pb-1.5 space-y-1.5">
                {/* 소스 선택(콤보) — 종류별 그룹. 새 데이터셋 생성은 전역 속성 패널 "＋ 빈 데이터셋"에서. */}
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-gray-400 shrink-0 whitespace-nowrap">데이터 소스</span>
                  <select
                    className={INPUT_CLS}
                    value={region.over}
                    title="데이터셋 추가는 전역 속성 패널의 '＋ 빈 데이터셋'에서 하세요."
                    onChange={(e) => onUpdateRepeatRegion(table.id, i, { over: e.target.value })}
                  >
                    <option value="">(소스 선택)</option>
                    {/* 부모 행 필드(중첩 반복) — 표가 부모 반복 안일 때만 후보가 있다. */}
                    {parentRowFieldSources && parentRowFieldSources.length > 0 && (
                      <optgroup label="부모 행 필드(중첩 반복)">
                        {parentRowFieldSources.map((p) => (
                          <option key={p.key} value={p.key}>
                            {p.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <RepeatSourceOptionGroups sources={sources} />
                    {region.over && !source && !parentMatch && (
                      <option value={region.over}>{region.over} (알 수 없음)</option>
                    )}
                  </select>
                </div>
                {parentMatch && (
                  <p className="text-[11px] text-gray-400 leading-snug">
                    부모 반복의 행마다 그 행의 「{parentMatch.label}」 목록을 반복합니다.
                  </p>
                )}
                {/* 선택된 소스를 고치는 자리로 바로 이동 — 저작자가 카드를 눈으로 찾지 않게. */}
                {onGoToDataCard && hasEditableDataCard(region.over, dataDefs, dataSources) && (
                  <div>
                    <DataSourceEditLink onGo={() => onGoToDataCard(region.over)} />
                  </div>
                )}

                {/* 조립/수동 데이터 소스(dataSource) 요약 — editMode 배지(편집/읽기 전용) + 필드 수.
                    편집 정책·위젯 명세는 속성 패널 "조립 데이터 소스"에서 관리(발견성 안내). */}
                {isDataSource && (
                  <div className="text-[11px] text-gray-400 flex flex-wrap items-center gap-x-1 gap-y-0.5">
                    <span>
                      {dsDef?.kind === 'manual' ? '수동 데이터셋' : '조립 데이터 소스'} · 칼럼{' '}
                      {flatFields.length}개
                    </span>
                    <span
                      className={
                        dsEditMode === 'readonly'
                          ? 'text-gray-500 bg-gray-100 border border-gray-200 rounded px-1'
                          : 'text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1'
                      }
                      title={
                        dsEditMode === 'readonly'
                          ? '작성폼에 편집 위젯을 노출하지 않습니다(resolve 산출 렌더만).'
                          : '선언이 곧 작성폼 행 데이터셋 — 작성폼이 편집 위젯을 노출합니다.'
                      }
                    >
                      {dsEditMode === 'readonly' ? '읽기 전용' : '편집 가능'}
                    </span>
                    <span>
                      · 편집 정책·위젯은{' '}
                      <span className="text-gray-500 font-medium">조립 데이터 소스</span>에서 관리
                    </span>
                  </div>
                )}

                {/* 칼럼 → 셀 매핑(소스 필드별 "셀 지정" 픽 → bindingKey 설정 + 영역 좌표 자동 추론).
                    조립/수동 데이터 소스(kind=dataSource)는 셀 바인딩이 정본이라
                    "＋칼럼 추가"·키 rename·칼럼 삭제·순서 이동까지 지원(동적 데이터셋 통합 D3). */}
                {source ? (
                  flatFields.length > 0 || isEditableSource ? (
                    <div className="space-y-0.5">
                      <span className="text-[11px] text-gray-500 block">칼럼 → 셀 지정</span>
                      {isEditableSource && flatFields.length === 0 && (
                        <p className="text-[11px] text-amber-600">
                          아직 칼럼이 없습니다. 아래 "＋칼럼 추가"로 캔버스 셀을 지정하세요.
                        </p>
                      )}
                      {/* 삭제는 선언까지 지운다 — 되살리는 곳을 화면에 적어 둔다(2026-08-11 사용자
                          제보: '＋칼럼 추가'에 삭제한 칼럼이 안 뜬다). 후보는 선언 필드라 지워진
                          칼럼은 없지만, 조립형은 '조회 경로에서 새 칼럼'으로 같은 자리에서 다시
                          만들 수 있다. 값 출처의 세부 조정은 여전히 데이터 소스 패널 몫이다. */}
                      {dsDef?.kind === 'assembled' && (
                        <p className="text-[11px] text-gray-400 leading-relaxed">
                          삭제하면 칼럼 선언(값 출처)까지 사라집니다. 되살리려면 실행 취소하거나
                          "＋칼럼 추가 &gt; 조회 경로에서 새 칼럼"으로 다시 만드세요.
                        </p>
                      )}
                      {flatFields.map((f, fi) => {
                        const mapped = table.cells.find(
                          (c) => c.binding?.bindingKey === f.key,
                        );
                        const token = `repeat-cell-${table.id}-${i}-${f.key}`;
                        const active = activePickToken === token;
                        const renaming =
                          isEditableSource && colRename?.regionIndex === i && colRename.field === f.key;
                        // 매핑 허용 밴드 = 이 영역의 행 범위(rowStart~rowEnd). 픽 중 그 밴드만
                        // 캔버스에서 강조되고 밖 클릭은 거부된다. (종전 "첫 매핑 셀의 행 하나"
                        // 제약은 다행 반복에서 아래 행 지정을 막는 결함이었다 — 2026-08-28.)
                        const anchorRows = {
                          start: Math.min(region.rowStart, region.rowEnd),
                          end: Math.max(region.rowStart, region.rowEnd),
                        };
                        // 칼럼 키 rename 검증 — flat 키 규칙은 flatKeyError 단일 출처 + 중복(자기 자신
                        // 제외: 선언 타 필드 + 표 셀 바인딩).
                        const validateColKey = (raw: string): string | null => {
                          const trimmed = raw.trim();
                          const flatErr = flatKeyError(trimmed);
                          if (flatErr) return flatErr;
                          if (trimmed === f.key) return null;
                          const used = new Set<string>();
                          for (const rf of source.fields)
                            if (rf.key !== f.key) used.add(rf.key);
                          for (const c of table.cells)
                            if (c.binding && c.binding.bindingKey !== f.key)
                              used.add(c.binding.bindingKey);
                          return used.has(trimmed) ? '이미 쓰이는 키입니다.' : null;
                        };
                        const commitRename = () => {
                          if (!colRename) return;
                          const err = validateColKey(colRename.value);
                          if (err) {
                            setColRename({
                              regionIndex: i,
                              field: f.key,
                              value: colRename.value,
                              error: err,
                            });
                            return;
                          }
                          const newField = colRename.value.trim();
                          if (newField !== f.key) {
                            renameEditableColumn(f.key, newField);
                          }
                          setColRename(null);
                        };
                        return (
                          <div key={f.key}>
                            <div className="flex items-center gap-1">
                              {renaming ? (
                                <input
                                  type="text"
                                  autoFocus
                                  className={`${INPUT_CLS} flex-1 min-w-0`}
                                  value={colRename!.value}
                                  onChange={(e) =>
                                    setColRename({
                                      regionIndex: i,
                                      field: f.key,
                                      value: e.target.value,
                                    })
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      commitRename();
                                    } else if (e.key === 'Escape') {
                                      setColRename(null);
                                    }
                                  }}
                                />
                              ) : (
                                <span
                                  className="text-[11px] text-gray-600 flex-1 truncate"
                                  title={f.key}
                                >
                                  {f.label}
                                  {isEditableSource && f.label !== f.key && (
                                    <span className="text-gray-400 font-mono"> ({f.key})</span>
                                  )}
                                </span>
                              )}
                              <span className="text-[10px] text-gray-400 font-mono shrink-0">
                                {mapped ? `R${mapped.row}C${mapped.col}` : '미지정'}
                              </span>
                              {renaming ? (
                                <>
                                  <button
                                    type="button"
                                    className={STRUCT_BTN_CLS}
                                    title="키 변경 확정"
                                    onClick={commitRename}
                                  >
                                    확정
                                  </button>
                                  <button
                                    type="button"
                                    className={STRUCT_BTN_CLS}
                                    title="취소"
                                    onClick={() => setColRename(null)}
                                  >
                                    취소
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className={
                                      active
                                        ? 'px-1.5 py-1 text-[11px] rounded border border-brand-400 bg-brand-50 text-brand-700'
                                        : STRUCT_BTN_CLS
                                    }
                                    title="캔버스에서 이 칼럼을 넣을 셀을 클릭하세요"
                                    // 호버 시 매핑된 셀을 캔버스에 하이라이트(미지정이면 없음) — 어느 셀인지 즉시 확인.
                                    onMouseEnter={() => {
                                      if (mapped)
                                        onHighlightSource?.(
                                          `cell:${table.id}:${mapped.row}:${mapped.col}`,
                                        );
                                    }}
                                    onMouseLeave={() => onHighlightSource?.(null)}
                                    onClick={() => {
                                      if (active) {
                                        onCancelCellPick();
                                        return;
                                      }
                                      onStartCellPick(token, (ref) => {
                                        // 밴드 행 범위 밖 클릭 거부(＋칼럼과 동일 가드 — 표 붕괴 방지, 2026-07-05).
                                        const bandMin = Math.min(region.rowStart, region.rowEnd);
                                        const bandMax = Math.max(region.rowStart, region.rowEnd);
                                        if (ref.row < bandMin || ref.row > bandMax) {
                                          showToast('반복영역(밴드) 행 안의 셀만 지정할 수 있습니다.', 'error');
                                          return;
                                        }
                                        // 1) 고른 셀에 필드 key를 bindingKey로 설정(정적 text 제거).
                                        setCellBinding(table.id, ref.row, ref.col, f.key, lifecycle);
                                        // 2) 칼럼 범위는 **확장만**(축소 금지) — 구 "min/max 리셋 + 클릭 행으로
                                        //    rowStart/End 재설정"은 첫 지정 시 밴드를 1칼럼×1행으로 축소시켜
                                        //    표 붕괴 + undo 2스텝을 유발했다(2026-07-05 실사고). 행 범위는
                                        //    생성 시 행 지정이 정본이라 불변, 명시 칼럼 범위 없으면(전폭) 그대로.
                                        if (region.colStart !== undefined && region.colEnd !== undefined) {
                                          const colStart = Math.min(region.colStart, ref.col);
                                          const colEnd = Math.max(region.colEnd, ref.col);
                                          if (colStart !== region.colStart || colEnd !== region.colEnd) {
                                            onUpdateRepeatRegion(table.id, i, { colStart, colEnd });
                                          }
                                        }
                                      }, { tableId: table.id, anchorRows });
                                    }}
                                  >
                                    {active ? '클릭 대기…' : mapped ? '셀 변경' : '셀 지정'}
                                  </button>
                                  {/* 해제 — 선언은 두고 매핑만 푼다. 삭제로 대신하면 조립형 소스의
                                      `from`(조회 연결)까지 사라지고 UI로 복구할 수 없다
                                      (unmapDataSourceColumn doc). 해제한 칼럼은 '미지정'으로 남아
                                      곧바로 다시 지정할 수 있다. */}
                                  {mapped && (
                                    <button
                                      type="button"
                                      className={STRUCT_BTN_CLS}
                                      title="이 칼럼의 셀 매핑만 해제(칼럼은 유지 — 다시 지정 가능)"
                                      onMouseEnter={() =>
                                        onHighlightSource?.(
                                          `cell:${table.id}:${mapped.row}:${mapped.col}`,
                                        )
                                      }
                                      onMouseLeave={() => onHighlightSource?.(null)}
                                      onClick={() =>
                                        onUnmapDataSourceColumn({ tableId: table.id, field: f.key })
                                      }
                                    >
                                      해제
                                    </button>
                                  )}
                                  {isEditableSource && (
                                    <>
                                      {/* 순서 이동 — 기본은 추가 순서, 필요 시 사용자가 조정(작성폼 입력 순서에 반영). */}
                                      <button
                                        type="button"
                                        className={`${STRUCT_BTN_CLS} disabled:text-gray-300 disabled:cursor-default`}
                                        title="위로 (칼럼 목록·작성폼 입력 순서)"
                                        disabled={fi === 0}
                                        onClick={() => moveEditableColumn(f.key, -1)}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        className={`${STRUCT_BTN_CLS} disabled:text-gray-300 disabled:cursor-default`}
                                        title="아래로 (칼럼 목록·작성폼 입력 순서)"
                                        disabled={fi === flatFields.length - 1}
                                        onClick={() => moveEditableColumn(f.key, 1)}
                                      >
                                        ↓
                                      </button>
                                    </>
                                  )}
                                  {isEditableSource && (
                                    <button
                                      type="button"
                                      className={STRUCT_BTN_CLS}
                                      title="이 칼럼 키 이름 변경"
                                      onClick={() =>
                                        setColRename({
                                          regionIndex: i,
                                          field: f.key,
                                          value: f.key,
                                        })
                                      }
                                    >
                                      ✎
                                    </button>
                                  )}
                                  {isEditableSource && (
                                    <button
                                      type="button"
                                      className={STRUCT_DEL_BTN_CLS}
                                      // 아이콘 버튼 — 한 행에 지정/해제/이동/이름/삭제가 늘어서
                                      // 폭이 모자란다. 뜻은 title이 낸다(선언 필드 제거라 '해제'와
                                      // 다르다는 점을 문구에 남길 것).
                                      aria-label="이 칼럼 삭제"
                                      title="이 칼럼 삭제(셀 매핑 해제 + 선언 필드 제거)"
                                      onClick={() => deleteEditableColumn(f.key)}
                                    >
                                      ×
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            {renaming && colRename.error && (
                              <p className="text-[11px] text-red-600 mt-0.5">{colRename.error}</p>
                            )}
                          </div>
                        );
                      })}
                      {/* ＋칼럼 추가 — 선언 필드 선택(미매핑) 또는 직접 입력 후 셀 픽(선언 fields가 정본). */}
                      {isEditableSource && (() => {
                        const addColToken = `repeat-addcol-${table.id}-${i}`;
                        const addColActive = activePickToken === addColToken;
                        const isSelecting = addColSelect?.regionIndex === i;
                        const isDirectInput = addColDirect?.regionIndex === i;
                        // 매핑 허용 밴드 = 이 영역의 행 범위(다행 반복 지원 — 위 anchorRows와 동일 규칙).
                        const addAnchorRows = {
                          start: Math.min(region.rowStart, region.rowEnd),
                          end: Math.max(region.rowStart, region.rowEnd),
                        };
                        // 후보 필드 픽 — field=선택 키, label=후보 라벨(헤더 유추보다 우선). 미매핑 선언 필드.
                        const startSeedAddColPick = (field: string, label: string) => {
                          onStartCellPick(addColToken, (ref) => {
                            addEditableColumn({ row: ref.row, col: ref.col, field, label });
                          }, { tableId: table.id, anchorRows: addAnchorRows });
                        };
                        // 조회 경로 픽 — 선언에 **없던** 필드를 경로와 함께 새로 만든다.
                        // 키는 경로의 마지막 마디(flat 키 규칙 위반이면 자동 키), 충돌 시 숫자 접미.
                        // 라벨은 소스 접두사('주 · ')를 뗀 부분 — 칼럼 라벨에 출처 표기는 군더더기다.
                        const startPathAddColPick = (opt: { value: string; label: string }) => {
                          const used = new Set<string>();
                          for (const rf of source.fields) used.add(rf.key);
                          for (const c of table.cells) if (c.binding) used.add(c.binding.bindingKey);
                          const leaf = opt.value.split('.').pop() ?? '';
                          let key = leaf;
                          if (leaf === '' || flatKeyError(leaf) !== null) {
                            key = nextAutoFieldKey([...used]);
                          } else {
                            let n = 2;
                            while (used.has(key)) key = `${leaf}${n++}`;
                          }
                          const label = opt.label.split(' · ').pop() ?? key;
                          onStartCellPick(addColToken, (ref) => {
                            addEditableColumn({
                              row: ref.row,
                              col: ref.col,
                              field: key,
                              label,
                              from: opt.value,
                            });
                          }, { tableId: table.id, anchorRows: addAnchorRows });
                        };
                        // 직접 입력 키 픽 — field=타이핑한 키, label=밴드 헤더 유추(자동 픽과 동일 규칙).
                        const startDirectAddColPick = (field: string) => {
                          onStartCellPick(addColToken, (ref) => {
                            const bandStart = Math.min(region.rowStart, region.rowEnd);
                            const label = deriveHeaderLabel(table, ref.col, bandStart) ?? field;
                            addEditableColumn({ row: ref.row, col: ref.col, field, label });
                          }, { tableId: table.id, anchorRows: addAnchorRows });
                        };
                        // 키 직접 입력 인라인 UI — 선언에 없는 키(예: improvementPlan)를 타이핑 후 셀 픽.
                        if (isDirectInput) {
                          const usedKeys = new Set<string>();
                          for (const rf of source.fields) usedKeys.add(rf.key);
                          for (const c of table.cells)
                            if (c.binding) usedKeys.add(c.binding.bindingKey);
                          const confirmDirect = () => {
                            const trimmed = (addColDirect?.value ?? '').trim();
                            const err =
                              flatKeyError(trimmed) ??
                              (usedKeys.has(trimmed) ? '이미 쓰는 키입니다.' : null);
                            if (err) {
                              setAddColDirect((s) => (s ? { ...s, error: err } : s));
                              return;
                            }
                            setAddColDirect(null);
                            startDirectAddColPick(trimmed);
                          };
                          return (
                            <div className="border border-brand-200 rounded p-1.5 space-y-1 bg-brand-50/40 mt-0.5">
                              <span className="text-[11px] text-gray-600 block">
                                새 칼럼 키 직접 입력
                              </span>
                              <input
                                autoFocus
                                type="text"
                                className={INPUT_CLS}
                                value={addColDirect?.value ?? ''}
                                placeholder="예: improvementPlan (점 없이)"
                                onChange={(e) =>
                                  setAddColDirect((s) =>
                                    s ? { ...s, value: e.target.value, error: undefined } : s,
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    confirmDirect();
                                  }
                                  if (e.key === 'Escape') setAddColDirect(null);
                                }}
                              />
                              {addColDirect?.error && (
                                <p className="text-[11px] text-red-600">{addColDirect.error}</p>
                              )}
                              <div className="flex gap-1">
                                <button type="button" className={STRUCT_BTN_CLS} onClick={confirmDirect}>
                                  확정 후 셀 픽
                                </button>
                                <button
                                  type="button"
                                  className={STRUCT_BTN_CLS}
                                  onClick={() => setAddColDirect(null)}
                                >
                                  취소
                                </button>
                              </div>
                            </div>
                          );
                        }
                        // 후보 선택 인라인 UI — 미매핑 선언 필드.
                        // 이미 매핑/사용 중인 키는 disabled(isAddColKeyUsed). "직접 입력…"으로 새 키 타이핑 전환.
                        if (isSelecting) {
                          return (
                            <div className="border border-brand-200 rounded p-1.5 space-y-1 bg-brand-50/40 mt-0.5">
                              <span className="text-[11px] text-gray-600 block">
                                선언 필드에서 칼럼 선택(또는 직접 입력)
                              </span>
                              <select
                                autoFocus
                                className={INPUT_CLS}
                                value=""
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') return;
                                  setAddColSelect(null);
                                  if (v === DIRECT_INPUT_SENTINEL) {
                                    // 키 타이핑 UI로 전환(이전: 자동 키 픽 — 라벨과 동작 불일치).
                                    setAddColDirect({ regionIndex: i, value: '' });
                                    return;
                                  }
                                  if (v.startsWith(ADD_COL_PATH_PREFIX)) {
                                    const from = v.slice(ADD_COL_PATH_PREFIX.length);
                                    const opt = addColPathCandidates.find((o) => o.value === from);
                                    if (opt) startPathAddColPick(opt);
                                    return;
                                  }
                                  const fld = addColCandidates.find((sf) => sf.key === v);
                                  if (fld) startSeedAddColPick(fld.key, fld.label);
                                }}
                              >
                                <option value="">(칼럼 선택)</option>
                                {addColCandidates.map((sf) => {
                                  const used = isAddColKeyUsed(sf.key);
                                  return (
                                    <option key={sf.key} value={sf.key} disabled={used}>
                                      {sf.label}
                                      {used ? ' (사용 중)' : ''}
                                    </option>
                                  );
                                })}
                                {/* 선언에 아직 없는 조회 경로 — 고르면 칼럼 선언(값 출처 포함)까지
                                    한 번에 만든다. 삭제한 조회 칼럼도 여기서 되살린다. */}
                                {addColPathCandidates.length > 0 && (
                                  <optgroup label="조회 경로에서 새 칼럼">
                                    {addColPathCandidates.map((o) => (
                                      <option
                                        key={o.value}
                                        value={`${ADD_COL_PATH_PREFIX}${o.value}`}
                                      >
                                        {o.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                <option value={DIRECT_INPUT_SENTINEL}>직접 입력…</option>
                              </select>
                              <button
                                type="button"
                                className={STRUCT_BTN_CLS}
                                onClick={() => setAddColSelect(null)}
                              >
                                취소
                              </button>
                            </div>
                          );
                        }
                        return (
                          <button
                            type="button"
                            className={
                              addColActive
                                ? 'w-full px-1.5 py-1 mt-0.5 text-[11px] rounded border border-brand-400 bg-brand-50 text-brand-700'
                                : `${STRUCT_BTN_CLS} w-full mt-0.5`
                            }
                            title="필드를 고른 뒤 캔버스 셀을 클릭하세요(직접 입력도 가능)"
                            onClick={() => {
                              if (addColActive) {
                                onCancelCellPick();
                                return;
                              }
                              setAddColSelect({ regionIndex: i });
                            }}
                          >
                            {addColActive ? '클릭 대기…' : '＋칼럼 추가'}
                          </button>
                        );
                      })()}
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-400">
                      이 소스에 평탄 칼럼이 없습니다(중첩 필드는 고급 경로).
                    </p>
                  )
                ) : (
                  <p className="text-[11px] text-gray-400">
                    소스를 선택하면 칼럼 목록이 표시됩니다.
                  </p>
                )}

                {/* 세부 설정 — 자동 추론 좌표/다단 옵션을 직접 덮어쓴다(아코디언이 접기 역할). */}
                <div className="mt-0.5 border-t border-gray-100 pt-1.5">
                  <span className="text-[11px] text-gray-500 block mb-1 select-none">
                    세부 설정
                  </span>
                  <div className="space-y-1.5">
                {/*
                  패턴 행 범위 — 숫자 입력이 아니라 **캔버스에서 직접 두 행을 찍어** 정한다.
                  숫자 입력은 표 아래 채움 행(빈 괘선 가상 행)을 실제 행으로 오인해 없는 행 번호를
                  넣는 혼선을 낳았다(2026-08-28 실측). 여기 표시는 읽기 전용이고, 지정은 「패턴 행 설정」
                  버튼 하나다 — 종전의 별도 「행 지정」(매핑 이동)은 이 픽이 흡수했다(2026-08-28 통합):
                   - 새 범위가 지금 범위와 **겹치면** 넓히거나 줄이는 편집 → 매핑은 제자리.
                   - **겹치지 않으면** 패턴을 옮기는 편집 → 매핑이 같은 상대 행으로 따라온다.
                   - 끝을 채움 행에 찍으면 그만큼 실제 행을 만들어 확장한다.
                */}
                <div className="space-y-1">
                  <div
                    className="text-[11px] text-gray-500"
                    title="행 번호는 0부터. 패턴 행 = 데이터 항목마다 반복되는 행 범위입니다."
                  >
                    패턴 행{' '}
                    <span className="font-medium text-gray-700">
                      {region.rowStart}~{region.rowEnd}
                    </span>
                    {' · 표 행 0~'}
                    {Math.max(0, table.rowCount - 1)}
                  </div>
                  <div className="flex items-center gap-1">
                    {(() => {
                      const patternActive =
                        activePickToken === patternPickToken(i, 'start') ||
                        activePickToken === patternPickToken(i, 'end');
                      return (
                        <button
                          type="button"
                          className={
                            patternActive
                              ? 'px-1.5 py-1 text-[11px] rounded border border-brand-400 bg-brand-50 text-brand-700 shrink-0'
                              : `${STRUCT_BTN_CLS} shrink-0`
                          }
                          title="캔버스에서 패턴 행 범위를 지정합니다 — 시작 행의 셀을 클릭한 뒤 끝 행을 클릭(한 행 패턴이면 같은 행을 다시 클릭). 끝 행을 표 아래 빈 행에 찍으면 그만큼 실제 행이 추가되고, 지금 범위와 겹치지 않는 곳을 찍으면 기존 매핑이 함께 옮겨집니다."
                          onClick={() => {
                            if (patternActive) {
                              onCancelCellPick();
                              return;
                            }
                            startPatternRowPick(i, { phase: 'start' });
                          }}
                        >
                          {patternActive ? '클릭 대기…' : '패턴 행 설정'}
                        </button>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const groupCols = effectiveGroupByCols(region);
                  const available = Array.from({ length: table.colCount }, (_, n) => n).filter(
                    (n) => !groupCols.includes(n),
                  );
                  // 신형(groupByCols)으로 기록 + 구형 단수 키 제거(이중 저장 금지).
                  const writeGroupCols = (next: number[]) =>
                    onUpdateRepeatRegion(table.id, i, {
                      groupByCols: next.length > 0 ? next : undefined,
                      groupByCol: undefined,
                    });
                  return (
                    <div>
                      <span
                        className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                        title="연속 동일 값을 rowspan으로 묶을 칼럼(0-based). 여러 개면 순서=계층(위=대분류): 하위 칼럼 병합은 상위 그룹 경계에서 끊깁니다. 없으면 미사용."
                      >
                        그룹 칼럼 (계층)
                      </span>
                      {groupCols.length > 0 ? (
                        <div className="space-y-1 mb-1">
                          {groupCols.map((gc, gi) => (
                            <div key={gc} className="flex items-center gap-1">
                              <span className="text-[11px] text-gray-600 flex-1 min-w-0">
                                {gi + 1}. 칼럼 {gc}
                              </span>
                              <button
                                type="button"
                                className={`${STRUCT_BTN_CLS} shrink-0 disabled:text-gray-300 disabled:cursor-default`}
                                disabled={gi === 0}
                                title="위로(상위 우선순위)"
                                onClick={() => {
                                  const next = [...groupCols];
                                  const tmp = next[gi - 1];
                                  next[gi - 1] = next[gi];
                                  next[gi] = tmp;
                                  writeGroupCols(next);
                                }}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className={`${STRUCT_BTN_CLS} shrink-0 disabled:text-gray-300 disabled:cursor-default`}
                                disabled={gi === groupCols.length - 1}
                                title="아래로(하위 우선순위)"
                                onClick={() => {
                                  const next = [...groupCols];
                                  const tmp = next[gi + 1];
                                  next[gi + 1] = next[gi];
                                  next[gi] = tmp;
                                  writeGroupCols(next);
                                }}
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                className={`${STRUCT_DEL_BTN_CLS} shrink-0`}
                                title="제거"
                                onClick={() => writeGroupCols(groupCols.filter((x) => x !== gc))}
                              >
                                제거
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[11px] text-gray-400 block mb-1">미사용</span>
                      )}
                      {available.length > 0 && (
                        <select
                          className={INPUT_CLS}
                          value=""
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '') return;
                            writeGroupCols([...groupCols, Number(v)]);
                          }}
                        >
                          <option value="">+ 칼럼 추가…</option>
                          {available.map((n) => (
                            <option key={n} value={n}>
                              칼럼 {n}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })()}
                <div className="grid grid-cols-2 gap-1.5">
                  <div>
                    <span
                      className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                      title="이 영역이 차지할 시작 칼럼(0-based). 같은 행밴드에 칼럼범위가 다른 영역들을 가로로 나란히 배치(좌/우 2단). 빈값=0(전폭 시작)"
                    >
                      시작 칼럼
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={Math.max(0, table.colCount - 1)}
                      className={INPUT_CLS}
                      placeholder="0"
                      value={region.colStart ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        onUpdateRepeatRegion(table.id, i, {
                          colStart: v === '' ? undefined : Number(v),
                        });
                      }}
                    />
                  </div>
                  <div>
                    <span
                      className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                      title="이 영역의 끝 칼럼(0-based 포함). 빈값=마지막 칼럼(전폭 끝)"
                    >
                      끝 칼럼
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={Math.max(0, table.colCount - 1)}
                      className={INPUT_CLS}
                      placeholder="끝"
                      value={region.colEnd ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        onUpdateRepeatRegion(table.id, i, {
                          colEnd: v === '' ? undefined : Number(v),
                        });
                      }}
                    />
                  </div>
                </div>
                <div>
                  <span
                    className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                    title="이 영역(밴드)의 최소 전개 행 수. 데이터가 적으면 빈셀로 패딩(빈 표 골격). 빈값=데이터 길이"
                  >
                    최소 행
                  </span>
                  <input
                    type="number"
                    min={0}
                    className={INPUT_CLS}
                    placeholder="미사용"
                    value={region.minRows ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      onUpdateRepeatRegion(table.id, i, {
                        minRows: v === '' ? undefined : Number(v),
                      });
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div>
                    <span
                      className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                      title="모델 A: 시작/끝 칼럼으로 잡은 1단 패턴을 가로로 몇 단 복제하나(기본 1). 1단만 바인딩하면 엔진이 우측 단을 복제 + 데이터 분배. 헤더 등 영역 밖은 그대로."
                    >
                      단 수
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={Math.max(1, table.colCount)}
                      className={INPUT_CLS}
                      placeholder="1"
                      value={region.columns ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        const n = Number(v);
                        onUpdateRepeatRegion(table.id, i, {
                          // 1 이하/빈값은 단일 단(undefined 정규화).
                          columns: v === '' || n <= 1 ? undefined : n,
                        });
                      }}
                    />
                  </div>
                  {(region.columns ?? 1) >= 2 && (
                    <div>
                      <span
                        className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                        title="세로 채우기: 한 단을 세로로 다 채우고 다음 단 / 가로 채우기: 행마다 좌→우 번갈아"
                      >
                        채우기
                      </span>
                      <select
                        className={INPUT_CLS}
                        value={region.flow ?? 'column'}
                        onChange={(e) =>
                          onUpdateRepeatRegion(table.id, i, {
                            // column(기본)은 undefined 정규화.
                            flow: e.target.value === 'row' ? 'row' : undefined,
                          })
                        }
                      >
                        <option value="column">세로 채우기</option>
                        <option value="row">가로 채우기</option>
                      </select>
                    </div>
                  )}
                </div>
                {/* 묶음행(그룹 구분 칼럼) 페이지 처리 — 이 반복영역이 그룹 구분 칼럼을 쓸 때만 노출.
                    keep-together는 첫(주) 칼럼 그룹 기준 + 세로 채우기 다단에서 어긋나 미지원(disabled). */}
                {effectiveGroupByCols(region).length > 0 &&
                  (() => {
                    // 중첩 게이트(pageFlowGate 공용). groupStart는 "세로 채우기 다단 미지원" 축이
                    // 하나 더 있어 그 판정·문구만 게이트 위에 얹는다(중첩 축은 공용 규칙 그대로).
                    const vertical = isVerticalMultiColumnRegion(region);
                    const startGate = pageFlowGate(
                      isTopLevel,
                      !!region.groupStartOnNewPage,
                      'ON: 이 영역의 묶음 그룹을 쪼개지 않고 통째로 다음 페이지로 넘깁니다. OFF: 경계에서 쪼갭니다(그룹이 페이지보다 크면 항상 쪼갬).',
                    );
                    const markGate = pageFlowGate(
                      isTopLevel,
                      !!region.groupContinuationMark,
                      "묶음 그룹이 페이지 경계에서 쪼개졌을 때, 이어진 페이지의 그룹 라벨에 ' (계속)'을 붙입니다.",
                    );
                    return (
                      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
                        <label
                          className={`flex items-start gap-1.5 text-[11px] text-gray-700 ${
                            vertical ? 'cursor-not-allowed opacity-50' : startGate.labelClass
                          }`}
                          title={
                            vertical
                              ? '세로 채우기 다단에서는 지원되지 않습니다(각 단의 묶음 그룹이 물리 행에서 어긋남). 가로 채우기(flow=row)나 단일 단에서만 사용하세요.'
                              : startGate.title
                          }
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={!!region.groupStartOnNewPage}
                            disabled={vertical || startGate.disabled}
                            onChange={(e) =>
                              onUpdateRepeatRegion(table.id, i, {
                                groupStartOnNewPage: e.target.checked || undefined,
                              })
                            }
                          />
                          <span>묶음행 분할 시 새 페이지에서 시작</span>
                        </label>
                        <label
                          className={`flex items-start gap-1.5 text-[11px] text-gray-700 ${markGate.labelClass}`}
                          title={markGate.title}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={!!region.groupContinuationMark}
                            disabled={markGate.disabled}
                            onChange={(e) =>
                              onUpdateRepeatRegion(table.id, i, {
                                groupContinuationMark: e.target.checked || undefined,
                              })
                            }
                          />
                          <span>묶음행 이어짐 표시</span>
                        </label>
                        <NestedPageFlowNote isTopLevel={isTopLevel} />
                      </div>
                    );
                  })()}
                {/* 헤더 행 — 다단(단 수≥2) 복제 시 함께 N복제할 헤더 행 범위. 자동/범위/없음. */}
                {(region.columns ?? 1) >= 2 && (
                  <div>
                    <div className="flex items-center justify-between mb-0.5">
                      <span
                        className="text-[11px] text-gray-400 whitespace-nowrap"
                        title="N단 복제 시 헤더로 함께 복제할 행 범위(0-based, 양끝 포함). 미지정=데이터 시작행 바로 위 1행 / 범위=그 행들 / 없음=헤더 복제 안 함"
                      >
                        헤더 행 (범위)
                      </span>
                      <label
                        className="flex items-center gap-1 text-[11px] text-gray-500 shrink-0 cursor-pointer whitespace-nowrap"
                        title="체크 시 헤더를 복제하지 않습니다(데이터 밴드만 N단 전개)."
                      >
                        <input
                          type="checkbox"
                          checked={hrNone}
                          onChange={(e) =>
                            onUpdateRepeatRegion(table.id, i, {
                              headerRows: e.target.checked ? 'none' : undefined,
                            })
                          }
                        />
                        헤더 없음
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <input
                        type="number"
                        min={0}
                        max={Math.max(0, table.rowCount - 1)}
                        className={INPUT_CLS}
                        placeholder="시작(자동)"
                        disabled={hrNone}
                        value={hrStart ?? ''}
                        onChange={(e) => setHeaderRow('start', e.target.value)}
                      />
                      <input
                        type="number"
                        min={0}
                        max={Math.max(0, table.rowCount - 1)}
                        className={INPUT_CLS}
                        placeholder="끝(자동)"
                        disabled={hrNone}
                        value={hrEnd ?? ''}
                        onChange={(e) => setHeaderRow('end', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                {/* 순번 prefix — 반복 항목 앞에 '1. '/'a. ' 등 증가 순번을 대상 칼럼 셀 앞에 부착(구 SMS 수시평가 구분 용례). */}
                <div className="space-y-1 pl-2 border-l-2 border-gray-100">
                  <label className="flex items-start gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={ip !== undefined}
                      onChange={(e) =>
                        onUpdateRepeatRegion(table.id, i, {
                          indexPrefix: e.target.checked
                            ? { col: region.colStart ?? 0, style: 'number' }
                            : undefined,
                        })
                      }
                    />
                    <span title="반복 항목 앞에 '1. ', 'a. ' 등 증가 순번을 대상 칼럼 셀 앞에 붙입니다(데이터 항목 기준, 다단·페이지 분할에도 연속). 빈 패딩 행에는 미부착.">
                      순번 prefix
                    </span>
                  </label>
                  {ip !== undefined && (
                    <div className="grid grid-cols-3 gap-1.5">
                      <div>
                        <span
                          className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                          title="순번을 붙일 대상 칼럼(0-based). 이 영역의 시작~끝 칼럼 범위 내."
                        >
                          대상 칼럼
                        </span>
                        <input
                          type="number"
                          min={region.colStart ?? 0}
                          max={region.colEnd ?? Math.max(0, table.colCount - 1)}
                          className={INPUT_CLS}
                          value={ip.col}
                          onChange={(e) =>
                            onUpdateRepeatRegion(table.id, i, {
                              indexPrefix: { ...ip, col: Number(e.target.value) },
                            })
                          }
                        />
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap">
                          스타일
                        </span>
                        <select
                          className={INPUT_CLS}
                          value={ip.style}
                          onChange={(e) =>
                            onUpdateRepeatRegion(table.id, i, {
                              indexPrefix: {
                                ...ip,
                                style: e.target.value as 'number' | 'alphaLower' | 'alphaUpper',
                              },
                            })
                          }
                        >
                          <option value="number">숫자 (1,2,3)</option>
                          <option value="alphaLower">소문자 (a,b,c)</option>
                          <option value="alphaUpper">대문자 (A,B,C)</option>
                        </select>
                      </div>
                      <div>
                        <span
                          className="text-[11px] text-gray-400 block mb-0.5 whitespace-nowrap"
                          title="순번 뒤 구분자. 빈값=기본 '. '"
                        >
                          구분자
                        </span>
                        <input
                          type="text"
                          className={INPUT_CLS}
                          placeholder=". "
                          value={ip.separator ?? ''}
                          onChange={(e) =>
                            onUpdateRepeatRegion(table.id, i, {
                              indexPrefix: {
                                ...ip,
                                separator: e.target.value === '' ? undefined : e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
                  </div>
                </div>
                </div>
                )}
              </div>
              );
            })}
          </div>
          {/* 신규 반복영역 생성 — ① 소스 선택 → ② 행 지정(캔버스 픽 / 새 행) → ③ region 생성 후 세부 설정 노출.
              region은 행 지정이 완료돼야 생성되므로, 취소/ESC 시 미완성 영역이 남지 않는다. */}
          {creating === null ? (
            <button
              type="button"
              className={`${STRUCT_BTN_CLS} mt-1`}
              title="데이터 소스를 고르고 반복 패턴이 될 행을 지정해 반복영역을 만듭니다"
              onClick={() => setCreating({ over: '' })}
            >
              + 반복영역 추가
            </button>
          ) : (
            <div className="mt-1 border border-brand-200 rounded p-1.5 space-y-1.5 bg-brand-50/40">
              <span className="text-[11px] text-gray-600 block font-medium">새 반복영역</span>
              {/* ① 데이터 소스 선택 — 선택 즉시 행 지정 픽 자동 시작 */}
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-gray-400 shrink-0 whitespace-nowrap">
                  데이터 소스
                </span>
                <select
                  className={INPUT_CLS}
                  value={creating.over}
                  title="데이터셋 추가는 전역 속성 패널의 '＋ 빈 데이터셋'에서 하세요."
                  onChange={(e) => {
                    const over = e.target.value;
                    setCreating({ over });
                    if (over) startCreateRowPick(over);
                    else onCancelCellPick();
                  }}
                >
                  <option value="">(소스 선택)</option>
                  <RepeatSourceOptionGroups sources={sources} />
                </select>
              </div>
              {/* ② 행 지정 안내 + 보조 버튼 */}
              {creating.over ? (
                <div className="space-y-1">
                  <p className="text-[11px] text-brand-700">
                    {createPickActive
                      ? '캔버스에서 반복 패턴이 될 행의 셀을 클릭하세요.'
                      : '행 지정이 취소되었습니다. 다시 지정하거나 새 행을 만드세요.'}
                  </p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {!createPickActive && (
                      <button
                        type="button"
                        className={STRUCT_BTN_CLS}
                        title="캔버스에서 패턴 행이 될 셀을 클릭"
                        onClick={() => startCreateRowPick(creating.over)}
                      >
                        행 지정
                      </button>
                    )}
                    <button
                      type="button"
                      className={STRUCT_BTN_CLS}
                      title="맨 끝에 새 패턴 행을 추가하고 그 행을 반복영역으로 지정"
                      onClick={() => createWithNewRow(creating.over)}
                    >
                      새 행을 만들어 지정
                    </button>
                    <button
                      type="button"
                      className={STRUCT_BTN_CLS}
                      title="반복영역 생성 취소"
                      onClick={cancelCreate}
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className={STRUCT_BTN_CLS}
                    title="반복영역 생성 취소"
                    onClick={cancelCreate}
                  >
                    취소
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
  );
}

// 폰트 굵기(font-weight) 옵션 — 텍스트/표 셀/결재 폰트 편집기 공통.
// value '' = 미설정(상속). 그 외는 number(CSS font-weight)를 문자열로.
const FONT_WEIGHT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '(상속)' },
  { value: '400', label: '보통(400)' },
  { value: '500', label: '중간(500)' },
  { value: '600', label: '약간 굵게(600)' },
  { value: '700', label: '굵게(700)' },
];

/**
 * 폰트 굵기 select 공통 — value=현재 fontWeight(미설정=undefined), 변경 시 number|undefined 전달.
 * mixed(다중선택 혼합) 시 '— 혼합 —' 표시 + 선택 시에만 적용.
 */
function FontWeightSelect({
  value,
  mixed,
  onChange,
  boldShortcut,
}: {
  value: number | undefined;
  mixed?: boolean;
  onChange: (weight: number | undefined) => void;
  // 굵게(700) 토글 단축키 Ctrl+B와 대응하는 편집기(텍스트/셀)에서만 true → title로 안내.
  boldShortcut?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] text-gray-500 w-9">굵기</span>
      <select
        className={SELECT_CLS}
        title={boldShortcut ? '굵기 토글: Ctrl+B' : undefined}
        value={mixed ? MIXED_OPTION : value === undefined ? '' : String(value)}
        onChange={(e) => {
          if (e.target.value === MIXED_OPTION) return;
          onChange(e.target.value === '' ? undefined : Number(e.target.value));
        }}
      >
        {mixed && (
          <option value={MIXED_OPTION} disabled>
            — 혼합 —
          </option>
        )}
        {FONT_WEIGHT_OPTIONS.map((o) => (
          // 각 옵션을 실제 굵기로 렌더(굵게=700은 700 두께로 보이게). '(상속)'은 기본 두께.
          <option key={o.value} value={o.value} style={o.value ? { fontWeight: Number(o.value) } : undefined}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * 자간(letter-spacing) 입력 공통 — 값은 글자 크기 대비 %(HWP 관례), 렌더 시 em 변환.
 * value=현재 letterSpacing(미설정=undefined). 빈값=undefined. mixed 시 placeholder만 '혼합'.
 */
function LetterSpacingInput({
  value,
  mixed,
  onPreview,
  onCommit,
  onRevert,
}: {
  value: number | undefined;
  mixed?: boolean;
  /** 유효 입력 실시간 미리보기(replacePresent). */
  onPreview: (spacing: number) => void;
  /** 확정(commitPresent). 빈값=undefined. */
  onCommit: (spacing: number | undefined) => void;
  /** 미리보기 폐기(discardTransient) — Esc/이탈. */
  onRevert: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] text-gray-500 w-9">자간(%)</span>
      <NumericField
        value={value}
        mixed={mixed}
        step={1}
        title="글자 크기 대비 % (-50~50 권장)"
        placeholder="0"
        onPreview={onPreview}
        onCommit={onCommit}
        onRevert={onRevert}
      />
    </div>
  );
}

/**
 * 줄간격(line-height, 무단위 배수) 입력 공통 — typography 표준상 무단위(폰트 크기에 비례, 소수 허용, step 0.1).
 * value=현재 lineHeight(미설정=undefined). 빈값=undefined. mixed 시 placeholder만 '혼합'.
 */
function LineHeightInput({
  value,
  mixed,
  onPreview,
  onCommit,
  onRevert,
}: {
  value: number | undefined;
  mixed?: boolean;
  onPreview: (lineHeight: number) => void;
  onCommit: (lineHeight: number | undefined) => void;
  onRevert: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] text-gray-500 w-9">줄간격</span>
      {/* 무단위 배수 — 미세값이라 화살표 base 0.1 / Alt 0.01 (mm·pt 계열의 1 단위보다 잘게). */}
      <NumericField
        value={value}
        mixed={mixed}
        step={0.1}
        stepConfig={{ base: 0.1, shift: 1, alt: 0.01 }}
        min={0}
        placeholder="1.5"
        onPreview={onPreview}
        onCommit={onCommit}
        onRevert={onRevert}
      />
    </div>
  );
}

/**
 * 첫 줄 들여쓰기(text-indent, mm) 입력 공통 — 단위는 mm(소수 허용, step 0.1). 구 SMS 한 글자 들여쓰기 재현용.
 * value=현재 textIndent(미설정=undefined=0). 빈값=undefined. mixed 시 placeholder만 '혼합'.
 */
function TextIndentInput({
  value,
  mixed,
  onPreview,
  onCommit,
  onRevert,
}: {
  value: number | undefined;
  mixed?: boolean;
  onPreview: (textIndent: number) => void;
  onCommit: (textIndent: number | undefined) => void;
  onRevert: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] text-gray-500 w-9">들여(mm)</span>
      {/* mm 단위지만 한 글자 들여쓰기 재현용 미세값이라 화살표 base 0.1 / Alt 0.01. */}
      <NumericField
        value={value}
        mixed={mixed}
        step={0.1}
        stepConfig={{ base: 0.1, shift: 1, alt: 0.01 }}
        min={0}
        placeholder="0"
        onPreview={onPreview}
        onCommit={onCommit}
        onRevert={onRevert}
      />
    </div>
  );
}

// 테두리 선 스타일 옵션 — 수치/스타일/색을 분리 입력하기 위한 공통 정의.
const BORDER_STYLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'none', label: '없음' },
  { value: 'solid', label: '실선' },
  { value: 'dashed', label: '파선' },
  { value: 'dotted', label: '점선' },
  { value: 'double', label: '이중선' },
];

/** CSS border 문자열("1px solid #000") → {두께(px 내부저장)/스타일/색}. px·mm 단위 모두 인식. */
function parseBorder(css: string | undefined): { width: number; style: string; color: string } {
  if (!css || css.trim() === '' || /\bnone\b/.test(css)) {
    return { width: mmToPx(0.26), style: 'none', color: '#000000' };
  }
  const widthM = css.match(/([\d.]+)\s*(px|mm)?/);
  const styleM = css.match(/\b(solid|dashed|dotted|double)\b/);
  const colorM = css.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/);
  let color = colorM ? colorM[0] : '#000000';
  if (color.length === 4) color = '#' + color.slice(1).split('').map((c) => c + c).join(''); // #abc→#aabbcc
  // 내부는 px로 통일(엔진/저장 px). mm 표기는 mm→px 변환해 보관.
  let widthPx = 1;
  if (widthM) widthPx = widthM[2] === 'mm' ? mmToPx(Number(widthM[1])) : Number(widthM[1]);
  return { width: widthPx, style: styleM ? styleM[1] : 'solid', color };
}

/** {두께/스타일/색} → CSS border 문자열. 스타일 '없음'/두께 0이면 undefined(테두리 제거). */
function composeBorder(p: { width: number; style: string; color: string }): string | undefined {
  if (p.style === 'none' || !p.width || p.width <= 0) return undefined;
  return `${p.width}px ${p.style} ${p.color}`;
}

/**
 * 테두리 편집 공통 컴포넌트 — CSS 문자열 대신 선 스타일·두께(px)·색을 분리 입력.
 * 두께는 px(테두리는 얇은 선이라 mm 대신 px 유지). mixed=다중선택 혼합 표시.
 */
function BorderEditor({
  value,
  mixed,
  onChange,
  onPreview,
  onRevert,
  label = '테두리',
  allowNone = false,
  baseValue,
  defaultSegmentLabel = '기본',
  noneValue = 'none',
  noneSegmentLabel = '없음',
}: {
  value: string | undefined;
  mixed?: boolean;
  onChange: (border: string | undefined) => void;
  /** 두께(mm) 실시간 미리보기(replacePresent). 미전달 시 onChange로 폴백(즉시 확정). */
  onPreview?: (border: string | undefined) => void;
  /** 미리보기 폐기(discardTransient) — Esc/이탈. 미전달 시 no-op. */
  onRevert?: () => void;
  /** 라벨(기본 '테두리'). 결재란 외곽/구분선 등 용도별 명칭으로 재사용. */
  label?: string;
  /**
   * 3-state 허용(결재란 외곽/구분선 전용). true면 기본(undefined=CSS 기본 유지)·지정·없음('none'=테두리 제거)을
   * 세그먼트 버튼으로 구분 가능. false(기본)면 기존 2-state(지정/해제=undefined) 동작 — rect/group/표셀 호환.
   */
  allowNone?: boolean;
  /**
   * 3-state 세그먼트의 '기본' 버튼 레이블(기본 '기본'). 변별 테두리 맥락에선 "상속"(공통 base 따름)으로
   * 표시. 미전달(결재란 외곽/구분선)이면 '기본' 유지 — 회귀 0.
   */
  defaultSegmentLabel?: string;
  /**
   * "기본" 세그먼트 클릭 시 적용할 값(결재란 전용, 구 SMS 검정 테두리 등). 전달 시 "기본"은 undefined가 아니라
   * 이 값을 onChange로 넘긴다. 미전달(rect/group/표셀)이면 기존대로 undefined(CSS 기본 회색) — 회귀 0.
   * active 판별도 value가 undefined 또는 === baseValue면 "기본"으로 본다(레거시 undefined 템플릿 호환).
   */
  baseValue?: string;
  /**
   * '없음' 세그먼트가 방출할 값(기본 'none'). border-collapse 표(셀·표 외곽)에서는 'hidden'을 주면
   * 이웃 셀 테두리까지 억제하는 강제 없음이 된다('none'은 collapse서 이웃이 우선해 실효 없음).
   */
  noneValue?: string;
  /** '없음' 세그먼트 레이블(기본 '없음'). 강제 hidden 맥락에선 '없음(강제)' 등으로 구분. */
  noneSegmentLabel?: string;
}) {
  const p = parseBorder(value);
  const set = (next: Partial<{ width: number; style: string; color: string }>) =>
    onChange(composeBorder({ ...p, ...next }));
  // 두께(mm) 실시간 미리보기 — onPreview 미전달이면 onChange로 폴백(즉시 확정).
  const setWidthLive = (widthPx: number) => (onPreview ?? onChange)(composeBorder({ ...p, width: widthPx }));
  const setWidthCommit = (widthPx: number | undefined) =>
    onChange(composeBorder({ ...p, width: widthPx ?? 0 }));
  const isNone = p.style === 'none';

  // 3-state(allowNone) 판별 — value 원본으로. parseBorder는 기본(undefined)과 없음('none')을
  // 둘 다 style:'none'으로 만들어 구분 불가하므로 raw value를 본다.
  // baseValue 전달 시: value가 undefined이거나 baseValue와 동일하면 "기본"으로 본다(검정 테두리도 "기본" 강조).
  const triState: 'default' | 'set' | 'none' =
    value === undefined || (baseValue !== undefined && value === baseValue)
      ? 'default'
      : /\bnone\b/.test(value) || /\bhidden\b/.test(value)
        ? 'none'
        : 'set';
  // '지정' 전환 시 기본 테두리값(SMS 결재란 톤). 내부 저장은 px(parseBorder/composeBorder와 동일).
  const SET_DEFAULT = `${mmToPx(0.26)}px solid #333333`;
  // 선 스타일 옵션 — allowNone이면 '없음'은 세그먼트 버튼이 담당하므로 드롭다운에서 제외.
  const styleOptions = allowNone
    ? BORDER_STYLE_OPTIONS.filter((o) => o.value !== 'none')
    : BORDER_STYLE_OPTIONS;
  // 상세(선 스타일·두께·색) 행 — 일반(2-state)은 항상, allowNone은 '지정'일 때만.
  const showDetail = !allowNone || triState === 'set';

  return (
    <div>
      <span className="text-[11px] text-gray-500 block mb-0.5">
        {label}{mixed && <span className="text-gray-400"> (혼합)</span>}
      </span>
      {/* 3-state 세그먼트(allowNone) — 기본/지정/없음. 없음='none'은 StepperLayout이 CSS 기본을 덮어 제거. */}
      {allowNone && (
        <div className="flex items-center gap-1 mb-1">
          {([
            { key: 'default', label: defaultSegmentLabel, title: `${defaultSegmentLabel} — 양식 기본 테두리 유지` },
            { key: 'set', label: '지정', title: '지정 — 선 스타일·두께·색 직접 설정' },
            { key: 'none', label: noneSegmentLabel, title: `${noneSegmentLabel} — 테두리 제거${noneValue === 'hidden' ? '(이웃 셀 테두리까지 강제 억제)' : ''}` },
          ] as const).map((o) => {
            const active = !mixed && triState === o.key;
            return (
              <button
                key={o.key}
                type="button"
                title={o.title}
                className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                  active
                    ? 'border-brand-400 bg-brand-50 text-brand-600 font-medium'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
                onClick={() =>
                  onChange(
                    o.key === 'default'
                      ? baseValue // 미전달이면 undefined(CSS 기본), 전달이면 구 SMS 검정 등
                      : o.key === 'none'
                        ? noneValue // 기본 'none', collapse 표 맥락은 'hidden'(강제 억제)
                        : SET_DEFAULT,
                  )
                }
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
      {showDetail && (
        <div className="flex items-center gap-1">
          {/* 선 스타일 */}
          <select
            className="text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
            value={mixed ? '' : p.style}
            onChange={(e) => set({ style: e.target.value })}
          >
            {mixed && <option value="" disabled>혼합</option>}
            {styleOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {/* 두께(mm) — 저장은 px로 변환(다른 치수와 동일 방식). 소수 2자리. 실시간 미리보기+확정. */}
          <NumericField
            value={isNone ? undefined : p.width}
            min={0}
            step={0.01}
            disabled={isNone}
            placeholder="0.26"
            toDisplay={pxToMm}
            fromDisplay={mmToPx}
            className="w-14 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:opacity-40"
            onPreview={setWidthLive}
            onCommit={setWidthCommit}
            onRevert={onRevert ?? (() => {})}
          />
          <span className="text-[10px] text-gray-400">mm</span>
          {/* 색 */}
          <input
            type="color"
            disabled={isNone}
            value={p.color}
            onChange={(e) => set({ color: e.target.value })}
            className="h-7 w-9 border border-gray-300 rounded p-0.5 disabled:opacity-40"
          />
        </div>
      )}
    </div>
  );
}

/** 변별 테두리 override 객체(상/우/하/좌 각 CSS border 문자열, 지정 변만 보유). */
type BorderOverridesValue = { top?: string; right?: string; bottom?: string; left?: string };

/**
 * 변별 테두리(상/하/좌/우) 편집 — 각 변별 override(비우면 공통 base 상속). 셀 스타일·박스 스타일(RectStyle)·
 * 표 외곽 테두리가 공용으로 사용한다. value=지정된 변만 담은 객체(전부 비면 undefined), onChange에 갱신본
 * (전부 비면 undefined) 전달. BorderEditor(allowNone 3-state: 상속/지정/없음)를 4변으로 감싼다.
 */
function BorderOverridesEditor({
  value,
  onChange,
  title = '변별 테두리(상/하/좌/우)',
  noneValue = 'none',
  noneSegmentLabel = '없음',
}: {
  value: BorderOverridesValue | undefined;
  onChange: (next: BorderOverridesValue | undefined) => void;
  /** 접이식 헤더 라벨(기본 '변별 테두리(상/하/좌/우)'). 표 외곽 등 맥락별 명칭. */
  title?: string;
  /** 각 변 '없음' 세그먼트가 방출할 값(기본 'none', collapse 표는 'hidden'=강제 억제). */
  noneValue?: string;
  /** 각 변 '없음' 세그먼트 레이블(기본 '없음'). */
  noneSegmentLabel?: string;
}) {
  // 한 변 갱신 — undefined면 그 변 제거(=base 상속), 전부 비면 객체 자체 undefined.
  const patchSide = (side: 'top' | 'right' | 'bottom' | 'left', v: string | undefined) => {
    const next: BorderOverridesValue = { ...(value ?? {}) };
    if (v === undefined) delete next[side];
    else next[side] = v;
    const hasAny =
      next.top !== undefined ||
      next.right !== undefined ||
      next.bottom !== undefined ||
      next.left !== undefined;
    onChange(hasAny ? next : undefined);
  };
  const hasAny =
    !!value &&
    (value.top !== undefined ||
      value.right !== undefined ||
      value.bottom !== undefined ||
      value.left !== undefined);
  return (
    <details className="border border-gray-200 rounded" open={hasAny}>
      <summary className="text-[11px] text-gray-500 px-2 py-1 cursor-pointer select-none">
        {title}
        {hasAny && <span className="ml-1 text-brand-600">●</span>}
      </summary>
      <div className="px-2 pb-2 pt-1 space-y-2">
        <p className="text-[10px] text-gray-400 leading-tight">
          각 변은 상속(공통 base 따름)·지정(그 변만 덮어씀)·없음(그 변만 안 그림)을 선택합니다.
        </p>
        <BorderEditor label="상" allowNone defaultSegmentLabel="상속" noneValue={noneValue} noneSegmentLabel={noneSegmentLabel} value={value?.top} onChange={(b) => patchSide('top', b)} />
        <BorderEditor label="하" allowNone defaultSegmentLabel="상속" noneValue={noneValue} noneSegmentLabel={noneSegmentLabel} value={value?.bottom} onChange={(b) => patchSide('bottom', b)} />
        <BorderEditor label="좌" allowNone defaultSegmentLabel="상속" noneValue={noneValue} noneSegmentLabel={noneSegmentLabel} value={value?.left} onChange={(b) => patchSide('left', b)} />
        <BorderEditor label="우" allowNone defaultSegmentLabel="상속" noneValue={noneValue} noneSegmentLabel={noneSegmentLabel} value={value?.right} onChange={(b) => patchSide('right', b)} />
      </div>
    </details>
  );
}

/**
 * 셀 대각선(사선) 방향 옵션 — 저장값(undefined=없음) + 글리프/접근성 이름.
 * HWP 셀 대각선(슬래시·백슬래시·십자)과 같은 집합. 좁은 속성 패널이라 버튼은 글리프만 두고
 * 이름은 aria-label/title이 갖는다(아이콘 버튼 접근성 이름 규약).
 */
const CELL_DIAGONAL_OPTIONS: {
  value: TableCellDiagonal | undefined;
  glyph: string;
  name: string;
}[] = [
  { value: undefined, glyph: '—', name: '대각선 없음' },
  { value: 'tlbr', glyph: '╲', name: '대각선 좌상→우하' },
  { value: 'trbl', glyph: '╱', name: '대각선 우상→좌하' },
  { value: 'both', glyph: '✕', name: '대각선 양방향' },
];

/**
 * 셀 대각선(사선) 편집 — 방향 4-세그먼트(없음/╲/╱/✕) + 선 스타일(BorderEditor 재사용).
 *
 * 저작 계약(rc `tableCellDiagonal`와 짝):
 *  - **방향이 곧 표시 여부**다. 방향이 지정되면 선은 항상 그려진다 — 선을 비우면 셀 테두리를
 *    따르고, 그것도 없으면 엔진 기본(1px 검정 실선). 그래서 "방향을 골랐는데 아무것도 안
 *    그려지는" 상태가 없다(저작 UI가 서버/엔진과 다른 말을 하지 않게 하는 규약).
 *  - 방향을 없음으로 되돌리면 선 키(`diagonalLine`)도 함께 지운다(고르지 않은 축의 키는 남기지
 *    않는 편집기 관례).
 * 선 어휘는 테두리와 같다(선 스타일·두께(mm)·색) — rc `parseDiagonalStroke`가 같은 문자열을
 * 해석하므로 이중선까지 그대로 그려진다.
 */
export function CellDiagonalEditor({
  diagonal,
  line,
  onPatch,
  onPreview,
  onRevert,
}: {
  diagonal: TableCellDiagonal | undefined;
  line: string | undefined;
  /** 방향/선 변경 — 두 키를 함께 담은 부분 패치(호출처가 셀 style에 머지). */
  onPatch: (next: Pick<TableCellStyle, 'diagonal' | 'diagonalLine'>) => void;
  /** 두께 드래그 실시간 미리보기(replacePresent). */
  onPreview: (next: Pick<TableCellStyle, 'diagonal' | 'diagonalLine'>) => void;
  /** 미리보기 폐기(Esc/이탈). */
  onRevert: () => void;
}) {
  return (
    <div>
      <span className="text-[11px] text-gray-500 block mb-0.5">대각선</span>
      <div className="flex items-center gap-1">
        {CELL_DIAGONAL_OPTIONS.map((o) => {
          const active = diagonal === o.value;
          return (
            <button
              key={o.value ?? 'none'}
              type="button"
              aria-label={o.name}
              aria-pressed={active}
              title={o.name}
              className={`text-xs leading-none px-2 py-1 rounded border transition-colors ${
                active
                  ? 'border-brand-400 bg-brand-50 text-brand-600 font-medium'
                  : 'border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
              onClick={() =>
                // 없음으로 되돌리면 선 키도 함께 지운다(고르지 않은 축의 키를 남기지 않음).
                onPatch(
                  o.value === undefined
                    ? { diagonal: undefined, diagonalLine: undefined }
                    : { diagonal: o.value },
                )
              }
            >
              {o.glyph}
            </button>
          );
        })}
      </div>
      {diagonal !== undefined && (
        <div className="mt-1 space-y-0.5">
          <BorderEditor
            label="대각선 선"
            value={line}
            onChange={(b) => onPatch({ diagonalLine: b })}
            onPreview={(b) => onPreview({ diagonalLine: b })}
            onRevert={onRevert}
          />
          <p className="text-[10px] text-gray-400 leading-tight">
            비우면 셀 테두리와 같은 선으로 그립니다(테두리도 없으면 1px 검정 실선).
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * 표 외곽 테두리 편집 — 전체 셀 테두리와 별개인 표 자체(외곽) 테두리(node.outerBorder / outerBorderOverrides).
 * 3-상태: 미지정(셀 가장자리에 맡김·스타일 미방출) / 지정(실선 등) / 없음(강제)=hidden(border-collapse서 가장자리
 * 셀 테두리까지 억제 → 테두리 없는 표를 셀 수정 없이 표 설정 한 번으로). 변별로 특정 변만 지정/강제없음도 가능.
 */
function TableOuterBorderSection({
  node,
  onPatch,
}: {
  node: TableNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
}) {
  return (
    <TableOuterBorderFields
      outerBorder={node.outerBorder}
      outerBorderOverrides={node.outerBorderOverrides}
      onBorderChange={(b) => onPatch({ outerBorder: b } as Partial<ComposerNode>)}
      onOverridesChange={(ov) => onPatch({ outerBorderOverrides: ov } as Partial<ComposerNode>)}
    />
  );
}

/**
 * 표 외곽 테두리 편집 필드(공용, presentational) — 양식 탭 TableNode와 작성폼 formTable 위젯이 공유
 * (parity P3, 2026-07-13). outerBorder/outerBorderOverrides 값·콜백만 받아 store 형태에 무관.
 */
function TableOuterBorderFields({
  outerBorder,
  outerBorderOverrides,
  onBorderChange,
  onOverridesChange,
}: {
  outerBorder?: string;
  outerBorderOverrides?: { top?: string; right?: string; bottom?: string; left?: string };
  onBorderChange: (b: string | undefined) => void;
  onOverridesChange: (
    ov: { top?: string; right?: string; bottom?: string; left?: string } | undefined,
  ) => void;
}) {
  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">표 외곽 테두리</dt>
      <dd className="space-y-2">
        <p className="text-[10px] text-gray-400 leading-tight">
          전체 셀 테두리와 별개인 표 자체 테두리입니다. 미지정=셀 가장자리에 맡김, 없음(강제)=가장자리 셀
          테두리까지 숨겨 테두리 없는 표를 만듭니다.
        </p>
        <BorderEditor
          label="외곽 테두리"
          allowNone
          defaultSegmentLabel="미지정"
          noneValue="hidden"
          noneSegmentLabel="없음(강제)"
          value={outerBorder}
          onChange={onBorderChange}
        />
        <BorderOverridesEditor
          title="외곽 변별 테두리(상/하/좌/우)"
          noneValue="hidden"
          noneSegmentLabel="없음(강제)"
          value={outerBorderOverrides}
          onChange={onOverridesChange}
        />
      </dd>
    </div>
  );
}

/**
 * 박스 스타일(RectStyle) 편집 필드 — 채움색/테두리(선 스타일·두께·색 분리)/모서리 둥글기(px).
 * group 노드의 박스 장식 편집에 사용(rect 노드는 별도 편집 UI가 없어 신설). 모든 필드 미설정=장식 없음.
 * 변경 시 style 전체를 새 객체로 만들어 onChange로 전달(상위가 통째 교체).
 */
function RectStyleFields({
  value,
  onChange,
  onPreview,
  onRevert,
  showBorder = true,
  defaultFill,
}: {
  value: RectStyle | undefined;
  onChange: (style: RectStyle | undefined) => void;
  /** 두께/모서리 등 숫자 입력 실시간 미리보기(replacePresent). 미전달 시 onChange 폴백(즉시 확정). */
  onPreview?: (style: RectStyle | undefined) => void;
  /** 미리보기 폐기(discardTransient) — Esc/이탈. 미전달 시 no-op. */
  onRevert?: () => void;
  /** 테두리(선/변별/모서리) 편집 노출 여부. 기본 true. 결재란 영역(배경 전용)은 false. */
  showBorder?: boolean;
  /**
   * 채움색 "초기화" 시 적용할 기본 fill(결재란 영역 전용, 'transparent' 등). 전달 시 "초기화"는
   * undefined(=장식 없음/CSS 기본 회색)가 아니라 이 값으로 되돌린다(폰트 글자색 초기화→DEFAULT와 동일 철학).
   * 미전달(group/rect/표셀)이면 기존대로 undefined — 회귀 0. fill이 이 값과 같으면 "초기화"는 숨김.
   */
  defaultFill?: string;
}) {
  const style: RectStyle = value ?? {};
  const build = (next: Partial<RectStyle>): RectStyle | undefined => {
    const merged: RectStyle = { ...style, ...next };
    (Object.keys(merged) as (keyof RectStyle)[]).forEach((k) => {
      if (merged[k] === undefined) delete merged[k];
    });
    return Object.keys(merged).length === 0 ? undefined : merged;
  };
  const patch = (next: Partial<RectStyle>) => onChange(build(next));
  // 숫자 입력 실시간 미리보기 — onPreview 미전달이면 onChange 폴백(즉시 확정).
  const patchLive = (next: Partial<RectStyle>) => (onPreview ?? onChange)(build(next));
  return (
    <div className="space-y-2">
      {/* 채움색 */}
      <div className="flex items-center gap-1">
        <span className="text-[11px] text-gray-500 w-12">채움색</span>
        <input
          type="color"
          className="h-7 w-10 border border-gray-300 rounded p-0.5"
          value={style.fill ?? '#ffffff'}
          onChange={(e) => patch({ fill: e.target.value })}
        />
        {/* 배경 없음(투명) — 단계명란처럼 CSS 기본 배경(회색)이 있는 영역에서 '없음'을 명시.
            color input은 투명을 표현 못 하므로 별도 버튼으로 fill='transparent' 설정. */}
        <button
          type="button"
          className={`text-[11px] underline ${style.fill === 'transparent' ? 'text-brand-600 font-medium' : 'text-gray-500'}`}
          onClick={() => patch({ fill: 'transparent' })}
        >
          투명
        </button>
        {/* 초기화 — defaultFill 전달 시 그 값(결재란='transparent')으로, 미전달이면 undefined(장식 없음).
            현재 fill이 기본과 같으면(undefined 또는 defaultFill) 버튼 숨김. */}
        {style.fill !== undefined && style.fill !== defaultFill && (
          <button
            type="button"
            className="text-[11px] text-gray-500 underline"
            onClick={() => patch({ fill: defaultFill })}
          >
            초기화
          </button>
        )}
      </div>
      {/* 테두리 — 선 스타일·두께(mm)·색 분리 입력. showBorder=false면 배경 전용(결재란 영역). */}
      {showBorder && (
        <BorderEditor
          value={style.border ?? undefined}
          onChange={(b) => patch({ border: b })}
          onPreview={(b) => patchLive({ border: b })}
          onRevert={onRevert}
        />
      )}
      {/* 테두리 변 선택(Excel식 박스 에지 클릭) — 테두리가 있을 때만. 닿는 변을 빼 단일 선으로. */}
      {showBorder && style.border && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500 w-12">테두리 변</span>
          {(() => {
            type Side = 'top' | 'right' | 'bottom' | 'left';
            const cur = style.borderSides ?? { top: true, right: true, bottom: true, left: true };
            const isOn = (k: Side) => cur[k] !== false;
            const toggle = (k: Side) => {
              const base = style.borderSides ?? { top: true, right: true, bottom: true, left: true };
              const next = { ...base, [k]: !(base[k] !== false) };
              const allOn =
                next.top !== false && next.right !== false && next.bottom !== false && next.left !== false;
              patch({ borderSides: allOn ? undefined : next });
            };
            const edge = (k: Side) => (isOn(k) ? '2px solid #4f46e5' : '2px solid #e5e7eb'); // brand-600
            const hitCls = 'absolute bg-transparent hover:bg-brand-100 cursor-pointer p-0 border-0';
            return (
              <div style={{ position: 'relative', width: 46, height: 46 }} title="각 변을 클릭해 테두리 켜기/끄기">
                {/* 시각 박스(현재 변 상태) */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 7,
                    borderTop: edge('top'),
                    borderRight: edge('right'),
                    borderBottom: edge('bottom'),
                    borderLeft: edge('left'),
                    background: '#f8fafc',
                    pointerEvents: 'none',
                  }}
                />
                {/* 에지 클릭 영역(코너는 좌우 변 쪽으로 양보) */}
                <button type="button" aria-label="위 테두리" title="위 테두리" onClick={() => toggle('top')} className={hitCls} style={{ top: 0, left: 7, right: 7, height: 11 }} />
                <button type="button" aria-label="아래 테두리" title="아래 테두리" onClick={() => toggle('bottom')} className={hitCls} style={{ bottom: 0, left: 7, right: 7, height: 11 }} />
                <button type="button" aria-label="왼쪽 테두리" title="왼쪽 테두리" onClick={() => toggle('left')} className={hitCls} style={{ left: 0, top: 7, bottom: 7, width: 11 }} />
                <button type="button" aria-label="오른쪽 테두리" title="오른쪽 테두리" onClick={() => toggle('right')} className={hitCls} style={{ right: 0, top: 7, bottom: 7, width: 11 }} />
              </div>
            );
          })()}
        </div>
      )}
      {/* 변별 테두리(상/하/좌/우) — 각 변별 override(비우면 공통 base 상속). 셀 편집기와 동일 공용 컴포넌트. */}
      {showBorder && (
        <BorderOverridesEditor
          value={style.borderOverrides}
          onChange={(ov) => patch({ borderOverrides: ov })}
        />
      )}
      {/* 모서리 둥글기(px) — 테두리 그룹에 속함(배경 전용 영역에서는 숨김). */}
      {showBorder && (
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-12">모서리(px)</span>
          <NumericField
            value={style.borderRadius ?? undefined}
            min={0}
            step={1}
            placeholder="0"
            onPreview={(v) => patchLive({ borderRadius: v })}
            onCommit={(v) => patch({ borderRadius: v })}
            onRevert={onRevert ?? (() => {})}
          />
        </div>
      )}
    </div>
  );
}

// 표 구조 편집 버튼(추가/삭제) 공통 클래스.
const STRUCT_BTN_CLS =
  'px-1.5 py-1 text-[11px] rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed';
const STRUCT_DEL_BTN_CLS =
  'px-1.5 py-1 text-[11px] rounded border border-red-300 bg-white text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * 텍스트 스타일 편집(text 노드 한정) — 폰트/볼드/이탤릭/밑줄 + 크기/정렬/색상.
 *
 * 모든 변경은 node.style 전체를 새 객체로 만들어 onPatch({ style })로 통째 교체한다
 * (onPatch가 patch를 노드에 얕은 병합하므로 style 부분 병합은 위험).
 */
function TextStyleEditor({
  node,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
}: {
  node: ComposerNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
}) {
  // node.kind==='text'에서만 렌더되므로 style은 TextStyle.
  const style: TextStyle = (node as { style?: TextStyle }).style ?? {};
  const patchStyle = (next: Partial<TextStyle>) =>
    onPatch({ style: { ...style, ...next } } as Partial<ComposerNode>);
  // 숫자 입력(NumericField) 미리보기/확정 — replace/commit 경유(patchStyle과 동일 shape).
  const patchStyleLive = (next: Partial<TextStyle>) =>
    onPreview({ style: { ...style, ...next } } as Partial<ComposerNode>);
  const patchStyleCommit = (next: Partial<TextStyle>) =>
    onCommit({ style: { ...style, ...next } } as Partial<ComposerNode>);

  const toggleBtn = (active: boolean) =>
    `px-2 py-1 text-xs rounded border ${
      active
        ? 'bg-brand-600 text-white border-brand-600'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`;

  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">텍스트 스타일</dt>
      <dd className="space-y-2">
        {/* 폰트 */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">폰트</span>
          <select
            className={SELECT_CLS}
            value={style.fontFamily ?? ''}
            onChange={(e) => patchStyle({ fontFamily: e.target.value || undefined })}
          >
            <option value="">(기본)</option>
            {COMPOSER_FONTS.map((f) => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        {/* 이탤릭 / 밑줄 토글 (볼드는 굵기 드롭다운으로 통합) */}
        <div className="flex gap-1">
          <button
            type="button"
            aria-pressed={!!style.italic}
            title="이탤릭"
            className={`${toggleBtn(!!style.italic)} italic`}
            onClick={() => patchStyle({ italic: style.italic ? undefined : true })}
          >
            I
          </button>
          <button
            type="button"
            aria-pressed={!!style.underline}
            title="밑줄"
            className={`${toggleBtn(!!style.underline)} underline`}
            onClick={() => patchStyle({ underline: style.underline ? undefined : true })}
          >
            U
          </button>
        </div>

        {/* 굵기(font-weight) — B 토글과 공존(토글은 700 setter). */}
        <FontWeightSelect
          value={style.fontWeight}
          onChange={(w) => patchStyle({ fontWeight: w })}
          boldShortcut
        />

        {/* 크기(pt) / 정렬 / 색상 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">크기(pt)</span>
          <NumericField
            value={style.fontSize ?? undefined}
            min={1}
            step={0.1}
            placeholder="(기본)"
            toDisplay={pxToPt}
            fromDisplay={ptToPx}
            onPreview={(px) => patchStyleLive({ fontSize: px })}
            onCommit={(px) => patchStyleCommit({ fontSize: px })}
            onRevert={onRevert}
          />
        </div>

        {/* 자간(%) */}
        <LetterSpacingInput
          value={style.letterSpacing}
          onPreview={(s) => patchStyleLive({ letterSpacing: s })}
          onCommit={(s) => patchStyleCommit({ letterSpacing: s })}
          onRevert={onRevert}
        />
        {/* 줄간격(line-height) */}
        <LineHeightInput
          value={style.lineHeight}
          onPreview={(s) => patchStyleLive({ lineHeight: s })}
          onCommit={(s) => patchStyleCommit({ lineHeight: s })}
          onRevert={onRevert}
        />
        {/* 첫 줄 들여쓰기(text-indent, mm) */}
        <TextIndentInput
          value={style.textIndent}
          onPreview={(s) => patchStyleLive({ textIndent: s })}
          onCommit={(s) => patchStyleCommit({ textIndent: s })}
          onRevert={onRevert}
        />
        {/* 텍스트 수평 정렬(align)은 콘텐츠 정렬(layout.contentAlignH)로 일원화되어 제거됨.
            정렬은 위 레이아웃 섹션의 '콘텐츠 정렬'에서 편집한다. */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">색상</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={style.color ?? '#000000'}
            onChange={(e) => patchStyle({ color: e.target.value })}
          />
          {style.color !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => patchStyle({ color: undefined })}
            >
              초기화
            </button>
          )}
        </div>
      </dd>
    </div>
  );
}

/**
 * 라디오 그룹 속성 편집 — 공유 bindingKey rename + 옵션(각 자식 checkbox) 라벨/값 편집 + 옵션 추가/삭제.
 * getRadioGroupInfo가 참(group + 자식 전부 같은 키 공유 checkbox)일 때만 노출된다. 편집은 onPatch로
 * 그룹의 children을 통째 교체(undo 1스텝) — 키 rename은 전 자식 binding.bindingKey를 일괄 갱신한다.
 * 옵션 추가만 신규 id 발급이 필요해 메인 콜백(onAddOption)에 위임한다. 옵션은 최소 1개 유지.
 * checkedValue는 non-empty여야 작성폼 라디오 파생이 동작(P1) — 값을 비우면 라벨을 값으로 대체 저장한다.
 * (결정 기록 2026-07-07-composer-radio-group-via-shared-key-checkbox)
 */
function RadioGroupPropsEditor({
  group,
  onPatch,
  onAddOption,
}: {
  group: GroupNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  onAddOption: () => void;
}) {
  const info = getRadioGroupInfo(group);
  if (!info) return null;
  const options = info.options;

  const patchChildren = (children: CheckboxNode[]) =>
    onPatch({ children } as Partial<ComposerNode>);

  // 공유 키 rename — 전 자식 checkbox의 binding.bindingKey를 새 값으로 일괄 갱신.
  const renameKey = (nextKey: string) => {
    patchChildren(
      options.map((cb) => ({
        ...cb,
        binding: { ...(cb.binding ?? { source: 'input', lifecycle: 'snapshot' }), bindingKey: nextKey },
      })),
    );
  };

  // 옵션 라벨/값 편집 — 값(checkedValue) 비면 라벨로 대체(라디오 파생 non-empty 보장).
  const updateOption = (id: string, patch: { label?: string; checkedValue?: string }) => {
    patchChildren(options.map((cb) => (cb.id === id ? { ...cb, ...patch } : cb)));
  };

  const removeOption = (id: string) => {
    if (options.length <= 1) return;
    patchChildren(options.filter((cb) => cb.id !== id));
  };

  // 공통 스타일 — 스타일 필드 하나를 전 옵션(자식 checkbox)에 일괄 적용(라벨/checkedValue는 제외, 개별 유지).
  const applyToAll = (patch: Partial<CheckboxNode>) =>
    patchChildren(options.map((cb) => ({ ...cb, ...patch })));

  // 옵션들의 스타일 필드 균일/혼합 판정(undefined 기본값은 정규화해 비교) — 균일하면 그 값, 혼재면 mixed.
  const cBoxShape = fold(options, (cb) => cb.boxShape ?? 'square');
  const cLabelPos = fold(options, (cb) => cb.labelPosition ?? 'after');
  const cVAlign = fold(options, (cb) => cb.labelVAlign ?? 'center');
  const cLabelGap = fold(options, (cb) => cb.labelGap);
  const cBoxSize = fold(options, (cb) => cb.boxSize);
  const cBorderColor = fold(options, (cb) => cb.boxBorderColor);
  const cBorderWidth = fold(options, (cb) => cb.boxBorderWidth);
  const cCheckedColor = fold(options, (cb) => cb.checkedColor);

  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">라디오 그룹</dt>
      <dd className="space-y-2">
        <p className="text-[11px] text-gray-400 leading-snug">
          같은 키를 공유하는 체크박스 세트입니다. 작성폼에서 이 키는 하나만 고르는 라디오 그룹으로 자동 표시됩니다.
        </p>
        {/* 공유 바인딩 키 */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">공유 키</span>
          <input
            type="text"
            className={INPUT_CLS}
            value={info.bindingKey}
            onChange={(e) => renameKey(e.target.value)}
            placeholder="예: radio-1"
          />
        </div>
        {/* 옵션 목록 — 각 자식 checkbox(라벨 + 값) */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">옵션 ({options.length})</span>
          <div className="space-y-1.5">
            {options.map((opt, index) => (
              <div key={opt.id} className="flex items-center gap-1.5">
                <input
                  type="text"
                  className={INPUT_CLS}
                  value={opt.label ?? ''}
                  onChange={(e) => updateOption(opt.id, { label: e.target.value })}
                  placeholder="라벨(표시)"
                  aria-label={`옵션 ${index + 1} 라벨`}
                />
                <input
                  type="text"
                  className={INPUT_CLS}
                  value={opt.checkedValue ?? ''}
                  onChange={(e) =>
                    updateOption(opt.id, { checkedValue: e.target.value || (opt.label ?? '') })
                  }
                  placeholder="값"
                  aria-label={`옵션 ${index + 1} 값`}
                />
                <button
                  type="button"
                  onClick={() => removeOption(opt.id)}
                  disabled={options.length <= 1}
                  title="옵션 삭제"
                  aria-label={`옵션 ${index + 1} 삭제`}
                  className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={onAddOption}
            className="mt-1.5 rounded border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600"
          >
            + 옵션 추가
          </button>
        </div>

        {/* 옵션 배치 — 그룹 방향(가로/세로)과 옵션(각 "체크박스+라벨") 사이 간격(그룹 gap). */}
        <div className="border-t border-gray-100 pt-2 space-y-2">
          <span className="text-[11px] font-semibold text-gray-500 block">옵션 배치</span>
          {/* 방향 */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">방향</span>
            <div className="inline-flex overflow-hidden rounded border border-gray-300">
              {(
                [
                  ['row', '가로'],
                  ['column', '세로'],
                ] as const
              ).map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => onPatch({ direction: val } as Partial<ComposerNode>)}
                  className={`px-2 py-0.5 text-[11px] ${
                    (group.direction ?? 'row') === val
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {/* 옵션 간격(px) — 각 "체크박스+라벨" 사이 간격(그룹 gap). labelGap(박스↔라벨)과 별개. */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">간격(px)</span>
            <NumericField
              value={group.gap ?? undefined}
              min={0}
              step={1}
              placeholder="8"
              onPreview={(v) => onPatch({ gap: v } as Partial<ComposerNode>)}
              onCommit={(v) => onPatch({ gap: v } as Partial<ComposerNode>)}
              onRevert={() => {}}
            />
          </div>
        </div>

        {/* 공통 스타일 — 스타일 필드를 전 옵션에 일괄 적용(라벨/값은 개별 유지). CheckboxPropsEditor와 동일 컨트롤. */}
        <div className="border-t border-gray-100 pt-2 space-y-2">
          <span className="text-[11px] font-semibold text-gray-500 block">공통 스타일</span>
          <p className="text-[11px] text-gray-400 leading-snug">
            전 옵션에 일괄 적용됩니다. 특정 옵션만 다르게 하려면 그 체크박스를 선택해 개별 변경하세요.
          </p>

          {/* 박스 크기(pt) */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">박스(pt)</span>
            <NumericField
              value={cBoxSize.value ?? undefined}
              mixed={cBoxSize.mixed}
              min={1}
              step={0.1}
              placeholder="10"
              toDisplay={pxToPt}
              fromDisplay={ptToPx}
              onPreview={(px) => applyToAll({ boxSize: px })}
              onCommit={(px) => applyToAll({ boxSize: px })}
              onRevert={() => {}}
            />
          </div>

          {/* 박스 모양 — 사각/원형 */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">모양</span>
            <div className="flex gap-1">
              {(
                [
                  ['square', '사각'],
                  ['circle', '원형'],
                ] as const
              ).map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  className={`px-2 py-0.5 text-xs rounded border ${
                    !cBoxShape.mixed && (cBoxShape.value ?? 'square') === val
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-gray-600 border-gray-300'
                  }`}
                  onClick={() => applyToAll({ boxShape: val === 'square' ? undefined : val })}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {cBoxShape.mixed && <span className="text-[11px] text-gray-400">혼합</span>}
          </div>

          {/* 라벨 위치 — 앞/뒤 */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">라벨 위치</span>
            <div className="flex gap-1">
              {(
                [
                  ['after', '뒤'],
                  ['before', '앞'],
                ] as const
              ).map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  className={`px-2 py-0.5 text-xs rounded border ${
                    !cLabelPos.mixed && (cLabelPos.value ?? 'after') === val
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-gray-600 border-gray-300'
                  }`}
                  onClick={() => applyToAll({ labelPosition: val === 'after' ? undefined : val })}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {cLabelPos.mixed && <span className="text-[11px] text-gray-400">혼합</span>}
          </div>

          {/* 라벨 수직 정렬 — 위/중/아래 */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">수직 정렬</span>
            <div className="flex gap-1">
              {(
                [
                  ['top', '위'],
                  ['center', '중'],
                  ['bottom', '아래'],
                ] as const
              ).map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  className={`px-2 py-0.5 text-xs rounded border ${
                    !cVAlign.mixed && (cVAlign.value ?? 'center') === val
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-gray-600 border-gray-300'
                  }`}
                  onClick={() => applyToAll({ labelVAlign: val === 'center' ? undefined : val })}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {cVAlign.mixed && <span className="text-[11px] text-gray-400">혼합</span>}
          </div>

          {/* 라벨 간격(px) */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">간격(px)</span>
            <NumericField
              value={cLabelGap.value ?? undefined}
              mixed={cLabelGap.mixed}
              min={0}
              step={1}
              placeholder="(기본)"
              onPreview={(v) => applyToAll({ labelGap: v })}
              onCommit={(v) => applyToAll({ labelGap: v })}
              onRevert={() => {}}
            />
          </div>

          {/* 테두리 색 + 두께 */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">테두리</span>
            <input
              type="color"
              className="h-7 w-10 border border-gray-300 rounded p-0.5"
              value={cBorderColor.value ?? '#000000'}
              onChange={(e) => applyToAll({ boxBorderColor: e.target.value })}
            />
            {cBorderColor.mixed && <span className="text-[11px] text-gray-400">혼합</span>}
            {(cBorderColor.mixed || cBorderColor.value !== undefined) && (
              <button
                type="button"
                className="text-[11px] text-gray-500 underline"
                onClick={() => applyToAll({ boxBorderColor: undefined })}
              >
                초기화
              </button>
            )}
            <NumericField
              value={cBorderWidth.value ?? undefined}
              mixed={cBorderWidth.mixed}
              min={0}
              step={0.5}
              placeholder="1"
              onPreview={(v) => applyToAll({ boxBorderWidth: v })}
              onCommit={(v) => applyToAll({ boxBorderWidth: v })}
              onRevert={() => {}}
            />
          </div>

          {/* 선택 표시 색 */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-14">선택 색</span>
            <input
              type="color"
              className="h-7 w-10 border border-gray-300 rounded p-0.5"
              value={cCheckedColor.value ?? '#000000'}
              onChange={(e) => applyToAll({ checkedColor: e.target.value })}
            />
            {cCheckedColor.mixed && <span className="text-[11px] text-gray-400">혼합</span>}
            {(cCheckedColor.mixed || cCheckedColor.value !== undefined) && (
              <button
                type="button"
                className="text-[11px] text-gray-500 underline"
                onClick={() => applyToAll({ checkedColor: undefined })}
              >
                초기화
              </button>
            )}
          </div>
        </div>
      </dd>
    </div>
  );
}

/**
 * 체크박스 노드 속성 편집 — 라벨/라벨 스타일(폰트·굵기·색)/박스 크기(pt)/checkedValue/정적 checked.
 * 체크 판정/바인딩 편집은 하단 공통 바인딩 섹션(BindingEditor)이 담당. boxSize는 px 저장·UI는 pt(폰트 관례).
 */
function CheckboxPropsEditor({
  node,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
}: {
  node: CheckboxNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
}) {
  const ls: TextStyle = node.labelStyle ?? {};
  // labelStyle 병합 + 빈 스타일 제거(undefined) 공통 — commit/preview가 동일 정규화를 공유.
  const buildLabelStyle = (next: Partial<TextStyle>): Partial<ComposerNode> => {
    const merged: TextStyle = { ...ls, ...next };
    const hasAny = Object.values(merged).some((v) => v !== undefined);
    return { labelStyle: hasAny ? merged : undefined } as Partial<ComposerNode>;
  };
  const patchLabelStyle = (next: Partial<TextStyle>) => onPatch(buildLabelStyle(next));
  const patchLabelStyleLive = (next: Partial<TextStyle>) => onPreview(buildLabelStyle(next));
  const patchLabelStyleCommit = (next: Partial<TextStyle>) => onCommit(buildLabelStyle(next));

  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">체크박스</dt>
      <dd className="space-y-2">
        {/* 라벨 */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">라벨</span>
          <input
            type="text"
            className={INPUT_CLS}
            value={node.label ?? ''}
            placeholder="(없으면 박스만)"
            onChange={(e) => onPatch({ label: e.target.value || undefined } as Partial<ComposerNode>)}
          />
        </div>

        {/* 박스 크기(pt) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">박스(pt)</span>
          <NumericField
            value={node.boxSize ?? undefined}
            min={1}
            step={0.1}
            placeholder="10"
            toDisplay={pxToPt}
            fromDisplay={ptToPx}
            onPreview={(px) => onPreview({ boxSize: px } as Partial<ComposerNode>)}
            onCommit={(px) => onCommit({ boxSize: px } as Partial<ComposerNode>)}
            onRevert={onRevert}
          />
        </div>

        {/* 박스 모양 — 사각(체크박스)/원형(라디오) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">모양</span>
          <div className="flex gap-1">
            {(
              [
                ['square', '사각'],
                ['circle', '원형'],
              ] as const
            ).map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`px-2 py-0.5 text-xs rounded border ${
                  (node.boxShape ?? 'square') === val
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
                onClick={() =>
                  onPatch({ boxShape: val === 'square' ? undefined : val } as Partial<ComposerNode>)
                }
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* 라벨 위치 — 박스 앞/뒤 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">라벨 위치</span>
          <div className="flex gap-1">
            {(
              [
                ['after', '뒤'],
                ['before', '앞'],
              ] as const
            ).map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`px-2 py-0.5 text-xs rounded border ${
                  (node.labelPosition ?? 'after') === val
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
                onClick={() =>
                  onPatch({
                    labelPosition: val === 'after' ? undefined : val,
                  } as Partial<ComposerNode>)
                }
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* 라벨 수직 정렬 — 위/중/아래 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">수직 정렬</span>
          <div className="flex gap-1">
            {(
              [
                ['top', '위'],
                ['center', '중'],
                ['bottom', '아래'],
              ] as const
            ).map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`px-2 py-0.5 text-xs rounded border ${
                  (node.labelVAlign ?? 'center') === val
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
                onClick={() =>
                  onPatch({
                    labelVAlign: val === 'center' ? undefined : val,
                  } as Partial<ComposerNode>)
                }
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* 라벨 간격(px) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">간격(px)</span>
          <NumericField
            value={node.labelGap ?? undefined}
            min={0}
            step={1}
            placeholder="(기본)"
            onPreview={(v) => onPreview({ labelGap: v } as Partial<ComposerNode>)}
            onCommit={(v) => onCommit({ labelGap: v } as Partial<ComposerNode>)}
            onRevert={onRevert}
          />
        </div>

        {/* 테두리 색 + 두께 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">테두리</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={node.boxBorderColor ?? '#000000'}
            onChange={(e) => onPatch({ boxBorderColor: e.target.value } as Partial<ComposerNode>)}
          />
          {node.boxBorderColor !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => onPatch({ boxBorderColor: undefined } as Partial<ComposerNode>)}
            >
              초기화
            </button>
          )}
          <NumericField
            value={node.boxBorderWidth ?? undefined}
            min={0}
            step={0.5}
            placeholder="1"
            onPreview={(v) => onPreview({ boxBorderWidth: v } as Partial<ComposerNode>)}
            onCommit={(v) => onCommit({ boxBorderWidth: v } as Partial<ComposerNode>)}
            onRevert={onRevert}
          />
        </div>

        {/* 선택 표시 색(체크마크/점) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">선택 색</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={node.checkedColor ?? '#000000'}
            onChange={(e) => onPatch({ checkedColor: e.target.value } as Partial<ComposerNode>)}
          />
          {node.checkedColor !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => onPatch({ checkedColor: undefined } as Partial<ComposerNode>)}
            >
              초기화
            </button>
          )}
        </div>

        {/* 라벨 스타일 — 폰트/굵기/크기/색(최소) */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">폰트</span>
          <select
            className={SELECT_CLS}
            value={ls.fontFamily ?? ''}
            onChange={(e) => patchLabelStyle({ fontFamily: e.target.value || undefined })}
          >
            <option value="">(기본)</option>
            {COMPOSER_FONTS.map((f) => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <FontWeightSelect value={ls.fontWeight} onChange={(w) => patchLabelStyle({ fontWeight: w })} boldShortcut />
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">크기(pt)</span>
          <NumericField
            value={ls.fontSize ?? undefined}
            min={1}
            step={0.1}
            placeholder="(기본)"
            toDisplay={pxToPt}
            fromDisplay={ptToPx}
            onPreview={(px) => patchLabelStyleLive({ fontSize: px })}
            onCommit={(px) => patchLabelStyleCommit({ fontSize: px })}
            onRevert={onRevert}
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">색상</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={ls.color ?? '#000000'}
            onChange={(e) => patchLabelStyle({ color: e.target.value })}
          />
          {ls.color !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => patchLabelStyle({ color: undefined })}
            >
              초기화
            </button>
          )}
        </div>

        {/* checkedValue(라디오식) — 바인딩 있을 때만 의미(정적 체크는 아래 토글). */}
        {node.binding && (
          <div>
            <span className="text-[11px] text-gray-500 block mb-0.5">checkedValue</span>
            <input
              type="text"
              className={INPUT_CLS}
              value={node.checkedValue ?? ''}
              placeholder="(미지정 = truthy 판정)"
              onChange={(e) => onPatch({ checkedValue: e.target.value || undefined } as Partial<ComposerNode>)}
            />
            <p className="text-[11px] text-gray-400 mt-0.5">
              지정 시 바인딩 값이 이 값과 같을 때 체크 — 같은 식별 키의 박스 여러 개로 선택지 표현.
              바인딩 값이 boolean 또는 문자열 'true'면 checkedValue 없이 체크됩니다.
            </p>
          </div>
        )}

        {/* 정적 checked — 바인딩 없을 때만 의미 */}
        {!node.binding && (
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={node.checked ?? false}
              onChange={(e) => onPatch({ checked: e.target.checked || undefined } as Partial<ComposerNode>)}
            />
            항상 체크(정적 표기)
          </label>
        )}
      </dd>
    </div>
  );
}

/**
 * 선택지 그룹(choiceGroup) 속성 편집 — **표현 스타일만**. 선택지 목록·값·라벨은 내장 데이터셋(dataDef)
 * 필드 options에서 렌더 시점에 자동 파생하므로 여기서 편집하지 않는다(선택지 편집 UI 없음).
 * 바인딩(행 필드 지정)은 하단 공통 바인딩 섹션에서 한다.
 */
function ChoiceGroupPropsEditor({
  node,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
}: {
  node: ChoiceGroupNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
}) {
  const ls: TextStyle = node.labelStyle ?? {};
  const buildLabelStyle = (next: Partial<TextStyle>): Partial<ComposerNode> => {
    const merged: TextStyle = { ...ls, ...next };
    const hasAny = Object.values(merged).some((v) => v !== undefined);
    return { labelStyle: hasAny ? merged : undefined } as Partial<ComposerNode>;
  };
  const patchLabelStyle = (next: Partial<TextStyle>) => onPatch(buildLabelStyle(next));
  const patchLabelStyleLive = (next: Partial<TextStyle>) => onPreview(buildLabelStyle(next));
  const patchLabelStyleCommit = (next: Partial<TextStyle>) => onCommit(buildLabelStyle(next));

  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500 mb-1">선택지 그룹</dt>
      <dd className="space-y-2">
        <p className="text-[11px] text-gray-400 leading-relaxed">
          선택지 목록·값·라벨은 내장 데이터셋 필드의 옵션에서 자동 파생됩니다. 아래 바인딩에서 행 필드를 지정하세요.
        </p>

        {/* 방향 — 가로/세로 나열 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">방향</span>
          <div className="flex gap-1">
            {(
              [
                ['row', '가로'],
                ['column', '세로'],
              ] as const
            ).map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`px-2 py-0.5 text-xs rounded border ${
                  (node.direction ?? 'row') === val
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
                onClick={() =>
                  onPatch({ direction: val === 'row' ? undefined : val } as Partial<ComposerNode>)
                }
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* 선택지 간 간격(px) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">항목 간격</span>
          <NumericField
            value={node.gap ?? undefined}
            min={0}
            step={1}
            placeholder="8"
            onPreview={(v) => onPreview({ gap: v } as Partial<ComposerNode>)}
            onCommit={(v) => onCommit({ gap: v } as Partial<ComposerNode>)}
            onRevert={onRevert}
          />
        </div>

        {/* 박스 크기(pt) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">박스(pt)</span>
          <NumericField
            value={node.boxSize ?? undefined}
            min={1}
            step={0.1}
            placeholder="10"
            toDisplay={pxToPt}
            fromDisplay={ptToPx}
            onPreview={(px) => onPreview({ boxSize: px } as Partial<ComposerNode>)}
            onCommit={(px) => onCommit({ boxSize: px } as Partial<ComposerNode>)}
            onRevert={onRevert}
          />
        </div>

        {/* 박스 모양 — 사각(체크박스)/원형(라디오) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">모양</span>
          <div className="flex gap-1">
            {(
              [
                ['square', '사각'],
                ['circle', '원형'],
              ] as const
            ).map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`px-2 py-0.5 text-xs rounded border ${
                  (node.boxShape ?? 'square') === val
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
                onClick={() =>
                  onPatch({ boxShape: val === 'square' ? undefined : val } as Partial<ComposerNode>)
                }
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* 라벨 수직 정렬 — 위/중/아래 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">수직 정렬</span>
          <div className="flex gap-1">
            {(
              [
                ['top', '위'],
                ['center', '중'],
                ['bottom', '아래'],
              ] as const
            ).map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`px-2 py-0.5 text-xs rounded border ${
                  (node.labelVAlign ?? 'center') === val
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
                onClick={() =>
                  onPatch({ labelVAlign: val === 'center' ? undefined : val } as Partial<ComposerNode>)
                }
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* 박스↔라벨 간격(px) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">라벨 간격</span>
          <NumericField
            value={node.labelGap ?? undefined}
            min={0}
            step={1}
            placeholder="(기본)"
            onPreview={(v) => onPreview({ labelGap: v } as Partial<ComposerNode>)}
            onCommit={(v) => onCommit({ labelGap: v } as Partial<ComposerNode>)}
            onRevert={onRevert}
          />
        </div>

        {/* 테두리 색 + 두께 */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">테두리</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={node.boxBorderColor ?? '#000000'}
            onChange={(e) => onPatch({ boxBorderColor: e.target.value } as Partial<ComposerNode>)}
          />
          {node.boxBorderColor !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => onPatch({ boxBorderColor: undefined } as Partial<ComposerNode>)}
            >
              초기화
            </button>
          )}
          <NumericField
            value={node.boxBorderWidth ?? undefined}
            min={0}
            step={0.5}
            placeholder="1"
            onPreview={(v) => onPreview({ boxBorderWidth: v } as Partial<ComposerNode>)}
            onCommit={(v) => onCommit({ boxBorderWidth: v } as Partial<ComposerNode>)}
            onRevert={onRevert}
          />
        </div>

        {/* 선택 표시 색(체크마크/점) */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">선택 색</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={node.checkedColor ?? '#000000'}
            onChange={(e) => onPatch({ checkedColor: e.target.value } as Partial<ComposerNode>)}
          />
          {node.checkedColor !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => onPatch({ checkedColor: undefined } as Partial<ComposerNode>)}
            >
              초기화
            </button>
          )}
        </div>

        {/* 라벨 스타일 — 폰트/굵기/크기/색 */}
        <div>
          <span className="text-[11px] text-gray-500 block mb-0.5">폰트</span>
          <select
            className={SELECT_CLS}
            value={ls.fontFamily ?? ''}
            onChange={(e) => patchLabelStyle({ fontFamily: e.target.value || undefined })}
          >
            <option value="">(기본)</option>
            {COMPOSER_FONTS.map((f) => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <FontWeightSelect value={ls.fontWeight} onChange={(w) => patchLabelStyle({ fontWeight: w })} boldShortcut />
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">크기(pt)</span>
          <NumericField
            value={ls.fontSize ?? undefined}
            min={1}
            step={0.1}
            placeholder="(기본)"
            toDisplay={pxToPt}
            fromDisplay={ptToPx}
            onPreview={(px) => patchLabelStyleLive({ fontSize: px })}
            onCommit={(px) => patchLabelStyleCommit({ fontSize: px })}
            onRevert={onRevert}
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-14">색상</span>
          <input
            type="color"
            className="h-7 w-10 border border-gray-300 rounded p-0.5"
            value={ls.color ?? '#000000'}
            onChange={(e) => patchLabelStyle({ color: e.target.value })}
          />
          {ls.color !== undefined && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => patchLabelStyle({ color: undefined })}
            >
              초기화
            </button>
          )}
        </div>
      </dd>
    </div>
  );
}

/**
 * 라벨 + 컨트롤 한 줄(바인딩 편집 행).
 * `help`를 주면 라벨 옆에 매뉴얼 `?` 링크가 인라인으로 붙는다(행 높이 불변 — HelpLink 참조).
 */
function BindingRow({
  label,
  help,
  children,
}: {
  label: string;
  help?: ManualLinkKey;
  children: React.ReactNode;
}) {
  return (
    <div>
      {/* help 없는 행은 종전 렌더(block) 그대로 — 도움말이 붙는 행만 인라인 flex로 아이콘을 얹는다. */}
      <span
        className={`${FIELD_LABEL_CLS} mb-0.5 ${help ? 'flex items-center gap-1' : 'block'}`}
      >
        {label}
        {help && <HelpLink topic={help} />}
      </span>
      {children}
    </div>
  );
}

const FORMAT_KINDS: Array<BindingFormat['kind'] | 'none'> = [
  'none',
  'unit',
  'date',
  'url',
  'template',
  'default',
  'join',
  'map',
  'switch',
  'rangeMap',
  'bool',
];

/** format kind 선택 옵션 라벨 — `한글 이름 (코드 값)`. 이름 정본은 bindingFormatLabels.ts. */
function formatKindOptionLabel(kind: BindingFormat['kind'] | 'none'): string {
  return bindingFormatOptionLabel(kind);
}

/**
 * format의 `empty`(결측 시 표시할 placeholder) 값 — 지원 kind에서만 읽는다.
 * union 좁히기로 구현해 새 kind 추가 시 여기 누락돼도 `undefined`(안전 기본)로 떨어진다.
 */
function formatEmptyOf(format: BindingFormat | undefined): string | undefined {
  if (!format) return undefined;
  switch (format.kind) {
    case 'unit':
    case 'date':
    case 'template':
    case 'join':
    case 'switch':
      return format.empty;
    default:
      return undefined;
  }
}

// format kind 선택 시의 기본 BindingFormat(none=undefined). 단일 셀(setFormatKind)·일괄(BulkCellEditor) 공유.
// prev(전환 직전 format)의 empty는 **새 kind도 empty를 지원하면 이어받는다** — kind를 바꿔볼 때마다
// 저작한 placeholder('-' 등)가 조용히 사라지던 문제 방지(다른 하위 필드는 kind별 의미가 달라 초기화).
function buildDefaultFormat(
  kind: BindingFormat['kind'] | 'none',
  prev?: BindingFormat,
): BindingFormat | undefined {
  const empty = formatEmptyOf(prev);
  const carry: { empty?: string } = empty !== undefined ? { empty } : {};
  // 보조 자료 출처 선언도 같은 취지로 이어받는다 — 다만 **새 kind가 스코프를 읽을 때만**(안 읽는
  // kind로 옮기면 죽은 선언이 저장에 남는다). 판정은 formatScopeSources.carryScopeSources 소유.
  const scoped = carryScopeSources(prev, kind);
  const scopeCarry: { scopeFrom?: FormatScopeSource[] } = scoped ? { scopeFrom: scoped } : {};
  switch (kind) {
    case 'none':
      return undefined;
    case 'unit':
      return { kind: 'unit', ...carry, ...scopeCarry };
    case 'date':
      return { kind: 'date', ...carry };
    case 'url':
      return { kind: 'url' };
    case 'template':
      return { kind: 'template', template: '', ...carry, ...scopeCarry };
    case 'default':
      return { kind: 'default', fallback: '' };
    case 'join':
      return { kind: 'join', ...carry };
    case 'map':
      return { kind: 'map', cases: {} };
    case 'switch':
      return { kind: 'switch', on: '', cases: {}, ...carry, ...scopeCarry };
    case 'rangeMap':
      return { kind: 'rangeMap', output: 'value-label', ...scopeCarry };
    case 'bool':
      return { kind: 'bool' };
    default: {
      // 신규 kind 추가 시 로컬 tsc가 누락을 잡도록 exhaustive 가드.
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/**
 * 표 셀들의 **값 스코프 경로 후보** — 셀이 반복영역 안이면 그 영역 소스의 행 필드, 밖이면 표를
 * 감싼 repeat 노드의 행 필드. 표시 형식의 경로 칸(switch on·병기 값·구간 키)이 읽는 스코프가
 * 이 스코프다. 여기에 **양식 전역 변수**를 행 필드 뒤로 합류시킨다.
 *
 * 변수를 합류시키는 근거: resolver가 `template.variables`를 RenderData 최상위에 싣고, 반복 항목은
 * 그 data를 바탕으로 `mergeRepeatScope`한 스코프에서 렌더된다 — 즉 반복 안에서도 변수 키가 그대로
 * 읽힌다. 실제로 DLR 반복영역 셀이 `on: 'assessmentMethod'`(전역 변수)로 분기하는데, 후보가
 * 행 필드뿐이라 그 저작은 경로를 외워 타이핑해야만 가능했다.
 *
 * 여러 셀이 서로 다른 행 스코프에 걸치면 행 필드 후보는 **비운다** — 어느 한쪽 후보를 보여 주면
 * 다른 쪽 셀에는 없는 경로를 고르게 만든다. 변수는 스코프와 무관하게 유효하므로 이때도 남는다.
 * 후보가 하나도 없으면 소비 측이 자유 텍스트 입력으로 degrade한다.
 */
function tableCellRowFieldOptions(
  table: TableNode,
  cells: { row: number; col: number }[],
  template: ReportTemplate,
  catalog: CatalogEntry[],
): HybridKeyOption[] {
  // 선택된 셀이 없으면 저작 대상 자체가 없다 — 변수도 내지 않는다(빈 목록 계약 유지).
  if (cells.length === 0) return [];
  return withVariableKeyOptions(
    tableCellScopeRowFields(table, cells, template, catalog),
    template.variables,
  );
}

/** 위 함수의 행 필드 절반 — 변수 합류 전 순수 행 스코프 후보. */
function tableCellScopeRowFields(
  table: TableNode,
  cells: { row: number; col: number }[],
  template: ReportTemplate,
  catalog: CatalogEntry[],
): HybridKeyOption[] {
  if (cells.length === 0) return [];
  const overs = cells.map((c) => isRepeatRegionCell(table, c.row, c.col)?.over);
  if (overs.some((o) => o !== overs[0])) return [];
  const sources = collectRepeatSources(template.dataDefs, catalog, template.dataSources);
  const regionOver = overs[0];
  if (regionOver !== undefined) {
    return rowFieldPickerOptions(findRepeatSource(sources, { over: regionOver })?.fields);
  }
  const path = findNodePath(template.body, table.id);
  const enclosingRepeat = path ? enclosingRepeatNode(path.slice(0, -1)) : undefined;
  return enclosingRepeat
    ? rowFieldPickerOptions(findRepeatSource(sources, enclosingRepeat)?.fields)
    : [];
}

/**
 * 식별 키 콤보+직접 입력 하이브리드 — bare 바인딩(반복 행 필드 등)의 식별 키를 후보 목록에서 고르거나
 * "직접 입력…"으로 자유 키를 타이핑한다. 동작은 HybridKeyPicker 소유, 여기서는 식별 키 어휘만 정한다.
 */
function BindingKeyPicker({
  value,
  options,
  duplicate,
  onChange,
  onFocus,
  onBlur,
}: {
  value: string;
  options: HybridKeyOption[];
  /** 직접 입력 값이 다른 노드/셀과 중복(값 공유 함정)일 때 true — direct 모드에서만 경고 표시. */
  duplicate?: boolean;
  onChange: (key: string) => void;
  /** 포커스 동안 같은 키를 쓰는 다른 자리를 캔버스에 강조(sharedKeyHighlight) — 그대로 전달. */
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  return (
    <HybridKeyPicker
      value={value}
      options={options}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder="식별 키 직접 입력"
      chooseLabel="— 행 필드 선택 —"
      invalid={duplicate}
      invalidMessage="이미 다른 노드에서 사용 중인 키입니다 — 같은 키는 입력값을 공유합니다"
      hint="행마다 자기 행 값을 읽으므로 다른 표·노드와 키가 같아도 값이 공유되지 않습니다."
    />
  );
}

/**
 * 식별 키를 바꿔 담는다 — 「의도된 공유」 선언(`sharedIntent`)은 **그 키의 관계**에 대한 것이라
 * 키가 실제로 바뀌면 함께 뗀다.
 *
 * 남겨 두면 새 키의 중복 경고가 저작자가 승인한 적 없는 이유로 조용해진다. 키 변경 경로는 넷
 * (콤보 선택·직접 입력·「자동으로」·출처 변경 시 자동 채움)이라 여기 한 자리로 모은다 —
 * 한 경로만 빠져도 그 경로로만 유령 선언이 남아 재현이 어려워진다.
 */
function withBindingKey(binding: Binding, bindingKey: string): Binding {
  const next: Binding = { ...binding, bindingKey };
  if (binding.bindingKey !== bindingKey) delete next.sharedIntent;
  return next;
}

/**
 * 노드 ↔ 카탈로그 바인딩 편집기 (3-binding).
 *
 * onPatch는 patch를 노드에 얕은 병합하므로, binding 변경은 **항상 전체 객체**를 새로 만들어 전달한다.
 * 레거시 source/api는 읽기 전용 표시만 한다(편집은 카탈로그 경로로만).
 *
 * export는 rtl 테스트용(nodeKindHasBindingEditor와 같은 전례) — 앱 코드의 진입은 이 파일 안
 * 호출처들뿐이다.
 */
export function BindingEditor({
  node,
  onPatch,
  catalog,
  hideRemoveButton,
  sourcePlaceholder,
  hideSelectPath,
  selectMix,
  usedKeys,
  keyOptions,
  rowScopeFieldBinding,
  rowScopeFieldSource,
  onGoToDataCard,
  editTargetId,
  selfOwnerId,
}: {
  node: ComposerNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  catalog: CatalogEntry[];
  /** true면 "바인딩 제거" 버튼 미렌더 — draft(임시 입력) 컨텍스트(BulkBindingAssignPanel)에서 셀 미반영 오해 방지. */
  hideRemoveButton?: boolean;
  /** 출처 콤보 placeholder override — 일괄 패널에서 출처가 혼합일 때 "— 혼합 —" 표시용(기본 "출처 검색..."). */
  sourcePlaceholder?: string;
  /**
   * true면 select 경로 picker(CatalogPathPicker 행)를 숨기고 자동 매핑 안내로 대체.
   * 묶음 모드(BulkBindingAssignPanel grouped)에서 사용 — 경로는 선택 셀 영역에 행=항목/열=필드로
   * 자동 산출되므로 단일 draft.select로 표현 불가(혼합으로 복원되면 "전체 나열" 오표시 방지).
   * 출처 콤보 등 나머지 BindingEditor 기능은 유지.
   */
  hideSelectPath?: boolean;
  /**
   * 일괄 바인딩에서 출처는 같은데 셀마다 select 경로가 달라 commonSelect가 undefined일 때 전달.
   * picker가 select 세그먼트(레벨) 단위로 비교해, 일치하는 레벨은 선택을 유지하고 갈라지는
   * 레벨만 '-'/"— 혼합 —"로 표시한다(한 레벨을 고치면 전체 셀에 통일).
   */
  selectMix?: SelectMix;
  /**
   * 같은 템플릿의 다른 노드/셀이 이미 쓰는 bindingKey 집합(현재 편집 대상 자신은 제외).
   * 식별 키 입력값이 이 집합에 있으면 "값 공유" 함정 경고를 표시한다. draft/일괄 컨텍스트에서는
   * 키가 대상별로 재산출되므로 전달하지 않는다(경고 무의미).
   */
  usedKeys?: Set<string>;
  /**
   * 식별 키 콤보 후보(반복 행 필드 등). **bare 바인딩(catalogKey 없음)일 때만** 식별 키 입력을
   * 콤보+직접 입력 하이브리드(BindingKeyPicker)로 바꾼다 — 카탈로그 바인딩의 식별 키는 저장 키
   * (rename)라 후보 선택이 출처 변경으로 오독될 수 있어 기존 텍스트 입력 유지.
   *
   * 표시 형식(FormatFields)의 경로 칸(병기 값·구간 키)에는 **bare 여부와 무관하게** 그대로 넘긴다 —
   * 그 칸들이 읽는 스코프가 이 후보와 같은 행 스코프라, 카탈로그 바인딩 셀에서도 후보가 유효하다.
   */
  keyOptions?: HybridKeyOption[];
  /**
   * 행 스코프 필드 바인딩 판정(`isRowScopeFieldBinding` — 반복 단위 안 bare 바인딩의 키가 반복
   * 소스의 행 필드 선언에 있음). true면 "값 갱신"(lifecycle) select를 비활성하고 사유를 표시한다
   * — 행 값은 mergeRepeatScope 행 객체에서 직접 읽혀 snapshot/live 분기를 타지 않으므로, 선택을
   * 살려 두면 "작성 시 고정"이 실제 동작이라는 거짓 신호가 된다(2026-09-04 TBM 일시 오해 사례).
   * 저장값은 건드리지 않는다(기존 템플릿 데이터 불변 — 표시만). 판정 재료(반복 조상·소스)가 없는
   * 컨텍스트(일괄 draft·계산 입력·조건 바인딩)는 미전달 → 기존 활성 유지.
   */
  rowScopeFieldBinding?: boolean;
  /**
   * 행 스코프 필드 바인딩의 **출처 정면 표시** 재료(`rowScopeFieldSourceInfo` + 카드 이동 키) —
   * 이 갈래는 출처(카탈로그) 콤보가 비어 보여 "바인딩이 아닌 것 같다"로 오독된다(2026-09-05
   * TBM 담당자 확인 사례). 전달되면 요약 아래에 "값은 「목록」의 행 필드 「필드」에서 옵니다"
   * 안내 + (cardKey·onGoToDataCard 있으면) 「소스 편집 →」을 낸다. `rowScopeFieldBinding`
   * (lifecycle 게이트)과 별개 축 — 판정 재료가 없는 컨텍스트(일괄 draft 등)는 미전달.
   */
  rowScopeFieldSource?: RowScopeFieldSourceInfo & { cardKey?: string };
  /** 「소스 편집 →」 이동 콜백(handleGoToDataCard) — rowScopeFieldSource.cardKey와 짝. */
  onGoToDataCard?: (key: string) => void;
  /**
   * 편집 대상의 **안정** 식별자 — BindingKeyPicker 리마운트 key(대상 변경 시 direct 모드 초기화).
   * 셀 컨텍스트(CellBindingEditor)는 fakeNode.id가 bindingKey 파생이라 타이핑마다 바뀌므로
   * 좌표 기반 안정 키를 여기로 전달한다. 미지정 시 node.id(실노드는 안정).
   */
  editTargetId?: string;
  /**
   * 이 편집기가 대표하는 캔버스 자리의 소유자 id(`node:<id>` / `cell:<tableId>:<row>:<col>`).
   * 식별 키 칸에 포커스가 있는 동안 **같은 키를 쓰는 다른 자리**를 캔버스에 강조하는 데 쓴다
   * (자기 자신은 이 값으로 제외 — 선택 링과 겹치면 관계가 안 읽힌다).
   *
   * 초안/일괄 패널·계산 입력처럼 **캔버스에 대응 자리가 없는 가짜 노드** 컨텍스트에서는 넘기지
   * 않는다. 넘기면 있지도 않은 노드를 가리키게 된다.
   */
  selfOwnerId?: string;
}) {
  const b = node.binding;

  // 식별 키 행 펼침 — 자동값 그대로면 접어 둔다(방금 고른 조회·필드의 재표시라 대개 소음).
  // ⚠️ 훅은 아래 early return보다 위에 있어야 한다(조건부 훅 금지).
  const [keyRowOpen, setKeyRowOpen] = useState(false);
  // 식별 키 칸 포커스 → 같은 키를 쓰는 다른 자리 캔버스 강조(selfOwnerId 없으면 no-op).
  // b가 아직 없을 수 있으므로(바인딩 추가 전) 빈 키로 호출 — 빈 키는 수집기가 빈 배열로 돌린다.
  const sharedKeyFocusProps = useSharedKeyFocus(selfOwnerId, b?.bindingKey ?? '');

  // 바인딩 없음 → 추가 버튼. 행 필드 후보(keyOptions)가 있으면 빈 키로 심어 하이브리드 콤보가
  // "— 행 필드 선택 —"으로 바로 열리게 한다(node.id 시드는 후보에 없어 직접 입력 모드로 시작해
  // 콤보 저작 목적을 역행 — 리뷰 W1).
  if (!b) {
    return (
      <div className="space-y-1">
        <p className="text-gray-400 text-xs">없음 (정적 노드)</p>
        <button
          type="button"
          onClick={() =>
            onPatch({
              binding: {
                bindingKey: keyOptions && keyOptions.length > 0 ? '' : node.id,
                lifecycle: 'snapshot',
              },
            } as Partial<ComposerNode>)
          }
          className="text-xs px-2 py-1 border border-gray-300 rounded text-brand-600 hover:bg-brand-50"
        >
          + 바인딩 추가
        </button>
      </div>
    );
  }

  // 전체 binding 객체를 만들어 patch(부분 갱신 금지).
  const patchBinding = (next: Binding) => {
    onPatch({ binding: next } as Partial<ComposerNode>);
  };

  const selectedEntry = catalog.find((e) => e.key === b.catalogKey);

  // bindingKey 자동값 계산(catalogKey [+ select] 기반).
  const autoKeyFor = (catalogKey: string | undefined, select: string | undefined): string =>
    catalogKey ? (select ? `${catalogKey}.${select}` : catalogKey) : '';
  const suggestedKey = autoKeyFor(b.catalogKey, b.select) || b.bindingKey;

  // catalogKey/select 변경 시 bindingKey 자동 채움.
  // 단 사용자가 직접 만진 적이 있으면(=현재 값이 "직전 자동값"과 다르고 비어있지도 않으면) 덮어쓰지 않는다.
  const applyAutoBindingKey = (next: Binding): Binding => {
    const prevAuto = autoKeyFor(b.catalogKey, b.select);
    const isUntouched = b.bindingKey === '' || b.bindingKey === prevAuto;
    if (!isUntouched) return next;
    const nextAuto = autoKeyFor(next.catalogKey, next.select);
    return nextAuto ? withBindingKey(next, nextAuto) : next;
  };

  // (b) 한글 바인딩 요약.
  const summary = buildBindingSummary(b, catalog);

  const formatKind: BindingFormat['kind'] | 'none' = b.format?.kind ?? 'none';

  const setFormatKind = (kind: BindingFormat['kind'] | 'none') => {
    patchBinding({ ...b, format: buildDefaultFormat(kind, b.format) });
  };

  // 식별 키 중복 경고 — 현재 입력 키를 같은 템플릿의 다른 노드/셀이 이미 쓰고 있으면 값이 공유된다.
  const keyIsDuplicate = !!b.bindingKey && !!usedKeys?.has(b.bindingKey);

  // 지금 키가 자동값 그대로인가(= 조회·필드 선택의 재표시라 접어도 잃는 정보가 없다).
  const keyIsAutoValue = !!b.bindingKey && b.bindingKey === autoKeyFor(b.catalogKey, b.select);

  return (
    <div className="space-y-2">
      {/* (b) 한글 바인딩 요약 readout */}
      <div
        className={`rounded border px-2 py-1.5 text-xs ${
          summary.legacy
            ? 'border-gray-200 bg-gray-50 text-gray-500'
            : 'border-brand-100 bg-brand-50/60 text-gray-700'
        }`}
        title={[summary.keys.catalogKey, summary.keys.select, summary.keys.bindingKey]
          .filter(Boolean)
          .join(' · ')}
      >
        <span aria-hidden className="mr-1">
          🔗
        </span>
        {summary.parts.map((part, i) => (
          <span key={i}>
            {i > 0 && <span className="text-gray-300 mx-0.5">·</span>}
            {part}
          </span>
        ))}
        {/* 개발자용 보조 키(작게/모노/회색) */}
        {(summary.keys.catalogKey || summary.keys.bindingKey) && (
          <div className="mt-0.5 text-[10px] text-gray-400 font-mono break-all">
            {summary.keys.catalogKey && <span>{summary.keys.catalogKey}</span>}
            {summary.keys.select && <span> · {summary.keys.select}</span>}
            {summary.keys.bindingKey && <span> → {summary.keys.bindingKey}</span>}
          </div>
        )}
      </div>

      {/* 행 스코프 필드 바인딩 — 출처 콤보가 비는 갈래라 출처를 여기서 정면으로 말한다. */}
      {rowScopeFieldSource && (
        <div className="rounded border border-sky-100 bg-sky-50/60 px-2 py-1.5 text-[11px] text-gray-600 leading-snug">
          이 칸은 시스템 조회(출처)를 따로 갖지 않습니다 — 값은{' '}
          <b>「{rowScopeFieldSource.sourceLabel}」</b> 목록의 행 필드{' '}
          <b>「{rowScopeFieldSource.fieldLabel}」</b>에서 옵니다.
          {rowScopeFieldSource.cardKey !== undefined && onGoToDataCard && (
            <span className="ml-1.5">
              <DataSourceEditLink
                onGo={() => onGoToDataCard(rowScopeFieldSource.cardKey as string)}
              />
            </span>
          )}
        </div>
      )}

      <BindingRow label="출처">
        {/* 공통 코어 CatalogEntryPicker(P4 이관) — legacyHidden 제외·'기존 값(보존)' 덧댐·ko 정렬은
            코어가 보장. 파라미터 인라인 블록은 아래 별도 삽입처(표시 형식·식별 키 뒤)가 이 편집기의
            정본 위치라 코어 자동 렌더는 끈다(paramsBlock={false} — 블록 위치 UX 불변). */}
        <CatalogEntryPicker
          entries={catalog}
          value={b.catalogKey}
          allowClear
          placeholder={sourcePlaceholder ?? '출처 검색...'}
          paramsBlock={false}
          onChange={(v, entry) => {
            // 출처를 비우면(allowClear) 바인딩 자체를 제거한다 — catalogKey만 비운 빈 binding이
            // 셀에 잔존해 '{}' placeholder로 남는 것을 방지(바인딩 해제 = binding undefined).
            if (!v) {
              onPatch({ binding: undefined } as Partial<ComposerNode>);
              return;
            }
            // 같은 출처 재선택 no-op은 코어 가드가 보장 — 여기 도달하면 출처가 실제로 바뀐 것.
            // entry마다 경로가 다르므로 catalogKey 변경 시 select 초기화. 단 배열
            // (cardinality='list') 출처면 '특정 항목 인덱스 0'으로 시작 → 인덱스 입력 즉시 노출.
            const initSelect = entry?.cardinality === 'list' ? '0' : undefined;
            patchBinding(applyAutoBindingKey({ ...b, catalogKey: v, select: initSelect }));
          }}
        />
      </BindingRow>

      {hideSelectPath ? (
        <div className="rounded border border-brand-100 bg-brand-50/60 px-2 py-1.5 text-[11px] text-gray-600">
          묶음 모드: 출처(배열)만 지정하면 선택한 셀 영역에 자동 매핑됩니다 (행=항목, 열=필드).
        </div>
      ) : (
        <BindingRow label="어떤 데이터를 넣을까요?" help="binding">
          <CatalogPathPicker
            entry={selectedEntry}
            value={b.select}
            disabled={!b.catalogKey}
            selectMix={selectMix}
            onChange={(v) => patchBinding(applyAutoBindingKey({ ...b, select: v }))}
          />
        </BindingRow>
      )}

      <BindingRow label="표시 형식" help="bindingFormat">
        <select
          value={formatKind}
          onChange={(e) => setFormatKind(e.target.value as BindingFormat['kind'] | 'none')}
          className={SELECT_CLS}
        >
          {FORMAT_KINDS.map((k) => (
            <option key={k} value={k}>
              {formatKindOptionLabel(k)}
            </option>
          ))}
        </select>
      </BindingRow>

      {/* 형식 상세 입력 — 표시 형식 select 바로 아래 배치. 경로 칸(병기 값·구간 키)의 후보는
          식별 키와 같은 행 스코프라 같은 목록을 그대로 넘긴다. */}
      <FormatFields
        format={b.format}
        onChange={(format) => patchBinding({ ...b, format })}
        catalog={catalog}
        keyOptions={keyOptions}
      />

      {/* 식별 키 — bare 바인딩 + 후보(keyOptions) 있으면 콤보+직접 입력 하이브리드(행 필드 저작),
          그 외(카탈로그 바인딩=저장 키 rename)는 기존 텍스트 입력. 중복 경고는 텍스트 입력에서만
          (행 필드는 행 scope에서 값이 분리되므로 전역 중복 경고가 오탐). */}
      {!b.catalogKey && keyOptions && keyOptions.length > 0 ? (
        <BindingRow label="식별 키 (행 필드)" help="bindingKey">
          <BindingKeyPicker
            key={editTargetId ?? node.id}
            value={b.bindingKey}
            options={keyOptions}
            duplicate={keyIsDuplicate}
            onChange={(key) => patchBinding(withBindingKey(b, key))}
            {...sharedKeyFocusProps}
          />
          {/* 사용처 목록은 콤보 안이 아니라 키 행 아래에 둔다 — HybridKeyPicker는 경고를 direct
              모드에서만 렌더하므로, 여기 두면 목록 모드에서도 같은 어포던스가 유지된다.
              경고 게이트 없이 항상 — 상호배타 슬롯만 공유하는 키는 경고가 억제되지만 값 공유
              관계는 보여야 한다(사용처 0곳이면 컴포넌트가 스스로 안 그린다). */}
          <SharedKeyUsageList bindingKey={b.bindingKey} selfOwnerId={selfOwnerId} />
        </BindingRow>
      ) : keyIsAutoValue && !keyIsDuplicate && !keyRowOpen ? (
        // 자동값 그대로 = 조작할 일이 드문 행 → 한 줄 회색 요약으로 접는다. 값 공유(별칭)나
        // 중복 경고처럼 **판단이 필요한 상태**에서는 아래 분기로 항상 펼쳐진다.
        <div className="flex items-center gap-1 text-[11px] text-gray-400 leading-snug">
          <span className="font-mono">식별 키: {b.bindingKey} (자동)</span>
          <button
            type="button"
            onClick={() => setKeyRowOpen(true)}
            className="rounded border border-gray-200 px-1 text-gray-500 hover:bg-gray-50"
            title="식별 키를 직접 지정합니다 — 다른 자리와 같은 키를 쓰면 값이 공유됩니다"
          >
            변경
          </button>
        </div>
      ) : (
        <BindingRow label={keyIsAutoValue ? '식별 키 (자동)' : '식별 키 (직접 지정)'} help="bindingKey">
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={b.bindingKey}
              placeholder={suggestedKey}
              onChange={(e) => patchBinding(withBindingKey(b, e.target.value))}
              className={
                keyIsDuplicate
                  ? `${INPUT_CLS} border-red-400 focus:ring-red-400`
                  : INPUT_CLS
              }
              aria-invalid={keyIsDuplicate || undefined}
              title="같은 키를 쓰는 다른 자리는 값이 공유됩니다 — 포커스를 두면 그 자리가 캔버스에 표시됩니다."
              {...sharedKeyFocusProps}
            />
            {/* 수동 지정에서 자동값으로 되돌리는 유일한 경로 — 없으면 한 번 손댄 키는 영영 수동으로
                남는다(자동 채움은 "손댄 적 없음"일 때만 덮어쓰므로). 이미 자동값이면 되돌릴 것이
                없으므로 대신 [접기]를 준다 — 펼쳐 놓고 안 바꿨을 때 돌아갈 길이 필요하다. */}
            {autoKeyFor(b.catalogKey, b.select) !== '' &&
              (keyIsAutoValue ? (
                !keyIsDuplicate && (
                  <button
                    type="button"
                    onClick={() => setKeyRowOpen(false)}
                    className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                    title="자동값 그대로입니다 — 한 줄로 접습니다"
                  >
                    접기
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    patchBinding(withBindingKey(b, autoKeyFor(b.catalogKey, b.select)));
                    setKeyRowOpen(false);
                  }}
                  className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                  title={`자동값으로 되돌립니다: ${autoKeyFor(b.catalogKey, b.select)}`}
                >
                  자동으로
                </button>
              ))}
          </div>
          {keyIsDuplicate && (
            <p className="mt-0.5 text-[11px] text-red-600 leading-snug">
              이미 다른 노드에서 사용 중인 키입니다 — 같은 키는 입력값을 공유합니다
            </p>
          )}
          {/* 경고 게이트 없이 항상 — 위 콤보 분기와 같은 이유. */}
          <SharedKeyUsageList bindingKey={b.bindingKey} selfOwnerId={selfOwnerId} />
        </BindingRow>
      )}

      {/* 「의도된 공유」 선언 — 식별 키 세 갈래(콤보·접힘 요약·텍스트) **바깥**에 한 번만 둔다.
          중복일 때 접힘 갈래는 걸리지 않으므로(keyIsAutoValue && !keyIsDuplicate) 한 자리로 두
          갈래를 다 덮는다.

          게이트가 keyIsDuplicate인 것은 배지 경고와 **같은 축**(collectUsedBindingKeys)을 쓰기
          위해서다. SharedKeyUsageList 안에 두지 않는 이유도 같다 — 그 목록은 라디오 그룹·배타
          슬롯처럼 경고가 뜨지 않는 공유까지 보여 주는 가시화라, 거기 두면 끌 경고가 없는 자리에
          "경고 끄기"를 내놓게 된다. */}
      {keyIsDuplicate && (
        <label className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-600">
          <input
            type="checkbox"
            checked={b.sharedIntent === true}
            onChange={(e) =>
              patchBinding(
                // 해제는 필드를 **없앤다** — false 저장 금지(미지정과 같은 뜻인데 값이 두 벌이 되면
                // 템플릿 diff가 의미 없이 흔들린다. shared Binding.sharedIntent doc 규약).
                e.target.checked
                  ? { ...b, sharedIntent: true }
                  : (() => {
                      const next = { ...b };
                      delete next.sharedIntent;
                      return next;
                    })(),
              )
            }
            className="mt-0.5 shrink-0"
          />
          <span title="같은 키를 쓰는 자리 전부 체크하면 경고가 꺼집니다. 나중에 새 자리가 추가되면 다시 알려드립니다.">
            의도된 공유
          </span>
        </label>
      )}

      <BindingRow label="값 갱신">
        <select
          value={b.lifecycle}
          onChange={(e) => patchBinding({ ...b, lifecycle: e.target.value as Binding['lifecycle'] })}
          className={SELECT_CLS}
          disabled={rowScopeFieldBinding}
        >
          <option value="snapshot">작성 시 고정</option>
          <option value="live">실시간</option>
        </select>
        {rowScopeFieldBinding && (
          <p className="mt-0.5 text-[11px] text-gray-500 leading-snug">
            이 값은 목록(반복 소스)의 행에서 읽습니다 — 작성 시 고정/실시간 구분이 적용되지
            않으며, 값의 신선도는 목록 데이터가 오는 경로가 정합니다.
          </p>
        )}
      </BindingRow>

      {/* 이 출처가 저작자에게 받는 파라미터를 그 자리에서 지정 — 값은 양식 탭 전역 편집기와 같은
          `template.catalogParams`에 쓴다(진입점만 추가). 저작 대상이 없으면 렌더되지 않는다. */}
      <CatalogParamInlineBlock catalogKey={b.catalogKey} entry={selectedEntry} />

      {/* 레거시(읽기 전용) */}
      {(b.source !== undefined || b.api !== undefined) && (
        <p className="text-[11px] text-gray-400 break-all">
          (레거시:{' '}
          {b.source !== undefined && <span>source={b.source} </span>}
          {b.api?.endpoint !== undefined && <span>api.endpoint={b.api.endpoint} </span>}
          {b.api?.select !== undefined && <span>api.select={b.api.select}</span>})
        </p>
      )}

      {!hideRemoveButton && (
        <button
          type="button"
          onClick={() => onPatch({ binding: undefined } as Partial<ComposerNode>)}
          className="text-xs px-2 py-1 border border-gray-300 rounded text-red-600 hover:bg-red-50"
        >
          바인딩 제거
        </button>
      )}
    </div>
  );
}

/**
 * 카탈로그 **entry 안**의 경로를 읽는다(구간 기준표 rangesFrom 전용).
 *
 * 엔진 쪽 짝은 `resolveCatalogScopeValue`로, 단수 entry가 resolvedRows 채널에서 배열로 감싸져 올 수
 * 있어 첫 항목에서 읽는다 — 그 관용을 흉내 내는 자리다.
 *
 * ⚠️ **스코프 경로(unitPath·switch.on·rangesKey·병기 값)에는 쓰지 말 것** — 그쪽 엔진은 관용 없는
 * `getByPath(withFormatScope(format, scope), path)`이고, 그 짝은 `formatPreview.readFormatPath`다.
 * 여기 관용을 스코프 경로에 적용하면 **미리보기엔 보이는데 렌더는 빈 칸**이 된다.
 */
function readPreviewPath(root: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, seg) => {
    if (acc === null || acc === undefined) return undefined;
    const cur = Array.isArray(acc) ? acc[0] : acc;
    return typeof cur === 'object' ? (cur as Record<string, unknown>)[seg] : undefined;
  }, root);
}

/** 미리보기 한 줄의 공통 결 — 안내는 흐리게, 실제 값은 진하게(구간·병기 미리보기와 같은 결). */
const PREVIEW_NOTE_CLS = 'text-[10px] text-gray-400 leading-snug';
const PREVIEW_VALUE_CLS = 'text-[10px] text-gray-500 leading-snug';

/**
 * 경로 하나의 해소 결과를 사람 말로 — `unresolved`와 `empty`를 **다른 문구로** 낸다.
 *
 * 그 둘을 한 문구로 뭉개면(엔진 toText가 그렇게 뭉갠다) 오타가 "정상적인 빈 값"으로 위장해, 정작
 * 저작자가 고쳐야 할 때 고칠 단서를 못 받는다. 그게 이 미리보기를 만든 이유다(줄기 P1).
 */
function PathPreviewText({ preview }: { preview: PathPreview }) {
  if (preview.state === 'resolved') {
    return <span className="text-gray-700">{preview.text}</span>;
  }
  return (
    <span className="text-gray-400">
      {preview.state === 'empty' ? '(값이 비어 있음)' : '(이 경로를 미리보기 채널에서 못 읽음)'}
    </span>
  );
}

/**
 * unit 형식이 **실제로 붙일 단위**를 보여준다 — 경로만 적고 끝내면 맞게 적었는지 알 수 없다
 * (구간·병기 미리보기와 같은 취지. 줄기 P1).
 *
 * 함께 드러내는 것이 하나 더 있다: `unitPath`와 고정 `unit`을 **둘 다** 적으면 엔진은 경로만 보므로
 * 고정값이 죽은 저작이 된다(해소 실패 시 고정값으로 폴백하지 **않는다** — 단위가 아예 안 붙는다).
 */
function UnitPreview({
  format,
  previewValues,
}: {
  format: Extract<BindingFormat, { kind: 'unit' }>;
  previewValues?: Record<string, unknown>;
}) {
  const r = resolveUnitPreview(format, previewValues);
  if (r.source === 'none') {
    return <p className={PREVIEW_NOTE_CLS}>단위 미지정 — 값만 그대로 나옵니다.</p>;
  }
  if (r.source === 'static') {
    return (
      <p className={PREVIEW_VALUE_CLS}>
        적용 단위: <span className="text-gray-700">{r.unit}</span> (고정값)
      </p>
    );
  }
  return (
    <div className="space-y-0.5">
      <p className={PREVIEW_VALUE_CLS}>
        적용 단위: <PathPreviewText preview={r.unit} />
      </p>
      {r.unit.state !== 'resolved' && (
        <p className={PREVIEW_NOTE_CLS}>
          해소되지 않으면 단위가 <b>아예 붙지 않습니다</b>(값만 나옵니다).
        </p>
      )}
      {r.staticIgnored !== undefined && (
        <p className="text-[10px] text-amber-700 leading-snug">
          위 경로가 지정돼 있어 고정 unit(<code>{r.staticIgnored}</code>)은 <b>쓰이지 않습니다</b> —
          고정값을 쓰려면 경로를 비우세요.
        </p>
      )}
    </div>
  );
}

/**
 * switch 형식이 **지금 고를** 케이스를 보여준다.
 *
 * 이 형식의 조용한 실패가 가장 고약하다 — `on` 경로를 잘못 적어도 엔진은 멈추지 않고 **빈 문자열을
 * 케이스 키로** 써서 default로 떨어진다. 증상이 "분기가 안 바뀐다"라서 저작자는 `cases`를 의심하고
 * 정작 원인인 경로는 끝까지 안 본다.
 */
function SwitchCasePreview({
  format,
  previewValues,
}: {
  format: Extract<BindingFormat, { kind: 'switch' }>;
  previewValues?: Record<string, unknown>;
}) {
  const r = resolveSwitchPreview(format, previewValues);
  const tplPreview =
    r.template === undefined ? undefined : resolveTemplatePreview(r.template, format, previewValues);
  return (
    <div className="mt-1 space-y-0.5 rounded border border-gray-200 bg-gray-50 p-1.5">
      <p className={PREVIEW_VALUE_CLS}>
        분기 기준 값: <PathPreviewText preview={r.on} />
      </p>
      {r.on.state !== 'resolved' && (
        <p className={PREVIEW_NOTE_CLS}>
          못 읽으면 <b>빈 문자열</b>을 케이스 키로 써서 아래로 떨어집니다 — 분기가 안 바뀌면 cases가
          아니라 이 경로를 먼저 보세요.
        </p>
      )}
      <p className={PREVIEW_VALUE_CLS}>
        {r.matched ? '선택되는 케이스' : r.template === undefined ? '선택' : '미매칭 → default'}:{' '}
        {r.template === undefined ? (
          <span className="text-gray-400">
            {r.fallsBackToEmpty ? `없음 → empty("${format.empty ?? ''}")로 대체` : '없음 → 빈 칸'}
          </span>
        ) : (
          <span className="text-gray-700">{r.template === '' ? '(빈 문구)' : r.template}</span>
        )}
      </p>
      {tplPreview && !tplPreview.staticOnly && (
        <p className={PREVIEW_VALUE_CLS}>
          치환 결과: <span className="text-gray-700 whitespace-pre-wrap">{tplPreview.result}</span>
        </p>
      )}
    </div>
  );
}

/**
 * template 형식의 **치환 결과**를 보여준다 — `{키}`를 외워 적는 칸이라 오타가 빈칸으로만 드러난다.
 *
 * ⚠️ `{value}`(이 칸의 값)는 자리표로 남는다 — 미리보기 채널은 해소된 prefill 묶음이고 이 패널은
 * "이 칸의 값"을 받지 않는다(formatPreview 머리말의 "못 재는 축"). 지어낸 값을 끼우면 거짓 안내가
 * 되므로 **무엇이 안 보이는지 그 자리에서 말한다**.
 */
function TemplateResultPreview({
  template,
  format,
  previewValues,
}: {
  template: string;
  format: BindingFormat;
  previewValues?: Record<string, unknown>;
}) {
  const r = resolveTemplatePreview(template, format, previewValues);
  if (r.staticOnly) {
    return (
      <p className={PREVIEW_NOTE_CLS}>
        참조(<code>{'{키}'}</code>)가 없는 정적 문구입니다 — 값과 무관하게 이 문구가 나옵니다.
      </p>
    );
  }
  const missing = r.refs.filter((x) => !x.isValue && x.text?.state !== 'resolved');
  return (
    <div className="mt-1 space-y-0.5 rounded border border-gray-200 bg-gray-50 p-1.5">
      <p className={PREVIEW_VALUE_CLS}>
        치환 결과: <span className="text-gray-700 whitespace-pre-wrap">{r.result}</span>
      </p>
      {r.refs.some((x) => x.isValue) && (
        <p className={PREVIEW_NOTE_CLS}>
          <code>{VALUE_PLACEHOLDER}</code> 자리에는 이 칸의 값이 들어갑니다(저작 화면에는 그 값이
          없어 자리표로 둡니다).
        </p>
      )}
      {missing.length > 0 && (
        <p className={PREVIEW_NOTE_CLS}>
          못 읽은 참조: {missing.map((x) => `{${x.key}}`).join(' · ')} — 빈 문자열로 치환됩니다
          (경로 오타·다른 스코프일 수 있습니다).
        </p>
      )}
    </div>
  );
}

/**
 * map 형식이 **케이스에 없는 값**을 만났을 때 하는 일을 보여준다.
 *
 * ⚠️ map은 "현재 값 매칭"을 보일 수 없다 — 입력이 경로가 아니라 바인딩 값 자체라 이 채널에 없다
 * (formatPreview 머리말). 대신 조용한 실패 하나를 드러낸다: `fallback`이 **빈 문자열**이면 미매칭이
 * 빈 칸으로 나와 "값 없는 칸"과 구별되지 않고, 열 하나가 통째로 빈 것처럼 보인다.
 */
function MapMissNote({ format }: { format: Extract<BindingFormat, { kind: 'map' }> }) {
  const r = resolveMapMissBehavior(format);
  if (r.kind === 'blank') {
    return (
      <p className="text-[10px] text-amber-700 leading-snug">
        케이스에 없는 값이 오면 <b>빈 칸</b>이 됩니다 — 값이 없는 칸과 구별되지 않습니다(fallback을
        비워 둔 결과). 흔적을 남기려면 fallback을 지우거나(원값 표시) 문구를 적으세요.
      </p>
    );
  }
  return (
    <p className={PREVIEW_NOTE_CLS}>
      케이스에 없는 값이 오면{' '}
      {r.kind === 'passthrough' ? (
        <b>원값이 그대로</b>
      ) : (
        <>
          <span className="text-gray-700">{r.label}</span>이
        </>
      )}{' '}
      나옵니다.
    </p>
  );
}

/**
 * 구간 판정이 **실제로 쓰는 범위**를 그대로 보여준다 — 구간마다 min~max·라벨·색.
 *
 * 출처(rangesFrom/rangesKey/ranges)를 고르는 것만으로는 "어떤 값이 적용되는지" 알 수 없다는
 * 제보(2026-08-05)에 대한 답이다. 해소 우선순위는 렌더(rc matchRangeCriterion)와 **같은 순서**로
 * 흉내 낸다: rangesFrom(선언 출처) → rangesKey(스코프 경로) → ranges(정적).
 * 미리보기 값이 없거나(컨텍스트 미선택) 배열이 아니면 안내 문구만 — 가짜 구간을 지어내지 않는다.
 */
function RangeCriteriaPreview({
  format,
  previewValues,
}: {
  format: Extract<BindingFormat, { kind: 'rangeMap' }>;
  previewValues?: Record<string, unknown>;
}) {
  const resolved = (() => {
    // ⚠️ 폴백 판정은 엔진(matchRangeCriterion)과 **같은 조건**이어야 한다: 각 축은 **비지 않은
    // 배열**일 때만 "해소됨"이다. 빈 배열([])도 truthy라 그냥 채택하면 미리보기만 "선언 출처 ·
    // 구간 0개"로 멈추고 엔진은 다음 축으로 내려가 — 저작자가 보는 것과 인쇄되는 것이 갈린다
    // (엔진 주석이 적듯 `riskLevelCriteria`는 non-null Json이라 []가 실제로 실려 온다).
    const nonEmptyArray = (v: unknown): unknown[] | undefined =>
      Array.isArray(v) && v.length > 0 ? v : undefined;
    if (format.rangesFrom?.catalogKey && previewValues) {
      // rangesFrom은 entry 안 경로라 readPreviewPath(관용)가 맞는 짝이다. 다만 카탈로그 키 자체가
      // 선언으로 얹힌 이름일 수 있어 스코프는 얹은 뒤의 것을 본다(엔진도 얹은 스코프를 넘긴다).
      const scope = (readFormatScopeRaw(format, format.rangesFrom.catalogKey, previewValues) ??
        previewValues[format.rangesFrom.catalogKey]) as unknown;
      const v = format.rangesFrom.select ? readPreviewPath(scope, format.rangesFrom.select) : scope;
      const rows = nonEmptyArray(v);
      if (rows) return { rows, from: '선언 출처' };
    }
    if (format.rangesKey && previewValues) {
      // ⚠️ 스코프 경로는 엔진과 같은 관용 없는 읽기(readFormatScopeRaw) — 종전엔 readPreviewPath의
      // 세그먼트별 배열 관용을 써서, 엔진이 못 읽는 경로를 미리보기만 읽어 주고 있었다.
      const rows = nonEmptyArray(readFormatScopeRaw(format, format.rangesKey, previewValues));
      if (rows) return { rows, from: 'rangesKey 경로' };
    }
    if (format.ranges?.length) return { rows: format.ranges as unknown[], from: '정적 구간' };
    return null;
  })();

  if (!resolved) {
    return (
      <p className="text-[10px] text-gray-400">
        적용 구간 미리보기: 컨텍스트(조직/현장 등)를 고르면 실제 구간이 여기 표시됩니다.
      </p>
    );
  }
  return (
    <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-1.5">
      <p className="mb-1 text-[10px] text-gray-500">적용 구간 ({resolved.from})</p>
      <div className="flex flex-wrap gap-1">
        {resolved.rows.slice(0, 12).map((raw, i) => {
          const r = (raw ?? {}) as { min?: unknown; max?: unknown; label?: unknown; color?: unknown };
          const hex = typeof r.color === 'string' && /^#[0-9a-f]{6}$/i.test(r.color) ? r.color : undefined;
          return (
            <span
              key={`${String(r.label ?? '')}-${i}`}
              className="text-[10px]"
              // 배지와 같은 결(색은 구간이 소유) — 색 없으면 중립 테두리.
              style={{
                display: 'inline-block',
                padding: '1px 6px',
                borderRadius: 9999,
                border: `1px solid ${hex ?? '#d1d5db'}`,
                color: hex,
              }}
            >
              {String(r.min ?? '?')}~{String(r.max ?? '?')} {String(r.label ?? '')}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 병기(annotationKey)로 **실제로 붙는 값**을 보여준다 — 구간 미리보기(RangeCriteriaPreview)와 같은
 * 취지다. 경로만 적고 끝내면 맞게 적었는지 알 수 없고, 오타는 "아무것도 안 나옴"으로만 드러난다.
 *
 * 값 채널은 미리보기 값(캔버스 prefill/미리보기 content)이라 **반복 행 스코프의 필드는 여기 없다** —
 * 그 경우 "행 값은 작성 화면에서 확인" 안내로 degrade한다(없는 값을 지어내지 않는다).
 */
function RangeAnnotationPreview({
  format,
  previewValues,
}: {
  format: Extract<BindingFormat, { kind: 'rangeMap' }>;
  previewValues?: Record<string, unknown>;
}) {
  if (!format.annotationKey) return null;
  // 엔진 짝은 `resolveRangeAnnotation` = `getByPath(withFormatScope(format, scope), annotationKey)`
  // + trim이다 — 관용 없는 읽기이므로 readFormatPath가 그 짝이다(종전 readPreviewPath는 세그먼트별
  // 배열 관용이 있어 엔진이 못 읽는 경로를 미리보기만 읽어 줬다).
  const preview = readFormatPath(format, format.annotationKey, previewValues);
  const text = preview.state === 'resolved' ? preview.text.trim() : '';
  if (text === '') {
    return (
      <p className="text-[10px] text-gray-400">
        병기 값 미리보기: 이 경로의 값이 미리보기 채널에 없습니다(반복 행 안의 필드는 작성 화면에서
        확인). 값이 비면 아무것도 표시되지 않습니다.
      </p>
    );
  }
  return (
    <p className="text-[10px] text-gray-500">
      병기 값: <span className="text-gray-700">{text}</span>
    </p>
  );
}

/**
 * rangeMap(구간 판정) 세부 저작 — **구간 기준표 선택이 주 경로**이고 나머지 두 출처는 고급으로 접는다.
 *
 * 해소 우선순위는 기준표(rangesFrom) → rangesKey(스코프 경로) → ranges(정적)인데, 셋을 나란히
 * 늘어놓으면 저작자는 "무엇을 골라야 하나"가 된다(2026-08-05 제보). 정답은 기준표 하나이고 나머지는
 * legacy·예외다. 다만 **값이 들어 있으면 항상 펼친다** — 접기로 기존 저작이 안 보이면 "사라졌다"가
 * 된다. 제거가 아니라 격하라는 원칙(raw는 탈출구로 남긴다)의 rangeMap 판이다.
 */
function RangeMapFields({
  format,
  onChange,
  catalog,
  previewValues,
  keyOptions,
}: {
  format: Extract<BindingFormat, { kind: 'rangeMap' }>;
  onChange: (format: BindingFormat) => void;
  catalog?: CatalogEntry[];
  previewValues?: Record<string, unknown>;
  /** 경로 칸(병기 값·구간 키) 후보 — 비어 있으면 자유 텍스트 입력으로 degrade. */
  keyOptions?: HybridKeyOption[];
}) {
  const staticRanges = format.ranges ?? [];
  // null = 저작자가 아직 토글하지 않음 → 값이 들어 있으면 펼친 채로 시작(접기로 기존 저작이
  // 안 보이면 "사라졌다"가 된다). 한 번 토글하면 **저작자의 선택이 이긴다** — 종전엔
  // `showAdvanced || hasAdvanced`라 legacy 값이 하나라도 있으면 영영 접을 수 없었다.
  const [showAdvanced, setShowAdvanced] = useState<boolean | null>(null);
  const hasAdvanced = !!format.rangesKey || staticRanges.length > 0;
  const advOpen = showAdvanced ?? hasAdvanced;
  // 경로 칸(병기 값·구간 키)의 후보 — 있으면 콤보+직접 입력, 없으면 자유 텍스트 입력 그대로.
  const hasKeyOptions = !!keyOptions && keyOptions.length > 0;
  const patchRange = (i: number, patch: Partial<{ min: number; max: number; label: string }>) => {
    const next = staticRanges.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ ...format, ranges: next });
  };
  return (
    <div className="space-y-1 pl-2 border-l-2 border-gray-100">
      {/* 구간 기준표(선언 출처) — 형식이 **스스로** 출처를 들고 있는다. 예전처럼 양식 어딘가에
          "표시 원료 전용 히든 위젯"을 두고 그 식별 키를 문자열로 가리킬 필요가 없다(저작자가
          그 히든 위젯을 지우면 배지가 조용히 죽던 트릭). 자동 채움과 같은 선택 UI를 쓴다. */}
      <BindingRow label="구간 기준표">
        <div className="space-y-0.5">
          <CatalogPrefillPicker
            // 카탈로그 미로드 경로(호출부에 따라 optional)면 빈 목록 — 후보 없음으로 degrade.
            catalog={catalog ?? []}
            value={format.rangesFrom}
            onChange={(v) => onChange({ ...format, rangesFrom: v })}
            // 배열 전부가 아니라 **구간을 가진 배열만** — 고를 수 있는데 안 되는 선택지를 없앤다.
            returnPaths={rangeReturnPaths}
            fieldPlaceholder="구간 배열 필드 검색·선택…"
          />
          <p className="text-[10px] text-gray-400">
            구간 배열([{'{'}min, max, label, color{'}'}])을 가진 데이터를 고릅니다. 배지 색도 여기
            구간이 가진 색을 씁니다.
          </p>
          {/* 실제로 무슨 값이 쓰이는지 보여준다 — 출처만 고르고 끝내면 "어떤 범위가 적용되는지
              모르겠다"가 된다(2026-08-05 제보). 미리보기 값이 있으면 그 구간을 그대로 나열한다. */}
          <RangeCriteriaPreview format={format} previewValues={previewValues} />
        </div>
      </BindingRow>
      <BindingRow label="output">
        <select
          value={format.output ?? 'value-label'}
          onChange={(e) => onChange({ ...format, output: e.target.value as 'label' | 'value-label' })}
          className={INPUT_CLS}
        >
          <option value="value-label">값 + 라벨 (9 (높음))</option>
          <option value="label">라벨만 (높음)</option>
        </select>
      </BindingRow>
      {/* display='badge'면 같은 표시 문구를 알약 배지로 그린다(bool의 '박스로 표시'와 동형 규약).
          색은 저작 대상이 아니다 — 매칭된 구간의 color(운영자 기준표 소유)를 그대로 쓰고 없으면
          중립 배지다. 표시 형식에 팔레트를 따로 저작하면 기준표와 이중 정본이 된다. */}
      <BindingRow label="표시 방식">
        <div className="space-y-0.5">
          <label className="flex items-center gap-1 text-[11px] text-gray-700">
            <input
              type="checkbox"
              checked={format.display === 'badge'}
              onChange={(e) => onChange({ ...format, display: e.target.checked ? 'badge' : undefined })}
            />
            배지로 표시 (알약)
          </label>
          <p className="text-[10px] text-gray-400">
            색은 구간 기준표의 색을 따릅니다(여기서 지정하지 않음). 색이 없는 구간은 회색 배지로
            나오고, 문구는 배지 여부와 관계없이 그대로 인쇄됩니다.
          </p>
        </div>
      </BindingRow>
      {/* 병기(annotationKey) — 한 셀에는 위젯이 하나뿐이라 "배지 + 산출 근거"를 보이려면 표를 두 칸
          으로 갈라야 했다(그 행에만 세로선이 생긴다). 형식이 병기할 값을 직접 들어 한 칸으로
          되돌린 축이다. 읽는 스코프·방식은 rangesKey와 같아(같은 값 스코프의 경로) 새 개념이 아니다. */}
      <BindingRow label="병기 값">
        <div className="space-y-0.5">
          {/* 후보를 알 수 있는 자리(반복 행 안 등)에서는 콤보로 고르게 한다 — 경로를 외워 타이핑하면
              오타가 "아무것도 안 나옴"으로만 드러난다. 후보 밖 경로도 직접 입력으로 계속 적을 수
              있다(선언이 불완전할 수 있어 하드 게이트 금지). */}
          {hasKeyOptions ? (
            <HybridKeyPicker
              value={format.annotationKey ?? ''}
              options={keyOptions ?? []}
              onChange={(v) => onChange({ ...format, annotationKey: v || undefined })}
              placeholder="같은 값 스코프의 경로 직접 입력 (예: riskAssessmentDetailText)"
              chooseLabel="— 병기할 값 선택 —"
              emptyLabel="지정 안 함 (병기 없음)"
            />
          ) : (
            <input
              type="text"
              value={format.annotationKey ?? ''}
              placeholder="같은 값 스코프의 경로 (예: riskAssessmentDetailText)"
              onChange={(e) => onChange({ ...format, annotationKey: e.target.value || undefined })}
              className={INPUT_CLS}
            />
          )}
          <p className="text-[10px] text-gray-400">
            표시 옆에 나란히 둘 값입니다(배지 <b>밖</b>이라 구간 색은 배지만 가집니다). 반복 행
            안에서는 <b>그 행의 필드 이름</b>을, 그 밖에서는 값 스코프의 경로를 적습니다 — 위
            rangesKey와 같은 스코프입니다. 값이 비면 아무것도 표시하지 않습니다(빈 칸 그대로).
          </p>
          {/* 실제로 무슨 값이 붙는지 보여준다 — 경로만 적고 끝내면 맞게 적었는지 알 수 없다
              (구간 미리보기와 같은 취지). 행 스코프 값은 미리보기 값 채널에 없어 안 보일 수 있다. */}
          <RangeAnnotationPreview format={format} previewValues={previewValues} />
        </div>
      </BindingRow>
      {!advOpen ? (
        <button
          type="button"
          onClick={() => setShowAdvanced(true)}
          className="text-[10px] text-gray-400 underline hover:text-gray-600"
          title="스코프 경로(legacy)·정적 구간 — 기준표를 쓸 수 없을 때의 예외 경로"
        >
          {/* 접었어도 값이 들어 있으면 그 사실은 남긴다 — 접기가 저작을 '없는 것'으로 보이게 하면 안 된다. */}
          고급: 다른 구간 출처(legacy·정적){hasAdvanced ? ' · 값 있음' : ''}
        </button>
      ) : (
        <>
          {/* 접기 — 값이 들어 있어도 접을 수 있다(내용이 사라지는 게 아니라 가려질 뿐). */}
          <button
            type="button"
            onClick={() => setShowAdvanced(false)}
            className="text-[10px] text-gray-400 underline hover:text-gray-600"
            title="고급 영역을 접습니다(값은 그대로 유지됩니다)"
          >
            고급 접기 ▴
          </button>
          <BindingRow label="rangesKey (legacy)">
            <div className="space-y-0.5">
              {/* 병기 값과 같은 스코프라 같은 후보 목록을 쓴다(후보 밖 경로는 직접 입력 유지). */}
              {hasKeyOptions ? (
                <HybridKeyPicker
                  value={format.rangesKey ?? ''}
                  options={keyOptions ?? []}
                  onChange={(v) => onChange({ ...format, rangesKey: v || undefined })}
                  placeholder="배열 경로 직접 입력 (예: riskLevelCriteria)"
                  chooseLabel="— 구간 배열 선택 —"
                  emptyLabel="지정 안 함"
                />
              ) : (
                <input
                  type="text"
                  value={format.rangesKey ?? ''}
                  placeholder="RenderData 배열 경로 (예: riskLevelCriteria)"
                  onChange={(e) => onChange({ ...format, rangesKey: e.target.value || undefined })}
                  className={INPUT_CLS}
                />
              )}
              <p className="text-[10px] text-gray-400">
                값 스코프의 배열 경로를 직접 가리키는 예전 축입니다.
                {format.rangesFrom
                  ? ' 위 구간 기준표가 지정돼 있어 이 경로는 그것이 해소되지 않을 때만 쓰입니다.'
                  : ' 새로 만들 때는 위 구간 기준표를 쓰세요.'}
              </p>
            </div>
          </BindingRow>
          <BindingRow label="ranges (정적)">
            <div className="space-y-1">
              {staticRanges.map((r, i) => (
                <div key={i} className="flex items-center gap-1">
                  <input
                    type="number"
                    value={Number.isFinite(r.min) ? r.min : ''}
                    placeholder="min"
                    onChange={(e) => patchRange(i, { min: Number(e.target.value) })}
                    className={`${INPUT_CLS} w-14`}
                  />
                  <span className="text-[11px] text-gray-400">~</span>
                  <input
                    type="number"
                    value={Number.isFinite(r.max) ? r.max : ''}
                    placeholder="max"
                    onChange={(e) => patchRange(i, { max: Number(e.target.value) })}
                    className={`${INPUT_CLS} w-14`}
                  />
                  <input
                    type="text"
                    value={r.label ?? ''}
                    placeholder="라벨"
                    onChange={(e) => patchRange(i, { label: e.target.value })}
                    className={INPUT_CLS}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      onChange({ ...format, ranges: staticRanges.filter((_, idx) => idx !== i) })
                    }
                    className="text-[11px] text-red-500 px-1"
                    title="구간 삭제"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  onChange({ ...format, ranges: [...staticRanges, { min: 0, max: 0, label: '' }] })
                }
                className="text-[11px] text-brand-600"
              >
                + 구간 추가
              </button>
              <p className="text-[10px] text-gray-400">
                기준표 없이 구간을 직접 적습니다. 색은 없어 중립 배지로 나옵니다.
              </p>
            </div>
          </BindingRow>
        </>
      )}
    </div>
  );
}

interface FormatFieldsProps {
  format: BindingFormat | undefined;
  onChange: (format: BindingFormat) => void;
  // 호출부에 따라 카탈로그가 아직 로드 전일 수 있다(미로드=후보 없음 — 구간 기준표 select가
  // 빈 목록으로 degrade). 필수로 두면 로드 전 경로에서 타입이 깨진다.
  catalog?: CatalogEntry[];
  /**
   * 해소된 미리보기 값(캔버스 prefill / 미리보기 content) — 구간 판정이 **실제로 어떤 범위를
   * 쓰는지** 보여주는 데 쓴다. 출처만 고르고 값이 안 보이면 저작자는 무엇이 적용되는지 모른다
   * (2026-08-05 제보). 미전달이면 컨텍스트(PreviewValuesContext)에서 같은 값을 집는다 —
   * 표 셀·일괄·계산·바인딩 편집기처럼 prop 사슬이 긴 호출부까지 값이 닿게 하는 경로다.
   */
  previewValues?: Record<string, unknown>;
  /**
   * 값 스코프의 경로 후보(반복 행 필드 등) — 형식의 **경로 칸**(병기 값·구간 키)을 콤보+직접 입력
   * 하이브리드로 저작하게 한다. 그 칸들이 읽는 스코프가 식별 키 후보와 같은 행 스코프라 같은 목록이다.
   * 스코프를 특정할 수 없는 호출부(여러 스코프에 걸친 일괄 편집 등)는 넘기지 않는다 — 그 경우
   * 기존 자유 텍스트 입력으로 degrade한다(후보를 지어내지 않는다).
   */
  keyOptions?: HybridKeyOption[];
}

/**
 * 표시 형식의 세부 저작 — kind별 설정(FormatKindFields) + **모든 kind 공통**인 보조 자료 출처
 * 선언(scopeFrom, FormatScopeFromEditor).
 *
 * ⚠️ **형식 세부를 그리는 자리는 여기 하나다.** `FORMAT_KINDS` select는 다섯 곳(표 셀 일괄 편집·표
 * 칼럼 매핑·계산 결과·바인딩 편집기·작성 컨트롤의 읽기 표시)에 흩어져 있지만 전부 이 컴포넌트를
 * 바로 아래 이어 붙인다. 그러니 형식에 축이 늘 때 **여기에만** 넣으면 다섯 곳이 함께 얻는다 —
 * 패널마다 손으로 큐레이션하면 같은 형식인데 어떤 패널에서만 저작 가능한 비대칭이 생긴다.
 */
function FormatFields(props: FormatFieldsProps) {
  const { format, onChange, catalog } = props;
  if (!format) return null;
  return (
    <>
      <FormatKindFields {...props} />
      {shouldShowScopeEditor(format) ? (
        <FormatScopeFromEditor format={format} onChange={onChange} catalog={catalog} />
      ) : null}
    </>
  );
}

/**
 * 보조 자료 출처 선언(`format.scopeFrom`) 편집 — 카탈로그 자료를 이름 붙여 **형식의 스코프에 얹는다**.
 *
 * 형식의 경로 표현식(unitPath·`{a.b}`·switch.on·rangesKey·병기 값)은 전부 문자열이라, 예전에는 그 이름이
 * 스코프에 실리도록 양식 어딘가에 캐리어(히든 위젯·분기 조건 바인딩)를 두는 관례가 필요했다. 연결이
 * 선언이 아니라 관례라 **저작 화면에서 보이지 않았고**, 캐리어를 지우면 소비처가 조용히 죽었다.
 * 결정 기록: docs/decisions/2026-08-07-format-scope-declaration-axis.md
 *
 * 접힘 규칙은 rangeMap 고급 영역과 같다 — 선언이 있으면 펼친 채로 시작한다(접기로 기존 저작이 안 보이면
 * "사라졌다"가 된다). 비어 있을 때는 접되, **버튼 문구가 축의 이름을 그대로 말하도록** 둔다.
 */
function FormatScopeFromEditor({
  format,
  onChange,
  catalog,
}: {
  format: BindingFormat;
  onChange: (next: BindingFormat) => void;
  catalog?: CatalogEntry[];
}) {
  const sources = scopeSourcesOf(format);
  const duplicates = duplicateScopeKeys(sources);
  // 이름·출처가 덜 채워진 행은 rc가 그냥 건너뛴다 — 접힌 상태에서 그 사실이 안 보이면
  // "지정했는데 왜 안 나오지"가 된다. 접기 버튼에 그대로 드러낸다.
  const incomplete = sources.filter((source) => !isScopeSourceUsable(source)).length;
  // null = 저작자가 아직 토글하지 않음 → 선언이 있으면 펼친 채로 시작.
  const [open, setOpen] = useState<boolean | null>(null);
  const isOpen = open ?? sources.length > 0;
  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`ml-2 text-[10px] underline ${
          incomplete > 0 ? 'text-amber-600 hover:text-amber-700' : 'text-gray-400 hover:text-gray-600'
        }`}
        title="시스템 조회 자료를 이 표시 형식이 읽을 수 있도록 이름을 붙여 얹습니다"
      >
        보조 자료 가져오기{sources.length > 0 ? ` · ${sources.length}개` : ''}
        {incomplete > 0 ? ` (${incomplete}개 미완성)` : ''}
      </button>
    );
  }
  return (
    <div className="space-y-1 pl-2 border-l-2 border-gray-100">
      <BindingRow label="보조 자료 가져오기" help="formatScopeFrom">
        <div className="space-y-1">
          {/* 스코프를 읽지 않는 kind에도 **선언이 남아 있으면** 이 편집기가 열린다(지울 수단을 주려고).
              그때는 안내가 아니라 경고여야 한다 — 저작해 두면 동작할 것처럼 읽히면 안 된다. */}
          {formatReadsScope(format.kind) ? (
            <p className="text-[10px] text-gray-500 leading-snug">
              다른 데이터를 이름 붙여 이 형식이 읽을 수 있게 합니다. {scopeUsageHint(format.kind)}
            </p>
          ) : (
            <p className="text-[10px] text-amber-600 leading-snug">
              {scopeUsageHint(format.kind)} 선언을 지우거나, 보조 자료를 읽는 형식(단위
              붙이기·문구에 값 넣기·다른 항목에 따라·구간 판정)으로 바꾸세요.
            </p>
          )}
          {sources.map((source, i) => {
            const issue = scopeSourceIssue(source, duplicates);
            return (
              <div key={i} className="rounded border border-gray-200 p-1.5 space-y-1">
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={source.key}
                    placeholder="이름 (경로에 쓸 이름)"
                    title="형식의 경로 표현식이 이 이름으로 자료를 찾습니다"
                    onChange={(e) => onChange(patchScopeSourceAt(format, i, { key: e.target.value }))}
                    className={INPUT_CLS}
                  />
                  <button
                    type="button"
                    onClick={() => onChange(removeScopeSourceAt(format, i))}
                    className="text-[11px] text-red-500 px-1"
                    title="이 선언 삭제"
                  >
                    ✕
                  </button>
                </div>
                <CatalogPrefillPicker
                  // 카탈로그 미로드 경로(호출부에 따라 optional)면 빈 목록 — 후보 없음으로 degrade.
                  catalog={catalog ?? []}
                  value={source.catalogKey ? { catalogKey: source.catalogKey, select: source.select } : undefined}
                  onChange={(v) => onChange(setScopeSourceOrigin(format, i, v))}
                  // 형상을 가리지 않는다 — 배열 통째로 얹는 것이 주 용도다(구간 기준표·목록).
                  returnPaths={scopeReturnPaths}
                  fieldPlaceholder="가져올 자료 검색·선택… (비우면 통째로)"
                  // 해소가 배열이면 첫 항목에서 읽으므로 행 인덱스를 고르지 않는다(붙이면 안 읽힌다).
                  firstRowOnly
                />
                {issue ? <p className="text-[10px] text-amber-600 leading-snug">{issue}</p> : null}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => onChange(addScopeSource(format))}
            className="text-[11px] text-brand-600"
          >
            + 자료 추가
          </button>
          <p className="text-[10px] text-gray-400 leading-snug">
            전역 변수와 <b>같은 행의 값</b>은 이미 읽을 수 있어 여기 적을 필요가 없습니다. 이름을 지금
            경로에 적혀 있는 이름과 <b>똑같이</b> 맞추면 경로를 고치지 않아도 그대로 동작합니다.
          </p>
          {sources.length > 0 ? (
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[10px] text-gray-400 underline hover:text-gray-600"
              title="영역을 접습니다(선언은 그대로 유지됩니다)"
            >
              접기 ▴
            </button>
          ) : null}
        </div>
      </BindingRow>
    </div>
  );
}

/**
 * format kind별 세부 입력(unit/date/template/default). url/none은 입력 없음.
 *
 * ⚠️ 모든 kind 공통인 축(보조 자료 출처 선언)은 여기가 아니라 **FormatFields**에 있다 — kind 분기마다
 * 같은 컨트롤을 반복하면 한 분기를 빠뜨렸을 때 그 형식에서만 저작이 불가능해진다.
 *
 * catalog — rangeMap의 구간 배열 **선언 출처**(rangesFrom) 지정에 쓰는 카탈로그 목록.
 * 자동 채움(prefillFrom)·옵션 소스(optionsFrom)와 **같은 선택 UI**(CatalogPrefillPicker)를
 * 재사용한다(저작 어휘 일원화).
 */
/**
 * join.itemTemplate에서 **항목 어디서나** 유효한 예약어(resolver applyFormat 'join' 참조).
 * 객체 항목의 필드명({field})은 배열의 항목 형태를 편집기가 알 수 없어 후보에 넣지 않는다 —
 * 틀린 후보를 권하느니 자유 입력으로 둔다.
 */
const JOIN_ITEM_TEMPLATE_KEYS: HybridKeyOption[] = [
  { key: 'value', label: '항목 값 (value)' },
  { key: 'n', label: '항목 번호 (n)' },
];

/**
 * join.groupTemplate의 예약어 — 그룹 1개의 모양을 정한다(joinGrouped 참조).
 * ⚠️ `{separator}`는 **토큰이 아니다** — 기본형을 만들 때 코드가 값을 끼워 넣을 뿐이라,
 * 저작자가 적으면 치환되지 않고 글자 그대로 나온다. 그래서 후보에도 넣지 않는다.
 */
const JOIN_GROUP_TEMPLATE_KEYS: HybridKeyOption[] = [
  { key: 'group', label: '구분 값 (group)' },
  { key: 'items', label: '그 그룹의 항목들 (items)' },
];

function FormatKindFields({ format, onChange, catalog, previewValues, keyOptions }: FormatFieldsProps) {
  // 훅은 early return보다 위에서 호출한다(rules-of-hooks). 명시 prop 우선, 없으면 컨텍스트.
  const contextPreviewValues = useContext(PreviewValuesContext);
  const effectivePreviewValues = previewValues ?? contextPreviewValues;

  if (!format) return null;

  if (format.kind === 'unit') {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
        {/* unitPath는 rangesKey·병기 값·switch.on과 **같은 가족**이다 — 엔진이 전부
            `getByPath(withFormatScope(format, scope), 경로)`로 읽는다(rc resolver.ts의 경로 표현식
            열거 주석). 그런데 이 칸만 콤보가 없어 저작자가 경로를 외워 타이핑해야 했고, 오타는
            "단위가 안 붙는다"로만 드러났다(줄기 P4 — 자유 입력 경로 전수). 후보가 없는 호출부는
            기존 자유 텍스트로 degrade한다(후보를 지어내지 않는다). */}
        <BindingRow label="unitPath">
          <div className="space-y-0.5">
            {keyOptions && keyOptions.length > 0 ? (
              <HybridKeyPicker
                value={format.unitPath ?? ''}
                options={keyOptions}
                onChange={(v) => onChange({ ...format, unitPath: v || undefined })}
                placeholder="같은 값 스코프의 경로 직접 입력 (예: measurement.unit)"
                chooseLabel="— 단위를 읽을 값 선택 —"
                emptyLabel="지정 안 함 (아래 고정 unit 사용)"
              />
            ) : (
              <input
                type="text"
                value={format.unitPath ?? ''}
                placeholder="같은 값 스코프의 경로 (예: measurement.unit)"
                onChange={(e) => onChange({ ...format, unitPath: e.target.value || undefined })}
                className={INPUT_CLS}
              />
            )}
            {/* 실제로 무슨 단위가 붙는지 보여준다 — 경로만 적고 끝내면 맞게 적었는지 알 수 없다
                (구간·병기 미리보기와 같은 규약. 줄기 P1). */}
            <UnitPreview format={format} previewValues={effectivePreviewValues} />
          </div>
        </BindingRow>
        <BindingRow label="unit">
          <input
            type="text"
            value={format.unit ?? ''}
            placeholder="고정 단위 (예: dB) — 위 경로가 비어 있을 때만 쓰입니다"
            onChange={(e) => onChange({ ...format, unit: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
        <BindingRow label="empty">
          <input
            type="text"
            value={format.empty ?? ''}
            onChange={(e) => onChange({ ...format, empty: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
      </div>
    );
  }

  if (format.kind === 'date') {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
        <BindingRow label="pattern">
          <input
            type="text"
            value={format.pattern ?? ''}
            placeholder="YYYY-MM-DD"
            onChange={(e) => onChange({ ...format, pattern: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
        <BindingRow label="empty">
          <input
            type="text"
            value={format.empty ?? ''}
            onChange={(e) => onChange({ ...format, empty: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
      </div>
    );
  }

  if (format.kind === 'template') {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
        {/* 템플릿은 정적 문구와 `{키}`가 섞이므로 값을 대체하는 콤보(HybridKeyPicker)가 아니라
            **커서 자리에 끼워 넣는** 콤보를 붙인다 — 저작자가 쓸 수 있는 키를 외우지 않아도 된다.
            후보는 이 셀의 스코프 필드(keyOptions, 반복영역이면 그 행의 칼럼들). */}
        <TemplateVarInput
          label="template"
          value={format.template}
          placeholder="{value}"
          options={keyOptions ?? []}
          onChange={(template) => onChange({ ...format, template })}
        />
        {/* 치환 결과 — `{키}`가 맞게 적혔는지는 결과를 봐야 안다(오타는 빈칸으로만 드러난다). */}
        <TemplateResultPreview
          template={format.template}
          format={format}
          previewValues={effectivePreviewValues}
        />
        <BindingRow label="empty">
          <input
            type="text"
            value={format.empty ?? ''}
            onChange={(e) => onChange({ ...format, empty: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
      </div>
    );
  }

  if (format.kind === 'default') {
    return (
      <div className="pl-2 border-l-2 border-gray-100">
        <BindingRow label="fallback">
          <input
            type="text"
            value={format.fallback}
            onChange={(e) => onChange({ ...format, fallback: e.target.value })}
            className={INPUT_CLS}
          />
        </BindingRow>
      </div>
    );
  }

  if (format.kind === 'join') {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
        <BindingRow label="separator">
          <input
            type="text"
            value={format.separator ?? ''}
            placeholder="\n (줄바꿈)"
            onChange={(e) => onChange({ ...format, separator: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
        {/* ⚠️ 여기 스코프는 **배열 항목**이지 행이 아니다 — keyOptions(행 필드)를 후보로 주면
            되지도 않을 키를 권하게 된다. 항목 어디서나 유효한 예약어만 후보로 두고, 객체 항목의
            필드명({field})은 배열의 항목 형태를 편집기가 모르므로 자유 입력(placeholder 안내)이다. */}
        <TemplateVarInput
          label="itemTemplate"
          value={format.itemTemplate ?? ''}
          placeholder="{value} / {field} / {n}"
          options={JOIN_ITEM_TEMPLATE_KEYS}
          includeValueKey={false}
          onChange={(v) => onChange({ ...format, itemTemplate: v || undefined })}
        />
        <BindingRow label="empty">
          <input
            type="text"
            value={format.empty ?? ''}
            onChange={(e) => onChange({ ...format, empty: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
        {/* plainWhenSingle — 항목이 1개뿐이면 itemTemplate을 건너뛰고 값만 출력한다(resolver의
            skipTemplate 분기). 번호 매김("1. …") itemTemplate이 단건에서 어색해지는 것을 막는 용도.
            미지정=항상 itemTemplate 적용(기존 동작). */}
        <label className="flex items-start gap-1.5 text-[11px] text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={format.plainWhenSingle === true}
            onChange={(e) =>
              onChange({ ...format, plainWhenSingle: e.target.checked || undefined })
            }
          />
          <span title="항목이 1개뿐이면 itemTemplate을 적용하지 않고 값만 출력합니다(번호 매김 itemTemplate이 단건에 붙는 것 방지). 끄면 항상 itemTemplate을 적용합니다.">
            항목이 1개면 번호 없이 값만 표시
          </span>
        </label>
        {/* 구분 묶음 — 객체 항목을 그 필드 값별로 묶어 "머리글 + 그 그룹 항목들"로 낸다(표 위젯
            groupByField와 같은 어휘·같은 의미). 엔진(joinGrouped)과 타입에는 2026-08-05부터
            있었는데 저작 칸이 없어 **DB를 직접 고쳐야만** 쓸 수 있었다 — 매뉴얼(r3)은 설정
            항목으로 안내하고 있어 화면과 어긋났다(2026-08-11 사용자 지적). */}
        {/* ⚠️ **여기에 keyOptions 콤보를 달지 말 것** — 형식의 다른 경로 칸(unitPath·switch.on·
            rangesKey·병기 값)은 전부 **값 스코프**를 읽지만 groupBy는 **배열 항목의 필드**다
            (joinGrouped가 각 항목에서 읽는다. 위 itemTemplate 주석과 같은 스코프 경계). 편집기는
            배열의 항목 형태를 모르므로 행 필드를 후보로 주면 **되지도 않을 키를 권하게 된다** —
            그래서 자유 입력이 맞고, 안내는 placeholder가 맡는다(줄기 P4 전수에서 확인). */}
        <BindingRow label="groupBy">
          <input
            type="text"
            value={format.groupBy ?? ''}
            placeholder="항목의 구분 필드(예: lawName) — 비우면 묶지 않음"
            onChange={(e) => onChange({ ...format, groupBy: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
        {format.groupBy !== undefined && (
          <>
            <p className="text-[11px] text-gray-400 leading-relaxed">
              항목을 이 필드 값별로 묶어 머리글과 함께 냅니다. 구분 값이 빈 항목은 묶이지 않고
              그 자리에 단독으로 나옵니다.
            </p>
            <TemplateVarInput
              label="groupTemplate"
              value={format.groupTemplate ?? ''}
              placeholder="{group}\n{items} (미지정=구분 값 다음에 구분자, 그 뒤 항목들)"
              options={JOIN_GROUP_TEMPLATE_KEYS}
              includeValueKey={false}
              onChange={(v) => onChange({ ...format, groupTemplate: v || undefined })}
            />
            <BindingRow label="groupSeparator">
              <input
                type="text"
                value={format.groupSeparator ?? ''}
                placeholder="미지정 = separator와 동일"
                onChange={(e) =>
                  onChange({ ...format, groupSeparator: e.target.value || undefined })
                }
                className={INPUT_CLS}
              />
            </BindingRow>
          </>
        )}
      </div>
    );
  }

  if (format.kind === 'map') {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
        <BindingRow label="cases (값 → 라벨)">
          <CasesEditor
            cases={format.cases}
            valueLabel="라벨"
            onChange={(cases) => onChange({ ...format, cases })}
          />
        </BindingRow>
        <BindingRow label="fallback">
          <div className="space-y-0.5">
            <input
              type="text"
              value={format.fallback ?? ''}
              onChange={(e) => onChange({ ...format, fallback: e.target.value || undefined })}
              className={INPUT_CLS}
            />
            {/* map은 "현재 값 매칭"을 보일 수 없다(입력이 경로가 아니라 바인딩 값 자체 —
                formatPreview 머리말). 대신 조용한 실패를 드러낸다: fallback이 빈 문자열이면
                미매칭이 빈 칸이 되어 "값 없는 칸"과 구별되지 않는다. */}
            <MapMissNote format={format} />
          </div>
        </BindingRow>
      </div>
    );
  }

  if (format.kind === 'switch') {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
        {/* on은 템플릿이 아니라 **경로** 한 개 — 값을 대체하는 콤보(rangeMap의 경로 칸과 같은
            HybridKeyPicker)가 맞다. 후보가 없으면 자유 입력으로 degrade한다. */}
        <BindingRow label="on (scope 경로)">
          <div className="space-y-0.5">
            {keyOptions && keyOptions.length > 0 ? (
              <HybridKeyPicker
                value={format.on}
                options={keyOptions}
                onChange={(on) => onChange({ ...format, on })}
                placeholder="경로 직접 입력"
                chooseLabel="— 분기 기준 값 선택 —"
              />
            ) : (
              <input
                type="text"
                value={format.on}
                placeholder="다른 scope 필드 경로"
                onChange={(e) => onChange({ ...format, on: e.target.value })}
                className={INPUT_CLS}
              />
            )}
            {/* 지금 어떤 케이스가 선택되는지 — 이 형식의 조용한 실패가 가장 고약하다(경로를 못
                읽어도 엔진은 빈 문자열을 키로 써서 default로 떨어진다. 증상은 "분기가 안 바뀜"). */}
            <SwitchCasePreview format={format} previewValues={effectivePreviewValues} />
          </div>
        </BindingRow>
        {/* cases/default 값은 template kind와 **같은 스코프의 템플릿**(applyTemplate 공유)이라
            변수 삽입 후보도 같다. */}
        <BindingRow label="cases (값 → 템플릿)">
          <CasesEditor
            cases={format.cases}
            valueLabel="템플릿"
            onChange={(cases) => onChange({ ...format, cases })}
            templateOptions={keyOptions ?? []}
          />
        </BindingRow>
        <TemplateVarInput
          label="default"
          value={format.default ?? ''}
          options={keyOptions ?? []}
          onChange={(v) => onChange({ ...format, default: v || undefined })}
        />
        <BindingRow label="empty">
          <input
            type="text"
            value={format.empty ?? ''}
            onChange={(e) => onChange({ ...format, empty: e.target.value || undefined })}
            className={INPUT_CLS}
          />
        </BindingRow>
      </div>
    );
  }

  if (format.kind === 'rangeMap') {
    // 세부가 접이식(고급 영역) 상태를 가지므로 별도 컴포넌트 — 이 함수는 kind별 early return
    // 구조라 여기서 훅을 부르면 조건부 훅이 된다.
    return (
      <RangeMapFields
        format={format}
        onChange={onChange}
        catalog={catalog}
        previewValues={effectivePreviewValues}
        keyOptions={keyOptions}
      />
    );
  }

  if (format.kind === 'bool') {
    // display='box'면 문자(V) 대신 체크박스 박스+체크 비주얼을 render가 그린다. box일 땐 trueText/falseText 무의미(숨김).
    const isBox = format.display === 'box';
    return (
      <div className="space-y-1 pl-2 border-l-2 border-gray-100">
        <BindingRow label="표시 방식">
          <label className="flex items-center gap-1 text-[11px] text-gray-700">
            <input
              type="checkbox"
              checked={isBox}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? { ...format, display: 'box', trueText: undefined, falseText: undefined }
                    : { ...format, display: undefined, boxSize: undefined },
                )
              }
            />
            박스로 표시 (☑/☐)
          </label>
        </BindingRow>
        {isBox ? (
          <BindingRow label="박스(pt)">
            <input
              type="number"
              min={1}
              step={0.1}
              value={format.boxSize == null ? '' : pxToPt(format.boxSize)}
              placeholder="10"
              onChange={(e) =>
                onChange({
                  ...format,
                  boxSize: e.target.value === '' ? undefined : ptToPx(Number(e.target.value)),
                })
              }
              className={INPUT_CLS}
            />
          </BindingRow>
        ) : (
          <>
            <BindingRow label="체크 문자">
              <input
                type="text"
                value={format.trueText ?? ''}
                placeholder="V"
                onChange={(e) => onChange({ ...format, trueText: e.target.value || undefined })}
                className={INPUT_CLS}
              />
            </BindingRow>
            <BindingRow label="미체크 문자">
              <input
                type="text"
                value={format.falseText ?? ''}
                placeholder="(빈칸)"
                onChange={(e) => onChange({ ...format, falseText: e.target.value || undefined })}
                className={INPUT_CLS}
              />
            </BindingRow>
          </>
        )}
      </div>
    );
  }

  // url: 입력 없음.
  return null;
}

/**
 * Record<string,string> 케이스 편집(키/값 행 리스트 + 추가/삭제).
 * 편집 중간 상태(빈 키·중복 키)를 Record로 즉시 직렬화하면 키 충돌로 행이 합쳐져 silent 소실 →
 * 로컬 CaseRow[] state를 source of truth로 두고(중복·빈 키 허용·순서 보존),
 * onChange(상위 format 반영) 시에만 serializeCases로 Record 변환(빈 키 제외, 동일 키 마지막 값 우선).
 *
 * **빈 값('') 케이스**: rc `map`은 값이 비면 `cases['']`를 라벨로 쓴다(사진 칸 「사진 없음」 안내 —
 * casesEditorUtils `CaseRow.emptyKey` 주석). 「+ 케이스 추가」 직후의 미완성 행도 키가 ''라 둘을
 * 키로는 구분할 수 없어, 행마다 「빈 값」 토글을 두고 켠 행만 '' 키로 보존한다(은닉 감사 D-1 —
 * 종전엔 편집기를 열기만 해도 그 케이스가 조용히 사라졌다).
 */
function CasesEditor({
  cases,
  valueLabel,
  onChange,
  templateOptions,
}: {
  cases: Record<string, string>;
  valueLabel: string;
  onChange: (cases: Record<string, string>) => void;
  /**
   * 값 칸이 **템플릿**인 경우(switch)의 변수 삽입 후보. 주면 값 칸이 TemplateVarInput이 된다.
   * map의 cases 값은 템플릿이 아니라 표시 **라벨**(치환 없음)이므로 주지 않는다 — 여기에 변수
   * 콤보가 뜨면 되지도 않을 `{키}`를 쓰라고 권하는 셈이다.
   */
  templateOptions?: HybridKeyOption[];
}) {
  const [rows, setRows] = useState<CaseRow[]>(() => rowsFromCases(cases));
  // 우리가 직전에 상위로 올린 직렬화 결과. 부모 cases가 이것과 다르면 외부 변경(다른 노드 선택 등)으로 보고 재초기화.
  const lastSerialized = useRef<Record<string, string>>(cases);

  useEffect(() => {
    if (!casesShallowEqual(cases, lastSerialized.current)) {
      setRows(rowsFromCases(cases));
      lastSerialized.current = cases;
    }
  }, [cases]);

  const update = (next: CaseRow[]) => {
    setRows(next);
    const obj = serializeCases(next);
    lastSerialized.current = obj;
    onChange(obj);
  };

  const patchAt = (i: number, patch: Partial<CaseRow>) => {
    update(rows.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  };
  const setKeyAt = (i: number, key: string) => patchAt(i, { key });
  const setValAt = (i: number, value: string) => patchAt(i, { value });
  // 토글을 켜면 키 칸을 비운다(저장값은 표식이 정하지만, 화면에 옛 글자가 남아 헷갈리지 않게).
  const setEmptyKeyAt = (i: number, emptyKey: boolean) =>
    patchAt(i, emptyKey ? { key: '', emptyKey: true } : { emptyKey: false });
  const removeAt = (i: number) => {
    update(rows.filter((_, idx) => idx !== i));
  };
  const addRow = () => {
    update([...rows, { key: '', value: '' }]);
  };

  return (
    <div className="space-y-1">
      {rows.map(({ key: k, value: v, emptyKey }, i) => (
        <div key={i} className="flex items-center gap-1">
          <input
            type="text"
            value={k}
            placeholder={emptyKey ? '(빈 값)' : '값'}
            disabled={emptyKey === true}
            onChange={(e) => setKeyAt(i, e.target.value)}
            className={`${INPUT_CLS} flex-1 disabled:bg-gray-100 disabled:text-gray-400`}
          />
          <label
            className="flex items-center gap-0.5 text-[10px] text-gray-500 whitespace-nowrap"
            title="값이 비어 있을 때('') 매칭되는 케이스 — 예: 사진이 없을 때 보일 안내 문구"
          >
            <input
              type="checkbox"
              checked={emptyKey === true}
              onChange={(e) => setEmptyKeyAt(i, e.target.checked)}
              aria-label="빈 값('') 케이스"
            />
            빈 값
          </label>
          <span className="text-gray-300 text-xs">→</span>
          <div className="flex-1 min-w-0">
            {templateOptions ? (
              <TemplateVarInput
                value={v}
                placeholder={valueLabel}
                options={templateOptions}
                onChange={(val) => setValAt(i, val)}
              />
            ) : (
              <input
                type="text"
                value={v}
                placeholder={valueLabel}
                onChange={(e) => setValAt(i, e.target.value)}
                className={`${INPUT_CLS} w-full`}
              />
            )}
          </div>
          <button
            type="button"
            onClick={() => removeAt(i)}
            className="text-xs px-1.5 py-1 border border-gray-300 rounded text-red-600 hover:bg-red-50"
            aria-label="케이스 삭제"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="text-xs px-2 py-1 border border-gray-300 rounded text-brand-600 hover:bg-brand-50"
      >
        + 케이스 추가
      </button>
    </div>
  );
}

/**
 * 페이지 채움 토글(group/stack/table) — layout.pageFill set/unset.
 * 켜면 이 노드가 페이지의 남은 세로 공간을 흡수해 다음 형제 1개(예약 바닥 블록)를 페이지 바닥에 flush한다.
 * height:'fill'(부모 박스 채움)과 별개 개념(페이지 흐름 레벨).
 * 표(table)는 잔여 높이를 통짜 여백 대신 **빈 괘선 행**으로 페인팅한다(구 SMS 위험성평가 표 동형) —
 * 활성 시 "채움행 높이(mm)" 입력 노출(빈값=자동: 밴드 rowHeights/엔진 근사, fillRowHeight는 px로 저장).
 */
function PageFillEditor({
  node,
  isTopLevel,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
}: {
  node: ComposerNode;
  /** 노드가 body 최상위인가 — pageFill·fillSplitGroup은 최상위에서만 동작(NESTED_PAGE_FLOW_HINT). */
  isTopLevel: boolean;
  onPatch: (patch: Partial<ComposerNode>) => void;
  /** 채움행 높이 실시간 미리보기(replacePresent) — 타이핑/화살표 중 캔버스 즉시 반영. */
  onPreview: (patch: Partial<ComposerNode>) => void;
  /** 채움행 높이 확정(commitPresent) — blur/Enter. 빈값=자동(undefined). */
  onCommit: (patch: Partial<ComposerNode>) => void;
  /** 미리보기 폐기(discardTransient) — Esc/무변경 이탈. */
  onRevert: () => void;
}) {
  const checked = node.layout.pageFill === true;
  const isTable = node.kind === 'table';
  const fillRowHeight = node.kind === 'table' ? node.fillRowHeight : undefined;
  // 미지정은 기본 'keep-below'로 취급.
  const mode: 'keep-below' | 'fill-all' = node.layout.pageFillMode === 'fill-all' ? 'fill-all' : 'keep-below';
  const setMode = (next: 'keep-below' | 'fill-all') =>
    onPatch({
      // keep-below(기본)는 undefined로 저장해 라운드트립 노이즈를 줄인다.
      layout: { ...node.layout, pageFillMode: next === 'fill-all' ? 'fill-all' : undefined },
    } as Partial<ComposerNode>);
  // 세로 채움 분배(fillSplitGroup) — 같은 키의 인접 노드끼리 페이지 세로 예산을 grow 비율로 양분(각자
  // min-content 하한, 사이 여백/제목 자동 고정). pageFill 단일 흡수와 배타(엔진이 fillSplitGroup 우선).
  // 분배 비율은 layout.grow 재사용. 켤 때 기본 키 'fill-split-1'(작성자가 인접 표에 같은 키로 맞춤).
  const splitOn = node.layout.fillSplitGroup != null;
  const setSplit = (on: boolean) =>
    onPatch({
      layout: {
        ...node.layout,
        fillSplitGroup: on ? node.layout.fillSplitGroup || 'fill-split-1' : undefined,
        // 분배를 켜면 단일 채움과 배타로 정리.
        pageFill: on ? undefined : node.layout.pageFill,
        pageFillMode: on ? undefined : node.layout.pageFillMode,
      },
    } as Partial<ComposerNode>);
  // 중첩 게이트(pageFlowGate 공용) — 새로 켜는 것만 막고 잔존값 해제는 허용.
  const fillGate = pageFlowGate(
    isTopLevel,
    checked,
    isTable
      ? '표의 데이터 행 아래 잔여 높이를 빈 괘선 행으로 페이지 끝까지 채웁니다.'
      : '이 노드가 페이지의 남은 세로 공간을 채웁니다.',
  );
  const splitGate = pageFlowGate(
    isTopLevel,
    splitOn,
    "같은 '분배 그룹' 키를 가진 위아래로 이어진 표들이 페이지 남은 세로 공간을 비율대로 나눠 각자 빈 괘선 행으로 채웁니다(수평 배열 아님). 사이의 여백/제목은 자동 고정됩니다.",
  );
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className={`flex items-center gap-2 ${fillGate.labelClass}`}
        title={fillGate.title}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={fillGate.disabled}
          onChange={(e) =>
            onPatch({
              layout: {
                ...node.layout,
                pageFill: e.target.checked ? true : undefined,
                // 체크 해제 시 모드도 함께 제거.
                pageFillMode: e.target.checked ? node.layout.pageFillMode : undefined,
                // 단일 채움을 켜면 세로 분배와 배타(둘 다면 엔진이 분배 우선이라 혼동 방지).
                fillSplitGroup: e.target.checked ? undefined : node.layout.fillSplitGroup,
              },
            } as Partial<ComposerNode>)
          }
          className="w-3.5 h-3.5"
        />
        <span className="text-xs text-gray-600">{isTable ? '빈 행으로 페이지 채움' : '페이지 채움'}</span>
      </label>
      {/* 페이지 채움 체크에도 안내 각주 — 종전엔 세로 분배 쪽만 있어 비활성 이유가 툴팁으로만 보였다. */}
      <NestedPageFlowNote isTopLevel={isTopLevel} />
      {checked && (
        <div className="flex flex-col gap-1 pl-5">
          <label className="flex items-center gap-2 cursor-pointer" title="채움 노드가 아래 노드 1개를 같은 페이지 바닥에 유지합니다(여러 개는 그룹으로 묶기).">
            <input
              type="radio"
              name={`pageFillMode-${node.id}`}
              checked={mode === 'keep-below'}
              onChange={() => setMode('keep-below')}
              className="w-3.5 h-3.5"
            />
            <span className="text-xs text-gray-600">아래 노드 1개를 바닥에 유지</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" title="흡수 없이 남은 공간을 전부 채웁니다. 다음 노드는 다음 페이지부터.">
            <input
              type="radio"
              name={`pageFillMode-${node.id}`}
              checked={mode === 'fill-all'}
              onChange={() => setMode('fill-all')}
              className="w-3.5 h-3.5"
            />
            <span className="text-xs text-gray-600">남은 공간 모두 채움</span>
          </label>
          {isTable && (
            <label
              className="flex items-center gap-2"
              title="빈 괘선 행 1행의 높이(mm). 빈값=자동(반복영역 행 높이/엔진 근사). 끝자락 나머지는 마지막 빈 행에 흡수됩니다."
            >
              <span className="text-xs text-gray-600">채움행 높이(mm)</span>
              {/* 표시 mm ↔ 저장 px. 타이핑/화살표 중 실시간 미리보기(onPreview) → 캔버스 즉시 반영. */}
              <NumericField
                value={fillRowHeight}
                min={0.5}
                step={0.5}
                placeholder="자동"
                toDisplay={pxToMm}
                fromDisplay={mmToPx}
                onPreview={(stored) => onPreview({ fillRowHeight: stored } as Partial<ComposerNode>)}
                onCommit={(stored) => onCommit({ fillRowHeight: stored } as Partial<ComposerNode>)}
                onRevert={onRevert}
                className="w-20 text-xs border border-gray-300 rounded px-1.5 py-0.5"
              />
            </label>
          )}
        </div>
      )}
      {/* 세로 채움 분배 — 같은 그룹 키를 가진 인접 노드끼리 페이지 세로 예산을 grow로 양분(min-content
          하한, 사이 여백/제목 자동 고정). 단일 채움(pageFill)과 배타. DLR A/B표가 대표 사례. */}
      <div className="flex flex-col gap-1 border-t border-gray-100 pt-1.5">
        <label
          className={`flex items-center gap-2 ${splitGate.labelClass}`}
          title={splitGate.title}
        >
          <input
            type="checkbox"
            checked={splitOn}
            disabled={splitGate.disabled}
            onChange={(e) => setSplit(e.target.checked)}
            className="w-3.5 h-3.5"
          />
          <span className="text-xs text-gray-600">세로 채움 분배(같은 그룹 표끼리 세로 나눔)</span>
        </label>
        <NestedPageFlowNote isTopLevel={isTopLevel} />
        {splitOn && (
          <div className="flex flex-col gap-1 pl-5">
            <label className="flex items-center gap-2" title="같은 키를 가진 인접 노드끼리 한 분배 묶음이 됩니다(사이의 마커 없는 여백/제목은 자동 고정).">
              <span className="text-xs text-gray-600">분배 그룹</span>
              <input
                type="text"
                value={node.layout.fillSplitGroup ?? ''}
                onChange={(e) =>
                  onPatch({
                    layout: { ...node.layout, fillSplitGroup: e.target.value || undefined },
                  } as Partial<ComposerNode>)
                }
                placeholder="그룹 키"
                className="w-24 text-xs border border-gray-300 rounded px-1.5 py-0.5"
              />
            </label>
            <label className="flex items-center gap-2" title="분배 비율(flex-grow). 2면 옆(1)보다 2배 높이를 받습니다. 0이면 고정(콘텐츠 높이만).">
              <span className="text-xs text-gray-600">분배 비율</span>
              <NumericField
                value={node.layout.grow}
                min={0}
                step={1}
                placeholder="1"
                onPreview={(stored) =>
                  onPreview({ layout: { ...node.layout, grow: stored } } as Partial<ComposerNode>)
                }
                onCommit={(stored) =>
                  onCommit({ layout: { ...node.layout, grow: stored } } as Partial<ComposerNode>)
                }
                onRevert={onRevert}
                className="w-16 text-xs border border-gray-300 rounded px-1.5 py-0.5"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

/** width 편집 — fill/auto 토글 + 숫자(mm) 입력 (저장은 px) */
function WidthEditor({
  node,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
  disabled = false,
  parentCtx,
}: {
  node: ComposerNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
  disabled?: boolean;
  parentCtx: 'group-row' | 'group-column' | 'stack' | 'flow';
}) {
  const w = node.layout.width;
  // 가로 축 실효 기본값(미지정 시): 가로가 주축(group-row)이면 콘텐츠(자동), 교차축/스택/flow면 채움.
  const effDefault: 'auto' | 'fill' = parentCtx === 'group-row' ? 'auto' : 'fill';
  // mode 판별: 미지정이면 실효 기본값을 표시, 명시값이면 그대로.
  const mode: 'fill' | 'auto' | 'mm' =
    w === undefined ? effDefault : w === 'auto' ? 'auto' : typeof w === 'number' ? 'mm' : 'fill';

  // 모드 전환(select)은 단발 apply. mm 수치는 preview/commit 2단계(NumericField).
  const setWidth = (next: number | string | undefined) => {
    onPatch({ layout: { ...node.layout, width: next } } as Partial<ComposerNode>);
  };

  const disabledCls = disabled ? 'opacity-50 cursor-not-allowed' : '';
  return (
    <div className="flex items-center gap-2" title={disabled ? '결재란은 내용 크기로 고정됩니다' : undefined}>
      <span className="text-xs text-gray-500 w-16">너비</span>
      <select
        value={mode}
        disabled={disabled}
        onChange={(e) => {
          const m = e.target.value as 'fill' | 'auto' | 'mm';
          if (m === 'mm') setWidth(typeof w === 'number' ? w : 200);
          // 실효 기본값을 고르면 미지정으로 유지(컨텍스트 기본 따름). 그 외는 명시 저장.
          else if (m === effDefault) setWidth(undefined);
          else setWidth(m);
        }}
        className={`text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 ${disabledCls}`}
      >
        {(['auto', 'fill', 'mm'] as const).map((opt) => (
          <option key={opt} value={opt}>
            {opt === 'auto' ? '자동' : opt === 'fill' ? '채움' : '고정'}
            {opt === effDefault ? ' (기본)' : ''}
          </option>
        ))}
      </select>
      {mode === 'mm' && (
        <NumericField
          value={typeof w === 'number' ? w : 0}
          min={0}
          step={0.01}
          disabled={disabled}
          toDisplay={pxToMm}
          fromDisplay={mmToPx}
          className={`w-20 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 ${disabledCls}`}
          onPreview={(px) => onPreview({ layout: { ...node.layout, width: px } } as Partial<ComposerNode>)}
          onCommit={(px) => onCommit({ layout: { ...node.layout, width: px ?? 0 } } as Partial<ComposerNode>)}
          onRevert={onRevert}
        />
      )}
      {mode === 'mm' && <span className="text-[10px] text-gray-400">mm</span>}
    </div>
  );
}

/**
 * height 편집 — 자동/채움(fill)/고정(mm) 토글 (저장은 px). width와 대칭.
 *
 * 자동=undefined(콘텐츠/minHeight 기준), 채움='fill'(부모 높이 100%), 고정=숫자(px).
 * minHeight 입력은 별개로 유지(MinHeightEditor).
 */
function HeightEditor({
  node,
  onPatch,
  onPreview,
  onCommit,
  onRevert,
  disabled = false,
  parentCtx,
}: {
  node: ComposerNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
  disabled?: boolean;
  parentCtx: 'group-row' | 'group-column' | 'stack' | 'flow';
}) {
  const h = node.layout.height;
  // 세로 축 실효 기본값(미지정 시): 세로가 교차축(group-row)이면 채움(equal-height), 주축/스택/flow면 콘텐츠(자동).
  const effDefault: 'auto' | 'fill' = parentCtx === 'group-row' ? 'fill' : 'auto';
  // mode 판별: 미지정이면 실효 기본값을 표시, 명시값이면 그대로.
  const mode: 'auto' | 'fill' | 'mm' =
    h === undefined ? effDefault : h === 'fill' ? 'fill' : typeof h === 'number' ? 'mm' : 'auto';

  const setHeight = (next: number | string | undefined) => {
    onPatch({ layout: { ...node.layout, height: next } } as Partial<ComposerNode>);
  };

  const disabledCls = disabled ? 'opacity-50 cursor-not-allowed' : '';
  return (
    <div className="flex items-center gap-2" title={disabled ? '결재란은 내용 크기로 고정됩니다' : undefined}>
      <span className="text-xs text-gray-500 w-16">높이</span>
      <select
        value={mode}
        disabled={disabled}
        onChange={(e) => {
          const m = e.target.value as 'auto' | 'fill' | 'mm';
          if (m === 'mm') setHeight(typeof h === 'number' ? h : 100);
          // 실효 기본값을 고르면 미지정으로 유지(컨텍스트 기본 따름). 그 외는 명시 저장('auto'/'fill').
          else if (m === effDefault) setHeight(undefined);
          else setHeight(m);
        }}
        className={`text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 ${disabledCls}`}
      >
        {(['auto', 'fill', 'mm'] as const).map((opt) => (
          <option key={opt} value={opt}>
            {opt === 'auto' ? '자동' : opt === 'fill' ? '채움' : '고정'}
            {opt === effDefault ? ' (기본)' : ''}
          </option>
        ))}
      </select>
      {mode === 'mm' && (
        <NumericField
          value={typeof h === 'number' ? h : 0}
          min={0}
          step={0.01}
          disabled={disabled}
          toDisplay={pxToMm}
          fromDisplay={mmToPx}
          className={`w-20 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 ${disabledCls}`}
          onPreview={(px) => onPreview({ layout: { ...node.layout, height: px } } as Partial<ComposerNode>)}
          onCommit={(px) => onCommit({ layout: { ...node.layout, height: px ?? 0 } } as Partial<ComposerNode>)}
          onRevert={onRevert}
        />
      )}
      {mode === 'mm' && <span className="text-[10px] text-gray-400">mm</span>}
    </div>
  );
}

/** minHeight 편집 — 숫자(mm) 입력 (저장은 px). 실시간 미리보기 + Enter/blur 확정. */
function MinHeightEditor({
  node,
  onPreview,
  onCommit,
  onRevert,
  disabled = false,
}: {
  node: ComposerNode;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
  disabled?: boolean;
}) {
  const mh = node.layout.minHeight;
  const disabledCls = disabled ? 'opacity-50 cursor-not-allowed' : '';
  return (
    <div className="flex items-center gap-2" title={disabled ? '결재란은 내용 크기로 고정됩니다' : undefined}>
      <span className="text-xs text-gray-500 w-16">최소 높이</span>
      <NumericField
        value={mh ?? undefined}
        min={0}
        step={0.01}
        disabled={disabled}
        placeholder="-"
        toDisplay={pxToMm}
        fromDisplay={mmToPx}
        className={`w-20 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 ${disabledCls}`}
        onPreview={(px) => onPreview({ layout: { ...node.layout, minHeight: px } } as Partial<ComposerNode>)}
        onCommit={(px) => onCommit({ layout: { ...node.layout, minHeight: px } } as Partial<ComposerNode>)}
        onRevert={onRevert}
      />
      <span className="text-[10px] text-gray-400">mm</span>
    </div>
  );
}

/**
 * 채움 비율(flex-grow) 편집 — 가로 그룹에서 '너비=채움'인 자식들이 남는 공간을 나눌 비율.
 * 미지정=1(균등). 2면 다른 채움 형제의 2배 폭. 호출 측에서 가로그룹+너비채움일 때만 노출.
 */
function GrowEditor({
  node,
  onPreview,
  onCommit,
  onRevert,
}: {
  node: ComposerNode;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
}) {
  const g = node.layout.grow;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-16">채움 비율</span>
      <NumericField
        value={g ?? undefined}
        min={1}
        step={1}
        placeholder="1"
        className="w-20 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
        onPreview={(v) => onPreview({ layout: { ...node.layout, grow: v } } as Partial<ComposerNode>)}
        onCommit={(v) => onCommit({ layout: { ...node.layout, grow: v } } as Partial<ComposerNode>)}
        onRevert={onRevert}
      />
      <span className="text-[10px] text-gray-400">채움 형제 간 비율(기본 1)</span>
    </div>
  );
}

type AlignVal = 'start' | 'center' | 'end';

/**
 * 정렬 2축(가로 H × 세로 V) 공용 3x3 그리드 피커.
 * 9칸 균일 셀(7x7)로 노드 위치(Outer)·콘텐츠 정렬(Inner) 양쪽에서 재사용.
 * onPick은 raw col/row(H,V)를 전달 — layout 키 매핑(명시값/키삭제)은 호출측 책임.
 * disabledH/disabledV로 축별 비활성(주축·채움 등). 비활성 축은 선택값 유지하고 픽 무시.
 */
function Align2DGrid({
  label,
  valueH,
  valueV,
  onPick,
  disabledH = false,
  disabledV = false,
  mixedH = false,
  mixedV = false,
  hint,
}: {
  label: string;
  valueH?: AlignVal;
  valueV?: AlignVal;
  onPick: (h: AlignVal, v: AlignVal) => void;
  disabledH?: boolean;
  disabledV?: boolean;
  /** 다중 선택 일괄 편집에서 가로/세로 정렬이 셀마다 다름(혼합). 어느 칸도 active로 표시하지 않는다. */
  mixedH?: boolean;
  mixedV?: boolean;
  hint?: string;
}) {
  const cols: AlignVal[] = ['start', 'center', 'end'];
  const rows: AlignVal[] = ['start', 'center', 'end'];
  const effH = valueH ?? 'start';
  const effV = valueV ?? 'start';
  // H/V 중 하나라도 혼합이면 (h,v) 쌍이 불확정하므로 9칸 전부 미선택이 옳다.
  const anyMixed = mixedH || mixedV;
  const allDisabled = disabledH && disabledV;
  const arrow = (h: AlignVal, v: AlignVal): string => {
    const m: Record<string, string> = {
      'start|start': '↖', 'center|start': '↑', 'end|start': '↗',
      'start|center': '←', 'center|center': '＋', 'end|center': '→',
      'start|end': '↙', 'center|end': '↓', 'end|end': '↘',
    };
    return m[`${h}|${v}`];
  };
  return (
    <div className="flex items-start gap-2" title={hint}>
      <span className="text-xs text-gray-500 w-16 pt-1" title={hint}>
        {label}
        {anyMixed && <span className="text-[11px] text-gray-400"> (혼합)</span>}
      </span>
      <div className={`inline-grid grid-cols-3 gap-px rounded border overflow-hidden ${anyMixed ? 'border-amber-300 bg-amber-200' : 'border-gray-300 bg-gray-300'} ${allDisabled ? 'opacity-40' : ''}`}>
        {rows.map((v) =>
          cols.map((h) => {
            // H/V 중 하나라도 혼합이면 쌍이 불확정 → 전 칸 미선택.
            const selected = !anyMixed && effH === h && effV === v;
            const dim = ((disabledH && h !== effH) || (disabledV && v !== effV)) && !selected;
            return (
              <button
                key={`${h}|${v}`}
                type="button"
                disabled={allDisabled}
                title={hint}
                onClick={() => onPick(disabledH ? effH : h, disabledV ? effV : v)}
                className={`w-7 h-7 text-xs leading-none flex items-center justify-center ${
                  selected ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                } ${dim ? 'opacity-30' : ''} ${allDisabled ? 'cursor-not-allowed' : ''}`}
              >
                {arrow(h, v)}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}

/**
 * 콘텐츠 정렬 편집 — 노드 박스 *안쪽* 콘텐츠(텍스트/이미지/자식)의 정렬.
 * layout.contentAlignH(수평: 좌/중/우) · layout.contentAlignV(수직: 위/중/아래)를 편집한다.
 * (기존 AlignEditor는 부모 안에서의 *노드 위치*. 이건 노드 *안* 콘텐츠 위치라 별개.)
 * 모든 노드 공통. (기본)=undefined → 엔진이 textAlign/verticalAlign/flex로 변환.
 * 수직은 박스 높이가 콘텐츠보다 클 때만 시각적 효과가 있다.
 */
function ContentAlignEditor({
  node,
  onPatch,
}: {
  node: ComposerNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
}) {
  // 콘텐츠 정렬은 '안에 정렬할 것이 있는' 노드만 노출 + 종류별 라벨로 직관 강화.
  // 데이터는 단일 contentAlignH/V 그대로 — 라벨/노출만 노드별로 맥락화(파편화 회귀 아님).
  const labelByKind: Partial<Record<ComposerNode['kind'], string>> = {
    text: '텍스트 정렬',
    image: '이미지 정렬',
    group: '자식 정렬',
  };
  const label = labelByKind[node.kind];
  // stack(오버레이=자식별 노드 위치)·rect·line·여백·표·반복·special 등은 정렬 대상 없음 → 숨김.
  if (!label) return null;

  const onPick = (h: AlignVal, v: AlignVal) => {
    const layout = { ...node.layout };
    // 'start'=엔진 기본 → 키 삭제(undefined). 회귀 가드(contentAlignV 미지정=flex 미부여) 보존 + 좌상단 셀=초기화.
    if (h === 'start') delete layout.contentAlignH;
    else layout.contentAlignH = h;
    if (v === 'start') delete layout.contentAlignV;
    else layout.contentAlignV = v;
    onPatch({ layout } as Partial<ComposerNode>);
  };
  const hint =
    node.kind === 'group'
      ? '자식 노드들의 정렬(세로는 박스가 자식보다 클 때 적용)'
      : '박스 안 콘텐츠 정렬(세로는 박스가 콘텐츠보다 클 때 적용)';
  return (
    <Align2DGrid
      label={label}
      valueH={node.layout.contentAlignH}
      valueV={node.layout.contentAlignV}
      onPick={onPick}
      hint={hint}
    />
  );
}

/**
 * 표 셀 콘텐츠 정렬 편집 — 노드 ContentAlignEditor와 동일하게 9방향 그리드(Align2DGrid) 재사용.
 * 셀은 layout이 아니라 TableCell.contentAlignH/V를 직접 쓰며, 'start'=엔진 기본이라 undefined로 저장.
 * 단일 셀(CellEditor)·일괄(BulkCellEditor) 양쪽에서 공유. onPick은 raw(H,V)를 받아 호출측이 patch.
 */
function CellContentAlignEditor({
  valueH,
  valueV,
  onPick,
  label = '셀 정렬',
  mixedH = false,
  mixedV = false,
}: {
  valueH: TableCell['contentAlignH'];
  valueV: TableCell['contentAlignV'];
  onPick: (h: AlignVal, v: AlignVal) => void;
  label?: string;
  /** 다중 셀 일괄 편집에서 정렬이 혼합이면 그리드 미선택 + 라벨에 (혼합) 표시. */
  mixedH?: boolean;
  mixedV?: boolean;
}) {
  return (
    <Align2DGrid
      label={label}
      valueH={valueH}
      valueV={valueV}
      onPick={onPick}
      mixedH={mixedH}
      mixedV={mixedV}
      hint="셀 안 콘텐츠 정렬(세로는 행 높이가 콘텐츠보다 클 때 적용)"
    />
  );
}

/**
 * 정렬(위치) 편집 — 가로(alignH: 왼쪽/가운데/오른쪽) · 세로(alignV: 위/가운데/아래).
 *
 * 크기/정렬 직교 모델: 정렬은 위치 전용(채움 stretch 없음). 각 축의 정렬이 무의미한 경우 비활성:
 *  - 주축(부모 흐름 방향) — 그 축 위치는 흐름이 결정하므로 정렬 비활성.
 *    parentCtx='group-row'면 가로가 주축, 'group-column'/'flow'면 세로가 주축.
 *  - 채움(size='fill' 명시) — 전폭/전고 점유라 위치 개념 없음.
 * content-hug(결재란)는 크기가 내용 고정이라 두 정렬 모두 유효(블록 위치 지정).
 */
function AlignEditor({
  node,
  onPatch,
  parentCtx,
  isContentHug,
}: {
  node: ComposerNode;
  onPatch: (patch: Partial<ComposerNode>) => void;
  parentCtx: 'group-row' | 'group-column' | 'stack' | 'flow';
  isContentHug: boolean;
}) {
  // 주축 판별: row 그룹은 가로가 주축, column 그룹·flow는 세로가 주축.
  const hMain = parentCtx === 'group-row';
  const vMain = parentCtx === 'group-column' || parentCtx === 'flow';
  // 채움 비활성: 주축은 명시 'fill'만(미지정=콘텐츠). 교차축(row의 세로/column의 가로)은 'fill' 또는 미지정(기본 채움)도 비활성. content-hug면 채움 무관.
  const hCross = parentCtx === 'group-column'; // column 그룹에서 가로가 교차축
  const vCross = parentCtx === 'group-row';    // row 그룹에서 세로가 교차축
  const hFillDisabled = !isContentHug && (node.layout.width === 'fill' || (hCross && node.layout.width === undefined));
  const vFillDisabled = !isContentHug && (node.layout.height === 'fill' || (vCross && node.layout.height === undefined));
  const hDisabled = hMain || hFillDisabled;
  const vDisabled = vMain || vFillDisabled;
  const hTitle = hMain
    ? '주축이라 정렬이 적용되지 않습니다'
    : hFillDisabled
      ? '채움이라 정렬이 적용되지 않습니다'
      : '부모 안에서 가로 위치';
  const vTitle = vMain
    ? '주축이라 정렬이 적용되지 않습니다'
    : vFillDisabled
      ? '채움이라 정렬이 적용되지 않습니다'
      : '부모 안에서 세로 위치';

  const onPick = (h: AlignVal, v: AlignVal) => {
    const layout = { ...node.layout };
    if (!hDisabled) layout.alignH = h; // 노드 위치는 명시값 기록(엔진 기본 ?? 'start'와 일치)
    if (!vDisabled) layout.alignV = v;
    onPatch({ layout } as Partial<ComposerNode>);
  };
  const hint = [hDisabled ? hTitle : '부모 안 가로 위치', vDisabled ? vTitle : '부모 안 세로 위치'].join(' / ');

  return (
    <Align2DGrid
      label="노드 위치"
      // 잠긴 축은 'center'로 표시 → 조절 가능한 교차축이 그리드 가운데 줄에 오게(＋=가운데). 저장값엔 영향 없음(잠긴 축은 onPick에서 미기록).
      valueH={hDisabled ? 'center' : node.layout.alignH}
      valueV={vDisabled ? 'center' : node.layout.alignV}
      onPick={onPick}
      disabledH={hDisabled}
      disabledV={vDisabled}
      hint={hint}
    />
  );
}

/** BoxSpacing(4방향) 공용 입력 — 표시=unit(양식 탭 mm 기본/작성 페이지 px), 저장=px. 0/빈값은 키 제거, 전부 비면 undefined. */
type BoxPad = NonNullable<ComposerNode['layout']['padding']>;
function BoxSpacingFields({
  value,
  onChange,
  onPreview,
  onRevert,
  unit = 'mm',
}: {
  value: BoxPad | undefined;
  onChange: (next: BoxPad | undefined) => void;
  /** 숫자 입력 실시간 미리보기(replacePresent). 미전달 시 onChange로 폴백(즉시 확정). */
  onPreview?: (next: BoxPad | undefined) => void;
  /** 미리보기 폐기(discardTransient) — Esc/이탈. 미전달 시 no-op. */
  onRevert?: () => void;
  /** 표시 단위 — 인쇄 지면(노드)=mm, 웹 폼(위젯)=px (결정: 2026-07-13-writeform-units-web-native). */
  unit?: 'mm' | 'px';
}) {
  const p = value ?? {};
  // 한 변 값(px, 미설정/0=키 제거) → 갱신본 BoxPad(전부 비면 undefined).
  const build = (side: 'top' | 'right' | 'bottom' | 'left', px: number | undefined): BoxPad | undefined => {
    const next: BoxPad = { ...p };
    if (px == null || px <= 0) delete next[side];
    else next[side] = px;
    return Object.keys(next).length === 0 ? undefined : next;
  };
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
      {(
        [
          ['top', '위'],
          ['bottom', '아래'],
          ['left', '왼쪽'],
          ['right', '오른쪽'],
        ] as const
      ).map(([side, label]) => (
        <label key={side} className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className="w-8 shrink-0">{label}</span>
          <NumericField
            value={p[side] ?? undefined}
            min={0}
            step={unit === 'px' ? 1 : 0.01}
            placeholder="0"
            toDisplay={unit === 'px' ? (n) => n : pxToMm}
            fromDisplay={unit === 'px' ? (n) => n : mmToPx}
            className="w-full px-1.5 py-1 border border-gray-300 rounded"
            onPreview={(px) => (onPreview ?? onChange)(build(side, px))}
            onCommit={(px) => onChange(build(side, px))}
            onRevert={onRevert ?? (() => {})}
          />
        </label>
      ))}
    </div>
  );
}

/**
 * 안쪽 여백(padding) 편집 — layout.padding(BoxSpacing) 4방향. 입력=mm(2자리), 저장=px.
 * 모든 노드(표·결재란·그룹·텍스트 등)에 적용 — 콘텐츠와 노드 경계 사이 간격.
 */
function PaddingEditor({
  node,
  onPreview,
  onCommit,
  onRevert,
}: {
  node: ComposerNode;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
  onRevert: () => void;
}) {
  const buildLayout = (next: BoxPad | undefined) => {
    const nextLayout = { ...node.layout };
    if (next === undefined) delete nextLayout.padding;
    else nextLayout.padding = next;
    return { layout: nextLayout } as Partial<ComposerNode>;
  };
  const setPadding = (next: BoxPad | undefined) => onCommit(buildLayout(next));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500 font-mono">안쪽 여백(mm)</span>
        <button
          type="button"
          onClick={() => setPadding(undefined)}
          disabled={node.layout.padding === undefined}
          className="text-[11px] text-brand-600 hover:underline disabled:text-gray-300 disabled:no-underline disabled:cursor-default"
        >
          초기화
        </button>
      </div>
      <BoxSpacingFields
        value={node.layout.padding}
        onChange={setPadding}
        onPreview={(next) => onPreview(buildLayout(next))}
        onRevert={onRevert}
      />
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500">{label}</dt>
      <dd className={`text-gray-800 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</dd>
    </div>
  );
}

// ============================================================
// 다중선택 공통 속성 일괄 편집 (BulkNodePropsEditor)
// ============================================================
// (foldNodes는 상단 제네릭 `fold`의 별칭으로 통합됨 — 별도 정의 제거.)

/** width/height 저장값을 세그먼트 모드로 정규화. undefined(컨텍스트 기본)는 'unset'(미선택). */
function sizeMode(v: number | string | undefined): 'auto' | 'fill' | 'mm' | 'unset' {
  if (v === undefined) return 'unset';
  if (v === 'auto') return 'auto';
  if (typeof v === 'number') return 'mm';
  return 'fill'; // 'fill' 및 기타 문자열('%' 등)은 채움 취급
}

/** padding 4방향이 동일하면 그 px, 없으면 0, 방향별로 다르면 null(혼합 취급). */
function uniformPad(p: BoxPad | undefined): number | null {
  if (!p) return 0;
  const vals = [p.top, p.right, p.bottom, p.left].map((x) => x ?? 0);
  return vals.every((x) => x === vals[0]) ? vals[0] : null;
}

/** 세그먼트(택1) 컨트롤 — 혼합이면 어느 값도 active로 표시하지 않는다. */
function BulkSegment<T extends string>({
  options,
  value,
  mixed,
  onPick,
}: {
  options: { value: T; label: string }[];
  value: T | undefined;
  mixed: boolean;
  onPick: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded border border-gray-300 overflow-hidden">
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onPick(o.value)}
          className={`px-2 py-1 text-xs ${i > 0 ? 'border-l border-gray-300' : ''} ${
            !mixed && value === o.value
              ? 'bg-brand-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * mm 입력(일괄) — 저장은 px. 혼합이면 placeholder "혼합"으로 표시하고 값은 비운다.
 * 타이핑/스피너/화살표로 값이 변하는 동안 onPreview(replacePresent)로 즉시 반영, Enter/blur에
 * onCommit(commitPresent)으로 확정한다(undo 1스텝). Esc는 편집 시작 전 값으로 복원(onRevert).
 * 화살표 ↑/↓ ±1, Shift ±10, Alt ±0.1(mm 단위). 빈값은 미리보기 skip이나 commit은 undefined로 전달.
 */
function BulkMmInput({
  px,
  mixed,
  placeholder = 'mm',
  onPreview,
  onCommit,
  onRevert,
}: {
  px: number | null | undefined;
  mixed: boolean;
  placeholder?: string;
  /** 유효 mm 입력 미리보기(replacePresent) — px 변환 후 전달. */
  onPreview: (px: number) => void;
  /** 확정(commitPresent). 빈값=undefined. */
  onCommit: (px: number | undefined) => void;
  /** 미리보기 폐기(discardTransient) — Esc/이탈. */
  onRevert: () => void;
}) {
  const display = mixed || px == null ? '' : String(pxToMm(px));
  const input = useNumericPreviewInput({
    display,
    // 빈칸은 null(미리보기 skip) — commit 시에만 undefined로 전달. 그 외 유한 숫자만 유효.
    parse: (raw) => {
      const t = raw.trim();
      if (t === '') return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    },
    format: (n) => String(n),
    onPreview: (raw) => onPreview(mmToPx(Number(raw))),
    onCommit: (raw) => onCommit(raw.trim() === '' ? undefined : mmToPx(Number(raw))),
    onRevert,
    min: 0,
  });
  return (
    <input
      type="number"
      min={0}
      step={0.01}
      placeholder={mixed ? '혼합' : placeholder}
      className="w-20 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
      {...input}
    />
  );
}

/**
 * 다중선택 공통 속성 일괄 편집 — 선택된 노드들에 폭/높이/최소높이/정렬/패딩을 일괄 적용한다.
 *
 * - 혼합(값이 노드마다 다름) 판정은 필드별 독립(폭은 일치·높이는 혼합이면 폭만 값 표시).
 * - 세그먼트=혼합이면 미선택, mm 입력=혼합이면 placeholder "혼합"(기존 값 강제 표시 금지).
 * - 사용자가 클릭/입력한 필드만 patch — 자동 전환이 미명시 필드를 덮지 않는다.
 * - 정렬은 가로/세로 세그먼트 2줄로 분리 → 클릭한 축만 적용(다른 축 보존).
 * - 각 적용은 노드별 기존 layout에 merge(통째 교체 금지) + 한 번의 apply = undo 1회.
 */
function BulkNodePropsEditor({
  nodeIds,
  template,
  apply,
  replacePresent,
  commitPresent,
  discardTransient,
}: {
  nodeIds: string[];
  template: ReportTemplate;
  apply: (fn: (t: ReportTemplate) => ReportTemplate) => void;
  /** 세그먼트 등 단발 변경은 apply(1엔트리). mm 입력은 preview/commit 2단계로 배선. */
  replacePresent: (fn: (t: ReportTemplate) => ReportTemplate) => void;
  commitPresent: (fn: (t: ReportTemplate) => ReportTemplate) => void;
  discardTransient: () => void;
}) {
  // 선택된 노드 실체(부재 id 제외).
  const nodes = useMemo(
    () =>
      nodeIds
        .map((id) => findNodeById(template.body, id))
        .filter((n): n is ComposerNode => !!n),
    [nodeIds, template.body],
  );

  // 공통 patch producer — 각 노드의 기존 layout에 merge(통째 교체 금지). reduce로 한 트랜잭션.
  const layoutProducer = useCallback(
    (patch: Partial<ComposerNode['layout']>) => (t: ReportTemplate) =>
      nodeIds.reduce((acc, id) => {
        const n = findNodeById(t.body, id);
        if (!n) return acc;
        return updateNode(acc, id, {
          layout: { ...n.layout, ...patch },
        } as Partial<ComposerNode>);
      }, t),
    [nodeIds],
  );
  // 세그먼트 등 단발 변경(undo 1회).
  const applyLayout = useCallback(
    (patch: Partial<ComposerNode['layout']>) => apply(layoutProducer(patch)),
    [apply, layoutProducer],
  );
  // mm 입력 실시간 미리보기(replacePresent — 히스토리 미적재).
  const previewLayout = useCallback(
    (patch: Partial<ComposerNode['layout']>) => replacePresent(layoutProducer(patch)),
    [replacePresent, layoutProducer],
  );
  // mm 입력 확정(commitPresent — 선행 미리보기 transient를 undo 1스텝으로 닫음).
  const commitLayout = useCallback(
    (patch: Partial<ComposerNode['layout']>) => commitPresent(layoutProducer(patch)),
    [commitPresent, layoutProducer],
  );

  if (nodes.length === 0) return null;

  // 필드별 혼합 판정.
  const widthMode = foldNodes(nodes, (n) => sizeMode(n.layout.width));
  const widthNum = foldNodes(nodes, (n) => (typeof n.layout.width === 'number' ? n.layout.width : null));
  const heightMode = foldNodes(nodes, (n) => sizeMode(n.layout.height));
  const heightNum = foldNodes(nodes, (n) => (typeof n.layout.height === 'number' ? n.layout.height : null));
  const minHeightFold = foldNodes(nodes, (n) => n.layout.minHeight ?? null);
  const alignHFold = foldNodes(nodes, (n) => n.layout.alignH ?? null);
  const alignVFold = foldNodes(nodes, (n) => n.layout.alignV ?? null);
  const paddingFold = foldNodes(nodes, (n) => uniformPad(n.layout.padding));

  const SIZE_OPTS = [
    { value: 'auto' as const, label: '자동' },
    { value: 'fill' as const, label: '채움' },
    { value: 'mm' as const, label: '고정' },
  ];
  const H_ALIGN_OPTS = [
    { value: 'start' as const, label: '좌' },
    { value: 'center' as const, label: '중' },
    { value: 'end' as const, label: '우' },
  ];
  const V_ALIGN_OPTS = [
    { value: 'start' as const, label: '위' },
    { value: 'center' as const, label: '중' },
    { value: 'end' as const, label: '아래' },
  ];

  // 세그먼트에 표시할 active 값('unset'/혼합이면 미선택).
  const widthActive =
    widthMode.mixed || widthMode.value === 'unset' || widthMode.value === undefined
      ? undefined
      : widthMode.value;
  const heightActive =
    heightMode.mixed || heightMode.value === 'unset' || heightMode.value === undefined
      ? undefined
      : heightMode.value;
  // padding 혼합: 노드 간 상이 OR 노드 내 방향별 상이(null).
  const paddingMixed = paddingFold.mixed || paddingFold.value == null;

  const rowCls = 'flex items-center gap-2';
  const labelCls = 'text-xs text-gray-500 w-16 shrink-0';

  return (
    <div className="pt-3 mt-1 border-t border-gray-200 space-y-2">
      <dt className="text-xs font-semibold text-gray-500">공통 속성</dt>
      <dd className="space-y-2">
        {/* 폭 */}
        <div className={rowCls}>
          <span className={labelCls}>너비</span>
          <BulkSegment
            options={SIZE_OPTS}
            value={widthActive}
            mixed={widthMode.mixed}
            onPick={(m) =>
              m === 'mm' ? applyLayout({ width: widthNum.value ?? mmToPx(50) }) : applyLayout({ width: m })
            }
          />
          {widthActive === 'mm' && (
            <BulkMmInput
              px={widthNum.value}
              mixed={widthNum.mixed}
              onPreview={(px) => previewLayout({ width: px })}
              onCommit={(px) => px !== undefined && commitLayout({ width: px })}
              onRevert={discardTransient}
            />
          )}
          {widthActive === 'mm' && <span className="text-[10px] text-gray-400">mm</span>}
          {widthMode.mixed && <span className="text-[11px] text-gray-400">— 혼합 —</span>}
        </div>

        {/* 높이 */}
        <div className={rowCls}>
          <span className={labelCls}>높이</span>
          <BulkSegment
            options={SIZE_OPTS}
            value={heightActive}
            mixed={heightMode.mixed}
            onPick={(m) =>
              m === 'mm' ? applyLayout({ height: heightNum.value ?? mmToPx(30) }) : applyLayout({ height: m })
            }
          />
          {heightActive === 'mm' && (
            <BulkMmInput
              px={heightNum.value}
              mixed={heightNum.mixed}
              onPreview={(px) => previewLayout({ height: px })}
              onCommit={(px) => px !== undefined && commitLayout({ height: px })}
              onRevert={discardTransient}
            />
          )}
          {heightActive === 'mm' && <span className="text-[10px] text-gray-400">mm</span>}
          {heightMode.mixed && <span className="text-[11px] text-gray-400">— 혼합 —</span>}
        </div>

        {/* 최소 높이 */}
        <div className={rowCls}>
          <span className={labelCls}>최소 높이</span>
          <BulkMmInput
            px={minHeightFold.value}
            mixed={minHeightFold.mixed}
            onPreview={(px) => previewLayout({ minHeight: px })}
            onCommit={(px) => commitLayout({ minHeight: px })}
            onRevert={discardTransient}
          />
          <span className="text-[10px] text-gray-400">mm</span>
        </div>

        {/* 정렬(가로/세로 분리 — 클릭한 축만 적용) */}
        <div className={rowCls}>
          <span className={labelCls}>가로 정렬</span>
          <BulkSegment
            options={H_ALIGN_OPTS}
            value={alignHFold.value ?? undefined}
            mixed={alignHFold.mixed}
            onPick={(v) => applyLayout({ alignH: v })}
          />
          {alignHFold.mixed && <span className="text-[11px] text-gray-400">— 혼합 —</span>}
        </div>
        <div className={rowCls}>
          <span className={labelCls}>세로 정렬</span>
          <BulkSegment
            options={V_ALIGN_OPTS}
            value={alignVFold.value ?? undefined}
            mixed={alignVFold.mixed}
            onPick={(v) => applyLayout({ alignV: v })}
          />
          {alignVFold.mixed && <span className="text-[11px] text-gray-400">— 혼합 —</span>}
        </div>

        {/* 패딩(전 방향 동일 — MVP) */}
        <div className={rowCls}>
          <span className={labelCls}>안쪽 여백</span>
          <BulkMmInput
            px={paddingMixed ? null : paddingFold.value}
            mixed={paddingMixed}
            onPreview={(px) =>
              previewLayout({ padding: { top: px, right: px, bottom: px, left: px } })
            }
            onCommit={(px) =>
              commitLayout({
                padding:
                  px === undefined ? undefined : { top: px, right: px, bottom: px, left: px },
              })
            }
            onRevert={discardTransient}
          />
          <span className="text-[10px] text-gray-400">mm (전 방향)</span>
        </div>
      </dd>
    </div>
  );
}

// ============================================================
// 작성폼 편집 뷰 (3e-A) — viewMode==='writeForm'
// ============================================================

// ── 작성 페이지 위젯 트리 저작 편집기 (단계 3, 버튼 기반) ──

interface ComposerWidgetTreeHandlers {
  onPlace: (widget: ComposerWidget, parentId: string | null) => void;
  onReorder: (id: string, dir: 'up' | 'down') => void;
  /** 드롭 시 부모 변경(dnd 전용) — 버튼 경로의 이동/제거는 우클릭 메뉴가 핸들러를 직접 호출한다. */
  onMoveTo: (id: string, parentId: string | null) => void;
  onAddGroup: (parentId: string | null) => void;
  onAddDesign: (kind: 'separator' | 'heading' | 'spacer', parentId: string | null) => void;
  onAddRegistered: (widgetKey: string, parentId: string | null) => void;
  onAddFormTable: (parentId: string | null) => void;
  /** 독립 입력 위젯(treeOwned) 추가 — body 바인딩 없이 카탈로그 자동 채움을 속성에서 지정. */
  onAddInput: (parentId: string | null) => void;
  /** 커스텀 모달 버튼 위젯 추가(버튼 + 빈 모달 셸). */
  onAddModalTrigger: (parentId: string | null) => void;
  /** 반복 위젯 추가 — 빈 반복(over 미지정) 삽입, 대상은 속성 패널 over select에서 지정(범용 항목). */
  onAddRepeat: (parentId: string | null) => void;
  /** 분기 위젯 추가 — 슬롯 2개(빈 else), 슬롯 조건은 속성 패널에서 지정(범용 항목). */
  onAddBranch: (parentId: string | null) => void;
  /** 프리셋 삽입 — "+ 위젯" '프리셋' optgroup(우클릭 메뉴 '프리셋' 항목과 동일 삽입 경로). */
  onInsertPreset: (presetId: string, parentId: string | null) => void;
  /** 삽입 가능한 프리셋 라이브러리 목록 — 비면 optgroup 미노출. */
  presetOptions: { id: string; name: string }[];
  onUpdate: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  // activeView(활성 반응형 뷰 키)는 제거 — 뷰별 델타를 쓰는 유일한 행 컨트롤이던 단 수 select가
  // 속성 패널로 일원화됐다(2026-07-22 위젯 트리=구조 / 속성=내용·표현 분리).
  /** formTable 셀 위젯 행 클릭 — 표 선택+셀 포커스(캔버스 셀 선택과 동일 경로, 2026-07-14). */
  onFocusCell: (tableId: string, rowId: string, columnId: string) => void;
  /** formTable 셀 위젯 행 hover — 캔버스 셀 하이라이트(ftHoverCell 채널, leave 시 null). */
  onHoverCell: (cell: { tableId: string; rowId: string; columnId: string } | null) => void;
  /** 트리 행 우클릭 — 캔버스 위젯 우클릭과 동일 컨텍스트 메뉴(2026-07-16 사용자 요청). */
  onRowContextMenu?: (widgetId: string, e: React.MouseEvent) => void;
  /** 셀 포커스 anchor 키(`tableId::rowId::columnId`) — 트리 셀 위젯 행 선택 하이라이트. */
  focusedCellKey: string | null;
  /** 분기 위젯 id → 활성 슬롯 index(캔버스 슬롯 탭과 공유). 트리 슬롯 서브행 활성 하이라이트용. */
  activeBranchSlots: Record<string, number>;
  /** 분기 슬롯 서브행 클릭 → 활성 슬롯 전환(캔버스 탭과 동일 경로). */
  onSetActiveBranchSlot: (branchId: string, index: number) => void;
}

/**
 * 뷰 컨텍스트 헤더 — 속성 패널이 지금 **어느 뷰를 편집 중이고, 그중 무엇이 이 뷰에만 저장되는지**를
 * 알린다. 비-base 뷰에서만 렌더(base는 기본 상태라 안내가 노이즈).
 *
 * 뷰별로 갈라지는 축은 `ComposerViewOverride`의 4개(단 수·폭 비율·숨김·순서)뿐이고 라벨·바인딩·
 * 표시 형식 등 나머지는 전부 전역인데, 화면에 그 경계가 없어 비-base 뷰에서 만진 속성이 이 뷰에만
 * 적용된다고 오해한다(2026-08-06 사용자 지적: "바형 기본정보 현장 위젯을 숨김 처리하면 모든
 * 뷰에서 감춰지는데 의도된 건가?" — 전역 `표시 모드 → 숨김`과 뷰별 `이 뷰에서 숨김`이 캔버스에서
 * 똑같이 dimmed로 보인다).
 *
 * ⚠️ 축 목록을 문구로 적는 이상 `ComposerViewOverride`에 축을 추가하면 여기도 고쳐야 한다 —
 * 안내만 낡으면 없느니만 못하다.
 */
function WriteFormViewContextHeader({
  activeView,
}: {
  activeView: string;
}): React.ReactElement | null {
  if (activeView === COMPOSER_BASE_VIEW_KEY) return null;
  return (
    <div className="rounded border border-brand-200 bg-brand-50 px-2 py-1.5">
      <p className="text-[11px] font-medium text-brand-700">
        {composerViewLabel(activeView)} 편집 중
      </p>
      <p className="mt-0.5 text-[10px] leading-snug text-brand-600">
        이 뷰에만 저장되는 속성은 <span className="font-medium">단 수 · 폭 비율 · 이 뷰에서 숨김 ·
        순서</span> 4가지입니다. 라벨·바인딩·표시 형식 등 나머지는 모든 뷰에 함께 적용됩니다.
      </p>
    </div>
  );
}

/**
 * 그룹 단 수를 활성 뷰에 기록. base 뷰=위젯 직접 columns / 그 외=responsive[view].columns 델타.
 * value=undefined면 그 뷰 오버라이드 제거(상속 복귀). 결정 2026-07-09-composer-responsive-view-overrides §2.
 */
function setGroupColumnsForView(
  w: ComposerWidget,
  view: string,
  value: number | undefined,
): ComposerWidget {
  if (w.kind !== 'group') return w;
  if (view === COMPOSER_BASE_VIEW_KEY) return { ...w, columns: value };
  const resp: Record<string, ComposerViewOverride> = { ...(w.responsive ?? {}) };
  const cur: ComposerViewOverride = { ...(resp[view] ?? {}) };
  if (value == null) delete cur.columns;
  else cur.columns = value;
  if (Object.keys(cur).length) resp[view] = cur;
  else delete resp[view];
  return { ...w, responsive: Object.keys(resp).length ? resp : undefined };
}

/**
 * 단 수 select 아래 상태 안내 — 저작자가 "지금 보이는 숫자가 어디서 왔는지"를 알게 한다.
 *
 * 비-base 뷰의 select는 **오버라이드 값**만 보여준다(없으면 '기본값 상속'). 그것만으로는 상속
 * 상태에서 실제 몇 단으로 그려지는지·기본이 몇 단인지 알 수 없다(2026-08-06 사용자 지적).
 * base 값과 해소값을 비교해 셋을 가른다.
 *
 * ⚠️ degrade 조건(폭 < md)을 여기서 다시 판정하지 않는다 — `resolveGroupColumns`가 그 규칙의
 * 정본이고, 같은 판정을 두 곳에 두면 규칙이 바뀔 때 안내만 거짓말이 된다.
 */
function GroupColumnsViewHint({
  widget,
  activeView,
}: {
  widget: GroupWidget;
  activeView: string;
}): React.ReactElement | null {
  if (activeView === COMPOSER_BASE_VIEW_KEY) return null;
  const base = Math.max(1, Math.floor(widget.columns ?? 1));
  const resolved = resolveGroupColumns(widget, activeView);
  const override = widget.responsive?.[activeView]?.columns;
  const viewLabel = composerViewLabel(activeView);

  if (override != null) {
    return (
      <p className="mt-0.5 text-[10px] leading-snug text-brand-600">
        기본 {base}단 — 이 뷰에서 {resolved}단으로 고정 중입니다. &lsquo;기본값 상속&rsquo;을 고르면
        되돌아갑니다.
      </p>
    );
  }
  if (resolved !== base) {
    return (
      <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
        기본 {base}단 — {viewLabel}은 폭이 좁아 자동으로 {resolved}단입니다. 값을 고르면 이 뷰에
        고정됩니다.
      </p>
    );
  }
  return (
    <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
      기본 {base}단을 그대로 씁니다. 값을 고르면 이 뷰에만 적용됩니다.
    </p>
  );
}

/** 위젯을 이 뷰에서 숨김/표시. base 뷰엔 미적용(글로벌 mode 사용). */
function setWidgetHiddenForView(w: ComposerWidget, view: string, hidden: boolean): ComposerWidget {
  if (view === COMPOSER_BASE_VIEW_KEY) return w;
  const resp: Record<string, ComposerViewOverride> = { ...(w.responsive ?? {}) };
  const cur: ComposerViewOverride = { ...(resp[view] ?? {}) };
  if (hidden) cur.hidden = true;
  else delete cur.hidden;
  if (Object.keys(cur).length) resp[view] = cur;
  else delete resp[view];
  return { ...w, responsive: Object.keys(resp).length ? resp : undefined };
}

/**
 * 위젯 폭 비율(span)을 활성 뷰에 기록. base 뷰=위젯 직접 span / 그 외=responsive[view].span 델타.
 * 정리 규약(빈 뷰 키·빈 responsive 제거)은 setGroupColumnsForView와 동형.
 *
 * ⚠️ 비-base 뷰의 `'auto'`는 "미지정"이 아니라 **명시적 해제**다 — resolveWidgetSpanRatio가
 * `override ?? base`로 읽으므로, base에 비율이 있어도 이 뷰만 자동 산출로 되돌린다. 그래서 상속
 * 복귀(=base를 그대로 따름)는 value=undefined(키 제거)이고, '자동'은 'auto'를 **값으로** 기록한다.
 * 둘을 같은 것으로 취급하면 base 비율을 뷰에서 해제할 수단이 사라진다('auto'의 존재 이유).
 */
function setWidgetSpanForView(
  w: ComposerWidget,
  view: string,
  value: number | 'auto' | undefined,
): ComposerWidget {
  if (view === COMPOSER_BASE_VIEW_KEY) return { ...w, span: value };
  const resp: Record<string, ComposerViewOverride> = { ...(w.responsive ?? {}) };
  const cur: ComposerViewOverride = { ...(resp[view] ?? {}) };
  if (value == null) delete cur.span;
  else cur.span = value;
  if (Object.keys(cur).length) resp[view] = cur;
  else delete resp[view];
  return { ...w, responsive: Object.keys(resp).length ? resp : undefined };
}

/** 위젯 안쪽 여백(padding) 설정 — 전 위젯 공통(ComposerWidgetBase.padding). next=undefined면 키 제거(JSON 미방출). */
function setWidgetPadding(x: ComposerWidget, next: BoxPad | undefined): ComposerWidget {
  return { ...x, padding: next };
}

/**
 * 위젯 안쪽 여백 편집 컨트롤(전 위젯 공통, parity P3 2026-07-13) — ComposerWidgetBase.padding(BoxSpacing)
 * 4방향. 입력=px(웹 폼 — 결정: 2026-07-13-writeform-units-web-native), 저장=px. 노드 PaddingEditor 동형.
 * 콘텐츠와 위젯 경계 사이 간격.
 */
function WidgetPaddingControl({
  w,
  onWidgetUpdate,
}: {
  w: ComposerWidget;
  onWidgetUpdate: (id: string, updater: (x: ComposerWidget) => ComposerWidget) => void;
}) {
  const setPadding = (next: BoxPad | undefined) =>
    onWidgetUpdate(w.id, (x) => setWidgetPadding(x, next));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">안쪽 여백(px)</span>
        <button
          type="button"
          onClick={() => setPadding(undefined)}
          disabled={w.padding === undefined}
          className="text-[11px] text-brand-600 hover:underline disabled:cursor-default disabled:text-gray-300 disabled:no-underline"
        >
          초기화
        </button>
      </div>
      <BoxSpacingFields unit="px" value={w.padding} onChange={setPadding} />
    </div>
  );
}

/** 위젯 한 줄 요약 텍스트. */
/**
 * 분기 위젯 슬롯 탭/서브행 표시 라벨 — label 우선 → 조건 요약(비지 않은 condition) → '슬롯 N'.
 * 본문 chainOfBranches.slotLabel 관례의 위젯판(위젯 슬롯은 condition undefined/''가 모두 else 의미라
 * 조건이 비면 순번 라벨로 폴백). 탭이 과하게 길어지지 않게 truncate.
 */
function widgetBranchSlotLabel(slot: BranchWidgetSlot, index: number): string {
  const label = slot.label?.trim();
  if (label) return label.length > 22 ? `${label.slice(0, 21)}…` : label;
  const c = slot.condition?.trim();
  if (c) return c.length > 22 ? `${c.slice(0, 21)}…` : c;
  return `슬롯 ${index + 1}`;
}

function composerWidgetSummary(w: ComposerWidget): string {
  switch (w.kind) {
    case 'input':
      if (w.widget === 'registered') {
        const meta = REGISTERED_FORM_WIDGETS_META.find((m) => m.key === w.widgetKey);
        return `등록 위젯 · ${meta?.label ?? w.widgetKey ?? '(위젯)'}`;
      }
      return `${w.label ?? w.bindingKey} · ${w.widget}${w.required ? ' *' : ''}`;
    case 'group':
      // 섹션명만 표시(2026-07-12 사용자) — 단 수는 속성 패널이, 자식 수는 하위 목록이 이미 보여줌.
      return w.title || '섹션';
    case 'formTable':
      return w.title || '양식 표';
    case 'modalTrigger':
      // 트리 패널 요약 — 라벨 + 모달 콘텐츠 개수(콘텐츠 저작은 P1b, 여기선 요약만).
      return `모달 버튼 · ${w.label || '구성'} (콘텐츠 ${w.children.length}개)`;
    case 'repeat':
      // 행 스코프 조립 컨테이너 — 반복 대상 데이터소스 key + 행 템플릿 위젯 수(저작은 P2).
      return `반복 · ${w.over} (행 템플릿 ${w.children.length}개)`;
    case 'branch':
      // 분기(투명 컨테이너) — 슬롯 수 요약(저작은 P3). BranchWidget엔 text 없어 default 폴백 불가.
      return `분기 · ${w.branches.length}슬롯`;
    default:
      return `${w.kind}${w.text ? ` · ${w.text}` : ''}`;
  }
}

/**
 * 등록형 폼 위젯 추가 팔레트 컨트롤("+위젯") — 카탈로그(REGISTERED_FORM_WIDGETS_META)에서 골라 추가.
 * select 후 즉시 추가(1종이라도 select 방식 유지 — 미래 확장). 값은 선택 즉시 초기화.
 */
/**
 * 위젯 트리 패널이 좁아졌을 때(컨테이너 폭 < 임계값) narrow 모드 여부를 하위 행에 전파.
 * 윈도우가 아닌 패널 컨테이너 폭 기준(스플리터로 좁힐 수 있음) — ComposerWidgetTreeEditor가 ResizeObserver로 측정.
 */
const TREE_PANEL_NARROW_PX = 360;
const TreePanelNarrowContext = createContext(false);

/**
 * 트리 행 라벨 rename의 **키보드(F2) 요청** 값 — 행의 id와 같으면 그 행이 인라인 편집으로 진입한다.
 * `nonce`는 같은 행에 대한 재요청을 구분한다(캔버스 인라인 편집 `wtEditRequest`와 같은 패턴).
 *
 * ⚠️ 행(`ComposerWidgetEditRow`)은 호출부가 **5곳**이고 자신을 재귀 렌더하므로 한 곳만 빠뜨리면
 * **그 자리에서만 F2가 조용히 죽는다.** 그래서 옆의 `TreePanelNarrowContext`처럼 context로 내리지
 * 않고 **필수 prop**으로 둔다 — 누락이 `TS2741`로 **컴파일 때** 드러나는 쪽이 낫다(조용한 실패를
 * 타입으로 바꾼 자리).
 */
type ComposerTreeRenameRequest = { id: string; nonce: number } | null;

/**
 * 트리 행 라벨을 그 자리에서 고칠 수 있는 kind — heading(text)·input(label)뿐이다.
 *
 * ⚠️ **행과 F2 핸들러가 이 하나를 공유한다.** 손으로 복제하면 한쪽만 넓어져 「F2는 먹는데 행은
 * 편집으로 안 들어가는」(또는 그 반대) 상태가 되고, 둘 다 틀리지 않았으므로 아무 검사도 안 깨진다.
 */
function composerCanRenameWidget(w: ComposerWidget | null | undefined): boolean {
  return !!w && (w.kind === 'heading' || w.kind === 'input');
}

/** 위젯 트리 팔레트 버튼/‘+위젯’ select 공통 스타일 — 동일 높이(h-6)·회색 보더로 통일. */
const TREE_PALETTE_CTRL_CLASS =
  'flex h-6 shrink-0 items-center whitespace-nowrap rounded border border-gray-300 bg-gray-50 px-1.5 text-gray-700 hover:bg-gray-100';
/** 배치 행 컨트롤(↑/↓) 공통 높이 — 라벨을 밀지 않도록 shrink-0 그룹으로 묶는다. */
const TREE_ROW_CTRL_CLASS = 'flex h-6 shrink-0 items-center whitespace-nowrap rounded px-1';
/**
 * 자식 컨테이너(섹션·모달·반복·분기 슬롯·표 셀 목록) 들여쓰기 — **단계당 12px 한 곳에서만** 준다
 * (행에도 12px을 더하던 구 방식은 단계당 24px이라 깊은 구조에서 라벨이 가로로 밀려났다).
 * 줄어든 들여쓰기의 계층 가독성은 좌측 세로 가이드선이 보완(2026-07-22 사용자 확정).
 */
const TREE_CHILD_INDENT_CLASS = 'border-l border-gray-200 pl-3';

// 행 삭제/빼기 아이콘(TRASH_ICON·EJECT_ICON)은 제거 — 배치 행의 삭제는 우클릭 메뉴 '삭제'로
// 통합됐다(2026-07-22 위젯 트리=구조 정리). 메뉴 항목은 텍스트라 아이콘 불필요.
/** 셀 컨트롤 아이콘 — 복사(겹친 사각형). */
const COPY_ICON = (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path strokeLinecap="round" d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
/** 셀 컨트롤 아이콘 — 붙여넣기(클립보드). */
const PASTE_ICON = (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <rect x="5" y="4" width="14" height="18" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h6" />
  </svg>
);

/**
 * 위젯의 "양식(body) 내 위치" 배치 목록 — 파생(body 바인딩) input에만 해당.
 * treeOwned(작성 탭 직접 생성)·등록형 위젯은 body 대응물이 없어 undefined(표시 없음).
 */
function bindingPlacementsForWidget(
  w: ComposerWidget,
  placementMap: Map<string, BindingPlacement[]>,
): BindingPlacement[] | undefined {
  if (w.kind !== 'input') return undefined;
  // treeOwned/등록형도 반복영역 over(행 소스 키) 소비처가 있으면 배치로 취급(2026-07-16 —
  // 행 목록 위젯의 '양식 노드로 이동'·풀 힌트). 매치 없으면 undefined(파생 아님 = 표시 자체 생략).
  if (w.treeOwned || w.widget === 'registered') {
    const list = placementMap.get(w.bindingKey);
    return list && list.length > 0 ? list : undefined;
  }
  // 파생 위젯이면 배열 반환(미대응이어도 [] → 속성창 "양식에서 사용되지 않음").
  return placementMap.get(w.bindingKey) ?? [];
}

/**
 * 위젯 트리 전체(group·formTable 고정 셀·modalTrigger children)에서 주어진 bindingKey를 쓰는
 * 첫 input 위젯을 찾는다 — 양식(body) 노드/셀 → 배치된 작성 위젯 역방향 내비게이션 대상 해석.
 * 매치가 없으면 null(양식 항목이 작성 탭에 배치되지 않은 경우).
 */
function findWidgetByBindingKey(tree: ComposerWidget[], bindingKey: string): InputWidget | null {
  let found: InputWidget | null = null;
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (found) return;
      if (w.kind === 'input') {
        if (w.bindingKey === bindingKey) found = w;
      } else if (w.kind === 'group' || w.kind === 'modalTrigger') {
        walk(w.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            if (cell.widget && cell.widget.bindingKey === bindingKey) {
              found = cell.widget;
              break;
            }
          }
          if (found) break;
        }
      }
    }
  };
  walk(tree);
  return found;
}

// ── 크로스 내비게이션 플래시(모듈 레벨 단일 상태) ──
// 노드↔위젯 이동 직후 대상 DOM에 잠깐 하이라이트를 씌워 시선을 유도한다. 연속 이동 시 이전
// 원복/타이머를 정리하도록 단일 ref를 유지(대상이 그새 언마운트돼도 안전한 null-safe 복원).
let crossNavFlashTimer: ReturnType<typeof setTimeout> | null = null;
let crossNavFlashEl: HTMLElement | null = null;
let crossNavFlashPrev: { outline: string; outlineOffset: string; transition: string } | null = null;

function restoreCrossNavFlash(): void {
  if (crossNavFlashEl && crossNavFlashPrev) {
    crossNavFlashEl.style.outline = crossNavFlashPrev.outline;
    crossNavFlashEl.style.outlineOffset = crossNavFlashPrev.outlineOffset;
    crossNavFlashEl.style.transition = crossNavFlashPrev.transition;
  }
  crossNavFlashEl = null;
  crossNavFlashPrev = null;
}

/**
 * 탭 전환·선택 커밋 뒤 대상 DOM(selector)으로 스크롤 + 잠깐 하이라이트(플래시).
 * 탭 전환 커밋 후 대상이 마운트되므로 rAF 2회 뒤 querySelector(레이아웃 확정 후 정확 스크롤).
 * 하이라이트는 outline만 사용(레이아웃 중립 + overflow:hidden 조상에 안 잘림 — box-shadow 대비 안전.
 * PreviewPopoutPage highlight-node 규약과 동형). 대상 미발견 시 no-op.
 */
function flashAndScroll(selector: string | string[]): void {
  const selectors = Array.isArray(selector) ? selector : [selector];
  void findAnchorEl(selectors).then((el) => {
    if (el) flashTarget(el);
  });
}

/**
 * 커밋 뒤 나타나는 대상 DOM을 기다렸다 돌려준다(못 찾으면 null).
 *
 * rAF 2회 뒤부터 보는 이유는 탭 전환·슬롯 전환 커밋 **다음**에 대상이 마운트되기 때문이고,
 * 거기서 끝내지 않고 ~1.5s 폴링하는 이유는 캔버스 마운트/프리필 재렌더가 rAF 2회보다 늦는 경우가
 * 있기 때문이다(2026-07-16 제보: 양식→작성 이동 시 스크롤 미동작). 복수 selector는 우선순위
 * 폴백(셀 앵커 → 노드 앵커).
 *
 * "못 찾았다"를 **호출자에게 돌려준다** — 분기 슬롯을 전환했는데도 앵커가 없으면 그건 다른 사유
 * (실데이터 반영 중 0행 반복 안)라, 도달을 요청한 쪽이 사유를 안내할 수 있어야 한다.
 */
function findAnchorEl(selectors: string[], timeoutMs = 1500): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const attempt = (): void => {
      let el: Element | null = null;
      for (const s of selectors) {
        el = document.querySelector(s);
        if (el) break;
      }
      if (!(el instanceof HTMLElement)) {
        if (Date.now() - started < timeoutMs) setTimeout(attempt, 80);
        else resolve(null);
        return;
      }
      resolve(el);
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(attempt);
    });
  });
}

/** 도달 플래시 기본 톤(brand-500) — 탭 전환·선택 커밋 뒤 "여기다"를 잠깐 짚어 준다. */
const CROSS_NAV_FLASH_OUTLINE = '2px solid #6366f1';
/**
 * 식별 키 공유 자리 도달 플래시(rose-600) — 관계 강조(ComputeSourceHighlightOverlay tone 'shared')와
 * **같은 색**을 쓴다. 목록에서 rose로 짚어 보던 자리로 이동했다는 것을 색으로 잇는다.
 */
const SHARED_KEY_FLASH_OUTLINE = '3px dashed #e11d48';

function flashTarget(el: HTMLElement, outline: string = CROSS_NAV_FLASH_OUTLINE): void {
  if (crossNavFlashTimer) {
    clearTimeout(crossNavFlashTimer);
    crossNavFlashTimer = null;
  }
  restoreCrossNavFlash(); // 이전 플래시 원복 후 새 대상으로 교체
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  crossNavFlashEl = el;
  crossNavFlashPrev = {
    outline: el.style.outline,
    outlineOffset: el.style.outlineOffset,
    transition: el.style.transition,
  };
  el.style.transition = 'outline-color 0.15s ease-in-out';
  el.style.outline = outline;
  el.style.outlineOffset = '2px';
  // 스크롤 재보정 — 양식 캔버스는 페이지 분할/측정 재렌더로 최초 스크롤 뒤 레이아웃이 밀릴 수 있어
  // 안정화 후 1회 즉시 재센터링("스크롤 안 됨"처럼 보이는 증상 방지).
  setTimeout(() => {
    if (el.isConnected) el.scrollIntoView({ block: 'center' });
  }, 350);
  crossNavFlashTimer = setTimeout(() => {
    restoreCrossNavFlash();
    crossNavFlashTimer = null;
  }, 1200);
}

// ── 속성 패널 hover 강조 → 캔버스 스크롤(모듈 레벨 단일 타이머) ──
// 매핑/콤보 항목 hover로 캔버스 위젯·셀이 강조될 때 대상이 뷰포트 밖이면 강조가 안 보이므로
// 보이는 위치까지 스크롤한다. 강조 채널 배선부(onHoverHighlightWidget/onFtCellHover 래퍼)에서
// 한 번만 호출해, 그 채널을 쓰는 모든 표면(writes·commit 매핑·labelSuffixFrom·visibleWhen 등)이
// 자동 수혜된다. 트리 행 hover(onHoverWidget 채널)는 스캔 중 캔버스 널뜀 방지를 위해 미적용.
// - 150ms 지연: 옵션 목록을 스치는 hover마다 캔버스가 널뛰지 않게, 머문 hover만 스크롤.
// - block:'nearest': 이미 보이는 대상은 no-op(재센터링 없음), 밖이면 최소 이동.
// - 해제(null)는 타이머 취소만 — 스크롤 되돌림 없음(자연 위치 유지).
let hoverScrollTimer: ReturnType<typeof setTimeout> | null = null;

function hoverScrollToAnchor(selector: string | null): void {
  if (hoverScrollTimer) {
    clearTimeout(hoverScrollTimer);
    hoverScrollTimer = null;
  }
  if (!selector) return;
  hoverScrollTimer = setTimeout(() => {
    hoverScrollTimer = null;
    const el = document.querySelector(selector);
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, 150);
}

/**
 * 대상 위젯 이동 버튼 — 매핑 콤보가 "선택된(닫힌)" 상태에서는 hover만으로 캔버스를 강조·스크롤하지
 * 않는다(스침 hover마다 캔버스가 이동해 산만 — 2026-07-18 사용자 정정). 이동은 이 버튼 클릭으로만,
 * 드롭다운이 열렸을 때의 옵션 hover 강조·스크롤(onHoverOption)은 탐색 컨텍스트라 유지.
 * 클릭=강조 채널 발화(스크롤 포함), 버튼에서 커서 이탈/blur=해제(hover 채널 의미론 유지).
 * 닫힘 상태 hover 강조를 재도입하지 말 것 — 이 정책의 단일 정본.
 */
function JumpToWidgetButton({
  onJump,
  onClear,
  disabled,
}: {
  onJump: () => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500 hover:border-brand-300 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
      title="선택된 대상 위젯을 캔버스에서 강조하고 그 위치로 이동"
      aria-label="대상 위젯으로 이동"
      disabled={disabled}
      onClick={onJump}
      onMouseLeave={onClear}
      onBlur={onClear}
    >
      이동
    </button>
  );
}

/**
 * 미배치 풀 행 위치 힌트 — 라벨과 중복되지 않는 마지막 1~2 조각(회색) + 전체 경로 title.
 * 배치가 없으면(=body 미대응) null. 라벨과 정확히 같은 조각은 제외(반복 회피).
 */
function poolPlacementHint(
  w: InputWidget,
  placementMap: Map<string, BindingPlacement[]>,
): { hint: string; full: string } | null {
  const placements = bindingPlacementsForWidget(w, placementMap);
  if (!placements || placements.length === 0) return null;
  const label = composerInputPrimaryLabel(w).replace(/\s+/g, ' ').trim();
  const distinct = placements[0].breadcrumb.filter((c) => c !== label);
  if (distinct.length === 0) return null;
  const hint = distinct.slice(-2).join(' › ');
  const full = placements.map((p) => p.breadcrumb.join(' › ')).join('\n');
  return { hint, full };
}

/**
 * "양식 내 위치" 브레드크럼 블록(속성창) — 파생 위젯의 body 배치를 조상 체인으로 표시.
 * 배치마다 한 줄(클릭 시 양식 탭 전환 + 그 노드 선택). body 미대응이면 "양식에서 사용되지 않음".
 */
function BindingLocationBreadcrumb({
  w,
  placementMap,
  onNavigate,
}: {
  w: ComposerWidget;
  placementMap: Map<string, BindingPlacement[]>;
  onNavigate?: (nodeId: string, cell?: { row: number; col: number }) => void;
}) {
  const placements = bindingPlacementsForWidget(w, placementMap);
  if (placements === undefined) return null; // 파생 위젯이 아님(등록형/treeOwned) — 블록 자체 없음
  return (
    <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
      <p className="mb-1 text-[11px] font-medium text-gray-500">양식 내 위치</p>
      {placements.length === 0 ? (
        <p className="text-[11px] text-gray-400">양식에서 사용되지 않음</p>
      ) : (
        <ul className="space-y-1">
          {placements.map((p, i) => {
            const path = p.breadcrumb.length > 0 ? p.breadcrumb.join(' › ') : '(최상위)';
            return (
              <li key={i}>
                {onNavigate ? (
                  <button
                    type="button"
                    className="w-full truncate text-left text-[11px] text-brand-600 hover:underline"
                    title={`${path}\n(클릭 시 양식 탭에서 위치로 이동)`}
                    onClick={() => onNavigate(p.nodeId, p.cell)}
                  >
                    {path}
                  </button>
                ) : (
                  <span className="block truncate text-[11px] text-gray-600" title={path}>
                    {path}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * 위젯 추가 optgroup 목록 — 트리 루트 팔레트와 분기 슬롯 행의 AddWidgetControl이 공유
 * (옵션/값을 두 벌 쓰지 않기 위한 추출, 2026-07-22). 값·라벨·게이트는 composerAddWidgetCatalog 정본.
 */
function renderAddWidgetOptions({
  allowModalTrigger = true,
  allowRepeat = true,
  presets,
}: {
  /** 모달 버튼 옵션 노출 여부 — modalTrigger 안(모달 children 추가)에선 중첩 금지로 false. 미지정=true. */
  allowModalTrigger?: boolean;
  /** 반복 옵션 노출 여부 — 모달 안에선 false(중첩 반복은 허용, 게이트 정본 addWidgetGates). 미지정=true. */
  allowRepeat?: boolean;
  /** 프리셋 라이브러리 목록 — 비면 '프리셋' optgroup 미노출(특화=코드 배포 위젯과 별개 그룹). */
  presets: { id: string; name: string }[];
}): React.ReactNode[] {
  const registered = registeredAddWidgetEntries();
  const groups: React.ReactNode[] = [
    <optgroup key="core" label="기본">
      {coreAddWidgetEntries({ allowModalTrigger, allowRepeat }).map((e) => (
        <option key={e.value} value={e.value}>
          {e.label}
        </option>
      ))}
    </optgroup>,
  ];
  // 등록형(특화) 위젯 — 범용 카탈로그. rowScopeOnly(위험도·개선계획)는 본문 팔레트 제외(행 그리드 뼈대 전용, P4b).
  if (registered.length > 0) {
    groups.push(
      <optgroup key="registered" label="특화">
        {registered.map((e) => (
          <option key={e.value} value={e.value}>
            {e.label}
          </option>
        ))}
      </optgroup>,
    );
  }
  if (presets.length > 0) {
    groups.push(
      <optgroup key="preset" label="프리셋">
        {presets.map((p) => (
          <option key={p.id} value={`preset:${p.id}`}>
            {p.name}
          </option>
        ))}
      </optgroup>,
    );
  }
  return groups;
}

/**
 * 위젯 추가 통합 셀렉트 — 기본(섹션/제목/구분선)과 특화(등록형)를 한 컨트롤로(2026-07-12 사용자 제안:
 * 팔레트 버튼 나열 대신 "+ 위젯" 클릭 시 대상 선택). 선택 즉시 추가 후 값 리셋.
 * 트리 루트 팔레트와 분기 슬롯 행에서 사용(배치 행의 추가는 우클릭 메뉴 '위젯 추가 ›'가 담당, 2026-07-22).
 * 옵션 목록은 buildWriteCtxItems의 addItems와 **같은 정본**(composerAddWidgetCatalog)에서 파생한다.
 */
function AddWidgetControl({
  presets,
  allowModalTrigger = true,
  allowRepeat = true,
  ...callbacks
}: AddWidgetCallbacks & {
  presets: { id: string; name: string }[];
  allowModalTrigger?: boolean;
  allowRepeat?: boolean;
}) {
  return (
    <select
      className={TREE_PALETTE_CTRL_CLASS}
      // 닫힘 상태는 "+" 아이콘 폭만(2026-07-12 아이콘 전환) — 팝업은 브라우저가 옵션 폭으로 연다.
      style={{ width: '2.4rem' }}
      value=""
      onChange={(e) => {
        dispatchAddWidgetValue(e.target.value, callbacks);
        e.currentTarget.value = '';
      }}
      title="위젯 추가"
      aria-label="위젯 추가"
    >
      <option value="">＋</option>
      {renderAddWidgetOptions({ allowModalTrigger, allowRepeat, presets })}
    </select>
  );
}

// 배치 행에는 ↑/↓만 두고 위젯 추가·이동·제거는 **우클릭 컨텍스트 메뉴**로 일원화했다
// (2026-07-22 사용자 확정). 진입점을 둘로 두지 않기 위한 것이며, 메뉴 항목 조립은 buildWriteCtxItems 담당.

/** 트리 내 그룹을 이동 대상 목록으로 수집(들여쓰기 라벨 = 그룹 안 heading 텍스트 우선). */
function collectGroupOptions(
  widgets: ComposerWidget[],
  depth = 0,
  acc: { id: string; label: string }[] = [],
): { id: string; label: string }[] {
  for (const w of widgets) {
    if (w.kind === 'group') {
      const heading = w.children.find((c) => c.kind === 'heading' && c.text);
      const label =
        w.title ??
        (heading && heading.kind === 'heading' && heading.text ? heading.text : '섹션');
      acc.push({ id: w.id, label: `${'　'.repeat(depth)}${label}` });
      collectGroupOptions(w.children, depth + 1, acc);
    }
  }
  return acc;
}

/** 위젯 트리(그룹·모달·반복 children 재귀)에서 id로 위젯을 찾는다. 미발견/ id null이면 null. */
function selectedWidgetById(tree: ComposerWidget[], id: string | null): ComposerWidget | null {
  if (!id) return null;
  for (const w of tree) {
    if (w.id === id) return w;
    if (w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat') {
      // 모달 콘텐츠 저작(P1b)·반복 행 템플릿 저작(P2): children 위젯 선택 시 속성창 연동 — 하강 조회.
      const found = selectedWidgetById(w.children, id);
      if (found) return found;
    } else if (w.kind === 'branch') {
      // 분기 슬롯 children(P3): 슬롯 안 위젯 선택 시 속성창 연동 — 전 슬롯 하강 조회(활성 무관 —
      // 트리 패널은 활성 슬롯만 펼치나 조회는 안전하게 전 슬롯).
      for (const slot of w.branches) {
        const found = selectedWidgetById(slot.children, id);
        if (found) return found;
      }
    } else if (w.kind === 'formTable') {
      // 표 고정 행 셀 위젯(중첩 InputWidget) 선택 시 속성창 연동 — 하강 조회.
      for (const row of w.rows) {
        if (row.type !== 'fixed') continue;
        for (const cell of row.cells) {
          if (cell.widget && cell.widget.id === id) return cell.widget;
        }
      }
    }
  }
  return null;
}

/** 트리의 모든 입력 bindingKey(등록형·treeOwned·표 셀 포함) — 신규 위젯 키 충돌 방지용. */
function collectAllInputBindingKeys(tree: ComposerWidget[]): Set<string> {
  const keys = new Set<string>();
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') keys.add(w.bindingKey);
      else if (w.kind === 'group') walk(w.children);
      else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget) keys.add(cell.widget.bindingKey);
        }
      } else if (w.kind === 'modalTrigger') {
        // 모달 UI 상태 저장 키(본문 values 점유)도 신규 키 충돌 회피 대상. children은 모달 스코프라
        // 본문 키와 충돌하지 않으나, 트리 통일성 위해 순회(P1은 비어 있음).
        keys.add(w.bindingKey);
        walk(w.children);
      } else if (w.kind === 'repeat') {
        // 행 스코프 키(행 필드 상대키)지만 순회 — 트리 '+'의 own-input-N 발급이 같은 repeat 안
        // 기존 own-input과 중복되면 두 위젯이 같은 행 필드를 편집하게 되므로 충돌 회피 대상(P4a).
        walk(w.children);
      } else if (w.kind === 'branch') {
        // 분기 슬롯 children(투명 스코프 — 부모 values 공유)도 키 충돌 회피 대상(P3).
        for (const slot of w.branches) walk(slot.children);
      }
    }
  };
  walk(tree);
  return keys;
}

/**
 * cut 스냅샷에서 bindingKey가 이미 트리에 배치된 파생(비-트리소유) input을 제거 — 잘라내기 후 사용자가
 * 풀(제거로 재파생)에서 재배치한 경우의 중복 배치 방지. 제거 지점은 clone 제외 규약과 동일(최상위/그룹
 * 자식은 스킵, 표 고정 셀은 widget만 스트립해 빈 입력 셀로). modalTrigger children은 모달 스코프(본문
 * 배치와 무충돌)라 미대조. 스냅샷은 불변 — 새 객체로 재구성해 반환.
 */
function stripPlacedDerivedInputs(
  widgets: ComposerWidget[],
  placed: Set<string>,
): { widgets: ComposerWidget[]; excluded: number } {
  let excluded = 0;
  const conflicts = (w: InputWidget): boolean =>
    w.widget !== 'registered' && !w.treeOwned && placed.has(w.bindingKey);
  const walk = (nodes: ComposerWidget[]): ComposerWidget[] => {
    const out: ComposerWidget[] = [];
    for (const w of nodes) {
      if (w.kind === 'input') {
        if (conflicts(w)) {
          excluded += 1;
          continue;
        }
        out.push(w);
      } else if (w.kind === 'group') {
        out.push({ ...w, children: walk(w.children) });
      } else if (w.kind === 'formTable') {
        const rows = w.rows.map((row): FormTableRow => {
          if (row.type !== 'fixed') return row;
          const cells = row.cells.map((cell): FormTableFixedCell => {
            if (!cell.widget || !conflicts(cell.widget)) return cell;
            excluded += 1;
            const stripped: FormTableFixedCell = { ...cell };
            delete stripped.widget;
            return stripped;
          });
          return { ...row, cells };
        });
        out.push({ ...w, rows });
      } else {
        out.push(w); // design/modalTrigger — 그대로 유지
      }
    }
    return out;
  };
  return { widgets: walk(widgets), excluded };
}

/** modalTrigger 모달 콘텐츠(children)의 input 위젯 수집(그룹 재귀) — commit 매핑 from 후보. */
function collectModalCommitSources(children: ComposerWidget[]): InputWidget[] {
  const out: InputWidget[] = [];
  const walk = (nodes: ComposerWidget[]): void => {
    for (const c of nodes) {
      if (c.kind === 'input') out.push(c);
      else if (c.kind === 'group') walk(c.children);
    }
  };
  walk(children);
  return out;
}

/** commit 매핑 후보 hover 시 캔버스에서 강조할 대상 — 본문 input 위젯(id) 또는 표 고정 셀 좌표. */
type CommitTargetHighlight =
  | { kind: 'widget'; id: string }
  | { kind: 'cell'; tableId: string; rowId: string; columnId: string };

/** commit 매핑 후보(from/to) 공통 형태 — 검색형 콤보박스(CatalogCombobox) 옵션 구성용. */
interface CommitTargetOption {
  bindingKey: string;
  label?: string;
  /** 위젯 타입(text/textarea/select/sortableSelect 등) — 콤보박스 badge */
  widget?: string;
  /** 속한 최상위 그룹(섹션) 라벨 — 콤보박스 카테고리 헤더(검색성 향상) */
  group?: string;
  /** hover 시 캔버스에서 강조할 대상(위젯 id 또는 표 셀 좌표). */
  highlight?: CommitTargetHighlight;
}

/**
 * commit 매핑 to 후보 — 본문 트리의 input 위젯 + 표 고정 셀 위젯(bindingKey+라벨+타입+그룹).
 * modalTrigger는 children(모달 스코프 — 본문 아님)과 자체 bindingKey(UI 상태 저장 키) 모두 제외.
 * group에는 위젯을 감싼 group.title(표 셀은 formTable.title)을 채워 콤보박스 카테고리로 묶는다.
 */
function collectBodyCommitTargets(tree: ComposerWidget[]): CommitTargetOption[] {
  const out: CommitTargetOption[] = [];
  const walk = (nodes: ComposerWidget[], groupLabel?: string): void => {
    for (const c of nodes) {
      if (c.kind === 'input')
        out.push({
          bindingKey: c.bindingKey,
          label: c.label,
          widget: c.widget,
          group: groupLabel,
          highlight: { kind: 'widget', id: c.id },
        });
      else if (c.kind === 'group') walk(c.children, c.title ?? groupLabel);
      else if (c.kind === 'formTable') {
        for (const row of c.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells)
            if (cell.widget)
              out.push({
                bindingKey: cell.widget.bindingKey,
                label: cell.widget.label,
                widget: cell.widget.widget,
                group: c.title ?? groupLabel,
                highlight: { kind: 'cell', tableId: c.id, rowId: row.id, columnId: cell.columnId },
              });
        }
      }
    }
  };
  walk(tree);
  return out;
}

/**
 * 컨테이너(모달 children/반복 행 템플릿) 스코프의 input을 참조·전파 후보 옵션으로 변환 —
 * collectModalCommitSources(그룹 하강, 모달·중첩 repeat 미하강) 공용 walk + 위젯 하이라이트 배선.
 */
function scopeChildrenCommitOptions(children: ComposerWidget[]): CommitTargetOption[] {
  return collectModalCommitSources(children).map(
    (c): CommitTargetOption => ({
      bindingKey: c.bindingKey,
      label: c.label,
      widget: c.widget,
      highlight: { kind: 'widget', id: c.id },
    }),
  );
}

/**
 * id 위젯을 감싼 modalTrigger(가장 안쪽) — visibleWhen 후보 스코프 판정용. 모달 안 위젯의 작성값
 * 스코프는 modalValues(본문과 격리)라, 표시 조건 후보도 같은 모달 children으로 제한해야 실폼에서
 * 평가 가능하다(본문 키를 지정하면 모달 스코프에 값이 없어 영영 숨김). 미발견/본문=null.
 * repeat children은 행 스코프(rowValues)가 바깥 모달 스코프를 격리하므로 진입 시 모달 컨텍스트를
 * 리셋(innermost 승자 — rc findEnclosingRepeatWidget의 modalTrigger 리셋과 대칭, P4a).
 */
function findEnclosingModalTrigger(
  tree: ComposerWidget[],
  id: string,
): ModalTriggerWidget | null {
  let result: ModalTriggerWidget | null = null;
  let found = false;
  const walk = (nodes: ComposerWidget[], modal: ModalTriggerWidget | null): void => {
    for (const n of nodes) {
      if (found) return;
      if (n.id === id) {
        result = modal;
        found = true;
        return;
      }
      if (n.kind === 'group') walk(n.children, modal);
      else if (n.kind === 'modalTrigger') walk(n.children, n);
      else if (n.kind === 'repeat') walk(n.children, null);
    }
  };
  walk(tree, null);
  return result;
}

/**
 * 선택 위젯들이 현재 값 스코프에서 참조할 수 있는 대상 후보 — 표시 조건(visibleWhen)·라벨 병기
 * (labelSuffixFrom) 공용. 모달 안 위젯이면 그 모달 children(작성값 스코프가 modalValues로
 * 격리 — 본문 키는 실폼에서 해석 불가), 아니면 본문(collectBodyCommitTargets — 표 고정 셀
 * 위젯 포함). 선택 위젯 자신들(bindingKey·위젯 id) 제외(자기/상호 참조 방지).
 * 복수 선택이 서로 다른 스코프(본문/서로 다른 모달)에 걸치면 scopeMixed=true(후보 산출 불가 —
 * 컨트롤은 사유 한 줄만 노출).
 */
function collectValueScopeRefCandidates(
  tree: ComposerWidget[],
  widgets: ComposerWidget[],
): { scopeMixed: boolean; candidates: CommitTargetOption[] } {
  const modals = widgets.map((w) => findEnclosingModalTrigger(tree, w.id));
  // 반복 행 스코프(P4a) — 모달과 상호배타(각 판정 함수가 상대 컨테이너 진입 시 컨텍스트를
  // 리셋하는 innermost 승자 규약이라 위젯당 최대 한쪽만 non-null).
  const repeats = widgets.map((w) => findEnclosingRepeatWidget(tree, w.id));
  const scopeKeyOf = (i: number): string | null => {
    const m = modals[i];
    if (m) return `modal:${m.id}`;
    const r = repeats[i];
    return r ? `repeat:${r.id}` : null;
  };
  const firstScope = widgets.length > 0 ? scopeKeyOf(0) : null;
  if (widgets.some((_, i) => scopeKeyOf(i) !== firstScope)) {
    return { scopeMixed: true, candidates: [] };
  }
  const selfBindings = new Set(widgets.flatMap((w) => (w.kind === 'input' ? [w.bindingKey] : [])));
  const selfIds = new Set(widgets.map((w) => w.id));
  // 모달/반복 행 템플릿 스코프면 그 children의 input이 후보(행 스코프 키는 rc가 rowValues에서
  // 평가 — 본문 키는 행 스코프에서 해석 불가라 후보에서 배제되는 것이 정확).
  const scopeChildren = modals[0]?.children ?? repeats[0]?.children;
  const base = scopeChildren
    ? scopeChildrenCommitOptions(scopeChildren)
    : collectBodyCommitTargets(tree);
  return {
    scopeMixed: false,
    candidates: base.filter(
      (t) =>
        !selfBindings.has(t.bindingKey) &&
        !(t.highlight?.kind === 'widget' && selfIds.has(t.highlight.id)),
    ),
  };
}

/**
 * 콤보 드롭다운 액션 항목(actionItem) 대상 후보 — 같은 값 스코프의 modalTrigger 위젯 수집.
 * 스코프 판정은 collectValueScopeRefCandidates와 동형(findEnclosingModalTrigger): 콤보가 본문이면
 * 본문 트리, 모달 안이면 그 모달 children에서 수집. modalTrigger children으로는 하강하지 않는다
 * (모달 스코프 격리 — rc 열기 채널도 트리 스코프 한정 + 모달 버튼 중첩 금지 규약과 정합).
 */
function collectActionItemModalCandidates(
  tree: ComposerWidget[],
  widgetId: string,
): ModalTriggerWidget[] {
  const enclosing = findEnclosingModalTrigger(tree, widgetId);
  // 반복 행 안 콤보(P4a) — rc 모달 열기 채널이 행 인스턴스의 중첩 트리 뷰 스코프라, 대상도 같은
  // 행 템플릿의 modalTrigger로 제한(본문 모달은 행 스코프 채널에서 열 수 없음). 모달과 상호배타.
  const enclosingRepeat = enclosing ? null : findEnclosingRepeatWidget(tree, widgetId);
  const scope = enclosing ? enclosing.children : enclosingRepeat ? enclosingRepeat.children : tree;
  const out: ModalTriggerWidget[] = [];
  const walk = (nodes: ComposerWidget[]): void => {
    for (const n of nodes) {
      if (n.kind === 'modalTrigger') out.push(n);
      else if (n.kind === 'group') walk(n.children);
    }
  };
  walk(scope);
  return out;
}

/**
 * 값 스코프 참조 콤보 — visibleWhen·labelSuffixFrom 공용 내부 컨트롤. 후보 검색 콤보 + hover 시
 * 대상 위젯/셀 캔버스 하이라이트(commit 매핑과 동형 — 위젯/셀 두 강조 채널을 한 함수로 수렴) +
 * 후보에 없는 저장값 보존 옵션(위젯 삭제 등으로 목록에서 빠져도 재저장 시 소실 방지).
 * mixed(복수 혼합)면 콤보 값을 비우고 placeholder로 혼합 표기 — 선택 시 전원 통일.
 */
function ScopeRefCombobox({
  value,
  mixed,
  candidates,
  onChange,
  placeholder,
  clearLabel,
  ctx,
}: {
  value: string | undefined;
  mixed: boolean;
  candidates: CommitTargetOption[];
  onChange: (v: string | undefined) => void;
  placeholder: string;
  clearLabel: string;
  ctx: WidgetPropCtx;
}) {
  const baseOptions: ComboboxOption[] = candidates.map((t) => ({
    value: t.bindingKey,
    label: t.label || t.bindingKey,
    key: t.bindingKey,
    badge: t.widget,
    group: t.group,
  }));
  const current = mixed ? undefined : value;
  // 후보에 없지만 이미 저장된 값(위젯 삭제 등)은 잃지 않게 보존 옵션으로 노출.
  const options: ComboboxOption[] =
    current && !candidates.some((t) => t.bindingKey === current)
      ? [
          ...baseOptions,
          {
            value: current,
            label: `${current} (목록에 없음 — 보존)`,
            key: current,
          },
        ]
      : baseOptions;
  const toHighlightByKey = new Map<string, CommitTargetHighlight>();
  for (const t of candidates) if (t.highlight) toHighlightByKey.set(t.bindingKey, t.highlight);
  const highlightTarget = (h: CommitTargetHighlight | null): void => {
    if (!h) {
      ctx.onHoverHighlightWidget?.(null);
      ctx.onFtCellHover?.(null);
      return;
    }
    if (h.kind === 'widget') {
      ctx.onFtCellHover?.(null);
      ctx.onHoverHighlightWidget?.(h.id);
    } else {
      ctx.onHoverHighlightWidget?.(null);
      ctx.onFtCellHover?.({ tableId: h.tableId, rowId: h.rowId, columnId: h.columnId });
    }
  };
  // 닫힘 상태 hover 강조·스크롤 없음 — 이동은 버튼 클릭으로만(JumpToWidgetButton 정책 참조).
  const currentHighlight = current ? toHighlightByKey.get(current) ?? null : null;
  return (
    <div className="flex items-center gap-1.5">
      <div className="min-w-0 flex-1">
        {/* eslint-disable-next-line local/catalog-picker-required -- commit 매핑 대상(본문 위젯/셀 bindingKey 목록) — 카탈로그 엔트리 아님 */}
        <CatalogCombobox
          value={current}
          options={options}
          onChange={onChange}
          placeholder={placeholder}
          allowClear
          clearLabel={clearLabel}
          onHoverOption={(v) => highlightTarget(v ? toHighlightByKey.get(v) ?? null : null)}
        />
      </div>
      <JumpToWidgetButton
        disabled={!currentHighlight}
        onJump={() => highlightTarget(currentHighlight)}
        onClear={() => highlightTarget(null)}
      />
    </div>
  );
}

/**
 * 표시 조건(visibleWhen) 공통 컨트롤 — 전 배치 위젯(input/group/텍스트/표/모달 버튼 공통 base 속성).
 * "다음 항목에 값이 있을 때만 표시": 대상 bindingKey를 검색 콤보로 지정(선택 위젯 자신들 제외),
 * 해제=항상 표시. 후보는 같은 값 스코프 — 복수 선택은 전원 같은 스코프일 때만 편집(혼합 스코프는
 * 사유 한 줄). 캔버스 반투명 표시·실폼 미렌더 평가는 rc가 담당(P3 — 여기는 저작만).
 * 결정: 2026-07-16-composer-combobox-dependent-value-axes.
 */
function WidgetVisibleWhenControl({ selection, ctx }: WidgetPropControlProps) {
  const scoped = collectValueScopeRefCandidates(ctx.tree ?? [], selection.widgets);
  if (scoped.scopeMixed) {
    return (
      <p className="text-[11px] leading-snug text-gray-400">
        표시 조건: 선택 위젯들의 값 스코프(본문/모달/반복 행)가 섞여 있어 일괄 설정에서 제외됩니다
      </p>
    );
  }
  const vw = fold(selection.widgets, readWidgetVisibleWhen);
  const reserve = fold(selection.widgets, (w) => !!w.visibleWhenReserveSpace);
  const hint = fold(selection.widgets, (w) => w.visibleWhenHint ?? '');
  const value = vw.mixed ? undefined : vw.value;
  return (
    <div>
      <label className={PROP_LABEL_CLS}>표시 조건{vw.mixed ? <MixedTag /> : null}</label>
      <ScopeRefCombobox
        value={vw.value}
        mixed={vw.mixed}
        candidates={scoped.candidates}
        placeholder={vw.mixed ? '— 혼합 —' : '(항상 표시)'}
        clearLabel="(항상 표시)"
        ctx={ctx}
        onChange={(v) =>
          selection.setAll((x) => {
            const nx = { ...x };
            if (v) nx.visibleWhen = v;
            else {
              delete nx.visibleWhen;
              // 자리 유지·안내문은 표시 조건의 부속 옵션 — 대상 해제 시 함께 제거(고아 값 방지).
              delete nx.visibleWhenReserveSpace;
              delete nx.visibleWhenHint;
            }
            return nx;
          })
        }
      />
      {value ? (
        <label className="mt-1.5 flex items-center gap-2 text-xs text-gray-600">
          <TriStateCheckbox
            className="h-3.5 w-3.5"
            checked={!!reserve.value}
            mixed={reserve.mixed}
            onChange={(next) =>
              selection.setAll((x) => {
                // visibleWhen 없는 위젯에 고아 자리 유지 값이 남지 않게 가드(복수 방어).
                if (!x.visibleWhen) return x;
                const nx = { ...x };
                if (next) nx.visibleWhenReserveSpace = true;
                else delete nx.visibleWhenReserveSpace;
                return nx;
              })
            }
          />
          숨김 중에도 자리 유지(레이아웃 고정)
          {reserve.mixed ? <MixedTag /> : null}
        </label>
      ) : null}
      {value ? (
        <div className="mt-1.5">
          <label className={PROP_LABEL_CLS}>미충족 안내문{hint.mixed ? <MixedTag /> : null}</label>
          <input
            type="text"
            className={PROP_INPUT_CLS}
            value={hint.mixed ? '' : (hint.value ?? '')}
            placeholder={hint.mixed ? '— 혼합 —' : '(비우면 숨김)'}
            onChange={(e) => {
              const v = e.target.value;
              selection.setAll((x) => {
                // visibleWhen 없는 위젯에 고아 안내문이 남지 않게 가드(복수 방어).
                if (!x.visibleWhen) return x;
                const nx = { ...x };
                if (v.trim()) nx.visibleWhenHint = v;
                else delete nx.visibleWhenHint;
                return nx;
              });
            }}
          />
          <p className="mt-1 text-[10px] leading-snug text-gray-400">
            입력하면 숨기는 대신 실제 작성 화면에 "라벨 + 회색 안내 박스(이 문구)"를 표시합니다
            (구 SMS "먼저 …을 입력하세요" 안내 동형). 자리 유지와 배타 — 안내문이 있으면 그 문구가 우선입니다.
          </p>
        </div>
      ) : null}
      <p className="mt-1 text-[10px] leading-snug text-gray-400">
        지정하면 그 항목에 값이 있을 때만 이 위젯이 표시됩니다(비면 숨김 + 필수 검증 제외).
        편집 캔버스에서는 자리 유지를 위해 반투명으로 계속 보입니다.
        {value
          ? ' 자리 유지를 켜면 실제 작성 화면에서도 숨김 중 그리드 자리가 유지되어 이웃 폭이 출렁이지 않습니다.'
          : ''}
      </p>
    </div>
  );
}

/**
 * 활성 조건(enabledWhen) 공통 컨트롤 — 표시 조건(visibleWhen)과 대칭인 범용 축(평가 규약 동일·
 * 표현만 다름: 숨김 vs 컨트롤 disabled — 구 SMS "장비명이 비면 측정 필드 회색 비활성" 동형).
 * 후보·스코프 게이트·자기 참조 제외는 표시 조건과 공용(collectValueScopeRefCandidates +
 * ScopeRefCombobox). 표시 조건과 독립 축(동시 지정 가능). disabled 렌더·required 제외는 rc 담당.
 */
function WidgetEnabledWhenControl({ selection, ctx }: WidgetPropControlProps) {
  const scoped = collectValueScopeRefCandidates(ctx.tree ?? [], selection.widgets);
  if (scoped.scopeMixed) {
    return (
      <p className="text-[11px] leading-snug text-gray-400">
        활성 조건: 선택 위젯들의 값 스코프(본문/모달/반복 행)가 섞여 있어 일괄 설정에서 제외됩니다
      </p>
    );
  }
  const ew = fold(selection.widgets, readWidgetEnabledWhen);
  return (
    <div>
      <label className={PROP_LABEL_CLS}>활성 조건{ew.mixed ? <MixedTag /> : null}</label>
      {/* disabled는 rc가 그리는 입력 컨트롤에만 걸리는 축 — 읽기전용 분기는 disabled를 읽지 않는다. */}
      <WidgetModeInertGate
        widgets={selection.widgets}
        needs="editable"
        hasValue={anyWidgetHasValue(selection.widgets, (w) => w.enabledWhen !== undefined)}
      >
        <ScopeRefCombobox
          value={ew.value}
          mixed={ew.mixed}
          candidates={scoped.candidates}
          placeholder={ew.mixed ? '— 혼합 —' : '(항상 활성)'}
          clearLabel="(항상 활성)"
          ctx={ctx}
          onChange={(v) =>
            selection.setAll((x) => {
              const nx = { ...x };
              if (v) nx.enabledWhen = v;
              else delete nx.enabledWhen;
              return nx;
            })
          }
        />
        <p className="mt-1 text-[10px] leading-snug text-gray-400">
          선택한 위젯의 값이 있을 때만 입력할 수 있습니다(비면 비활성). 비활성 중에도 위젯과 기존
          값은 그대로 보이며 필수 검증에서 제외됩니다. 표시 조건과 독립으로 동시 지정할 수 있습니다.
        </p>
      </WidgetModeInertGate>
    </div>
  );
}

/**
 * 라벨 병기(labelSuffixFrom) 컨트롤 — 다른 위젯의 값을 라벨 뒤 괄호로 병기(구 SMS "기준치(dB)").
 * 후보는 표시 조건(visibleWhen)과 동일 값 스코프(본문/모달 격리, 선택 위젯 자신들 제외 —
 * collectValueScopeRefCandidates 공용, 복수 혼합 스코프는 사유 한 줄). 해제=병기 없음.
 * 괄호·빈 값 생략 규칙은 rc 렌더러 소유(구조화 선언 1필드 — 문자열 보간/템플릿 아님).
 */
function WidgetLabelSuffixFromControl({ selection, ctx }: WidgetPropControlProps) {
  const scoped = collectValueScopeRefCandidates(ctx.tree ?? [], selection.widgets);
  if (scoped.scopeMixed) {
    return (
      <p className="text-[11px] leading-snug text-gray-400">
        라벨 병기: 선택 위젯들의 값 스코프(본문/모달/반복 행)가 섞여 있어 일괄 설정에서 제외됩니다
      </p>
    );
  }
  const suffix = fold(selection.widgets, readWidgetLabelSuffixFrom);
  return (
    <div>
      <label className={PROP_LABEL_CLS}>
        라벨 병기(다른 위젯 값){suffix.mixed ? <MixedTag /> : null}
      </label>
      <ScopeRefCombobox
        value={suffix.value}
        mixed={suffix.mixed}
        candidates={scoped.candidates}
        placeholder={suffix.mixed ? '— 혼합 —' : '(없음)'}
        clearLabel="(없음)"
        ctx={ctx}
        onChange={(v) =>
          selection.setAll((x) => {
            if (x.kind !== 'input') return x;
            const nx = { ...x };
            if (v) nx.labelSuffixFrom = v;
            else delete nx.labelSuffixFrom;
            return nx;
          })
        }
      />
      <p className="mt-1 text-[10px] leading-snug text-gray-400">
        선택한 위젯의 값이 있으면 라벨 뒤에 괄호로 병기됩니다 (예: 기준치 (dB)).
      </p>
    </div>
  );
}

/** 접기 가능한 노드 id(children 컨테이너 — group·modalTrigger·repeat) 전 트리 수집. 트리 패널 "모두 접기"용. */
function collectCollapsibleIds(tree: ComposerWidget[]): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat') {
        ids.add(w.id);
        walk(w.children);
      }
    }
  };
  walk(tree);
  return ids;
}

/** id로 가는 경로의 조상(group·modalTrigger·repeat) id 목록 — 선택 시 조상 자동 펼침용. 미발견=[]. */
function collectAncestorIds(tree: ComposerWidget[], id: string): string[] {
  const path: string[] = [];
  const walk = (nodes: ComposerWidget[]): boolean => {
    for (const w of nodes) {
      if (w.id === id) return true;
      if (w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat') {
        if (walk(w.children)) {
          path.push(w.id);
          return true;
        }
      }
    }
    return false;
  };
  walk(tree);
  return path;
}

/** 표 셀 복수 포커스 — cells의 마지막이 anchor(단일 셀 속성/복사 기준). source로 스크롤 여부 구분. */
type FtCellFocus = {
  tableId: string;
  cells: { rowId: string; columnId: string }[];
  source?: 'canvas' | 'panel';
};
/** 셀 포커스 반영 콜백 — additive=Ctrl/⌘ 토글(같은 표만). */
type OnFtCellFocus = (
  c: { tableId: string; rowId: string; columnId: string },
  opts?: { additive?: boolean; range?: boolean },
) => void;
/** 확인 모달 요청(EditorPage confirmState 위임) — 표 행/열 삭제 등 내용 손실 경고. */
type FtConfirmRequest = (opts: {
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
}) => void;

/**
 * 표 셀 클립보드 — 단일(선택 1개, 기존 형상)과 블록(복수 선택 bounding box)을 판별.
 * block.cells는 top-left 기준 상대 오프셋(dr,dc)별 payload — 빈 그리드 위치는 cell:null(대상 비움).
 * 위젯 payload는 복사 시점에 prefill이 해소된 상태(붙여넣기는 셀마다 새 id/bindingKey로 독립 복제).
 * src/srcTableId/srcRowId = 복사 원본 위치 — 계산 셀 붙여넣기의 행 상대 재매핑(엑셀 상대참조
 * 동형, 2026-07-14)에 사용. 미보유(과거 클립보드)면 재매핑 없이 기존 그대로.
 */
type FtCellClipboard =
  | { kind: 'single'; cell: FormTableFixedCell; src?: { tableId: string; rowId: string } }
  | {
      kind: 'block';
      rows: number;
      cols: number;
      cells: { dr: number; dc: number; cell: FormTableFixedCell | null; srcRowId?: string }[];
      srcTableId?: string;
    };

/** 표(formTable)의 고정 행 셀을 좌표(tableId/rowId/columnId)로 조회 — 캔버스 Ctrl+C 복사용. */
function findFtFixedCell(
  tree: ComposerWidget[],
  loc: { tableId: string; rowId: string; columnId: string },
): FormTableFixedCell | undefined {
  const t = selectedWidgetById(tree, loc.tableId);
  if (!t || t.kind !== 'formTable') return undefined;
  const row = t.rows.find((r) => r.id === loc.rowId);
  if (!row || row.type !== 'fixed') return undefined;
  return row.cells.find((c) => c.columnId === loc.columnId);
}

/** 위젯 subtree id 집합(이동 대상에서 자기·후손 그룹 제외용). */
function composerSubtreeIds(w: ComposerWidget): Set<string> {
  const ids = new Set<string>();
  const walk = (x: ComposerWidget): void => {
    ids.add(x.id);
    if (x.kind === 'group' || x.kind === 'modalTrigger' || x.kind === 'repeat')
      for (const c of x.children) walk(c);
    else if (x.kind === 'branch') for (const slot of x.branches) for (const c of slot.children) walk(c);
    else if (x.kind === 'formTable') {
      for (const row of x.rows) {
        if (row.type !== 'fixed') continue;
        for (const cell of row.cells) if (cell.widget) walk(cell.widget);
      }
    }
  };
  walk(w);
  return ids;
}

/**
 * 작성 페이지 위젯 트리 저작 편집기 (2026-07-08 신모델, 단계 3 — 버튼 + dnd 드래그).
 * 미배치 풀→트리 배치(드래그/버튼), 제거(input은 풀 복귀), reorder(↑↓), 그룹/디자인 추가,
 * 그룹 이동(드래그/우클릭 '이동 ›'), 라벨 더블클릭 rename. 내용·표현 편집은 속성 패널. 반응형 렌더는 단계 4.
 * 드래그: 풀/배치 위젯의 grip(⠿)을 끌어 그룹/루트 드롭존에 놓으면 배치/이동. reorder는 ↑↓ 버튼.
 */
export function ComposerWidgetTreeEditor({
  sync,
  placementMap,
  selectedId,
  selectedIds,
  onSelect,
  onHoverWidget,
  onPoolHover,
  onPoolPreview,
  persistKey,
  rootParentId = null,
  renameRequest = null,
  ...handlers
}: {
  sync: ComposerWidgetSyncResult;
  /** 파생 위젯 bindingKey → 양식 내 배치 위치(미배치 풀 행 위치 힌트용). */
  placementMap: Map<string, BindingPlacement[]>;
  // 3-패널 셸(Slice A) — 행 클릭 선택. 선택 행은 bg 하이라이트, 우 속성창과 연동.
  selectedId: string | null;
  // 다중선택 집합(하이라이트). Ctrl/⌘ 클릭 토글·Shift 범위는 onSelect(id, additive, range)로 host가 갱신.
  selectedIds: string[];
  onSelect: (id: string, additive?: boolean, range?: boolean) => void;
  // 트리 행 hover → 캔버스 해당 위젯 하이라이트 연동(leave 시 null).
  onHoverWidget: (id: string | null) => void;
  /** 미배치 풀 행 hover → 팝업 미리보기의 해당 body 노드 하이라이트 방송(leave 시 null). */
  onPoolHover: (nodeId: string | null, cell?: { row: number; col: number }) => void;
  /** 미배치 풀 행 hover → 에디터 내부 미니 미리보기 팝오버(enter=배치+행 rect, leave=null). */
  onPoolPreview: (placement: BindingPlacement | null, anchorRect: DOMRect | null) => void;
  /** 접힘 상태 localStorage 영속용 템플릿 key(미지정=비영속, 세션 로컬). */
  persistKey?: string;
  /**
   * 트리 최상위 "+ 위젯" 팔레트가 추가할 부모 — **모달 저작 중이면 그 모달 id**(2026-07-22 리뷰 적발).
   * 리터럴 null로 두면 모달 편집 중에도 위젯이 루트에 붙어, 차단 레이어 뒤에서 아무 일도 안 일어난 것처럼
   * 보인다(우클릭 경로만 modalAuthoringId를 타고 있었다). 미지정=루트.
   */
  rootParentId?: string | null;
  /**
   * 키보드(F2) rename 요청 — 이 id의 행이 인라인 편집으로 진입한다(`ComposerTreeRenameRequest` doc이
   * 정본). 접힌 조상은 아래 자동 펼침 effect가 함께 처리한다.
   */
  renameRequest?: ComposerTreeRenameRequest;
} & ComposerWidgetTreeHandlers) {
  const orphanSet = useMemo(() => new Set(sync.orphanKeys), [sync.orphanKeys]);
  const groupOptions = useMemo(() => collectGroupOptions(sync.tree), [sync.tree]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const rootDrop = useDroppable({ id: 'wt-drop-root', data: { parentId: null } });
  // 패널 컨테이너 폭 측정(스플리터로 좁힐 수 있음) → narrow 모드 전파. 윈도우 폭 무관.
  const panelRef = useRef<HTMLDetailsElement | null>(null);
  const [narrow, setNarrow] = useState(false);
  // 트리 접기/펴기 — persistKey(템플릿) 있으면 localStorage 영속(새로고침 후 복원, 2026-07-15 사용자
  // 요청). 기본 전부 펼침, 접힌 부모의 자식 행은 미렌더. stale id(트리에서 삭제된 위젯)는 렌더에서
  // 매칭되지 않아 무해 — 별도 필터 없음(간결 우선).
  const collapsedStorageKey = persistKey ? `composer-wt-collapsed:${persistKey}` : null;
  const loadCollapsed = (): Set<string> => {
    if (!collapsedStorageKey) return new Set();
    try {
      const raw = localStorage.getItem(collapsedStorageKey);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      /* storage 차단/파손 무시 */
    }
    return new Set();
  };
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(loadCollapsed);
  // 템플릿 전환(persistKey 변경) 시 그 템플릿의 저장 상태로 재로드(마운트 시엔 lazy init과 동일 값 — 무해).
  useEffect(() => {
    setCollapsedIds(loadCollapsed());
    // eslint 미적용(admin은 react-hooks 플러그인 없음) — loadCollapsed는 storageKey에만 의존.
  }, [collapsedStorageKey]);
  // 변경 시 저장(마운트 직후 재저장은 같은 값이라 무해).
  useEffect(() => {
    if (!collapsedStorageKey) return;
    try {
      localStorage.setItem(collapsedStorageKey, JSON.stringify([...collapsedIds]));
    } catch {
      /* storage 차단 무시 */
    }
  }, [collapsedIds, collapsedStorageKey]);
  const toggleCollapse = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const collapseAll = useCallback(
    () => setCollapsedIds(collectCollapsibleIds(sync.tree)),
    [sync.tree],
  );
  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  // 선택 위젯이 접힌 조상 안에 있으면 그 조상만 자동 펼침(선택 위치가 트리에서 보이게).
  // ⚠️ **F2 rename 요청도 같은 펼침을 필요로 한다** — 접힌 섹션 안의 행은 미렌더라 요청을 소비할
  // 컴포넌트가 없고, 그러면 F2가 «조용히 아무 일도 안 하는» 상태가 된다(선택 직후엔 selectedId
  // 경로가 이미 펴 두지만, 선택 후 사용자가 그 섹션을 접으면 어긋난다). 그래서 둘이 한 effect를
  // 공유한다 — 따로 두면 한쪽만 고쳐져 갈린다.
  const renameTargetId = renameRequest?.id ?? null;
  const renameNonce = renameRequest?.nonce ?? null;
  useEffect(() => {
    const target = renameTargetId ?? selectedId;
    if (!target) return;
    const anc = collectAncestorIds(sync.tree, target);
    if (anc.length === 0) return;
    setCollapsedIds((prev) => {
      if (!anc.some((a) => prev.has(a))) return prev;
      const next = new Set(prev);
      for (const a of anc) next.delete(a);
      return next;
    });
  }, [selectedId, sync.tree, renameTargetId, renameNonce]);
  useEffect(() => {
    const el = panelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setNarrow(w > 0 && w < TREE_PANEL_NARROW_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const handleDragEnd = (e: DragEndEvent): void => {
    const a = e.active.data.current as
      | { kind: 'pool' | 'node'; widget?: ComposerWidget; id?: string }
      | undefined;
    const o = e.over?.data.current as { parentId: string | null } | undefined;
    if (!a || !e.over || !o) return;
    if (a.kind === 'pool' && a.widget) handlers.onPlace(a.widget, o.parentId);
    else if (a.kind === 'node' && a.id) handlers.onMoveTo(a.id, o.parentId);
  };
  // 미배치 풀 검색 — 표시 라벨(운영자 라벨/카탈로그 라벨) + raw bindingKey + widget 타입 매칭.
  const [poolQuery, setPoolQuery] = useState('');
  const poolQueryTrimmed = poolQuery.trim().toLowerCase();
  const filteredUnplaced = poolQueryTrimmed
    ? sync.unplaced.filter(
        (w) =>
          composerInputPrimaryLabel(w).toLowerCase().includes(poolQueryTrimmed) ||
          w.bindingKey.toLowerCase().includes(poolQueryTrimmed) ||
          w.widget.toLowerCase().includes(poolQueryTrimmed),
      )
    : sync.unplaced;
  return (
    <TreePanelNarrowContext.Provider value={narrow}>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <details ref={panelRef} className="rounded border border-brand-300 bg-brand-50/40 text-xs" open>
          <summary className="cursor-pointer select-none truncate whitespace-nowrap px-3 py-2 font-medium text-brand-700">
            작성 페이지 위젯 트리 · 배치 {sync.tree.length} · 미배치 {sync.unplaced.length}
            {sync.orphanKeys.length > 0 ? ` · orphan ${sync.orphanKeys.length}` : ''}{' '}
            <HelpLink topic="widgetTree" />
          </summary>
          <div className="space-y-3 px-3 pb-3">
            <section>
              {/* "+ 위젯"을 제목 행에 인라인 배치 — 팔레트 전용 행 제거(세로 한 줄 절약, 2026-07-12). */}
              <div className="mb-1 flex items-center gap-2">
                <span className="flex items-center gap-1 whitespace-nowrap font-medium text-gray-500">
                  배치된 위젯
                  <HelpLink topic="widgetPalette" />
                </span>
                {/* 추가 대상은 rootParentId — 모달 저작 중이면 그 모달 안에 들어간다(리터럴 null이면
                    차단 레이어 뒤 루트에 붙어 "아무 일도 안 일어난 것"처럼 보인다, 2026-07-22 리뷰). */}
                <AddWidgetControl
                  onAddGroup={() => handlers.onAddGroup(rootParentId)}
                  onAddDesign={(kind) => handlers.onAddDesign(kind, rootParentId)}
                  onAddRegistered={(key) => handlers.onAddRegistered(key, rootParentId)}
                  onAddFormTable={() => handlers.onAddFormTable(rootParentId)}
                  onAddInput={() => handlers.onAddInput(rootParentId)}
                  onAddModalTrigger={() => handlers.onAddModalTrigger(rootParentId)}
                  onAddRepeat={() => handlers.onAddRepeat(rootParentId)}
                  onAddBranch={() => handlers.onAddBranch(rootParentId)}
                  onInsertPreset={(presetId) => handlers.onInsertPreset(presetId, rootParentId)}
                  presets={handlers.presetOptions}
                />
                {/* 트리 접기/펴기 — children 가진 노드(섹션·모달)를 일괄 접거나 편다. */}
                <span className="ml-auto flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                    title="모두 접기"
                    onClick={collapseAll}
                  >
                    모두 접기
                  </button>
                  <button
                    type="button"
                    className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                    title="모두 펼치기"
                    onClick={expandAll}
                  >
                    모두 펼치기
                  </button>
                </span>
              </div>
            <div
              ref={rootDrop.setNodeRef}
              className={`rounded p-1 ${rootDrop.isOver ? 'bg-brand-100 ring-1 ring-brand-400' : ''}`}
            >
              {sync.tree.length === 0 ? (
                <p className="py-1 text-gray-400">배치된 위젯이 없습니다(풀에서 배치·드래그).</p>
              ) : (
                // onMouseLeave 백스톱 — 행 단위 mouseleave는 클릭에 의한 행 재구성/언마운트로
                // 유실될 수 있어(hover 하이라이트가 캔버스에 고착, 2026-07-13 사용자 제보)
                // 트리 영역을 벗어나면 무조건 해제한다.
                <ul className="space-y-1" onMouseLeave={() => onHoverWidget(null)}>
                  {sync.tree.map((w) => (
                    <ComposerWidgetEditRow
                      key={w.id}
                      widget={w}
                      orphanSet={orphanSet}
                      handlers={handlers}
                      selectedId={selectedId}
                      selectedIds={selectedIds}
                      onSelect={onSelect}
                      onHoverWidget={onHoverWidget}
                      collapsedIds={collapsedIds}
                      onToggleCollapse={toggleCollapse}
                      renameRequest={renameRequest}
                    />
                  ))}
                </ul>
              )}
            </div>
          </section>
          <section>
            <div className="mb-1 flex items-center gap-2">
              <span className="whitespace-nowrap font-medium text-gray-500">
                미배치 위젯 풀
                {poolQueryTrimmed ? ` ${filteredUnplaced.length}/${sync.unplaced.length}` : ''}{' '}
                <HelpLink topic="unplacedPool" />
              </span>
              <input
                type="search"
                className="h-6 w-full min-w-0 flex-1 rounded border border-gray-300 bg-white px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
                placeholder="검색(라벨·바인딩 키)"
                value={poolQuery}
                onChange={(e) => setPoolQuery(e.target.value)}
              />
            </div>
            {sync.unplaced.length === 0 ? (
              <p className="py-1 text-gray-400">미배치 위젯이 없습니다.</p>
            ) : filteredUnplaced.length === 0 ? (
              <p className="py-1 text-gray-400">검색 결과가 없습니다.</p>
            ) : (
              <ul className="space-y-1">
                {filteredUnplaced.map((w) => (
                  <PoolWidgetRow
                    key={w.id}
                    widget={w}
                    placementMap={placementMap}
                    groupOptions={groupOptions}
                    onPlace={handlers.onPlace}
                    selectedId={selectedId}
                    selectedIds={selectedIds}
                    onSelect={onSelect}
                    onPoolHover={onPoolHover}
                    onPoolPreview={onPoolPreview}
                  />
                ))}
              </ul>
            )}
          </section>
          </div>
        </details>
      </DndContext>
    </TreePanelNarrowContext.Provider>
  );
}

/** 미배치 풀 위젯 한 줄 — grip 드래그(배치) + 배치 버튼 + 그룹 select. */
function PoolWidgetRow({
  widget: w,
  placementMap,
  groupOptions,
  onPlace,
  selectedId,
  selectedIds,
  onSelect,
  onPoolHover,
  onPoolPreview,
}: {
  widget: InputWidget;
  placementMap: Map<string, BindingPlacement[]>;
  groupOptions: { id: string; label: string }[];
  onPlace: (widget: ComposerWidget, parentId: string | null) => void;
  selectedId: string | null;
  selectedIds: string[];
  onSelect: (id: string, additive?: boolean, range?: boolean) => void;
  onPoolHover: (nodeId: string | null, cell?: { row: number; col: number }) => void;
  onPoolPreview: (placement: BindingPlacement | null, anchorRect: DOMRect | null) => void;
}) {
  const narrow = useContext(TreePanelNarrowContext);
  const drag = useDraggable({ id: `wt-drag-pool-${w.id}`, data: { kind: 'pool', widget: w } });
  const isSelected = selectedId === w.id || selectedIds.includes(w.id);
  const label = composerInputPrimaryLabel(w);
  // 양식 내 위치 힌트(회색) — 같은 라벨이 풀에 반복될 때 구분용(마지막 1~2 조상 조각). 전체 경로는 title.
  const placementHint = poolPlacementHint(w, placementMap);
  // hover 시 팝업 미리보기로 방송할 첫 배치(있으면). 배치 0곳이면 hover no-op(leave만 해제 방송).
  const firstPlacement = bindingPlacementsForWidget(w, placementMap)?.[0];
  return (
    <li
      className={`flex items-center gap-1.5 rounded ${isSelected ? 'bg-brand-100' : ''}`}
      onClick={(e) => onSelect(w.id, e.ctrlKey || e.metaKey, e.shiftKey)}
      onMouseEnter={
        firstPlacement
          ? (e) => {
              onPoolHover(firstPlacement.nodeId, firstPlacement.cell);
              onPoolPreview(firstPlacement, e.currentTarget.getBoundingClientRect());
            }
          : undefined
      }
      onMouseLeave={() => {
        onPoolHover(null);
        onPoolPreview(null, null);
      }}
    >
      <span
        ref={drag.setNodeRef}
        {...drag.listeners}
        {...drag.attributes}
        className="shrink-0 cursor-grab select-none px-0.5 text-gray-400"
        title="드래그로 배치"
      >
        ⠿
      </span>
      {narrow ? null : (
        <span className="inline-block min-w-[46px] shrink-0 rounded bg-white px-1.5 py-0.5 text-center font-mono text-[10px] text-brand-600 ring-1 ring-brand-200">
          {w.widget}
        </span>
      )}
      {/* title 툴팁 없음(2026-07-16 사용자 확정) — 전체 경로는 hover 미니 미리보기 footer가 담당,
          native 툴팁이 커서 근처에 겹쳐 떠 시각 노이즈. */}
      <span className="min-w-0 flex-1 truncate text-gray-700">
        {label}
        {placementHint ? (
          <span className="ml-1 text-gray-400">— {placementHint.hint}</span>
        ) : null}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="primary"
          size="xs"
          className="shrink-0 whitespace-nowrap px-1"
          title="루트에 배치"
          onClick={() => onPlace(w, null)}
        >
          배치
        </Button>
        {groupOptions.length > 0 ? (
          <select
            // 배치 행 "⋯" 콤보와 동형(2026-07-22 사용자 요청) — 목적지 목록을 상시 드롭다운으로
            // 펼쳐 두지 않고 행 우측 메뉴로 접는다. 접힘 폭은 "⋯" 폭만(2.4rem): native select는
            // 가장 긴 option(긴 섹션명)에 맞춰 부풀어 라벨 영역(flex-1)을 0으로 짜부라뜨린다
            // (2026-07-16 제보: 풀 행에서 위젯 라벨이 안 보임). 목록은 브라우저가 option 폭으로 펼침.
            className={`${TREE_ROW_CTRL_CLASS} border border-gray-300`}
            style={{ width: '2.4rem' }}
            value=""
            title="섹션에 배치"
            aria-label="섹션에 배치"
            onChange={(e) => {
              if (e.target.value) onPlace(w, e.target.value);
              e.currentTarget.value = '';
            }}
          >
            <option value="">⋯</option>
            {/* 루트 배치는 왼쪽 "배치" 버튼(1클릭 유지)이 담당하므로 여기엔 섹션만. */}
            <optgroup label="섹션에 배치">
              {groupOptions.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.label}
                </option>
              ))}
            </optgroup>
          </select>
        ) : null}
      </div>
    </li>
  );
}

/**
 * 배치된 위젯 한 줄 + 순서 컨트롤(↑/↓), children 재귀.
 * 행은 구조만 다룬다 — 위젯 추가·부모 이동·제거는 **우클릭 메뉴**, 내용·표현(단 수·스타일·바인딩)은
 * **속성 패널** 관할이고, 라벨(heading text / input label)만 더블클릭 rename으로 그 자리에서
 * 고칠 수 있다(2026-07-22 사용자 확정).
 */
function ComposerWidgetEditRow({
  widget: w,
  orphanSet,
  handlers,
  selectedId,
  selectedIds,
  onSelect,
  onHoverWidget,
  collapsedIds,
  onToggleCollapse,
  renameRequest,
}: {
  widget: ComposerWidget;
  orphanSet: Set<string>;
  handlers: ComposerWidgetTreeHandlers;
  selectedId: string | null;
  selectedIds: string[];
  onSelect: (id: string, additive?: boolean, range?: boolean) => void;
  /** 행 hover 시 캔버스의 이 위젯을 하이라이트(leave 시 null). group 자식 행 enter가 부모 leave보다 늦어도 최신 enter가 이김. */
  onHoverWidget: (id: string | null) => void;
  /** 접힌 노드 id 집합(트리 세션 로컬). 이 집합에 든 부모는 자식 행을 미렌더. */
  collapsedIds: Set<string>;
  /** 부모 노드(group·modalTrigger) 접기/펴기 토글. */
  onToggleCollapse: (id: string) => void;
  /**
   * F2 rename 요청(`ComposerTreeRenameRequest` doc이 정본) — **옵셔널로 만들지 말 것.**
   * 이 행은 호출부가 5곳 + 재귀라, 필수여야 누락이 `TS2741`로 드러난다.
   */
  renameRequest: ComposerTreeRenameRequest;
}) {
  // narrow(패널 축소) 게이트는 배치 행에서 제거 — 타입 배지는 좁아져도 항상 표시한다(2026-07-22).
  // TreePanelNarrowContext는 미배치 풀 행이 계속 사용.
  const isOrphan = w.kind === 'input' && orphanSet.has(w.bindingKey);
  // 이동 대상 섹션 목록(자기 서브트리 제외) 산출은 행에서 제거 — 우클릭 메뉴의 '이동 ›'이
  // buildWriteCtxItems에서 같은 규칙(composerSubtreeIds 필터)으로 직접 계산한다(2026-07-22).
  const drag = useDraggable({ id: `wt-drag-${w.id}`, data: { kind: 'node', id: w.id } });
  const groupDrop = useDroppable({ id: `wt-drop-${w.id}`, data: { parentId: w.id } });
  const isSelected = selectedId === w.id || selectedIds.includes(w.id);
  // children 컨테이너(섹션·모달·반복 행 템플릿·분기 슬롯)만 접기 대상 — 접히면 자식 행을 미렌더(숨김 아님).
  const hasChildContainer =
    w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat' || w.kind === 'branch';
  const collapsed = hasChildContainer && collapsedIds.has(w.id);
  // 행 라벨 = 읽기 전용 텍스트 + 더블클릭 rename(2026-07-22 위젯 트리=구조 / 속성=내용·표현 분리 —
  // 상시 노출 input이 폭을 먹고 "행에서 내용 편집"이라 역할이 섞였음). 편집 가능 kind는 기존 인라인
  // 편집 범위와 동일하게 heading(text)·input(label)만.
  const canRename = composerCanRenameWidget(w);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const renameSource =
    w.kind === 'heading' ? (w.text ?? '') : w.kind === 'input' ? (w.label ?? '') : '';
  // F2 요청 소비(2026-10-03) — 아래 더블클릭 진입과 **같은 두 줄**(draft seed + 편집 켜기)을 실행한다.
  // 둘이 갈리면 「F2로 들어간 편집만 초기값이 비는」 식으로 어긋나고 양쪽 다 「동작한다」로 보인다.
  const renameNonce = renameRequest?.id === w.id ? renameRequest.nonce : null;
  // renameSource를 effect 의존에 넣지 않기 위한 ref — 요청(nonce) 하나당 **한 번만** 진입해야 하는데,
  // 의존에 넣으면 편집 커밋으로 라벨이 바뀔 때 effect가 다시 돌아 draft를 덮어쓴다.
  const renameSourceRef = useRef(renameSource);
  renameSourceRef.current = renameSource;
  useEffect(() => {
    if (renameNonce === null || !canRename) return;
    setDraft(renameSourceRef.current);
    setRenaming(true);
  }, [renameNonce, canRename]);
  // 타입 배지 텍스트 — input은 widget 타입·필수(*)까지 흡수. title로도 재사용(잘림 대비).
  const rowKindBadge =
    w.kind === 'input' ? `${w.kind}·${w.widget}${w.required ? '*' : ''}` : w.kind;
  // input 라벨 규칙은 composerInputPrimaryLabel이 정본(풀 행·우클릭 목록과 공유) — 여기서 따로 쓰면
  // 한쪽만 바뀌어 갈라진다. composerWidgetSummary의 input 분기는 배지 없는 표시면용이라 별개(통합 금지).
  const rowLabelText =
    w.kind === 'heading'
      ? w.text || '(빈 텍스트)'
      : w.kind === 'input'
        ? composerInputPrimaryLabel(w)
        : composerWidgetSummary(w);
  const commitRename = () => {
    // 무변경이면 아무것도 하지 않는다 — wtApply는 항상 새 template 객체를 만들어 useEditorHistory의
    // next===present 스킵이 무력화되므로, 더블클릭 후 그냥 벗어나도 dirty+undo 1스텝이 쌓인다.
    if (draft === renameSource) {
      setRenaming(false);
      return;
    }
    // kind로 narrow 후 spread(union 직접 spread 금지 — TS2322 전례). 빈 텍스트도 허용.
    if (w.kind === 'heading')
      handlers.onUpdate(w.id, (x) => (x.kind === 'heading' ? { ...x, text: draft } : x));
    // input 한정 갱신 — 유니온에 label 필수인 modalTrigger가 있어 무조건 spread하면 TS2322.
    // 빈 값은 undefined로 저장(라벨 미설정 = 작성 화면에 bindingKey 폴백).
    else if (w.kind === 'input')
      handlers.onUpdate(w.id, (x) =>
        x.kind === 'input' ? { ...x, label: draft || undefined } : x,
      );
    setRenaming(false);
  };
  return (
    // 들여쓰기는 자식 컨테이너(TREE_CHILD_INDENT_CLASS)의 12px 한 곳에서만 준다 — 행에 또 12px을
    // 더하면 단계당 24px이 되어 깊은 구조에서 라벨이 밀려난다(2026-07-22 사용자 제보).
    <li>
      {/* 행은 패널 폭에 맞춘다(가로 스크롤 없음) — 넘치는 건 라벨이 …로 흡수하고, ↑/↓는 flex 끝이라
          항상 우측에 붙는다. w-max(내용 폭)로 늘리면 스크롤이 생겨 금지(2026-07-22 사용자 확정). */}
      <div
        className={`flex items-center gap-1.5 rounded ${isSelected ? 'bg-brand-100' : ''}`}
        onClick={(e) => onSelect(w.id, e.ctrlKey || e.metaKey, e.shiftKey)}
        onMouseEnter={() => onHoverWidget(w.id)}
        onMouseLeave={() => onHoverWidget(null)}
        // 행 우클릭 = 캔버스 위젯과 동일 컨텍스트 메뉴(핸들러가 stopPropagation — 자식 행에서
        // 조상 행으로 중복 전파 없음). 행 안 컨트롤(select/버튼) 위 우클릭은 핸들러가 skip.
        onContextMenu={
          handlers.onRowContextMenu ? (e) => handlers.onRowContextMenu?.(w.id, e) : undefined
        }
      >
        {/* 접기/펴기 chevron — children 컨테이너(섹션·모달)만. 별도 버튼이라 행 선택/편집과 클릭 충돌 없음.
            비컨테이너는 폭 맞춤 placeholder로 라벨 정렬 유지. */}
        {hasChildContainer ? (
          <button
            type="button"
            className="w-4 shrink-0 select-none text-gray-400 hover:text-gray-600"
            title={collapsed ? '펼치기' : '접기'}
            aria-label={collapsed ? '펼치기' : '접기'}
            aria-expanded={!collapsed}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(w.id);
            }}
          >
            {collapsed ? '▸' : '▾'}
          </button>
        ) : (
          <span className="w-4 shrink-0" aria-hidden="true" />
        )}
        <span
          ref={drag.setNodeRef}
          {...drag.listeners}
          {...drag.attributes}
          className="shrink-0 cursor-grab select-none px-0.5 text-gray-400"
          title="드래그로 이동"
        >
          ⠿
        </span>
        {/* kind 배지 — narrow에서도 항상 표시. input은 widget 타입/필수(*)를 배지에 흡수(2026-07-22:
            라벨 우측 접미 텍스트를 없애 라벨 폭 확보). min-w는 정렬용 하한.
            max-w+truncate 필수 — 패널 기본 폭(20%≈272px)에서 긴 배지(`input·dataSourceRows*`≈138px)는
            깊이 2만 돼도 라벨 자리를 0으로 만들고, 스크롤바가 숨김이라 그냥 잘려 보인다. 전문은 title. */}
        <span
          className="inline-block min-w-[46px] max-w-[88px] shrink-0 truncate rounded bg-white px-1.5 py-0.5 text-center font-mono text-[10px] text-brand-600 ring-1 ring-brand-200"
          title={rowKindBadge}
        >
          {rowKindBadge}
        </span>
        {renaming ? (
          // 인라인 rename(파일 탐색기 방식) — 평상시엔 읽기 전용 텍스트, 더블클릭으로만 편집 진입.
          // 편집 대상은 heading(text)·input(label) 두 kind로 한정(기존 인라인 편집 범위와 동일).
          <input
            className="h-6 min-w-0 flex-1 rounded border border-gray-300 px-1"
            value={draft}
            autoFocus
            placeholder={w.kind === 'input' ? w.bindingKey : '텍스트'}
            onFocus={(e) => e.currentTarget.select()}
            // 편집 중 클릭/우클릭이 행 선택·컨텍스트 메뉴로 새지 않게.
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              // ⚠️ Ctrl/⌘ 조합은 **통과**시킨다 — 전역 undo/redo는 "편집 중이면 blur로 draft를 정식
              // 1스텝으로 커밋한 뒤 undo"라는 정본 규약(2026-07-04 v3, 이 파일의 document keydown)이고,
              // Ctrl+S 저장도 window 리스너다. 여기서 막으면 rename 중 되돌리기·저장이 통째로 죽는다.
              // 그 외 키(문자·Delete·방향키 등)만 에디터 전역 단축키로 새지 않게 차단.
              if (!e.ctrlKey && !e.metaKey) e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setRenaming(false); // 취소 = 원복(draft 폐기)
              }
            }}
          />
        ) : (
          <span
            // 남는 폭을 모두 차지하고 넘치면 …로 자른다(전문은 title 툴팁) — 행 폭 초과를 라벨이 흡수해
            // 가로 스크롤을 막는 지점.
            // h-6/leading-6 — 내용이 비어도 행 높이만큼 히트 영역을 유지한다(빈 텍스트 위젯 등에서
            // 높이 0이 되면 더블클릭 rename 대상이 사라진다).
            className={`h-6 min-w-0 flex-1 truncate leading-6 ${isOrphan ? 'text-red-500 line-through' : 'text-gray-700'}`}
            // 잘린 라벨의 전문 + (input이면) 바인딩 키까지 — truncate라 툴팁이 유일한 확인 경로.
            title={w.kind === 'input' ? `${rowLabelText}\n바인딩: ${w.bindingKey}` : rowLabelText}
            onDoubleClick={
              canRename
                ? (e) => {
                    e.stopPropagation();
                    setDraft(renameSource);
                    setRenaming(true);
                  }
                : undefined
            }
          >
            {rowLabelText}
          </span>
        )}
        {isOrphan ? (
          <span className="shrink-0 whitespace-nowrap rounded bg-red-100 px-1 text-[10px] text-red-600">
            바인딩 삭제됨
          </span>
        ) : null}
        {/* 컨트롤 그룹 — shrink-0로 묶어 라벨(flex-1 min-w-0)을 밀거나 겹치지 않게. 동일 높이(h-6).
            행에는 자주 쓰는 순서 조작(↑/↓)만 남긴다 — 위젯 추가·부모 이동·제거는 **우클릭 메뉴**의
            '위젯 추가 ›' / '이동 ›' / '삭제'로 일원화(2026-07-22 사용자 확정). 내용·표현(단 수·스타일·
            바인딩)은 속성 패널 관할. */}
        <div className="flex shrink-0 items-center gap-1">
          <button
            className={`${TREE_ROW_CTRL_CLASS} bg-gray-100`}
            title="위로"
            onClick={() => handlers.onReorder(w.id, 'up')}
          >
            ↑
          </button>
          <button
            className={`${TREE_ROW_CTRL_CLASS} bg-gray-100`}
            title="아래로"
            onClick={() => handlers.onReorder(w.id, 'down')}
          >
            ↓
          </button>
        </div>
      </div>
      {/* formTable 셀 위젯 자식 목록(2026-07-14 사용자 요청 — 표 안 배치 위젯 가시성) — 고정 행
          셀의 입력 위젯을 표 자식으로 노출. 클릭=표 선택+그 셀 포커스(캔버스 셀 선택 동일),
          hover=캔버스 셀 하이라이트. 이동/삭제 컨트롤 없음 — 셀은 그리드 슬롯이라 reorder가
          무의미하고 편집은 캔버스/셀 속성 패널 관할. 텍스트/계산 셀은 위젯이 아니라 제외. */}
      {w.kind === 'formTable'
        ? (() => {
            const cellItems = w.rows.flatMap((r, ri) =>
              r.type === 'fixed'
                ? r.cells
                    .filter((c) => c.widget)
                    .map((c) => ({
                      rowId: r.id,
                      columnId: c.columnId,
                      ri,
                      ci: w.columns.findIndex((col) => col.id === c.columnId),
                      cw: c.widget as InputWidget,
                    }))
                : [],
            );
            if (cellItems.length === 0) return null;
            return (
              <ul className={`mt-1 space-y-1 ${TREE_CHILD_INDENT_CLASS}`}>
                {cellItems.map((it) => {
                  const key = `${w.id}::${it.rowId}::${it.columnId}`;
                  const active = handlers.focusedCellKey === key;
                  return (
                    <li key={key}>
                      <div
                        className={`flex h-6 cursor-pointer items-center gap-1.5 rounded border px-1.5 ${
                          active
                            ? 'border-brand-400 bg-brand-50'
                            : 'border-transparent hover:bg-gray-50'
                        }`}
                        title={`바인딩: ${it.cw.bindingKey}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlers.onFocusCell(w.id, it.rowId, it.columnId);
                        }}
                        onMouseEnter={() =>
                          handlers.onHoverCell({
                            tableId: w.id,
                            rowId: it.rowId,
                            columnId: it.columnId,
                          })
                        }
                        onMouseLeave={() => handlers.onHoverCell(null)}
                      >
                        {/* 선두 슬롯(접기 chevron·드래그 핸들) 자리만 비워 위젯 행과 들여쓰기·정렬을
                            맞춘다(2026-07-22 사용자 요청) — 셀은 접기·드래그 대상이 아니라 컨트롤 자체는 없다. */}
                        <span className="w-4 shrink-0" aria-hidden="true" />
                        <span
                          className="shrink-0 select-none px-0.5 text-transparent"
                          aria-hidden="true"
                        >
                          ⠿
                        </span>
                        {/* 타입 배지 — 위젯 행과 동형(`input·<widget>` + 필수 *). 셀 위젯은 항상 input kind. */}
                        <span className="inline-block min-w-[46px] shrink-0 rounded bg-white px-1.5 py-0.5 text-center font-mono text-[10px] text-brand-600 ring-1 ring-brand-200">
                          {`input·${it.cw.widget}${it.cw.required ? '*' : ''}`}
                        </span>
                        {/* 라벨은 풀 행과 동일 폴백(등록형=카탈로그 라벨 → label → bindingKey). */}
                        <span className="min-w-0 flex-1 truncate text-gray-700">
                          {composerInputPrimaryLabel(it.cw)}
                        </span>
                        {/* 행/열 좌표 — 표 안 위치라 truncate에 먹히지 않게 shrink-0 우측 메타로. */}
                        <span className="shrink-0 whitespace-nowrap font-mono text-[10px] text-gray-400">
                          {it.ri + 1}행 {it.ci >= 0 ? `${it.ci + 1}열` : '?열'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          })()
        : null}
      {w.kind === 'group' && !collapsed ? (
        <div
          ref={groupDrop.setNodeRef}
          className={`mt-1 rounded ${TREE_CHILD_INDENT_CLASS} ${groupDrop.isOver ? 'bg-brand-100 ring-1 ring-brand-400' : ''}`}
        >
          {/* 위젯 추가는 우클릭 메뉴 '위젯 추가 ›'로 이동 — 별도 팔레트 행 제거. */}
          {w.children.length > 0 ? (
            <ul className="space-y-1">
              {w.children.map((c) => (
                <ComposerWidgetEditRow
                  key={c.id}
                  widget={c}
                  orphanSet={orphanSet}
                  handlers={handlers}
                  selectedId={selectedId}
                  selectedIds={selectedIds}
                  onSelect={onSelect}
                  onHoverWidget={onHoverWidget}
                  collapsedIds={collapsedIds}
                  onToggleCollapse={onToggleCollapse}
                  renameRequest={renameRequest}
                />
              ))}
            </ul>
          ) : (
            <p className="text-gray-400">빈 섹션 — 풀에서 위젯을 이 섹션에 배치하세요.</p>
          )}
        </div>
      ) : null}
      {/* 모달 콘텐츠(children) 저작(P1b) — group과 동형: 들여쓴 자식 행 재귀 + 우클릭 '위젯 추가'로 자식 추가.
          모달 스코프라 풀(미배치) 대응물은 없고 treeOwned 위젯만(추가=즉시 트리 소유). */}
      {w.kind === 'modalTrigger' && !collapsed ? (
        <div className={`mt-1 rounded ${TREE_CHILD_INDENT_CLASS}`}>
          {w.children.length > 0 ? (
            <ul className="space-y-1">
              {w.children.map((c) => (
                <ComposerWidgetEditRow
                  key={c.id}
                  widget={c}
                  orphanSet={orphanSet}
                  handlers={handlers}
                  selectedId={selectedId}
                  selectedIds={selectedIds}
                  onSelect={onSelect}
                  onHoverWidget={onHoverWidget}
                  collapsedIds={collapsedIds}
                  onToggleCollapse={onToggleCollapse}
                  renameRequest={renameRequest}
                />
              ))}
            </ul>
          ) : (
            <p className="text-gray-400">빈 모달 — 행을 우클릭해 "위젯 추가"로 콘텐츠를 넣으세요.</p>
          )}
        </div>
      ) : null}
      {/* 반복 행 템플릿(children) 저작(P2) — group과 동형: 들여쓴 자식 행 재귀 + 우클릭 '위젯 추가' +
          드롭존(풀 배치/이동 dnd). children bindingKey는 행 필드 상대키(행 스코프)라 풀 파생 대응물
          없음 — 뼈대(treeOwned)와 직접 추가 위젯이 대상. */}
      {w.kind === 'repeat' && !collapsed ? (
        <div
          ref={groupDrop.setNodeRef}
          className={`mt-1 rounded ${TREE_CHILD_INDENT_CLASS} ${groupDrop.isOver ? 'bg-brand-100 ring-1 ring-brand-400' : ''}`}
        >
          {w.children.length > 0 ? (
            <ul className="space-y-1">
              {w.children.map((c) => (
                <ComposerWidgetEditRow
                  key={c.id}
                  widget={c}
                  orphanSet={orphanSet}
                  handlers={handlers}
                  selectedId={selectedId}
                  selectedIds={selectedIds}
                  onSelect={onSelect}
                  onHoverWidget={onHoverWidget}
                  collapsedIds={collapsedIds}
                  onToggleCollapse={onToggleCollapse}
                  renameRequest={renameRequest}
                />
              ))}
            </ul>
          ) : (
            <p className="text-gray-400">빈 행 템플릿 — 행을 우클릭해 "위젯 추가"로 넣으세요.</p>
          )}
        </div>
      ) : null}
      {/* 분기 슬롯 서브행(P3) — group/repeat과 달리 슬롯이 한 층 더 있다: branch 행 아래 슬롯 서브행
          (라벨 폴백 label→조건 요약→'슬롯 N', 활성 하이라이트+클릭 전환, 슬롯 안 추가 '+'=parentId 슬롯 id)
          + 그 아래 슬롯 children 재귀. 슬롯 서브행 자체는 위젯이 아니라 선택/dnd 대상 아님(본문 트리 대응). */}
      {w.kind === 'branch' && !collapsed ? (
        <div className={`mt-1 space-y-1 ${TREE_CHILD_INDENT_CLASS}`}>
          {w.branches.map((slot, si) => {
            const slotActive = (handlers.activeBranchSlots[w.id] ?? 0) === si;
            return (
              <div key={slot.id}>
                <div
                  className={`flex items-center gap-1.5 rounded border px-1.5 py-0.5 ${
                    slotActive ? 'border-teal-400 bg-teal-50' : 'border-gray-200'
                  }`}
                >
                  <button
                    type="button"
                    title="이 슬롯을 활성화(캔버스에 표시)"
                    className={`min-w-0 flex-1 truncate text-left text-[11px] ${
                      slotActive ? 'font-medium text-teal-700' : 'text-gray-600'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlers.onSetActiveBranchSlot(w.id, si);
                    }}
                  >
                    {slotActive ? '● ' : ''}
                    {widgetBranchSlotLabel(slot, si)}
                  </button>
                  {/* 슬롯 안 위젯 추가의 정규 경로 — parentId=슬롯 id(branch id 아님). */}
                  <span className="shrink-0">
                    <AddWidgetControl
                      onAddGroup={() => handlers.onAddGroup(slot.id)}
                      onAddDesign={(kind) => handlers.onAddDesign(kind, slot.id)}
                      onAddRegistered={(key) => handlers.onAddRegistered(key, slot.id)}
                      onAddFormTable={() => handlers.onAddFormTable(slot.id)}
                      onAddInput={() => handlers.onAddInput(slot.id)}
                      onAddModalTrigger={() => handlers.onAddModalTrigger(slot.id)}
                      onAddRepeat={() => handlers.onAddRepeat(slot.id)}
                      onAddBranch={() => handlers.onAddBranch(slot.id)}
                      onInsertPreset={(presetId) => handlers.onInsertPreset(presetId, slot.id)}
                      presets={handlers.presetOptions}
                    />
                  </span>
                </div>
                {slot.children.length > 0 ? (
                  <ul className={`mt-1 space-y-1 ${TREE_CHILD_INDENT_CLASS}`}>
                    {slot.children.map((c) => (
                      <ComposerWidgetEditRow
                        key={c.id}
                        widget={c}
                        orphanSet={orphanSet}
                        handlers={handlers}
                        selectedId={selectedId}
                        selectedIds={selectedIds}
                        onSelect={onSelect}
                        onHoverWidget={onHoverWidget}
                        collapsedIds={collapsedIds}
                        onToggleCollapse={onToggleCollapse}
                        renameRequest={renameRequest}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className={`mt-0.5 text-gray-400 ${TREE_CHILD_INDENT_CLASS}`}>
                    빈 슬롯 — 슬롯 행의 "+"로 위젯을 추가하세요.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Slice B: 중앙 미리보기 위젯 래퍼 — 그립 핸들 draggable + insert-before droppable을 주입한다.
 * ComposerWidgetTreeView의 renderWrapper hook으로 각 위젯을 감싼다(패키지는 dnd 미의존).
 * drag listeners/attributes는 **그립에만** 부착(위젯 본문 클릭=선택/입력 유지). droppable ref는 바깥 컨테이너.
 * isOver면 insert 위치(gridded=좌측, 아니면 상단)에 파란 라인. dragging 중엔 반투명.
 */
/**
 * 모달 편집 모드 — **중앙(미리보기) 패널만** 덮는 차단 dim(2026-07-15 사용자 확정: 캔버스를 명시적으로
 * 닫기 전에는 모달에만 마우스 이벤트). 실폼 모달 오버레이와 동일 톤(bg-black/40).
 * ⚠️ 범위는 **패널 루트 기준 `absolute inset-0`** — 좌 트리·우 속성 패널은 이 레이어 밖이라 모달 저작
 * 중에도 조작 가능하다(2026-07-22 사용자 확정: **의도된 동작**, 병행 저작을 위해). 다만 그 패널들에서
 * 실행하는 조작은 모달 스코프를 따라야 한다 — 트리 최상위 "+ 위젯"이 리터럴 root로 추가해 어두워진
 * 본문에 위젯이 붙던 결함이 그래서 났다(rootParentId={modalAuthoringId ?? null}로 수정).
 * 스크롤러(CanvasViewport) *밖*(패널 루트)에 두어 레이어 위 wheel이 에디터 스크롤로 체이닝되지 않게
 * 하고, native wheel preventDefault로 조상 스크롤 유출도 방어(React onWheel은 passive라 preventDefault
 * 불가 — ref 등록). 모달은 body portal이라 이 위에서 정상 조작.
 */
function ModalAuthoringDim() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const block = (e: Event) => e.preventDefault();
    el.addEventListener('wheel', block, { passive: false });
    return () => el.removeEventListener('wheel', block);
  }, []);
  return (
    <div
      ref={ref}
      className="absolute inset-0 z-30 cursor-default bg-black/40"
      aria-hidden="true"
      title="모달 편집 중 — 본문은 편집을 닫은 뒤 조작할 수 있습니다"
    />
  );
}

function ComposerCanvasWidget({
  ctx,
  view,
  onContextMenu,
  children,
}: {
  ctx: {
    widget: ComposerWidget;
    parentId: string | null;
    index: number;
    gridded: boolean;
    gridColumn?: string;
  };
  /** 활성 반응형 뷰 키 — 전폭(span) 뷰 오버라이드 해소용(rc renderWrapper ctx엔 view가 없다). */
  view: string;
  /** 위젯 우클릭 — 호스트가 선택 보정 + 컨텍스트 메뉴를 연다(호스트에서 stopPropagation). */
  onContextMenu?: (widgetId: string, e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  const drag = useDraggable({
    id: `cwnode-${ctx.widget.id}`,
    data: { id: ctx.widget.id, parentId: ctx.parentId, index: ctx.index },
  });
  const drop = useDroppable({
    id: `cwins-${ctx.parentId ?? 'root'}-${ctx.index}`,
    data: { parentId: ctx.parentId, index: ctx.index },
  });
  // 다단 그룹(gridded) 전폭 판정은 rc와 동일 규칙(resolveWidgetFullWidth — span:1 명시 시만)로
  // WYSIWYG 유지. 비율(0<r<1) 배치는 이 클래스 축이 아니라 rc 행 컨테이너가 폭을 소유하며,
  // 그때 rc가 gridded=false를 넘겨 여기 클래스가 붙지 않는다(폭이 두 번 정해지는 것 방지).
  // 과거 "디자인 위젯=무조건 전폭" 하드코딩은 단 우선 모델(2026-07-15)로 폐기.
  // renderWrapper 경로에선 이 wrapper가 그리드 직속 자식이라 여기에 col-span을 부여한다.
  // 부모 섹션 채움 스킴 활성 시(ctx.gridColumn)엔 클래스 span 대신 인라인 grid-column을 이 wrapper에 부여.
  // ⚠️ view를 넘겨야 **뷰 오버라이드**(responsive[view].span, 특히 base 전폭을 이 뷰만 해제하는
  // 'auto')가 캔버스에 반영된다 — 안 넘기면 base 값만 보여 저작 UI가 조용히 거짓말을 한다.
  // 클래스도 rc widgetSpanClass와 같은 규칙(뷰 지정=고정 col-span-full / 미지정=md: 자동 degrade).
  const spanFull =
    !ctx.gridColumn && ctx.gridded && resolveWidgetFullWidth(ctx.widget, view)
      ? view
        ? 'col-span-full'
        : 'md:col-span-full'
      : '';
  return (
    <div
      ref={drop.setNodeRef}
      className={`group relative ${spanFull} ${drag.isDragging ? 'opacity-50' : ''}`}
      style={ctx.gridColumn ? { gridColumn: ctx.gridColumn } : undefined}
      onContextMenu={onContextMenu ? (e) => onContextMenu(ctx.widget.id, e) : undefined}
    >
      {drop.isOver ? (
        <div
          className={
            ctx.gridded
              ? 'pointer-events-none absolute inset-y-0 -left-1 w-0.5 bg-brand-500'
              : 'pointer-events-none absolute inset-x-0 -top-1 h-0.5 bg-brand-500'
          }
          aria-hidden="true"
        />
      ) : null}
      <span
        ref={drag.setNodeRef}
        {...drag.listeners}
        {...drag.attributes}
        className="absolute -left-1 top-0 z-10 cursor-grab select-none text-gray-400 opacity-0 group-hover:opacity-100"
        title="드래그로 이동"
        // grip 클릭이 "빈 영역=선택 해제" 컨테이너로 버블돼 선택이 풀리는 것 방지.
        onClick={(e) => e.stopPropagation()}
      >
        ⠿
      </span>
      {children}
    </div>
  );
}

/**
 * Slice B: 컨테이너(root/그룹) 말미 append 드롭존 — 맨끝·빈 그룹 드롭 대상.
 * index=MAX_SAFE_INTEGER(insertWidgetAt이 length로 clamp=append). 평소 거의 안 보이고 isOver 시 강조.
 */
function ComposerCanvasDropSlot({
  parentId,
  className,
  style,
}: {
  parentId: string | null;
  /** 다단 grid 전폭 배치(rc renderDropSlot layout) — 루트에 직접 적용(래퍼 금지, rc 주석 참조). */
  className?: string;
  style?: React.CSSProperties;
}) {
  const drop = useDroppable({
    id: `cwslot-${parentId ?? 'root'}`,
    data: { parentId, index: Number.MAX_SAFE_INTEGER },
  });
  // 드래그 중에만 흐름에 존재(display:none은 flex/grid gap도 미발생) — 평상시엔 스택 항목으로
  // 끼어 마지막 위젯 아래를 실폼보다 +gap+10px 벌려 놓던 WYSIWYG 결함(2026-07-14 사용자 제보,
  // 캔버스=실폼 레이아웃 결 차이는 전부 결함 원칙).
  return (
    <div
      ref={drop.setNodeRef}
      className={`${drop.active ? 'block' : 'hidden'} min-h-[10px] rounded text-center text-[10px] leading-[10px] text-brand-500 ${
        drop.isOver ? 'bg-brand-100 ring-1 ring-brand-400' : ''
      } ${className ?? ''}`}
      style={style}
    >
      {drop.isOver ? '여기에 놓기' : ''}
    </div>
  );
}

/**
 * 3-패널 셸(Slice A) 우측 속성창 — 선택된 위젯 1개의 속성을 세로 폼으로 편집한다.
 * input/group/heading/design 모두 렌더 대상인 ComposerWidget(composerWidgetTree)을 onWidgetUpdate로
 * 직접 갱신한다(WYSIWYG — 중앙 미리보기·실제 작성 페이지가 그 트리를 렌더하므로 즉시 반영). input의
 * 위젯 종류 후보 산출용 dataType만 파생 작성폼(writeFormFields)에서 읽는다(read-only). 삭제/빼기는 onRemove.
 */
/* 활성 반응형 뷰의 위젯 오버라이드(숨김) 컨트롤은 디스크립터 레지스트리(hiddenOnView)로 통합 —
   WidgetHiddenOnViewControl(단일/복수 공용) 참조. */

/** sortableSelect 후보 소스 카테고리(그룹 소제목) — 표시 순서 = 이 배열 순. */
const SORTABLE_SOURCE_CATEGORIES = {
  dataset: '내장 데이터셋',
  dataSource: '조립 데이터 소스',
  catalog: '시스템 조회',
  ai: 'AI 생성 데이터',
  preserved: '기타(보존)',
} as const;
type SortableSourceCategory =
  (typeof SORTABLE_SOURCE_CATEGORIES)[keyof typeof SORTABLE_SOURCE_CATEGORIES];

/** 카테고리별 안내 문구(그룹 소제목 아래 노출) — 없으면 생략. */
const SORTABLE_SOURCE_CATEGORY_HINTS: Partial<Record<SortableSourceCategory, string>> = {
  [SORTABLE_SOURCE_CATEGORIES.ai]:
    '작성 시점에 점검 내용 기반으로 AI가 생성합니다(같은 점검의 이전 생성분은 재사용). 파트별 키(안전관리 권고사항/요약/결론)는 그 파트만 요청해 불필요한 생성을 줄입니다.',
  [SORTABLE_SOURCE_CATEGORIES.catalog]:
    '시스템 조회 목록(list) 반환 결과를 후보 행으로 사용합니다(작성 시점 컨텍스트로 서버가 채움).',
};

/** sortableSelect 후보 소스 1건 — 복수 선택 UI와 필드 매핑 셀렉트가 공유. */
interface SortableSourceOption {
  key: string;
  label: string;
  /** 카테고리(그룹 소제목) — 체크박스 목록을 카테고리로 나눠 표시. */
  category: SortableSourceCategory;
  /** 이 소스가 제공하는 행 필드({key, label}) — 필드 매핑 셀렉트 후보(공통 필드) 산출용. */
  fields: { key: string; label: string }[];
}

/**
 * AI 생성 데이터 파트 관례 키(guidance/summary/conclusion) → 한글 표시 라벨. dataDefs/dataSources에
 * 없는 런타임 생성 계약 키. guidance 라벨은 범용명 "안전관리 권고사항"(2026-07-16 사용자 확정 —
 * 생성물의 실제 성격은 "~바랍니다" 지시·권고 목록이라 구 SMS 섹션명 "관리적 수행 내용"과 어긋남.
 * SMS 보고서 양식 안 섹션 제목은 문서 규격이라 템플릿 저작에서 별도 결정 — 여기는 데이터 계층 라벨).
 */
const AI_CANDIDATE_SOURCE_LABELS: Record<string, string> = {
  [COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS.guidance]: '안전관리 권고사항',
  [COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS.summary]: '요약',
  [COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS.conclusion]: '결론',
};

/**
 * 후보 소스 옵션 목록(카테고리 부여) — 내장 데이터셋(dataDefs) + 조립/수동 소스(dataSources) +
 * 카탈로그 list entry(cardinality==='list') + AI 생성 데이터 파트 관례 키(3종).
 * 카탈로그 필드 매핑 후보는 entry.returns(path+label) 스키마에서 산출한다.
 */
function collectSortableSourceOptions(
  dataDefs: DataDef[] | undefined,
  dataSources: DataSourceDef[] | undefined,
  catalog: CatalogEntry[] | undefined,
): SortableSourceOption[] {
  const out: SortableSourceOption[] = [];
  for (const d of dataDefs ?? []) {
    out.push({
      key: d.key,
      label: d.label,
      category: SORTABLE_SOURCE_CATEGORIES.dataset,
      fields: d.fields.map((f) => ({ key: f.key, label: f.label })),
    });
  }
  for (const s of dataSources ?? []) {
    out.push({
      key: s.key,
      label: s.label,
      category: SORTABLE_SOURCE_CATEGORIES.dataSource,
      fields: s.fields.map((f) => ({ key: f.field, label: f.label ?? f.field })),
    });
  }
  for (const e of catalog ?? []) {
    if (e.cardinality !== 'list') continue;
    // kind:'ai' entry(구 aiSelect 보조 위젯 채널 — ai-sms-improvement-opinions 등)는 제외: 같은 AI
    // 생성물의 정본 채널은 "AI 생성 데이터" 카테고리(파트 관례 키)라 병렬 노출이 혼란(2026-07-16 제보).
    if (e.kind === 'ai') continue;
    out.push({
      key: e.key,
      label: `${e.label} (${e.key})`,
      category: SORTABLE_SOURCE_CATEGORIES.catalog,
      fields: e.returns.map((r) => ({ key: r.path, label: r.label })),
    });
  }
  for (const [key, label] of Object.entries(AI_CANDIDATE_SOURCE_LABELS)) {
    out.push({
      key,
      label,
      category: SORTABLE_SOURCE_CATEGORIES.ai,
      fields: COMPOSER_AI_CANDIDATE_ROW_FIELDS,
    });
  }
  return out;
}

/** 등록형 위젯 전파 대상 후보. value=target 문자열(스칼라 bindingKey 또는 `defKey[].field`). */
interface WriteTargetCandidate {
  value: string;
  label: string;
  /** 배열 일괄 대상 여부(그룹 표시용). */
  isArray: boolean;
}

/**
 * 전파 대상 후보 수집 — ①배치된 다른 input 위젯 bindingKey(스칼라) + table 위젯 필드(배열)
 * ②템플릿 dataDefs·dataSources(list) 필드(배열, `defKey[].field`). 자기 자신·등록형은 제외.
 */
function collectWriteTargetCandidates(
  tree: ComposerWidget[],
  dataDefs: DataDef[] | undefined,
  dataSources: DataSourceDef[] | undefined,
  selfId: string,
): WriteTargetCandidate[] {
  const scalars: WriteTargetCandidate[] = [];
  const arrays: WriteTargetCandidate[] = [];
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'group') {
        walk(w.children);
        continue;
      }
      if (w.kind !== 'input' || w.id === selfId || w.widget === 'registered') continue;
      if (w.widget === 'table') {
        for (const f of w.fields ?? []) {
          arrays.push({
            value: `${w.bindingKey}[].${f.key}`,
            label: `${w.label ?? w.bindingKey} · ${f.label} (모든 행)`,
            isArray: true,
          });
        }
      } else {
        scalars.push({ value: w.bindingKey, label: w.label ?? w.bindingKey, isArray: false });
      }
    }
  };
  walk(tree);
  for (const d of dataDefs ?? []) {
    for (const f of d.fields) {
      arrays.push({
        value: `${d.key}[].${f.key}`,
        label: `${d.label} · ${f.label} (모든 행)`,
        isArray: true,
      });
    }
  }
  for (const s of dataSources ?? []) {
    if (s.cardinality !== 'list') continue;
    for (const f of s.fields) {
      arrays.push({
        value: `${s.key}[].${f.field}`,
        label: `${s.label} · ${f.label ?? f.field} (모든 행)`,
        isArray: true,
      });
    }
  }
  // 같은 target 문자열 중복 제거 — 배치 table 위젯이 dataDef를 바인딩하면(bindingKey===defKey)
  // 트리 walk와 dataDefs 루프가 같은 `defKey[].field`를 이중 산출한다(콤보 옵션 중복 표시 +
  // React key 충돌 경고, 2026-07-18). 첫 등장(캔버스 배치 위젯 라벨) 우선.
  const seen = new Set<string>();
  return [...scalars, ...arrays].filter((c) => {
    if (seen.has(c.value)) return false;
    seen.add(c.value);
    return true;
  });
}

/**
 * writes(전파 매핑) 편집기의 target 후보 두 축(`bodyTargets`·`arrayTargets`) — 콤보·등록형·제안 위젯
 * 세 패널이 같은 규칙으로 산출한다(호출부마다 같은 삼항을 복제하지 말 것).
 *
 * - 반복 행 안(`enclosingRepeat`)이면 행 스코프(P4a): rc가 전파를 rowValues에 실행하므로 후보도 같은 행
 *   템플릿 배치 위젯 ∪ 행 필드 선언(hidden·객체 자식 포함 — 배치 위젯 없는 필드도 행 객체에 실린다,
 *   2026-09-14). 배열 일괄 대상(dataDef/데이터소스)은 본문 스코프라 행 안 미제공.
 *   `allowSelf`는 자기 키 허용 축 — 제안(suggestionsFrom) writes만 true(rc pick 순서 onChange(text)→writes라
 *   마지막 쓰기가 살아남아 제안 표기로 확정 텍스트를 덮는 저작이 성립). 콤보·등록형은 false — 무의미가
 *   아니라 위험하다(`repeatScopeWritesTargets` doc: 콤보는 선택 메모리 회수, 등록형은 값 객체 형상).
 * - 본문 스코프면 종전(본문 후보 자기 제외 + 배열 일괄 후보).
 */
function writesTargetProps(args: {
  w: { id: string; bindingKey: string };
  tree: ComposerWidget[];
  enclosingRepeat: { over: string; children: ComposerWidget[] } | null | undefined;
  /** enclosingRepeat.over로 찾은 편집형 list 선언(없으면 배치 위젯만). */
  repeatDef: DataSourceDef | undefined;
  catalog: CatalogEntry[] | undefined;
  dataDefs: DataDef[] | undefined;
  dataSources: DataSourceDef[] | undefined;
  allowSelf: boolean;
}): { bodyTargets: CommitTargetOption[]; arrayTargets: WriteTargetCandidate[] } {
  const { w, tree, enclosingRepeat, repeatDef, catalog, dataDefs, dataSources, allowSelf } = args;
  if (enclosingRepeat) {
    return {
      bodyTargets: repeatScopeWritesTargets({
        selfBindingKey: w.bindingKey,
        allowSelf,
        placed: scopeChildrenCommitOptions(enclosingRepeat.children),
        def: repeatDef,
        catalog,
      }),
      arrayTargets: [],
    };
  }
  return {
    bodyTargets: collectBodyCommitTargets(tree).filter((t) => t.bindingKey !== w.bindingKey),
    arrayTargets: collectWriteTargetCandidates(tree, dataDefs, dataSources, w.id).filter(
      (c) => c.isArray,
    ),
  };
}

/**
 * writes(전파 매핑) 공용 편집기 — 이 컴포넌트가 단일 정본, 별도 재구현 금지.
 * 콤보 위젯 "선택 전파 매핑"과 등록형(특화) 위젯 "전파 매핑"이 공유한다(2026-07-17 통일 —
 * 두 벌 유지가 등록형 쪽 hover 강조 부재·검색 부재 격차의 원인이었음). source·target 모두
 * 검색형 콤보(CatalogCombobox) + "목록에 없음 — 보존" 규약 + target 캔버스 강조(드롭다운
 * 옵션 hover 시 강조·스크롤, 닫힘 상태는 JumpToWidgetButton 클릭 — modalTrigger commit 매핑과 동형 배선).
 */
function WritesMappingEditor({
  writes,
  onChange,
  sourceOptions,
  bodyTargets,
  arrayTargets,
  onHoverHighlightWidget,
  onFtCellHover,
  title,
  emptyText,
  sourceLabel,
  sourcePlaceholder,
  targetPlaceholder = '대상 위젯 검색…',
  helpText,
  labelCls,
}: {
  writes: RegisteredWidgetWrite[];
  onChange: (next: RegisteredWidgetWrite[]) => void;
  /** source 후보 — 콤보=단일 제안 소스 entry.returns(path/label), 등록형=meta.outputs. */
  sourceOptions: { key: string; label: string }[];
  /** target 스칼라 후보(hover 강조 정보 포함) — collectBodyCommitTargets 산출(자기 자신 제외). */
  bodyTargets: CommitTargetOption[];
  /** target 배열 일괄 후보 — collectWriteTargetCandidates(...).filter(isArray). */
  arrayTargets: { value: string; label: string }[];
  onHoverHighlightWidget?: (id: string | null) => void;
  onFtCellHover?: (c: { tableId: string; rowId: string; columnId: string } | null) => void;
  /** 섹션 라벨 — 콤보 "선택 전파 매핑" / 등록형 "전파 매핑". */
  title: string;
  /** 매핑 0건일 때 안내문. */
  emptyText: string;
  /** source 행 라벨 — 콤보 "행 필드" / 등록형 "산출 필드". */
  sourceLabel: string;
  /** source 콤보 placeholder — 콤보 "행 필드 검색…" / 등록형 "산출 필드 검색…". */
  sourcePlaceholder: string;
  /** target 콤보 placeholder — 기본은 위젯 대상. 표 칼럼 전파처럼 대상 축이 다르면 바꾼다. */
  targetPlaceholder?: string;
  /** 하단 안내문(없으면 미표시). */
  helpText?: string;
  labelCls: string;
}) {
  const updateWriteRow = (idx: number, patch: Partial<RegisteredWidgetWrite>): void =>
    onChange(writes.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  // source도 target과 같은 검색형 콤보(라벨 주 표시 + key 회색 모노 병기)로 시각 통일.
  const sourceComboOptions: ComboboxOption[] = sourceOptions.map((f) => ({
    value: f.key,
    label: f.label,
    key: f.key,
  }));
  const targetComboOptions: ComboboxOption[] = [
    ...bodyTargets.map((t) => ({
      value: t.bindingKey,
      label: t.label || t.bindingKey,
      key: t.bindingKey,
      badge: t.widget,
      group: t.group,
    })),
    ...arrayTargets.map((c) => ({
      value: c.value,
      label: c.label,
      key: c.value,
      group: '배열(모든 행)',
    })),
  ];
  const toHighlightByKey = new Map<string, CommitTargetHighlight>();
  for (const t of bodyTargets) if (t.highlight) toHighlightByKey.set(t.bindingKey, t.highlight);
  // commit 매핑과 동형 — 위젯/셀 두 강조 채널을 한 함수로 수렴(전환 시 반대 채널 해제).
  const highlightTarget = (h: CommitTargetHighlight | null): void => {
    if (!h) {
      onHoverHighlightWidget?.(null);
      onFtCellHover?.(null);
      return;
    }
    if (h.kind === 'widget') {
      onFtCellHover?.(null);
      onHoverHighlightWidget?.(h.id);
    } else {
      onHoverHighlightWidget?.(null);
      onFtCellHover?.({
        tableId: h.tableId,
        rowId: h.rowId,
        columnId: h.columnId,
      });
    }
  };
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className={labelCls}>{title}</label>
        <Button
          variant="success"
          size="xs"
          className="px-1.5"
          onClick={() =>
            onChange([...writes, { source: sourceOptions[0]?.key ?? '', target: '' }])
          }
        >
          + 행
        </Button>
      </div>
      {writes.length === 0 ? (
        <p className="text-[11px] text-gray-400">{emptyText}</p>
      ) : (
        <ul className="space-y-2">
          {writes.map((row, idx) => (
            <li key={idx} className="space-y-1.5 rounded border border-gray-200 bg-white p-2">
              <div className="flex items-center gap-1.5">
                <span className="w-14 shrink-0 text-[11px] text-gray-500">{sourceLabel}</span>
                {/* eslint-disable-next-line local/catalog-picker-required -- 등록 위젯 write 매핑 source(행 필드 키 목록) — 카탈로그 엔트리 아님 */}
                <CatalogCombobox
                  value={row.source || undefined}
                  options={
                    // 후보에 없지만 이미 지정된 source(소스 교체 등)는 잃지 않게 보존.
                    row.source && !sourceOptions.some((f) => f.key === row.source)
                      ? [
                          ...sourceComboOptions,
                          {
                            value: row.source,
                            label: `${row.source} (목록에 없음 — 보존)`,
                            key: row.source,
                          },
                        ]
                      : sourceComboOptions
                  }
                  onChange={(v) => updateWriteRow(idx, { source: v ?? '' })}
                  placeholder={sourcePlaceholder}
                  allowClear
                  clearLabel="(선택)"
                />
                <button
                  type="button"
                  className="shrink-0 rounded px-1.5 text-xs text-gray-400 hover:text-red-500"
                  aria-label="매핑 삭제"
                  onClick={() => onChange(writes.filter((_, i) => i !== idx))}
                >
                  ×
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-14 shrink-0 text-[11px] text-gray-500">→ 대상</span>
                {/* 옵션 호버 시 그 대상 강조. 닫힘(선택됨) 상태는 이동 버튼 클릭으로만(hover 이동 금지). */}
                <div className="flex-1 min-w-0">
                  {/* eslint-disable-next-line local/catalog-picker-required -- 등록 위젯 write 매핑 대상(본문 위젯/배열 bindingKey 목록) — 카탈로그 엔트리 아님 */}
                  <CatalogCombobox
                    value={row.target || undefined}
                    options={
                      row.target && !targetComboOptions.some((o) => o.value === row.target)
                        ? [
                            ...targetComboOptions,
                            {
                              value: row.target,
                              label: `${row.target} (목록에 없음 — 보존)`,
                              key: row.target,
                            },
                          ]
                        : targetComboOptions
                    }
                    onChange={(v) => updateWriteRow(idx, { target: v ?? '' })}
                    placeholder={targetPlaceholder}
                    allowClear
                    clearLabel="(선택)"
                    onHoverOption={(v) =>
                      highlightTarget(v ? toHighlightByKey.get(v) ?? null : null)
                    }
                  />
                </div>
                <JumpToWidgetButton
                  disabled={!row.target || !toHighlightByKey.get(row.target)}
                  onJump={() =>
                    highlightTarget(row.target ? toHighlightByKey.get(row.target) ?? null : null)
                  }
                  onClear={() => highlightTarget(null)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      {helpText ? (
        <p className="mt-1 text-[11px] leading-4 text-gray-400">{helpText}</p>
      ) : null}
    </div>
  );
}

/**
 * 등록형 위젯 속성 패널 — 라벨 편집 + widgetKey 표시(읽기) + 전파 매핑 편집(행 추가/삭제,
 * source=위젯 산출 필드, target=배치 위젯/배열 필드 후보). writes 불변 갱신.
 */
function RegisteredWidgetPropertyPanel({
  widget,
  bodyTargets,
  arrayTargets,
  onHoverHighlightWidget,
  onFtCellHover,
  onWidgetUpdate,
  labelCls,
  inputCls,
  activeView,
}: {
  widget: InputWidget;
  /** 전파 target 스칼라 후보(hover 강조 포함) — 콤보 writes와 동일 산출(자기 자신 제외). */
  bodyTargets: CommitTargetOption[];
  /** 전파 target 배열 일괄 후보. */
  arrayTargets: { value: string; label: string }[];
  onHoverHighlightWidget?: (id: string | null) => void;
  onFtCellHover?: (c: { tableId: string; rowId: string; columnId: string } | null) => void;
  onWidgetUpdate: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  labelCls: string;
  inputCls: string;
  activeView: string;
}) {
  const meta = REGISTERED_FORM_WIDGETS_META.find((m) => m.key === widget.widgetKey);
  const writes = widget.writes ?? [];
  const setWrites = (next: RegisteredWidgetWrite[]): void =>
    onWidgetUpdate(widget.id, (x) =>
      x.kind === 'input' ? { ...x, writes: next.length ? next : undefined } : x,
    );

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-gray-500">특화 위젯</p>
      <p className="font-mono text-[11px] text-gray-400 break-all">{widget.widgetKey}</p>
      {meta?.description ? (
        <p className="text-[11px] leading-snug text-gray-500">{meta.description}</p>
      ) : null}
      {/* 동작 조건 안내(호스트 주입 등 보이지 않는 조건) — 소스 필드 표의 '위젯 키' select와 같은
          함수(registeredWidgetKeyHint). 종전엔 그 표면에만 있어 트리에서 고른 저작자는 볼 수
          없었다(2026-09-14 은닉 감사 3차 B). */}
      {registeredWidgetKeyHint(widget.widgetKey) ? (
        <p className="text-[10px] leading-snug text-gray-400">
          {registeredWidgetKeyHint(widget.widgetKey)}
        </p>
      ) : null}
      <div>
        <label className={labelCls}>라벨</label>
        <input
          type="text"
          className={inputCls}
          value={widget.label ?? ''}
          onChange={(e) => onWidgetUpdate(widget.id, (x) => ({ ...x, label: e.target.value }))}
        />
      </div>
      <WidgetPropSingle
        prop="required"
        w={widget}
        onWidgetUpdate={onWidgetUpdate}
        ctx={{ activeView }}
      />
      {/* 모드(편집 가능/읽기전용/숨김) — 일반 input 패널과 동일 디스크립터. 등록형(위험도 등)도
          mode:'readonly'면 rc 브리지가 editable=false로 전달하므로 여기서 토글 가능해야 한다(2026-07-20). */}
      <WidgetPropSingle
        prop="mode"
        w={widget}
        onWidgetUpdate={onWidgetUpdate}
        ctx={{ activeView }}
      />
      {/* 라벨 표시 계열 — 디스크립터 레지스트리 공유(구 LabelPresentationControls 분해, 2026-08-24).
          이 패널은 단일 전용이지만 Control을 복수 패널과 공유해야 두 화면의 저작 어휘가 갈라지지 않는다.
          노출 게이트(정렬=top일 때만, 폰트·보조 설명=라벨 있을 때만)는 디스크립터 applicable 소유. */}
      <WidgetPropSingle
        prop="labelHint"
        w={widget}
        onWidgetUpdate={onWidgetUpdate}
        ctx={{ activeView }}
      />
      <WidgetPropSingle
        prop="labelPlacement"
        w={widget}
        onWidgetUpdate={onWidgetUpdate}
        ctx={{ activeView }}
      />
      <WidgetPropSingle
        prop="labelAlign"
        w={widget}
        onWidgetUpdate={onWidgetUpdate}
        ctx={{ activeView }}
      />
      <WidgetPropSingle
        prop="labelStyle"
        w={widget}
        onWidgetUpdate={onWidgetUpdate}
        ctx={{ activeView }}
      />
      <div className="border-t border-gray-200 pt-3">
        {/* writes 편집 UI는 WritesMappingEditor 단일 정본 — 별도 재구현 금지. */}
        <WritesMappingEditor
          writes={writes}
          onChange={setWrites}
          sourceOptions={meta?.outputs ?? []}
          bodyTargets={bodyTargets}
          arrayTargets={arrayTargets}
          onHoverHighlightWidget={onHoverHighlightWidget}
          onFtCellHover={onFtCellHover}
          title="전파 매핑"
          emptyText="선택 결과를 어느 필드에 쓸지 매핑을 추가하세요."
          sourceLabel="산출 필드"
          sourcePlaceholder="산출 필드 검색…"
          helpText="위젯에서 선택한 결과의 산출 필드 값이 대상 위젯에 자동 기록됩니다."
          labelCls={labelCls}
        />
      </div>
      {/* 뷰별 숨김은 공통 푸터의 뷰별 축 묶음으로 이동(2026-08-06) — 이 패널도 kindBody의 한
          갈래라 공통 푸터를 함께 받는다. 여기 남겨 두면 같은 컨트롤이 두 번 그려진다. */}
    </div>
  );
}

function WriteFormPropertyPanel({
  selectedWidgets,
  unplacedSelected,
  unplacedPool,
  placementMap,
  onNavigateToBodyNode,
  anchorId,
  writeFormFields,
  onWidgetUpdate,
  onWidgetPreview,
  onWidgetCommit,
  onWidgetRevert,
  onWidgetsUpdate,
  onRemove,
  onRemoveMany,
  onTreeUpdate,
  onPlace,
  activeView,
  tree,
  dataDefs,
  variables,
  dataSources,
  catalogParams,
  onDataSourcesChange,
  onTemplateUpdate,
  countKeyRefs,
  catalog,
  ftFocusCell,
  onFtCellHover,
  onFtCellFocus,
  onFtCellFocusClear,
  ftCellClipboard,
  onFtCellCopy,
  onFtCellBlockCopy,
  onFtCellPaste,
  onFtCellPasteMany,
  onFtCellsContentClear,
  onFtRequestConfirm,
  writeValues,
  bindingPrefillMap,
  modalAuthoringId,
  onModalAuthoringToggle,
  onApplyPresetVersion,
  onPublishPresetFromInstance,
  onUpdatePresetMeta,
  onDeletePreset,
  onSelectWidget,
  onHoverHighlightWidget,
  activeBranchSlots,
  onSetActiveBranchSlot,
  onWidgetAddBranchSlot,
  onWidgetRemoveBranchSlot,
  onWidgetMoveBranchSlot,
  onWidgetUpdateBranchSlot,
}: {
  /** 선택된 위젯(단일=1, 다중=2+). 비면 안내 문구. */
  selectedWidgets: ComposerWidget[];
  /** 선택된 미배치(풀) 위젯 — 파생물이라 편집 불가, 읽기 정보 + 배치 액션만 노출. */
  unplacedSelected: InputWidget[];
  /** 전체 미배치(풀) 입력 위젯 — 표 위젯의 입력 셀에 배치할 후보 목록. */
  unplacedPool: InputWidget[];
  /** 파생 위젯 bindingKey → 양식(body) 내 배치 위치("양식 내 위치" 브레드크럼용). */
  placementMap: Map<string, BindingPlacement[]>;
  /** 브레드크럼 클릭 → 양식 탭 전환 + body 노드 선택. */
  onNavigateToBodyNode: (nodeId: string, cell?: { row: number; col: number }) => void;
  /** 표 셀 복수 포커스 — cells 마지막=anchor. source로 스크롤 여부 구분(canvas만 스크롤). */
  ftFocusCell?: FtCellFocus | null;
  /** 속성 셀 카드 hover → 캔버스 표 안 셀 하이라이트(leave 시 null). */
  onFtCellHover?: (c: { tableId: string; rowId: string; columnId: string } | null) => void;
  /** 속성 셀 카드 클릭 → 셀 포커스(additive=Ctrl/⌘ 토글) — 캔버스 하이라이트+단일 클릭 전환 모드 진입. */
  onFtCellFocus?: OnFtCellFocus;
  /** 셀 포커스 해제(셀 속성 → 표 속성 복귀 / 복수 선택 전체 해제). */
  onFtCellFocusClear?: () => void;
  /** 공유 셀 클립보드 + 복사/붙여넣기(캔버스 Ctrl+C/V와 공용). */
  ftCellClipboard?: FtCellClipboard | null;
  onFtCellCopy?: (cell: FormTableFixedCell) => void;
  /** 복수 선택 → 블록 복사(선택 bounding box를 상대 오프셋 슬롯으로 수집). */
  onFtCellBlockCopy?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  onFtCellPaste?: (tableId: string, rowId: string, columnId: string) => void;
  /** 복사 셀 → 선택된 전 셀 일괄 붙여넣기(undo 1스텝). */
  onFtCellPasteMany?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  /** 선택된 전 셀 내용 제거(undo 1스텝). */
  onFtCellsContentClear?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  /** 표 행/열 삭제 등 내용 손실 경고 확인 모달 요청(EditorPage confirmState 위임). */
  onFtRequestConfirm?: FtConfirmRequest;
  /** 현재 미리보기 컨텍스트의 작성폼 값(prefill+캔버스 편집) — 미배치/셀 배치 시 값 미리보기용. */
  writeValues?: Record<string, unknown>;
  /** body 바인딩 자동 채움 출처(bindingKey→catalogKey·select) — 파생 위젯의 prefill 표시 fallback. */
  bindingPrefillMap?: Map<string, { catalogKey: string; select?: string }>;
  /** 주 선택(anchor) — 단일 편집 기준. */
  anchorId: string | null;
  writeFormFields: InputWidget[];
  onWidgetUpdate: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  /** 위젯 수치 입력 실시간 미리보기(replacePresent — 히스토리 미적재). formTable 열/행 치수 DimInput용. */
  onWidgetPreview: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  /** 위젯 수치 입력 확정(commitPresent — 선행 미리보기 transient를 undo 1스텝으로 닫음). */
  onWidgetCommit: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  /** 위젯 수치 입력 미리보기 폐기(discardTransient) — Esc/무변경/이탈. */
  onWidgetRevert: () => void;
  /** 다중 공통 속성 일괄 편집(선택 전체를 1 undo 스텝으로 통일). */
  onWidgetsUpdate: (ids: string[], updater: (w: ComposerWidget) => ComposerWidget) => void;
  onRemove: (id: string) => void;
  /** 다중 선택 일괄 제거. */
  onRemoveMany: (ids: string[]) => void;
  /** 트리 전체 갱신(wtApply) — 형제 집합 단위 조작(뷰 순서 초기화)용. WidgetPropCtx.onTreeUpdate. */
  onTreeUpdate?: (fn: (tree: ComposerWidget[]) => ComposerWidget[]) => void;
  /** 미배치 위젯 배치(정보 패널의 배치 버튼 — 풀 행 버튼과 동일 경로). */
  onPlace: (widget: ComposerWidget, parentId: string | null) => void;
  /** 활성 반응형 뷰 — 단 수 편집이 base면 직접 columns, 아니면 responsive[view] 델타. */
  activeView: string;
  /** 등록형 위젯 전파 대상 후보 산출용(배치된 다른 위젯 bindingKey). */
  tree: ComposerWidget[];
  /** 등록형 위젯 전파 배열 대상 후보 산출용(dataDefs 필드). */
  dataDefs?: DataDef[];
  /** 변수 파생 위젯의 역전파 대체 안내(영속 정본=rememberScope)용. */
  variables?: TemplateVariable[];
  /** 등록형 위젯 전파 배열 대상 후보 산출용(dataSources list 필드) + 행 그리드 위젯 속성 편집 대상. */
  dataSources?: DataSourceDef[];
  /** 미배치 위젯 정보 패널의 파라미터 행에 지정 값을 함께 표시하기 위한 템플릿 수준 지정. */
  catalogParams?: TemplateCatalogParamBindings;
  /** 행 그리드(dataSourceRows) 위젯 속성(필드별 위젯 매핑)이 dataSources를 갱신하는 콜백. */
  onDataSourcesChange: (next: DataSourceDef[]) => void;
  /** 템플릿 원자 갱신(apply) — 표 칼럼 참조 모드(P3)의 정본 쓰기·detach용. */
  onTemplateUpdate?: (fn: (t: ReportTemplate) => ReportTemplate) => void;
  /** bindingKey 참조 수(countDataSourceUsage 위임) — detach 차단 판정. */
  countKeyRefs?: (key: string) => number;
  /** 미배치 위젯 정보 패널의 데이터 출처(API/파라미터) 표시용 카탈로그. */
  catalog?: CatalogEntry[];
  /** 저작 열림 중인 modalTrigger id(P1b) — modalTrigger 속성창 버튼 라벨 토글. */
  modalAuthoringId?: string | null;
  /** 모달 저작 열림/닫힘 토글(id로 열고 null로 닫기). */
  onModalAuthoringToggle?: (id: string | null) => void;
  /**
   * 프리셋 인스턴스(presetRef 보유) 버전 적용 — 로컬 수정 감지·경고·교체는 EditorPage가 담당.
   * (widgetId, presetId, 배치 스냅샷, 적용 스냅샷, 적용 버전).
   */
  onApplyPresetVersion?: (
    widgetId: string,
    presetId: string,
    placedSnapshot: ComposerWidget | undefined,
    chosenSnapshot: ComposerWidget,
    chosenVersion: number,
  ) => void;
  /** 현재 인스턴스 서브트리를 프리셋 새 버전으로 발행(normalize+publish+presetRef 재스탬프). 성공 true. */
  onPublishPresetFromInstance?: (
    widgetId: string,
    presetId: string,
    changeNote: string,
  ) => Promise<boolean>;
  /** 프리셋 이름/설명 수정(메타만). 성공 true. */
  onUpdatePresetMeta?: (
    presetId: string,
    body: { name: string; description: string },
  ) => Promise<boolean>;
  /** 프리셋 soft delete(배치 인스턴스는 복사본이라 무영향). 성공 true. */
  onDeletePreset?: (presetId: string) => Promise<boolean>;
  /** 네비게이션(브레드크럼·자식 목록) 클릭 선택 — 트리 패널과 동일 경로(승격 없는 직접 선택). */
  onSelectWidget?: (id: string) => void;
  /** commit 매핑 콤보 hover → 캔버스 위젯 하이라이트(SingleWidgetProperties 경유). */
  onHoverHighlightWidget?: (id: string | null) => void;
  /** 분기 위젯 id → 활성 슬롯 index(캔버스 탭·트리 서브행과 공유) — 속성 패널 슬롯 에디터 활성 표시. */
  activeBranchSlots: Record<string, number>;
  /** 분기 슬롯 활성 전환(속성 슬롯 에디터 ● 버튼). */
  onSetActiveBranchSlot: (branchId: string, index: number) => void;
  /** 분기 슬롯 추가(rc addBranchWidgetSlot via wtApply). */
  onWidgetAddBranchSlot: (branchId: string) => void;
  /** 분기 슬롯 삭제(최소 1 가드는 rc). */
  onWidgetRemoveBranchSlot: (branchId: string, slotId: string) => void;
  /** 분기 슬롯 이동(위/아래). */
  onWidgetMoveBranchSlot: (branchId: string, slotId: string, dir: 'up' | 'down') => void;
  /** 분기 슬롯 label/condition 편집(부분 patch). */
  onWidgetUpdateBranchSlot: (
    branchId: string,
    slotId: string,
    patch: { label?: string; condition?: string },
  ) => void;
}) {
  if (selectedWidgets.length === 0) {
    // 미배치(풀) 위젯 선택 — 파생물이라 편집은 배치 후에만 유효. 읽기 정보 + 배치 액션 노출(2026-07-12).
    if (unplacedSelected.length === 1) {
      return (
        <div className="space-y-3">
          {/* 타입 요약 헤더 — 배치 위젯 속성과 동형(미배치 분기에만 없었음, 2026-07-16 사용자 요청). */}
          <WidgetSelectionSummary widgets={[unplacedSelected[0]]} />
          <BindingLocationBreadcrumb
            w={unplacedSelected[0]}
            placementMap={placementMap}
            onNavigate={onNavigateToBodyNode}
          />
          <UnplacedWidgetInfo
            w={unplacedSelected[0]}
            writeFormFields={writeFormFields}
            tree={tree}
            onPlace={onPlace}
            catalog={catalog}
            dataSources={dataSources}
            catalogParams={catalogParams}
            variables={variables}
            writeValues={writeValues}
            bindingPrefillMap={bindingPrefillMap}
          />
        </div>
      );
    }
    if (unplacedSelected.length >= 2) {
      return (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500">미배치 위젯 {unplacedSelected.length}개 선택</p>
          <p className="text-[11px] leading-snug text-gray-400">
            {unplacedSelected.map((w) => composerWidgetDisplayLabel(w)).join(' · ')}
          </p>
          <p className="text-[11px] text-gray-400">속성 편집은 배치 후 가능합니다.</p>
        </div>
      );
    }
    return (
      <p className="rounded border border-dashed border-gray-300 px-3 py-6 text-center text-xs text-gray-400">
        위젯을 선택하면 속성이 표시됩니다.
      </p>
    );
  }
  const anchor = selectedWidgets.find((x) => x.id === anchorId) ?? selectedWidgets[0];

  // 다중 선택(2개+) — 노드 다중 선택 패널처럼 공통 속성 일괄 편집(tri-state)을 본문으로 바로
  // 노출. 라벨·위젯 타입·바인딩·섹션 설정 등 개별 성격 속성은 제외(단일 선택으로 편집).
  if (selectedWidgets.length >= 2) {
    return (
      <div className="space-y-3">
        <WidgetSelectionSummary widgets={selectedWidgets} />
        <BulkWidgetProperties
          widgets={selectedWidgets}
          activeView={activeView}
          onWidgetsUpdate={onWidgetsUpdate}
          writeFormFields={writeFormFields}
          bindingPrefillMap={bindingPrefillMap}
          catalog={catalog}
          tree={tree}
          dataDefs={dataDefs}
          variables={variables}
          onHoverHighlightWidget={onHoverHighlightWidget}
          onFtCellHover={onFtCellHover}
        />
        <div className="border-t border-gray-200 pt-3">
          <button
            type="button"
            className="rounded bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
            onClick={() => onRemoveMany(selectedWidgets.map((x) => x.id))}
          >
            선택 {selectedWidgets.length}개 제거
          </button>
        </div>
      </div>
    );
  }

  // 단일 선택 — 요약 헤더 + 네비게이션(브레드크럼·자식 목록, 양식 탭 동형) + 종류별 편집 본문.
  // 순서/구조 이동(↑/↓/섹션 밖으로)은 여기서 제거 — 계층·순서·부모는 위젯 트리 관할, 속성 패널은
  // 내용·표현만(2026-07-22 사용자 확정: 위젯 트리=구조 / 속성=내용·표현).
  return (
    <div className="space-y-3">
      <WidgetSelectionSummary widgets={selectedWidgets} />
      {anchor.presetRef && onApplyPresetVersion && (
        <PresetInstanceVersionPanel
          key={anchor.id}
          presetRef={anchor.presetRef}
          onApplyVersion={(placed, chosen, version) => {
            if (anchor.presetRef) {
              onApplyPresetVersion(anchor.id, anchor.presetRef.presetId, placed, chosen, version);
            }
          }}
          onPublishCurrentVersion={
            onPublishPresetFromInstance && anchor.presetRef
              ? (changeNote) =>
                  onPublishPresetFromInstance(anchor.id, anchor.presetRef!.presetId, changeNote)
              : undefined
          }
          // 배치 스냅샷 대비 수정 판정(handleApplyPresetVersion의 로컬 수정 감지와 동일 비교기) —
          // "새 버전으로 저장" 버튼을 미수정 상태에서 비활성.
          computeModified={(placedSnapshot) => !presetSubtreeEquivalent(anchor, placedSnapshot)}
          onUpdateMeta={
            onUpdatePresetMeta && anchor.presetRef
              ? (body) => onUpdatePresetMeta(anchor.presetRef!.presetId, body)
              : undefined
          }
          onDeletePreset={
            onDeletePreset && anchor.presetRef
              ? () => onDeletePreset(anchor.presetRef!.presetId)
              : undefined
          }
        />
      )}
      <BindingLocationBreadcrumb
        w={anchor}
        placementMap={placementMap}
        onNavigate={onNavigateToBodyNode}
      />
      {onSelectWidget && (
        <WidgetNavigation
          w={anchor}
          tree={tree}
          onSelect={onSelectWidget}
          // 모달 편집 모드면 네비게이션 스코프를 그 모달로 제한(본문 조상 브레드크럼 숨김 —
          // 캔버스 차단 상태에서 본문 선택 문 방지).
          scopeRootId={modalAuthoringId ?? undefined}
        />
      )}
      <SingleWidgetProperties
        w={anchor}
        writeFormFields={writeFormFields}
        onWidgetUpdate={onWidgetUpdate}
        onWidgetPreview={onWidgetPreview}
        onWidgetCommit={onWidgetCommit}
        onWidgetRevert={onWidgetRevert}
        onRemove={onRemove}
        onTreeUpdate={onTreeUpdate}
        activeView={activeView}
        tree={tree}
        dataDefs={dataDefs}
        variables={variables}
        dataSources={dataSources}
        onDataSourcesChange={onDataSourcesChange}
        onTemplateUpdate={onTemplateUpdate}
        countKeyRefs={countKeyRefs}
        unplacedPool={unplacedPool}
        ftFocusCell={ftFocusCell}
        onFtCellHover={onFtCellHover}
        onFtCellFocus={onFtCellFocus}
        onFtCellFocusClear={onFtCellFocusClear}
        ftCellClipboard={ftCellClipboard}
        onFtCellCopy={onFtCellCopy}
        onFtCellBlockCopy={onFtCellBlockCopy}
        onFtCellPaste={onFtCellPaste}
        onFtCellPasteMany={onFtCellPasteMany}
        onFtCellsContentClear={onFtCellsContentClear}
        onFtRequestConfirm={onFtRequestConfirm}
        writeValues={writeValues}
        bindingPrefillMap={bindingPrefillMap}
        catalog={catalog}
        modalAuthoringId={modalAuthoringId}
        onModalAuthoringToggle={onModalAuthoringToggle}
        onHoverHighlightWidget={onHoverHighlightWidget}
        activeBranchSlots={activeBranchSlots}
        onSetActiveBranchSlot={onSetActiveBranchSlot}
        onWidgetAddBranchSlot={onWidgetAddBranchSlot}
        onWidgetRemoveBranchSlot={onWidgetRemoveBranchSlot}
        onWidgetMoveBranchSlot={onWidgetMoveBranchSlot}
        onWidgetUpdateBranchSlot={onWidgetUpdateBranchSlot}
        onSelectWidget={onSelectWidget}
      />
    </div>
  );
}

/**
 * 서버 ensureRecentTwoYearSlots가 행 순서를 보장하는 2개년 슬롯 카탈로그 — select 인덱스 의미 표시용.
 * (index 0=당해년도, 1=전년도. 서버 report-composer.service와 규약 공유 — 표시 전용이라 하드코딩 허용.)
 */
const TWO_YEAR_SLOT_CATALOG_KEYS = new Set(['site-worker-stats', 'site-accident-stats']);

/** select 경로의 첫 배열 인덱스 해석 — 2개년 슬롯 카탈로그는 당해/전년도 라벨, 그 외는 N번째 행. */
function selectIndexInfo(catalogKey: string | undefined, select: string | undefined): string | null {
  if (!select) return null;
  const m = select.match(/(?:^|\.)(\d+)(?:\.|$)/);
  if (!m) return null;
  const idx = Number(m[1]);
  if (catalogKey && TWO_YEAR_SLOT_CATALOG_KEYS.has(catalogKey)) {
    const label = idx === 0 ? '당해년도' : idx === 1 ? '전년도' : `${idx + 1}번째 행`;
    return `${idx} — ${label}`;
  }
  return `${idx} (${idx + 1}번째 행)`;
}

/** 현재 컨텍스트 값 한 줄 미리보기 — 객체/배열은 JSON 축약(기본 60자, 툴팁은 max 상향). 값 부재는 '(없음)'. */
function composerValuePreview(v: unknown, max = 60): string {
  if (v === undefined || v === null || v === '') return '(없음)';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > max ? `${s.slice(0, max - 3)}…` : s;
  } catch {
    return String(v);
  }
}

/**
 * 카탈로그 entry의 returns에서 select 경로가 가리키는 필드 정보(라벨/타입)를 찾는다.
 * 경로의 숫자 세그먼트(배열 index)는 건너뛰고 필드 path 이름만 매칭. 미발견 시 null.
 */
function catalogSelectFieldInfo(
  entry: CatalogEntry | undefined,
  select: string | undefined,
): { label?: string; type?: string } | null {
  if (!entry || !select) return null;
  const segs = select.split('.').filter((s) => s !== '' && !/^\d+$/.test(s));
  if (segs.length === 0) return null;
  let fields = entry.returns;
  let found: { label?: string; type?: string } | null = null;
  for (const seg of segs) {
    const f = (fields ?? []).find((x) => x.path === seg);
    if (!f) return found; // 중간까지만 매칭되면 마지막 매칭 정보 반환(경로 표기 변형에 관대)
    found = { label: f.label, type: f.type };
    fields = f.fields ?? [];
  }
  return found;
}

// 역전파 가용 판정(writeBackAvailability·writeBackWritableFieldMatch)과 안내문(writeBackHelpText)은
// writeBackAvailability.ts(순수 함수 + co-located spec). combobox 합성 prefill의 본문은 shared
// `synthesizeComboboxPrefill`(서버 수집기와 같은 함수)이고 rowScopeWriteBack.ts는 그 별칭 진입점이다.

/**
 * 위젯 단위 역전파 select — 저장 시 편집값을 현장/조직 정본에 재저장(다음 보고서 자동 채움 재사용).
 * 활성/비활성·저장 대상은 prefillFrom + 카탈로그 writer 등록으로 자동 판정(저작자는 여부·스코프만 지정).
 */
function WriteBackSelector({
  writeBack,
  prefillFrom,
  catalog,
  pairedCoverage,
  rowScope,
  onChange,
  labelCls,
  selectCls,
}: {
  writeBack: { scope: 'site' | 'organization'; useAsDefault?: boolean } | undefined;
  prefillFrom: { catalogKey: string; select?: string } | undefined;
  catalog?: CatalogEntry[];
  /** keyed upsert 페어링 커버(keyedPairingCoverage) — 키 위젯의 역전파에 함께 저장되는 부속 위젯. */
  pairedCoverage?: { keyLabel: string } | null;
  /**
   * 반복 행 안 위젯이면 호출부가 judgeRowScopeWriteBack으로 구해 넘긴다(rowScopeWriteBack.ts —
   * 서버 행 스코프 수집기 게이트 미러). 미충족이면 서버가 아무것도 저장하지 않으므로 select를
   * 잠그고 사유를 보인다(종전엔 이 판정이 없어 「저장소에 보관되어 … 재사용됩니다」라 거짓
   * 안내했다 — 2026-09-14 은닉 감사 3차). 반복 밖이면 undefined.
   */
  rowScope?: RowScopeWriteBackJudgement;
  onChange: (
    next: { scope: 'site' | 'organization'; useAsDefault?: boolean } | undefined,
  ) => void;
  labelCls: string;
  selectCls: string;
}) {
  const avail = writeBackAvailability(prefillFrom, catalog);
  // 페어링 커버 위젯은 별도 역전파 지정을 차단하되, 기존 저장값이 있으면 '없음'으로 해제할 수
  // 있어야 하므로 완전 잠금 대신 경고로 대체(해제하면 잠금 상태로 전환).
  const pairedLocked = !!pairedCoverage && !writeBack;
  // 행 스코프 미충족도 같은 규약 — 이미 켜진 값은 '없음'으로 끌 수 있어야 한다(트랩 방지).
  const rowIneligible = rowScope?.kind === 'ineligible';
  const rowLocked = rowIneligible && !writeBack;
  const disabled = avail.kind === 'disabled' || pairedLocked || rowLocked;
  // 카탈로그 writer는 entry 선언 스코프(writeBack.scope)의 정본에만 저장 가능 — 반대 스코프 비활성.
  // 미선언이면 종전 규약(현장 전용) 유지.
  const catalogScope = writeBackCatalogScope(avail);
  const siteDisabled = catalogScope === 'organization';
  const orgDisabled = catalogScope === 'site';
  return (
    <div>
      <label className={labelCls}>역전파(정본 저장)</label>
      <select
        className={selectCls}
        disabled={disabled}
        value={writeBack?.scope ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          // scope 전환 시 useAsDefault 보존(불변 갱신) — '없음'은 전체 해제.
          onChange(v === 'site' || v === 'organization' ? { ...writeBack, scope: v } : undefined);
        }}
      >
        <option value="">없음(보고서만)</option>
        <option value="site" disabled={siteDisabled}>
          현장에 저장
        </option>
        <option value="organization" disabled={orgDisabled}>
          조직에 저장
        </option>
      </select>
      <p
        className={`mt-1 text-xs leading-snug ${
          (pairedCoverage || rowIneligible) && writeBack ? 'text-amber-600' : 'text-gray-400'
        }`}
      >
        {writeBackHelpText({ avail, writeBack, pairedCoverage, rowScope })}
      </p>
      {/* '다음 보고서 기본값으로 사용'(writeBack.useAsDefault) — 도메인 writer(카탈로그) 라우팅
          위젯 전용(제네릭 `input:` 경로는 역전파 자체가 기억+자동 채움 루프라 미노출).
          결정: 2026-07-16-composer-combobox-dependent-value-axes 추가 결정(2026-07-19).
          반복 행 안(rowScope 지정)에서는 미노출 — 서버 행 스코프 패스는 키 값 등록만 하고
          useAsDefault(`__last:`)를 읽지 않는다(service applyWriteBack 행 스코프 절). */}
      {writeBack && avail.kind === 'catalog' && rowScope === undefined ? (
        <div className="mt-1.5">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={!!writeBack.useAsDefault}
              onChange={(e) =>
                onChange(
                  writeBack
                    ? { ...writeBack, useAsDefault: e.target.checked || undefined }
                    : undefined,
                )
              }
            />
            다음 보고서 기본값으로 사용
          </label>
          <p className="mt-0.5 text-[11px] leading-snug text-gray-400">
            저장 시 선택을 기억해 다음 보고서에서 기본값으로 채웁니다.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 미배치(풀) 위젯 읽기 정보 패널 — 풀 위젯은 바인딩 파생물이라 편집이 영속되지 않으므로(트리 배치 후
 * 편집), 배치 전 판단에 필요한 정보(종류/라벨/바인딩 키/데이터 타입/자동 채움 소스/API·파라미터)와
 * 배치 액션만 노출.
 */
/**
 * 미배치 위젯 정보 패널의 "해소 값" 행 — 편집 채널(Context)에서 스탬프를 읽어 그린다.
 *
 * prop으로 내리지 않는 이유는 인라인 파라미터 블록과 같다: 이 패널은 속성 패널 여러 겹 아래라
 * 중간 컴포넌트 하나만 빠뜨리면 그 화면에서만 조용히 안 뜬다. Provider 밖이면 미렌더(값 없음을
 * 지어내지 않는다).
 */
function UnplacedWidgetParamEffectiveRow({
  catalogKey,
  row,
}: {
  catalogKey: string;
  row: (label: string, value: React.ReactNode) => React.ReactNode;
}) {
  const channel = useContext(CatalogParamsEditContext);
  if (!channel) return null;
  const note = catalogParamEffectiveNote(
    channel.resolution?.[catalogKey]?.effective,
    channel.hasPreviewContext === true,
  );
  return (
    <>{row('적용 값', <span className="text-[11px] text-gray-500">{note.text}</span>)}</>
  );
}

function UnplacedWidgetInfo({
  w,
  writeFormFields,
  tree,
  onPlace,
  catalog,
  dataSources,
  catalogParams,
  variables,
  writeValues,
  bindingPrefillMap,
}: {
  w: InputWidget;
  writeFormFields: InputWidget[];
  tree: ComposerWidget[];
  onPlace: (widget: ComposerWidget, parentId: string | null) => void;
  catalog?: CatalogEntry[];
  dataSources?: DataSourceDef[];
  /** 카탈로그 파라미터 지정(템플릿 수준 정본) — 파라미터 행에 **지정 값까지** 표시하기 위해. */
  catalogParams?: TemplateCatalogParamBindings;
  /** 변수 참조 지정(`{from:'variable'}`)의 라벨 표시용. */
  variables?: TemplateVariable[];
  /** 현재 미리보기 컨텍스트 값 — 이 위젯이 실제로 갖게 될 값 미리보기. */
  writeValues?: Record<string, unknown>;
  /** body 바인딩 자동 채움 출처 fallback — 바인딩 파생 위젯은 prefill이 body 바인딩에만 있음. */
  bindingPrefillMap?: Map<string, { catalogKey: string; select?: string }>;
}) {
  const field = writeFormFields.find((f) => f.bindingKey === w.bindingKey);
  const meta =
    w.widget === 'registered'
      ? REGISTERED_FORM_WIDGETS_META.find((m) => m.key === w.widgetKey)
      : undefined;
  // 해소 체인 마지막에 body 바인딩 합성(서버 collectPrefillBindings 동일 규칙) — 파생 위젯(예: 표
  // 셀 바인딩 cell-N-M)은 catalogKey·select가 body 바인딩에만 있어 이 fallback이 실제 표시 경로.
  const prefillFrom =
    w.prefillFrom ??
    field?.prefillFrom ??
    meta?.defaultPrefillFrom ??
    bindingPrefillMap?.get(w.bindingKey);
  // 데이터 출처(어떤 API·파라미터로 불러오는지) — 배치 전 판단에 필요(2026-07-12 사용자 요청).
  // 행 그리드(dataSourceRows)는 def.primary(조립 주 소스), 그 외는 prefill 카탈로그 키로 조회.
  const sourceEntryKey =
    w.widget === 'dataSourceRows'
      ? dataSources?.find((d) => d.key === w.bindingKey)?.primary || undefined
      : prefillFrom?.catalogKey;
  const sourceEntry = sourceEntryKey
    ? catalog?.find((e) => e.key === sourceEntryKey)
    : undefined;
  const sourceParams =
    sourceEntry?.kind === 'data' || sourceEntry?.kind === 'computed'
      ? sourceEntry.params
      : undefined;
  const groupOptions = collectGroupOptions(tree);
  const row = (label: string, value: ReactNode): ReactNode => (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="break-all text-xs text-gray-700">{value}</p>
    </div>
  );
  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-gray-500">
        미배치 위젯 <span className="ml-1 rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-brand-600 ring-1 ring-brand-200">{w.widget}</span>
      </p>
      {row('라벨', composerWidgetDisplayLabel(w))}
      {row('바인딩 키', <span className="font-mono text-[11px] text-gray-500">{w.bindingKey}</span>)}
      {field?.dataType ? row('데이터 타입', field.dataType) : null}
      {/* 표 성격 — 작성 페이지의 두 표는 타입 계층이 달라(위젯 종류 vs 노드 종류) 겉모습만으로
          구분되지 않는다. 배치 전에 "작성자가 행을 늘릴 수 있나 / 칸 종류를 어디서 정하나"를 알려
          결과를 만나고 나서 되돌리는 일을 줄인다.
          레이아웃 표(formTable)는 이 패널에 올 수 없다 — 미배치 풀은 InputWidget[]이고
          (composerWidgetSync.unplaced) formTable은 별도 위젯 kind라 파생 대상이 아니다. 그래서
          분기를 추측으로 만들지 않고 데이터셋 표 한 경우만 둔다. */}
      {w.widget === 'table'
        ? row(
            '표 성격',
            '데이터셋 표 — 작성자가 행을 추가·삭제합니다. 칸 종류는 데이터셋 정의의 칼럼에서 지정합니다.',
          )
        : null}
      {prefillFrom
        ? row(
            '자동 채움(prefill)',
            `${prefillFrom.catalogKey}${prefillFrom.select ? ` · ${prefillFrom.select}` : ''}`,
          )
        : null}
      {sourceEntry ? row('데이터 소스', `${sourceEntry.group} · ${sourceEntry.label}`) : null}
      {sourceEntry?.kind === 'data' ? (
        row('API', <span className="font-mono text-[11px] text-gray-500">{sourceEntry.endpoint}</span>)
      ) : sourceEntry?.kind === 'computed' ? (
        row('계산 함수', <span className="font-mono text-[11px] text-gray-500">{sourceEntry.computeId}</span>)
      ) : null}
      {sourceParams && sourceParams.length > 0
        ? row(
            '파라미터',
            // 출처(provenance)만이 아니라 **지정된 값까지** 보여준다 — 값이 안 보이면 저작자가 고른
            // 것을 검증할 수 없다(은닉 금지). 지정 정본은 템플릿 수준 catalogParams.
            <span className="space-y-0.5">
              {sourceParams.map((p) => {
                const bound = sourceEntryKey
                  ? catalogParams?.[sourceEntryKey]?.[p.name]
                  : undefined;
                // 부재 시 동작 — 편집 진입점(인라인 블록)에만 있던 안내를 읽기 전용 요약에도 싣는다.
                // 여기서만 안 보이면 "이 위젯이 무슨 값을 쓰나"를 훑는 흐름에서 그 사실이 은닉된다.
                const absent = catalogParamAbsentNote(
                  buildCatalogParamItems([p], bound ? { [p.name]: bound } : {}, variables ?? [])[0],
                );
                return (
                  <span key={p.name} className="block">
                    <span className="font-mono text-[11px] text-gray-500">{p.name}</span>
                    {' ← '}
                    {CATALOG_PARAM_PROVENANCE_LABEL[p.provenance] ?? p.provenance}
                    {p.provenance === 'user' && (
                      <span className="text-gray-500">
                        {' · '}
                        {catalogParamBindingSummary(p, bound, variables)}
                      </span>
                    )}
                    {absent && (
                      <span className="mt-0.5 block text-[10px] leading-snug text-gray-400">
                        {absent.text}
                      </span>
                    )}
                  </span>
                );
              })}
            </span>,
          )
        : null}
      {/* 해소 결과 — 선언(무엇을 하나)만으로는 "그래서 지금 무슨 값인가"를 알 수 없다.
          편집 채널 밖(Provider 미장착)이면 스스로 렌더하지 않는다(값 지어내기 금지). */}
      {sourceEntryKey && sourceParams && sourceParams.some((p) => p.provenance === 'user') ? (
        <UnplacedWidgetParamEffectiveRow catalogKey={sourceEntryKey} row={row} />
      ) : null}
      {(() => {
        // 반환값 — select 경로가 가리키는 카탈로그 반환 필드(라벨·타입).
        const fi = catalogSelectFieldInfo(sourceEntry, prefillFrom?.select);
        return fi
          ? row('반환값', `${fi.label ?? prefillFrom?.select}${fi.type ? ` (${fi.type})` : ''}`)
          : null;
      })()}
      {(() => {
        // 행 인덱스 — select 경로의 배열 인덱스 의미(근로자/재해 현황은 0=당해년도·1=전년도 슬롯 고정).
        const ii = selectIndexInfo(prefillFrom?.catalogKey, prefillFrom?.select);
        return ii ? row('행 인덱스', ii) : null;
      })()}
      {writeValues
        ? row('현재 값(미리보기 컨텍스트)', composerValuePreview(writeValues[w.bindingKey]))
        : null}
      {meta?.description ? (
        <p className="text-[11px] leading-snug text-gray-500">{meta.description}</p>
      ) : null}
      <p className="text-[11px] text-gray-400">속성 편집은 배치 후 가능합니다.</p>
      {/* 배치 컨트롤 — 트리 행과 달리 이 패널의 본문 폰트(text-xs)에 맞춘다(패널 기본 폰트 상속 방지). */}
      <div className="flex items-center gap-1 border-t border-gray-200 pt-3 text-xs">
        <Button
          variant="primary"
          size="xs"
          className="shrink-0 whitespace-nowrap px-1"
          onClick={() => onPlace(w, null)}
        >
          루트에 배치
        </Button>
        {groupOptions.length > 0 ? (
          <select
            // 접힘 폭 고정 — 풀 행 셀렉트와 동일 사유(긴 섹션명 option이 접힘 폭을 부풀림).
            className={`${TREE_ROW_CTRL_CLASS} w-[80px] border border-gray-300`}
            value=""
            title="섹션선택"
            onChange={(e) => {
              if (e.target.value) onPlace(w, e.target.value);
            }}
          >
            <option value="">섹션선택</option>
            {groupOptions.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>
    </div>
  );
}

/** 위젯 종류 라벨(요약 뱃지용). */
const COMPOSER_WIDGET_KIND_LABEL: Record<ComposerWidgetKind, string> = {
  input: '입력',
  group: '섹션',
  // heading은 자유 텍스트 요소 — UI 명칭 '텍스트'(2026-07-12 사용자 정정, 코드 kind는 heading 유지).
  heading: '텍스트',
  separator: '구분선',
  spacer: '여백',
  // '양식 표' = 칸 단위 구성 폼 표 — 입력 위젯의 '데이터 표'(행=데이터 목록)와 명명 구분(2026-07-18).
  formTable: '양식 표',
  modalTrigger: '모달 버튼',
  // 반복(행 스코프 조립) 컨테이너 — kind 추가(P1 shared)에 따른 라벨. 저작 UI는 P2
  // (결정 2026-07-20-composer-repeat-widget-row-scope).
  repeat: '반복',
  // 분기(투명 컨테이너) — kind 추가(P1 shared)에 따른 라벨. 저작 UI는 P3.
  branch: '분기',
};

/** 위젯 한 줄 표시 라벨 — 라벨 없으면 bindingKey/섹션명/종류로 폴백. */
function composerWidgetDisplayLabel(w: ComposerWidget): string {
  switch (w.kind) {
    case 'input':
      if (w.widget === 'registered') {
        const meta = REGISTERED_FORM_WIDGETS_META.find((m) => m.key === w.widgetKey);
        return meta?.label ?? w.widgetKey ?? '등록 위젯';
      }
      return w.label || w.bindingKey;
    case 'group':
      return w.title || '섹션';
    case 'heading':
      return w.text || '텍스트';
    case 'formTable':
      return w.title || '양식 표';
    default:
      return COMPOSER_WIDGET_KIND_LABEL[w.kind];
  }
}

/** 종류 뱃지(작은 회색 pill). */
function WidgetKindBadge({ kind }: { kind: ComposerWidgetKind }) {
  return (
    <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
      {COMPOSER_WIDGET_KIND_LABEL[kind]}
    </span>
  );
}

/**
 * 속성 패널 상단 선택 요약 — 지금 무엇을 편집 중인지 명시.
 * 단일: 종류 뱃지 + 라벨 한 줄. 다중: "N개 선택" + 라벨 나열(정보 표시만 — 탭/칩형 선택 UI 아님,
 * 노드 다중 선택 패널처럼 공통 편집이 본문. 개별 편집은 캔버스/트리에서 단일 선택으로).
 */
function WidgetSelectionSummary({ widgets }: { widgets: ComposerWidget[] }) {
  if (widgets.length === 1) {
    const w = widgets[0];
    const label = composerWidgetDisplayLabel(w);
    return (
      <div className="space-y-1 border-b border-gray-200 pb-2">
        <div className="flex items-center gap-1.5">
          <WidgetKindBadge kind={w.kind} />
          {/* 입력 위젯은 서브타입(text/textarea 등)까지 병기 — 종류만으론 구분 불가(2026-07-16 사용자 요청). */}
          {w.kind === 'input' ? (
            <span className="shrink-0 rounded bg-brand-50 px-1.5 py-0.5 font-mono text-[10px] text-brand-600 ring-1 ring-brand-200">
              {w.widget}
            </span>
          ) : null}
          <span className="truncate text-xs font-medium text-gray-700" title={label}>
            {label}
          </span>
        </div>
        <CopyableIdLine id={w.id} />
      </div>
    );
  }
  const labels = widgets.map(composerWidgetDisplayLabel);
  return (
    <div className="space-y-1 border-b border-gray-200 pb-2">
      <p className="text-xs font-semibold text-gray-700">{widgets.length}개 선택</p>
      <p className="text-[11px] leading-relaxed text-gray-400" title={labels.join(', ')}>
        {labels.join(' · ')}
      </p>
    </div>
  );
}

/** 속성 패널 id 표시줄 — 클릭=클립보드 복사(위젯/노드 지칭 대화·디버깅용, 2026-07-16 사용자 요청). */
function CopyableIdLine({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      title="클릭하여 id 복사"
      className="block max-w-full truncate rounded px-1 py-0.5 -mx-1 text-left font-mono text-[10px] text-gray-400 hover:bg-gray-100 hover:text-gray-600"
    >
      {copied ? '✓ 복사됨' : id}
    </button>
  );
}

/**
 * 위젯 네비게이션(식별·네비) — 양식 탭 노드 속성 패널 규약 동형(ground-truth 대조 2026-07-15):
 * ① 상위 브레드크럼: 조상(섹션/모달 버튼) 클릭=선택, `›` 구분, 끝=현재 위젯(조상 없으면 미표시).
 * ② 컨테이너 자식 목록: 섹션(group)·모달 버튼(modalTrigger)의 직속 자식 위젯을 나열, 클릭=선택
 *    (양식 탭 "그룹 내 노드 (N)" 대응물 — formTable은 셀 모델이라 셀 포커스 UI가 별도 담당, 제외).
 * 선택은 트리 패널과 동일 경로(onSelect=selectWidget, 승격 없는 직접 선택).
 */
function WidgetNavigation({
  w,
  tree,
  onSelect,
  scopeRootId,
}: {
  w: ComposerWidget;
  tree: ComposerWidget[];
  onSelect: (id: string) => void;
  /**
   * 네비게이션 스코프 루트(모달 편집 모드의 modalTrigger id). 지정 시 브레드크럼을 이 위젯부터
   * 표시(그 위 본문 조상 숨김 — 모달 편집 중 캔버스는 차단인데 브레드크럼이 본문 선택 문을 열어주는
   * 모순 방지, 2026-07-15 사용자 제보). 선택이 스코프 밖이면 무시(기존 전체 체인).
   */
  scopeRootId?: string;
}) {
  // 조상 체인은 deepest→top — 표시는 양식 탭처럼 바깥→안(현재 제외 역순).
  const chainIds = writeWidgetAncestorChain(tree, w.id).slice(1).reverse();
  // 모달 편집 스코프 제한 — 체인에 스코프 루트(모달 버튼)가 있으면 그 지점부터(본문 조상 절단).
  // 편집 중인 모달 버튼 자신을 선택한 경우 조상은 전부 본문이라 브레드크럼 전체 숨김.
  const scopeIdx = scopeRootId ? chainIds.indexOf(scopeRootId) : -1;
  const scopedIds = scopeRootId === w.id ? [] : scopeIdx >= 0 ? chainIds.slice(scopeIdx) : chainIds;
  const ancestors = scopedIds
    .map((id) => selectedWidgetById(tree, id))
    .filter((x): x is ComposerWidget => x != null);
  const children =
    w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat' ? w.children : null;
  if (ancestors.length === 0 && (children === null || children.length === 0)) return null;
  return (
    <div className="space-y-3">
      {/* 상위 이동 브레드크럼 — 조상 클릭 시 그 위젯 선택, 끝은 현재 위젯. */}
      {ancestors.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[11px]">
          {ancestors.map((g) => (
            <span key={g.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onSelect(g.id)}
                title={`${g.id} 선택`}
                className="px-1.5 py-0.5 rounded border border-gray-200 text-brand-600 hover:bg-brand-50"
              >
                {composerWidgetDisplayLabel(g)}
              </button>
              <span className="text-gray-300">›</span>
            </span>
          ))}
          <span className="px-1.5 py-0.5 text-gray-500 font-medium">{composerWidgetDisplayLabel(w)}</span>
        </div>
      )}
      {/* 컨테이너 내 위젯 목록(네비게이션) — 클릭 시 자식 선택(드릴다운). */}
      {children !== null && children.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-1">
            {w.kind === 'modalTrigger'
              ? '모달 내 위젯'
              : w.kind === 'repeat'
                ? '행 템플릿 위젯'
                : '섹션 내 위젯'}{' '}
            ({children.length})
          </p>
          <ul className="space-y-0.5">
            {children.map((child) => (
              <li key={child.id}>
                <button
                  type="button"
                  onClick={() => onSelect(child.id)}
                  title={`${child.id} 선택`}
                  className="w-full text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-brand-50 flex items-center justify-between gap-2"
                >
                  <span className="truncate">{composerWidgetDisplayLabel(child)}</span>
                  <WidgetKindBadge kind={child.kind} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 위젯 공통 속성 디스크립터 레지스트리
//
// 배경: 단일 속성 패널과 복수 선택 패널(BulkWidgetProperties)이 별개 손 큐레이션이라 새 위젯
// 속성이 단일에만 붙고 복수에서 누락되는 사고 반복(역전파 2026-07-13, labelSuffixFrom·
// visibleWhen 2026-07-17 제보). 공통 속성 하나 = 디스크립터 하나로, **같은 Control**이
// 단일(selection 1개)과 복수(fold tri-state + setAll 일괄 적용)를 모두 렌더한다.
//
// 불변식: **새 공통 위젯 속성은 반드시 WIDGET_PROP_DESCRIPTORS에 디스크립터로 추가**(복수 패널
// 자동 노출·수정). BulkWidgetProperties나 단일 패널에 속성 JSX를 직접 추가하지 말 것 —
// label·bindingKey·placeholder류 정체성/개별 속성만 단일 패널 고유 JSX(bulk 비대상)로 남긴다.
// ─────────────────────────────────────────────────────────────────────────────

/** 속성 컨트롤 공용 스타일 — 단일 패널(구 SingleWidgetProperties labelCls/inputCls)과 동일. */
const PROP_LABEL_CLS = 'block text-xs font-medium text-gray-600 mb-1';
const PROP_INPUT_CLS =
  'w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400';

type WidgetPropKey =
  | 'hiddenOnView'
  | 'viewOrder'
  | 'spanOnView'
  | 'required'
  | 'mode'
  | 'writeBack'
  // 「시작값」 통합 구획 — 구 defaultValue·prefillFrom·autoFill 세 자리를 하나로 합친 축
  // (결정 2026-09-05 대조표 1). 셋 다 개별 값 성격이라 원래 bulk 제외였으므로 합쳐도 손실이 없다.
  | 'startValue'
  | 'labelSuffixFrom'
  // 라벨 표시 계열 — 구 LabelPresentationControls(단일 전용 bespoke)를 축별로 분해해 편입
  // (2026-08-24). 축을 나눈 기준은 **fold(혼합) 판정이 뜻을 갖는 단위**다: 위치·정렬·폰트는
  // "여러 칸에 같은 표시를 준다"가 자연스러운 레이아웃/시각 축이라 bulk 대상이고,
  // 보조 설명(labelHint)은 칸마다 다른 문구라 defaultValue와 같은 이유로 bulk 제외다.
  | 'labelPlacement'
  | 'labelAlign'
  | 'labelStyle'
  | 'labelHint'
  | 'visibleWhen'
  | 'enabledWhen'
  | 'inputFormat'
  // 선택지 표시 변형(radio 전용) — 네이티브 라디오 / 가로 버튼. 같은 표시를 여러 칸에 일괄로
  // 주는 게 자연스러운 시각 축이라 editEntry·collapseLines와 같은 이유로 bulk 대상.
  | 'displayVariant'
  | 'editEntry'
  | 'collapseLines'
  | 'displayFormat'
  | 'suggestionsFrom'
  // 표 위젯(widget==='table') 전용 축 — 키·노출 순서·게이트는 tableWidgetProps(순수 층) 소유.
  | TableWidgetPropKey;

/** 선택(단일 1개/복수 N개)을 속성 컨트롤에 공급하는 어댑터. */
interface WidgetPropSelection {
  /** 선택 위젯(단일=1, 복수=2+). */
  widgets: ComposerWidget[];
  /** 단일 선택이면 그 위젯 — 단일 전용 표기(오버라이드 badge·안내 문구 분기)에만 사용. */
  single?: ComposerWidget;
  /** 선택 전체 일괄 갱신 — 1회 호출 = undo 1스텝(단일=onWidgetUpdate, 복수=onWidgetsUpdate). */
  setAll: (updater: (w: ComposerWidget) => ComposerWidget) => void;
}

/** 속성 컨트롤이 참조하는 에디터 컨텍스트 — 없는 필드에 의존하는 속성은 applicable에서 스스로 제외. */
interface WidgetPropCtx {
  activeView: string;
  /** 값 스코프 후보 산출용 전체 트리(visibleWhen·labelSuffixFrom) — 미제공 시 그 속성 미노출. */
  tree?: ComposerWidget[];
  writeFormFields?: InputWidget[];
  bindingPrefillMap?: Map<string, { catalogKey: string; select?: string }>;
  catalog?: CatalogEntry[];
  dataDefs?: DataDef[];
  /** 데이터소스 — 반복(행 스코프) 소스 def 해소용(검색 제안 필터 필드 후보). 미제공 시 행 필드 select 미노출. */
  dataSources?: DataSourceDef[];
  /**
   * 이 위젯의 값 스코프 필드 후보(반복 행 필드) — 표시 형식의 경로 칸(병기 값·구간 키) 저작용.
   * 스코프 판정 원천(트리 위치 / 셀 위젯을 담은 표의 반복 소스)이 호출부마다 달라, **아는 자리에서
   * 산출해 실어 보낸다**. 미제공/빈 목록이면 그 칸들은 자유 텍스트 입력으로 degrade한다.
   */
  rowFieldOptions?: HybridKeyOption[];
  /**
   * 캔버스 미리보기 값(서버 prefill 해소분) — 표시 형식의 "적용 구간 미리보기"가 실제 값을
   * 보여주는 데 쓴다(구간 판정이 무엇을 쓰는지 저작자가 확인). 미제공이면 안내 문구만.
   */
  previewValues?: Record<string, unknown>;
  /** 양식 전역 변수 — 변수 파생 위젯의 역전파 UI 대체 안내 판정용(영속 정본=rememberScope). */
  variables?: TemplateVariable[];
  onHoverHighlightWidget?: (id: string | null) => void;
  onFtCellHover?: (c: { tableId: string; rowId: string; columnId: string } | null) => void;
  /**
   * 트리 전체 갱신(wtApply) — **형제 집합 단위 조작**(뷰 순서 초기화)용. selection.setAll은 선택
   * 위젯에만 닿아 순서 축(형제 전원에 order가 박히는 축)을 되돌릴 수 없다. 미제공이면 그 UI 미노출.
   */
  onTreeUpdate?: (fn: (tree: ComposerWidget[]) => ComposerWidget[]) => void;
  /**
   * 템플릿 원자 갱신(apply — 1회 호출=undo 1스텝) — 표 위젯 칼럼의 **참조 모드**에서 선언 축을
   * 정본(template.dataDefs)에 쓰거나, detach(정본 복제 + 이 위젯 bindingKey 전환)처럼 dataDefs와
   * composerWidgetTree를 **한 스텝에 함께** 바꿔야 하는 조작용. 미제공이면 그 편집·동선 미노출.
   */
  onTemplateUpdate?: (fn: (t: ReportTemplate) => ReportTemplate) => void;
  /**
   * bindingKey 참조 수(countDataSourceUsage 위임 — body 바인딩·반복 over·집계·계산 입력·writes 등
   * 전 축) — detach 차단 판정용(이 위젯 밖 참조가 있으면 저장 키 전환이 그 참조를 끊는다).
   */
  countKeyRefs?: (key: string) => number;
  /** 확인 모달 요청(EditorPage confirmState 퍼널) — 정본 채택(사본 제거)·detach 확인. */
  requestConfirm?: FtConfirmRequest;
}

interface WidgetPropControlProps {
  selection: WidgetPropSelection;
  ctx: WidgetPropCtx;
}

interface WidgetPropertyDescriptor {
  key: WidgetPropKey;
  /** 복수 선택 일괄 편집 대상 여부 — label·bindingKey류 정체성 속성은 false. */
  bulk: boolean;
  /** 이 위젯이 이 속성을 가지는가 — 복수 패널은 선택 전원 applicable일 때만 노출. */
  applicable: (w: ComposerWidget, ctx: WidgetPropCtx) => boolean;
  /** 정규화 값 getter — fold(tri-state 혼합 판정)의 기준. Control도 같은 getter를 공유한다. */
  read: (w: ComposerWidget, ctx: WidgetPropCtx) => unknown;
  /** 단일/복수 공용 컨트롤 — 단일은 selection.single 표기 분기만 다르고 같은 렌더 경로. */
  Control: (props: WidgetPropControlProps) => ReactElement | null;
}

// 디스크립터별 정규화 getter — fold와 Control이 공유(값 접근 정본).
const readWidgetHidden = (w: ComposerWidget, ctx: WidgetPropCtx): boolean =>
  resolveWidgetHidden(w, ctx.activeView);
/** 뷰 순서 오버라이드 — **형제 집합 단위** 판정(이 위젯에 order가 없어도 형제가 조정됐으면 true). */
const readWidgetViewOrder = (w: ComposerWidget, ctx: WidgetPropCtx): boolean =>
  ctx.activeView !== COMPOSER_BASE_VIEW_KEY &&
  !!ctx.tree &&
  hasSiblingViewOrder(ctx.tree, w.id, ctx.activeView);
/**
 * 폭 비율(span) — 활성 뷰에 **기록된 값**을 문자열 토큰으로(`''`=미지정/상속 | `'auto'`=해제 |
 * 그 외=비율의 String). 해소값이 아니다: 해소값을 읽으면 비-base 뷰에서 '상속'을 고를 수 없어
 * 오버라이드를 되돌릴 방법이 사라진다(단 수 select가 오버라이드 값을 value로 쓰는 것과 같은 이유).
 * 문자열로 정규화하는 이유는 fold(다중 선택 혼합 판정)가 Object.is 비교라, 같은 비율이면
 * 한 토큰으로 모여야 하기 때문이다.
 *
 * base에서는 'auto'를 ''로 정규화한다 — base의 'auto'는 미지정과 같은 뜻이고(shared JSDoc),
 * 해제 어휘는 뷰 오버라이드 전용이라 base 선택지에 둘을 나눠 두면 같은 뜻의 옵션이 둘이 된다.
 */
const readWidgetSpanChoice = (w: ComposerWidget, ctx: WidgetPropCtx): string => {
  const raw =
    ctx.activeView === COMPOSER_BASE_VIEW_KEY
      ? typeof w.span === 'number'
        ? w.span
        : undefined
      : w.responsive?.[ctx.activeView]?.span;
  return raw == null ? '' : String(raw);
};
const readWidgetRequired = (w: ComposerWidget): boolean =>
  w.kind === 'input' ? (w.required ?? false) : false;
const readWidgetMode = (w: ComposerWidget): 'editable' | 'readonly' | 'hidden' =>
  w.kind === 'input' ? (w.mode ?? 'editable') : 'editable';
const readWidgetWriteBackScope = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.writeBack?.scope ?? '') : '';
const readWidgetVisibleWhen = (w: ComposerWidget): string | undefined => w.visibleWhen;
const readWidgetEnabledWhen = (w: ComposerWidget): string | undefined => w.enabledWhen;
const readWidgetLabelSuffixFrom = (w: ComposerWidget): string | undefined =>
  w.kind === 'input' ? w.labelSuffixFrom : undefined;
/** 라벨 위치 — 미지정='top'(기본). 'none'=라벨 미표시(rc가 부속까지 함께 미렌더). */
const readWidgetLabelPlacement = (w: ComposerWidget): 'top' | 'side' | 'none' =>
  w.kind === 'input' ? (w.labelPlacement ?? 'top') : 'top';
/** 라벨 정렬 — 미지정='start'(기본). rc는 top 배치일 때만 소비. */
const readWidgetLabelAlign = (w: ComposerWidget): 'start' | 'center' | 'end' =>
  w.kind === 'input' ? (w.labelAlign ?? 'start') : 'start';
/** 라벨 보조 설명 — 미지정=''(fold 혼합 판정 기준). */
const readWidgetLabelHint = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.labelHint ?? '') : '';
/**
 * 텍스트 스타일 fold 키 — 객체는 참조 비교(Object.is)가 늘 false라 fold가 항상 '혼합'이 된다.
 * 필드 조합을 문자열로 정규화해 같은 스타일이면 한 토큰으로 모이게 한다(spanChoice와 같은 이유).
 */
const textStyleFoldKey = (s: ComposerTextStyle | undefined): string =>
  `${s?.fontSize ?? ''}/${s?.fontWeight ?? ''}/${s?.color ?? ''}/${s?.align ?? ''}`;
/** 라벨 폰트 스타일 — 필드 조합 문자열(위 doc). */
const readWidgetLabelStyleKey = (w: ComposerWidget): string =>
  w.kind === 'input' ? textStyleFoldKey(w.labelStyle) : '';
/** 보조 설명 폰트 스타일 — 라벨 폰트와 같은 정규화. */
const readWidgetLabelHintStyleKey = (w: ComposerWidget): string =>
  w.kind === 'input' ? textStyleFoldKey(w.labelHintStyle) : '';
/**
 * 선택 전체의 텍스트 스타일을 **필드별로** fold해 컨트롤에 보여줄 대표 스타일을 만든다.
 * 혼합 필드는 undefined(=컨트롤이 '기본' placeholder로 표시)로 접는다 — 값을 하나 골라 보여주면
 * 나머지 위젯의 값을 지운 것처럼 오해되기 때문이다. 혼합 여부 자체는 MixedTag로 따로 알린다.
 */
function foldTextStyle(
  widgets: ComposerWidget[],
  pick: (w: ComposerWidget) => ComposerTextStyle | undefined,
): ComposerTextStyle | undefined {
  const one = <T,>(get: (s: ComposerTextStyle | undefined) => T): T | undefined => {
    const f = fold(widgets, (w) => get(pick(w)));
    return f.mixed ? undefined : f.value;
  };
  return {
    fontSize: one((s) => s?.fontSize),
    fontWeight: one((s) => s?.fontWeight),
    color: one((s) => s?.color),
    align: one((s) => s?.align),
  };
}
const readWidgetInputFormat = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.inputFormat ?? '') : '';
const readWidgetEditEntry = (w: ComposerWidget): 'always' | 'click' =>
  w.kind === 'input' ? (w.editEntry ?? 'always') : 'always';
/** 선택지 표시 변형(radio) — 미지정=네이티브 라디오('default'로 정규화해 fold 비교 가능하게). */
const readWidgetDisplayVariant = (w: ComposerWidget): 'default' | 'buttons' =>
  w.kind === 'input' && w.displayVariant === 'buttons' ? 'buttons' : 'default';
const readWidgetSuggestionsFrom = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.suggestionsFrom?.catalogKey ?? '') : '';
/**
 * 「시작값」 구획 — 세 저장 축(고정값 `defaultValue` · 시스템 조회 `prefillFrom` · 목록 파생
 * `autoFill`)을 비교 가능한 한 문자열로 정규화(미지정=''). bulk:false라 fold 혼합 판정을 타지
 * 않지만, 값 접근 정본을 Control과 공유해야 표시와 판정이 갈리지 않는다.
 *
 * 직렬화는 JSON으로 — 손으로 고른 구분자는 필드 값에 그 문자가 들어오면 서로 다른 선언을 같은
 * 값으로 접는다. 세 축을 **함께** 실어야 복수 선언 저장본이 한 축만 같을 때 같은 값으로 접히지 않는다.
 */
const readWidgetStartValue = (w: ComposerWidget): string => {
  if (w.kind !== 'input') return '';
  const af = w.autoFill;
  const pf = w.prefillFrom;
  if (w.defaultValue === undefined && !af && !pf) return '';
  return JSON.stringify([
    w.defaultValue ?? null,
    pf ? [pf.catalogKey, pf.select ?? ''] : null,
    af ? [af.source, af.mode, af.from] : null,
  ]);
};
/** 접기 줄 수 — 0 = 접지 않음(미지정과 같은 뜻으로 정규화, fold 혼합 판정 기준). */
const readWidgetCollapseLines = (w: ComposerWidget): number =>
  w.kind === 'input' ? (w.collapseLines ?? 0) : 0;
/** 읽기 표시 형식 — kind만 비교 가능한 문자열로 정규화(미지정='none'). */
const readWidgetFormatKind = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.format?.kind ?? 'none') : 'none';
/**
 * 읽기 표시 형식을 **소비하지 않는** 위젯 — rc renderWriteNode의 공통 readonly 텍스트 분기를 타지
 * 않는 것들이다(자기 case로 렌더: table/sortableSelect/image/annotated-image, 호스트가 렌더:
 * registered/dataSourceRows — rc READONLY_SELF_RENDER_WIDGETS + 트리 렌더 분기와 짝).
 * rc가 소비 위젯을 늘리면 여기서도 빼야 한다.
 */
const FORMAT_UNSUPPORTED_WIDGETS = new Set<string>([
  'table',
  'sortableSelect',
  'image',
  'annotated-image',
  'registered',
  'dataSourceRows',
]);

// ── 표 위젯(widget==='table') 전용 축 getter — 전부 "미지정=기본값"으로 정규화한다(fold 혼합 판정
//    기준이자 Control의 value 원천). 저장은 기본값이면 키를 지우는 현행 규약을 그대로 유지한다.
/** 칼럼 헤더 숨김 — 미지정=false. */
const readWidgetTableHideHeader = (w: ComposerWidget): boolean =>
  w.kind === 'input' ? (w.hideHeader ?? false) : false;
/** 테두리 프리셋 — 미지정='rows'(행 구분선 기본). */
const readWidgetTableBorder = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.tableBorder ?? 'rows') : 'rows';
/** 작성 표시 단수 — 미지정은 ''(빈 입력=1단). 저장은 2 이상만(1은 기본이라 키 제거). */
const readWidgetTableDisplayColumns = (w: ComposerWidget): string =>
  w.kind === 'input' && typeof w.displayColumns === 'number' ? String(w.displayColumns) : '';
/** 다단 채움 방향 — 미지정='row'(너비 우선). */
const readWidgetTableFillDirection = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.fillDirection ?? 'row') : 'row';
/** 구분 그룹 표시 칼럼 key — 미지정=''(사용 안 함). */
const readWidgetTableGroupByField = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.groupByField ?? '') : '';
/** 행 클릭 체크 토글 대상 칼럼 key — 미지정=''(사용 안 함). */
const readWidgetTableRowClickToggleField = (w: ComposerWidget): string =>
  w.kind === 'input' ? (w.rowClickToggleField ?? '') : '';
/**
 * 칼럼 편집기의 fold 기준 — 편집 대상이 `fields[]` **배열 자체**라 값 비교가 성립하지 않는다
 * (배열 참조는 늘 다르다). bulk 미대상이라 혼합 판정이 쓰이지 않으므로 칼럼 수만 돌려준다.
 */
const readWidgetTableColumnCount = (w: ComposerWidget): number =>
  w.kind === 'input' ? (w.fields?.length ?? 0) : 0;

/** 단일 선택 어댑터 — 단일 패널이 디스크립터 Control을 복수 패널과 공유하기 위한 selection. */
function singleWidgetSelection(
  w: ComposerWidget,
  onWidgetUpdate: (id: string, updater: (x: ComposerWidget) => ComposerWidget) => void,
): WidgetPropSelection {
  return { widgets: [w], single: w, setAll: (updater) => onWidgetUpdate(w.id, updater) };
}

/** 단일 패널에서 디스크립터 속성 1개를 현 위치에 렌더 — 복수 패널과 같은 Control 공유(수렴 정본). */
function WidgetPropSingle({
  prop,
  w,
  onWidgetUpdate,
  ctx,
}: {
  prop: WidgetPropKey;
  w: ComposerWidget;
  onWidgetUpdate: (id: string, updater: (x: ComposerWidget) => ComposerWidget) => void;
  ctx: WidgetPropCtx;
}) {
  const desc = WIDGET_PROP_DESCRIPTORS[prop];
  if (!desc.applicable(w, ctx)) return null;
  const Control = desc.Control;
  return <Control selection={singleWidgetSelection(w, onWidgetUpdate)} ctx={ctx} />;
}

/**
 * 역전파 가용성 판정용 prefill 해소 체인 — 명시 prefillFrom → combobox 합성(서버 수집과 순서
 * 일치: ref.prefillFrom 합성이 body 바인딩 fallback보다 우선) → 파생 작성폼 필드 → 등록형 기본 →
 * body 바인딩 fallback. 단일/복수 공용(구 단일 WriteBackSelector 체인 + 복수 bulkPrefill 통합 —
 * 셀 다중 뷰 widgetPrefill과 동형).
 */
function effectiveWriteBackPrefill(
  w: InputWidget,
  ctx: Pick<WidgetPropCtx, 'catalog' | 'writeFormFields' | 'bindingPrefillMap'>,
): { catalogKey: string; select?: string } | undefined {
  return (
    w.prefillFrom ??
    comboboxSyntheticPrefill(w, ctx.catalog) ??
    ctx.writeFormFields?.find((f) => f.bindingKey === w.bindingKey)?.prefillFrom ??
    (w.widget === 'registered'
      ? REGISTERED_FORM_WIDGETS_META.find((m) => m.key === w.widgetKey)?.defaultPrefillFrom
      : undefined) ??
    ctx.bindingPrefillMap?.get(w.bindingKey)
  );
}

/** keyed upsert 페어링 판정 컨텍스트 — 디스크립터 ctx 외 표 셀 패널(FormTablePropertyPanel)도 공급. */
type WriteBackJudgeCtx = Pick<
  WidgetPropCtx,
  'tree' | 'catalog' | 'writeFormFields' | 'bindingPrefillMap'
>;

/**
 * keyed upsert 페어링 커버 판정 — 위젯 W가 어떤 키 콤보 C의 `writes` target이면, W의 값은 W 자신의
 * 역전파 지정과 무관하게 C의 역전파(keyed upsert)로 도메인 정본 행에 함께 저장된다. 이때 W에
 * 역전파를 또 지정하면 도메인 정본이 아니라 제네릭 저장소로 가는 별개 채널이 생겨 "키(장비) 무관
 * 마지막 값 자동 채움" 같은 어긋난 동작이 되므로 저작 단계에서 차단한다(2026-07-17 사용자 요구).
 *
 * 키 콤보 C 판정:
 *  - input kind + widget 'combobox' + writeBack 지정
 *  - effective prefill(명시 prefillFrom 우선, comboboxSyntheticPrefill 합성 포함)의 카탈로그
 *    entry(kind:'data')가 writeBack.upsertKey 선언 + prefill select === upsertKey(키 위젯)
 *  - C.writes 중 {target: W.bindingKey} 존재 + 그 source가 writableFields에 매칭
 *    (source===upsertKey는 제외 — 키 필드 자체 전파는 페어링 저장 대상이 아님)
 * walk 범위: 트리 전체(중첩 group/modalTrigger children + formTable 고정 셀 위젯) —
 * findWidgetByBindingKey와 동일 하강 규칙.
 * 서버 대응: apps/server/src/report-composer/report-composer.writeback.ts buildKeyedUpsertGroups
 * 페어링 규약 미러(동기화 필요).
 */
function keyedPairingCoverage(
  w: InputWidget,
  ctx: WriteBackJudgeCtx,
): { keyLabel: string } | null {
  if (!ctx.tree) return null;
  const isCoveringKeyCombo = (c: InputWidget): boolean => {
    if (c.id === w.id || c.widget !== 'combobox' || !c.writeBack) return false;
    const writes = (c.writes ?? []).filter((x) => x.target === w.bindingKey);
    if (writes.length === 0) return false;
    const prefill = effectiveWriteBackPrefill(c, ctx);
    if (!prefill) return false;
    const entry = (ctx.catalog ?? []).find((e) => e.key === prefill.catalogKey);
    if (!entry || entry.kind !== 'data') return false;
    const upsertKey = entry.writeBack?.upsertKey;
    if (!upsertKey || prefill.select !== upsertKey) return false;
    const writableFields = entry.writeBack?.writableFields ?? [];
    return writes.some(
      (x) => x.source !== upsertKey && writeBackWritableFieldMatch(x.source, writableFields),
    );
  };
  const walk = (nodes: ComposerWidget[]): InputWidget | null => {
    for (const n of nodes) {
      if (n.kind === 'input') {
        if (isCoveringKeyCombo(n)) return n;
      } else if (n.kind === 'group' || n.kind === 'modalTrigger') {
        const r = walk(n.children);
        if (r) return r;
      } else if (n.kind === 'formTable') {
        for (const row of n.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells)
            if (cell.widget && isCoveringKeyCombo(cell.widget)) return cell.widget;
        }
      }
    }
    return null;
  };
  const key = walk(ctx.tree);
  return key ? { keyLabel: composerWidgetDisplayLabel(key) } : null;
}

/**
 * 위젯이 반복 행 안이면 행 스코프 역전파 판정(judgeRowScopeWriteBack — 서버 수집기와 같은 shared 정본), 밖이면
 * undefined. 트리·데이터소스가 없으면 판정 불가라 undefined(최상위 규약으로 표시 — 트리 없는
 * 호출부는 반복 위젯을 다루지 않는다).
 */
function rowScopeWriteBackJudgement(
  w: InputWidget,
  ctx: Pick<WidgetPropCtx, 'tree' | 'catalog' | 'dataSources'>,
): RowScopeWriteBackJudgement | undefined {
  if (!ctx.tree) return undefined;
  const repeat = findEnclosingRepeatWidget(ctx.tree, w.id);
  if (!repeat) return undefined;
  return judgeRowScopeWriteBack({
    widget: w,
    over: repeat.over,
    dataSources: ctx.dataSources,
    catalog: ctx.catalog,
  });
}

/**
 * 반복 행 안 **photo 위젯**의 발견사항 조치사진 반영(findingWriteBack) 컨트롤 — 선언은 트리 위젯이
 * 아니라 **소스 필드**(`DataSourceFieldMap.findingWriteBack`, 위젯 bindingKey와 같은 이름의 행 필드)에
 * 산다. 서버는 소스 선언만 읽으므로 트리 패널에만 두면 「어디서 켜나」가 은닉되고, 소스 필드 표에만
 * 두면 사진 위젯을 고른 저작자가 역전파 패널(WriteBackSelector) 옆에서 이 축을 못 본다 — 둘 다 같은
 * 선언을 편집한다(`FindingWriteBackFieldSettings` 한 컴포넌트, `onTemplateUpdate`로 dataSources 갱신).
 * 반복 밖·photo 아님·소스/필드 미해소·템플릿 갱신 미배선이면 미노출.
 */
function RepeatRowFindingWriteBackControl({
  widget: w,
  ctx,
}: {
  widget: InputWidget;
  ctx: Pick<WidgetPropCtx, 'tree' | 'dataSources' | 'onTemplateUpdate'>;
}) {
  if (w.widget !== 'photo' || !ctx.tree || !ctx.onTemplateUpdate) return null;
  const repeat = findEnclosingRepeatWidget(ctx.tree, w.id);
  if (!repeat) return null;
  const defs = ctx.dataSources ?? [];
  const defIndex = defs.findIndex((d) => d.key === repeat.over);
  if (defIndex < 0) return null;
  const def = defs[defIndex];
  const fieldIndex = def.fields.findIndex((f) => f.field === w.bindingKey);
  if (fieldIndex < 0) return null;
  const onTemplateUpdate = ctx.onTemplateUpdate;
  return (
    <div className="mt-2">
      <label className={PROP_LABEL_CLS}>점검 반영(발견사항 조치사진)</label>
      <FindingWriteBackFieldSettings
        def={def}
        defIndex={defIndex}
        field={def.fields[fieldIndex]}
        fieldIndex={fieldIndex}
        dataSources={defs}
        onChange={(next) => onTemplateUpdate((t) => ({ ...t, dataSources: next }))}
      />
    </div>
  );
}

/**
 * 전폭(span)이 **소비될 수 있는 배치**인지 판정 — 그 배치를 만드는 부모 섹션(group)을 돌려준다.
 * 아니면 null(=컨트롤 미노출).
 *
 * rc 렌더 기준: span은 `gridded`일 때만 읽힌다(widgetSpanClass/fillChildGridColumn/
 * computeGroupFillLastRowLayout). 그리고 `gridded=true`가 되는 유일한 자리는 **group 자식 +
 * 그 group이 다단**이다(rc renderWidget 호출부: root·branch 슬롯·탭 본문은 전부 false).
 * 그래서 kind로 가르지 않는다 — 다단 섹션 안이면 입력·제목·구분선·여백·표·섹션 전부 전폭을
 * 쓸 수 있다("design 위젯은 항상 전폭"은 2026-07-15 단 우선 모델로 폐기된 옛 규약이다).
 *
 * 단 수는 **활성 뷰가 아니라 전 뷰**로 본다 — 활성 뷰 해소값(resolveGroupColumns)으로 게이트하면
 * 바형(자동 1단)으로 전환하는 순간 컨트롤이 사라져 그 뷰에 남은 오버라이드를 되돌릴 수 없다.
 * 1단 뷰에서의 무의미함은 노출을 끊는 대신 상태 문구가 말한다.
 */
function spanHostGroupOf(tree: ComposerWidget[] | undefined, id: string): GroupWidget | null {
  if (!tree) return null;
  const parentId = widgetParentId(tree, id);
  if (!parentId) return null; // root 직속(null) 또는 미발견(undefined)
  const parent = selectedWidgetById(tree, parentId);
  // 부모가 group이 아니면(모달·반복 children, branch 슬롯, 표 셀) rc가 gridded를 주지 않는다.
  if (!parent || parent.kind !== 'group') return null;
  // 탭 섹션은 본문이 1단 수직 flow로 고정(rc: isTabs → cols=1) — 어떤 뷰에서도 소비되지 않는다.
  if (parent.displayVariant === 'tabs') return null;
  const multiColumnSomewhere =
    Math.max(1, Math.floor(parent.columns ?? 1)) > 1 ||
    Object.values(parent.responsive ?? {}).some((o) => (o.columns ?? 1) > 1);
  return multiColumnSomewhere ? parent : null;
}

/** 비율을 % 문자열로(소수 첫째 자리까지) — 1/3처럼 무한소수인 프리셋이 있어 반올림한다. */
function formatSpanPercent(ratio: number): string {
  return String(Math.round(ratio * 1000) / 10);
}

/**
 * 해소된 비율을 사람 말로. 미지정(undefined)은 **자동 산출**이라고 말해야 한다 — 그 폭은 고정값이
 * 아니라 같은 줄의 지정분이 남긴 폭을 이웃과 나눈 결과다(이웃이 바뀌면 같이 바뀐다).
 */
function spanRatioLabel(ratio: number | undefined, cols: number): string {
  if (ratio === undefined) return `자동 (지정이 없으면 ${cols}단 중 1칸)`;
  if (ratio >= 1) return '줄 전체';
  return `줄의 ${formatSpanPercent(ratio)}%`;
}

/**
 * 폭 비율(span) 상태 안내 — GroupColumnsViewHint와 같은 결(지금 값이 어디서 왔는지 + 되돌리는 법).
 * 단일 선택 전용(복수는 혼합 문구가 대신한다).
 *
 * 세 축을 말한다:
 *  1) 이 뷰의 값이 base 상속인지 이 뷰 오버라이드인지
 *  2) **1단 뷰에서는 설정이 무의미**하다는 것 — cols=1이면 rc가 gridded=false라 span을 아예 읽지
 *     않고 모든 위젯이 이미 폭을 가득 쓴다. 이 말이 없으면 저작자가 바형에서 비율을 잡아 놓고
 *     "왜 아무 변화가 없지"에 빠진다.
 *  3) 되돌리는 방법(base=자동 / 비-base=기본값 상속)
 *
 * ⚠️ 해소 규칙을 여기서 다시 구현하지 않는다 — 판정은 rc의 resolveWidgetSpanRatio·
 * resolveGroupColumns가 정본이고, 같은 규칙을 두 곳에 두면 규칙이 바뀔 때 안내만 거짓말이 된다.
 */
function WidgetSpanViewHint({
  widget,
  host,
  activeView,
}: {
  widget: ComposerWidget;
  host: GroupWidget;
  activeView: string;
}): React.ReactElement | null {
  const isBase = activeView === COMPOSER_BASE_VIEW_KEY;
  const cols = resolveGroupColumns(host, activeView);
  const ratio = resolveWidgetSpanRatio(widget, activeView);
  const override = isBase ? undefined : widget.responsive?.[activeView]?.span;
  const viewLabel = composerViewLabel(activeView);

  // 1단 뷰 — 값이 무엇이든 적용되지 않는다. 이 사실이 상속/오버라이드보다 우선해서 말해져야 한다.
  if (cols === 1) {
    return (
      <p className="mt-0.5 text-[10px] leading-snug text-amber-600">
        {isBase ? '이 섹션' : `${viewLabel}은 이 섹션이`} 1단이라 이 설정은 적용되지 않습니다 —
        1단에서는 모든 위젯이 이미 줄 전체를 씁니다. 2단 이상에서만 효과가 있습니다.
        {override != null ? ' (이 뷰에 값이 기록돼 있습니다.)' : null}
      </p>
    );
  }
  if (override != null) {
    return (
      <p className="mt-0.5 text-[10px] leading-snug text-brand-600">
        이 뷰에서 {spanRatioLabel(ratio, cols)}로 고정 중입니다. &lsquo;기본값 상속&rsquo;을 고르면
        되돌아갑니다.
      </p>
    );
  }
  if (isBase) {
    return (
      <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
        {ratio === undefined
          ? `${cols}단 섹션에서 같은 줄의 지정분이 남긴 폭을 이웃과 나눠 씁니다(지정이 없으면 1칸). 비율을 고르면 그만큼 고정됩니다.`
          : `${cols}단 섹션 안에서 이 위젯이 ${spanRatioLabel(ratio, cols)}를 씁니다. '자동'을 고르면 이웃과 나눠 쓰는 폭으로 돌아갑니다.`}
      </p>
    );
  }
  return (
    <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
      기본값({spanRatioLabel(ratio, cols)})을 그대로 씁니다. 값을 고르면 이 뷰에만 적용됩니다.
    </p>
  );
}

/**
 * 폭 비율 빠른 선택지 — 수치 입력만 두면 흔한 배치(절반·3분할)를 만들 때마다 33.3을 타이핑해야 한다.
 * 값은 **정확한 분수**를 쓴다(0.333을 저장하면 세 칸이 줄을 다 못 채운다).
 */
const SPAN_RATIO_PRESETS: { token: string; label: string; ratio: number }[] = [
  { token: 'r1', label: '줄 전체 (1/1)', ratio: 1 },
  { token: 'r3-4', label: '3/4 (75%)', ratio: 3 / 4 },
  { token: 'r2-3', label: '2/3 (66.7%)', ratio: 2 / 3 },
  { token: 'r1-2', label: '1/2 (절반)', ratio: 1 / 2 },
  { token: 'r1-3', label: '1/3 (33.3%)', ratio: 1 / 3 },
  { token: 'r1-4', label: '1/4 (25%)', ratio: 1 / 4 },
];
/** 프리셋에 없는 비율을 기록 중일 때 select가 가리키는 값(선택 불가 — 수치 입력이 정본). */
const SPAN_CUSTOM_OPTION = '__CUSTOM__';
/** 기록된 비율이 어느 프리셋인지 — 1/3처럼 무한소수라 근사 비교. 없으면 undefined(사용자 지정). */
function spanPresetTokenOf(ratio: number): string | undefined {
  return SPAN_RATIO_PRESETS.find((p) => Math.abs(p.ratio - ratio) < 1e-6)?.token;
}

/**
 * 폭 비율(span) 저작 컨트롤 — 다단 섹션 안에서 이 위젯이 줄 폭의 얼마를 차지할지.
 *
 * 2026-07-15에 UI만 제거했다가 2026-08-06 복원, 같은 날 2값(줄 전체/일반)에서 비율 축으로 확장.
 * 제거 기간 동안 span은 **코드로만 넣을 수 있는 축**이 되어, 저작자가 그 배치를 만들 수도 되돌릴
 * 수도 없었다(템플릿에 박힌 값은 손댈 방법이 없었다).
 *
 * 입력은 두 갈래다 — 프리셋 select(흔한 분수를 한 번에)와 % 수치 입력(임의 비율). 수치만 받으면
 * 절반·3분할 같은 일상 배치가 번거롭고, 프리셋만 두면 임의 비율을 못 만든다.
 *
 * 비-base 뷰의 선택지가 셋인 이유: '기본값 상속'(키 제거)과 '자동'('auto' 기록)이 다르다 —
 * base에 비율이 있어도 이 뷰만 해제하는 게 'auto'의 존재 이유다(setWidgetSpanForView 주석).
 */
function WidgetSpanOnViewControl({ selection, ctx }: WidgetPropControlProps) {
  const isBase = ctx.activeView === COMPOSER_BASE_VIEW_KEY;
  const choice = fold(selection.widgets, (w) => readWidgetSpanChoice(w, ctx));
  const single = selection.single;
  const host = single ? spanHostGroupOf(ctx.tree, single.id) : null;
  const overridden = !isBase && !!single && single.responsive?.[ctx.activeView]?.span != null;
  // 기록값 토큰('' | 'auto' | 비율 문자열) → select 값 / % 입력 값.
  const recorded = choice.mixed ? '' : (choice.value ?? '');
  const recordedRatio =
    recorded !== '' && recorded !== 'auto' && Number.isFinite(Number(recorded))
      ? Number(recorded)
      : undefined;
  const selectValue = choice.mixed
    ? MIXED_OPTION
    : recordedRatio !== undefined
      ? (spanPresetTokenOf(recordedRatio) ?? SPAN_CUSTOM_OPTION)
      : recorded;
  const apply = (value: number | 'auto' | undefined): void =>
    selection.setAll((x) => setWidgetSpanForView(x, ctx.activeView, value));
  // % 입력(비제어)을 언제 다시 초기화할지 — 선택·뷰가 바뀌거나 기록값이 밖에서(select 등) 바뀔 때만.
  const spanInputKey = `${selection.widgets.map((w) => w.id).join(',')}-${ctx.activeView}`;
  // base 값 안내(상속하면 무엇이 되는지) — 해소값(오버라이드 반영)을 쓰면 'auto'로 해제해 둔
  // 위젯에서 "상속=자동"이라 거짓말한다(상속하면 base에 적힌 비율이 된다).
  const baseLabel =
    single && typeof single.span === 'number'
      ? single.span >= 1
        ? '줄 전체'
        : `${formatSpanPercent(single.span)}%`
      : '자동';
  return (
    <div>
      <label className={PROP_LABEL_CLS}>
        폭 비율
        {isBase ? '(기본)' : ` (${composerViewLabel(ctx.activeView)} 뷰 오버라이드)`}
        {/* '이 뷰에서 숨김'·단 수와 같은 배지 — 뷰별 축의 오버라이드 표시를 한 결로 맞춘다. */}
        {overridden ? (
          <span className="ml-1 rounded bg-brand-100 px-1 text-[9px] text-brand-700">
            오버라이드
          </span>
        ) : null}
        {choice.mixed ? <MixedTag /> : null}
      </label>
      <select
        className={`${PROP_INPUT_CLS} ${overridden ? 'border-brand-400 bg-brand-50 text-brand-700' : ''}`}
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === MIXED_OPTION || v === SPAN_CUSTOM_OPTION) return;
          if (v === '') return apply(undefined);
          if (v === 'auto') return apply('auto');
          apply(SPAN_RATIO_PRESETS.find((p) => p.token === v)?.ratio);
        }}
      >
        {choice.mixed ? (
          <option value={MIXED_OPTION} disabled>
            — 혼합 —
          </option>
        ) : null}
        {isBase ? (
          // base엔 상속 개념이 없다 — 'auto'는 미지정과 같은 뜻이라(shared JSDoc) 빈 값 하나로 합친다.
          <option value="">자동 (남는 폭을 이웃과 나눔)</option>
        ) : (
          <option value="">기본값 상속{single ? ` (기본 ${baseLabel})` : ''}</option>
        )}
        {SPAN_RATIO_PRESETS.map((p) => (
          <option key={p.token} value={p.token}>
            {p.label}
          </option>
        ))}
        {isBase ? null : <option value="auto">자동 (이 뷰만 해제)</option>}
        {selectValue === SPAN_CUSTOM_OPTION ? (
          <option value={SPAN_CUSTOM_OPTION} disabled>
            사용자 지정 ({recordedRatio !== undefined ? formatSpanPercent(recordedRatio) : ''}%)
          </option>
        ) : null}
      </select>
      <div className="mt-1 flex items-center gap-1">
        <input
          type="number"
          min={1}
          max={100}
          step={0.1}
          className={PROP_INPUT_CLS}
          // 타이핑 중에는 커밋하지 않는다(간격(mm) 입력과 같은 결 — key+defaultValue로 외부
          // 변경만 반영, 커밋은 blur/Enter). 매 글자 커밋하는 제어 input이면 "12.5"를 칠 때
          // Number("12.")=12로 스냅백해 소수점을 칠 수 없고, 그 중간 상태에서 Chrome이 주는
          // value는 ''라 "비우면 삭제"가 발동해 지정이 통째로 날아간다.
          key={`${spanInputKey}-${recorded}`}
          defaultValue={recordedRatio !== undefined ? formatSpanPercent(recordedRatio) : ''}
          placeholder={choice.mixed ? '혼합' : '자동'}
          onBlur={(e) => {
            const el = e.currentTarget;
            const display = recordedRatio !== undefined ? formatSpanPercent(recordedRatio) : '';
            const raw = el.value.trim();
            // 비우면 값 삭제(base=자동 / 비-base=상속) — select의 빈 값 옵션과 같은 상태다.
            if (raw === '') {
              if (recordedRatio !== undefined) apply(undefined);
              return;
            }
            const pct = Number(raw);
            // 무효 입력은 커밋하지 않되 표시를 기록값으로 되돌린다 — 안 되돌리면 화면의 수치와
            // 실제 지정이 갈라진 채 남는다.
            if (!Number.isFinite(pct) || pct <= 0) {
              el.value = display;
              return;
            }
            const next = Math.min(1, pct / 100);
            if (next !== recordedRatio) apply(next);
            else el.value = display;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <span className="text-xs text-gray-500">%</span>
      </div>
      {host && single ? (
        <WidgetSpanViewHint widget={single} host={host} activeView={ctx.activeView} />
      ) : (
        <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
          다단 섹션 안에서 이 위젯이 줄 폭의 얼마를 차지할지. 1단 섹션·1단 뷰에서는 적용되지 않습니다.
        </p>
      )}
    </div>
  );
}

/**
 * 뷰 숨김(비-base 뷰 한정) — 반응형 박스. 단일=오버라이드 badge / 복수=혼합 태그.
 * 배지는 "걸림"만 말하고 "안 걸림"은 침묵하므로, 상태 문구를 함께 둔다(단 수의
 * GroupColumnsViewHint와 같은 결 — 저작자가 기본 상태를 확인할 수 있어야 한다).
 */
function WidgetHiddenOnViewControl({ selection, ctx }: WidgetPropControlProps) {
  const isBase = ctx.activeView === COMPOSER_BASE_VIEW_KEY;
  const hidden = fold(selection.widgets, (w) => readWidgetHidden(w, ctx));
  return (
    <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
      <p className="text-[11px] font-medium text-gray-500">
        반응형 · {isBase ? '기본(데스크톱)' : `${composerViewLabel(ctx.activeView)} 뷰`}
      </p>
      {/* 폭 비율(span)은 이 박스가 아니라 공통 푸터의 별도 디스크립터(spanOnView)다 — 숨김은 input
          전용이지만 폭 비율은 전 kind가 가지는 축이라, 여기에 얹으면 제목·구분선·섹션이 설정할 자리를
          잃는다(viewOrder를 푸터에 둔 것과 같은 이유).
          이력: 2026-07-15에 UI만 제거했다가 2026-08-06 복원 — 제거 기간 동안 span은 코드로만 넣을 수
          있는 축이 되어 저작자가 그 배치를 만들 수도 되돌릴 수도 없었다(렌더는 내내 살아 있었다). */}
      {!isBase ? (
        <>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <TriStateCheckbox
              className="h-4 w-4"
              checked={!!hidden.value}
              mixed={hidden.mixed}
              onChange={(next) =>
                selection.setAll((x) => setWidgetHiddenForView(x, ctx.activeView, next))
              }
            />
            이 뷰에서 숨김
            {selection.single?.responsive?.[ctx.activeView]?.hidden ? (
              <span className="rounded bg-brand-100 px-1 text-[9px] text-brand-700">오버라이드</span>
            ) : null}
            {hidden.mixed ? <MixedTag /> : null}
          </label>
          {/* 숨김 축 상태 안내 — 배지는 "걸림"만 말하고 "안 걸림"은 침묵해서, 저작자가 기본 상태를
              확인할 방법이 없었다. 복수 선택 혼합은 별도 문구(전원 숨김이라고 거짓말하지 않게 —
              TriStateCheckbox는 혼합에서 클릭 시 전원 ON으로 통일한다). */}
          {hidden.mixed ? (
            <p className="text-[10px] leading-snug text-gray-400">
              선택한 위젯의 숨김 상태가 서로 다릅니다 — 체크하면 전원이 이 뷰에서 숨겨집니다.
            </p>
          ) : hidden.value ? (
            <p className="text-[10px] leading-snug text-brand-600">
              이 뷰에서만 숨겨집니다 — 체크를 해제하면 기본대로 표시됩니다.
            </p>
          ) : (
            <p className="text-[10px] leading-snug text-gray-400">
              기본대로 표시됩니다. 체크하면 이 뷰에서만 숨겨집니다.
            </p>
          )}
        </>
      ) : (
        <p className="text-[10px] text-gray-400">
          숨김은 바형/폴드형 뷰에서 설정(기본 뷰는 {selection.single ? '위' : '아래'} 표시 모드
          사용).
        </p>
      )}
    </div>
  );
}

/**
 * 뷰 순서 오버라이드 표시 + 초기화(비-base 뷰 한정) — 숨김 박스와 **별도 디스크립터**인 이유:
 * 숨김은 input 전용 축이지만(design/group은 대상 아님) 순서는 전 kind가 가진다. 숨김 박스에
 * 얹으면 섹션·제목의 순서 오버라이드가 되돌릴 수단 없이 남는다.
 *
 * 순서는 **형제 집합 단위** 축이다 — reorderSiblingsForView가 한 번 옮길 때 형제 전원에 order를
 * 박으므로(base 배열 불변), 되돌리기도 형제 전원이어야 한다. 그래서 버튼 라벨/보조 문구가
 * "형제 전체가 돌아간다"를 말한다(한 위젯만 복귀한다고 오해하면 결과가 예상과 달라진다).
 *
 * 단일 선택 전용(bulk 미대상) — 복수 선택은 서로 다른 형제 집합에 걸칠 수 있어 "어느 집합을
 * 되돌리는가"가 정해지지 않는다. 오버라이드가 없으면 applicable=false로 통째 미노출.
 */
function WidgetViewOrderControl({ selection, ctx }: WidgetPropControlProps) {
  const single = selection.single;
  const onTreeUpdate = ctx.onTreeUpdate;
  if (!single || !onTreeUpdate) return null;
  return (
    <div className="space-y-1 rounded border border-gray-200 bg-gray-50 p-2">
      <p className="text-[11px] font-medium text-gray-500">
        반응형 · {composerViewLabel(ctx.activeView)} 뷰 순서
      </p>
      <p className="text-[10px] leading-snug text-brand-600">이 뷰에서 순서가 조정돼 있습니다.</p>
      <button
        type="button"
        className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50"
        onClick={() =>
          onTreeUpdate((tree) => clearSiblingViewOrder(tree, single.id, ctx.activeView))
        }
      >
        이 뷰 순서 초기화
      </button>
      <p className="text-[10px] leading-snug text-gray-400">
        순서는 형제 단위 설정이라, 같은 부모 안 형제 전체가 기본 순서로 돌아갑니다.
      </p>
    </div>
  );
}

/** 필수 입력 체크박스 — 복수는 tri-state(혼합=indeterminate). */
function WidgetRequiredControl({ selection }: WidgetPropControlProps) {
  const required = fold(selection.widgets, readWidgetRequired);
  return (
    <label className="flex items-center gap-2 text-xs text-gray-600">
      <TriStateCheckbox
        className="h-4 w-4"
        checked={!!required.value}
        mixed={required.mixed}
        onChange={(next) =>
          selection.setAll((x) => (x.kind === 'input' ? { ...x, required: next } : x))
        }
      />
      필수 입력
      {required.mixed ? <MixedTag /> : null}
    </label>
  );
}

/** 표시 모드(편집/읽기/숨김) 버튼 그룹 — 복수 혼합이면 활성 표시 없음 + 혼합 태그. */
function WidgetModeControl({ selection }: WidgetPropControlProps) {
  const mode = fold(selection.widgets, readWidgetMode);
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        표시 모드
        {mode.mixed ? <MixedTag /> : null}
      </label>
      <div className="inline-flex overflow-hidden rounded border border-gray-300">
        {WRITE_FORM_MODE_OPTIONS.map((opt) => {
          const active = !mode.mixed && mode.value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                selection.setAll((x) => (x.kind === 'input' ? { ...x, mode: opt.value } : x))
              }
              className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                active ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 선택지 표시 변형(displayVariant) 버튼 그룹 — radio 전용(라디오 버튼 / 가로 버튼).
 * '가로 버튼'은 옵션 수만큼 등분한 칸에 꽉 찬 버튼을 깔고 선택된 것만 강조한다(구 TBM 작성 폼
 * 「위험성평가 실시여부」의 예/아니오 토글 동형). 저장값·필수 검증은 그대로 — 보여주는 방식만
 * 바뀐다(shared InputWidget.displayVariant JSDoc). 기본(라디오 버튼)=키 제거(JSON 미방출).
 */
function WidgetDisplayVariantControl({ selection }: WidgetPropControlProps) {
  const variant = fold(selection.widgets, readWidgetDisplayVariant);
  const options: { value: 'default' | 'buttons'; label: string }[] = [
    { value: 'default', label: '라디오 버튼' },
    { value: 'buttons', label: '가로 버튼' },
  ];
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        선택지 표시
        {variant.mixed ? <MixedTag /> : null}
      </label>
      <div className="inline-flex overflow-hidden rounded border border-gray-300">
        {options.map((opt) => {
          const active = !variant.mixed && variant.value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                selection.setAll((x) => {
                  if (x.kind !== 'input') return x;
                  const copy = { ...x };
                  if (opt.value === 'buttons') copy.displayVariant = 'buttons';
                  else delete copy.displayVariant;
                  return copy;
                })
              }
              className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                active ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 편집 진입(editEntry) 버튼 그룹 — text/textarea/textarea-list/text-list 전용(항상 입력 /
 * 클릭 시 편집). 'click'이면 평소 민짜 텍스트로 표시하고 클릭해야 입력 컨트롤로 전환 —
 * 목록형은 항목 단위(구 SI 작성 페이지 EditableField 결 — shared InputWidget.editEntry JSDoc).
 * 기본(항상 입력)=키 제거(JSON 미방출).
 */
function WidgetEditEntryControl({ selection }: WidgetPropControlProps) {
  const entry = fold(selection.widgets, readWidgetEditEntry);
  const options: { value: 'always' | 'click'; label: string }[] = [
    { value: 'always', label: '항상 입력' },
    { value: 'click', label: '클릭 시 편집' },
  ];
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        편집 진입
        {entry.mixed ? <MixedTag /> : null}
      </label>
      {/* 클릭 진입은 편집 컨트롤을 여는 축 — 읽기전용/숨김이면 열 컨트롤 자체가 없다. */}
      <WidgetModeInertGate
        widgets={selection.widgets}
        needs="editable"
        hasValue={anyWidgetHasValue(selection.widgets, (w) => w.editEntry !== undefined)}
      >
        <div className="inline-flex overflow-hidden rounded border border-gray-300">
          {options.map((opt) => {
            const active = !entry.mixed && entry.value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() =>
                  selection.setAll((x) => {
                    if (x.kind !== 'input') return x;
                    const copy = { ...x };
                    if (opt.value === 'click') copy.editEntry = 'click';
                    else delete copy.editEntry;
                    return copy;
                  })
                }
                className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                  active ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                aria-pressed={active}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </WidgetModeInertGate>
    </div>
  );
}

/**
 * 긴 텍스트 접기(collapseLines) — text/textarea/textarea-list/text-list 전용. 켬/끔 버튼 그룹 +
 * 줄 수 입력(켬일 때만 노출, 단위 '줄' 표기 — 에디터 숫자 입력 단위 표기 정책).
 * 켬 기본값은 2줄(조문 행이면 법령명+조문 헤더가 남는 정도 — 저작자가 곧바로 조정한다).
 * 끔=키 제거(JSON 미방출). 접힘 **상태**는 저장되지 않는다 — 저장되는 건 이 줄 수뿐이고,
 * 본문·상세·공유·PDF에는 언제나 전문이 나간다(shared collapseLines JSDoc 규약).
 */
function WidgetCollapseLinesControl({ selection }: WidgetPropControlProps) {
  const lines = fold(selection.widgets, readWidgetCollapseLines);
  const on = !lines.mixed && (lines.value ?? 0) >= 1;
  const setLines = (next: number | undefined): void =>
    selection.setAll((x) => {
      if (x.kind !== 'input') return x;
      const copy = { ...x };
      if (next === undefined) delete copy.collapseLines;
      else copy.collapseLines = next;
      return copy;
    });
  const options: { value: 'off' | 'on'; label: string }[] = [
    { value: 'off', label: '접지 않음' },
    { value: 'on', label: '접기' },
  ];
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        긴 텍스트 접기
        {lines.mixed ? <MixedTag /> : null}
      </label>
      <div className="flex items-center gap-2">
        <div className="inline-flex overflow-hidden rounded border border-gray-300">
          {options.map((opt) => {
            const active = !lines.mixed && (opt.value === 'on' ? on : !on);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setLines(opt.value === 'on' ? (lines.value || 2) : undefined)}
                className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                  active ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                aria-pressed={active}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {on ? (
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              className="w-16 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
              value={lines.value ?? 2}
              onChange={(e) => {
                const n = Math.floor(Number(e.target.value));
                // 1 미만은 "접지 않음"과 같은 뜻 — 켬 상태를 유지하려면 최소 1줄.
                setLines(Number.isFinite(n) && n >= 1 ? n : 1);
              }}
            />
            <span className="text-xs text-gray-500">줄</span>
          </div>
        ) : null}
      </div>
      <p className="mt-1 text-[11px] leading-snug text-gray-400">
        작성 화면에서 이 줄 수까지만 보이고 나머지는 &quot;펼치기&quot;로 접습니다. 값 표시 상태
        전용이며(편집 중에는 전문), 본문·PDF에는 언제나 전문이 인쇄됩니다.
      </p>
    </div>
  );
}

// ── 라벨 표시 계열(위치·정렬·폰트·보조 설명) — 구 LabelPresentationControls 분해 편입(2026-08-24).
//    분해 단위 = fold(혼합) 판정이 뜻을 갖는 축. 한 덩어리로 두면 복수 선택에서 "무엇이 혼합인지"를
//    말할 수 없고, 실제로 이 계열이 통째로 복수 패널에서 빠져 있었다(장소·세부장소 동시 선택 제보).

/** 라벨 위치 세그(상단/좌측/없음) — 기본(top)=키 제거(JSON 미방출), 그 외만 저장. */
function WidgetLabelPlacementControl({ selection }: WidgetPropControlProps) {
  const placement = fold(selection.widgets, readWidgetLabelPlacement);
  const options: { value: 'top' | 'side' | 'none'; label: string }[] = [
    { value: 'top', label: '상단' },
    { value: 'side', label: '좌측' },
    { value: 'none', label: '없음' },
  ];
  const isNone = !placement.mixed && placement.value === 'none';
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        라벨 위치
        {placement.mixed ? <MixedTag /> : null}
      </label>
      <div className="inline-flex overflow-hidden rounded border border-gray-300">
        {options.map((opt) => {
          const active = !placement.mixed && placement.value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                selection.setAll((x) => {
                  if (x.kind !== 'input') return x;
                  const copy = { ...x };
                  if (opt.value === 'top') delete copy.labelPlacement;
                  else copy.labelPlacement = opt.value;
                  return copy;
                })
              }
              className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                active ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {isNone ? (
        <p className="mt-1 text-[11px] leading-snug text-gray-400">
          라벨을 화면에 그리지 않습니다 — 보조 설명·라벨 병기도 함께 사라집니다. 필수 표식은
          라벨 대신 <b>입력 칸의 배지</b>로 계속 표시되고, 필수 입력 <b>검증</b>도 그대로
          동작합니다. 라벨 글은 화면에 안 보여도 화면낭독기가 읽을 이름으로 쓰이니{' '}
          <b>비우지 마세요</b>.
        </p>
      ) : null}
    </div>
  );
}

/**
 * 라벨 정렬(좌/중/우) — rc가 top 배치에서만 소비하므로 노출 게이트도 top 전원일 때다
 * (구 단일 패널의 `placement === 'top' &&` 조건을 디스크립터 applicable로 옮긴 것).
 * 기본(start)=키 제거.
 */
function WidgetLabelAlignControl({ selection }: WidgetPropControlProps) {
  const align = fold(selection.widgets, readWidgetLabelAlign);
  const options: { value: 'start' | 'center' | 'end'; label: string }[] = [
    { value: 'start', label: '좌' },
    { value: 'center', label: '중' },
    { value: 'end', label: '우' },
  ];
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        라벨 정렬
        {align.mixed ? <MixedTag /> : null}
      </label>
      <div className="inline-flex overflow-hidden rounded border border-gray-300">
        {options.map((opt) => {
          const active = !align.mixed && align.value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                selection.setAll((x) => {
                  if (x.kind !== 'input') return x;
                  const copy = { ...x };
                  if (opt.value === 'start') delete copy.labelAlign;
                  else copy.labelAlign = opt.value;
                  return copy;
                })
              }
              className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                active ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** 라벨 폰트 스타일 — 복수는 필드별 fold(혼합 필드는 '기본'으로 접고 MixedTag로 알림). */
function WidgetLabelStyleControl({ selection }: WidgetPropControlProps) {
  const styleKey = fold(selection.widgets, readWidgetLabelStyleKey);
  return (
    <WidgetTextStyleControls
      // WidgetTextStyleControls가 제목 줄을 소유하므로 혼합 표시는 제목 문자열에 섞는다
      // (별도 MixedTag 줄을 얹으면 같은 제목이 두 줄이 된다).
      label={styleKey.mixed ? '라벨 스타일 — 혼합 —' : '라벨 스타일'}
      style={foldTextStyle(selection.widgets, (w) => (w.kind === 'input' ? w.labelStyle : undefined))}
      defaults={COMPOSER_INPUT_LABEL_DEFAULT_STYLE}
      labelCls={PROP_LABEL_CLS}
      onField={(k, v) => selection.setAll((x) => setWidgetTextStyleField(x, k, v))}
    />
  );
}

/**
 * 라벨 보조 설명(labelHint) + 그 폰트 스타일 — 폰트는 보조 설명이 있을 때만 노출되는 **종속 UI**라
 * 같은 디스크립터로 묶는다(displayFormat이 kind와 세부 배선을 한 디스크립터로 묶는 선례 동형).
 * bulk 제외: 칸마다 다른 문구라 일괄 주입할 일이 사실상 없고, 오조작 시 선택 전 위젯의 설명이 한
 * 문구로 통일되는 파급이 크다(defaultValue와 같은 근거).
 */
function WidgetLabelHintControl({ selection }: WidgetPropControlProps) {
  const hint = fold(selection.widgets, readWidgetLabelHint);
  const hintStyleKey = fold(selection.widgets, readWidgetLabelHintStyleKey);
  const hasHint = !hint.mixed && !!hint.value;
  return (
    <>
      <div>
        <label className={PROP_LABEL_CLS}>보조 설명</label>
        <input
          type="text"
          className={PROP_INPUT_CLS}
          value={hint.mixed ? '' : (hint.value ?? '')}
          placeholder={hint.mixed ? '— 혼합 —' : '(위험성평가 실시자)'}
          onChange={(e) =>
            selection.setAll((x) =>
              x.kind === 'input' ? { ...x, labelHint: e.target.value || undefined } : x,
            )
          }
        />
      </div>
      {/* 보조 설명이 있을 때만 파트별 폰트 지정 노출 — 본문(labelStyle)과 별개(2026-07-12 사용자 요청). */}
      {hasHint ? (
        <WidgetTextStyleControls
          label={hintStyleKey.mixed ? '보조 설명 스타일 — 혼합 —' : '보조 설명 스타일'}
          style={foldTextStyle(selection.widgets, (w) =>
            w.kind === 'input' ? w.labelHintStyle : undefined,
          )}
          defaults={COMPOSER_LABEL_HINT_DEFAULT_STYLE}
          labelCls={PROP_LABEL_CLS}
          onField={(k, v) =>
            selection.setAll((x) =>
              x.kind === 'input'
                ? { ...x, labelHintStyle: mergeTextStyleField(x.labelHintStyle, k, v) }
                : x,
            )
          }
        />
      ) : null}
    </>
  );
}

/**
 * 읽기 표시 형식(InputWidget.format) — **본문 셀 바인딩의 표시 형식과 같은 편집기**(FormatFields +
 * FORMAT_KINDS + buildDefaultFormat)를 그대로 재사용한다. 축이 같은 union(BindingFormat)이므로
 * 편집기를 따로 만들면 두 화면의 저작 어휘가 갈라진다 — 새 kind는 FormatFields 한 곳만 늘리면
 * 본문·작성폼이 함께 얻는다.
 *
 * 위험성평가 배지가 1차 소비처다: kind=구간 판정(rangeMap) + "구간 기준표"(rangesFrom)로 운영자
 * 기준표 카탈로그를 가리키고 표시 방식을 배지로 켜면, 매칭 구간의 색(기준표 소유)으로 알약이 그려진다.
 *
 * 노출 규약: rc가 실제로 형식을 소비하는 위젯에만 노출하고(FORMAT_UNSUPPORTED_WIDGETS 제외),
 * **읽기 표시 전용 축**이라 표시 모드가 '읽기 전용'이 아니면 지금은 적용되지 않는다고 화면에
 * 명시한다(은닉 금지 — 지정한 대로 동작하는지를 저작자가 알 수 있어야 한다). 편집 컨트롤 값에
 * 형식을 먹이지 않는 건 rc 규약이다(포맷 문자열이 편집값으로 새면 저장이 오염된다 —
 * shared InputWidget.format JSDoc).
 */
function WidgetDisplayFormatControl({ selection, ctx }: WidgetPropControlProps) {
  const single = selection.single;
  const format = single?.kind === 'input' ? single.format : undefined;
  const kind: BindingFormat['kind'] | 'none' = format?.kind ?? 'none';
  const setFormat = (next: BindingFormat | undefined): void =>
    selection.setAll((x) => {
      if (x.kind !== 'input') return x;
      const copy = { ...x };
      // 미지정은 키 제거(JSON 미방출) — 접기·편집 진입 축과 같은 규약.
      if (next === undefined) delete copy.format;
      else copy.format = next;
      return copy;
    });
  return (
    <div>
      {/* 도움말은 본문 셀 표시 형식과 같은 문서(r3) — 같은 축이므로 링크도 같은 곳을 가리킨다. */}
      <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        표시 형식
        <HelpLink topic="bindingFormat" />
      </span>
      {/* 읽기 표시 전용 축 — rc의 readonly 조기 분기 안에서만 소비된다(모드 무의미 표기 공용 게이트). */}
      <WidgetModeInertGate
        widgets={selection.widgets}
        needs="readonly"
        hasValue={anyWidgetHasValue(selection.widgets, (w) => w.format !== undefined)}
      >
        <select
          className={PROP_INPUT_CLS}
          value={kind}
          onChange={(e) =>
            setFormat(buildDefaultFormat(e.target.value as BindingFormat['kind'] | 'none', format))
          }
        >
          {FORMAT_KINDS.map((k) => (
            <option key={k} value={k}>
              {formatKindOptionLabel(k)}
            </option>
          ))}
        </select>
        {format ? (
          <div className="mt-1">
            <FormatFields
              format={format}
              onChange={(next) => setFormat(next)}
              catalog={ctx.catalog ?? []}
              previewValues={ctx.previewValues}
              keyOptions={ctx.rowFieldOptions}
            />
          </div>
        ) : null}
        <p className="mt-1 text-[11px] leading-snug text-gray-400">
          값을 읽기 전용으로 보여줄 때의 표시 규칙입니다(본문 셀의 표시 형식과 같은 축). 입력
          컨트롤의 값에는 적용되지 않아 저장값은 원값 그대로입니다.
        </p>
      </WidgetModeInertGate>
    </div>
  );
}

/**
 * 범용 검색 제안 소스(suggestionsFrom) 저작 — text·textarea-list·text-list 전용(rc 소비 위젯).
 * 축 3요소: ① 검색 소스(catalogKey — 호스트 어댑터가 아는 키, 목록 정본 suggestionSources.ts)
 * ② 검색 필터 필드(filterByRowField — 현재 값 스코프에서 읽는 필드: 반복 안이면 행 필드 select
 * 정본(repeatRowBindingOptions), 밖이면 본문 바인딩 검색 콤보) ③ 선택 전파 매핑(writes —
 * WritesMappingEditor 단일 정본 재사용, 콤보 "선택 전파 매핑"과 같은 계약).
 * 미설정이면 축 자체를 저장하지 않는다(patchSuggestionsFrom이 정규화 — 빈 객체 금지).
 * 결정: 2026-08-05-composer-suggestion-source-axis.
 */
function WidgetSuggestionsFromControl({ selection, ctx }: WidgetPropControlProps) {
  const w = selection.single;
  // bulk:false — 단일 선택 전용(전파 대상·필터 필드가 위젯별 개별 배선이라 일괄 적용이 무의미).
  if (!w || w.kind !== 'input') return null;
  const cfg = w.suggestionsFrom;
  const patch = (p: {
    catalogKey?: string;
    filterByRowField?: string;
    writes?: RegisteredWidgetWrite[];
    requireSelection?: boolean;
  }): void =>
    selection.setAll((x) => {
      if (x.kind !== 'input') return x;
      const next = { ...x };
      const nextCfg = patchSuggestionsFrom(x.suggestionsFrom, p);
      if (nextCfg) next.suggestionsFrom = nextCfg;
      else delete next.suggestionsFrom;
      return next;
    });
  const source = findSuggestionSource(cfg?.catalogKey);
  const tree = ctx.tree;
  // 값 스코프 판정 — 반복 children이면 행 객체(행 필드), 밖이면 본문 values(본문 바인딩).
  // rc buildSuggestFilter가 현재 스코프 values에서 읽으므로 후보도 같은 스코프여야 한다.
  const enclosingRepeat = tree ? findEnclosingRepeatWidget(tree, w.id) : null;
  const repeatDef = enclosingRepeat
    ? (ctx.dataSources ?? []).find((d) => d.key === enclosingRepeat.over)
    : undefined;
  const bodyFilterOptions: ComboboxOption[] = tree
    ? collectBodyCommitTargets(tree)
        .filter((t) => t.bindingKey !== w.bindingKey)
        .map((t) => ({
          value: t.bindingKey,
          label: t.label || t.bindingKey,
          key: t.bindingKey,
          badge: t.widget,
          group: t.group,
        }))
    : [];
  const currentFilter = cfg?.filterByRowField ?? '';
  return (
    // 제안 드롭다운은 rc의 text·textarea-list·text-list **편집 컨트롤**을 감싸 열린다 —
    // 읽기전용/숨김이면 그 case에 닿기 전에 값 표시로 빠져 소스·필터·선택 전파가 모두 죽는다.
    <WidgetModeInertGate
      widgets={selection.widgets}
      needs="editable"
      hasValue={anyWidgetHasValue(selection.widgets, (w) => w.suggestionsFrom !== undefined)}
    >
    <div className="space-y-2">
      <div>
        <label className={PROP_LABEL_CLS}>검색 제안 소스</label>
        <select
          className={PROP_INPUT_CLS}
          value={cfg?.catalogKey ?? ''}
          onChange={(e) => patch({ catalogKey: e.target.value })}
        >
          <option value="">없음(일반 입력)</option>
          {suggestionSourceOptions(cfg?.catalogKey).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] leading-4 text-gray-400">
          {source?.description ??
            (cfg?.requireSelection
              ? '입력 중 서버 검색 결과를 드롭다운으로 제안합니다. 이 칸은 제안에서 고른 값만 받습니다.'
              : '입력 중 서버 검색 결과를 드롭다운으로 제안합니다. 자유 입력은 그대로 허용되고, 제안은 보조입니다.')}
        </p>
      </div>
      {cfg ? (
        <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
          {/* 선택 강제 — 자유 입력을 막고 제안에서 고른 값만 받는다. **칸 단위 선택**이지 소스의
              성질이 아니다(같은 소스라도 대책 라이브러리 칸은 자유 입력이 정답). 값이 원본 문서에서
              오는 칸(법령 조문 등)에만 켤 것. */}
          <label className="flex items-start gap-1.5 text-[11px] leading-4 text-gray-600">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={cfg.requireSelection === true}
              onChange={(e) => patch({ requireSelection: e.target.checked })}
            />
            <span>
              검색 결과에서만 선택(자유 입력 금지)
              <span className="mt-0.5 block text-gray-400">
                켜면 타이핑한 글자는 검색어로만 쓰이고, 제안을 골라야 값이 들어갑니다. 고르지 않고
                빠져나오면 방금 추가한 항목·행은 남지 않습니다. 작성자가 지어낼 값이 아닌 칸(법령
                조문 원문 등)에 쓰세요.
              </span>
            </span>
          </label>
          {/* 검색 필터 — 값 스코프에 따라 후보 축이 다르다(행 필드 / 본문 바인딩). 스코프를 알 수
              없으면(트리 미제공) 축 자체를 감춘다(자유 텍스트 입력 금지 — 오타가 조용한 미필터). */}
          {repeatDef ? (
            <div>
              <label className={PROP_LABEL_CLS}>
                검색 필터 필드 (반복 소스: {repeatDef.label ?? repeatDef.key})
              </label>
              <select
                className={PROP_INPUT_CLS}
                value={currentFilter}
                onChange={(e) => patch({ filterByRowField: e.target.value })}
              >
                <option value="">필터 없음(전체 검색)</option>
                {repeatRowBindingOptions(repeatDef, currentFilter, ctx.catalog)
                  .filter((o) => o.value !== '')
                  .map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
              </select>
            </div>
          ) : tree ? (
            <div>
              <label className={PROP_LABEL_CLS}>검색 필터 필드 (본문 값)</label>
              {/* eslint-disable-next-line local/catalog-picker-required -- 검색 필터 필드(본문 값 바인딩 키 목록) — 카탈로그 엔트리 아님 */}
              <CatalogCombobox
                value={currentFilter || undefined}
                options={
                  currentFilter && !bodyFilterOptions.some((o) => o.value === currentFilter)
                    ? [
                        ...bodyFilterOptions,
                        {
                          value: currentFilter,
                          label: `${currentFilter} (목록에 없음 — 보존)`,
                          key: currentFilter,
                        },
                      ]
                    : bodyFilterOptions
                }
                onChange={(v) => patch({ filterByRowField: v ?? '' })}
                placeholder="필터로 쓸 필드 검색…"
                allowClear
                clearLabel="(필터 없음)"
              />
            </div>
          ) : null}
          {repeatDef || tree ? (
            <p className="text-[11px] leading-4 text-gray-400">
              선택한 필드의 작성값이 검색 필터로 전달됩니다(값이 비면 필터 없이 검색).
              {source && source.filterKeys.length > 0
                ? ` 이 소스가 이해하는 필터 키는 ${source.filterKeys.join(' · ')} — 필드 이름이 다르면 필터 없이 검색됩니다.`
                : ''}
            </p>
          ) : null}
          {/* 선택 전파 매핑 — 콤보 writes와 같은 계약(제안 행 선택 시에만 raw 필드 전파).
              편집 UI는 WritesMappingEditor 단일 정본 재사용(별도 재구현 금지). */}
          {tree ? (
            <WritesMappingEditor
              writes={cfg.writes ?? []}
              onChange={(next) => patch({ writes: next })}
              // source 후보 — 제안 행 raw 필드(호스트 반환 형태). 콤보의 entry.returns 자리 대응물.
              sourceOptions={suggestionSourceRowFields(cfg.catalogKey)}
              // target 후보 — 행 스코프면 행 템플릿 배치 위젯 ∪ 행 필드 선언(hidden·객체 자식 포함)
              // + **자기 키 허용**(rc pick 순서 onChange(text)→writes라 마지막 쓰기가 살아남아
              // 제안 표기로 확정 텍스트를 덮는 저작이 성립 — 종전엔 자기 제외라 만들 수 없었다,
              // 2026-09-14 은닉 감사 3차 D). 본문 스코프는 종전(본문 후보, 자기 제외).
              {...writesTargetProps({
                w,
                tree,
                enclosingRepeat,
                repeatDef,
                catalog: ctx.catalog,
                dataDefs: ctx.dataDefs,
                dataSources: ctx.dataSources,
                allowSelf: true,
              })}
              onHoverHighlightWidget={ctx.onHoverHighlightWidget}
              onFtCellHover={ctx.onFtCellHover}
              title="선택 전파 매핑"
              emptyText="제안을 선택했을 때 그 행의 필드 값을 다른 위젯에 쓰려면 매핑을 추가하세요."
              sourceLabel="행 필드"
              sourcePlaceholder="행 필드 검색…"
              helpText="제안 항목을 선택하면 그 행의 필드 값이 대상 위젯에 자동 기록됩니다. 직접 타이핑한 값은 전파되지 않습니다."
              labelCls={PROP_LABEL_CLS}
            />
          ) : null}
          <p className="text-[11px] leading-4 text-gray-400">
            캔버스(저작 미리보기)에서는 제안 검색이 비활성입니다 — 실제 작성 화면에서만 조회됩니다.
          </p>
        </div>
      ) : null}
    </div>
    </WidgetModeInertGate>
  );
}

/**
 * 「시작값」 구획의 **「조회된 행들에서 만들기」 방식 하위 입력**(autoFill 저작) — 이 칸의 시작값을 조립
 * 데이터셋의 행에서 만든다(결정 2026-09-04 잠재위험요인 조립 전환 ④ — 구 `policy.seed`의
 * firstRowTo/distinctJoinTo가 칸 곁으로 옮겨온 자리. policy에 있을 땐 에디터에 표시가 없어
 * 저작자가 재현할 수 없었다).
 *
 * 방식 선택 자체는 상위 `WidgetStartValueControl`이 소유한다(결정 2026-09-05 대조표 1 — 같은
 * 질문의 답이 세 구획에 흩어져 있던 것을 하나로 모은 자리). 여기는 그 방식을 고른 뒤의 세 칸만
 * 그린다: ① 데이터(조립 def 키) ② 채우는 방식(첫 행의 값 / 여러 행의 값 합치기) ③ 읽을 칼럼.
 *
 * 소스 후보를 **조립·행 배열 def로만** 거르는 근거는 서버다: 주입부가 `assembled[source]`를
 * 읽는데(injectAutoFillValues) 조립기가 수동(manual) 선언은 아예 방출하지 않고
 * (data-source-assembler `kind !== 'assembled'` continue), 배열이 아니면 경고 후 건너뛴다.
 * 편집 정책(editMode)은 조건이 아니다 — 읽기 전용 def의 조립 산출도 그대로 주입된다.
 *
 * ⚠️ `WidgetModeInertGate`에 태우지 않는다 — 서버 `collectAutoFillTargets`는 표시 모드를 보지
 * 않아 읽기전용·숨김 칸에도 값이 들어간다(게이트 모듈 JSDoc이 "모드로 무의미해지지 않는 축은
 * 태우지 말 것"이라며 드는 기본값 시드와 같은 성질). 잠갔다면 그게 거짓 안내가 된다.
 */
function StartValueFromListFields({
  selection,
  ctx,
  keepMethod,
}: WidgetPropControlProps & {
  /**
   * 선언을 지웠는데도 「조회된 행들에서 만들기」 구획을 계속 띄워 달라는 신호 — 데이터 칸을
   * 「(고르세요)」로 되돌리면 `autoFill` 키가 사라지므로, 상위가 방식을 파생만 하면 구획이
   * 통째로 사라져 저작이 끊긴다(고르는 중인 상태와 안 쓰는 상태는 다른 뜻이다).
   */
  keepMethod: () => void;
}) {
  const w = selection.single;
  // bulk:false — 여러 칸에 같은 선언을 일괄 적용하면 전부 같은 값이 되어 뜻이 없다(labelHint와 같은 근거).
  if (!w || w.kind !== 'input') return null;
  const cfg = w.autoFill;
  // 서버 주입부가 받아들이는 형상만 후보로 — 여기서 넓히면 저작자가 지정한 선언이 조용히 무시된다.
  const sourceDefs = (ctx.dataSources ?? []).filter(
    (d) => d.kind === 'assembled' && d.cardinality === 'list',
  );
  // 아래 술어들은 cfg를 콜백 안에서 좁히지 않고 **미리 뽑은 지역 상수**로 비교한다 — 클로저
  // 안 좁힘에 기대면 리팩터 한 번에 조용히 깨진다.
  const cfgSource = cfg?.source ?? '';
  const cfgFrom = cfg?.from ?? '';
  const selectedDef = cfg ? sourceDefs.find((d) => d.key === cfgSource) : undefined;
  // 소스가 목록에 없다 = 그 데이터가 지워졌거나 이름이 바뀌었다. 선언은 살아 있으나 값은 절대
  // 안 들어온다(주입부가 경고 후 건너뜀) — 죽은 선언을 방치하지 않도록 사유와 해제 버튼을 함께 낸다.
  const brokenSource = !!cfg && !selectedDef;
  const fields = selectedDef?.fields ?? [];
  // 조립 후 행 기준이라 hidden 칼럼도 후보다 — 표에는 안 보이지만 행 데이터에는 실리고, 실제로
  // TBM 「작업내용」이 hidden 칼럼(workTaskName)을 읽는다.
  const brokenField =
    !!cfg && !!selectedDef && cfgFrom !== '' && !fields.some((f) => f.field === cfgFrom);
  const setCfg = (next: InputAutoFill | undefined): void =>
    selection.setAll((x) => {
      if (x.kind !== 'input') return x;
      const patched = { ...x };
      if (next) patched.autoFill = next;
      else delete patched.autoFill;
      return patched;
    });
  return (
    <div className="space-y-2">
      <div>
        <label className={PROP_LABEL_CLS}>데이터</label>
        {/* 이 패널의 라벨은 컨트롤과 묶이지 않은 형제 span 관례라(PROP_LABEL_CLS) select에 이름이
            안 붙는다 — title로 접근 이름을 준다. 세 칸 모두 같은 이유로 title을 갖는다. */}
        <select
          title="데이터"
          className={PROP_INPUT_CLS}
          value={cfg?.source ?? ''}
          onChange={(e) => {
            const source = e.target.value;
            if (!source) {
              // 선언은 지우되 구획은 유지 — 고르는 중과 안 쓰는 상태를 구분한다.
              setCfg(undefined);
              keepMethod();
              return;
            }
            // 데이터가 바뀌면 칼럼은 그 데이터의 것이어야 한다 — 같은 이름 칼럼이 있을 때만
            // 유지한다. 그대로 들고 오면 "지정했는데 값이 안 나오는" 죽은 선언이 된다.
            const nextDef = sourceDefs.find((d) => d.key === source);
            const keepFrom =
              cfgFrom !== '' && nextDef?.fields.some((f) => f.field === cfgFrom) ? cfgFrom : '';
            // 「값 사이 문구」는 데이터와 무관한 표기 축 — 데이터를 바꿔도 유지한다(잃으면
            // 저작자가 데이터만 바꿨는데 표기가 조용히 기본으로 돌아간다).
            setCfg({
              source,
              mode: cfg?.mode ?? 'firstRow',
              from: keepFrom,
              ...(cfg?.separator !== undefined ? { separator: cfg.separator } : {}),
            });
          }}
        >
          <option value="">(고르세요)</option>
          {/* 「원료 → 카드명」 흐름 표기(결정 2026-09-05 대조표 3) — 카드명만 보이면 그 목록의
              진짜 원천(발견사항 등)이 가려져, def를 경유하는 것이 우회처럼 읽힌다. 원료 문구는
              데이터 카드 접힘 요약(DataFlowSummary)이 쓰는 것과 **같은 유도**(dataSourceOrigin). */}
          {sourceDefs.map((d) => (
            <option key={d.key} value={d.key}>
              {`${dataSourceOrigin(d, ctx.catalog ?? [])} → ${d.label || d.key}`}
            </option>
          ))}
          {/* 없는 데이터를 가리키는 저장값도 선택을 보존한다 — 목록에서 빠지면 속성창을 여는
              것만으로 선언이 조용히 지워지고, 저작자는 사라진 이유를 알 수 없다. */}
          {brokenSource && cfg && (
            <option value={cfg.source}>{cfg.source + ' (없는 데이터 — 보존)'}</option>
          )}
        </select>
        <p className="mt-1 text-[11px] leading-4 text-gray-400">
          이 칸의 시작값을 여기서 고른 데이터의 행에서 만듭니다(작성자가 고쳐 쓸 수 있습니다).
        </p>
      </div>
      {cfg && (
        <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
          {brokenSource ? (
            <>
              <p className="text-[11px] leading-4 text-amber-600">
                「{cfg.source}」데이터가 지금 이 양식에 없습니다 — 이 시작값은 만들어지지
                않습니다. 데이터 이름이 바뀌었다면 위에서 다시 고르고, 아니면 해제하세요.
              </p>
              <button
                type="button"
                onClick={() => {
                  setCfg(undefined);
                  keepMethod();
                }}
                className="rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-white"
              >
                시작값 선언 해제
              </button>
            </>
          ) : (
            <>
              <div>
                <label className={PROP_LABEL_CLS}>채우는 방식</label>
                <select
                  title="채우는 방식"
                  className={PROP_INPUT_CLS}
                  value={cfg.mode}
                  onChange={(e) => {
                    const mode = e.target.value as InputAutoFill['mode'];
                    // 「값 사이 문구」는 합치기 전용 — 첫 행 방식으로 바꾸면 키를 지운다(방식
                    // 전환의 비선택 축 키 삭제와 같은 규약: 화면 밖 잠복 키를 남기지 않는다).
                    const next = { ...cfg, mode };
                    if (mode !== 'distinctJoin') delete next.separator;
                    setCfg(next);
                  }}
                >
                  {/* 라벨 풀어쓰기(결정 2026-09-05 대조표 2·4) — 코드 값은 불변.
                      「첫 행」은 정렬이 정한다는 전제가, 「합치기」는 원천이 여러 행 목록이라는
                      전제가 각각 이름에 없어 오인을 낳았다. */}
                  <option value="firstRow">첫 행의 값(정렬 맨 위)</option>
                  <option value="distinctJoin">여러 행의 값 합치기(중복 제거)</option>
                </select>
                <p className="mt-1 text-[11px] leading-4 text-gray-400">
                  {cfg.mode === 'firstRow'
                    ? '정렬한 순서에서 맨 위 행의 값을 가져옵니다 — 데이터 카드의 「행 순서」가 곧 어느 행인지를 정합니다.'
                    : '모든 행의 값을 중복 없이 이어 붙입니다(사이 문구는 아래에서 정합니다). 값이 하나도 없으면 칸은 빈 채로 둡니다.'}
                </p>
              </div>
              {cfg.mode === 'distinctJoin' && (
                <div>
                  <label className={PROP_LABEL_CLS}>값 사이 문구</label>
                  {/* 공백이 곧 표기라 trim하지 않는다(「 / 」의 양끝 공백). 비우면 키를 지워
                      기본(쉼표+띄어쓰기)으로 되돌린다 — 빈 문자열 저장(붙여쓰기)은 화면에서
                      기본과 구분이 안 가 UI로는 열지 않는다(템플릿 파일 선언으로는 유효). */}
                  <input
                    type="text"
                    title="값 사이 문구"
                    className={PROP_INPUT_CLS}
                    value={cfg.separator ?? ''}
                    placeholder=", "
                    onChange={(e) => {
                      const next = { ...cfg };
                      if (e.target.value === '') delete next.separator;
                      else next.separator = e.target.value;
                      setCfg(next);
                    }}
                  />
                  <p className="mt-1 text-[11px] leading-4 text-gray-400">
                    합칠 때 값과 값 사이에 넣는 문구입니다. 비우면 쉼표+띄어쓰기(「, 」)를 씁니다.
                  </p>
                </div>
              )}
              <div>
                <label className={PROP_LABEL_CLS}>읽을 칼럼</label>
                <select
                  title="읽을 칼럼"
                  className={PROP_INPUT_CLS}
                  value={cfg.from}
                  onChange={(e) => setCfg({ ...cfg, from: e.target.value })}
                >
                  <option value="">(고르세요)</option>
                  {fields.map((f) => (
                    <option key={f.field} value={f.field}>
                      {(f.label || f.field) + (f.hidden ? ' (표에 숨김)' : '')}
                    </option>
                  ))}
                  {brokenField && <option value={cfg.from}>{cfg.from + ' (없는 칼럼 — 보존)'}</option>}
                </select>
                {brokenField ? (
                  <p className="mt-1 text-[11px] leading-4 text-amber-600">
                    「{cfg.from}」칼럼이 이 데이터에 없습니다 — 값이 채워지지 않습니다.
                  </p>
                ) : cfg.from === '' ? (
                  <p className="mt-1 text-[11px] leading-4 text-amber-600">
                    읽을 칼럼을 골라야 값이 채워집니다.
                  </p>
                ) : null}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 입력 형식(inputFormat) select — text 위젯 전용(일반/전화번호/사업자등록번호/이메일). 전화번호·
 * 사업자등록번호는 rc가 숫자만+자동 하이픈으로 강제, 이메일은 inputMode만(자유 문자라 필터 불가),
 * 셋 다 submitAction 등록 모달의 확인 게이트(완결 형식)에 참여한다.
 * 표시용 BindingFormat과 별개 축(shared InputWidget.inputFormat JSDoc). 해제(일반)=키 제거(JSON 미방출).
 */
function WidgetInputFormatControl({ selection }: WidgetPropControlProps) {
  const fmt = fold(selection.widgets, readWidgetInputFormat);
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        입력 형식
        {fmt.mixed ? <MixedTag /> : null}
      </label>
      {/* 자동 하이픈·입력 필터는 rc case 'text'(편집 컨트롤)에서만 소비 — 읽기전용/숨김이면 죽는다. */}
      <WidgetModeInertGate
        widgets={selection.widgets}
        needs="editable"
        hasValue={anyWidgetHasValue(selection.widgets, (w) => w.inputFormat !== undefined)}
      >
        <select
          className={PROP_INPUT_CLS}
          value={fmt.mixed ? '' : ((fmt.value as string) ?? '')}
          onChange={(e) => {
            const next = e.target.value;
            selection.setAll((x) => {
              if (x.kind !== 'input') return x;
              const copy = { ...x };
              if (next === 'phone' || next === 'business-number' || next === 'email')
                copy.inputFormat = next;
              else delete copy.inputFormat;
              return copy;
            });
          }}
        >
          <option value="">일반</option>
          <option value="phone">전화번호 (자동 하이픈)</option>
          <option value="business-number">사업자등록번호 (자동 하이픈)</option>
          <option value="email">이메일</option>
        </select>
      </WidgetModeInertGate>
    </div>
  );
}

/**
 * 기본값 저작을 노출하는 스칼라 값 위젯 종류(디스크립터 applicable 공용).
 * photo/aiTrigger/table/sortableSelect/registered/dataSourceRows는 값 형태가 복합
 * (파일/배열/객체/host 위임)이라 스칼라 기본값 저작이 성립하지 않아 비노출.
 */
const DEFAULT_VALUE_SCALAR_WIDGETS: ReadonlySet<WidgetType> = new Set<WidgetType>([
  'text',
  'textarea',
  'combobox',
  'date',
  'time',
  'select',
  'radio',
  'checkbox',
  'number',
]);

/**
 * 「시작값」 구획의 **「고정값」 방식 하위 입력**(defaultValue 저작) — 작성 페이지가 처음 열릴 때
 * 채워질 값. 캔버스에 타이핑한 값은 미리보기 편집(writeCanvasEdits)일 뿐 템플릿에 저장되지
 * 않으므로(2026-07-17 사용자 오해 지점), 템플릿 defaultValue 저작은 이 입력이 유일한 경로.
 * 해제(없음/빈 값)=키 제거 — `defaultValue: undefined`가 아니라 delete로 JSON에 미방출.
 * 값 형식은 rc 렌더러와 동일(date=YYYY-MM-DD·time=HH:MM 문자열, number=number, checkbox=boolean,
 * select/radio=option.value 문자열) — 네이티브 input type이 그 형식을 보장.
 *
 * 라벨·안내는 상위 `WidgetStartValueControl`이 소유한다(구획 통합, 결정 2026-09-05 대조표 1) —
 * 여기서 「기본값」 라벨을 다시 달면 통합해 없앤 어휘가 되살아난다.
 */
function StartValueFixedInput({ selection }: WidgetPropControlProps) {
  const w = selection.single;
  // bulk:false — 단일 선택 전용(위젯 개별 값 성격). 복수 경로로는 렌더되지 않음.
  if (!w || w.kind !== 'input') return null;
  const setDefault = (v: unknown): void =>
    selection.setAll((x) => {
      if (x.kind !== 'input') return x;
      const next = { ...x };
      if (v === undefined) delete next.defaultValue;
      else next.defaultValue = v;
      return next;
    });
  const dv = w.defaultValue;
  return (() => {
    switch (w.widget) {
      case 'number':
        return (
          <input
            type="number"
            className={PROP_INPUT_CLS}
            value={typeof dv === 'number' ? dv : ''}
            placeholder="(없음)"
            onChange={(e) =>
              setDefault(e.target.value === '' ? undefined : Number(e.target.value))
            }
          />
        );
      case 'date':
      case 'time':
        return (
          <input
            type={w.widget}
            className={PROP_INPUT_CLS}
            value={typeof dv === 'string' ? dv : ''}
            onChange={(e) => setDefault(e.target.value || undefined)}
          />
        );
      case 'checkbox': {
        // 3상(없음/체크/해제) — "미지정"과 "해제로 시작"은 다른 저작 의도라 select로 구분.
        const cur = dv === true ? 'true' : dv === false ? 'false' : '';
        return (
          <select
            className={PROP_INPUT_CLS}
            value={cur}
            onChange={(e) =>
              setDefault(e.target.value === '' ? undefined : e.target.value === 'true')
            }
          >
            <option value="">(없음)</option>
            <option value="true">체크됨</option>
            <option value="false">해제됨</option>
          </select>
        );
      }
      case 'select':
      case 'radio': {
        // 그 위젯 options에서 선택 — 저장돼 있으나 목록에 없는 값은 보존 옵션으로 노출(재저장 시 소실 방지).
        const cur = typeof dv === 'string' ? dv : '';
        const inOptions = !cur || (w.options ?? []).some((o) => o.value === cur);
        return (
          <select
            className={PROP_INPUT_CLS}
            value={cur}
            onChange={(e) => setDefault(e.target.value || undefined)}
          >
            <option value="">(없음)</option>
            {(w.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {!inOptions && <option value={cur}>{cur} (목록에 없음 — 보존)</option>}
          </select>
        );
      }
      // text/textarea/combobox — 자유 텍스트(빈 문자열=해제). 카탈로그 유래 number 값은 문자열로 표시.
      default:
        return (
          <input
            type="text"
            className={PROP_INPUT_CLS}
            value={typeof dv === 'string' || typeof dv === 'number' ? String(dv) : ''}
            placeholder="(없음)"
            onChange={(e) => setDefault(e.target.value || undefined)}
          />
        );
    }
  })();
}

/**
 * 시작값 방식 — **UI 표현 축**이다. 저장 스키마는 종전 그대로 세 키(`defaultValue`·`prefillFrom`·
 * `autoFill`)이고, 합치는 것은 "이 칸은 뭘로 시작하나"라는 **하나의 질문**뿐이다(결정 2026-09-05
 * 대조표 1 — 답이 「기본값」·「자동 채움(시스템 조회)」·「시작값 자동 채우기」 세 구획으로 흩어져
 * 한 글자 차이의 별개 축이 나란히 서 있었다). 키를 그대로 두므로 옛 저장본은 로드만으로 그대로
 * 표시되고, 서버·작성폼은 아무것도 달라지지 않는다.
 */
type StartValueMethod = 'none' | 'fixed' | 'lookup' | 'fromList';

const START_VALUE_METHOD_LABEL: Record<StartValueMethod, string> = {
  none: '사용 안 함',
  fixed: '고정값',
  lookup: '시스템 조회 값 하나',
  fromList: '조회된 행들에서 만들기',
};

const START_VALUE_METHOD_ORDER: StartValueMethod[] = ['none', 'fixed', 'lookup', 'fromList'];

/**
 * 이 위젯에 **실제로 선언된** 방식들 — 서버에서 이기는 순서대로 낸다(첫 항목이 곧 화면에 표시할
 * 방식이자, 복수 선언 경고가 "이게 쓰입니다"라고 지목할 축).
 *
 * 순서의 근거는 서버 코드다: 두 경로가 **같은 순서**로 돈다 — 작성폼 경로 `resolvePrefill`
 * (3-b `injectPrefillBindingValues` → 3-i `injectAutoFillValues`)와 분할 직행 compose
 * (`previewResolveByContext`, 2026-09-05에 같은 순서로 통일 + 소스 스캔 가드 신설). 둘 다
 * **비덮음**이라 먼저 온 채널이 이기고, web 작성폼(new-composed)은 그 응답을
 * `collectWidgetDefaultValues` seed **위에** 덮는다. ⇒ prefillFrom > autoFill > defaultValue.
 *
 * ⚠️ 이 순서가 서버에서 갈리면 화면 경고가 **곧 거짓 안내**가 된다 — 서버 순서를 바꿀 때 여기와
 * 아래 경고 문구를 함께 고칠 것(그 전까지는 두 경로가 같다는 것이 가드 spec으로 지켜진다).
 */
function startValueDeclaredMethods(w: InputWidget): StartValueMethod[] {
  const out: StartValueMethod[] = [];
  if (w.prefillFrom) out.push('lookup');
  if (w.autoFill) out.push('fromList');
  if (w.defaultValue !== undefined) out.push('fixed');
  return out;
}

/**
 * 「시작값」 통합 구획 — 방식 하나를 고르고 그 하위 입력만 노출한다(결정 2026-09-05 대조표 1).
 *
 * 종전에는 같은 질문의 답이 세 자리에 있었다: 본문 단일 패널의 「기본값」(디스크립터) ·
 * 「자동 채움(시스템 조회)」(패널 bespoke JSX, treeOwned 위젯만) · 「시작값 자동 채우기」(디스크립터).
 * 셋을 이 컨트롤 하나로 모으면서 **디스크립터도 하나로 합쳤다**(레지스트리 불변식: 공통 속성 하나 =
 * 디스크립터 하나). 합쳐도 bulk 축 손실이 없다 — 셋 다 개별 값 성격이라 원래 `bulk:false`였다.
 *
 * 방식을 바꾸면 고르지 않은 축의 키는 **지운다**. 남겨 두면 화면은 한 방식만 보여 주는데 서버는
 * 다른 축을 쓰는, 화면이 거짓말하는 상태가 된다(복수 선언 저장본은 경고로 드러낸다).
 */
export function WidgetStartValueControl({ selection, ctx }: WidgetPropControlProps) {
  // 아직 선언이 없는 상태에서 고른 방식 — 저장본에 쓸 값이 아직 없으므로(빈 선언을 쓰면 서버가
  // 경고 후 건너뛰는 죽은 선언이 된다) 화면에만 둔다. 패널은 선택이 바뀌어도 재마운트되지 않으므로
  // (표 칼럼 편집기 주석과 같은 성질) **위젯 id와 함께** 들고 있어야 앞 칸의 방식이 다음 칸에 얹히지 않는다.
  const [pending, setPending] = useState<{ id: string; method: StartValueMethod } | null>(null);
  const w = selection.single;
  // bulk:false — 단일 선택 전용(위젯 개별 값 성격).
  if (!w || w.kind !== 'input') return null;

  // 「조회된 행들에서 만들기」는 반복(행) 안에서 서버 순회가 하강하지 않고, 「시스템 조회 값 하나」는 그에
  // 더해 body 바인딩 파생 위젯이면 출처를 바인딩이 정한다(패널 bespoke 시절의 게이트 그대로).
  const enclosingRepeat = ctx.tree ? findEnclosingRepeatWidget(ctx.tree, w.id) : undefined;
  const canFixed = DEFAULT_VALUE_SCALAR_WIDGETS.has(w.widget);
  const canLookup = !!w.treeOwned && !enclosingRepeat;
  const canFromList = !enclosingRepeat;
  const declared = startValueDeclaredMethods(w);
  const method: StartValueMethod =
    declared[0] ?? (pending && pending.id === w.id ? pending.method : 'none');
  // 지금은 못 고르는 방식이라도 **이미 선언돼 있으면** 후보에 남긴다 — 속성창을 여는 것만으로
  // 선언이 화면에서 사라지면 저작자는 사라진 이유를 알 수 없다(깨진 소스 보존 option과 같은 규약).
  const offered = (m: StartValueMethod): boolean =>
    m === 'none' ||
    declared.includes(m) ||
    (m === 'fixed' ? canFixed : m === 'lookup' ? canLookup : canFromList);
  const setMethod = (next: StartValueMethod): void => {
    setPending({ id: w.id, method: next });
    selection.setAll((x) => {
      if (x.kind !== 'input') return x;
      const y = { ...x };
      delete y.defaultValue;
      delete y.prefillFrom;
      delete y.autoFill;
      return y;
    });
  };
  return (
    <div className="space-y-2">
      <div>
        <label className={PROP_LABEL_CLS}>시작값</label>
        {/* 이 패널의 라벨은 컨트롤과 묶이지 않은 형제 span 관례라(PROP_LABEL_CLS) select에 이름이
            안 붙는다 — title로 접근 이름을 준다. */}
        <select
          title="시작값"
          className={PROP_INPUT_CLS}
          value={method}
          onChange={(e) => setMethod(e.target.value as StartValueMethod)}
        >
          {START_VALUE_METHOD_ORDER.filter(offered).map((m) => (
            <option key={m} value={m}>
              {START_VALUE_METHOD_LABEL[m]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[10px] leading-snug text-gray-400">
          작성 페이지가 처음 열릴 때 이 칸에 채워지는 값입니다 — 작성자가 고쳐 쓸 수 있습니다.
        </p>
        {declared.length > 1 && (
          <p className="mt-1 text-[11px] leading-4 text-amber-600">
            이 칸에 값 지정이 {declared.length}개 남아 있습니다 — 실제로는 「
            {START_VALUE_METHOD_LABEL[declared[0]]}」이 쓰입니다. 방식을 다시 고르면 나머지는
            지워집니다.
          </p>
        )}
        {!canLookup && !w.prefillFrom ? (
          <p className="mt-1 text-[10px] leading-snug text-gray-400">
            {enclosingRepeat
              ? '반복 영역 안에서는 시스템 조회 값을 칸마다 지정하지 않습니다 — 행 값은 반복 소스가 정합니다.'
              : '시스템 조회 값은 이 칸의 데이터 연결(본문 바인딩)이 정합니다 — 여기서 고르지 않습니다.'}
          </p>
        ) : null}
      </div>
      {method === 'fixed' ? (
        canFixed ? (
          <StartValueFixedInput selection={selection} ctx={ctx} />
        ) : (
          <p className="text-[11px] leading-4 text-amber-600">
            이 위젯 종류는 고정값을 저작할 수 없습니다(값 형태가 복합) — 저장된 값은 그대로
            보존되며, 다른 방식을 고르면 지워집니다.
          </p>
        )
      ) : null}
      {method === 'lookup' ? (
        <div>
          <CatalogPrefillPicker
            catalog={ctx.catalog ?? []}
            value={w.prefillFrom}
            onChange={(v) =>
              selection.setAll((x) => {
                if (x.kind !== 'input') return x;
                const next = { ...x };
                if (v) next.prefillFrom = v;
                else delete next.prefillFrom;
                return next;
              })
            }
          />
          <p className="mt-1 text-[10px] leading-snug text-gray-400">
            시스템 조회 값 하나를 작성 시 채우고, 작성자가 편집한 최종값이 저장됩니다.
          </p>
          {!canLookup && (
            <p className="mt-1 text-[11px] leading-4 text-amber-600">
              이 칸은 시스템 조회 값을 여기서 정하는 자리가 아닙니다 — 선언은 보존되지만 값이
              채워지지 않을 수 있습니다.
            </p>
          )}
        </div>
      ) : null}
      {method === 'fromList' ? (
        <StartValueFromListFields
          selection={selection}
          ctx={ctx}
          keepMethod={() => setPending({ id: w.id, method: 'fromList' })}
        />
      ) : null}
    </div>
  );
}

/**
 * 역전파(정본 저장) — 가용성·스코프는 prefill + 카탈로그 writer로 자동 판정(writeBackAvailability).
 * 단일: WriteBackSelector(비활성 사유 포함) — dataDef 표 위젯은 데이터셋 영속 설정 안내로 대체
 * (2026-07-15 채널 이원화 방지). 복수: 전원 활성 가능해야 select 노출, 하나라도 비활성이면 사유
 * 한 줄(formTable 셀 다중 뷰와 동형 — 2026-07-13 사용자 제보 후 규약).
 */
function WidgetWriteBackControl({ selection, ctx }: WidgetPropControlProps) {
  const single = selection.single;
  if (single && single.kind === 'input') {
    // 전역 변수 파생 위젯 — 영속 정본은 변수의 기억 범위(rememberScope, `__var:` 키). 위젯
    // 역전파를 함께 노출하면 제네릭 `input:` 키 이중 기록으로 채널 이원화(dataDef 표와 동일
    // 유형, 2026-07-19 사용자 지적). 숨기고 안내로 대체(서버 수집도 변수 키 무시).
    const asVariable = (ctx.variables ?? []).find((v) => v.key === single.bindingKey);
    if (asVariable) {
      const rememberLabel =
        asVariable.rememberScope === 'site'
          ? '현장 공유로 기억'
          : asVariable.rememberScope === 'site-per-report'
            ? '현장×보고서 타입별로 기억'
            : '기억 안 함(기본값·자동 채움만)';
      return (
        <div>
          <label className={PROP_LABEL_CLS}>저장·재사용</label>
          <p className="text-[11px] leading-4 text-gray-400">
            이 위젯은 양식 전역 변수({asVariable.key})의 입력이며, 저장·재사용은 변수의 기억
            범위가 정본입니다 — 현재: {rememberLabel}. 변경은 전역 변수 설정에서 하세요(위젯
            역전파 미적용).
          </p>
        </div>
      );
    }
    if (single.widget === 'table') {
      // dataDef 표의 저장 정책은 **데이터셋 정의**(persistScope + 칼럼별 저장 제외)가 정본 —
      // 위젯 역전파를 함께 노출하면 채널이 이원화돼 혼란(2026-07-15 사용자 지적). 여기선
      // 숨기고 현재 정의의 영속 설정을 읽기 안내로만 표시(서버도 dataDef/dataSource 키
      // 위젯의 writeBack을 무시 — 과거 저작으로 남은 값 무해화).
      const def = (ctx.dataDefs ?? []).find((d) => d.key === single.bindingKey);
      const scopeLabel =
        def?.persistScope === 'site'
          ? '현장'
          : def?.persistScope === 'organization'
            ? '조직'
            : null;
      const excluded = (def?.fields ?? [])
        .filter((f) => f.excludeFromPersist)
        .map((f) => f.label || f.key);
      return (
        <div>
          <label className={PROP_LABEL_CLS}>저장·재사용</label>
          <p className="text-[11px] leading-4 text-gray-400">
            {scopeLabel
              ? `이 표의 편집값은 내장 데이터셋 정의의 영속 설정에 따라 ${scopeLabel}에 저장·재사용됩니다.`
              : '이 표는 내장 데이터셋 정의를 따르며, 영속 미지정이라 이 보고서에만 저장됩니다.'}
            {excluded.length > 0 ? ` 저장 제외 칼럼: ${excluded.join(', ')}.` : ''}{' '}
            변경은 내장 데이터셋 설정에서 하세요(위젯 역전파 미적용).
          </p>
        </div>
      );
    }
    return (
      <>
        <WriteBackSelector
          writeBack={single.writeBack}
          // combobox 합성 경로는 명시 prefillFrom 다음·파생 fallback보다 앞 — 서버 수집(ref.prefillFrom
          // 합성이 body 바인딩 fallback보다 우선)과 가용성 판정이 일치해야 한다.
          prefillFrom={effectiveWriteBackPrefill(single, ctx)}
          pairedCoverage={keyedPairingCoverage(single, ctx)}
          // 반복 행 안이면 서버 행 스코프 게이트 미러(제네릭 저장 없음) — 최상위와 같은 문구를
          // 내면 거짓 안내가 된다(2026-09-14 은닉 감사 3차).
          rowScope={rowScopeWriteBackJudgement(single, ctx)}
          catalog={ctx.catalog}
          labelCls={PROP_LABEL_CLS}
          selectCls={PROP_INPUT_CLS}
          onChange={(v) =>
            selection.setAll((x) => {
              if (x.kind !== 'input') return x;
              const next = { ...x };
              if (v) next.writeBack = v;
              else delete next.writeBack;
              return next;
            })
          }
        />
        <RepeatRowFindingWriteBackControl widget={single} ctx={ctx} />
      </>
    );
  }

  // 복수 — 전원 활성 가능해야 select 노출(하나라도 비활성이면 사유 한 줄). prefill 판정은 단일과
  // 같은 체인(effectiveWriteBackPrefill). keyed upsert 페어링 커버 위젯도 비활성 취급(일괄
  // 지정 차단 — 기존 설정 해제는 단일 선택에서).
  const inputs = selection.widgets.filter((w): w is InputWidget => w.kind === 'input');
  const wbAvails = inputs.map((x) =>
    writeBackAvailability(effectiveWriteBackPrefill(x, ctx), ctx.catalog),
  );
  const wbPaired = inputs
    .map((x) => keyedPairingCoverage(x, ctx))
    .find((p): p is { keyLabel: string } => p !== null);
  // 반복 행 안 위젯이 하나라도 서버 행 스코프 게이트 미충족이면 일괄 설정 제외(단일과 같은 판정).
  // 판정은 한 번만 순회(wbRowJudgements) — 아래 '다음 보고서 기본값' 노출 조건도 같은 결과를 읽는다.
  const wbRowJudgements = inputs.map((x) => rowScopeWriteBackJudgement(x, ctx));
  const wbRowIneligible = wbRowJudgements.find(
    (j): j is Extract<RowScopeWriteBackJudgement, { kind: 'ineligible' }> =>
      j?.kind === 'ineligible',
  );
  const wbAllAvailable =
    inputs.length > 0 &&
    !wbPaired &&
    !wbRowIneligible &&
    wbAvails.every((a) => a.kind !== 'disabled');
  const wbDisabledReason = wbPaired
    ? `'${wbPaired.keyLabel}'의 선택 전파로 연결된 위젯이 있어 일괄 설정에서 제외됩니다(그 위젯의 역전파에 함께 저장 — 별도 지정 불필요)`
    : wbRowIneligible
      ? wbRowIneligible.reason
      : wbAvails.find((a) => a.kind === 'disabled')?.reason;
  // 카탈로그 writer는 entry 선언 스코프(미선언=현장)의 정본만 — 하나라도 반대 스코프면 그 옵션 비활성.
  const wbCatalogScopes = wbAvails.flatMap((a) => (a.kind === 'catalog' ? [a.scope ?? 'site'] : []));
  const wbOrgDisabled = wbCatalogScopes.includes('site');
  const wbSiteDisabled = wbCatalogScopes.includes('organization');
  const wb = fold(inputs, (w) => w.writeBack?.scope ?? '');
  // '다음 보고서 기본값' 체크박스 — 전원 카탈로그(도메인 writer) 라우팅 + 역전파 지정(비혼합)일 때만
  // 노출(단일 WriteBackSelector 노출 조건과 동형 — 제네릭 경로 위젯 미노출). 혼합은 indeterminate.
  // 반복 행 안 위젯이 섞이면 미노출 — 서버 행 스코프 패스는 useAsDefault를 읽지 않는다(단일 동형).
  const wbAllCatalog =
    inputs.length > 0 &&
    wbAvails.every((a) => a.kind === 'catalog') &&
    wbRowJudgements.every((j) => j === undefined);
  const wbUse = fold(inputs, (w) => !!w.writeBack?.useAsDefault);
  const WB_MIXED = '__mixed__';
  if (!wbAllAvailable) {
    return (
      <p className="text-[11px] leading-snug text-gray-400">
        역전파: {wbDisabledReason ?? '일부 위젯이 역전파할 수 없어 일괄 설정에서 제외됩니다'}
      </p>
    );
  }
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        역전파(정본 저장)
        {wb.mixed ? <MixedTag /> : null}
      </label>
      <select
        className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
        value={wb.mixed ? WB_MIXED : (wb.value ?? '')}
        onChange={(e) => {
          const v = e.target.value;
          if (v === WB_MIXED) return;
          selection.setAll((x) => {
            if (x.kind !== 'input') return x;
            const next = { ...x };
            // scope 전환 시 useAsDefault 보존(단일과 동형 불변 갱신) — '없음'은 전체 해제.
            if (v === 'site' || v === 'organization') next.writeBack = { ...x.writeBack, scope: v };
            else delete next.writeBack;
            return next;
          });
        }}
      >
        {wb.mixed ? (
          <option value={WB_MIXED} disabled>
            — 혼합 —
          </option>
        ) : null}
        <option value="">없음(보고서만)</option>
        <option value="site" disabled={wbSiteDisabled}>
          현장에 저장
        </option>
        <option value="organization" disabled={wbOrgDisabled}>
          조직에 저장
        </option>
      </select>
      {wbAllCatalog && !wb.mixed && wb.value ? (
        <label className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            ref={(el) => {
              if (el) el.indeterminate = wbUse.mixed;
            }}
            checked={!wbUse.mixed && wbUse.value === true}
            onChange={(e) => {
              const checked = e.target.checked;
              selection.setAll((x) => {
                if (x.kind !== 'input' || !x.writeBack) return x;
                return { ...x, writeBack: { ...x.writeBack, useAsDefault: checked || undefined } };
              });
            }}
          />
          다음 보고서 기본값으로 사용
          {wbUse.mixed ? <MixedTag /> : null}
        </label>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 표 위젯(widget==='table') 전용 속성 컨트롤
//
// 종전에는 단일 속성 패널 인라인 JSX였다 — 그래서 표가 **양식 표(formTable) 셀 안**에 있으면
// 어느 패널에서도 편집할 수 없었다(셀 패널에 table 분기가 없었다, 2026-08-07 저작 UI 공백 감사).
// 디스크립터로 올려 단일 패널과 셀 패널이 같은 Control을 공유한다(TABLE_WIDGET_PROP_ORDER).
//
// 여기 있는 건 **화면(JSX)**뿐이다 — 키·노출 순서·bulk 여부·게이트는 tableWidgetProps(순수 층,
// spec 동반)가 소유한다. 새 표 축은 그 파일에 키를 더하는 것부터 시작할 것(Record 3개가 누락을
// tsc로 잡는다).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 선택지·편집 대상이 **그 위젯 자신의 `fields[]`**에서 나오는 표 축(구분 그룹·행 클릭 토글·칼럼
 * 편집기)의 앵커 위젯 — 복수 선택은 위젯마다 칼럼 집합이 달라 일괄 적용이 성립하지 않는다
 * (그래서 이 셋은 bulk:false). 앵커가 표 위젯이 아니면 null → Control 미렌더.
 */
function tableAnchorWidget(selection: WidgetPropSelection): InputWidget | null {
  const w = selection.single ?? selection.widgets[0];
  return w && isTableWidget(w) ? w : null;
}

/** 칼럼 헤더 숨김 — 표시 전용(데이터·양식 출력 무영향). 복수는 tri-state. */
function WidgetTableHideHeaderControl({ selection }: WidgetPropControlProps) {
  const hide = fold(selection.widgets, readWidgetTableHideHeader);
  return (
    <div>
      <label className="flex items-center gap-2 text-xs text-gray-600">
        <TriStateCheckbox
          checked={!!hide.value}
          mixed={hide.mixed}
          onChange={(next) =>
            selection.setAll((x) =>
              x.kind === 'input' ? { ...x, hideHeader: next || undefined } : x,
            )
          }
        />
        칼럼 헤더 숨김
        {hide.mixed ? <MixedTag /> : null}
      </label>
      <p className="mt-1 text-[11px] leading-4 text-gray-400">
        목록형 체크리스트(수시 평가 구분 등)처럼 헤더가 군더더기일 때. 표시 전용 —
        데이터·양식 출력 무영향, 열 너비는 유지됩니다.
      </p>
    </div>
  );
}

/** 테두리 프리셋(행 구분선/격자/없음) — 작성 표시 전용. 기본('rows')=키 제거. */
function WidgetTableBorderControl({ selection }: WidgetPropControlProps) {
  const border = fold(selection.widgets, readWidgetTableBorder);
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        테두리(작성 표시 전용)
        {border.mixed ? <MixedTag /> : null}
      </label>
      <select
        className={PROP_INPUT_CLS}
        value={border.mixed ? MIXED_OPTION : ((border.value as string) ?? 'rows')}
        onChange={(e) => {
          if (e.target.value === MIXED_OPTION) return;
          const v = e.target.value as 'rows' | 'grid' | 'none';
          selection.setAll((x) =>
            x.kind === 'input' ? { ...x, tableBorder: v === 'rows' ? undefined : v } : x,
          );
        }}
      >
        {border.mixed && (
          <option value={MIXED_OPTION} disabled>
            — 혼합 —
          </option>
        )}
        <option value="rows">행 구분선 (기본)</option>
        <option value="grid">격자 (모든 칸)</option>
        <option value="none">없음 (외곽 박스 포함)</option>
      </select>
    </div>
  );
}

/** 작성 표시 단수 — 2 이상만 저장(1=기본이라 키 제거). 양식 출력에는 영향이 없다. */
function WidgetTableDisplayColumnsControl({ selection }: WidgetPropControlProps) {
  const cols = fold(selection.widgets, readWidgetTableDisplayColumns);
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        작성 표시 단수(양식 출력 무영향)
        {cols.mixed ? <MixedTag /> : null}
      </label>
      <input
        type="number"
        min={1}
        className="w-20 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
        value={cols.mixed ? '' : ((cols.value as string) ?? '')}
        placeholder="1"
        onChange={(e) => {
          const n = Number(e.target.value);
          selection.setAll((x) =>
            x.kind === 'input'
              ? { ...x, displayColumns: Number.isFinite(n) && n >= 2 ? n : undefined }
              : x,
          );
        }}
      />
    </div>
  );
}

/** 다단 채움 방향 — displayColumns >= 2일 때만 의미가 있어 그때만 노출(applicable 게이트). */
function WidgetTableFillDirectionControl({ selection }: WidgetPropControlProps) {
  const dir = fold(selection.widgets, readWidgetTableFillDirection);
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        채움 방향(작성 표시 전용)
        {dir.mixed ? <MixedTag /> : null}
      </label>
      <select
        className={PROP_INPUT_CLS}
        value={dir.mixed ? MIXED_OPTION : ((dir.value as string) ?? 'row')}
        onChange={(e) => {
          if (e.target.value === MIXED_OPTION) return;
          const v = e.target.value;
          selection.setAll((x) => {
            if (x.kind !== 'input') return x;
            const next = { ...x };
            if (v === 'column') next.fillDirection = 'column';
            else delete next.fillDirection;
            return next;
          });
        }}
      >
        {dir.mixed && (
          <option value={MIXED_OPTION} disabled>
            — 혼합 —
          </option>
        )}
        <option value="row">너비 우선(가로로 먼저)</option>
        <option value="column">높이 우선(세로로 먼저)</option>
      </select>
      <p className="mt-1 text-[11px] leading-4 text-gray-400">
        2단 이상일 때 항목을 가로로 먼저 채울지(왼→오·위→아래) 세로로 먼저 채울지(한 단 위→아래
        먼저) 선택. 구 SMS 점검현황(좌 관리·우 현장 각 세로 나열)은 높이 우선.
      </p>
    </div>
  );
}

/** 구분 그룹 표시 칼럼 — 선택지가 이 위젯의 유효 칼럼(사본 ?? 정본)이라 단일 앵커 전용(bulk 미대상). */
function WidgetTableGroupByFieldControl({ selection, ctx }: WidgetPropControlProps) {
  const w = tableAnchorWidget(selection);
  if (!w) return null;
  return (
    <div>
      <label className={PROP_LABEL_CLS}>구분 그룹 표시(작성 전용)</label>
      <select
        className={PROP_INPUT_CLS}
        value={readWidgetTableGroupByField(w)}
        onChange={(e) => {
          const v = e.target.value;
          selection.setAll((x) => {
            if (x.kind !== 'input') return x;
            const next = { ...x };
            if (v) next.groupByField = v;
            else delete next.groupByField;
            // groupValues(deprecated 수동 목록) 잔존분 정리 — 그룹 틀은 렌더가 내장
            // 데이터셋 기본 행에서 자동 파생(2026-07-15 채널 일원화, 신규 저작 불가).
            delete next.groupValues;
            return next;
          });
        }}
      >
        <option value="">사용 안 함</option>
        {/* 참조 모드(사본 없음)에서도 정본 칼럼이 후보로 떠야 한다 — 렌더와 같은 해소 규칙.
            기간(date-range) 칼럼만 뺀다: 그룹 식별은 값을 문자열로 접어 비교하는데 기간은
            객체라 모든 행이 한 그룹으로 뭉개진다(고를 수는 있는데 동작하지 않는 선택지). */}
        {resolveTableFields(w, ctx.dataDefs)
          .filter((f) => f.widget !== 'date-range')
          .map((f) => (
            <option key={f.key} value={f.key}>
              {f.label || f.key}
            </option>
          ))}
      </select>
      <p className="mt-1 text-[11px] leading-4 text-gray-400">
        지정한 필드 값별로 그룹 제목 + 표로 나눠 표시(구 SMS 점검현황 동형). 그 칼럼은
        숨겨지고 그룹별 행 추가에 값이 자동 지정됩니다. 그룹 틀은 내장 데이터셋 기본
        행의 구분값에서 자동 유지(행 전부 삭제에도 잔존 — 그룹 구성 변경은 데이터셋
        기본 행에서). 데이터·양식 출력·현장 저장은 단일 목록 그대로이며, 표시 단수는
        각 그룹 안에서 적용됩니다.
      </p>
    </div>
  );
}

/** 행 클릭 체크 토글 대상 칼럼 — 후보가 이 위젯의 유효 checkbox 칼럼이라 단일 앵커 전용(bulk 미대상). */
function WidgetTableRowClickToggleControl({ selection, ctx }: WidgetPropControlProps) {
  const w = tableAnchorWidget(selection);
  if (!w) return null;
  return (
    <div>
      <label className={PROP_LABEL_CLS}>행 클릭 체크 토글</label>
      <select
        className={PROP_INPUT_CLS}
        value={readWidgetTableRowClickToggleField(w)}
        onChange={(e) => {
          const v = e.target.value;
          selection.setAll((x) => {
            if (x.kind !== 'input') return x;
            const next = { ...x };
            if (v) next.rowClickToggleField = v;
            else delete next.rowClickToggleField;
            return next;
          });
        }}
      >
        <option value="">사용 안 함</option>
        {/* 참조 모드(사본 없음)에서도 정본 칼럼이 후보로 떠야 한다 — 렌더와 같은 해소 규칙. */}
        {resolveTableFields(w, ctx.dataDefs)
          .filter((f) => f.widget === 'checkbox')
          .map((f) => (
            <option key={f.key} value={f.key}>
              {f.label || f.key}
            </option>
          ))}
      </select>
      <p className="mt-1 text-[11px] leading-4 text-gray-400">
        행의 읽기전용 표시 셀(예: 잠긴 점검항목)을 클릭해도 이 체크박스가 토글됩니다
        (구 SMS &quot;항목 클릭=점검 토글&quot; 동형). 편집 가능한 셀 클릭은 입력이 우선입니다.
      </p>
    </div>
  );
}

/**
 * 칼럼 목록 — 표시 축(순서·너비·읽기전용) + 칼럼 선언(라벨·셀 위젯·접기·옵션·검색 제안).
 * 편집기 본체는 TableWidgetColumnsEditor 재사용(정의 위 JSDoc이 판단 근거 정본).
 *
 * 편집 대상이 앵커 위젯의 `fields[]`라 단일 앵커 전용(bulk 미대상) — 복수 선택에 일괄 적용하면
 * 서로 다른 칼럼 집합에 남의 순서·너비를 덮어쓴다.
 */
function WidgetTableColumnsControl({ selection, ctx }: WidgetPropControlProps) {
  const w = tableAnchorWidget(selection);
  if (!w) return null;
  return (
    <TableWidgetColumnsEditor
      // 위젯이 바뀌면 인스턴스를 갈아 로컬 상태(펼친 칼럼·드리프트 배너 접기·detach 차단 안내)를
      // 초기화한다 — 패널은 선택만 바뀌고 재마운트되지 않아, key 없이는 앞 위젯의 UI 상태가
      // 다음 위젯에 그대로 얹힌다(다른 표에 없는 칼럼이 펼쳐진 채로 보이는 등).
      key={w.id}
      w={w}
      // 앵커 위젯 1개에만 닿는 갱신 채널 — id는 selection이 이미 알고 있어 무시한다.
      onWidgetUpdate={(_id, updater) => selection.setAll(updater)}
      // 참조/동결 판정(dataDefs)·파급 카운트(tree)·정본 쓰기/detach(onTemplateUpdate 등) 공급원.
      ctx={ctx}
      labelCls={PROP_LABEL_CLS}
      inputCls={PROP_INPUT_CLS}
    />
  );
}

/**
 * 공통 위젯 속성 디스크립터 정본 — ⚠️ 새 공통 속성은 반드시 여기에 추가(복수 패널 자동 노출).
 * BulkWidgetProperties/단일 패널에 속성 JSX 직접 추가 금지(단일-복수 비대칭 누락 사고의 원인).
 */
const WIDGET_PROP_DESCRIPTORS: Record<WidgetPropKey, WidgetPropertyDescriptor> = {
  hiddenOnView: {
    key: 'hiddenOnView',
    bulk: true,
    // 숨김 오버라이드는 input 전용 — design(제목·구분선·여백)은 항상 전폭(shared 규약), 섹션은
    // 자체 설정만(단일 패널과 동일 게이트).
    applicable: (w) => w.kind === 'input',
    read: readWidgetHidden,
    Control: WidgetHiddenOnViewControl,
  },
  viewOrder: {
    key: 'viewOrder',
    // 단일 전용 — 형제 집합 단위 조작이라 복수 선택은 대상 집합이 정해지지 않는다.
    bulk: false,
    // 되돌릴 오버라이드가 실제로 있을 때만 노출(전 kind 대상 — 순서는 input 전용 축이 아니다).
    // 트리 갱신 채널이 없는 패널(표 셀 등)에선 조작 자체가 불가능하므로 함께 게이트.
    applicable: (w, ctx) => !!ctx.onTreeUpdate && readWidgetViewOrder(w, ctx),
    read: readWidgetViewOrder,
    Control: WidgetViewOrderControl,
  },
  spanOnView: {
    key: 'spanOnView',
    // 같은 배치를 여러 위젯에 한 번에 주는 게 자연스러운 레이아웃 축(숨김과 동형) — bulk 대상.
    bulk: true,
    // kind가 아니라 **배치**로 게이트한다 — 다단 섹션(group)의 자식일 때만 rc가 span을 읽는다
    // (spanHostGroupOf 주석: gridded가 되는 유일한 자리). 트리 없으면 부모를 알 수 없어 미노출.
    applicable: (w, ctx) => !!spanHostGroupOf(ctx.tree, w.id),
    read: readWidgetSpanChoice,
    Control: WidgetSpanOnViewControl,
  },
  required: {
    key: 'required',
    bulk: true,
    applicable: (w) => w.kind === 'input',
    read: readWidgetRequired,
    Control: WidgetRequiredControl,
  },
  mode: {
    key: 'mode',
    bulk: true,
    applicable: (w) => w.kind === 'input',
    read: readWidgetMode,
    Control: WidgetModeControl,
  },
  writeBack: {
    key: 'writeBack',
    bulk: true,
    applicable: (w) => w.kind === 'input',
    read: readWidgetWriteBackScope,
    Control: WidgetWriteBackControl,
  },
  startValue: {
    key: 'startValue',
    // 위젯 개별 값 성격(label과 동급) — 여러 위젯에 같은 시작값을 일괄 주입할 일이 사실상 없고,
    // 오조작 시 선택 전 위젯 값이 한 값으로 통일되는 파급이 커 bulk 제외. 합치기 전 세 축
    // (defaultValue·autoFill)도 같은 근거로 bulk 제외였다.
    bulk: false,
    // 종전 두 게이트의 **합집합**이다 — 고정값은 스칼라 위젯만, 목록 파생은 반복 밖만이라
    // 어느 한쪽만 가능한 위젯도 구획 자체는 떠야 한다(방식 후보는 Control이 다시 거른다).
    // 이미 선언이 있으면 게이트와 무관하게 노출한다: 속성창을 여는 것만으로 선언이 화면에서
    // 사라지면 저작자가 그 이유를 알 수 없다.
    applicable: (w, ctx) =>
      w.kind === 'input' &&
      (DEFAULT_VALUE_SCALAR_WIDGETS.has(w.widget) ||
        !ctx.tree ||
        !findEnclosingRepeatWidget(ctx.tree, w.id) ||
        startValueDeclaredMethods(w).length > 0),
    read: readWidgetStartValue,
    Control: WidgetStartValueControl,
  },
  labelSuffixFrom: {
    key: 'labelSuffixFrom',
    bulk: true,
    // registered=실폼 라벨을 host 위젯 구현이 소유(rc 병기 미적용), dataSourceRows=필드 매핑
    // 전용 패널이라 제외. 후보 산출에 tree 필요.
    applicable: (w, ctx) =>
      !!ctx.tree && w.kind === 'input' && w.widget !== 'registered' && w.widget !== 'dataSourceRows',
    read: readWidgetLabelSuffixFrom,
    Control: WidgetLabelSuffixFromControl,
  },
  // ── 라벨 표시 계열(2026-08-24 편입) — 구 LabelPresentationControls는 단일 패널 bespoke라
  //    복수 선택에서 통째로 빠져 있었다(장소·세부장소 동시 선택 시 라벨 위치 부재 제보).
  labelPlacement: {
    key: 'labelPlacement',
    // 같은 라벨 배치를 여러 칸에 한 번에 주는 게 자연스러운 레이아웃 축(spanOnView 동형).
    bulk: true,
    applicable: (w) => w.kind === 'input',
    read: readWidgetLabelPlacement,
    Control: WidgetLabelPlacementControl,
  },
  labelAlign: {
    key: 'labelAlign',
    bulk: true,
    // rc는 top 배치에서만 정렬을 소비한다(side=인라인, none=라벨 자체가 없음) — 안 먹는 자리에
    // 컨트롤을 띄우지 않는다. 구 단일 패널의 `placement === 'top' &&` 조건을 그대로 옮긴 것이라
    // 단일 동작은 불변이고, 복수는 전원 top일 때만 노출된다.
    applicable: (w) => w.kind === 'input' && readWidgetLabelPlacement(w) === 'top',
    read: readWidgetLabelAlign,
    Control: WidgetLabelAlignControl,
  },
  labelStyle: {
    key: 'labelStyle',
    bulk: true,
    // 라벨이 없으면(none) 폰트를 먹일 대상이 없다 — 무의미한 컨트롤을 숨긴다.
    applicable: (w) => w.kind === 'input' && readWidgetLabelPlacement(w) !== 'none',
    read: readWidgetLabelStyleKey,
    Control: WidgetLabelStyleControl,
  },
  labelHint: {
    key: 'labelHint',
    // 칸마다 다른 문구(placeholder·label과 같은 개별 값 성격) — defaultValue와 같은 근거로 bulk 제외.
    bulk: false,
    // 보조 설명은 rc에서 라벨 안에 그려지므로 라벨이 없으면(none) 함께 사라진다 — 그래서 숨긴다.
    applicable: (w) => w.kind === 'input' && readWidgetLabelPlacement(w) !== 'none',
    read: readWidgetLabelHint,
    Control: WidgetLabelHintControl,
  },
  visibleWhen: {
    key: 'visibleWhen',
    bulk: true,
    // 전 배치 위젯 공통 base 속성(P3) — 후보 산출에 tree 필요. 값 스코프 혼합 게이트는 Control이
    // 사유 한 줄로 처리(역전파 비활성 사유와 동형).
    applicable: (_w, ctx) => !!ctx.tree,
    read: readWidgetVisibleWhen,
    Control: WidgetVisibleWhenControl,
  },
  enabledWhen: {
    key: 'enabledWhen',
    bulk: true,
    // disabled는 rc가 렌더하는 입력 컨트롤에만 의미(컨테이너/디자인 위젯 무효과 — shared JSDoc) —
    // input 전용. 후보 산출에 tree 필요(표시 조건과 공용 스코프 게이트).
    applicable: (w, ctx) => !!ctx.tree && w.kind === 'input',
    read: readWidgetEnabledWhen,
    Control: WidgetEnabledWhenControl,
  },
  inputFormat: {
    key: 'inputFormat',
    bulk: true,
    // 입력 형식 강제는 rc case 'text'만 소비 — text 위젯 전용 노출(복수는 전원 text일 때만).
    applicable: (w) => w.kind === 'input' && w.widget === 'text',
    read: readWidgetInputFormat,
    Control: WidgetInputFormatControl,
  },
  displayVariant: {
    key: 'displayVariant',
    bulk: true,
    // rc는 트리 radio case에서만 이 축을 소비 — radio 위젯 전용 노출(복수는 전원 radio일 때만).
    // 표시 모드는 별도 축이라 게이트하지 않는다(readonly면 rc가 공통 값 표시 분기로 먼저 빠져 무해).
    applicable: (w) => w.kind === 'input' && w.widget === 'radio',
    read: readWidgetDisplayVariant,
    Control: WidgetDisplayVariantControl,
  },
  editEntry: {
    key: 'editEntry',
    bulk: true,
    // 클릭 진입은 rc text/textarea(단일 값) + textarea-list/text-list(항목 단위) case만 소비 —
    // 그 위젯 전용 노출. readonly/hidden 모드에선 무의미하지만 모드는 별도 축이라 게이트하지
    // 않는다(rc가 readonly 분기에서 먼저 빠져 무해).
    applicable: (w) =>
      w.kind === 'input' &&
      (w.widget === 'text' ||
        w.widget === 'textarea' ||
        w.widget === 'textarea-list' ||
        w.widget === 'text-list'),
    read: readWidgetEditEntry,
    Control: WidgetEditEntryControl,
  },
  collapseLines: {
    key: 'collapseLines',
    // 같은 줄 수를 여러 칸에 일괄 적용하는 게 자연스러운 표시 축(editEntry 동형) — bulk 대상.
    bulk: true,
    // rc는 text/textarea(단일 값) + textarea-list/text-list(항목 단위) 표시에서만 접기를 소비 —
    // editEntry와 같은 위젯 집합. 모드는 별도 축이라 게이트하지 않는다(입력 컨트롤에선 무해 무시).
    applicable: (w) =>
      w.kind === 'input' &&
      (w.widget === 'text' ||
        w.widget === 'textarea' ||
        w.widget === 'textarea-list' ||
        w.widget === 'text-list'),
    read: readWidgetCollapseLines,
    Control: WidgetCollapseLinesControl,
  },
  displayFormat: {
    key: 'displayFormat',
    // 위젯 개별 배선(rangesKey·단위 경로·케이스 맵이 위젯마다 다름) — suggestionsFrom과 같은 이유로
    // bulk 제외. 여러 위젯에 같은 형식을 일괄 주입하면 서로 다른 값에 같은 구간표를 물리게 된다.
    bulk: false,
    // rc의 **공통 readonly 텍스트 표시 분기**를 타는 위젯만 — 자기 case로 렌더하거나
    // (table/sortableSelect/image/annotated-image) 호스트가 그리는 위젯(registered/dataSourceRows)은
    // 그 분기를 타지 않아 형식을 소비하지 않는다. 안 먹는 자리에 컨트롤을 띄우지 않는다.
    // 표시 모드(읽기 전용 여부)는 별도 축이라 게이트하지 않는다 — 적용 여부는 Control이 힌트로 명시.
    applicable: (w) => w.kind === 'input' && !FORMAT_UNSUPPORTED_WIDGETS.has(w.widget),
    read: readWidgetFormatKind,
    Control: WidgetDisplayFormatControl,
  },
  suggestionsFrom: {
    key: 'suggestionsFrom',
    // 위젯 개별 배선(전파 대상 bindingKey·필터 필드가 위젯마다 다름) — defaultValue와 같은 이유로
    // bulk 제외. 여러 위젯에 같은 전파 매핑을 일괄 주입하면 서로 같은 대상에 쓰는 배선이 된다.
    bulk: false,
    // rc는 text·textarea-list·text-list 케이스에서만 제안을 소비(그 외 위젯은 선언이 있어도 무시,
    // shared JSDoc) — 그 위젯 전용 노출. textarea(단일 여러 줄)는 rc 미소비라 제외.
    applicable: (w) =>
      w.kind === 'input' &&
      (w.widget === 'text' || w.widget === 'textarea-list' || w.widget === 'text-list'),
    read: readWidgetSuggestionsFrom,
    Control: WidgetSuggestionsFromControl,
  },
  // ── 표 위젯 전용 축 — bulk 여부·노출 게이트는 tableWidgetProps(순수 층)가 정본이다.
  //    여기서 직접 판정식을 쓰지 말 것: spec이 그 파일의 술어만 검사하므로 갈라지면 검증이 헛돈다.
  //    게이트에 ctx.dataDefs를 넘긴다 — 참조 모드(사본 없음, 정본 live 참조) 위젯의 칼럼 의존
  //    게이트가 정본 칼럼으로 판정돼야 한다(P3 정규화 직후 축 소실 방지).
  tableHideHeader: {
    key: 'tableHideHeader',
    bulk: TABLE_WIDGET_PROP_BULK.tableHideHeader,
    applicable: (w, ctx) => TABLE_WIDGET_PROP_APPLICABLE.tableHideHeader(w, ctx.dataDefs),
    read: readWidgetTableHideHeader,
    Control: WidgetTableHideHeaderControl,
  },
  tableBorder: {
    key: 'tableBorder',
    bulk: TABLE_WIDGET_PROP_BULK.tableBorder,
    applicable: (w, ctx) => TABLE_WIDGET_PROP_APPLICABLE.tableBorder(w, ctx.dataDefs),
    read: readWidgetTableBorder,
    Control: WidgetTableBorderControl,
  },
  tableDisplayColumns: {
    key: 'tableDisplayColumns',
    bulk: TABLE_WIDGET_PROP_BULK.tableDisplayColumns,
    applicable: (w, ctx) => TABLE_WIDGET_PROP_APPLICABLE.tableDisplayColumns(w, ctx.dataDefs),
    read: readWidgetTableDisplayColumns,
    Control: WidgetTableDisplayColumnsControl,
  },
  tableFillDirection: {
    key: 'tableFillDirection',
    bulk: TABLE_WIDGET_PROP_BULK.tableFillDirection,
    applicable: (w, ctx) => TABLE_WIDGET_PROP_APPLICABLE.tableFillDirection(w, ctx.dataDefs),
    read: readWidgetTableFillDirection,
    Control: WidgetTableFillDirectionControl,
  },
  tableGroupByField: {
    key: 'tableGroupByField',
    bulk: TABLE_WIDGET_PROP_BULK.tableGroupByField,
    applicable: (w, ctx) => TABLE_WIDGET_PROP_APPLICABLE.tableGroupByField(w, ctx.dataDefs),
    read: readWidgetTableGroupByField,
    Control: WidgetTableGroupByFieldControl,
  },
  tableRowClickToggleField: {
    key: 'tableRowClickToggleField',
    bulk: TABLE_WIDGET_PROP_BULK.tableRowClickToggleField,
    applicable: (w, ctx) => TABLE_WIDGET_PROP_APPLICABLE.tableRowClickToggleField(w, ctx.dataDefs),
    read: readWidgetTableRowClickToggleField,
    Control: WidgetTableRowClickToggleControl,
  },
  tableColumns: {
    key: 'tableColumns',
    bulk: TABLE_WIDGET_PROP_BULK.tableColumns,
    applicable: (w, ctx) => TABLE_WIDGET_PROP_APPLICABLE.tableColumns(w, ctx.dataDefs),
    read: readWidgetTableColumnCount,
    Control: WidgetTableColumnsControl,
  },
};

/** 복수 패널 노출 순서 — 기존 순서(반응형→필수→표시 모드→역전파) 유지 + 신규는 뒤에 추가. */
const WIDGET_PROP_BULK_ORDER: WidgetPropKey[] = [
  'hiddenOnView',
  'spanOnView',
  'required',
  'mode',
  'writeBack',
  'labelSuffixFrom',
  'visibleWhen',
  'enabledWhen',
  'inputFormat',
  'displayVariant',
  'editEntry',
  'collapseLines',
  // 라벨 표시 계열(2026-08-24 편입) — 단일 패널과 같은 묶음 순서(위치 → 정렬 → 폰트).
  // 보조 설명(labelHint)은 bulk 미대상이라 여기 없다.
  'labelPlacement',
  'labelAlign',
  'labelStyle',
  // 표 전용 표시 축 — 목록은 tableWidgetProps가 소유(bulk 여부와 순서가 한 곳에서 갈린다).
  ...TABLE_WIDGET_BULK_PROP_ORDER,
];

/**
 * 다중 선택 공통 속성 일괄 편집(tri-state) — **디스크립터 레지스트리 순회**(bulk && 선택 전원
 * applicable인 속성만 노출). 속성별 개별 판정(전원 동일=값 표시 / 하나라도 다름=혼합), 컨트롤
 * 조작 시 선택 전체를 그 값으로 통일(setAll=onWidgetsUpdate 1회=undo 1스텝).
 * 구 allInput 하드 게이트는 디스크립터별 applicable로 일반화 — 기존 4속성은 전부 input 전용이라
 * 동작 동일, visibleWhen은 전 위젯 공통이라 혼합 kind 선택에서도 노출된다.
 * ⚠️ 새 공통 속성을 여기에 직접 JSX로 추가하지 말 것 — WIDGET_PROP_DESCRIPTORS에 추가해야
 * 단일/복수 양쪽에 자동 반영된다.
 */
function BulkWidgetProperties({
  widgets,
  activeView,
  onWidgetsUpdate,
  writeFormFields,
  bindingPrefillMap,
  catalog,
  tree,
  dataDefs,
  variables,
  onHoverHighlightWidget,
  onFtCellHover,
}: {
  widgets: ComposerWidget[];
  activeView: string;
  onWidgetsUpdate: (ids: string[], updater: (x: ComposerWidget) => ComposerWidget) => void;
  writeFormFields?: InputWidget[];
  bindingPrefillMap?: Map<string, { catalogKey: string; select?: string }>;
  catalog?: CatalogEntry[];
  /** 값 스코프 후보 산출용 전체 트리(visibleWhen·labelSuffixFrom). */
  tree?: ComposerWidget[];
  /** dataDef 표 위젯의 저장 정책 안내(writeBack 단일 분기)용. */
  dataDefs?: DataDef[];
  /** 변수 파생 위젯의 역전파 대체 안내(영속 정본=rememberScope)용. */
  variables?: TemplateVariable[];
  /** 참조 콤보 hover → 캔버스 위젯/셀 하이라이트. */
  onHoverHighlightWidget?: (id: string | null) => void;
  onFtCellHover?: (c: { tableId: string; rowId: string; columnId: string } | null) => void;
}) {
  const ctx: WidgetPropCtx = {
    activeView,
    tree,
    writeFormFields,
    bindingPrefillMap,
    catalog,
    dataDefs,
    variables,
    onHoverHighlightWidget,
    onFtCellHover,
  };
  const ids = widgets.map((w) => w.id);
  const selection: WidgetPropSelection = {
    widgets,
    setAll: (updater) => onWidgetsUpdate(ids, updater),
  };
  const visible = WIDGET_PROP_BULK_ORDER.map((k) => WIDGET_PROP_DESCRIPTORS[k]).filter(
    (d) => d.bulk && widgets.every((w) => d.applicable(w, ctx)),
  );
  if (visible.length === 0) {
    return (
      <p className="rounded border border-dashed border-gray-300 px-3 py-4 text-[11px] leading-relaxed text-gray-400">
        선택한 위젯들에 공통으로 편집할 속성이 없습니다. 같은 종류끼리 선택하거나, 캔버스/트리에서
        단일 선택으로 편집하세요.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {visible.map((d) => {
        const Control = d.Control;
        return <Control key={d.key} selection={selection} ctx={ctx} />;
      })}
    </div>
  );
}

/** 다중 편집에서 값이 섞였음을 나타내는 작은 태그. */
function MixedTag() {
  return (
    <span className="rounded bg-amber-100 px-1 text-[9px] text-amber-700">— 혼합 —</span>
  );
}

/** 표시 조합 빌더의 필드 후보 1건 — key=행 필드 경로(카탈로그 returns path 등), label=표시 라벨. */
interface DisplayTemplateFieldOption {
  key: string;
  label: string;
}

/**
 * 표시 조합 빌더 — sourceFieldMaps의 textTemplate/badgeTemplate 저작 UI(콤보·선택 목록 공용).
 * 세그먼트([앞 구분자]+필드+[뒤 구분자]+빈 값 생략)를 쌓으면 shared 직렬화로 템플릿 문자열을
 * 저장하고, 로드는 파싱(왕복). 합성·미리보기는 rc 정규화와 같은 shared 정본을 공유한다.
 * 결정: docs/decisions/2026-08-24-composer-display-template-axis.md
 *
 * - 단일 모드(template 미지정) = 기존 단일 필드 경로 그대로 — 라디오로 조합과 전환.
 * - 조합 전환 시 현행 단일 필드(defaultField)로 첫 세그먼트를 시드, 단일 복귀 시 키 삭제
 *   (빈 템플릿 저장 금지 — 미지정=단일 필드 경로 하위호환 규약).
 * - 빌더로 표현할 수 없는 템플릿(외부 저작 등)은 원문 입력 폴백으로 보존.
 */
function DisplayTemplateBuilder({
  label,
  singleModeLabel,
  template,
  fields,
  defaultField,
  onChange,
  labelCls,
  inputCls,
}: {
  label: string;
  /** 단일(조합 미사용) 모드 라디오 라벨 — 콤보는 자동 규칙, 선택 목록은 단일 필드 매핑을 가리킴. */
  singleModeLabel: string;
  template: string | undefined;
  fields: DisplayTemplateFieldOption[];
  /** 조합 전환 시 첫 세그먼트 시드 필드(현행 단일 필드). 후보에 없으면 첫 후보로 시드. */
  defaultField: string | undefined;
  /** undefined = 키 삭제(단일 모드 복귀). */
  onChange: (template: string | undefined) => void;
  labelCls: string;
  inputCls: string;
}) {
  const validFields = fields.filter((f) => DISPLAY_TEMPLATE_FIELD_RE.test(f.key));
  // 세그먼트는 로컬 소유 + 직렬화 문자열만 저장 — 저장값에서 매 렌더 재파싱하면 top-level
  // 구분자의 prefix/suffix 슬롯 귀속 정규화 때문에 타이핑 중인 구분자가 옆 조각으로 이동한다.
  const [segments, setSegments] = useState<DisplayTemplateSegment[] | null>(() =>
    template ? parseDisplayTemplateSegments(template) : null,
  );
  // 원문 폴백 input 편집 중 플래그 — 타이핑으로 템플릿이 파싱 가능해져도 blur 전엔 빌더로
  // 갈아치우지 않는다(전환 시 input이 언마운트돼 타이핑 중 포커스를 잃는다). blur에서 재동기화.
  const rawEditingRef = useRef(false);
  // 외부 저장값이 로컬 직렬화와 다를 때만(위젯/소스 전환·외부 수정) 재파싱 동기화.
  useEffect(() => {
    if (rawEditingRef.current) return;
    setSegments((prev) => {
      const local = prev && prev.length > 0 ? serializeDisplayTemplateSegments(prev) : undefined;
      if ((template || undefined) === (local || undefined)) return prev;
      return template ? parseDisplayTemplateSegments(template) : null;
    });
  }, [template]);

  const commitSegments = (next: DisplayTemplateSegment[]): void => {
    setSegments(next);
    onChange(serializeDisplayTemplateSegments(next) || undefined);
  };
  const updateSegment = (index: number, patch: Partial<DisplayTemplateSegment>): void => {
    if (!segments) return;
    commitSegments(segments.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };
  const moveSegment = (index: number, delta: number): void => {
    if (!segments) return;
    const next = [...segments];
    const [picked] = next.splice(index, 1);
    next.splice(index + delta, 0, picked);
    commitSegments(next);
  };
  const enterCompositeMode = (): void => {
    const seedField =
      defaultField && DISPLAY_TEMPLATE_FIELD_RE.test(defaultField)
        ? defaultField
        : validFields[0]?.key;
    if (!seedField) return;
    commitSegments([{ prefix: '', field: seedField, suffix: '', omitWhenEmpty: false }]);
  };
  const enterSingleMode = (): void => {
    setSegments(null);
    onChange(undefined);
  };

  const fieldLabelOf = (key: string): string => fields.find((f) => f.key === key)?.label ?? key;
  // PROP_INPUT_CLS의 w-full은 여기 좁은 구분자 입력(w-12)과 충돌 — Tailwind는 클래스 나열
  // 순서가 아니라 스타일시트 순서로 승자가 정해져 w-full이 이기고, 행이 속성창 폭을 뚫는다
  // (2026-08-24 실사고, feedback-tailwind-shared-class-const-bg-conflict 동형). 폭 유틸만 제거.
  const narrowInputCls = `${inputCls.replace(/\bw-full\b/, '').trim()} w-12 flex-none`;
  const composite = template !== undefined && template !== '';
  const compositeDisabled = !composite && validFields.length === 0;
  return (
    <div className="space-y-1">
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-3 text-xs text-gray-600">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={!composite}
            onChange={() => {
              if (composite) enterSingleMode();
            }}
          />
          {singleModeLabel}
        </label>
        <label
          className={`flex items-center gap-1 ${compositeDisabled ? 'text-gray-300' : ''}`}
          title={compositeDisabled ? '이 소스는 필드 스키마를 알 수 없어 조합을 만들 수 없습니다' : undefined}
        >
          <input
            type="radio"
            checked={composite}
            disabled={compositeDisabled}
            onChange={() => {
              if (!composite) enterCompositeMode();
            }}
          />
          여러 필드 조합
        </label>
      </div>
      {composite && segments ? (
        <div className="space-y-1">
          {segments.map((s, i) => {
            const known = validFields.some((f) => f.key === s.field);
            return (
              // 세그먼트에 고유 id가 없어 index key — 이동/삭제 시 통째 재렌더돼도 로컬 입력
              // 상태가 없는 controlled 입력이라 안전.
              <div key={i} className="flex items-center gap-1">
                <input
                  className={narrowInputCls}
                  value={s.prefix}
                  placeholder="앞"
                  title="필드 값 앞 구분자 텍스트(예: ' - '). 문법 문자 {}[]는 쓸 수 없습니다."
                  onChange={(e) =>
                    updateSegment(i, { prefix: sanitizeDisplayTemplateLiteral(e.target.value) })
                  }
                />
                <select
                  className={`${inputCls} min-w-0 flex-1`}
                  value={s.field}
                  onChange={(e) => updateSegment(i, { field: e.target.value })}
                >
                  {validFields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label} ({f.key})
                    </option>
                  ))}
                  {!known && <option value={s.field}>{s.field} (목록에 없음 — 보존)</option>}
                </select>
                <input
                  className={narrowInputCls}
                  value={s.suffix}
                  placeholder="뒤"
                  title="필드 값 뒤 구분자 텍스트(예: ')'). 문법 문자 {}[]는 쓸 수 없습니다."
                  onChange={(e) =>
                    updateSegment(i, { suffix: sanitizeDisplayTemplateLiteral(e.target.value) })
                  }
                />
                <label
                  className="flex flex-none items-center gap-0.5 text-[10px] text-gray-600"
                  title="이 필드 값이 비어 있으면 앞뒤 구분자째 통째 표시하지 않습니다(구멍 방지)"
                >
                  <input
                    type="checkbox"
                    checked={s.omitWhenEmpty}
                    onChange={(e) => updateSegment(i, { omitWhenEmpty: e.target.checked })}
                  />
                  빈 값 생략
                </label>
                <span className="flex shrink-0 gap-0.5">
                  <button
                    type="button"
                    disabled={i === 0}
                    className="px-1 text-gray-500 hover:text-gray-800 disabled:text-gray-300"
                    aria-label="조각 위로"
                    onClick={() => moveSegment(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={i === segments.length - 1}
                    className="px-1 text-gray-500 hover:text-gray-800 disabled:text-gray-300"
                    aria-label="조각 아래로"
                    onClick={() => moveSegment(i, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    disabled={segments.length === 1}
                    className="px-1 text-gray-500 hover:text-red-600 disabled:text-gray-300"
                    aria-label="조각 삭제"
                    title={
                      segments.length === 1
                        ? '마지막 조각은 삭제할 수 없습니다 — 단일 필드로 전환하세요'
                        : '조각 삭제'
                    }
                    onClick={() => commitSegments(segments.filter((_, j) => j !== i))}
                  >
                    ✕
                  </button>
                </span>
              </div>
            );
          })}
          <button
            type="button"
            className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
            onClick={() => {
              const field = validFields[0]?.key;
              if (field) commitSegments([...segments, { prefix: '', field, suffix: '', omitWhenEmpty: false }]);
            }}
          >
            + 조각 추가
          </button>
          <div className="rounded border border-gray-200 bg-white px-2 py-1.5 text-[11px] leading-4">
            <span className="text-gray-400">미리보기: </span>
            {segments.map((s, i) => (
              <span
                key={i}
                className={s.omitWhenEmpty ? 'text-gray-400' : 'text-gray-700'}
                title={s.omitWhenEmpty ? '빈 값이면 구분자째 생략되는 조각' : undefined}
              >
                {s.prefix}
                {fieldLabelOf(s.field)}
                {s.suffix}
              </span>
            ))}
          </div>
        </div>
      ) : composite ? (
        // 빌더로 표현할 수 없는 템플릿(외부 저작·손 편집) — 원문 입력 폴백(보존).
        <div className="space-y-1">
          <input
            className={`${inputCls} font-mono`}
            value={template}
            onChange={(e) => onChange(e.target.value || undefined)}
            // 타이핑 중 파싱 가능해져도 blur 전엔 원문 모드 유지(rawEditingRef 주석 — 포커스 보존).
            onFocus={() => {
              rawEditingRef.current = true;
            }}
            onBlur={() => {
              rawEditingRef.current = false;
              setSegments(template ? parseDisplayTemplateSegments(template) : null);
            }}
          />
          <p className="text-[11px] leading-4 text-gray-400">
            조합 편집기로 표현할 수 없는 형태라 원문으로 표시합니다 — 문법: {'{필드}'} 자리표시자,
            [ … ] 빈 값 생략 조각. 미리보기:{' '}
            {renderDisplayTemplate(
              template,
              Object.fromEntries(fields.map((f) => [f.key, f.label])),
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 표형 열 헤더의 **출처** 한국어 설명 — rc repeatTableColumnHeaderSource 토큰 ↔ 저작 축 문구.
 * 헤더가 별도 설정값이 아니라 파생이라, "무엇을 고치면 이 열 이름이 바뀌는가"를 그대로 적는다.
 */
const REPEAT_TABLE_HEADER_SOURCE_TEXT: Record<RepeatTableColumnHeaderSource, string> = {
  input: '위젯 라벨',
  groupTitle: '그룹 제목',
  groupDescend: '그룹에 제목이 없어 첫 자식의 라벨',
  branchSlot: '활성 슬롯 이름 (조건에 맞는 슬롯에 따라 바뀜)',
  branchDescend: '활성 슬롯에 이름이 없어 그 슬롯 첫 자식의 라벨',
  none: '라벨·제목·슬롯 이름을 지정하세요',
};

/**
 * repeat 표형 속성 패널의 "열 헤더" 목록 — 열마다 현재 헤더 텍스트 + 그 출처 + **순서 이동·제거**.
 *
 * ⚠️ 헤더 텍스트는 **rc `repeatTableColumnHeaderLabel`을 그대로 호출**해 얻는다(admin 재구현
 * 금지 — 이중 구현이면 캔버스 표와 안내가 갈라진다). 열 집합·표시 순서도 캔버스 렌더와 같은
 * 규칙(비-base 뷰=orderSiblingsForView, 비시각 위젯=isRepeatTableNonColumnChild 제외)이다.
 *
 * ⚠️ 조건 평가 스코프는 **top-level 미리보기 값(writeValues)** 이다 — 캔버스 표 헤더도 같은
 * 값으로 평가하므로 두 화면이 일치한다. 다만 캔버스는 반복이 모달·중첩 인스턴스 안일 때
 * registeredWidgetContext.values를 아래에 깔고, 속성 패널은 그 값에 접근할 수 없다 — 그런
 * 중첩 배치에서만 branch 열의 활성 슬롯 판정이 캔버스와 어긋날 수 있다.
 *
 * ── 왜 여기에 조작 버튼이 있나(2026-08-24) ─────────────────────────────────────
 * 표형 열의 **선택 채널이 캔버스에 없을 수 있다**: `<thead>`는 행 수와 무관하게 그려지지만
 * 선택 가능한 위젯 인스턴스는 `<tbody>` 행 렌더(renderRowBody)에서만 나온다. 미리보기 행이
 * 0개면 열은 보이는데 `data-composer-widget-id`가 붙은 DOM이 하나도 없어 클릭·Delete가 닿지
 * 않는다(행이 있어도 repeat 드릴다운을 거쳐야 해 발견성이 낮다). rc는 repeat 행 안으로
 * dnd 래퍼를 의도적으로 넘기지 않으므로(`repeatRowScopedInteractive` — 같은 위젯 id가 행마다
 * 중복 등록되는 충돌 회피), **행 템플릿 구조 편집의 정본은 트리 패널·속성 패널**이다. 이 목록이
 * 그 정본 채널의 표형 판이다 — 행 수·드릴 상태와 무관하게 항상 동작한다.
 *
 * ⚠️ 제거는 **기존 단일 삭제 경로(onRemove → removeWidgetById)를 그대로 재사용**한다(새 삭제
 * 로직 금지 — input의 미배치 풀 복귀 등 규약이 그 경로에 걸려 있다).
 */
function RepeatTableColumnHeaderGuide({
  repeatId,
  rowChildren,
  activeView,
  writeValues,
  activeBranchSlots,
  onWidgetUpdate,
  onTreeUpdate,
  onRemove,
}: {
  /** 이 열들을 소유한 repeat 위젯 id — base 뷰 이동이 그 children 배열을 직접 재배열한다. */
  repeatId: string;
  /** 행 템플릿 위젯(= 표의 열 후보). `children`이 아닌 이름 — JSX 특수 prop 회피. */
  rowChildren: ComposerWidget[];
  activeView: string;
  /** 캔버스 미리보기 값 — branch 열 조건 평가 스코프. */
  writeValues?: Record<string, unknown>;
  /** 캔버스 슬롯 탭 선택(branch id → 슬롯 index) — 헤더 함수에 그대로 전달. */
  activeBranchSlots: Record<string, number>;
  onWidgetUpdate: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  /** 트리 전체 갱신(wtApply) — 비-base 뷰 표시순 이동에만 사용. */
  onTreeUpdate?: (fn: (tree: ComposerWidget[]) => ComposerWidget[]) => void;
  /** 위젯 제거(단일 경로 재사용) — input이면 미배치 풀 복귀. */
  onRemove: (id: string) => void;
}) {
  const isViewScoped = !!activeView && activeView !== COMPOSER_BASE_VIEW_KEY;
  const ordered = isViewScoped ? orderSiblingsForView(rowChildren, activeView) : rowChildren;
  // 열 집합은 캔버스 표(thead·td)와 같은 단일 함수 — 비시각 제외 + 현재 뷰 숨김(유령 열) 제외.
  const columns = repeatTableColumnChildren(rowChildren, isViewScoped ? activeView : undefined);
  // 이동 헬퍼의 "열" 판정도 같은 집합 — 숨김 위젯과 자리만 바꾸는 무의미 이동 방지.
  const isColumnCandidate = (w: ComposerWidget): boolean =>
    !isRepeatTableNonColumnChild(w) &&
    !resolveWidgetHidden(w, isViewScoped ? activeView : undefined);
  const scope = writeValues ?? {};

  /**
   * 열 한 칸 이동(undo 1스텝 — wtApply/onWidgetUpdate 관례).
   *
   * 목표 인덱스는 순수 헬퍼가 계산한다(비시각 위젯 건너뛰기 — `repeatColumnOrder.ts`). 반환값은
   * **제거 후 그 인덱스에 삽입**하는 splice 의미라, 두 경로가 같은 계약을 쓰게 맞춘다:
   *  - 비-base 뷰: rc `moveWidgetToIndexForView`(= reorderSiblingsForView, 같은 splice 의미).
   *    base 배열·구조는 불변이고 형제 전원의 responsive[view].order만 새 표시순으로 materialize.
   *  - base 뷰: repeat children 배열을 직접 splice. `moveWidget`(insert-before 의미 + dnd의
   *    -1 시프트 보정 필요)을 쓰지 않는 이유가 이것 — 부모를 이미 알고 있으니 의미가 어긋날
   *    여지 없이 그 자리에서 재배열하는 편이 정확하다.
   */
  const moveColumn = (id: string, dir: 'up' | 'down'): void => {
    const target = repeatColumnMoveTargetIndex(ordered, id, dir, isColumnCandidate);
    if (target === null) return; // 경계 — 무변경
    if (isViewScoped) {
      onTreeUpdate?.((tree) => moveWidgetToIndexForView(tree, id, target, activeView));
      return;
    }
    onWidgetUpdate(repeatId, (rw) => {
      if (rw.kind !== 'repeat') return rw;
      const next = [...rw.children];
      const from = next.findIndex((c) => c.id === id);
      if (from < 0) return rw;
      const [moved] = next.splice(from, 1);
      next.splice(target, 0, moved);
      return { ...rw, children: next };
    });
  };

  const btnCls =
    'rounded border border-gray-200 bg-white px-1 leading-none text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-white';

  return (
    <div className="rounded border border-gray-200 bg-gray-50 px-2 py-2">
      <p className="mb-1 flex items-center gap-1 text-[11px] font-medium text-gray-600">
        열 헤더 {columns.length}개
        <HelpLink topic="repeatTableHeader" />
      </p>
      {columns.length === 0 ? (
        <p className="text-[10px] leading-snug text-gray-400">
          열이 될 자식 위젯이 없습니다. 행 템플릿에 입력·그룹·분기를 추가하세요.
        </p>
      ) : (
        <ol className="space-y-1">
          {columns.map((c) => {
            const label = repeatTableColumnHeaderLabel(c, scope, activeBranchSlots);
            const source = repeatTableColumnHeaderSource(c, scope, activeBranchSlots);
            const name = label ? `"${label}"` : '(없음)';
            return (
              <li key={c.id} className="flex min-w-0 items-start gap-1 text-[10px] leading-snug">
                <span className="min-w-0 flex-1">
                  <span className="text-gray-700">{name}</span>
                  <span className="text-gray-400"> — {REPEAT_TABLE_HEADER_SOURCE_TEXT[source]}</span>
                </span>
                <span className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    className={btnCls}
                    disabled={!canMoveRepeatColumn(ordered, c.id, 'up', isColumnCandidate)}
                    aria-label={`${name} 열 왼쪽으로`}
                    title="왼쪽으로"
                    onClick={() => moveColumn(c.id, 'up')}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className={btnCls}
                    disabled={!canMoveRepeatColumn(ordered, c.id, 'down', isColumnCandidate)}
                    aria-label={`${name} 열 오른쪽으로`}
                    title="오른쪽으로"
                    onClick={() => moveColumn(c.id, 'down')}
                  >
                    →
                  </button>
                  <button
                    type="button"
                    className={`${btnCls} hover:text-red-500`}
                    aria-label={`${name} 열 제거`}
                    title="열 제거"
                    onClick={() => onRemove(c.id)}
                  >
                    ×
                  </button>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      <p className="mt-1.5 text-[10px] leading-snug text-gray-400">
        열 순서는 카드형의 세로 순서와 같은 축(행 템플릿 자식 순서)입니다 — 여기서 옮기면
        카드형으로 되돌렸을 때의 순서도 함께 바뀝니다. 열 헤더는 따로 입력하는 값이 아니라 열
        위젯에서 파생됩니다(입력은 라벨, 그룹은 제목, 분기는 조건에 맞는 활성 슬롯의 이름) —
        헤더 문구를 바꾸려면 위 출처를 고치세요.
      </p>
    </div>
  );
}

function SingleWidgetProperties({
  w,
  writeFormFields,
  onWidgetUpdate,
  onWidgetPreview,
  onWidgetCommit,
  onWidgetRevert,
  onRemove,
  onTreeUpdate,
  activeView,
  tree,
  dataDefs,
  variables,
  dataSources,
  onDataSourcesChange,
  onTemplateUpdate,
  countKeyRefs,
  unplacedPool,
  ftFocusCell,
  onFtCellHover,
  onFtCellFocus,
  onFtCellFocusClear,
  ftCellClipboard,
  onFtCellCopy,
  onFtCellBlockCopy,
  onFtCellPaste,
  onFtCellPasteMany,
  onFtCellsContentClear,
  onFtRequestConfirm,
  writeValues,
  bindingPrefillMap,
  catalog,
  modalAuthoringId,
  onModalAuthoringToggle,
  onHoverHighlightWidget,
  activeBranchSlots,
  onSetActiveBranchSlot,
  onWidgetAddBranchSlot,
  onWidgetRemoveBranchSlot,
  onWidgetMoveBranchSlot,
  onWidgetUpdateBranchSlot,
  onSelectWidget,
}: {
  w: ComposerWidget;
  writeFormFields: InputWidget[];
  onWidgetUpdate: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  /** 위젯 수치 입력 실시간 미리보기(replacePresent). formTable 열/행 치수 DimInput용. */
  onWidgetPreview: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  /** 위젯 수치 입력 확정(commitPresent — undo 1스텝). */
  onWidgetCommit: (id: string, updater: (w: ComposerWidget) => ComposerWidget) => void;
  /** 위젯 수치 입력 미리보기 폐기(discardTransient). */
  onWidgetRevert: () => void;
  onRemove: (id: string) => void;
  /** 트리 전체 갱신(wtApply) — 형제 집합 단위 조작(뷰 순서 초기화)용. WidgetPropCtx.onTreeUpdate. */
  onTreeUpdate?: (fn: (tree: ComposerWidget[]) => ComposerWidget[]) => void;
  activeView: string;
  tree: ComposerWidget[];
  dataDefs?: DataDef[];
  /** 변수 파생 위젯의 역전파 대체 안내(영속 정본=rememberScope)용. */
  variables?: TemplateVariable[];
  dataSources?: DataSourceDef[];
  onDataSourcesChange: (next: DataSourceDef[]) => void;
  /** 템플릿 원자 갱신(apply) — 표 칼럼 참조 모드의 정본 쓰기·detach용. WidgetPropCtx.onTemplateUpdate. */
  onTemplateUpdate?: (fn: (t: ReportTemplate) => ReportTemplate) => void;
  /** bindingKey 참조 수(countDataSourceUsage 위임) — detach 차단 판정. WidgetPropCtx.countKeyRefs. */
  countKeyRefs?: (key: string) => number;
  /** 표 위젯 입력 셀 배치 후보(전체 미배치 풀). */
  unplacedPool: InputWidget[];
  /** 표 셀 복수 포커스(formTable 전용) — cells 마지막=anchor. source로 스크롤 여부 구분. */
  ftFocusCell?: FtCellFocus | null;
  /** 속성 셀 카드 hover → 캔버스 셀 하이라이트(formTable 전용). */
  onFtCellHover?: (c: { tableId: string; rowId: string; columnId: string } | null) => void;
  /** 속성 셀 카드 클릭 → 셀 포커스(formTable 전용, additive=Ctrl/⌘ 토글). */
  onFtCellFocus?: OnFtCellFocus;
  /** 셀 포커스 해제(셀 속성 → 표 속성 복귀 / 복수 전체 해제). */
  onFtCellFocusClear?: () => void;
  /** 공유 셀 클립보드 + 복사/붙여넣기(formTable 전용 — 캔버스 Ctrl+C/V와 공용). */
  ftCellClipboard?: FtCellClipboard | null;
  onFtCellCopy?: (cell: FormTableFixedCell) => void;
  /** 복수 선택 → 블록 복사. */
  onFtCellBlockCopy?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  onFtCellPaste?: (tableId: string, rowId: string, columnId: string) => void;
  /** 복사 셀 → 선택된 전 셀 일괄 붙여넣기. */
  onFtCellPasteMany?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  /** 선택된 전 셀 내용 제거. */
  onFtCellsContentClear?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  /** 표 행/열 삭제 등 내용 손실 경고 확인 모달 요청. */
  onFtRequestConfirm?: FtConfirmRequest;
  /** 현재 미리보기 컨텍스트의 작성폼 값 — 셀 배치 후보 값 미리보기용. */
  writeValues?: Record<string, unknown>;
  /** body 바인딩 자동 채움 출처 fallback(formTable 셀 정보). */
  bindingPrefillMap?: Map<string, { catalogKey: string; select?: string }>;
  /** 카탈로그 — treeOwned 위젯의 자동 채움 편집 + 표 셀 즉석 생성 소스. */
  catalog?: CatalogEntry[];
  /** 저작 열림 중인 modalTrigger id(P1b) — modalTrigger 속성창 버튼 라벨(열기/닫기) 토글에 사용. */
  modalAuthoringId?: string | null;
  /** 모달 저작 열림/닫힘 토글 — id로 열고, null로 닫는다(캔버스에서 모달을 열린 상태로 유지). */
  onModalAuthoringToggle?: (id: string | null) => void;
  /** commit 매핑 콤보 hover → 캔버스 위젯 하이라이트(null=해제). 셀 하이라이트는 onFtCellHover 공용. */
  onHoverHighlightWidget?: (id: string | null) => void;
  /** 분기 위젯 id → 활성 슬롯 index(캔버스 탭·트리 서브행과 공유). branch 슬롯 에디터 활성 표시. */
  activeBranchSlots: Record<string, number>;
  /** 분기 슬롯 활성 전환(슬롯 에디터 ● 버튼). */
  onSetActiveBranchSlot: (branchId: string, index: number) => void;
  /** 분기 슬롯 추가. */
  onWidgetAddBranchSlot: (branchId: string) => void;
  /** 분기 슬롯 삭제(최소 1 가드는 rc). */
  onWidgetRemoveBranchSlot: (branchId: string, slotId: string) => void;
  /** 분기 슬롯 이동. */
  onWidgetMoveBranchSlot: (branchId: string, slotId: string, dir: 'up' | 'down') => void;
  /** 분기 슬롯 label/condition 편집(부분 patch). */
  onWidgetUpdateBranchSlot: (
    branchId: string,
    slotId: string,
    patch: { label?: string; condition?: string },
  ) => void;
  /** 슬롯 자식 위젯 클릭 선택(활성 슬롯 자식 목록 드릴다운). */
  onSelectWidget?: (id: string) => void;
}) {
  const labelCls = PROP_LABEL_CLS;
  const inputCls = PROP_INPUT_CLS;

  // 반복(행 스코프 조립) children 판정(P4a) — bindingKey가 행 필드 상대키라 input 패널의 body 전제
  // 축(writeFormFields dataType 파생·자동 채움·풀 복귀)을 행 스코프로 가른다. 모달 스코프가
  // innermost면 null(모달 안 위젯은 반복 안이어도 행 스코프 아님 — findEnclosingRepeatWidget 규약).
  const enclosingRepeat = findEnclosingRepeatWidget(tree, w.id);
  // 이 위젯의 값 스코프 필드 후보 — 표시 형식의 경로 칸(병기 값·구간 키) 저작용. 반복 밖이면 빈 목록
  // (본문 스코프의 경로 후보는 아직 이 축에 없다 → 자유 텍스트 입력 유지).
  const repeatRowScopeDef = enclosingRepeat
    ? (dataSources ?? []).find((d) => d.key === enclosingRepeat.over)
    : undefined;
  // current='' 호출이라 목록 선두에 value:''인 보존(preserved) 옵션이 인공물로 붙는다 — 그것만 걷어낸다
  // (formTable 패널의 같은 목록은 !o.preserved로 거르지만, current=''에서는 두 필터가 같은 1건을 가리킨다).
  const rowFieldOptions: HybridKeyOption[] = repeatRowScopeDef
    ? repeatRowBindingOptions(repeatRowScopeDef, '', catalog)
        .filter((o) => o.value !== '')
        .map((o) => ({ key: o.value, label: o.label }))
    : [];

  // 디스크립터 레지스트리 컨텍스트 — 공통 속성(WidgetPropSingle)은 복수 패널과 같은 Control을
  // 공유한다(단일/복수 비대칭 누락 방지 정본 — 레지스트리 정의부 불변식 주석 참조).
  const propCtx: WidgetPropCtx = {
    activeView,
    tree,
    writeFormFields,
    bindingPrefillMap,
    catalog,
    dataDefs,
    dataSources,
    rowFieldOptions,
    variables,
    // 표시 형식의 적용 구간 미리보기 원료 — 캔버스 작성값이 곧 서버 prefill 해소분을 담고 있다
    // (별도 채널을 새로 뚫지 않는다). 컨텍스트 미선택이면 비어 있어 안내 문구로 degrade.
    previewValues: writeValues,
    onHoverHighlightWidget,
    onFtCellHover,
    onTreeUpdate,
    // 표 칼럼 참조 모드(P3) 채널 — 정본 쓰기/detach(onTemplateUpdate)·detach 차단 판정
    // (countKeyRefs)·확인 퍼널(requestConfirm=confirmState 위임).
    onTemplateUpdate,
    countKeyRefs,
    requestConfirm: onFtRequestConfirm,
  };

  // 정렬(alignH)이 의미 있는 위젯 = 고유 폭(텍스트·모달 버튼)만. 채움 위젯(섹션/입력/표)은 무의미
  // ("채움이면 정렬 무의미" 양식 규약)이고, 섹션은 flex 래퍼 수축으로 내부 레이아웃이 깨지며
  // 구분선은 폭 0으로 소멸할 수 있어 노출 자체를 제한(2026-07-15 사용자 확인 — 설정 경로 차단이 방어).
  const alignApplicable = w.kind === 'heading' || w.kind === 'modalTrigger';

  // 공통 위젯 푸터 — 표시 조건·정렬(해당 위젯만)·안쪽 여백(전 위젯 공통) + 삭제. 각 kind 패널이 끝에 배치.
  const removeBtn = (
    <>
      <div className="border-t border-gray-200 pt-3 space-y-3">
        {/* 표시 조건(visibleWhen) — 전 위젯 공통 base 속성(P3). */}
        <WidgetPropSingle prop="visibleWhen" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* 활성 조건(enabledWhen) — 표시 조건 바로 아래 대칭 축(input 전용, applicable이 게이트). */}
        <WidgetPropSingle prop="enabledWhen" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* 뷰별 축 묶음(숨김·순서·폭 비율) — 저작자가 "이 뷰에만 저장되는 것"을 한 자리에서 보게
            모아 둔다. 흩어져 있으면 전역 속성과 섞여 구분되지 않는다(2026-08-06 사용자 지적:
            "이 뷰에서 숨김이 따로 있구나. 이 옵션은 가장 위에 있어야 하지 않을까?").
            남은 뷰별 축인 group의 단 수만 그룹 패널에 있다 — 단 수는 그 위젯의 본질 속성이라
            뷰 축으로 떼면 오히려 찾기 어렵다(대신 GroupColumnsViewHint가 뷰 상태를 말한다).
            각 디스크립터의 applicable이 대상 kind·배치를 게이트하므로 전 kind 공통 푸터에 둬도
            무관한 위젯에는 노출되지 않는다. */}
        <WidgetPropSingle prop="hiddenOnView" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        <WidgetPropSingle prop="viewOrder" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        <WidgetPropSingle prop="spanOnView" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
      </div>
      {alignApplicable && (
      <div className="border-t border-gray-200 pt-3">
        {/* Outer 정렬(칸/부모 안 수평 위치, alignH). 수평 1축이라 3버튼 그룹
            (2026-07-15 사용자 결정 — 수직 축은 표 셀 외 사용 상황 없어 미도입). */}
        <div
          className="flex items-center gap-2"
          title="칸/부모 안 나의 수평 위치"
        >
          <span className="text-xs text-gray-500 w-16">정렬</span>
          <div className="inline-flex rounded border border-gray-300 overflow-hidden">
            {(
              [
                ['start', '좌측'],
                ['center', '중간'],
                ['end', '우측'],
              ] as const
            ).map(([v, lbl]) => {
              const active = (w.alignH ?? 'start') === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() =>
                    onWidgetUpdate(w.id, (x) => {
                      const nx = { ...x };
                      if (v === 'start') delete nx.alignH;
                      else nx.alignH = v;
                      return nx;
                    })
                  }
                  className={`px-2.5 py-1 text-xs leading-none border-r border-gray-300 last:border-r-0 ${
                    active ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}
      <div className="border-t border-gray-200 pt-3">
        <WidgetPaddingControl w={w} onWidgetUpdate={onWidgetUpdate} />
      </div>
      <div className="border-t border-gray-200 pt-3">
        <button
          type="button"
          className="rounded bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
          onClick={() => onRemove(w.id)}
        >
          {/* treeOwned(직접 생성)·반복 children(행 스코프 — 풀 파생 대응물 없음)은 제거=삭제. */}
          {w.kind === 'input' && !w.treeOwned && !enclosingRepeat ? '작성폼에서 빼기' : '삭제'}
        </button>
      </div>
    </>
  );

  // 공통 프레임 — kind별 편집 본문(kindBody)을 골라내고, 공통 푸터(removeBtn)를 프레임 레벨에서
  // 한 번만 붙인다. kind 분기는 본문만 반환하고 removeBtn을 삽입/전달하지 않으므로, 새 위젯 kind가
  // 공통 푸터를 누락할 수 없다(양식 노드 속성 패널과 동형 — 2026-07-15 사용자 승인).
  const kindBody: ReactNode = (() => {
  // input: 렌더 대상인 InputWidget(composerWidgetTree)을 직접 편집한다(WYSIWYG — 중앙 미리보기·
  // 실제 작성 페이지가 InputWidget을 렌더하므로 여기서 편집해야 즉시 반영). dataType은 위젯 종류
  // 후보 산출용으로 파생 작성폼(writeFormFields)에서 읽는다(read-only). 저장된 flat 오버라이드 편집은
  // composerWidgetTree 정본화로 대체됐고 그 축 자체가 2026-10-02(S6)에 철거됐다 —
  // `writeFormFields`는 파생 산출(`InputWidget[]`)이다.
  // 등록형 위젯: 라벨 + widgetKey(읽기) + 전파 매핑 편집기. 일반 input 편집(위젯 종류/모드 등)과 분리.
  if (w.kind === 'input' && w.widget === 'registered') {
    return (
      <RegisteredWidgetPropertyPanel
        widget={w}
        // target 후보 산출은 콤보 writes와 동일(writesTargetProps — 스칼라=body 후보로 hover 강조
        // 확보, 배열=일괄 후보). 자기 키는 제외 — 등록형 자기 값은 값 객체라 원천 행 필드로 덮으면
        // 형상이 깨진다(repeatScopeWritesTargets doc).
        {...writesTargetProps({
          w,
          tree,
          enclosingRepeat,
          repeatDef: repeatRowScopeDef,
          catalog,
          dataDefs,
          dataSources,
          allowSelf: false,
        })}
        onHoverHighlightWidget={onHoverHighlightWidget}
        onFtCellHover={onFtCellHover}
        onWidgetUpdate={onWidgetUpdate}
        labelCls={labelCls}
        inputCls={inputCls}
        activeView={activeView}
      />
    );
  }

  // 행 그리드(dataSourceRows) 위젯: 연결된 편집형 list def의 필드별 작성 위젯 매핑을 속성으로 편집
  // (구 상시 DataSourceWidgetsEditor의 단일 def 분량 — 정본 def.fields, dataSources 갱신).
  if (w.kind === 'input' && w.widget === 'dataSourceRows') {
    return (
      <DataSourceRowsPropertyPanel
        widget={w}
        dataSources={dataSources}
        onDataSourcesChange={onDataSourcesChange}
        labelCls={labelCls}
        catalog={catalog}
      />
    );
  }

  if (w.kind === 'input') {
    // 행 스코프(반복 children, P4a): bindingKey는 행 필드 상대키 — body 파생 풀(writeFormFields)
    // 조회를 차단(행 상대키가 body 키와 우연 동명이면 dataType이 엉뚱하게 파생됨). 후보는 소속
    // repeat의 over 데이터소스 def.fields.
    const repeatDef = enclosingRepeat
      ? (dataSources ?? []).find((d) => d.key === enclosingRepeat.over)
      : undefined;
    const field = enclosingRepeat
      ? undefined
      : writeFormFields.find((f) => f.bindingKey === w.bindingKey);
    // 옵션 소스(optionsFrom) 편집용 — 변수 편집기(VariablesEditor) 동형 헬퍼. list data entry만
    // 소스 후보(단수/입력/AI entry는 rows 형상 아님), field path는 top-level 스칼라만.
    const optSrcListEntries = (catalog ?? []).filter(
      (e) => e.kind === 'data' && e.cardinality === 'list' && !e.legacyHidden,
    );
    const optSrcFieldPaths = (key: string): { path: string; label: string }[] => {
      const entry = (catalog ?? []).find((e) => e.key === key);
      return (entry?.returns ?? [])
        .filter((r) => r.type === 'string' || r.type === 'number' || r.type === 'enum')
        .map((r) => ({ path: r.path, label: r.label }));
    };
    // 코어 픽커 주입 entries — 카탈로그 list entry(legacyHidden 제외는 코어가) + 주입 rows 소스
    // (CatalogEntry 형상이 아니라 어댑터 injectedRowSourceEntry로 변환). '가져올 데이터' 콤보 후보.
    const optionSourceEntries: CatalogEntry[] = [
      ...(catalog ?? []).filter((e) => e.kind === 'data' && e.cardinality === 'list'),
      ...INJECTED_ROW_SOURCES.map(injectedRowSourceEntry),
    ];
    // 소스 키 → 필드 목록({path,label,type}) 또는 undefined(필드 정보 미상 = 직접 지정 키 → 텍스트 폴백).
    // 주입 rows는 레지스트리 fields, 카탈로그는 returns에서 스칼라/enum만.
    const optionSourceFields = (
      key: string,
    ): { path: string; label: string; type: string }[] | undefined => {
      const injected = INJECTED_ROW_SOURCES.find((s) => s.key === key);
      if (injected) return injected.fields.map((f) => ({ path: f.path, label: f.label, type: f.type }));
      const entry = (catalog ?? []).find((e) => e.key === key);
      if (!entry) return undefined;
      return (entry.returns ?? [])
        .filter((r) => r.type === 'string' || r.type === 'number' || r.type === 'enum')
        .map((r) => ({ path: r.path, label: r.label, type: r.type }));
    };
    // 소스 선택 시 기본 값/이름 필드 + 값 타입(number 자동 유도용). 주입 rows는 선언 기본,
    // 카탈로그는 첫 필드. 필드 정보 미상이면 undefined(기존 값 보존).
    const optionSourceDefaults = (
      key: string,
    ): { valueField: string; labelField: string; valueType?: string } | undefined => {
      const injected = INJECTED_ROW_SOURCES.find((s) => s.key === key);
      if (injected) {
        const vf = injected.fields.find((f) => f.path === injected.defaultValueField);
        return {
          valueField: injected.defaultValueField,
          labelField: injected.defaultLabelField,
          valueType: vf?.type,
        };
      }
      const fields = optionSourceFields(key);
      if (!fields || fields.length === 0) return undefined;
      return { valueField: fields[0].path, labelField: fields[0].path, valueType: fields[0].type };
    };
    // '가져올 데이터' 선택 — catalogKey 설정 + 저장할 값/표시할 이름 자동 채움 + dataType 자동 유도.
    const selectOptionDataSource = (catalogKey: string): void => {
      const defaults = optionSourceDefaults(catalogKey);
      onWidgetUpdate(w.id, (x) => {
        if (x.kind !== 'input') return x;
        const next = { ...x };
        next.optionsFrom = {
          catalogKey,
          valueField: defaults?.valueField ?? x.optionsFrom?.valueField ?? '',
          labelField: defaults?.labelField ?? x.optionsFrom?.labelField ?? '',
        };
        if (defaults?.valueType === 'number') next.dataType = 'number';
        else delete next.dataType;
        return next;
      });
    };
    // '저장할 값'(valueField) 변경 — 선택한 필드 타입으로 dataType 자동 재유도.
    const selectOptionValueField = (valueField: string): void => {
      const vt = optionSourceFields(w.optionsFrom?.catalogKey ?? '')?.find(
        (f) => f.path === valueField,
      )?.type;
      onWidgetUpdate(w.id, (x) => {
        if (x.kind !== 'input' || !x.optionsFrom) return x;
        const next = { ...x, optionsFrom: { ...x.optionsFrom, valueField } };
        if (vt === 'number') next.dataType = 'number';
        else delete next.dataType;
        return next;
      });
    };
    // 계약 자식 해소·자동 배선 흔적 판정 — repeatRowScope 정본 재사용(2026-08-05 승격 — formTable
    // 셀 카드와 공유. 종전 로컬 구현을 그대로 이동한 것이라 동작 동일).
    const resolveContractSub = (key: string): RegisteredObjectSubfield | undefined =>
      repeatDef ? resolveRepeatContractSub(repeatDef, key) : undefined;
    const isAutoWiredOptionsFrom = isAutoWiredContractOptionsFrom;
    return (
      <div className="space-y-3">
        {enclosingRepeat && repeatDef ? (
          // 행 필드 매핑 편집기(자유 편집 — 결정 2026-07-20 §자유 편집): def.fields에서 select,
          // 선언 밖 현재값은 보존 옵션. 라벨은 자동 갱신하지 않음(사용자 라벨 존중) — 미설정일 때만
          // 필드 label 제안.
          <div>
            {/* 스코프 배지 — 같은 입력 위젯이라도 본문/반복 행 스코프에 따라 속성 축이 갈리는 것을
                명시(2026-07-21 사용자 제보: "본문에선 자동 채움·카탈로그였는데 반복 안에선 행 필드라
                혼란"). 본문 스코프는 종전 표시 유지. */}
            <p className="mb-1.5">
              <span className="rounded bg-teal-50 border border-teal-200 px-1.5 py-0.5 text-[10px] font-medium text-teal-700">
                반복 행 스코프 · {repeatDef.label || repeatDef.key}
              </span>
            </p>
            <label className={labelCls}>행 필드(값 저장 위치)</label>
            <select
              className={inputCls}
              value={w.bindingKey}
              onChange={(e) => {
                const nextKey = e.target.value;
                const sub = resolveContractSub(nextKey);
                const fieldLabel = sub
                  ? sub.label
                  : repeatDef.fields.find((f) => f.field === nextKey)?.label;
                onWidgetUpdate(w.id, (x) => {
                  if (x.kind !== 'input') return x;
                  const nx = { ...x, bindingKey: nextKey };
                  if (!nx.label && fieldLabel) nx.label = fieldLabel;
                  if (sub) {
                    // 계약 자식 선택 — 옵션 소스·숫자 기록을 사전값으로 자동 배선(배관 은닉).
                    // 선택지 없는 자식(improvement-image url 등)은 optionsFrom 흔적을 지운다.
                    if (sub.optionsFrom) nx.optionsFrom = { ...sub.optionsFrom };
                    else if (isAutoWiredOptionsFrom(nx.optionsFrom)) delete nx.optionsFrom;
                    if (sub.dataType) nx.dataType = sub.dataType;
                    else delete nx.dataType;
                  } else if (isAutoWiredOptionsFrom(nx.optionsFrom)) {
                    // 계약 자식 → 비계약 전환: 자동 배선 흔적(정확 일치)만 제거(수동 커스텀 보존).
                    delete nx.optionsFrom;
                    delete nx.dataType;
                  }
                  return nx;
                });
              }}
            >
              {repeatRowBindingOptions(repeatDef, w.bindingKey, catalog).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[10px] leading-snug text-gray-400">
              반복 데이터({repeatDef.label})의 행 객체에서 이 입력이 읽고 쓸 필드입니다.
            </p>
            {/* 은닉① 인과 — 이 경로(또는 위험도 등록형 위젯)를 고르는 것 자체가 서버 주입
                스위치다(riskInjectionHint 모듈 주석 — 게이트 두 조건). */}
            {treeWidgetTriggersRiskInjection(w) && (
              <p className="mt-1 text-[10px] leading-snug text-teal-600">
                {RISK_NESTED_BINDING_HINT}
              </p>
            )}
          </div>
        ) : (
          <>
            <p className="font-mono text-[11px] text-gray-400 break-all">{w.bindingKey}</p>
            {/* 반복 밖에서도 서버 게이트는 트리 전체를 본다(templatePlacesRegisteredWidget·
                templateHasNestedRiskBinding 둘 다 트리 재귀) — 같은 안내를 낸다. */}
            {treeWidgetTriggersRiskInjection(w) && (
              <p className="mt-1 text-[10px] leading-snug text-teal-600">
                {RISK_NESTED_BINDING_HINT}
              </p>
            )}
          </>
        )}
        <div>
          <label className={labelCls}>라벨</label>
          <input
            type="text"
            className={inputCls}
            value={w.label ?? ''}
            onChange={(e) => onWidgetUpdate(w.id, (x) => ({ ...x, label: e.target.value }))}
          />
        </div>
        {/* 라벨 병기(labelSuffixFrom) — 라벨 입력 바로 아래(라벨 관련 속성 묶음). registered
            제외(실폼 라벨을 host 위젯 구현이 소유해 rc 병기 미적용)는 디스크립터 applicable 소유. */}
        <WidgetPropSingle
          prop="labelSuffixFrom"
          w={w}
          onWidgetUpdate={onWidgetUpdate}
          ctx={propCtx}
        />
        {/* 시스템 조회 값(prefillFrom) 저작은 「시작값」 통합 구획으로 이관됐다(결정 2026-09-05
            대조표 1) — 여기 있던 「자동 채움(시스템 조회)」 블록은 그 방식 하위 입력이 되었다.
            treeOwned·반복 게이트는 그대로 WidgetStartValueControl이 소유한다. */}
        <div>
          <label className={labelCls}>위젯</label>
          {(() => {
            // 행 스코프는 body dataType 파생 대신 전 위젯 후보(자유 편집 — textarea-list 포함,
            // photo/table/dataSourceRows류 행 안 미지원 종류는 REPEAT_ROW_WIDGETS에서 제외).
            const opts = enclosingRepeat
              ? widgetOptionsForRepeatRow(w.widget)
              : widgetOptionsForDataType(field?.dataType, w.widget);
            // 대안 위젯이 없는 데이터 구조(배열=데이터 표뿐)면 select 대신 고정 표시 —
            // 선택지 1개짜리 select는 조작 여지가 없는데 조작 UI로 보임(2026-07-18 사용자 지적).
            if (opts.length <= 1) {
              return (
                <>
                  <p className="text-sm text-gray-700">
                    {WIDGET_OPTION_LABELS[w.widget] ?? w.widget}
                    <span className="ml-1 text-[11px] text-gray-400">
                      — 이 데이터 구조(배열)는 이 위젯 전용이라 변경할 수 없습니다
                    </span>
                  </p>
                  {/* 위젯을 못 바꾼다는 말만 남기면 "칸마다 다른 입력"의 길이 막힌 것처럼 읽힌다 —
                      실제 길은 칼럼 쪽이다. ⚠️ 이 자리에서 formTable(레이아웃 표)을 대안으로 안내하면
                      안 된다: 작성자가 행을 늘릴 수 없는 다른 구조라, 갈아타면 기능을 잃는다.
                      ⚠️ 위 고정 표시 분기는 표 전용이 아니다(boolean→checkbox도 후보가 1개라 여기 온다)
                      → 표일 때만 덧붙인다. */}
                  {w.widget === 'table' ? (
                    <p className="mt-1 text-[11px] leading-snug text-gray-500">
                      칸마다 다른 입력이 필요하면 위젯이 아니라 <b>이 표의 칼럼에서 지정</b>하세요(칼럼별
                      체크박스·사진 등).
                    </p>
                  ) : null}
                </>
              );
            }
            return (
              <select
                className={inputCls}
                value={w.widget}
                onChange={(e) =>
                  onWidgetUpdate(w.id, (x) =>
                    withWidgetTypeChange(x, e.target.value as WidgetType),
                  )
                }
              >
                {opts.map((opt) => (
                  <option key={opt} value={opt}>
                    {WIDGET_OPTION_LABELS[opt] ?? opt}
                  </option>
                ))}
              </select>
            );
          })()}
        </div>
        {/* 옵션 소스(select·radio) — 직접 입력(정적 options) ↔ 소스 rows(optionsFrom). 위험도 조립처럼
            주입 rows(risk-*-criteria-rows)를 값 소스로 잇는 UI(2026-07-21 사용자: 변수 편집기에만
            있던 optionsFrom 연결을 위젯에도). 변수 optionsFrom UI(VariablesEditor) 미러 — 다만 주입
            키는 카탈로그 목록 밖이라 datalist(콤보+자유 입력)로 소스 키·값/라벨 필드 직접 지정 허용.
            본문/반복 행 스코프 공통(w 편집 = rc 렌더 정본). */}
        {(w.widget === 'select' || w.widget === 'radio') &&
          (() => {
            // '선택지'(드롭다운 항목) — 일반 편집 UI(사람 라벨 select)를 요약 아래 노출하고, raw
            // 키·경로·숫자 수동 제어는 접이식 '고급'으로 격하(2026-07-21 사용자 어휘 확정).
            // 계약 자동 배선(계약 자식 bindingKey + optionsFrom 정확 일치)이면 '평가기준에 따라 자동'.
            const contractSub = resolveContractSub(w.bindingKey);
            const autoWired =
              !!contractSub &&
              !!contractSub.optionsFrom &&
              !!w.optionsFrom &&
              contractSub.optionsFrom.catalogKey === w.optionsFrom.catalogKey &&
              contractSub.optionsFrom.valueField === w.optionsFrom.valueField &&
              contractSub.optionsFrom.labelField === w.optionsFrom.labelField;
            const curKey = w.optionsFrom?.catalogKey ?? '';
            // 라벨 조회는 픽커 주입 목록(optionSourceEntries)과 같은 목록에서 — legacyHidden 제외한
            // 평행 목록을 따로 두면 legacyHidden entry 선택 시 픽커는 보존 한글 라벨·요약은 raw 키로
            // 갈라진다(같은 도메인 두 목록 드리프트, 리뷰 W-E).
            const curSourceLabel =
              optionSourceEntries.find((e) => e.key === curKey)?.label || curKey || '미지정';
            // 자동 배선 요약에도 **어떤 데이터**인지 명시(2026-07-21 제보: "평가기준에 따라 자동"만으론
            // 의미 불명) — 소스 라벨 + 행 필드 연동 사유.
            const optSummary = autoWired
              ? `선택지: ${curSourceLabel} — 행 필드에 맞춰 자동 연동`
              : w.optionsFrom
                ? `선택지: 데이터에서 가져오기(${curSourceLabel})`
                : '선택지: 직접 입력';
            const curFields = w.optionsFrom ? optionSourceFields(curKey) : undefined;
            return (
              <div className="space-y-1.5 rounded border border-gray-200 bg-gray-50 p-2">
                <p className="text-[11px] font-semibold text-gray-700">
                  선택지 <span className="font-normal text-gray-400">· 드롭다운에 보여줄 항목</span>
                </p>
                <p className="text-[11px] font-medium text-gray-600">{optSummary}</p>
                {/* 일반 편집 UI(한글 콤보) — 자동 배선 상태에서도 표시(2026-07-21 제보: 자동이면 요약만
                    보여 고급의 raw 영문 입력만 노출됨 → 현재 연동 내용을 한글 콤보로 확인·변경 가능하게).
                    자동 상태에서 값을 바꾸면 수동 지정이 된다(계약 흔적 제거 규칙은 행 필드 전환 시 적용). */}
                {(
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500 w-20 shrink-0">방식</span>
                      <select
                        value={w.optionsFrom ? 'catalog' : 'static'}
                        onChange={(e) =>
                          onWidgetUpdate(w.id, (x) => {
                            if (x.kind !== 'input') return x;
                            const next = { ...x };
                            if (e.target.value === 'static') {
                              delete next.optionsFrom; // 정적 전환 — 기존 정적 options는 보존
                            } else {
                              next.optionsFrom = { catalogKey: '', valueField: '', labelField: '' };
                            }
                            return next;
                          })
                        }
                        className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                      >
                        <option value="static">직접 입력</option>
                        <option value="catalog">데이터에서 가져오기</option>
                      </select>
                    </div>
                    {/* 직접 입력 — 정적 옵션 목록. 칼럼(DataDefField) 편집기와 **같은 편집기**를 쓴다
                        (문구·동작이 갈라지지 않게). 방식을 데이터로 바꿔도 기존 정적 목록은 보존되고,
                        rc는 동적 해소 실패 시 이 목록으로 폴백한다. 빈 목록이면 키를 저장하지 않는다. */}
                    {!w.optionsFrom && (
                      <div className="space-y-1">
                        <InputOptionsEditor
                          options={w.options}
                          onChange={(options) =>
                            onWidgetUpdate(w.id, (x) => {
                              if (x.kind !== 'input') return x;
                              const next = { ...x };
                              if (options.length > 0) next.options = options;
                              else delete next.options;
                              return next;
                            })
                          }
                        />
                      </div>
                    )}
                    {w.optionsFrom && (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500 w-20 shrink-0">가져올 데이터</span>
                          {/* 공통 코어 CatalogEntryPicker(P3 이관) — 고급의 직접 지정 키 등 목록 밖
                              값은 코어 보존 그룹이 표시 유지. dataType 자동 유도는 기존
                              selectOptionDataSource 경로 그대로. */}
                          <CatalogEntryPicker
                            entries={optionSourceEntries}
                            value={curKey || undefined}
                            onChange={(key) => selectOptionDataSource(key ?? '')}
                            placeholder="데이터 선택…"
                          />
                        </div>
                        {curFields ? (
                          <>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 w-20 shrink-0">저장할 값</span>
                              <select
                                value={w.optionsFrom.valueField}
                                onChange={(e) => selectOptionValueField(e.target.value)}
                                className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                              >
                                {!w.optionsFrom.valueField && <option value="">필드 선택…</option>}
                                {curFields.map((f) => (
                                  <option key={f.path} value={f.path} title={f.path}>
                                    {f.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 w-20 shrink-0">표시할 이름</span>
                              <select
                                value={w.optionsFrom.labelField}
                                onChange={(e) => {
                                  const labelField = e.target.value;
                                  onWidgetUpdate(w.id, (x) =>
                                    x.kind === 'input' && x.optionsFrom
                                      ? { ...x, optionsFrom: { ...x.optionsFrom, labelField } }
                                      : x,
                                  );
                                }}
                                className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white"
                              >
                                {!w.optionsFrom.labelField && <option value="">필드 선택…</option>}
                                {curFields.map((f) => (
                                  <option key={f.path} value={f.path} title={f.path}>
                                    {f.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </>
                        ) : (
                          // 필드 정보 미상(직접 지정 키) — 텍스트 폴백(경로 직접 입력).
                          <>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 w-20 shrink-0">저장할 값</span>
                              <input
                                type="text"
                                value={w.optionsFrom.valueField}
                                onChange={(e) => {
                                  const valueField = e.target.value;
                                  onWidgetUpdate(w.id, (x) =>
                                    x.kind === 'input' && x.optionsFrom
                                      ? { ...x, optionsFrom: { ...x.optionsFrom, valueField } }
                                      : x,
                                  );
                                }}
                                placeholder="값 필드"
                                className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white font-mono"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 w-20 shrink-0">표시할 이름</span>
                              <input
                                type="text"
                                value={w.optionsFrom.labelField}
                                onChange={(e) => {
                                  const labelField = e.target.value;
                                  onWidgetUpdate(w.id, (x) =>
                                    x.kind === 'input' && x.optionsFrom
                                      ? { ...x, optionsFrom: { ...x.optionsFrom, labelField } }
                                      : x,
                                  );
                                }}
                                placeholder="라벨 필드"
                                className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white font-mono"
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
                {/* 고급 — 직접 지정(키·필드 경로·숫자 수동). raw 편집기 유지(데이터 모드에서만 유의미). */}
                {w.optionsFrom && (
                  <details>
                    <summary className="cursor-pointer text-[11px] text-gray-500">
                      고급 — 직접 지정(키·필드 경로)
                    </summary>
                    <div className="space-y-1.5 pt-1.5">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 w-16 shrink-0">소스 키</span>
                        <input
                          type="text"
                          list={`opt-src-catalog-${w.id}`}
                          value={w.optionsFrom.catalogKey}
                          onChange={(e) => {
                            const catalogKey = e.target.value;
                            onWidgetUpdate(w.id, (x) =>
                              x.kind === 'input' && x.optionsFrom
                                ? { ...x, optionsFrom: { ...x.optionsFrom, catalogKey } }
                                : x,
                            );
                          }}
                          placeholder="시스템 조회 목록 또는 주입 키(예: risk-likelihood-criteria-rows)"
                          title="옵션 rows를 만들 소스 — 시스템 조회 목록 항목 또는 런타임 주입 키(직접 입력 가능)"
                          className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white font-mono"
                        />
                        <datalist id={`opt-src-catalog-${w.id}`}>
                          {optSrcListEntries.map((e) => (
                            <option key={e.key} value={e.key}>
                              {e.label}
                            </option>
                          ))}
                        </datalist>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 w-16 shrink-0">값/라벨</span>
                        <input
                          type="text"
                          list={`opt-src-fields-${w.id}`}
                          value={w.optionsFrom.valueField}
                          onChange={(e) => {
                            const valueField = e.target.value;
                            onWidgetUpdate(w.id, (x) =>
                              x.kind === 'input' && x.optionsFrom
                                ? { ...x, optionsFrom: { ...x.optionsFrom, valueField } }
                                : x,
                            );
                          }}
                          placeholder="값 필드"
                          title="옵션 값(value) 필드 — 저장되는 값(예: level)"
                          className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white font-mono"
                        />
                        <input
                          type="text"
                          list={`opt-src-fields-${w.id}`}
                          value={w.optionsFrom.labelField}
                          onChange={(e) => {
                            const labelField = e.target.value;
                            onWidgetUpdate(w.id, (x) =>
                              x.kind === 'input' && x.optionsFrom
                                ? { ...x, optionsFrom: { ...x.optionsFrom, labelField } }
                                : x,
                            );
                          }}
                          placeholder="라벨 필드"
                          title="옵션 라벨(label) 필드 — 표시되는 이름(예: label)"
                          className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded bg-white font-mono"
                        />
                        <datalist id={`opt-src-fields-${w.id}`}>
                          {optSrcFieldPaths(w.optionsFrom.catalogKey).map((f) => (
                            <option key={f.path} value={f.path}>
                              {f.label} ({f.path})
                            </option>
                          ))}
                        </datalist>
                      </div>
                      <label className="flex items-center gap-1.5 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={w.dataType === 'number'}
                          onChange={(e) =>
                            onWidgetUpdate(w.id, (x) => {
                              if (x.kind !== 'input') return x;
                              const next = { ...x };
                              if (e.target.checked) next.dataType = 'number';
                              else delete next.dataType;
                              return next;
                            })
                          }
                        />
                        <span title="옵션 값을 숫자(number)로 기록 — 기준표 level처럼 숫자 소스일 때. 미체크면 문자열로 저장.">
                          값을 숫자로 기록
                        </span>
                      </label>
                      <p className="text-[11px] leading-4 text-gray-400">
                        소스 rows에서 옵션을 만듭니다 — 값 필드가 저장값, 라벨 필드가 표시입니다. rows가
                        해소되지 않는 환경(외부 위임 등)에서는 위젯의 정적 옵션이 폴백으로 쓰입니다.
                      </p>
                    </div>
                  </details>
                )}
              </div>
            );
          })()}
        {/* placeholder — rc가 text/number/textarea/combobox 컨트롤에만 전달(그 외 위젯은 무의미라 미노출).
            타입·렌더는 기존 지원, 속성창 노출만 없었음(2026-07-16 사용자 질문). */}
        {w.widget === 'text' ||
        w.widget === 'textarea' ||
        w.widget === 'number' ||
        w.widget === 'combobox' ? (
          <div>
            <label className={labelCls}>안내 문구 (placeholder)</label>
            <input
              className={inputCls}
              value={w.placeholder ?? ''}
              placeholder="입력 전 회색으로 표시될 안내"
              onChange={(e) =>
                onWidgetUpdate(w.id, (x) =>
                  x.kind === 'input'
                    ? { ...x, placeholder: e.target.value || undefined }
                    : x,
                )
              }
            />
          </div>
        ) : null}
        {/* 입력 형식 — text 위젯 전용(일반/전화번호). 노출 게이트는 디스크립터 applicable 소유. */}
        <WidgetPropSingle prop="inputFormat" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* 선택지 표시(라디오 버튼/가로 버튼) — radio 전용. 선택지 편집 바로 아래에 둔다(같은
            선택지 축). 게이트는 디스크립터 applicable 소유. */}
        <WidgetPropSingle
          prop="displayVariant"
          w={w}
          onWidgetUpdate={onWidgetUpdate}
          ctx={propCtx}
        />
        {/* 시작값(고정값·시스템 조회 값 하나·조회된 행들에서 만들기) — placeholder와 같은 값 속성 묶음.
            방식별 하위 입력·게이트는 디스크립터 소유(결정 2026-09-05 대조표 1로 3구획 통합). */}
        <WidgetPropSingle prop="startValue" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        <WidgetPropSingle prop="required" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        <WidgetPropSingle prop="mode" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* 편집 진입(항상 입력/클릭 시 편집) — text/textarea 전용. 게이트는 디스크립터 applicable 소유. */}
        <WidgetPropSingle prop="editEntry" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* 긴 텍스트 접기(N줄 + 펼치기) — 편집 진입과 같은 위젯 집합. 게이트는 디스크립터 소유. */}
        <WidgetPropSingle prop="collapseLines" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* 읽기 표시 형식 — 본문 셀 바인딩과 같은 편집기(FormatFields) 재사용. 위험성평가 배지 등. */}
        <WidgetPropSingle prop="displayFormat" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* 검색 제안 소스(suggestionsFrom) — text/목록형 전용. 게이트는 디스크립터 applicable 소유. */}
        <WidgetPropSingle
          prop="suggestionsFrom"
          w={w}
          onWidgetUpdate={onWidgetUpdate}
          ctx={propCtx}
        />
        {/* 라벨 표시 계열(보조 설명 → 위치 → 정렬 → 폰트) — 구 LabelPresentationControls를 축별
            디스크립터로 분해해 복수 패널과 같은 Control을 공유한다(2026-08-24). 게이트(정렬=top
            전용, 폰트·보조 설명=라벨 있을 때만)는 디스크립터 applicable 소유. */}
        <WidgetPropSingle prop="labelHint" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        <WidgetPropSingle
          prop="labelPlacement"
          w={w}
          onWidgetUpdate={onWidgetUpdate}
          ctx={propCtx}
        />
        <WidgetPropSingle prop="labelAlign" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        <WidgetPropSingle prop="labelStyle" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
        {/* sortableSelect 저작 — 복수 후보 소스 + 소스별 필드 매핑. combobox는 단일 카탈로그 소스
            시맨틱이라 전용 섹션으로 분리(2026-07-17 도그푸딩 — 공유 UI가 combobox 저작을 과복잡화). */}
        {w.widget === 'sortableSelect' &&
          (() => {
            const selectedSources = effectiveOptionsSources(w);
            const sourceOptions = collectSortableSourceOptions(dataDefs, dataSources, catalog);
            // 저장돼 있으나 목록에 없는 키(알 수 없는 소스)도 체크 옵션으로 보존(재저장 시 소실 방지).
            const optionKeys = new Set(sourceOptions.map((o) => o.key));
            const allOptions: SortableSourceOption[] = [
              ...sourceOptions,
              ...selectedSources
                .filter((k) => !optionKeys.has(k))
                .map((k) => ({
                  key: k,
                  label: `${k} (알 수 없는 소스 — 보존)`,
                  category: SORTABLE_SOURCE_CATEGORIES.preserved,
                  fields: [] as { key: string; label: string }[],
                })),
            ];
            // 소스 신규 체크 시 기본 매핑 시드 — AI 소스는 고정 스키마 4필드 자동, 그 외는
            // fields에 'key'/'text'가 있으면 그 둘만 자동(없으면 비움 → 위젯 기본 key/text).
            const seedFieldMap = (
              opt: SortableSourceOption | undefined,
            ): SortableSourceFieldMap | undefined => {
              if (!opt) return undefined;
              if (opt.category === SORTABLE_SOURCE_CATEGORIES.ai)
                return { keyField: 'key', textField: 'text', badgeField: 'badge', flagField: 'fromCache' };
              const entry: SortableSourceFieldMap = {};
              if (opt.fields.some((f) => f.key === 'key')) entry.keyField = 'key';
              if (opt.fields.some((f) => f.key === 'text')) entry.textField = 'text';
              return Object.keys(entry).length > 0 ? entry : undefined;
            };

            const toggleSource = (key: string, checked: boolean): void =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'input') return x;
                const current = effectiveOptionsSources(x);
                const nextSel = checked ? [...current, key] : current.filter((k) => k !== key);
                const next = { ...x };
                delete next.optionsSource; // 단수 → 복수 이전(하위호환 저장값 마이그레이션)
                if (nextSel.length > 0) next.optionsSources = nextSel;
                else delete next.optionsSources;
                // 소스별 매핑 동기화: 체크 시 기본값 시드(기존 entry 없을 때만), 해제 시 entry 제거.
                const maps: Record<string, SortableSourceFieldMap> = { ...x.sourceFieldMaps };
                if (checked) {
                  if (!maps[key]) {
                    const seeded = seedFieldMap(allOptions.find((o) => o.key === key));
                    if (seeded) maps[key] = seeded;
                  }
                } else {
                  delete maps[key];
                }
                if (Object.keys(maps).length > 0) next.sourceFieldMaps = maps;
                else delete next.sourceFieldMaps;
                return next;
              });

            type SourceFieldProp =
              | 'keyField'
              | 'textField'
              | 'badgeField'
              | 'flagField'
              | 'textTemplate'
              | 'badgeTemplate';
            const setSourceField = (sourceKey: string, prop: SourceFieldProp, value: string): void =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'input') return x;
                const maps: Record<string, SortableSourceFieldMap> = { ...x.sourceFieldMaps };
                // entry 신설 시 위젯 레벨 legacy 필드(itemKeyField 등)를 base로 시드 — rc 정규화의
                // "맵이 있으면 그 맵으로만"(위젯 레벨과 혼합 없음) 규약 때문에, 슬롯 하나만 만들면
                // legacy 저작물의 나머지 유효 필드가 조용히 기본값(key/text)으로 강등된다.
                // combobox setComboTemplate의 맵 신설 시 자동 규칙 전체 시드와 같은 이유의 대응물 —
                // 저쪽 base는 소스 스키마 자동 규칙(comboboxAutoFieldMap), 이쪽 base는 위젯 legacy 값
                // (sortableSelect는 소스별 자동 규칙이 없고 toggleSource 시드는 legacy 위젯에 없다).
                const entry: SortableSourceFieldMap = maps[sourceKey]
                  ? { ...maps[sourceKey] }
                  : {
                      ...(x.itemKeyField ? { keyField: x.itemKeyField } : {}),
                      ...(x.itemTextField ? { textField: x.itemTextField } : {}),
                      ...(x.itemBadgeField ? { badgeField: x.itemBadgeField } : {}),
                      ...(x.itemFlagField ? { flagField: x.itemFlagField } : {}),
                    };
                if (value) entry[prop] = value;
                else delete entry[prop]; // 빈 값 선택 = 그 필드 삭제
                if (Object.keys(entry).length > 0) maps[sourceKey] = entry;
                else delete maps[sourceKey]; // 맵이 전부 비면 entry 삭제(→ 위젯 legacy fallback)
                const next = { ...x };
                if (Object.keys(maps).length > 0) next.sourceFieldMaps = maps;
                else delete next.sourceFieldMaps;
                return next;
              });

            const FIELD_MAP_SLOTS: { prop: SourceFieldProp; label: string; placeholder: string }[] = [
              { prop: 'keyField', label: '키 필드', placeholder: 'key' },
              { prop: 'textField', label: '본문 필드', placeholder: 'text' },
              { prop: 'badgeField', label: '배지 필드(선택)', placeholder: '' },
              { prop: 'flagField', label: '플래그 필드(선택)', placeholder: '' },
            ];
            // 소스 1건의 필드 매핑 편집 — 그 소스의 fields로 셀렉트, fields 미상(보존 소스)은 직접 입력 폴백.
            const renderSourceFieldMap = (o: SortableSourceOption) => {
              const map: SortableSourceFieldMap = w.sourceFieldMaps?.[o.key] ?? {};
              // 조합 템플릿 사용 중인 축의 단일 필드 슬롯은 숨김(rc가 template 우선 — 이중 노출 혼동 방지).
              const slots = FIELD_MAP_SLOTS.filter(
                ({ prop }) =>
                  !(prop === 'textField' && map.textTemplate !== undefined) &&
                  !(prop === 'badgeField' && map.badgeTemplate !== undefined),
              );
              return (
                <div key={o.key} className="space-y-1 rounded border border-gray-200 bg-white p-2">
                  <p className="text-[11px] font-medium text-gray-600">{o.label}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {slots.map(({ prop, label, placeholder }) => {
                      const currentVal = map[prop] ?? '';
                      if (o.fields.length === 0) {
                        // 필드 스키마 미상(보존/알 수 없는 소스) → 직접 입력 폴백(저장값 보존).
                        return (
                          <div key={prop}>
                            <label className={labelCls}>{label}</label>
                            <input
                              className={inputCls}
                              value={currentVal}
                              placeholder={placeholder || '위젯 기본(key/text)'}
                              onChange={(e) => setSourceField(o.key, prop, e.target.value)}
                            />
                          </div>
                        );
                      }
                      const hasCurrent = !currentVal || o.fields.some((f) => f.key === currentVal);
                      // 기본 필드(key/text)가 이 소스에 실재할 때만 "(기본: …)" 표기 — 없는 소스에서
                      // 비워 두면 정규화가 빈 값을 읽으므로(본문 공백 행) "(선택 필요)"로 경고.
                      const defaultExists =
                        !placeholder || o.fields.some((f) => f.key === placeholder);
                      return (
                        <div key={prop}>
                          <label className={labelCls}>{label}</label>
                          <select
                            className={inputCls}
                            value={currentVal}
                            onChange={(e) => setSourceField(o.key, prop, e.target.value)}
                          >
                            <option value="">
                              {placeholder
                                ? defaultExists
                                  ? `(기본: ${placeholder})`
                                  : `(선택 필요 — 이 소스에 '${placeholder}' 필드 없음)`
                                : '선택 안 함'}
                            </option>
                            {o.fields.map((f) => (
                              <option key={f.key} value={f.key}>
                                {f.label} ({f.key})
                              </option>
                            ))}
                            {!hasCurrent && (
                              <option value={currentVal}>{currentVal} (목록에 없음 — 보존)</option>
                            )}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  {/* 표시 조합(textTemplate/badgeTemplate) — 본문/배지를 여러 필드 조합으로.
                      rc 정규화가 template 우선(미지정 시 위 단일 필드 그대로 — 하위호환).
                      fields 미상(보존 소스)은 저장된 템플릿이 있을 때만 원문 보존 폴백 노출.
                      결정: 2026-08-24-composer-display-template-axis. */}
                  {(o.fields.length > 0 ||
                    map.textTemplate !== undefined ||
                    map.badgeTemplate !== undefined) && (
                    <div className="space-y-2 border-t border-gray-100 pt-1.5">
                      {(o.fields.length > 0 || map.textTemplate !== undefined) && (
                        <DisplayTemplateBuilder
                          key={`${w.id}:${o.key}:text`}
                          label="본문 표시"
                          singleModeLabel="단일 필드(위 본문 필드)"
                          template={map.textTemplate}
                          fields={o.fields.map((f) => ({ key: f.key, label: f.label }))}
                          defaultField={map.textField}
                          onChange={(t) => setSourceField(o.key, 'textTemplate', t ?? '')}
                          labelCls={labelCls}
                          inputCls={inputCls}
                        />
                      )}
                      {(o.fields.length > 0 || map.badgeTemplate !== undefined) && (
                        <DisplayTemplateBuilder
                          key={`${w.id}:${o.key}:badge`}
                          label="배지 표시"
                          singleModeLabel="단일 필드(위 배지 필드)"
                          template={map.badgeTemplate}
                          fields={o.fields.map((f) => ({ key: f.key, label: f.label }))}
                          defaultField={map.badgeField}
                          onChange={(t) => setSourceField(o.key, 'badgeTemplate', t ?? '')}
                          labelCls={labelCls}
                          inputCls={inputCls}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            };

            return (
              <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
                <p className="text-[11px] font-medium text-gray-500">선택 정렬 목록 설정</p>
                <div>
                  <label className={labelCls}>후보 소스(복수 선택)</label>
                  <div className="max-h-56 space-y-2 overflow-y-auto rounded border border-gray-200 bg-white p-2">
                    {Object.values(SORTABLE_SOURCE_CATEGORIES).map((category) => {
                      const opts = allOptions.filter((o) => o.category === category);
                      if (opts.length === 0) return null;
                      const hint = SORTABLE_SOURCE_CATEGORY_HINTS[category];
                      return (
                        <div key={category} className="space-y-1">
                          <p className="text-[11px] font-semibold text-gray-500">{category}</p>
                          {hint && <p className="text-[10px] leading-4 text-gray-400">{hint}</p>}
                          {opts.map((o) => (
                            <label
                              key={o.key}
                              className="flex items-start gap-2 pl-1 text-xs text-gray-700"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={selectedSources.includes(o.key)}
                                onChange={(e) => toggleSource(o.key, e.target.checked)}
                              />
                              <span>{o.label}</span>
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-[11px] leading-4 text-gray-400">
                    후보 항목 rows를 가져올 소스를 카테고리에서 고릅니다. 여러 개 선택하면 지정
                    순서대로 합쳐집니다. rows가 0개면(그리고 모달 버튼의 &quot;데이터 없으면
                    숨김&quot;이 켜져 있으면) 버튼이 숨겨집니다.
                  </p>
                </div>
                {selectedSources.length > 0 && (
                  <div>
                    <label className={labelCls}>소스별 필드 매핑</label>
                    <div className="space-y-2">
                      {selectedSources.map((k) => {
                        const o = allOptions.find((opt) => opt.key === k);
                        return o ? renderSourceFieldMap(o) : null;
                      })}
                    </div>
                    <p className="mt-1 text-[11px] leading-4 text-gray-400">
                      소스마다 행 구조가 달라도 각자 매핑으로 정규화되어 합쳐집니다. 매핑을 비우면
                      위젯 기본(key/text)을 사용합니다.
                    </p>
                  </div>
                )}
                <div>
                  <label className={labelCls}>보조 배지 라벨</label>
                  <input
                    className={inputCls}
                    value={w.itemFlagLabel ?? ''}
                    placeholder="캐시 사용"
                    onChange={(e) =>
                      onWidgetUpdate(w.id, (x) =>
                        x.kind === 'input' ? { ...x, itemFlagLabel: e.target.value || undefined } : x,
                      )
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>조립 접두</label>
                  <input
                    className={inputCls}
                    value={w.composePrefix ?? ''}
                    placeholder="- "
                    onChange={(e) =>
                      onWidgetUpdate(w.id, (x) =>
                        x.kind === 'input' ? { ...x, composePrefix: e.target.value || undefined } : x,
                      )
                    }
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={!!w.showPreview}
                    onChange={(e) =>
                      onWidgetUpdate(w.id, (x) =>
                        x.kind === 'input' ? { ...x, showPreview: e.target.checked || undefined } : x,
                      )
                    }
                  />
                  미리보기 표시
                </label>
              </div>
            );
          })()}
        {/* combobox(제안형 콤보) 전용 저작 — 제안 소스는 단일 카탈로그 목록(list data entry)만
            (AI 생성·내장 데이터셋·조립 소스 제외), 필드 매핑은 제안 텍스트/병기 표시 2select로
            단순화(2026-07-17 도그푸딩 — sortableSelect 공유 UI가 combobox 저작을 과복잡화).
            저장 모델은 기존 그대로 optionsSources(1개)·sourceFieldMaps(keyField=textField 동일,
            flagField 미사용) — rc 렌더·서버 합성 라우팅(collectWriteBackWidgets)과 호환. */}
        {w.widget === 'combobox' &&
          (() => {
            const catalogListEntries = (catalog ?? []).filter(
              (e): e is Extract<CatalogEntry, { kind: 'data' }> =>
                e.kind === 'data' && e.cardinality === 'list',
            );
            // 기존 저장값이 복수/비카탈로그 소스면 첫 카탈로그 키만 표시 — 이 UI로 저장하는 순간
            // 단일 소스로 정규화된다(combobox는 단일 제안 소스 시맨틱).
            const selectedKey =
              effectiveOptionsSources(w).find((k) =>
                catalogListEntries.some((e) => e.key === k),
              ) ?? '';
            const entry = catalogListEntries.find((e) => e.key === selectedKey);
            const map = selectedKey ? w.sourceFieldMaps?.[selectedKey] : undefined;

            const setSource = (key: string): void =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'input') return x;
                const next = { ...x };
                delete next.optionsSource; // 단수 → 복수 이전(하위호환 저장값 마이그레이션)
                delete next.sourceFieldMaps; // 소스 교체/해제 = 이전 소스 매핑 폐기(단일화)
                if (!key) {
                  delete next.optionsSources;
                  return next;
                }
                next.optionsSources = [key];
                // 필드 매핑은 자동 규칙(comboboxAutoFieldMap — 서버 합성 라우팅과 공유)으로 자동 저장:
                // keyField=규칙값(upsertIdField 선언 소스는 id — 동명이인 행 key 고유), textField=규칙값,
                // badgeField=규칙값, idField=카탈로그 upsertIdField 복사(자동 합성 채널 — rc가 제안 선택
                // 시 행 id를 content 합성 키에 기록). 저작자 선택 UI 없음(2026-07-17 정정 —
                // 제안 내용=입력 내용이라 선택 여지가 없음). flagField 미사용.
                const en = catalogListEntries.find((e) => e.key === key);
                const seeded = en ? comboboxAutoFieldMap(en) : undefined;
                if (seeded?.textField)
                  next.sourceFieldMaps = {
                    [key]: {
                      keyField: seeded.keyField ?? seeded.textField,
                      textField: seeded.textField,
                      ...(seeded.badgeField ? { badgeField: seeded.badgeField } : {}),
                      ...(seeded.idField ? { idField: seeded.idField } : {}),
                    },
                  };
                return next;
              });

            // 표시 조합(textTemplate/badgeTemplate) 저장 — 값 없음 = 키 삭제. legacy 저작물(맵
            // 미저장)에 템플릿 키만 만들면 "맵이 있으면 그 맵으로만" 규약(rc 정규화) 때문에
            // key/text가 기본값('key'/'text')으로 깨지므로, 맵 신설 시 자동 규칙 전체를 먼저
            // 시드한다(setSource의 시드와 동형). 결정: 2026-08-24-composer-display-template-axis.
            const setComboTemplate = (
              prop: 'textTemplate' | 'badgeTemplate',
              value: string | undefined,
            ): void =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'input' || !selectedKey) return x;
                const maps: Record<string, SortableSourceFieldMap> = { ...x.sourceFieldMaps };
                let base: SortableSourceFieldMap | undefined = maps[selectedKey];
                if (!base) {
                  const seeded = entry ? comboboxAutoFieldMap(entry) : undefined;
                  base = seeded?.textField
                    ? {
                        keyField: seeded.keyField ?? seeded.textField,
                        textField: seeded.textField,
                        ...(seeded.badgeField ? { badgeField: seeded.badgeField } : {}),
                        ...(seeded.idField ? { idField: seeded.idField } : {}),
                      }
                    : {};
                }
                const nextEntry: SortableSourceFieldMap = { ...base };
                if (value) nextEntry[prop] = value;
                else delete nextEntry[prop];
                if (Object.keys(nextEntry).length > 0) maps[selectedKey] = nextEntry;
                else delete maps[selectedKey];
                const next = { ...x };
                if (Object.keys(maps).length > 0) next.sourceFieldMaps = maps;
                else delete next.sourceFieldMaps;
                return next;
              });

            // 표시용 유효 매핑 — 저장 정본(sourceFieldMaps) 우선, 없으면 자동 규칙(소스 선택 시 자동
            // 저장되므로 legacy 저작물 표시 fallback). 선택 UI 없음(2026-07-17 정정).
            const auto = entry ? comboboxAutoFieldMap(entry) : undefined;
            const effTextField = map?.textField ?? auto?.textField;
            const effBadgeField = map?.badgeField ?? auto?.badgeField;
            const fieldLabel = (path: string | undefined): string | undefined =>
              path ? (entry?.returns.find((r) => r.path === path)?.label ?? path) : undefined;

            return (
              <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
                <p className="text-[11px] font-medium text-gray-500">제안형 콤보 설정</p>
                <div>
                  <label className={labelCls}>제안 소스(시스템 조회 목록)</label>
                  {/* 공통 코어 CatalogEntryPicker(P3 이관) — 검색·그룹 헤더·legacyHidden 제외는
                      코어가. 저장은 기존 관행 그대로 배열 optionsSources(1개)·해제는 ''→setSource가
                      복수/매핑 폐기 부수효과 수행(단일화). */}
                  <CatalogEntryPicker
                    entries={catalogListEntries}
                    value={selectedKey || undefined}
                    onChange={(key) => setSource(key ?? '')}
                    allowClear
                    clearLabel="없음(일반 텍스트 입력)"
                    placeholder="없음(일반 텍스트 입력)"
                  />
                  <p className="mt-1 text-[11px] leading-4 text-gray-400">
                    시스템 조회 목록(list) 반환 rows가 입력값 부분일치 제안이 됩니다(작성 시점
                    컨텍스트로 서버가 채움). 소스가 없거나 rows가 0개면 일반 텍스트 입력으로
                    동작합니다.
                  </p>
                </div>
                {/* 선택 강제 — suggestionsFrom requireSelection과 같은 축 규약(칸 단위 선택,
                    shared InputWidget doc이 정본). 제안 소스와 독립(드롭다운 액션의 등록 모달만으로도
                    성립)이라 소스 유무와 무관하게 노출. 해제는 키 삭제가 아니라 **명시적 false 저장** —
                    삭제하면 위젯 종류 왕복 시 withWidgetTypeChange의 `=== undefined` 시드가 꺼둔 값을
                    true로 재시드한다(W8, widgetTypeChange.ts 주석이 정본). */}
                <label className="flex items-start gap-1.5 text-[11px] leading-4 text-gray-600">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={w.requireSelection === true}
                    onChange={(e) =>
                      onWidgetUpdate(w.id, (x) =>
                        x.kind === 'input' ? { ...x, requireSelection: e.target.checked } : x,
                      )
                    }
                  />
                  <span>
                    제안에서만 선택(자유 입력 금지)
                    <span className="mt-0.5 block text-gray-400">
                      켜면 타이핑한 글자는 제안 검색어로만 쓰이고, 제안 항목(또는 드롭다운 액션의
                      등록 모달)을 골라야 값이 들어갑니다. 필수와 함께 켜면 고르기 전엔 제출이
                      차단됩니다.
                    </span>
                  </span>
                </label>
                {/* 내 입력 누적(suggestionDictScope) — 입력값을 현장/조직 사전에 쌓아 다음 보고서
                    제안으로 자동 표시. 제안 소스와 독립(사전 키는 rc가 암묵 소스로 append —
                    optionsSources/sourceFieldMaps 저작 불요). shared InputWidget doc이 규약 정본. */}
                <div>
                  <label className={labelCls}>내 입력 누적</label>
                  <select
                    className={inputCls}
                    value={w.suggestionDictScope ?? ''}
                    onChange={(e) =>
                      onWidgetUpdate(w.id, (x) => {
                        if (x.kind !== 'input') return x;
                        const next = { ...x };
                        const v = e.target.value;
                        if (v === 'site' || v === 'organization') next.suggestionDictScope = v;
                        else delete next.suggestionDictScope;
                        return next;
                      })
                    }
                  >
                    <option value="">끔</option>
                    <option value="site">현장별</option>
                    <option value="organization">조직별</option>
                  </select>
                  <p className="mt-1 text-[11px] leading-4 text-gray-400">
                    켜면 이 칸에 입력한 값이 쌓여 다음 보고서부터 제안으로 자동 표시됩니다.
                  </p>
                </div>
                {entry ? (
                  <>
                    {/* 필드 매핑은 자동 규칙(comboboxAutoFieldMap) — 선택 여지가 없어 읽기 전용 안내만.
                        (표시 조합을 켜면 제안·확정 텍스트만 조합으로 대체 — 필드 매핑 자체는 불변.) */}
                    <p className="rounded border border-gray-200 bg-white px-2 py-1.5 text-[11px] leading-4 text-gray-600">
                      제안: {fieldLabel(effTextField) ?? '(유도 불가 — 이 소스에 텍스트 필드 없음)'} ·
                      병기: {fieldLabel(effBadgeField) ?? '없음'}
                    </p>
                    {/* 표시 조합 — 제안 텍스트/병기를 여러 필드 조합으로(예: "직위 이름"). 저장은
                        sourceFieldMaps[소스].textTemplate/badgeTemplate — rc 정규화가 shared 합성
                        정본으로 template 우선 적용(확정값도 조합 문자열이 content에 실림). 행 id
                        필드(upsertIdField)는 식별 축이라 조합 후보에서 제외.
                        결정: 2026-08-24-composer-display-template-axis. */}
                    {(() => {
                      const templateFields = entry.returns
                        .filter((r) => r.path !== entry.writeBack?.upsertIdField)
                        .map((r) => ({ key: r.path, label: r.label }));
                      return (
                        <div className="space-y-2">
                          <DisplayTemplateBuilder
                            key={`${w.id}:combo:text`}
                            label="제안·입력값 표시"
                            singleModeLabel={`자동(${fieldLabel(effTextField) ?? '유도 불가'})`}
                            template={map?.textTemplate}
                            fields={templateFields}
                            defaultField={effTextField}
                            onChange={(t) => setComboTemplate('textTemplate', t)}
                            labelCls={labelCls}
                            inputCls={inputCls}
                          />
                          <DisplayTemplateBuilder
                            key={`${w.id}:combo:badge`}
                            label="병기(배지) 표시"
                            singleModeLabel={`자동(${fieldLabel(effBadgeField) ?? '없음'})`}
                            template={map?.badgeTemplate}
                            fields={templateFields}
                            defaultField={effBadgeField ?? effTextField}
                            onChange={(t) => setComboTemplate('badgeTemplate', t)}
                            labelCls={labelCls}
                            inputCls={inputCls}
                          />
                          {map?.textTemplate !== undefined && (
                            <p className="text-[11px] leading-4 text-gray-400">
                              제안을 선택하면 조합된 문자열이 입력값으로 저장됩니다(예: &quot;소장
                              김소장&quot;). 이름 그대로 저장해야 하는 칸이면 조합 대신
                              자동(단일 필드)을 유지하세요.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    {/* 선택 전파 매핑(writes) — P2 계약: 제안 행 선택 시에만 raw 필드 전파.
                        source=단일 제안 소스(카탈로그 entry) returns 필드, target=본문 위젯/표 셀
                        (캔버스 hover 강조 — modalTrigger commit 매핑과 동형 배선) + 배열 일괄 대상. */}
                    <WritesMappingEditor
                      writes={w.writes ?? []}
                      onChange={(next) =>
                        onWidgetUpdate(w.id, (x) =>
                          x.kind === 'input'
                            ? { ...x, writes: next.length ? next : undefined }
                            : x,
                        )
                      }
                      // source 후보 — 단일 제안 소스(카탈로그 entry)의 returns 필드(path/label).
                      // (entry는 바깥 `entry ? …` 분기 안이지만, 클로저 narrowing은 TS 버전 의존이라 옵셔널로.)
                      sourceOptions={(entry?.returns ?? []).map((r) => ({
                        key: r.path,
                        label: r.label,
                      }))}
                      // target 후보 — 본문 위젯/표 셀(hover 강조 정보 포함, 자기 자신 제외) + 배열 일괄
                      // (writesTargetProps — 반복 children이면 행 스코프). 자기 키는 제외 — 콤보 선택
                      // 메모리(rowText)가 확정 텍스트≠rowText를 회수 트리거로 보므로 자기 키를 덮으면
                      // 다음 입력에서 즉시 ''로 회수된다(repeatScopeWritesTargets doc).
                      {...writesTargetProps({
                        w,
                        tree,
                        enclosingRepeat,
                        repeatDef: repeatRowScopeDef,
                        catalog,
                        dataDefs,
                        dataSources,
                        allowSelf: false,
                      })}
                      onHoverHighlightWidget={onHoverHighlightWidget}
                      onFtCellHover={onFtCellHover}
                      title="선택 전파 매핑"
                      emptyText="제안 선택 시 선택 행의 필드 값을 다른 위젯에 쓰려면 매핑을 추가하세요."
                      sourceLabel="행 필드"
                      sourcePlaceholder="행 필드 검색…"
                      helpText="제안 항목을 선택하면 그 행의 필드 값(예: 단위)이 대상 위젯에 자동 기록됩니다. 직접 타이핑한 값은 전파되지 않습니다."
                      labelCls={labelCls}
                    />
                  </>
                ) : null}
                {/* 드롭다운 액션 항목 — 제안 목록 하단 고정 항목(클릭 시 지정 모달 열기, 구 SMS
                    "+새 담당자 추가" 동형). 제안 소스와 독립(소스 없음/rows 0이어도 드롭다운 유지).
                    결정: 2026-07-17-composer-widget-preset-and-registered-widget-path(액션 항목 축). */}
                {(() => {
                  const ai = w.actionItem;
                  const modalCandidates = collectActionItemModalCandidates(tree, w.id);
                  const baseOptions: ComboboxOption[] = modalCandidates.map((m) => ({
                    value: m.id,
                    label: m.label || m.modalTitle || m.id,
                    key: m.id,
                    badge: 'modalTrigger',
                  }));
                  // 저장값이 후보에 없으면(대상 삭제 등) 보존 옵션으로 노출(재저장 시 소실 방지).
                  const options: ComboboxOption[] =
                    ai?.opensWidgetId && !modalCandidates.some((m) => m.id === ai.opensWidgetId)
                      ? [
                          ...baseOptions,
                          {
                            value: ai.opensWidgetId,
                            label: `${ai.opensWidgetId} (목록에 없음 — 보존)`,
                            key: ai.opensWidgetId,
                          },
                        ]
                      : baseOptions;
                  const updateActionItem = (
                    fn: (x: InputWidget) => InputWidget,
                  ): void =>
                    onWidgetUpdate(w.id, (x) => (x.kind === 'input' ? fn({ ...x }) : x));
                  return (
                    <div className="space-y-1.5 border-t border-gray-200 pt-2">
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={!!ai}
                          onChange={(e) =>
                            updateActionItem((x) => {
                              if (e.target.checked)
                                x.actionItem = { label: '+ 새 항목 추가', opensWidgetId: '' };
                              else delete x.actionItem;
                              return x;
                            })
                          }
                        />
                        드롭다운 액션 항목(모달 열기)
                      </label>
                      {ai ? (
                        <>
                          <div>
                            <label className={labelCls}>항목 라벨</label>
                            <input
                              className={inputCls}
                              value={ai.label}
                              onChange={(e) =>
                                updateActionItem((x) => {
                                  // 현재 상태(x) 우선 — 렌더 시점 ai는 표시용(연타 갱신 유실 방지).
                                  x.actionItem = { ...(x.actionItem ?? ai), label: e.target.value };
                                  return x;
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className={labelCls}>여는 모달 버튼</label>
                            {/* 옵션 hover 시 대상 modalTrigger 캔버스 강조. 닫힘 상태 hover 이동
                                없음 — 이동은 버튼 클릭으로만(JumpToWidgetButton 정책). */}
                            <div className="flex items-center gap-1.5">
                              <div className="min-w-0 flex-1">
                                {/* eslint-disable-next-line local/catalog-picker-required -- 여는 모달 버튼 선택(modalTrigger 위젯 id 목록) — 카탈로그 엔트리 아님 */}
                                <CatalogCombobox
                                  value={ai.opensWidgetId || undefined}
                                  options={options}
                                  onChange={(v) =>
                                    updateActionItem((x) => {
                                      x.actionItem = { ...(x.actionItem ?? ai), opensWidgetId: v ?? '' };
                                      return x;
                                    })
                                  }
                                  placeholder="모달 버튼 위젯 검색…"
                                  allowClear
                                  clearLabel="(선택)"
                                  onHoverOption={(v) => onHoverHighlightWidget?.(v ?? null)}
                                />
                              </div>
                              <JumpToWidgetButton
                                disabled={!ai.opensWidgetId}
                                onJump={() => onHoverHighlightWidget?.(ai.opensWidgetId)}
                                onClear={() => onHoverHighlightWidget?.(null)}
                              />
                            </div>
                          </div>
                          <p className="text-[11px] leading-4 text-gray-400">
                            제안 목록 맨 아래 고정 항목으로 표시되고(일치 0개여도 유지), 클릭하면
                            지정한 모달 버튼의 모달이 열립니다. 그 모달 버튼에 "버튼 숨김"을 켜면
                            본문 버튼 없이 이 액션으로만 열립니다.
                          </p>
                        </>
                      ) : null}
                    </div>
                  );
                })()}
                {/* 본문 선택 추종(followKey) — 반복 행 안 콤보 전용. 본문(반복 밖) 콤보의 선택이
                    바뀌면 전 행의 이 콤보에 같은 선택(값·id·writes 전파)을 적용, 새 행에도 시드.
                    구 SMS "사업장 담당자 변경 시 모든 행 개선계획 담당 덮어쓰기" 동형. */}
                {enclosingRepeat &&
                  (() => {
                    // 후보 — 본문(반복 밖) 콤보 위젯만(collectBodyCommitTargets는 repeat 미하강).
                    const followCandidates = collectBodyCommitTargets(tree).filter(
                      (t) => t.widget === 'combobox' && t.bindingKey !== w.bindingKey,
                    );
                    const baseOptions: ComboboxOption[] = followCandidates.map((t) => ({
                      value: t.bindingKey,
                      label: t.label ? `${t.label} (${t.bindingKey})` : t.bindingKey,
                      key: t.bindingKey,
                      badge: t.group,
                    }));
                    const cur = w.followKey;
                    // 저장값이 후보에 없으면(대상 삭제 등) 보존 옵션으로 노출(재저장 시 소실 방지).
                    const options: ComboboxOption[] =
                      cur && !followCandidates.some((t) => t.bindingKey === cur)
                        ? [
                            ...baseOptions,
                            { value: cur, label: `${cur} (목록에 없음 — 보존)`, key: cur },
                          ]
                        : baseOptions;
                    return (
                      <div className="space-y-1.5 border-t border-gray-200 pt-2">
                        <label className={labelCls}>본문 선택 추종</label>
                        {/* eslint-disable-next-line local/catalog-picker-required -- 본문 선택 추종 대상(본문 콤보 위젯 bindingKey 목록) — 카탈로그 엔트리 아님 */}
                        <CatalogCombobox
                          value={cur || undefined}
                          options={options}
                          onChange={(v) =>
                            onWidgetUpdate(w.id, (x) => {
                              if (x.kind !== 'input') return x;
                              const nx = { ...x };
                              if (v) nx.followKey = v;
                              else delete nx.followKey;
                              return nx;
                            })
                          }
                          placeholder="본문 콤보 위젯 검색…"
                          allowClear
                          clearLabel="(추종 안 함)"
                        />
                        <p className="text-[11px] leading-4 text-gray-400">
                          선택한 본문 콤보의 선택이 바뀌면 모든 행의 이 콤보에 같은 선택(값·전파)이
                          적용됩니다. 새 행에도 현재 본문 선택으로 시드됩니다(기존 행은 본문 선택이
                          바뀔 때만 덮어쓰며, 행별 수정은 유지). 본문 값이 비면 그대로 둡니다.
                        </p>
                      </div>
                    );
                  })()}
              </div>
            );
          })()}
        {/* optionsFrom 파생 select 선택 전파(writes) — 개정 5: 동적 옵션 select도 콤보와 동일하게
            선택 시 원천 행(raw) 필드를 전파(rc applyDynamicSelectSelection). 저작 게이트는 런타임
            발화 조건과 일치 — 변수(optionsFrom) 파생 select만(정적 options는 원천 행이 없어 전파
            불가). 편집기는 WritesMappingEditor 단일 정본(콤보 "선택 전파 매핑"과 공유).
            결정: 2026-07-07-composer-template-variables 개정 5. */}
        {w.widget === 'select' &&
          (() => {
            const optionsFrom = (variables ?? []).find(
              (v) => v.key === w.bindingKey && v.optionsFrom,
            )?.optionsFrom;
            if (!optionsFrom) return null;
            const entry = (catalog ?? []).find(
              (e): e is Extract<CatalogEntry, { kind: 'data' }> =>
                e.kind === 'data' && e.key === optionsFrom.catalogKey,
            );
            return (
              <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
                <p className="text-[11px] font-medium text-gray-500">
                  동적 옵션 select 설정 — 변수({w.bindingKey}) · 소스 {optionsFrom.catalogKey}
                </p>
                <WritesMappingEditor
                  writes={w.writes ?? []}
                  onChange={(next) =>
                    onWidgetUpdate(w.id, (x) =>
                      x.kind === 'input'
                        ? { ...x, writes: next.length ? next : undefined }
                        : x,
                    )
                  }
                  // source 후보 — 변수 optionsFrom이 참조하는 카탈로그 entry의 returns 필드.
                  sourceOptions={(entry?.returns ?? []).map((r) => ({
                    key: r.path,
                    label: r.label,
                  }))}
                  bodyTargets={collectBodyCommitTargets(tree).filter(
                    (t) => t.bindingKey !== w.bindingKey,
                  )}
                  arrayTargets={collectWriteTargetCandidates(
                    tree,
                    dataDefs,
                    dataSources,
                    w.id,
                  ).filter((c) => c.isArray)}
                  onHoverHighlightWidget={onHoverHighlightWidget}
                  onFtCellHover={onFtCellHover}
                  title="선택 전파 매핑"
                  emptyText="옵션 선택 시 원천 행의 필드 값을 다른 위젯에 쓰려면 매핑을 추가하세요."
                  sourceLabel="행 필드"
                  sourcePlaceholder="행 필드 검색…"
                  helpText="옵션을 선택하면 그 원천 행의 필드 값이 대상 위젯에 자동 기록됩니다(예: 평가기준 세트 선택 → 기준표 합성 객체가 기준표 상세 위젯에 전파)."
                  labelCls={labelCls}
                />
              </div>
            );
          })()}
        {/* 표 위젯 전용 축(헤더 숨김·테두리·표시 단수·채움 방향·구분 그룹·행 클릭 토글·칼럼) —
            표 셀 패널과 **같은 배열**을 순회한다(TABLE_WIDGET_PROP_ORDER). 여기에 표 속성 JSX를
            직접 더하지 말 것: 셀 패널에는 안 붙어 형태가 다시 갈라진다.
            노출 게이트(단수 2 이상·칼럼 존재·checkbox 칼럼 등)는 디스크립터 applicable 소유. */}
        {TABLE_WIDGET_PROP_ORDER.map((prop) => (
          <WidgetPropSingle
            key={prop}
            prop={prop}
            w={w}
            onWidgetUpdate={onWidgetUpdate}
            ctx={propCtx}
          />
        ))}
        {/* 이 뷰에서 숨김은 공통 푸터의 뷰별 축 묶음으로 이동(2026-08-06). */}
        {/* 역전파(dataDef 표는 데이터셋 영속 설정 안내로 대체) — 분기는 디스크립터 Control 소유. */}
        <WidgetPropSingle prop="writeBack" w={w} onWidgetUpdate={onWidgetUpdate} ctx={propCtx} />
      </div>
    );
  }

  // 여기부터는 input이 아닌 kind — switch로 정리해 default:never로 새 kind 본문 누락을 tsc가 강제 검출.
  switch (w.kind) {
  // group(UI 용어 "섹션"): 섹션명 + 단 수(1/2/3) + 접이식/기본 펼침.
  case 'group': {
    return (
      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500">섹션</p>
        <div>
          <label className={labelCls}>섹션명</label>
          <input
            type="text"
            className={inputCls}
            value={w.title ?? ''}
            placeholder="(선택) 섹션 헤더로 표시"
            onChange={(e) =>
              onWidgetUpdate(w.id, (x) =>
                x.kind === 'group' ? { ...x, title: e.target.value || undefined } : x,
              )
            }
          />
        </div>
        <WidgetTextStyleControls
          label="섹션명 스타일"
          style={w.titleStyle}
          defaults={COMPOSER_SECTION_TITLE_DEFAULT_STYLE}
          labelCls={labelCls}
          onField={(k, v) => onWidgetUpdate(w.id, (x) => setWidgetTextStyleField(x, k, v))}
        />
        {w.title ? (
          <div>
            <label className={labelCls}>제목 스타일</label>
            {/* titleVariant: 'section'(회색 헤더 바+테두리 박스, 기본) / 'label'(plain 필드 라벨 룩,
                박스 없이 바로 아래 내용 — 구 SMS 위험도 "라벨 + 컨트롤" 동형). 'section'이면 키 삭제로 기본값 유지. */}
            <select
              className={inputCls}
              value={w.titleVariant ?? 'section'}
              onChange={(e) =>
                onWidgetUpdate(w.id, (x) => {
                  if (x.kind !== 'group') return x;
                  const next = { ...x };
                  if (e.target.value === 'label') next.titleVariant = 'label';
                  else delete next.titleVariant;
                  return next;
                })
              }
            >
              <option value="section">섹션 박스(회색 헤더 바 + 테두리)</option>
              <option value="label">plain 라벨(박스 없이 필드 라벨처럼)</option>
            </select>
            <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
              plain 라벨은 제목을 필드 라벨처럼 표시하고 바로 아래에 내용을 놓습니다(구 SMS 위험도 "라벨 + 컨트롤" 룩).
            </p>
          </div>
        ) : null}
        <div>
          <label className={labelCls}>표시 방식</label>
          {/* displayVariant: 미지정=기본(자식 세로 나열) / 'tabs'=직계 자식 섹션(그룹)들을 탭 바로
              전환(구 SMS 이행상태 탭 룩). 기본이면 키 삭제로 회귀 0. */}
          <select
            className={inputCls}
            value={w.displayVariant ?? 'default'}
            onChange={(e) =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'group') return x;
                const next = { ...x };
                if (e.target.value === 'tabs') next.displayVariant = 'tabs';
                else delete next.displayVariant;
                return next;
              })
            }
          >
            <option value="default">기본(자식 세로 나열)</option>
            <option value="tabs">탭(자식 섹션을 탭으로 전환)</option>
          </select>
          <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
            탭: 직계 자식 섹션(그룹)들을 탭 바로 전환해 하나만 표시합니다. 탭 라벨=자식 섹션명, badge=자식 안 첫 반복의 행 수.
          </p>
        </div>
        <div>
          <label className={labelCls}>
            단 수
            {activeView === COMPOSER_BASE_VIEW_KEY
              ? '(기본)'
              : ` (${composerViewLabel(activeView)} 뷰 오버라이드)`}
            {/* '이 뷰에서 숨김'과 같은 배지 — 두 뷰별 축의 오버라이드 표시를 한 결로 맞춘다. */}
            {activeView !== COMPOSER_BASE_VIEW_KEY &&
            w.responsive?.[activeView]?.columns != null ? (
              <span className="ml-1 rounded bg-brand-100 px-1 text-[9px] text-brand-700">
                오버라이드
              </span>
            ) : null}
          </label>
          {/* 비-base 뷰의 select value는 **오버라이드 값**(없으면 '' = 상속)이다 — 해소값을
              value로 쓰면 상속 상태를 고를 수 없어 되돌릴 방법이 사라진다. 해소된 숫자는 상속
              옵션 라벨과 바로 아래 GroupColumnsViewHint가 말한다. base 뷰는 상속 개념이 없어
              그대로 해소값. */}
          <select
            className={`${inputCls} ${
              activeView !== COMPOSER_BASE_VIEW_KEY &&
              w.responsive?.[activeView]?.columns != null
                ? 'border-brand-400 bg-brand-50 text-brand-700'
                : ''
            }`}
            value={
              activeView === COMPOSER_BASE_VIEW_KEY
                ? resolveGroupColumns(w, activeView)
                : (w.responsive?.[activeView]?.columns ?? '')
            }
            onChange={(e) =>
              onWidgetUpdate(w.id, (x) =>
                setGroupColumnsForView(
                  x,
                  activeView,
                  e.target.value === '' ? undefined : Number(e.target.value) || undefined,
                ),
              )
            }
          >
            {activeView !== COMPOSER_BASE_VIEW_KEY ? (
              <option value="">기본값 상속 (현재 {resolveGroupColumns(w, activeView)}단)</option>
            ) : null}
            <option value={1}>1단</option>
            <option value={2}>2단</option>
            <option value={3}>3단</option>
            <option value={4}>4단</option>
            <option value={5}>5단</option>
            <option value={6}>6단</option>
          </select>
          <GroupColumnsViewHint widget={w} activeView={activeView} />
        </div>
        {resolveGroupColumns(w, activeView) > 1 ? (
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={!!w.fillLastRow}
              onChange={(e) =>
                onWidgetUpdate(w.id, (x) =>
                  x.kind === 'group'
                    ? { ...x, fillLastRow: e.target.checked ? true : undefined }
                    : x,
                )
              }
            />
            마지막 줄 남은 폭 채우기
          </label>
        ) : null}
        {resolveGroupColumns(w, activeView) > 1 ? (
          <p className="-mt-1 text-[10px] leading-snug text-gray-400">
            마지막 줄에 남은 위젯이 단 수보다 적을 때, 그 위젯들이 줄 전체를 나눠 씁니다. 예를 들어
            2단에 위젯이 5개면 마지막 1개가 줄 전체 폭을 차지합니다. 끄면 왼쪽에 붙고 오른쪽이
            빈칸으로 남습니다.
          </p>
        ) : null}
        {/* 자식 간격 — 양식 탭 그룹 노드 gap 대응(2026-07-14, 암묵 16px 하드코딩을 지정 가능하게).
            빈 값=기본(키 제거). gap=세로(행), gapX=다단 열(수평, 단수>1일 때만 노출, 빈 값=세로와 동일). */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className={labelCls}>자식 간격 세로 (px)</label>
            <input
              type="number"
              min={0}
              className={inputCls}
              value={w.gap ?? ''}
              placeholder="16 (기본)"
              onChange={(e) =>
                onWidgetUpdate(w.id, (x) => {
                  if (x.kind !== 'group') return x;
                  const v = e.target.value === '' ? undefined : Math.max(0, Number(e.target.value));
                  const next = { ...x };
                  if (v == null || Number.isNaN(v)) delete next.gap;
                  else next.gap = v;
                  return next;
                })
              }
            />
          </div>
          {resolveGroupColumns(w, activeView) > 1 ? (
            <div className="flex-1">
              <label className={labelCls}>가로 (px)</label>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={w.gapX ?? ''}
                placeholder="세로와 동일"
                onChange={(e) =>
                  onWidgetUpdate(w.id, (x) => {
                    if (x.kind !== 'group') return x;
                    const v =
                      e.target.value === '' ? undefined : Math.max(0, Number(e.target.value));
                    const next = { ...x };
                    if (v == null || Number.isNaN(v)) delete next.gapX;
                    else next.gapX = v;
                    return next;
                  })
                }
              />
            </div>
          ) : null}
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={!!w.collapsible}
            onChange={(e) =>
              onWidgetUpdate(w.id, (x) =>
                x.kind === 'group' ? { ...x, collapsible: e.target.checked } : x,
              )
            }
          />
          접이식(펼침/접힘 토글)
        </label>
        {w.collapsible ? (
          <div>
            <label className={labelCls}>기본 상태</label>
            {/* 3모드(2026-07-14, 구 SMS 사업장 개요 동형): 열림/접힘=정적 defaultOpen,
                채워져 있으면 접힘=collapseWhenFilled(초기값·prefill 준비 후 1회 판정 —
                섹션 안 편집 입력이 모두 채워져 있으면 접힘, 비면 열림). */}
            <select
              className={inputCls}
              value={w.collapseWhenFilled ? 'auto' : (w.defaultOpen ?? true) ? 'open' : 'closed'}
              onChange={(e) => {
                const v = e.target.value as 'open' | 'closed' | 'auto';
                onWidgetUpdate(w.id, (x) => {
                  if (x.kind !== 'group') return x;
                  const next = { ...x };
                  delete next.defaultOpen;
                  delete next.collapseWhenFilled;
                  if (v === 'closed') next.defaultOpen = false;
                  if (v === 'auto') next.collapseWhenFilled = true;
                  return next;
                });
              }}
            >
              <option value="open">항상 열림</option>
              <option value="closed">항상 접힘</option>
              <option value="auto">입력이 채워져 있으면 접힘</option>
            </select>
            {w.collapseWhenFilled ? (
              <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
                작성 화면 진입 시 이 섹션의 입력이 모두 채워져 있으면(기존 데이터/자동 채움 포함)
                접힌 채로, 하나라도 비어 있으면 열린 채로 시작합니다.
              </p>
            ) : null}
          </div>
        ) : null}
        {/* 박스 스타일 — 양식 탭 노드 박스 스타일 대응(채움/테두리/모서리). RectStyle 재사용, 미설정=장식 없음.
            작성 탭 패널은 onPreview/onRevert 미배선(즉시 확정 onChange만) — RectStyleFields가 onPreview
            미전달 시 onChange로 폴백. */}
        <div>
          <label className={labelCls}>박스 스타일</label>
          <RectStyleFields
            value={w.boxStyle}
            onChange={(next) =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'group') return x;
                const nx = { ...x };
                if (next === undefined) delete nx.boxStyle;
                else nx.boxStyle = next;
                return nx;
              })
            }
          />
        </div>
        {/* 섹션 채움색 분리 축(2026-08-04) — 타이틀 바 / 본문(콘텐츠) 배경을 각각 지정.
            미지정=기존(타이틀: 펼침 흰색·접힘 그라데이션 / 본문: 투명 — 페이지 바탕). */}
        {(['titleFill', 'contentFill'] as const).map((fillKey) => (
          <div key={fillKey} className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 w-20">
              {fillKey === 'titleFill' ? '타이틀 채움색' : '콘텐츠 채움색'}
            </span>
            <input
              type="color"
              className="h-7 w-10 rounded border border-gray-300 p-0.5"
              value={w.kind === 'group' ? (w[fillKey] ?? '#ffffff') : '#ffffff'}
              onChange={(e) =>
                onWidgetUpdate(w.id, (x) =>
                  x.kind === 'group' ? { ...x, [fillKey]: e.target.value } : x,
                )
              }
            />
            {w.kind === 'group' && w[fillKey] != null && (
              <button
                type="button"
                className="text-[11px] text-gray-500 underline"
                onClick={() =>
                  onWidgetUpdate(w.id, (x) => {
                    if (x.kind !== 'group') return x;
                    const nx = { ...x };
                    delete nx[fillKey];
                    return nx;
                  })
                }
              >
                기본으로
              </button>
            )}
          </div>
        ))}
      </div>
    );
  }

  // heading: 제목 문구.
  case 'heading': {
    return (
      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500">텍스트</p>
        <div>
          <label className={labelCls}>문구</label>
          <input
            type="text"
            className={inputCls}
            value={w.text ?? ''}
            placeholder="텍스트"
            onChange={(e) => onWidgetUpdate(w.id, (x) => ({ ...x, text: e.target.value }))}
          />
        </div>
        <WidgetTextStyleControls
          label="텍스트 스타일"
          style={w.textStyle}
          defaults={COMPOSER_HEADING_DEFAULT_STYLE}
          labelCls={labelCls}
          onField={(k, v) => onWidgetUpdate(w.id, (x) => setWidgetTextStyleField(x, k, v))}
        />
        {/* 자동 채움(카탈로그) — 지정 시 정적 문구 대신 카탈로그 resolve 값을 표시(미해석/빈값→문구 fallback). */}
        <div>
          <label className={labelCls}>자동 채움(시스템 조회)</label>
          <CatalogPrefillPicker
            catalog={catalog ?? []}
            value={w.prefillFrom}
            onChange={(v) =>
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'heading') return x;
                const next = { ...x };
                if (v) next.prefillFrom = v;
                else delete next.prefillFrom;
                return next;
              })
            }
          />
          <p className="mt-1 text-[10px] leading-snug text-gray-400">
            지정 시 작성 시점에 시스템 조회 값이 문구로 표시됩니다(값이 없으면 위 문구로 표시).
          </p>
        </div>
      </div>
    );
  }

  // formTable(표): 제목 + 열(라벨/그룹/폭/정렬) + 행(고정/반복) + 셀 편집.
  case 'formTable': {
    return (
      <FormTablePropertyPanel
        w={w}
        onWidgetUpdate={onWidgetUpdate}
        onWidgetPreview={onWidgetPreview}
        onWidgetCommit={onWidgetCommit}
        onWidgetRevert={onWidgetRevert}
        labelCls={labelCls}
        inputCls={inputCls}
        dataSources={dataSources}
        dataDefs={dataDefs}
        onTemplateUpdate={onTemplateUpdate}
        countKeyRefs={countKeyRefs}
        unplacedPool={unplacedPool}
        focusCell={ftFocusCell}
        onCellHover={onFtCellHover}
        onCellFocus={onFtCellFocus}
        onFocusClear={onFtCellFocusClear}
        writeValues={writeValues}
        writeFormFields={writeFormFields}
        bindingPrefillMap={bindingPrefillMap}
        catalog={catalog}
        tree={tree}
        previewValues={writeValues}
        ftCellClipboardValue={ftCellClipboard}
        onCellCopy={onFtCellCopy}
        onCellBlockCopy={onFtCellBlockCopy}
        onCellPaste={onFtCellPaste}
        onCellPasteMany={onFtCellPasteMany}
        onCellsContentClear={onFtCellsContentClear}
        onRequestConfirm={onFtRequestConfirm}
        onSelectWidget={onSelectWidget}
      />
    );
  }

  // modalTrigger: 버튼 라벨/목적/모달 제목·크기/자동 호출/숨김/commit 매핑 편집. 모달 콘텐츠 저작은 P1b.
  case 'modalTrigger': {
    const updateModal = (fn: (m: ModalTriggerWidget) => ModalTriggerWidget): void =>
      onWidgetUpdate(w.id, (x) => (x.kind === 'modalTrigger' ? fn(x) : x));
    // 목적 카테고리(결정 2026-07-16-composer-modal-purpose-category) — 읽기는 resolver 경유
    // (purpose 부재 구 템플릿은 top-level commit으로 valueCommit 파생).
    const purpose = effectiveModalPurpose(w);
    // valueCommit 매핑 쓰기 — purpose가 정본이 되도록 기록 + top-level commit도 동일 값 dual-write
    // (commit은 타입 필수 + purpose 도입 전 읽기 경로 호환. resolver가 purpose 우선이라 충돌 없음).
    const updateCommit = (fn: (commit: ModalCommitMap[]) => ModalCommitMap[]): void =>
      updateModal((x) => {
        const cur = effectiveModalPurpose(x);
        const commit = fn(cur.kind === 'valueCommit' ? cur.commit : []);
        return { ...x, purpose: { kind: 'valueCommit', commit }, commit };
      });
    // commit 매핑 후보 — from: 모달 안 input / to: 본문 input+표 고정 셀(구조 기반 선택).
    const commitSources = collectModalCommitSources(w.children);
    const commitTargets = collectBodyCommitTargets(tree);
    // 검색형 콤보박스(CatalogCombobox) 옵션 — 일반 사용자가 native select로 위젯을 찾기 어려운 문제
    // 해소(라벨 검색 + 타입 badge + 섹션 group). 현재값이 후보에 없으면 행 단위로 "보존" 옵션을 덧댄다.
    const fromBaseOptions: ComboboxOption[] = commitSources.map((s) => ({
      value: s.bindingKey,
      label: s.label || s.bindingKey,
      key: s.bindingKey,
      badge: s.widget,
    }));
    const toBaseOptions: ComboboxOption[] = commitTargets.map((t) => ({
      value: t.bindingKey,
      label: t.label || t.bindingKey,
      key: t.bindingKey,
      badge: t.widget,
      group: t.group,
    }));
    // commit 매핑 콤보 hover → 캔버스 강조 배선. to=본문 대상(bindingKey→위젯/셀), from=모달 안 위젯.
    // 두 채널(위젯/셀)을 하나의 highlight()로 수렴 — 강조 대상이 바뀔 때 다른 채널은 항상 해제한다.
    const toHighlightByKey = new Map<string, CommitTargetHighlight>();
    for (const t of commitTargets) if (t.highlight) toHighlightByKey.set(t.bindingKey, t.highlight);
    const fromIdByKey = new Map<string, string>();
    for (const s of commitSources) fromIdByKey.set(s.bindingKey, s.id);
    const modalOpen = modalAuthoringId === w.id;
    const highlightTarget = (h: CommitTargetHighlight | null): void => {
      if (!h) {
        onHoverHighlightWidget?.(null);
        onFtCellHover?.(null);
        return;
      }
      if (h.kind === 'widget') {
        onFtCellHover?.(null);
        onHoverHighlightWidget?.(h.id);
      } else {
        onHoverHighlightWidget?.(null);
        onFtCellHover?.({ tableId: h.tableId, rowId: h.rowId, columnId: h.columnId });
      }
    };
    // from은 모달 콘텐츠(children)의 위젯 — 저작 열림 시 그 위젯 id를 강조, 닫힘 시(캔버스에 child
    // 미표시) modalTrigger 버튼(w.id)으로 폴백해 어느 버튼인지라도 짚어준다.
    const highlightFrom = (bindingKey: string | null): void => {
      if (!bindingKey) {
        highlightTarget(null);
        return;
      }
      const childId = fromIdByKey.get(bindingKey);
      highlightTarget({ kind: 'widget', id: modalOpen && childId ? childId : w.id });
    };
    // 등록 대상(writer) 카탈로그 — keyed upsert(writeBack.upsertKey 선언) 화이트리스트.
    // '확인 동작' select와 submitAction 편집 블록이 같은 목록을 공유한다(중복 정의 금지 — 필터가
    // 갈라지면 select로 고른 기본 catalogKey가 편집 블록 목록 밖이 되는 드리프트).
    const submitCatalogEntries = (catalog ?? []).filter(
      (e) => e.kind === 'data' && !!e.writeBack?.upsertKey,
    );
    return (
      <div className="space-y-3">
        <div>
          <label className={labelCls}>버튼 라벨</label>
          <input
            className={inputCls}
            value={w.label}
            onChange={(e) => updateModal((x) => ({ ...x, label: e.target.value }))}
          />
        </div>
        <div>
          <label className={labelCls}>모달 제목</label>
          <input
            className={inputCls}
            value={w.modalTitle}
            onChange={(e) => updateModal((x) => ({ ...x, modalTitle: e.target.value }))}
          />
        </div>
        <div>
          <label className={labelCls}>모달 크기</label>
          <select
            className={inputCls}
            value={w.modalSize ?? 'md'}
            onChange={(e) =>
              updateModal((x) => ({ ...x, modalSize: e.target.value as 'md' | 'lg' | 'xl' }))
            }
          >
            <option value="md">보통(md)</option>
            <option value="lg">크게(lg)</option>
            <option value="xl">아주 크게(xl)</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={!!w.autoOpen}
            onChange={(e) => updateModal((x) => ({ ...x, autoOpen: e.target.checked || undefined }))}
          />
          진입 시 자동 호출(1회)
        </label>
        <div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={!!w.hideWhenNoData}
              onChange={(e) =>
                updateModal((x) => ({ ...x, hideWhenNoData: e.target.checked || undefined }))
              }
            />
            데이터 없으면 버튼 숨김
          </label>
          <p className="mt-0.5 text-[11px] text-gray-400">
            모달 콘텐츠의 선택 정렬 목록(sortableSelect) 후보 rows가 전부 0개면 버튼을 숨기고 자동
            호출도 생략합니다.
          </p>
        </div>
        <div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={!!w.hideButton}
              onChange={(e) =>
                updateModal((x) => ({ ...x, hideButton: e.target.checked || undefined }))
              }
            />
            버튼 숨김(외부 트리거 전용)
          </label>
          <p className="mt-0.5 text-[11px] text-gray-400">
            작성 페이지 본문에 버튼을 표시하지 않습니다 — 콤보 드롭다운 액션 항목 등 외부 트리거로만
            모달이 열립니다. 캔버스에서는 저작을 위해 반투명으로 계속 보입니다.
          </p>
        </div>
        {/* 확인 동작 — 본문 반영(commit, 기본) vs 카탈로그 행 즉시 등록(submitAction).
            등록 선택 시: writer 화이트리스트(keyed upsert = writeBack.upsertKey 선언) 카탈로그만 +
            writable 필드별 → 모달 children 위젯 매핑(fieldMap). upsertKey 필드 매핑은 필수.
            결정: 2026-07-17-composer-widget-preset-and-registered-widget-path("등록 모달 = 즉시 등록"). */}
        <div className="space-y-3 border-t border-gray-200 pt-3">
          <div>
            <label className={labelCls}>확인 동작</label>
            <select
              className={inputCls}
              value={w.submitAction ? 'submitAction' : 'commit'}
              onChange={(e) => {
                if (e.target.value === 'submitAction') {
                  updateModal((x) => {
                    if (x.submitAction) return x;
                    const first = submitCatalogEntries[0];
                    return {
                      ...x,
                      submitAction: {
                        catalogKey: first?.key ?? '',
                        fieldMap: {},
                        // 카탈로그 upsert 키 자동 복사 — rc 등록 게이트의 정본(추론 폴백 회피).
                        keyField:
                          first?.kind === 'data' ? first.writeBack?.upsertKey : undefined,
                      },
                    };
                  });
                } else {
                  updateModal((x) => ({ ...x, submitAction: undefined }));
                }
              }}
            >
              <option value="commit">본문 반영 (기본)</option>
              <option value="submitAction">시스템 조회 행 등록 (즉시 등록)</option>
            </select>
            <p className="mt-0.5 text-[11px] text-gray-400">
              {w.submitAction
                ? '확인 시 모달 값을 본문에 넣지 않고, 시스템 정본(현장 담당자 등)에 즉시 등록합니다. 등록 성공 시 이 모달을 여는 콤보가 새 항목으로 선택됩니다.'
                : '확인 시 아래 "확인 시 값 매핑"대로 모달 값을 본문에 기록합니다.'}
            </p>
          </div>
          {w.submitAction
            ? (() => {
                const sa = w.submitAction;
                const entry = submitCatalogEntries.find((e) => e.key === sa.catalogKey);
                const writable =
                  entry?.kind === 'data' ? (entry.writeBack?.writableFields ?? []) : [];
                const upsertKey = entry?.kind === 'data' ? entry.writeBack?.upsertKey : undefined;
                const fieldLabel = (f: string): string =>
                  (entry?.returns ?? []).find((r) => r.path === f)?.label ?? f;
                type SubmitActionValue = {
                  catalogKey: string;
                  fieldMap: Record<string, string>;
                  keyField?: string;
                };
                const updateSubmit = (
                  fn: (s: SubmitActionValue) => SubmitActionValue,
                ): void =>
                  updateModal((x) =>
                    x.submitAction ? { ...x, submitAction: fn(x.submitAction) } : x,
                  );
                return (
                  <div className="space-y-1.5">
                    <div>
                      <label className={labelCls}>등록 대상 시스템 조회</label>
                      {/* 공통 코어 CatalogEntryPicker(P3 이관) — writer 화이트리스트(keyed upsert)
                          필터는 호출부 그대로. */}
                      <CatalogEntryPicker
                        entries={submitCatalogEntries}
                        value={sa.catalogKey || undefined}
                        onChange={(key) =>
                          // 카탈로그 전환(해제 포함) 시 fieldMap 초기화 — writable 필드 집합이 달라짐.
                          // 같은 값 재선택 no-op은 코어 가드가 보장(fieldMap 소실 방지).
                          updateSubmit(() => {
                            const next = submitCatalogEntries.find((en) => en.key === key);
                            return {
                              catalogKey: key ?? '',
                              fieldMap: {},
                              // 카탈로그 upsert 키 자동 복사 — rc 등록 게이트 정본(추론 폴백 회피).
                              keyField:
                                next?.kind === 'data' ? next.writeBack?.upsertKey : undefined,
                            };
                          })
                        }
                        allowClear
                        clearLabel="시스템 조회 선택"
                        placeholder="시스템 조회 선택…"
                      />
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        행 등록(keyed upsert writer)을 지원하는 조회만 표시됩니다.
                      </p>
                    </div>
                    {writable.map((f) => {
                      const cur = sa.fieldMap[f];
                      // 현재값이 후보에 없으면 "보존" 옵션을 덧대 기존 매핑을 잃지 않는다(commit 콤보 동형).
                      const options: ComboboxOption[] =
                        cur && !commitSources.some((s) => s.bindingKey === cur)
                          ? [
                              ...fromBaseOptions,
                              { value: cur, label: `${cur} (목록에 없음 — 보존)`, key: cur },
                            ]
                          : fromBaseOptions;
                      return (
                        <div key={f} className="flex items-center gap-1.5">
                          <span className="w-24 shrink-0 text-[11px] text-gray-500">
                            {fieldLabel(f)}
                            {f === upsertKey ? <span className="text-red-500"> *</span> : null}
                          </span>
                          <div className="flex-1 min-w-0">
                            {/* eslint-disable-next-line local/catalog-picker-required -- submitAction fieldMap 소스(모달 안 위젯 bindingKey 목록) — 카탈로그 엔트리 아님 */}
                            <CatalogCombobox
                              value={cur || undefined}
                              options={options}
                              onChange={(v) =>
                                updateSubmit((s) => {
                                  const fieldMap = { ...s.fieldMap };
                                  if (v) fieldMap[f] = v;
                                  else delete fieldMap[f];
                                  return { ...s, fieldMap };
                                })
                              }
                              placeholder="모달 안 위젯 검색…"
                              allowClear
                              clearLabel="(미지정)"
                              onHoverOption={(v) => highlightFrom(v ?? null)}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {upsertKey && !sa.fieldMap[upsertKey] ? (
                      <p className="text-[11px] text-amber-600">
                        키 필드({fieldLabel(upsertKey)}) 매핑은 필수입니다 — 누락 시 작성 페이지에서
                        등록 버튼이 비활성화됩니다.
                      </p>
                    ) : null}
                    {purpose.kind === 'valueCommit' && purpose.commit.length > 0 ? (
                      <p className="text-[11px] text-amber-600">
                        아래 "확인 시 값 매핑"이 함께 저작되어 있지만, 등록 동작이 지정된 동안 확인은
                        등록만 실행합니다(상호배타 권장 — 값 매핑 제거를 권장).
                      </p>
                    ) : null}
                  </div>
                );
              })()
            : null}
        </div>
        {/* 모달 목적 + 그 목적의 설정을 한 섹션으로 통합 — 셀렉트가 머리, 아래가 purpose.kind별
            설정(현재는 valueCommit 하나). 새 ModalPurpose 카테고리 추가 시 아래 분기에 그 설정 섹션을
            더한다(shared ModalPurpose JSDoc 레시피 ③과 정합). */}
        <div className="space-y-3 border-t border-gray-200 pt-3">
          <div>
            <label className={labelCls}>모달 목적</label>
            <select
              className={inputCls}
              value={purpose.kind}
              onChange={(e) => {
                // 현재 카테고리는 valueCommit 하나 — 선택 시 purpose를 명시 기록(purpose 부재 구
                // 템플릿 정본화). 새 카테고리 추가 시 여기서 kind 전환(카테고리 설정 초기화) 처리.
                if (e.target.value === 'valueCommit') updateCommit((commit) => commit);
              }}
            >
              <option value="valueCommit">값 전달 (valueCommit)</option>
            </select>
            <p className="mt-0.5 text-[11px] text-gray-400">
              {purpose.kind === 'valueCommit'
                ? '모달 안 위젯의 값을 확인 시 본문에 기록합니다.'
                : ''}
            </p>
          </div>
          {/* purpose.kind별 설정 섹션 — 목적을 바꾸면 여기서 그 목적의 설정을 처리한다. */}
          {purpose.kind === 'valueCommit' ? (
            <div>
              <label className={labelCls}>확인 시 값 매핑 (모달 값 → 본문)</label>
              <p className="mb-1.5 text-[11px] leading-snug text-gray-400">
                확인 클릭 시 모달 안 위젯의 값을 본문 위젯에 기록합니다. 위젯 종류별 정본 출력(미리보기에
                보이는 결과)이 자동으로 기록됩니다.
              </p>
              <div className="space-y-1.5">
                {purpose.commit.map((m, i) => {
                  const fromWidget = commitSources.find((s) => s.bindingKey === m.from);
                  // 현재값이 후보에 없으면 "보존" 옵션을 덧대 기존 매핑을 잃지 않는다.
                  const fromOptions: ComboboxOption[] =
                    m.from && !commitSources.some((s) => s.bindingKey === m.from)
                      ? [
                          ...fromBaseOptions,
                          { value: m.from, label: `${m.from} (목록에 없음 — 보존)`, key: m.from },
                        ]
                      : fromBaseOptions;
                  const toOptions: ComboboxOption[] =
                    m.to && !commitTargets.some((t) => t.bindingKey === m.to)
                      ? [
                          ...toBaseOptions,
                          { value: m.to, label: `${m.to} (목록에 없음 — 보존)`, key: m.to },
                        ]
                      : toBaseOptions;
                  return (
                    <div key={i} className="space-y-1.5 rounded border border-gray-200 bg-white p-2">
                      <div className="flex items-center gap-1.5">
                        <span className="w-11 shrink-0 text-[11px] text-gray-500">모달 값</span>
                        {/* 드롭다운 옵션 호버 시 그 옵션 강조(모달 저작 닫힘 시 modalTrigger 버튼 폴백 —
                            highlightFrom). 닫힘(선택됨) 상태는 이동 버튼 클릭으로만. */}
                        <div className="flex-1 min-w-0">
                          {/* eslint-disable-next-line local/catalog-picker-required -- commit 매핑 from(모달 안 위젯 bindingKey 목록) — 카탈로그 엔트리 아님 */}
                          <CatalogCombobox
                            value={m.from || undefined}
                            options={fromOptions}
                            onChange={(v) =>
                              updateCommit((commit) =>
                                commit.map((c, j) => (j === i ? { ...c, from: v ?? '' } : c)),
                              )
                            }
                            placeholder="모달 안 위젯 검색…"
                            allowClear
                            clearLabel="(선택)"
                            onHoverOption={(v) => highlightFrom(v ?? null)}
                          />
                        </div>
                        <JumpToWidgetButton
                          disabled={!m.from}
                          onJump={() => highlightFrom(m.from || null)}
                          onClear={() => highlightTarget(null)}
                        />
                        {/* 값 객체 키 선택(pick)은 구현 내부 사정 — 저작자에겐 위젯 종류별 정본
                            출력을 엔진이 자동 적용한다. sortableSelect는 조립 텍스트가 정본이라 그 사실만
                            힌트로 알린다(스칼라 위젯은 값 그대로라 힌트 없음). 저장된 pick 값은 존치(미노출). */}
                        {fromWidget?.widget === 'sortableSelect' && (
                          <span className="shrink-0 text-[10px] leading-tight text-gray-400">
                            기록 값: 조립 텍스트
                            <br />
                            (미리보기 결과 자동 기록)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-11 shrink-0 text-[11px] text-gray-500">→ 본문</span>
                        {/* 옵션 호버 시 그 대상 강조. 닫힘(선택됨) 상태는 이동 버튼 클릭으로만. */}
                        <div className="flex-1 min-w-0">
                          {/* eslint-disable-next-line local/catalog-picker-required -- commit 매핑 to(본문 대상 위젯/셀 bindingKey 목록) — 카탈로그 엔트리 아님 */}
                          <CatalogCombobox
                            value={m.to || undefined}
                            options={toOptions}
                            onChange={(v) =>
                              updateCommit((commit) =>
                                commit.map((c, j) => (j === i ? { ...c, to: v ?? '' } : c)),
                              )
                            }
                            placeholder="본문 대상 위젯 검색…"
                            allowClear
                            clearLabel="(선택)"
                            onHoverOption={(v) => highlightTarget(v ? toHighlightByKey.get(v) ?? null : null)}
                          />
                        </div>
                        <JumpToWidgetButton
                          disabled={!m.to || !toHighlightByKey.get(m.to)}
                          onJump={() =>
                            highlightTarget(m.to ? toHighlightByKey.get(m.to) ?? null : null)
                          }
                          onClear={() => highlightTarget(null)}
                        />
                        <button
                          type="button"
                          className="shrink-0 rounded px-1.5 text-xs text-gray-400 hover:text-red-500"
                          aria-label="매핑 삭제"
                          onClick={() => updateCommit((commit) => commit.filter((_, j) => j !== i))}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="text-xs font-medium text-brand-600 hover:text-brand-700"
                  onClick={() => updateCommit((commit) => [...commit, { from: '', to: '' }])}
                >
                  + 매핑 추가
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <div className="border-t border-gray-200 pt-3">
          <label className={labelCls}>모달 콘텐츠 ({w.children.length}개 위젯)</label>
          {(() => {
            const authoring = modalAuthoringId === w.id;
            return (
              <>
                <button
                  type="button"
                  className={
                    authoring
                      ? 'w-full rounded-md border border-brand-300 bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-100'
                      : 'w-full rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700'
                  }
                  onClick={() => onModalAuthoringToggle?.(authoring ? null : w.id)}
                >
                  {authoring ? '모달 편집 닫기' : '모달 편집 열기'}
                </button>
                <p className="mt-1 text-[11px] text-gray-400">
                  캔버스에서 모달을 연 상태로 콘텐츠를 저작합니다(위젯 클릭 선택·인라인 편집·드래그
                  배치·추가/붙여넣기 — 본문과 동일). 왼쪽 트리 패널의 모달 항목에서도 가능합니다. Esc
                  또는 "편집 닫기"로 종료.
                </p>
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  // spacer: 높이(px)만 편집(웹 폼 단위 정책 — 2026-07-13-writeform-units-web-native).
  case 'spacer': {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-gray-500">높이(px)</span>
          <input
            type="number"
            min={1}
            className={inputCls}
            value={w.height ?? ''}
            placeholder="8 (기본)"
            onChange={(e) => {
              const n = Number(e.target.value);
              onWidgetUpdate(w.id, (x) =>
                x.kind === 'spacer'
                  ? { ...x, height: e.target.value !== '' && Number.isFinite(n) && n > 0 ? n : undefined }
                  : x,
              );
            }}
          />
        </div>
      </div>
    );
  }

  // separator: 편집 속성 없음.
  case 'separator': {
    return (
      <div className="space-y-3">
        <p className="text-xs text-gray-500">구분선은 편집할 속성이 없습니다.</p>
      </div>
    );
  }

  // repeat: 반복(행 스코프 조립) 컨테이너(P2) — over(편집형 조립 데이터소스) 선택 + 행 템플릿 요약 +
  // drift(미배치 필드) 안내·원클릭 추가(뼈대 매핑과 같은 규칙 — repeatSkeletonWidgetForField 공유).
  // 행 템플릿 내부 편집(추가/이동/속성)은 트리 패널·컨테이너 자식 목록이 정본.
  // 결정 2026-07-20-composer-repeat-widget-row-scope §뼈대 생성 규칙("drift 주의").
  case 'repeat': {
    // over 후보 — 최상위는 편집형 list(현행), 중첩(반복 안 반복)이면 부모 행 배열 필드와 같은
    // key의 자식 소스(readonly 포함)를 추가 노출(P4 — 부모 판정은 이 패널 공통 enclosingRepeat).
    const overCandidates = repeatOverCandidates(dataSources, enclosingRepeat?.over);
    const candidateDefs = [...overCandidates.parentRowFields, ...overCandidates.editable];
    const def = (dataSources ?? []).find((d) => d.key === w.over);
    const missing = def ? repeatMissingFields(w, def) : [];
    // "최소 1행 필수" 토글 노출 게이트 — 판정은 순수 헬퍼(repeatRequiredGate, co-located spec).
    const canRequireMinRow = canRepeatRequireMinRow(w.over, dataSources);
    // 게이트가 닫힌 채 required가 저장돼 있을 때의 안내 강도(같은 헬퍼) — 조립형·삭제만 허용 소스는
    // 행이 prefill에서 오므로 amber 안내, 그 밖은 종전 red.
    const requiredVerdict = judgeRepeatRequiredMinRow(w.over, dataSources);
    // required 갱신 — 미지정=false는 키 삭제로 저장 안 함(displayVariant 저장 관례 동형). 토글과
    // 안내 두 갈래의 「필수 해제」가 같은 핸들러를 쓴다.
    const setRequired = (checked: boolean): void =>
      onWidgetUpdate(w.id, (x) => {
        if (x.kind !== 'repeat') return x;
        const next = { ...x };
        if (checked) next.required = true;
        else delete next.required;
        return next;
      });
    // 원클릭 추가 — 뼈대 매핑 규칙 그대로 해당 필드 위젯 1개 생성·행 템플릿 말미 append.
    const appendFields = (fields: DataSourceFieldMap[]): void => {
      const widgets = fields
        .map((f) => repeatSkeletonWidgetForField(f, () => crypto.randomUUID()))
        .filter((x): x is ComposerWidget => x !== null);
      if (widgets.length === 0) return;
      onWidgetUpdate(w.id, (x) =>
        x.kind === 'repeat' ? { ...x, children: [...x.children, ...widgets] } : x,
      );
    };
    return (
      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500">반복(행 스코프 조립)</p>
        <div>
          <label className={labelCls}>반복 대상(데이터 소스)</label>
          <select
            className={inputCls}
            value={candidateDefs.some((d) => d.key === w.over) ? w.over : ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const picked = (dataSources ?? []).find((d) => d.key === v);
              onWidgetUpdate(w.id, (x) => {
                if (x.kind !== 'repeat') return x;
                // 최초 지정(children 비었으면) 뼈대 자동 생성 — 이후 over 변경은 children 유지(파괴 방지).
                const children =
                  x.children.length === 0 && picked
                    ? buildRepeatSkeletonWidgets(picked, () => crypto.randomUUID())
                    : x.children;
                return { ...x, over: v, children };
              });
            }}
          >
            {!candidateDefs.some((d) => d.key === w.over) ? (
              <option value="">{w.over ? `${w.over} (선언 없음)` : '데이터 소스 선택'}</option>
            ) : null}
            {/* 중첩 전용 자식 소스 그룹 — 안쪽 rows=부모 행 객체의 그 필드(선언=계약, P4). */}
            {overCandidates.parentRowFields.length > 0 ? (
              <optgroup label="부모 행 필드(중첩 반복)">
                {overCandidates.parentRowFields.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label} ({d.key})
                  </option>
                ))}
              </optgroup>
            ) : null}
            {overCandidates.editable.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label} ({d.key})
              </option>
            ))}
          </select>
          {/* over 변경은 뼈대 재생성하지 않음(기존 children 유지 — 파괴 방지, 사용자 지시). */}
          <p className="mt-1 text-[10px] leading-snug text-gray-400">
            변경해도 기존 행 템플릿은 유지됩니다(뼈대 재생성 없음) — 필드 구성이 다르면 아래 미배치
            필드 안내로 추가하세요.
          </p>
        </div>
        {!def ? (
          <p className="rounded border border-dashed border-amber-300 bg-amber-50 px-3 py-3 text-xs text-amber-700">
            연결된 데이터 소스(<span className="font-mono">{w.over}</span>)를 찾을 수 없습니다. 양식
            편집 탭에서 삭제되었을 수 있습니다.
          </p>
        ) : (
          <>
            <p className="text-[11px] text-gray-400">
              행 템플릿 위젯 {w.children.length}개 — 내부 편집(추가·이동·속성)은 왼쪽 위젯 트리
              패널과 아래 목록에서.
            </p>
            {/* drift 안내 — 뼈대는 생성 시점 스냅샷이라 이후 필드 선언 추가는 자동 미반영(placed
                copy drift). hidden 필드는 집계 제외(repeatMissingFields). */}
            {missing.length > 0 ? (
              <div className="rounded border border-amber-300 bg-amber-50 px-2 py-2">
                <p className="mb-1 text-[11px] font-medium text-amber-700">
                  이 반복에 미배치 필드 {missing.length}개
                </p>
                <ul className="space-y-1">
                  {missing.map((f) => (
                    <li key={f.field} className="flex items-center gap-1.5">
                      <span className="min-w-0 flex-1 truncate text-[11px] text-amber-800">
                        {f.label ?? f.field}
                        <span className="ml-1 font-mono text-[10px] text-amber-600">{f.field}</span>
                      </span>
                      <button
                        type="button"
                        className="shrink-0 rounded border border-amber-400 bg-white px-1.5 py-0.5 text-[11px] text-amber-700 hover:bg-amber-100"
                        onClick={() => appendFields([f])}
                      >
                        추가
                      </button>
                    </li>
                  ))}
                </ul>
                {missing.length > 1 ? (
                  <button
                    type="button"
                    className="mt-1.5 rounded border border-amber-400 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700 hover:bg-amber-100"
                    onClick={() => appendFields(missing)}
                  >
                    모두 추가
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        )}
        {/* 표시 방식/행 골격/다단 — 표형(displayVariant:'table')은 자체 골격 소유라 행 골격·다단과
            상호배타(결정 2026-08-24-repeat-table-display-variant — 'table'이 우선). */}
        <div className="border-t border-gray-100 pt-3 space-y-2">
          <div>
            <label className={labelCls}>표시 방식</label>
            {/* displayVariant: 미지정=카드(기존, 회귀 0 — 키 삭제) / 'table'=children을 열로 하는
                표형(헤더=자식 라벨, 행=항목). group displayVariant:'tabs' 저장 관례 동형. */}
            <select
              className={inputCls}
              value={w.displayVariant ?? 'default'}
              onChange={(e) =>
                onWidgetUpdate(w.id, (x) => {
                  if (x.kind !== 'repeat') return x;
                  const next = { ...x };
                  if (e.target.value === 'table') next.displayVariant = 'table';
                  else delete next.displayVariant;
                  return next;
                })
              }
            >
              <option value="default">카드(기본 — 행 카드 나열)</option>
              <option value="table">표형(자식을 열로 하는 표)</option>
            </select>
            <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
              표형: 행 템플릿 자식들이 열이 되고(헤더=자식 라벨/섹션명) 항목이 표의 행이 됩니다.
              좁은 화면에서는 카드로 폴백합니다.
            </p>
          </div>
          {/* required: 최소 1행 필수 — 검증 축(표시 무변). 미지정=false는 키 삭제로 저장 안 함
              (displayVariant 저장 관례 동형). 행 0개면 제출/완료의 required 검증이 차단
              (구 DLR "작업 행 ≥1" 게이트의 일반화).
              노출 게이트(canRepeatRequireMinRow): 작성자가 행을 추가할 수 있는 편집형 소스일
              때만 — compute 파생 dataDef·readonly·행 추가 불허(allowAddRemove:false 또는
              allowAdd:false — shared effectiveRowAddRemove의 add 축) 소스에 걸면 행 0개
              차단을 작성자가 풀 방법이 없는 막다른 게이트가 된다(리뷰 r0824b code W1).
              게이트 미충족인데 기존 저장값 required=true면 안내 — 강도는 judgeRepeatRequiredMinRow:
              조립형·삭제만 허용 소스(RA 항목 — 행이 prefill에서 온다)는 amber 안내(필수 유지가 의도일
              수 있어 해제를 권고하지 않음), 그 밖은 red 경고 + 해제 유도(5차 은닉 감사, 2026-09-14). */}
          {canRequireMinRow ? (
            <div>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={w.required === true}
                  onChange={(e) => setRequired(e.target.checked)}
                />
                <span className="text-xs font-medium text-gray-600">최소 1행 필수</span>
              </label>
              <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
                켜면 행이 0개일 때 제출·완료가 차단됩니다(표시는 그대로 — 검증만).
              </p>
            </div>
          ) : w.required === true &&
            requiredVerdict.kind === 'ineligible' &&
            requiredVerdict.reason === 'seeded-remove-only' ? (
            <div className="rounded border border-amber-300 bg-amber-50 px-2 py-2">
              <p className="text-[11px] font-medium text-amber-700">
                &quot;최소 1행 필수&quot;가 켜져 있고 이 소스는 시스템 조회 조립이 행을 채웁니다(작성자는
                행을 뺄 수만 있음).
              </p>
              <p className="mt-0.5 text-[10px] leading-snug text-amber-700">
                조립 결과가 0행(발견사항 없음)이면 작성자가 채울 수 없어 제출·완료가 막힙니다 — 「전부
                지우면 제출 불가」가 의도라면 그대로 두세요.
              </p>
              <button
                type="button"
                className="mt-1.5 rounded border border-amber-400 bg-white px-2 py-0.5 text-[11px] text-amber-700 hover:bg-amber-100"
                onClick={() => setRequired(false)}
              >
                필수 해제
              </button>
            </div>
          ) : w.required === true ? (
            <div className="rounded border border-red-300 bg-red-50 px-2 py-2">
              <p className="text-[11px] font-medium text-red-700">
                &quot;최소 1행 필수&quot;가 켜져 있지만 이 소스는 작성자가 행을 추가할 수
                없습니다(계산 파생·읽기 전용·행 추가 금지).
              </p>
              <p className="mt-0.5 text-[10px] leading-snug text-red-600">
                행이 0개로 조립되면 채울 방법이 없어 제출·완료가 영구 차단됩니다 — 필수를
                해제하거나 소스를 행 추가 가능한 편집형으로 바꾸세요.
              </p>
              <button
                type="button"
                className="mt-1.5 rounded border border-red-400 bg-white px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-100"
                onClick={() => setRequired(false)}
              >
                필수 해제
              </button>
            </div>
          ) : null}
          {w.displayVariant === 'table' ? (
            <>
              {/* 열 헤더 안내(읽기 전용) — 헤더는 별도 설정값이 아니라 열 위젯 파생이라, 저작자가
                  "무엇을 고치면 헤더가 바뀌는지" 알 수 있게 현재 값 + 출처를 나열한다. 텍스트는
                  rc 정본(repeatTableColumnHeaderLabel) 재사용 — 캔버스 표와 같은 값. */}
              <RepeatTableColumnHeaderGuide
                repeatId={w.id}
                rowChildren={w.children}
                activeView={activeView}
                writeValues={writeValues}
                activeBranchSlots={activeBranchSlots}
                onWidgetUpdate={onWidgetUpdate}
                onTreeUpdate={onTreeUpdate}
                onRemove={onRemove}
              />
              <p className="text-[10px] leading-snug text-gray-400">
                표형에서는 행 골격·단 수·간격이 적용되지 않습니다(표가 자체 골격을 소유).
              </p>
            </>
          ) : null}
          <div>
            <label className={labelCls}>행 골격</label>
            {/* chrome: 미지정=기본(행 카드 + 요약 헤더/접기) / 'none'=민짜(장식 없이 children만).
                기본이면 키 삭제로 회귀 0. 표형에선 무의미 — 비활성(값은 보존해 되돌리기 무손실). */}
            <select
              className={inputCls}
              value={w.chrome ?? 'default'}
              disabled={w.displayVariant === 'table'}
              onChange={(e) =>
                onWidgetUpdate(w.id, (x) => {
                  if (x.kind !== 'repeat') return x;
                  const next = { ...x };
                  if (e.target.value === 'none') next.chrome = 'none';
                  else delete next.chrome;
                  return next;
                })
              }
            >
              <option value="default">기본(행 카드 + 요약 헤더/접기)</option>
              <option value="none">민짜(장식 없이 내용만 나열)</option>
            </select>
            <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
              민짜: 행 요약 헤더·번호·접기·"모두 접기" 없이 행 내용만 나열합니다(구 SMS 이행상태 리스트 룩).
            </p>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className={labelCls}>단 수</label>
              <select
                className={inputCls}
                value={w.columns ?? 1}
                disabled={w.displayVariant === 'table'}
                onChange={(e) =>
                  onWidgetUpdate(w.id, (x) => {
                    if (x.kind !== 'repeat') return x;
                    const v = Number(e.target.value) || 1;
                    const next = { ...x };
                    if (v <= 1) delete next.columns;
                    else next.columns = v;
                    return next;
                  })
                }
              >
                <option value={1}>1단</option>
                <option value={2}>2단</option>
                <option value={3}>3단</option>
              </select>
            </div>
            <div className="flex-1">
              <label className={labelCls}>간격 (px)</label>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={w.gap ?? ''}
                placeholder="0 (기본)"
                disabled={w.displayVariant === 'table'}
                onChange={(e) =>
                  onWidgetUpdate(w.id, (x) => {
                    if (x.kind !== 'repeat') return x;
                    const v =
                      e.target.value === '' ? undefined : Math.max(0, Number(e.target.value));
                    const next = { ...x };
                    if (v == null || Number.isNaN(v)) delete next.gap;
                    else next.gap = v;
                    return next;
                  })
                }
              />
            </div>
          </div>
          <p className="text-[10px] leading-snug text-gray-400">
            단 수 2 이상이면 행을 가로 다단 grid로 배치합니다(민짜 골격 권장 — 기본 행 카드는 다단 부적합). 실폼은 좁은 화면에서 1단.
          </p>
        </div>
      </div>
    );
  }

  case 'branch': {
    // 분기(투명 컨테이너) 속성 편집(P3) — 본문 BranchSlotsEditor 동형(위젯판). 슬롯 목록(라벨·조건·
    // ↑/↓·삭제·추가) + 활성 슬롯 전환(캔버스 탭·트리 서브행과 공유 state) + 활성 슬롯 자식 드릴다운.
    // 조건은 본문 동형 빌더(WidgetSlotConditionEditor) — 키/값 후보만 작성폼 값에서 얻는다(tree 전달).
    const activeIndex = Math.max(
      0,
      Math.min(activeBranchSlots[w.id] ?? 0, w.branches.length - 1),
    );
    return (
      <WidgetBranchSlotsEditor
        node={w}
        tree={tree}
        activeIndex={activeIndex}
        onSetActive={(i) => onSetActiveBranchSlot(w.id, i)}
        onAddSlot={() => onWidgetAddBranchSlot(w.id)}
        onRemoveSlot={(slotId) => onWidgetRemoveBranchSlot(w.id, slotId)}
        onMoveSlot={(slotId, dir) => onWidgetMoveBranchSlot(w.id, slotId, dir)}
        onUpdateCondition={(slotId, condition) =>
          onWidgetUpdateBranchSlot(w.id, slotId, { condition: condition ?? '' })
        }
        onUpdateLabel={(slotId, label) =>
          onWidgetUpdateBranchSlot(w.id, slotId, { label: label ?? '' })
        }
        onSelectNode={(id) => onSelectWidget?.(id)}
      />
    );
  }

  default: {
    // 새 위젯 kind 추가 시 여기서 tsc 에러(never 미할당) → 본문 분기 누락을 강제 검출.
    const _exhaustive: never = w;
    return _exhaustive;
  }
  }
  })();

  // 공통 프레임: kind별 본문 + 공통 푸터(정렬·안쪽 여백·삭제)를 한 space-y-3 컨테이너에 배치.
  return (
    <div className="space-y-3">
      {kindBody}
      {removeBtn}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 작성폼 표 위젯(formTable) 순수 뮤테이션 헬퍼 — onWidgetUpdate(w.id, (x)=> x.kind==='formTable'? ftXxx(x): x).
// 열 삭제 시 그 열 참조 셀(고정 columnId / 반복 cellFields)까지 정리(정합). id는 자동 생성(비노출).
// ─────────────────────────────────────────────────────────────

type FormTableRepeatRow = Extract<FormTableRow, { type: 'repeat' }>;

/**
 * 열 추가(맨 끝) — 마지막 열 기준 ftInsertColumn 위임: 인접 상속(폭·셀 표현 속성)이 셀 기준
 * 삽입과 동일하게 적용(2026-07-14 경로 통일 — 사용자 제보 "표/셀 경로 동작 상이").
 * 헤더=일반 셀 모델(2026-07-13)이라 label 부여 금지(정규화가 헤더 행 재생성).
 */
function ftAddColumn(w: FormTableWidget): FormTableWidget {
  const last = w.columns[w.columns.length - 1];
  if (!last) return { ...w, columns: [{ id: crypto.randomUUID() }] };
  return ftInsertColumn(w, last.id, 'right');
}

/** 고정 행 추가(맨 끝) — 마지막 행 기준 ftInsertRow 위임(인접 상속 동일 적용, 경로 통일). */
function ftAddFixedRow(w: FormTableWidget): FormTableWidget {
  const last = w.rows[w.rows.length - 1];
  if (!last) {
    return {
      ...w,
      rows: [
        {
          type: 'fixed',
          id: crypto.randomUUID(),
          cells: w.columns.map((c) => ({ columnId: c.id, text: '' })),
        },
      ],
    };
  }
  return ftInsertRow(w, last.id, 'below');
}

/** 반복 행 추가 — sourceKey/매핑 미설정(속성 패널에서 지정). */
function ftAddRepeatRow(w: FormTableWidget): FormTableWidget {
  const row: FormTableRow = { type: 'repeat', id: crypto.randomUUID(), sourceKey: '', cellFields: [] };
  return { ...w, rows: [...w.rows, row] };
}

/** 행 순서 이동(up/down) — 경계면 무변경. */
function ftMoveRow(w: FormTableWidget, rowId: string, dir: 'up' | 'down'): FormTableWidget {
  const idx = w.rows.findIndex((r) => r.id === rowId);
  if (idx < 0) return w;
  const to = dir === 'up' ? idx - 1 : idx + 1;
  if (to < 0 || to >= w.rows.length) return w;
  const rows = [...w.rows];
  [rows[idx], rows[to]] = [rows[to], rows[idx]];
  return { ...w, rows };
}

/**
 * 고정 행 높이 설정 — 숫자(px, 최소 36 규약)/'%' 문자열/undefined(=키 제거, auto). 반복 행은 무시.
 * 양식 탭 rowHeights 원소와 동형(mm 입력→px 저장은 호출측 buildFtHeight가 변환).
 */
function ftSetFixedRowHeight(
  w: FormTableWidget,
  rowId: string,
  height: number | string | undefined,
): FormTableWidget {
  return {
    ...w,
    rows: w.rows.map((r): FormTableRow => {
      if (r.id !== rowId || r.type !== 'fixed') return r;
      // 미지정=height 키만 제거(빈 auto) — 다른 행 축(visibleWhen 등)은 보존(spread+delete.
      // 구현이 행을 {type,id,cells}로 재조립하면 다른 축이 소실된다). 지정 시 height만 갱신.
      if (height == null) {
        const next = { ...r };
        delete next.height;
        return next;
      }
      return { ...r, height };
    }),
  };
}

/**
 * 고정 행 표시 조건(visibleWhen) 설정 — undefined=축 제거(빈 문자열 저장 금지). 반복 행은 무시.
 * 문법·렌더 규약은 FormTableRow.visibleWhen docblock 참조(결정 2026-08-17-formtable-row-visiblewhen).
 */
function ftSetFixedRowVisibleWhen(
  w: FormTableWidget,
  rowId: string,
  visibleWhen: string | undefined,
): FormTableWidget {
  return {
    ...w,
    rows: w.rows.map((r): FormTableRow => {
      if (r.id !== rowId || r.type !== 'fixed') return r;
      if (visibleWhen === undefined) {
        const next = { ...r };
        delete next.visibleWhen;
        return next;
      }
      return { ...r, visibleWhen };
    }),
  };
}

/**
 * 고정 행 표시 조건 편집기(행 목록용) — branch 슬롯과 같은 SlotConditionBuilder(콤보 빌더 기본 +
 * "직접 입력" 토글)로 편집한다(2026-08-17 사용자 확정 — 직접 입력만으로는 일반 저작자가 어렵다).
 *
 * 후보 키 공급(WidgetSlotConditionEditor와 같은 규약 + 행 필드):
 *  - (반복 안이면) 반복 소스의 **행 필드**가 1순위 — 행 조건의 주 용도가 "이 행 값 존재 판정"이다.
 *    점 경로 자식(계약 하위 필드)은 제외 — 조건 식별자는 단일 키만(evaluateCondition 문법).
 *  - 작성폼 스코프 select/radio bindingKey + 평가기준(서버 자동 제공) — branch 편집기와 동일.
 * 빈 결과 = 축 제거(undefined). 빌더가 파싱 못 하는 기존 식은 자동으로 직접 입력 모드가 되고,
 * 평가 불가 절(하이픈 식별자 등) 경고는 SlotConditionBuilder가 소유한다(세 진입점 공통).
 */
function FtRowVisibleWhenEditor({
  value,
  disabled,
  disabledReason,
  rowFields,
  scopeWidgets,
  onCommit,
}: {
  value: string | undefined;
  disabled?: boolean;
  /** disabled 사유 — rowSpan 병합 구간에 걸린 행 등. 입력 title + 아래 안내문에 함께 노출. */
  disabledReason?: string;
  /**
   * 반복 소스 행 필드 후보(반복 밖이면 빈 배열). 표 단위로 **미리 계산해** 넘긴다 — 행 목록
   * 안에서 만들면 행마다 새 배열이 되어 아래 useMemo가 매번 무효화된다.
   */
  rowFields: RepeatRowBindingOption[];
  /** 작성폼 스코프 select/radio 후보 — 위와 같은 이유로 표 단위 1회 수집분을 받는다. */
  scopeWidgets: { bindingKey: string; label?: string; options?: InputOption[] }[];
  onCommit: (visibleWhen: string | undefined) => void;
}) {
  const registeredKeys = useMemo(() => {
    const keys: string[] = rowFields.map((o) => o.value);
    if (!keys.includes(RISK_METHOD_TYPE_KEY)) keys.push(RISK_METHOD_TYPE_KEY);
    for (const sw of scopeWidgets) if (!keys.includes(sw.bindingKey)) keys.push(sw.bindingKey);
    return keys;
  }, [rowFields, scopeWidgets]);
  const valueSetForKey = (key: string): { value: string; label: string }[] | undefined => {
    if (key === RISK_METHOD_TYPE_KEY) return RISK_METHOD_TYPE_OPTIONS;
    const sw = scopeWidgets.find((x) => x.bindingKey === key);
    return sw && sw.options && sw.options.length > 0 ? sw.options : undefined;
  };
  const keyLabelFor = (key: string): string | undefined => {
    const rf = rowFields.find((o) => o.value === key);
    if (rf) return rf.label;
    if (key === RISK_METHOD_TYPE_KEY) return '평가기준';
    return scopeWidgets.find((x) => x.bindingKey === key)?.label?.trim() || undefined;
  };
  const keyTitleFor = (key: string): string => {
    if (rowFields.some((o) => o.value === key)) return `${key} — 반복 행(이 행)의 필드 값`;
    if (key === RISK_METHOD_TYPE_KEY)
      return `${key} — 위험성평가 개요의 '평가기준' 선택에서 서버가 자동 제공`;
    const sw = scopeWidgets.find((x) => x.bindingKey === key);
    if (sw) return `${key} — '${sw.label?.trim() || key}' 위젯의 현재 값`;
    return key;
  };
  if (disabled) {
    // 사유는 title(hover)만으로는 닿지 않는다 — 왜 못 쓰는지가 다음 행동(병합 해제)을 결정하므로
    // 입력 아래 텍스트로도 보여 준다. 래퍼는 활성 분기와 같은 구조(폭 계산 동일).
    return (
      <div className="min-w-0 flex-1">
        <input
          type="text"
          value={value ?? ''}
          disabled
          title={disabledReason}
          placeholder="비우면 항상 표시"
          className={`${INPUT_CLS} font-mono disabled:bg-gray-100 disabled:text-gray-400`}
        />
        {disabledReason ? (
          <p className="mt-0.5 text-[10px] leading-tight text-gray-400">{disabledReason}</p>
        ) : null}
      </div>
    );
  }
  return (
    <div className="min-w-0 flex-1">
      <SlotConditionBuilder
        condition={value}
        registeredKeys={registeredKeys}
        valueSetForKey={valueSetForKey}
        keyLabelFor={keyLabelFor}
        keyTitleFor={keyTitleFor}
        rawPlaceholder="예: resolutionNotes (비우면 항상 표시)"
        rawTitle="화이트리스트 문법: 식별키 / !식별키 / 식별키 === '리터럴' / && · || 결합. 키는 행 필드·작성폼 값. 비우면 항상 표시."
        onChange={onCommit}
      />
    </div>
  );
}

/**
 * 열 너비 설정 — 숫자(px)/'%' 문자열/undefined(=키 제거, auto·균등). 양식 탭 colWidths 원소와 동형.
 * widthPct(구)는 정규화가 width로 변환하므로 편집은 width만 기록(widthPct 잔존 시 함께 제거).
 */
function ftSetColumnWidth(
  w: FormTableWidget,
  columnId: string,
  width: number | string | undefined,
): FormTableWidget {
  return {
    ...w,
    columns: w.columns.map((c): FormTableColumn => {
      if (c.id !== columnId) return c;
      const next: FormTableColumn = { ...c };
      delete next.widthPct;
      if (width == null) delete next.width;
      else next.width = width;
      return next;
    }),
  };
}

/**
 * 붙여넣은 계산 셀의 행 상대 참조 재매핑(엑셀 상대참조 동형, 2026-07-14) — 원본과 **같은 행**의
 * 위젯을 참조하던 입력은 대상 행 같은 열의 위젯 키로 치환. 대상 행 그 열에 위젯이 없으면 그 입력
 * 제거(옛 행 값이 섞이는 것 방지). 그 외(교차 표/다른 행 참조·상수) 입력은 그대로 보존 — 의도적
 * 참조(예: 근로자수=타 표 근로자 합)는 기능이며 편집기 "표 밖 입력" 목록에서 관리.
 * 대상 행의 위젯 clone 키가 반영된 **배치 완료 후** 트리(w)로 호출할 것.
 */
function ftRemapComputeRow(
  w: FormTableWidget,
  compute: CellCompute,
  srcRowId: string,
  dstRowId: string,
): CellCompute {
  if (srcRowId === dstRowId) return compute;
  const srcRow = w.rows.find((r) => r.id === srcRowId);
  const dstRow = w.rows.find((r) => r.id === dstRowId);
  if (!srcRow || srcRow.type !== 'fixed' || !dstRow || dstRow.type !== 'fixed') return compute;
  const inputs: ComputeInput[] = [];
  for (const inp of compute.inputs) {
    const key = inp.binding?.bindingKey;
    if (!key) {
      inputs.push(inp);
      continue;
    }
    const srcCell = srcRow.cells.find((c) => c.widget?.bindingKey === key);
    if (!srcCell) {
      inputs.push(inp); // 원본 행 참조 아님 — 그대로(교차 표/다른 행/상수 보존)
      continue;
    }
    const dstKey = dstRow.cells.find((c) => c.columnId === srcCell.columnId)?.widget?.bindingKey;
    if (dstKey && inp.binding) {
      inputs.push({ ...inp, binding: { ...inp.binding, bindingKey: dstKey } });
    }
  }
  return { ...compute, inputs };
}

/** 고정 행 셀 upsert(columnId 기준) — cell=null이면 그 열 셀 제거(비움). */
function ftSetFixedCell(
  w: FormTableWidget,
  rowId: string,
  columnId: string,
  cell: FormTableFixedCell | null,
): FormTableWidget {
  const rows = w.rows.map((row): FormTableRow => {
    if (row.id !== rowId || row.type !== 'fixed') return row;
    if (cell === null) return { ...row, cells: row.cells.filter((c) => c.columnId !== columnId) };
    const exists = row.cells.some((c) => c.columnId === columnId);
    const cells = exists
      ? row.cells.map((c) => (c.columnId === columnId ? cell : c))
      : [...row.cells, cell];
    return { ...row, cells };
  });
  return { ...w, rows };
}

/**
 * 표 전체 고정 셀 그리드 위치 열거 — 표 수준 "전 고정 셀 일괄 스타일" 대상(2026-07-13 parity P3).
 * rowSpan/colSpan에 덮인 위치는 제외(렌더의 occupiedColsByRowId 규약과 동형). 미생성 위치는 cell=undefined
 * (style-only upsert 대상). 반복 행은 제외(고정 셀 스타일 축만). 슬롯 순서=행→열(안정, undo 1스텝).
 */
function ftAllFixedCellSlots(
  w: FormTableWidget,
): { rowId: string; columnId: string; cell?: FormTableFixedCell }[] {
  const cols = w.columns;
  const out: { rowId: string; columnId: string; cell?: FormTableFixedCell }[] = [];
  // 열 인덱스 → 아래로 남은 rowSpan 커버 행수(렌더 pending 맵 동형).
  const pending = new Map<number, number>();
  for (const row of w.rows) {
    if (row.type !== 'fixed') {
      pending.clear();
      continue;
    }
    const occ = new Set<number>();
    for (const [ci, n] of pending) if (n > 0) occ.add(ci);
    // 이 행 소비 → 잔여 -1.
    for (const ci of Array.from(pending.keys())) {
      const nn = (pending.get(ci) ?? 0) - 1;
      if (nn <= 0) pending.delete(ci);
      else pending.set(ci, nn);
    }
    const cellByCol = new Map(row.cells.map((c) => [c.columnId, c]));
    let ci = 0;
    while (ci < cols.length) {
      if (occ.has(ci)) {
        ci += 1;
        continue;
      }
      const col = cols[ci];
      const cell = cellByCol.get(col.id);
      out.push({ rowId: row.id, columnId: col.id, cell });
      const cs = Math.max(1, cell?.colSpan ?? 1);
      const rs = Math.max(1, cell?.rowSpan ?? 1);
      if (rs > 1) for (let k = ci; k < ci + cs; k++) pending.set(k, rs - 1);
      ci += cs;
    }
  }
  return out;
}

/** 반복 행 patch(sourceKey/allowAddRemove 등). */
function ftPatchRepeatRow(
  w: FormTableWidget,
  rowId: string,
  patch: Partial<FormTableRepeatRow>,
): FormTableWidget {
  const rows = w.rows.map((row): FormTableRow =>
    row.id === rowId && row.type === 'repeat' ? { ...row, ...patch } : row,
  );
  return { ...w, rows };
}

/** 반복 행 열↔def 필드 매핑 설정 — field 빈값이면 그 열 매핑 제거. */
function ftSetRepeatCellField(
  w: FormTableWidget,
  rowId: string,
  columnId: string,
  field: string,
): FormTableWidget {
  const rows = w.rows.map((row): FormTableRow => {
    if (row.id !== rowId || row.type !== 'repeat') return row;
    const rest = row.cellFields.filter((cf) => cf.columnId !== columnId);
    return { ...row, cellFields: field ? [...rest, { columnId, field }] : rest };
  });
  return { ...w, rows };
}

/**
 * 표 위젯 열 셀의 현행 종류 판정(모델 기준).
 * 셀이 존재하는데 text/widget 둘 다 없으면 '입력(위젯 미선택)' 상태 — 종류 전환을 모델로 영속해
 * 로컬 pending state 없이도 select가 되돌아가지 않는다(2026-07-12 '입력' 미반영 버그 수정).
 */
function ftFixedCellType(
  cell: FormTableFixedCell | undefined,
): 'empty' | 'text' | 'input' | 'compute' {
  if (!cell) return 'empty';
  if (cell.widget) return 'input';
  if (cell.compute) return 'compute';
  if (cell.text !== undefined) return 'text';
  return 'input';
}

/**
 * 복수 선택 공통값 판정 — 전부 동일하면 그 값, 하나라도 다르면 mixed(값 없음). 빈 배열=값 없음.
 * 복수 셀 공통 속성 일괄 편집(2026-07-13)의 tri-state(혼합) 표현 판정용.
 */
function ftCommon<T>(items: T[]): { mixed: boolean; value?: T } {
  if (items.length === 0) return { mixed: false };
  const first = items[0];
  return items.every((x) => x === first) ? { mixed: false, value: first } : { mixed: true };
}

/**
 * 셀 종류 전환 시 새 셀(또는 null=비움) 생성 — 단일 셀 뷰의 전환 규약과 동일.
 * 텍스트↔입력 전환 시 기존 colSpan 보존, 입력 전환 시 기존 위젯 유지(있으면), 텍스트 전환 시 기존 text 유지.
 */
function ftCellWithType(
  cell: FormTableFixedCell | undefined,
  columnId: string,
  v: 'empty' | 'text' | 'input' | 'compute',
): FormTableFixedCell | null {
  if (v === 'empty') return null;
  // 셀 정렬은 종류와 독립 속성이라 전환 시 보존(colSpan과 동일 취급).
  const keepAlign = ftKeepCellAlign(cell);
  if (v === 'text')
    return {
      columnId,
      text: cell?.text ?? '',
      ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
      ...keepAlign,
    };
  if (v === 'compute')
    return {
      columnId,
      compute: cell?.compute ?? { inputs: [], mode: 'op', op: 'sum' },
      ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
      ...keepAlign,
    };
  return {
    columnId,
    ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
    ...(cell?.widget ? { widget: cell.widget } : {}),
    ...keepAlign,
  };
}

/** legacy align(left/center/right) → 신형 contentAlignH(AlignVal). 표시/하위호환 매핑. */
const LEGACY_ALIGN_TO_H: Record<'left' | 'center' | 'right', AlignVal> = {
  left: 'start',
  center: 'center',
  right: 'end',
};

/**
 * 셀 정렬 + 스타일 등 **종류 불변(presentation)** 필드를 보존용으로 추출(parity P2.5로 style 합류).
 * 종류/텍스트/위젯 변경·복붙 시 병합해 정렬·스타일 유지(colSpan과 동일 취급). legacy align은 그대로
 * 실어 데이터 손실을 막고, reader가 하위호환 해석(정렬 편집 시엔 onPick이 신형으로 대체 + 구형 제거).
 * ⚠️ 스타일을 직접 patch하는 경로(스타일 편집기)에서는 셀 전체 spread로 style을 넘기므로 이 헬퍼를
 * 추가로 spread하지 말 것(옛 style이 편집분을 덮음).
 */
function ftKeepCellAlign(cell: FormTableFixedCell | undefined): Partial<FormTableFixedCell> {
  return {
    ...(cell?.contentAlignH ? { contentAlignH: cell.contentAlignH } : {}),
    ...(cell?.contentAlignV ? { contentAlignV: cell.contentAlignV } : {}),
    ...(cell?.align ? { align: cell.align } : {}),
    ...(cell?.style ? { style: cell.style } : {}),
  };
}

/**
 * 셀 스타일 병합(parity P2.5) — 기존 style에 next(부분)를 얹어 반환. 결과가 빈 객체면 undefined
 * (모델 정리). 단일/다중 스타일 편집기(SingleCellStyleFields/BulkCellStyleFields)의 셀 반영에 공용.
 */
function ftMergeStyle(
  prev: TableCellStyle | undefined,
  next: Partial<TableCellStyle>,
): TableCellStyle | undefined {
  const merged: TableCellStyle = { ...(prev ?? {}), ...next };
  return Object.keys(merged).length > 0 ? merged : undefined;
}

/**
 * 셀 정렬 편집 결과(9방향 그리드 onPick의 H,V)를 정렬 필드로 변환.
 * 'start'=엔진 기본이라 미저장(키 제거). 신형만 기록하고 legacy align 키는 제거(이중 저장 금지).
 */
function ftCellAlignFields(h: AlignVal, v: AlignVal): Partial<FormTableFixedCell> {
  return {
    contentAlignH: h === 'start' ? undefined : h,
    contentAlignV: v === 'start' ? undefined : v,
    align: undefined,
  };
}

// ⚠️ bg 미포함 — 사용처가 `${FT_CTRL_CLS} bg-*`로 덧붙일 때 bg-white와 충돌하면 CSS 생성 순서에
// 따라 흰 배경이 이겨 버튼이 투명해질 수 있음(2026-07-12 '만들어 배치' 버튼 실사고).
const FT_CTRL_CLS =
  'text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400';

/** 표의 계산 대상 후보(고정 행 widget 보유 셀, bindingKey 중복 제거) 수집 — 라벨=열·행 번호. */
function collectFtComputeCandidates(t: FormTableWidget): {
  bindingKey: string;
  label: string;
  rowId: string;
  columnId: string;
  widgetLabel?: string;
}[] {
  const seen = new Set<string>();
  const out: ReturnType<typeof collectFtComputeCandidates> = [];
  t.rows.forEach((row, ri) => {
    if (row.type !== 'fixed') return;
    for (const c of row.cells) {
      if (!c.widget || seen.has(c.widget.bindingKey)) continue;
      seen.add(c.widget.bindingKey);
      // columns는 구조 축만(라벨 없음 — 헤더=일반 셀 모델) → 열 번호로 표기.
      const ci = t.columns.findIndex((cc) => cc.id === c.columnId);
      out.push({
        bindingKey: c.widget.bindingKey,
        label: `열 ${ci >= 0 ? ci + 1 : '?'} · 행 ${ri + 1}`,
        rowId: row.id,
        columnId: c.columnId,
        widgetLabel: c.widget.label,
      });
    }
  });
  return out;
}

/**
 * 작성폼 formTable 계산 셀 편집기(2026-07-13) — 위젯 셀의 bindingKey를 inputs로 조립.
 * 엔진은 렌더에서 CellCompute(computeCellCompute) 재사용, 여기선 draft(inputs/op·expr/format)만 저작.
 * 합산 대상 = 같은 표 + 다른 표(2026-07-14 교차 표 개방) 위젯 보유 셀(체크 다중 선택),
 * 연산 기본 sum(고급 expr), 단위(format.unit '명').
 * 표시 전용(박제 안 함) — 결정 기록: docs/decisions/2026-07-13-writeform-compute-cell.md
 */
function FtComputeCellEditor({
  w,
  otherTables,
  compute,
  onPatch,
  onCellHover,
}: {
  w: FormTableWidget;
  /** 같은 작성 폼의 다른 formTable들 — "다른 표" 합산 대상 섹션(교차 표 참조 저작, 2026-07-14). */
  otherTables?: FormTableWidget[];
  compute: CellCompute;
  onPatch: (next: CellCompute) => void;
  /** 합산 대상 항목 hover → 캔버스 해당 셀 하이라이트(ftHoverCell 재사용 — 표별 tableId 포함). */
  onCellHover?: (loc: { tableId: string; rowId: string; columnId: string } | null) => void;
}) {
  // 같은 표 위젯 셀 후보 — 고정 행의 widget 보유 셀(bindingKey 중복 제거). 라벨=열·행.
  const candidates = useMemo(() => collectFtComputeCandidates(w), [w]);
  // 다른 표 후보 섹션 — 값 scope가 전역 flat이라 교차 표 참조는 엔진이 원래 지원(예: 근로자수=
  // 타 표 근로자 합). 편집기에서만 불가능하던 것을 표별 섹션으로 개방.
  const otherSections = useMemo(
    () =>
      (otherTables ?? [])
        .map((t) => ({
          tableId: t.id,
          tableLabel: t.title || '(제목 없는 표)',
          items: collectFtComputeCandidates(t),
        }))
        .filter((s) => s.items.length > 0),
    [otherTables],
  );
  // 편집기 언마운트 시 hover 잔존 해제 — 카드 언마운트로 mouseleave가 영영 안 오는 stale hover 방지.
  // ⚠️ ref로 고정: 호출측이 인라인 콜백을 넘기면 deps=[onCellHover]는 매 렌더 cleanup이 돌아
  //    mouseenter 직후 하이라이트를 즉시 지움(hover가 재렌더를 유발하므로 — 2026-07-13 실버그).
  const onCellHoverRef = useRef(onCellHover);
  onCellHoverRef.current = onCellHover;
  useEffect(() => () => onCellHoverRef.current?.(null), []);

  const selectedKeys = new Set(
    compute.inputs.map((inp) => inp.binding?.bindingKey).filter((k): k is string => !!k),
  );

  const toggleKey = (bindingKey: string): void => {
    if (selectedKeys.has(bindingKey)) {
      onPatch({
        ...compute,
        inputs: compute.inputs.filter((inp) => inp.binding?.bindingKey !== bindingKey),
      });
    } else {
      const name = nextComputeInputName(compute.inputs.length);
      onPatch({
        ...compute,
        inputs: [
          ...compute.inputs,
          { id: crypto.randomUUID(), name, binding: { bindingKey, lifecycle: 'snapshot' } },
        ],
      });
    }
  };

  const unit = compute.format?.kind === 'unit' ? (compute.format.unit ?? '') : '';
  const setUnit = (u: string): void =>
    onPatch({ ...compute, format: u ? { kind: 'unit', unit: u } : undefined });

  return (
    <div className="space-y-2 border-t border-gray-200 pt-3">
      <p className="text-[11px] leading-relaxed text-gray-500">
        같은 표의 입력 셀 값을 실시간 합산해 표시합니다(저장 안 함 — 최종 보고서는 양식이 동일 계산).
      </p>
      <div className="flex items-center gap-1.5">
        <select
          className={FT_CTRL_CLS}
          title="방식"
          value={compute.mode}
          onChange={(e) => {
            const m = e.target.value as 'op' | 'expr';
            onPatch(
              m === 'op'
                ? { ...compute, mode: 'op', op: compute.op ?? 'sum' }
                : { ...compute, mode: 'expr', expr: compute.expr ?? '' },
            );
          }}
        >
          <option value="op">연산</option>
          <option value="expr">수식(고급)</option>
        </select>
        {compute.mode === 'op' ? (
          <select
            className={FT_CTRL_CLS}
            title="연산"
            value={compute.op ?? 'sum'}
            onChange={(e) => onPatch({ ...compute, op: e.target.value as CellComputeOp })}
          >
            {CELL_COMPUTE_OPS.map((op) => (
              <option key={op} value={op}>
                {CELL_COMPUTE_OP_LABELS[op]}
              </option>
            ))}
          </select>
        ) : null}
        <input
          type="text"
          className={`${FT_CTRL_CLS} w-14`}
          value={unit}
          placeholder="단위"
          title="단위(예: 명)"
          onChange={(e) => setUnit(e.target.value)}
        />
      </div>
      {compute.mode === 'expr' ? (
        <div>
          <input
            type="text"
            className={`${FT_CTRL_CLS} w-full`}
            value={compute.expr ?? ''}
            placeholder="(a + b) * 0.5"
            onChange={(e) => onPatch({ ...compute, expr: e.target.value })}
          />
          <p className="mt-0.5 text-[10px] text-gray-400">
            사용 가능한 입력:{' '}
            {compute.inputs
              .map((inp) => inp.name)
              .filter(Boolean)
              .join(', ') || '(아래에서 선택)'}
          </p>
        </div>
      ) : null}
      <div>
        <p className="mb-1 text-[11px] font-medium text-gray-500">합산 대상(입력 셀)</p>
        {candidates.length === 0 ? (
          <p className="text-[11px] text-gray-400">
            같은 표에 입력 셀이 없습니다. 먼저 입력 셀을 배치하세요.
          </p>
        ) : (
          <div className="space-y-1">
            {candidates.map((cand) => (
              <label
                key={cand.bindingKey}
                className="flex cursor-pointer items-center gap-1.5 rounded px-0.5 text-xs text-gray-600 hover:bg-brand-50"
                title={`${cand.label}${cand.widgetLabel ? ` — ${cand.widgetLabel}` : ''} (${cand.bindingKey})`}
                onMouseEnter={() =>
                  onCellHover?.({ tableId: w.id, rowId: cand.rowId, columnId: cand.columnId })
                }
                onMouseLeave={() => onCellHover?.(null)}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={selectedKeys.has(cand.bindingKey)}
                  onChange={() => toggleKey(cand.bindingKey)}
                />
                <span className="min-w-0 truncate">{cand.label}</span>
                <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">
                  {cand.bindingKey}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
      {/* 다른 표 합산 대상 — 표별 접이식 섹션(기본 접힘 — 주 사용은 같은 표, 교차 참조는 의도적
          선택). hover는 그 표의 셀 하이라이트. */}
      {otherSections.map((sec) => (
        <details key={sec.tableId}>
          <summary className="cursor-pointer text-[11px] font-medium text-gray-500">
            다른 표 — {sec.tableLabel}
          </summary>
          <div className="mt-1 space-y-1">
            {sec.items.map((cand) => (
              <label
                key={cand.bindingKey}
                className="flex cursor-pointer items-center gap-1.5 rounded px-0.5 text-xs text-gray-600 hover:bg-brand-50"
                title={`${sec.tableLabel} · ${cand.label}${cand.widgetLabel ? ` — ${cand.widgetLabel}` : ''} (${cand.bindingKey})`}
                onMouseEnter={() =>
                  onCellHover?.({
                    tableId: sec.tableId,
                    rowId: cand.rowId,
                    columnId: cand.columnId,
                  })
                }
                onMouseLeave={() => onCellHover?.(null)}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={selectedKeys.has(cand.bindingKey)}
                  onChange={() => toggleKey(cand.bindingKey)}
                />
                <span className="min-w-0 truncate">{cand.label}</span>
                <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">
                  {cand.bindingKey}
                </span>
              </label>
            ))}
          </div>
        </details>
      ))}
      {/* 표 밖/상수 입력 — 복붙 잔재 또는 의도적 교차 표 참조(예: 근로자수=타 표 근로자 합).
          같은 표 후보 목록에 없으면 보이지도 해제되지도 않는 유령 입력이 됨(2026-07-14 제보:
          복붙된 계 셀에 타 표 키 2개 잔존 → 대상 선택이 반영 안 되는 것처럼 보임). 표시 +
          개별 제거만 제공 — 자동 제거는 금지(교차 표 참조는 값 scope가 전역 flat이라 정상 기능). */}
      {(() => {
        const candidateKeys = new Set(
          [...candidates, ...otherSections.flatMap((s) => s.items)].map((c) => c.bindingKey),
        );
        const externals = compute.inputs.filter(
          (inp) =>
            inp.const !== undefined || !inp.binding || !candidateKeys.has(inp.binding.bindingKey),
        );
        if (externals.length === 0) return null;
        return (
          <div>
            <p className="mb-1 text-[11px] font-medium text-amber-600">
              표 밖 입력 {externals.length}개 — 함께 계산됩니다(복붙 잔재라면 제거하세요)
            </p>
            <div className="space-y-1">
              {externals.map((inp, i) => (
                <div
                  key={inp.id ?? `${inp.name}-${i}`}
                  className="flex items-center gap-1.5 rounded bg-amber-50 px-1 py-0.5 text-[11px] text-gray-600"
                >
                  <span className="min-w-0 flex-1 truncate font-mono text-[10px]">
                    {inp.const !== undefined ? `상수 ${inp.const}` : inp.binding?.bindingKey}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 text-red-600 hover:underline"
                    onClick={() =>
                      onPatch({ ...compute, inputs: compute.inputs.filter((x) => x !== inp) })
                    }
                  >
                    제거
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/** 범용 검색 콤보박스 — 입력 필터 + 드롭다운(라벨/키/보조 텍스트 매칭). 선택 시 입력에 라벨 표시. */
function SearchCombo({
  items,
  value,
  placeholder,
  onPick,
  onClear,
}: {
  items: { key: string; label: string; sub?: string }[];
  /** 선택된 항목 key(미선택 undefined). */
  value?: string;
  placeholder: string;
  onPick: (key: string) => void;
  /** 지정 시 선택 해제 버튼(✕) 노출. */
  onClear?: () => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.key === value);
  const qt = q.trim().toLowerCase();
  const filtered = qt
    ? items.filter(
        (i) =>
          i.label.toLowerCase().includes(qt) ||
          i.key.toLowerCase().includes(qt) ||
          (i.sub ?? '').toLowerCase().includes(qt),
      )
    : items;
  return (
    <div className="relative flex min-w-0 flex-1 items-center gap-1">
      <input
        type="text"
        className={`${FT_CTRL_CLS} min-w-0 flex-1`}
        placeholder={placeholder}
        // 닫힘 상태엔 선택 라벨 표시, 열림 상태엔 검색어(포커스 시 비워 전체 노출).
        value={open ? q : (selected?.label ?? '')}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setQ('');
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      {onClear && selected ? (
        <button
          type="button"
          className="shrink-0 text-gray-400 hover:text-red-500"
          title="선택 해제"
          onClick={onClear}
        >
          ✕
        </button>
      ) : null}
      {open ? (
        <ul className="absolute left-0 top-full z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <li className="px-2 py-1.5 text-[11px] text-gray-400">검색 결과가 없습니다.</li>
          ) : (
            filtered.map((i) => (
              <li key={i.key}>
                <button
                  type="button"
                  className="block w-full px-2 py-1.5 text-left hover:bg-brand-50"
                  title={`${i.label}${i.sub ? `\n${i.sub}` : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onPick(i.key);
                    setOpen(false);
                    setQ('');
                  }}
                >
                  <span className="block truncate text-xs text-gray-700">{i.label}</span>
                  {i.sub ? (
                    <span className="block truncate text-[10px] text-gray-400">{i.sub}</span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * 카탈로그 자동 채움(prefill) 소스 편집기 — entry 선택 + (list면) 행 인덱스 + 반환 필드 select,
 * select 경로는 자유 수정 가능([key=…] 필터 등 고급 경로 직접 입력). treeOwned 독립 위젯의
 * 속성 편집과 표 셀 "카탈로그에서 만들기"가 공용(2026-07-12 — 양식에 숨은 바인딩 없이 API 사용).
 */
/**
 * raw select 경로 입력 — 위 콤보(데이터 소스·반환 필드) 선택이 조합돼 들어오는 칸이자, 콤보로는
 * 만들 수 없는 경로(`[key=…]` 필터·중첩)를 직접 쓰는 탈출구.
 * `onHide`가 있을 때만 접기를 제공한다 — 콤보로 표현되는 값이라 접어도 잃는 정보가 없는 경우다.
 */
function RawSelectPathField({
  sel,
  emit,
  onHide,
}: {
  sel: string;
  emit: (select: string | undefined) => void;
  onHide?: () => void;
}) {
  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between gap-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
          select 경로 (위 선택의 결과 — [key=…] 필터 등 고급 경로는 직접 수정)
        </p>
        {onHide ? (
          <button
            type="button"
            onClick={onHide}
            className="text-[10px] text-gray-400 underline hover:text-gray-600"
          >
            접기
          </button>
        ) : null}
      </div>
      <input
        type="text"
        className={`${FT_CTRL_CLS} w-full font-mono`}
        value={sel}
        placeholder="예: 0.categories[key=office].count"
        title="최종 select 경로 — 행 인덱스·반환 필드 선택이 여기로 조합되며, 직접 수정도 가능"
        onChange={(e) => emit(e.target.value || undefined)}
      />
    </div>
  );
}

function CatalogPrefillPicker({
  catalog,
  value,
  onChange,
  returnPaths,
  fieldPlaceholder,
  firstRowOnly,
}: {
  catalog: CatalogEntry[];
  value?: { catalogKey: string; select?: string };
  onChange: (v: { catalogKey: string; select?: string } | undefined) => void;
  /** 반환 필드 후보 산출 override — 기본은 스칼라 후보(prefillReturnPaths). 배열만 필요한
   *  소비처(rangeMap 구간 기준표)가 arrayReturnPaths를 넣는다. */
  returnPaths?: (entry: CatalogEntry | undefined) => { path: string; label: string }[];
  /** 반환 필드 콤보 placeholder override(기본 "반환 필드 검색·선택…"). */
  fieldPlaceholder?: string;
  /**
   * 소비처가 **배열의 첫 항목에서** 읽는다 — 행 인덱스를 고르지 않고 select도 `0.` 접두 없이 낸다.
   * 표시 형식의 보조 자료 출처(scopeFrom)가 그렇다: rc `resolveCatalogScopeValue`가 카탈로그 값이
   * 배열이면 첫 항목을 잡은 **뒤에** select를 적용하므로, 인덱스를 붙이면 그 경로는 영영 안 읽힌다
   * (고를 수 있는데 조용히 안 되는 선택지가 된다). 미지정=종전 그대로(행 인덱스 노출).
   */
  firstRowOnly?: boolean;
}) {
  const entries = catalog.filter((e) => e.kind === 'data' || e.kind === 'computed');
  const entry = entries.find((e) => e.key === value?.catalogKey);
  const isList = entry?.cardinality === 'list' && !firstRowOnly;
  const sel = value?.select ?? '';
  const idxMatch = sel.match(/^(\d+)\.(.*)$/);
  // 행 인덱스 draft — 반환 필드 선택 전엔 select가 조합되지 않아 파생값('0')이 입력을 덮던 버그 수정
  // (2026-07-12): 필드 없는 동안 로컬로 보존하고, 필드 선택 시 draft로 조합. select 파생값이 우선.
  const [idxDraft, setIdxDraft] = useState<string | null>(null);
  // raw select 경로 펼침 — 콤보로 도달할 수 없는 값(필터·중첩 경로)일 때만 기본 노출한다.
  const [showRaw, setShowRaw] = useState(false);
  const rowIdx = isList ? (idxMatch ? idxMatch[1] : (idxDraft ?? '0')) : '';
  const fieldPath = isList && idxMatch ? idxMatch[2] : sel;
  const compose = (idx: string, path: string): string | undefined =>
    path ? (isList ? `${idx || '0'}.${path}` : path) : undefined;
  const paths = (returnPaths ?? prefillReturnPaths)(entry);
  // 2개년 슬롯 카탈로그(근로자/재해 현황)는 행 인덱스를 숫자 대신 당해/전년도 라벨 select로.
  const isTwoYearSlot = !!entry && TWO_YEAR_SLOT_CATALOG_KEYS.has(entry.key);
  const emit = (select: string | undefined) =>
    entry ? onChange({ catalogKey: entry.key, ...(select ? { select } : {}) }) : undefined;
  return (
    <div className="space-y-1">
      {/* 데이터 소스 — 공통 코어 CatalogEntryPicker(P2 이관). 그룹 헤더·kind 배지·ko 정렬·
          legacyHidden 보존 표기·allowClear는 코어가 보장하고, 선택된 출처가 저작 파라미터를
          선언하면 파라미터 인라인 블록도 코어가 자동 렌더한다(전역 편집기와 같은 값). */}
      <CatalogEntryPicker
        entries={entries}
        value={value?.catalogKey}
        allowClear
        placeholder="데이터 소스 검색·선택…"
        onChange={(k) => {
          setIdxDraft(null);
          if (!k) {
            onChange(undefined);
            return;
          }
          // 반환 필드는 **저작자가 명시적으로 고른다** — 후보가 하나뿐이어도 자동으로 채우지 않는다
          // (2026-08-05 사용자 확정, 결정 §3). 자동으로 정해지면 저작물의 근거가 화면에서 사라지고,
          // 후보가 늘어나는 순간(다른 카탈로그가 같은 형상을 내면) 조용히 의미가 달라진다.
          onChange({ catalogKey: k });
        }}
      />
      {entry ? (
        <div className="flex items-center gap-1">
          {isList ? (
            isTwoYearSlot ? (
              <select
                className={`${FT_CTRL_CLS} w-24 shrink-0`}
                value={rowIdx === '1' ? '1' : '0'}
                title="연도 행 — 서버가 항상 당해년도(0)·전년도(1) 순으로 내려줍니다"
                onChange={(e) => {
                  setIdxDraft(e.target.value);
                  if (fieldPath) emit(compose(e.target.value, fieldPath));
                }}
              >
                <option value="0">당해년도</option>
                <option value="1">전년도</option>
              </select>
            ) : (
              <input
                type="number"
                min={0}
                className={`${FT_CTRL_CLS} w-14`}
                value={rowIdx}
                title="행 인덱스 (0=첫 행)"
                onChange={(e) => {
                  setIdxDraft(e.target.value);
                  if (fieldPath) emit(compose(e.target.value, fieldPath));
                }}
              />
            )
          ) : null}
          {/* 반환 필드는 후보 수와 무관하게 **항상 콤보로 고른다** — 하나뿐이라고 텍스트로 대체하면
              저작자가 지정을 바꾸거나 해제할 수 없고, 나중에 같은 형상의 카탈로그가 늘면 UI가 갑자기
              달라진다. 고르면 아래 select 경로에 그 결과가 그대로 보인다(2026-08-05 결정 §3).

              ⚠️ **순서는 카탈로그 `returns` 선언 순서이고, 그대로 둔다**(줄기 P5 판단 — 2026-10-27).
              데이터 소스 목록(CatalogEntryPicker)은 ko 라벨 정렬인데 여기는 아니다 — 축이 다르다.
              그쪽 근거는 「배열 선언 순서는 목록에서 못 읽는다」(entry는 여러 그룹에 흩어진 집합이라
              선언 순서가 임의다)인데, returns는 **스키마라 그 순서를 읽을 수 있다**. 실측 셋:
                · `organization.returns` = 조직명 → 사업자번호 → 대표자명 → 주소(**서류 기재 순서**)
                · `LEGAL_POSITIONS` = 제5조 → 15 → 62 → 16 → 17 → 18 → 22 → 19(**법 조항 순서**이고
                  조항 번호 순도 아니다 — 대표자→책임자→총괄→감독자 위계다). ko 정렬은 이 위계를 부순다
                · `prefillReturnPaths`의 enum 전개 라벨을 정렬하면 **value-set 선언 순서까지** 재배열된다
                  (`risk-method-types`에서 서술형↔체크리스트가 뒤바뀐다)
              **되짚을 조건**: 한 entry의 후보가 "눈으로 훑기 어려울 만큼" 많아져 저작자가 라벨 정렬을
              요구할 때. 재는 재료는 **이 콤보가 받는 `paths` 길이**다 — 다만 그 산출 함수
              (`prefillReturnPaths`)는 이 파일 안에 있고 **export되지 않아** spec에서 바로 못 센다.
              세려면 그 함수를 `catalogPaths.ts`로 옮겨(이미 `rangeReturnPaths`·`scopeReturnPaths`가
              사는 자리다) 짝 spec에서 카탈로그 전수로 돌리는 것이 순서다. 정렬로 가더라도
              **enum 전개분은 value-set 순서를 지켜야** 한다(위 셋째 근거). */}
          <SearchCombo
            items={paths.map((p) => ({ key: p.path, label: p.label, sub: p.path }))}
            value={paths.some((p) => p.path === fieldPath) ? fieldPath : undefined}
            placeholder={fieldPlaceholder ?? '반환 필드 검색·선택…'}
            onPick={(p) => emit(compose(rowIdx, p))}
          />
        </div>
      ) : null}
      {/* raw select 경로 — 위 콤보 선택의 **결과**라, 콤보로 표현되는 값이면 늘 펼쳐 둘 이유가 없다
          (저작자 눈에는 그 칸이 입력칸으로 보여 "여기에 경로를 쳐야 하나"가 된다 — 2026-08-05 제보).
          제거가 아니라 격하다: `[key=…]` 필터·중첩 경로는 콤보로 못 만들므로 탈출구를 남기고,
          **콤보로 도달할 수 없는 값이면 처음부터 펼쳐** 기존 저작물이 숨지 않게 한다. */}
      {entry ? (
        !sel || paths.some((p) => p.path === fieldPath) ? (
          showRaw ? (
            <RawSelectPathField sel={sel} emit={emit} onHide={() => setShowRaw(false)} />
          ) : (
            <button
              type="button"
              onClick={() => setShowRaw(true)}
              className="text-[10px] text-gray-400 underline hover:text-gray-600"
              title="필터·중첩 경로 등 콤보로 만들 수 없는 select 경로를 직접 씁니다"
            >
              고급: select 경로 직접 수정
            </button>
          )
        ) : (
          <RawSelectPathField sel={sel} emit={emit} />
        )
      ) : null}
    </div>
  );
}

/**
 * 표 셀 배치용 미배치 위젯 콤보박스 — 검색 입력 + 필터 드롭다운(라벨/바인딩 키/카탈로그 경로 매칭).
 * 항목은 2줄: 라벨 + 출처(catalogKey·select [행 인덱스]) — 값: 현재 컨텍스트 미리보기.
 */
function FtCellWidgetPicker({
  pool,
  widgetPrefill,
  writeValues,
  onPick,
}: {
  pool: InputWidget[];
  widgetPrefill: (u: InputWidget) => { catalogKey: string; select?: string } | undefined;
  writeValues?: Record<string, unknown>;
  onPick: (u: InputWidget) => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const qt = q.trim().toLowerCase();
  const filtered = qt
    ? pool.filter((u) => {
        const pf = widgetPrefill(u);
        return (
          (u.label ?? '').toLowerCase().includes(qt) ||
          u.bindingKey.toLowerCase().includes(qt) ||
          (pf ? `${pf.catalogKey} ${pf.select ?? ''}`.toLowerCase().includes(qt) : false)
        );
      })
    : pool;
  return (
    <div className="relative">
      <input
        type="text"
        className={`${FT_CTRL_CLS} w-full`}
        placeholder="미배치 위젯 검색·선택…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // blur로 닫되 항목 클릭이 먼저 처리되게 지연(항목 버튼은 mousedown preventDefault로 blur 억제).
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open ? (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <li className="px-2 py-1.5 text-[11px] text-gray-400">검색 결과가 없습니다.</li>
          ) : (
            filtered.map((u) => {
              const pf = widgetPrefill(u);
              const ii = selectIndexInfo(pf?.catalogKey, pf?.select);
              const pv = composerValuePreview(writeValues?.[u.bindingKey]);
              const labelLine = u.label ?? u.bindingKey;
              const sourcePart = pf
                ? `${pf.catalogKey}${pf.select ? ` · ${pf.select}` : ''}${ii ? ` [${ii}]` : ''} — `
                : '';
              const infoLine = `${sourcePart}값: ${pv}`;
              // 툴팁은 값 축약도 완화(400자) — truncate·미리보기 캡으로 생략된 내용 전체 확인.
              const tooltipInfo = `${sourcePart}값: ${composerValuePreview(writeValues?.[u.bindingKey], 400)}`;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    className="block w-full px-2 py-1.5 text-left hover:bg-brand-50"
                    // truncate로 생략된 내용은 hover 툴팁으로 전체 확인(라벨+출처 두 줄 결합).
                    title={`${labelLine}\n${tooltipInfo}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onPick(u);
                      setOpen(false);
                      setQ('');
                    }}
                  >
                    <span className="block truncate text-xs text-gray-700">{labelLine}</span>
                    <span className="block truncate text-[10px] text-gray-400">{infoLine}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * 표 셀 "카탈로그에서 새 위젯 만들기" — 미배치 풀에 없는 데이터도 카탈로그 직결(treeOwned)
 * 위젯으로 즉석 생성해 셀에 배치. 라벨·위젯 타입은 카탈로그 필드에서 유추(당해/전년도 라벨 포함).
 */
function FtCatalogCellCreator({
  catalog,
  takenKeys,
  onCreate,
}: {
  catalog: CatalogEntry[];
  takenKeys: Set<string>;
  onCreate: (w: InputWidget) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pf, setPf] = useState<{ catalogKey: string; select?: string } | undefined>(undefined);
  const entry = catalog.find((e) => e.key === pf?.catalogKey);
  const info = catalogSelectFieldInfo(entry, pf?.select);
  const create = (): void => {
    if (!pf || !entry) return;
    const widgetType = info?.type === 'number' ? 'number' : info?.type === 'date' ? 'date' : 'text';
    const ii = selectIndexInfo(pf.catalogKey, pf.select);
    const idxLabel = ii && ii.includes('— ') ? ii.split('— ')[1] : undefined; // '당해년도' 등
    const baseLabel = info?.label ?? entry.label;
    const base = `own-${pf.catalogKey}${pf.select ? `-${pf.select}` : ''}`.replace(/[^\w-]+/g, '-');
    let key = base;
    let n = 2;
    while (takenKeys.has(key)) key = `${base}-${n++}`;
    onCreate({
      kind: 'input',
      id: crypto.randomUUID(),
      bindingKey: key,
      widget: widgetType,
      label: idxLabel ? `${baseLabel}(${idxLabel})` : baseLabel,
      ...(info?.type === 'number'
        ? { dataType: 'number' as const }
        : info?.type === 'date'
          ? { dataType: 'date' as const }
          : {}),
      prefillFrom: pf,
      treeOwned: true,
    });
    setPf(undefined);
    setOpen(false);
  };
  if (!open) {
    return (
      <button
        type="button"
        className="text-[11px] font-medium text-brand-600 hover:text-brand-700"
        onClick={() => setOpen(true)}
      >
        ＋ 시스템 조회에서 새 위젯 만들기
      </button>
    );
  }
  return (
    // stopPropagation: 생성기 내부 클릭이 셀 카드 onClick(셀 포커스)으로 번지지 않게 격리.
    <div
      className="space-y-1 rounded border border-dashed border-brand-300 p-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <CatalogPrefillPicker catalog={catalog} value={pf} onChange={setPf} />
      <div className="flex items-center gap-1">
        {/* ⚠️ FT_CTRL_CLS(입력용, bg-white 포함) 재사용 금지 — 채움색과 충돌해 흰 배경이
            이기면 흰 글자 버튼이 투명해짐(2026-07-12 실사고: 확인 버튼이 안 보였음).
            시각은 `Button`이 소유하므로 className으로 면을 덧칠하지 말 것. */}
        <Button variant="primary" size="xs" disabled={!pf} onClick={create}>
          만들어 배치
        </Button>
        <Button
          variant="secondary"
          size="xs"
          onClick={() => {
            setOpen(false);
            setPf(undefined);
          }}
        >
          취소
        </Button>
      </div>
    </div>
  );
}

/**
 * 작성폼 표 위젯(formTable) 속성 패널 — 제목/열/행/셀 편집. 모든 편집은 onWidgetUpdate(undo 1스텝) 경유.
 * 고정 셀 입력은 미배치 풀 위젯을 셀에 배치(자동 풀 판정), 반복 행은 편집형 list def 필드에 열을 매핑한다.
 */
function FormTablePropertyPanel({
  w,
  onWidgetUpdate,
  onWidgetPreview,
  onWidgetCommit,
  onWidgetRevert,
  labelCls,
  inputCls,
  dataSources,
  dataDefs,
  onTemplateUpdate,
  countKeyRefs,
  unplacedPool,
  focusCell,
  onCellHover,
  onCellFocus,
  onFocusClear,
  writeValues,
  writeFormFields,
  bindingPrefillMap,
  catalog,
  tree,
  previewValues,
  ftCellClipboardValue,
  onCellCopy,
  onCellBlockCopy,
  onCellPaste,
  onCellPasteMany,
  onCellsContentClear,
  onRequestConfirm,
  onSelectWidget,
}: {
  w: FormTableWidget;
  onWidgetUpdate: (id: string, updater: (x: ComposerWidget) => ComposerWidget) => void;
  /** 열 너비/행 높이 px 입력 실시간 미리보기(replacePresent — 히스토리 미적재). */
  onWidgetPreview: (id: string, updater: (x: ComposerWidget) => ComposerWidget) => void;
  /** 열 너비/행 높이 px 입력 확정(commitPresent — undo 1스텝). */
  onWidgetCommit: (id: string, updater: (x: ComposerWidget) => ComposerWidget) => void;
  /** 치수 입력 미리보기 폐기(discardTransient) — Esc/이탈. */
  onWidgetRevert: () => void;
  labelCls: string;
  inputCls: string;
  dataSources?: DataSourceDef[];
  /** 셀 안 표 위젯의 참조/동결 판정(tableFieldsMode)·정본 칼럼 해소용 — 본문 단일 패널과 동일 공급. */
  dataDefs?: DataDef[];
  /** 셀 안 표 칼럼 참조 모드의 정본 쓰기·detach용(WidgetPropCtx.onTemplateUpdate). */
  onTemplateUpdate?: (fn: (t: ReportTemplate) => ReportTemplate) => void;
  /** 셀 안 표 detach 차단 판정(WidgetPropCtx.countKeyRefs). */
  countKeyRefs?: (key: string) => number;
  unplacedPool: InputWidget[];
  /** 표 셀 복수 포커스 — cells 마지막=anchor. 캔버스 더블클릭(source:'canvas')이면 anchor 행 펼침+스크롤. */
  focusCell?: FtCellFocus | null;
  /** 셀 카드 hover → 캔버스 표 안 해당 셀 하이라이트(leave 시 null). */
  onCellHover?: (c: { tableId: string; rowId: string; columnId: string } | null) => void;
  /** 셀 카드 클릭 → 셀 포커스(additive=Ctrl/⌘ 토글) — 캔버스 하이라이트 유지 + 캔버스 단일 클릭 전환 모드. */
  onCellFocus?: OnFtCellFocus;
  /** 셀 포커스 해제(셀 속성 → 표 속성 복귀 / 복수 전체 해제). */
  onFocusClear?: () => void;
  /** 현재 미리보기 컨텍스트 값 — 셀 배치 후보/배치된 위젯의 값 미리보기. */
  writeValues?: Record<string, unknown>;
  /** prefill 경로 해소용(위젯 미지정 시 파생 작성폼 노드의 prefillFrom fallback). */
  writeFormFields?: InputWidget[];
  /** body 바인딩 자동 채움 출처 fallback — 파생 위젯은 prefill이 body 바인딩에만 있음. */
  bindingPrefillMap?: Map<string, { catalogKey: string; select?: string }>;
  /** 카탈로그 — 셀 "카탈로그에서 새 위젯 만들기"(treeOwned) 소스 목록. */
  catalog?: CatalogEntry[];
  /** 전체 위젯 트리 — 신규 위젯 bindingKey 충돌 방지(전 키 수집). */
  tree?: ComposerWidget[];
  /** 캔버스 미리보기 값 — 셀 위젯 표시 형식의 "적용 구간 미리보기" 원료(WidgetPropCtx로 전달). */
  previewValues?: Record<string, unknown>;
  /** 공유 셀 클립보드(EditorPage — 캔버스 Ctrl+C/V와 공용). */
  ftCellClipboardValue?: FtCellClipboard | null;
  /** 셀 복사(공유 클립보드에 저장). */
  onCellCopy?: (cell: FormTableFixedCell) => void;
  /** 복수 선택 → 블록 복사(선택 bounding box 상대 오프셋 슬롯). */
  onCellBlockCopy?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  /** 셀 붙여넣기(대상 셀 교체 — 위젯은 독립 복제). */
  onCellPaste?: (tableId: string, rowId: string, columnId: string) => void;
  /** 복사 셀 → 선택된 전 셀 일괄 붙여넣기. */
  onCellPasteMany?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  /** 선택된 전 셀 내용 제거. */
  onCellsContentClear?: (tableId: string, cells: { rowId: string; columnId: string }[]) => void;
  /** 행/열 삭제 시 내용 손실 경고 확인 모달 요청(내용 없으면 즉시 적용). */
  onRequestConfirm?: FtConfirmRequest;
  /**
   * 셀 위젯을 **anchor로 선택**(셀 포커스 해제 + 단일 위젯 속성 패널) — 트리/캔버스와 같은 선택
   * 채널(selectWidget). 셀 패널은 위젯 축 일부만 복제하므로(선택지·옵션 소스, 안내 문구, 입력
   * 형식, combobox·sortableSelect 저작, 라벨 표시 정책, 뷰별 축은 단일 패널에만 있다) 여기서 한
   * 걸음 더 들어가는 수단이 필요하다(2026-08-07 감사 ③ — 표 축은 디스크립터 이관으로 해소).
   */
  onSelectWidget?: (id: string) => void;
}) {
  const applyFt = (fn: (t: FormTableWidget) => FormTableWidget) =>
    onWidgetUpdate(w.id, (x) => (x.kind === 'formTable' ? fn(x) : x));
  // 열 너비/행 높이 px 입력 전용 — 실시간 미리보기(replacePresent)와 확정(commitPresent) 2단계.
  // 양식 탭 테이블 노드 치수 입력과 동형(타이핑/화살표=preview, blur/Enter=commit, Esc=revert).
  const applyFtPreview = (fn: (t: FormTableWidget) => FormTableWidget) =>
    onWidgetPreview(w.id, (x) => (x.kind === 'formTable' ? fn(x) : x));
  const applyFtCommit = (fn: (t: FormTableWidget) => FormTableWidget) =>
    onWidgetCommit(w.id, (x) => (x.kind === 'formTable' ? fn(x) : x));
  // rowSpan 병합 구간에 걸린 행 집합 — 행 표시 조건 입력 비활성 판정(방향 ②). 반대 방향(조건
  // 행 가로지르는 병합)은 ftCanMergeCells(ftMergePlan)가 차단. 정규화 strip과 같은 단일 소스.
  const rowSpanInvolvedIds = ftRowSpanInvolvedRowIds(w);
  // 열 너비/행 높이 raw 입력 → 저장값: 빈칸·'auto'=미지정(균등/자동), 순수 숫자=px 그대로 저장,
  // 그 외 문자열('30%' 등)=그대로. 작성 페이지는 웹 폼이라 px/% 표시(양식 탭 mm와 달리 변환 없음 —
  // 결정: 2026-07-13-writeform-units-web-native).
  const parseFtDim = (raw: string): number | string | undefined => {
    const t = raw.trim();
    if (t === '' || t === 'auto') return undefined;
    if (/^\d+(\.\d+)?$/.test(t)) return Number(t);
    return t;
  };
  // ── 구모델(columns.label/group/headerStyle) → 신모델(헤더=일반 셀) 정규화를 트리에 기록 ──
  // 속성 패널 진입(표 선택) 시 1회 — 캔버스 렌더의 즉석 정규화와 결정적으로 동일한 결과라
  // 셀 포커스 좌표(헤더 행 id)가 저장 트리와 일치하게 된다. 이후 편집·저장은 신형.
  // 신형이면 참조 동일(no-op)이라 재기록 없음. (결정: 2026-07-13-formtable-header-cell-model)
  useEffect(() => {
    if (normalizeFormTable(w) !== w) {
      onWidgetUpdate(w.id, (x) => (x.kind === 'formTable' ? normalizeFormTable(x) : x));
    }
  }, [w, onWidgetUpdate]);
  // anchor(복수 선택의 마지막) — 단일 셀 속성/스크롤 기준. focusCell이 이 표가 아니면 null.
  const anchor =
    focusCell && focusCell.tableId === w.id && focusCell.cells.length > 0
      ? focusCell.cells[focusCell.cells.length - 1]
      : null;
  const focusCount = focusCell && focusCell.tableId === w.id ? focusCell.cells.length : 0;
  const isCellFocused = (rowId: string, columnId: string): boolean =>
    !!focusCell &&
    focusCell.tableId === w.id &&
    focusCell.cells.some((c) => c.rowId === rowId && c.columnId === columnId);
  // 다른 formTable 목록 — 계산 셀 "다른 표" 합산 대상 섹션(2026-07-14 교차 표 참조 저작 지원).
  const otherFormTables = useMemo(() => {
    const out: FormTableWidget[] = [];
    const walkT = (ws: ComposerWidget[]): void => {
      for (const x of ws) {
        if (x.kind === 'formTable' && x.id !== w.id) out.push(x);
        else if (x.kind === 'group') walkT(x.children);
      }
    };
    walkT(tree ?? []);
    return out;
  }, [tree, w.id]);
  // 신규(treeOwned) 위젯 bindingKey 충돌 방지 — 트리 전체 + 풀 + 파생 작성폼 키.
  const ftTakenKeys = useMemo(() => {
    const s = collectAllInputBindingKeys(tree ?? []);
    for (const u of unplacedPool) s.add(u.bindingKey);
    for (const f of writeFormFields ?? []) s.add(f.bindingKey);
    return s;
  }, [tree, unplacedPool, writeFormFields]);
  // 셀 복사/붙여넣기 — 클립보드는 EditorPage 공유(캔버스 Ctrl+C/V와 동일 버퍼). 패널 버튼은 위임만.
  const copyCell = (cell: FormTableFixedCell): void => onCellCopy?.(cell);
  const pasteCell = (rowId: string, columnId: string): void => onCellPaste?.(w.id, rowId, columnId);
  const cellClipboard = ftCellClipboardValue;
  // 위젯의 prefill 소스(명시 → 파생 작성폼 노드 → 등록형 메타 → body 바인딩 합성) — 셀 배치 판단 정보.
  const widgetPrefill = (u: InputWidget) =>
    u.prefillFrom ??
    writeFormFields?.find((f) => f.bindingKey === u.bindingKey)?.prefillFrom ??
    (u.widget === 'registered'
      ? REGISTERED_FORM_WIDGETS_META.find((m) => m.key === u.widgetKey)?.defaultPrefillFrom
      : undefined) ??
    bindingPrefillMap?.get(u.bindingKey);
  // 확장된 행(셀 편집 노출) — 단일. '입력(위젯 미선택)' 상태는 모델(빈 셀 객체)로 영속(ftFixedCellType).
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  // 캔버스 셀 더블클릭 연동 ①: 해당 행 펼침. (스크롤은 아래 ②에서 — setState가 커밋되기 전
  // rAF가 먼저 돌면 셀 카드가 아직 DOM에 없어 스크롤이 유실되던 타이밍 버그 수정, 2026-07-12.)
  const anchorRowId = anchor?.rowId ?? null;
  const anchorColumnId = anchor?.columnId ?? null;
  useEffect(() => {
    if (!anchorRowId) return;
    setExpandedRowId(anchorRowId);
  }, [anchorRowId]);
  // ②: 펼침이 실제 렌더된 뒤(expandedRowId 일치 커밋 후) anchor 셀 카드로 스크롤 — DOM 존재 보장.
  // 패널 셀 카드 클릭(source:'panel')은 이미 보이는 카드라 스크롤 점프 생략(포커스만).
  useEffect(() => {
    if (!anchorRowId || !anchorColumnId || expandedRowId !== anchorRowId) return;
    if (focusCell?.source === 'panel') return;
    document
      .getElementById(`ftcell-${anchorRowId}-${anchorColumnId}`)
      ?.scrollIntoView({ block: 'center' });
  }, [anchorRowId, anchorColumnId, expandedRowId, focusCell?.source]);

  const editableDefs = (dataSources ?? []).filter(isEditableListDataSource);

  // 반복 스코프 감지 — 이 표가 repeat 안이면 셀 바인딩 후보 = 그 반복 소스의 행 필드
  // (2026-08-05 사용자 제안: SMS 일반 위젯 패널의 "행 필드" select와 동일 UX). 판정·옵션·생성
  // 전부 일반 위젯 패널과 같은 정본(findEnclosingRepeatWidget·repeatRowBindingOptions·
  // repeatSkeletonWidgetForField) 재사용 — 별도 약식 구현 금지(중복 드리프트 방지).
  const enclosingRepeatOfTable = findEnclosingRepeatWidget(tree ?? [], w.id);
  const repeatSourceDef = enclosingRepeatOfTable
    ? (dataSources ?? []).find((d) => d.key === enclosingRepeatOfTable.over)
    : undefined;
  // 반복 소스의 행 필드 후보 — 소비처 3곳(셀 표시 형식 경로 칸·셀 위젯 필드 select·행 표시 조건
  // 키 후보)이 각자 호출하던 것을 여기 1회로 모은다. 행 목록 안에서 호출하면 행마다 새 배열이
  // 생겨 자식(FtRowVisibleWhenEditor)의 useMemo가 전부 무효화된다.
  // current='' 호출이라 목록 선두에 value:''인 preserved 옵션 1건이 인공물로 붙는다
  // (repeatRowBindingOptions는 "현재값이 선언 밖"이면 그 값을 보존 옵션으로 얹는데, ''는 어떤
  // 필드와도 일치하지 않는다). 아래 필터가 그 인공물을 걷어낸다 — 선택형 소비처에는 무의미한 항목.
  const repeatRowFieldOptions = useMemo(
    () =>
      repeatSourceDef
        ? repeatRowBindingOptions(repeatSourceDef, '', catalog).filter((o) => !o.preserved)
        : [],
    [repeatSourceDef, catalog],
  );
  // 셀 위젯 표시 형식의 경로 칸(병기 값·구간 키) 후보 — 셀 위젯의 값 스코프가 곧 이 반복 행이라
  // 위 "행 필드" select와 같은 목록이다(같은 정본 재사용).
  const cellRowFieldOptions: HybridKeyOption[] = useMemo(
    () => repeatRowFieldOptions.map((o) => ({ key: o.value, label: o.label })),
    [repeatRowFieldOptions],
  );
  // 행 표시 조건의 키 후보 — 점 경로 자식(계약 하위 필드)은 제외한다. 조건 식별자는 단일 키만
  // 허용하는 문법이라(evaluateCondition) `부모.자식`은 평가되지 않는다.
  const repeatRowConditionFields = useMemo(
    () => repeatRowFieldOptions.filter((o) => !o.value.includes('.')),
    [repeatRowFieldOptions],
  );
  // 행 표시 조건 키 후보 중 작성폼 스코프 select/radio — branch 슬롯 편집기와 같은 정본.
  // 행마다 트리를 순회하지 않도록 표 단위 1회 수집.
  const rowConditionScopeWidgets = useMemo(
    () => collectBranchScopeSelectRadio(tree ?? [], w.id),
    [tree, w.id],
  );
  // 표시 조건이 걸린 고정 행 수 — 접힌 summary 병기용(행 목록 렌더 밖에서 1회 계산).
  const visibleWhenRowCount = w.rows.filter(
    (r) => r.type === 'fixed' && r.visibleWhen !== undefined,
  ).length;

  // ── 복수 선택 요약 뷰(2026-07-13) — 2개+ 셀 선택 시 일괄 액션만 노출 ──
  if (focusCell && focusCell.tableId === w.id && focusCount > 1) {
    const backBtn = (
      <button
        type="button"
        className="shrink-0 rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
        onClick={() => onFocusClear?.()}
      >
        ← 표 속성
      </button>
    );
    const cellLabel = (c: { rowId: string; columnId: string }): string => {
      const ri = w.rows.findIndex((r) => r.id === c.rowId);
      // columns는 구조 축만(라벨 없음 — 헤더=일반 셀 모델) → 열 번호로 표기.
      const ci = w.columns.findIndex((cc) => cc.id === c.columnId);
      return `행 ${ri + 1} · 열 ${ci >= 0 ? ci + 1 : '?'}`;
    };
    // ── 공통 속성 일괄 편집 파생 데이터(2026-07-13) ──
    // 고정 셀만 대상(반복 행 셀은 종류/colSpan/위젯 개념 없음).
    const fixedTargets = focusCell.cells.flatMap((c) => {
      const row = w.rows.find((r) => r.id === c.rowId);
      if (!row || row.type !== 'fixed') return [];
      return [
        { rowId: c.rowId, columnId: c.columnId, cell: row.cells.find((cc) => cc.columnId === c.columnId) },
      ];
    });
    const typeCommon = ftCommon(fixedTargets.map((t) => ftFixedCellType(t.cell)));
    const applyType = (v: 'empty' | 'text' | 'input') =>
      applyFt((t) => {
        let next = t;
        for (const tg of fixedTargets)
          next = ftSetFixedCell(next, tg.rowId, tg.columnId, ftCellWithType(tg.cell, tg.columnId, v));
        return next;
      });
    // 셀 정렬 — 종류 무관(빈 셀 포함) 전 선택 셀 대상. H/V 각각 독립 혼합 판정(표준).
    // 효과값: 신형 contentAlignH/V, 미지정 시 legacy align 하위호환 해석(기본 'start').
    const effAlignH = (c: FormTableFixedCell | undefined): AlignVal =>
      c?.contentAlignH ?? (c?.align ? LEGACY_ALIGN_TO_H[c.align] : 'start');
    const effAlignV = (c: FormTableFixedCell | undefined): AlignVal => c?.contentAlignV ?? 'start';
    const alignHCommon = ftCommon(fixedTargets.map((t) => effAlignH(t.cell)));
    const alignVCommon = ftCommon(fixedTargets.map((t) => effAlignV(t.cell)));
    // 양축 모두 'start'(기본)면 빈 셀은 정렬만 가진 셀을 만들 이유가 없어 생성 안 함.
    const applyAlign = (h: AlignVal, v: AlignVal) =>
      applyFt((t) => {
        let next = t;
        const isDefault = h === 'start' && v === 'start';
        for (const tg of fixedTargets) {
          if (!tg.cell && isDefault) continue;
          next = ftSetFixedCell(next, tg.rowId, tg.columnId, {
            ...(tg.cell ?? { columnId: tg.columnId }),
            columnId: tg.columnId,
            ...ftCellAlignFields(h, v),
          });
        }
        return next;
      });
    // 셀 스타일 일괄(parity P2.5) — 선택 전 셀에 next(부분)를 각 셀 기존 style에 머지 적용(undo 1스텝).
    // 빈 셀 포함 시 style만 가진 셀 upsert(빈 셀도 배경/테두리 등 부여). BulkCellStyleFields 공용.
    const applyBulkStyle = (next: Partial<TableCellStyle>) =>
      applyFt((t) => {
        let n = t;
        for (const tg of fixedTargets)
          n = ftSetFixedCell(n, tg.rowId, tg.columnId, {
            ...(tg.cell ?? { columnId: tg.columnId }),
            columnId: tg.columnId,
            style: ftMergeStyle(tg.cell?.style, next),
          });
        return n;
      });
    // 위젯 보유 셀만 — 위젯 공통 섹션 대상.
    const widgetTargets = fixedTargets.filter(
      (t): t is typeof t & { cell: FormTableFixedCell & { widget: InputWidget } } => !!t.cell?.widget,
    );
    const widgets = widgetTargets.map((t) => t.cell.widget);
    // 위젯 종류 후보 = 각 위젯 widgetOptionsForDataType 교집합(비면 전환 불가).
    const widgetTypeOptions = widgets.length
      ? widgets.reduce<WidgetType[]>(
          (acc, x) => acc.filter((o) => widgetOptionsForDataType(x.dataType, x.widget).includes(o)),
          widgetOptionsForDataType(widgets[0].dataType, widgets[0].widget),
        )
      : [];
    const widgetTypeCommon = ftCommon(widgets.map((x) => x.widget));
    const requiredCommon = ftCommon(widgets.map((x) => x.required ?? false));
    const modeCommon = ftCommon<'editable' | 'readonly' | 'hidden'>(
      widgets.map((x) => x.mode ?? 'editable'),
    );
    // 역전파 — 전원 활성 가능해야 노출. 하나라도 비활성 사유 있으면 항목 제외 + 사유 한 줄.
    // keyed upsert 페어링 커버 위젯도 비활성 취급(일괄 지정 차단 — 해제는 단일 셀 선택에서).
    const wbAvails = widgets.map((x) =>
      writeBackAvailability(
        x.prefillFrom ?? comboboxSyntheticPrefill(x, catalog) ?? widgetPrefill(x),
        catalog,
      ),
    );
    const wbPaired = widgets
      .map((x) => keyedPairingCoverage(x, { tree, catalog, writeFormFields, bindingPrefillMap }))
      .find((p): p is { keyLabel: string } => p !== null);
    // 이 표가 반복 안이면 셀 전원이 행 스코프 — 하나라도 서버 게이트 미충족이면 일괄 제외(트리 동형).
    const wbRowJudgements = enclosingRepeatOfTable
      ? widgets.map((x) =>
          judgeRowScopeWriteBack({
            widget: x,
            over: enclosingRepeatOfTable.over,
            dataSources,
            catalog,
          }),
        )
      : [];
    const wbRowIneligible = wbRowJudgements.find(
      (j): j is Extract<RowScopeWriteBackJudgement, { kind: 'ineligible' }> =>
        j.kind === 'ineligible',
    );
    const wbAllAvailable =
      widgets.length > 0 &&
      !wbPaired &&
      !wbRowIneligible &&
      wbAvails.every((a) => a.kind !== 'disabled');
    const wbDisabledReason = wbPaired
      ? `'${wbPaired.keyLabel}'의 선택 전파로 연결된 위젯이 있어 일괄 설정에서 제외됩니다(그 위젯의 역전파에 함께 저장 — 별도 지정 불필요)`
      : wbRowIneligible
        ? wbRowIneligible.reason
        : wbAvails.find((a) => a.kind === 'disabled')?.reason;
    // 카탈로그 writer는 entry 선언 스코프(미선언=현장)의 정본만 — 하나라도 반대 스코프면 그 옵션 비활성.
    const wbCatalogScopes = wbAvails.flatMap((a) =>
      a.kind === 'catalog' ? [a.scope ?? 'site'] : [],
    );
    const wbOrgDisabled = wbCatalogScopes.includes('site');
    const wbSiteDisabled = wbCatalogScopes.includes('organization');
    const wbCommon = ftCommon(widgets.map((x) => x.writeBack?.scope ?? ''));
    // '다음 보고서 기본값' 체크박스 — 트리 복수(WidgetWriteBackControl)와 동형: 전원 카탈로그
    // (도메인 writer) 라우팅 + 역전파 지정(비혼합)일 때만 노출, 혼합은 indeterminate.
    // 반복 안 표면 미노출 — 서버 행 스코프 패스는 useAsDefault를 읽지 않는다(트리 동형).
    const wbAllCatalog =
      widgets.length > 0 &&
      !enclosingRepeatOfTable &&
      wbAvails.every((a) => a.kind === 'catalog');
    const wbUseCommon = ftCommon(widgets.map((x) => !!x.writeBack?.useAsDefault));
    const applyWidgets = (fn: (x: InputWidget) => InputWidget) =>
      applyFt((t) => {
        let next = t;
        for (const tg of widgetTargets)
          next = ftSetFixedCell(next, tg.rowId, tg.columnId, {
            ...tg.cell,
            widget: fn(tg.cell.widget),
          });
        return next;
      });
    // ── 자동 채움(카탈로그) 세그먼트별 일괄 편집 파생 데이터 ──
    // 단일 뷰와 동일 게이트(treeOwned 위젯만) — 그 외 위젯의 prefill은 body 바인딩/등록형 메타 유래라
    // 셀에서 직접 편집하지 않음. 표시는 해소값(widgetPrefill)로, 편집 기록은 명시 prefillFrom에.
    const prefillTargets = widgetTargets.filter((t) => t.cell.widget.treeOwned);
    const prefillWidgets = prefillTargets.map((t) => t.cell.widget);
    // 텍스트 셀의 자동 채움(cell.prefillFrom)도 일괄 대상 — 행 단위 선택에서 연도(텍스트) 셀만
    // 인덱스 교체에서 빠져 "일부 셀 미반영"이 되던 문제(2026-07-14 제보).
    const prefillTextTargets = fixedTargets.filter(
      (
        t,
      ): t is (typeof fixedTargets)[number] & {
        cell: FormTableFixedCell & { prefillFrom: NonNullable<FormTableFixedCell['prefillFrom']> };
      } => !!t.cell && t.cell.text !== undefined && !!t.cell.prefillFrom,
    );
    const prefillResolved = [
      ...prefillWidgets.map((x) => widgetPrefill(x)),
      ...prefillTextTargets.map((t) => t.cell.prefillFrom),
    ];
    const prefillCount = prefillWidgets.length + prefillTextTargets.length;
    const pfEntries = (catalog ?? []).filter((e) => e.kind === 'data' || e.kind === 'computed');
    // 1) 데이터 소스: 전원 동일 catalogKey면 그 값 / 다르면 mixed / 전원 없으면 '' → 자동 채움 없음.
    const catalogCommon = ftCommon(prefillResolved.map((r) => r?.catalogKey ?? ''));
    const pfEntry =
      !catalogCommon.mixed && catalogCommon.value
        ? pfEntries.find((e) => e.key === catalogCommon.value)
        : undefined;
    const pfIsList = pfEntry?.cardinality === 'list';
    const pfIsTwoYear = !!pfEntry && TWO_YEAR_SLOT_CATALOG_KEYS.has(pfEntry.key);
    const pfPaths = prefillReturnPaths(pfEntry);
    // select('{idx}.{field}' | '{field}')를 세그먼트로 분해 — list일 때만 선두 숫자를 인덱스로 취급.
    const parsePfSel = (sel: string | undefined): { idx: string; field: string } => {
      const s = sel ?? '';
      if (pfIsList) {
        const m = s.match(/^(\d+)\.(.*)$/);
        return m ? { idx: m[1], field: m[2] } : { idx: '', field: s };
      }
      return { idx: '', field: s };
    };
    // 2) 행 인덱스 / 3) 반환 필드 — 카탈로그 통일된 경우에만 각각 독립 공통/혼합 판정.
    const pfIdxCommon = ftCommon(prefillResolved.map((r) => parsePfSel(r?.select).idx));
    const pfFieldCommon = ftCommon(prefillResolved.map((r) => parsePfSel(r?.select).field));
    const pfCompose = (idx: string, field: string): string | undefined =>
      field ? (pfIsList ? `${idx || '0'}.${field}` : field) : undefined;
    // 위젯 셀(widget.prefillFrom) + 텍스트 셀(cell.prefillFrom)에 같은 prefill 변환을 undo 1스텝으로
    // 일괄 적용. fn이 'keep'을 반환하면 그 셀은 무변경.
    type PfRef = { catalogKey: string; select?: string };
    const applyPrefillAll = (fn: (r: PfRef | undefined) => PfRef | undefined | 'keep') =>
      applyFt((t) => {
        let next = t;
        for (const tg of prefillTargets) {
          const r = fn(widgetPrefill(tg.cell.widget));
          if (r === 'keep') continue;
          const wdg = { ...tg.cell.widget };
          if (r) wdg.prefillFrom = r;
          else delete wdg.prefillFrom;
          next = ftSetFixedCell(next, tg.rowId, tg.columnId, { ...tg.cell, widget: wdg });
        }
        for (const tg of prefillTextTargets) {
          const r = fn(tg.cell.prefillFrom);
          if (r === 'keep') continue;
          const cell: FormTableFixedCell = { ...tg.cell };
          if (r) cell.prefillFrom = r;
          else delete cell.prefillFrom;
          next = ftSetFixedCell(next, tg.rowId, tg.columnId, cell);
        }
        return next;
      });
    // 데이터 소스 교체 = 전 대상 prefillFrom 교체(select 초기화 — 단일 picker entry 변경 규약과 동일).
    const setPfCatalog = (k: string | undefined) =>
      applyPrefillAll(() => (k ? { catalogKey: k } : undefined));
    // 인덱스 세그먼트만 일괄 교체 — 반환 필드는 셀별 유지(핵심 사용례: 행 복사 후 인덱스만 전년도로).
    const setPfIndex = (idx: string) =>
      applyPrefillAll((r) => {
        if (!r) return 'keep';
        const { field } = parsePfSel(r.select);
        const select = pfCompose(idx, field);
        return { catalogKey: r.catalogKey, ...(select ? { select } : {}) };
      });
    // 반환 필드 세그먼트만 일괄 교체 — 인덱스는 셀별 유지.
    const setPfField = (field: string) =>
      applyPrefillAll((r) => {
        const catalogKey = r?.catalogKey ?? pfEntry?.key;
        if (!catalogKey) return 'keep';
        const { idx } = parsePfSel(r?.select);
        const select = pfCompose(idx, field);
        return { catalogKey, ...(select ? { select } : {}) };
      });
    // ── 선택 범위 병합(parity P2) — 직사각 + 전원 1×1일 때만 활성. top-left 한 셀로 병합 후 단일 포커스. ──
    const mergeCells = focusCell.cells as FtCellRef[];
    const canMergeSelection = ftCanMergeCells(w, mergeCells);
    const selectionTopLeft = (): { rowId: string; columnId: string } | null => {
      const colIdx = new Map(w.columns.map((c, i) => [c.id, i]));
      const fixedRows = w.rows.filter((r) => r.type === 'fixed');
      const fixedIdx = new Map(fixedRows.map((r, i) => [r.id, i]));
      let minR = Infinity;
      let minC = Infinity;
      for (const c of mergeCells) {
        const r = fixedIdx.get(c.rowId);
        const ci = colIdx.get(c.columnId);
        if (r === undefined || ci === undefined) continue;
        if (r < minR) minR = r;
        if (ci < minC) minC = ci;
      }
      if (!Number.isFinite(minR) || !Number.isFinite(minC)) return null;
      return { rowId: fixedRows[minR].id, columnId: w.columns[minC].id };
    };
    const doMergeSelection = () => {
      const tl = selectionTopLeft();
      applyFt((t) => ftMergeCells(t, mergeCells) ?? t);
      if (tl) onCellFocus?.({ tableId: w.id, ...tl }, {}); // 병합 셀 단일 포커스로 전환
    };
    const mergeSelection = () => {
      const tl = selectionTopLeft();
      const hasLoss = mergeCells.some((c) => {
        if (tl && c.rowId === tl.rowId && c.columnId === tl.columnId) return false; // top-left 보존
        const r = w.rows.find((rr) => rr.id === c.rowId);
        if (!r || r.type !== 'fixed') return false;
        const cc = r.cells.find((x) => x.columnId === c.columnId);
        return (
          !!cc &&
          (!!cc.widget || !!cc.compute || (typeof cc.text === 'string' && cc.text.trim() !== ''))
        );
      });
      if (hasLoss && onRequestConfirm) {
        onRequestConfirm({
          title: '셀 병합',
          body: '병합하면 좌상단 셀만 남고 나머지 셀의 내용이 사라집니다. 병합하시겠습니까?',
          confirmLabel: '병합',
          danger: true,
          onConfirm: doMergeSelection,
        });
      } else doMergeSelection();
    };
    const MIXED = '__mixed__';
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-1">
          <p className="min-w-0 truncate text-xs font-medium text-gray-500">
            {focusCount}개 셀 선택
          </p>
          {backBtn}
        </div>
        <div className="max-h-40 space-y-0.5 overflow-auto rounded border border-gray-100 p-1.5">
          {focusCell.cells.map((c, i) => (
            <p
              key={`${c.rowId}-${c.columnId}`}
              className={`truncate text-[11px] ${
                i === focusCell.cells.length - 1 ? 'font-medium text-gray-600' : 'text-gray-400'
              }`}
            >
              {cellLabel(c)}
              {i === focusCell.cells.length - 1 ? ' (기준)' : ''}
            </p>
          ))}
        </div>
        <div className="space-y-1.5">
          <button
            type="button"
            className="w-full rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
            onClick={() => onCellBlockCopy?.(w.id, focusCell.cells)}
          >
            복사({focusCount}개 셀)
          </button>
          {cellClipboard ? (
            <button
              type="button"
              className="w-full rounded bg-brand-50 px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-100"
              onClick={() => onCellPasteMany?.(w.id, focusCell.cells)}
            >
              {cellClipboard.kind === 'block'
                ? `블록 붙여넣기(${cellClipboard.rows}×${cellClipboard.cols})`
                : `붙여넣기(${focusCount}개 셀)`}
            </button>
          ) : null}
          <button
            type="button"
            className="w-full rounded bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
            onClick={() => onCellsContentClear?.(w.id, focusCell.cells)}
          >
            내용 지우기
          </button>
          <button
            type="button"
            className="w-full rounded bg-brand-50 px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!canMergeSelection}
            title={
              canMergeSelection
                ? '선택한 직사각 범위를 하나로 병합'
                : // ftMergePlan은 사유 없이 null만 돌려주므로(FtMergePlan | null) 거부 이유를
                  // 특정할 수 없다. 한 가지만 적으면 표시 조건 때문에 막힌 경우 틀린 안내가 되니
                  // 두 사유를 병기한다.
                  '직사각 범위(병합되지 않은 셀)가 아니거나, 표시 조건이 걸린 행을 가로지릅니다'
            }
            onClick={mergeSelection}
          >
            선택 범위 병합
          </button>
          <button
            type="button"
            className="w-full rounded bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
            onClick={() => onFocusClear?.()}
          >
            선택 해제
          </button>
        </div>

        {/* ── 공통 속성 일괄 편집(2026-07-13) — 선택 셀 전원 대상, 각 속성 개별 공통/혼합 판정 ── */}
        {fixedTargets.length > 0 ? (
          <div className="space-y-3 border-t border-gray-200 pt-3">
            <p className="text-xs font-medium text-gray-500">공통 속성 — 선택 {fixedTargets.length}개 셀</p>
            {/* 셀 종류 일괄 — colSpan 일괄 입력은 제거(2026-07-13 사용자 지적: 라벨 없는 정체불명
                입력) — 병합은 "선택 영역 병합"(Ctrl+M)으로 일원화. */}
            <div className="flex items-center gap-1.5">
              <span className="shrink-0 text-[11px] text-gray-500">종류</span>
              <select
                className={FT_CTRL_CLS}
                title="셀 종류"
                value={typeCommon.mixed ? MIXED : typeCommon.value}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'empty' || v === 'text' || v === 'input') applyType(v);
                }}
              >
                {typeCommon.mixed ? (
                  <option value={MIXED} disabled>
                    — 혼합 —
                  </option>
                ) : null}
                {!typeCommon.mixed && typeCommon.value === 'compute' ? (
                  <option value="compute" disabled>
                    계산
                  </option>
                ) : null}
                <option value="empty">비움</option>
                <option value="text">텍스트</option>
                <option value="input">입력</option>
              </select>
            </div>

            {/* 셀 정렬 — 단일 뷰와 동일 9방향 그리드(가로 × 세로). H/V 각각 독립 혼합 판정. 전 선택 셀 일괄(undo 1스텝). */}
            <CellContentAlignEditor
              label="셀 정렬 일괄"
              valueH={alignHCommon.mixed ? undefined : alignHCommon.value}
              valueV={alignVCommon.mixed ? undefined : alignVCommon.value}
              mixedH={alignHCommon.mixed}
              mixedV={alignVCommon.mixed}
              onPick={applyAlign}
            />

            {/* 셀 스타일 일괄 — 양식 탭 BulkCellEditor와 동일 편집기(공용 BulkCellStyleFields, parity P2.5).
                tri-state 혼합 판정 + 전 선택 셀에 머지 적용(빈 셀도 style-only로 upsert). */}
            <BulkCellStyleFields
              styles={fixedTargets.map((t) => t.cell?.style)}
              onPatch={applyBulkStyle}
              onPreview={applyBulkStyle}
              onCommit={applyBulkStyle}
              onRevert={() => {}}
            />

            {/* 위젯 공통 섹션 — 위젯 보유 셀만 대상 */}
            {widgets.length > 0 ? (
              <div className="space-y-3 border-t border-gray-100 pt-3">
                <p className="text-[11px] font-medium text-gray-400">
                  위젯 있는 {widgets.length}개 셀에 적용
                </p>
                {/* 위젯 종류 */}
                <div>
                  <label className={labelCls}>위젯</label>
                  {widgetTypeOptions.length === 0 ? (
                    <p className="text-[11px] text-amber-600">
                      선택 위젯들의 공통 종류 후보가 없어 일괄 변경할 수 없습니다.
                    </p>
                  ) : (
                    <select
                      className={`${FT_CTRL_CLS} w-full`}
                      value={widgetTypeCommon.mixed ? MIXED : widgetTypeCommon.value}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v !== MIXED)
                          applyWidgets((x) => withWidgetTypeChange(x, v as WidgetType));
                      }}
                    >
                      {widgetTypeCommon.mixed ? (
                        <option value={MIXED} disabled>
                          — 혼합 —
                        </option>
                      ) : null}
                      {widgetTypeOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {WIDGET_OPTION_LABELS[opt] ?? opt}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {/* 필수 — tri-state(indeterminate) */}
                <div className="flex items-center gap-2">
                  <input
                    ref={(el) => {
                      if (el) el.indeterminate = requiredCommon.mixed;
                    }}
                    id="ftcell-multi-required"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={requiredCommon.mixed ? false : (requiredCommon.value ?? false)}
                    onChange={(e) => applyWidgets((x) => ({ ...x, required: e.target.checked }))}
                  />
                  <label htmlFor="ftcell-multi-required" className="text-xs text-gray-600">
                    필수 입력{requiredCommon.mixed ? ' (혼합)' : ''}
                  </label>
                </div>
                {/* 표시 모드 — 단일 뷰·작성 위젯 다중 편집과 동일한 세그먼트 버튼(혼합=전체 미활성+라벨) */}
                <div>
                  <label className={labelCls}>
                    표시 모드{modeCommon.mixed ? ' (혼합)' : ''}
                  </label>
                  <div className="inline-flex overflow-hidden rounded border border-gray-300">
                    {WRITE_FORM_MODE_OPTIONS.map((opt) => {
                      const active = !modeCommon.mixed && modeCommon.value === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => applyWidgets((x) => ({ ...x, mode: opt.value }))}
                          className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                            active
                              ? 'bg-brand-500 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                          }`}
                          aria-pressed={active}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* 자동 채움(카탈로그) — treeOwned 위젯 + 자동 채움 텍스트 셀 대상. 데이터소스/행/반환필드 세그먼트별 공통·혼합 */}
                {prefillCount > 0 ? (
                  <div className="space-y-1">
                    <label className={labelCls}>자동 채움(시스템 조회) · {prefillCount}개</label>
                    {/* 공통 코어 CatalogEntryPicker(P4 이관) — 혼합 세그먼트는 mixed prop(코어가 혼합
                        placeholder 표시, 선택 시 일괄 적용). 공통·혼합 판정과 일괄 적용은 위 로직 유지.
                        파라미터 블록은 단일 셀 자동 채움 자리와 동형으로 코어 기본(공통 entry 선택 시
                        자동 렌더 — 값은 template.catalogParams 전역이라 일괄 패널에서도 동일). */}
                    <CatalogEntryPicker
                      entries={pfEntries}
                      value={
                        !catalogCommon.mixed && catalogCommon.value ? catalogCommon.value : undefined
                      }
                      mixed={catalogCommon.mixed}
                      allowClear
                      placeholder="데이터 소스 검색·선택… (자동 채움 없음)"
                      onChange={(k) => setPfCatalog(k)}
                    />
                    {catalogCommon.mixed ? (
                      <p className="text-[11px] leading-snug text-gray-400">
                        데이터 소스가 혼합입니다 — 먼저 통일하면 행·반환 필드를 일괄 편집할 수 있습니다.
                      </p>
                    ) : pfEntry ? (
                      <div className="flex items-center gap-1">
                        {pfIsList ? (
                          pfIsTwoYear ? (
                            <select
                              className={`${FT_CTRL_CLS} w-24 shrink-0`}
                              title="연도 행 — 서버가 항상 당해년도(0)·전년도(1) 순으로 내려줍니다. 인덱스만 일괄 교체(반환 필드는 셀별 유지)"
                              value={pfIdxCommon.mixed ? MIXED : pfIdxCommon.value === '1' ? '1' : '0'}
                              onChange={(e) => {
                                if (e.target.value !== MIXED) setPfIndex(e.target.value);
                              }}
                            >
                              {pfIdxCommon.mixed ? (
                                <option value={MIXED} disabled>
                                  — 혼합 —
                                </option>
                              ) : null}
                              <option value="0">당해년도</option>
                              <option value="1">전년도</option>
                            </select>
                          ) : (
                            <input
                              type="number"
                              min={0}
                              className={`${FT_CTRL_CLS} w-14`}
                              title="행 인덱스 (0=첫 행) — 인덱스만 일괄 교체(반환 필드는 셀별 유지)"
                              value={pfIdxCommon.mixed ? '' : (pfIdxCommon.value ?? '')}
                              placeholder={pfIdxCommon.mixed ? '혼합' : '0'}
                              onChange={(e) => setPfIndex(e.target.value)}
                            />
                          )
                        ) : null}
                        <SearchCombo
                          items={pfPaths.map((p) => ({ key: p.path, label: p.label, sub: p.path }))}
                          value={
                            !pfFieldCommon.mixed &&
                            pfPaths.some((p) => p.path === pfFieldCommon.value)
                              ? pfFieldCommon.value
                              : undefined
                          }
                          placeholder={pfFieldCommon.mixed ? '— 혼합 —' : '반환 필드 검색·선택…'}
                          onPick={(p) => setPfField(p)}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {/* 역전파 — 전원 활성 가능할 때만 노출 */}
                {wbAllAvailable ? (
                  <div>
                    <label className={labelCls}>역전파(정본 저장)</label>
                    <select
                      className={`${FT_CTRL_CLS} w-full`}
                      value={wbCommon.mixed ? MIXED : wbCommon.value}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === MIXED) return;
                        applyWidgets((x) => {
                          const next = { ...x };
                          // scope 전환 시 useAsDefault 보존(단일과 동형 불변 갱신) — '없음'은 전체 해제.
                          if (v === 'site' || v === 'organization')
                            next.writeBack = { ...x.writeBack, scope: v };
                          else delete next.writeBack;
                          return next;
                        });
                      }}
                    >
                      {wbCommon.mixed ? (
                        <option value={MIXED} disabled>
                          — 혼합 —
                        </option>
                      ) : null}
                      <option value="">없음(보고서만)</option>
                      <option value="site" disabled={wbSiteDisabled}>
                        현장에 저장
                      </option>
                      <option value="organization" disabled={wbOrgDisabled}>
                        조직에 저장
                      </option>
                    </select>
                    {wbAllCatalog && !wbCommon.mixed && wbCommon.value ? (
                      <label className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          ref={(el) => {
                            if (el) el.indeterminate = wbUseCommon.mixed;
                          }}
                          checked={!wbUseCommon.mixed && wbUseCommon.value === true}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            applyWidgets((x) => {
                              if (!x.writeBack) return x;
                              return {
                                ...x,
                                writeBack: {
                                  ...x.writeBack,
                                  useAsDefault: checked || undefined,
                                },
                              };
                            });
                          }}
                        />
                        다음 보고서 기본값으로 사용
                        {wbUseCommon.mixed ? <MixedTag /> : null}
                      </label>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-[11px] leading-snug text-gray-400">
                    역전파: {wbDisabledReason ?? '일부 위젯이 역전파할 수 없어 일괄 설정에서 제외됩니다'}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  // ── 셀 속성 모드(2026-07-13 사용자 결정: 셀 = 1급 선택 대상) ──
  // 셀 포커스 중엔 표 속성 대신 그 셀의 속성(종류/텍스트/colSpan/배치/복붙)을 표시하고,
  // 입력 위젯이 배치돼 있으면 위젯 속성(라벨/종류/필수/모드/자동 채움)을 이어서 보여준다.
  // 단일(anchor) 기준 — 복수 선택은 위 요약 뷰에서 처리.
  if (anchor) {
    const row = w.rows.find((r) => r.id === anchor.rowId);
    const col = w.columns.find((c) => c.id === anchor.columnId);
    // columns는 구조 축만(라벨 없음 — 헤더=일반 셀 모델) → 셀 표기는 열 번호.
    const colNo = w.columns.findIndex((c) => c.id === anchor.columnId) + 1;
    const backBtn = (
      <button
        type="button"
        className="shrink-0 rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
        onClick={() => onFocusClear?.()}
      >
        ← 표 속성
      </button>
    );
    if (!row || !col) {
      return (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500">셀</p>
          <p className="text-[11px] text-gray-400">셀을 찾을 수 없습니다(행/열이 삭제됨).</p>
          {backBtn}
        </div>
      );
    }
    // ── 행/열 캔버스 직접 조작(parity P1) — 선택 셀 기준 인접 삽입·삭제. 양식 탭 표기 관례와 동일. ──
    // 내용 있는 행/열 삭제는 확인 모달(onRequestConfirm), 없으면 즉시. 삽입은 포커스 유지, 삭제는 포커스 해제.
    const cellHasContent = (c: FormTableFixedCell | undefined): boolean => {
      if (!c) return false;
      if (c.widget || c.compute) return true;
      return typeof c.text === 'string' && c.text.trim() !== '';
    };
    const rowHasContent = (r: FormTableRow): boolean =>
      r.type === 'fixed' ? r.cells.some((c) => cellHasContent(c)) : r.cellFields.length > 0;
    const columnHasContent = (columnId: string): boolean =>
      w.rows.some((r) =>
        r.type === 'fixed'
          ? r.cells.some((c) => c.columnId === columnId && cellHasContent(c))
          : r.cellFields.some((cf) => cf.columnId === columnId),
      );
    const insertRow = (where: 'above' | 'below') =>
      applyFt((t) => ftInsertRow(t, row.id, where));
    const insertColumn = (where: 'left' | 'right') =>
      applyFt((t) => ftInsertColumn(t, col.id, where));
    const deleteRow = () => {
      const doDelete = () => {
        applyFt((t) => ftRemoveRow(t, row.id));
        onFocusClear?.();
      };
      if (rowHasContent(row) && onRequestConfirm) {
        onRequestConfirm({
          title: '행 삭제',
          body: '이 행에 입력된 내용이 함께 삭제됩니다. 삭제하시겠습니까?',
          confirmLabel: '삭제',
          danger: true,
          onConfirm: doDelete,
        });
      } else doDelete();
    };
    const deleteColumn = () => {
      const doDelete = () => {
        applyFt((t) => ftRemoveColumn(t, col.id));
        onFocusClear?.();
      };
      if (columnHasContent(col.id) && onRequestConfirm) {
        onRequestConfirm({
          title: '열 삭제',
          body: '이 열의 모든 행에 입력된 내용이 함께 삭제됩니다. 삭제하시겠습니까?',
          confirmLabel: '삭제',
          danger: true,
          onConfirm: doDelete,
        });
      } else doDelete();
    };
    const structOps = (
      <div className="space-y-1.5 border-b border-gray-100 pb-2.5">
        <p className="text-xs font-semibold text-gray-500">
          행/열 ({w.rows.length} × {w.columns.length})
        </p>
        <div className="flex items-center gap-1">
          <span className="w-5 text-[11px] text-gray-500">행</span>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="이 행 위에 삽입"
            onClick={() => insertRow('above')}
          >
            ↑ 위 삽입
          </button>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="이 행 아래에 삽입"
            onClick={() => insertRow('below')}
          >
            ↓ 아래 삽입
          </button>
          <button
            type="button"
            className={STRUCT_DEL_BTN_CLS}
            title="이 행 삭제"
            disabled={w.rows.length <= 1}
            onClick={deleteRow}
          >
            행 삭제
          </button>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-5 text-[11px] text-gray-500">열</span>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="이 열 왼쪽에 삽입"
            onClick={() => insertColumn('left')}
          >
            ← 좌 삽입
          </button>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="이 열 오른쪽에 삽입"
            onClick={() => insertColumn('right')}
          >
            → 우 삽입
          </button>
          <button
            type="button"
            className={STRUCT_DEL_BTN_CLS}
            title="이 열 삭제"
            disabled={w.columns.length <= 1}
            onClick={deleteColumn}
          >
            열 삭제
          </button>
        </div>
        {/* 열 너비/행 높이는 표 위젯 속성의 "표 구조" 섹션(열/행 목록)으로 일원화(2026-07-13 사용자 확정) —
            셀 속성의 중복 크기 편집 경로 제거. */}
      </div>
    );
    if (row.type === 'repeat') {
      const def = (dataSources ?? []).find((d) => d.key === row.sourceKey);
      const cur = row.cellFields.find((cf) => cf.columnId === col.id)?.field ?? '';
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-1">
            <p className="min-w-0 truncate text-xs font-medium text-gray-500">
              셀 — 열 {colNo} · 반복 행
            </p>
            {backBtn}
          </div>
          {structOps}
          <div>
            <label className={labelCls}>
              필드 매핑 — {def ? `${def.label} · ${def.key}` : row.sourceKey || '(소스 미지정)'}
            </label>
            {def ? (
              <select
                className={`${FT_CTRL_CLS} w-full`}
                value={cur}
                onChange={(e) =>
                  applyFt((t) => ftSetRepeatCellField(t, row.id, col.id, e.target.value))
                }
              >
                <option value="">(비움)</option>
                {def.fields.map((f) => (
                  <option key={f.field} value={f.field}>
                    {f.label ? `${f.label} · ${f.field}` : f.field}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-[11px] text-amber-600">
                반복 행의 데이터셋을 먼저 지정하세요(표 속성 → 행).
              </p>
            )}
          </div>
        </div>
      );
    }
    const ri = w.rows.indexOf(row);
    const cell = row.cells.find((c) => c.columnId === col.id);
    const type = ftFixedCellType(cell);
    const cw = cell?.widget;
    // 셀 정렬은 종류·내용과 독립 속성 — 종류/텍스트/위젯 변경 시에도 보존(재구성에 항상 병합).
    const keepAlign = ftKeepCellAlign(cell);
    // 셀 스타일 반영(parity P2.5) — 셀 전체 spread + style만 교체(keepAlign 미사용: 옛 style 덮음 방지).
    // 빈 style은 undefined로 정리. 편집기(SingleCellStyleFields)의 onPatch/preview/commit·reset 공용.
    const setFtCellStyle = (s: TableCellStyle | undefined) =>
      applyFt((t) =>
        ftSetFixedCell(t, row.id, col.id, {
          ...(cell ?? { columnId: col.id }),
          columnId: col.id,
          style: s,
        }),
      );
    // ── 셀 병합(parity P2) — 우/아래 인접 1칸과 직사각 구성해 병합, 또는 해제(양식 탭 관례). ──
    const thisRef: FtCellRef = { rowId: row.id, columnId: col.id };
    const curColIdx = w.columns.findIndex((c) => c.id === col.id);
    const rightCol = curColIdx >= 0 ? w.columns[curColIdx + 1] : undefined;
    const rightPair: FtCellRef[] | null = rightCol
      ? [thisRef, { rowId: row.id, columnId: rightCol.id }]
      : null;
    const rowPosIdx = w.rows.indexOf(row);
    const belowRow = w.rows[rowPosIdx + 1];
    const downPair: FtCellRef[] | null =
      belowRow && belowRow.type === 'fixed'
        ? [thisRef, { rowId: belowRow.id, columnId: col.id }]
        : null;
    const canMergeRight = !!rightPair && ftCanMergeCells(w, rightPair);
    const canMergeDown = !!downPair && ftCanMergeCells(w, downPair);
    const isMergedCell = (cell?.colSpan ?? 1) > 1 || (cell?.rowSpan ?? 1) > 1;
    // 병합 실행(대상 셀 내용 있으면 확인 모달). top-left=현재 셀이라 포커스 유지(refocus 불필요).
    const mergePair = (pair: FtCellRef[], lossBody: string) => {
      const doIt = () => applyFt((t) => ftMergeCells(t, pair) ?? t);
      const target = pair[1];
      const targetRow = w.rows.find((r) => r.id === target.rowId);
      const targetCell =
        targetRow?.type === 'fixed'
          ? targetRow.cells.find((c) => c.columnId === target.columnId)
          : undefined;
      if (cellHasContent(targetCell) && onRequestConfirm) {
        onRequestConfirm({
          title: '셀 병합',
          body: lossBody,
          confirmLabel: '병합',
          danger: true,
          onConfirm: doIt,
        });
      } else doIt();
    };
    const mergeOps = (
      <div className="space-y-1.5 border-b border-gray-100 pb-2.5">
        <p className="text-xs font-semibold text-gray-500">
          병합 ({cell?.colSpan ?? 1}열 × {cell?.rowSpan ?? 1}행)
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="오른쪽 인접 셀과 병합"
            disabled={!canMergeRight}
            onClick={() =>
              rightPair && mergePair(rightPair, '병합하면 오른쪽 셀의 내용이 사라집니다. 병합하시겠습니까?')
            }
          >
            → 오른쪽 병합
          </button>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="아래 인접 셀과 병합"
            disabled={!canMergeDown}
            onClick={() =>
              downPair && mergePair(downPair, '병합하면 아래 셀의 내용이 사라집니다. 병합하시겠습니까?')
            }
          >
            ↓ 아래 병합
          </button>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="병합 해제"
            disabled={!isMergedCell}
            onClick={() => {
              const rs = Math.max(1, cell?.rowSpan ?? 1);
              const cs = Math.max(1, cell?.colSpan ?? 1);
              applyFt((t) => ftUnmergeCell(t, thisRef));
              // 해제로 복원된 영역 전체 선택(2026-07-14) — 첫 셀 plain(선택 재설정) 후 나머지 additive.
              if (onCellFocus && (rs > 1 || cs > 1)) {
                const fixedRows = w.rows.filter((r) => r.type === 'fixed');
                const ri0 = fixedRows.findIndex((r) => r.id === row.id);
                const ci0 = w.columns.findIndex((c) => c.id === col.id);
                const refs: FtCellRef[] = [];
                for (let dr = 0; dr < rs; dr++) {
                  for (let dc = 0; dc < cs; dc++) {
                    const r = fixedRows[ri0 + dr];
                    const c = w.columns[ci0 + dc];
                    if (r && c) refs.push({ rowId: r.id, columnId: c.id });
                  }
                }
                refs.forEach((ref, i) =>
                  onCellFocus({ tableId: w.id, ...ref }, i === 0 ? {} : { additive: true }),
                );
              }
            }}
          >
            병합 해제
          </button>
        </div>
      </div>
    );
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-1">
          <p className="min-w-0 truncate text-xs font-medium text-gray-500">
            셀 — 열 {colNo} · 행 {ri + 1}
          </p>
          {backBtn}
        </div>
        {structOps}
        {mergeOps}
        {/* 셀 자체 속성 — 종류/복사·붙여넣기. colSpan 숫자 입력은 제거(2026-07-13 사용자 지적:
            라벨 없는 정체불명 입력) — 병합 편집은 위 "병합" 섹션(인접 병합/해제)으로 일원화. */}
        <div className="flex items-center gap-1.5">
          <span className="shrink-0 text-[11px] text-gray-500">종류</span>
          <select
            className={FT_CTRL_CLS}
            value={type}
            title="셀 종류"
            onChange={(e) => {
              const v = e.target.value as 'empty' | 'text' | 'input' | 'compute';
              if (v === 'empty') {
                applyFt((t) => ftSetFixedCell(t, row.id, col.id, null));
              } else if (v === 'text') {
                applyFt((t) =>
                  ftSetFixedCell(t, row.id, col.id, {
                    columnId: col.id,
                    text: cell?.text ?? '',
                    // 텍스트 셀 자동 채움(prefillFrom) 보존 — 다른 종류에는 없으니 실질 텍스트 유지 시에만 이월.
                    ...(cell?.prefillFrom ? { prefillFrom: cell.prefillFrom } : {}),
                    ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                    ...keepAlign,
                  }),
                );
              } else if (v === 'compute') {
                applyFt((t) =>
                  ftSetFixedCell(t, row.id, col.id, {
                    columnId: col.id,
                    compute: cell?.compute ?? { inputs: [], mode: 'op', op: 'sum' },
                    ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                    ...keepAlign,
                  }),
                );
              } else {
                applyFt((t) =>
                  ftSetFixedCell(t, row.id, col.id, {
                    columnId: col.id,
                    ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                    ...(cell?.widget ? { widget: cell.widget } : {}),
                    ...keepAlign,
                  }),
                );
              }
            }}
          >
            <option value="empty">비움</option>
            <option value="text">텍스트</option>
            <option value="input">입력</option>
            <option value="compute">계산</option>
          </select>
          {type !== 'empty' && cell ? (
            <button
              type="button"
              className={`${FT_CTRL_CLS} bg-gray-100`}
              title="셀 복사"
              aria-label="셀 복사"
              onClick={() => copyCell(cell)}
            >
              {COPY_ICON}
            </button>
          ) : null}
          {cellClipboard ? (
            <button
              type="button"
              className={`${FT_CTRL_CLS} bg-brand-50 text-brand-600`}
              title={
                cellClipboard.kind === 'block'
                  ? `블록 붙여넣기(${cellClipboard.rows}×${cellClipboard.cols}) — 이 셀을 좌상단으로 배치`
                  : `셀 붙여넣기 — ${
                      cellClipboard.cell.text !== undefined
                        ? `텍스트 "${cellClipboard.cell.text}"`
                        : cellClipboard.cell.widget
                          ? `위젯 ${cellClipboard.cell.widget.label ?? cellClipboard.cell.widget.bindingKey}(독립 복제)`
                          : '입력(위젯 미선택)'
                    }`
              }
              aria-label="셀 붙여넣기"
              onClick={() => pasteCell(row.id, col.id)}
            >
              {PASTE_ICON}
            </button>
          ) : null}
        </div>
        {/* 셀 정렬 — 양식 탭과 동일한 9방향 그리드(가로 × 세로). contentAlignH/V > column.align > 기본. 종류 무관 노출(빈 셀 제외). */}
        {type !== 'empty' ? (
          <CellContentAlignEditor
            valueH={cell?.contentAlignH ?? (cell?.align ? LEGACY_ALIGN_TO_H[cell.align] : undefined)}
            valueV={cell?.contentAlignV}
            onPick={(h, v) =>
              applyFt((t) =>
                ftSetFixedCell(t, row.id, col.id, {
                  ...(cell ?? { columnId: col.id }),
                  columnId: col.id,
                  ...ftCellAlignFields(h, v),
                }),
              )
            }
          />
        ) : null}
        {/* 셀 스타일 — 양식 탭 단일 셀과 동일 편집기(공용 SingleCellStyleFields, parity P2.5). 빈 셀 제외.
            formTable 고정 셀은 backgroundKey(데이터 바인딩 배경) 개념이 없어 slot 미주입. */}
        {type !== 'empty' ? (
          <SingleCellStyleFields
            style={cell?.style ?? {}}
            onPatch={(s) => setFtCellStyle(ftMergeStyle(undefined, s))}
            onPreview={(s) => setFtCellStyle(ftMergeStyle(undefined, s))}
            onCommit={(s) => setFtCellStyle(ftMergeStyle(undefined, s))}
            onRevert={() => {}}
            onReset={() => setFtCellStyle(defaultCellStyle())}
          />
        ) : null}
        {type === 'text' ? (
          <div className="space-y-2">
            <input
              type="text"
              className={`${FT_CTRL_CLS} w-full`}
              value={cell?.text ?? ''}
              placeholder="셀 텍스트"
              onChange={(e) =>
                applyFt((t) =>
                  ftSetFixedCell(t, row.id, col.id, {
                    columnId: col.id,
                    text: e.target.value,
                    // 자동 채움(prefillFrom)은 텍스트 편집 시에도 보존(종류/텍스트 변경에 소실 방지).
                    ...(cell?.prefillFrom ? { prefillFrom: cell.prefillFrom } : {}),
                    ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                    ...keepAlign,
                  }),
                )
              }
            />
            {/* 자동 채움(카탈로그) — 지정 시 작성 시점에 카탈로그 값이 텍스트로 표시(값 없으면 위 문구 fallback).
                heading 자동 채움과 동형(CatalogPrefillPicker 재사용). 셀 전체 spread로 text/스타일/정렬 보존. */}
            <div>
              <label className={labelCls}>자동 채움(시스템 조회)</label>
              <CatalogPrefillPicker
                catalog={catalog ?? []}
                value={cell?.prefillFrom}
                onChange={(v) =>
                  applyFt((t) => {
                    const next: FormTableFixedCell = { ...(cell ?? { columnId: col.id }), columnId: col.id };
                    if (v) next.prefillFrom = v;
                    else delete next.prefillFrom;
                    return ftSetFixedCell(t, row.id, col.id, next);
                  })
                }
              />
              <p className="mt-1 text-[10px] leading-snug text-gray-400">
                지정 시 작성 시점에 시스템 조회 값이 표시됩니다(값이 없으면 위 문구로 표시).
              </p>
            </div>
          </div>
        ) : null}
        {type === 'compute' && cell?.compute ? (
          <FtComputeCellEditor
            w={w}
            otherTables={otherFormTables}
            // 편집기가 표별 tableId를 직접 실어 보냄(교차 표 hover) — 그대로 전달.
            onCellHover={onCellHover}
            compute={cell.compute}
            onPatch={(next) =>
              applyFt((t) =>
                ftSetFixedCell(t, row.id, col.id, {
                  columnId: col.id,
                  compute: next,
                  ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                  ...keepAlign,
                }),
              )
            }
          />
        ) : null}
        {type === 'input' && !cw ? (
          <div className="space-y-1">
            {/* 반복 스코프 — 소스 행 필드에서 직접 배치(일반 위젯 패널의 "행 필드" select와 동일
                옵션 정본 — 계약 자식·카탈로그 선언 자식 포함, 생성도 같은 정본 cellWidgetFromRepeatOption
                이 옵션 전부를 해소한다). 반복 안 셀의 실질 후보는 행 필드 전부라 자유 키/카탈로그
                경로보다 이 select가 정본 UX. 셀 슬롯은 InputWidget 전용이라 조립형(risk-estimation
                group) 필드만 생성 불가 옵션 — 선택해도 무동작(cellWidgetFromRepeatOption null). */}
            {repeatSourceDef ? (
              <div>
                <label className={labelCls}>
                  행 필드에서 배치 (반복 소스: {repeatSourceDef.label ?? repeatSourceDef.key})
                </label>
                <select
                  className={`${FT_CTRL_CLS} w-full`}
                  value=""
                  onChange={(e) => {
                    const nw = cellWidgetFromRepeatOption(
                      repeatSourceDef,
                      e.target.value,
                      catalog,
                      () => crypto.randomUUID(),
                    );
                    if (!nw) return;
                    applyFt((t) =>
                      ftSetFixedCell(t, row.id, col.id, {
                        columnId: col.id,
                        widget: nw,
                        ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                        ...keepAlign,
                      }),
                    );
                  }}
                >
                  <option value="">필드 선택…</option>
                  {repeatRowFieldOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
                  반복 행 객체의 해당 필드 값이 이 셀에 표시·편집됩니다.
                </p>
              </div>
            ) : null}
            {unplacedPool.length > 0 ? (
              <FtCellWidgetPicker
                pool={unplacedPool}
                widgetPrefill={widgetPrefill}
                writeValues={writeValues}
                onPick={(picked) =>
                  applyFt((t) =>
                    ftSetFixedCell(t, row.id, col.id, {
                      columnId: col.id,
                      widget: picked,
                      ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                      ...keepAlign,
                    }),
                  )
                }
              />
            ) : (
              <p className="text-[11px] text-gray-400">배치 가능한 미배치 입력 위젯이 없습니다.</p>
            )}
            <FtCatalogCellCreator
              catalog={catalog ?? []}
              takenKeys={ftTakenKeys}
              onCreate={(nw) =>
                applyFt((t) =>
                  ftSetFixedCell(t, row.id, col.id, {
                    columnId: col.id,
                    widget: nw,
                    ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                    ...keepAlign,
                  }),
                )
              }
            />
          </div>
        ) : null}
        {cw ? (
          <div className="space-y-3 border-t border-gray-200 pt-3">
            {/* 셀 = 위젯 통합 개념 — 위젯 속성을 셀 속성에 이어서(별도 위젯 선택 없음). */}
            <div className="flex items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-500">입력 위젯</p>
              {/* 표 축(칼럼 편집기 포함)은 2026-08-07 디스크립터 이관으로 이 패널에서 직접 편집된다.
                  그래도 **아직 단일 패널에만 있는 축**이 남아 있다 — 선택지·옵션 소스(select/radio),
                  안내 문구(placeholder), 입력 형식, combobox·sortableSelect 저작, 라벨 표시 정책,
                  뷰별 축(숨김·순서·폭). 그 축들은 위젯을 anchor로 골라야 보이므로 이 버튼을 남긴다.
                  트리 클릭=표 선택+셀 포커스 규약은 그대로 두고, 여기서만 한 걸음 더 들어간다. */}
              <span className="flex shrink-0 items-center gap-1">
                {onSelectWidget ? (
                  <button
                    type="button"
                    className={FT_CTRL_CLS}
                    title="이 위젯을 선택해 전체 속성(선택지·안내 문구·라벨 표시 등)을 엽니다 — 셀 포커스는 해제됩니다."
                    onClick={() => onSelectWidget(cw.id)}
                  >
                    편집
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`${FT_CTRL_CLS} bg-red-50 text-red-600`}
                  title="셀에서 빼기(파생=풀 복귀, 직접 생성=삭제)"
                  onClick={() =>
                    applyFt((t) =>
                      ftSetFixedCell(t, row.id, col.id, {
                        columnId: col.id,
                        ...(cell?.colSpan ? { colSpan: cell.colSpan } : {}),
                        ...keepAlign,
                      }),
                    )
                  }
                >
                  빼기
                </button>
              </span>
            </div>
            {/* 반복 스코프 — bindingKey를 소스 행 필드 select로 표시·변경(2026-08-05 사용자 제안).
                옵션·계약 자식 자동 배선·라벨 정책(미설정일 때만 제안) 전부 일반 위젯 패널의
                "행 필드(값 저장 위치)"와 동일 정본. 반복 밖은 기존 mono 표기 유지. */}
            {repeatSourceDef ? (
              <div>
                <label className={labelCls}>
                  행 필드 (반복 소스: {repeatSourceDef.label ?? repeatSourceDef.key})
                </label>
                <select
                  className={`${FT_CTRL_CLS} w-full`}
                  value={cw.bindingKey}
                  onChange={(e) => {
                    const nextKey = e.target.value;
                    const sub = resolveRepeatContractSub(repeatSourceDef, nextKey);
                    const fieldLabel = sub
                      ? sub.label
                      : repeatSourceDef.fields.find((f) => f.field === nextKey)?.label;
                    onWidgetUpdate(cw.id, (x) => {
                      if (x.kind !== 'input') return x;
                      const nx = { ...x, bindingKey: nextKey };
                      if (!nx.label && fieldLabel) nx.label = fieldLabel;
                      if (sub) {
                        // 선택지 없는 계약 자식(improvement-image 등)은 자동 배선 흔적만 지운다.
                        if (sub.optionsFrom) nx.optionsFrom = { ...sub.optionsFrom };
                        else if (isAutoWiredContractOptionsFrom(nx.optionsFrom)) delete nx.optionsFrom;
                        if (sub.dataType) nx.dataType = sub.dataType;
                        else delete nx.dataType;
                      } else if (isAutoWiredContractOptionsFrom(nx.optionsFrom)) {
                        delete nx.optionsFrom;
                        delete nx.dataType;
                      }
                      return nx;
                    });
                  }}
                >
                  {repeatRowBindingOptions(repeatSourceDef, cw.bindingKey, catalog).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="break-all font-mono text-[11px] text-gray-400">{cw.bindingKey}</p>
            )}
            {(() => {
              const pf = widgetPrefill(cw);
              const pv = composerValuePreview(writeValues?.[cw.bindingKey]);
              const ii = selectIndexInfo(pf?.catalogKey, pf?.select);
              return (
                <p className="break-all text-[10px] leading-snug text-gray-400">
                  {pf
                    ? `${pf.catalogKey}${pf.select ? ` · ${pf.select}` : ''}${ii ? ` [${ii}]` : ''} — `
                    : ''}
                  값: {pv}
                </p>
              );
            })()}
            <div>
              <label
                className={labelCls}
                title="셀 안에서는 라벨이 화면에 표시되지 않습니다. 필수 미입력 안내 문구와 위젯 목록 식별에 쓰입니다."
              >
                라벨
              </label>
              <input
                type="text"
                className={`${FT_CTRL_CLS} w-full`}
                value={cw.label ?? ''}
                onChange={(e) =>
                  onWidgetUpdate(cw.id, (x) =>
                    x.kind === 'input' ? { ...x, label: e.target.value || undefined } : x,
                  )
                }
              />
              <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
                셀 안에서는 표시되지 않음 — 필수 안내·목록 식별용
              </p>
            </div>
            <div>
              <label className={labelCls}>위젯</label>
              <select
                className={`${FT_CTRL_CLS} w-full`}
                value={cw.widget}
                onChange={(e) =>
                  onWidgetUpdate(cw.id, (x) =>
                    x.kind === 'input'
                      ? withWidgetTypeChange(x, e.target.value as WidgetType)
                      : x,
                  )
                }
              >
                {widgetOptionsForDataType(cw.dataType, cw.widget).map((opt) => (
                  <option key={opt} value={opt}>
                    {WIDGET_OPTION_LABELS[opt] ?? opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                id={`ftcell-required-${cw.id}`}
                type="checkbox"
                className="h-4 w-4"
                checked={cw.required ?? false}
                onChange={(e) =>
                  onWidgetUpdate(cw.id, (x) =>
                    x.kind === 'input' ? { ...x, required: e.target.checked } : x,
                  )
                }
              />
              <label htmlFor={`ftcell-required-${cw.id}`} className="text-xs text-gray-600">
                필수 입력
              </label>
            </div>
            <div>
              <label className={labelCls}>표시 모드</label>
              <div className="inline-flex overflow-hidden rounded border border-gray-300">
                {WRITE_FORM_MODE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      onWidgetUpdate(cw.id, (x) =>
                        x.kind === 'input' ? { ...x, mode: opt.value } : x,
                      )
                    }
                    className={`whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
                      (cw.mode ?? 'editable') === opt.value
                        ? 'bg-brand-500 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    aria-pressed={(cw.mode ?? 'editable') === opt.value}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {/* 편집 진입(항상 입력/클릭 시 편집) — 본문 단일 패널과 같은 디스크립터 Control 공유.
                text/textarea/목록형 게이트는 디스크립터 applicable 소유(미해당 위젯이면 미렌더). */}
            <WidgetPropSingle
              prop="editEntry"
              w={cw}
              onWidgetUpdate={onWidgetUpdate}
              ctx={{ activeView: COMPOSER_BASE_VIEW_KEY }}
            />
            {/* 긴 텍스트 접기 — 셀 위젯도 같은 디스크립터 Control 공유(조문 본문처럼 긴 값이
                셀에 오는 경우가 이 축의 1차 소비처). Control은 ctx 미사용이라 base 뷰만 전달. */}
            <WidgetPropSingle
              prop="collapseLines"
              w={cw}
              onWidgetUpdate={onWidgetUpdate}
              ctx={{ activeView: COMPOSER_BASE_VIEW_KEY }}
            />
            {/* 읽기 표시 형식 — 셀 위젯도 같은 디스크립터 Control 공유(위험성평가 배지의 1차
                소비처가 이 경로: 작성 카드 괘선 표의 readonly 셀 위젯).
                ⚠️ **catalog를 반드시 넘긴다** — 구간 판정(rangeMap)의 "구간 기준표" 선택기가
                카탈로그에서 후보를 만든다. 안 넘기면 빈 목록이라 저작자가 출처를 고를 수도, 무엇이
                쓰이는지 볼 수도 없다(2026-08-05 제보: "배지 속성에 rangeMap만 뜨고 출처가 없다"). */}
            <WidgetPropSingle
              prop="displayFormat"
              w={cw}
              onWidgetUpdate={onWidgetUpdate}
              ctx={{
                activeView: COMPOSER_BASE_VIEW_KEY,
                catalog,
                tree,
                previewValues,
                // 경로 칸(병기 값·구간 키) 후보 — 셀 위젯은 tree에서 id로 찾을 수 없어(표 안에 산다)
                // Control이 스스로 스코프를 판정할 수 없다. 표가 아는 반복 소스를 여기서 실어 보낸다.
                rowFieldOptions: cellRowFieldOptions,
              }}
            />
            {/* 시작값 — 본문 단일 패널과 같은 디스크립터 Control 공유(셀 위젯도 InputWidget 값 속성 동일 노출).
                ⚠️ **catalog·dataSources를 반드시 넘긴다** — 「시스템 조회 값 하나」의 출처 선택기와
                「조회된 행들에서 만들기」의 데이터 후보가 각각 그 둘에서 나온다. 안 넘기면 방식은 보이는데
                고를 것이 없다. 셀 위젯은 tree에서 id로 찾히지 않아 반복 게이트는 자연히 통과한다
                (서버 `walkComposerWidgetsForPrefill`도 formTable 고정 셀을 순회 대상으로 삼는다). */}
            <WidgetPropSingle
              prop="startValue"
              w={cw}
              onWidgetUpdate={onWidgetUpdate}
              ctx={{ activeView: COMPOSER_BASE_VIEW_KEY, catalog, dataSources }}
            />
            {/* 검색 제안 소스 — 본문 단일 패널과 같은 디스크립터 Control 공유. 필터 필드·전파 대상
                후보 산출에 트리/데이터소스가 필요해 ctx로 전달(셀 위젯도 반복 안이면 행 스코프).
                hover 강조는 표 셀 채널만 전달(onCellHover) — 위젯 대상 강조·이동은 무동작이고
                매핑 편집 자체는 본문 패널과 동일. */}
            <WidgetPropSingle
              prop="suggestionsFrom"
              w={cw}
              onWidgetUpdate={onWidgetUpdate}
              ctx={{
                activeView: COMPOSER_BASE_VIEW_KEY,
                tree,
                dataSources,
                catalog,
                writeFormFields,
                bindingPrefillMap,
                onFtCellHover: onCellHover,
              }}
            />
            {/* 표 위젯 전용 축(헤더 숨김·테두리·표시 단수·채움 방향·구분 그룹·행 클릭 토글·칼럼) —
                본문 단일 패널과 **같은 배열·같은 Control**을 공유한다(TABLE_WIDGET_PROP_ORDER).
                셀 안 표는 종전에 어느 패널에서도 이 축을 못 고쳤다(2026-08-07 이관).
                표 위젯이 아니면 디스크립터 applicable이 전부 false라 아무것도 렌더되지 않는다.
                dataDefs·tree·정본 쓰기 채널을 전달한다 — 참조 모드(P3) 셀 안 표의 게이트 판정과
                칼럼 편집기(정본 쓰기·detach·확인 퍼널)가 본문 단일 패널과 같은 동작을 해야 한다. */}
            {TABLE_WIDGET_PROP_ORDER.map((prop) => (
              <WidgetPropSingle
                key={prop}
                prop={prop}
                w={cw}
                onWidgetUpdate={onWidgetUpdate}
                ctx={{
                  activeView: COMPOSER_BASE_VIEW_KEY,
                  dataDefs,
                  tree,
                  onTemplateUpdate,
                  countKeyRefs,
                  requestConfirm: onRequestConfirm,
                }}
              />
            ))}
            {/* 시스템 조회 값(prefillFrom)은 위 「시작값」 구획의 방식 하위 입력으로 이관됐다
                (결정 2026-09-05 대조표 1) — 본문 단일 패널과 같은 자리·같은 Control. */}
            <WriteBackSelector
              writeBack={cw.writeBack}
              // combobox 합성 경로는 명시 prefillFrom 다음·파생 fallback(widgetPrefill 후순위)보다 앞
              // — 서버 수집과 가용성 판정 일치(역전파 전용, 자동 채움 편집에는 미반영).
              prefillFrom={cw.prefillFrom ?? comboboxSyntheticPrefill(cw, catalog) ?? widgetPrefill(cw)}
              pairedCoverage={keyedPairingCoverage(cw, {
                tree,
                catalog,
                writeFormFields,
                bindingPrefillMap,
              })}
              // 이 표가 반복 안이면 셀 위젯도 행 스코프 — 서버 행 스코프 수집기는 repeat children의
              // formTable 고정 셀까지 하강하므로 같은 게이트를 미러한다(트리 단일 패널 동형).
              rowScope={
                enclosingRepeatOfTable
                  ? judgeRowScopeWriteBack({
                      widget: cw,
                      over: enclosingRepeatOfTable.over,
                      dataSources,
                      catalog,
                    })
                  : undefined
              }
              catalog={catalog}
              labelCls={labelCls}
              selectCls={`${FT_CTRL_CLS} w-full`}
              onChange={(v) =>
                onWidgetUpdate(cw.id, (x) => {
                  if (x.kind !== 'input') return x;
                  const next = { ...x };
                  if (v) next.writeBack = v;
                  else delete next.writeBack;
                  return next;
                })
              }
            />
          </div>
        ) : null}
      </div>
    );
  }

  // ── 표 전체 셀 일괄 스타일(parity P3) — 전 고정 셀 그리드 위치 대상(미생성 위치는 style-only upsert) ──
  // 슬롯은 매 적용 시 최신 t에서 재계산(rowSpan/colSpan 덮인 위치 제외). 1회 적용=undo 1스텝.
  const allFixedSlots = ftAllFixedCellSlots(w);
  const applyAllBulkStyle = (next: Partial<TableCellStyle>) =>
    applyFt((t) => {
      let n = t;
      for (const slot of ftAllFixedCellSlots(t))
        n = ftSetFixedCell(n, slot.rowId, slot.columnId, {
          ...(slot.cell ?? { columnId: slot.columnId }),
          columnId: slot.columnId,
          style: ftMergeStyle(slot.cell?.style, next),
        });
      return n;
    });
  // 표 전체 셀 정렬 일괄 — 복수 선택 뷰와 동일 규약(H/V 독립 혼합 판정, 양축 기본이면 빈 셀 미생성).
  const allEffAlignH = (c: FormTableFixedCell | undefined): AlignVal =>
    c?.contentAlignH ?? (c?.align ? LEGACY_ALIGN_TO_H[c.align] : 'start');
  const allAlignH = ftCommon(allFixedSlots.map((s) => allEffAlignH(s.cell)));
  const allAlignV = ftCommon(allFixedSlots.map((s) => s.cell?.contentAlignV ?? 'start'));
  const applyAllAlign = (h: AlignVal, v: AlignVal) =>
    applyFt((t) => {
      let n = t;
      const isDefault = h === 'start' && v === 'start';
      for (const slot of ftAllFixedCellSlots(t)) {
        if (!slot.cell && isDefault) continue;
        n = ftSetFixedCell(n, slot.rowId, slot.columnId, {
          ...(slot.cell ?? { columnId: slot.columnId }),
          columnId: slot.columnId,
          ...ftCellAlignFields(h, v),
        });
      }
      return n;
    });

  // ── 표 구조(양식 탭 동형) — 마지막 행/열 삭제(내용 있으면 확인) ──
  const cellHasContent = (c: FormTableFixedCell | undefined): boolean => {
    if (!c) return false;
    if (c.widget || c.compute) return true;
    return typeof c.text === 'string' && c.text.trim() !== '';
  };
  const rowHasContent = (r: FormTableRow): boolean =>
    r.type === 'fixed' ? r.cells.some((c) => cellHasContent(c)) : r.cellFields.length > 0;
  const columnHasContent = (columnId: string): boolean =>
    w.rows.some((r) =>
      r.type === 'fixed'
        ? r.cells.some((c) => c.columnId === columnId && cellHasContent(c))
        : r.cellFields.some((cf) => cf.columnId === columnId),
    );
  const removeLastRow = () => {
    const last = w.rows[w.rows.length - 1];
    if (!last) return;
    const doDelete = () => applyFt((t) => ftRemoveRow(t, last.id));
    if (rowHasContent(last) && onRequestConfirm) {
      onRequestConfirm({
        title: '행 삭제',
        body: '이 행에 입력된 내용이 함께 삭제됩니다. 삭제하시겠습니까?',
        confirmLabel: '삭제',
        danger: true,
        onConfirm: doDelete,
      });
    } else doDelete();
  };
  const removeLastCol = () => {
    const last = w.columns[w.columns.length - 1];
    if (!last) return;
    const doDelete = () => applyFt((t) => ftRemoveColumn(t, last.id));
    if (columnHasContent(last.id) && onRequestConfirm) {
      onRequestConfirm({
        title: '열 삭제',
        body: '이 열의 모든 행에 입력된 내용이 함께 삭제됩니다. 삭제하시겠습니까?',
        confirmLabel: '삭제',
        danger: true,
        onConfirm: doDelete,
      });
    } else doDelete();
  };
  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-gray-500">양식 표</p>

      {/* 제목 */}
      <div>
        <label className={labelCls}>제목(선택)</label>
        <input
          type="text"
          className={inputCls}
          value={w.title ?? ''}
          placeholder="(선택) 표 위 캡션"
          onChange={(e) => applyFt((t) => ({ ...t, title: e.target.value || undefined }))}
        />
      </div>
      <WidgetTextStyleControls
        label="제목 스타일"
        style={w.titleStyle}
        defaults={COMPOSER_SECTION_TITLE_DEFAULT_STYLE}
        labelCls={labelCls}
        onField={(k, v) => onWidgetUpdate(w.id, (x) => setWidgetTextStyleField(x, k, v))}
      />
      {/* 헤더/본문 폰트 2벌(headerStyle/cellStyle) UI는 제거(2026-07-13 사용자 확정) — 본문은
          아래 전체 셀 일괄 + 셀별 style과 완전 중복. headerStyle은 헤더·열 모델 단순화로 축 자체가
          소멸(@deprecated) — normalizeFormTable이 기존 지정값을 헤더 셀 style로 박제해 반영 유지. */}

      {/* 섹션 순서는 양식 탭 테이블 노드 패널과 동형(2026-07-14 사용자 확정): 표 구조(+전 셀
          일괄 스타일) → 표 외곽 테두리 → 반복 영역. */}
      {/* 표 구조 — 끝행/끝열 추가·삭제 + 열 너비/행 높이 목록(2026-07-13 헤더·열 모델 단순화).
          열/행 상세 카드는 제거 — 셀 편집은 캔버스 셀 선택. 균등 버튼은 열 너비 목록 헤더 옆(양식 탭 관례).
          행 높이 균등은 없음 — 웹 폼은 표 높이 개념이 없어 행 N등분이 성립하지 않음(auto=내용 높이가 기본). */}
      <div className="border-t border-gray-200 pt-3">
        <p className="mb-2 text-xs font-semibold text-gray-500">
          표 구조 (행 {w.rows.length} × 열 {w.columns.length})
        </p>
        {/* 행 줄 — 끝 고정 행 추가 + 마지막 행 삭제(내용 있으면 확인). 반복 행 추가는 "반복 영역" 섹션. */}
        <div className="mb-1.5 flex items-center gap-1">
          <span className="w-6 shrink-0 text-[11px] text-gray-500">행</span>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="맨 끝에 고정 행 추가"
            onClick={() => applyFt(ftAddFixedRow)}
          >
            + 끝 행
          </button>
          <button
            type="button"
            className={STRUCT_DEL_BTN_CLS}
            title="마지막 행 삭제"
            disabled={w.rows.length <= 1}
            onClick={removeLastRow}
          >
            − 마지막 행
          </button>
        </div>
        {/* 열 줄 — 끝 열 추가 + 마지막 열 삭제(내용 있으면 확인) */}
        <div className="mb-1.5 flex items-center gap-1">
          <span className="w-6 shrink-0 text-[11px] text-gray-500">열</span>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="맨 끝에 열 추가"
            onClick={() => applyFt(ftAddColumn)}
          >
            + 끝 열
          </button>
          <button
            type="button"
            className={STRUCT_DEL_BTN_CLS}
            title="마지막 열 삭제"
            disabled={w.columns.length <= 1}
            onClick={removeLastCol}
          >
            − 마지막 열
          </button>
        </div>

        {/* 열 너비 목록 — 웹 폼이라 %(권장)/px/auto 표시(양식 탭 mm와 달리 변환 없음). 열엔 라벨이 없어 인덱스(N열).
            균등 = %N등분 박제(양식 탭 균등과 동일 의미) — auto 제거 방식은 auto layout에서 내용 비례라 정확 균등이 아님. */}
        <div className="mb-2">
          <div className="mb-0.5 flex items-center justify-between">
            <span className="text-[11px] text-gray-500">열 너비 (% / px / auto)</span>
            <button
              type="button"
              onClick={() => {
                const pct = `${(100 / w.columns.length).toFixed(4).replace(/\.?0+$/, '')}%`;
                applyFt((t) => ({
                  ...t,
                  columns: t.columns.map((c) => {
                    const nc = { ...c, width: pct };
                    delete nc.widthPct;
                    return nc;
                  }),
                }));
              }}
              title="모든 열을 균등(N등분)으로 — 내용과 무관하게 같은 폭"
              className="text-[11px] text-brand-600 hover:underline"
            >
              균등
            </button>
          </div>
          <div className="space-y-1">
            {w.columns.map((c, i) => (
              <div key={c.id} className="flex items-center gap-1">
                <span className="w-8 shrink-0 font-mono text-[11px] text-gray-400">{i + 1}열</span>
                <DimInput
                  unit="px"
                  value={c.width ?? 'auto'}
                  onPreview={(raw) => applyFtPreview((t) => ftSetColumnWidth(t, c.id, parseFtDim(raw)))}
                  onCommit={(raw) => applyFtCommit((t) => ftSetColumnWidth(t, c.id, parseFtDim(raw)))}
                  onRevert={onWidgetRevert}
                />
              </div>
            ))}
          </div>
        </div>

        {/* 행 높이 목록 — fixed 행만 편집(숫자 px/'%'/'auto', 최소 36 규약). repeat 행은 편집 불가 표기. */}
        <div>
          <span className="mb-0.5 block text-[11px] text-gray-500">행 높이 (px / % / auto)</span>
          <div className="space-y-1">
            {w.rows.map((r, i) => (
              <div key={r.id} className="flex items-center gap-1">
                <span className="w-8 shrink-0 font-mono text-[11px] text-gray-400">{i + 1}행</span>
                {r.type === 'fixed' ? (
                  <DimInput
                    unit="px"
                    value={r.height ?? 'auto'}
                    onPreview={(raw) =>
                      applyFtPreview((t) => ftSetFixedRowHeight(t, r.id, parseFtDim(raw)))
                    }
                    onCommit={(raw) =>
                      applyFtCommit((t) => ftSetFixedRowHeight(t, r.id, parseFtDim(raw)))
                    }
                    onRevert={onWidgetRevert}
                  />
                ) : (
                  <span className="text-[11px] text-gray-400">반복 영역 (자동 높이)</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 행 표시 조건(visibleWhen) — fixed 행만(반복 행은 데이터가 행 존재를 결정). 조건 falsy면
            실폼에서 그 행 미렌더, 캔버스는 항상 렌더 + 조건 배지. rowSpan 병합 구간과 상호배타 —
            병합에 걸린 행은 입력 비활성(반대 방향은 병합 게이트가 차단). 결정 2026-08-17.
            섹션은 접이식(기본 접힘 — 안 쓰는 표가 다수, 2026-08-17 사용자 확정). 지정 행 수를
            summary에 병기해 접힌 채로도 조건 존재를 알 수 있게. '조건 데이터 출처' details와 동형. */}
        <details className="mt-2 rounded border border-gray-200 px-2 py-1">
          <summary className="cursor-pointer text-[11px] text-gray-500">
            행 표시 조건
            {visibleWhenRowCount > 0 ? ` (지정 ${visibleWhenRowCount}행)` : ' (없음 — 항상 표시)'}
          </summary>
          <div className="mt-1 space-y-1">
            {w.rows.map((r, i) => (
              <div key={r.id} className="flex items-start gap-1">
                <span className="w-8 shrink-0 pt-1 font-mono text-[11px] text-gray-400">{i + 1}행</span>
                {r.type === 'fixed' ? (
                  <FtRowVisibleWhenEditor
                    value={r.visibleWhen}
                    disabled={rowSpanInvolvedIds.has(r.id)}
                    disabledReason="세로 병합(rowSpan) 구간에 걸린 행에는 표시 조건을 줄 수 없습니다 — 먼저 병합을 해제하세요."
                    rowFields={repeatRowConditionFields}
                    scopeWidgets={rowConditionScopeWidgets}
                    onCommit={(vw) => applyFt((t) => ftSetFixedRowVisibleWhen(t, r.id, vw))}
                  />
                ) : (
                  <span className="pt-1 text-[11px] text-gray-400">반복 영역 (해당 없음)</span>
                )}
              </div>
            ))}
          </div>
        </details>
      </div>

      {/* 표 전체 셀 스타일(일괄) — 전 고정 셀에 정렬/폰트/색/배경/테두리/패딩 일괄 적용(parity P3).
          헤더=일반 고정 행(2026-07-13)이라 헤더 행 셀도 동일 대상. 양식 탭에선 표 구조 섹션 말미의
          "전체 셀 일괄 스타일"에 해당 — 순서 동형(표 구조 바로 다음). */}
      <div className="border-t border-gray-200 pt-3">
        <p className="mb-1.5 text-[10px] leading-tight text-gray-400">
          아래는 전 고정 셀(헤더 행 포함)에 일괄 적용됩니다.
        </p>
        {/* 셀 정렬 일괄 — 양식 탭 표 일괄과 동형 9방향 그리드(H/V 독립 혼합 판정). */}
        <CellContentAlignEditor
          label="셀 정렬 일괄"
          valueH={allAlignH.mixed ? undefined : allAlignH.value}
          valueV={allAlignV.mixed ? undefined : allAlignV.value}
          mixedH={allAlignH.mixed}
          mixedV={allAlignV.mixed}
          onPick={applyAllAlign}
        />
        <BulkCellStyleFields
          styles={allFixedSlots.map((s) => s.cell?.style)}
          onPatch={applyAllBulkStyle}
          onPreview={applyAllBulkStyle}
          onCommit={applyAllBulkStyle}
          onRevert={() => {}}
        />
      </div>

      {/* 표 외곽 테두리 — 전체 셀 테두리와 별개(양식 탭 TableNode와 공용 편집기, parity P3). */}
      <div className="border-t border-gray-200 pt-3">
        <TableOuterBorderFields
          outerBorder={w.outerBorder}
          outerBorderOverrides={w.outerBorderOverrides}
          onBorderChange={(b) => applyFt((t) => ({ ...t, outerBorder: b }))}
          onOverridesChange={(ov) => applyFt((t) => ({ ...t, outerBorderOverrides: ov }))}
        />
      </div>

      {/* 반복 영역 — 반복 행(편집형 list 데이터셋 → 행 복제) 관리. 표 구조에서 분리(양식 탭
          반복영역 편집 관례 동형 — 2026-07-13 헤더·열 모델 단순화). 데이터셋 연결·열↔필드 매핑·
          작성 화면 행 추가/삭제 허용 + 표 내 위치(↑↓)/제거. 고정 행 편집은 캔버스 셀 선택으로 일원화. */}
      <div className="border-t border-gray-200 pt-3">
        <div className="mb-1.5 flex items-center justify-between">
          <span
            className="text-xs font-semibold text-gray-500"
            title="편집형 list 데이터셋을 데이터 배열만큼 행으로 펼칩니다"
          >
            반복 영역 ({w.rows.filter((r) => r.type === 'repeat').length})
          </span>
          <button
            type="button"
            className={STRUCT_BTN_CLS}
            title="맨 끝에 반복 영역 추가"
            onClick={() => applyFt(ftAddRepeatRow)}
          >
            ＋ 반복 영역
          </button>
        </div>
        {w.rows.every((r) => r.type !== 'repeat') ? (
          <p className="text-[11px] text-gray-400">
            없음 (정적 표). 데이터 배열을 행으로 펼치려면 추가하세요.
          </p>
        ) : null}
        <div className="space-y-1.5">
          {w.rows.map((row, ri) => {
            if (row.type !== 'repeat') return null;
            const expanded = expandedRowId === row.id;
            return (
              <div key={row.id} className="rounded border border-gray-200">
                {/* 카드 헤더 전체가 펼침 토글 + 표 내 위치(행 N)·순서 이동·제거 */}
                <div
                  className="flex cursor-pointer items-center gap-1 bg-gray-50 px-1.5 py-1"
                  onClick={() => setExpandedRowId(expanded ? null : row.id)}
                >
                  <button
                    type="button"
                    className="shrink-0 text-gray-400"
                    title={expanded ? '접기' : '펼치기'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedRowId(expanded ? null : row.id);
                    }}
                  >
                    {expanded ? '▾' : '▸'}
                  </button>
                  <span className="shrink-0 rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-medium text-brand-700">
                    행 {ri + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                    {row.sourceKey || '(데이터셋 미지정)'}
                  </span>
                  <button
                    type="button"
                    className={`${FT_CTRL_CLS} bg-gray-100`}
                    title="위로"
                    disabled={ri === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      applyFt((t) => ftMoveRow(t, row.id, 'up'));
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={`${FT_CTRL_CLS} bg-gray-100`}
                    title="아래로"
                    disabled={ri === w.rows.length - 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      applyFt((t) => ftMoveRow(t, row.id, 'down'));
                    }}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className={`${FT_CTRL_CLS} bg-red-50 text-red-600`}
                    title="반복 영역 제거"
                    onClick={(e) => {
                      e.stopPropagation();
                      applyFt((t) => ftRemoveRow(t, row.id));
                    }}
                  >
                    ✕
                  </button>
                </div>
                {expanded ? (
                  <div className="space-y-2 px-2 py-2">
                    {(() => {
                      const def = (dataSources ?? []).find((d) => d.key === row.sourceKey);
                      return (
                        <div className="space-y-2">
                          <div>
                            <label className={labelCls}>데이터셋(편집형 list)</label>
                            <select
                              className={`${FT_CTRL_CLS} w-full`}
                              value={row.sourceKey}
                              onChange={(e) =>
                                applyFt((t) =>
                                  ftPatchRepeatRow(t, row.id, { sourceKey: e.target.value }),
                                )
                              }
                            >
                              <option value="">(선택)</option>
                              {editableDefs.map((d) => (
                                <option key={d.key} value={d.key}>
                                  {d.label} · {d.key}
                                </option>
                              ))}
                            </select>
                          </div>
                          {def ? (
                            <div>
                              <label className={labelCls}>열 ↔ 필드 매핑</label>
                              <div className="space-y-1">
                                {w.columns.map((col, ci) => {
                                  const cur =
                                    row.cellFields.find((cf) => cf.columnId === col.id)?.field ?? '';
                                  // 반복 행 셀은 단일 선택만(additive 미적용).
                                  const focused = isCellFocused(row.id, col.id);
                                  return (
                                    <div
                                      key={col.id}
                                      id={`ftcell-${row.id}-${col.id}`}
                                      className={`flex cursor-pointer items-center gap-1.5 rounded ${
                                        focused ? 'ring-1 ring-brand-300' : ''
                                      }`}
                                      onClick={() =>
                                        onCellFocus?.({
                                          tableId: w.id,
                                          rowId: row.id,
                                          columnId: col.id,
                                        })
                                      }
                                      onMouseEnter={() =>
                                        onCellHover?.({
                                          tableId: w.id,
                                          rowId: row.id,
                                          columnId: col.id,
                                        })
                                      }
                                      onMouseLeave={() => onCellHover?.(null)}
                                    >
                                      <span className="min-w-0 flex-1 truncate text-xs text-gray-600">
                                        열 {ci + 1}
                                      </span>
                                      <select
                                        className={`${FT_CTRL_CLS} w-40`}
                                        value={cur}
                                        onChange={(e) =>
                                          applyFt((t) =>
                                            ftSetRepeatCellField(t, row.id, col.id, e.target.value),
                                          )
                                        }
                                      >
                                        <option value="">(비움)</option>
                                        {def.fields.map((f) => (
                                          <option key={f.field} value={f.field}>
                                            {f.label ? `${f.label} · ${f.field}` : f.field}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : row.sourceKey ? (
                            <p className="text-[11px] text-amber-600">
                              데이터셋 &apos;{row.sourceKey}&apos;을 찾을 수 없습니다(양식 탭에서 삭제됨).
                            </p>
                          ) : null}
                          <label className="flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={row.allowAddRemove ?? def?.allowAddRemove ?? true}
                              onChange={(e) =>
                                applyFt((t) =>
                                  ftPatchRepeatRow(t, row.id, { allowAddRemove: e.target.checked }),
                                )
                              }
                            />
                            작성 화면에서 행 추가/삭제 허용
                          </label>
                          {/* 하위 축(추가/삭제 분리 — 2026-09-14) — 표시값·비활성 판정은 RowAddRemoveToggles
                              (shared effectiveRowAddRemove(def, row)·rowAddRemoveMaster: 키마다 행 → def → true).
                              체크 상태 = 지금 작성 화면의 실제 동작(데이터소스 탭과 같은 규약). */}
                          <RowAddRemoveToggles
                            def={def}
                            node={row}
                            variant="formTableRow"
                            onAddChange={(checked) =>
                              applyFt((t) => ftPatchRepeatRow(t, row.id, { allowAdd: checked }))
                            }
                            onRemoveChange={(checked) =>
                              applyFt((t) => ftPatchRepeatRow(t, row.id, { allowRemove: checked }))
                            }
                            masterOffNote="상위 「행 추가/삭제 허용」이 꺼져 있어 추가·삭제가 모두 막힙니다."
                          />
                          {/* 데이터소스 탭의 같은 스위치와 같은 안내(2026-09-14) — 삭제의 뜻은 표면과 무관. */}
                          <p className="text-[10px] leading-4 text-gray-400">
                            삭제 = 문서에서 제외. 추가만 끄면 작성자는 행을 뺄 수만 있습니다. 편집
                            재진입 시 삭제된 행은 복원되지 않습니다(저장 전엔 저장하지 않고 나가면
                            되돌아옵니다).
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * ComposerTextStyle 한 필드를 갱신(숫자 모델) — 값 undefined/빈값이면 그 키 제거,
 * 모든 키가 부재면 스타일 객체 자체 제거(JSON에 기본값을 남기지 않아 diff/저장 최소화).
 */
function mergeTextStyleField(
  cur: ComposerTextStyle | undefined,
  key: 'fontSize' | 'fontWeight' | 'color' | 'align',
  value: number | string | undefined,
): ComposerTextStyle | undefined {
  const next: ComposerTextStyle = { ...(cur ?? {}) };
  if (value === undefined || value === '') {
    delete next[key];
  } else if (key === 'color') {
    next.color = value as string;
  } else if (key === 'align') {
    next.align = value as 'start' | 'center' | 'end';
  } else {
    next[key] = value as number;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * 텍스트 위젯의 폰트 스타일 필드 갱신 — 위젯 kind별로 대상 스타일 prop을 정한다.
 * input→labelStyle / group→titleStyle / heading→textStyle. update 콜백에서 x를 narrow 후 spread(TS2322 방지).
 */
function setWidgetTextStyleField(
  x: ComposerWidget,
  key: 'fontSize' | 'fontWeight' | 'color' | 'align',
  value: number | string | undefined,
): ComposerWidget {
  if (x.kind === 'input') {
    return { ...x, labelStyle: mergeTextStyleField(x.labelStyle, key, value) };
  }
  if (x.kind === 'group') {
    return { ...x, titleStyle: mergeTextStyleField(x.titleStyle, key, value) };
  }
  if (x.kind === 'heading') {
    return { ...x, textStyle: mergeTextStyleField(x.textStyle, key, value) };
  }
  if (x.kind === 'formTable') {
    return { ...x, titleStyle: mergeTextStyleField(x.titleStyle, key, value) };
  }
  return x;
}

/**
 * 텍스트 위젯(라벨/섹션명/제목)의 폰트 스타일 컨트롤(크기 px·굵기·색) 공통 UI — 캔버스 TextStyle과 동일 조정 방식.
 * 위젯별 기본값(defaults)은 rc export 상수를 주입해 placeholder/색 초기값에 반영. 빈값/초기화=해당 키 제거.
 */
function WidgetTextStyleControls({
  label,
  style,
  defaults,
  labelCls,
  onField,
}: {
  label: string;
  style: ComposerTextStyle | undefined;
  defaults: ComposerTextStyleDefaults;
  labelCls: string;
  onField: (
    key: 'fontSize' | 'fontWeight' | 'color' | 'align',
    value: number | string | undefined,
  ) => void;
}) {
  const colorValue = style?.color ?? defaults.color ?? '#374151';
  const alignBtnCls = (active: boolean): string =>
    `flex-1 whitespace-nowrap border-l border-gray-300 px-2 py-1 text-xs first:border-l-0 ${
      active ? 'bg-brand-50 font-medium text-brand-700' : 'bg-white text-gray-600 hover:bg-gray-50'
    }`;
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          {/* 단위 정책(2026-07-13): 폰트 편집 표시=pt, 저장=px — 셀 스타일 편집기 "크기(pt)"와 동일.
              과거 px 직입력이라 pt 값을 입력하면 그대로 px 저장되는 오입력(제목 10.5px 사고)의 원인. */}
          <span className="text-[11px] text-gray-500 w-9">크기(pt)</span>
          <input
            type="number"
            min={6}
            step={0.5}
            className={SELECT_CLS}
            value={style?.fontSize != null ? pxToPt(style.fontSize) : ''}
            placeholder={`${pxToPt(defaults.fontSize)} (기본)`}
            onChange={(e) =>
              onField(
                'fontSize',
                e.target.value === '' ? undefined : ptToPx(Number(e.target.value)),
              )
            }
          />
        </div>
        <FontWeightSelect
          value={style?.fontWeight}
          onChange={(w) => onField('fontWeight', w)}
        />
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">색</span>
          <input
            type="color"
            className="h-7 w-10 rounded border border-gray-300 p-0.5"
            value={colorValue}
            onChange={(e) => onField('color', e.target.value)}
          />
          {style?.color != null && (
            <button
              type="button"
              className="text-[11px] text-gray-500 underline"
              onClick={() => onField('color', undefined)}
            >
              기본으로
            </button>
          )}
        </div>
        {/* 정렬(2026-08-04 신설 축) — 미지정=기본(좌측). 다시 누르면 해제(키 제거). */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 w-9">정렬</span>
          <div className="flex flex-1 overflow-hidden rounded border border-gray-300">
            {(
              [
                { value: 'start', label: '왼쪽' },
                { value: 'center', label: '가운데' },
                { value: 'end', label: '오른쪽' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={alignBtnCls(style?.align === opt.value)}
                onClick={() =>
                  onField('align', style?.align === opt.value ? undefined : opt.value)
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// 구 LabelPresentationControls(라벨 표시 계열 단일 전용 bespoke UI)는 2026-08-24에
// WIDGET_PROP_DESCRIPTORS의 labelHint/labelPlacement/labelAlign/labelStyle 네 디스크립터로
// 분해 편입됐다 — 단일·복수 패널이 같은 Control을 공유해야 한다는 레지스트리 불변식대로다.
// (이 계열이 복수 패널에서 통째로 빠져 있던 것이 그 불변식을 어긴 결과였다.)

/**
 * 편집형 list 데이터셋 1개의 필드별 작성 위젯 매핑 테이블(위젯/위젯 설정/편집).
 *
 * 정본은 dataSourceDef.fields[].widget/widgetKey/editable 1곳 — 작성 위젯 쪽에 이중 저장 안 함.
 * editable=false 필드는 위젯 콤보/키를 disabled(값 보존, 시각만). 모든 변경은 onChange(다음 dataSources
 * 배열)로 상위 apply에 전달 — history undo(Ctrl+Z) 대상. defIndex는 dataSources 전체 배열 기준 인덱스.
 * (구 상시 DataSourceWidgetsEditor에서 단일 def용으로 분리 — 2026-07-11 Phase 1, 행 그리드 위젯 속성.)
 */
function DataSourceFieldWidgetTable({
  def,
  defIndex,
  dataSources,
  onChange,
  catalog,
}: {
  def: DataSourceDef;
  defIndex: number;
  dataSources: DataSourceDef[];
  onChange: (next: DataSourceDef[]) => void;
  /**
   * 객체 필드 판정용 — `from`을 카탈로그로 해소해 선언 자식이 있으면 위젯 칸에 「객체(자식 N개)」를
   * 보인다(은닉 감사 B — 종전엔 `widget ?? 'text'`라 riskTypeFlags 같은 객체 필드도 「한 줄」로
   * 표시돼, 트리의 자식 경로 checkbox 13개가 이 필드를 소유한다는 사실을 표에서 알 수 없었다).
   */
  catalog?: CatalogEntry[];
}) {
  if (def.fields.length === 0) {
    return (
      <p className="text-xs text-gray-400">
        필드가 없습니다 — 양식 편집 탭에서 필드를 추가하세요.
      </p>
    );
  }
  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="text-left text-xs font-semibold text-gray-500 border-b border-gray-200">
          <th className="py-2 pr-3">필드</th>
          <th className="py-2 pr-3">위젯</th>
          {/* 위젯 종속 설정 칸 — 위젯 종류에 따라 무엇을 묻는지 달라진다(특화=위젯 키,
              서명=서명자 이름 필드). 그 외 위젯은 물을 것이 없어 '—'. */}
          <th className="py-2 pr-3">위젯 설정</th>
          <th className="py-2 pr-3 text-center">편집</th>
          <th className="py-2 pr-3 text-center" title="작성폼 행 그리드 표시 여부 — 끄면 행 id/조인 키 같은 내부 필드로 숨김(양식 바인딩·조립·저장 데이터에는 유지)">
            표시
          </th>
        </tr>
      </thead>
      <tbody>
        {def.fields.map((f, fi) => {
          const fieldEditable = f.editable !== false;
          // 카탈로그가 자식을 선언한 객체 필드 — 위젯 미지정이면 행 그리드 어휘 밖(작성폼 트리의
          // `<필드>.<자식>` 입력이 소유). 그 사실을 「한 줄」 대신 객체 표기로 드러낸다.
          const declaredChildren = declaredChildrenForDataSourceField(def, f, catalog);
          const objectLabel = declaredChildren.length
            ? `객체(자식 ${declaredChildren.length}개 — 작성폼 자식 경로로 배치)`
            : undefined;
          return (
            <tr key={fi} className="border-b border-gray-100">
              <td className="py-2 pr-3 align-middle">
                <span className="font-mono text-xs text-gray-600 break-all">{f.field}</span>
                {f.label && <span className="ml-1.5 text-xs text-gray-400">{f.label}</span>}
                {/* 행별 표시 조건(visibleWhen) 병기 — 같은 field를 상보 조건 두 선언으로 가르는
                    용법(예: photoUrl을 image/photo로)에서 이 표가 같은 이름 두 줄이 되므로,
                    조건을 보여야 어느 줄이 어느 행 상태의 칸인지 구분된다(편집은 index 기반이라
                    기능은 이미 정확 — 표시만의 문제. 조건 저작 UI는 후속, 결정 2026-09-07 추기 4). */}
                {f.visibleWhen && (
                  <span
                    className="ml-1.5 text-[11px] text-indigo-500 whitespace-nowrap"
                    title="행별 표시 조건(visibleWhen) — 이 조건이 참인 행에서만 이 칸이 보입니다"
                  >
                    표시조건: {f.visibleWhen}
                  </span>
                )}
              </td>
              <td className="py-2 pr-3 align-middle">
                <select
                  // 객체 필드는 미지정(undefined)이 정상 상태라 ''(객체 옵션)로 보인다 — 종전
                  // `?? 'text'`는 객체를 「한 줄」로 거짓 표시했다. 비객체 필드는 종전 기본값 유지.
                  value={f.widget ?? (objectLabel ? '' : 'text')}
                  disabled={!fieldEditable}
                  onChange={(e) =>
                    onChange(
                      e.target.value === ''
                        ? clearDataSourceFieldWidget(dataSources, defIndex, fi)
                        : setDataSourceFieldWidget(
                            dataSources,
                            defIndex,
                            fi,
                            e.target.value as DataSourceFieldWidget,
                          ),
                    )
                  }
                  className="text-sm border border-gray-300 rounded px-1.5 py-1 bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-brand-400"
                  title={
                    objectLabel
                      ? `카탈로그 선언 자식: ${declaredChildren.map((c) => `${f.field}.${c.path}`).join(', ')}`
                      : undefined
                  }
                >
                  {objectLabel && <option value="">{objectLabel}</option>}
                  {DATA_SOURCE_FIELD_WIDGETS.map((w) => (
                    <option key={w} value={w}>
                      {DATA_SOURCE_FIELD_WIDGET_LABEL[w]}
                    </option>
                  ))}
                </select>
              </td>
              <td className="py-2 pr-3 align-middle">
                {dataSourceFieldSettingsKind(f.widget) === 'widget-key' ? (
                  <>
                    {/* 자유 텍스트가 아니라 레지스트리 목록에서 고른다 — 오타는 "작성 화면에서
                        위젯이 안 뜬다"로만 드러나 원인을 찾을 수 없고, 어떤 키가 유효한지는
                        코드를 읽지 않으면 알 방법이 없었다. 목록은 rc 레지스트리에서 파생하므로
                        새 특화 위젯이 등재되면 자동으로 후보가 된다. */}
                    <select
                      value={f.widgetKey ?? ''}
                      disabled={!fieldEditable}
                      onChange={(e) =>
                        onChange(
                          patchDataSourceField(dataSources, defIndex, fi, {
                            widgetKey: e.target.value || undefined,
                          }),
                        )
                      }
                      className="w-full min-w-0 text-sm border border-gray-300 rounded px-1.5 py-1 bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-brand-400"
                      title="특화 위젯 구현 키(작성 화면이 주입)"
                    >
                      <option value="">— 없음 —</option>
                      {/* 저장값이 목록 밖이면(레지스트리에서 빠진 옛 키) 보존 옵션으로 남긴다 —
                          조용히 비우면 재저장 때 배선이 사라진다(actionItem 보존 규약 동형). */}
                      {f.widgetKey && !REGISTERED_ROW_WIDGET_KEYS.includes(f.widgetKey) ? (
                        <option value={f.widgetKey}>{f.widgetKey} (목록에 없음 — 보존)</option>
                      ) : null}
                      {REGISTERED_ROW_WIDGET_OPTIONS.map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {/* 은닉① 인과 — 이 키를 고르는 것만으로 서버 주입이 켜진다는 사실은 코드
                        (templateNeedsRiskConfigInjection)에만 있었다. */}
                    {widgetKeyImpliesRiskInjection(f.widgetKey) && (
                      <p className="mt-1 text-[10px] leading-snug text-gray-400">
                        {RISK_NESTED_BINDING_HINT}
                      </p>
                    )}
                    {/* 은닉 방지 — 개선 이미지 위젯이 동작하는 조건(작성 화면의 호스트 주입)과 값·
                        입력 키는 rc 사전(IMPROVEMENT_IMAGE_HOST_HINT)이 소유한다(표면별 문구 사본 금지).
                        키→안내 판정은 registeredWidgetKeyHint — 트리 등록형 위젯 패널과 같은 함수. */}
                    {registeredWidgetKeyHint(f.widgetKey) && (
                      <p className="mt-1 text-[10px] leading-snug text-gray-400">
                        {registeredWidgetKeyHint(f.widgetKey)}
                      </p>
                    )}
                  </>
                ) : dataSourceFieldSettingsKind(f.widget) === 'image-display' ? (
                  // 사진 표시 크기 — 전폭(기존 결)이 기본이고, 행마다 사진이 붙는 카드에서만
                  // 썸네일로 줄인다. ⚠️ 여기만 `disabled={!fieldEditable}`을 걸지 않는다:
                  // image 계열은 언제나 표시 전용이라 실사용 필드가 대개 editable:false인데,
                  // 잠그면 정작 이 옵션이 필요한 필드에서 못 고른다(편집 가능 여부와 표시 크기는
                  // 다른 축이다 — 위젯 키·서명자 이름은 '입력 배선'이라 잠그는 것이 맞다).
                  <select
                    value={f.displayVariant ?? ''}
                    onChange={(e) =>
                      onChange(
                        patchDataSourceField(dataSources, defIndex, fi, {
                          displayVariant: e.target.value === 'thumbnail' ? 'thumbnail' : undefined,
                        }),
                      )
                    }
                    className="w-full min-w-0 text-sm border border-gray-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
                    title="작성 화면 행 카드에서 이 사진을 얼마나 크게 보일지"
                  >
                    <option value="">전폭 (기본)</option>
                    <option value="thumbnail">썸네일 (카드 폭 30%)</option>
                  </select>
                ) : dataSourceFieldSettingsKind(f.widget) === 'signer-name' ? (
                  <SignatureSlotFieldSettings
                    def={def}
                    defIndex={defIndex}
                    field={f}
                    fieldIndex={fi}
                    dataSources={dataSources}
                    disabled={!fieldEditable}
                    onChange={onChange}
                  />
                ) : dataSourceFieldSettingsKind(f.widget) === 'finding-write-back' ? (
                  // 사진 업로드 → 결재 완료 시 점검 발견사항 조치사진 반영(findingWriteBack). 조건·잠금은 shared
                  // judgeFindingWriteBackField — 서버 수집기와 같은 함수(은닉 방지). 작성폼 트리의 같은
                  // 필드 photo 위젯 패널(WidgetWriteBackControl)에도 같은 컨트롤이 있다(같은 선언을 편집).
                  <FindingWriteBackFieldSettings
                    def={def}
                    defIndex={defIndex}
                    field={f}
                    fieldIndex={fi}
                    dataSources={dataSources}
                    disabled={!fieldEditable}
                    onChange={onChange}
                  />
                ) : (
                  <span className="text-xs text-gray-300">—</span>
                )}
              </td>
              <td className="py-2 pr-3 text-center align-middle">
                <input
                  type="checkbox"
                  checked={fieldEditable}
                  onChange={(e) =>
                    onChange(
                      patchDataSourceField(dataSources, defIndex, fi, {
                        editable: e.target.checked,
                      }),
                    )
                  }
                  className="h-4 w-4"
                />
              </td>
              <td className="py-2 pr-3 text-center align-middle">
                {/* 표시 — 끄면(hidden) 행 그리드 컬럼·요약 헤더에서 제외(행 id/조인 키 등 내부 필드).
                    데이터·양식 바인딩에는 유지. 저장값은 hidden(기본 undefined=표시)로 기록. */}
                <input
                  type="checkbox"
                  checked={f.hidden !== true}
                  onChange={(e) =>
                    onChange(
                      patchDataSourceField(dataSources, defIndex, fi, {
                        hidden: e.target.checked ? undefined : true,
                      }),
                    )
                  }
                  className="h-4 w-4"
                  title="작성폼 행 그리드 표시 여부"
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * 행 그리드(dataSourceRows) 위젯 속성 패널 — 그 위젯이 연결한 편집형 list def의 필드별 작성 위젯 매핑을
 * 편집(구 상시 DataSourceWidgetsEditor의 단일 def 분량). bindingKey=def.key로 dataSources에서 def를 찾는다.
 * def가 없으면(양식 탭에서 삭제된 orphan) 안내 + 빼기 버튼만. 결정: 2026-07-11 Phase 1.
 */
function DataSourceRowsPropertyPanel({
  widget,
  dataSources,
  onDataSourcesChange,
  labelCls,
  catalog,
}: {
  widget: InputWidget;
  dataSources?: DataSourceDef[];
  onDataSourcesChange: (next: DataSourceDef[]) => void;
  labelCls: string;
  /** 객체 필드 표기용(DataSourceFieldWidgetTable.catalog 참조). */
  catalog?: CatalogEntry[];
}) {
  const defs = dataSources ?? [];
  const defIndex = defs.findIndex((d) => d.key === widget.bindingKey);
  const def = defIndex >= 0 ? defs[defIndex] : undefined;

  if (!def) {
    return (
      <div className="space-y-3">
        <p className="rounded border border-dashed border-amber-300 bg-amber-50 px-3 py-3 text-xs text-amber-700">
          연결된 데이터셋(<span className="font-mono">{widget.bindingKey}</span>)을 찾을 수 없습니다.
          양식 편집 탭에서 삭제되었을 수 있습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="font-mono text-[11px] text-gray-400 break-all">{def.key}</p>
      <div>
        <label className={labelCls}>필드별 작성 위젯</label>
        <p className="mb-2 text-[11px] text-gray-400">
          데이터 계약(필드 키·값 경로·소스)은 양식 편집 탭에서 설정합니다.
        </p>
        <DataSourceFieldWidgetTable
          def={def}
          defIndex={defIndex}
          dataSources={defs}
          onChange={onDataSourcesChange}
          catalog={catalog}
        />
      </div>
    </div>
  );
}
